/* v78_355 - Permissionamento dinâmico padronizado + versionamento da aplicação.
 * v77_354 - Usuários/perfis: refinamentos de layout e vínculos.
 * v76_353 - AEC: nomenclatura unificada, usuários/perfis e permissionamentos reorganizados.
 * =============================================================================
 * ARQUIVO REORGANIZADO - VERSAO SEGURA PARA NAVEGACAO
 * =============================================================================
 * OBJETIVO
 * - manter o comportamento original do arquivo;
 * - adicionar apenas comentarios seguros para busca e navegacao;
 * - evitar comentarios inseridos dentro de JSX de forma invalida.
 *
 * COMO BUSCAR
 * - use Ctrl+F por tags como:
 *   [SEC_01_IMPORTS_FIREBASE]
 *   [START_MOD_AVALIACAO_SYSTEM]
 *   [END_MOD_FINANCEIRO_SYSTEM]
 *   [APP_GET_FIRST_ALLOWED_MODULE]
 *   [APP_RENDER__COLLAB_CENTER__TIME_TRACKING]
 *
 * OBSERVACAO
 * - as referencias de linhas abaixo apontam para o arquivo ORIGINAL.
 * =============================================================================
 * MAPA RAPIDO - SECOES
 * - [SEC_01_IMPORTS_FIREBASE]      ~18-79
 * - [SEC_02_CONSTANTES_SEED]       ~80-498
 * - [SEC_03_UTILITARIOS]           ~499-536
 * - [SEC_04_REGRAS_NEGOCIO]        ~537-969
 * - [SEC_05_SERVICOS]              ~970-1212
 * - [SEC_06_HOOKS_PERSISTENCIA]    ~1213-1287
 * - [SEC_07_UI_ATOMOS]             ~1288-1416
 * - [SEC_08_UI_COMPLEXOS]          ~1417-38925
 * - [SEC_09_APP_BOOTSTRAP]         ~38926-45110
 * =============================================================================
 * MAPA RAPIDO - MODULOS PRINCIPAIS
 * - [START_MOD_CONTROLE_PRODUTIVIDADE_SYSTEM] ~1417
 * - [START_MOD_STATUS_STACK_CELL]             ~2602
 * - [START_MOD_AUTH_SCREEN]                   ~2699
 * - [START_MOD_FORCE_PASSWORD_CHANGE_MODAL]   ~2896
 * - [START_MOD_DASHBOARD_SYSTEM]              ~2936
 * - [START_MOD_CADASTRO_MODAL]                ~5626
 * - [START_MOD_AUDIT_LOG_TAB]                 ~6752
 * - [START_MOD_PERCEPTION_CONFIG_MODAL]       ~6895
 * - [START_MOD_PERCEPTION_FORM_MODAL]         ~7155
 * - [START_MOD_CADASTROS_SYSTEM]              ~7368
 * - [START_MOD_APONTAMENTOS_SYSTEM]           ~12268
 * - [START_MOD_AVALIACAO_SYSTEM]              ~13175
 * - [START_MOD_FINANCEIRO_SYSTEM]             ~17482
 * - [START_MOD_PONTO_INPUT]                   ~21478
 * - [START_MOD_PONTO_MANAGEMENT]              ~21506
 * - [START_MOD_PONTO_CLOCK]                   ~21910
 * - [START_MOD_PONTO_BANK]                    ~22473
 * - [START_MOD_SURVEYS_SYSTEM]                ~24378
 * - [START_MOD_RH_SYSTEM]                     ~26467
 * - [START_MOD_RECRUITMENT_TEST_BUILDER]      ~29444
 * - [START_MOD_PUBLIC_TEST_RUNNER]            ~30066
 * - [START_MOD_RECOGNITION_TAB]               ~31290
 * - [START_MOD_ACAD_COINS_SYSTEM]             ~31986
 * - [START_MOD_CARD_NAVIGATION_MENU]          ~38120
 * - [START_MOD_PONTO_REPORTS_MODULE]          ~38485
 * - [START_MOD_APP_MAIN]                      ~38926
 * - [START_MOD_GLOBAL_STYLES]                 ~44561
 * - [START_MOD_PUBLIC_POINT_SHEET]            ~44667
 * - [START_MOD_ROOT_COMPONENT]                ~45071
 * =============================================================================
 */

/**
     * ============================================================================ff
     * SISTEMA DE AEC - ACOMPANHAMENTO DE EVOLUÇÃO CONTINUADA E PRODUTIVIDADE
     * ============================================================================
     * * Este arquivo contém a lógica principal, componentes UI e integração com Firebase
     * para o sistema de gestão de desempenho.
     * * ESTRUTURA DO ARQUIVO:
     * 1. IMPORTS E CONFIGURAÇÃO FIREBASE
     * 2. CONSTANTES GLOBAIS E SEED DATA (Dados Iniciais)
     * 3. UTILITÁRIOS GERAIS (Formatadores, Helpers)
     * 4. LÓGICA DE NEGÓCIO (Cálculos, Regras de Visualização, Tendências)
     * 5. CAMADA DE SERVIÇOS (Importação de Arquivos, PDF, AI)
     * 6. CUSTOM HOOKS (Persistência de Dados)
     * 7. COMPONENTES UI - ÁTOMOS (Botões, Inputs, Cards)
     * 8. COMPONENTES UI - COMPLEXOS (Auth, Tabelas, Modais)
     */

    /* ===============================================================================
     * [SEC_01_IMPORTS_FIREBASE]
     * INICIO DA SECAO 01 - IMPORTS E CONFIGURACAO FIREBASE
     * =============================================================================== */
    // --- 1. IMPORTS E CONFIGURAÇÃO FIREBASE ---
    // Gestao de Pessoas v82_359 - Dashboard/Resumo: titulo quantitativo padronizado como Avaliacao Quantitativa.
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

    // Certifique-se de que a linha abaixo está exatamente assim:
    import { initializeApp, getApp } from 'firebase/app';
    import { 
    getAuth, signInWithCustomToken, signInAnonymously, 
    onAuthStateChanged, signOut, setPersistence, browserSessionPersistence,
    // --- NOVOS IMPORTS ABAIXO ---
    GoogleAuthProvider, signInWithPopup 
    } from 'firebase/auth';

    import { 
    getFirestore, doc, collection, onSnapshot, setDoc, 
    deleteDoc, updateDoc, getDocs, query, where, writeBatch, addDoc, orderBy,
    arrayUnion, deleteField, getDoc
    } from 'firebase/firestore';

    // UI Icons (Lucide React) - CORRIGIDO
    import { 
    Loader2, LayoutDashboard, Users, Trophy, Medal, FileText, ClipboardCheck, 
    Plus, Save, Trash2, Sun, Moon, Search, ChevronRight, AlertCircle, TrendingUp, 
    TrendingDown, Minus, Sparkles, Settings, Database, UserCheck, RefreshCw, 
    Pencil, X, CheckSquare, Square, Printer, FileDown, Lock, CheckCircle, Send, ExternalLink, 
    Upload, Download, Eye, Calendar, Info, CircleHelp, Clock, AlertTriangle, 
    List, Award, Star, MessageSquare, Unlock, Edit3, Bot, Menu, ArrowLeft, 
    Filter, FileSpreadsheet, BadgeDollarSign, Coins, History, UserPlus, Key,
    Shield, UserCog, ToggleLeft, ToggleRight, FileCheck, Eraser, LogOut,
    Mail, Briefcase, User, Check, PenTool, BookOpen, Archive, Fingerprint, FileUp, Paperclip, Maximize2,
    Copy, Building, ChevronUp, ChevronDown, Heart, Globe, Gift, Play, UploadCloud, Folder, Settings2, Bell,
    Grid, ChevronLeft, ShieldCheck, XCircle, MessageCircle, Reply, Megaphone
    } from 'lucide-react';

    import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, 
    LineChart, Line, PieChart, Pie, Cell, LabelList,
    RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar 
    } from 'recharts';

    // Configuração do App ID (RESTAURADO PARA RECUPERAR SEU BANCO DE DADOS REAL)
    // TRAVADO MANUALMENTE para evitar que ambientes de preview criem subpastas fantasmas no banco de dados.
    const appId = 'default-app-id';

    // Credenciais Firebase
    const firebaseConfig = {
    apiKey: "AIzaSyAm_sOKndqhqyMz2fm9WWApDDo8iYv1ZQE",
    authDomain: "apex-grupoacadcontabilidade.firebaseapp.com",
    projectId: "apex-grupoacadcontabilidade",
    storageBucket: "apex-grupoacadcontabilidade.firebasestorage.app",
    messagingSenderId: "450158161974",
    appId: "1:450158161974:web:abd2a8c283c59791b66897",
    measurementId: "G-PVRC2MCBYM"
    };

    // Inicialização Global de variáveis do Firebase (serão atribuídas no useEffect principal do app)
    let db, auth;
    const initialAuthToken = null; 

    // Caminho base para Firestore
    const FIRESTORE_PATH_PUBLIC = `artifacts/${appId}/public/data`;


    // ============================================================================
    // VERSIONAMENTO DA APLICAÇÃO — padrão sequencial adotado no ecossistema ACAD
    // Incrementar APP_VERSION a cada nova versão publicada do código.
    // APP_BUILD_ID identifica exatamente o build/deploy para diagnóstico de cache.
    // ============================================================================
    const APP_VERSION = 'v1.007';
    const APP_BUILD_ID = '20261029-1217';


    // Padroniza a terminologia quantitativa exibida ao usuario sem alterar chaves/dados legados no banco.
    const formatOccurrenceTerm = (value) => {
        if (typeof value !== 'string') return value;
        return value
            .replace(/\bErros\b/g, 'Ocorrências')
            .replace(/\berros\b/g, 'ocorrências')
            .replace(/\bErro\b/g, 'Ocorrência')
            .replace(/\berro\b/g, 'ocorrência');
    };
    const PEOPLE_PERMISSION_APP_ID = 'PESSOAS';
    const PEOPLE_PERMISSION_APP_NAME = 'Gestão de Pessoas';

    if (typeof window !== 'undefined') {
        window.__PESSOAS_APP_VERSION__ = APP_VERSION;
        window.__PESSOAS_APP_BUILD_ID__ = APP_BUILD_ID;
        try {
            document.documentElement.dataset.pessoasVersion = APP_VERSION;
            document.documentElement.dataset.pessoasBuild = APP_BUILD_ID;
        } catch (_) {}
    }


    /* ===============================================================================
     * [END_SEC_01_IMPORTS_FIREBASE]
     * [SEC_02_CONSTANTES_SEED]
     * FIM DA SECAO 01 / INICIO DA SECAO 02 - CONSTANTES GLOBAIS E SEED DATA
     * =============================================================================== */
    // --- 2. CONSTANTES GLOBAIS E SEED DATA ---

    /** Módulos do Sistema e suas chaves de permissão */
    const SYSTEM_MODULES = [
        {id: 'dash', label: 'Dashboard', permKey: 'access_dash'},
        {id: 'reg', label: 'Cadastros', permKey: 'access_reg'},
        {id: 'apt', label: 'Ocorrências', permKey: 'access_apt'},
        {id: 'eval', label: 'Avaliação', permKey: 'access_eval'},
        {id: 'fin', label: 'Financeiro', permKey: 'access_fin'}
    ];

    /** Critérios Qualitativos Padrão (Colaborador) */
    const DEFAULT_QUALI = [
        { id: 'q1', dim: 'Institucional', name: 'ORIENTAÇÃO PARA RESULTADOS', description: 'Concentra-se nos resultados.', order: 1, personType: 'Colaborador' },
        { id: 'q2', dim: 'Institucional', name: 'MARKETING PESSOAL', description: 'Aparência e postura.', order: 2, personType: 'Colaborador' },
        { id: 'q3', dim: 'Funcional', name: 'QUALIDADE E PRODUTIVIDADE', description: 'Atividades precisas.', order: 3, personType: 'Colaborador' },
        { id: 'q4', dim: 'Funcional', name: 'HABILIDADE TÉCNICA', description: 'Conhecimento técnico.', order: 4, personType: 'Colaborador' },
        { id: 'q5', dim: 'Funcional', name: 'ENERGIA E DISPOSIÇÃO', description: 'Entusiasmo.', order: 5, personType: 'Colaborador' },
        { id: 'q6', dim: 'Funcional', name: 'PONTUALIDADE/ASSIDUIDADE', description: 'Cumpre jornada.', order: 6, personType: 'Colaborador' },
        { id: 'q7', dim: 'Funcional', name: 'TRABALHO EM EQUIPE', description: 'Cooperação.', order: 7, personType: 'Colaborador' },
        { id: 'q8', dim: 'Individual', name: 'ATUALIZAÇÃO', description: 'Desenvolvimento.', order: 8, personType: 'Colaborador' },
        { id: 'q9', dim: 'Individual', name: 'FOCO NO CLIENTE', description: 'Solução de problemas.', order: 9, personType: 'Colaborador' },
        { id: 'q10', dim: 'Individual', name: 'FLEXIBILIDADE', description: 'Adaptação.', order: 10, personType: 'Colaborador' },
        { id: 'q11', dim: 'Individual', name: 'RELACIONAMENTO', description: 'Trato com pessoas.', order: 11, personType: 'Colaborador' },
        { id: 'q12', dim: 'Individual', name: 'TRABALHO SOB PRESSÃO', description: 'Prazos e demandas.', order: 12, personType: 'Colaborador' },
    ];

    /** Critérios Quantitativos Padrão (Penalidades) */
    const DEFAULT_QUANTI = [
        { id: 'qt1', name: '[0] Conferências sem erros', peso: 0, personType: 'Colaborador' },
        { id: 'qt2', name: '[1] Descumprimento de Norma', peso: 0, personType: 'Colaborador' },
        { id: 'qt3', name: '[2] Ocorrência leve', peso: 0.5, personType: 'Colaborador' },
        { id: 'qt4', name: '[3] Prejuízo interno (até R$ 50)', peso: 1, personType: 'Colaborador' },
        { id: 'qt5', name: '[3] Prejuízo interno (> R$ 50)', peso: 3, personType: 'Colaborador' },
        { id: 'qt6', name: '[4] Insatisfação cliente (sem prejuízo)', peso: 5, personType: 'Colaborador' },
        { id: 'qt7', name: '[5] Insatisfação + Prejuízo (até R$ 50)', peso: 9, bgColor: '#ef4444', textColor: '#ffffff', personType: 'Colaborador' }, 
        { id: 'qt8', name: '[5] Insatisfação + Prejuízo (> R$ 50)', peso: 11, bgColor: '#b91c1c', textColor: '#ffffff', personType: 'Colaborador' } 
    ];

    /// --- LISTA DE FRASES MOTIVACIONAIS (ATUALIZADA COM NOVAS INCLUSÕES) ---
    const MOTIVATIONAL_QUOTES = [
        // --- CLÁSSICOS DO SUCESSO E TRABALHO ---
        "O sucesso é a soma de pequenos esforços repetidos dia após dia. – Robert Collier",
        "A única maneira de fazer um ótimo trabalho é amar o que você faz. – Steve Jobs",
        "Não espere por oportunidades. Crie-as. – George Bernard Shaw",
        "O futuro pertence àqueles que acreditam na beleza de seus sonhos. – Eleanor Roosevelt",
        "A persistência é o caminho do êxito. – Charles Chaplin",
        "Só existe um êxito: a capacidade de levar a vida que se quer. – Christopher Morley",
        "A coragem não é ausência do medo; é a persistência apesar do medo. – Mark Twain",
        "Tudo o que um sonho precisa para ser realizado é de alguém que acredite que ele possa ser realizado. – Roberto Shinyashiki",
        "O insucesso é apenas uma oportunidade para recomeçar com mais inteligência. – Henry Ford",
        "Você nunca é velho demais para definir outro objetivo ou sonhar um novo sonho. – C.S. Lewis",
        "Acredite em si próprio e todo o resto virá naturalmente. – Elon Musk",
        "A satisfação reside no esforço, não no resultado. O esforço total é a vitória total. – Mahatma Gandhi",
        "Talento é dom, é graça. E sucesso nada tem a ver com sorte, mas com determinação e trabalho. – Augusto Branco",
        "Para de perseguir o dinheiro e comece a perseguir o sucesso. – Tony Hsieh",
        "A chave do sucesso é focar nossa mente nas coisas que desejamos, não nas que tememos. – Brian Tracy",
        "Não tenha medo de desistir do bom para perseguir o ótimo. – John D. Rockefeller",
        "O sucesso normalmente vem para quem está ocupado demais para procurar por ele. – Henry David Thoreau",
        "O segredo do sucesso é a constância do propósito. – Benjamin Disraeli",
        "A verdadeira motivação vem de realização, desenvolvimento pessoal, satisfação no trabalho e reconhecimento. – Frederick Herzberg",
        "Liderança é a capacidade de traduzir a visão em realidade. – Warren Bennis",
        "Se você quer chegar onde a maioria não chega, faça o que a maioria não faz. – Bill Gates",
        "A disciplina é a mãe do êxito. – Ésquilo",
        "Mesmo que a jornada seja lenta, abdicar não acelera. – Provérbio",
        "O sucesso não é o final, o fracasso não é fatal: é a coragem de continuar que conta. – Winston Churchill",
        "A sorte favorece a mente preparada. – Louis Pasteur",
        "Sorte é o que acontece quando a preparação encontra a oportunidade. – Sêneca",
        "Seja a mudança que você quer ver no mundo. – Mahatma Gandhi",
        "O único lugar onde o sucesso vem antes do trabalho é no dicionário. – Vidal Sassoon",
        "Não se orgulhe de trabalhar muito. Se orgulhe de produzir bastante enquanto trabalha. – Kreisler R. Belo",
        "Nós somos o que fazemos repetidamente. A excelência, portanto, não é um ato, mas um hábito. – Aristóteles",
        "Não encontro defeitos. Encontro soluções. Qualquer um sabe queixar-se. – Henry Ford",
        "Aquele que não tem confiança nos outros, não lhes pode ganhar a confiança. – Lao Tsé",
        "O sucesso é ir de fracasso em fracasso sem perder o entusiasmo. – Winston Churchill",
        "A maior glória de viver não está em nunca cair, mas em nos levantarmos toda vez que caímos. – Nelson Mandela",
        "O homem que remove uma montanha começa carregando pequenas pedras. – Provérbio Chinês",
        "Acredite que você pode, assim você já está no meio do caminho. – Theodore Roosevelt",
        "Vontade de vencer, desejo de sucesso, o impulso para alcançar todo o seu potencial... estas são as chaves que irão abrir a porta para a excelência pessoal. – Confúcio",
        "Onde há uma vontade, há um caminho. – Provérbio Inglês",
        "A diferença entre o impossível e o possível reside na determinação de um homem. – Tommy Lasorda",
        "O fracasso é o tempero que dá sabor ao sucesso. – Truman Capote",

        // --- SABEDORIA BÍBLICA E ESPIRITUAL (FOCO EM FORÇA E TRABALHO) ---
        "Tudo posso naquele que me fortalece. – Filipenses 4:13",
        "Seja forte e corajoso! Não se apavore nem desanime. – Josué 1:9",
        "Consagre ao Senhor tudo o que você faz, e os seus planos serão bem-sucedidos. – Provérbios 16:3",
        "O ferro afia o ferro, e uma pessoa afia a outra. – Provérbios 27:17",
        "Tudo o que fizerem, façam de todo o coração, como para o Senhor, e não para os homens. – Colossenses 3:23",
        "Pois Deus não nos deu espírito de covardia, mas de poder, de amor e de equilíbrio. – 2 Timóteo 1:7",
        "Há tempo para tudo, e um tempo para todo o propósito debaixo do céu. – Eclesiastes 3:1",
        "Até aqui nos ajudou o Senhor. – 1 Samuel 7:12",
        "Aquele que observa o vento não plantará; e o que olha para as nuvens não colherá. – Eclesiastes 11:4",
        "Onde não há visão, o povo perece. – Provérbios 29:18",
        "Não nos cansemos de fazer o bem, pois no tempo próprio colheremos, se não desanimarmos. – Gálatas 6:9",
        "Vistam-se de toda a armadura de Deus, para que possam resistir. – Efésios 6:11",
        "Alegrem-se na esperança, sejam pacientes na tribulação, perseverem na oração. – Romanos 12:12",
        "Assim como a água reflete o rosto, o coração reflete quem somos. – Provérbios 27:19",
        "Prepara o trabalho de fora, apronta o teu campo, e depois edifica a tua casa. – Provérbios 24:27",
        "O preguiçoso deseja e nada tem, mas a alma dos diligentes engorda. – Provérbios 13:4",
        "Tudo tem o seu tempo determinado, e há tempo para todo o propósito debaixo do céu. – Eclesiastes 3:1",
        "Se podes crer, tudo é possível ao que crê. – Marcos 9:23",
        "Esquecendo-me das coisas que atrás ficam, avanço para as que estão diante de mim. – Filipenses 3:13",
        "Ainda que eu andasse pelo vale da sombra da morte, não temeria mal algum. – Salmos 23:4",
        "Porque sou eu que conheço os planos que tenho para vocês, diz o Senhor, planos de fazê-los prosperar e não de causar dano, planos de dar a vocês esperança e um futuro. – Jeremias 29:11",
        "Mas os que esperam no Senhor renovarão as forças, subirão com asas como águias; correrão, e não se cansarão; caminharão, e não se fatigarão. – Isaías 40:31",
        "O coração alegre aformoseia o rosto. – Provérbios 15:13",
        "Ensina-nos a contar os nossos dias, de tal maneira que alcancemos corações de sabedoria. – Salmos 90:12",
        "Pois onde estiver o vosso tesouro, aí estará também o vosso coração. – Mateus 6:21",

        // --- LIDERANÇA, GESTÃO E INOVAÇÃO ---
        "As pessoas não compram o que você faz, elas compram o porquê você faz. – Simon Sinek",
        "Liderar não é impor; liderar é despertar nos outros a vontade de fazer. – James Hunter",
        "Gerenciar é fazer as coisas direito. Liderar é fazer a coisa certa. – Peter Drucker",
        "A melhor maneira de prever o futuro é criá-lo. – Peter Drucker",
        "Inovação distingue um líder de um seguidor. – Steve Jobs",
        "Se você não pode medir, você não pode gerenciar. – Peter Drucker",
        "Qualidade significa fazer certo quando ninguém está olhando. – Henry Ford",
        "A tecnologia é apenas uma ferramenta. No que diz respeito a motivar as crianças e fazê-las trabalhar juntas, o professor é o mais importante. – Bill Gates",
        "Não tenha receio de desistir do bom para correr atrás do ótimo. – John D. Rockefeller",
        "Um líder é um vendedor de esperança. – Napoleão Bonaparte",
        "Liderança e aprendizagem são indispensáveis um ao outro. – John F. Kennedy",
        "Reunir-se é um começo, permanecer juntos é um progresso, e trabalhar juntos é um sucesso. – Henry Ford",
        "Nenhum de nós é tão inteligente quanto todos nós juntos. – Ken Blanchard",
        "Se quer ir rápido, vá sozinho. Se quer ir longe, vá em grupo. – Provérbio Africano",
        "Grandes coisas em negócios nunca são feitas por uma única pessoa. São feitas por uma equipe de pessoas. – Steve Jobs",
        "A maior habilidade de um líder é desenvolver habilidades extraordinárias em pessoas comuns. – Abraham Lincoln",
        "Cultura é o que acontece quando as pessoas não estão sendo supervisionadas. – Desconhecido",
        "Contrate caráter. Treine habilidades. – Peter Schutz",
        "O cliente mais insatisfeito é sua melhor fonte de aprendizado. – Bill Gates",
        "Você não constrói nada sozinho. Você descobre o que as pessoas querem e constrói para elas. – Walt Disney",
        "A inovação é o que distingue um líder de um seguidor. – Steve Jobs",
        "Um bom líder toma um pouco mais de sua parte na culpa e um pouco menos de sua parte no crédito. – Arnold Glasow",
        "Gerenciamento é substituir músculos por pensamentos, folclore e superstição por conhecimento, e força por cooperação. – Peter Drucker",
        "O melhor executivo é aquele que tem senso suficiente para escolher bons homens para fazer o que ele quer que seja feito, e autocontrole suficiente para não se intrometer com eles enquanto o fazem. – Theodore Roosevelt",
        "Não diga às pessoas como fazer as coisas, diga-lhes o que fazer e deixe-as surpreendê-lo com seus resultados. – George S. Patton",

        // --- RESILIÊNCIA E SUPERAÇÃO ---
        "O que não provoca minha morte faz com que eu fique mais forte. – Friedrich Nietzsche",
        "Não importa o quão devagar você vá, desde que você não pare. – Confúcio",
        "A adversidade desperta em nós capacidades que, em circunstâncias favoráveis, teriam ficado adormecidas. – Horácio",
        "Pedras no caminho? Eu guardo todas. Um dia vou construir um castelo. – Fernando Pessoa",
        "É parte da cura o desejo de ser curado. – Sêneca",
        "No meio da dificuldade encontra-se a oportunidade. – Albert Einstein",
        "A vida é 10% o que acontece comigo e 90% como eu reajo a isso. – Charles Swindoll",
        "Caia sete vezes. Levante-se oito. – Provérbio Japonês",
        "Você não é derrotado quando perde. Você é derrotado quando desiste. – O Poderoso Chefão",
        "A glória não consiste em jamais cair, mas em levantar-se a cada vez que caímos. – Oliver Goldsmith",
        "Vencedores não são pessoas que nunca falham, são pessoas que nunca desistem. – Edwin Louis Cole",
        "A dor é temporária. A desistência dura para sempre. – Lance Armstrong",
        "Não sabendo que era impossível, foi lá e fez. – Jean Cocteau",
        "O guerreiro de sucesso é um homem comum com um foco de laser. – Bruce Lee",
        "Obstáculos não podem parar você. Se você topar com uma parede, não vire e desista. Descubra como escalá-la. – Michael Jordan",
        "A persistência é a irmã gêmea da excelência. Uma é mãe da qualidade, a outra é a mãe do tempo. – Marabel Morgan",
        "Nossa maior fraqueza está em desistir. A maneira mais certa de ter sucesso é sempre tentar apenas mais uma vez. – Thomas Edison",
        "Coragem é a resistência ao medo, domínio do medo, e não a ausência do medo. – Mark Twain",
        "Embora ninguém possa voltar atrás e fazer um novo começo, qualquer um pode começar agora e fazer um novo fim. – Chico Xavier",
        "A vida não é sobre esperar a tempestade passar, é sobre aprender a dançar na chuva. – Vivian Greene",

        // --- FOCO E PRODUTIVIDADE ---
        "Falta de direção, não falta de tempo, é o problema. Todos nós temos dias de 24 horas. – Zig Ziglar",
        "Não conte os dias, faça os dias contarem. – Muhammad Ali",
        "Produtividade nunca é um acidente. É sempre o resultado de comprometimento com a excelência, planejamento inteligente e esforço focado. – Paul J. Meyer",
        "Concentre-se em ser produtivo, em vez de ocupado. – Tim Ferriss",
        "O simples ato de prestar atenção positiva às pessoas tem muito a ver com a produtividade. – Tom Peters",
        "Comece de onde você está. Use o que você tem. Faça o que você pode. – Arthur Ashe",
        "A mente que se abre a uma nova ideia jamais voltará ao seu tamanho original. – Oliver Wendell Holmes Sr.",
        "Otimismo é a fé em ação. Nada se pode levar a efeito sem otimismo. – Helen Keller",
        "Se tudo parece sob controle, você não está indo rápido o suficiente. – Mario Andretti",
        "Planejamento, organização e motivação são fatores essenciais para a prosperidade. – Desconhecido",
        "A única coisa que cai do céu é chuva. O resto é luta. – Desconhecido",
        "Aja como se o que você faz fizesse a diferença. Faz. – William James",
        "O primeiro passo para chegar a algum lugar é decidir que você não vai ficar onde está. – J.P. Morgan",
        "Defina metas altas e não pare até chegar lá. – Bo Jackson",
        "A melhor época para plantar uma árvore foi há 20 anos. A segunda melhor é agora. – Provérbio Chinês",
        "A excelência não é um feito, mas um hábito. O sucesso é a consequência natural de fazer o melhor sempre. – Aristóteles",
        "Sua carreira é uma maratona, não um tiro de 100 metros. Respire e continue. – Desconhecido",
        "Não deixe que o medo de perder seja maior que a excitação de ganhar. – Robert Kiyosaki",
        "Você perde 100% dos tiros que não dá. – Wayne Gretzky",
        "O pessimista vê dificuldade em cada oportunidade; o otimista vê oportunidade em cada dificuldade. – Winston Churchill",
        "Amadores sentam e esperam por inspiração. O resto de nós apenas se levanta e vai trabalhar. – Stephen King",
        "A maneira de começar é parar de falar e começar a fazer. – Walt Disney",
        "O segredo de ir em frente é começar. – Mark Twain",
        "Se você tem tempo para reclamar, tem tempo para encontrar uma solução. – Dee Dee Artner",
        "Não é o que fazemos de vez em quando que molda nossas vidas. É o que fazemos consistentemente. – Tony Robbins"
    ];

    /** Configurações Globais Padrão */
    const DEFAULT_GLOBAL = [{ 
        id: 'def', name: 'Padrão 2024', mediaPromocao: 8.0, mediaPremiacao: 7.0, 
        maxPointsLost: 10.0, startDate: '2024-01-01', endDate: '2025-12-31', 
        promoBgColor: '#FFD700', promoTextColor: '#8B4513', awardBgColor: '#DCDCDC', awardTextColor: '#2F4F4F' 
    }];


    // --- CONSTANTE DE PROMPT PARA IA (CORREÇÃO AVANÇADA) ---
    const SYSTEM_CORRECTION_PROMPT = `
    <goal>
    Realizar a correção automatizada de questões discursivas e de resposta curta em processos seletivos, avaliando a resposta do candidato com foco em equivalência conceitual (não literal), aplicando a rubrica e as regras de tolerância a variações semânticas, e retornando um parecer estruturado com nota, classificação e justificativa técnica.
    </goal>

    <PlanningAndVerification>
    1) Identificar o tipo de questão e selecionar a regra de pontuação.
    2) Ler o enunciado e a competência avaliada.
    3) Extrair do gabarito expandido: conceito principal, respostas aceitáveis, variações permitidas.
    4) Comparar a resposta do candidato com o conceito esperado (equivalência semântica).
    5) Para resposta curta: correta (1), parcialmente correta (0.5), incorreta (0).
    6) Para discursiva: pontuar de 0 a 10 (compreensão, técnica, lógica, aplicabilidade, clareza).
    7) Autoauditoria: O conceito está correto? Se sim, considerar válida mesmo com erros de forma.
    8) Produzir saída JSON estruturada.
    </PlanningAndVerification>

    <return_format>
    Responder APENAS um JSON puro (sem markdown) com os campos:
    {
        "percentual": numero_de_0_a_1,
        "classificacao": "Correta" | "Parcialmente correta" | "Incorreta",
        "analise": "Justificativa técnica objetiva e verificável.",
        "pontos_positivos": "Lista de acertos",
        "erros": "Lista de erros",
        "obs_semantica": "Observação sobre equivalência (sinônimos, siglas, etc)"
    }
    </return_format>

    <ReasoningSteps>
    - Resposta curta: 
        * Equivalente/Suficiente -> 1.0 (Correta)
        * Relacionada mas incompleta/ambígua -> 0.5 (Parcial)
        * Incorreta/Contraditória -> 0.0 (Incorreta)
        * Não descontar por acentuação ou pequenos erros ortográficos.
    - Discursiva:
        * Avaliar compreensão, correção técnica, lógica, aplicabilidade e clareza.
        * Conceder crédito parcial por raciocínio correto.
    </ReasoningSteps>

    <warnings>
    - Não exigir correspondência literal. Avaliar equivalência conceitual.
    - Ignorar erros de acentuação/ortografia que não mudem o sentido.
    - Penalizar erros conceituais e confusões técnicas.
    - Considerar explicitamente: sinônimos, siglas, abreviações e variações permitidas.
    </warnings>
    `;

    /** * DADOS DE SEED (Inicialização do Banco de Dados)
     * Contém estrutura inicial de departamentos, usuários, avaliações e configurações.
     */
    const SEED_DATA = {
        departments: [
            {id: 'd_ctb', name: 'CTB', startDate: '2024-01-01', endDate: ''},
            {id: 'd_fsc', name: 'FSC', startDate: '2024-01-01', endDate: ''},
        ],
        managers: [{id: 'm1', name: 'Roberto Gestor', departmentId: 'd_ctb', startDate: '2024-01-01', endDate: '', email: 'roberto@corp.com'}],
        collaborators: [
            // --- ALTERAÇÃO AQUI: ANA SILVA VINCULADA AO SUPER ADM ---
            {
                id: 'c1', 
                name: 'Ana Silva', 
                role: 'Vendedora', 
                seniority: 'JR', 
                level: 'I', 
                departmentId: 'd_ctb', // Mudado para CTB (ID d_ctb)
                managerIds: ['m1'], 
                evalTypeId: 'et1', 
                personType: 'Colaborador', 
                department: 'CTB',     // Mudado para CTB (Sigla)
                salary: 1500,          // Mudado para 1500
                startDate: '2024-01-01', 
                endDate: '', 
                email: 'super@adm.com' // Mudado para vincular ao login
            },
            // --------------------------------------------------------
            {id: 'c2', name: 'Carlos Gerente', role: 'Gerente Geral', seniority: 'SR', level: 'III', departmentId: 'd_dir', managerIds: [], evalTypeId: 'et1', personType: 'Gestão', department: 'DIR', salary: 8000, startDate: '2024-01-01', endDate: '', email: 'carlos@corp.com'},
            {id: 'c3', name: 'Bruno Contador', role: 'Contador', seniority: 'PL', level: 'II', departmentId: 'd_ctb', managerIds: [], evalTypeId: 'et1', personType: 'Colaborador', department: 'CTB', salary: 5000, startDate: '2024-01-01', endDate: '', email: 'bruno@corp.com'}
        ],
        evaluationTypes: [{
            id: 'et1', name: 'Vendas', mediaPromocao: 8.5, mediaPremiacao: 7.5, maxPointsLost: 5.0, startDate: '2024-01-01', endDate: '2024-12-31', 
            selectedQualiIds: ['q1','q2','q7','q9','q11'], selectedQuantiIds: ['qt4','qt6','qt8'], personType: 'Colaborador'
        }],
        pdfMessages: [
            {id: 'pm1', type: 'Promoção', personType: 'Colaborador', departmentId: 'all', title: 'Parabéns pela Promoção em {AnoAtual}!', body: 'É com orgulho que comunicamos sua promoção.', bgColor: '#FFD700', textColor: '#000000'},
            {id: 'pm2', type: 'Premiação', personType: 'Colaborador', departmentId: 'all', title: 'Parabéns pela Premiação em {AnoAtual}!', body: 'Parabéns pelo seu desempenho.', bgColor: '#DCDCDC', textColor: '#2F4F4F'},
            {id: 'pm3', type: 'Promoção', personType: 'Gestão', departmentId: 'all', title: 'Excelente Desempenho e Promoção!', body: 'Sua liderança foi excepcional e resulta em sua promoção.', bgColor: '#00BFFF', textColor: '#FFFFFF'},
            {id: 'pm4', type: 'Premiação', personType: 'Gestão', departmentId: 'all', title: 'Parabéns pela Premiação da Gestão!', body: 'Sua atuação gerou resultados notáveis, parabéns pela sua premiação.', bgColor: '#DCDCDC', textColor: '#2F4F4F'},
        ],
        pcs: [
            { id: 'pcs1', role: 'Vendedora', seniority: 'JR', level: 'I', department: 'COM', salary: 1500, validFrom: '2024-01-01' },
            { id: 'pcs2', role: 'Vendedora', seniority: 'JR', level: 'II', department: 'COM', salary: 1800, validFrom: '2024-01-01' },
        ],
                users: [
            {
                id: 'u_preview_god',
                name: 'Mauricio SuperAdm',
                nickname: 'Bremide',
                email: 'super@adm.com',
                password: '123',
                role: 'SuperAdm',
                departmentId: 'd_ctb',
                authProvider: 'local',
                previewOnly: true,
                permissions: {
                    viewGestao: true,
                    access_dash: true,
                    access_reg: true,
                    access_apt: true,
                    access_eval: true,
                    access_fin: true,
                    access_tab_users: true,
                    access_rh: true,
                    access_collab_center: true,
                    access_proj_av_desempenho: true,
                    access_tab_dept: true,
                    access_tab_manager: true,
                    access_tab_criteria: true,
                    access_sub_criteria_list: true,
                    access_sub_conference_types: true,
                    access_tab_types: true,
                    access_tab_collab: true,
                    access_tab_config: true,
                    access_sub_metricas: true,
                    access_sub_deadlines: true,
                    access_sub_messages: true,
                    access_sub_competencias: true,
                    access_sub_minwage: true,
                    access_sub_logo: true,
                    access_sub_migration: true,
                    access_sub_users_manage: true,
                    access_sub_users_approve: true,
                    access_tab_audit: true,
                    access_tab_pcs: true,
                    access_tab_ponto: true,
                    access_sub_ponto_rules: true,
                    access_sub_ponto_sits: true,
                    ponto_can_manual: true,
                    ponto_can_justify: true,
                    ponto_can_edit: true,
                    ponto_can_delete: true,
                    access_tab_training: true,
                    access_sub_capacitacoes: true,
                    access_sub_percepcao: true,
                    access_sub_ai: true,
                    access_sub_collab_area_config: true,
                    access_tab_backup: true,
                    access_rh_central: true,
                    access_rh_feedbacks: true,
                    access_tab_fin_central: true,
                    access_tab_feedbacks: true,
                    access_tab_proj: true,
                    access_sub_execute_promo: true,
                    access_sub_fin_blind_gestao: true,
                    access_tab_analise: true,
                    access_talent: true,
                    access_rh_surveys: true,
                    collab_tab_time: true,
                    collab_tab_training: true,
                    collab_tab_perception: true,
                    access_sub_surveys: true,
                    collab_tab_surveys: true,
                    access_surveys_config: true,
                    collab_tab_signatures: true,
                    collab_tab_growth: true,
                    collab_tab_recognition: true,
                    collab_tab_acad_coins: true,
                    acad_coins_admin: true,
                    eval_view_calibration: true,
                    eval_edit_goals_locked: true,
                    eval_view_9box: true,
                    eval_reopen_cycle: true,
                    eval_bypass_deadlines: true,
                    eval_delete_record: true,
                    viewGestaoCadastros: true,
                    viewGestaoDashEval: true,
                    allow_view_change: true,
                    access_proj_folha_ponto: true,
                    access_ponto_basic: true,
                    access_proj_contr_prod: true,
                    access_prod_basic: true
                }
            }
        ],
        activePermissions: { viewGestao: false },
        evaluations: [
            {
                id: 'e1', status: 'Finalizada', collaboratorId: 'c1', collaboratorName: 'Ana Silva', periodYear: '2025', department: 'CTB', role: 'Vendedora', seniority: 'JR', level: 'I', evalTypeId: 'et1', personType: 'Colaborador',
                qualitative: { criteria: DEFAULT_QUALI.filter(q => ['q1','q2','q7','q9','q11'].includes(q.id)).map(cr => ({...cr, q1:3.0, q2:3.5, q3:4.0, q4:null})), finalAverage: 8.8 }, 
                quantitative: { errors: DEFAULT_QUANTI.filter(q => ['qt4','qt6','qt8'].includes(q.id)).map(e => ({...e, q1:0, q2:1, q3:0, q4:0})), meanPoints: 2.0 }, 
                feedbacks: { q1:'Bom início', q2:'Manter foco', q3:'Excelente', q4:'' }, feedbacksHistory: {}, pdi: { knowledge: '', plan: '' }, 
                atualizacao: { D1_hours: 0, D2_hours: 0, D1_certs: 0, D2_certs: 0, D1_nota: 0, D2_nota: 0, total: 0 },
                labels: [{ text: 'Promoção', color: 'bg-green-100 text-green-700', details: { score: 8.8, needed: 8.5, lost: 2.0, maxLost: 5.0, type: 'PROMOÇÃO' } }]
            },
            // ... mantenha os outros itens de evaluations se desejar ...
        ],
        apontamentos: [
            { id: 'ap1', collaboratorId: 'c1', date: '2025-02-15', type: '[4] Insatisfação cliente (sem prejuízo)', weight: 5, description: 'Ocorrência na entrega do Q1.'},
            { id: 'ap2', collaboratorId: 'c3', date: '2025-01-10', type: '[3] Prejuízo interno (até R$ 50)', weight: 1, description: 'Perda de arquivo.'}
        ],
        minWageHistory: [
            { id: 'mw1', value: 1412, validFrom: '2024-01-01' }
        ],
        gestaoMetasPercepcao: [
            { id: 'gm1', type: 'Metas', name: 'Planejamento Estratégico e Cumprimento de Metas', maxPoints: 20, description: '5 pontos por trimestre, somando 20 por ano (Nota 1-5)', scores: { q1: null, q2: null, q3: null, q4: null } },
            { id: 'gm2', type: 'Percepção', name: 'Percepção da Equipe sobre atuação da Gestão', maxPoints: 20, description: '5 pontos por trimestre, somando 20 por ano (Nota 1-5)', scores: { q1: null, q2: null, q3: null, q4: null } },
        ]
    };

        // --- DADOS INICIAIS PARA O MÓDULO RH ---
            const SEED_RH = {
                resumes: [
                    { id: 'r1', name: 'Juliana Paes', email: 'ju.paes@email.com', deptInterest: 'Comercial', link: 'linkedin.com/in/ju', status: 'Em Análise', classification: 'Possibilidade Futura', date: '2024-02-10', testScore: 8.5 },
                    { id: 'r2', name: 'Marcos Mion', email: 'mion@email.com', deptInterest: 'Marketing', link: 'linkedin.com/in/mion', status: 'Aprovado', classification: 'Contratar', date: '2024-02-12', testScore: 9.8 },
                    { id: 'r3', name: 'Fausto Silva', email: 'fausto@email.com', deptInterest: 'Financeiro', link: 'linkedin.com/in/fausto', status: 'Reprovado', classification: 'Não Chamar', date: '2024-01-20', testScore: 4.0 },
                ],
                recruitmentTests: [
                    { 
                        id: 'rt1', title: 'Lógica Básica', dept: 'Geral', 
                        questions: [
                            { id: 'q1', text: 'Quanto é 2 + 2?', type: 'text', correctAnswer: '4' },
                            { id: 'q2', text: 'A capital do Brasil é?', type: 'text', correctAnswer: 'Brasília' }
                        ] 
                    }
                ]
            };

    /* ===============================================================================
     * [END_SEC_02_CONSTANTES_SEED]
     * [SEC_03_UTILITARIOS]
     * FIM DA SECAO 02 / INICIO DA SECAO 03 - UTILITARIOS GERAIS
     * =============================================================================== */
    // --- 3. UTILITÁRIOS GERAIS (HELPERS) ---

    /** Converte Hex (#RRGGBB) para array [r, g, b] */
    const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : null;
    };

    /** Formata valor numérico para Moeda Brasileira (R$) */
    const formatCurrency = (val) => {
        const num = parseFloat(val);
        if (isNaN(num)) return 'R$ 0,00';
        return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    /** Formata data string para MM/YYYY */
    const formatMonthYear = (dateStr) => {
        if (!dateStr) return '-';
        try {
            const d = new Date(dateStr + 'T00:00:00'); 
            if (isNaN(d.getTime())) return dateStr;
            const m = (d.getMonth() + 1).toString().padStart(2, '0');
            const y = d.getFullYear();
            return `${m}/${y}`;
        } catch {
            return dateStr;
        }
    };

    /** Concatena Cargo + Senioridade + Nível */
    const getFullRole = (collab) => {
        const role = collab.role || '';
        const seniority = collab.seniority || '';
        const level = collab.level || '';
        return `${role} ${seniority} ${level}`.trim();
    };


    /* ===============================================================================
     * [END_SEC_03_UTILITARIOS]
     * [SEC_04_REGRAS_NEGOCIO]
     * FIM DA SECAO 03 / INICIO DA SECAO 04 - REGRAS DE NEGOCIO
     * =============================================================================== */
    // --- 4. LÓGICA DE NEGÓCIO (BUSINESS LOGIC) ---

            /**
             * Verifica se um usuário pode ver um colaborador específico.
             * Regras:
             * 1. Bloqueio de Auto-Visualização (NOVO).
             * 2. SuperAdm vê tudo.
             * 3. Gestão é "Soberana": Apenas quem tem 'viewGestao' vê colaboradores do tipo 'Gestão'.
             * 4. ADM comum: Vê apenas colaboradores do seu departamento.
             */
        const canViewCollab = (collab, currentUser) => {
        if (!collab || !currentUser) return false;
        
        // --- 0. NOVA REGRA: BLOQUEIO DA PRÓPRIA AVALIAÇÃO ---
        // Se a permissão estiver ativa E o email for igual, esconde o registro.
        if (currentUser.permissions?.eval_block_self_view) {
            const myEmail = (currentUser.email || '').toLowerCase().trim();
            const targetEmail = (collab.email || '').toLowerCase().trim();
            
            // Verifica se tem email cadastrado e se são iguais
            if (myEmail && targetEmail && myEmail === targetEmail) {
                return false; // Bloqueia a si mesmo
            }
        }
        // ----------------------------------------------------

        // 1. SuperAdm
        if (currentUser.role === 'SuperAdm') return true;
        
        // 2. Regra de Gestão (Dados Sensíveis)
        if (collab.personType === 'Gestão') {
            return !!currentUser.permissions?.viewGestao;
        }

        // 3. Regra de Departamento
        if (currentUser.role === 'ADM' && collab.departmentId !== currentUser.departmentId) {
            return false;
        }

        return true;
    };

    /**
     * Define o status visual da avaliação (Ícone, Texto, Cor).
     * Considera trimestres iniciados, concluídos e status final.
     */
    const getEvaluationStatusDisplay = (evaluation) => {
        if (!evaluation) return { text: 'N/A', icon: null, color: 'text-gray-500' };
        const status = evaluation.status;

        if (status === 'Finalizada' || status === 'T4-Concluido') {
            return { text: 'Finalizada', icon: CheckCircle, color: 'text-green-600' };
        }

        const isT1Started = evaluation.qualitative.criteria?.some(c => c.q1 !== null && c.q1 !== '' && c.q1 !== undefined);
        const isT2Started = evaluation.qualitative.criteria?.some(c => c.q2 !== null && c.q2 !== '' && c.q2 !== undefined);
        const isT3Started = evaluation.qualitative.criteria?.some(c => c.q3 !== null && c.q3 !== '' && c.q3 !== undefined);
        const isT4Started = evaluation.qualitative.criteria?.some(c => c.q4 !== null && c.q4 !== '' && c.q4 !== undefined);
        
        if (!isT1Started && !isT2Started && !isT3Started && !isT4Started) {
            return { text: 'Em Andamento', icon: Clock, color: 'text-yellow-600' };
        }
        
        const concludedStatusMap = { 'T1-Concluido': 'T1', 'T2-Concluido': 'T2', 'T3-Concluido': 'T3' };
        let currentQuarterIndex = 0;

        if (status.startsWith('T') && status.includes('-Concluido')) {
            currentQuarterIndex = parseInt(status.split('-')[0].replace('T', ''), 10);
        } else if (isT4Started) currentQuarterIndex = 4;
        else if (isT3Started) currentQuarterIndex = 3;
        else if (isT2Started) currentQuarterIndex = 2;
        else if (isT1Started) currentQuarterIndex = 1;
        
        if (concludedStatusMap[status]) {
            return { 
                text: concludedStatusMap[status], icon: Lock, color: 'text-gray-400',
                tooltip: `Concluído: Trimestre ${concludedStatusMap[status]} fechado.`
            };
        }
        
        if (currentQuarterIndex > 0) {
            const nextQuarter = `T${currentQuarterIndex}`;
            return { 
                text: nextQuarter, icon: Unlock, color: 'text-green-600', 
                tooltip: `Em Preenchimento: Trimestre ${nextQuarter} em aberto.` 
            };
        }
        
        return { text: 'Em Andamento', icon: Clock, color: 'text-yellow-600' };
    };

    /** Critérios de Desempate para Ranking */
    const resolveTie = (a, b) => {
        // 1. Maior Nota Final
        const scoreA = a.personType === 'Gestão' ? (a.finalScoreGestao || 0) : (a.qualitative.finalAverage || 0);
        const scoreB = b.personType === 'Gestão' ? (b.finalScoreGestao || 0) : (b.qualitative.finalAverage || 0);
        if (scoreB !== scoreA) return scoreB - scoreA;
        
        // 2. Menor Pontuação Perdida (Apenas Colaborador)
        if (a.quantitative.meanPoints !== b.quantitative.meanPoints) {
            return a.quantitative.meanPoints - b.quantitative.meanPoints;
        }
        // 3. Ordem Alfabética
        return a.collaboratorName.localeCompare(b.collaboratorName);
    };

    /** Verifica trimestres com preenchimento parcial */
    const checkPendingIssues = (ev) => {
        const quarters = ['q1', 'q2', 'q3', 'q4'];
        const totalQuali = ev.qualitative.criteria?.length || 0;
        let issues = [];
        quarters.forEach(q => {
            let filled = 0;
            ev.qualitative.criteria?.forEach(c => {
                if (c[q] !== null && c[q] !== '' && c[q] !== undefined) filled++;
            });
            if (filled > 0 && filled < totalQuali) issues.push(q.toUpperCase());
        });
        return issues;
    };

    /** Análise de Tendência Qualitativa (Histórico 0-10 ou 0-40) */
    const getTrendAnalysis = (ev) => {
        const quarters = ['q1', 'q2', 'q3', 'q4'];
        const history = [];
        quarters.forEach(q => {
            let sum = 0, count = 0;
            ev.qualitative.criteria?.forEach(c => { if (c[q]) { sum += parseFloat(c[q]); count++; } });
            
            const maxScore = 4;
            const normalizationFactor = 10 / maxScore; // Normaliza de 4 para 10

            if (count > 0) {
                // Nota normalizada para 0-10 para Colaborador (Qualitativa)
                const normalizedAvg = (sum / count) * normalizationFactor;
                // Para Gestão, a nota exibida é a nota total (0-100), mas a tendência é da Qualitativa (0-40)
                const scoreToUse = ev.personType === 'Gestão' ? (ev.finalScoreGestao || 0) : normalizedAvg;
                history.push({ quarter: q.toUpperCase(), val: scoreToUse });
            }
        });
        let result = { history, type: 'neutral', label: 'Início' };
        if (history.length >= 2) {
            const diff = history[history.length - 1].val - history[history.length - 2].val;
            if (diff >= 0.1) { result.type = 'up'; result.label = 'Evolução'; }
            else if (diff <= -0.1) { result.type = 'down'; result.label = 'Declínio'; }
            else { result.type = 'stable'; result.label = 'Estável'; }
        }
        return result;
    };

    /** Análise de Tendência Quantitativa (Pontos Perdidos) */
    const getQuantiTrend = (ev) => {
        const quarters = ['q1', 'q2', 'q3', 'q4'];
        const history = [];
        quarters.forEach(q => {
            const isQuarterActive = ev.qualitative.criteria?.some(c => c[q] !== null && c[q] !== '');
            if (isQuarterActive) {
                let sum = 0;
                if (ev.personType === 'Colaborador') {
                    ev.quantitative.errors?.forEach(e => { 
                        if(e[q] !== null && e[q] !== undefined && e[q] !== '') { 
                            sum += (parseInt(e[q]) * e.peso); 
                        } 
                    });
                }
                history.push({ quarter: q.toUpperCase(), val: sum });
            }
        });
        let result = { history, type: 'neutral', label: 'Início' };
        if (history.length >= 2) {
            const current = history[history.length - 1].val;
            const previous = history[history.length - 2].val;
            if (current < previous) { result.type = 'up'; result.label = 'Melhoria'; } 
            else if (current > previous) { result.type = 'down'; result.label = 'Piora'; }
            else { result.type = 'stable'; result.label = 'Estável'; }
        }
        return result;
    };

    /** Tendência Trimestral Específica (Qualitativa) */
    const getQualiQuarterTrend = (ev, currentQuarter) => {
        const quarters = ['q1', 'q2', 'q3', 'q4'];
        const currentQIdx = quarters.indexOf(currentQuarter);
        if (currentQIdx === 0) return { type: 'neutral' }; 

        const prevQuarter = quarters[currentQIdx - 1];
        let currentSum = 0, currentCount = 0;
        let prevSum = 0, prevCount = 0;
        const maxScore = 4;
        const normalizationFactor = 10 / maxScore;

        ev.qualitative.criteria?.forEach(c => {
            if (c[currentQuarter] !== null && c[currentQuarter] !== '' && c[currentQuarter] !== undefined) {
                currentSum += parseFloat(c[currentQuarter]); currentCount++;
            }
            if (c[prevQuarter] !== null && c[prevQuarter] !== '' && c[prevQuarter] !== undefined) {
                prevSum += parseFloat(c[prevQuarter]); prevCount++;
            }
        });
        if (currentCount === 0 || prevCount === 0) return { type: 'neutral' };
        
        const currentAvg = (currentSum / currentCount) * normalizationFactor;
        const prevAvg = (prevSum / prevCount) * normalizationFactor;
        const diff = currentAvg - prevAvg;
        
        if (diff >= 0.1) return { type: 'up', val: currentAvg, label: 'Melhora' };
        if (diff <= -0.1) return { type: 'down', val: currentAvg, label: 'Piora' };
        return { type: 'stable', val: currentAvg, label: 'Estável' };
    };

    /** Tendência Trimestral Específica (Quantitativa) */
    const getQuantiQuarterTrend = (ev, currentQuarter) => {
        const quarters = ['q1', 'q2', 'q3', 'q4'];
        const currentQIdx = quarters.indexOf(currentQuarter);
        if (currentQIdx === 0) return { type: 'neutral' };

        const prevQuarter = quarters[currentQIdx - 1];
        let currentTotalPoints = 0;
        let prevTotalPoints = 0;
        
        const isQuarterActive = ev.qualitative.criteria?.some(c => c[currentQuarter] !== null && c[currentQuarter] !== '');
        if (!isQuarterActive || ev.personType !== 'Colaborador') return { type: 'neutral' };
        
        ev.quantitative.errors?.forEach(e => {
            if(e[currentQuarter] !== null && e[currentQuarter] !== undefined && e[currentQuarter] !== '') 
                currentTotalPoints += (parseInt(e[currentQuarter]) * e.peso); 
            if(e[prevQuarter] !== null && e[prevQuarter] !== undefined && e[prevQuarter] !== '') 
                prevTotalPoints += (parseInt(e[prevQuarter]) * e.peso); 
        });
        
        if (currentTotalPoints < prevTotalPoints) return { type: 'up', val: currentTotalPoints, label: 'Melhoria' };
        if (currentTotalPoints > prevTotalPoints) return { type: 'down', val: currentTotalPoints, label: 'Piora' };
        return { type: 'stable', val: currentTotalPoints, label: 'Estável' };
    };

    /**
     * Função unificada de recálculo da Avaliação (VERSÃO BLINDADA)
     * Substitua a função existente por esta.
     */
    const recalculateFinalAverage = (evaluation) => {
            // 1. Proteção imediata contra nulos ou objetos inválidos
            if (!evaluation || typeof evaluation !== 'object') {
                return {
                    qualitative: { criteria: [], finalAverage: 0, finalScoreGestaoQuali: 0 },
                    quantitative: { errors: [], meanPoints: 0 },
                    atualizacao: { total: 0 },
                    gestaoMetasPercepcao: []
                };
            }

            // 2. Cópia profunda segura e reconstrução estrutural (evita erro de undefined em propriedades)
            let upd;
            try {
                upd = JSON.parse(JSON.stringify(evaluation));
            } catch (e) {
                return evaluation;
            }

            if (!upd.qualitative) upd.qualitative = {};
            if (!Array.isArray(upd.qualitative.criteria)) upd.qualitative.criteria = [];
            if (typeof upd.qualitative.finalAverage !== 'number') upd.qualitative.finalAverage = 0;

            // SOLUÇÃO: Forçar a quebra de referência clonando o array e seus itens
            if (!upd.quantitative) upd.quantitative = {};
            if (!Array.isArray(upd.quantitative.errors)) {
                upd.quantitative.errors = [];
            } else {
                // Clona os erros para forçar o React a perceber a mudança
                upd.quantitative.errors = upd.quantitative.errors.map(err => ({ ...err }));
            }
            if (typeof upd.quantitative.meanPoints !== 'number') upd.quantitative.meanPoints = 0;
            if (typeof upd.quantitative.meanPoints !== 'number') upd.quantitative.meanPoints = 0;

            if (!upd.atualizacao) upd.atualizacao = { D1_hours: 0, D2_hours: 0, D1_nota: 0, D2_nota: 0, total: 0 };
    
 
        const isGestaoType = upd.personType === 'Gestão';
        const startQ = upd.activeFromQuarter || 1; 
        
        // --- NOVO: CONTA QUANTOS TRIMESTRES POSSUEM NOTAS (QUALITATIVA) ---
        let filledQuartersCount = 0;
        ['q1','q2','q3','q4'].forEach(q => {
            // Verifica se em pelo menos 1 critério o trimestre atual tem preenchimento
            const hasAnyGrade = upd.qualitative.criteria.some(c => c && c[q] !== null && c[q] !== '' && c[q] !== undefined);
            if (hasAnyGrade) filledQuartersCount++;
        });
        // O divisor será a quantidade de trimestres preenchidos (Mínimo 1 para evitar divisão por 0)
        const quantiDivisor = filledQuartersCount > 0 ? filledQuartersCount : 1;
        // ----------------------------------------------------------------

        // --- 4. Cálculo Qualitativo ---
        const maxScore = 4;
        const normFactor = 10 / maxScore; 
        let qualiTrimestreMedias = { q1: 0, q2: 0, q3: 0, q4: 0 };
        
        ['q1','q2','q3','q4'].forEach(q => { 
            let sum = 0, count = 0;
            upd.qualitative.criteria.forEach(c => { 
                if(c && c[q] !== null && c[q] !== '' && c[q] !== undefined){ 
                    sum += parseFloat(c[q]); 
                    count++; 
                } 
            });
            if (count > 0) {
                qualiTrimestreMedias[q] = (sum / count) * normFactor;
            }
        });
        
        let totalAvg = 0, trimCount = 0;
        ['q1','q2','q3','q4'].forEach(q => { 
            if (qualiTrimestreMedias[q] > 0) { totalAvg += qualiTrimestreMedias[q]; trimCount++; } 
        });
        upd.qualitative.finalAverage = trimCount > 0 ? (totalAvg / trimCount) : 0;
        
        const avgScoreGestao = trimCount > 0 ? (totalAvg / trimCount) : 0;
        upd.qualitative.finalScoreGestaoQuali = avgScoreGestao * 4;

        // --- 5. Cálculo Quantitativo ---
        let totalQuanti = 0;
        if (!isGestaoType) {
            upd.quantitative.errors.forEach(e => {
                if (e) {
                    for (let i = startQ; i <= 4; i++) {
                        const qKey = `q${i}`;
                        totalQuanti += (parseFloat(e[qKey]) || 0) * (e.peso || 0);
                    }
                }
            });
            // CORREÇÃO: Usa o quantiDivisor baseado no preenchimento real
            upd.quantitative.meanPoints = totalQuanti / quantiDivisor;
        }

        // --- 6. Cálculo Gestão ---
        if (isGestaoType) {
            if (!Array.isArray(upd.gestaoMetasPercepcao)) upd.gestaoMetasPercepcao = [];
            let sumMetas = 0, sumPerc = 0;

            upd.gestaoMetasPercepcao.forEach(item => {
                if (item) {
                    let iSum = 0, iCount = 0;
                    ['q1','q2','q3','q4'].forEach((q, idx) => {
                        if ((idx + 1) >= startQ) {
                            const val = parseFloat(item.scores?.[q]);
                            if (!isNaN(val)) { iSum += val; iCount++; }
                        }
                    });
                    const iAvg = iCount > 0 ? (iSum / iCount) : 0;
                    const proj = iAvg * 4; 

                    if (item.type === 'Metas') sumMetas += proj;
                    else sumPerc += proj;
                }
            });
            
            upd.totalGestaoMetas = sumMetas;
            upd.totalGestaoPercepcao = sumPerc;
            upd.totalGestaoMetasPercepcao = sumMetas + sumPerc;
            
            // Atualização
            const D1_max = 15.00, D2_max = 5.00, D1_min = 36, D2_min = 18;
            const d1 = upd.atualizacao.D1_hours || 0;
            const d2 = upd.atualizacao.D2_hours || 0;
            upd.atualizacao.D1_nota = Math.min(D1_max, d1 >= D1_min ? D1_max : (d1/D1_min)*D1_max);
            upd.atualizacao.D2_nota = Math.min(D2_max, d2 >= D2_min ? D2_max : (d2/D2_min)*D2_max);
            upd.atualizacao.total = upd.atualizacao.D1_nota + upd.atualizacao.D2_nota;
            
            upd.finalScoreGestao = upd.qualitative.finalScoreGestaoQuali + upd.totalGestaoMetasPercepcao + upd.atualizacao.total;
        }
        
        return upd;
    };

    /**
     * Helper para sincronizar a lista de erros quantitativos com os critérios configurados
     * e os apontamentos (ocorrências) vigentes no banco de dados.
     * Impede duplicações e preserva os pesos configurados.
     */
    const getSyncedQuantitativeErrors = (currEval, apontamentosList, quantiCriteriaList, collaboratorsList, evaluationTypesList) => {
        if (!currEval || !currEval.quantitative) return [];

        const c = collaboratorsList.find(x => x.id === currEval.collaboratorId);
        const et = c ? evaluationTypesList.find(t => t.id === c.evalTypeId) : null;
        const personType = currEval.personType || (c ? (c.personType === 'Gestor' ? 'Gestão' : (c.personType || 'Colaborador')) : 'Colaborador');

        // Pega os critérios ativos para este colaborador/tipo de avaliação
        const activeQuanti = (quantiCriteriaList || []).filter(q => {
            if (et?.selectedQuantiIds?.length > 0) {
                return et.selectedQuantiIds.includes(q.id);
            }
            const qType = q.personType === 'Gestor' ? 'Gestão' : (q.personType || 'Colaborador');
            return qType === personType || qType === 'Ambos';
        });

        const yearStr = String(currEval.periodYear);
        const userAps = (apontamentosList || []).filter(ap => 
            ap.collaboratorId === currEval.collaboratorId && 
            ap.date.startsWith(yearStr)
        );

        const startQ = currEval.activeFromQuarter || 1;

        return activeQuanti.map(aq => {
            const qValues = { q1: 0, q2: 0, q3: 0, q4: 0 };
            userAps.filter(ap => ap.type === aq.name).forEach(ap => {
                const m = parseInt(ap.date.split('-')[1]);
                const qInt = m <= 3 ? 1 : m <= 6 ? 2 : m <= 9 ? 3 : 4;
                if (qInt >= startQ) {
                    const qKey = `q${qInt}`;
                    qValues[qKey] += (parseInt(ap.qtde) || 1);
                }
            });

            return {
                id: aq.id,
                name: aq.name,
                peso: aq.peso,
                ...qValues
            };
        });
    };

    // Funções de Prazo e Status para Dashboard (CORRIGIDA E ROBUSTA)
    const getDeadlineRule = (collab, deadlines) => {
        if (!deadlines || !collab || deadlines.length === 0) return null;

        // Tenta encontrar regra exata: Mesmo Depto E Mesmo Tipo
        let rule = deadlines.find(d => 
            d.departmentId === collab.departmentId && 
            d.personType === collab.personType
        );
        if (rule) return rule;

        // Tenta: Mesmo Depto E Tipo 'Todos' (ou genérico se houver)
        rule = deadlines.find(d => 
            d.departmentId === collab.departmentId && 
            (d.personType === 'Todos' || !d.personType)
        );
        if (rule) return rule;

        // Tenta: Depto 'Todos' (geral) E Mesmo Tipo
        rule = deadlines.find(d => 
            (d.departmentId === '' || d.departmentId === 'all') && 
            d.personType === collab.personType
        );
        if (rule) return rule;

        // Tenta: Depto 'Todos' E Tipo 'Todos' (Regra Global)
        rule = deadlines.find(d => 
            (d.departmentId === '' || d.departmentId === 'all') && 
            (d.personType === 'Todos' || !d.personType)
        );
        
        return rule || null;
    };

    const checkQuarterLate = (q, rule, periodYear) => {
        if (!rule || !rule[q]) return false;
        const [d, m] = rule[q].split('/');
        let deadlineYear = parseInt(periodYear);
        if (q === 'q4' && parseInt(m) < 6) deadlineYear++;
        const deadlineDate = new Date(deadlineYear, parseInt(m)-1, parseInt(d), 23, 59, 59);
        return new Date() > deadlineDate;
    };

    const getCurrentQuarterKey = () => {
        const month = new Date().getMonth() + 1;
        if (month <= 3) return 'q1';
        if (month <= 6) return 'q2';
        if (month <= 9) return 'q3';
        return 'q4';
    };

    const checkQuarterStarted = (evaluation, q) => {
        return evaluation.qualitative.criteria?.some(c => c[q] !== null && c[q] !== '' && c[q] !== undefined);
    };

    // 5. Verifica se o trimestre está oficialmente concluído no status
    const checkQuarterConcluded = (status, q) => {
        if (status === 'Finalizada' || status === 'T4-Concluido') return true;
        if (q === 'q3' && status === 'T3-Concluido') return true;
        if (q === 'q2' && (status === 'T3-Concluido' || status === 'T2-Concluido')) return true;
        if (q === 'q1' && (status === 'T3-Concluido' || status === 'T2-Concluido' || status === 'T1-Concluido')) return true;
        return false;
    };



    /* ===============================================================================
     * [END_SEC_04_REGRAS_NEGOCIO]
     * [SEC_05_SERVICOS]
     * FIM DA SECAO 04 / INICIO DA SECAO 05 - CAMADA DE SERVICOS
     * =============================================================================== */
    // --- 5. CAMADA DE SERVIÇOS (FILES & API) ---

    // --- CARREGADOR DE LIBS PARA 2FA ---
    const load2FALibs = async () => {
        const promises = [];
        if (!window.OTPAuth) {
            promises.push(new Promise((resolve, reject) => {
                const s = document.createElement('script');
                s.src = "https://cdnjs.cloudflare.com/ajax/libs/otpauth/9.1.2/otpauth.umd.min.js";
                s.onload = resolve;
                s.onerror = () => reject("Erro ao carregar OTPAuth");
                document.head.appendChild(s);
            }));
        }
        if (!window.QRCode) {
            promises.push(new Promise((resolve, reject) => {
                const s = document.createElement('script');
                s.src = "https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js";
                s.onload = resolve;
                s.onerror = () => reject("Erro ao carregar QRCode");
                document.head.appendChild(s);
            }));
        }
        if (promises.length > 0) await Promise.all(promises);
        console.log("Bibliotecas 2FA prontas.");
    };

    /** Carrega bibliotecas de PDF (jsPDF) dinamicamente */
    const loadPdfLibs = async () => {
        if (window.jspdf && window.jspdf.jsPDF) { window.jsPDF = window.jspdf.jsPDF; return; }
        console.log("Baixando biblioteca PDF...");
        await new Promise((resolve, reject) => {
            const s = document.createElement('script'); 
            s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"; 
            s.onload = () => { 
                if (window.jspdf && window.jspdf.jsPDF) { window.jsPDF = window.jspdf.jsPDF; resolve(); } 
                else { reject("Falha ao inicializar jsPDF."); }
            }; 
            s.onerror = () => reject("Erro de rede ao baixar jsPDF.");
            document.head.appendChild(s);
        });
        await new Promise((resolve, reject) => {
            const s = document.createElement('script'); 
            s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.31/jspdf.plugin.autotable.min.js"; 
            s.onload = resolve;
            s.onerror = () => reject("Erro de rede ao baixar AutoTable.");
            document.head.appendChild(s);
        });
        console.log("Bibliotecas PDF prontas.");
    };

    /** Carregador Unificado de libs de Importação (Excel + PDF Reader) */
    const loadImportLibs = async () => {
        const promises = [];
        if (!window.XLSX) {
            console.log("Baixando biblioteca XLSX...");
            promises.push(new Promise((resolve, reject) => {
                const s = document.createElement('script');
                s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
                s.onload = resolve;
                s.onerror = () => reject("Erro ao baixar XLSX.");
                document.head.appendChild(s);
            }));
        }
        if (!window.pdfjsLib) {
            console.log("Baixando biblioteca PDF Reader...");
            promises.push(new Promise((resolve, reject) => {
                const s = document.createElement('script');
                s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
                s.onload = () => {
                    const workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
                    window.pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
                    resolve();
                };
                s.onerror = () => reject("Erro ao baixar PDF.js");
                document.head.appendChild(s);
            }));
        }
        if (promises.length > 0) await Promise.all(promises);
        console.log("Bibliotecas de Importação prontas.");
    };

    /** Lê arquivos importados (Suporta PDF e Excel/CSV) */
    const readImportFile = (file) => {
        return new Promise(async (resolve, reject) => {
            // CASO 1: PDF (Financeiro)
            if (file.name.toLowerCase().endsWith('.pdf')) {
                try {
                    if (!window.pdfjsLib) return reject("Biblioteca PDF.js não carregada.");
                    const arrayBuffer = await file.arrayBuffer();
                    const loadingTask = window.pdfjsLib.getDocument({ data: arrayBuffer });
                    const pdf = await loadingTask.promise;
                    let extractedRows = [];
                    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                        const page = await pdf.getPage(pageNum);
                        const textContent = await page.getTextContent();
                        const rowsMap = {};
                        textContent.items.forEach(item => {
                            const y = Math.round(item.transform[5]);
                            const existingY = Object.keys(rowsMap).find(key => Math.abs(key - y) < 5); // Tolerância 5px
                            const key = existingY || y;
                            if (!rowsMap[key]) rowsMap[key] = [];
                            rowsMap[key].push({ str: item.str, x: item.transform[4] });
                        });
                        Object.keys(rowsMap).forEach(yKey => {
                            const rowItems = rowsMap[yKey].sort((a, b) => a.x - b.x);
                            extractedRows.push(rowItems.map(i => i.str).join("   "));
                        });
                    }
                    resolve(extractedRows);
                } catch (e) { reject(`Erro ao ler PDF: ${e.message}`); }
            
            // CASO 2: EXCEL/CSV
            } else if (file.name.match(/\.(xlsx|xls|csv|txt)$/i)) {
                try {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        const data = new Uint8Array(e.target.result);
                        const workbook = window.XLSX.read(data, { type: 'array' });
                        const sheet = workbook.Sheets[workbook.SheetNames[0]];
                        const jsonData = window.XLSX.utils.sheet_to_json(sheet, { header: 1 });
                        resolve(jsonData);
                    };
                    reader.onerror = (e) => reject(e);
                    reader.readAsArrayBuffer(file);
                } catch (e) { reject("Erro ao ler Excel: " + e.message); }
            } else {
                reject("Formato não suportado. Use .pdf ou .xlsx");
            }
        });
    };

    /** Processa dados importados (PDF Financeiro) e converte para estrutura PCS */
    const mapImportedDataToPCS = (importedRows, importConfig, importDate) => {
        if (!importedRows || importedRows.length === 0 || typeof importedRows[0] !== 'string') return [];
        const newPCS = [];
        const targetDept = (importConfig.targetDept === 'Todos' || !importConfig.targetDept) ? 'Todos' : importConfig.targetDept;
        const moneyRegex = /[\d\.]+,[\d]{2}/g;

        importedRows.forEach(rowStr => {
            if (rowStr.includes("CARGO") && rowStr.includes("NÍVEL")) return;
            if (rowStr.trim().length < 5) return;
            const moneyMatches = rowStr.match(moneyRegex);
            if (moneyMatches && moneyMatches.length > 0) {
                const validSalaries = moneyMatches.filter(valStr => {
                    const numericVal = parseFloat(valStr.replace(/\./g, '').replace(',', '.'));
                    return numericVal > 200; // Ignora percentuais
                });
                if (validSalaries.length === 0) return;

                const firstMoneyIndex = rowStr.indexOf(moneyMatches[0]);
                let rawRoleName = rowStr.substring(0, firstMoneyIndex).trim();
                // Limpeza
                rawRoleName = rawRoleName.replace(/\(sem formula\)/gi, "").replace(/R\$/gi, "").replace(/R\$ /gi, "");
                rawRoleName = rawRoleName.replace(/[\.\-_\:]+$/, "").trim();
                if (rawRoleName.length < 2) return;

                if (importConfig.ignoreTerm) {
                    const regexIgnore = new RegExp(importConfig.ignoreTerm, "gi");
                    rawRoleName = rawRoleName.replace(regexIgnore, "").trim();
                }

                let seniority = 'N/A';
                if (rawRoleName.match(/\bJR\b/i) || rawRoleName.match(/\bJUNIOR\b/i)) seniority = 'JR';
                else if (rawRoleName.match(/\bPL\b/i) || rawRoleName.match(/\bPLENO\b/i)) seniority = 'PL';
                else if (rawRoleName.match(/\bSR\b/i) || rawRoleName.match(/\bSENIOR\b/i)) seniority = 'SR';

                let cleanRole = rawRoleName.replace(/\b(JR|PL|SR|JUNIOR|PLENO|SENIOR)\b/gi, "").trim();
                const levels = ['I', 'II', 'III', 'IV'];
                validSalaries.forEach((valStr, idx) => {
                    if (idx < levels.length) {
                        const salaryVal = parseFloat(valStr.replace(/\./g, '').replace(',', '.'));
                        newPCS.push({
                            id: Date.now() + Math.random().toString(36).substr(2, 9),
                            role: cleanRole, seniority: seniority, level: levels[idx], 
                            department: targetDept, salary: salaryVal, validFrom: importDate
                        });
                    }
                });
            }
        });

        // Remove duplicatas
        const uniquePCS = {};
        newPCS.forEach(p => { uniquePCS[`${p.role}-${p.seniority}-${p.level}`] = p; });
        return Object.values(uniquePCS);
    };

    // --- FUNÇÃO DE IA COM AUTO-DIAGNÓSTICO E CONFIGURAÇÃO CENTRAL ---
        const DEFAULT_AI_PROXY_URL = 'https://geminiproxy-lqn64wkzdq-uc.a.run.app';
        const AI_CONFIG_COLLECTION = 'aiConfigs';
        const AI_CONFIG_DOC_ID = 'default';

        const isUsableAIText = (value) => {
            const text = String(value || '').trim();
            if (!text) return false;
            const normalized = text.toLowerCase();
            if (normalized === 'sem resposta.') return false;
            if (normalized === 'sem resposta') return false;
            if (normalized.startsWith('erro na ia:')) return false;
            if (normalized.startsWith('erro de conexão')) return false;
            if (normalized.startsWith('erro de conexao')) return false;
            return true;
        };

        const extractAIResponseText = (data) => {
            if (!data) return '';
            if (typeof data === 'string') return data;
            if (typeof data.text === 'string') return data.text;
            if (typeof data.response === 'string') return data.response;
            if (typeof data.result === 'string') return data.result;
            if (typeof data.output === 'string') return data.output;
            if (typeof data.message === 'string' && !data.error) return data.message;

            const candidateText = data?.candidates?.[0]?.content?.parts
                ?.map(part => part?.text || '')
                .join('')
                .trim();
            if (candidateText) return candidateText;

            return '';
        };

        const getActiveAIConfig = async () => {
            const fallback = {
                id: AI_CONFIG_DOC_ID,
                provider: 'gemini',
                model: 'gemini-1.5-flash',
                apiKey: '',
                proxyUrl: DEFAULT_AI_PROXY_URL,
                isActive: false
            };

            if (!db) return fallback;

            try {
                const snap = await getDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/${AI_CONFIG_COLLECTION}`, AI_CONFIG_DOC_ID));
                if (!snap.exists()) return fallback;
                return { ...fallback, ...snap.data(), id: AI_CONFIG_DOC_ID };
            } catch (error) {
                console.error('Erro ao ler configuração de IA:', error);
                return fallback;
            }
        };

        const callGeminiAI = async (prompt, systemInstruction = "", type = "general") => {
            try {
                const aiConfig = await getActiveAIConfig();
                const proxyUrl = String(aiConfig.proxyUrl || DEFAULT_AI_PROXY_URL).trim();
                const apiKey = String(aiConfig.apiKey || '').trim();

                if (!aiConfig.isActive) {
                    return 'Erro na IA: configuração de IA inativa. Ative em Cadastros > Dados Externos > IA.';
                }

                if (!apiKey) {
                    return 'Erro na IA: chave da API não cadastrada. Configure em Cadastros > Dados Externos > IA.';
                }

                if (!proxyUrl) {
                    return 'Erro na IA: URL do proxy não cadastrada. Configure em Cadastros > Dados Externos > IA.';
                }

                const response = await fetch(proxyUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        provider: aiConfig.provider || 'gemini',
                        model: aiConfig.model || 'gemini-1.5-flash',
                        apiKey,
                        prompt,
                        systemInstruction,
                        type
                    })
                });

                let data = null;
                try {
                    data = await response.json();
                } catch (parseError) {
                    data = { error: 'Resposta inválida do serviço de IA.' };
                }

                if (!response.ok) {
                    return `Erro na IA: ${data?.error || data?.message || "Desconhecido"}`;
                }

                const text = extractAIResponseText(data);
                return isUsableAIText(text) ? text : "Sem resposta.";
            } catch (error) {
                console.error("Erro Conexão IA:", error);
                return "Erro de conexão. Verifique a internet.";
            }
        };

    /* ===============================================================================
     * [END_SEC_05_SERVICOS]
     * [SEC_06_HOOKS_PERSISTENCIA]
     * FIM DA SECAO 05 / INICIO DA SECAO 06 - HOOKS DE PERSISTENCIA
     * =============================================================================== */
    // --- 6. CUSTOM HOOKS (PERSISTÊNCIA) ---

    /**
     * Hook para gerenciar dados do Firestore.
     * CUIDADO: seedData removido das dependências para evitar loop infinito de reads no Firebase.
     */
    // Passo 1: Adicionamos "queryConstraints = []" nos parâmetros para receber os filtros
const useFirestoreData = (collectionName, seedData = [], authReady, currentUser, queryConstraints = [], enabled = true) => {
    const [data, setData] = useState(seedData);
    const [isLoading, setIsLoading] = useState(true);
    const fullCollectionPath = `${FIRESTORE_PATH_PUBLIC}/${collectionName}`;

    const currentUserKey = currentUser
        ? String(currentUser.id || currentUser.uid || currentUser.email || '')
        : '';

    useEffect(() => {
        if (!enabled || !db || !authReady) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        const collectionRef = collection(db, fullCollectionPath);
        
        const q = queryConstraints.length > 0 
            ? query(collectionRef, ...queryConstraints) 
            : collectionRef;

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                let list = snapshot.docs.map((docSnap) => ({
                    id: docSnap.id,
                    ...docSnap.data()
                }));

                const isTestEnv =
                    typeof window !== 'undefined' &&
                    !window.location.hostname.includes('firebaseapp.com') &&
                    !window.location.hostname.includes('web.app');

                // Mantém o usuário deus apenas em preview/local
                if (isTestEnv && collectionName === 'users' && Array.isArray(seedData)) {
                    const superUserSeed = seedData.find(
                        (u) => (u.email || '').trim().toLowerCase() === 'super@adm.com'
                    );

                    const alreadyExists = list.find(
                        (u) => (u.email || '').trim().toLowerCase() === 'super@adm.com'
                    );

                    if (superUserSeed && !alreadyExists) {
                        list.push(superUserSeed);
                    }
                }

                setData(list);
                setIsLoading(false);
            },
            (error) => {
                console.error(`Erro ao ouvir coleção ${collectionName}:`, error);

                // Importante:
                // Se deu erro antes do login, não sobrescreve depois com seed fixo.
                // Mantém o estado atual e permite nova tentativa quando currentUser mudar.
                setIsLoading(false);
            }
        );

        return () => unsubscribe();
    }, [
        authReady,
        collectionName,
        fullCollectionPath,
        enabled,
        currentUserKey,
        JSON.stringify(queryConstraints)
    ]);

    const saveItem = useCallback(async (item) => {
        if (!db) {
            console.error("Firestore não inicializado.");
            return;
        }

        try {
            const cleanedItem = JSON.parse(JSON.stringify(item));
            const itemToSave = { ...cleanedItem };
            const id = String(itemToSave.id || Date.now());

            delete itemToSave.id;

            if (Object.keys(itemToSave).length === 0) {
                console.warn(`Tentativa de salvar item vazio em ${collectionName}`);
                return;
            }

            await setDoc(doc(db, fullCollectionPath, id), itemToSave);
            console.log(`Item salvo em ${collectionName}: ${id}`);
        } catch (error) {
            console.error(`Erro ao salvar item em ${collectionName}:`, error);
        }
    }, [collectionName, fullCollectionPath]);

    const deleteItem = useCallback(async (id) => {
        if (!db) {
            console.error("Firestore não inicializado.");
            return;
        }

        try {
            await deleteDoc(doc(db, fullCollectionPath, String(id)));
            console.log(`Item deletado de ${collectionName}: ${id}`);
        } catch (error) {
            console.error(`Erro ao deletar item em ${collectionName}:`, error);
        }
    }, [collectionName, fullCollectionPath]);

    return { data, isLoading, saveItem, deleteItem, fullCollectionPath };
};


    /* ===============================================================================
     * [END_SEC_06_HOOKS_PERSISTENCIA]
     * [SEC_07_UI_ATOMOS]
     * FIM DA SECAO 06 / INICIO DA SECAO 07 - COMPONENTES UI ATOMICOS
     * =============================================================================== */
    // --- 7. COMPONENTES UI - ÁTOMOS ---

    const Card = ({children, className="", darkMode=false}) => (
        <div className={`${darkMode ? 'bg-slate-800/80 backdrop-blur-md border-slate-700/50 text-slate-200 shadow-lg' : 'bg-white border-gray-200 text-gray-800 shadow-sm'} rounded-xl border p-6 transition-colors duration-300 ${className}`}>
            {children}
        </div>
    );

    const Button = ({children, onClick, variant='primary', className="", disabled=false, title="", icon: Icon}) => {
        const styles = {
            primary: "bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-500 dark:hover:bg-blue-600",
            secondary: "bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 dark:border dark:border-slate-600",
            danger: "bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/50",
            ai: "bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:opacity-90 shadow-md font-bold",
            warning: "bg-yellow-50 text-yellow-700 border border-yellow-300 hover:bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800 dark:hover:bg-yellow-900/50",
            success: "bg-green-600 hover:bg-green-700 text-white dark:bg-green-500 dark:hover:bg-green-600",
            purple: "bg-purple-600 hover:bg-purple-700 text-white dark:bg-purple-500 dark:hover:bg-purple-600"
        };
        return (
            <button onClick={onClick} disabled={disabled} title={title} className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${styles[variant]} ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                {Icon && <Icon size={16}/>}
                {children}
            </button>
        );
    };

    const Input = (props) => <input {...props} className={`w-full rounded-lg border-gray-300 p-2.5 focus:ring-2 focus:ring-blue-500 outline-none border ${props.className}`} />;

    const CheckboxList = ({ items, selectedIds, onChange, isDarkMode }) => {
        const toggle = (id) => {
            if (selectedIds.includes(id)) onChange(selectedIds.filter(x => x !== id));
            else onChange([...selectedIds, id]);
        };
        return (
            <div className={`max-h-48 overflow-y-auto border rounded p-2 space-y-1 ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                {items.length === 0 ? <p className={`text-xs p-2 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Nenhum item disponível.</p> :
                items.map(i => (
                    <div key={i.id} onClick={() => toggle(i.id)} className={`flex items-center gap-2 p-1.5 rounded cursor-pointer text-xs transition-colors ${selectedIds.includes(i.id) ? (isDarkMode ? 'bg-blue-900/40 text-blue-300 border border-blue-800' : 'bg-blue-100 text-blue-800') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-gray-700 hover:bg-gray-200')}`}>
                        {selectedIds.includes(i.id) ? <CheckSquare size={14}/> : <Square size={14}/>}
                        <span>{i.peso !== undefined ? formatOccurrenceTerm(i.name) : i.name} {i.peso !== undefined ? `(Peso: ${i.peso})` : ''}</span>
                    </div>
                ))}
            </div>
        );
    };

    const TrendIconTrigger = ({ trend, title, onHover, onLeave, invert = false }) => {
        if (!trend || trend.type === 'neutral') return null;
        let Icon, colorClass;
        if (trend.type === 'up') { Icon = TrendingUp; colorClass = invert ? "text-red-500" : "text-green-500"; } 
        else if (trend.type === 'down') { Icon = TrendingDown; colorClass = invert ? "text-green-500" : "text-red-500"; } 
        else { Icon = Minus; colorClass = "text-yellow-500"; }

        return (
            <div className="inline-flex items-center ml-2 cursor-help p-1 rounded-full hover:bg-gray-100 transition-colors" onMouseEnter={(e) => onHover(e, trend, title)} onMouseLeave={onLeave}>
                <Icon className={colorClass} size={20} />
            </div>
        );
    };

    const DetailTooltip = ({ data, onLeave, x, y }) => {
        if (!data) return null;
        const titleMap = { total: "Colaboradores Ativos", pending: "Pendentes (Não Iniciadas)", inProgress: "Em Andamento", late: "Fora do Prazo (Atrasadas)", finished: "Concluídas" };
        const quarterColorClass = (qName) => { const map = { 'T1': 'q1-text-color', 'T2': 'q2-text-color', 'T3': 'q3-text-color', 'T4': 'q4-text-color' }; return map[qName] || 'text-gray-500'; };
        
        const getDisplayData = () => {
            let display = {};
            const quarters = ['q1', 'q2', 'q3', 'q4'];
            const consolidatedMap = {};
            const rawDetails = Array.isArray(data) ? { q1:data, q2:data, q3:data, q4:data } : data;

            quarters.forEach(q => {
                (rawDetails[q] || []).forEach(item => {
                    if (!consolidatedMap[item.name]) consolidatedMap[item.name] = { name: item.name, statuses: {} };
                    consolidatedMap[item.name].statuses[q] = item.status; 
                });
            });
            Object.values(consolidatedMap).forEach(collabEntry => {
                let quartersDisplay = [];
                quarters.forEach((q, index) => {
                    const status = collabEntry.statuses[q];
                    if(status) quartersDisplay.push(`T${index + 1}`);
                });
                if(quartersDisplay.length > 0) display[collabEntry.name] = quartersDisplay;
            });
            return Object.entries(display).sort((a, b) => a[0].localeCompare(b[0]));
        };

        const finalData = getDisplayData();
        // Lógica anti-tremedeira do Tooltip
        const popupWidth = 288;
        const popupHeight = Math.min(finalData.length * 30 + 50, 300);
        const screenW = window.innerWidth;
        const screenH = window.innerHeight;
        let topPos = y + 15;
        if (y + popupHeight > screenH - 20) { topPos = y - popupHeight - 10; }
        let leftPos = x - popupWidth - 15;
        if (leftPos < 10) { leftPos = x + 15; }

        const style = { top: topPos, left: leftPos, position: 'fixed', zIndex: 9999, pointerEvents: 'none' };

        return (
            <div className="w-72 bg-white rounded-lg shadow-2xl border border-slate-200 flex flex-col" style={style}>
                <div className="font-bold border-b border-gray-100 p-3 bg-gray-50 rounded-t-lg text-sm text-blue-600 truncate">
                    {titleMap[data.type] || "Detalhes"}
                </div>
                <div className="overflow-y-auto custom-scrollbar p-2" style={{ maxHeight: '250px' }}>
                    <ul className="text-xs space-y-1">
                        {finalData.map(([name, quarters], idx) => (
                            <li key={idx} className="flex justify-between items-center text-gray-700 border-b border-gray-50 pb-1 mb-1 last:border-0 last:mb-0 last:pb-0">
                                <span className="font-bold truncate w-2/3" title={name}>{name}</span>
                                <span className="flex gap-1 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">
                                {quarters.map(q => (<span key={q} className={`font-mono text-[10px] font-bold ${quarterColorClass(q)}`}>{q}</span>))}
                                </span>
                            </li>
                        ))}
                        {finalData.length === 0 && <li className="text-center text-gray-400 p-2">Nenhum dado nesta categoria.</li>}
                    </ul>
                </div>
            </div>
        );
    };


    /**
     * ============================================================================
     * COMPONENTE: CONTROLE DE PRODUTIVIDADE (COMPLETO COM FERRAMENTAS REAIS)
     * ============================================================================
     */

    /* ===============================================================================
     * [START_MOD_CONTROLE_PRODUTIVIDADE_SYSTEM]
     * INICIO DO BLOCO: ControleProdutividadeSystem
     * =============================================================================== */
     // Cronómetro Isolado: Atualiza apenas 1 quadradinho da tabela a cada segundo!
    const CronometroObrigacao = ({ task, isDarkMode, isRunning, isCompleted }) => {
        const [agora, setAgora] = useState(new Date());

        useEffect(() => {
            if (!isRunning) return; // Se a tarefa estiver pausada, o relógio dorme (Poupando CPU)
            const interval = setInterval(() => setAgora(new Date()), 1000);
            return () => clearInterval(interval);
        }, [isRunning]);

        // Recalcula os minutos localmente
        let totalMins = task.tempoGasto || 0;
        if (isRunning) {
            const lastStart = [...(task.timestamps || [])].reverse().find(t => t.action === 'started' || t.action === 'resumed');
            if (lastStart) {
                const diffMs = agora.getTime() - new Date(lastStart.timestamp).getTime();
                totalMins += Math.max(0, diffMs / 60000);
            }
        }

        // Função formatadora interna
        const formatTime = (totalMinutes) => {
            if (isNaN(totalMinutes) || totalMinutes < 0) return "00:00:00";
            const h = Math.floor(totalMinutes / 60);
            const m = Math.floor(totalMinutes % 60);
            const s = Math.floor((totalMinutes * 60) % 60);
            return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        };

        return (
            <div className={`${isRunning ? 'text-blue-500 animate-pulse' : isCompleted ? 'text-green-500' : isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                {formatTime(totalMins)}
            </div>
        );
    };
    
        const ControleProdutividadeSystem = ({ currentUser, isDarkMode }) => {
        // --- 1. ESTADOS E DADOS ---
        const [activeTab, setActiveTab] = useState('principal'); // Controle das abas
        
        const [tasks, setTasks] = useState([
            {
                id: '1', codigoEmpresa: '001', descricaoObrigacao: 'Fechamento Contábil Mensal', 
                competencia: '02/2026', colaborador: currentUser?.name || 'Ana Silva', 
                tempoEstimado: 120, tempoGasto: 0, status: 'pending', timestamps: [],
                prazoConlusao: '10', dpto: 'CTB', lib_movto_descricao: 'Liberado: 05/02'
            },
            {
                id: '2', codigoEmpresa: '002', descricaoObrigacao: 'Apuração Simples Nacional', 
                competencia: '02/2026', colaborador: currentUser?.name || 'Ana Silva', 
                tempoEstimado: 60, tempoGasto: 45, status: 'completed', timestamps: [{action: 'completed', timestamp: new Date().toISOString()}],
                prazoConlusao: '20', dpto: 'FSC', lib_movto_descricao: 'Sem Movimento'
            },
            {
                id: '3', codigoEmpresa: '003', descricaoObrigacao: 'Obrigação Sem Prazo (Inconsistência)', 
                competencia: '02/2026', colaborador: '', 
                tempoEstimado: 0, tempoGasto: 0, status: 'pending', timestamps: [],
                prazoConlusao: '', dpto: 'DPS', lib_movto_descricao: ''
            }
        ]);

        // Estados de Ferramentas Auxiliares
        const [templates, setTemplates] = useState([
            { id: 't1', codigoEmpresa: '999', descricaoObrigacao: 'Fechamento Padrão Completo', tempoEstimado: 180, dpto: 'CTB' }
        ]);
        const [deletedLogs, setDeletedLogs] = useState([]);
        const [dupSource, setDupSource] = useState('');
        const [dupTarget, setDupTarget] = useState('');
        const [exportMonth, setExportMonth] = useState('');

        // --- ESTADO DE CONFIGURAÇÃO DE DEPARTAMENTOS ---
        const [dptosConfig, setDptosConfig] = useState([
            { id: 'CTB', name: 'Contábil', active: true },
            { id: 'DPS', name: 'Departamento Pessoal', active: true },
            { id: 'FSC', name: 'Fiscal', active: true },
            { id: 'FIN', name: 'Financeiro', active: true },
            { id: 'LGL', name: 'Legalização', active: true },
        ]);

        const [filters, setFilters] = useState({ status: 'all', search: '', analysis: '' });
        const [progressFilter, setProgressFilter] = useState(false);

        // Seleção em Lote (Bulk Actions)
        const [selectedTasks, setSelectedTasks] = useState([]);

        // Controle de Menus Dropdown
        const [activeMenu, setActiveMenu] = useState(null); // 'users', 'obrigacoes', 'tools'

        // Controle de Modais de Ferramentas
        const [activeModal, setActiveModal] = useState(null); // 'add', 'import', 'duplicate', 'templates', 'export', 'inconsistencies', 'logs', 'sync'
        const [modalForm, setModalForm] = useState({});
        const [isProcessing, setIsProcessing] = useState(false);

         const formatTimePonto = (totalMinutes) => {
            if (isNaN(totalMinutes) || totalMinutes < 0) return "00:00:00";
            const h = Math.floor(totalMinutes / 60);
            const m = Math.floor(totalMinutes % 60);
            const s = Math.floor((totalMinutes * 60) % 60);
            return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        };

        const calculateRealTimeMinutes = (task) => {
            let totalMins = task.tempoGasto || 0;
            if (task.status === 'started' || task.status === 'resumed') {
                const lastStart = [...(task.timestamps || [])].reverse().find(t => t.action === 'started' || t.action === 'resumed');
                if (lastStart) {
                    // CORREÇÃO: Usa a data estática do momento em que a função corre, prevenindo o erro!
                    const diffMs = new Date().getTime() - new Date(lastStart.timestamp).getTime();
                    totalMins += Math.max(0, diffMs / 60000);
                }
            }
            return totalMins;
        };

        // --- 3. ANÁLISE DE DESEMPENHO ---
        const getTaskAnalysis = useCallback((timeSpentMins, estimatedTimeMins) => {
            if (!estimatedTimeMins || estimatedTimeMins === 0) return { text: "Não definido", variant: "secondary", color: "bg-gray-100 text-gray-700" };
            const percentageDiff = ((timeSpentMins - estimatedTimeMins) / estimatedTimeMins) * 100;
            
            if (percentageDiff <= -30) return { text: "Excelente", variant: "excellent", color: "bg-green-100 text-green-700 border-green-200" };
            if (percentageDiff <= 0) return { text: "Bom", variant: "adequate", color: "bg-blue-100 text-blue-700 border-blue-200" };
            if (percentageDiff <= 30) return { text: "Regular", variant: "acceptable", color: "bg-yellow-100 text-yellow-700 border-yellow-200" };
            return { text: "Ruim", variant: "inadequate", color: "bg-red-100 text-red-700 border-red-200" };
        }, []);

        // --- 4. AÇÕES DA TABELA E EM LOTE ---
        const handleStatusChange = (taskId, newStatus) => {
            setTasks(prev => prev.map(task => {
                if (task.id !== taskId) return task;
                const now = new Date().toISOString();
                const newTs = { action: newStatus, timestamp: now, userId: currentUser?.id };
                
                let novoTempoGasto = task.tempoGasto;
                if (newStatus === 'paused' || newStatus === 'completed') {
                    const lastStart = [...task.timestamps].reverse().find(t => t.action === 'started' || t.action === 'resumed');
                    if (lastStart) novoTempoGasto += Math.max(0, (new Date(now).getTime() - new Date(lastStart.timestamp).getTime()) / 60000);
                }
                return { ...task, status: newStatus, tempoGasto: novoTempoGasto, timestamps: [...task.timestamps, newTs] };
            }));
        };

        const handleDeleteTask = (task) => {
            if (window.confirm(`Deseja realmente excluir a obrigação ${task.codigoEmpresa}?`)) {
                // Salva no log de exclusão
                setDeletedLogs(prev => [{ ...task, deletedAt: new Date().toISOString(), deletedBy: currentUser?.name }, ...prev]);
                // Remove da lista ativa
                setTasks(prev => prev.filter(t => t.id !== task.id));
            }
        };

        // ---> ADICIONE ESTA FUNÇÃO AQUI <---
        const handleUndoMarkings = (taskId) => {
            if (window.confirm("Deseja realmente zerar o tempo e o status desta obrigação?")) {
                setTasks(prev => prev.map(task => {
                    if (task.id === taskId) {
                        return { ...task, status: 'pending', tempoGasto: 0, timestamps: [] };
                    }
                    return task;
                }));
            }
        };

        const handleBulkAction = (action) => {
            if (selectedTasks.length === 0) return;
            if (action === 'delete') {
                if (window.confirm(`Excluir ${selectedTasks.length} tarefas?`)) {
                    const tasksToDelete = tasks.filter(t => selectedTasks.includes(t.id));
                    setDeletedLogs(prev => [...tasksToDelete.map(t => ({ ...t, deletedAt: new Date().toISOString(), deletedBy: currentUser?.name })), ...prev]);
                    setTasks(prev => prev.filter(t => !selectedTasks.includes(t.id)));
                    setSelectedTasks([]);
                }
                return;
            }

            const statusMap = { 'start': 'started', 'pause': 'paused', 'complete': 'completed' };
            const targetStatus = statusMap[action];

            setTasks(prev => prev.map(task => {
                if (!selectedTasks.includes(task.id)) return task;
                const now = new Date().toISOString();
                const newTs = { action: targetStatus, timestamp: now, userId: currentUser?.id };
                
                let novoTempoGasto = task.tempoGasto;
                if (targetStatus === 'paused' || targetStatus === 'completed') {
                    const lastStart = [...task.timestamps].reverse().find(t => t.action === 'started' || t.action === 'resumed');
                    if (lastStart) novoTempoGasto += Math.max(0, (new Date(now).getTime() - new Date(lastStart.timestamp).getTime()) / 60000);
                }
                return { ...task, status: targetStatus, tempoGasto: novoTempoGasto, timestamps: [...(task.timestamps||[]), newTs] };
            }));
            setSelectedTasks([]);
            alert(`${selectedTasks.length} tarefas atualizadas com sucesso!`);
        };

        const toggleSelectTask = (id) => {
            if (selectedTasks.includes(id)) setSelectedTasks(prev => prev.filter(tId => tId !== id));
            else setSelectedTasks(prev => [...prev, id]);
        };

        const toggleSelectAll = () => {
            if (selectedTasks.length === filteredTasks.length && filteredTasks.length > 0) setSelectedTasks([]);
            else setSelectedTasks(filteredTasks.map(t => t.id));
        };

        // --- 5. FUNÇÕES DAS FERRAMENTAS REAIS ---
        const saveObligation = () => {
            if (!modalForm.codigoEmpresa || !modalForm.descricaoObrigacao || !modalForm.competencia) {
                return alert("Código, Descrição e Competência são obrigatórios.");
            }
            setTasks(prev => {
                if (modalForm.id && prev.some(t => t.id === modalForm.id)) {
                    // Atualiza
                    return prev.map(t => t.id === modalForm.id ? { ...t, ...modalForm } : t);
                } else {
                    // Cria
                    return [{ 
                        ...modalForm, 
                        id: Date.now().toString(), 
                        status: 'pending', 
                        tempoGasto: 0, 
                        timestamps: [] 
                    }, ...prev];
                }
            });
            setActiveModal(null);
            alert("Obrigação salva com sucesso!");
        };

        const handleDuplicateMonth = () => {
            if (!dupSource || !dupTarget) return alert("Selecione os dois meses.");
            const tasksToClone = tasks.filter(t => t.competencia === dupSource);
            if (tasksToClone.length === 0) return alert("Nenhuma tarefa encontrada no mês de origem.");
            
            const clonedTasks = tasksToClone.map(t => ({
                ...t,
                id: Date.now().toString() + Math.random().toString(36).substr(2,5),
                competencia: dupTarget,
                status: 'pending',
                tempoGasto: 0,
                timestamps: [],
                lib_movto_descricao: '' // Limpa a liberação pois é um mês novo
            }));

            setTasks(prev => [...clonedTasks, ...prev]);
            setActiveModal(null);
            alert(`${clonedTasks.length} tarefas duplicadas para ${dupTarget} com sucesso!`);
        };

        const handleExportCSV = () => {
            const targetTasks = exportMonth ? tasks.filter(t => t.competencia === exportMonth) : tasks;
            if (targetTasks.length === 0) return alert("Nenhum dado para exportar neste filtro.");

            const headers = ["Codigo", "Descricao", "Competencia", "Colaborador", "Status", "TempoEstimado", "TempoGasto", "Dpto"];
            const csvRows = [headers.join(";")];
            
            targetTasks.forEach(t => {
                csvRows.push([
                    t.codigoEmpresa, 
                    `"${t.descricaoObrigacao}"`, 
                    t.competencia, 
                    t.colaborador, 
                    t.status, 
                    t.tempoEstimado, 
                    Math.round(t.tempoGasto), 
                    t.dpto
                ].join(";"));
            });

            const csvString = "\uFEFF" + csvRows.join("\n"); // \uFEFF para UTF-8 BOM no Excel
            const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `Obrigacoes_${exportMonth || 'Geral'}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setActiveModal(null);
        };

        const handleSyncFSC = () => {
            setIsProcessing(true);
            setTimeout(() => {
                // Simula uma atualização onde 30% das tarefas recebem "Sem Movimento" ou "Liberado"
                setTasks(prev => prev.map(t => {
                    if (Math.random() > 0.7) {
                        return { ...t, lib_movto_descricao: Math.random() > 0.5 ? 'Sem Movimento' : `Liberado: ${new Date().toLocaleDateString('pt-BR')}` };
                    }
                    return t;
                }));
                setIsProcessing(false);
                setActiveModal(null);
                alert("Sincronização com FSC concluída. Status de movimentação atualizados.");
            }, 1500);
        };

        // --- 6. FILTRAGEM E ESTATÍSTICAS DA TELA ---
        const filteredTasks = tasks.filter(t => {
            if (progressFilter) {
                if (!['started', 'paused', 'resumed'].includes(t.status)) return false;
            } else if (filters.status !== 'all' && t.status !== filters.status) return false;

            if (filters.analysis && t.status === 'completed') {
                const analise = getTaskAnalysis(t.tempoGasto, t.tempoEstimado);
                if (analise.variant !== filters.analysis) return false;
            }

            if (filters.search) {
                const s = filters.search.toLowerCase();
                return t.descricaoObrigacao.toLowerCase().includes(s) || t.codigoEmpresa.toLowerCase().includes(s) || t.colaborador.toLowerCase().includes(s);
            }
            return true;
        });

        const stats = {
            total: filteredTasks.length,
            pending: filteredTasks.filter(t => t.status === 'pending').length,
            progress: filteredTasks.filter(t => ['started', 'paused', 'resumed'].includes(t.status)).length,
            completed: filteredTasks.filter(t => t.status === 'completed').length
        };

        const completedTasks = filteredTasks.filter(t => t.status === 'completed' && t.tempoEstimado > 0);
        const analysisStats = {
            excellent: completedTasks.filter(t => getTaskAnalysis(t.tempoGasto, t.tempoEstimado).variant === 'excellent').length,
            adequate: completedTasks.filter(t => getTaskAnalysis(t.tempoGasto, t.tempoEstimado).variant === 'adequate').length,
            acceptable: completedTasks.filter(t => getTaskAnalysis(t.tempoGasto, t.tempoEstimado).variant === 'acceptable').length,
            inadequate: completedTasks.filter(t => getTaskAnalysis(t.tempoGasto, t.tempoEstimado).variant === 'inadequate').length,
        };
        const totalCompleted = completedTasks.length;

        const inconsistenciasCount = tasks.filter(t => !t.colaborador || !t.tempoEstimado || t.tempoEstimado === 0 || !t.prazoConlusao).length;

        // --- 7. RENDERIZAÇÃO DA TELA ---
        return (
            <div className={`flex flex-col h-full space-y-4 animate-fade-in ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                
                {/* ================================================================= */}
                {/* BARRA SUPERIOR: MENUS DROPDOWN (Usuários, Obrigações, Ferramentas) */}
                {/* ================================================================= */}
                <div className="flex flex-col md:flex-row justify-between items-center gap-4 pb-2 border-b dark:border-slate-700">
                    <div className="flex items-center gap-2">
                        <h2 className="text-2xl font-black flex items-center gap-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-500">
                            <TrendingUp size={24} className="text-blue-600" />
                            Produtividade
                        </h2>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        {/* MENU: USUÁRIOS */}
                        <div className="relative">
                            <Button variant="secondary" onClick={() => setActiveMenu(activeMenu === 'users' ? null : 'users')} className={`h-9 text-xs border ${isDarkMode ? 'bg-slate-800 border-slate-600 hover:bg-slate-700' : 'bg-white'}`}>
                                <User size={14} className="mr-2 text-blue-500"/> Usuários <ChevronDown size={12} className="ml-1 opacity-50"/>
                            </Button>
                            {activeMenu === 'users' && (
                                <div className={`absolute right-0 mt-1 w-48 rounded-lg shadow-xl border z-50 overflow-hidden animate-fade-in ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'}`}>
                                    <div className="px-4 py-2 border-b dark:border-slate-700 text-[10px] font-bold uppercase text-gray-400">Opções de Conta</div>
                                    <button onClick={() => alert("Função gerenciada no painel principal do sistema.")} className="w-full text-left px-4 py-2 text-xs hover:bg-blue-50 dark:hover:bg-slate-700 flex items-center gap-2"><User size={14}/> Meu Perfil</button>
                                    {/* Fluxo de senha local removido */}
                                </div>
                            )}
                        </div>

                        {/* MENU: OBRIGAÇÕES */}
                        <div className="relative">
                            <Button onClick={() => setActiveMenu(activeMenu === 'obrigacoes' ? null : 'obrigacoes')} className="h-9 text-xs bg-blue-600 hover:bg-blue-700 text-white shadow-sm">
                                <List size={14} className="mr-2"/> Obrigações <ChevronDown size={12} className="ml-1 opacity-50"/>
                            </Button>
                            {activeMenu === 'obrigacoes' && (
                                <div className={`absolute right-0 mt-1 w-56 rounded-lg shadow-xl border z-50 overflow-hidden animate-fade-in ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'}`}>
                                    <div className="px-4 py-2 border-b dark:border-slate-700 text-[10px] font-bold uppercase text-gray-400">Gerenciamento</div>
                                    <button onClick={() => { setModalForm({ codigoEmpresa: '', descricaoObrigacao: '', competencia: '', colaborador: '', tempoEstimado: 60, prazoConlusao: '' }); setActiveModal('add'); setActiveMenu(null); }} className="w-full text-left px-4 py-2 text-xs hover:bg-blue-50 dark:hover:bg-slate-700 flex items-center gap-2"><Plus size={14} className="text-blue-500"/> Nova Obrigação</button>
                                    <button onClick={() => { setActiveModal('import'); setActiveMenu(null); }} className="w-full text-left px-4 py-2 text-xs hover:bg-blue-50 dark:hover:bg-slate-700 flex items-center gap-2"><Upload size={14} className="text-green-500"/> Importar Planilha</button>
                                    <button onClick={() => { setActiveModal('duplicate'); setActiveMenu(null); }} className="w-full text-left px-4 py-2 text-xs hover:bg-blue-50 dark:hover:bg-slate-700 flex items-center gap-2"><Copy size={14} className="text-orange-500"/> Duplicar Mês Anterior</button>
                                    <div className="border-t dark:border-slate-700 my-1"></div>
                                    <button onClick={() => { setActiveModal('templates'); setActiveMenu(null); }} className="w-full text-left px-4 py-2 text-xs hover:bg-blue-50 dark:hover:bg-slate-700 flex items-center gap-2"><FileText size={14} className="text-purple-500"/> Modelos (Templates)</button>
                                    {/* --- NOVO BOTÃO AQUI --- */}
                                    <button onClick={() => { setActiveModal('config_dpto'); setActiveMenu(null); }} className="w-full text-left px-4 py-2 text-xs hover:bg-blue-50 dark:hover:bg-slate-700 flex items-center gap-2"><Settings size={14} className="text-gray-500"/> Configurar Departamentos</button>
                                </div>
                            )}
                        </div>

                        {/* MENU: FERRAMENTAS */}
                        <div className="relative">
                            <Button variant="secondary" onClick={() => setActiveMenu(activeMenu === 'tools' ? null : 'tools')} className={`h-9 text-xs border ${isDarkMode ? 'bg-slate-800 border-slate-600 hover:bg-slate-700' : 'bg-white hover:bg-gray-50'}`}>
                                <Settings size={14} className="mr-2 text-gray-500"/> Ferramentas <ChevronDown size={12} className="ml-1 opacity-50"/>
                            </Button>
                            {activeMenu === 'tools' && (
                                <div className={`absolute right-0 mt-1 w-56 rounded-lg shadow-xl border z-50 overflow-hidden animate-fade-in ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'}`}>
                                    <div className="px-4 py-2 border-b dark:border-slate-700 text-[10px] font-bold uppercase text-gray-400">Sistema</div>
                                    <button onClick={() => { setActiveModal('export'); setActiveMenu(null); }} className="w-full text-left px-4 py-2 text-xs hover:bg-blue-50 dark:hover:bg-slate-700 flex items-center gap-2"><Download size={14}/> Exportar Dados</button>
                                    <button onClick={() => { setActiveModal('inconsistencies'); setActiveMenu(null); }} className="w-full text-left px-4 py-2 text-xs hover:bg-blue-50 dark:hover:bg-slate-700 flex items-center gap-2 justify-between">
                                        <div className="flex items-center gap-2"><AlertCircle size={14} className="text-red-500"/> Inconsistências</div>
                                        {inconsistenciasCount > 0 && <span className="bg-red-500 text-white text-[9px] px-1.5 rounded-full">{inconsistenciasCount}</span>}
                                    </button>
                                    <button onClick={() => { setActiveModal('sync'); setActiveMenu(null); }} className="w-full text-left px-4 py-2 text-xs hover:bg-blue-50 dark:hover:bg-slate-700 flex items-center gap-2"><RefreshCw size={14} className="text-teal-500"/> Sincronizar Lib. FSC</button>
                                    <div className="border-t dark:border-slate-700 my-1"></div>
                                    <button onClick={() => { setActiveModal('logs'); setActiveMenu(null); }} className="w-full text-left px-4 py-2 text-xs hover:bg-blue-50 dark:hover:bg-slate-700 flex items-center gap-2"><History size={14}/> Logs de Exclusão</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Overlay invisível para fechar menus */}
                {activeMenu && <div className="fixed inset-0 z-40" onClick={() => setActiveMenu(null)}></div>}

                {/* ================================================================= */}
                {/* ABAS DE NAVEGAÇÃO */}
                {/* ================================================================= */}
                <div className={`flex border-b shrink-0 mb-2 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                    <button onClick={() => setActiveTab('principal')} className={`px-6 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'principal' ? (isDarkMode ? 'border-blue-500 text-blue-400 bg-slate-800' : 'border-blue-600 text-blue-600 bg-white') : (isDarkMode ? 'border-transparent text-slate-400 hover:bg-slate-800' : 'border-transparent text-gray-500 hover:bg-gray-100')}`}>
                        <List size={14}/> Principal
                    </button>
                    <button onClick={() => setActiveTab('analise')} className={`px-6 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'analise' ? (isDarkMode ? 'border-purple-500 text-purple-400 bg-slate-800' : 'border-purple-600 text-purple-600 bg-white') : (isDarkMode ? 'border-transparent text-slate-400 hover:bg-slate-800' : 'border-transparent text-gray-500 hover:bg-gray-100')}`}>
                        <TrendingUp size={14}/> Análise e Gráficos
                    </button>
                </div>

                {/* CONTEÚDO: ABA PRINCIPAL */}
                {activeTab === 'principal' && (
                    <div className="space-y-4 animate-fade-in">

                {/* ================================================================= */}
                {/* CARDS SUPERIORES: DASHBOARD E ANÁLISE */}
                {/* ================================================================= */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    
                    {/* BLOCO ESQUERDO: Controle das Obrigações */}
                    <div className="space-y-3">
                        <h3 className="text-sm uppercase font-bold tracking-wider opacity-70">Painel de Obrigações</h3>
                        <div className="grid grid-cols-2 gap-3">
                            <div onClick={() => { setProgressFilter(false); setFilters({...filters, status: 'all', analysis: ''}); }} className={`p-4 rounded-xl cursor-pointer transition-transform hover:scale-105 shadow-sm border-0 ${isDarkMode ? 'bg-gradient-to-br from-slate-700 to-slate-800 border-slate-600' : 'bg-gradient-to-br from-gray-100 to-gray-200'}`}>
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-xs font-bold uppercase opacity-70">Total</span>
                                    <FileText size={16} className="opacity-50"/>
                                </div>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-2xl font-black">{stats.total}</span>
                                </div>
                            </div>
                            <div onClick={() => { setProgressFilter(false); setFilters({...filters, status: 'pending', analysis: ''}); }} className={`p-4 rounded-xl cursor-pointer transition-transform hover:scale-105 shadow-sm text-yellow-900 ${isDarkMode ? 'bg-gradient-to-br from-yellow-900/40 to-yellow-800/40 border border-yellow-700/50 text-yellow-400' : 'bg-gradient-to-br from-yellow-200 to-yellow-300'}`}>
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-xs font-bold uppercase opacity-80">Pendentes</span>
                                    <Clock size={16} className="opacity-60"/>
                                </div>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-2xl font-black">{stats.pending}</span>
                                    <span className="text-xs opacity-80">{stats.total > 0 ? Math.round((stats.pending/stats.total)*100) : 0}%</span>
                                </div>
                            </div>
                            <div onClick={() => { setProgressFilter(true); setFilters({...filters, status: 'all', analysis: ''}); }} className={`p-4 rounded-xl cursor-pointer transition-transform hover:scale-105 shadow-sm text-blue-900 ${isDarkMode ? 'bg-gradient-to-br from-blue-900/40 to-blue-800/40 border border-blue-700/50 text-blue-400' : 'bg-gradient-to-br from-blue-200 to-blue-300'}`}>
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-xs font-bold uppercase opacity-80">Em Andamento</span>
                                    <Play size={16} className="opacity-60"/>
                                </div>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-2xl font-black">{stats.progress}</span>
                                    <span className="text-xs opacity-80">{stats.total > 0 ? Math.round((stats.progress/stats.total)*100) : 0}%</span>
                                </div>
                            </div>
                            <div onClick={() => { setProgressFilter(false); setFilters({...filters, status: 'completed', analysis: ''}); }} className={`p-4 rounded-xl cursor-pointer transition-transform hover:scale-105 shadow-sm text-green-900 ${isDarkMode ? 'bg-gradient-to-br from-emerald-900/40 to-emerald-800/40 border border-emerald-700/50 text-emerald-400' : 'bg-gradient-to-br from-green-200 to-green-300'}`}>
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-xs font-bold uppercase opacity-80">Concluídas</span>
                                    <CheckCircle size={16} className="opacity-60"/>
                                </div>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-2xl font-black">{stats.completed}</span>
                                    <span className="text-xs opacity-80">{stats.total > 0 ? Math.round((stats.completed/stats.total)*100) : 0}%</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* BLOCO DIREITO: Análise de Desempenho */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <h3 className="text-sm uppercase font-bold tracking-wider opacity-70">Análise de Desempenho</h3>
                            <Info size={14} className="text-gray-400 cursor-help" title="Baseado na diferença entre Tempo Gasto e Estimado"/>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div onClick={() => setFilters({...filters, analysis: 'excellent'})} className={`p-4 rounded-xl cursor-pointer transition-transform hover:scale-105 shadow-sm text-white bg-gradient-to-br from-green-500 to-green-600 ${filters.analysis === 'excellent' ? 'ring-2 ring-offset-2 ring-green-500' : ''}`}>
                                <div className="flex justify-between items-center mb-1"><span className="text-xs font-bold uppercase opacity-90">Acima da Meta</span><TrendingUp size={16} className="opacity-80"/></div>
                                <div className="flex items-baseline gap-2"><span className="text-2xl font-black">{analysisStats.excellent}</span><span className="text-xs opacity-80">{totalCompleted > 0 ? Math.round((analysisStats.excellent/totalCompleted)*100) : 0}%</span></div>
                            </div>
                            <div onClick={() => setFilters({...filters, analysis: 'adequate'})} className={`p-4 rounded-xl cursor-pointer transition-transform hover:scale-105 shadow-sm text-white bg-gradient-to-br from-blue-500 to-blue-600 ${filters.analysis === 'adequate' ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}>
                                <div className="flex justify-between items-center mb-1"><span className="text-xs font-bold uppercase opacity-90">Dentro da Meta</span><TrendingUp size={16} className="opacity-80"/></div>
                                <div className="flex items-baseline gap-2"><span className="text-2xl font-black">{analysisStats.adequate}</span><span className="text-xs opacity-80">{totalCompleted > 0 ? Math.round((analysisStats.adequate/totalCompleted)*100) : 0}%</span></div>
                            </div>
                            <div onClick={() => setFilters({...filters, analysis: 'acceptable'})} className={`p-4 rounded-xl cursor-pointer transition-transform hover:scale-105 shadow-sm text-white bg-gradient-to-br from-yellow-500 to-yellow-600 ${filters.analysis === 'acceptable' ? 'ring-2 ring-offset-2 ring-yellow-500' : ''}`}>
                                <div className="flex justify-between items-center mb-1"><span className="text-xs font-bold uppercase opacity-90">Atenção Necessária</span><Minus size={16} className="opacity-80"/></div>
                                <div className="flex items-baseline gap-2"><span className="text-2xl font-black">{analysisStats.acceptable}</span><span className="text-xs opacity-80">{totalCompleted > 0 ? Math.round((analysisStats.acceptable/totalCompleted)*100) : 0}%</span></div>
                            </div>
                            <div onClick={() => setFilters({...filters, analysis: 'inadequate'})} className={`p-4 rounded-xl cursor-pointer transition-transform hover:scale-105 shadow-sm text-white bg-gradient-to-br from-red-500 to-red-600 ${filters.analysis === 'inadequate' ? 'ring-2 ring-offset-2 ring-red-500' : ''}`}>
                                <div className="flex justify-between items-center mb-1"><span className="text-xs font-bold uppercase opacity-90">Precisa Revisão</span><TrendingDown size={16} className="opacity-80"/></div>
                                <div className="flex items-baseline gap-2"><span className="text-2xl font-black">{analysisStats.inadequate}</span><span className="text-xs opacity-80">{totalCompleted > 0 ? Math.round((analysisStats.inadequate/totalCompleted)*100) : 0}%</span></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ================================================================= */}
                {/* PAINEL DE AÇÕES EM LOTE (BULK ACTIONS) */}
                {/* ================================================================= */}
                {selectedTasks.length > 0 && (
                    <div className={`p-3 rounded-lg shadow-md border flex flex-col sm:flex-row items-center justify-between gap-4 animate-fade-in ${isDarkMode ? 'bg-indigo-900/30 border-indigo-800' : 'bg-indigo-50 border-indigo-200'}`}>
                        <div className="flex items-center gap-2">
                            <span className="bg-indigo-600 text-white font-bold px-2 py-1 rounded text-xs">{selectedTasks.length}</span>
                            <span className={`text-sm font-bold ${isDarkMode ? 'text-indigo-300' : 'text-indigo-800'}`}>tarefas selecionadas</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button onClick={() => handleBulkAction('start')} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8"><Play size={14} className="mr-1"/> Iniciar</Button>
                            <Button onClick={() => handleBulkAction('pause')} size="sm" className="bg-yellow-500 hover:bg-yellow-600 text-white text-xs h-8 border-0"><Square size={14} className="mr-1"/> Pausar</Button>
                            <Button onClick={() => handleBulkAction('complete')} size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs h-8 border-0"><Check size={14} className="mr-1"/> Finalizar</Button>
                            <div className="w-px h-8 bg-gray-300 dark:bg-slate-600 mx-1"></div>
                            <Button onClick={() => handleBulkAction('delete')} size="sm" className="bg-red-500 hover:bg-red-600 text-white text-xs h-8 border-0"><Trash2 size={14} className="mr-1"/> Excluir</Button>
                            <Button variant="secondary" onClick={() => setSelectedTasks([])} size="sm" className={`text-xs h-8 ${isDarkMode ? 'bg-slate-800 text-slate-300 border-slate-600' : ''}`}>Cancelar</Button>
                        </div>
                    </div>
                )}

                {/* ================================================================= */}
                {/* BARRA DE PESQUISA (NÍVEL TABELA) */}
                {/* ================================================================= */}
                <div className="flex gap-2 w-full max-w-md">
                    <div className="relative w-full">
                        <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
                        <input 
                            type="text" 
                            placeholder="Buscar por código, descrição ou responsável..." 
                            value={filters.search}
                            onChange={(e) => setFilters({...filters, search: e.target.value})}
                            className={`w-full pl-9 p-2 rounded-lg border text-sm outline-none ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-300'}`}
                        />
                    </div>
                </div>

                {/* ================================================================= */}
                {/* TABELA PRINCIPAL (TaskTable) */}
                {/* ================================================================= */}
                <div className={`flex-1 border rounded-xl overflow-hidden shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                    <div className="overflow-x-auto custom-scrollbar max-h-[500px]">
                        <table className={`w-full text-xs text-left whitespace-nowrap ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                            <thead className={`uppercase font-bold sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 text-slate-400 border-b border-slate-700' : 'bg-gray-100 text-gray-600 border-b border-gray-200'}`}>
                                <tr>
                                    <th className="p-3 w-10 text-center">
                                        <input type="checkbox" className="rounded cursor-pointer" checked={selectedTasks.length === filteredTasks.length && filteredTasks.length > 0} onChange={toggleSelectAll} />
                                    </th>
                                    <th className="p-3">Código</th>
                                    <th className="p-3">Descrição</th>
                                    <th className="p-3 text-center">Mês</th>
                                    <th className="p-3">Responsável</th>
                                    <th className="p-3 text-center">Tempo Est.</th>
                                    <th className="p-3">Lib. Movto</th>
                                    <th className="p-3 text-center">Prazo</th>
                                    <th className="p-3 text-center">Status</th>
                                    <th className="p-3 text-center">Tempo Gasto</th>
                                    <th className="p-3 text-center">Análise</th>
                                    <th className="p-3 text-center sticky right-0 z-20 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.1)] bg-inherit">Ações</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700/50' : 'divide-gray-100'}`}>
                                {filteredTasks.map((t, idx) => {
                                    const isRunning = t.status === 'started' || t.status === 'resumed';
                                    const isCompleted = t.status === 'completed';
                                    const realMins = calculateRealTimeMinutes(t);
                                    const analise = getTaskAnalysis(realMins, t.tempoEstimado);
                                    const isSelected = selectedTasks.includes(t.id);
                                    
                                    const rowClass = isSelected ? (isDarkMode ? 'bg-indigo-900/20' : 'bg-indigo-50') : (idx % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/60' : 'bg-gray-50'));

                                    return (
                                        <tr key={t.id} className={`${rowClass} hover:opacity-90 transition-colors ${isRunning ? (isDarkMode ? '!bg-blue-900/20' : '!bg-blue-50') : ''}`}>
                                            <td className="p-3 text-center">
                                                <input type="checkbox" className="rounded cursor-pointer" checked={isSelected} onChange={() => toggleSelectTask(t.id)} />
                                            </td>
                                            <td className="p-3 font-bold">{t.codigoEmpresa}</td>
                                            <td className="p-3 font-medium truncate max-w-[200px]" title={t.descricaoObrigacao}>{t.descricaoObrigacao}</td>
                                            <td className="p-3 text-center font-mono">{t.competencia}</td>
                                            <td className="p-3 truncate max-w-[120px]">{t.colaborador || <span className="text-red-500 italic">Sem Atribuição</span>}</td>
                                            <td className="p-3 text-center font-mono">{t.tempoEstimado}m</td>
                                            <td className="p-3 text-[10px] truncate max-w-[120px]">{t.lib_movto_descricao || '-'}</td>
                                            <td className={`p-3 text-center font-bold ${!t.prazoConlusao ? 'text-red-500 italic' : 'text-red-600'}`}>{t.prazoConlusao || 'Sem Prazo'}</td>
                                            
                                            {/* STATUS BADGE */}
                                            <td className="p-3 text-center">
                                                <span className={`px-2 py-1 rounded-full text-[9px] font-bold uppercase border flex items-center justify-center gap-1 mx-auto w-fit
                                                    ${isCompleted ? 'bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400' : 
                                                    isRunning ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400' :
                                                    t.status === 'paused' ? 'bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                                    'bg-gray-100 text-gray-600 border-gray-300 dark:bg-slate-700 dark:text-slate-300'}`}>
                                                    
                                                    {isCompleted ? <CheckCircle size={10}/> : isRunning ? <Play size={10} fill="currentColor" className="animate-pulse"/> : t.status === 'paused' ? <Square size={8} fill="currentColor"/> : <Clock size={10}/>}
                                                    {t.status === 'pending' ? 'Pendente' : t.status === 'paused' ? 'Pausado' : t.status === 'completed' ? 'Concluído' : 'Em Andam.'}
                                                </span>
                                            </td>

                                            {/* CRONÔMETRO ISOLADO E OTIMIZADO */}
                                            <td className="p-3 text-center font-mono font-black text-sm">
                                                <CronometroObrigacao 
                                                    task={t} 
                                                    isDarkMode={isDarkMode} 
                                                    isRunning={isRunning} 
                                                    isCompleted={isCompleted} 
                                                />
                                            </td>

                                            {/* ANÁLISE BADGE */}
                                            <td className="p-3 text-center">
                                                {isCompleted && (
                                                    <span className={`px-2 py-1 rounded text-[9px] font-bold uppercase border shadow-sm ${analise.color}`}>
                                                        {analise.text}
                                                    </span>
                                                )}
                                            </td>

                                            {/* AÇÕES FIXAS À DIREITA */}
                                            <td className={`p-2 text-center sticky right-0 z-20 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.1)] ${rowClass} ${isRunning ? (isDarkMode ? '!bg-slate-800' : '!bg-blue-50') : ''}`}>
                                                <div className="flex justify-center items-center gap-1.5 bg-inherit">
                                                    {!isCompleted && (
                                                        <button onClick={() => handleStatusChange(t.id, isRunning ? 'paused' : 'started')} className={`w-7 h-7 rounded flex items-center justify-center transition-transform hover:scale-110 shadow-sm ${isRunning ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`} title={isRunning ? "Pausar" : "Iniciar"}>
                                                            {isRunning ? <Square size={10} fill="currentColor"/> : <Play size={12} fill="currentColor" className="ml-0.5"/>}
                                                        </button>
                                                    )}
                                                    <button onClick={() => handleStatusChange(t.id, isCompleted ? 'pending' : 'completed')} className={`w-7 h-7 rounded flex items-center justify-center transition-transform hover:scale-110 shadow-sm ${isCompleted ? 'bg-gray-200 text-gray-600 hover:bg-gray-300' : 'bg-green-100 text-green-700 hover:bg-green-200'}`} title={isCompleted ? "Reabrir" : "Finalizar"}>
                                                        {isCompleted ? <RefreshCw size={14} /> : <Check size={16} strokeWidth={3}/>}
                                                    </button>
                                                    
                                                    <div className={`w-px h-4 mx-0.5 ${isDarkMode ? 'bg-slate-600' : 'bg-gray-300'}`}></div>

                                                    <button onClick={() => handleUndoMarkings(t.id)} className="p-1.5 text-orange-500 hover:bg-orange-100 rounded transition-colors" title="Zerar Tempos"><History size={14}/></button>
                                                    <button onClick={() => { setModalForm(t); setActiveModal('add'); }} className="p-1.5 text-blue-500 hover:bg-blue-100 rounded transition-colors" title="Editar Tarefa"><Edit3 size={14}/></button>
                                                    <button onClick={() => handleDeleteTask(t)} className="p-1.5 text-red-500 hover:bg-red-100 rounded transition-colors" title="Excluir"><Trash2 size={14}/></button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filteredTasks.length === 0 && (
                                    <tr>
                                        <td colSpan="12" className="p-10 text-center text-gray-400 italic">
                                            <AlertCircle size={40} className="mx-auto mb-3 opacity-30" />
                                            Nenhuma tarefa encontrada com os filtros atuais.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                </div> 
                
                )}

                {/* CONTEÚDO: ABA ANÁLISE (DASHBOARDS RECHARTS) */}
                {activeTab === 'analise' && (
                    <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-2 pb-10 space-y-6 animate-fade-in">
                        {(() => {
                            // --- 1. PROCESSAMENTO DE DADOS ---
                            
                            // A. Comparativo de Tempo e Ranking (Por Colaborador)
                            const collabDataMap = {};
                            tasks.forEach(t => {
                                const c = t.colaborador || 'Sem Atribuição';
                                if (!collabDataMap[c]) collabDataMap[c] = { name: c, estimado: 0, gasto: 0, concluidas: 0 };
                                collabDataMap[c].estimado += (t.tempoEstimado || 0);
                                collabDataMap[c].gasto += (t.tempoGasto || 0);
                                if (t.status === 'completed') collabDataMap[c].concluidas++;
                            });
                            const collabData = Object.values(collabDataMap).sort((a, b) => b.concluidas - a.concluidas);

                            // B. Distribuição de Desempenho (Excelente, Bom, Regular, Ruim)
                            const perfMap = { Excelente: 0, Bom: 0, Regular: 0, Ruim: 0 };
                            tasks.filter(t => t.status === 'completed').forEach(t => {
                                const analysis = getTaskAnalysis(t.tempoGasto, t.tempoEstimado);
                                if (analysis.variant === 'excellent') perfMap['Excelente']++;
                                else if (analysis.variant === 'adequate') perfMap['Bom']++;
                                else if (analysis.variant === 'acceptable') perfMap['Regular']++;
                                else perfMap['Ruim']++;
                            });
                            const perfData = [
                                { name: 'Excelente', value: perfMap['Excelente'], color: '#22c55e' },
                                { name: 'Bom', value: perfMap['Bom'], color: '#3b82f6' },
                                { name: 'Regular', value: perfMap['Regular'], color: '#eab308' },
                                { name: 'Ruim', value: perfMap['Ruim'], color: '#ef4444' }
                            ].filter(d => d.value > 0);

                            // C. Comparativo por Departamento (Radar)
                            const deptMap = {};
                            tasks.forEach(t => {
                                const d = t.dpto || 'N/D';
                                if (!deptMap[d]) deptMap[d] = { name: d, total: 0, concluidas: 0 };
                                deptMap[d].total++;
                                if (t.status === 'completed') deptMap[d].concluidas++;
                            });
                            const deptData = Object.values(deptMap);

                            // D. Evolução Temporal (Por Mês/Competência)
                            const evolMap = {};
                            tasks.forEach(t => {
                                const m = t.competencia || 'N/D';
                                if (!evolMap[m]) evolMap[m] = { mes: m, concluidas: 0, pendentes: 0 };
                                if (t.status === 'completed') evolMap[m].concluidas++;
                                else evolMap[m].pendentes++;
                            });
                            const evolutionData = Object.values(evolMap).sort((a,b) => {
                                if (a.mes === 'N/D') return 1;
                                if (b.mes === 'N/D') return -1;
                                const [mA, yA] = a.mes.split('/');
                                const [mB, yB] = b.mes.split('/');
                                return new Date(yA, mA-1) - new Date(yB, mB-1);
                            });

                            // --- 2. COMPONENTE TOOLTIP CUSTOMIZADO ---
                            const CustomTooltip = ({ active, payload, label }) => {
                                if (active && payload && payload.length) {
                                    return (
                                        <div className={`p-3 rounded-lg shadow-xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`}>
                                            <p className="font-bold mb-2 text-sm">{label}</p>
                                            {payload.map((entry, index) => (
                                                <div key={index} className="flex items-center gap-2 text-xs mb-1">
                                                    <div className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: entry.color }}></div>
                                                    <span className="opacity-80">{entry.name}:</span>
                                                    <span className="font-bold">{entry.value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                }
                                return null;
                            };

                            // --- 3. RENDERIZAÇÃO DOS GRÁFICOS ---
                            return (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    
                                    {/* GRÁFICO 1: COMPARATIVO DE TEMPO (BARRAS LADO A LADO) */}
                                    <div className={`p-6 rounded-xl border shadow-sm flex flex-col h-[400px] lg:col-span-2 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                        <h3 className={`font-bold text-lg mb-6 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                            <Clock size={18} className="text-blue-500"/> Comparativo de Tempo (Estimado vs Realizado)
                                        </h3>
                                        <div className="flex-1 min-h-0">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={collabData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#334155' : '#e2e8f0'} vertical={false} />
                                                    <XAxis dataKey="name" stroke={isDarkMode ? '#94a3b8' : '#64748b'} fontSize={12} tickLine={false} axisLine={false} />
                                                    <YAxis stroke={isDarkMode ? '#94a3b8' : '#64748b'} fontSize={12} tickLine={false} axisLine={false} />
                                                    <RechartsTooltip content={<CustomTooltip />} cursor={{ fill: isDarkMode ? '#1e293b' : '#f8fafc' }} />
                                                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                                                    <Bar dataKey="estimado" name="Tempo Estimado (min)" fill={isDarkMode ? '#64748b' : '#94a3b8'} radius={[4, 4, 0, 0]} />
                                                    <Bar dataKey="gasto" name="Tempo Gasto (min)" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>

                                    {/* GRÁFICO 2: RANKING DE PRODUTIVIDADE (BARRAS HORIZONTAIS) */}
                                    <div className={`p-6 rounded-xl border shadow-sm flex flex-col h-[400px] ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                        <h3 className={`font-bold text-lg mb-6 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                            <Trophy size={18} className="text-amber-500"/> Top 5 Entregas (Concluídas)
                                        </h3>
                                        <div className="flex-1 min-h-0">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={collabData.slice(0, 5)} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#334155' : '#e2e8f0'} horizontal={false} />
                                                    <XAxis type="number" stroke={isDarkMode ? '#94a3b8' : '#64748b'} fontSize={12} />
                                                    <YAxis dataKey="name" type="category" stroke={isDarkMode ? '#94a3b8' : '#64748b'} fontSize={11} tickLine={false} axisLine={false} />
                                                    <RechartsTooltip content={<CustomTooltip />} cursor={{ fill: isDarkMode ? '#1e293b' : '#f8fafc' }} />
                                                    <Bar dataKey="concluidas" name="Tarefas Concluídas" radius={[0, 4, 4, 0]} barSize={25}>
                                                        {collabData.slice(0, 5).map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={index === 0 ? '#f59e0b' : index === 1 ? '#94a3b8' : index === 2 ? '#b45309' : '#10b981'} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>

                                    {/* GRÁFICO 3: DISTRIBUIÇÃO DE DESEMPENHO (PIE CHART) */}
                                    <div className={`p-6 rounded-xl border shadow-sm flex flex-col h-[400px] ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                        <h3 className={`font-bold text-lg mb-6 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                            <Award size={18} className="text-purple-500"/> Distribuição de Desempenho
                                        </h3>
                                        <div className="flex-1 min-h-0 relative">
                                            {perfData.length > 0 ? (
                                                <>
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <PieChart>
                                                            <Pie data={perfData} cx="50%" cy="50%" innerRadius={75} outerRadius={110} paddingAngle={5} dataKey="value">
                                                                {perfData.map((entry, index) => (
                                                                    <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                                                                ))}
                                                            </Pie>
                                                            <RechartsTooltip content={<CustomTooltip />} />
                                                            <Legend wrapperStyle={{ fontSize: '12px' }} verticalAlign="bottom" height={36}/>
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                    {/* Center Label */}
                                                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none mb-8">
                                                        <span className={`text-4xl font-black ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{perfData.reduce((a, b) => a + b.value, 0)}</span>
                                                        <span className={`text-[10px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Avaliadas</span>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="flex items-center justify-center h-full text-gray-400 text-sm">Dados insuficientes para análise de desempenho.</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* GRÁFICO 4: COMPARATIVO POR DEPARTAMENTO (RADAR CHART) */}
                                    <div className={`p-6 rounded-xl border shadow-sm flex flex-col h-[400px] ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                        <h3 className={`font-bold text-lg mb-6 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                            <Building size={18} className="text-indigo-500"/> Produtividade por Departamento
                                        </h3>
                                        <div className="flex-1 min-h-0">
                                            {deptData.length > 0 ? (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <RadarChart cx="50%" cy="50%" outerRadius="70%" data={deptData}>
                                                        <PolarGrid stroke={isDarkMode ? '#334155' : '#e2e8f0'} />
                                                        <PolarAngleAxis dataKey="name" tick={{ fill: isDarkMode ? '#cbd5e1' : '#475569', fontSize: 11, fontWeight: 'bold' }} />
                                                        <PolarRadiusAxis angle={30} domain={[0, 'auto']} tick={{ fill: isDarkMode ? '#64748b' : '#94a3b8', fontSize: 10 }} />
                                                        <Radar name="Total Atribuído" dataKey="total" stroke="#64748b" fill="#94a3b8" fillOpacity={0.3} />
                                                        <Radar name="Concluídas" dataKey="concluidas" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.5} />
                                                        <RechartsTooltip content={<CustomTooltip />} />
                                                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                                                    </RadarChart>
                                                </ResponsiveContainer>
                                            ) : (
                                                <div className="flex items-center justify-center h-full text-gray-400 text-sm">Sem dados departamentais.</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* GRÁFICO 5: EVOLUÇÃO TEMPORAL (LINE CHART) */}
                                    <div className={`p-6 rounded-xl border shadow-sm flex flex-col h-[400px] lg:col-span-2 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                        <h3 className={`font-bold text-lg mb-6 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                            <TrendingUp size={18} className="text-emerald-500"/> Evolução de Entregas (Por Competência/Mês)
                                        </h3>
                                        <div className="flex-1 min-h-0">
                                            {evolutionData.length > 0 ? (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <LineChart data={evolutionData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#334155' : '#e2e8f0'} vertical={false} />
                                                        <XAxis dataKey="mes" stroke={isDarkMode ? '#94a3b8' : '#64748b'} fontSize={12} tickLine={false} axisLine={false} />
                                                        <YAxis stroke={isDarkMode ? '#94a3b8' : '#64748b'} fontSize={12} tickLine={false} axisLine={false} />
                                                        <RechartsTooltip content={<CustomTooltip />} />
                                                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                                                        <Line type="monotone" dataKey="concluidas" name="Concluídas" stroke="#10b981" strokeWidth={3} dot={{ r: 4, fill: '#10b981', strokeWidth: 2, stroke: isDarkMode ? '#1e293b' : '#fff' }} activeDot={{ r: 6 }} />
                                                        <Line type="monotone" dataKey="pendentes" name="Em Aberto/Atrasadas" stroke="#f59e0b" strokeWidth={3} dot={{ r: 4, fill: '#f59e0b', strokeWidth: 2, stroke: isDarkMode ? '#1e293b' : '#fff' }} />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            ) : (
                                                <div className="flex items-center justify-center h-full text-gray-400 text-sm">Sem histórico suficiente para traçar evolução.</div>
                                            )}
                                        </div>
                                    </div>
                                    
                                </div>
                            );
                        })()}
                    </div>
                )}

                {/* ================================================================= */}
                {/* MODAIS DE FERRAMENTAS E INTEGRAÇÕES (FUNCIONALIDADE REAL) */}
                {/* ================================================================= */}

                {/* 1. Modal: Nova/Editar Obrigação (Layout Fiel ao Print) */}
                {activeModal === 'add' && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in overflow-y-auto" onClick={() => setActiveModal(null)}>
                        <div className={`p-8 rounded-2xl w-full max-w-2xl shadow-2xl my-8 ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800 border border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            
                            <h3 className="font-bold text-xl mb-6 flex items-center gap-2">
                                <FileText size={24} className="text-blue-600 dark:text-blue-500"/> 
                                {modalForm.id ? 'Editar Obrigação' : 'Nova Obrigação'}
                            </h3>
                            
                            <div className="space-y-5">
                                {/* Linha 1: Código e Competência */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className={`text-sm font-semibold block mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Código</label>
                                        <input type="text" className={`w-full p-2.5 border rounded-lg text-sm outline-none transition-colors focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 focus:border-blue-500' : 'bg-white border-gray-300'}`} placeholder="Ex: 1234" value={modalForm.codigoEmpresa || ''} onChange={e=>setModalForm({...modalForm, codigoEmpresa: e.target.value})}/>
                                    </div>
                                    <div>
                                        <label className={`text-sm font-semibold block mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Competência</label>
                                        <input type="text" className={`w-full p-2.5 border rounded-lg text-sm outline-none transition-colors focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 focus:border-blue-500' : 'bg-white border-gray-300'}`} placeholder="MM/AAAA" value={modalForm.competencia || ''} onChange={e=>setModalForm({...modalForm, competencia: e.target.value})}/>
                                    </div>
                                </div>

                                {/* Linha 2: Descrição */}
                                <div>
                                    <label className={`text-sm font-semibold block mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Descrição da Obrigação</label>
                                    <input type="text" className={`w-full p-2.5 border rounded-lg text-sm outline-none transition-colors focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 focus:border-blue-500' : 'bg-white border-gray-300'}`} placeholder="Digite a descrição da obrigação..." value={modalForm.descricaoObrigacao || ''} onChange={e=>setModalForm({...modalForm, descricaoObrigacao: e.target.value})}/>
                                </div>

                                {/* Linha 3: Colaborador */}
                                <div>
                                    <label className={`text-sm font-semibold block mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Colaborador Responsável</label>
                                    <select className={`w-full p-2.5 border rounded-lg text-sm outline-none transition-colors focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 focus:border-blue-500' : 'bg-white border-gray-300'}`} value={modalForm.colaborador || ''} onChange={e=>setModalForm({...modalForm, colaborador: e.target.value})}>
                                        <option value="">Selecione o responsável...</option>
                                        {currentUser?.name && <option value={currentUser.name}>{currentUser.name.toUpperCase()} (LOGADO)</option>}
                                        <option value="FÁBIO COLARES CAETANO (DEPARTAMENTO PESSOAL)">FÁBIO COLARES CAETANO (DEPARTAMENTO PESSOAL)</option>
                                        <option value="ANA SILVA (CONTÁBIL)">ANA SILVA (CONTÁBIL)</option>
                                        <option value="CARLOS SOUZA (FISCAL)">CARLOS SOUZA (FISCAL)</option>
                                    </select>
                                </div>

                                {/* Linha 4: Tempo e Prazo */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className={`text-sm font-semibold block mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Tempo Estimado (minutos)</label>
                                        <input type="number" className={`w-full p-2.5 border rounded-lg text-sm outline-none transition-colors focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 focus:border-blue-500' : 'bg-white border-gray-300'}`} placeholder="60" value={modalForm.tempoEstimado || ''} onChange={e=>setModalForm({...modalForm, tempoEstimado: parseInt(e.target.value)||0})}/>
                                    </div>
                                    <div>
                                        <label className={`text-sm font-semibold block mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Prazo (Dia)</label>
                                        <input type="text" className={`w-full p-2.5 border rounded-lg text-sm outline-none transition-colors focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 focus:border-blue-500' : 'bg-white border-gray-300'}`} placeholder="20" value={modalForm.prazoConlusao || ''} onChange={e=>setModalForm({...modalForm, prazoConlusao: e.target.value})}/>
                                    </div>
                                </div>

                                {/* Linha 5: Departamento e Prioridade */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className={`text-sm font-semibold block mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Departamento</label>
                                        <select className={`w-full p-2.5 border rounded-lg text-sm outline-none transition-colors focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 focus:border-blue-500' : 'bg-white border-gray-300'}`} value={modalForm.dpto || ''} onChange={e=>setModalForm({...modalForm, dpto: e.target.value})}>
                                            <option value="">Selecione o departamento...</option>
                                            <option value="CTB">CTB</option>
                                            <option value="DPS">DPS</option>
                                            <option value="FSC">FSC</option>
                                            <option value="FIN">FIN</option>
                                            <option value="LGL">LGL</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className={`text-sm font-semibold block mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Prioridade</label>
                                        <select className={`w-full p-2.5 border rounded-lg text-sm outline-none transition-colors focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 focus:border-blue-500' : 'bg-white border-gray-300'}`} value={modalForm.prioridade || 'Normal'} onChange={e=>setModalForm({...modalForm, prioridade: e.target.value})}>
                                            <option value="Baixa">Baixa</option>
                                            <option value="Normal">Normal</option>
                                            <option value="Alta">Alta</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Linha 6: Observações */}
                                <div>
                                    <label className={`text-sm font-semibold block mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Observações</label>
                                    <textarea rows="3" className={`w-full p-2.5 border rounded-lg text-sm outline-none transition-colors focus:ring-2 focus:ring-blue-500 resize-none ${isDarkMode ? 'bg-slate-900 border-slate-600 focus:border-blue-500' : 'bg-white border-gray-300'}`} placeholder="Adicione observações adicionais..." value={modalForm.anotacoes || ''} onChange={e=>setModalForm({...modalForm, anotacoes: e.target.value})}></textarea>
                                </div>
                            </div>

                            {/* Botões do Rodapé */}
                            <div className="flex justify-end gap-3 mt-8 pt-5 border-t dark:border-slate-700">
                                <Button variant="secondary" className="px-6 py-2.5" onClick={() => setActiveModal(null)}>Cancelar</Button>
                                <Button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 font-bold shadow-sm" onClick={saveObligation}>Salvar Obrigação</Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 8. Modal: Configuração de Departamentos */}
                {activeModal === 'config_dpto' && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setActiveModal(null)}>
                        <div className={`p-8 rounded-2xl w-full max-w-md shadow-2xl ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800 border border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            
                            <h3 className="font-bold text-xl mb-2 flex items-center gap-2">
                                <Settings size={24} className="text-gray-600 dark:text-gray-400"/>
                                Configuração de Departamentos
                            </h3>
                            <p className={`text-sm mb-6 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                Ative ou desative departamentos. Eles aparecerão no formulário de Nova Obrigação.
                            </p>

                            <div className="space-y-3 mb-6 max-h-[40vh] overflow-y-auto custom-scrollbar pr-2">
                                {dptosConfig.map((dpto) => (
                                    <div key={dpto.id} className={`flex items-center justify-between p-3 rounded-lg border ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                        <div className="flex items-center gap-3">
                                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-black text-xs ${isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-white text-gray-700 border shadow-sm'}`}>
                                                {dpto.id}
                                            </div>
                                            <span className="font-semibold text-sm">{dpto.name}</span>
                                        </div>
                                        {/* Botão Toggle (Liga/Desliga) */}
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input type="checkbox" className="sr-only peer" checked={dpto.active} onChange={() => {
                                                setDptosConfig(prev => prev.map(d => d.id === dpto.id ? { ...d, active: !d.active } : d));
                                            }}/>
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                                        </label>
                                    </div>
                                ))}
                            </div>

                            {/* Bloco: Adicionar novo departamento */}
                            <div className={`p-4 rounded-xl border border-dashed flex items-center gap-2 ${isDarkMode ? 'border-slate-600 bg-slate-800/50' : 'border-gray-300 bg-gray-50/50'}`}>
                                <input type="text" placeholder="Sigla (RH)" className={`w-20 p-2.5 border rounded-lg text-sm outline-none transition-colors focus:ring-2 focus:ring-blue-500 uppercase ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-300'}`} id="newDptoId" maxLength="4"/>
                                <input type="text" placeholder="Nome do Departamento" className={`flex-1 p-2.5 border rounded-lg text-sm outline-none transition-colors focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-300'}`} id="newDptoName"/>
                                <button onClick={() => {
                                    const idInput = document.getElementById('newDptoId');
                                    const nameInput = document.getElementById('newDptoName');
                                    if(idInput.value && nameInput.value) {
                                        setDptosConfig(prev => [...prev, { id: idInput.value.toUpperCase(), name: nameInput.value, active: true }]);
                                        idInput.value = ''; nameInput.value = '';
                                    }
                                }} className="p-2.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"><Plus size={20}/></button>
                            </div>

                            <div className="flex justify-end gap-3 mt-8 pt-5 border-t dark:border-slate-700">
                                <Button className="bg-blue-600 hover:bg-blue-700 text-white w-full py-2.5 font-bold shadow-sm" onClick={() => setActiveModal(null)}>Concluir</Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 2. Modal: Duplicar Mês Anterior (Layout Refinado) */}
                {activeModal === 'duplicate' && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in overflow-y-auto" onClick={() => setActiveModal(null)}>
                        <div className={`p-8 rounded-2xl w-full max-w-lg shadow-2xl my-8 ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800 border border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            
                            <h3 className="font-bold text-xl mb-6 flex items-center gap-2">
                                <Copy size={24} className="text-orange-500"/>
                                Duplicar Mês Anterior
                            </h3>

                            {/* Caixa de Alerta / Informação */}
                            <div className={`p-4 rounded-xl mb-6 flex gap-3 ${isDarkMode ? 'bg-orange-900/20 text-orange-300 border border-orange-900/50' : 'bg-orange-50 text-orange-800 border border-orange-200'}`}>
                                <Info size={20} className="shrink-0 mt-0.5" />
                                <p className="text-sm leading-relaxed">
                                    Todas as tarefas do mês de origem selecionado serão copiadas para o mês de destino. 
                                    <strong className="block mt-1">Os status voltarão para "Pendente" e os tempos gastos serão zerados.</strong>
                                </p>
                            </div>

                            <div className="space-y-5">
                                <div>
                                    <label className={`text-sm font-semibold block mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Mês de Origem</label>
                                    <input type="text" className={`w-full p-2.5 border rounded-lg text-sm outline-none transition-colors focus:ring-2 focus:ring-orange-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 focus:border-orange-500' : 'bg-white border-gray-300'}`} placeholder="Ex: 01/2026" value={dupSource} onChange={e=>setDupSource(e.target.value)}/>
                                </div>
                                <div>
                                    <label className={`text-sm font-semibold block mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Mês de Destino</label>
                                    <input type="text" className={`w-full p-2.5 border rounded-lg text-sm outline-none transition-colors focus:ring-2 focus:ring-orange-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 focus:border-orange-500' : 'bg-white border-gray-300'}`} placeholder="Ex: 02/2026" value={dupTarget} onChange={e=>setDupTarget(e.target.value)}/>
                                </div>
                            </div>

                            {/* Botões do Rodapé */}
                            <div className="flex justify-end gap-3 mt-8 pt-5 border-t dark:border-slate-700">
                                <Button variant="secondary" className="px-6 py-2.5" onClick={() => setActiveModal(null)}>Cancelar</Button>
                                <Button className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 font-bold shadow-sm border-0" onClick={handleDuplicateMonth}>Duplicar Tarefas</Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 3. Modal: Exportar Dados */}
                {activeModal === 'export' && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setActiveModal(null)}>
                        <div className={`p-6 rounded-xl w-full max-w-sm shadow-2xl text-center ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800 border border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4"><Download size={32}/></div>
                            <h3 className="font-bold text-lg mb-2">Exportar Relatório</h3>
                            <p className={`text-xs mb-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Gere um arquivo CSV contendo os tempos gastos e status das tarefas.</p>
                            
                            <div className="text-left mb-6">
                                <label className="text-[10px] font-bold uppercase opacity-70 block mb-1">Filtrar por Mês (Deixe vazio para Todos)</label>
                                <input type="text" placeholder="MM/AAAA" value={exportMonth} onChange={e=>setExportMonth(e.target.value)} className={`w-full p-2 border rounded text-sm outline-none ${isDarkMode?'bg-slate-900 border-slate-600':''}`}/>
                            </div>

                            <div className="flex justify-center gap-2">
                                <Button variant="secondary" onClick={() => setActiveModal(null)}>Cancelar</Button>
                                <Button className="bg-blue-600 hover:bg-blue-700 text-white border-0" onClick={handleExportCSV}>Baixar CSV</Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 4. Modal: Sincronizar FSC */}
                {activeModal === 'sync' && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => !isProcessing && setActiveModal(null)}>
                        <div className={`p-8 rounded-xl w-full max-w-md shadow-2xl text-center flex flex-col items-center ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800 border border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${isProcessing ? 'bg-teal-100 text-teal-600 animate-spin' : 'bg-teal-50 text-teal-500'}`}>
                                <RefreshCw size={40}/>
                            </div>
                            <h3 className="font-bold text-xl mb-2">Sincronização FSC</h3>
                            <p className={`text-sm mb-8 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                {isProcessing ? "Buscando dados no sistema externo. Por favor aguarde..." : "Clique para buscar os status de liberação de movimento no sistema e atualizar as obrigações pendentes."}
                            </p>
                            <div className="flex w-full gap-2">
                                <Button variant="secondary" className="flex-1" onClick={() => setActiveModal(null)} disabled={isProcessing}>Cancelar</Button>
                                <Button className="flex-1 bg-teal-500 hover:bg-teal-600 text-white border-0" onClick={handleSyncFSC} disabled={isProcessing}>
                                    {isProcessing ? 'Processando...' : 'Iniciar Sincronização'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 5. Modal: Templates (Modelos) */}
                {activeModal === 'templates' && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setActiveModal(null)}>
                        <div className={`p-6 rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[80vh] ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800 border border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                <h3 className="font-bold text-lg flex items-center gap-2 text-purple-500"><FileText size={20}/> Modelos de Obrigação</h3>
                                <button onClick={() => setActiveModal(null)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                            </div>
                            
                            <div className="flex-1 overflow-y-auto custom-scrollbar border rounded-lg dark:border-slate-700 mb-4">
                                <table className="w-full text-xs text-left">
                                    <thead className={`uppercase font-bold sticky top-0 ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-600'}`}>
                                        <tr><th className="p-3">Código</th><th className="p-3">Descrição Padrão</th><th className="p-3">Dpto</th><th className="p-3 text-center">Tempo Est.</th><th className="p-3 text-center">Ações</th></tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                        {templates.map((t, i) => (
                                            <tr key={t.id} className={i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50')}>
                                                <td className="p-3 font-bold">{t.codigoEmpresa}</td>
                                                <td className="p-3">{t.descricaoObrigacao}</td>
                                                <td className="p-3">{t.dpto}</td>
                                                <td className="p-3 text-center">{t.tempoEstimado}m</td>
                                                <td className="p-3 text-center">
                                                    <Button size="sm" className="h-7 text-[10px] bg-purple-600 text-white border-0" onClick={() => {
                                                        setModalForm({ ...t, id: null, competencia: '', colaborador: '', prazoConlusao: '' });
                                                        setActiveModal('add');
                                                    }}>Usar Modelo</Button>
                                                </td>
                                            </tr>
                                        ))}
                                        {templates.length === 0 && <tr><td colSpan="5" className="p-6 text-center opacity-50 italic">Nenhum modelo salvo.</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* 6. Modal: Inconsistências */}
                {activeModal === 'inconsistencies' && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setActiveModal(null)}>
                        <div className={`p-6 rounded-xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[85vh] ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800 border border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                <h3 className="font-bold text-lg flex items-center gap-2 text-red-500"><AlertCircle size={20}/> Relatório de Inconsistências</h3>
                                <button onClick={() => setActiveModal(null)}><X size={20}/></button>
                            </div>
                            <p className={`text-xs mb-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Abaixo estão listadas as obrigações que possuem falhas no cadastro (sem responsável, sem prazo ou sem tempo estimado).</p>
                            <div className="flex-1 overflow-y-auto custom-scrollbar border rounded-lg dark:border-slate-700">
                                <table className="w-full text-xs text-left">
                                    <thead className={`uppercase font-bold sticky top-0 ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-600'}`}>
                                        <tr><th className="p-3">Código</th><th className="p-3">Descrição</th><th className="p-3">Inconsistência</th><th className="p-3 text-center">Ação</th></tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                        {tasks.filter(t => !t.colaborador || !t.tempoEstimado || t.tempoEstimado === 0 || !t.prazoConlusao).map((t, i) => (
                                            <tr key={t.id} className={i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50')}>
                                                <td className="p-3 font-bold">{t.codigoEmpresa}</td>
                                                <td className="p-3">{t.descricaoObrigacao}</td>
                                                <td className="p-3 text-red-500 font-bold">
                                                    {!t.colaborador && <span className="block">- Sem Colaborador</span>}
                                                    {(!t.tempoEstimado || t.tempoEstimado === 0) && <span className="block">- Sem Tempo Estimado</span>}
                                                    {!t.prazoConlusao && <span className="block">- Sem Prazo</span>}
                                                </td>
                                                <td className="p-3 text-center"><Button size="sm" onClick={() => {setActiveModal('add'); setModalForm(t);}} className="h-7 text-[10px]">Corrigir</Button></td>
                                            </tr>
                                        ))}
                                        {inconsistenciasCount === 0 && <tr><td colSpan="4" className="p-8 text-center text-green-500 font-bold">Tudo certo! Nenhuma inconsistência encontrada.</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* 7. Modal: Logs de Exclusão */}
                {activeModal === 'logs' && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setActiveModal(null)}>
                        <div className={`p-6 rounded-xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[85vh] ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800 border border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                <h3 className="font-bold text-lg flex items-center gap-2"><History size={20} className="text-gray-500"/> Logs de Exclusão</h3>
                                <button onClick={() => setActiveModal(null)}><X size={20}/></button>
                            </div>
                            <div className="flex-1 overflow-y-auto custom-scrollbar border rounded-lg dark:border-slate-700">
                                <table className="w-full text-xs text-left">
                                    <thead className={`uppercase font-bold sticky top-0 ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-600'}`}>
                                        <tr><th className="p-3">Data Exclusão</th><th className="p-3">Excluído Por</th><th className="p-3">Obrigação Excluída</th><th className="p-3">Competência</th><th className="p-3">Status Anterior</th></tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                        {deletedLogs.map((log, i) => (
                                            <tr key={i} className={`opacity-80 ${i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50')}`}>
                                                <td className="p-3 font-mono">{new Date(log.deletedAt).toLocaleString('pt-BR')}</td>
                                                <td className="p-3 font-bold">{log.deletedBy || 'Sistema'}</td>
                                                <td className="p-3">[{log.codigoEmpresa}] {log.descricaoObrigacao}</td>
                                                <td className="p-3">{log.competencia}</td>
                                                <td className="p-3">{log.status}</td>
                                            </tr>
                                        ))}
                                        {deletedLogs.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-gray-400 italic">Nenhum registro de exclusão recente.</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal Auxiliar Simplificado: Importação (Permanece estático pois o original usava Supabase batch insert) */}
                {activeModal === 'import' && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setActiveModal(null)}>
                        <div className={`p-6 rounded-xl w-full max-w-sm shadow-2xl text-center ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800 border border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4"><Upload size={32}/></div>
                            <h3 className="font-bold text-lg mb-2">Importar Tarefas</h3>
                            <p className={`text-xs mb-6 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Faça o upload de uma planilha Excel (.xlsx) contendo as tarefas e obrigações do mês.</p>
                            <input type="file" accept=".xlsx, .csv" className={`w-full text-xs p-2 border rounded border-dashed mb-4 ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50'}`}/>
                            <div className="flex justify-center gap-2">
                                <Button variant="secondary" onClick={() => setActiveModal(null)}>Cancelar</Button>
                                <Button className="bg-green-600 hover:bg-green-700 text-white border-0" onClick={() => { alert('Upload processado (Simulação)'); setActiveModal(null); }}>Processar Arquivo</Button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        );
    };

    /* ===============================================================================
     * [END_SEC_07_UI_ATOMOS]
     * [SEC_08_UI_COMPLEXOS]
     * FIM DA SECAO 07 / INICIO DA SECAO 08 - COMPONENTES UI COMPLEXOS E MODULOS
     * =============================================================================== */
    // --- 8. COMPONENTES UI - COMPLEXOS ---

    // --- COMPONENTE DE CÉLULA DE STATUS (LÓGICA DE PRAZOS REAIS) ---


    /* ===============================================================================
     * [END_MOD_CONTROLE_PRODUTIVIDADE_SYSTEM]
     * FIM DO BLOCO: ControleProdutividadeSystem
     * [START_MOD_STATUS_STACK_CELL]
     * INICIO DO BLOCO: StatusStackCell
     * =============================================================================== */
    const StatusStackCell = ({ evaluation, deadlines, collaborators }) => {
        // 1. Dados Básicos
        const today = new Date();
        const currentMonth = today.getMonth() + 1;
        const currentYear = today.getFullYear();
        const evalYear = parseInt(evaluation.periodYear);

        // 2. Determina o Trimestre Atual do Calendário (1, 2, 3 ou 4)
        let currentQNum = 1;
        if (currentMonth > 3) currentQNum = 2;
        if (currentMonth > 6) currentQNum = 3;
        if (currentMonth > 9) currentQNum = 4;

        // Se a avaliação for de um ano anterior (ex: estamos em 2026 olhando 2025),
        // consideramos que o "Trimestre Atual" para fins de visualização é o T4 (Ciclo Fechado).
        if (evalYear < currentYear) currentQNum = 4;
        // Se for ano futuro, trava em T1
        if (evalYear > currentYear) currentQNum = 1;

        // 3. Mapeia o Status da Avaliação para um Nível Numérico (0 a 4)
        // 0=Nada, 1=T1 Feito, 2=T2 Feito, etc.
        const statusMap = { 
            'Em Andamento': 0, 
            'T1-Concluido': 1, 
            'T2-Concluido': 2, 
            'T3-Concluido': 3, 
            'T4-Concluido': 4, 
            'Finalizada': 4 
        };
        const progressLevel = statusMap[evaluation.status] || 0;

        // 4. Busca a Regra de Prazos para este Colaborador
        const collab = collaborators.find(c => c.id === evaluation.collaboratorId);
        // Usa a função existente getDeadlineRule (certifique-se que ela está no escopo)
        const rule = getDeadlineRule(collab, deadlines);

        // 5. Calcula Trimestres Atrasados (Linha de Baixo)
        const lateQuarters = [];
        
        // Verificamos apenas os trimestres ANTERIORES ou IGUAIS ao atual que não foram concluídos
        // Mas a regra de negócio diz: "a segunda será sempre para informar avaliações que estejam atrasadas"
        // Um trimestre só está atrasado se HOJE > PRAZO DO TRIMESTRE
        
        for (let q = 1; q <= 4; q++) {
            // Se já foi concluído, não está atrasado
            if (progressLevel >= q) continue;

            // Se não foi concluído, verificamos o prazo
            if (rule && rule[`q${q}`]) {
                const [d, m] = rule[`q${q}`].split('/');
                // Ajuste do ano do prazo (Ex: prazo do T4 pode ser Jan do ano seguinte)
                let deadlineYear = evalYear;
                if (q === 4 && parseInt(m) < 6) deadlineYear++; // Vira o ano

                const deadlineDate = new Date(deadlineYear, parseInt(m) - 1, parseInt(d), 23, 59, 59);
                
                // Se hoje passou do prazo, está atrasado
                if (today > deadlineDate) {
                    lateQuarters.push(`T${q}`);
                }
            }
        }

        // 6. Define Visual da Linha 1 (Trimestre Atual)
        const isCurrentLocked = progressLevel >= currentQNum;
        
        return (
            <div className="flex flex-col items-center justify-center gap-1 w-full">
                
                {/* LINHA 1: STATUS DO TRIMESTRE VIGENTE (HOJE) */}
                <div 
                    className={`flex items-center justify-center gap-1.5 px-3 py-1 rounded border text-xs font-bold shadow-sm w-[80px]
                    ${isCurrentLocked 
                        ? 'bg-blue-50 border-blue-200 text-blue-600'  // Concluído
                        : 'bg-green-50 border-green-200 text-green-600' // Em Aberto / No Prazo
                    }`}
                    title={isCurrentLocked ? `Trimestre ${currentQNum} Concluído` : `Trimestre ${currentQNum} em Aberto`}
                >
                    <span>T{currentQNum}</span>
                    {isCurrentLocked ? <Lock size={12} strokeWidth={2.5} /> : <Unlock size={12} strokeWidth={2.5} />}
                </div>

                {/* LINHA 2: ATRASADOS (Vermelho) */}
                {lateQuarters.length > 0 && (
                    <div 
                        className="flex items-center justify-center gap-1 text-[10px] font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded border border-red-100 animate-pulse w-fit max-w-[100px]"
                        title={`Prazos vencidos em: ${rule ? 'Regra: ' + rule.name : 'Data padrão'}`}
                    >
                        <AlertTriangle size={10} className="stroke-2 shrink-0"/>
                        <span className="truncate">{lateQuarters.join(', ')}</span>
                    </div>
                )}
            </div>
        );
    };




    /* ===============================================================================
     * [END_MOD_STATUS_STACK_CELL]
     * FIM DO BLOCO: StatusStackCell
     * [START_MOD_AUTH_SCREEN]
     * INICIO DO BLOCO: AuthScreen
     * =============================================================================== */
    const AuthScreen = ({ onLogin, onRequestAccess, departments, usersList }) => {
    const [view, setView] = useState('login');
    const [formData, setFormData] = useState({
        nickname: '',
        departmentId: ''
    });
    const [statusMsg, setStatusMsg] = useState(null);
    const [googleUserTemp, setGoogleUserTemp] = useState(null);

    const isTestEnvironment =
        typeof window !== 'undefined' &&
        !window.location.hostname.includes('firebaseapp.com') &&
        !window.location.hostname.includes('web.app');

    const handleGoogleLogin = async (forceSwitch = false) => {
            const auth = getAuth();
            const provider = new GoogleAuthProvider();

            if (forceSwitch) {
                provider.setCustomParameters({ prompt: 'select_account' });
            }

            setStatusMsg(null);

            try {
                const result = await signInWithPopup(auth, provider);
                const googleAuthUser = result.user;
                const email = (googleAuthUser.email || '').trim().toLowerCase();

                if (!email) {
                    setStatusMsg({ type: 'error', text: 'Não foi possível obter o e-mail da conta Google.' });
                    await signOut(auth);
                    return;
                }

                let existingUser = usersList.find(
                            (u) => (u.email || '').trim().toLowerCase() === email
                        );

                        // Fallback obrigatório: busca direta no Firestore.
                        // Evita falha no primeiro login quando usersList ainda não carregou.
                        if (!existingUser && db) {
                            try {
                                const usersRef = collection(db, `${FIRESTORE_PATH_PUBLIC}/users`);
                                const userByEmailQuery = query(
                                    usersRef,
                                    where('email', '==', email)
                                );

                                const userByEmailSnap = await getDocs(userByEmailQuery);

                                if (!userByEmailSnap.empty) {
                                    const userDoc = userByEmailSnap.docs[0];

                                    existingUser = {
                                        id: userDoc.id,
                                        ...userDoc.data()
                                    };
                                }
                            } catch (firestoreError) {
                                console.error("Erro ao buscar usuário Google por e-mail:", firestoreError);
                            }
                        }

                        if (existingUser) {
                            if (existingUser.isPending) {
                                setStatusMsg({ type: 'error', text: 'Seu acesso ainda está pendente de aprovação.' });
                                await signOut(auth);
                                return;
                            }

                            // Garante vínculo futuro com o UID do Google.
                            // Isso ajuda outros projetos que consultam o usuário pelo googleUid.
                            try {
                                await setDoc(
                                    doc(db, `${FIRESTORE_PATH_PUBLIC}/users`, String(existingUser.id)),
                                    {
                                        googleUid: googleAuthUser.uid,
                                        authProvider: 'google',
                                        isGoogleAuth: true,
                                        email: email
                                    },
                                    { merge: true }
                                );
                            } catch (syncError) {
                                console.warn("Não foi possível sincronizar googleUid no cadastro:", syncError);
                            }

                            localStorage.setItem('last_login_email', email);

                            const loginResult = await onLogin(email, null, {
                                authMode: 'google',
                                firebaseUser: {
                                    uid: googleAuthUser.uid,
                                    email: googleAuthUser.email,
                                    displayName: googleAuthUser.displayName,
                                    photoURL: googleAuthUser.photoURL
                                }
                            });

                            if (!loginResult?.success) {
                                setStatusMsg({ type: 'error', text: loginResult?.msg || 'Falha ao concluir o login.' });
                                await signOut(auth);
                            }

                            return;
                        }

                const fallbackName = googleAuthUser.displayName || 'Usuário';
                const fallbackNickname = fallbackName.split(' ')[0];

                setGoogleUserTemp({
                    email,
                    name: fallbackName,
                    photoUrl: googleAuthUser.photoURL || '',
                    nickname: fallbackNickname
                });

                setFormData({
                    nickname: fallbackNickname,
                    departmentId: ''
                });

                setView('google_complete');
            } catch (error) {
                console.error(error);
                setStatusMsg({ type: 'error', text: 'Erro na autenticação Google: ' + error.message });
            }
        };

    const handleCompleteGoogleRegistration = (e) => {
        e.preventDefault();

        if (!formData.departmentId) {
            setStatusMsg({ type: 'error', text: 'Selecione seu departamento.' });
            return;
        }

        onRequestAccess({
    email: googleUserTemp.email,
    name: googleUserTemp.name,
    nickname: formData.nickname,
    departmentId: formData.departmentId,
    isGoogleAuth: true,
    authProvider: 'google'
}).then(result => {
            if (result.success) {
                setStatusMsg({ type: 'success', text: 'Cadastro enviado! Aguarde aprovação.' });
                setTimeout(() => {
                    setView('login');
                    setGoogleUserTemp(null);
                }, 3000);
            } else {
                setStatusMsg({ type: 'error', text: result.msg });
            }
        });
    };

    return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
            <div className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-700 animate-fade-in relative">
                <div className="p-8">
                    <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-white tracking-tight cursor-default select-none">
                            Gestão de Pessoas
                        </h1>
                        <p className="text-slate-400 text-sm mt-2">
                            {view === 'google_complete'
                                ? 'Complete seu cadastro para acessar'
                                : 'Acesse com sua conta Google para continuar'}
                        </p>
                    </div>

                    {statusMsg && (
                        <div
                            className={`mb-4 p-3 rounded-lg text-sm font-medium flex items-center gap-2 ${
                                statusMsg.type === 'error'
                                    ? 'bg-red-500/20 text-red-200 border border-red-500/50'
                                    : 'bg-green-500/20 text-green-200 border border-green-500/50'
                            }`}
                        >
                            {statusMsg.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
                            {statusMsg.text}
                        </div>
                    )}

                    {view === 'google_complete' ? (
                        <form onSubmit={handleCompleteGoogleRegistration} className="space-y-4">
                            <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg mb-4 border border-slate-600">
                                {googleUserTemp?.photoUrl ? (
                                    <img
                                        src={googleUserTemp.photoUrl}
                                        alt="Avatar"
                                        className="w-12 h-12 rounded-full border border-slate-500"
                                    />
                                ) : (
                                    <div className="w-12 h-12 rounded-full bg-slate-600 flex items-center justify-center text-white font-bold">
                                        {(googleUserTemp?.name || 'U').charAt(0).toUpperCase()}
                                    </div>
                                )}

                                <div className="min-w-0">
                                    <div className="text-white font-bold truncate">{googleUserTemp?.name}</div>
                                    <div className="text-slate-400 text-xs truncate">{googleUserTemp?.email}</div>
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-1">
                                    Apelido
                                </label>
                                <Input
                                    value={formData.nickname}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, nickname: e.target.value }))}
                                    placeholder="Como deseja ser identificado"
                                    className="bg-slate-900 border-slate-600 text-white"
                                />
                            </div>

                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-1">
                                    Departamento
                                </label>
                                <select
                                    value={formData.departmentId}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, departmentId: e.target.value }))}
                                    className="w-full rounded-lg border p-2.5 bg-slate-900 border-slate-600 text-white outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Selecione...</option>
                                    {departments.map((d) => (
                                        <option key={d.id} value={d.id}>
                                            {d.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3">
                                Enviar solicitação de acesso
                            </Button>

                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => {
                                    setView('login');
                                    setGoogleUserTemp(null);
                                    setStatusMsg(null);
                                }}
                                className="w-full"
                            >
                                Voltar
                            </Button>
                        </form>
                    ) : (
                        <div className="space-y-4">
                            <button
                                type="button"
                                onClick={() => handleGoogleLogin(false)}
                                className="w-full bg-white hover:bg-slate-100 text-slate-800 font-bold py-3 rounded-xl transition-colors text-sm flex justify-center items-center gap-3 shadow-lg"
                            >
                                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                                    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.6 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
                                    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3 0 5.8 1.1 7.9 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4c-7.7 0-14.3 4.3-17.7 10.7z"/>
                                    <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.3 35.1 26.8 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                                    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.1-3.3 5.6-6 7.1l.1-.1 6.3 5.3C35.3 40.6 44 34 44 24c0-1.2-.1-2.3-.4-3.5z"/>
                                </svg>
                                Entrar com Google
                            </button>

                            <div className="text-center pt-2">
                                <button
                                    type="button"
                                    onClick={() => handleGoogleLogin(true)}
                                    className="text-xs text-slate-400 hover:text-white underline decoration-slate-600 hover:decoration-white transition-all cursor-pointer"
                                >
                                    Entrar com outra conta do Google
                                </button>
                            </div>

                            <p className="text-xs text-slate-400 text-center leading-relaxed">
                                O acesso ao sistema é feito pela sua conta Google autorizada.
                                Se for seu primeiro acesso, entre com Google e conclua o cadastro.
                            </p>

                            {isTestEnvironment && (
                                <div className="animate-fade-in pt-6 border-t border-slate-700/50 mt-6">
                                    <div className="text-[10px] text-red-400 text-center mb-4 uppercase tracking-widest font-bold flex items-center justify-center gap-1">
                                        <AlertTriangle size={12} /> Ambiente de Teste Detectado
                                    </div>

                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            onLogin('super@adm.com', '123').then((result) => {
                                                if (result.success) {
                                                    localStorage.setItem('last_login_email', 'super@adm.com');
                                                } else {
                                                    setStatusMsg({ type: 'error', text: result.msg });
                                                }
                                            });
                                        }}
                                        className="w-full bg-red-900/30 border border-red-500 text-red-400 hover:bg-red-600 hover:text-white font-bold py-3 rounded-xl transition-colors text-sm flex justify-center items-center gap-2 shadow-lg"
                                    >
                                        <Unlock size={18} /> Acesso Mestre Automático
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};



    /* ===============================================================================
     * [END_MOD_AUTH_SCREEN]
     * FIM DO BLOCO: AuthScreen
     * [START_MOD_FORCE_PASSWORD_CHANGE_MODAL]
     * INICIO DO BLOCO: ForcePasswordChangeModal
     * =============================================================================== */
    const ForcePasswordChangeModal = () => null;

    /**
     * ============================================================================
     * COMPONENTE: DASHBOARD SYSTEM (ATUALIZADO: LEGENDA DE STATUS NO HOVER)
     * ============================================================================
     * Painel principal com cards de métricas, filtros, tabela de status e prazos.
     */


    /* ===============================================================================
     * [END_MOD_FORCE_PASSWORD_CHANGE_MODAL]
     * FIM DO BLOCO: ForcePasswordChangeModal
     * [START_MOD_DASHBOARD_SYSTEM]
     * INICIO DO BLOCO: DashboardSystem
     * =============================================================================== */
    const DashboardSystem = ({ 
        data, 
        actions, 
        onDelete, 
        onUpdateStatus, 
        onEdit, 
        permissions, 
        currentUser, 
        isDarkMode 
    }) => {

        const hasPerm = (key) => currentUser?.role === 'SuperAdm' || permissions?.[key] === true;
        const [validationAlert, setValidationAlert] = useState(null);
        
        // --- 1. Desestruturação dos Dados ---
        const { 
            evaluations, 
            collaborators, 
            departments, 
            managers, 
            globalConfigs, 
            evaluationTypes, 
            pdfMessages, 
            deadlines 
        } = data;

        // --- 2. Estados Persistentes (VIA BANCO DE DADOS) ---
        // Tenta pegar do banco (currentUser.preferences.dash) ou usa padrão
        const prefs = currentUser?.preferences?.dash || {};

        

        const [filterYear, setFilterYear] = useState(prefs.filterYear || new Date().getFullYear().toString());
        const [globalSearch, setGlobalSearch] = useState(''); 

        const [filterDept, setFilterDept] = useState(prefs.filterDept || '');
        const [filterStatus, setFilterStatus] = useState(prefs.filterStatus || '');
        const [filterResult, setFilterResult] = useState(prefs.filterResult || 'Todos');
        const [filterPersonType, setFilterPersonType] = useState(prefs.filterPersonType || 'Colaborador');
        // Estado para SuperAdm visualizar todos no modal de Nova Avaliação (Dashboard)
        const [showAllOverride, setShowAllOverride] = useState(false);

        
        // --- 3.1 REGRA DE NEGÓCIO: DIRETORIA (DIR) ---
        useEffect(() => {
            if (currentUser && departments.length > 0) {
                const userDept = departments.find(d => d.id === currentUser.departmentId);
                const isDir = userDept && ['DIR', 'DIRETORIA', 'DIR.'].includes(userDept.name.toUpperCase());

                if (isDir) {
                    setFilterPersonType(current => current === 'Colaborador' ? 'Gestão' : current);
                    setFilterDept(current => (current === userDept.name || current === '') ? '' : current);
                }
            }
        }, [currentUser, departments]);

        // --- EFEITO DE SALVAMENTO NO BANCO ---
        useEffect(() => {
            const timer = setTimeout(() => {
                if (currentUser && actions.saveUserPreferences) {
                    const newState = { filterYear, filterDept, filterStatus, filterResult, filterPersonType };
                    // Só salva se for diferente do que já está no banco para evitar loop
                    if (JSON.stringify(currentUser.preferences?.dash) !== JSON.stringify(newState)) {
                        actions.saveUserPreferences({ dash: newState });
                    }
                }
            }, 1000); // Salva 1 segundo após parar de mexer
            return () => clearTimeout(timer);
        }, [filterYear, filterDept, filterStatus, filterResult, filterPersonType, currentUser, actions]);

        // --- 3. Estados de UI e Modais ---
        const [selectedEval, setSelectedEval] = useState(null);
        const [modal, setModal] = useState(null);
        const [isGenerating, setIsGenerating] = useState(false);
        const [tooltip, setTooltip] = useState(null); 
        const [reportModalOpen, setReportModalOpen] = useState(false);
        const [reportResultType, setReportResultType] = useState('Promoção');

        // Estados de Ações Auxiliares
        const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
        const [syncYearInput, setSyncYearInput] = useState(new Date().getFullYear().toString());
        const [isUpdatingSheet, setIsUpdatingSheet] = useState(false);
        const [isNewEvalModalOpen, setIsNewEvalModalOpen] = useState(false);
        const [showEndedEvaluationCollaborators, setShowEndedEvaluationCollaborators] = useState(false);
        const [newEvalData, setNewEvalData] = useState({ 
            collabId: '', 
            year: new Date().getFullYear().toString() 
        });

    // --- 3.1 REGRA DE NEGÓCIO: DIRETORIA (DIR) ---
        // Se o usuário for DIR, define padrão: Tipo = Gestão e Depto = Todos
        useEffect(() => {
            if (currentUser && departments.length > 0) {
                const userDept = departments.find(d => d.id === currentUser.departmentId);
                
                // Verifica se o departamento é DIR (aceita variações comuns)
                const isDir = userDept && ['DIR', 'DIRETORIA', 'DIR.'].includes(userDept.name.toUpperCase());

                if (isDir) {
                    // 1. Muda Tipo de Pessoa para 'Gestão' se estiver no padrão 'Colaborador'
                    setFilterPersonType(currentType => {
                        // Só altera se estiver no valor padrão ("Colaborador"), 
                        // respeitando se o usuário tiver salvo outra preferência manual anteriormente
                        return currentType === 'Colaborador' ? 'Gestão' : currentType;
                    });

                    // 2. Muda Departamento para 'Todos' (vazio)
                    setFilterDept(currentDept => {
                        // Se o filtro estiver selecionando o próprio depto (DIR) ou vazio, garante vazio (Todos)
                        // para ver a gestão da empresa inteira
                        return (currentDept === userDept.name || currentDept === '') ? '' : currentDept;
                    });
                }
            }
        }, [currentUser, departments]); // Executa apenas ao carregar o usuário/departamentos

        // --- 4. Memos e Lógica de Negócio ---
        const allowedCollaborators = useMemo(() => {
            return collaborators.filter(c => canViewCollab(c, currentUser));
        }, [collaborators, currentUser]);

        // --- LÓGICA DE FILTRAGEM PARA NOVA AVALIAÇÃO (DASHBOARD) ---
        const modalCollaboratorsList = useMemo(() => {
            const userDept = departments.find(d => d.id === currentUser?.departmentId);
            // Verifica se é diretoria (DIR, DIRETORIA, etc)
            const isUserDir = userDept && ['DIR', 'DIRETORIA', 'DIR.'].some(term => userDept.name.toUpperCase().includes(term));
            const isSuperAdm = currentUser?.role === 'SuperAdm';
            const hasGestaoPerm = currentUser?.permissions?.viewGestao; // Permissão específica de ADM

            return collaborators.filter(c => {
                // REGRA GERAL: por padrão, pessoas com Fim Avaliação preenchido ficam ocultas.
                // O checkbox do modal permite exibi-las novamente sem ignorar as demais regras de acesso.
                if (!showEndedEvaluationCollaborators && String(c.endDate || '').trim()) {
                    return false;
                }

                // --- REGRA 1: SUPER ADM + CHECKBOX MARCADO ---
                // "passa a ver todas as pessoas de todos os departamentos"
                if (isSuperAdm && showAllOverride) {
                    return true;
                }

                // --- REGRA 2: SUPER ADM (DIR) ---
                // "vê pessoas apenas do tipo 'gestão' independente do departamento"
                if (isSuperAdm && isUserDir) {
                    return c.personType === 'Gestão';
                }

                // --- REGRA 3: SUPER ADM (OUTROS DEPTOS) ---
                // "vê pessoas do tipo 'colaboradores e gestão' vinculados ao seu próprio departamento"
                if (isSuperAdm) {
                    return c.departmentId === currentUser.departmentId;
                }

                // --- REGRAS DE ADM (Nível Hierárquico Abaixo) ---
                
                // Pré-requisito ADM: Sempre restrito ao próprio departamento
                if (c.departmentId !== currentUser.departmentId) {
                    return false;
                }

                // --- REGRA 4: ADM COM PERMISSÃO GESTÃO ---
                // "se marcar o checkbox da tela vê tipo de pessoa 'gestão' do seu próprio departamento"
                // Se tem permissão E marcou checkbox -> Vê Tudo do Depto (Gestão + Colaborador)
                if (hasGestaoPerm && showAllOverride) {
                    return true; 
                }

                // --- REGRA 5: ADM PADRÃO (ou Checkbox desmarcado) ---
                // "vê apenas pessoas do tipo 'colaboradores'"
                return c.personType === 'Colaborador';
            });
        }, [collaborators, currentUser, departments, showAllOverride, showEndedEvaluationCollaborators]);

        // Função Auxiliar de Posicionamento de Tooltip
        const calculateTooltipPos = (rect, type) => {
            const popupHeight = type === 'details' ? 300 : type === 'statusLegend' ? 220 : 150; 
            const spaceBelow = window.innerHeight - rect.bottom;
            let yPos;
            if (spaceBelow < popupHeight + 10) {
                yPos = rect.top - 10;
                return { x: rect.left + rect.width / 2, y: yPos, position: 'top' };
            } else {
                yPos = rect.bottom + 5;
                return { x: rect.left + rect.width / 2, y: yPos, position: 'bottom' };
            }
        };
        const onHoverTrend = (e, trend, title) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const pos = calculateTooltipPos(rect, 'trend');
            setTooltip({ 
                x: pos.x, 
                y: pos.y, 
                placement: pos.position,
                type: 'trend', 
                data: { trend, title } 
            });
        };
        
        const onHoverStatus = (e, label) => {
            if(label.text === '-') return;
            const rect = e.currentTarget.getBoundingClientRect();
            const pos = calculateTooltipPos(rect, 'status');
            setTooltip({ 
                x: pos.x, 
                y: pos.y, 
                placement: pos.position,
                type: 'status', 
                data: label.details 
            });
        };

        const onHoverStatusLegend = (e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const pos = calculateTooltipPos(rect, 'statusLegend');
            setTooltip({
                x: pos.x,
                y: pos.y,
                placement: pos.position,
                type: 'statusLegend',
                data: {}
            });
        };

        const onHoverCardDetails = (e, cardType, data) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const pos = calculateTooltipPos(rect, 'details');
            
            let xPos = pos.x;
            if (xPos + 150 > window.innerWidth) xPos = window.innerWidth - 160;
            setTooltip({ 
                x: xPos, 
                y: pos.y, 
                placement: pos.position,
                type: 'details', 
                data: { ...data, type: cardType } 
            });
        };

        // --- Lógica Central: Processamento da Lista e Métricas ---
        const { metrics, processedList } = useMemo(() => {
            const year = filterYear || new Date().getFullYear().toString();
            
            const resolveTie = (a, b) => {
                const scoreA = a.personType === 'Gestão' ? (a.finalScoreGestao || 0) : (a.qualitative.finalAverage || 0);
                const scoreB = b.personType === 
                'Gestão' ? (b.finalScoreGestao || 0) : (b.qualitative.finalAverage || 0);
                
                if (Math.abs(scoreB - scoreA) > 0.0001) return scoreB - scoreA; 
                
                if (a.personType === 'Colaborador') {
                    const lostA = a.quantitative?.meanPoints || 0;
                    const lostB = b.quantitative?.meanPoints || 0;
                    if (lostA !== lostB) return lostA - lostB; 
                }
                return a.collaboratorName.localeCompare(b.collaboratorName);
            };

            const isQuarterLate = (ev, q, deadlineRule) => {
                if (!deadlineRule || !deadlineRule[q]) return false;
                
                const status = ev.status || '';
                let closedLevel = 0;
                if (status === 'Finalizada' || status === 'T4-Concluido') closedLevel = 4;
                else if (status.startsWith('T')) closedLevel = parseInt(status.replace('T', '').split('-')[0]);
                if (closedLevel >= parseInt(q.replace('q', ''))) return false;
                
                const [d, m] = deadlineRule[q].split('/');
                let deadlineYear = parseInt(ev.periodYear);
                if (q === 'q4' && parseInt(m) < 6) deadlineYear++;
                return new Date() > new Date(deadlineYear, parseInt(m)-1, parseInt(d), 23, 59, 59);
            };
            const activeCollabs = allowedCollaborators.filter(c => 
                (!filterDept || (departments.find(d => d.id === c.departmentId)?.name === filterDept)) &&
                (filterPersonType === 'Todos' || c.personType === filterPersonType)
            );
            const stats = { 
                pending: { q1: 0, q2: 0, q3: 0, q4: 0, details: { q1: [], q2: [], q3: [], q4: [], type: 'pending' } }, 
                inProgress: { q1: 0, q2: 0, q3: 0, q4: 0, details: { q1: [], q2: [], q3: [], q4: [], type: 'inProgress' } }, 
                late: { q1: 0, q2: 0, q3: 0, q4: 0, details: { q1: [], q2: [], q3: [], q4: [], type: 'late' } }, 
                finished: { q1: 0, q2: 0, q3: 0, q4: 0, details: { q1: [], q2: [], q3: [], q4: [], type: 'finished' } } 
            };
            activeCollabs.forEach(c => {
                const ev = evaluations.find(e => e.collaboratorId === c.id && e.periodYear === year);
                const deadlineRule = getDeadlineRule(c, deadlines);

                ['q1', 'q2', 'q3', 'q4'].forEach((q, index) => {
                    if (!ev) {
                        stats.pending[q]++; 
                        stats.pending.details[q].push({ name: c.name, status: 'Não Iniciado' });
                    } else {
                        const status = ev.status || '';
                        let closedLevel = 0;
                        
                        if (status === 'Finalizada' || status === 'T4-Concluido') closedLevel = 4;
                        else if (status.startsWith('T')) closedLevel = parseInt(status.replace('T', '').split('-')[0]);
                        
                        let dynamicClosedLevel = closedLevel;
                        if (dynamicClosedLevel === 0 && status === 'Em Andamento') {
                            const criteria = ev.qualitative?.criteria || [];
                            if (criteria.some(c => c.q4)) dynamicClosedLevel = 3; 
                            else if (criteria.some(c => c.q3)) dynamicClosedLevel = 2;
                            else if (criteria.some(c => c.q2)) dynamicClosedLevel = 1;
                        }
                        
                        if (closedLevel >= index + 1) { 
                            stats.finished[q]++;
                            stats.finished.details[q].push({ name: c.name, status: status }); 
                        } else if (isQuarterLate(ev, q, deadlineRule)) { 
                            stats.late[q]++;
                            stats.late.details[q].push({ name: c.name, status: 'Atrasado' }); 
                        } else {
                            if (dynamicClosedLevel === index) { 
                                stats.inProgress[q]++;
                                stats.inProgress.details[q].push({ name: c.name, status: status }); 
                            } else if (dynamicClosedLevel < index) { 
                                stats.pending[q]++;
                                stats.pending.details[q].push({ name: c.name, status: 'Pendente' }); 
                            } else { 
                                stats.inProgress[q]++;
                                stats.inProgress.details[q].push({ name: c.name, status: status }); 
                            }
                        }
                    }
                });
            });

            let allEvaluationsForYear = evaluations.filter(ev => {
                const collab = collaborators.find(c => c.id === ev.collaboratorId);
                if (!collab || !canViewCollab(collab, currentUser)) return false; 
                const matchYear = filterYear ? ev.periodYear.includes(filterYear) : true;
                return matchYear;
            }).map(ev => {
                const collab = collaborators.find(c => c.id === ev.collaboratorId);
                let config = collab?.evalTypeId ? evaluationTypes.find(et => et.id === collab.evalTypeId) : null;
                
                if (!config) {
                    config = globalConfigs.find(gc => ev.periodYear >= new Date(gc.startDate).getFullYear()) || globalConfigs[0] || {};
                }
                
                const evWithScores = recalculateFinalAverage(ev);
                
                // --- CORREÇÃO DA NOTA DE GESTÃO (Soma estrita dos 4 pilares) ---
                if (evWithScores.personType === 'Gestão') {
                    const quali = evWithScores.qualitative?.finalScoreGestaoQuali || 0;
                    const metas = evWithScores.totalGestaoMetas || 0;
                    const percepcao = evWithScores.totalGestaoPercepcao || 0;
                    const extras = evWithScores.atualizacao?.total || 0;
                    evWithScores.finalScoreGestao = quali + metas + percepcao + extras;
                }

                let score = evWithScores.personType === 'Gestão' ? (evWithScores.finalScoreGestao || 0) : (evWithScores.qualitative?.finalAverage || 0);
                const lost = evWithScores.personType === 'Colaborador' ? (evWithScores.quantitative?.meanPoints || 0) : (evWithScores.atualizacao?.total || 0);
                
                const mediaPromo = config.mediaPromocao || 8.0;
                const mediaPremiacao = config.mediaPremiacao || 7.0;
                const maxLost = config.maxPointsLost || 10.0;
                
                const promoThreshold = evWithScores.personType === 'Gestão' ? (mediaPromo / 10.0) * 100.0 : mediaPromo;
                const premioThreshold = evWithScores.personType === 'Gestão' ? (mediaPremiacao / 10.0) * 100.0 : mediaPremiacao;
                const canWin = evWithScores.personType === 'Colaborador' ? (lost <= maxLost) : true;
                
                const isPromoCandidate = score >= promoThreshold && canWin;
                const isPremioCandidate = score >= premioThreshold && canWin;
                
                let labels = [];
                if (isPromoCandidate) {
                    labels.push({ text: 'Promoção', color: 'bg-green-100 text-green-700', details: { score, needed: promoThreshold, lost, maxLost, type: 'PROMOÇÃO', personType: collab.personType } });
                }
                if (isPremioCandidate) {
                    labels.push({ text: 'Premiação', color: 'bg-blue-100 text-blue-700', details: { score, needed: premioThreshold, lost, maxLost, type: 'PREMIAÇÃO', personType: collab.personType } });
                }
                if (labels.length === 0) {
                    labels.push({ text: '-', color: 'text-gray-400', details: { personType: collab.personType } });
                }
                
                // Gestores vinculados (para busca)
                const linkedManagers = managers.filter(m => collab?.managerIds && collab.managerIds.includes(m.id));
                const managerNames = linkedManagers.length > 0 ? linkedManagers.map(m => m.name).join(' ') : 'N/D';

                const statusDisplay = getEvaluationStatusDisplay(evWithScores);
                const deadlineRule = getDeadlineRule(collab, deadlines);
                
                return { 
                    ...evWithScores, 
                    labels, 
                    trend: getTrendAnalysis(evWithScores), 
                    quantiTrend: getQuantiTrend(evWithScores), 
                    config, 
                    pendingIssues: checkPendingIssues(evWithScores),
                    managerName: managerNames, // String concatenada para busca
                    linkedManagers, // Array para renderização visual
                    personType: collab.personType, 
                    statusDisplay, 
                    finalAverage: score,
                    deadlineRule
                };
            });

            let finalPromotedIds = new Set();
            const gestoresPromotable = allEvaluationsForYear.filter(e => e.personType === 'Gestão' && e.labels.some(l => l.text === 'Promoção'));
            if (gestoresPromotable.length > 0) { 
                gestoresPromotable.sort(resolveTie); 
                finalPromotedIds.add(gestoresPromotable[0].id);
            }
            
            const collabsPromotable = allEvaluationsForYear.filter(e => e.personType === 'Colaborador' && e.labels.some(l => l.text === 'Promoção'));
            const collabsByDept = collabsPromotable.reduce((acc, ev) => { 
        // LÓGICA PADRÃO (Sem agrupamento RLC)
        // Usa sempre o departamento original do colaborador
        const groupKey = ev.department; 

        if (!acc[groupKey]) acc[groupKey] = []; 
        acc[groupKey].push(ev); 
        return acc; 
        }, {});

            Object.values(collabsByDept).forEach(deptGroup => { 
                deptGroup.sort(resolveTie); 
                // Pega o melhor de cada grupo (RLC Unificado conta como 1 grupo agora)
                if (deptGroup.length > 0) finalPromotedIds.add(deptGroup[0].id); 
            });

            // --- FILTRO DE PESQUISA GLOBAL ---
            let filteredList = allEvaluationsForYear.filter(ev => {
            if (ev.personType === 'Gestão' && currentUser?.permissions?.viewGestaoDashEval !== true) {
                return false;
            }

            const matchDept = filterDept ? ev.department === filterDept : true;
            const matchPersonType = filterPersonType !== 'Todos' ? ev.personType === filterPersonType : true;
            const matchStatus = filterStatus ? ev.status === filterStatus : true;
                
                // Lógica de Pesquisa Global (todos os campos)
                const searchLower = globalSearch.toLowerCase();
                const matchSearch = globalSearch ? (
                    ev.collaboratorName.toLowerCase().includes(searchLower) || // Nome
                    ev.department.toLowerCase().includes(searchLower) ||       // Depto
                    ev.managerName.toLowerCase().includes(searchLower) ||      // Gestor(es)
                    ev.evalTypeName.toLowerCase().includes(searchLower) ||     // Tipo
                    ev.periodYear.includes(searchLower) ||                     // Ano
                    ev.status.toLowerCase().includes(searchLower) ||           // Status
                    (ev.finalAverage?.toFixed(4) || '').includes(searchLower) || // Nota
                    ev.labels.some(l => l.text.toLowerCase().includes(searchLower)) // Resultado
                ) : true;

                return matchDept && matchPersonType && matchStatus && matchSearch;
            });

            filteredList = filteredList.map(ev => {
                const hasPromoLabel = ev.labels.some(l => l.text === 'Promoção');
                if (hasPromoLabel) {
                    const actuallyPromoted = finalPromotedIds.has(ev.id);
                    if (!actuallyPromoted) {
                        ev.labels = ev.labels.filter(l => l.text !== 'Promoção');
                        if (ev.labels.length === 0) ev.labels.push({ text: '-', color: 'text-gray-400', details: { personType: ev.personType } });
                    }
                }
                return ev;
            });
            if (filterResult !== 'Todos') {
                if(filterResult === 'Sem Prom/Prem') {
                    filteredList = filteredList.filter(e => e.labels[0].text === '-');
                } else {
                    filteredList = filteredList.filter(ev => ev.labels.some(l => l.text === filterResult));
                }
            }

            const dashboardSort = (a, b) => {
                const getScore = (ev) => ev.personType === 'Gestão' ? ev.finalScoreGestao : ev.qualitative.finalAverage;
                const getResultWeight = (ev) => {
                    const labels = ev.labels || [];
                    if (labels.some(l => l.text === 'Promoção')) return 3;
                    if (labels.some(l => l.text === 'Premiação')) return 2;
                    return 1;
                };
                const weightA = getResultWeight(a); 
                const weightB = getResultWeight(b);
                
                if (a.personType === 'Gestão' && b.personType === 'Gestão') {
                    if (weightA !== weightB) return weightB - weightA;
                    return getScore(b) - getScore(a); 
                }
                if (a.personType === 'Colaborador' && b.personType === 'Colaborador') {
                    const deptA = a.department || ''; 
                    const deptB = b.department || '';
                    const diffDept = deptA.localeCompare(deptB);
                    if (diffDept !== 0) return diffDept;
                    if (weightA !== weightB) return weightB - weightA;
                    return getScore(b) - getScore(a);
                }
                if (a.personType === 'Gestão') return -1;
                if (b.personType === 'Gestão') return 1;
                return 0;
            };

            return { metrics: stats, processedList: filteredList.sort(dashboardSort) };
        }, [evaluations, filterYear, globalSearch, filterDept, filterStatus, filterResult, filterPersonType, allowedCollaborators, departments, managers, globalConfigs, evaluationTypes, permissions, deadlines, currentUser]);

        // --- LÓGICA DO BANCO DE TALENTOS (CORRIGIDA: NOTA VS MÉDIA) ---
        const talentPoolData = useMemo(() => {
            // 1. Filtra candidatos elegíveis comparando Nota vs Média Necessária (Config)
            // Ignora a etiqueta 'Promoção' para incluir todos que atingiram a meta, mesmo se não ganharam o desempate.
            const candidates = processedList.filter(ev => {
                const config = ev.config || {};
                const mediaPromo = parseFloat(config.mediaPromocao) || 8.0;
                const maxLost = parseFloat(config.maxPointsLost) || 10.0;

                if (ev.personType === 'Gestão') {
                    const score = ev.finalScoreGestao || 0;
                    // Converte média (ex: 8.0) para escala 100 (ex: 80.0)
                    const threshold = (mediaPromo / 10.0) * 100.0; 
                    return score >= threshold;
                } else {
                    const score = ev.qualitative?.finalAverage || 0;
                    const lost = ev.quantitative?.meanPoints || 0;
                    // Para colaborador: Nota >= Média E Pontos Perdidos <= Limite
                    return score >= mediaPromo && lost <= maxLost;
                }
            });

            // Função de Ordenação (Decrescente por nota)
            const sortCandidates = (list) => {
                return list.sort((a, b) => {
                    const scoreA = a.personType === 'Gestão' ? (a.finalScoreGestao || 0) : (a.qualitative.finalAverage || 0);
                    const scoreB = b.personType === 'Gestão' ? (b.finalScoreGestao || 0) : (b.qualitative.finalAverage || 0);
                    return scoreB - scoreA;
                });
            };

            // LÓGICA DO REQUISITO 3 (AGRUPAMENTO):
            const isUserDir = currentUser && departments.find(d => d.id === currentUser.departmentId)?.name.toUpperCase().includes('DIR');
            const isAllDepts = !filterDept || filterDept === 'Todos';
            const shouldGroup = isAllDepts && !isUserDir;

            if (shouldGroup) {
                // Modo Agrupado (Lista longa com scroll)
                const grouped = {};
                candidates.forEach(c => {
                    if (!grouped[c.department]) grouped[c.department] = [];
                    grouped[c.department].push(c);
                });
                
                // Ordena internamente cada grupo (Top 4 por departamento)
                const sortedGrouped = {};
                Object.keys(grouped).sort().forEach(key => {
                    sortedGrouped[key] = sortCandidates(grouped[key]).slice(0, 4);
                });

                return { 
                    mode: 'grouped', 
                    data: sortedGrouped
                };
            } else {
                // Modo Padrão (Top 4 Global ou do Depto selecionado)
                return { 
                    mode: 'standard', 
                    data: sortCandidates(candidates).slice(0, 4) 
                };
            }
        }, [processedList, filterDept, currentUser, departments]);


        // --- GERAÇÃO DE PDF COMPLETA (VERSÃO FINAL EXPANDIDA) ---
        const generatePDF = useCallback(async (evParam, typeParam) => {
            // 1. Definição do Alvo
            const evRaw = evParam || selectedEval;
            const type = typeParam || 'Completo';

            // Validação inicial
            if (!evRaw) {
                return;
            }

            setIsGenerating(true);

            try {
                // ===========================================================
                // 1. LÓGICA DE FILTRAGEM DE DADOS (SEGURANÇA / HIGIENE)
                // ===========================================================
                
                // Cria uma cópia profunda para não alterar a visualização na tela
                const ev = JSON.parse(JSON.stringify(evRaw));

                // Determina até qual trimestre está OFICIALMENTE LIBERADO no PDF.
                // Regra de segurança: trimestre com dado lançado, mas ainda sem assinatura, NÃO aparece.
                // Isso evita que ocorrências/notas do trimestre seguinte vazem antes do fechamento.
                const mgrSigs = Array.isArray(ev.managerSignatures)
                    ? ev.managerSignatures
                    : (ev.managerSignature ? [ev.managerSignature] : []);

                const getSignedQuarterNumber = (sig) => {
                    const ref = String(sig?.statusRef || sig?.quarter || sig?.status || '').toUpperCase();
                    if (ref.includes('T4') || ref.includes('4º') || ref.includes('FINALIZADA') || ref.includes('ASSINADA')) return 4;
                    if (ref.includes('T3') || ref.includes('3º')) return 3;
                    if (ref.includes('T2') || ref.includes('2º')) return 2;
                    if (ref.includes('T1') || ref.includes('1º')) return 1;
                    return 0;
                };

                let maxAllowedQ = mgrSigs.reduce((maxQ, sig) => Math.max(maxQ, getSignedQuarterNumber(sig)), 0);

                // Arquivamento financeiro pressupõe ciclo integralmente concluído.
                if (ev.status === 'Concluída Financeiro') maxAllowedQ = 4;

                const getQuarterNumberFromValue = (quarterValue, dateValue, timestampValue) => {
                    const raw = String(quarterValue || '').toUpperCase();
                    if (raw.includes('Q4') || raw.includes('T4') || raw.includes('4º')) return 4;
                    if (raw.includes('Q3') || raw.includes('T3') || raw.includes('3º')) return 3;
                    if (raw.includes('Q2') || raw.includes('T2') || raw.includes('2º')) return 2;
                    if (raw.includes('Q1') || raw.includes('T1') || raw.includes('1º')) return 1;

                    let d = null;
                    if (dateValue) d = new Date(String(dateValue).length === 10 ? `${dateValue}T12:00:00` : dateValue);
                    else if (timestampValue) d = new Date(timestampValue);
                    if (!d || Number.isNaN(d.getTime())) return 0;
                    return Math.floor(d.getMonth() / 3) + 1;
                };

                const formatPdfGrade = (value) => {
                    if (value === null || value === undefined || value === '') return '-';

                    const num = Number(value);
                    if (!isNaN(num)) return num.toFixed(1);

                    return String(value);
                };

                const formatPdfCount = (value) => {
                    if (value === null || value === undefined || value === '') return '-';

                    const num = Number(value);
                    if (!isNaN(num)) return String(num);

                    return String(value);
                };

                const getQuarterWeightedPoints = (error, q) => {
                    const qtd = Number(error?.[q] || 0);
                    const peso = Number(error?.peso || 0);
                    return qtd * peso;
                };

                // Aplica a limpeza dos dados futuros (não assinados)
                if (maxAllowedQ < 4) {
                    // Limpa Critérios Qualitativos
                    if (ev.qualitative && ev.qualitative.criteria) {
                        ev.qualitative.criteria.forEach(c => {
                            if (maxAllowedQ < 1) {
                                c.q1 = null;
                            }
                            if (maxAllowedQ < 2) {
                                c.q2 = null;
                            }
                            if (maxAllowedQ < 3) {
                                c.q3 = null;
                            }
                            if (maxAllowedQ < 4) {
                                c.q4 = null;
                            }
                        });
                    }
                    
                    // Limpa Erros Quantitativos
                    if (ev.quantitative && ev.quantitative.errors) {
                        ev.quantitative.errors.forEach(e => {
                            if (maxAllowedQ < 1) {
                                e.q1 = 0;
                            }
                            if (maxAllowedQ < 2) {
                                e.q2 = 0;
                            }
                            if (maxAllowedQ < 3) {
                                e.q3 = 0;
                            }
                            if (maxAllowedQ < 4) {
                                e.q4 = 0;
                            }
                        });
                    }
                    
                    // Limpa Metas de Gestão
                    if (ev.gestaoMetasPercepcao) {
                        ev.gestaoMetasPercepcao.forEach(item => {
                            if (item.scores) {
                                if (maxAllowedQ < 1) {
                                    item.scores.q1 = null;
                                }
                                if (maxAllowedQ < 2) {
                                    item.scores.q2 = null;
                                }
                                if (maxAllowedQ < 3) {
                                    item.scores.q3 = null;
                                }
                                if (maxAllowedQ < 4) {
                                    item.scores.q4 = null;
                                }
                            }
                        });
                    }
                }

            // Recalcula as notas finais baseadas APENAS nos dados filtrados
                const evWithScores = recalculateFinalAverage(ev);
                const isGestao = ev.personType === 'Gestão';

                // --- CORREÇÃO DA NOTA DE GESTÃO NO PDF ---
                if (isGestao) {
                    const quali = evWithScores.qualitative?.finalScoreGestaoQuali || 0;
                    const metas = evWithScores.totalGestaoMetas || 0;
                    const percepcao = evWithScores.totalGestaoPercepcao || 0;
                    const extras = evWithScores.atualizacao?.total || 0;
                    evWithScores.finalScoreGestao = quali + metas + percepcao + extras;
                }

                // ===========================================================
                // 2. INICIALIZAÇÃO DO PDF
                // ===========================================================

                if (typeof loadPdfLibs !== 'undefined') {
                    await loadPdfLibs();
                } else if (!window.jspdf) {
                    throw new Error("Biblioteca PDF não carregada. Verifique sua conexão.");
                }

                const PDFConstructor = (window.jspdf && window.jspdf.jsPDF) ? window.jspdf.jsPDF : window.jsPDF;
                const doc = new PDFConstructor('p', 'mm', 'a4');
                
                const margin = 14;
                let y = 20;

                // Dados Auxiliares para Cabeçalho
                const collabData = data.collaborators.find(c => c.id === ev.collaboratorId);
                
                let realDeptName = ev.department;
                if (collabData) {
                    const deptObj = data.departments.find(d => d.id === collabData.departmentId);
                    if (deptObj) {
                        realDeptName = deptObj.name;
                    }
                }

                const linkedManagers = data.managers.filter(m => {
                    return collabData && collabData.managerIds && collabData.managerIds.includes(m.id);
                });
                
                const managerNameFallback = linkedManagers.length > 0 
                    ? linkedManagers.map(m => m.name).join(' / ') 
                    : 'Gestor(a) Responsável';

                // ===========================================================
                // 3. HELPERS DE DESENHO (FUNÇÕES INTERNAS)
                // ===========================================================

                // Helper: Tabela de Percepção (Página 2)
                const drawPerceptionSection = (startYPosition) => {
                    const perceptionCols = (data.perceptionCycles || []).sort((a, b) => {
                        return (a.order || 0) - (b.order || 0);
                    });
                    
                    if (perceptionCols.length === 0) {
                        return startYPosition;
                    }

                    const targetEmail = collabData ? collabData.email.toLowerCase() : '';
                    
                    const rawRecords = (data.teamPerception || []).filter(p => {
                        const pManagerEmail = p.manager_email ? p.manager_email.toLowerCase() : '';
                        const hasDate = p.date && p.date.endsWith(ev.periodYear);
                        return pManagerEmail === targetEmail && hasDate;
                    });

                    if (rawRecords.length === 0) {
                        return startYPosition;
                    }

                    const tableBody = rawRecords.map(rec => {
                        const row = [rec.date, String(rec.nota || '-')];
                        perceptionCols.forEach(col => {
                            const val = rec[col.linkedField || 'nota'] || '-';
                            row.push(String(val));
                        });
                        return row;
                    });

                    if (startYPosition + 40 > doc.internal.pageSize.getHeight()) { 
                        doc.addPage('a4', 'l'); 
                        startYPosition = 20; 
                    }

                    doc.setFontSize(12); 
                    doc.setTextColor(230, 81, 0); 
                    doc.setFont('helvetica', 'bold');
                    doc.text("DETALHAMENTO: PERCEPÇÃO DA EQUIPE", 14, startYPosition);
                    startYPosition += 6;

                    const headers = ["DATA", "NOTA", ...perceptionCols.map(c => c.description)];

                    doc.autoTable({
                        startY: startYPosition,
                        head: [headers],
                        body: tableBody,
                        theme: 'grid',
                        styles: { 
                            fontSize: 7, 
                            cellPadding: 2, 
                            overflow: 'linebreak',
                            valign: 'middle' // Garante que o texto fique no meio verticalmente
                        },
                        headStyles: { 
                            fillColor: [239, 108, 0], 
                            textColor: [255, 255, 255], 
                            halign: 'center', 
                            fontStyle: 'bold' 
                        },
                        columnStyles: {
                            1: { halign: 'center', fontStyle: 'bold' }, // Coluna 1 (NOTA) centralizada
                        },
                        didParseCell: (data) => {
                            // Aplicamos a lógica em todas as colunas de dados da tabela (seção body)
                            if (data.section === 'body') {
                                const cellValue = String(data.cell.raw).trim();

                                // Mapeamento completo de cores para garantir que todos os campos pintem
                                if (cellValue.includes('Insatisfatório')) {
                                    data.cell.styles.fillColor = [244, 67, 54]; // Vermelho
                                    data.cell.styles.textColor = [255, 255, 255]; // Letra Branca
                                } else if (cellValue.includes('Regular')) {
                                    data.cell.styles.fillColor = [230, 81, 0]; // Laranja
                                    data.cell.styles.textColor = [255, 255, 255];
                                } else if (cellValue.includes('Bom') && !cellValue.includes('Muito Bom')) {
                                    data.cell.styles.fillColor = [245, 127, 23]; // Amarelo Escuro
                                    data.cell.styles.textColor = [255, 255, 255];
                                } else if (cellValue.includes('Muito Bom')) {
                                    data.cell.styles.fillColor = [46, 125, 50]; // Verde
                                    data.cell.styles.textColor = [255, 255, 255];
                                } else if (cellValue.includes('Excelente')) {
                                    data.cell.styles.fillColor = [21, 101, 192]; // Azul
                                    data.cell.styles.textColor = [255, 255, 255];
                                }

                                // Centralizar apenas a coluna da NOTA (índice 1)
                                if (data.column.index === 1) {
                                    data.cell.styles.halign = 'center';
                                }
                            }
                        }
                    });
                    return doc.lastAutoTable.finalY + 10;
                };

        // Helper: Quadro de Assinaturas (CORRIGIDO: INVALID DATE + LINHAS EXTRAS)
                const drawSignatureTable = (startYPosition) => {
                    const year = evRaw.periodYear; 
                    
                    const headers = [
                        "TIPO ASSINATURA", 
                        `1º TRIM/${year}`, 
                        `2º TRIM/${year}`, 
                        `3º TRIM/${year}`, 
                        `4º TRIM/${year}`
                    ];

                    const formatSigCell = (sigData) => {
                        if (!sigData) {
                            return "Aguardando assinatura...";
                        }
                        
                        let dateStr = '-';
                        
                        // --- CORREÇÃO DE DATA (INVALID DATE) ---
                        // 1. Se já vier como string formatada BR (ex: "25/10/2023, 14:00")
                        if (sigData.date && typeof sigData.date === 'string' && sigData.date.includes('/')) {
                            // Pega apenas a parte da data, ignorando a hora após a vírgula ou espaço
                            dateStr = sigData.date.split(',')[0].split(' ')[0].trim();
                        } 
                        // 2. Se for ISO (YYYY-MM-DD) ou Timestamp numérico
                        else if (sigData.date) {
                            try {
                                const d = new Date(sigData.date);
                                // Verifica se a data é válida
                                if (!isNaN(d.getTime())) {
                                    dateStr = d.toLocaleDateString('pt-BR');
                                } else {
                                    dateStr = String(sigData.date); // Fallback: mostra como está
                                }
                            } catch (e) {
                                dateStr = '-';
                            }
                        }

                        return `Assinado em: ${dateStr}\n\n${sigData.name}\nID: ${sigData.id || 'N/D'}`;
                    };

                    // 1. Linha GESTOR
                    const mgrRow = ["ASSINATURA\nGESTOR"];
                    let mgrSigs = [];
                    if (evRaw.managerSignatures) {
                        mgrSigs = evRaw.managerSignatures;
                    } else if (evRaw.managerSignature) {
                        mgrSigs = [evRaw.managerSignature];
                    }
                    
                    const quartersMap = [['T1', '1º'], ['T2', '2º'], ['T3', '3º'], ['T4', '4º', 'FINALIZADA', 'ASSINADA']];

                    quartersMap.forEach(qTags => {
                        const signaturesFound = mgrSigs.filter(s => {
                            const ref = (s.statusRef || '').toUpperCase();
                            return qTags.some(tag => ref.includes(tag));
                        });

                        if (signaturesFound.length > 0) {
                            const textContent = signaturesFound.map(sig => 
                                // Passa o objeto completo para o formatador corrigido
                                formatSigCell(sig).replace('Assinado em: ', '') // Pequeno ajuste para evitar duplicar texto se necessário, ou usar direto
                            ).join('\n----------------\n');
                            
                            // Reconstrói a string formatada usando o helper (mais seguro)
                            // Como pode haver múltiplas, pegamos a mais recente ou formatamos individualmente
                            const formattedSigs = signaturesFound.map(sig => {
                                // Extração manual da data corrigida para este loop específico
                                let dStr = '-';
                                if (sig.date && typeof sig.date === 'string' && sig.date.includes('/')) dStr = sig.date.split(',')[0].split(' ')[0].trim();
                                else if (sig.date) try { dStr = new Date(sig.date).toLocaleDateString('pt-BR'); } catch(e){}
                                
                                return `Assinado em: ${dStr}\n${sig.gestorName || sig.name}\n(ID: ${sig.gestorId || sig.id})`;
                            }).join('\n----\n');

                            mgrRow.push(formattedSigs);
                        } else {
                            mgrRow.push("Aguardando assinatura\nGestor");
                        }
                    });

                    // 2. Linha COLABORADOR
                    const colRow = ["ASSINATURA\nCOLABORADOR"];
                    const colSigs = evRaw.collabSignatures || {};
                    
                    ['q1', 'q2', 'q3', 'q4'].forEach(qKey => {
                        const sig = colSigs[qKey];
                        if (sig) {
                            // Garante email
                            let emailDisplay = sig.email;
                            if (!emailDisplay) {
                                const cRegister = data.collaborators.find(c => c.id === ev.collaboratorId);
                                if (cRegister) emailDisplay = cRegister.email;
                            }
                            colRow.push(formatSigCell({
                                date: sig.date,
                                name: sig.name || ev.collaboratorName, 
                                id: sig.id
                            }));
                        } else {
                            colRow.push("Aguardando assinatura\nColaborador");
                        }
                    });

                    // --- 3. Linha DIRETORIA ---
                    const dirSig = evRaw.diretoriaSignature;
                    const dirRow = [
                        "ASSINATURA\nDIRETORIA",
                        "-", "-", "-", // T1-T3
                        dirSig ? formatSigCell(dirSig) : "Aguardando Validação" // T4
                    ];

                    // --- 4. Linha RH ---
                    const rhSig = evRaw.rhSignature;
                    const rhRow = [
                        "ASSINATURA\nRH",
                        "-", "-", "-", // T1-T3
                        rhSig ? formatSigCell(rhSig) : "Aguardando Validação" // T4
                    ];

                    // Montagem do Corpo
                    const tableBody = [mgrRow, colRow];
                    if (evRaw.personType !== 'Gestão') {
                        tableBody.push(dirRow);
                    }
                    tableBody.push(rhRow);

                    if (startYPosition + 70 > doc.internal.pageSize.getHeight()) {
                        doc.addPage();
                        startYPosition = 20;
                    }

                    doc.autoTable({
                        startY: startYPosition,
                        head: [headers],
                        body: tableBody,
                        theme: 'grid',
                        styles: { 
                            fontSize: 6, 
                            cellPadding: 2, 
                            valign: 'middle', 
                            halign: 'center', 
                            lineWidth: 0.1, 
                            lineColor: [200, 200, 200],
                            overflow: 'linebreak' 
                        },
                        headStyles: { 
                            fillColor: [30, 58, 138], 
                            textColor: [255, 255, 255], 
                            fontStyle: 'bold', 
                            halign: 'center',
                            minCellHeight: 8
                        },
                        columnStyles: {
                            0: { fontStyle: 'bold', fillColor: [243, 244, 246], cellWidth: 30, valign: 'middle' }, 
                            1: { cellWidth: 40 }, 
                            2: { cellWidth: 40 }, 
                            3: { cellWidth: 40 }, 
                            4: { cellWidth: 40 }
                        },
                        didParseCell: (data) => {
                            if (data.section === 'body') {
                                if (data.column.index > 0 && typeof data.cell.raw === 'string' && data.cell.raw.includes("Aguardando")) {
                                    data.cell.styles.textColor = [150, 150, 150];
                                    data.cell.styles.fontStyle = 'italic';
                                }
                                if (data.row.index >= 2 && data.column.index === 0) { 
                                    data.cell.styles.fillColor = [230, 230, 230];
                                }
                            }
                        }
                    });

                    return doc.lastAutoTable.finalY + 10;
                };

                // ===========================================================
                // 4. CONSTRUÇÃO DO CORPO DO PDF
                // ===========================================================

                // Cabeçalho e Logo
                const logoUrl = data.globalConfigs && data.globalConfigs[0] ? data.globalConfigs[0].logoUrl : null;
                
                if (logoUrl) {
                    try {
                        doc.addImage(logoUrl, 'PNG', 14, 10, 25, 25);
                        doc.setFontSize(11); 
                        doc.setFont("helvetica", "bold"); 
                        doc.text("AEC - ACOMPANHAMENTO DE EVOLUÇÃO CONTINUADA", 105, 25, { align: "center" });
                        y = 45;
                    } catch (e) {
                        doc.setFillColor(18, 169, 149); 
                        doc.rect(0, 0, 210, 30, 'F'); 
                        doc.setTextColor(255, 255, 255); 
                        doc.setFontSize(11); 
                        doc.setFont("helvetica", "bold"); 
                        doc.text("AEC - ACOMPANHAMENTO DE EVOLUÇÃO CONTINUADA", margin, 20);
                        y = 40;
                    }
                } else {
                    doc.setFillColor(18, 169, 149); 
                    doc.rect(0, 0, 210, 30, 'F'); 
                    doc.setTextColor(255, 255, 255); 
                    doc.setFontSize(11); 
                    doc.setFont("helvetica", "bold"); 
                    doc.text("AEC - ACOMPANHAMENTO DE EVOLUÇÃO CONTINUADA", margin, 20);
                    y = 40;
                }

                // Tabela de Identificação
                const identificationData = [
                    ["Colaborador:", ev.collaboratorName, "Cargo:", ev.role || '-'],
                    ["Departamento:", realDeptName, "Gestor(es):", managerNameFallback],
                    ["Ano Avaliado:", ev.periodYear, "Tipo:", ev.evalTypeName || 'Padrão']
                ];

                doc.autoTable({
                    startY: y, 
                    body: identificationData, 
                    theme: 'grid',
                    styles: { 
                        fontSize: 9, 
                        cellPadding: 1.5, 
                        lineColor: [200, 200, 200], 
                        textColor: [0,0,0] 
                    },
                    columnStyles: { 
                        0: { fontStyle: 'bold', fillColor: [240, 240, 240], cellWidth: 30 }, 
                        1: { cellWidth: 70 }, 
                        2: { fontStyle: 'bold', fillColor: [240, 240, 240], cellWidth: 25 }, 
                        3: { cellWidth: 'auto' } 
                    }
                });
                
                y = doc.lastAutoTable.finalY + 10;

                // ===========================================================
                // 5. CONTEÚDO BASEADO NO TIPO (SIMPLES / COMPLETO)
                // ===========================================================
                
                // CASO A: SIMPLIFICADO GESTÃO
                if (type === 'Simplificado Gestão' && isGestao) {
                    doc.setFontSize(12); 
                    doc.setTextColor(0, 0, 0); 
                    doc.setFont('helvetica', 'bold'); 
                    doc.text("QUADRO RESUMO DE PONTUAÇÃO (GESTÃO)", margin, y); 
                    y += 6;

                    // Funções de resumo
                    const getQualiQ = (q) => { 
                        let sum = 0, count = 0; 
                        if (evWithScores.qualitative && evWithScores.qualitative.criteria) {
                            evWithScores.qualitative.criteria.forEach(c => {
                                if (c[q] !== null && c[q] !== '' && c[q] !== undefined) { 
                                    sum += parseFloat(c[q]); 
                                    count++; 
                                }
                            }); 
                        }
                        if (count === 0) return 0; 
                        return (sum / count) * 2.5; 
                    };

                    const getGestaoItemQ = (typeFilter, q) => { 
                        if (!evWithScores.gestaoMetasPercepcao) return 0; 
                        const items = evWithScores.gestaoMetasPercepcao.filter(i => {
                            if (typeFilter === 'Metas') {
                                return (i.type === 'Metas');
                            } else {
                                return (i.type === 'Percepção' || i.name.toLowerCase().includes('percepção'));
                            }
                        }); 
                        return items.reduce((acc, item) => {
                            const val = item.scores && item.scores[q] ? parseFloat(item.scores[q]) : 0;
                            return acc + val;
                        }, 0); 
                    };

                    const summaryData = [
                        ["1. Qualitativa (Máx: 40)", getQualiQ('q1').toFixed(2), getQualiQ('q2').toFixed(2), getQualiQ('q3').toFixed(2), getQualiQ('q4').toFixed(2), evWithScores.qualitative.finalScoreGestaoQuali?.toFixed(2), '#0b5394'],
                        ["2. Plan. Estratégico (Máx: 20)", getGestaoItemQ('Metas', 'q1').toFixed(2), getGestaoItemQ('Metas', 'q2').toFixed(2), getGestaoItemQ('Metas', 'q3').toFixed(2), getGestaoItemQ('Metas', 'q4').toFixed(2), evWithScores.totalGestaoMetas?.toFixed(2), '#ef4444'],
                        ["3. Percepção Equipe (Máx: 20)", getGestaoItemQ('Percepção', 'q1').toFixed(2), getGestaoItemQ('Percepção', 'q2').toFixed(2), getGestaoItemQ('Percepção', 'q3').toFixed(2), getGestaoItemQ('Percepção', 'q4').toFixed(2), evWithScores.totalGestaoPercepcao?.toFixed(2), '#f97316'],
                        ["4. Capacitações (Máx: 20)", "-", "-", "-", "-", evWithScores.atualizacao?.total?.toFixed(2), '#9333ea'],
                        [`Total Final`, "", "", "", "", evWithScores.finalScoreGestao?.toFixed(2), "TOTAL_ROW"]
                    ];

                    doc.autoTable({
                        startY: y, 
                        head: [['Componente', 'Trim 1', 'Trim 2', 'Trim 3', 'Trim 4', 'Total Obtido']],
                        body: summaryData.map(row => row.slice(0, 6)),
                        theme: 'grid',
                        headStyles: { halign: 'center', fillColor: [220, 220, 220], textColor: [0, 0, 0], fontStyle: 'bold' },
                        columnStyles: { 
                            0: { cellWidth: 60, fontStyle: 'bold' }, 
                            1: { halign: 'center' }, 
                            2: { halign: 'center' }, 
                            3: { halign: 'center' }, 
                            4: { halign: 'center' }, 
                            5: { halign: 'center', fontStyle: 'bold' } 
                        },
                        didParseCell: (data) => {
                            if (data.section === 'body' && data.row.index < 4) {
                                const color = summaryData[data.row.index][6];
                                const rgb = hexToRgb(color);
                                if (data.column.index === 5 && rgb) {
                                    data.cell.styles.fillColor = rgb;
                                    data.cell.styles.textColor = [255, 255, 255];
                                }
                            }
                        },
                        willDrawCell: (data) => {
                            if (data.section === 'body' && data.row.index === 4) { 
                                data.row.height = data.row.height * 1.1; 
                            }
                        },
                        didDrawCell: (data) => {
                            if (data.section === 'body' && data.row.index === 4 && data.column.index === 5) {
                                const score = evWithScores.finalScoreGestao?.toFixed(4) || '0.0000';
                                const total = " / 100.00";
                                const fullText = score + total;
                                
                                doc.setFontSize(11); 
                                doc.setFont('helvetica', 'bold'); 
                                doc.setTextColor(16, 185, 129);
                                
                                const textWidth = doc.getTextWidth(fullText);
                                const centerX = data.cell.x + (data.cell.width / 2) - (textWidth / 2);
                                const centerY = data.cell.y + (data.cell.height / 2) + 1.5;
                                
                                doc.text(fullText, centerX, centerY);
                                
                                doc.setFontSize(10); 
                                doc.setTextColor(0, 0, 0); 
                                doc.setFont('helvetica', 'normal');
                            }
                        }
                    });
                    y = doc.lastAutoTable.finalY + 3;

                // CASO B: SIMPLIFICADO COLABORADOR
                } else if (type === 'Simplificado Colaborador' && !isGestao) {
                    doc.setFontSize(12); 
                    doc.setTextColor(0, 0, 0); 
                    doc.setFont(undefined, 'bold'); 
                    doc.text("1. AVALIAÇÃO QUALITATIVA", margin, y); 
                    y += 5;
                    
                    const qualiRows = (evWithScores.qualitative?.criteria || []).map(c => [
                        c.name || '-',
                        c.dim || '-',
                        formatPdfGrade(c.q1),
                        formatPdfGrade(c.q2),
                        formatPdfGrade(c.q3),
                        formatPdfGrade(c.q4)
                    ]);
                    doc.autoTable({ 
                        startY: y, 
                        head: [['Critério', 'Dimensão', 'Q1', 'Q2', 'Q3', 'Q4']], 
                        body: qualiRows, 
                        theme: 'grid', 
                        headStyles: { fillColor: [11, 83, 148], fontSize: 8, halign: 'center', textColor: [255, 255, 255] }, 
                        bodyStyles: { fontSize: 7 },
                        columnStyles: { 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' } }
                    });
                    y = doc.lastAutoTable.finalY + 8;
                    
                    doc.setFillColor(219, 234, 254);
                    doc.setDrawColor(96, 165, 250);
                    doc.roundedRect(125, y - 4, 70, 8, 2, 2, 'FD');
                    doc.setFontSize(9);
                    doc.setTextColor(30, 64, 175);
                    doc.setFont('helvetica', 'bold');
                    doc.text(`Média Qualitativa: ${evWithScores.qualitative.finalAverage.toFixed(4)}`, 192, y + 1, { align: 'right' });
                    doc.setTextColor(0, 0, 0);
                    y += 11;

                    doc.setFontSize(12); 
                    doc.setFont(undefined, 'bold'); 
                    doc.text("2. AVALIAÇÃO QUANTITATIVA", margin, y); 
                    y += 5;
                    
                    // CORREÇÃO AQUI: Ordenação Numérica Natural e Alinhamento
                    const sortedQuantiErrors = [...(evWithScores.quantitative?.errors || [])].sort((a, b) =>
                        (a.name || '').localeCompare((b.name || ''), undefined, { numeric: true })
                    );

                    const quantiRows = sortedQuantiErrors.map(e => {
                        const totalRow = ['q1', 'q2', 'q3', 'q4'].reduce(
                            (sum, q) => sum + getQuarterWeightedPoints(e, q),
                            0
                        );

                        return [
                            formatOccurrenceTerm(e.name) || '-',
                            Number(e.peso || 0).toFixed(1),
                            formatPdfCount(e.q1),
                            formatPdfCount(e.q2),
                            formatPdfCount(e.q3),
                            formatPdfCount(e.q4),
                            totalRow.toFixed(1)
                        ];
                    });
                    
                    doc.autoTable({ 
                        startY: y, 
                        head: [['Tipo de Ocorrência', 'Peso', 'Q1', 'Q2', 'Q3', 'Q4', 'Total Pts']], 
                        body: quantiRows, 
                        theme: 'grid', 
                        headStyles: { fillColor: [192, 57, 43], fontSize: 8, halign: 'center', textColor: [255, 255, 255] }, 
                        bodyStyles: { fontSize: 7 },
                        columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' }, 6: { halign: 'center' } }
                    });
                    y = doc.lastAutoTable.finalY + 8; 
                    
                    doc.setFillColor(254, 215, 170);
                    doc.setDrawColor(251, 146, 60);
                    doc.roundedRect(125, y - 4, 70, 8, 2, 2, 'FD');
                    doc.setFontSize(9);
                    doc.setTextColor(154, 52, 18);
                    doc.setFont('helvetica', 'bold');
                    doc.text(`Média Quantitativa: ${evWithScores.quantitative.meanPoints.toFixed(4)}`, 192, y + 1, { align: 'right' });
                    doc.setTextColor(0, 0, 0);
                    y += 11;

                // CASO C: COMPLETO (AMBOS)
                } else if (type === 'Completo') {
                    doc.setFontSize(12); 
                    doc.setTextColor(0, 0, 0); 
                    doc.setFont(undefined, 'bold'); 
                    doc.text("1. AVALIAÇÃO QUALITATIVA", margin, y);

                    const qualiRows = (evWithScores.qualitative?.criteria || []).map(c => [
                        c.name || '-',
                        c.dim || '-',
                        formatPdfGrade(c.q1),
                        formatPdfGrade(c.q2),
                        formatPdfGrade(c.q3),
                        formatPdfGrade(c.q4)
                    ]);
                    doc.autoTable({
                        startY: y + 5,
                        head: [['Critério', 'Dimensão', 'Q1', 'Q2', 'Q3', 'Q4']],
                        body: qualiRows,
                        theme: 'grid',
                        headStyles: { fillColor: [11, 83, 148] },
                        // CORREÇÃO: Alinhamento Qualitativo
                        columnStyles: { 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' } },
                        foot: [[
                            { content: isGestao ? 'Pontuação Qualitativa Total (0-40):' : 'Média Qualitativa Final (0-10):', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold', fillColor: [239, 246, 255], textColor: [30, 64, 175] } },
                            { content: isGestao ? evWithScores.qualitative.finalScoreGestaoQuali?.toFixed(4) : evWithScores.qualitative.finalAverage?.toFixed(4), styles: { fontStyle: 'bold', fillColor: [219, 234, 254], textColor: [30, 64, 175], halign: 'center', lineColor: [96, 165, 250], lineWidth: 0.4 } }
                        ]]
                    });
                    y = doc.lastAutoTable.finalY + 10;

                    if (!isGestao) {
                        doc.setFontSize(12);
                        doc.text("2. AVALIAÇÃO QUANTITATIVA", margin, y);
                        
                        // CORREÇÃO AQUI: Ordenação Numérica Natural e Alinhamento
                        const sortedQuantiErrors = [...(evWithScores.quantitative?.errors || [])].sort((a, b) =>
                            (a.name || '').localeCompare((b.name || ''), undefined, { numeric: true })
                        );

                        const quantiRows = sortedQuantiErrors.map(e => [
                            formatOccurrenceTerm(e.name) || '-',
                            Number(e.peso || 0).toFixed(1),
                            formatPdfCount(e.q1),
                            formatPdfCount(e.q2),
                            formatPdfCount(e.q3),
                            formatPdfCount(e.q4)
                        ]);
                        
                        doc.autoTable({
                            startY: y + 5,
                            head: [['Ocorrência', 'Peso', 'Q1', 'Q2', 'Q3', 'Q4']],
                            body: quantiRows,
                            theme: 'grid',
                            headStyles: { fillColor: [192, 57, 43] },
                            columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' } },
                            foot: [[
                                { content: 'Média de Pontos Perdidos:', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 247, 237], textColor: [154, 52, 18] } },
                                { content: evWithScores.quantitative.meanPoints?.toFixed(4), styles: { fontStyle: 'bold', fillColor: [254, 215, 170], textColor: [154, 52, 18], halign: 'center', lineColor: [251, 146, 60], lineWidth: 0.4 } }
                            ]]
                        });
                        y = doc.lastAutoTable.finalY + 10;
                    } else {
                        if (y + 60 > doc.internal.pageSize.getHeight()) { doc.addPage(); y = 40; }
                        doc.setFontSize(12);
                        doc.text("2. RESULTADOS (METAS E PERCEPÇÃO)", margin, y);
                        const metasRows = (evWithScores.gestaoMetasPercepcao || []).map(item => {
                            const totalItem = ['q1', 'q2', 'q3', 'q4'].reduce((acc, q) => acc + parseFloat(item.scores?.[q] || 0), 0);
                            return [item.name, item.scores.q1 || '-', item.scores.q2 || '-', item.scores.q3 || '-', item.scores.q4 || '-', totalItem.toFixed(1)];
                        });
                        doc.autoTable({
                            startY: y + 5,
                            head: [['Item', 'Q1', 'Q2', 'Q3', 'Q4', 'Total']],
                            body: metasRows,
                            theme: 'grid',
                            foot: [[
                                { content: 'Total Metas + Percepção (0-40):', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold' } },
                                { content: evWithScores.totalGestaoMetasPercepcao?.toFixed(2), styles: { fontStyle: 'bold', fillColor: [255, 237, 213] } }
                            ]]
                        });
                        y = doc.lastAutoTable.finalY + 10;

                        if (y + 40 > doc.internal.pageSize.getHeight()) { doc.addPage(); y = 40; }
                        doc.setFontSize(12);
                        doc.setTextColor(0, 0, 0);
                        doc.text("3. EXTRAS: ATUALIZAÇÃO E RECICLAGEM", margin, y);

                        const updateRows = [
                            ["Na Área (D1) - Min 36h", (evWithScores.atualizacao?.D1_hours || 0) + "h", (evWithScores.atualizacao?.D1_nota || 0).toFixed(2)],
                            ["Outras Áreas (D2) - Min 18h", (evWithScores.atualizacao?.D2_hours || 0) + "h", (evWithScores.atualizacao?.D2_nota || 0).toFixed(2)]
                        ];

                        doc.autoTable({
                            startY: y + 5,
                            head: [['Categoria', 'Horas Registradas', 'Pontos Obtidos']],
                            body: updateRows,
                            theme: 'grid',
                            headStyles: { fillColor: [147, 51, 234] },
                            foot: [[
                                { content: 'Total Pontos Extras (0-20):', colSpan: 2, styles: { halign: 'right', fontStyle: 'bold' } },
                                { content: evWithScores.atualizacao?.total?.toFixed(2), styles: { fontStyle: 'bold', fillColor: [243, 232, 255] } }
                            ]]
                        });
                        y = doc.lastAutoTable.finalY + 10;
                    }
                }

                // =================================================================
                // 4. FEEDBACKS E MENSAGENS FINAIS
                // =================================================================
                
                // PONTOS DE ATENÇÃO (RH)
                if (type.startsWith('Simplificado')) {
                    const avDesFeedbacks = Object.values(ev.feedbacksHistory || {})
                        .filter(f => f.type === 'AvDes' || f.type === 'AvPDF')
                        .sort((a, b) => b.timestamp - a.timestamp);

                    if (avDesFeedbacks.length > 0) {
                        if (y + 40 > doc.internal.pageSize.getHeight()) { doc.addPage(); y = 40; }
                        y += 5;
                        doc.setFontSize(12); 
                        doc.setTextColor(185, 28, 28); 
                        doc.setFont(undefined, 'bold');
                        doc.text("FEEDBACKS", margin, y);
                        y += 4;
                        
                        const feedbackRows = avDesFeedbacks.map(f => [
                            new Date(f.timestamp).toLocaleDateString('pt-BR'),
                            f.quarter ? f.quarter.toUpperCase() : '-',
                            f.text
                        ]);
                        
                        doc.autoTable({
                            startY: y,
                            head: [['Data', 'Ref.', 'Descrição']],
                            body: feedbackRows,
                            theme: 'grid',
                            headStyles: { fillColor: [220, 38, 38], textColor: [255, 255, 255] },
                            columnStyles: { 0: { cellWidth: 25 }, 1: { cellWidth: 20 }, 2: { cellWidth: 'auto' } },
                            styles: { fontSize: 9 }
                        });
                        y = doc.lastAutoTable.finalY + 15;
                    }
                } 
                // HISTÓRICO DE FEEDBACKS - COMPLETO
                else if (type === 'Completo') {
                    if (y + 40 > doc.internal.pageSize.getHeight()) { doc.addPage(); y = 40; }
                    doc.setFontSize(12); 
                    doc.setTextColor(0, 0, 0); 
                    doc.setFont(undefined, 'bold');
                    doc.text(isGestao ? "4. HISTÓRICO COMPLETO DE FEEDBACKS" : "3. HISTÓRICO COMPLETO DE FEEDBACKS", margin, y);

                    const historyList = Object.values(ev.feedbacksHistory || {}).sort((a, b) => b.timestamp - a.timestamp);
                    if (historyList.length > 0) {
                        const feedbackRows = historyList.map(f => [new Date(f.timestamp).toLocaleDateString('pt-BR'), f.type, f.quarter?.toUpperCase() || '-', f.text]);
                        doc.autoTable({ startY: y + 5, head: [['Data', 'Tipo', 'Trim', 'Descrição']], body: feedbackRows, theme: 'grid', headStyles: { fillColor: [243, 156, 18], textColor: [255, 255, 255] }, columnStyles: { 3: { cellWidth: 90 } } });
                        y = doc.lastAutoTable.finalY + 10;
                    } else {
                        doc.setFontSize(10); doc.setFont(undefined, 'normal'); doc.text("Nenhum feedback registrado.", margin, y + 10); y += 20;
                    }

                    // OCORRÊNCIAS - COMPLETO
                    if (y + 40 > doc.internal.pageSize.getHeight()) { doc.addPage(); y = 40; }
                    y += 5;
                    doc.setFontSize(12); 
                    doc.setTextColor(0, 0, 0); 
                    doc.setFont(undefined, 'bold');
                    const sectionNum = isGestao ? "5." : "4.";
                    doc.text(`${sectionNum} RELAÇÃO DE CONFERÊNCIAS (OCORRÊNCIAS)`, margin, y);

                    const occurrences = (data.apontamentos || []).filter(ap => {
                        if (ap.collaboratorId !== ev.collaboratorId) return false;
                        if (!String(ap.date || '').startsWith(String(ev.periodYear))) return false;
                        const qNum = getQuarterNumberFromValue('', ap.date, ap.timestamp);
                        return qNum > 0 && qNum <= maxAllowedQ;
                    }).sort((a, b) => new Date(b.date) - new Date(a.date));

                    if (occurrences.length > 0) {
                        const occRows = occurrences.map(ap => [
                            new Date(ap.date + 'T00:00:00').toLocaleDateString('pt-BR'),
                            ap.weight || ap.peso || 0,
                            ap.type,
                            ap.description
                        ]);
                        doc.autoTable({
                            startY: y + 5,
                            head: [['Data', 'Peso', 'Tipo', 'Descrição']],
                            body: occRows,
                            theme: 'grid',
                            headStyles: { fillColor: [192, 57, 43], textColor: [255, 255, 255], halign: 'center' },
                            columnStyles: { 0: { cellWidth: 25 }, 1: { cellWidth: 15 }, 2: { cellWidth: 50 }, 3: { cellWidth: 'auto' } },
                            styles: { cellPadding: 2 }
                        });
                        y = doc.lastAutoTable.finalY + 10;
                    } else {
                        doc.setFontSize(10); doc.setFont(undefined, 'normal');
                        doc.text("Nenhuma ocorrência registrada neste período.", margin, y + 10);
                        y += 20;
                    }
                }

                // MENSAGEM FINAL (PROMOÇÃO/PREMIAÇÃO)
                const resultLabel = ev.labels?.find(l => l.text === 'Promoção' || l.text === 'Premiação');
                const isConcluded = ev.status === 'Finalizada' || ev.status === 'T4-Concluido' || ev.status === 'Concluída Financeiro';

                if (type.startsWith('Simplificado') && resultLabel && isConcluded) {
                    const msg = data.pdfMessages.find(m => m.type === resultLabel.text && m.personType === ev.personType);
                    if (msg) {
                        const nextYear = (parseInt(ev.periodYear) + 1).toString();
                        const replaceVars = (text) => text ? text.replace(/{AnoAtual}/g, ev.periodYear).replace(/{AnoSeg}/g, nextYear).replace(/{colaborador}/g, ev.collaboratorName).replace(/{gestor}/g, managerNameFallback) : "";
                        const finalTitle = replaceVars(msg.title);
                        const finalBody = replaceVars(msg.body);
                        const rgbBg = hexToRgb(msg.bgColor); const rgbText = hexToRgb(msg.textColor);

                        if (y + 50 > doc.internal.pageSize.getHeight()) { doc.addPage(); y = 40; }
                        if (rgbBg) doc.setFillColor(rgbBg[0], rgbBg[1], rgbBg[2]);
                        doc.roundedRect(margin, y, 182, 40, 5, 5, 'F');
                        if (rgbText) doc.setTextColor(rgbText[0], rgbText[1], rgbText[2]);
                        doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.text(finalTitle, 105, y + 15, { align: 'center' });
                        doc.setFontSize(11); doc.setFont("helvetica", "normal"); doc.text(doc.splitTextToSize(finalBody, 170), 105, y + 25, { align: 'center' });
                        y += 50;
                    }
                }

                // =================================================================
                // 5. FINALIZAÇÃO: QUADRO DE ASSINATURAS E PÁGINA 2
                // =================================================================

                // Verifica espaço para assinaturas. Se não couber, nova página.
                const estimatedSignatureHeight = 60;
                if (y + estimatedSignatureHeight > doc.internal.pageSize.getHeight()) {
                    doc.addPage();
                    y = 20;
                } else {
                    y += 10;
                }

                // 2. Desenha o Quadro de Assinaturas
                y = drawSignatureTable(y);

                // 3. Página 2 (Percepção) - Lógica ajustada para filtrar por Gestor Alvo e Trimestre
                    const targetEmail = collabData ? collabData.email.toLowerCase() : '';

                    const hasPerception = (data.teamPerception || []).some(p => {
                        const pManagerEmail = p.manager_email ? p.manager_email.toLowerCase() : '';
                        const pDate = p.date || ''; // Ex: "25/10/2023"
                        
                        // Verifica se o destinatário é a Joice (targetEmail)
                        const isToThisManager = pManagerEmail === targetEmail;
                        // Verifica se pertence ao ano correto
                        const isCorrectYear = pDate.endsWith(ev.periodYear);
                        
                        // IMPORTANTE: Aqui filtramos para que a página só exista se houver dados do TRIMESTRE atual
                        // A função drawPerceptionSection já filtra os registros, aqui validamos a existência
                        return isToThisManager && isCorrectYear;
                    });

                    // SÓ ADICIONA A PÁGINA SE: 
                    // 1. O avaliado for do tipo 'Gestão'
                    // 2. Existirem percepções cujo manager_email seja o da Joice (exemplo) no ano da avaliação
                    if (ev.personType === 'Gestão' && hasPerception) {
                        // Verificamos novamente dentro de drawPerceptionSection se o filtro de trimestre retornará linhas
                        // Para garantir que não saia uma página com cabeçalho mas sem dados.
                        doc.addPage('a4', 'l'); 
                        drawPerceptionSection(20);
                    }

                // 4. Salvar/Abrir
                const cleanType = type.includes('Simplificado') ? 'Simplificado' : 'Completo';
                const safeFileName = `${ev.collaboratorName}-${realDeptName}-${ev.evalTypeName || 'Padrao'}-${cleanType}-${ev.periodYear}`.replace(/[^a-z0-9\-]/gi, '_');

                doc.setProperties({ title: safeFileName });
                const pdfUrl = doc.output('bloburl');
                window.open(pdfUrl, '_blank');

            } catch (e) {
                console.error("Erro PDF:", e);
                alert("Erro ao gerar PDF: " + e.message);
            }
            setIsGenerating(false);
            setModal(null);
        }, [selectedEval, data, recalculateFinalAverage]);

        const generateBatchReport = async (format) => {
            const targets = processedList.filter(ev => ev.labels.some(l => l.text === reportResultType));
            if (targets.length === 0) return;
            
            if (format === 'excel') {
                const headers = ["Nome", "Departamento", "Gestor", "Resultado", "Nota", "Tipo"];
                const rows = targets.map(ev => { 
                    const manager = data.managers.find(m => data.collaborators.find(c=>c.id===ev.collaboratorId)?.managerIds.includes(m.id)); 
                    const note = ev.personType === 'Gestão' ? ev.finalScoreGestao : ev.qualitative.finalAverage; 
                    return [ev.collaboratorName, ev.department, manager?.name||'N/D', reportResultType, note?.toFixed(4), ev.personType]; 
                });
                const BOM = "\uFEFF"; 
                let csvContent = BOM + headers.join(";") + "\n" + rows.map(r => r.join(";")).join("\n");
                let blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }); 
                const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `Relatorio_${reportResultType}.csv`; 
                document.body.appendChild(a);
                a.click(); 
                document.body.removeChild(a);
            } else {
                await loadPdfLibs();
                const PDFConstructor = (window.jspdf && window.jspdf.jsPDF) ? window.jspdf.jsPDF : window.jsPDF;
                const doc = new PDFConstructor('p', 'mm', 'a4');
                const logoUrl = globalConfigs?.[0]?.logoUrl;
                let startY = 20;
                
                if (logoUrl) {
                    try { 
                        doc.addImage(logoUrl, 'PNG', 14, 10, 25, 25);
                        doc.text(`Relatório: ${reportResultType} - ${filterYear}`, 45, 25); startY = 45; 
                    } catch (e) { 
                        doc.text(`Relatório: ${reportResultType} - ${filterYear}`, 14, 20);
                    }
                } else { 
                    doc.text(`Relatório: ${reportResultType} - ${filterYear}`, 14, 20);
                }
                
                const body = targets.map(ev => [ev.collaboratorName, ev.department, reportResultType, (ev.personType==='Gestão'?ev.finalScoreGestao:ev.qualitative.finalAverage).toFixed(4)]);
                doc.autoTable({ startY: startY, head: [['Nome', 'Depto', 'Resultado', 'Nota']], body, theme: 'grid' }); 
                doc.save(`Relatorio_${reportResultType}.pdf`);
            }
            setReportModalOpen(false);
        };
        const handleConfirmSync = async () => { 
            if (!syncYearInput || syncYearInput.length !== 4) return alert("Ano inválido.");
            setIsUpdatingSheet(true);
            try { 
                const result = await actions.syncTrainingsGlobal(syncYearInput);
                setIsUpdatingSheet(false); 
                setIsSyncModalOpen(false); 
                alert(`Sincronização concluída! ${result.count} registros importados.`); 
            } catch (error) { 
                console.error("Erro:", error);
                alert("Erro na sincronização: " + error.message); 
                setIsUpdatingSheet(false); 
            } 
        };

        const handleCreateEval = async () => {
            if (!newEvalData.collabId || !newEvalData.year) return alert("Selecione colaborador e ano.");
            const c = data.collaborators.find(x => x.id === newEvalData.collabId); 
            if (!c) return; 
            
            // --- LÓGICA DE TRIMESTRES ATIVOS ---
            let activeFromQuarter = 1; 
            
            if (c.startDate) {
                const start = new Date(c.startDate + "T00:00:00");
                const startYear = start.getFullYear();
                const evalYear = parseInt(newEvalData.year);

                if (startYear > evalYear) {
                    return alert("Erro: Data de admissão posterior ao ano da avaliação.");
                }

                if (startYear === evalYear) {
                    const month = start.getMonth() + 1; 
                    if (month <= 3) activeFromQuarter = 1;      
                    else if (month <= 6) activeFromQuarter = 2; 
                    else if (month <= 9) activeFromQuarter = 3; 
                    else activeFromQuarter = 4;                 
                }
            }
            // -----------------------------------

            const personType = c.personType || 'Colaborador';
            const et = data.evaluationTypes.find(t => t.id === c.evalTypeId); 
            const departmentName = data.departments.find(d=>d.id===c.departmentId)?.name ?? '';
            
            // --- CORREÇÃO AQUI: LÓGICA DE CARREGAMENTO DE CRITÉRIOS ---
            // Se o Tipo de Avaliação (et) tem critérios selecionados, obedecemos aos IDs (confiamos na configuração).
            // Caso contrário (padrão), filtramos pelo tipo de pessoa, aceitando também os sem tipo (!q.personType).

            const activeQuali = data.qualiCriteria.filter(q => {
                if (et?.selectedQualiIds?.length > 0) {
                    return et.selectedQualiIds.includes(q.id);
                }
                return (!q.personType || q.personType === personType || q.personType === 'Ambos');
            });

            const activeQuanti = data.quantiCriteria.filter(q => {
                if (et?.selectedQuantiIds?.length > 0) {
                    return et.selectedQuantiIds.includes(q.id);
                }
                return (!q.personType || q.personType === personType || q.personType === 'Ambos');
            });
            // ----------------------------------------------------------
            
            const newEval = { 
                id: Date.now().toString(), 
                status: 'Em Andamento', 
                collaboratorId: c.id, 
                collaboratorName: c.name, 
                periodYear: newEvalData.year, 
                department: departmentName, 
                role: c.role, 
                seniority: c.seniority, 
                level: c.level, 
                evalTypeName: et?.name || 'Padrão', 
                personType: personType,
                activeFromQuarter: activeFromQuarter,
                
                qualitative: { criteria: activeQuali.map(cr => ({...cr, q1:null, q2:null, q3:null, q4:null})), finalAverage: 0, finalScoreGestaoQuali: 0 }, 
                quantitative: { errors: activeQuanti.map(er => ({...er, q1:0, q2:0, q3:0, q4:0})), meanPoints: 0 }, 
                
                feedbacks: { q1:'', q2:'', q3:'', q4:'' }, 
                feedbacksHistory: {}, 
                pdi: { knowledge: '', plan: '' }, 
                atualizacao: { D1_hours: 0, D2_hours: 0, D1_certs: 0, D2_certs: 0, D1_nota: 0, D2_nota: 0, total: 0 }, 
                gestaoMetasPercepcao: personType === 'Gestão' ? data.gestaoMetasPercepcao.map(item => ({...item, scores: item.scores || { q1: null, q2: null, q3: null, q4: null } })) : undefined, 
                totalGestaoMetasPercepcao: 0, totalGestaoMetas: 0, totalGestaoPercepcao: 0, finalScoreGestao: 0, 
            };

            // Importa apontamentos existentes
            newEval.quantitative.errors = getSyncedQuantitativeErrors(newEval, data.apontamentos, data.quantiCriteria, data.collaborators, data.evaluationTypes);

            const evWithScores = recalculateFinalAverage(newEval); 
            
            // --- CORREÇÃO DA NOTA DE GESTÃO NA CRIAÇÃO ---
            if (evWithScores.personType === 'Gestão') {
                const quali = evWithScores.qualitative?.finalScoreGestaoQuali || 0;
                const metas = evWithScores.totalGestaoMetas || 0;
                const percepcao = evWithScores.totalGestaoPercepcao || 0;
                const extras = evWithScores.atualizacao?.total || 0;
                evWithScores.finalScoreGestao = quali + metas + percepcao + extras;
            }

            await actions.saveEvaluation(evWithScores); 
            
            actions.logAction('CRIACAO_AVALIACAO', {
                colaborador: c.name, 
                ano: newEvalData.year,
                tipo: personType,
                inicioTrimestre: activeFromQuarter
            });

            setIsNewEvalModalOpen(false); 
            setShowEndedEvaluationCollaborators(false);
            onEdit(evWithScores); 
        };

        const colorClasses = { 
            blue: { bg: 'bg-blue-600', border: 'border-blue-500' }, 
            orange: { bg: 'bg-orange-600', border: 'border-orange-500' }, 
            green: { bg: 'bg-green-600', border: 'border-green-500' }, 
            teal: { bg: 'bg-teal-600', border: 'border-teal-500' }, 
            red: { bg: 'bg-red-600', border: 'border-red-500' } 
        };
        // ========================================================================
        // 8. RENDERIZAÇÃO
        // ========================================================================
        const isUserDir = currentUser && departments.find(d => d.id === currentUser.departmentId)?.name.toUpperCase().includes('DIR');
        const showDeptOnCard = isUserDir || !filterDept || filterDept === 'Todos';

        return (
            <div className="flex flex-col h-[calc(100vh-140px)] gap-3 animate-fade-in relative">
                
                {/* Tooltip (MANTIDO IGUAL) */}
                {tooltip && (
                    <div 
                        className="fixed float-popup shadow-2xl border border-gray-200 bg-white rounded-lg pointer-events-none z-[9999]" 
                        style={{ 
                            top: tooltip.y, 
                            left: tooltip.x, 
                            transform: tooltip.placement === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)', 
                            marginTop: tooltip.placement === 'top' ? '-8px' : '8px'
                        }}
                    >
                        {/* ... (Conteúdo do Tooltip mantido igual ao original) ... */}
                        {tooltip.type === 'trend' && (
                            <div className="p-2 text-gray-700 min-w-[150px]">
                                <div className="font-bold mb-2 border-b pb-1 text-blue-600">{tooltip.data.title}</div>
                                {tooltip.data.trend.history.map((h, idx) => (
                                    <div key={idx} className="flex justify-between mb-1 text-xs">
                                        <span>{h.quarter}:</span><span className="font-bold">{h.val.toFixed(4)}</span>
                                    </div>
                                ))}
                                <div className="mt-2 pt-1 border-t text-[10px] text-gray-400">Status: {tooltip.data.trend.label}</div>
                            </div>
                        )}
                        {tooltip.type === 'status' && (
                            <div className="p-2 text-gray-700 w-48">
                                <div className="font-bold text-center border-b pb-1 mb-1 text-xs uppercase tracking-wider text-blue-600">{tooltip.data.type}</div>
                                <div className="flex justify-between text-xs mb-1"><span>Sua Nota:</span> <span className="font-bold">{tooltip.data.score.toFixed(4)}</span></div>
                                <div className="flex justify-between text-xs text-gray-500 mb-2"><span>Necessário:</span> <span>{tooltip.data.needed.toFixed(2)}</span></div>
                                <div className="border-t mb-2"></div>
                                <div className="flex justify-between text-xs mb-1"><span>Pts Perdidos:</span> <span className="font-bold text-red-600">{tooltip.data.lost.toFixed(4)}</span></div>
                                <div className="flex justify-between text-xs text-gray-500"><span>Máx Permitido:</span> <span>{tooltip.data.maxLost.toFixed(1)}</span></div>
                            </div>
                        )}
                        {tooltip.type === 'statusLegend' && (
                        <div className={`p-3 w-64 text-xs ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                                <div className="font-bold border-b pb-2 mb-2 text-blue-500 flex items-center gap-2">
                                    <CircleHelp size={14}/> Legenda de Status
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2"><div className={`flex items-center justify-center w-6 h-6 rounded border font-bold ${isDarkMode ? 'bg-slate-800 border-slate-600 text-blue-400' : 'bg-blue-50 border-blue-200 text-blue-600'}`}><Lock size={12}/></div><span>Trimestre Concluído</span></div>
                                    <div className="flex items-center gap-2"><div className={`flex items-center justify-center w-6 h-6 rounded border font-bold ${isDarkMode ? 'bg-slate-800 border-slate-600 text-green-400' : 'bg-green-50 border-green-200 text-green-600'}`}><Unlock size={12}/></div><span>Aberto / No Prazo</span></div>
                                    <div className="flex items-center gap-2"><div className={`flex items-center justify-center w-6 h-6 rounded border font-bold ${isDarkMode ? 'bg-slate-800 border-slate-600 text-red-400' : 'bg-red-50 border-red-200 text-red-600'}`}>T1</div><span>Pendente / Atrasado</span></div>
                                    <div className="flex items-center gap-2"><div className={`flex items-center justify-center w-6 h-6 rounded border font-bold ${isDarkMode ? 'bg-slate-800 border-slate-600 text-slate-400' : 'bg-slate-100 border-slate-300 text-slate-500'}`}><Unlock size={12}/></div><span>Não Iniciado</span></div>
                                </div>
                            </div>
                        )}
                    {tooltip.type === 'details' && (
                            <div className="w-72 bg-white rounded-lg shadow-2xl border border-slate-200 flex flex-col">
                                <div className="font-bold border-b border-gray-100 p-3 bg-gray-50 rounded-t-lg text-sm text-blue-600 truncate flex justify-between items-center">
                                    <span>
                                        {tooltip.data.type === 'total' ? "Colaboradores Ativos" : 
                                        tooltip.data.type === 'pending' ? "Pendentes (Não Iniciadas)" : 
                                        tooltip.data.type === 'inProgress' ? "Em Andamento" : 
                                        tooltip.data.type === 'late' ? "Fora do Prazo (Atrasadas)" : 
                                        tooltip.data.type === 'finished' ? "Concluídas" : "Detalhes"}
                                    </span>
                                </div>
                                <div className="overflow-y-auto custom-scrollbar p-2" style={{ maxHeight: '250px' }}>
                                    <ul className="text-xs space-y-1">
                                        {(() => {
                                            // Lógica consolidada: Agrupa trimestres por pessoa
                                            const consolidated = {};
                                            const quartersMap = { q1: 'T1', q2: 'T2', q3: 'T3', q4: 'T4' };
                                            // Cores fieis à imagem enviada
                                            const colorsMap = { 
                                                q1: 'text-blue-600', 
                                                q2: 'text-amber-500', 
                                                q3: 'text-red-500', 
                                                q4: 'text-emerald-500' 
                                            };
                                            
                                            ['q1', 'q2', 'q3', 'q4'].forEach(q => {
                                                (tooltip.data[q] || []).forEach(item => {
                                                    if (!consolidated[item.name]) consolidated[item.name] = { name: item.name, badges: [] };
                                                    consolidated[item.name].badges.push({ label: quartersMap[q], color: colorsMap[q] });
                                                });
                                            });
                                            
                                            const list = Object.values(consolidated).sort((a, b) => a.name.localeCompare(b.name));
                                            
                                            if (list.length === 0) return <li className="text-center text-gray-400 p-2">Nenhum registro.</li>;

                                            return list.map((item, i) => (
                                                <li key={i} className="flex justify-between items-center text-gray-700 border-b border-gray-50 pb-1 mb-1 last:border-0 last:pb-0 last:mb-0">
                                                    <span className="font-bold truncate w-2/3" title={item.name}>{item.name}</span>
                                                    <span className="flex gap-1 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">
                                                        {item.badges.map((b, bi) => (
                                                            <span key={bi} className={`font-mono text-[10px] font-bold ${b.color}`}>{b.label}</span>
                                                        ))}
                                                    </span>
                                                </li>
                                            ));
                                        })()}
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                )}

            {/* --- CARDS DO TOPO (GRID 5 COLUNAS) --- */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 shrink-0">
                    {/* 4 Cards de Métricas Originais */}
                    {[ 
                        { label: 'Pendentes', icon: AlertCircle, color: 'orange', typeKey: 'pending', data: metrics.pending }, 
                        { label: 'Em Andamento', icon: Clock, color: 'teal', typeKey: 'inProgress', data: metrics.inProgress }, 
                        { label: 'Atrasadas', icon: AlertTriangle, color: 'red', typeKey: 'late', data: metrics.late }, 
                        { label: 'Concluídas', icon: CheckCircle, color: 'blue', typeKey: 'finished', data: metrics.finished } 
                    ].map((card, i) => (
                        <div 
                            key={i} 
                            className={`${colorClasses[card.color]?.bg || 'bg-gray-600'} text-white rounded-xl p-3 shadow-md relative group cursor-pointer transition-transform hover:scale-[1.02] flex flex-col justify-start gap-3 h-auto z-0`} 
                            onMouseEnter={(e) => onHoverCardDetails(e, card.typeKey, card.data.details)} 
                            onMouseLeave={() => setTooltip(null)}
                        >
                            <div className="absolute top-2 right-2 p-1.5 rounded-full bg-white/20 hover:bg-white/40 transition-all cursor-help z-10 shadow-sm" title="Ver detalhes"><Info size={16} className="text-white"/></div>
                            <div className="flex items-center gap-2 mb-0 pr-6"><card.icon size={18} /><h3 className="font-bold text-xs">{card.label}</h3></div>
                            <div className="space-y-0 text-[10px]">
                                {['q1','q2','q3','q4'].map((q, idx) => (
                                    <div key={q} className={`flex justify-between border-b ${colorClasses[card.color]?.border || 'border-white/20'} py-0.5 last:border-0`}>
                                        <span className="opacity-90">{idx+1}º Trim/{filterYear}</span>
                                        <span className="font-bold text-xs">{card.data[q]}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    {/* --- NOVO CARD 5: LIDERANÇA / DESTAQUES (ATUALIZADO) --- */}
                    <div className="rounded-xl p-3 shadow-md relative group flex flex-col h-auto z-0 bg-gradient-to-br from-yellow-500 via-amber-500 to-amber-600 text-white">
                        <div className="flex items-center gap-2 mb-2 pr-6 border-b border-white/20 pb-2 shrink-0">
                            <Trophy size={18} className="text-white drop-shadow-sm"/>
                            <h3 className="font-bold text-xs">
                                {talentPoolData.mode === 'grouped' ? 'Destaques (Por Depto)' : 'Liderança (Top 4)'}
                            </h3>
                        </div>
                        
                        <div className={`flex-1 flex flex-col gap-1 ${talentPoolData.mode === 'grouped' ? 'overflow-y-auto custom-scrollbar pr-1 max-h-[120px]' : 'overflow-hidden'}`}>
                            {/* MODO AGRUPADO (COM SCROLL) */}
                            {talentPoolData.mode === 'grouped' ? (
                                Object.keys(talentPoolData.data).length > 0 ? (
                                    Object.entries(talentPoolData.data).map(([deptName, items]) => (
                                        <div key={deptName} className="mb-2 last:mb-0">
                                            <div className="text-[9px] font-bold uppercase bg-black/20 px-1.5 py-0.5 rounded text-white/90 mb-1 w-fit backdrop-blur-sm sticky top-0 z-10">
                                                {deptName}
                                            </div>
                                            {items.map((talent) => {
                                                const score = talent.personType === 'Gestão' ? talent.finalScoreGestao : talent.qualitative.finalAverage;
                                                return (
                                                    <div key={talent.id} onClick={() => onEdit(talent)} className="flex justify-between items-center py-1 pl-1 pr-1 border-b border-white/10 last:border-0 cursor-pointer hover:bg-white/20 rounded transition-colors group/item">
                                                        <div className="truncate flex items-baseline gap-1.5 pr-2 w-full">
                                                            {/* REQUISITO 1: Nome com destaque Dourado/Branco forte e sombra */}
                                                            <span className="text-[11px] font-black text-white drop-shadow-md truncate" title={talent.collaboratorName}>
                                                                {talent.collaboratorName}
                                                            </span>
                                                            {/* REQUISITO 2: Depto na frente entre parênteses */}
                                                            <span className="text-[9px] text-white/80 font-medium truncate">
                                                                ({talent.department})
                                                            </span>
                                                        </div>
                                                        {/* AQUI: 4 CASAS NO MODO AGRUPADO */}
                                                        <div className="text-[10px] font-black bg-white text-amber-600 px-1.5 rounded shadow-sm shrink-0">
                                                            {score.toFixed(4)}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-[10px] opacity-70 text-center py-4 italic">Nenhum destaque encontrado.</div>
                                )
                            ) : (
                                // MODO PADRÃO (TOP 4)
                                talentPoolData.data.length > 0 ? (
                                    talentPoolData.data.map((talent, idx) => {
                                        const score = talent.personType === 'Gestão' ? talent.finalScoreGestao : talent.qualitative.finalAverage;
                                        return (
                                            <div key={talent.id} onClick={() => onEdit(talent)} className="flex justify-between items-center py-1 border-b border-white/20 last:border-0 cursor-pointer hover:bg-white/20 rounded px-1 transition-colors group/item" title="Clique para abrir avaliação">
                                                <div className="flex items-center gap-2 truncate pr-2 w-full">
                                                    <div className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center text-[9px] font-bold shrink-0">
                                                        {idx + 1}
                                                    </div>
                                                    <div className="truncate flex items-baseline gap-1.5">
                                                        {/* REQUISITO 1: Nome Destaque */}
                                                        <span className="text-[11px] font-black text-white drop-shadow-md truncate">{talent.collaboratorName}</span>
                                                        {/* REQUISITO 2: Depto Frente */}
                                                        <span className="text-[9px] text-white/80 font-medium truncate">({talent.department})</span>
                                                    </div>
                                                </div>
                                                {/* AQUI: 4 CASAS NO MODO PADRÃO (Provavelmente onde estava faltando) */}
                                                <div className="text-[10px] font-black bg-white/20 px-1.5 rounded text-white shadow-sm shrink-0">
                                                    {score.toFixed(4)}
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="text-[10px] opacity-70 text-center py-4 italic">Nenhum destaque encontrado.</div>
                                )
                            )}
                        </div>
                    </div>
                </div>            

                {/* Barra de Filtros (TAMANHOS AJUSTADOS) */}
                <Card className={`py-2 px-3 shadow-sm min-h-[50px] shrink-0 overflow-x-auto ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`} darkMode={isDarkMode}>
                    <div className="flex items-center gap-3 w-full">
                        
                        {/* Ano */}
                        <div className={`flex items-center gap-2 border rounded-lg px-2 h-8 w-28 shrink-0 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200'}`}>
                            <label className={`text-[10px] font-bold whitespace-nowrap ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Ano:</label>
                            <Input type="number" value={filterYear} onChange={e=>setFilterYear(e.target.value)} className={`bg-transparent border-0 h-full text-xs py-0 w-full text-center focus:ring-0 px-0 ${isDarkMode ? 'text-white' : ''}`}/>
                        </div>
                        
                        {/* Departamento (REDUZIDO de w-48 para w-40) */}
                        <div className={`flex items-center gap-2 border rounded-lg px-2 h-8 w-40 shrink-0 ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                            <label className={`text-[10px] font-bold whitespace-nowrap ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Depto:</label>
                            <select className={`bg-transparent border-0 h-full text-xs w-full focus:ring-0 px-0 outline-none cursor-pointer ${isDarkMode ? 'text-white bg-slate-700' : ''}`} value={filterDept} onChange={e=>setFilterDept(e.target.value)}>
                                <option value="">Todos</option>
                                {departments.slice().sort((a, b) => a.name.localeCompare(b.name)).map(d=><option key={d.id} value={d.name}>{d.name}</option>)}
                            </select>
                        </div>                    

                        {/* Tipo Avaliação */}
                        <div className={`flex items-center gap-2 border rounded-lg px-2 h-8 w-44 shrink-0 ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                            <label className={`text-[10px] font-bold whitespace-nowrap ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Tipo:</label>
                            <select className={`bg-transparent border-0 h-full text-xs w-full focus:ring-0 px-0 outline-none cursor-pointer ${isDarkMode ? 'text-white' : ''}`} value={filterPersonType} onChange={e=>setFilterPersonType(e.target.value)}>
                                <option value="Todos" className={isDarkMode ? 'bg-slate-700' : ''}>Todos</option>
                                <option value="Colaborador" className={isDarkMode ? 'bg-slate-700' : ''}>Colaborador</option>
                                <option value="Gestão" className={isDarkMode ? 'bg-slate-700' : ''}>Gestão</option>
                            </select>
                        </div>

                        {/* Status (REDUZIDO de w-44 para w-36) */}
                        <div className={`flex items-center gap-2 border rounded-lg px-2 h-8 w-36 shrink-0 ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                            <label className={`text-[10px] font-bold whitespace-nowrap ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Status:</label>
                            <select className={`bg-transparent border-0 h-full text-xs w-full focus:ring-0 px-0 outline-none cursor-pointer ${isDarkMode ? 'text-white' : ''}`} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
                                <option value="" className={isDarkMode ? 'bg-slate-700' : ''}>Todos</option>
                                <option value="Em Andamento" className={isDarkMode ? 'bg-slate-700' : ''}>Andamento</option>
                                <option value="Finalizada" className={isDarkMode ? 'bg-slate-700' : ''}>Finalizada</option>
                            </select>
                        </div>

                        {/* Resultado */}
                        <div className={`flex items-center gap-2 border rounded-lg px-2 h-8 w-44 shrink-0 ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                            <label className={`text-[10px] font-bold whitespace-nowrap ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Resultado:</label>
                            <select className={`bg-transparent border-0 h-full text-xs w-full focus:ring-0 px-0 outline-none cursor-pointer ${isDarkMode ? 'text-white' : ''}`} value={filterResult} onChange={e=>setFilterResult(e.target.value)}>
                                <option value="Todos" className={isDarkMode ? 'bg-slate-700' : ''}>Todos</option>
                                <option value="Promoção" className={isDarkMode ? 'bg-slate-700' : ''}>Promoção</option>
                                <option value="Premiação" className={isDarkMode ? 'bg-slate-700' : ''}>Premiação</option>
                            </select>
                        </div>

                        {/* Pesquisa (REDUZIDO em 20%) */}
                        <div className="relative flex-1 min-w-[160px] max-w-[240px]">
                            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} size={14} />
                            <input 
                                type="text" 
                                placeholder="Buscar..." 
                                value={globalSearch} 
                                onChange={e => setGlobalSearch(e.target.value)}
                                className={`w-full h-8 pl-9 pr-3 rounded-lg border text-xs outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-gray-50 border-gray-200 text-gray-700 placeholder-gray-400'}`}
                            />
                        </div>

                        {/* Botões de Ação */}
                        <Button onClick={() => setIsNewEvalModalOpen(true)} className={`text-xs px-3 py-1.5 h-8 shrink-0 whitespace-nowrap shadow-sm ${isDarkMode ? 'bg-blue-600 hover:bg-blue-500 border border-blue-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}><Plus size={14}/> Nova</Button>
                        <button onClick={() => { setSyncYearInput(new Date().getFullYear().toString()); setIsSyncModalOpen(true); }} className={`text-xs px-3 py-1.5 h-8 rounded font-bold flex items-center gap-2 shadow-sm whitespace-nowrap transition-colors shrink-0 ${isDarkMode ? 'bg-green-600 hover:bg-green-500 border border-green-500 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}`}><RefreshCw size={14}/> Capacitações</button>
                        <Button onClick={() => setReportModalOpen(true)} className={`text-xs px-3 py-1.5 h-8 shrink-0 whitespace-nowrap shadow-sm ${isDarkMode ? 'bg-indigo-600 hover:bg-indigo-500 border border-indigo-500 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}><FileSpreadsheet size={14}/> Relatório</Button>
                        
                    </div>
                </Card>
                
                {/* Tabela Principal (MANTIDA IGUAL) */}
                <Card className="flex-1 min-h-0 p-0 overflow-hidden flex flex-col border-0 shadow-md" darkMode={isDarkMode}>
                    <div className="overflow-auto relative h-full">
                        <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : ''}`}>
                            <thead className="sticky top-0 z-20 shadow-sm">
                                <tr className={`${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-100 text-gray-500 border-gray-200'} uppercase text-xs font-bold`}>
                                    <th className={`px-2 py-3 border-b text-center w-10 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-100 border-gray-200'}`}></th>
                                    <th className={`px-2 py-3 border-b ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-100 border-gray-200'}`}>Colaborador</th>
                                    <th className="px-2 py-3 bg-gray-100 border-b border-gray-200">Depto</th>
                                    <th className="px-2 py-3 bg-gray-100 border-b border-gray-200">Gestor</th>
                                    <th className="px-2 py-3 bg-gray-100 border-b border-gray-200">Tipo Av.</th>
                                    <th className="px-2 py-3 bg-gray-100 border-b border-gray-200">Ano</th>
                                    <th 
                                        className="px-2 py-3 bg-gray-100 border-b border-gray-200 text-center cursor-help group"
                                        onMouseEnter={onHoverStatusLegend}
                                        onMouseLeave={() => setTooltip(null)}
                                    >
                                        <div className="flex items-center justify-center gap-1">Status <CircleHelp size={14} className="opacity-50 group-hover:opacity-100 transition-opacity"/></div>
                                    </th>
                                    <th className="px-2 py-3 bg-gray-100 border-b border-gray-200 text-center">Prazo Conclusão</th>
                                    <th className="px-2 py-3 bg-gray-100 border-b border-gray-200 text-center">{filterPersonType === 'Gestão' ? 'Nota Final' : 'Qualitativa'}</th>
                                    {filterPersonType !== 'Todos' && filterPersonType === 'Colaborador' && (<th className="px-2 py-3 bg-gray-100 border-b border-gray-200 text-center">Quantitativa </th>)}
                                    <th className="px-2 py-3 bg-gray-100 border-b border-gray-200 text-center">Resultado</th>
                                    <th className="px-2 py-3 bg-gray-100 border-b border-gray-200 text-center">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {processedList.map((ev, i) => {
                                    const statusDisplay = ev.statusDisplay;
                                    const isFinalized = ev.status === 'Finalizada' || ev.status === 'T4-Concluido' || ev.status === 'Assinada pelo Gestor';                                
                                    const EditIcon = isFinalized ? Eye : Pencil;
                                    const editTitle = isFinalized ? "Visualizar (Fechado)" : "Editar Avaliação";
                                    //const editTitle = isFinalized ? "Visualizar" : "Editar";
                                    const isGestao = ev.personType === 'Gestão';
                                    const scoreText = isGestao ? ev.finalScoreGestao.toFixed(4) : ev.qualitative.finalAverage.toFixed(4);
                                    const scoreColor = isGestao ? 'text-green-600' : 'text-blue-600';
                                    const quantiDisplay = isGestao ? (ev.atualizacao?.total || 0).toFixed(4) : ev.quantitative.meanPoints.toFixed(4);
                                    const hasPromo = ev.labels?.some(l => l.text === 'Promoção');
                                    const hasPremio = ev.labels?.some(l => l.text === 'Premiação');
                                    let AwardIcon = null;
                                    if (hasPromo) AwardIcon = <Trophy size={20} className="text-yellow-500 fill-yellow-500 drop-shadow-sm" />;
                                    else if (hasPremio) AwardIcon = <Medal size={20} className="text-slate-400 fill-slate-300 drop-shadow-sm" />;
                                    
                                    const rowClass = i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');
                                    const deadlineRule = ev.deadlineRule;
                                    let currentDeadlineDate = '-';
                                    if (deadlineRule && !isFinalized) {
                                        let targetQ = 'q1';
                                        if (ev.status === 'T3-Concluido') { targetQ = 'q4'; } 
                                        else if (ev.status === 'T2-Concluido') { targetQ = 'q3'; } 
                                        else if (ev.status === 'T1-Concluido') { targetQ = 'q2'; } 
                                        else {
                                            const criteria = ev.qualitative?.criteria || [];
                                            const hasDataQ4 = criteria.some(c => c.q4 !== null && c.q4 !== '');
                                            const hasDataQ3 = criteria.some(c => c.q3 !== null && c.q3 !== '');
                                            const hasDataQ2 = criteria.some(c => c.q2 !== null && c.q2 !== '');
                                            const hasDataQ1 = criteria.some(c => c.q1 !== null && c.q1 !== '');
                                            if (hasDataQ4) { targetQ = 'q4'; } 
                                            else if (hasDataQ3) { targetQ = 'q4'; }
                                            else if (hasDataQ2) { targetQ = 'q3'; } 
                                            else if (hasDataQ1) { targetQ = 'q2'; } 
                                            else { targetQ = 'q1'; }
                                        }
                                        currentDeadlineDate = deadlineRule[targetQ] || '-';
                                    }

                                    return (
                                        <tr key={ev.id} className={`${rowClass} ${isDarkMode ? 'hover:bg-slate-700 border-b border-slate-700' : 'hover:bg-blue-50 border-b border-gray-100'} transition-colors`}>
                                            <td className="px-2 py-4 text-center align-middle">{AwardIcon && <div className="flex justify-center" title={hasPromo ? "Promoção (Ouro)" : "Premiação (Prata)"}>{AwardIcon}</div>}</td>
                                            {/* CÉLULA DO NOME NO DASHBOARD - COM ALERTAS DE PENDÊNCIA E OBS */}
                                            <td className={`px-4 py-4 font-bold flex items-center gap-2 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                                
                                                {/* 1. ALERTA PENDÊNCIAS (VERMELHO) */}
                                                {(() => {
                                                    // Filtra pendências não concluídas
                                                    const openPendencies = Object.values(ev.personalNotes || {}).filter(n => (n.tipo === 'Pendênca' || n.tipo === 'Pendência') && n.status !== 'Concluída');
                                                    
                                                    if (openPendencies.length > 0) {
                                                        return (
                                                            <div className="relative group/dashpnd">
                                                                <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center text-white animate-pulse cursor-help border border-white shadow-md">
                                                                    <AlertCircle size={14} strokeWidth={3} />
                                                                </div>
                                                                
                                                                {/* Popup Vermelho (Cores Ajustadas) */}
                                                                <div className={`absolute left-0 bottom-full mb-2 w-80 p-3 rounded-lg shadow-2xl opacity-0 invisible group-hover/dashpnd:opacity-100 group-hover/dashpnd:visible transition-all z-[100] border border-red-500 text-[10px] 
                                                                    ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`}>
                                                                    
                                                                    <p className="font-bold uppercase text-red-500 mb-2 border-b border-red-200 pb-1 flex justify-between items-center">
                                                                        <span>Pendências ({openPendencies.length})</span>
                                                                        <span className="text-[9px] opacity-70 normal-case">Clique no check para resolver</span>
                                                                    </p>
                                                                    
                                                                    <ul className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                                                                        {openPendencies.map(p => (
                                                                            <li key={p.id} className={`flex gap-2 items-start border-b pb-2 last:border-0 last:pb-0 ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                                                                
                                                                                {/* Botão de Concluir Rápido */}
                                                                                <button 
                                                                                    onClick={async (e) => {
                                                                                        e.stopPropagation();
                                                                                        if(window.confirm("Concluir esta pendência agora?")) {
                                                                                            const updatedNotes = { ...ev.personalNotes, [p.id]: { ...p, status: 'Concluída' } };
                                                                                            await actions.saveEvaluation({ ...ev, personalNotes: updatedNotes });
                                                                                        }
                                                                                    }}
                                                                                    className="mt-0.5 text-gray-400 hover:text-green-600 bg-gray-100 dark:bg-slate-700 p-1 rounded transition-colors flex-shrink-0"
                                                                                    title="Marcar como Resolvido"
                                                                                >
                                                                                    <CheckCircle size={14} />
                                                                                </button>

                                                                                <div className="flex-1">
                                                                                    <span className="font-mono font-bold text-red-500 whitespace-nowrap block mb-0.5">
                                                                                        [{new Date(p.date + 'T00:00:00').toLocaleDateString('pt-BR').substring(0, 5)}]
                                                                                    </span>
                                                                                    <span className="opacity-90 leading-tight block">{p.text}</span>
                                                                                </div>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                    {/* Seta do Popup */}
                                                                    <div className={`absolute -bottom-1.5 left-2 w-3 h-3 rotate-45 border-b border-r border-red-500 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}></div>
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                })()}

                                                {/* 2. ALERTA OBSERVAÇÕES (AMARELO) */}
                                                {(() => {
                                                    // Filtra OBS não concluídas
                                                    const openObs = Object.values(ev.personalNotes || {}).filter(n => n.tipo === 'OBS' && n.status !== 'Concluída');
                                                    
                                                    if (openObs.length > 0) {
                                                        return (
                                                            <div className="relative group/dashobs">
                                                                <div className="w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center text-white cursor-help border border-white shadow-md">
                                                                    <span className="font-black text-[10px]">!</span>
                                                                </div>
                                                                
                                                                {/* Popup Amarelo (Cores Ajustadas) */}
                                                                <div className={`absolute left-0 bottom-full mb-2 w-80 p-3 rounded-lg shadow-2xl opacity-0 invisible group-hover/dashobs:opacity-100 group-hover/dashobs:visible transition-all z-[100] border border-yellow-500 text-[10px]
                                                                    ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`}>
                                                                    
                                                                    <p className="font-bold uppercase text-yellow-500 mb-2 border-b border-yellow-200 pb-1 flex justify-between items-center">
                                                                        <span>Observações ({openObs.length})</span>
                                                                        <span className="text-[9px] opacity-70 normal-case">Clique no check para resolver</span>
                                                                    </p>
                                                                    
                                                                    <ul className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                                                                        {openObs.map(p => (
                                                                            <li key={p.id} className={`flex gap-2 items-start border-b pb-2 last:border-0 last:pb-0 ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                                                                
                                                                                {/* Botão de Concluir Rápido */}
                                                                                <button 
                                                                                    onClick={async (e) => {
                                                                                        e.stopPropagation();
                                                                                        if(window.confirm("Arquivar esta observação?")) {
                                                                                            const updatedNotes = { ...ev.personalNotes, [p.id]: { ...p, status: 'Concluída' } };
                                                                                            await actions.saveEvaluation({ ...ev, personalNotes: updatedNotes });
                                                                                        }
                                                                                    }}
                                                                                    className="mt-0.5 text-gray-400 hover:text-green-600 bg-gray-100 dark:bg-slate-700 p-1 rounded transition-colors flex-shrink-0"
                                                                                    title="Arquivar Observação"
                                                                                >
                                                                                    <CheckCircle size={14} />
                                                                                </button>

                                                                                <div className="flex-1">
                                                                                    <span className="font-mono font-bold text-yellow-500 whitespace-nowrap block mb-0.5">
                                                                                        [{new Date(p.date + 'T00:00:00').toLocaleDateString('pt-BR').substring(0, 5)}]
                                                                                    </span>
                                                                                    <span className="opacity-90 leading-tight block">{p.text}</span>
                                                                                </div>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                    {/* Seta do Popup */}
                                                                    <div className={`absolute -bottom-1.5 left-2 w-3 h-3 rotate-45 border-b border-r border-yellow-500 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}></div>
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                })()}

                                                {/* NOME DO COLABORADOR */}
                                                <span>{ev.collaboratorName}</span>

                                                {/* ÍCONE DE ERRO DE DADOS FALTANTES (TRIANGULO LARANJA) */}
                                                {ev.pendingIssues.length > 0 && (
                                                    <div className="tooltip-trigger ml-1">
                                                        <AlertTriangle size={14} className="text-orange-500 cursor-help"/>
                                                        <div className="tooltip-bubble">Notas em falta: {ev.pendingIssues.join(', ')}</div>
                                                    </div>
                                                )}
                                            </td>
                                            <td className={`px-4 py-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{ev.department}</td>
                                            <td className={`px-4 py-4 text-left align-middle text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                                                {ev.linkedManagers && ev.linkedManagers.length > 0 ? (
                                                    <div className="flex flex-col gap-1">
                                                        {ev.linkedManagers.map(m => (
                                                            <span key={m.id} className="whitespace-nowrap font-medium block">{m.name}</span>
                                                        ))}
                                                    </div>
                                                ) : '-'}
                                            </td>
                                            <td className={`px-4 py-4 text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>{ev.evalTypeName || 'Padrão'}</td>
                                            <td className="px-4 py-4 text-gray-600">{ev.periodYear}</td>
                                            <td className="px-4 py-4 text-center align-middle"><StatusStackCell evaluation={ev} deadlines={deadlines} collaborators={collaborators} /></td>
                                            <td className={`px-4 py-4 text-center text-xs font-mono font-bold ${isDarkMode ? 'text-orange-300' : 'text-orange-600'}`}>{currentDeadlineDate}</td>
                                            <td className="px-4 py-4 text-center align-middle-cell pulsed font-bold flex items-center justify-center gap-1"><div className={`flex items-center justify-center ${scoreColor}`}>{scoreText}<TrendIconTrigger trend={ev.trend} title={isGestao ? "Evolução Nota Final" : "Evolução Qualitativa"} onHover={onHoverTrend} onLeave={() => setTooltip(null)} /></div></td>
                                            {filterPersonType !== 'Todos' && filterPersonType === 'Colaborador' && (<td className="px-4 py-4 text-center align-middle-cell font-bold text-red-600"><div className="flex items-center justify-center">{quantiDisplay}{!isGestao && <TrendIconTrigger trend={ev.quantiTrend} title="Evolução Ocorrências" onHover={onHoverTrend} onLeave={() => setTooltip(null)} />}</div></td>)}
                                            <td className="px-4 py-4 text-center"><div className="flex flex-col gap-1 items-center">{ev.labels.map((l,i)=><span key={i} onMouseEnter={(e)=>onHoverStatus(e,l)} onMouseLeave={()=>setTooltip(null)} className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider ${l.color} cursor-help`}>{l.text}</span>)}</div></td>
                                            <td className="px-4 py-4 text-center flex justify-center gap-2 text-gray-400">
                                                <button onClick={()=>{setSelectedEval(ev); setModal('status')}} className="text-green-500 hover:text-green-700" title="Status"><CheckCircle size={18}/></button>
                                            
                                        {/* BOTÃO INTELIGENTE: ASSINAR OU DESFAZER (CORRIGIDO: TRAVA DE STATUS) */}
                                        {(() => {
                                            // 1. Mapeia o Nível do Status Atual (O teto do que PODE ser assinado)
                                            const statusMap = { 
                                                'Em Andamento': 0, 'T1-Concluido': 1, 'T2-Concluido': 2, 
                                                'T3-Concluido': 3, 'T4-Concluido': 4, 'Finalizada': 4, 
                                                'Assinada pelo Gestor': 4, 'Concluída Financeiro': 4
                                            };
                                            const currentStatusLevel = statusMap[ev.status] || 0;

                                            // 2. Verifica assinaturas DO GESTOR ATUAL
                                            const signatures = Array.isArray(ev.managerSignatures) ? ev.managerSignatures : [];
                                            const mySignatures = signatures.filter(s => s.gestorEmail === currentUser.email);
                                            
                                            // 3. Calcula Pendências: Apenas trimestres CONCLUÍDOS (pelo status) que ainda não assinei
                                            const pendingQuarters = [];
                                            // O loop vai de 1 até o nível do status atual. 
                                            // Se status for "Em Andamento" (0), o loop não roda, impedindo assinatura.
                                            for (let q = 1; q <= currentStatusLevel; q++) {
                                                const isSigned = mySignatures.some(s => s.statusRef.includes(`T${q}`) || s.statusRef.includes('Finalizada'));
                                                if (!isSigned) pendingQuarters.push(q);
                                            }

                                            const hasPending = pendingQuarters.length > 0;
                                            const hasHistory = mySignatures.length > 0;

                                            return (
                                                <button 
                                                    onClick={() => {
                                                        setSelectedEval(ev);
                                                        ev.pendingToSign = pendingQuarters; 
                                                        ev.mySignaturesCount = mySignatures.length; 

                                                        if (hasPending) {
                                                            // FLUXO DE ASSINATURA (CRIAR)
                                                            if (pendingQuarters.length > 1) {
                                                                setModal('signature_options'); 
                                                            } else {
                                                                setModal('sign_confirm'); 
                                                            }
                                                        } else if (hasHistory) {
                                                            // FLUXO DE DESFAZER (REMOVER)
                                                            if (mySignatures.length > 1) {
                                                                setModal('undo_options'); 
                                                            } else {
                                                                selectedEval.undoMode = 'single';
                                                                setModal('undo_confirm'); 
                                                            }
                                                        }
                                                    }} 
                                                    disabled={!hasPending && !hasHistory}
                                                    className={`p-1.5 rounded transition relative group ${
                                                        hasHistory && !hasPending
                                                            ? 'text-green-600 hover:text-blue-600 hover:bg-blue-50' // Tudo assinado para o status atual
                                                            : 'text-purple-500 hover:text-purple-700 disabled:opacity-20' // Tem pendência ou nada a fazer
                                                    }`} 
                                                    title={hasPending 
                                                        ? `Assinar ${pendingQuarters.length} pendente(s)` 
                                                        : "Gerenciar/Desfazer Assinaturas"}
                                                >
                                                    {/* Ícone muda se já tiver assinado tudo DISPONÍVEL ATÉ O MOMENTO */}
                                                    {hasHistory && !hasPending ? <UserCheck size={18} strokeWidth={3}/> : <PenTool size={18}/>}
                                                    
                                                    {/* Badge contador de pendências */}
                                                    {pendingQuarters.length > 0 && (
                                                        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[9px] w-4 h-4 flex items-center justify-center rounded-full border border-white animate-pulse">
                                                            {pendingQuarters.length}
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })()}

                                                <button onClick={()=>onEdit(ev)} className="text-blue-500 hover:text-blue-700" title={editTitle}><EditIcon size={18}/></button>
                                                <button onClick={()=>{setSelectedEval(ev); setModal('pdf'); console.log("[PDF] Avaliação selecionada:", ev);}} className="text-gray-500 hover:text-gray-700" title="PDF"><Printer size={18}/></button>
                                                <button onClick={() => {
                                                    // Define qual avaliação será excluída
                                                    setSelectedEval(ev);
                                                    // Abre o modal customizado (que criamos no Passo 2)
                                                    setModal('delete_confirm');
                                                }} className="text-red-500 hover:text-red-700" title="Excluir">
                                                    <Trash2 size={18}/>
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>

                {modal === 'pdf' && selectedEval && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className={`p-6 rounded-lg w-80 shadow-2xl ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`}>
                            <h3 className="font-bold mb-4">Gerar PDF</h3>
                            
                            {/* CORREÇÃO: Passar selectedEval como primeiro parâmetro */}
                            <Button onClick={()=>generatePDF(selectedEval, 'Completo')} disabled={isGenerating} icon={FileText} className="w-full mb-2">Completo</Button>
                            
                            {selectedEval.personType === 'Gestão' ? (
                                <Button variant="secondary" onClick={() => generatePDF(selectedEval, 'Simplificado Gestão')} disabled={isGenerating} icon={FileText} className={`w-full mb-2 ${isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : ''}`}>Simplificado (Gestão)</Button>
                            ) : (
                                <Button variant="secondary" onClick={() => generatePDF(selectedEval, 'Simplificado Colaborador')} disabled={isGenerating} icon={FileText} className={`w-full mb-2 ${isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : ''}`}>Simplificado (Colaborador)</Button>
                            )}
                            
                            <Button variant="danger" onClick={()=>setModal(null)} icon={X} className="w-full">Cancelar</Button>
                        </div>
                    </div>
                )}

                {/* NOVO MODAL DE CONFIRMAÇÃO DE EXCLUSÃO (SUBSTITUI O WINDOW.CONFIRM) */}
                {modal === 'delete_confirm' && selectedEval && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] backdrop-blur-sm" onClick={() => setModal(null)}>
                        <div className={`p-6 rounded-xl w-96 shadow-2xl animate-fade-in border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex items-center gap-3 mb-4 text-red-600">
                                <div className="p-2 bg-red-100 rounded-full"><AlertTriangle size={24}/></div>
                                <h3 className="font-bold text-lg">Excluir Avaliação?</h3>
                            </div>
                            
                            <p className={`text-sm mb-6 leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                Você está prestes a excluir a avaliação de <b>{selectedEval.collaboratorName}</b> referente ao ano <b>{selectedEval.periodYear}</b>.
                                <br/><br/>
                                Isso apagará todas as notas e não pode ser desfeito.
                            </p>

                            <div className="flex justify-end gap-2">
                                <Button 
                                    variant="secondary" 
                                    onClick={() => setModal(null)} 
                                    className={isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600' : ''}
                                >
                                    Cancelar
                                </Button>
                                
                                <Button 
                                    onClick={() => {
                                        // 1. Executa a exclusão
                                        onDelete(selectedEval.id);
                                        
                                        // 2. Grava o Log de Auditoria
                                        if (actions.logAction) {
                                            actions.logAction('EXCLUSAO_AVALIACAO', { 
                                                colaborador: selectedEval.collaboratorName, 
                                                ano: selectedEval.periodYear 
                                            });
                                        }
                                        
                                        // 3. Fecha o modal
                                        setModal(null);
                                        setSelectedEval(null);
                                    }} 
                                    className="bg-red-600 hover:bg-red-700 text-white shadow-md border-0"
                                >
                                    Sim, Excluir
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {modal === 'status' && selectedEval && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm" onClick={() => setModal(null)}>
                        <div className={`p-6 rounded-xl w-96 shadow-2xl animate-fade-in ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center mb-2">
                                <h3 className="font-bold text-lg">Atualizar Status</h3>
                                <button onClick={() => setModal(null)} className={`hover:text-gray-600 ${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-400'}`}><X size={20}/></button>
                            </div>
                            <p className={`text-sm mb-6 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Selecione o estágio atual da avaliação de {selectedEval.collaboratorName}:</p>
                            
                                <div className="space-y-3">
                                    {['Em Andamento', 'T1-Concluido', 'T2-Concluido', 'T3-Concluido'].map(st => (
                                        <button key={st} onClick={() => { 
                                            // 1. MAPEAMENTO DE NÍVEIS
                                            const statusMap = { 
                                                'Em Andamento': 0, 'T1-Concluido': 1, 'T2-Concluido': 2, 'T3-Concluido': 3, 'T4-Concluido': 4 
                                            };
                                            const currentLevel = statusMap[selectedEval.status] || 0;
                                            const targetLevel = statusMap[st] || 0;

                                            // 2. REGRA DE BLOQUEIO POR ASSINATURA (NOVO)
                                            // Se estiver tentando "voltar" o status (ex: T1 -> Em Andamento), verifica se o atual já foi assinado
                                            if (targetLevel < currentLevel) {
                                                const currentQuarterTag = `T${currentLevel}`; // Ex: T1
                                                const hasSignature = (selectedEval.managerSignatures || []).some(sig => 
                                                    sig.statusRef && sig.statusRef.includes(currentQuarterTag)
                                                );

                                                if (hasSignature) {
                                                    setValidationAlert({
                                                        title: "Ação Bloqueada",
                                                        message: `O trimestre ${currentQuarterTag} já possui uma assinatura registrada.\n\nPara alterar o status, você deve primeiro EXCLUIR a assinatura clicando no ícone de caneta/check na tabela principal.`
                                                    });
                                                    return; // Aborta a ação
                                                }
                                            }

                                            // 3. REGRA DE PENDÊNCIAS (NOTAS) - JÁ EXISTENTE
                                            if (st.includes('Concluido')) {
                                                const targetQNum = parseInt(st.split('-')[0].replace('T', ''));
                                                const pendencies = Object.values(selectedEval.personalNotes || {}).filter(n => {
                                                    const isPendency = (n.tipo === 'Pendênca' || n.tipo === 'Pendência');
                                                    if (!isPendency || n.status === 'Concluída') return false;
                                                    const m = parseInt(n.date.split('-')[1]);
                                                    let noteQ = m <= 3 ? 1 : m <= 6 ? 2 : m <= 9 ? 3 : 4;
                                                    return noteQ <= targetQNum;
                                                });

                                                if (pendencies.length > 0) {
                                                    setValidationAlert({
                                                        title: `Bloqueio do T${targetQNum}`,
                                                        message: `Existem ${pendencies.length} pendências não resolvidas que impedem o fechamento deste período.\n\nResolva-as na aba de anotações antes de concluir.`
                                                    });
                                                    return;
                                                }
                                            }

                                            onUpdateStatus(selectedEval.id, st);
                                            if (actions.logAction) {
                                                actions.logAction('ALTERACAO_STATUS_AVALIACAO', { colaborador: selectedEval.collaboratorName, de: selectedEval.status, para: st });
                                            }
                                            setModal(null);
                                        }} 
                                        className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${selectedEval.status === st ?
                                            'border-blue-500 ring-1 ring-blue-500' : 
                                            (isDarkMode ? 'border-slate-600 hover:border-slate-500 hover:bg-slate-700' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50')
                                        }`}>
                                            <div className="flex items-center gap-3">
                                                <div className={`p-1.5 rounded-full ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-50 text-gray-600'}`}><List size={16} /></div>
                                                <span className={`font-medium ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>{st}</span>
                                            </div>
                                            {selectedEval.status === st && <CheckCircle size={18} className="text-blue-500" />}
                                        </button>
                                    ))}

                                    {/* BOTAO FINALIZAR T4 - TRAVA GERAL DO ANO (MANTIDO) */}
                                    {/* ... Mantenha o botão de T4 original aqui ... */}
                                    <button onClick={() => { 
                                        // Lógica de bloqueio de T4
                                        const hasT4Sig = (selectedEval.managerSignatures || []).some(s => s.statusRef.includes('T4') || s.statusRef.includes('Finalizada'));
                                        if (selectedEval.status === 'T4-Concluido' && hasT4Sig) {
                                            // Se tentar clicar para "re-finalizar" ou algo assim, mas a lógica aqui é mais simples pois é o último estágio
                                        }
                                        
                                        // ... (resto da lógica original do botão T4) ...
                                        const allOpenPendencies = Object.values(selectedEval.personalNotes || {}).filter(n => {
                                            const isPendency = (n.tipo === 'Pendênca' || n.tipo === 'Pendência');
                                            return isPendency && n.status !== 'Concluída';
                                        });
                                        
                                        if (allOpenPendencies.length > 0) {
                                            setValidationAlert({
                                                title: "Bloqueio de Finalização (T4)",
                                                message: `Não é possível finalizar o Ciclo Anual.\nExistem ${allOpenPendencies.length} pendências não resolvidas.`
                                            });
                                            return;
                                        }

                                        onUpdateStatus(selectedEval.id, 'T4-Concluido');
                                        setModal(null);
                                    }}
                                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${selectedEval.status === 'T4-Concluido' || selectedEval.status === 'Finalizada' ?
                                        'border-green-500 bg-green-50 dark:bg-green-900/40' : 
                                        (isDarkMode ? 'border-green-800 hover:border-green-700 bg-green-900/20' : 'border-green-200 hover:border-green-300 hover:bg-green-50')
                                    }`}>
                                        <div className="flex items-center gap-3">
                                            <div className={`p-1.5 rounded-full border ${isDarkMode ? 'bg-slate-800 text-green-500 border-green-900' : 'bg-white text-green-600 border-green-200'}`}><Lock size={16} /></div>
                                            <span className={`font-bold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>T4 - Concluído (Finalizar)</span>
                                        </div>
                                        {(selectedEval.status === 'T4-Concluido' || selectedEval.status === 'Finalizada') && <CheckCircle size={18} className="text-green-600" />}
                                    </button>
                                </div>
                        </div>
                    </div>
                )}

                {/* Outros modais (Relatório, Sync, Nova) mantidos... (não alterei os modais abaixo, apenas o conteúdo principal) */}
                {reportModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setReportModalOpen(false)}>
                        <div className={`p-6 rounded-xl w-96 shadow-2xl animate-fade-in space-y-4 ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
                            <div className={`flex justify-between items-center border-b pb-2 ${isDarkMode ? 
                            'border-slate-700' : 'border-gray-100'}`}>
                                <h3 className={`font-bold text-lg ${isDarkMode ? 'text-slate-100' : 'text-gray-800'}`}>Exportar Relatório</h3>
                                <button onClick={() => setReportModalOpen(false)} className={`hover:text-gray-600 ${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-400'}`}><X size={20}/></button>
                            </div>
                            <div>
                                <label className={`text-sm font-bold block mb-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Selecione o Resultado:</label>
                                <div className="flex gap-2">
                                    <Button onClick={() => setReportResultType('Promoção')} className={`flex-1 py-2 rounded border transition-all ${reportResultType === 'Promoção' ? 'bg-blue-600 border-blue-600 text-white font-bold shadow-md' : (isDarkMode ? 'bg-slate-700 border-slate-600 text-blue-300 hover:bg-slate-600' : 'bg-white border-blue-200 text-blue-600 hover:bg-blue-50 font-medium')}`}>Promoção</Button>
                                    <Button onClick={() => setReportResultType('Premiação')} className={`flex-1 py-2 rounded border transition-all ${reportResultType === 'Premiação' ? 'bg-green-600 border-green-600 text-white font-bold shadow-md' : (isDarkMode ? 'bg-slate-700 border-slate-600 text-green-300 hover:bg-slate-600' : 'bg-white border-green-200 text-green-600 hover:bg-green-50 font-medium')}`}>Premiação</Button>
                                </div>
                            </div>
                            <div>
                                <label className={`text-sm font-bold block mb-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Formato do Arquivo:</label>
                                <div className="flex flex-col gap-2">
                                    <Button onClick={() => generateBatchReport('pdf')} className={`w-full justify-center ${isDarkMode ? 'bg-blue-600 hover:bg-blue-500 text-white' : ''}`}><FileText size={16}/> Gerar PDF</Button>
                                    <Button variant="success" onClick={() => generateBatchReport('excel')} className={`w-full justify-center ${isDarkMode ? 'bg-green-600 hover:bg-green-500 text-white' : ''}`}><FileSpreadsheet size={16}/> Gerar Excel (CSV)</Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {isSyncModalOpen && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className={`p-6 rounded-xl w-96 shadow-2xl animate-fade-in space-y-4 ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white'}`}>
                            <h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-slate-100' : 'text-gray-800'}`}><RefreshCw size={18}/> Sincronizar Treinamentos</h3>
                            <div>
                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Ano Base (C1)</label>
                                <Input type="number" value={syncYearInput} onChange={e => setSyncYearInput(e.target.value)} className={`text-center text-lg tracking-widest font-bold ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`} maxLength={4}/>
                                <p className={`text-[10px] mt-2 ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>O sistema atualizará a planilha e importará os dados. Aguarde.</p>
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <Button variant="secondary" onClick={() => setIsSyncModalOpen(false)} disabled={isUpdatingSheet} className={isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600' : ''}>Cancelar</Button>
                                <Button onClick={handleConfirmSync} disabled={isUpdatingSheet} className={`text-white ${isDarkMode ? 'bg-green-600 hover:bg-green-500' : 'bg-green-600 hover:bg-green-700'}`}>
                                    {isUpdatingSheet ? <><Loader2 size={16} className="animate-spin mr-2"/> Processando...</> : "Confirmar"}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {isNewEvalModalOpen && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className={`p-6 rounded-xl w-96 shadow-2xl animate-fade-in space-y-4 ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white'}`}>
                            <h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-slate-100' : 'text-gray-800'}`}><Plus size={18}/> Iniciar Nova Avaliação</h3>
                            
                            <div className="space-y-3">
                            {/* --- CHECKBOX DE VISIBILIDADE (DINÂMICO) --- */}
                            { (currentUser?.role === 'SuperAdm' || currentUser?.permissions?.viewGestao) && (
                                <div className={`flex items-center gap-2 p-2 rounded border ${isDarkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-100'}`}>
                                    <input 
                                        type="checkbox" 
                                        id="dashOverrideChk"
                                        checked={showAllOverride}
                                        onChange={(e) => setShowAllOverride(e.target.checked)}
                                        className="rounded cursor-pointer text-blue-600 focus:ring-blue-500"
                                    />
                                    <label htmlFor="dashOverrideChk" className={`text-xs font-bold cursor-pointer select-none ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                                        {currentUser.role === 'SuperAdm' 
                                            ? "Exibir TODOS (Ignorar regras de Depto/Tipo)" 
                                            : "Incluir Gestão (Do meu departamento)"}
                                    </label>
                                </div>
                            )}

                                <div className={`flex items-center gap-2 p-2 rounded border ${isDarkMode ? 'bg-slate-900/40 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                                    <input
                                        type="checkbox"
                                        id="showEndedEvaluationCollaboratorsChk"
                                        checked={showEndedEvaluationCollaborators}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setShowEndedEvaluationCollaborators(checked);
                                            if (!checked && newEvalData.collabId) {
                                                const selected = collaborators.find(c => c.id === newEvalData.collabId);
                                                if (selected && String(selected.endDate || '').trim()) {
                                                    setNewEvalData(prev => ({ ...prev, collabId: '' }));
                                                }
                                            }
                                        }}
                                        className="rounded cursor-pointer text-amber-600 focus:ring-amber-500"
                                    />
                                    <label htmlFor="showEndedEvaluationCollaboratorsChk" className={`text-xs font-bold cursor-pointer select-none ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                                        Exibir pessoas com Fim Avaliação preenchido
                                    </label>
                                </div>

                                <div>
                                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Colaborador</label>
                                    <select 
                                        className={`w-full p-2 border rounded text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`} 
                                        value={newEvalData.collabId} 
                                        onChange={e => setNewEvalData({...newEvalData, collabId: e.target.value})}
                                    >
                                        <option value="">Selecione...</option>
                                        {/* USA A NOVA LISTA FILTRADA AQUI */}
                                        {modalCollaboratorsList.map(c => (
                                            <option key={c.id} value={c.id} className={isDarkMode ? 'bg-slate-800' : ''}>
                                                {c.name} ({c.personType})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div>
                                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Ano Referência</label>
                                    <Input type="number" value={newEvalData.year} onChange={e => setNewEvalData({...newEvalData, year: e.target.value})} className={`text-center font-bold ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`}/>
                                </div>
                            </div>
                            
                            <div className="flex justify-end gap-2 pt-2">
                                <Button 
                                    variant="secondary" 
                                    onClick={() => { 
                                        setIsNewEvalModalOpen(false); 
                                        setShowAllOverride(false); // Reseta o override ao fechar
                                        setShowEndedEvaluationCollaborators(false);
                                    }} 
                                    className={isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600' : ''}
                                >
                                    Cancelar
                                </Button>
                                <Button onClick={handleCreateEval} className={`text-white ${isDarkMode ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-600 hover:bg-blue-700'}`}>Iniciar</Button>
                            </div>
                        </div>
                    </div>
                )}
                    {/* MODAL GLOBAL DE ALERTA (DINÂMICO: SUCESSO vs ERRO) */}
                        {validationAlert && (
                            <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setValidationAlert(null)}>
                                <div className={`p-6 rounded-xl w-96 shadow-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()}>
                                    
                                    {(() => {
                                        // Verifica se é Sucesso pelo título
                                        const isSuccess = validationAlert.title === 'Sucesso!' || validationAlert.title === 'Sucesso';
                                        const isAISuggestion = validationAlert.type === 'aiSuggestion';
                                        
                                        return (
                                            <>
                                                <div className={`flex items-center gap-3 mb-4 ${isSuccess ? 'text-green-500' : isAISuggestion ? 'text-purple-500' : 'text-red-500'}`}>
                                                    <div className={`p-2 rounded-full ${isSuccess ? 'bg-green-100' : isAISuggestion ? 'bg-purple-100' : 'bg-red-100'}`}>
                                                        {isSuccess ? <CheckCircle size={24} /> : isAISuggestion ? <Sparkles size={24} /> : <AlertTriangle size={24} />}
                                                    </div>
                                                    <h3 className="font-bold text-lg">{validationAlert.title || 'Atenção'}</h3>
                                                </div>
                                                
                                                <p className={`text-sm mb-6 whitespace-pre-wrap leading-relaxed max-h-[45vh] overflow-y-auto custom-scrollbar ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                    {validationAlert.message || (typeof validationAlert === 'string' ? validationAlert : 'Erro desconhecido')}
                                                </p>
                                                
                                                <div className="flex justify-end gap-2">
                                                    <Button 
                                                        variant="secondary"
                                                        onClick={() => setValidationAlert(null)} 
                                                        className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}
                                                    >
                                                        {validationAlert.cancelText || (isSuccess ? 'Continuar' : 'Entendi')}
                                                    </Button>
                                                    {validationAlert.onConfirm && (
                                                        <Button
                                                            onClick={validationAlert.onConfirm}
                                                            className={`${isAISuggestion ? 'bg-purple-600 hover:bg-purple-700' : 'bg-red-600 hover:bg-red-700'} text-white shadow-md`}
                                                        >
                                                            {validationAlert.confirmText || 'Confirmar'}
                                                        </Button>
                                                    )}
                                                </div>
                                            </>
                                        );
                                    })()}
                                    
                                </div>
                            </div>
                        )}

            
        {/* --- MODAIS DE GERENCIAMENTO DE ASSINATURAS --- */}

                                {/* 1. MODAL DE OPÇÕES DE ASSINATURA (PENDENTES) */}
                                {modal === 'signature_options' && selectedEval && (
                                    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] backdrop-blur-sm" onClick={() => setModal(null)}>
                                        <div className={`p-6 rounded-xl w-96 shadow-2xl border animate-fade-in ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()}>
                                            <div className="flex items-center gap-3 mb-4 text-blue-600">
                                                <div className="p-2 bg-blue-100 rounded-full"><Settings size={24}/></div>
                                                <h3 className="font-bold text-lg">Assinaturas Pendentes</h3>
                                            </div>
                                            
                                            <p className={`text-sm mb-6 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                Existem <b>{selectedEval.pendingToSign?.length} trimestres</b> concluídos aguardando sua assinatura ({selectedEval.pendingToSign?.map(q => `T${q}`).join(', ')}).
                                            </p>

                                            <div className="flex flex-col gap-3">
                                                <button 
                                                    onClick={() => { selectedEval.signMode = 'all'; setModal('sign_confirm'); }}
                                                    className="w-full flex items-center justify-between p-3 rounded-lg border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                                                >
                                                    <span className="font-bold text-sm flex items-center gap-2"><Check size={16}/> Assinar Todos ({selectedEval.pendingToSign?.length})</span>
                                                    <ChevronRight size={16}/>
                                                </button>

                                                <button 
                                                    onClick={() => { selectedEval.signMode = 'single'; setModal('sign_confirm'); }}
                                                    className="w-full flex items-center justify-between p-3 rounded-lg border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors"
                                                >
                                                    <span className="font-bold text-sm flex items-center gap-2"><PenTool size={16}/> Assinar Apenas T{selectedEval.pendingToSign?.[0]}</span>
                                                    <ChevronRight size={16}/>
                                                </button>
                                            </div>
                                            <div className="mt-4 pt-4 border-t flex justify-center"><button onClick={() => setModal(null)} className="text-xs text-gray-500">Cancelar</button></div>
                                        </div>
                                    </div>
                                )}

                                {/* 2. MODAL CONFIRMAR ASSINATURA */}
                                {modal === 'sign_confirm' && selectedEval && (
                                    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] backdrop-blur-sm" onClick={() => setModal(null)}>
                                        <div className={`p-6 rounded-xl w-96 shadow-2xl border animate-fade-in ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()}>
                                            <div className="flex items-center gap-3 mb-4 text-purple-600">
                                                <div className="p-2 bg-purple-100 rounded-full"><PenTool size={24}/></div>
                                                <h3 className="font-bold text-lg">Confirmar Assinatura?</h3>
                                            </div>
                                            <p className={`text-sm mb-6 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                {selectedEval.signMode === 'all' ? `Você vai assinar: ${selectedEval.pendingToSign?.map(q => `T${q}`).join(', ')}.` : `Você vai assinar: T${selectedEval.pendingToSign?.[0]}.`}
                                            </p>
                                            <div className="flex justify-end gap-2">
                                                <Button 
                                                    variant="secondary" 
                                                    onClick={() => setModal(null)}
                                                    className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}
                                                >
                                                    Cancelar
                                                </Button>
                                                <Button onClick={() => {
                                                    let quartersToProcess = selectedEval.signMode === 'all' ? (selectedEval.pendingToSign || []) : [selectedEval.pendingToSign?.[0]];
                                                    let currentSigs = Array.isArray(selectedEval.managerSignatures) ? [...selectedEval.managerSignatures] : [];
                                                    let currentNotes = { ...selectedEval.personalNotes };

                                                    quartersToProcess.forEach(qNum => {
                                                        const loopStatus = `T${qNum}-Concluido`;
                                                        const alreadySigned = currentSigs.some(s => s.gestorEmail === currentUser.email && s.statusRef.includes(`T${qNum}`));
                                                        if (!alreadySigned) {
                                                            currentSigs.push({
                                                                date: new Date().toLocaleString('pt-BR'),
                                                                gestorName: currentUser.name,
                                                                gestorEmail: currentUser.email,
                                                                gestorId: currentUser.id,
                                                                statusRef: loopStatus, 
                                                                img: data.managers.find(m => m.email === currentUser.email)?.signatureImg || null
                                                            });
                                                            const noteId = `SIG_${currentUser.id}_T${qNum}_${Date.now()}`;
                                                            currentNotes[noteId] = { id: noteId, text: `Assinatura Digital do T${qNum} (Gestor) realizada por ${currentUser.name}.`, date: new Date().toISOString().split('T')[0], tipo: 'Assinatura', prioridade: 'Alta', status: 'Concluída', timestamp: Date.now() + qNum, colorIndex: 2 };
                                                        }
                                                    });

                                                    let nextStatus = selectedEval.status;
                                                    if (currentSigs.some(s => s.statusRef.includes('T4'))) nextStatus = 'Assinada pelo Gestor';

                                                    actions.saveEvaluation({ ...selectedEval, managerSignatures: currentSigs, personalNotes: currentNotes, status: nextStatus });
                                                    setModal(null);
                                                }} className="bg-purple-600 text-white hover:bg-purple-700">Confirmar</Button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* 3. MODAL DE OPÇÕES DE DESFAZER (RESTAURADO) */}
                                {modal === 'undo_options' && selectedEval && (
                                    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] backdrop-blur-sm" onClick={() => setModal(null)}>
                                        <div className={`p-6 rounded-xl w-96 shadow-2xl border animate-fade-in ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()}>
                                            <div className="flex items-center gap-3 mb-4 text-red-600">
                                                <div className="p-2 bg-red-100 rounded-full"><History size={24}/></div>
                                                <h3 className="font-bold text-lg">Gerenciar Assinaturas</h3>
                                            </div>
                                            
                                            <p className={`text-sm mb-6 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                Você possui <b>{selectedEval.mySignaturesCount} assinaturas</b>. O que deseja fazer?
                                            </p>

                                            <div className="flex flex-col gap-3">
                                                <button onClick={() => { selectedEval.undoMode = 'all'; setModal('undo_confirm'); }} className="w-full flex items-center justify-between p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors">
                                                    <span className="font-bold text-sm flex items-center gap-2"><Trash2 size={16}/> Remover TODAS</span>
                                                    <ChevronRight size={16}/>
                                                </button>

                                                <button onClick={() => { selectedEval.undoMode = 'single'; setModal('undo_confirm'); }} className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 transition-colors">
                                                    <span className="font-bold text-sm flex items-center gap-2"><History size={16}/> Remover Apenas a Última</span>
                                                    <ChevronRight size={16}/>
                                                </button>
                                            </div>
                                            <div className="mt-4 pt-4 border-t flex justify-center"><button onClick={() => setModal(null)} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">Cancelar</button></div>
                                        </div>
                                    </div>
                                )}

                                {/* 4. MODAL CONFIRMAR EXCLUSÃO DE ASSINATURA */}
                                {modal === 'undo_confirm' && selectedEval && (
                                    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] backdrop-blur-sm" onClick={() => setModal(null)}>
                                        <div className={`p-6 rounded-xl w-96 shadow-2xl border animate-fade-in ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()}>
                                            <div className="flex items-center gap-3 mb-4 text-red-600">
                                                <div className="p-2 bg-red-100 rounded-full"><AlertTriangle size={24}/></div>
                                                <h3 className="font-bold text-lg">{selectedEval.undoMode === 'all' ? 'Excluir TUDO?' : 'Excluir Última?'}</h3>
                                            </div>
                                            <p className={`text-sm mb-6 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                {selectedEval.undoMode === 'all' ? "Todas as suas assinaturas serão removidas." : "Apenas a assinatura mais recente será removida."}
                                            </p>
                                            <div className="flex justify-end gap-2">
                                                <Button variant="secondary" onClick={() => setModal(null)}>Cancelar</Button>
                                                <Button onClick={async () => {
                                                    const currentSigs = Array.isArray(selectedEval.managerSignatures) ? [...selectedEval.managerSignatures] : [];
                                                    let updatedSigs = [];
                                                    const currentNotes = { ...selectedEval.personalNotes };

                                                    if (selectedEval.undoMode === 'all') {
                                                        updatedSigs = currentSigs.filter(s => s.gestorEmail !== currentUser.email);
                                                        // Remove notas do tipo assinatura deste usuário
                                                        Object.keys(currentNotes).forEach(key => {
                                                            if (currentNotes[key].tipo === 'Assinatura' && currentNotes[key].text.includes(currentUser.name)) delete currentNotes[key];
                                                        });
                                                    } else {
                                                        const lastIdx = currentSigs.findLastIndex(s => s.gestorEmail === currentUser.email);
                                                        if (lastIdx !== -1) {
                                                            const sigToRemove = currentSigs[lastIdx];
                                                            updatedSigs = currentSigs.filter((_, i) => i !== lastIdx);
                                                            // Remove nota específica se encontrar
                                                            Object.keys(currentNotes).forEach(key => {
                                                                if (currentNotes[key].tipo === 'Assinatura' && currentNotes[key].text.includes(currentUser.name) && currentNotes[key].text.includes(sigToRemove.statusRef.split('-')[0])) delete currentNotes[key];
                                                            });
                                                        } else updatedSigs = currentSigs;
                                                    }

                                                    let newStatus = selectedEval.status;
                                                    if (selectedEval.status === 'Assinada pelo Gestor' && updatedSigs.length === 0) newStatus = 'T4-Concluido';

                                                    actions.saveEvaluation({ ...selectedEval, managerSignatures: updatedSigs, personalNotes: currentNotes, status: newStatus });
                                                    setModal(null);
                                                }} className="bg-red-600 text-white hover:bg-red-700">Confirmar</Button>
                                            </div>
                                        </div>
                                    </div>
                                )}       
            </div>
        );
    };

    /**
     * ============================================================================
     * COMPONENTE: CADASTRO MODAL
     * ============================================================================
     * Modal polimórfico utilizado para criar e editar diversas entidades do sistema:
     * Usuários, Colaboradores, Cargos (PCS), Departamentos, Configurações, etc.
     * Adapta seu formulário dinamicamente com base na prop 'tab'.
     */


    /* ===============================================================================
     * [END_MOD_DASHBOARD_SYSTEM]
     * FIM DO BLOCO: DashboardSystem
     * [START_MOD_CADASTRO_MODAL]
     * INICIO DO BLOCO: CadastroModal
     * =============================================================================== */
    const CadastroModal = ({ 
        isOpen, 
        onClose, 
        tab, 
        form, 
        setForm, 
        data, 
        handleSave, 
        criteriaType, 
        setCriteriaType, 
        currentUser, 
        handleSaveUser, 
        isDarkMode 
    }) => {
        
        // ========================================================================
        // 1. ESTILIZAÇÃO DINÂMICA (THEME ENGINE)
        // ========================================================================
        const themeStyles = {
            modalBase: isDarkMode ? 'bg-slate-800 text-slate-100 border border-slate-700' : 'bg-white text-gray-800',
            headerBorder: isDarkMode ? 'border-slate-700' : 'border-gray-200',
            input: `w-full p-2.5 border rounded text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white placeholder-slate-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'}`,
            label: `text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`,
            subCard: isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200',
            calendar: isDarkMode ? 'invert-calendar' : '',
            option: isDarkMode ? 'bg-slate-800' : ''
        };

        // ========================================================================
        // 2. ESTADOS LOCAIS (LOCAL STATE)
        // ========================================================================
        
        const [roleSearch, setRoleSearch] = useState('');
        const [searchQuali, setSearchQuali] = useState('');
        const [searchQuanti, setSearchQuanti] = useState('');
        
        // Controle de Edição de Histórico
        const [editingHistId, setEditingHistId] = useState(null);
        const [tempHistDates, setTempHistDates] = useState({ validFrom: '', endDate: '' });
        
        // Controle de Busca de Cargos (PCS)
        const [pcsSearch, setPcsSearch] = useState('');
        const [showPcsList, setShowPcsList] = useState(false);
        
        // Controle de Rateio de Departamento
        const [tempDeptShare, setTempDeptShare] = useState({ deptId: '', share: '' });

        // --- NOVOS ESTADOS PARA GESTORES (MOVIDOS PARA CÁ) ---
        const [managerSearch, setManagerSearch] = useState('');
        const [showManagerList, setShowManagerList] = useState(false);

        // NOVO ESTADO: Controle das abas do formulário de colaborador
        const [collabTab, setCollabTab] = useState('cadastrais');

        // ========================================================================
        // 3. EFEITOS E LÓGICA DE NEGÓCIO
        // ========================================================================

        useEffect(() => { 
            if (tab === 'collab' && !form.evalTypeId && data.evaluationTypes.length > 0) { 
                const availableTypes = data.evaluationTypes.filter(t => !t.personType || t.personType === (form.personType || 'Colaborador')); 
                if (availableTypes.length > 0) { setForm(prev => ({ ...prev, evalTypeId: availableTypes[0].id })); } 
            } 
        }, [tab, form.personType, data.evaluationTypes, form.evalTypeId]);

        const uniqueRoles = [...new Set(data.pcs.map(p => p.role))].sort();
        const pcsOptions = data.pcs.map(p => ({ id: p.id, label: `${p.role} ${p.seniority} ${p.level}`, original: p })).sort((a, b) => a.label.localeCompare(b.label));

        // --- FUNÇÕES AUXILIARES DE GESTORES (MOVIDAS PARA CÁ) ---
        const handleRemoveManager = (mId) => {
            const currentIds = form.managerIds || [];
            setForm(prev => ({ ...prev, managerIds: currentIds.filter(id => id !== mId) }));
        };

        const handleAddManager = (mId) => {
            const currentIds = form.managerIds || [];
            if (!currentIds.includes(mId)) {
                setForm(prev => ({ ...prev, managerIds: [...currentIds, mId] }));
            }
            setManagerSearch('');
            setShowManagerList(false);
        };

        const getDiretoriaId = () => {
            const dir = data.departments.find(d => d.name.toUpperCase() === 'DIR' || d.name.toUpperCase().includes('DIRETORIA') || d.name.toUpperCase().includes('DIR.'));
            return dir ? dir.id : null;
        };

        const filterManagersByDept = (targetDeptId) => {
            if (!targetDeptId) return [];
            return data.managers.filter(m => {
                if (m.departmentId === targetDeptId) return true;
                if (m.deptShare && Array.isArray(m.deptShare)) {
                    return m.deptShare.some(share => share.deptId === targetDeptId);
                }
                return false;
            });
        };

        const getAvailableManagers = () => {
            let targetDeptId = form.departmentId;
            if (form.personType === 'Gestão') {
                const dirId = getDiretoriaId();
                if (!dirId) return []; 
                targetDeptId = dirId;
            }
            if (!targetDeptId) return [];
            const validManagers = filterManagersByDept(targetDeptId);
            return validManagers.filter(m => !(form.managerIds || []).includes(m.id) && m.name.toLowerCase().includes(managerSearch.toLowerCase()));
        };

        const handleCollabDeptChange = (e) => {
            const newDeptId = e.target.value;
            let targetDeptForManagers = newDeptId;
            if (form.personType === 'Gestão') {
                const dirId = getDiretoriaId();
                if (dirId) targetDeptForManagers = dirId;
            }
            const managersFound = filterManagersByDept(targetDeptForManagers);
            setForm(prev => ({ ...prev, departmentId: newDeptId, managerIds: managersFound.map(m => m.id) }));
        };

        const handleCollabTypeChange = (e) => {
            const newType = e.target.value;
            let targetDeptForManagers = form.departmentId;
            if (newType === 'Gestão') {
                const dirId = getDiretoriaId();
                if (dirId) targetDeptForManagers = dirId;
            } else if (newType === 'Colaborador' && form.departmentId) {
                targetDeptForManagers = form.departmentId;
            }
            if (targetDeptForManagers) {
                const managersFound = filterManagersByDept(targetDeptForManagers);
                setForm(prev => ({ ...prev, personType: newType, managerIds: managersFound.map(m => m.id) }));
            } else {
                setForm(prev => ({ ...prev, personType: newType }));
            }
        };

        // --- FUNÇÕES AUXILIARES GERAIS ---
        const deleteHistoryItem = (index) => { const newHistory = [...(form.roleHistory || [])]; newHistory.splice(index, 1); setForm({ ...form, roleHistory: newHistory }); };
        const startEditHistory = (item) => { setEditingHistId(item.id); setTempHistDates({ validFrom: item.validFrom || '', endDate: item.endDate || '' }); };
        const saveEditHistory = (index) => { const newHistory = [...(form.roleHistory || [])]; newHistory[index] = { ...newHistory[index], validFrom: tempHistDates.validFrom, endDate: tempHistDates.endDate }; setForm({ ...form, roleHistory: newHistory }); setEditingHistId(null); };
        const addDeptShare = () => { if (!tempDeptShare.deptId) return; const currentShares = form.deptShare || []; const filtered = currentShares.filter(s => s.deptId !== tempDeptShare.deptId); const currentTotal = filtered.reduce((acc, item) => acc + (item.share || 0), 0); let newShare = parseFloat(tempDeptShare.share); if (isNaN(newShare)) { if (filtered.length === 0) { newShare = 100; } else { return alert("Para múltiplos departamentos, informe a porcentagem."); } } if (currentTotal + newShare > 100) { return alert(`O total do rateio não pode ultrapassar 100%. Atual: ${currentTotal}%. Disponível: ${100 - currentTotal}%`); } setForm({ ...form, deptShare: [...filtered, { deptId: tempDeptShare.deptId, share: newShare }] }); setTempDeptShare({ deptId: '', share: '' }); };
        const removeDeptShare = (deptId) => { setForm({ ...form, deptShare: (form.deptShare || []).filter(s => s.deptId !== deptId) }); };
        const handlePcsSelectChange = (e) => { const selectedPcsId = e.target.value; const selectedPcs = data.pcs.find(p => p.id === selectedPcsId); if (selectedPcs) { setForm(prev => ({ ...prev, role: selectedPcs.role, seniority: selectedPcs.seniority, level: selectedPcs.level, salary: parseFloat(selectedPcs.salary), _pcsId: selectedPcs.id })); } else { setForm(prev => ({ ...prev, role: '', seniority: '', level: '', salary: '' })); } };
        const getCurrentPcsId = () => { if (form._pcsId) return form._pcsId; const found = data.pcs.find(p => p.role?.trim() === form.role?.trim() && p.seniority?.trim() === form.seniority?.trim() && p.level?.trim() === form.level?.trim()); return found ? found.id : ''; };

        if (!isOpen) return null;


        // ========================================================================
        // 5. RENDERIZAÇÃO DOS CAMPOS (FIELDS)
        // ========================================================================

        const renderFields = () => {
            
    // --- 5.1 ABA: USUÁRIOS (ATUALIZADA) ---
            if (tab === 'users') {
                
                // Função auxiliar para garantir integridade estrutural das permissões
                    const fixProjectPermissions = (perms) => {
                        const safePerms = { ...perms };
                        
                        // 1. Garante a ativação da chave Mestre de Produtividade se tiver a permissão básica
                        if (safePerms['access_prod_basic']) {
                            safePerms['access_proj_contr_prod'] = true;
                        }

                        // 2. Garante a ativação da chave Mestre da Folha de Ponto se tiver a permissão básica
                        if (safePerms['access_ponto_basic']) {
                            safePerms['access_proj_folha_ponto'] = true;
                        }

                        // 3. Mantém a lógica original para o projeto principal (AEC - Acompanhamento de Evolução Continuada)
                        const hasAvDesempenho = Object.keys(safePerms).some(k => 
                            k !== 'access_proj_contr_prod' && 
                            k !== 'access_prod_basic' && 
                            k !== 'access_proj_folha_ponto' && 
                            k !== 'access_ponto_basic'
                        );
                        
                        if (hasAvDesempenho && Object.keys(safePerms).length > 0) {
                            safePerms['access_proj_av_desempenho'] = true;
                        }

                        return safePerms;
                    };

                // Lógica de restrição do Dropdown
                const getAvailableRoles = () => {
                    const roles = ['ADM', 'Gestor', 'Colaborador', 'SuperAdm', 'Solicitante'];
                    // Se quem está logado NÃO é SuperAdm, filtra a lista
                    if (currentUser.role !== 'SuperAdm') {
                        return roles.filter(r => ['Gestor', 'Colaborador', 'Solicitante'].includes(r));
                    }
                    return roles;
                };

                const handleRoleChange = (newRole) => {
                let updates = { role: newRole };
                
                if (newRole === 'Colaborador') {
                    updates.permissions = {};
                    updates.profileId = '';
                    updates._profileId = '';
                } else {
                    // Tenta carregar perfil padrão para outros cargos se houver
                    const linkedProfile = data.permissionProfiles.find(p => p.linkedRole === newRole);
                    if (linkedProfile) {
                        updates.permissions = fixProjectPermissions(linkedProfile.permissions);
                        updates.profileId = linkedProfile.id;
                        updates._profileId = linkedProfile.id;
                    } else {
                        updates.profileId = '';
                        updates._profileId = '';
                    }
                }

                setForm(prev => ({ ...prev, ...updates }));
            };

                return (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            
                                                    {/* ... dentro do grid do modal de usuários ... */}
                            <div className="col-span-2 p-3 rounded border border-purple-200 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-800 flex items-center justify-between">
                                <div>
                                    <label className={`text-sm font-bold ${isDarkMode ? 'text-purple-300' : 'text-purple-800'}`}>Autenticação em Dois Fatores (2FA)</label>
                                    <p className="text-[10px] opacity-70">Exige código do Google Authenticator ao logar.</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        className="sr-only peer"
                                        checked={!!form.is2FAEnabled}
                                        onChange={(e) => setForm({ ...form, is2FAEnabled: e.target.checked })}
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                                </label>
                            </div>
                            {/* Se quiser permitir resetar o segredo caso o usuário perca o celular */}
                            {form.is2FASetupComplete && (
                                <div className="col-span-2 text-right">
                                    <button 
                                        type="button"
                                        onClick={() => setForm({...form, is2FASetupComplete: false, twoFactorSecret: null})}
                                        className="text-xs text-red-500 underline hover:text-red-700"
                                    >
                                        Resetar Configuração 2FA (O usuário terá que configurar novamente)
                                    </button>
                                </div>
                            )}
                            {/* 1. TIPO DE USUÁRIO */}
                            <div className="col-span-2 p-3 rounded border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
                                <label className={themeStyles.label}>1. Nível de Acesso (Tipo de Usuário)</label>
                                <select 
                                    className={`${themeStyles.input} font-bold`} 
                                    value={form.role || 'Colaborador'} 
                                    onChange={(e) => handleRoleChange(e.target.value)}
                                >
                                    {getAvailableRoles().map(r => (
                                        <option key={r} value={r} className={themeStyles.option}>{r}</option>
                                    ))}
                                </select>
                                <p className="text-[10px] text-blue-600 dark:text-blue-400 mt-1">
                                    {form.role === 'Colaborador' 
                                        ? "* Colaboradores iniciam sem permissões (definir manualmente ou via perfil)." 
                                        : "* Perfil de permissões padrão será carregado se houver."}
                                </p>
                            </div>

                            {/* 2. CARREGAR PERFIL */}
                            <div className="col-span-2">
                                <label className={themeStyles.label}>2. Perfil de Permissões (Opcional)</label>
                                <select 
                                    className={themeStyles.input} 
                                    value={form._profileId || ''} 
                                    onChange={(e) => {
                                        const pid = e.target.value;
                                        const updates = { _profileId: pid };
                                        if(pid) {
                                            const prof = data.permissionProfiles.find(p => p.id === pid);
                                            if(prof) {
                                                updates.permissions = fixProjectPermissions(prof.permissions);
                                            }
                                        }
                                        setForm(prev => ({ ...prev, ...updates }));
                                    }}
                                >
                                    <option value="">Personalizado / Manual</option>
                                    {data.permissionProfiles.map(p => (
                                        <option key={p.id} value={p.id}>
                                            {p.name} {p.linkedRole ? `(Padrão: ${p.linkedRole})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* 3. DADOS PESSOAIS */}
                            <div className="col-span-2">
                                <label className={themeStyles.label}>Nome Completo</label>
                                <input className={themeStyles.input} value={form.name||''} onChange={e=>setForm({...form, name:e.target.value})} />
                            </div>
                            <div>
                                <label className={themeStyles.label}>E-mail de Acesso</label>
                                <input type="email" className={themeStyles.input} value={form.email||''} onChange={e=>setForm({...form, email:e.target.value})} />
                            </div>
                            <div>
                                <label className={themeStyles.label}>Apelido</label>
                                <input className={themeStyles.input} value={form.nickname||''} onChange={e=>setForm({...form, nickname:e.target.value})} />
                            </div>
                            <div className="col-span-2">
                                <label className={themeStyles.label}>Departamento</label>
                                <select className={themeStyles.input} value={form.departmentId||''} onChange={e=>setForm({...form, departmentId:e.target.value})}>
                                    <option value="">Selecione...</option>
                                    {data.departments.map(d=><option key={d.id} value={d.id} className={themeStyles.option}>{d.name}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* 4. DATAS */}
                        <div className={`grid grid-cols-2 gap-4 p-3 rounded border ${themeStyles.subCard}`}>
                            <div>
                                <label className={themeStyles.label}>Data Início (Acesso)</label>
                                <input type="date" className={themeStyles.input} value={form.startDate || new Date().toISOString().split('T')[0]} onChange={e=>setForm({...form, startDate:e.target.value})} />
                            </div>
                            <div>
                                <label className={themeStyles.label}>Data Fim (Encerramento)</label>
                                <input type="date" className={themeStyles.input} value={form.endDate||''} onChange={e=>setForm({...form, endDate:e.target.value})} />
                                <p className="text-[10px] text-gray-400 mt-1">Preencher para inativar.</p>
                            </div>
                        </div>
                    </div>
                );
            }

            // --- 5.2 ABA: CARGOS E SALÁRIOS (PCS) ---
            if (tab === 'pcs') {
                return (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className={themeStyles.label}>Título do Cargo</label>
                                <input className={themeStyles.input} value={form.role||''} onChange={e=>setForm({...form, role:e.target.value})} placeholder="Ex: Analista Financeiro" />
                            </div>
                            <div>
                                <label className={themeStyles.label}>Senioridade</label>
                                <select className={themeStyles.input} value={form.seniority||'JR'} onChange={e=>setForm({...form, seniority:e.target.value})}>
                                    {['JR','PL','SR','N/A'].map(opt => <option key={opt} className={themeStyles.option}>{opt}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={themeStyles.label}>Nível</label>
                                <select className={themeStyles.input} value={form.level||'I'} onChange={e=>setForm({...form, level:e.target.value})}>
                                    {['I','II','III','IV'].map(opt => <option key={opt} className={themeStyles.option}>{opt}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={themeStyles.label}>Salário Base</label>
                                <input type="number" className={themeStyles.input} value={form.salary||''} onChange={e=>setForm({...form, salary:e.target.value})} />
                            </div>
                            <div>
                                <label className={themeStyles.label}>Depto Associado</label>
                                <select className={themeStyles.input} value={form.department||'Todos'} onChange={e=>setForm({...form, department:e.target.value})}>
                                    <option className={themeStyles.option} value="Todos">Todos</option>
                                    {data.departments.map(d=><option className={themeStyles.option} key={d.id} value={d.name}>{d.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={themeStyles.label}>Início da Vigência</label>
                                <input type="date" className={themeStyles.input} value={form.validFrom||''} onChange={e=>setForm({...form, validFrom:e.target.value})} />
                            </div>
                        </div>
                    </div>
                );
            }

            // --- 5.3 ABA: CONFERÊNCIA (NOVO) ---
            if (tab === 'conferencia') {
                return (
                    <div className="space-y-4">
                        <div>
                            <label className={themeStyles.label}>Descrição / Nome do Tipo</label>
                            <input className={themeStyles.input} value={form.name||''} onChange={e=>setForm({...form, name:e.target.value})} placeholder="Ex: Conferência de Estoque" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className={themeStyles.label}>Departamento</label>
                                <select className={themeStyles.input} value={form.departmentId||''} onChange={e=>setForm({...form, departmentId:e.target.value})}>
                                    <option className={themeStyles.option} value="">Selecione...</option>
                                    <option className={themeStyles.option} value="all">Todos</option>
                                    {data.departments.map(d=><option key={d.id} value={d.id} className={themeStyles.option}>{d.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={themeStyles.label}>Público Alvo</label>
                                <select className={themeStyles.input} value={form.personType||'Colaborador'} onChange={e=>setForm({...form, personType:e.target.value})}>
                                    <option className={themeStyles.option} value="Colaborador">Colaborador</option>
                                    <option className={themeStyles.option} value="Gestão">Gestão</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><label className={themeStyles.label}>Data Início</label><input type="date" className={`${themeStyles.input} ${themeStyles.calendar}`} value={form.startDate||''} onChange={e=>setForm({...form, startDate:e.target.value})} /></div>
                            <div><label className={themeStyles.label}>Data Fim</label><input type="date" className={`${themeStyles.input} ${themeStyles.calendar}`} value={form.endDate||''} onChange={e=>setForm({...form, endDate:e.target.value})} /></div>
                        </div>
                    </div>
                );
            }

            // --- 5.4 ABA: COLABORADOR (PESSOAS) - COM ABA PONTO ---
            if (tab === 'collab') {
                return (
                    <div className="space-y-4 animate-fade-in" onClick={() => { if(showManagerList) setShowManagerList(false); }}>
                        
                        {/* NAVEGAÇÃO DE ABAS INTERNAS */}
                        <div className={`flex border-b mb-4 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                            <button onClick={() => setCollabTab('cadastrais')} className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${collabTab === 'cadastrais' ? 'border-blue-500 text-blue-500' : 'border-transparent opacity-60'}`}>1. Dados Cadastrais</button>
                            <button onClick={() => setCollabTab('contratuais')} className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${collabTab === 'contratuais' ? 'border-blue-500 text-blue-500' : 'border-transparent opacity-60'}`}>2. Dados Contratuais</button>
                            <button onClick={() => setCollabTab('ponto')} className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${collabTab === 'ponto' ? 'border-blue-500 text-blue-500' : 'border-transparent opacity-60'}`}>3. Ponto (Jornada)</button>
                            {/* --- NOVO BOTÃO DA ABA ACADCOINS --- */}
                            <button onClick={() => setCollabTab('acadcoins')} className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${collabTab === 'acadcoins' ? 'border-blue-500 text-blue-500' : 'border-transparent opacity-60'}`}>4. AcadCoins</button>
                        </div>

                        {/* === ABA 1: DADOS CADASTRAIS === */}
                        {collabTab === 'cadastrais' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-in">
                                <div className="col-span-1"><label className={themeStyles.label}>ID do Sistema</label><div className="relative"><input disabled className={`${themeStyles.input} bg-gray-100 text-gray-500 font-mono text-xs`} value={form.uniqueId || 'Gerado ao Salvar'} /><Lock size={12} className="absolute right-2 top-3 text-gray-400"/></div></div>
                                <div className="col-span-1"><label className={themeStyles.label}>Tipo de Pessoa</label><select className={themeStyles.input} value={form.personType||'Colaborador'} onChange={handleCollabTypeChange}><option className={themeStyles.option} value="Colaborador">Colaborador</option><option className={themeStyles.option} value="Gestão">Gestão</option></select></div>
                                <div className="col-span-1"><label className={themeStyles.label}>Nome Completo</label><input className={themeStyles.input} value={form.name||form.title||''} onChange={e=>setForm({...form, [form.title !== undefined ? 'title' : 'name']: e.target.value})} /></div>
                                <div className="col-span-1"><label className={themeStyles.label}>CPF</label><input className={themeStyles.input} placeholder="000.000.000-00" value={form.cpf||''} onChange={e=>setForm({...form, cpf:e.target.value})} /></div>
                                <div className="col-span-1"><label className={themeStyles.label}>Email Corporativo</label><input type="email" className={themeStyles.input} value={form.email||''} onChange={e=>setForm({...form, email:e.target.value})} /></div>
                                <div className="col-span-1"><label className={themeStyles.label}>Departamento</label><select className={themeStyles.input} value={form.departmentId||''} onChange={handleCollabDeptChange}><option className={themeStyles.option} value="">Selecione...</option>{data.departments.map(d=><option className={themeStyles.option} key={d.id} value={d.id}>{d.name}</option>)}</select></div>
                                
                                {/* GESTOR (CAMPO DE BUSCA) */}
                                <div className="col-span-2 relative">
                                    <label className={themeStyles.label}>Gestor(es) Imediato(s)</label>
                                    <div className={`flex flex-wrap items-center gap-1.5 p-1.5 rounded border min-h-[38px] transition-colors cursor-text ${isDarkMode ? 'bg-slate-900 border-slate-600 focus-within:border-blue-500' : 'bg-white border-gray-300 focus-within:border-blue-500 ring-1 ring-transparent focus-within:ring-blue-500/20'}`} onClick={(e) => { e.stopPropagation(); document.getElementById('mgr-search-input')?.focus(); }}>
                                        {(form.managerIds || []).map(mId => {
                                            const m = data.managers.find(x => x.id === mId);
                                            if (!m) return null;
                                            return (<span key={mId} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold border animate-fade-in ${isDarkMode ? 'bg-slate-800 text-slate-200 border-slate-600' : 'bg-gray-100 text-gray-700 border-gray-200'}`}>{m.name}<button type="button" onClick={(e) => { e.stopPropagation(); handleRemoveManager(mId); }} className="text-red-500 hover:text-red-700 hover:bg-red-100/50 rounded-full p-0.5 transition-colors" title="Remover"><X size={12} strokeWidth={3} /></button></span>);
                                        })}
                                        <div className="flex-1 relative min-w-[120px]"><input id="mgr-search-input" type="text" className={`w-full bg-transparent outline-none text-xs h-7 px-1 ${isDarkMode ? 'text-white placeholder-slate-500' : 'text-gray-700 placeholder-gray-400'}`} placeholder={(form.managerIds || []).length > 0 ? "Adicionar..." : "Digite para buscar gestor..."} value={managerSearch} onChange={(e) => { setManagerSearch(e.target.value); setShowManagerList(true); }} onFocus={() => setShowManagerList(true)} />
                                            {showManagerList && (<div className={`absolute left-0 top-full mt-1 w-full max-w-sm max-h-40 overflow-y-auto rounded-lg border shadow-xl z-50 ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'}`}>{getAvailableManagers().length > 0 ? (getAvailableManagers().map(m => (<div key={m.id} onClick={(e) => { e.stopPropagation(); handleAddManager(m.id); }} className={`px-3 py-2 text-xs cursor-pointer border-b last:border-0 transition-colors ${isDarkMode ? 'text-slate-200 hover:bg-slate-700 border-slate-700' : 'text-gray-700 hover:bg-blue-50 border-gray-50'}`}>{m.name}</div>))) : <div className={`px-3 py-2 text-xs italic ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>{managerSearch ? "Nenhum gestor encontrado." : "Digite para buscar..."}</div>}</div>)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* === ABA 2: DADOS CONTRATUAIS === */}
                        {collabTab === 'contratuais' && (
                            <div className="space-y-4 animate-fade-in">
                                <div className={`p-4 rounded border space-y-4 ${themeStyles.subCard}`}>
                                    <h4 className={`font-bold text-sm border-b pb-1 mb-2 ${isDarkMode ? 'text-slate-300 border-slate-600' : 'text-gray-700 border-gray-200'}`}>Vínculo Empregatício</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="col-span-2"><label className={themeStyles.label}>Empresa Vinculada</label><input className={themeStyles.input} placeholder="Razão Social" value={form.companyName||''} onChange={e=>setForm({...form, companyName:e.target.value})} /></div>
                                        <div><label className={themeStyles.label}>CNPJ</label><input className={themeStyles.input} placeholder="00.000.000/0000-00" value={form.companyCNPJ||''} onChange={e=>setForm({...form, companyCNPJ:e.target.value})} /></div>
                                        <div><label className={themeStyles.label}>Matrícula</label><input className={themeStyles.input} placeholder="Nº Registro" value={form.registrationNumber||''} onChange={e=>setForm({...form, registrationNumber:e.target.value})} /></div>
                                    </div>
                                </div>
                                <div className={`p-4 rounded border space-y-4 ${themeStyles.subCard}`}>
                                    <h4 className={`font-bold text-sm border-b pb-1 mb-2 ${isDarkMode ? 'text-slate-300 border-slate-600' : 'text-gray-700 border-gray-200'}`}>Cargo e Salário</h4>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="col-span-1 relative">
                                            <label className={`${themeStyles.label} ${isDarkMode ? 'text-yellow-400' : 'bg-yellow-100 text-gray-600 px-1 rounded w-fit'}`}>Cargo (PCS)</label>
                                            <input type="text" className={`${themeStyles.input} pl-7`} placeholder="Buscar..." value={showPcsList ? pcsSearch : (pcsOptions.find(o => o.id === getCurrentPcsId())?.label || form.role || '')} onChange={(e) => { setPcsSearch(e.target.value); setShowPcsList(true); }} onClick={() => { setPcsSearch(''); setShowPcsList(true); }} />
                                            <Search size={14} className="absolute left-2 top-8 text-gray-400 pointer-events-none"/>
                                            {showPcsList && (<div className={`absolute z-50 w-full border shadow-xl rounded-b-lg max-h-60 overflow-y-auto mt-1 ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'}`}>{pcsOptions.filter(opt => opt.label.toLowerCase().includes(pcsSearch.toLowerCase())).map(opt => (<div key={opt.id} className={`p-2 text-xs cursor-pointer border-b ${isDarkMode ? 'hover:bg-slate-700 border-slate-700 text-slate-300' : 'hover:bg-yellow-50 text-gray-700 border-gray-100'}`} onClick={() => { const fakeEvent = { target: { value: opt.id } }; handlePcsSelectChange(fakeEvent); setShowPcsList(false); }}>{opt.label}</div>))}</div>)}
                                        </div>
                                        <div><label className={themeStyles.label}>Senioridade</label><input disabled className={`${themeStyles.input} opacity-60 cursor-not-allowed`} value={form.seniority || ''} /></div>
                                        <div><label className={themeStyles.label}>Nível</label><input disabled className={`${themeStyles.input} opacity-60 cursor-not-allowed`} value={form.level || ''} /></div>
                                    </div>
                                    <div><label className={themeStyles.label}>Salário Atual (R$)</label><input type="text" disabled className={`${themeStyles.input} font-bold opacity-70`} value={formatCurrency(form.salary)} onChange={()=>{}} /></div>
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div><label className={themeStyles.label}>Regra Avaliação</label><select className={themeStyles.input} value={form.evalTypeId||''} onChange={e=>setForm({...form, evalTypeId:e.target.value})}>{data.evaluationTypes.filter(t => !t.personType || t.personType === (form.personType || 'Colaborador')).map(t=><option key={t.id} value={t.id} className={themeStyles.option}>{t.name}</option>)}</select></div>
                                    <div><label className={themeStyles.label}>Início Avaliação</label><input type="date" className={themeStyles.input} value={form.startDate||''} onChange={e=>setForm({...form, startDate:e.target.value})} /></div>
                                    <div><label className={themeStyles.label}>Fim Avaliação</label><input type="date" className={themeStyles.input} value={form.endDate||''} onChange={e=>setForm({...form, endDate:e.target.value})} /></div>
                                </div>
                                <div className={`p-3 rounded border ${themeStyles.subCard}`}>
                                    <h4 className={`font-bold text-sm mb-2 flex items-center gap-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}><History size={16}/> Histórico de Cargos e Salários</h4>
                                    <div className={`max-h-40 overflow-y-auto border rounded ${isDarkMode ? 'border-slate-600' : 'border-gray-200'}`}>
                                        <table className={`w-full text-xs text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                                            <thead className={`font-bold sticky top-0 ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-500'}`}><tr><th className="p-2">Função / Cargo</th><th className="p-2">Salário</th><th className="p-2">Vigência</th><th className="p-2 text-center">Ações</th></tr></thead>
                                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                {(form.roleHistory || []).sort((a,b) => new Date(b.validFrom) - new Date(a.validFrom)).map((h, i) => (
                                                    <tr key={i} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}>
                                                        <td className="p-2 font-medium">{h.role} {h.seniority} {h.level}</td>
                                                        <td className="p-2 text-green-500">{formatCurrency(h.salary)}</td>
                                                        <td className="p-2 opacity-80">{editingHistId === h.id ? (<div className="flex gap-1"><input type="date" className={`${themeStyles.input} py-0 h-6 text-[10px] w-20`} value={tempHistDates.validFrom} onChange={e=>setTempHistDates({...tempHistDates, validFrom:e.target.value})} /><input type="date" className={`${themeStyles.input} py-0 h-6 text-[10px] w-20`} value={tempHistDates.endDate} onChange={e=>setTempHistDates({...tempHistDates, endDate:e.target.value})} /></div>) : (<span>{formatMonthYear(h.validFrom)} {h.endDate ? `até ${formatMonthYear(h.endDate)}` : '(Atual)'}</span>)}</td>
                                                        <td className="p-2 text-center flex justify-center gap-2">{editingHistId === h.id ? (<button onClick={() => saveEditHistory(i)} className="text-green-500 hover:text-green-400"><CheckCircle size={14}/></button>) : (<button onClick={() => startEditHistory(h)} className="text-blue-500 hover:text-blue-400"><Edit3 size={14}/></button>)}<button type="button" onClick={() => deleteHistoryItem(i)} className="text-red-500 hover:text-red-400"><Trash2 size={14}/></button></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* === ABA 3: PONTO (NOVO) === */}
                        {collabTab === 'ponto' && (
                            <div className="space-y-4 animate-fade-in">
                                <div className={`p-4 rounded border ${themeStyles.subCard}`}>
                                    <h4 className={`font-bold text-sm border-b pb-2 mb-3 ${isDarkMode ? 'text-slate-300 border-slate-600' : 'text-gray-700 border-gray-200'}`}>Configuração de Jornada</h4>
                                    <div>
                                        <label className={themeStyles.label}>Regra Jornada de Trabalho</label>
                                        <select className={themeStyles.input} value={form.workRuleId || ''} onChange={e => setForm({...form, workRuleId: e.target.value})}>
                                            <option value="">Selecione...</option>
                                            <option value="padrao">Jornada Padrão (08:00 - 17:30)</option>
                                            {(data.pontoConfig && Array.isArray(data.pontoConfig)) && data.pontoConfig.map(r => (
                                                <option key={r.id} value={r.id}>{r.name} ({r.workHours})</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className={`p-4 rounded border ${themeStyles.subCard}`}>
                                    <h4 className={`font-bold text-sm border-b pb-2 mb-3 ${isDarkMode ? 'text-slate-300 border-slate-600' : 'text-gray-700 border-gray-200'}`}>Banco de Horas Inicial</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className={themeStyles.label}>Data Implantação Saldo</label>
                                            <input type="date" className={themeStyles.input} value={form.initialDate || ''} onChange={e=>setForm({...form, initialDate:e.target.value})} />
                                        </div>
                                        <div>
                                            <label className={themeStyles.label}>Saldo Inicial (Minutos)</label>
                                            <div className="flex gap-2">
                                                <input type="number" className={themeStyles.input} placeholder="Minutos (ex: 60)" value={form.initialBalanceMinutes || ''} onChange={e=>setForm({...form, initialBalanceMinutes: parseInt(e.target.value) || 0})} />
                                                <div className={`text-xs flex items-center ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                                    = {minutesToTimePonto(form.initialBalanceMinutes || 0)}h
                                                </div>
                                            </div>
                                            <p className="text-[10px] text-gray-400 mt-1">* Use negativo para saldo devedor.</p>
                                        </div>
                                    </div>
                                </div>

                                {/* NOVO BLOCO: EXCEÇÕES E PRIVILÉGIOS DE PONTO */}
                                <div className={`p-4 rounded border ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-orange-50/50 border-orange-200'}`}>
                                    <h4 className={`text-xs font-bold mb-3 uppercase tracking-wider ${isDarkMode ? 'text-orange-400' : 'text-orange-700'}`}>Exceções e Privilégios (Sobrepõe Regras Globais)</h4>
                                    <div className="space-y-3">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500" checked={form.skipPunchApproval || false} onChange={e => setForm({...form, skipPunchApproval: e.target.checked})} />
                                            <div>
                                                <span className={`text-sm font-bold block ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>Dispensar aprovação de marcações manuais</span>
                                                <span className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Qualquer ponto manual será aprovado automaticamente.</span>
                                            </div>
                                        </label>

                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500" checked={form.skipJustificationNotify || false} onChange={e => setForm({...form, skipJustificationNotify: e.target.checked})} />
                                            <div>
                                                <span className={`text-sm font-bold block ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>Não notificar justificativas</span>
                                                <span className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Inibe o envio de notificações para a Gestão/RH quando este usuário inserir justificativas.</span>
                                            </div>
                                        </label>

                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500" checked={form.skipDelayCritique || false} onChange={e => setForm({...form, skipDelayCritique: e.target.checked})} />
                                            <div>
                                                <span className={`text-sm font-bold block ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>Não criticar atrasos ou adiantos</span>
                                                <span className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Ignora os limites de tolerância para este colaborador (não gera pendências por horário).</span>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* === ABA 4: ACADCOINS === */}
                        {collabTab === 'acadcoins' && (
                            <div className="space-y-4 p-4 animate-fade-in">
                                <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                    <label className="flex items-center gap-3 font-bold text-sm cursor-pointer mb-4">
                                        <input 
                                            type="checkbox" 
                                            className="w-5 h-5 accent-blue-600 rounded"
                                            checked={form.participatesAcadCoins !== false} 
                                            onChange={e => setForm({...form, participatesAcadCoins: e.target.checked})} 
                                        />
                                        Colaborador Elegível ao Programa AcadCoins
                                    </label>
                                    
                                    {form.participatesAcadCoins !== false && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 pt-4 border-t dark:border-slate-700">
                                            <div>
                                                <label className={themeStyles.label}>Data de Início no Programa</label>
                                                <input 
                                                    type="date" 
                                                    value={form.acadCoinsStartDate || ''} 
                                                    onChange={e => setForm({...form, acadCoinsStartDate: e.target.value})} 
                                                    className={themeStyles.input}
                                                />
                                                <p className="text-[10px] text-red-500 font-bold mt-1 leading-tight">Obrigatório preencher para liberar saldo.</p>
                                            </div>
                                            <div>
                                                <label className={themeStyles.label}>Data Final no Programa</label>
                                                <input 
                                                    type="date" 
                                                    value={form.acadCoinsEndDate || ''} 
                                                    onChange={e => setForm({...form, acadCoinsEndDate: e.target.value})} 
                                                    className={themeStyles.input}
                                                />
                                                <p className="text-[10px] opacity-60 mt-1 leading-tight">Deixe em branco para manter elegível sempre.</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                    </div>
                );
            }

            // --- 5.5 ABA: PARÂMETROS GESTÃO ---
            if (tab === 'gestao') {
                return (
                    <div className="space-y-4">
                        <div><label className={themeStyles.label}>Nome do Indicador</label><input className={themeStyles.input} value={form.name||''} onChange={e=>setForm({...form, name:e.target.value})} /></div>
                        <div><label className={themeStyles.label}>Descrição Detalhada</label><textarea className={`${themeStyles.input} h-20 resize-none`} value={form.description||''} onChange={e=>setForm({...form, description:e.target.value})} /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className={themeStyles.label}>Tipo</label>
                                <select className={themeStyles.input} value={form.type||'Metas'} onChange={e=>setForm({...form, type:e.target.value})}>
                                    <option className={themeStyles.option} value="Metas">Metas</option>
                                    <option className={themeStyles.option} value="Percepção">Percepção</option>
                                </select>
                            </div>
                            <div><label className={themeStyles.label}>Pontuação Máxima (Ano)</label><input type="number" className={themeStyles.input} value={form.maxPoints||20} onChange={e=>setForm({...form, maxPoints:e.target.value})} /></div>
                        </div>
                    </div>
                );
            }

            // --- 5.6 ABA: CRITÉRIOS ---
            if (tab === 'criteria') {
                return (
                    <div className="space-y-4">
                        <div className={`flex gap-4 p-3 rounded border ${themeStyles.subCard}`}>
                            <label className={`flex items-center gap-2 cursor-pointer ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                                <input type="radio" className="text-blue-600 focus:ring-blue-500" checked={criteriaType==='quali'} onChange={()=>{ setCriteriaType('quali'); setForm(prev => ({...prev, dim: ''})); }} /> 
                                <span className="text-sm font-bold">Qualitativo (Competência)</span>
                            </label>
                            <label className={`flex items-center gap-2 cursor-pointer ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                                <input type="radio" className="text-blue-600 focus:ring-blue-500" checked={criteriaType==='quanti'} onChange={()=>{ setCriteriaType('quanti'); setForm(prev => ({...prev, dim: undefined})); }} /> 
                                <span className="text-sm font-bold">Quantitativo (Ocorrência)</span>
                            </label>
                        </div>
                    
                        <div><label className={themeStyles.label}>{criteriaType === 'quali' ? 'Nome da Competência' : 'Nome da Ocorrência'}</label><input className={themeStyles.input} value={form.name||''} onChange={e=>setForm({...form, name:e.target.value})} /></div>
                        
                        {criteriaType === 'quali' ? (
                            <>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><label className={themeStyles.label}>Dimensão</label><input className={themeStyles.input} value={form.dim||''} onChange={e=>setForm({...form, dim:e.target.value})} placeholder="Ex: Institucional"/></div>
                                    <div><label className={themeStyles.label}>Ordem</label><input type="number" className={themeStyles.input} value={form.order||''} onChange={e=>setForm({...form, order:e.target.value})} /></div>
                                </div>
                                <div><label className={themeStyles.label}>Descrição do Comportamento</label><textarea className={`${themeStyles.input} h-20 resize-none`} value={form.description||''} onChange={e=>setForm({...form, description:e.target.value})} /></div>
                            </>
                        ) : (
                            <div className="grid grid-cols-3 gap-4">
                                <div><label className={themeStyles.label}>Peso</label><input type="number" step="0.5" className={themeStyles.input} value={form.peso!==undefined?form.peso:''} onChange={e=>setForm({...form, peso:parseFloat(e.target.value)})} /></div>
                                <div>
                                    <label className={themeStyles.label}>Cor Fundo</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" className="h-10 w-12 p-0 border-0 rounded cursor-pointer bg-transparent" value={form.bgColor||'#ffffff'} onChange={e=>setForm({...form, bgColor:e.target.value})} />
                                        <input type="text" className={themeStyles.input} value={form.bgColor||'#ffffff'} onChange={e=>setForm({...form, bgColor:e.target.value})} />
                                    </div>
                                </div>
                                <div>
                                    <label className={themeStyles.label}>Cor Texto</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" className="h-10 w-12 p-0 border-0 rounded cursor-pointer bg-transparent" value={form.textColor||'#000000'} onChange={e=>setForm({...form, textColor:e.target.value})} />
                                        <input type="text" className={themeStyles.input} value={form.textColor||'#000000'} onChange={e=>setForm({...form, textColor:e.target.value})} />
                                    </div>
                                </div>
                            </div>
                        )}
                        <div>
                            <label className={`${themeStyles.label} mt-2`}>Aplica-se a:</label>
                            <select className={themeStyles.input} value={form.personType||'Colaborador'} onChange={e=>setForm({...form, personType:e.target.value})}>
                                <option className={themeStyles.option} value="Colaborador">Colaborador</option>
                                <option className={themeStyles.option} value="Gestão">Gestão</option>
                                <option className={themeStyles.option} value="Ambos">Ambos</option>
                            </select>
                        </div>
                    </div>
                );
            }
    // --- 5.X ABA: TREINAMENTO (EDIÇÃO) ---
            if (tab === 'training') {
                return (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className={themeStyles.label}>Email do Colaborador</label>
                                <input className={themeStyles.input} value={form.email||''} onChange={e=>setForm({...form, email:e.target.value})} disabled />
                            </div>
                            <div>
                                <label className={themeStyles.label}>Data</label>
                                <input className={themeStyles.input} value={form.date||''} onChange={e=>setForm({...form, date:e.target.value})} placeholder="DD/MM/AAAA" />
                            </div>
                            <div>
                                <label className={themeStyles.label}>Horas</label>
                                <input type="number" step="0.1" className={themeStyles.input} value={form.hours||''} onChange={e=>setForm({...form, hours:parseFloat(e.target.value)})} />
                            </div>
                            <div className="col-span-2">
                                <label className={themeStyles.label}>Descrição do Curso</label>
                                <input className={themeStyles.input} value={form.description||''} onChange={e=>setForm({...form, description:e.target.value})} />
                            </div>
                            <div>
                                <label className={themeStyles.label}>Tipo</label>
                                <input className={themeStyles.input} value={form.type||''} onChange={e=>setForm({...form, type:e.target.value})} placeholder="Ex: 1-Área de Atuação" />
                            </div>
                            <div>
                                <label className={themeStyles.label}>Ano Ref.</label>
                                <input className={themeStyles.input} value={form.year||''} onChange={e=>setForm({...form, year:e.target.value})} />
                            </div>
                            <div className="col-span-2">
                                <label className={themeStyles.label}>Link Resumo</label>
                                <input className={themeStyles.input} value={form.summaryLink||''} onChange={e=>setForm({...form, summaryLink:e.target.value})} />
                            </div>
                            <div className="col-span-2">
                                <label className={themeStyles.label}>Link Certificado</label>
                                <input className={themeStyles.input} value={form.certLink||''} onChange={e=>setForm({...form, certLink:e.target.value})} />
                            </div>
                        </div>
                    </div>
                );
            }
            // --- 5.7 ABAS GENÉRICAS / ESPECÍFICAS MENORES ---
            return (
                <div className="space-y-4">
                    
            {/* CAMPOS DE DADOS CADASTRAIS (REESTRUTURADO) */}
                    {tab !== 'types' && tab !== 'config' && tab !== 'messages' && tab !== 'competencias' && tab !== 'prazos' && tab !== 'minWage' && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-4">
                                <div className="col-span-1">
                                <label className={themeStyles.label}>Nome / Título</label>
                                    <input className={themeStyles.input} value={form.name||form.title||''} onChange={e=>setForm({...form, [form.title !== undefined ? 'title' : 'name']: e.target.value})} />
                                </div>
                                
                                    {/* Campo de ID Único (Não editável) */}
                                    <div className="col-span-1">
                                        <label className={themeStyles.label}>ID Único do Sistema</label>
                                        <input disabled className={`${themeStyles.input} opacity-60 bg-gray-100`} value={form.uniqueId || 'Gerado automaticamente'} />
                                    </div>
                                    {/* --- RESTAURAÇÃO: CAMPO EMAIL (APENAS PARA GESTOR) --- */}
                                {tab === 'manager' && (
                                    <div className="col-span-1">
                                        <label className={themeStyles.label}>Email Corporativo</label>
                                        <input 
                                            type="email" 
                                            className={themeStyles.input} 
                                            value={form.email || ''} 
                                            onChange={e => setForm({ ...form, email: e.target.value })} 
                                            placeholder="email@empresa.com"
                                        />
                                    </div>
                                )}

                                    {/* Upload de Assinatura apenas para Gestores */}
                                    {tab === 'manager' && (
                                        <div className="col-span-3">
                                            <label className={themeStyles.label}>Imagem da Assinatura (Para PDFs)</label>
                                            <div className="flex items-center gap-4">
                                                <input type="file" accept="image/*" className={themeStyles.input} onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (file) {
                                                        const reader = new FileReader();
                                                        reader.onloadend = () => setForm({ ...form, signatureImg: reader.result });
                                                        reader.readAsDataURL(file);
                                                    }
                                                }} />
                                                {form.signatureImg && <img src={form.signatureImg} alt="Preview" className="h-10 border rounded bg-white"/>}
                                            </div>
                                        </div>
                                    )}
                                
                                {/* CAMPO DEPARTAMENTO (AGORA VISÍVEL TAMBÉM PARA GESTORES) */}
                                {tab !== 'dept' && (
                                    <div className="col-span-1">
                                        <label className={themeStyles.label}>
                                            {tab === 'manager' ? 'Departamento Principal' : 'Departamento'}
                                        </label>
                                        <select 
                                            className={themeStyles.input} 
                                            value={form.departmentId||''} 
                                            onChange={e=>setForm({...form, departmentId:e.target.value})}
                                        >
                                            <option value="">Selecione...</option>
                                            {data.departments.map(d=><option key={d.id} value={d.id} className={isDarkMode?'bg-slate-800':''}>{d.name}</option>)}
                                        </select>
                                    </div>
                                )}
                            </div>

                            {/* WIDGET DE RATEIO PARA GESTORES (MOVIDO PARA LINHA PRÓPRIA) */}
                            {tab === 'manager' && (
                                <div className={`p-3 rounded border relative ${isDarkMode ? 'bg-orange-900/20 border-orange-800' : 'bg-orange-50 border-orange-200'}`}>
                                    <label className={`text-[10px] font-bold block mb-2 flex justify-between items-center ${isDarkMode ? 'text-orange-400' : 'text-orange-800'}`}>
                                        <span>Rateio de Custos (Centros de Custo)</span>
                                        <span className={(form.deptShare||[]).reduce((a,b)=>a+b.share,0) !== 100 ? "text-red-500" : "text-green-500"}>
                                            Total Alocado: {(form.deptShare||[]).reduce((a,b)=>a+b.share,0)}%
                                        </span>
                                    </label>
                                    
                                    <div className="flex gap-2 mb-3">
                                        <select className={`${themeStyles.input} py-1 text-xs`} value={tempDeptShare.deptId} onChange={e=>setTempDeptShare({...tempDeptShare, deptId:e.target.value})}>
                                            <option value="">+ Adicionar Depto no Rateio</option>
                                            {data.departments.filter(d => !(form.deptShare||[]).find(ds=>ds.deptId===d.id)).map(d=><option key={d.id} value={d.id} className={isDarkMode?'bg-slate-800':''}>{d.name}</option>)}
                                        </select>
                                        <input type="number" placeholder="%" className={`${themeStyles.input} py-1 w-20 text-xs`} value={tempDeptShare.share} onChange={e=>setTempDeptShare({...tempDeptShare, share:e.target.value})}/>
                                        <button onClick={addDeptShare} className="bg-orange-500 hover:bg-orange-600 text-white px-3 rounded text-xs font-bold transition-colors">Adicionar</button>
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                        {(form.deptShare || []).map((ds, idx) => (
                                            <div key={idx} className={`flex items-center gap-2 px-3 py-1.5 rounded border text-xs ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-orange-200 shadow-sm'}`}>
                                                <span className={`font-bold ${isDarkMode?'text-slate-300':'text-gray-700'}`}>
                                                    {data.departments.find(d=>d.id===ds.deptId)?.name}
                                                </span>
                                                <span className="font-bold text-orange-500 border-l pl-2 ml-1 border-gray-300 dark:border-slate-600">
                                                    {ds.share}%
                                                </span>
                                                <button onClick={()=>removeDeptShare(ds.deptId)} className="text-red-400 hover:text-red-600 ml-1">
                                                    <X size={12}/>
                                                </button>
                                            </div>
                                        ))}
                                        {(form.deptShare || []).length === 0 && (
                                            <span className="text-xs text-orange-400 italic opacity-70">Nenhum rateio definido (Será considerado 100% no Depto Principal).</span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Email + Datas para Dept e Manager */}
                        {(tab === 'dept' || tab === 'manager') && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {tab === 'dept' && (
                                    <div>
                                        <label className={themeStyles.label}>Email do Departamento</label>
                                        <input
                                            type="email"
                                            className={themeStyles.input}
                                            value={form.email || ''}
                                            onChange={e => setForm({ ...form, email: e.target.value })}
                                            placeholder="departamento@empresa.com"
                                        />
                                    </div>
                                )}

                                <div>
                                    <label className={themeStyles.label}>Início Vigência</label>
                                    <input
                                        type="date"
                                        className={themeStyles.input}
                                        value={form.startDate || ''}
                                        onChange={e => setForm({ ...form, startDate: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className={themeStyles.label}>Fim Vigência</label>
                                    <input
                                        type="date"
                                        className={themeStyles.input}
                                        value={form.endDate || ''}
                                        onChange={e => setForm({ ...form, endDate: e.target.value })}
                                    />
                                </div>
                            </div>
                        )}

                    {/* TIPOS DE AVALIAÇÃO e CONFIG */}
                    {(tab === 'types' || tab === 'config') && (
                        <div className="space-y-3">
                            <div className="mb-2"><label className={themeStyles.label}>Nome da Configuração</label><input className={themeStyles.input} value={form.name||''} onChange={e=>setForm({...form, name:e.target.value})} placeholder="Ex: Padrão 2025" /></div>
                            
                            <div className={`grid grid-cols-2 gap-4 p-3 rounded border mb-2 ${isDarkMode ? 'bg-indigo-900/20 border-indigo-800' : 'bg-indigo-50 border-indigo-100'}`}>
                                <div>
                                    <label className={themeStyles.label}>Público Alvo:</label>
                                    <select className={themeStyles.input} value={form.personType||'Colaborador'} onChange={e => { 
                                        const newVal = e.target.value;
                                        const newIds = newVal === 'Gestão' ? [] : (form.selectedQuantiIds || []); 
                                        setForm({...form, personType: newVal, selectedQuantiIds: newIds});
                                    }}>
                                        <option className={themeStyles.option} value="Colaborador">Colaborador</option>
                                        <option className={themeStyles.option} value="Gestão">Gestão</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={themeStyles.label}>Restringir ao Depto:</label>
                                    <select className={themeStyles.input} value={form.departmentId||''} onChange={e=>setForm({...form, departmentId:e.target.value})}>
                                        <option className={themeStyles.option} value="">Geral (Todos)</option>
                                        {data.departments.map(d=><option key={d.id} value={d.id} className={themeStyles.option}>{d.name}</option>)}
                                    </select>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className={themeStyles.label}>Início Vigência</label><input type="date" className={`${themeStyles.input} ${themeStyles.calendar}`} value={form.startDate||''} onChange={e=>setForm({...form, startDate:e.target.value})} /></div>
                                <div><label className={themeStyles.label}>Fim Vigência</label><input type="date" className={`${themeStyles.input} ${themeStyles.calendar}`} value={form.endDate||''} onChange={e=>setForm({...form, endDate:e.target.value})} /></div>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div><label className={themeStyles.label}>Média Promoção</label><input type="number" step="0.1" className={themeStyles.input} value={form.mediaPromocao||''} onChange={e=>setForm({...form, mediaPromocao:parseFloat(e.target.value)})} /></div>
                                <div><label className={themeStyles.label}>Média Premiação</label><input type="number" step="0.1" className={themeStyles.input} value={form.mediaPremiacao||''} onChange={e=>setForm({...form, mediaPremiacao:parseFloat(e.target.value)})} /></div>
                                <div><label className={themeStyles.label}>Máx Pts Perdidos</label><input type="number" step="0.1" className={themeStyles.input} value={form.maxPointsLost||''} onChange={e=>setForm({...form, maxPointsLost:parseFloat(e.target.value)})} /></div>
                            </div>
        
                            {tab === 'types' && (
                                <div className="grid grid-cols-2 gap-4 mt-2">
                                    {/* Seletor Qualitativo */}
                                        <div className={`flex flex-col h-64 border rounded overflow-hidden ${themeStyles.subCard}`}>
                                            <div className={`p-2 border-b flex flex-col gap-2 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-100 border-gray-200'}`}>
                                                <label className={themeStyles.label}>Critérios Qualitativos</label>
                                                <div className="relative"><Search className="absolute left-2 top-1.5 text-gray-400" size={12}/><input className={`${themeStyles.input} pl-6 py-1 text-xs`} placeholder="Buscar..." value={searchQuali} onChange={e=>setSearchQuali(e.target.value)}/></div>
                                            </div>
                                            <div className="overflow-y-auto p-2 flex-1">
                                                <CheckboxList 
                                                    items={data.qualiCriteria.filter(i => {
                                                        // 1. Pega o tipo selecionado no formulário atual (Padrão: Colaborador)
                                                        const currentTarget = form.personType || 'Colaborador';
                                                        
                                                        // 2. Verifica compatibilidade
                                                        // CORREÇÃO: Aceita se for igual, se for 'Ambos' OU se estiver indefinido/vazio (erro legado)
                                                        const typeMatch = !i.personType || i.personType === currentTarget || i.personType === 'Ambos';
                                                        
                                                        // 3. Filtro de texto
                                                        const nameMatch = i.name.toLowerCase().includes(searchQuali.toLowerCase());
                                                        
                                                        return typeMatch && nameMatch;
                                                    })} 
                                                    selectedIds={form.selectedQualiIds || []} 
                                                    onChange={(ids)=>setForm({...form, selectedQualiIds: ids})} 
                                                    isDarkMode={isDarkMode} 
                                                />
                                            </div>
                                        </div>
                                    {/* Seletor Quantitativo */}
                                    <div className={`flex flex-col h-64 border rounded overflow-hidden ${themeStyles.subCard} ${form.personType === 'Gestão' ? 'opacity-50 pointer-events-none' : ''}`}>
                                        <div className={`p-2 border-b flex flex-col gap-2 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-100 border-gray-200'}`}>
                                            <label className={themeStyles.label}>Critérios Quantitativos</label>
                                            <div className="relative"><Search className="absolute left-2 top-1.5 text-gray-400" size={12}/><input className={`${themeStyles.input} pl-6 py-1 text-xs`} placeholder="Buscar..." value={searchQuanti} onChange={e=>setSearchQuanti(e.target.value)} disabled={form.personType === 'Gestão'}/></div>
                                        </div>
                                        <div className="overflow-y-auto p-2 flex-1">
                                            <CheckboxList 
                                                items={(data.quantiCriteria || []).filter(i => {
                                                    // 1. Pega o tipo selecionado (Padrão: Colaborador)
                                                    const currentTarget = form.personType || 'Colaborador';
                                                    
                                                    // 2. Proteção contra registros legados (aceita se vazio, igual ao alvo ou Ambos)
                                                    const typeMatch = !i.personType || i.personType === currentTarget || i.personType === 'Ambos';
                                                    
                                                    // 3. Proteção no filtro de texto (evita erro toLowerCase of undefined)
                                                    const nameMatch = (i.name || '').toLowerCase().includes((searchQuanti || '').toLowerCase());
                                                    
                                                    return typeMatch && nameMatch;
                                                })} 
                                                selectedIds={form.selectedQuantiIds || []} 
                                                onChange={(ids)=>setForm({...form, selectedQuantiIds: ids})} 
                                                isDarkMode={isDarkMode} 
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

    {/* MENSAGENS PDF (ATUALIZADO) */}
                    {tab === 'messages' && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={themeStyles.label}>Tipo de Resultado</label>
                                    <select className={themeStyles.input} value={form.type||'Promoção'} onChange={e=>setForm({...form, type:e.target.value})}>
                                        <option className={themeStyles.option}>Promoção</option>
                                        <option className={themeStyles.option}>Premiação</option>
                                        <option className={themeStyles.option} value="Manutenção">Manutenção</option>
                                        <option className={themeStyles.option} value="Recuperação">Recuperação</option>
                                        <option className={themeStyles.option} value="Desligamento">Desligamento</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={themeStyles.label}>Público Alvo</label>
                                    <select className={themeStyles.input} value={form.personType||'Colaborador'} onChange={e=>setForm({...form, personType:e.target.value})}>
                                        <option className={themeStyles.option}>Colaborador</option>
                                        <option className={themeStyles.option}>Gestão</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className={themeStyles.label}>Depto Específico</label>
                                <select className={themeStyles.input} value={form.departmentId||'all'} onChange={e=>setForm({...form, departmentId:e.target.value})}>
                                    <option className={themeStyles.option} value="all">Todos (Padrão)</option>
                                    {data.departments.map(d=><option key={d.id} value={d.id} className={themeStyles.option}>{d.name}</option>)}
                                </select>
                            </div>
                            <div><label className={themeStyles.label}>Título</label><input className={themeStyles.input} value={form.title||''} onChange={e=>setForm({...form, title:e.target.value})} placeholder="EX: PARABÉNS!" /></div>
                            
                            {/* --- QUADRO DE VARIÁVEIS (EVIDENCIADO) --- */}
                            <div className={`p-3 rounded-lg border text-xs mb-1 ${isDarkMode ? 'bg-blue-900/30 border-blue-800 text-blue-200' : 'bg-blue-50 border-blue-100 text-blue-800'}`}>
                                <div className="flex items-center gap-2 mb-2 font-bold uppercase tracking-wider">
                                    <Info size={14}/> Variáveis Disponíveis para o Texto:
                                </div>
                                <div className="grid grid-cols-2 gap-2 font-mono text-[11px]">
                                    <span className="bg-white/50 px-1 rounded dark:bg-black/30">{`{colaborador}`} <span className="opacity-60 font-sans">- Nome do Func.</span></span>
                                    <span className="bg-white/50 px-1 rounded dark:bg-black/30">{`{gestor}`} <span className="opacity-60 font-sans">- Nome do Gestor</span></span>
                                    <span className="bg-white/50 px-1 rounded dark:bg-black/30">{`{AnoAtual}`} <span className="opacity-60 font-sans">- Ano Avaliado</span></span>
                                    <span className="bg-white/50 px-1 rounded dark:bg-black/30">{`{AnoSeg}`} <span className="opacity-60 font-sans">- Próximo Ano</span></span>
                                </div>
                            </div>

                            <div>
                                <label className={themeStyles.label}>Mensagem</label>
                                <textarea 
                                    className={`${themeStyles.input} h-24 custom-scrollbar`} 
                                    value={form.body||''} 
                                    onChange={e=>setForm({...form, body:e.target.value})}
                                    placeholder="Digite a mensagem utilizando as variáveis acima..."
                                ></textarea>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className={themeStyles.label}>Cor Fundo (Hex)</label><div className="flex gap-2"><input type="color" className="w-10 h-10 border-0 rounded cursor-pointer" value={form.bgColor||'#ffffff'} onChange={e=>setForm({...form, bgColor:e.target.value})} /><input className={themeStyles.input} value={form.bgColor||'#ffffff'} onChange={e=>setForm({...form, bgColor:e.target.value})} /></div></div>
                                <div><label className={themeStyles.label}>Cor Texto (Hex)</label><div className="flex gap-2"><input type="color" className="w-10 h-10 border-0 rounded cursor-pointer" value={form.textColor||'#000000'} onChange={e=>setForm({...form, textColor:e.target.value})} /><input className={themeStyles.input} value={form.textColor||'#000000'} onChange={e=>setForm({...form, textColor:e.target.value})} /></div></div>
                            </div>
                        </div>
                    )}
                    
                    {/* COMPETÊNCIAS */}
                    {tab === 'competencias' && (
                        <div className="space-y-3">
                            <div><label className={themeStyles.label}>Competência (Nome)</label><input className={themeStyles.input} value={form.name||''} onChange={e=>setForm({...form, name:e.target.value})} /></div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={themeStyles.label}>Departamento</label>
                                    <select className={themeStyles.input} value={form.departmentId||''} onChange={e=>setForm({...form, departmentId:e.target.value})}>
                                        <option value="">Todos</option>
                                        {data.departments.map(d=><option key={d.id} value={d.id} className={themeStyles.option}>{d.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={themeStyles.label}>Tipo de Público</label>
                                    <select className={themeStyles.input} value={form.personType||'Todos'} onChange={e=>setForm({...form, personType:e.target.value})}>
                                        <option className={themeStyles.option} value="Todos">Todos</option>
                                        <option className={themeStyles.option} value="Colaborador">Colaborador</option>
                                        <option className={themeStyles.option} value="Gestão">Gestão</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className={themeStyles.label}>Início Vigência</label><input type="date" className={`${themeStyles.input} ${themeStyles.calendar}`} value={form.startDate||''} onChange={e=>setForm({...form, startDate:e.target.value})} /></div>
                                <div><label className={themeStyles.label}>Fim Vigência</label><input type="date" className={`${themeStyles.input} ${themeStyles.calendar}`} value={form.endDate||''} onChange={e=>setForm({...form, endDate:e.target.value})} /></div>
                            </div>
                            <div className={`mt-2 border rounded h-64 flex flex-col overflow-hidden ${themeStyles.subCard}`}>
                                <div className={`p-3 border-b shrink-0 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-100 border-gray-200'}`}>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className={themeStyles.label}>Vincular aos Cargos</label>
                                        <div className="relative"><Search className="absolute left-2 top-1.5 text-gray-400" size={12}/><input className={`${themeStyles.input} pl-6 py-1 text-xs w-40`} placeholder="Buscar cargo..." value={roleSearch} onChange={e => setRoleSearch(e.target.value)}/></div>
                                    </div>
                                    <div className="flex gap-3">
                                        <button type="button" onClick={() => setForm({...form, roleIds: uniqueRoles})} className="text-[10px] font-bold text-blue-600 hover:underline">Marcar Todos</button>
                                        <button type="button" onClick={() => setForm({...form, roleIds: []})} className="text-[10px] font-bold text-red-500 hover:underline">Desmarcar Todos</button>
                                    </div>
                                </div>
                                <div className="overflow-y-auto flex-1 p-2">
                                    <CheckboxList 
                                        items={uniqueRoles.filter(r => r.toLowerCase().includes(roleSearch.toLowerCase())).map(r => ({id: r, name: r}))} 
                                        selectedIds={form.roleIds || []} 
                                        onChange={(ids)=>setForm({...form, roleIds: ids})} 
                                        isDarkMode={isDarkMode} 
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* PRAZOS */}
                    {tab === 'prazos' && (
                        <div className="space-y-3">
                            <div><label className={themeStyles.label}>Descrição da Regra</label><input className={themeStyles.input} placeholder="Ex: Prazos Fiscais" value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} /></div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={themeStyles.label}>Departamento</label>
                                    <select className={themeStyles.input} value={form.departmentId || ''} onChange={e=>setForm({...form, departmentId:e.target.value})}>
                                        <option value="">Aplicar a Todos</option>
                                        {data.departments.map(d=><option key={d.id} value={d.id} className={themeStyles.option}>{d.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={themeStyles.label}>Tipo de Pessoa</label>
                                    <select className={themeStyles.input} value={form.personType || 'Colaborador'} onChange={e=>setForm({...form, personType:e.target.value})}>
                                        <option className={themeStyles.option} value="Colaborador">Colaborador</option>
                                        <option className={themeStyles.option} value="Gestão">Gestão</option>
                                    </select>
                                </div>
                            </div>
                            <div className={`grid grid-cols-2 gap-3 pt-2 p-3 rounded border ${isDarkMode ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-100'}`}>
                                <div><label className={themeStyles.label}>1º Trim</label><input className={`${themeStyles.input} text-center`} placeholder="DD/MM" value={form.q1 || ''} onChange={e=>setForm({...form, q1:e.target.value})} maxLength={5}/></div>
                                <div><label className={themeStyles.label}>2º Trim</label><input className={`${themeStyles.input} text-center`} placeholder="DD/MM" value={form.q2 || ''} onChange={e=>setForm({...form, q2:e.target.value})} maxLength={5}/></div>
                                <div><label className={themeStyles.label}>3º Trim</label><input className={`${themeStyles.input} text-center`} placeholder="DD/MM" value={form.q3 || ''} onChange={e=>setForm({...form, q3:e.target.value})} maxLength={5}/></div>
                                <div><label className={themeStyles.label}>4º Trim</label><input className={`${themeStyles.input} text-center`} placeholder="DD/MM" value={form.q4 || ''} onChange={e=>setForm({...form, q4:e.target.value})} maxLength={5}/></div>
                            </div>
                        </div>
                    )}

                    {/* SALÁRIO MÍNIMO */}
                    {tab === 'minWage' && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className={themeStyles.label}>Valor (R$)</label><input type="number" step="0.01" className={themeStyles.input} value={form.value||''} onChange={e=>setForm({...form, value:e.target.value})} /></div>
                                <div><label className={themeStyles.label}>Vigência a partir de</label><input type="date" className={themeStyles.input} value={form.validFrom||''} onChange={e=>setForm({...form, validFrom:e.target.value})} /></div>
                            </div>
                        </div>
                    )}
            </div>
            );
        };

        // ========================================================================
        // 6. RENDERIZAÇÃO PRINCIPAL (MAIN RENDER)
        // ========================================================================
        return (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm animate-fade-in">
                <div className={`p-6 rounded-xl w-full max-w-4xl shadow-2xl max-h-[90vh] overflow-y-auto ${themeStyles.modalBase}`}>
                    
                    {/* Header */}
                    <div className={`flex justify-between items-center mb-4 border-b pb-2 sticky top-0 z-10 ${isDarkMode ? 'bg-slate-800' : 'bg-white'} ${themeStyles.headerBorder}`}>
                        <h3 className="font-bold text-lg capitalize flex items-center gap-2">
                            {form.id ? <><Edit3 size={20}/> Editar Registro</> : <><Plus size={20}/> Novo Registro</>}
                        </h3>
                        <button onClick={onClose} className={`p-1 rounded-full ${isDarkMode ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}>
                            <X size={20}/>
                        </button>
                    </div>
                    
                    {/* Body (Campos) */}
                    <div className="mb-6">
                        {renderFields()}
                    </div>

                    {/* Footer (Ações) */}
                    <div className={`flex justify-end gap-2 pt-4 border-t sticky bottom-0 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                        <Button variant="secondary" onClick={onClose} className={isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600' : ''}>Cancelar</Button>
                        <Button onClick={tab === 'users' ? handleSaveUser : handleSave} icon={Save}>Salvar</Button>
                    </div>
                </div>
            </div>
        );
    };

    // --- COMPONENTE DE AUDITORIA (LAYOUT TABELA DETALHADA) ---


    /* ===============================================================================
     * [END_MOD_CADASTRO_MODAL]
     * FIM DO BLOCO: CadastroModal
     * [START_MOD_AUDIT_LOG_TAB]
     * INICIO DO BLOCO: AuditLogTab
     * =============================================================================== */
    const AuditLogTab = ({ data, isDarkMode }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const logs = data.auditLogs || [];

    const parseDetails = (raw) => {
        if (!raw) return [];

        try {
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

            if (Array.isArray(parsed)) {
                return parsed.map(item => ({
                    campo: item?.campo || item?.field || 'N/D',
                    de: item?.de ?? item?.from ?? '',
                    para: item?.para ?? item?.to ?? ''
                }));
            }

            if (parsed && typeof parsed === 'object') {
                    if ('campo' in parsed || 'de' in parsed || 'para' in parsed) {
                        return [{
                            campo: parsed?.campo || 'N/D',
                            de: parsed?.de ?? '',
                            para: parsed?.para ?? ''
                        }];
                    }

                    return Object.entries(parsed).map(([campo, valor]) => ({
                        campo,
                        de: '',
                        para: typeof valor === 'object' ? JSON.stringify(valor) : String(valor ?? '')
                    }));
                }

            return [{ campo: 'mensagem', de: '', para: String(parsed) }];
        } catch {
            return [{ campo: 'mensagem', de: '', para: String(raw) }];
        }
    };

    const filteredLogs = logs
        .filter(log => {
            const s = searchTerm.toLowerCase();
            const detailsText = parseDetails(log.details)
                .map(item => `${item.campo} ${item.de} ${item.para}`)
                .join(' ')
                .toLowerCase();

            return (
                (log.userName || '').toLowerCase().includes(s) ||
                (log.userId || '').toLowerCase().includes(s) ||
                (log.userRole || '').toLowerCase().includes(s) ||
                (log.action || '').toLowerCase().includes(s) ||
                (log.entity || '').toLowerCase().includes(s) ||
                (log.recordId || '').toLowerCase().includes(s) ||
                detailsText.includes(s)
            );
        })
        .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

    const getActionStyle = (action) => {
        const act = (action || '').toUpperCase();
        if (act.includes('DELETE') || act.includes('EXCLU') || act.includes('REVOGAR')) {
            return isDarkMode ? 'bg-red-900/40 text-red-300 border-red-800' : 'bg-red-50 text-red-700 border-red-200';
        }
        if (act.includes('EDIT') || act.includes('ALTER') || act.includes('ATUALIZ') || act.includes('EDICAO')) {
            return isDarkMode ? 'bg-orange-900/40 text-orange-300 border-orange-800' : 'bg-orange-50 text-orange-700 border-orange-200';
        }
        if (act.includes('CRI') || act.includes('NOVO') || act.includes('ADD') || act.includes('CONCEDER')) {
            return isDarkMode ? 'bg-green-900/40 text-green-300 border-green-800' : 'bg-green-50 text-green-700 border-green-200';
        }
        return isDarkMode ? 'bg-blue-900/40 text-blue-300 border-blue-800' : 'bg-blue-50 text-blue-700 border-blue-200';
    };

    const formatDateTime = (log) => {
        try {
            return new Date(log.date || log.timestamp).toLocaleString('pt-BR');
        } catch {
            return '-';
        }
    };

    const renderDetails = (raw) => {
        const rows = parseDetails(raw);

        if (!rows.length) {
            return <span className="text-xs opacity-60">Sem detalhes</span>;
        }

        return (
            <div className="space-y-2">
                {rows.map((item, idx) => (
                    <div
                        key={idx}
                        className={`rounded border p-2 text-[11px] ${
                            isDarkMode
                                ? 'bg-slate-900 border-slate-700 text-slate-200'
                                : 'bg-gray-50 border-gray-200 text-gray-800'
                        }`}
                    >
                        <div><b>Campo:</b> {item.campo || '-'}</div>
                        <div><b>De:</b> {String(item.de || '-')}</div>
                        <div><b>Para:</b> {String(item.para || '-')}</div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="space-y-4 h-full flex flex-col animate-fade-in">
                {/* Header / Filtro */}
                <div className={`flex justify-between items-center p-2 rounded-lg border shrink-0 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'}`}>
                    <div className="flex items-center gap-2">
                        <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-50 text-purple-600'}`}>
                            <Shield size={18}/>
                        </div>
                        <div>
                            <h4 className={`font-bold text-sm ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>Auditoria do Sistema</h4>
                            <p className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>Rastreabilidade completa de eventos.</p>
                        </div>
                    </div>
                    <div className="relative w-64">
                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} size={14} />
                        <Input 
                            placeholder="Buscar em logs..." 
                            value={searchTerm} 
                            onChange={e => setSearchTerm(e.target.value)} 
                            className={`pl-9 h-9 text-xs ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-500' : 'bg-gray-50 border-gray-200'}`}
                        />
                    </div>
                </div>

                {/* Tabela Reformulada */}
                <div className={`rounded-xl shadow-sm border flex-1 overflow-hidden flex flex-col ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                    <div className="overflow-y-auto flex-1 custom-scrollbar">
                        <table className={`w-full text-xs text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                            <thead className={isDarkMode ? 'bg-slate-900 text-slate-300' : 'bg-gray-100 text-gray-700'}>
                                <tr>
                                    <th className="p-3 text-left">Data</th>
                                    <th className="p-3 text-left">Ação</th>
                                    <th className="p-3 text-left">Entidade</th>
                                    <th className="p-3 text-left">Registro</th>
                                    <th className="p-3 text-left">Usuário</th>
                                    <th className="p-3 text-left">Perfil</th>
                                    <th className="p-3 text-left min-w-[320px]">Detalhes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredLogs.map((log) => (
                                    <tr key={log.id} className={isDarkMode ? 'border-t border-slate-700' : 'border-t border-gray-200'}>
                                        <td className="p-3 whitespace-nowrap">{formatDateTime(log)}</td>
                                        <td className="p-3">
                                            <span className={`px-2 py-1 rounded border text-[10px] font-bold ${getActionStyle(log.action)}`}>
                                                {log.action || '-'}
                                            </span>
                                        </td>
                                        <td className="p-3">{log.entity || '-'}</td>
                                        <td className="p-3 font-mono text-[11px]">{log.recordId || '-'}</td>
                                        <td className="p-3">
                                            <div className="font-semibold">{log.userName || '-'}</div>
                                            <div className="opacity-70 text-[10px]">{log.userId || '-'}</div>
                                        </td>
                                        <td className="p-3">{log.userRole || '-'}</td>
                                        <td className="p-3">{renderDetails(log.details)}</td>
                                    </tr>
                                ))}

                                {filteredLogs.length === 0 && (
                                    <tr>
                                        <td colSpan="7" className="p-6 text-center opacity-60 italic">
                                            Nenhum log encontrado.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };


    /**
     * MODAL: CONFIGURAÇÃO DE PRAZOS E PERGUNTAS (PERCEPÇÃO)
     * Apenas SuperAdm. Gerencia calendário e questionário.
     */


    /* ===============================================================================
     * [END_MOD_AUDIT_LOG_TAB]
     * FIM DO BLOCO: AuditLogTab
     * [START_MOD_PERCEPTION_CONFIG_MODAL]
     * INICIO DO BLOCO: PerceptionConfigModal
     * =============================================================================== */
    const PerceptionConfigModal = ({ isOpen, onClose, config, questions, onSaveConfig, onSaveQuestions, isDarkMode }) => {
        const [activeTab, setActiveTab] = React.useState('prazos');
        const [dates, setDates] = React.useState({
            overrideStart: config?.overrideStart || '',
            overrideEnd: config?.overrideEnd || ''
        });

        // Estado local para gerenciar as perguntas antes de salvar
        const [localQuestions, setLocalQuestions] = React.useState([]);

        // Carrega perguntas existentes ao abrir
        React.useEffect(() => {
            if (isOpen) {
                setLocalQuestions((questions && questions.length > 0) ? questions : []);
            }
        }, [questions, isOpen]);

    // --- FUNÇÃO DE IMPORTAR PADRÃO (BASEADA NO PDF ENVIADO) ---
        const handleImportDefault = () => {
            // Perguntas extraídas do "Formulário Permanente de Desempenho de Gestores"
            const HARDCODED_QUESTIONS = [
                { 
                    id: 'pq1', 
                    order: 1, 
                    text: "O(a) Gestor(a) toma decisões oportunas: lida com problemas urgentes e importantes sem demora?", 
                    type: 'scale', 
                    startDate: '2025-01-01', 
                    endDate: '', 
                    personType: 'Gestão' 
                },
                { 
                    id: 'pq2', 
                    order: 2, 
                    text: "O(a) Gestor(a) compartilha seu conhecimento e experiência com outros, participa ativamente de reuniões e discussões?", 
                    type: 'scale', 
                    startDate: '2025-01-01', 
                    endDate: '', 
                    personType: 'Gestão' 
                },
                { 
                    id: 'pq3', 
                    order: 3, 
                    text: "O(a) Gestor(a) permanece objetivo(a) e imparcial quando lida com conflitos, procura soluções ganha-ganha quando há desentendimentos?", 
                    type: 'scale', 
                    startDate: '2025-01-01', 
                    endDate: '', 
                    personType: 'Gestão' 
                },
                { 
                    id: 'pq4', 
                    order: 4, 
                    text: "O(a) Gestor(a) planeja e organiza as tarefas para cumprir prazos, respeita e mantém padrão de segurança, assume responsabilidade por erros/enganos e protege a propriedade intelectual da empresa?", 
                    type: 'scale', 
                    startDate: '2025-01-01', 
                    endDate: '', 
                    personType: 'Gestão' 
                },
                { 
                    id: 'pq5', 
                    order: 5, 
                    text: "O(a) Gestor(a) trabalha de forma eficaz com membros da equipe para assegurar que as necessidades dos clientes sejam atendidas com qualidade?", 
                    type: 'scale', 
                    startDate: '2025-01-01', 
                    endDate: '', 
                    personType: 'Gestão' 
                },
                { 
                    id: 'pq6', 
                    order: 6, 
                    text: "O(a) Gestor(a) encoraja outros a desafiar as mudanças/inovações e práticas do dia-a-dia de modo construtivo; ajuda os outros a administrar as suas reações a mudanças feitas na empresa?", 
                    type: 'scale', 
                    startDate: '2025-01-01', 
                    endDate: '', 
                    personType: 'Gestão' 
                },
                { 
                    id: 'pq7', 
                    order: 7, 
                    text: "Utilize o espaço a seguir para sugerir, bem como para realizar as considerações que achar necessárias em relação ao seu(sua) Gestor(a):", 
                    type: 'text', 
                    startDate: '2025-01-01', 
                    endDate: '', 
                    personType: 'Gestão' 
                }
            ];

            // Define o estado com as perguntas extraídas
            setLocalQuestions(HARDCODED_QUESTIONS);
            
            // Feedback visual simples (já que alert/confirm podem ser bloqueados)
            console.log("Perguntas do formulário oficial importadas com sucesso.");
        };

        const handleAddQuestion = () => {
            const newId = Date.now().toString();
            const newOrder = localQuestions.length + 1;
            setLocalQuestions([...localQuestions, { 
                id: newId, 
                order: newOrder, 
                text: '', 
                type: 'scale', 
                startDate: new Date().toISOString().split('T')[0], 
                endDate: '',
                personType: 'Gestão'
            }]);
        };

        const updateQuestion = (id, field, value) => {
            setLocalQuestions(prev => prev.map(q => q.id === id ? { ...q, [field]: value } : q));
        };

        const removeQuestion = (id) => {
            if(window.confirm("Excluir esta pergunta?")) {
                setLocalQuestions(prev => prev.filter(q => q.id !== id));
            }
        };

        const handleSaveAll = () => {
            onSaveConfig(dates);
            onSaveQuestions(localQuestions);
            onClose();
            // Pequeno delay para garantir que o modal feche antes do alert
            setTimeout(() => alert("Configurações salvas com sucesso!"), 300);
        };

        if (!isOpen) return null;

        return (
            <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={onClose}>
                <div className={`rounded-xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                    
                    {/* Header */}
                    <div className={`flex justify-between items-center p-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                        <h3 className="font-bold text-lg flex items-center gap-2"><Settings size={18}/> Configuração: Avaliação de Gestão</h3>
                        <button onClick={onClose}><X size={20}/></button>
                    </div>

                    {/* Abas */}
                    <div className={`flex border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                        <button onClick={() => setActiveTab('prazos')} className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'prazos' ? 'border-purple-500 text-purple-500' : 'border-transparent opacity-60'}`}>Prazos e Datas</button>
                        <button onClick={() => setActiveTab('perguntas')} className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'perguntas' ? 'border-purple-500 text-purple-500' : 'border-transparent opacity-60'}`}>Questionário</button>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                        {/* ABA PRAZOS */}
                        {activeTab === 'prazos' && (
                            <div className="space-y-4 max-w-md mx-auto">
                                <div className={`text-xs p-4 rounded border ${isDarkMode ? 'bg-blue-900/20 border-blue-800 text-blue-200' : 'bg-blue-50 border-blue-100 text-blue-800'}`}>
                                    <p className="font-bold mb-2 text-sm flex items-center gap-2"><Info size={16}/> Regra Automática:</p>
                                    <p>O formulário abre automaticamente entre os dias <b>01 e 10</b> dos meses seguintes ao fechamento do trimestre:</p>
                                    <ul className="list-disc pl-5 mt-2 space-y-1">
                                        <li><b>Janeiro</b> (ref. Q4 anterior)</li>
                                        <li><b>Abril</b> (ref. Q1)</li>
                                        <li><b>Julho</b> (ref. Q2)</li>
                                        <li><b>Outubro</b> (ref. Q3)</li>
                                    </ul>
                                </div>

                                <div className={`p-4 rounded border ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                    <h4 className="font-bold text-sm mb-3">Reabertura Manual (Exceção)</h4>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="text-xs font-bold block mb-1">Início Exceção</label>
                                            <input type="date" className={`w-full p-2 border rounded text-sm ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white invert-calendar' : ''}`} value={dates.overrideStart} onChange={e=>setDates({...dates, overrideStart: e.target.value})} />
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold block mb-1">Fim Exceção</label>
                                            <input type="date" className={`w-full p-2 border rounded text-sm ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white invert-calendar' : ''}`} value={dates.overrideEnd} onChange={e=>setDates({...dates, overrideEnd: e.target.value})} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ABA PERGUNTAS */}
                        {activeTab === 'perguntas' && (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm font-bold">Total: {localQuestions.length} perguntas</span>
                                    <div className="flex gap-2">
                                        <Button variant="secondary" onClick={handleImportDefault} className="text-xs" icon={FileText}>Importar Padrão (15 Itens)</Button>
                                        <Button onClick={handleAddQuestion} className="text-xs bg-purple-600 hover:bg-purple-700 text-white" icon={Plus}>Nova Pergunta</Button>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    {localQuestions.sort((a,b) => a.order - b.order).map((q, idx) => (
                                        <div key={q.id} className={`p-3 rounded border flex flex-col gap-2 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                            <div className="flex justify-between items-start gap-3">
                                                <span className="font-bold text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded h-fit">#{q.order}</span>
                                                
                                                <div className="flex-1 space-y-2">
                                                    <input 
                                                        className={`w-full p-1.5 border rounded text-sm font-medium ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-300'}`} 
                                                        value={q.text} 
                                                        onChange={e => updateQuestion(q.id, 'text', e.target.value)}
                                                        placeholder="Digite a pergunta..."
                                                    />
                                                    
                                                    {/* Detalhes da Pergunta */}
                                                    <div className="flex flex-wrap gap-2 items-center">
                                                        <select 
                                                            className={`text-xs p-1 border rounded ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white'}`}
                                                            value={q.type}
                                                            onChange={e => updateQuestion(q.id, 'type', e.target.value)}
                                                        >
                                                            <option value="scale">Escala 1-5 (Com justificativa)</option>
                                                            <option value="text">Texto Aberto</option>
                                                        </select>

                                                        <select 
                                                            className={`text-xs p-1 border rounded ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white'}`}
                                                            value={q.personType}
                                                            onChange={e => updateQuestion(q.id, 'personType', e.target.value)}
                                                        >
                                                            <option value="Gestão">Gestão</option>
                                                            <option value="Colaborador">Colaborador</option>
                                                            <option value="Todos">Todos</option>
                                                        </select>
                                                        
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-[10px] uppercase font-bold text-gray-500">Vigência:</span>
                                                            <input type="date" className={`text-xs p-1 border rounded w-24 ${isDarkMode ? 'bg-slate-800 border-slate-600 invert-calendar' : ''}`} value={q.startDate} onChange={e => updateQuestion(q.id, 'startDate', e.target.value)} />
                                                            <span className="text-xs text-gray-400">até</span>
                                                            <input type="date" className={`text-xs p-1 border rounded w-24 ${isDarkMode ? 'bg-slate-800 border-slate-600 invert-calendar' : ''}`} value={q.endDate} onChange={e => updateQuestion(q.id, 'endDate', e.target.value)} />
                                                        </div>
                                                    </div>
                                                </div>

                                                <button onClick={() => removeQuestion(q.id)} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={16}/></button>
                                            </div>
                                        </div>
                                    ))}
                                    {localQuestions.length === 0 && <div className="text-center p-8 text-gray-400 border border-dashed rounded-lg">Nenhuma pergunta configurada.<br/>Clique em "Importar Padrão" para carregar o modelo.</div>}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={`p-4 border-t flex justify-end gap-2 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                        <Button onClick={handleSaveAll} className="bg-purple-600 hover:bg-purple-700 text-white shadow-md">Salvar Tudo</Button>
                    </div>
                </div>
            </div>
        );
    };

    /**
     * MODAL: AVALIAÇÃO DE GESTÃO
     * - Bloqueio de datas
     * - Retificação
     * - Mapeamento Dinâmico por Pergunta
     */
    /**
     * MODAL: AVALIAÇÃO DE GESTÃO (Corrigido e Formatado)
     */
    /**
     * MODAL: AVALIAÇÃO DE GESTÃO (CORREÇÃO FINAL DE CONTRASTE)
     */


    /* ===============================================================================
     * [END_MOD_PERCEPTION_CONFIG_MODAL]
     * FIM DO BLOCO: PerceptionConfigModal
     * [START_MOD_PERCEPTION_FORM_MODAL]
     * INICIO DO BLOCO: PerceptionFormModal
     * =============================================================================== */
    const PerceptionFormModal = ({ isOpen, onClose, data, questions, currentUser, onSave, isDarkMode }) => {
    const [step, setStep] = React.useState(1);
    const [managerId, setManagerId] = React.useState('');
    const [evalDate, setEvalDate] = React.useState(new Date().toISOString().split('T')[0]);
    const [answers, setAnswers] = React.useState({});
    const [existingEval, setExistingEval] = React.useState(null);

    

        // Cores padronizadas para os botões de seleção
        const RATING_OPTIONS = [
            { v: 1, l: '1 - Insatisfatório', colorClass: 'bg-red-600 border-red-600 hover:bg-red-700' },
            { v: 2, l: '2 - Regular',        colorClass: 'bg-red-400 border-red-400 hover:bg-red-500' },
            { v: 3, l: '3 - Bom',            colorClass: 'bg-orange-600 border-orange-600 hover:bg-orange-700' },
            { v: 4, l: '4 - Muito Bom',      colorClass: 'bg-green-600 border-green-600 hover:bg-green-700' },
            { v: 5, l: '5 - Excelente',      colorClass: 'bg-blue-600 border-blue-600 hover:bg-blue-700' }
        ];

        const calculatedQuarterRef = React.useMemo(() => {
            if (!evalDate) return { dateDisplay: '-', label: '-', fullDate: '' };
            const [yearStr, monthStr] = evalDate.split('-');
            const year = parseInt(yearStr);
            const month = parseInt(monthStr);
            let refDay, refMonth, refYear, refQuarterLabel;
            if (month >= 1 && month <= 3) { refDay = 31; refMonth = 12; refYear = year - 1; refQuarterLabel = `4º Trimestre de ${refYear}`; } 
            else if (month >= 4 && month <= 6) { refDay = 31; refMonth = 3; refYear = year; refQuarterLabel = `1º Trimestre de ${refYear}`; }
            else if (month >= 7 && month <= 9) { refDay = 30; refMonth = 6; refYear = year; refQuarterLabel = `2º Trimestre de ${refYear}`; }
            else { refDay = 30; refMonth = 9; refYear = year; refQuarterLabel = `3º Trimestre de ${refYear}`; }
            const dateDisplay = `${String(refDay).padStart(2, '0')}/${String(refMonth).padStart(2, '0')}/${refYear}`;
            return { dateDisplay, label: refQuarterLabel, fullDate: `${refYear}-${String(refMonth).padStart(2, '0')}-${String(refDay).padStart(2, '0')}` };
        }, [evalDate]);

        // CORREÇÃO 1: Incluir também os gestores vinculados via rateio (deptShare)
        const myManagers = React.useMemo(() => {
            if (!currentUser || !currentUser.departmentId) return [];
            return (data.managers || []).filter(m => {
                if (m.departmentId === currentUser.departmentId) return true;
                if (m.deptShare && Array.isArray(m.deptShare)) {
                    return m.deptShare.some(share => share.deptId === currentUser.departmentId);
                }
                return false;
            });
        }, [data.managers, currentUser]);

        const activeQuestions = React.useMemo(() => {
            const qList = (questions && questions.length > 0) ? questions : [];
            const now = new Date();
            return qList.filter(q => {
                if (q.personType !== 'Gestão' && q.personType !== 'Todos') return false;
                if (q.endDate && new Date(q.endDate) < now) return false;
                return true;
            }).sort((a,b) => a.order - b.order);
        }, [questions]);

        // CORREÇÃO 2: Resetar o formulário apenas quando a janela for aberta (isOpen)
        // Isso remove o bug que limpava o setManagerId('') em loop
        React.useEffect(() => {
            if (isOpen) {
                setStep(1); 
                setManagerId(''); 
                setExistingEval(null); 
                setEvalDate(new Date().toISOString().split('T')[0]);
            } else {
                setAnswers({}); // Limpa as respostas quando o modal for fechado
            }
        }, [isOpen]); 

        // CORREÇÃO 3: As perguntas são vinculadas separadamente para não resetar a seleção do select
        React.useEffect(() => {
            if (isOpen && activeQuestions.length > 0) {
                setAnswers(prev => {
                    // Só inicializa o objeto de respostas se ele ainda estiver vazio
                    if (Object.keys(prev).length > 0) return prev;
                    const initial = {};
                    activeQuestions.forEach(q => { initial[q.id] = { value: 0, obs: '' }; });
                    return initial;
                });
            }
        }, [isOpen, activeQuestions]);

        const checkExistingRecord = (selectedManagerId) => {
            setManagerId(selectedManagerId);
            if (!selectedManagerId) { setExistingEval(null); return; }
            const managerObj = myManagers.find(m => m.id === selectedManagerId);
            if (!managerObj) return;
            const found = (data.teamPerception || []).find(record => 
                record.email === currentUser.email && record.gestor_avaliado === managerObj.name && record.refQuarterDate === calculatedQuarterRef.fullDate
            );
            if (found) setExistingEval(found); else setExistingEval(null);
        };

        const handleNextStep = () => {
            if (!managerId) return alert("Selecione um gestor.");
            if (existingEval && existingEval.respostas_detalhadas) setAnswers(existingEval.respostas_detalhadas);
            setStep(2);
        };

        const handleAnswerChange = (qId, field, val) => {
            setAnswers(prev => ({
                ...prev,
                [qId]: {
                    ...(prev[qId] || { value: 0, obs: '' }),
                    [field]: val
                }
            }));
        };

        const validateAndSubmit = () => {
            for (const q of activeQuestions) {
                const ans = answers[q.id];
                if (q.type === 'scale' && (!ans || !ans.value || ans.value === 0)) return alert(`A pergunta "${q.order}" não foi respondida.`);
                if (q.type === 'scale' && ans.value <= 3) {
                    if (!ans.obs || ans.obs.trim().length < 5) return alert(`Na pergunta "${q.order}", você avaliou como ${ans.value}. É obrigatório justificar.`);
                }
            }
            const manager = myManagers.find(m => m.id === managerId);
            const scaleQuestions = activeQuestions.filter(q => q.type === 'scale');
            const sumScores = scaleQuestions.reduce((acc, q) => acc + (answers[q.id]?.value || 0), 0);
            const maxPossiblePoints = scaleQuestions.length * 5;
            let finalScore = 0;
            if (maxPossiblePoints > 0) finalScore = 5 * (sumScores / maxPossiblePoints);

            const dynamicColumnsData = {};
            const scaleLabels = { 1: "Insatisfatório", 2: "Regular", 3: "Bom", 4: "Muito Bom", 5: "Excelente" };
            const configuredColumns = data.perceptionCycles || [];
            configuredColumns.forEach(col => {
                if (col.linkedField && col.linkedQuestionId) {
                    const ans = answers[col.linkedQuestionId];
                    if (ans) {
                        let cellContent = "";
                        const originalQ = activeQuestions.find(q => q.id === col.linkedQuestionId);
                        if (originalQ && originalQ.type === 'scale') {
                            cellContent = scaleLabels[ans.value] || ans.value;
                            if (ans.obs && ans.obs.trim()) cellContent += `\nObs: ${ans.obs}`;
                        } else { cellContent = ans.obs || ans.value || "-"; }
                        dynamicColumnsData[col.linkedField] = cellContent;
                    }
                }
            });

            const entryToSave = {
                id: existingEval ? existingEval.id : Date.now().toString(),

                // Colaborador que enviou a avaliação
                email: currentUser?.email || '',
                collaboratorEmail: currentUser?.email || '',
                collaboratorName: currentUser?.name || '',

                // Gestor avaliado
                manager_email: manager?.email || '',
                managerEmail: manager?.email || '',
                emailGestor: manager?.email || '',
                gestorEmail: manager?.email || '',
                managerId: manager?.id || '',
                gestor_avaliado: manager?.name || 'N/D',
                managerName: manager?.name || 'N/D',

                date: calculatedQuarterRef.dateDisplay,
                refQuarterDate: calculatedQuarterRef.fullDate,
                nota: parseFloat(finalScore.toFixed(4)),
                comentarios: existingEval ? "Avaliação Retificadora" : "Avaliação enviada",
                respostas_detalhadas: answers,
                ...dynamicColumnsData,
                origin: 'app_interno_v2'
            };
            onSave(entryToSave); onClose();
            if (existingEval) alert("Avaliação retificada com sucesso!");
        };

        if (!isOpen) return null;

        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={onClose}>
                <div className={`rounded-xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                    <div className={`p-5 border-b shrink-0 flex justify-between items-center ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                        <div>
                            <h3 className="font-bold text-xl flex items-center gap-2 text-purple-600">{existingEval ? <Edit3 size={24}/> : <Star size={24}/>} {existingEval ? "Retificar Avaliação" : "Avaliação de Gestão"}</h3>
                            <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Etapa {step} de 2</p>
                        </div>
                        <button onClick={onClose}><X size={24} className="text-gray-400 hover:text-red-500"/></button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                        {step === 1 && (
                            <div className="space-y-6 max-w-md mx-auto py-4">
                                <div className="text-center mb-4"><h4 className="font-bold text-lg">Vamos começar!</h4><p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Selecione o líder referente ao ciclo encerrado.</p></div>
                                <div><label className="text-xs font-bold block mb-1">Data de Envio (Hoje)</label><input type="date" disabled className={`w-full p-3 border rounded-lg outline-none bg-gray-100 text-gray-500 cursor-not-allowed ${isDarkMode ? 'bg-slate-900 border-slate-700 text-slate-500 invert-calendar' : ''}`} value={evalDate} /></div>
                                <div className={`p-3 rounded-lg border-l-4 ${isDarkMode ? 'bg-slate-900 border-slate-700 border-l-purple-500' : 'bg-purple-50 border-purple-100 border-l-purple-500'}`}><label className={`text-xs font-bold block mb-1 uppercase tracking-wide ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>Referente ao Fechamento de:</label><div className="flex justify-between items-end"><div className={`text-lg font-black ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{calculatedQuarterRef.dateDisplay}</div><div className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{calculatedQuarterRef.label}</div></div></div>
                                <div><label className="text-xs font-bold block mb-1">Selecione o Gestor</label><select className={`w-full p-3 border rounded-lg outline-none focus:ring-2 focus:ring-purple-500 text-sm ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`} value={managerId} onChange={e => checkExistingRecord(e.target.value)}><option value="">Selecione...</option>{myManagers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</select>{myManagers.length === 0 && <p className="text-xs text-red-400 mt-2">Nenhum gestor vinculado.</p>}</div>
                                {existingEval && (<div className={`p-3 rounded border flex items-start gap-2 text-xs ${isDarkMode ? 'bg-yellow-900/30 border-yellow-800 text-yellow-200' : 'bg-yellow-50 border-yellow-200 text-yellow-800'}`}><AlertTriangle size={16} className="shrink-0 mt-0.5"/><div><strong>Atenção:</strong> Você já enviou uma avaliação para este gestor neste período.<br/>Ao continuar, você entrará em modo de <b>Retificação</b>.</div></div>)}
                                <button onClick={handleNextStep} className={`w-full py-3 font-bold rounded-lg shadow-md transition-all mt-4 text-white ${existingEval ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-purple-600 hover:bg-purple-700'}`}>{existingEval ? "Continuar (Retificar)" : "Continuar para Perguntas"}</button>
                            </div>
                        )}
                        {step === 2 && (
                            <div className="space-y-8 animate-fade-in">
                                {existingEval && <div className="text-center p-2 bg-yellow-100 text-yellow-800 text-xs font-bold rounded border border-yellow-200 mb-4">MODO DE EDIÇÃO: Alterando avaliação enviada em {existingEval.date}</div>}
                                {activeQuestions.length === 0 ? <div className="text-center p-10 text-gray-500">Nenhuma pergunta configurada.</div> : activeQuestions.map((q) => {
                                    const ans = answers[q.id] || { value: 0, obs: '' };
                                    const showJustification = q.type === 'scale' && ans.value > 0 && ans.value <= 3;
                                    return (
                                        <div key={q.id} className={`p-5 rounded-xl border transition-all ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200 hover:border-purple-200 hover:bg-white hover:shadow-sm'}`}>
                                            <div className="flex gap-3 mb-3"><span className="font-bold text-sm bg-slate-200 text-slate-700 w-6 h-6 flex items-center justify-center rounded-full shrink-0">{q.order}</span><p className={`font-medium text-sm ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{q.text}</p></div>
                                            {q.type === 'scale' ? (
                                                    <div className="ml-9">
                                                        <div className="flex flex-row gap-2 mt-3 w-full max-w-2xl">
                                                            {[
                                                                { val: 1, label: '1 - Insatisfatório', colorActive: 'bg-red-600 border-red-700 text-white', colorHover: 'hover:bg-red-50' },
                                                                { val: 2, label: '2 - Regular',        colorActive: 'bg-orange-500 border-orange-600 text-white', colorHover: 'hover:bg-orange-50' },
                                                                { val: 3, label: '3 - Bom',            colorActive: 'bg-yellow-600 border-yellow-700 text-white', colorHover: 'hover:bg-yellow-50' },
                                                                { val: 4, label: '4 - Muito Bom',      colorActive: 'bg-green-600 border-green-700 text-white', colorHover: 'hover:bg-green-50' },
                                                                { val: 5, label: '5 - Excelente',      colorActive: 'bg-blue-600 border-blue-700 text-white', colorHover: 'hover:bg-blue-50' }
                                                            ].map(opt => {
                                                                const isSelected = ans.value === opt.val;
                                                                
                                                                return (
                                                                    <button
                                                                        key={opt.val}
                                                                        type="button"
                                                                        onClick={() => handleAnswerChange(q.id, 'value', opt.val)}
                                                                        className={`flex-1 py-3 px-1 text-xs font-bold rounded-lg border transition-all duration-200 text-center shadow-sm
                                                                            ${isSelected 
                                                                                ? opt.colorActive 
                                                                                : 'bg-white border-gray-300 text-black'
                                                                            }`}
                                                                    >
                                                                        {opt.label}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>

                                                        {showJustification && (
                                                            <div className="mt-3">
                                                                <label className="text-xs font-bold text-red-500 block mb-1">
                                                                    Justifique sua nota:
                                                                </label>
                                                                <textarea
                                                                    className={`w-full p-2 text-sm border-l-4 border-red-500 rounded bg-red-50 text-black placeholder-red-400 dark:bg-red-900/10 dark:text-white focus:outline-none resize-none h-20`}
                                                                    placeholder='O que falta para o avaliado ser "Muito Bom" ou "Excelente"?'
                                                                    value={ans.obs || ''}
                                                                    onChange={(e) => handleAnswerChange(q.id, 'obs', e.target.value)}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                <div className="ml-9">
                                                <textarea
                                                    className={`w-full p-3 rounded border text-sm focus:ring-2 focus:ring-purple-500 outline-none h-24 ${
                                                        isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                    }`}
                                                    placeholder="Digite seus comentários..."
                                                    value={ans.obs || ''}
                                                    onChange={(e) => handleAnswerChange(q.id, 'obs', e.target.value)}
                                                />
                                            </div>)}
                                        </div>
                                    );
                                })}
                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-slate-700">
                                    <Button variant="secondary" onClick={() => setStep(1)} className={isDarkMode ? 'bg-slate-700 text-white border-slate-600 hover:bg-slate-600' : ''}>Voltar</Button>
                                    <Button onClick={validateAndSubmit} className={`${existingEval ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-purple-600 hover:bg-purple-700'} text-white shadow-lg px-8`}>{existingEval ? "Confirmar Retificação" : "Finalizar Avaliação"}</Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    /**
     * ============================================================================
     * COMPONENTE: CADASTROS SYSTEM (VERSÃO COMPLETA E CORRIGIDA)
     * ============================================================================
     * Gerencia todas as entidades: Departamentos, Usuários, Treinamentos, Configurações, etc.
     */


    /* ===============================================================================
     * [END_MOD_PERCEPTION_FORM_MODAL]
     * FIM DO BLOCO: PerceptionFormModal
     * [START_MOD_CADASTROS_SYSTEM]
     * INICIO DO BLOCO: CadastrosSystem
     * =============================================================================== */
    const CadastrosSystem = ({ 
        data, 
        actions, 
        currentUser, 
        authReady, 
        pendingUsersCount, 
        initialTab, 
        initialSubTab, // <--- 1. ADICIONADO AQUI (CORREÇÃO DO ERRO)
        isDarkMode 
    }) => {
        
        // ... (Bloco de permissões effectivePermissions e hasPerm mantidos iguais) ...
        const effectivePermissions = useMemo(() => {
            return {
                ...(data.activePermissions || {}),
                ...(currentUser?.permissions || {})
            };
        }, [data.activePermissions, currentUser]);

        const hasPerm = (key) => {
            if (!key) return true;
            // Removido bypass do SuperAdm para as abas internas
            // if (currentUser?.role === 'SuperAdm') return true;
            return effectivePermissions[key] === true;
        };
        
        // ========================================================================
        // 1. ESTADOS (STATES)
        // ========================================================================
        
        // --- LÓGICA DE ABA INICIAL INTELIGENTE ---
        const getSafeInitialTab = () => {
            if (initialTab && hasPerm(`access_tab_${initialTab}`)) return initialTab;
            if (initialTab === 'training' && hasPerm('access_tab_training')) return 'training';
            // Fallbacks...
            if (hasPerm('access_tab_dept')) return 'dept';
            if (hasPerm('access_tab_manager')) return 'manager';
            if (hasPerm('access_tab_collab')) return 'collab';
            if (hasPerm('access_tab_training')) return 'training';
            return initialTab || 'dept';
        };

        const [tab, setTab] = useState(getSafeInitialTab());
        const [search, setSearch] = useState('');
        
        // --- LÓGICA DE SUB-ABA INTELIGENTE ---
        const getSafeExternalSubTab = () => {
            // Se veio do favorito, usa ele
            if (initialTab === 'training' && initialSubTab) return initialSubTab;
            
            if (hasPerm('access_sub_collab_area')) return 'colaborador';
            if (hasPerm('access_sub_capacitacoes')) return 'capacitacoes';
            if (hasPerm('access_sub_percepcao')) return 'percepcao';
            if (hasPerm('access_sub_ai')) return 'ia';
            return 'colaborador';
        };

        // --- 2. ESTADOS DE SUB-ABAS (INICIALIZADOS COM O FAVORITO) ---
        const [usersSubTab, setUsersSubTab] = useState((initialTab === 'users' && initialSubTab) ? initialSubTab : 'list');
        const [externalSubTab, setExternalSubTab] = useState(getSafeExternalSubTab()); 
        const [configSubTab, setConfigSubTab] = useState((initialTab === 'config' && initialSubTab) ? initialSubTab : 'metricas');
        const [criteriaSubTab, setCriteriaSubTab] = useState((initialTab === 'criteria' && initialSubTab) ? initialSubTab : 'criterios');
        const [pontoTab, setPontoTab] = useState((initialTab === 'ponto' && initialSubTab) ? initialSubTab : 'config');

        // --- 3. EFEITO PARA MUDANÇA DINÂMICA VIA FAVORITO ---
        // Se o usuário clicar num favorito enquanto já está na tela, isso força a troca da aba
        useEffect(() => {
            if (initialSubTab) {
                if (initialTab === 'users') setUsersSubTab(initialSubTab);
                if (initialTab === 'config') setConfigSubTab(initialSubTab);
                if (initialTab === 'ponto') setPontoTab(initialSubTab);
                if (initialTab === 'criteria') setCriteriaSubTab(initialSubTab);
                if (initialTab === 'training') setExternalSubTab(initialSubTab);
            }
        }, [initialTab, initialSubTab]);

        // Modais (Controle de Visibilidade)
        const [isCadModalOpen, setIsCadModalOpen] = useState(false);
        const [isUserModalOpen, setIsUserModalOpen] = useState(false);
        const [isPermModalOpen, setIsPermModalOpen] = useState(false);
        const [isResetModalOpen, setIsResetModalOpen] = useState(false);
        const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
        const [isPerceptionSyncModalOpen, setIsPerceptionSyncModalOpen] = useState(false);
        const [isPercepEditModalOpen, setIsPercepEditModalOpen] = useState(false);
        const [isBulkEditCriteriaOpen, setIsBulkEditCriteriaOpen] = useState(false);
        const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);

            // --- ESTADOS PARA EDIÇÃO EM BLOCO DE PESSOAS (NOVO) ---
        const [isBulkCollabModalOpen, setIsBulkCollabModalOpen] = useState(false);
        const [bulkCollabSelectedIds, setBulkCollabSelectedIds] = useState([]);
        const [editAcadCoins, setEditAcadCoins] = useState(false);
        const [bulkAcadCoins, setBulkAcadCoins] = useState({ participates: true, startDate: '', endDate: '' });
        const [bulkCollabFilters, setBulkCollabFilters] = useState({ dept: '', type: 'Todos', search: '' });
        const [bulkCollabForm, setBulkCollabForm] = useState({
            personType: '',
            departmentId: '',
            companyName: '',
            companyCNPJ: '',
            evalTypeId: '',
            startDate: '',
            endDate: '',
            workRuleId: '',
            skipPunchApproval: '',
            skipJustificationNotify: '',
            skipDelayCritique: ''
        });
        

        const [pontoSettings, setPontoSettings] = useState({
            id: 'ponto_settings',
            financialUsers: [],
            rules: {
                delay10: { label: 'Atrasos/Adiantos (Até 10 min)', notify: true, requireApproval: false },
                delayX: { label: 'Atrasos/Adiantos (Acima limite)', notify: true, requireApproval: true, limit: 15 },
                certificate: { label: 'Atestados Médicos', notify: true, requireApproval: true },
                absence: { label: 'Ausências/Faltas', notify: true, requireApproval: true },
                manualAll: { label: 'Marcações Manuais (Todas)', notify: true, requireApproval: false },
                manualX: { label: 'Marcações Manuais (Acima limite)', notify: true, requireApproval: true, limit: 15 },
                otherJust: { label: 'Demais Justificativas', notify: true, requireApproval: true }
            }
        });

        // Sincroniza com o banco de forma segura após o carregamento
        useEffect(() => {
            if (data && data.globalConfigs) {
                const savedSettings = data.globalConfigs.find(c => c.id === 'ponto_settings');
                if (savedSettings) {
                    setPontoSettings(prev => ({
                        ...prev,
                        ...savedSettings,
                        rules: { ...prev.rules, ...(savedSettings.rules || {}) }
                    }));
                }
            }
        }, [data]);

        // =====================================================================
        // --- INÍCIO DA LÓGICA DE EDIÇÃO EM BLOCO DE PESSOAS ---
        // =====================================================================
        const filteredCollabsForBulk = useMemo(() => {
            if (!data || !data.collaborators) return [];
            return data.collaborators.filter(c => {
                if (bulkCollabFilters.dept && c.departmentId !== bulkCollabFilters.dept) return false;
                if (bulkCollabFilters.type !== 'Todos' && c.personType !== bulkCollabFilters.type) return false;
                if (bulkCollabFilters.search && !c.name.toLowerCase().includes(bulkCollabFilters.search.toLowerCase())) return false;
                return true;
            }).sort((a, b) => a.name.localeCompare(b.name));
        }, [data, bulkCollabFilters]);

        const executeBulkCollabUpdate = async () => {
            if (bulkCollabSelectedIds.length === 0) {
                setValidationAlert({ title: "Atenção", message: "Selecione ao menos uma pessoa na lista." });
                return;
            }
            
            const hasChanges = Object.values(bulkCollabForm).some(val => val !== '' && val !== null);
            
            // CORREÇÃO AQUI: Só bloqueia se não preencheu o form E não marcou o check do AcadCoins
            if (!hasChanges && !editAcadCoins) {
                setValidationAlert({ title: "Atenção", message: "Preencha ao menos um campo ou marque a alteração do AcadCoins para continuar." });
                return;
            }

            setConfirmModal({
                isOpen: true,
                title: "Confirmar Edição em Bloco",
                message: `Tem certeza que deseja atualizar os dados de ${bulkCollabSelectedIds.length} colaborador(es)?`,
                onConfirm: async () => {
                    let updatedCount = 0;
                    for (const id of bulkCollabSelectedIds) {
                        const original = data.collaborators.find(c => c.id === id);
                        if (original) {
                            const updates = {};
                            if (bulkCollabForm.personType) updates.personType = bulkCollabForm.personType;
                            if (bulkCollabForm.departmentId) updates.departmentId = bulkCollabForm.departmentId;
                            if (bulkCollabForm.companyName) updates.companyName = bulkCollabForm.companyName;
                            if (bulkCollabForm.companyCNPJ) updates.companyCNPJ = bulkCollabForm.companyCNPJ;
                            if (bulkCollabForm.evalTypeId) updates.evalTypeId = bulkCollabForm.evalTypeId;
                            if (bulkCollabForm.startDate) updates.startDate = bulkCollabForm.startDate;
                            if (bulkCollabForm.endDate) updates.endDate = bulkCollabForm.endDate;
                            if (bulkCollabForm.workRuleId) updates.workRuleId = bulkCollabForm.workRuleId;
                            
                            // Atualização em Bloco das Exceções/Privilégios de Ponto
                            if (bulkCollabForm.skipPunchApproval !== '') updates.skipPunchApproval = bulkCollabForm.skipPunchApproval === 'true';
                            if (bulkCollabForm.skipJustificationNotify !== '') updates.skipJustificationNotify = bulkCollabForm.skipJustificationNotify === 'true';
                            if (bulkCollabForm.skipDelayCritique !== '') updates.skipDelayCritique = bulkCollabForm.skipDelayCritique === 'true';

                            if (editAcadCoins) {
                                updates.participatesAcadCoins = bulkAcadCoins.participates;
                                updates.acadCoinsStartDate = bulkAcadCoins.startDate;
                                updates.acadCoinsEndDate = bulkAcadCoins.endDate;
                            }
                            // ================================================================
                            // NOVO: REATRIBUIÇÃO AUTOMÁTICA DE GESTORES (Se Depto ou Tipo mudar)
                            // ================================================================
                            if (bulkCollabForm.departmentId || bulkCollabForm.personType) {
                                const targetDeptId = bulkCollabForm.departmentId || original.departmentId;
                                const targetPersonType = bulkCollabForm.personType || original.personType;
                                
                                let finalDeptForManagers = targetDeptId;

                                // Se a pessoa virou "Gestão", seu gestor deve ser a Diretoria (DIR)
                                if (targetPersonType === 'Gestão') {
                                    const dir = data.departments.find(d => 
                                        d.name.toUpperCase() === 'DIR' || 
                                        d.name.toUpperCase().includes('DIRETORIA') || 
                                        d.name.toUpperCase().includes('DIR.')
                                    );
                                    if (dir) finalDeptForManagers = dir.id;
                                }

                                // Filtra os gestores que cuidam deste departamento (Direto ou via Rateio)
                                const newManagers = data.managers.filter(m => {
                                    if (m.departmentId === finalDeptForManagers) return true;
                                    if (m.deptShare && Array.isArray(m.deptShare)) {
                                        return m.deptShare.some(share => share.deptId === finalDeptForManagers);
                                    }
                                    return false;
                                });

                                // Atualiza a lista de IDs de gestores vinculados
                                updates.managerIds = newManagers.map(m => m.id);
                            }
                            // ================================================================

                            if (actions.saveCollab) {
                                await actions.saveCollab({ ...original, ...updates });
                                updatedCount++;
                            }
                        }
                    }
                    
                    setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
                    setIsBulkCollabModalOpen(false);
                    setBulkCollabSelectedIds([]);
                    setBulkCollabForm({ personType: '', departmentId: '', companyName: '', companyCNPJ: '', evalTypeId: '', startDate: '', endDate: '', workRuleId: '', skipPunchApproval: '', skipJustificationNotify: '', skipDelayCritique: '' });
                    setValidationAlert({ title: "Sucesso!", message: `${updatedCount} registros foram atualizados e os gestores foram reatribuídos automaticamente.` });
                }
            });
        };

        const toggleBulkCollabSelect = (id) => {
            setBulkCollabSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
        };
        // =====================================================================
        // --- FIM DA LÓGICA DE EDIÇÃO EM BLOCO ---
        // =====================================================================

        // --- ESTADOS DO PONTO (NOVO) ---
        const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
        const [isSitModalOpen, setIsSitModalOpen] = useState(false);

        // --- ESTADOS PARA MIGRAÇÃO DE DADOS ---
        const [isMigrationModalOpen, setIsMigrationModalOpen] = useState(false);
        const [importing, setImporting] = useState(false);

        // --- ESTADOS PARA LIMPEZA DE ARTEFATOS ---
        const [artifactPrefix, setArtifactPrefix] = useState('');
        const [isCleaning, setIsCleaning] = useState(false);

        // --- ESTADOS PARA AVALIAÇÃO DE GESTÃO ---
        const [isEvaluationModalOpen, setIsEvaluationModalOpen] = useState(false);
        const [isPerceptionConfigOpen, setIsPerceptionConfigOpen] = useState(false);
        
        // CORREÇÃO: Lê a configuração do banco de dados (Global) para que funcione para todos
        // Usa um ID fixo 'perception_dates' para identificar este registro específico
        const perceptionConfig = data.globalConfigs.find(c => c.id === 'perception_dates') || { overrideStart: '', overrideEnd: '' };

        // --- NOVOS ESTADOS PARA SINCRONIZAÇÃO ---
        const [syncMenuOpen, setSyncMenuOpen] = useState(null); 
        // ... (restante dos estados mantidos) 
        const [syncModalConfig, setSyncModalConfig] = useState({ 
            isOpen: false, title: '', type: '', source: '', callback: null 
        });
        const [batchYearInput, setBatchYearInput] = useState(new Date().getFullYear().toString());
        const [isProcessingBatch, setIsProcessingBatch] = useState(false);
        // -----------------------------------------------------------

        // --- ESTADOS DO MÓDULO CARGOS E SALÁRIOS (PCS) ---
        const [pcsSearch, setPcsSearch] = useState('');
        const [isPCSModalOpen, setIsPCSModalOpen] = useState(false);
        const [isBulkEditPcsOpen, setIsBulkEditPcsOpen] = useState(false);
        const [bulkPcsForm, setBulkPcsForm] = useState({});
        const [selectedPcsIds, setSelectedPcsIds] = useState([]);
        
        // Estados para Importação e Reajuste
        const [modal, setModal] = useState(null); // Para o modal de reajuste
        const [readjustPercent, setReadjustPercent] = useState(0);
        const [readjustDate, setReadjustDate] = useState('');
        const [importModalOpen, setImportModalOpen] = useState(false);
        const [importConfig, setImportConfig] = useState({ targetDept: '', ignoreTerm: '' });
        const [importDate, setImportDate] = useState(new Date().toISOString().split('T')[0]);
        const [selectedFile, setSelectedFile] = useState(null);
        const [isProcessing, setIsProcessing] = useState(false);

        // --- COLE AQUI (No escopo principal do componente) ---
        const { data: perceptionQuestions, saveItem: savePerceptionQuestion, deleteItem: deletePerceptionQuestion } = useFirestoreData('perceptionQuestions', [], authReady, currentUser);
        
        const { data: surveyList, saveItem: saveSurvey, deleteItem: deleteSurvey } = useFirestoreData('surveys', [], authReady, currentUser);
        const { data: surveyResponses, saveItem: saveSurveyResponse, deleteItem: deleteSurveyResponse } = useFirestoreData('surveyResponses', [], authReady, currentUser);

        // --- FUNÇÃO QUE CORRIGE O ERRO "isPerceptionOpen is not defined" ---
        const isPerceptionOpen = () => {
            const today = new Date();
            const currentMonth = today.getMonth(); // 0=Jan, 1=Fev...
            const currentDay = today.getDate();

            // 1. Verifica Override (Reabertura Manual pelo SuperAdm)
            if (perceptionConfig.overrideStart && perceptionConfig.overrideEnd) {
                const start = new Date(perceptionConfig.overrideStart);
                const end = new Date(perceptionConfig.overrideEnd);
                start.setHours(0,0,0,0); 
                end.setHours(23,59,59,999);
                if (today >= start && today <= end) return true;
            }

            // 2. Verifica Regra Padrão (Dia 1 a 10 dos meses Jan, Abr, Jul, Out)
            // Meses alvo: Jan(0), Abr(3), Jul(6), Out(9)
            const allowedMonths = [0, 3, 6, 9];
            if (allowedMonths.includes(currentMonth) && currentDay >= 1 && currentDay <= 10) {
                return true;
            }

            return false;
        };

        // Função de Exportar (Gera o JSON)
        const handleExportBackup = () => {
            const backupData = {
                departments: data.departments,
                managers: data.managers,
                collaborators: data.collaborators,
                evaluations: data.evaluations,
                apontamentos: data.apontamentos,
                qualiCriteria: data.qualiCriteria,
                quantiCriteria: data.quantiCriteria,
                globalConfigs: data.globalConfigs,
                evaluationTypes: data.evaluationTypes,
                pdfMessages: data.pdfMessages,
                pcs: data.pcs,
                users: data.users,
                minWageHistory: data.minWageHistory,
                gestaoMetasPercepcao: data.gestaoMetasPercepcao,
                competencias: data.competencias, // mapeado como 'competenciasMap' no App, mas aqui usamos o nome da collection
                deadlines: data.deadlines,
                trainingData: data.trainingData,
                teamPerception: data.teamPerception,
                permissionProfiles: data.permissionProfiles || [],
                conferenciaTypes: data.conferenciaTypes || [],
                perceptionCycles: data.perceptionCycles || []
            };

            const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(backupData));
            const downloadAnchorNode = document.createElement('a');
            downloadAnchorNode.setAttribute("href", dataStr);
            downloadAnchorNode.setAttribute("download", `backup_aec_${new Date().toISOString().split('T')[0]}.json`);
            document.body.appendChild(downloadAnchorNode);
            downloadAnchorNode.click();
            downloadAnchorNode.remove();
        };

        // Função de Importar (Lê o JSON e Salva)
        const handleImportBackup = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            if (!window.confirm("ATENÇÃO: Isso irá substituir/adicionar dados ao projeto atual. Recomenda-se fazer isso apenas em um projeto novo/vazio. Continuar?")) return;

            setImporting(true);
            const reader = new FileReader();
            
            reader.onload = async (event) => {
                try {
                    const importedData = JSON.parse(event.target.result);
                    
                    // Mapeamento: Chave do JSON -> Função de Salvar
                    const mapActions = {
                        departments: actions.saveDept,
                        managers: actions.saveManager,
                        collaborators: actions.saveCollab,
                        evaluations: actions.saveEvaluation,
                        apontamentos: actions.addApontamento, // addApontamento chama sync
                        qualiCriteria: actions.saveCriteria, // saveCriteria detecta se é quali
                        quantiCriteria: actions.saveCriteria, // saveCriteria detecta se é quanti
                        globalConfigs: actions.saveConfig,
                        evaluationTypes: actions.saveType,
                        pdfMessages: actions.saveMsg,
                        pcs: actions.savePCS,
                        users: actions.saveUser,
                        minWageHistory: actions.addMinWageHistory,
                        gestaoMetasPercepcao: actions.saveGestaoMetasPercepcao,
                        competencias: actions.saveCompetencia,
                        deadlines: actions.saveDeadline,
                        trainingData: actions.saveTraining,
                        teamPerception: actions.saveTeamPerception,
                        permissionProfiles: actions.savePermissionProfile,
                        conferenciaTypes: actions.saveConferenciaType,
                        perceptionCycles: actions.savePerceptionCycle
                    };

                    let totalItems = 0;

                    // Percorre cada coleção do backup
                    for (const [key, items] of Object.entries(importedData)) {
                        if (Array.isArray(items) && mapActions[key]) {
                            console.log(`Importando ${items.length} itens de ${key}...`);
                            // Salva um por um (para garantir que os hooks do App funcionem)
                            for (const item of items) {
                                await mapActions[key](item);
                                totalItems++;
                            }
                        }
                    }

                    alert(`Importação concluída com sucesso! ${totalItems} registros processados.`);
                    setIsMigrationModalOpen(false);
                    window.location.reload(); // Recarrega para garantir que tudo apareça

                } catch (error) {
                    console.error(error);
                    alert("Erro ao processar arquivo de backup: " + error.message);
                } finally {
                    setImporting(false);
                }
            };
            
            reader.readAsText(file);
        };

        // --- FUNÇÃO DE LIMPEZA DE ARTEFATOS ---
        const handleCleanArtifacts = async () => {
            if (!artifactPrefix.trim()) return alert("Digite parte do nome do documento (prefixo).");
            if (!window.confirm(`ATENÇÃO: Você está prestes a buscar e excluir registros da coleção principal 'artifacts' que contenham "${artifactPrefix}".\n\nEsta ação não tem volta. Deseja continuar?`)) return;

            setIsCleaning(true);
            try {
                // 1. Busca todos os documentos na coleção 'artifacts' (raiz)
               const artifactsRef = collection(db, 'artifacts');

                // Passo 1: Cria uma consulta (query) inteligente. 
                // O __name__ procura diretamente pelo ID do documento no Firebase.
                // O \uf8ff é um caractere especial que diz ao Firebase "qualquer coisa que venha depois do prefixo".
                const q = query(
                    artifactsRef, 
                    where('__name__', '>=', artifactPrefix.trim()), 
                    where('__name__', '<=', artifactPrefix.trim() + '\uf8ff') 
                );

                // Passo 2: O getDocs agora baixa APENAS os itens que deram match no prefixo, economizando muita leitura!
                const snap = await getDocs(q);

                const docsToDelete = [];
                snap.forEach(d => {
                    docsToDelete.push(d.id); // Já vem filtrado, é só adicionar na lista de exclusão
                });
                if (docsToDelete.length === 0) {
                    alert(`Nenhum documento encontrado contendo "${artifactPrefix}".`);
                    setIsCleaning(false);
                    return;
                }

                // 3. Confirmação final mostrando a quantidade
                if (!window.confirm(`Encontramos ${docsToDelete.length} documentos correspondentes.\nConfirma a EXCLUSÃO PERMANENTE de todos eles?`)) {
                    setIsCleaning(false);
                    return;
                }

                // 4. Exclusão em Lote (Batch Delete - Exclui de 500 em 500 para não travar o Firebase)
                let batch = writeBatch(db);
                let count = 0;
                
                for (const docId of docsToDelete) {
                    batch.delete(doc(db, 'artifacts', docId));
                    count++;
                    
                    if (count % 450 === 0) {
                        await batch.commit();
                        batch = writeBatch(db);
                    }
                }
                
                if (count % 450 !== 0) {
                    await batch.commit();
                }

                alert(`Sucesso! ${docsToDelete.length} documentos lixo foram excluídos da base.`);
                setArtifactPrefix('');
                
            } catch (error) {
                console.error("Erro na exclusão:", error);
                alert("Erro ao excluir documentos: " + error.message);
            } finally {
                setIsCleaning(false);
            }
        };

        // Adicionado 'linkedField' com padrão 'nota' (que é a Coluna D)
        const [columnForm, setColumnForm] = useState({ description: '', notes: '', startDate: '', endDate: '', order: 1, linkedField: 'nota' });
        // Formulários
        const [form, setForm] = useState({});
        const [userForm, setUserForm] = useState({ id:'', name:'', email:'', role:'ADM', departmentId:'', permissions: {} });
        const [permissionForm, setPermissionForm] = useState({});
        const [percepForm, setPercepForm] = useState({ id: '', email: '', date: '', nota: '' });
        const [bulkCriteriaForm, setBulkCriteriaForm] = useState({});

        // Seleções e Filtros Avançados
        const [selectedCriteriaIds, setSelectedCriteriaIds] = useState([]);
        const [selectedTrainingIds, setSelectedTrainingIds] = useState([]);
        const [selectedPerceptionIds, setSelectedPerceptionIds] = useState([]);
        
        // Filtros de Treinamento
        const [trainingView, setTrainingView] = useState('detalhado');
        const [trainingFilterYear, setTrainingFilterYear] = useState('');
        const [trainingFilterDept, setTrainingFilterDept] = useState('');
        const [trainingFilterType, setTrainingFilterType] = useState('');
        const [trainingPeriodMode, setTrainingPeriodMode] = useState('ano');
        const [trainingPeriodValue, setTrainingPeriodValue] = useState('1');
        const [trainingCustomStart, setTrainingCustomStart] = useState('');
        const [trainingCustomEnd, setTrainingCustomEnd] = useState('');
        const [trainingModalTab, setTrainingModalTab] = useState('principal');
        const [trainingRules, setTrainingRules] = useState([]); // Regras de limite de envio
        const [newTrainingRule, setNewTrainingRule] = useState({ nomeRegra: '', departamento: 'Todos', tipoPessoa: 'Todos', dataLimiteEnvio: '' });

        // Configuração central da IA em Cadastros > Dados Externos > IA
        const defaultAIConfigForm = {
            id: 'default',
            isActive: false,
            provider: 'gemini',
            model: 'gemini-1.5-flash',
            apiKey: '',
            proxyUrl: DEFAULT_AI_PROXY_URL
        };
        const [aiConfigForm, setAiConfigForm] = useState(defaultAIConfigForm);
        const [showAIKey, setShowAIKey] = useState(false);
        const [savingAIConfig, setSavingAIConfig] = useState(false);

        // Estados Auxiliares
        const [criteriaType, setCriteriaType] = useState('quali');
        const [logoBase64, setLogoBase64] = useState(null);
        const [syncYearInput, setSyncYearInput] = useState(new Date().getFullYear().toString());
        const [perceptionYearInput, setPerceptionYearInput] = useState(new Date().getFullYear().toString());
        const [isUpdatingSheet, setIsUpdatingSheet] = useState(false);
        const [expandedPermGroups, setExpandedPermGroups] = useState({});
        const [editingUserName, setEditingUserName] = useState('');
        const [userToReset, setUserToReset] = useState(null);
        const [resetPasswordValue, setResetPasswordValue] = useState('Acad2025**');

        useEffect(() => {
            const savedAIConfig = (data.aiConfigs || []).find(cfg => cfg.id === 'default') || (data.aiConfigs || [])[0];
            if (savedAIConfig) {
                setAiConfigForm(prev => ({ ...defaultAIConfigForm, ...savedAIConfig, id: savedAIConfig.id || 'default' }));
            }
        }, [data.aiConfigs]);

        const handleSaveAIConfig = async () => {
            const payload = {
                ...aiConfigForm,
                id: 'default',
                provider: aiConfigForm.provider || 'gemini',
                model: aiConfigForm.model || 'gemini-1.5-flash',
                proxyUrl: aiConfigForm.proxyUrl || DEFAULT_AI_PROXY_URL,
                updatedAt: new Date().toISOString(),
                updatedBy: currentUser?.email || currentUser?.name || ''
            };

            if (payload.isActive && !String(payload.apiKey || '').trim()) {
                alert('Informe a chave da API antes de ativar a IA.');
                return;
            }

            setSavingAIConfig(true);
            try {
                await actions.saveAIConfig(payload);
                alert('Configuração de IA salva com sucesso.');
            } catch (error) {
                console.error('Erro ao salvar configuração de IA:', error);
                alert('Erro ao salvar configuração de IA: ' + (error?.message || error));
            } finally {
                setSavingAIConfig(false);
            }
        };

            // --- INCLUIR ESTES NOVOS ESTADOS ---
        const [isBulkPermModalOpen, setIsBulkPermModalOpen] = useState(false);
        const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
        const [selectedUserIds, setSelectedUserIds] = useState([]); // Para seleção em bloco na tabela
        const [profileForm, setProfileForm] = useState({ name: '', permissions: {} });
        const [profilePermissionSearch, setProfilePermissionSearch] = useState('');
        const [profileShowOnlyNew, setProfileShowOnlyNew] = useState(false);
        const [peoplePermissionCatalog, setPeoplePermissionCatalog] = useState([]);
        const [peoplePermissionCatalogLoading, setPeoplePermissionCatalogLoading] = useState(false);
        const [peoplePermissionCatalogError, setPeoplePermissionCatalogError] = useState('');
        const permissionCatalogSyncedRef = useRef(false);
        const [profileMembersModal, setProfileMembersModal] = useState(null);
        const [profileMembersFilter, setProfileMembersFilter] = useState('unlinked'); // 'unlinked' ou 'all'
        const [bulkActionType, setBulkActionType] = useState('add'); // 'add' ou 'remove'
        const [isMigrating, setIsMigrating] = useState(false);

        // ... outros estados existentes
        const [bulkTargetMode, setBulkTargetMode] = useState('selection'); // 'selection' ou 'role'
        const [bulkTargetRole, setBulkTargetRole] = useState('ADM'); // 'ADM', 'SuperAdm', 'Solicitante', etc.

        // --- NOVOS ESTADOS PARA O SELETOR MANUAL ---
        const [manualSearch, setManualSearch] = useState(''); // Busca de usuários no modal
        const [manualSelectedIds, setManualSelectedIds] = useState([]); // IDs selecionados manualmente

        // NOVO ESTADO PARA ALERTAS (Substituto do window.alert)
        const [validationAlert, setValidationAlert] = useState(null);
        const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

        // ========================================================================
        // 2. CONFIGURAÇÕES E PERMISSÕES (ESTRUTURA HIERÁRQUICA ATUALIZADA)
        // ========================================================================

        const PERMISSION_STRUCTURE = [
            {
                id: 'av_desempenho',
                label: 'AEC - Acompanhamento de Evolução Continuada',
                key: 'access_proj_av_desempenho', 
                children: [
                    { label: 'Dashboard', key: 'access_dash', defaultAdm: true },
                    { 
                        label: 'Cadastros', 
                        key: 'access_reg', 
                        defaultAdm: true,
                        children: [
                            { label: 'Departamentos', key: 'access_tab_dept' },
                            { label: 'Gestores', key: 'access_tab_manager' },
                                                    { 
                                label: 'Critérios Avaliação', 
                                key: 'access_tab_criteria',
                                children: [
                                    { label: 'Lista de Critérios', key: 'access_sub_criteria_list' },
                                    { label: 'Tipos de Conferência', key: 'access_sub_conference_types' }
                                ]
                            },
                            { label: 'Tipos de Avaliação', key: 'access_tab_types', defaultAdm: true },
                            { label: 'Pessoas', key: 'access_tab_collab' },
                            { 
                                label: 'Parâmetros Avaliação', 
                                key: 'access_tab_config', 
                                defaultAdm: true,
                                children: [
                                    { label: 'Métricas Globais', key: 'access_sub_metricas', defaultAdm: true },
                                    { label: 'Prazos', key: 'access_sub_deadlines', defaultAdm: true },
                                    { label: 'Mensagens PDF', key: 'access_sub_messages', defaultAdm: true },
                                    { label: 'Competências', key: 'access_sub_competencias', defaultAdm: true },
                                    { label: 'Salário Mínimo', key: 'access_sub_minwage', defaultAdm: true },
                                    { label: 'Logomarca', key: 'access_sub_logo', defaultAdm: true },
                                    { label: 'Migração / Backup', key: 'access_sub_migration' }
                                ]
                            },
                                                    {
                                label: 'Usuários',
                                key: 'access_tab_users',
                                children: [
                                    { label: 'Gerenciar Usuários (Full)', key: 'access_sub_users_manage' },
                                    { label: 'Apenas Aprovar Pendentes', key: 'access_sub_users_approve', defaultAdm: true },
                                    { label: 'Auditoria e Logs', key: 'access_tab_audit', defaultAdm: true }
                                ]
                            },
                            { label: 'Cargos e Salários', key: 'access_tab_pcs' },
                            { 
                                label: 'Cadastros do Ponto', 
                                key: 'access_tab_ponto',
                                children: [
                                    { label: 'Aba: Regras Gerais (Jornada)', key: 'access_sub_ponto_rules' },
                                    { label: 'Aba: Situações (Justificativas)', key: 'access_sub_ponto_sits' },
                                    { label: 'Aba: Configurações de Notificações', key: 'access_sub_ponto_settings', defaultAdm: true }
                                ]
                            },                     
                            { 
                                label: 'Dados Externos', 
                                key: 'access_tab_training', 
                                children: [
                                    { label: 'Cursos e Treinamentos', key: 'access_sub_capacitacoes' },
                                    { label: 'Percepção da Equipe', key: 'access_sub_percepcao' },
                                    { label: 'IA', key: 'access_sub_ai', defaultAdm: true },
                                    { label: 'Config. Prazos Gestão', key: 'access_sub_collab_area_config', defaultAdm: true }
                                ]
                            },
                            { label: 'Backup e Restore', key: 'access_tab_backup' }
                        ]
                    },
                    { label: 'Ocorrências Técnicas', key: 'access_apt', defaultAdm: true },
                    { label: 'AEC - Lançamentos e Acompanhamento', key: 'access_eval', defaultAdm: true },
                    { 
                        label: 'Gerencial',
                        key: 'access_fin', 
                        defaultAdm: true,
                        children: [
                            { 
                                label: 'Estimativa Financeira', 
                                key: 'access_tab_proj', 
                                defaultAdm: true,
                                children: [
                                    { label: 'Efetivar Promoções', key: 'access_sub_execute_promo' },
                                    { label: 'Ver Custos Gestão (Anonimizado)', key: 'access_sub_fin_blind_gestao' }
                                ]
                            },
                            { label: 'Análise Gerencial', key: 'access_tab_analise', defaultAdm: true }
                        ]
                    },
                    {
                        label: 'Recursos Humanos (RH)',
                        key: 'access_rh',
                        defaultAdm: true,
                        children: [
                            { label: 'Central AEC', key: 'access_rh_central', defaultAdm: true },
                            { label: 'Acomp. PDIs | Feedbacks', key: 'access_rh_feedbacks', defaultAdm: true },
                            { label: 'Gestão de Talentos', key: 'access_talent', defaultAdm: true },
                            { label: 'Gestão de Pesquisas', key: 'access_rh_surveys', defaultAdm: true },
                            { label: 'Gestão de Cultura Acad', key: 'hr_cultura_acad', defaultAdm: true }
                        ]
                    },
                    {
                        label: 'Central do Colaborador (Visualização)',
                        key: 'access_collab_center',
                        defaultAdm: true,
                        children: [
                            { 
                                label: 'Gestão de Ponto (Operacional)', 
                                key: 'collab_tab_time', 
                                defaultAdm: true,
                                children: [
                                    { label: 'Aba: Registro de Ponto (Relógio)', key: 'ponto_tab_clock', defaultAdm: true },
                                    { label: 'Aba: Banco de Horas', key: 'ponto_tab_bank', defaultAdm: true },
                                    { label: 'Aba: Gestão do Ponto (Relatórios)', key: 'ponto_tab_reports', defaultAdm: true },
                                    { label: 'Ver Botão: Solicitações (Gestor)', key: 'ponto_btn_requests', defaultAdm: true },
                                    { label: 'Ver Botão: Inconsistências (Gestor)', key: 'ponto_btn_inconsistencies', defaultAdm: true },
                                    { label: 'Permitir Marcação Manual', key: 'ponto_can_manual', defaultAdm: true },
                                    { label: 'Permitir Envio de Justificativas', key: 'ponto_can_justify', defaultAdm: true },
                                    { label: 'Permitir Editar Marcações (Batidas)', key: 'ponto_can_edit', defaultAdm: true },
                                    { label: 'Permitir Excluir Marcações (Batidas)', key: 'ponto_can_delete', defaultAdm: true },
                                    { label: 'Incluir/Editar Observações Manuais', key: 'ponto_edit_manual_obs', defaultAdm: true },
                                    { label: 'Excluir Observações Manuais', key: 'ponto_delete_obs', defaultAdm: true },
                                    { label: 'Visão Global (Ver Todos Deptos)', key: 'ponto_view_all', defaultAdm: true }
                                ]
                            },
                            { label: 'Produtividade', key: 'access_prod_basic', defaultAdm: true }, // <--- ADICIONADO AQUI
                            { label: 'Capacitações', key: 'collab_tab_training', defaultAdm: true },
                            { label: 'Avaliar Gestão', key: 'collab_tab_perception', defaultAdm: true },
                            // Dentro da PERMISSION_STRUCTURE:
                            { 
                                label: 'Pesquisas & Clima', 
                                key: 'access_sub_surveys',
                                children: [
                                    { label: 'Responder Pesquisas', key: 'collab_tab_surveys', defaultAdm: true },
                                    { label: 'Configurações de Pesquisas (Admin)', key: 'access_surveys_config' } // <--- REQUISITO 2
                                ]
                            },
                            { label: 'Minhas Assinaturas', key: 'collab_tab_signatures', defaultAdm: true },
                            { label: 'Minha Evolução', key: 'collab_tab_growth', defaultAdm: true },
                            { label: 'Reconhecimento (Elogios)', key: 'collab_tab_recognition', defaultAdm: true },
                            { 
                                label: 'AcadCoins (Moeda Virtual)', 
                                key: 'collab_tab_acad_coins', 
                                defaultAdm: true,
                                children: [
                                    { label: 'Administrar AcadCoins (Caixa e Gestão)', key: 'acad_coins_admin', defaultAdm: true }
                                ]
                            },
                            { 
                                label: 'Cultura Acad (Normas e Documentos)', 
                                key: 'collab_tab_cultura_acad', 
                                defaultAdm: true,
                                children: [
                                    { label: 'Gestor de Cultura Acad (Criar/Liberar Conteúdos)', key: 'manage_cultura_acad', defaultAdm: true }
                                ]
                            },
                            { 
                                label: 'Gestão de Cultura Acad (RH)', 
                                key: 'hr_cultura_acad', 
                                defaultAdm: true 
                            },
                        ]
                    },
                    {
                    label: 'Permissões Avançadas',
                    key: 'advanced_features_group',
                    children: [
                        { label: 'Ver Pessoas do Tipo Gestão em Cadastros', key: 'viewGestaoCadastros' },
                        { label: 'Ver Notas de Calibração', key: 'eval_view_calibration', defaultAdm: true },
                        { label: 'Editar Metas (Pós-Início)', key: 'eval_edit_goals_locked' },
                        { label: 'Visualizar 9-Box', key: 'eval_view_9box', defaultAdm: true },
                        { label: 'Reabrir Avaliações Fechadas', key: 'eval_reopen_cycle' },
                        { label: 'Ignorar Prazos', key: 'eval_bypass_deadlines' },
                        { label: 'Excluir Avaliações', key: 'eval_delete_record' },
                        { label: 'Exibir Gestão no Dashboard/Avaliação', key: 'viewGestaoDashEval' },
                        { label: 'Bloquear Visualização Própria', key: 'eval_block_self_view' },
                        { label: 'Alterar Visão do Sistema (Cards/Lista)', key: 'allow_view_change', defaultAdm: true }
                    ]
                }
                ]
            },
    ];

        // ========================================================================
        // PERMISSIONAMENTO DINÂMICO PADRONIZADO (PRD multiaplicação)
        // - A aplicação é dona do seu catálogo.
        // - Catálogo publicado em permission_catalog com merge, sem apagar terceiros.
        // - A tela local lê somente APP_ID=PESSOAS.
        // - Perfis continuam no mesmo local; permissões individuais viram overrides.
        // ========================================================================
        const localPeoplePermissionCatalog = useMemo(() => {
            const output = [];
            let visualOrder = 0;

            const walk = (nodes = [], parentKey = null, moduleName = 'Gestão de Pessoas', depth = 0) => {
                (nodes || []).forEach((node) => {
                    if (!node?.key) return;
                    visualOrder += 10;
                    const isProjectRoot = depth === 0;
                    const currentModule = isProjectRoot
                        ? 'Aplicação'
                        : (depth === 1 ? String(node.label || moduleName) : moduleName);
                    output.push({
                        key: String(node.key),
                        label: isProjectRoot ? PEOPLE_PERMISSION_APP_NAME : String(node.label || node.key),
                        appId: PEOPLE_PERMISSION_APP_ID,
                        appName: PEOPLE_PERMISSION_APP_NAME,
                        module: currentModule,
                        parentKey: parentKey || null,
                        order: visualOrder,
                        defaultAdm: node.defaultAdm === true,
                        sensitive: /delete|excluir|audit|permission|permiss|global|migration|backup|reset|salary|sal[aá]rio/i.test(String(node.key || '') + ' ' + String(node.label || '')),
                        active: true,
                        source: 'application',
                        version: APP_VERSION,
                        treeId: node.id || node.key
                    });
                    walk(node.children || [], node.key, currentModule, depth + 1);
                });
            };

            walk(PERMISSION_STRUCTURE);

            // Permissão legada ainda usada em regras sensíveis e que precisa constar no catálogo.
            if (!output.some(item => item.key === 'viewGestao')) {
                visualOrder += 10;
                output.push({
                    key: 'viewGestao',
                    label: 'Acesso a Dados Sensíveis (Gestão)',
                    appId: PEOPLE_PERMISSION_APP_ID,
                    appName: PEOPLE_PERMISSION_APP_NAME,
                    module: 'Permissões Avançadas',
                    parentKey: 'advanced_features_group',
                    order: visualOrder,
                    defaultAdm: false,
                    sensitive: true,
                    active: true,
                    source: 'application',
                    version: APP_VERSION,
                    treeId: 'viewGestao'
                });
            }

            return output;
        }, []);

        // Converte o catálogo plano do Firebase na árvore usada pela UI.
        const dynamicPeoplePermissionStructure = useMemo(() => {
            const source = (peoplePermissionCatalog && peoplePermissionCatalog.length)
                ? peoplePermissionCatalog
                : localPeoplePermissionCatalog;

            const map = new Map();
            source
                .filter(item => item && item.key && item.active !== false)
                .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
                .forEach(item => {
                    map.set(item.key, {
                        ...item,
                        id: item.treeId || item.key,
                        children: [],
                        isNew: !item.reviewedAt
                    });
                });

            const roots = [];
            map.forEach(node => {
                const parent = node.parentKey ? map.get(node.parentKey) : null;
                if (parent) parent.children.push(node);
                else roots.push(node);
            });

            const sortTree = (nodes) => {
                nodes.sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
                nodes.forEach(n => sortTree(n.children || []));
                return nodes;
            };
            return sortTree(roots);
        }, [peoplePermissionCatalog, localPeoplePermissionCatalog]);

        const filterPeoplePermissionTree = useCallback((nodes = [], search = '', onlyNew = false) => {
            const term = String(search || '').trim().toLowerCase();
            const walk = (items) => (items || []).map(item => {
                const children = walk(item.children || []);
                const selfMatches = !term || [item.label, item.key, item.module]
                    .some(v => String(v || '').toLowerCase().includes(term));
                const newMatches = !onlyNew || item.isNew === true || children.length > 0;
                if ((selfMatches && newMatches) || children.length > 0) return { ...item, children };
                return null;
            }).filter(Boolean);
            return walk(nodes);
        }, []);

        // Publica/atualiza somente o catálogo desta aplicação e preserva metadados de revisão.
        useEffect(() => {
            if (!db || permissionCatalogSyncedRef.current) return;
            permissionCatalogSyncedRef.current = true;
            let cancelled = false;

            const catalogPath = `${FIRESTORE_PATH_PUBLIC}/permission_catalog`;

            const syncCatalog = async () => {
                setPeoplePermissionCatalogLoading(true);
                setPeoplePermissionCatalogError('');
                try {
                    const catalogRef = collection(db, catalogPath);
                    const snap = await getDocs(query(catalogRef, where('appId', '==', PEOPLE_PERMISSION_APP_ID)));
                    const existing = new Map(snap.docs.map(d => [d.id, { id: d.id, ...d.data() }]));
                    const batch = writeBatch(db);
                    const now = new Date().toISOString();

                    localPeoplePermissionCatalog.forEach(permission => {
                        const docId = `${PEOPLE_PERMISSION_APP_ID}__${permission.key}`;
                        const old = existing.get(docId) || {};
                        batch.set(doc(db, catalogPath, docId), {
                            ...permission,
                            firstPublishedAt: old.firstPublishedAt || now,
                            reviewedAt: old.reviewedAt ?? null,
                            reviewedBy: old.reviewedBy ?? null,
                            updatedAt: now
                        }, { merge: true });
                    });

                    await batch.commit();
                } catch (error) {
                    console.error('[PERMISSOES] Falha ao sincronizar permission_catalog:', error);
                    if (!cancelled) setPeoplePermissionCatalogError(error?.message || 'Falha ao sincronizar catálogo.');
                } finally {
                    if (!cancelled) setPeoplePermissionCatalogLoading(false);
                }
            };

            syncCatalog();
            return () => { cancelled = true; };
        }, [localPeoplePermissionCatalog]);

        // Listener ativo enquanto Cadastros está montado. A aplicação local enxerga só PESSOAS.
        useEffect(() => {
            if (!db) return undefined;
            const catalogPath = `${FIRESTORE_PATH_PUBLIC}/permission_catalog`;
            const catalogRef = collection(db, catalogPath);
            const qCatalog = query(catalogRef, where('appId', '==', PEOPLE_PERMISSION_APP_ID));
            const unsub = onSnapshot(qCatalog, snap => {
                const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setPeoplePermissionCatalog(list);
            }, error => {
                console.warn('[PERMISSOES] Listener do catálogo indisponível:', error);
                setPeoplePermissionCatalogError(error?.message || 'Falha ao ler catálogo.');
            });
            return () => unsub();
        }, []);

        // ATUALIZAÇÃO DO HELPERS DE PERMISSÕES PADRÃO (Necessário pois mudamos a estrutura)
        const DEFAULT_ADM_PERMISSIONS = useMemo(() => { 
            const defaults = {}; 
            const traverse = (items) => {
                items.forEach(item => {
                    if (item.defaultAdm && item.key) defaults[item.key] = true;
                    if (item.children) traverse(item.children);
                });
            };
            // Libera acesso mestre aos projetos principais por padrão
            PERMISSION_STRUCTURE.forEach(proj => {
                if(proj.id === 'av_desempenho') {
                    if(proj.key) defaults[proj.key] = true;
                    if(proj.children) traverse(proj.children);
                }
            });
            defaults.viewGestao = false;
            defaults.viewGestaoDashEval = false;
            return defaults; 
        }, []);


        // --- RENDERIZADOR RECURSIVO DE PERMISSÕES (PADRÃO HUB) ---
        const renderPermissionTree = (items, formState, setFormState, depth = 0, cascadeStructure = PERMISSION_STRUCTURE) => {
            if (!items || !Array.isArray(items)) return null;

            return items.map((item) => {
                const isChecked = item.key ? !!formState[item.key] : false;
                const hasChildren = item.children && item.children.length > 0;
                // No padrão HUB, os grupos começam abertos e podem ser recolhidos individualmente.
                const isExpanded = expandedPermGroups[item.key || item.label] !== false;
                const rowKey = item.key || item.label;
                const rowTone = isChecked
                    ? (isDarkMode ? 'bg-emerald-950/35 border-emerald-700/50' : 'bg-emerald-50 border-emerald-200')
                    : (isDarkMode ? 'bg-slate-800/45 border-slate-700' : 'bg-[#fbfaf7] border-slate-100');

                return (
                    <div key={rowKey} className="relative">
                        <div
                            className={`gp-permission-tree-row flex items-center min-h-[38px] rounded-md border mb-1 transition-colors ${rowTone}`}
                            style={{ marginLeft: `${depth * 28}px`, paddingLeft: '8px', paddingRight: '10px' }}
                        >
                            <div className="w-6 shrink-0 flex justify-center">
                                {hasChildren ? (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedPermGroups(prev => ({ ...prev, [rowKey]: !isExpanded }));
                                        }}
                                        className={`w-5 h-5 inline-flex items-center justify-center rounded ${isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-100'}`}
                                        title={isExpanded ? 'Recolher' : 'Expandir'}
                                    >
                                        {isExpanded ? <ChevronDown size={13}/> : <ChevronRight size={13}/>} 
                                    </button>
                                ) : null}
                            </div>

                            {item.key && (
                                <label className="shrink-0 inline-flex items-center justify-center mr-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => {
                                            const newVal = e.target.checked;
                                            const newPermissions = handlePermissionChange(item.key, newVal, formState, cascadeStructure);
                                            if (newVal && hasChildren) {
                                                setExpandedPermGroups(prev => ({ ...prev, [rowKey]: true }));
                                            }
                                            setFormState(newPermissions);
                                        }}
                                        className="w-4 h-4 accent-teal-600 cursor-pointer"
                                    />
                                </label>
                            )}

                            <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
                                <span className={`text-[12px] leading-5 ${hasChildren || isChecked ? 'font-black' : 'font-semibold'} ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                    {item.label}
                                </span>
                                {item.key && (
                                    <span className={`text-[9px] font-mono ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                        ({item.key})
                                    </span>
                                )}
                                {item.isNew && <span className="text-[8px] font-black uppercase tracking-wide text-orange-500">NOVA</span>}
                                {item.sensitive && <span className="px-1.5 py-0.5 rounded border border-rose-300 bg-rose-50 text-rose-600 text-[8px] font-black">Sensível</span>}
                            </div>
                        </div>

                        {hasChildren && isExpanded && (
                            <div className={`gp-permission-tree-children relative ${isDarkMode ? 'before:bg-slate-700' : 'before:bg-slate-200'}`}>
                                {renderPermissionTree(item.children, formState, setFormState, depth + 1, cascadeStructure)}
                            </div>
                        )}
                    </div>
                );
            });
        };

        // --- LÓGICA DE PERMISSÕES EM CASCATA (REQUISITOS 1 e 2) ---
        const handlePermissionChange = (targetKey, newValue, currentPerms, structure) => {
            let updates = { ...currentPerms };
            updates[targetKey] = newValue;

            // 1. Função Recursiva para encontrar o item e seus filhos (Cascata para Baixo)
            const cascadeDown = (items, keyToFind, forceValue) => {
                for (const item of items) {
                    if (item.key === keyToFind) {
                        // Encontrou o item alvo. Se tiver filhos, aplica o valor a todos eles recursivamente.
                        if (item.children) {
                            const setAllChildren = (children) => {
                                children.forEach(child => {
                                    if (child.key) updates[child.key] = forceValue;
                                    if (child.children) setAllChildren(child.children);
                                });
                            };
                            setAllChildren(item.children);
                        }
                        return true; // Stop search
                    }
                    if (item.children) {
                        if (cascadeDown(item.children, keyToFind, forceValue)) return true;
                    }
                }
                return false;
            };

            // 2. Função para encontrar e marcar os pais (Cascata para Cima)
            // Só executa se estiver MARCANDO (newValue === true)
            if (newValue === true) {
                const cascadeUp = (items, keyToFind, path = []) => {
                    for (const item of items) {
                        if (item.key === keyToFind) {
                            // Encontrou! Marca todos os pais no caminho como true
                            path.forEach(parentKey => {
                                if (parentKey) updates[parentKey] = true;
                            });
                            return true;
                        }
                        if (item.children) {
                            if (cascadeUp(item.children, keyToFind, [...path, item.key])) return true;
                        }
                    }
                    return false;
                };
                cascadeUp(structure, targetKey);
            }

            // Executa a cascata para baixo
            cascadeDown(structure, targetKey, newValue);

            return updates;
        };

        // ========================================================================
        // 3. EFEITOS (EFFECTS)
        // ========================================================================

        useEffect(() => { 
            if(initialTab) { 
                setTab(initialTab); setSearch(''); setSelectedTrainingIds([]); setSelectedCriteriaIds([]); setForm({}); 
                if(initialTab === 'users') setUsersSubTab('list'); 
            } 
        }, [initialTab]);

        // Auto-Link Departments logic (Trainings)
        useEffect(() => { 
            if (!data.trainingData || data.trainingData.length === 0) return; 
            const linkOrphanTrainings = () => { 
                let updatedCount = 0; 
                data.trainingData.forEach(t => { 
                    if (!t.departmentId || t.departmentName === 'N/D') { 
                        const collab = data.collaborators.find(c => c.email?.toLowerCase() === t.email?.toLowerCase()); 
                        if (collab && collab.departmentId) { 
                            const deptName = data.departments.find(d => d.id === collab.departmentId)?.name || 'N/D'; 
                            actions.saveTraining({ ...t, departmentId: collab.departmentId, departmentName: deptName }); 
                            updatedCount++; 
                        } 
                    } 
                }); 
                if (updatedCount > 0) console.log(`[Auto-Link] ${updatedCount} treinamentos vinculados.`); 
            }; 
            const intervalId = setInterval(linkOrphanTrainings, 5 * 60 * 1000); 
            return () => clearInterval(intervalId); 
        }, [data.trainingData, data.collaborators, data.departments, actions]);

        // ========================================================================
        // 4. HANDLERS (DEFINIDOS ANTES DO RENDER)
        // ========================================================================

        const filterList = (l) => l.filter(i => JSON.stringify(i).toLowerCase().includes(search.toLowerCase()));
        
        const visibleCollaborators = useMemo(() => data.collaborators.filter(c => canViewCollab(c, currentUser)), [data.collaborators, currentUser]);

        const getFilteredCriteria = () => { 
        return [...data.qualiCriteria, ...data.quantiCriteria].filter(item => {
            const s = search.toLowerCase();
            const isQuali = item.dim !== undefined && item.dim !== null;
            const typeLabel = isQuali ? 'qualitativa' : 'quantitativa';
            
            // Garante que campos nulos não quebrem o filtro de busca
            const itemName = item.name || '';
            const itemDim = item.dim || '';
            const itemPersonType = item.personType || '';
            const itemDesc = item.description || '';

            return (
                itemName.toLowerCase().includes(s) || 
                itemDim.toLowerCase().includes(s) ||
                itemPersonType.toLowerCase().includes(s) || 
                typeLabel.includes(s) || 
                itemDesc.toLowerCase().includes(s)
            );
        }).sort((a, b) => { 
            const isQualiA = a.dim !== undefined && a.dim !== null; 
            const isQualiB = b.dim !== undefined && b.dim !== null; 
            
            // 1. Mantém qualitativas primeiro
            if (isQualiA && !isQualiB) return -1; 
            if (!isQualiA && isQualiB) return 1; 
            
            // 2. Ordenação alfabética com "Optional Chaining" para evitar o erro de undefined
            const nameA = a.name || "";
            const nameB = b.name || "";
            return nameA.localeCompare(nameB); 
        });
        };
        // --- Handlers de Critérios ---
        const toggleSelectCriteria = (id) => { if (selectedCriteriaIds.includes(id)) setSelectedCriteriaIds(prev => prev.filter(i => i !== id)); else setSelectedCriteriaIds(prev => [...prev, id]); };
        const toggleSelectAllCriteria = () => { 
            const allItems = getFilteredCriteria(); const allIds = allItems.map(i => i.id); 
            const allSelected = allIds.every(id => selectedCriteriaIds.includes(id)); 
            if (allSelected) setSelectedCriteriaIds(prev => prev.filter(id => !allIds.includes(id))); 
            else setSelectedCriteriaIds(prev => [...new Set([...prev, ...allIds])]); 
        };
        const handleBulkDeleteCriteria = () => { 
            if (selectedCriteriaIds.length === 0) return;
            if (!window.confirm(`Deseja excluir ${selectedCriteriaIds.length} critérios?`)) return; 
            selectedCriteriaIds.forEach(id => actions.genericDelete('criteria', id));
            setSelectedCriteriaIds([]); 
        };

        // --- Handlers de Treinamento ---
        const toggleSelectTraining = (id) => { if (selectedTrainingIds.includes(id)) setSelectedTrainingIds(prev => prev.filter(i => i !== id)); else setSelectedTrainingIds(prev => [...prev, id]); };
        const toggleSelectAllTraining = (visibleItems) => { 
            const allIds = visibleItems.map(i => i.id); 
            const allSelected = allIds.every(id => selectedTrainingIds.includes(id)); 
            if (allSelected) setSelectedTrainingIds(prev => prev.filter(id => !allIds.includes(id))); 
            else setSelectedTrainingIds([...new Set([...selectedTrainingIds, ...allIds])]); 
        };
        const handleBulkDelete = async () => {
            if (selectedTrainingIds.length === 0) return;

            setConfirmModal({
                isOpen: true,
                title: "Excluir Cursos",
                message: `Deseja realmente excluir ${selectedTrainingIds.length} curso(s) selecionado(s)? Esta ação também excluirá os AcadCoins vinculados.`,
                onConfirm: async () => {
                    try {
                        const selectedIds = selectedTrainingIds.map(String);

                        const selectedTrainings = (data.trainingData || []).filter(t =>
                            selectedIds.includes(String(t.id))
                        );

                        let removedCoursesCount = 0;
                        let removedCoinsCount = 0;

                        const distRef = collection(db, `${FIRESTORE_PATH_PUBLIC}/acadCoinsDist`);
                        const trainingCoinsQuery = query(
                            distRef,
                            where('type', '==', 'treinamento')
                        );

                        const trainingCoinsSnap = await getDocs(trainingCoinsQuery);
                        const batch = writeBatch(db);

                        selectedTrainings.forEach(training => {
                            const deterministicCoinId = `training_${String(training.id).replace(/[^a-zA-Z0-9_-]/g, '_')}`;
                            const courseTitle = String(training.description || training.title || '').trim().toLowerCase();

                            trainingCoinsSnap.forEach(docSnap => {
                                const coin = {
                                    id: docSnap.id,
                                    ...docSnap.data()
                                };

                                const sameSourceId =
                                    String(coin.sourceId || '') === String(training.id);

                                const sameDeterministicId =
                                    String(docSnap.id) === String(deterministicCoinId);

                                if (sameSourceId || sameDeterministicId) {
                                batch.delete(docSnap.ref);
                                removedCoinsCount++;
                            }
                            });

                            batch.delete(doc(db, `${FIRESTORE_PATH_PUBLIC}/acadCoinsDist`, deterministicCoinId));
                            batch.delete(doc(db, `${FIRESTORE_PATH_PUBLIC}/trainingData`, String(training.id)));

                            removedCoursesCount++;
                        });

                        await batch.commit();

                        setSelectedTrainingIds([]);
                        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });

                        setValidationAlert({
                            title: "Sucesso!",
                            message: `${removedCoursesCount} curso(s) excluído(s) com sucesso.\n\n${removedCoinsCount} lançamento(s) de AcadCoins vinculado(s) foram excluídos.`
                        });

                    } catch (error) {
                        console.error(error);

                        setValidationAlert({
                            title: "Erro ao excluir cursos",
                            message: error.message
                        });
                    }
                }
            });
        };

        // --- Handlers de Percepção ---
        const toggleSelectPerception = (id) => { if (selectedPerceptionIds.includes(id)) setSelectedPerceptionIds(prev => prev.filter(i => i !== id)); else setSelectedPerceptionIds(prev => [...prev, id]); };
        const toggleSelectAllPerception = () => { const allIds = (data.teamPerception || []).map(i => i.id); if (selectedPerceptionIds.length === allIds.length) setSelectedPerceptionIds([]); else setSelectedPerceptionIds(allIds); };
        const handleBulkDeletePerception = () => { if (!window.confirm(`Excluir ${selectedPerceptionIds.length} registros?`)) return; selectedPerceptionIds.forEach(id => actions.genericDelete('teamPerception', id)); setSelectedPerceptionIds([]); };
        const handleEditPerception = (item) => { setPercepForm({ ...item }); setIsPercepEditModalOpen(true); };
        const handleSavePerception = () => { 
            if (!percepForm.email || !percepForm.nota) return alert("Preencha os campos."); 
            const notaFloat = parseFloat(String(percepForm.nota).replace(',', '.'));
            actions.saveTeamPerception({ ...percepForm, nota: notaFloat }); setIsPercepEditModalOpen(false); 
        };

                const handleSaveColumn = () => {
                        if (!columnForm.description || !columnForm.startDate) {
                            console.error("Campos obrigatórios vazios.");
                            return; 
                        }
                        
                        try {
                            actions.savePerceptionCycle({
                                ...columnForm,
                                order: parseInt(columnForm.order) || 1, 
                                id: columnForm.id || Date.now().toString()
                            });
                            
                            setIsColumnModalOpen(false);
                            
                            // --- CORREÇÃO: Limpar também os campos de vínculo ---
                            setColumnForm({ 
                                description: '', 
                                notes: '', 
                                startDate: '', 
                                endDate: '', 
                                order: 1, 
                                linkedField: '', 
                                linkedQuestionId: '' 
                            });
                            // ----------------------------------------------------
                            
                            console.log("Coluna salva.");
                        } catch (error) {
                            console.error("Erro ao salvar:", error);
                        }
                    };

            const handleEditColumn = (col) => {
                    setColumnForm({
                        id: col.id,
                        description: col.description,
                        notes: col.notes || '',
                        startDate: col.startDate,
                        endDate: col.endDate || '',
                        order: col.order || 1,
                        // --- CORREÇÃO: Adicionando os campos de vínculo ---
                        linkedField: col.linkedField || '', 
                        linkedQuestionId: col.linkedQuestionId || '' 
                        // --------------------------------------------------
                    });
                    setIsColumnModalOpen(true);
                };

        // ========================================================================
        // LÓGICA MIGRADA: CARGOS E SALÁRIOS (PCS)
        // ========================================================================

        const getPcsRank = (role, seniority, level) => {
            let score = 0;
            const r = (role || '').toUpperCase().trim();
            if (r.includes('ESTAGI')) score += 10000;
            else if (r.includes('AUXILIAR')) score += 20000;
            else if (r.includes('ASSISTENTE')) score += 30000;
            else if (r.includes('ANALISTA')) score += 40000;
            else if (r.includes('ENCARREGADO')) score += 50000;
            else if (r.includes('COORDENADOR')) score += 55000;
            else if (r.includes('GERENTE')) score += 60000;
            else if (r.includes('DIRETOR')) score += 70000;
            else score += 1000;
            // Senioridade
            const s = (seniority || '').toUpperCase().trim();
            if (s.includes('JR')) score += 100; else if (s.includes('PL')) score += 200; else if (s.includes('SR')) score += 300;
            // Nível
            const l = (level || '').toUpperCase().trim();
            if (l === 'I' || l === '1') score += 1; else if (l === 'II' || l === '2') score += 2; else if (l === 'III' || l === '3') score += 3; else if (l === 'IV' || l === '4') score += 4;
            return score;
        };
        const getPcsSortRank = (item) => getPcsRank(item.role, item.seniority, item.level);

        const filteredPcsList = useMemo(() => {
            return (data.pcs || []).filter((p) => {
                if (currentUser.role !== 'SuperAdm') {
                    const userDeptName = data.departments.find(d => d.id === currentUser.departmentId)?.name;
                    if (p.department !== 'Todos' && p.department !== 'Geral' && p.department !== userDeptName) return false;
                }
                const s = pcsSearch.toLowerCase();
                return !pcsSearch || p.role.toLowerCase().includes(s) || p.department.toLowerCase().includes(s);
            }).sort((a, b) => getPcsSortRank(a) - getPcsSortRank(b));
        }, [data.pcs, pcsSearch, currentUser, data.departments]);

        // HANDLERS PCS
        const handleSavePCSForm = () => {
            if (!form.role || !form.salary) return alert("Preencha Cargo e Salário.");
            actions.savePCS({ ...form, id: form.id || Date.now().toString() });
            actions.logAction('EDICAO_PCS_MANUAL', { acao: form.id ? 'Atualização' : 'Novo Cargo', cargo: form.role, salario: form.salary });
            setIsPCSModalOpen(false);
            setForm({});
        };

        const toggleSelectPcs = (id) => {
            if (selectedPcsIds.includes(id)) setSelectedPcsIds((prev) => prev.filter(i => i !== id));
            else setSelectedPcsIds((prev) => [...prev, id]);
        };
        const toggleSelectAllPcs = () => {
            const allIds = filteredPcsList.map(p => p.id);
            const allSelected = allIds.every(id => selectedPcsIds.includes(id));
            if (allSelected) setSelectedPcsIds((prev) => prev.filter(id => !allIds.includes(id)));
            else setSelectedPcsIds((prev) => [...new Set([...prev, ...allIds])]);
        };

        const handleBulkDeletePcs = () => {
            if (selectedPcsIds.length === 0) return;
            if(!window.confirm(`Excluir ${selectedPcsIds.length} cargos?`)) return;
            selectedPcsIds.forEach(id => actions.deletePCS(id));
            actions.logAction('EXCLUSAO_EM_BLOCO_PCS', { qtd: selectedPcsIds.length });
            setSelectedPcsIds([]);
        };

        const handleBulkEditSave = () => {
            let count = 0;
            selectedPcsIds.forEach((id) => {
                const original = data.pcs.find(p => p.id === id);
                if (!original) return;
                const updated = { ...original };
                let changed = false;
                if (bulkPcsForm.role && bulkPcsForm.role.trim() !== "") { updated.role = bulkPcsForm.role; changed = true; }
                if (bulkPcsForm.seniority && bulkPcsForm.seniority.trim() !== "") { updated.seniority = bulkPcsForm.seniority; changed = true; }
                if (bulkPcsForm.level && bulkPcsForm.level.trim() !== "") { updated.level = bulkPcsForm.level; changed = true; }
                if (bulkPcsForm.department && bulkPcsForm.department.trim() !== "") { updated.department = bulkPcsForm.department; changed = true; }
                if (bulkPcsForm.validFrom && bulkPcsForm.validFrom.trim() !== "") { updated.validFrom = bulkPcsForm.validFrom; changed = true; }
                if (bulkPcsForm.salary && bulkPcsForm.salary !== "") { updated.salary = parseFloat(bulkPcsForm.salary); changed = true; }
                if (changed) { actions.savePCS(updated); count++; }
            });
            if (count > 0) {
                actions.logAction('EDICAO_EM_BLOCO_PCS', {
                    entity: 'PCS',
                    recordId: 'BULK_PCS',
                    details: [
                        { campo: 'qtd', de: '', para: count }
                    ]
                });
                alert(`${count} cargos atualizados com sucesso.`);
            }
            setIsBulkEditPcsOpen(false);
            setSelectedPcsIds([]);
        };

        const handleMassReadjust = () => {
            if (!readjustPercent || !readjustDate) return alert("Preencha os dados.");
            const newRecords = data.pcs.map((p) => ({
                ...p,
                id: Date.now() + Math.random().toString(),
                salary: parseFloat(p.salary) * (1 + parseFloat(readjustPercent) / 100),
                validFrom: readjustDate
            }));
            actions.importPCSBatch(newRecords);
            setModal(null);
            alert("Reajuste aplicado com sucesso (Novos registros criados).");
        };

        // FUNÇÕES DE IMPORTAÇÃO (PDF/EXCEL)
        const handleFileSelect = (e) => { if (e.target.files && e.target.files[0]) setSelectedFile(e.target.files[0]); };
        const confirmImport = async () => {
            setImportModalOpen(false);
            setIsProcessing(true);
            try {
                if (typeof loadImportLibs !== 'undefined') await loadImportLibs();
                // readImportFile e mapImportedDataToPCS são funções globais que já existem no seu arquivo
                const importedData = await readImportFile(selectedFile);
                if (!importedData || importedData.length === 0) throw new Error("Arquivo vazio ou inválido.");
                const newPCS = mapImportedDataToPCS(importedData, importConfig, importDate);
                if (newPCS.length === 0) throw new Error("Nenhum cargo identificado.");
                actions.importPCSBatch(newPCS);
                setSelectedFile(null);
                alert("Importação realizada com sucesso!");
            } catch (e) {
                console.error(e);
                alert("Erro na importação: " + e.message);
            } finally {
                setIsProcessing(false);
            }
        };
        // ========================================================================

        // --- Handlers Gerais ---
        const handleNewClick = () => {
            if (tab === 'users') { 
                setUserForm({ id:'', name:'', email:'', role:'ADM', departmentId:'', permissions: { ...DEFAULT_ADM_PERMISSIONS } });
                setIsUserModalOpen(true); 
            } else { 
                setForm({}); 
                if (tab === 'criteria') setCriteriaType('quali');
                if (tab === 'collab') setForm({ personType: 'Colaborador' }); 
                if (tab === 'gestao') setForm({ type: 'Metas', maxPoints: 20 });
                if (tab === 'config') { 
                    if (configSubTab === 'metricas') setForm({ name: '', mediaPromocao: 8.0, mediaPremiacao: 7.0, maxPointsLost: 10.0, startDate: '', endDate: '' });
                    else if (configSubTab === 'deadlines') setForm({ departmentId: '', personType: 'Colaborador', q1: '', q2: '', q3: '', q4: '' });
                    else if (configSubTab === 'messages') setForm({ type: 'Promoção', personType: 'Colaborador', departmentId: 'all', title: '', body: '', bgColor: '#ffffff', textColor: '#000000' });
                    else if (configSubTab === 'competencias') setForm({ name: '', departmentId: '', personType: 'Todos', startDate: '', roleIds: [] });
                    else if (configSubTab === 'minWage') setForm({ value: '', validFrom: '' }); 
                } 
                setIsCadModalOpen(true); 
            }
        };

                        // ========================================================================
                    // --- FUNÇÃO DE EXCLUSÃO SEGURA (BLINDAGEM REFERENCIAL) ---
                    // ========================================================================
                    const handleSafeDelete = (type, item) => {
                        let isBlocked = false;
                        let blockReason = "";
                        const itemName = item.name || item.title || item.description || 'este registo';

                        // 1. Regras para Departamentos
                        if (type === 'dept') {
                            const hasCollabs = data.collaborators.some(c => c.departmentId === item.id);
                            const hasManagers = data.managers.some(m => m.departmentId === item.id || (m.deptShare && m.deptShare.some(ds => ds.deptId === item.id)));
                            if (hasCollabs) { isBlocked = true; blockReason = "Existem pessoas vinculadas a este departamento."; }
                            else if (hasManagers) { isBlocked = true; blockReason = "Existem gestores vinculados a este departamento."; }
                        }
                        // 2. Regras para Pessoas (Colaboradores)
                        else if (type === 'collab') {
                            const hasEvals = data.evaluations.some(e => e.collaboratorId === item.id);
                            const hasTrainings = data.trainingData.some(t => t.email?.toLowerCase() === item.email?.toLowerCase());
                            const hasAps = data.apontamentos.some(a => a.collaboratorId === item.id);
                            if (hasEvals) { isBlocked = true; blockReason = "Esta pessoa possui registros de AEC - Acompanhamento de Evolução Continuada."; }
                            else if (hasTrainings) { isBlocked = true; blockReason = "Esta pessoa possui cursos/treinamentos registados."; }
                            else if (hasAps) { isBlocked = true; blockReason = "Esta pessoa possui ocorrências (apontamentos) registadas."; }
                        }
                        // 3. Regras para Gestores
                        else if (type === 'manager') {
                            const hasCollabs = data.collaborators.some(c => c.managerIds && c.managerIds.includes(item.id));
                            if (hasCollabs) { isBlocked = true; blockReason = "Existem pessoas que têm este gestor como responsável direto."; }
                        }
                        // 4. Regras para Tipos de Avaliação
                        else if (type === 'types') {
                            const hasCollabs = data.collaborators.some(c => c.evalTypeId === item.id);
                            const hasEvals = data.evaluations.some(e => e.evalTypeName === item.name);
                            if (hasCollabs) { isBlocked = true; blockReason = "Existem pessoas a utilizar este tipo de avaliação no cadastro."; }
                            else if (hasEvals) { isBlocked = true; blockReason = "Existem avaliações já geradas com este tipo."; }
                        }
                        // 5. Regras para Critérios (Quali e Quanti)
                        else if (type === 'criteria') {
                            const isUsedInTypes = data.evaluationTypes.some(t => 
                                (t.selectedQualiIds && t.selectedQualiIds.includes(item.id)) || 
                                (t.selectedQuantiIds && t.selectedQuantiIds.includes(item.id))
                            );
                            if (isUsedInTypes) { isBlocked = true; blockReason = "Este critério está a ser utilizado dentro de um ou mais 'Tipos de Avaliação'."; }
                        }

                        // Se encontrou algum bloqueio, exibe o alerta visual e cancela a exclusão
                        if (isBlocked) {
                            setValidationAlert({ 
                                title: "Exclusão Bloqueada", 
                                message: `Não é possível excluir '${itemName}'.\n\nMotivo: ${blockReason}\n\nTem de remover ou transferir estes vínculos antes de excluir.` 
                            });
                            return;
                        }

                        // Se passou por todas as travas, abre o modal de confirmação para excluir
                        setConfirmModal({
                            isOpen: true,
                            title: "Confirmar Exclusão",
                            message: `Tem a certeza que deseja excluir '${itemName}' permanentemente?`,
                            onConfirm: () => {
                                actions.genericDelete(type, item.id);
                                setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
                            }
                        });
                    };


                    const handleSave = () => { 
                        // 1. Função interna ajustada para usar o nome/título completo
                        const generateUniqueInternalId = (obj) => {
                            // Remove espaços e caracteres especiais básicos para garantir um ID limpo no banco
                            const namePart = (obj.name || obj.title || '').trim().replace(/\s+/g, '-');
                            const emailPart = (obj.email || '').substring(0,3);
                            
                            // Retorna o ID com o nome completo + sufixo aleatório para evitar duplicidade exata
                            return `ID-${namePart.toUpperCase()}-${Math.random().toString(36).substr(2, 4)}`;
                        };

                        // 2. Lógica de Identidade: Garante que o uniqueId seja o ID do documento no banco
                        const newUniqueId = form.uniqueId || generateUniqueInternalId(form);
                        const finalId = form.id || newUniqueId;

                        const item = { 
                            ...form, 
                            id: finalId,
                            uniqueId: newUniqueId
                        };

                        // 3. Validação Genérica: Data de Início obrigatória para Departamentos e Pessoas
                        if ((tab === 'dept' || tab === 'collab') && !item.startDate) {
                            setValidationAlert("Por favor, preencha a data de início.");
                            return;
                        }
                        
                        // 4. Validação Específica: Tipos de Avaliação
                        if (tab === 'types') {
                            const missingFields = [];
                            if (!item.name || !item.name.trim()) missingFields.push("Nome da Configuração");
                            if (!item.startDate) missingFields.push("Data de Início");
                            if (item.mediaPromocao === undefined || item.mediaPromocao === '' || isNaN(item.mediaPromocao)) {
                                missingFields.push("Média para Promoção");
                            }
                            if (item.mediaPremiacao === undefined || item.mediaPremiacao === '' || isNaN(item.mediaPremiacao)) {
                                missingFields.push("Média para Premiação");
                            }
                            if (item.maxPointsLost === undefined || item.maxPointsLost === '' || isNaN(item.maxPointsLost)) {
                                missingFields.push("Máximo de Pontos Perdidos");
                            }

                            if (missingFields.length > 0) {
                                setValidationAlert(`Não foi possível salvar.\n\nPor favor, preencha os seguintes campos obrigatórios:\n\n• ${missingFields.join('\n• ')}`);
                                return;
                            }
                        }

                        // 5. Validação Específica: Gestores (Rateio)
                        if (tab === 'manager') { 
                            if (!item.departmentId) {
                                setValidationAlert("Por favor, selecione o Departamento Principal do Gestor.");
                                return;
                            }

                            const shares = item.deptShare || [];
                            if (shares.length > 0) { 
                                const total = shares.reduce((a,b) => a + (b.share || 0), 0);
                                if (shares.length > 1 && Math.abs(total - 100) > 0.1) {
                                    setValidationAlert(`Erro de Rateio: A soma das porcentagens deve ser 100%.\nAtual: ${total}%`);
                                    return;
                                }
                                if (shares.length === 1) item.deptShare[0].share = 100;
                            } 
                        } 
                        
                        // 6. Mapeamento de Ações de Salvamento
                        const map = { 
                            dept: actions.saveDept, 
                            manager: actions.saveManager, 
                            collab: actions.saveCollab, 
                            criteria: actions.saveCriteria, 
                            types: actions.saveType, 
                            gestao: actions.saveGestaoMetasPercepcao, 
                            training: actions.saveTraining, 
                            pcs: actions.savePCS 
                        };

                        // 7. Logs de Auditoria para Colaboradores
                        if (tab === 'collab') { 
                            const oldRecord = data.collaborators.find(c => c.id === item.id);
                            if (oldRecord) { 
                                const oldRoleFull = `${oldRecord.role} ${oldRecord.seniority} ${oldRecord.level}`;
                                const newRoleFull = `${item.role} ${item.seniority} ${item.level}`;

                                if (oldRoleFull !== newRoleFull) {
                                    actions.logAction('ALTERACAO_CARGO', {
                                            entity: 'COLABORADORES',
                                            recordId: item.id,
                                            details: [
                                                {
                                                    campo: 'cargo',
                                                    de: oldRoleFull,
                                                    para: newRoleFull
                                                }
                                            ]
                                        });

                                        actions.logAction('ALTERACAO_SALARIAL_MANUAL', {
                                            entity: 'COLABORADORES',
                                            recordId: item.id,
                                            details: [
                                                {
                                                    campo: 'salario',
                                                    de: oldRecord.salary,
                                                    para: item.salary
                                                }
                                            ]
                                        });

                                        actions.logAction('EDICAO_DADOS_CADASTRAIS', {
                                            entity: 'COLABORADORES',
                                            recordId: item.id,
                                            details: [
                                                {
                                                    campo: 'nome',
                                                    de: oldRecord?.name || '',
                                                    para: item.name
                                                }
                                            ]
                                        });

                                        actions.logAction('NOVO_COLABORADOR', {
                                            entity: 'COLABORADORES',
                                            recordId: item.id,
                                            details: [
                                                {
                                                    campo: 'nome',
                                                    de: '',
                                                    para: item.name
                                                }
                                            ]
                                        });
                                }
                            } else { 
                                actions.logAction('NOVO_COLABORADOR', { nome: item.name });
                            } 
                        } 
                        
                        // 8. Execução do Salvamento (Configurações vs Cadastros)
                        if (tab === 'config') { 
                            if (configSubTab === 'metricas') actions.saveConfig(item);
                            else if (configSubTab === 'deadlines') { 
                                if (!item.q1 || !item.q4) {
                                    setValidationAlert("Preencha as datas dos prazos.");
                                    return;
                                } 
                                actions.saveDeadline(item);
                            } 
                            else if (configSubTab === 'messages') actions.saveMsg(item);
                            else if (configSubTab === 'competencias') actions.saveCompetencia(item);
                            else if (configSubTab === 'minWage') { 
                                const mwItem = { id: item.id, value: parseFloat(item.value), validFrom: item.validFrom };
                                if(item.id) actions.updateMinWageHistory(mwItem); else actions.addMinWageHistory(mwItem); 
                            } 
                        } else { 
                            if (map[tab]) map[tab](item);
                        } 

                        setForm({}); 
                        setIsCadModalOpen(false); 
                    };

        
        const handleEdit = (item) => { 
            // Certifica-se de que o ID e o uniqueId originais entram no estado do formulário
            setForm({ ...item }); 
            if (tab === 'criteria') setCriteriaType(item.dim ? 'quali' : 'quanti');
            if (tab === 'collab') setForm({...item, personType: item.personType || 'Colaborador'}); 
            setIsCadModalOpen(true); 
        };

        // --- Handlers de Usuário (COM LOGS DE AUDITORIA) ---
        const handleSaveUser = async () => { 
            if (!userForm.name || !userForm.email) return;
            
            // Identifica o contexto da ação
            const isNew = !userForm.id;
            const existingUser = !isNew ? data.users.find(u => u.id === userForm.id) : null;
            const isApproval = existingUser && existingUser.isPending && userForm.isPending === false;

            const userToSave = {
                ...userForm,
                id: userForm.id || Date.now().toString(),
                role: userForm.role || 'ADM',
                isPending: false,

                // mantém o vínculo do usuário com o perfil de permissões
                profileId: userForm.profileId || userForm._profileId || '',
                _profileId: userForm._profileId || userForm.profileId || ''
            };

            await actions.saveUser(userToSave);

            // Log de Auditoria
            if (isNew) {
                actions.logAction('CRIACAO_USUARIO', {
                    entity: 'USUARIOS',
                    recordId: userToSave.id,
                    details: [
                        { campo: 'nome', de: '', para: userForm.name },
                        { campo: 'email', de: '', para: userForm.email },
                        { campo: 'perfil', de: '', para: userForm.role }
                    ]
                });
            } else if (isApproval) {
                actions.logAction('LIBERACAO_USUARIO_PENDENTE', {
                    entity: 'USUARIOS',
                    recordId: userToSave.id,
                    details: [
                        { campo: 'status', de: 'PENDENTE', para: 'LIBERADO' },
                        { campo: 'aprovado_por', de: '', para: currentUser.name }
                    ]
                });
            } else {
                actions.logAction('EDICAO_CADASTRO_USUARIO', {
                        entity: 'USUARIOS',
                        recordId: userToSave.id,
                        details: [
                            { campo: 'nome', de: existingUser?.name || '', para: userForm.name },
                            { campo: 'email', de: existingUser?.email || '', para: userForm.email },
                            { campo: 'perfil', de: existingUser?.role || '', para: userForm.role },
                            { campo: 'departmentId', de: existingUser?.departmentId || '', para: userForm.departmentId || '' }
                        ]
                    });
            }
        };

        // Nova função para Exclusão com Log (Substitui a chamada direta no JSX)
        const handleDeleteUserWithLog = async (userId) => {
            const userToDelete = data.users.find(u => u.id === userId);
            if (!userToDelete) return;
            
            if (window.confirm(`Tem certeza que deseja excluir o usuário ${userToDelete.name}?`)) {
                await actions.deleteUser(userId);
                actions.logAction('EXCLUSAO_USUARIO', {
                    entity: 'USUARIOS',
                    recordId: userToDelete.id,
                    details: [
                        { campo: 'nome', de: userToDelete.name, para: '' },
                        { campo: 'email', de: userToDelete.email, para: '' },
                        { campo: 'deletado_por', de: '', para: currentUser.name }
                    ]
                });
            }
        };

        const handleApproveUser = (u) => { setUserForm({...u, isPending: false}); setIsUserModalOpen(true); }; // Prepara form já tirando o pendente visualmente
        const handleEditUser = (u) => { setUserForm(u); setIsUserModalOpen(true); };
        
        // --- Handlers de Permissões (COM LOGS) ---
        const handleOpenGlobalPermissions = () => { 
            setPermissionForm(data.activePermissions || {}); 
            setEditingUserName('Global'); 
            setIsPermModalOpen(true);
        };
        const handleOpenUserPermissions = (u) => {
            const profileId = u.profileId || u._profileId || '';
            const profile = (data.permissionProfiles || []).find(p => p.id === profileId);
            const baseProfilePermissions = profile?.permissions || {};
            const effectivePermissions = profileId
                ? { ...baseProfilePermissions, ...(u.permissionOverrides || {}) }
                : { ...(u.permissions || {}), ...(u.permissionOverrides || {}) };
            setPermissionForm({ ...effectivePermissions, _userId: u.id });
            setEditingUserName(u.name);
            setIsPermModalOpen(true);
        };

        const handleSavePermissions = async () => { 
            if (permissionForm._userId) { 
                const targetUser = data.users.find(u => u.id === permissionForm._userId);
                if (targetUser) { 
                    const { _userId, ...cleanPerms } = permissionForm;
                    const profileId = targetUser.profileId || targetUser._profileId || '';
                    const profile = (data.permissionProfiles || []).find(p => p.id === profileId);
                    const baseProfilePermissions = profile?.permissions || {};
                    const overrides = {};
                    const allKeys = new Set([
                        ...Object.keys(baseProfilePermissions),
                        ...Object.keys(cleanPerms)
                    ]);
                    allKeys.forEach(key => {
                        const baseValue = baseProfilePermissions[key] === true;
                        const selectedValue = cleanPerms[key] === true;
                        if (!profileId || baseValue !== selectedValue) overrides[key] = selectedValue;
                    });
                    await actions.saveUser({
                        ...targetUser,
                        profileId,
                        _profileId: profileId,
                        permissionOverrides: overrides,
                        // Snapshot materializado para manter compatibilidade com as regras legadas atuais.
                        permissions: cleanPerms
                    });
                    
                    // Log de Auditoria (Individual)
                    actions.logAction('ALTERACAO_PERMISSOES_USUARIO', { 
                        alvo: targetUser.name, 
                        permissoes_alteradas: Object.keys(cleanPerms).length 
                    });
                } 
            } else { 
                await actions.updateActivePermissions(permissionForm);
                
                // Log de Auditoria (Global)
                actions.logAction('ALTERACAO_PERMISSOES_GLOBAIS', { 
                    detalhes: 'Alteração nas chaves mestras do sistema' 
                });
            } 
            setIsPermModalOpen(false); 
        };

        // --- Reset de Senha (COM LOG) ---
        const handleOpenReset = (u) => { setUserToReset(u); setIsResetModalOpen(true); };
        
        const handleConfirmReset = async () => { 
            if (!userToReset || !resetPasswordValue) return; 
            await actions.saveUser({ ...userToReset, password: resetPasswordValue, mustChangePassword: true });
            
            // Log de Auditoria
            actions.logAction('RESET_SENHA_USUARIO', { 
                alvo: userToReset.name, 
                motivo: 'Reset manual administrativo' 
            });

            alert(`Senha de ${userToReset.name} alterada com sucesso!`); 
            setIsResetModalOpen(false); setUserToReset(null); 
        };

                    // --- HANDLERS DE SINCRONIZAÇÃO (RESTAURADOS DO ANTERIOR) ---
                        const handleConfirmSync = async () => { 
                            if (!syncYearInput || syncYearInput.length !== 4) return alert("Ano inválido."); // Alterado notify para alert para garantir compatibilidade
                            setIsUpdatingSheet(true);
                            try { 
                                const result = await actions.syncTrainingsGlobal(syncYearInput); 
                                setIsUpdatingSheet(false); setIsSyncModalOpen(false); 
                                alert(`Sincronização concluída! ${result.count} registros importados.`); 
                            } catch (error) { 
                                setIsUpdatingSheet(false); alert("Erro: " + error.message);
                            } 
                        };

                        const handleConfirmPerceptionSync = async () => {
                            if (!perceptionYearInput || perceptionYearInput.length !== 4) return alert("Ano inválido.");
                            setIsUpdatingSheet(true);
                            try {
                                const result = await actions.syncTeamPerceptionGlobal(perceptionYearInput);
                                setIsUpdatingSheet(false); setIsPerceptionSyncModalOpen(false);
                                alert(`Sincronização concluída! ${result.count} registros importados.`);
                            } catch (error) {
                                setIsUpdatingSheet(false); alert("Erro: " + error.message);
                            }
                        };

                        // --- FUNÇÃO DE MIGRAÇÃO EM MASSA (VINCULAR AO PROJETO) ---
                    const handleLinkAllToProject = async () => {
                        if (!window.confirm("Isso dará permissão de acesso ao projeto 'AEC - Acompanhamento de Evolução Continuada' para TODOS os usuários cadastrados. Continuar?")) return;
                        
                        setIsMigrating(true);
                        let count = 0;
                        
                        try {
                            const batchPromises = data.users.map(async (u) => {
                                // Verifica se já tem a permissão
                                if (!u.permissions?.access_proj_av_desempenho) {
                                    const newPerms = { ...(u.permissions || {}), access_proj_av_desempenho: true };
                                    await actions.saveUser({ ...u, permissions: newPerms });
                                    count++;
                                }
                            });
                            
                            await Promise.all(batchPromises);
                            alert(`Processo concluído! ${count} usuários foram atualizados.`);
                            actions.logAction('MIGRACAO_EM_BLOCO_PROJETO', { afetados: count });
                        } catch (e) {
                            console.error(e);
                            alert("Erro ao processar: " + e.message);
                        } finally {
                            setIsMigrating(false);
                        }
                    };
        
        const handleBulk2FAToggle = async () => {
        if (selectedUserIds.length === 0) return alert("Selecione usuários na tabela.");
        
        const action = window.confirm("Clique em OK para HABILITAR 2FA ou Cancelar para DESABILITAR nos usuários selecionados.");
        const newValue = action; // true = habilitar, false = desabilitar

        setIsUpdatingSheet(true); // Reusando estado de loading
        try {
            const batchPromises = selectedUserIds.map(async (uid) => {
                const user = data.users.find(u => u.id === uid);
                if (user) {
                    // Se desabilitar, removemos também o setup para limpar
                    const updates = { is2FAEnabled: newValue };
                    if (!newValue) {
                        updates.is2FASetupComplete = false;
                        updates.twoFactorSecret = null;
                    }
                    await actions.saveUser({ ...user, ...updates });
                }
            });
            await Promise.all(batchPromises);
            alert(`2FA ${newValue ? 'habilitado' : 'desabilitado'} para ${selectedUserIds.length} usuários.`);
            setSelectedUserIds([]); // Limpa seleção
        } catch (e) {
            console.error(e);
            alert("Erro ao atualizar em bloco.");
        } finally {
            setIsUpdatingSheet(false);
        }
        };

        const handleSaveBulkPermissions = async () => {
            // 1. Determina quem são os alvos
            let targetUsers = [];

            if (bulkTargetMode === 'selection') {
                // AGORA OLHA PARA O NOVO ESTADO manualSelectedIds
                if (manualSelectedIds.length === 0) return alert("Selecione ao menos um usuário na lista.");
                targetUsers = data.users.filter(u => manualSelectedIds.includes(u.id));
            } else if (bulkTargetMode === 'role') {
                if (!bulkTargetRole) return alert("Selecione um tipo de usuário.");
                targetUsers = data.users.filter(u => u.role === bulkTargetRole);
            }

            if (targetUsers.length === 0) return alert("Nenhum usuário encontrado para a opção selecionada.");

            // 2. Validações
            const permsToApply = permissionForm; 
            if (Object.keys(permsToApply).length === 0) return alert("Selecione ao menos uma permissão para alterar.");

            const actionText = bulkActionType === 'add' ? 'ADICIONAR' : 'REMOVER';
            const targetText = bulkTargetMode === 'selection' 
                ? `${targetUsers.length} usuários selecionados manualmente` 
                : `TODOS os usuários do tipo '${bulkTargetRole}' (${targetUsers.length} usuários)`;

            if (!window.confirm(`Você vai ${actionText} permissões para ${targetText}. Confirmar?`)) return;

            try {
                for (const user of targetUsers) {
                    let newPerms = { ...(user.permissions || {}) };
                    
                    Object.keys(permsToApply).forEach(key => {
                        if (permsToApply[key]) { // Se checkbox marcada
                            if (bulkActionType === 'add') {
                                newPerms[key] = true;
                            } else {
                                newPerms[key] = false;
                                delete newPerms[key]; 
                            }
                        }
                    });
                    
                    await actions.saveUser({ ...user, permissions: newPerms });
                }
                
                alert("Operação em bloco concluída com sucesso!");
                if(actions.logAction) {
                    actions.logAction('PERMISSOES_EM_BLOCO', {
                        entity: 'USUARIOS',
                        recordId: 'BULK_PERMISSIONS',
                        details: [
                            { campo: 'tipo', de: '', para: bulkActionType },
                            { campo: 'modo', de: '', para: bulkTargetMode },
                            { campo: 'qtdUsuarios', de: '', para: targetUsers.length },
                            { campo: 'perms', de: '', para: JSON.stringify(permsToApply) }
                        ]
                    });
                }
                
                setIsBulkPermModalOpen(false);
                setManualSelectedIds([]); // Limpa a seleção manual
                setPermissionForm({}); // Limpa o formulário de permissões
            } catch (e) {
                console.error(e);
                alert("Erro: " + e.message);
            }
        };


                    // --- FUNÇÕES DE PERFIL (COM LOGS) ---
                        const handleSaveProfile = async () => {
                            if (!profileForm.name) return alert("Dê um nome ao perfil.");

                            const profileId = profileForm.id || Date.now().toString();
                            const isNew = !profileForm.id;

                            const savedProfile = {
                                ...profileForm,
                                id: profileId
                            };

                            // 1. salva o perfil
                            await actions.savePermissionProfile(savedProfile);

                            // 2. encontra usuários vinculados a esse perfil
                            const linkedUsers = (data.users || []).filter(u =>
                                u.profileId === profileId || u._profileId === profileId
                            );

                            // 3. atualiza automaticamente as permissões dos usuários vinculados
                            for (const user of linkedUsers) {
                                const overrides = { ...(user.permissionOverrides || {}) };
                                await actions.saveUser({
                                    ...user,
                                    permissions: { ...(savedProfile.permissions || {}), ...overrides },
                                    permissionOverrides: overrides,
                                    profileId: profileId,
                                    _profileId: profileId
                                });
                            }

                            actions.logAction(
                                isNew ? 'CRIACAO_PERFIL_ACESSO' : 'EDICAO_PERFIL_ACESSO',
                                {
                                    entity: 'PERFIS_ACESSO',
                                    recordId: profileId,
                                    details: [
                                        { campo: 'nome_perfil', de: '', para: profileForm.name },
                                        { campo: 'permissoes_qtd', de: '', para: Object.keys(profileForm.permissions || {}).length },
                                        { campo: 'usuarios_atualizados', de: '', para: linkedUsers.length }
                                    ]
                                }
                            );

                            setProfileForm({ name: '', permissions: {} });
                            alert(`Perfil salvo. ${linkedUsers.length} usuário(s) vinculado(s) foram atualizados automaticamente.`);
                        };

                        // Função Auxiliar para Exclusão de Perfil (Para usar no JSX)
                        const handleDeleteProfileWithLog = async (p) => {
                            if(window.confirm(`Excluir perfil ${p.name}?`)) {
                                await actions.deletePermissionProfile(p.id);
                                actions.logAction('EXCLUSAO_PERFIL_ACESSO', {
                                    entity: 'PERFIS_ACESSO',
                                    recordId: p.id,
                                    details: [
                                        { campo: 'nome_perfil', de: p.name, para: '' }
                                    ]
                                });
                            }
                        };

                    const handleLoadProfileToUser = (profileId) => {
                        const prof = data.permissionProfiles.find(p => p.id === profileId);
                        if (prof) {
                            setUserForm(prev => ({
                                ...prev,
                                profileId: prof.id,
                                _profileId: prof.id,
                                permissions: { ...prof.permissions },
                                permissionOverrides: {}
                            }));
                        }
                    };
            const renderUsuariosTab = () => {
            // --- 1. VERIFICAÇÃO DE PERMISSÕES ---
            const canManageUsers = (currentUser.role === 'SuperAdm' && currentUser.permissions?.access_sub_users_manage !== false) || currentUser.permissions?.access_sub_users_manage === true;
            const canViewAudit = (currentUser.role === 'SuperAdm' && currentUser.permissions?.access_tab_audit !== false) || currentUser.permissions?.access_tab_audit === true;

            // --- 2. HANDLER DE AÇÃO EM MASSA (2FA) ---
            const handleBulk2FA = async (enable) => {
                if (selectedUserIds.length === 0) return alert("Selecione ao menos um usuário na lista.");
                
                const actionText = enable ? 'HABILITAR' : 'DESABILITAR';
                if (window.confirm(`Deseja ${actionText} a autenticação em 2 fatores para os ${selectedUserIds.length} usuários selecionados?`)) {
                    let count = 0;
                    for (const uid of selectedUserIds) {
                        const u = data.users.find(x => x.id === uid);
                        if (u) {
                            // Salva a preferência no perfil do usuário
                            await actions.saveUser({ ...u, use2FA: enable });
                            count++;
                        }
                    }
                    
                    if (actions.logAction) {
                        actions.logAction(enable ? 'ENABLE_2FA_BULK' : 'DISABLE_2FA_BULK', {
                            entity: 'USUARIOS',
                            recordId: 'BULK_2FA',
                            details: [
                                { campo: 'quantidade', de: '', para: count },
                                { campo: 'acao', de: '', para: enable ? 'HABILITAR_2FA' : 'DESABILITAR_2FA' }
                            ]
                        });
                    }
                    
                    alert(`${count} usuários atualizados com sucesso.`);
                    setSelectedUserIds([]); // Limpa seleção após ação
                }
            };
            
    
        
            // --- 3. RENDERIZAÇÃO PRINCIPAL ---
            return (
                <div className={`gp-users-admin flex flex-col h-full rounded-xl shadow-sm border overflow-hidden min-h-[600px] ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                    
                    {/* --- HEADER DAS SUB-ABAS (Gestão / Auditoria) --- */}
                    <div className={`flex border-b shrink-0 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                        {canManageUsers && (
                            <button 
                                onClick={() => setUsersSubTab('list')} 
                                className={`px-6 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${usersSubTab === 'list' ? (isDarkMode ? 'border-blue-500 text-blue-400 bg-slate-800' : 'border-blue-600 text-blue-600 bg-white') : (isDarkMode ? 'border-transparent text-slate-400 hover:bg-slate-800' : 'border-transparent text-gray-500 hover:bg-gray-100')}`}
                            >
                                <UserCog size={16}/> Lista de Usuários 
                                {pendingUsersCount > 0 && <span className="bg-red-500 text-white text-[9px] px-1.5 rounded-full ml-2 animate-pulse">{pendingUsersCount}</span>}
                            </button>
                        )}
                        {canViewAudit && (
                            <button 
                                onClick={() => setUsersSubTab('audit')} 
                                className={`px-6 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${usersSubTab === 'audit' ? (isDarkMode ? 'border-purple-500 text-purple-400 bg-slate-800' : 'border-purple-600 text-purple-600 bg-white') : (isDarkMode ? 'border-transparent text-slate-400 hover:bg-slate-800' : 'border-transparent text-gray-500 hover:bg-gray-100')}`}
                            >
                                <Shield size={16}/> Auditoria
                            </button>
                        )}
                    </div>

                    <div className="flex-1 overflow-hidden relative">
                        
                        {/* --- SUB-ABA: LISTA DE USUÁRIOS --- */}
                        {usersSubTab === 'list' && canManageUsers && (
                            <div className="h-full flex flex-col p-4 animate-fade-in space-y-4">
                                
                                {/* BARRA DE FERRAMENTAS (Busca e Botões) */}
                                <div className={`flex justify-between items-center p-2 rounded-lg border shrink-0 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'}`}>
                                    <div className="relative w-64">
                                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} size={16} />
                                        <Input 
                                            className={`w-full pl-9 h-9 text-xs ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-gray-50 border-gray-200'}`} 
                                            placeholder="Pesquisar..." 
                                            value={search} 
                                            onChange={e=>setSearch(e.target.value)} 
                                        />
                                    </div>
                                    
                                    <div className="flex items-center gap-2">
                                        {currentUser.role === 'SuperAdm' && (
                                            <>
                                                {/* --- BOTÕES DE AÇÃO EM MASSA (2FA) --- */}
                                                {selectedUserIds.length > 0 && (
                                                    <div className={`flex items-center gap-1 border-r pr-2 mr-2 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}>
                                                        <span className={`text-[10px] font-bold uppercase mr-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>2FA:</span>
                                                        <button 
                                                            onClick={() => handleBulk2FA(true)} 
                                                            className="p-1.5 rounded bg-green-100 text-green-700 hover:bg-green-200 transition-colors" 
                                                            title="Habilitar 2FA para selecionados"
                                                        >
                                                            <Shield size={14}/>
                                                        </button>
                                                        <button 
                                                            onClick={() => handleBulk2FA(false)} 
                                                            className="p-1.5 rounded bg-red-100 text-red-700 hover:bg-red-200 transition-colors" 
                                                            title="Desabilitar 2FA para selecionados"
                                                        >
                                                            <Unlock size={14}/>
                                                        </button>
                                                    </div>
                                                )}

                                                <Button 
                                                    onClick={() => { setPermissionForm({}); setIsBulkPermModalOpen(true); }} 
                                                    className={`h-9 text-xs shadow-sm border-0 ${isDarkMode ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white'}`} 
                                                    icon={List}
                                                >
                                                    Em Bloco ({selectedUserIds.length})
                                                </Button>

                                                <Button 
                                                    onClick={() => setIsProfileModalOpen(true)} 
                                                    className={`h-9 text-xs shadow-sm border-0 ${isDarkMode ? 'bg-teal-600 hover:bg-teal-700 text-white' : 'bg-teal-500 hover:bg-teal-600 text-white'}`} 
                                                    icon={UserCheck}
                                                >
                                                    Perfis
                                                </Button>

                                                <Button 
                                                    onClick={handleOpenGlobalPermissions} 
                                                    className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm h-9 text-xs" 
                                                    icon={Settings}
                                                >
                                                    Globais
                                                </Button>
                                            </>
                                        )}
                                        <Button 
                                            onClick={() => { setUserForm({ 
                                                    id:'', 
                                                    name:'', 
                                                    email:'', 
                                                    role:'ADM', 
                                                    departmentId:'', 
                                                    permissions: {},
                                                    profileId: '',
                                                    _profileId: ''
                                                });
                                            setIsUserModalOpen(true); }} 
                                            icon={Plus} 
                                            className="h-9 text-xs shadow-sm"
                                        >
                                            Novo Registro
                                        </Button>
                                    </div>
                                </div>

                                {/* TABELA DE USUÁRIOS */}
                                <div className={`rounded-lg border overflow-hidden flex-1 overflow-y-auto ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                    <table className={`w-full text-sm text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                        <thead className={`uppercase text-xs font-bold border-b sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                                            <tr>
                                                <th className="p-4 w-10 text-center">
                                                    <input 
                                                        type="checkbox" 
                                                        onChange={(e) => {
                                                            if (e.target.checked) setSelectedUserIds(data.users.map(u => u.id));
                                                            else setSelectedUserIds([]);
                                                        }} 
                                                    />
                                                </th>
                                                <th className="p-4">ID Firebase</th>
                                                <th className="p-4">Usuário</th>
                                                <th className="p-4">Email</th>
                                                <th className="p-4">Departamento</th>
                                                <th className="p-4 text-center">Tipo</th>
                                                <th className="p-4 text-center">Perfil</th>
                                                <th className="p-4 text-center">Status Acesso</th>
                                                <th className="p-4 text-center">Conexão</th>
                                                <th className="p-4 text-center">Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                            {data.users
                                                .filter(u => (u.name || '').toLowerCase().includes(search.toLowerCase()) || (u.email || '').toLowerCase().includes(search.toLowerCase()))
                                                .sort((a,b) => (b.isPending === true) - (a.isPending === true))
                                                .map(u => { 
                                                    const isInactive = u.endDate && new Date(u.endDate) <= new Date(); 
                                                    const rowClass = u.isPending 
                                                        ? (isDarkMode ? 'bg-yellow-900/20' : 'bg-yellow-50') 
                                                        : isInactive 
                                                            ? (isDarkMode ? 'bg-slate-900 text-slate-600 grayscale' : 'bg-gray-100 text-gray-400 grayscale') 
                                                            : (isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'); 
                                                    
                                                    return (
                                                    <tr key={u.id} className={`transition-colors ${rowClass}`}>
                                                        <td className="p-4 text-center">
                                                            <input 
                                                                type="checkbox" 
                                                                checked={selectedUserIds.includes(u.id)}
                                                                onChange={() => {
                                                                    if (selectedUserIds.includes(u.id)) setSelectedUserIds(prev => prev.filter(id => id !== u.id));
                                                                    else setSelectedUserIds(prev => [...prev, u.id]);
                                                                }}
                                                            />
                                                        </td>
                                                        <td className="p-4 font-mono text-[10px] font-bold whitespace-nowrap">
                                                            <span className="inline-flex items-center gap-1">{u.id}<Copy size={11} className="opacity-50"/></span>
                                                        </td>
                                                        <td className={`p-4 font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                                            <div className="flex items-center gap-2">{u.name}
                                                            {u.isPending && <span className="bg-yellow-200 text-yellow-800 text-[10px] px-2 py-0.5 rounded-full uppercase font-bold animate-pulse">Pendente</span>}
                                                            </div>
                                                        </td>
                                                        <td className="p-4">{u.email}</td>
                                                        <td className="p-4">{data.departments.find(d => d.id === u.departmentId)?.name || '-'}</td>
                                                        <td className="p-4 text-center">
                                                            {(() => {
                                                                const roleLabel = u.role === 'Solicitante' && !u.isPending ? 'ADM' : String(u.role || '-').toUpperCase();
                                                                return (
                                                                    <span className={`inline-flex min-w-[88px] items-center justify-center rounded-full border px-3 py-1 text-[10px] font-black tracking-wide ${isInactive ? 'border-slate-300 text-slate-400 bg-transparent' : 'border-green-500 text-green-600 bg-transparent'}`}>
                                                                        {roleLabel}
                                                                    </span>
                                                                );
                                                            })()}
                                                        </td>
                                                        
                                                        {/* PERFIL VINCULADO */}
                                                        <td className="p-4 text-center">
                                                            {(() => {
                                                                const profileId = u.profileId || u._profileId || '';
                                                                const profile = data.permissionProfiles.find(p => p.id === profileId);
                                                                const label = profile?.name || 'Sem Perfil';
                                                                const normalized = label.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                                                                let profileClass = 'bg-slate-400';
                                                                if (normalized.includes('superadm')) profileClass = 'bg-cyan-600';
                                                                else if (normalized.includes('supervis')) profileClass = 'bg-pink-600';
                                                                else if (normalized.includes('gerente fnc')) profileClass = 'bg-violet-600';
                                                                else if (normalized.includes('gerenc')) profileClass = 'bg-teal-600';
                                                                else if (normalized.includes('colaborador')) profileClass = 'bg-indigo-600';
                                                                else if (profile) profileClass = 'bg-teal-600';
                                                                return <span className={`inline-flex min-w-[92px] justify-center px-3 py-1 rounded-full text-[10px] font-black text-white ${profileClass}`}>{label}</span>;
                                                            })()}
                                                        </td>

                                                        <td className="p-4 text-center">
                                                            {isInactive ? (<span className="inline-flex min-w-[90px] justify-center items-center gap-1 px-2 py-1 rounded-full text-xs font-bold bg-red-50 text-red-600 border border-red-300"><LogOut size={10}/> Inativo</span>) : (<span className="inline-flex min-w-[90px] justify-center items-center gap-1 px-2 py-1 rounded-full text-xs font-bold bg-green-50 text-green-600 border border-green-300"><CheckCircle size={10}/> Ativo</span>)}
                                                        </td>
                                                        <td className="p-4 text-center">
                                                            {(() => {
                                                                const online = u.isOnline === true || String(u.connectionStatus || '').toLowerCase() === 'online';
                                                                return <span className={`inline-flex min-w-[90px] justify-center items-center gap-1 px-2 py-1 rounded-full text-xs font-bold text-white ${online ? 'bg-emerald-500' : 'bg-red-500'}`}><span className="w-1.5 h-1.5 rounded-full bg-white"/> {online ? 'Online' : 'Offline'}</span>;
                                                            })()}
                                                        </td>
                                                        
                                                        <td className="p-4 text-center flex justify-center gap-2">
                                                            {u.isPending && (hasPerm('access_sub_users_approve') || hasPerm('access_sub_users_manage')) && (
                                                                <button 
                                                                    onClick={()=>handleApproveUser(u)} 
                                                                    className="text-green-600 hover:text-green-800 p-2 bg-green-50 rounded-lg hover:bg-green-100 transition shadow-sm" 
                                                                    title="Aprovar Usuário"
                                                                >
                                                                    <CheckCircle size={18}/>
                                                                </button>
                                                            )}
                                                            
                                                            {hasPerm('access_sub_users_manage') && (
                                                                <>
                                                                    <button 
                                                                        onClick={()=>handleEditUser(u)} 
                                                                        className="text-blue-500 hover:text-blue-400 p-2 rounded-lg hover:bg-blue-500/10 transition" 
                                                                        title="Editar"
                                                                    >
                                                                        <Pencil size={18}/>
                                                                    </button>
                                                                    
                                                                    <button 
                                                                        onClick={()=>handleOpenReset(u)} 
                                                                        className="text-orange-500 hover:text-orange-400 p-2 rounded-lg hover:bg-orange-500/10 transition" 
                                                                        title="Resetar Senha"
                                                                    >
                                                                        <Key size={18}/>
                                                                    </button>
                                                                    
                                                                    {(currentUser.role === 'SuperAdm' || currentUser.permissions?.access_tab_users) && (
                                                                        <button 
                                                                            onClick={()=>handleOpenUserPermissions(u)} 
                                                                            className="text-purple-500 hover:text-purple-400 p-2 rounded-lg hover:bg-purple-500/10 transition" 
                                                                            title="Permissões"
                                                                        >
                                                                            <Shield size={18}/>
                                                                        </button>
                                                                    )}
                                                                    
                                                                    {u.id !== currentUser.id && (
                                                                        <button 
                                                                            onClick={()=>handleDeleteUserWithLog(u.id)} 
                                                                            className="text-red-500 hover:text-red-400 p-2 rounded-lg hover:bg-red-500/10 transition" 
                                                                            title="Excluir"
                                                                        >
                                                                            <Trash2 size={18}/>
                                                                        </button>
                                                                    )}
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    );
                                                })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* --- SUB-ABA: AUDITORIA --- */}
                        {usersSubTab === 'audit' && canViewAudit && (
                            <div className="h-full p-0">
                                <AuditLogTab data={data} isDarkMode={isDarkMode} />
                            </div>
                        )}
                    </div>
                </div>
            );
        };
        
        const renderTrainingTab = () => {
            // const canViewCollabArea = hasPerm('access_sub_collab_area'); // REMOVIDO
            const canViewCapacitacao = hasPerm('access_sub_capacitacoes');
            const canViewPerceptionList = currentUser.role === 'SuperAdm' || currentUser.role === 'Gestor' || hasPerm('access_sub_percepcao');
            const canViewAI = currentUser.role === 'SuperAdm' || hasPerm('access_sub_ai');
            // const isOpen = isPerceptionOpen(); // Não é mais necessário aqui para exibição, pois a aba foi removida

            return (
                <div className={`flex flex-col h-full min-h-[500px] ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                    
                    {/* MENU SUPERIOR - (Item 1: Aba Colaborador Removida) */}
                    <div className={`flex border-b shrink-0 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                        {/* Botão Área do Colaborador REMOVIDO DAQUI */}
                        
                        {canViewCapacitacao && (
                            <button onClick={() => setExternalSubTab('capacitacoes')} className={`px-6 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${externalSubTab === 'capacitacoes' ? (isDarkMode ? 'border-blue-500 text-blue-400 bg-slate-800' : 'border-blue-600 text-blue-600 bg-white') : (isDarkMode ? 'border-transparent text-slate-400 hover:bg-slate-800' : 'border-transparent text-gray-500 hover:bg-gray-100')}`}>
                                <RefreshCw size={14}/> Cursos e Treinamentos
                            </button>
                        )}
                        {canViewPerceptionList && (
                            <button onClick={() => setExternalSubTab('percepcao')} className={`px-6 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${externalSubTab === 'percepcao' ? (isDarkMode ? 'border-purple-500 text-purple-400 bg-slate-800' : 'border-purple-600 text-purple-600 bg-white') : (isDarkMode ? 'border-transparent text-slate-400 hover:bg-slate-800' : 'border-transparent text-gray-500 hover:bg-gray-100')}`}>
                                <Users size={14}/> Percepção Equipe
                            </button>
                        )}
                        {canViewAI && (
                            <button onClick={() => setExternalSubTab('ia')} className={`px-6 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${externalSubTab === 'ia' ? (isDarkMode ? 'border-fuchsia-500 text-fuchsia-400 bg-slate-800' : 'border-fuchsia-600 text-fuchsia-600 bg-white') : (isDarkMode ? 'border-transparent text-slate-400 hover:bg-slate-800' : 'border-transparent text-gray-500 hover:bg-gray-100')}`}>
                                <Sparkles size={14}/> IA
                            </button>
                        )}
                    </div>

                    {/* CONTEÚDO DA ÁREA DO COLABORADOR FOI REMOVIDO DAQUI (Item 1) */}

                    {/* CONTEÚDO: CONFIGURAÇÃO DE IA */}
                    {externalSubTab === 'ia' && canViewAI && (
                        <div className="p-6 animate-fade-in">
                            <div className={`max-w-4xl rounded-xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
                                <div className="flex items-start justify-between gap-4 mb-6">
                                    <div>
                                        <h3 className={`text-lg font-black flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                                            <Sparkles size={20} className="text-fuchsia-500"/> Configuração da IA
                                        </h3>
                                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                            Esta configuração é usada pelos botões “Melhorar com IA” dos feedbacks de avaliação e das ocorrências técnicas.
                                        </p>
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-[10px] font-bold border ${aiConfigForm.isActive ? 'bg-green-100 text-green-700 border-green-200' : 'bg-yellow-100 text-yellow-700 border-yellow-200'}`}>
                                        {aiConfigForm.isActive ? 'IA ativa' : 'IA inativa'}
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <label className="text-xs font-bold">
                                        Provedor
                                        <select value={aiConfigForm.provider || 'gemini'} onChange={e => setAiConfigForm(prev => ({ ...prev, provider: e.target.value }))} className={`mt-1 w-full p-2.5 rounded-lg border outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`}>
                                            <option value="gemini">Gemini</option>
                                        </select>
                                    </label>

                                    <label className="text-xs font-bold">
                                        Modelo
                                        <input value={aiConfigForm.model || ''} onChange={e => setAiConfigForm(prev => ({ ...prev, model: e.target.value }))} placeholder="gemini-1.5-flash" className={`mt-1 w-full p-2.5 rounded-lg border outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-gray-300'}`}/>
                                    </label>

                                    <label className="text-xs font-bold md:col-span-2">
                                        Chave da API
                                        <div className="relative mt-1">
                                            <input type={showAIKey ? 'text' : 'password'} value={aiConfigForm.apiKey || ''} onChange={e => setAiConfigForm(prev => ({ ...prev, apiKey: e.target.value }))} placeholder="Cole aqui a chave da API" className={`w-full p-2.5 pr-10 rounded-lg border outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-gray-300'}`}/>
                                            <button type="button" onClick={() => setShowAIKey(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-500" title={showAIKey ? 'Ocultar chave' : 'Exibir chave'}>
                                                <Eye size={16}/>
                                            </button>
                                        </div>
                                    </label>

                                    <label className="text-xs font-bold md:col-span-2">
                                        URL do proxy/serviço de IA
                                        <input value={aiConfigForm.proxyUrl || ''} onChange={e => setAiConfigForm(prev => ({ ...prev, proxyUrl: e.target.value }))} placeholder={DEFAULT_AI_PROXY_URL} className={`mt-1 w-full p-2.5 rounded-lg border outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-gray-300'}`}/>
                                    </label>
                                </div>

                                <div className={`mt-5 p-4 rounded-lg border text-xs ${isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-slate-50 border-gray-200 text-gray-600'}`}>
                                    <label className="flex items-center gap-2 font-bold cursor-pointer">
                                        <input type="checkbox" checked={!!aiConfigForm.isActive} onChange={e => setAiConfigForm(prev => ({ ...prev, isActive: e.target.checked }))}/>
                                        Ativar IA para melhorias de texto
                                    </label>
                                    <p className="mt-2">
                                        Quando a IA não retornar conteúdo válido, o sistema preserva o texto original digitado pelo gestor e exibe uma mensagem de orientação.
                                    </p>
                                </div>

                                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-slate-700">
                                    <Button variant="secondary" onClick={() => setAiConfigForm(defaultAIConfigForm)} disabled={savingAIConfig}>Limpar</Button>
                                    <Button onClick={handleSaveAIConfig} disabled={savingAIConfig} icon={savingAIConfig ? Loader2 : Save} className={savingAIConfig ? 'animate-pulse' : ''}>
                                        {savingAIConfig ? 'Salvando...' : 'Salvar Configuração'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 2. CONTEÚDO: CURSOS E TREINAMENTOS (MANTIDO IGUAL) */}
                    {externalSubTab === 'capacitacoes' && canViewCapacitacao && (
                        <div className="p-4 space-y-4">
                            <div className={`flex flex-col xl:flex-row justify-between items-center gap-3 p-3 rounded-xl border shadow-sm mb-4 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                <div className="flex items-center gap-2">
                                    {selectedTrainingIds.length > 0 && (<button onClick={handleBulkDelete} className="px-3 py-1.5 rounded-md text-xs font-bold bg-red-600 text-white hover:bg-red-700 flex items-center gap-2 shadow-sm animate-fade-in transition-all"><Trash2 size={14}/> Excluir ({selectedTrainingIds.length})</button>)}
                                    <div className={`flex p-1 rounded-lg shrink-0 ${isDarkMode ? 'bg-slate-900' : 'bg-gray-100'}`}>
                                        <button onClick={() => setTrainingView('detalhado')} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-2 ${trainingView === 'detalhado' ? (isDarkMode ? 'bg-slate-700 text-blue-400 shadow-sm' : 'bg-white text-blue-600 shadow-sm') : (isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-700')}`}><List size={14}/> Detalhado</button>
                                        <button onClick={() => setTrainingView('consolidado')} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-2 ${trainingView === 'consolidado' ? (isDarkMode ? 'bg-slate-700 text-purple-400 shadow-sm' : 'bg-white text-purple-600 shadow-sm') : (isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-700')}`}><FileSpreadsheet size={14}/> Consolidado</button>
                                    </div>
                                </div>
                                
                                <div className="flex flex-col md:flex-row items-center gap-3 w-full xl:w-auto">
                                    <div className={`flex items-center border rounded-lg overflow-hidden h-9 shadow-sm w-full md:w-auto ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`}>
                                        <select className={`text-xs font-bold outline-none h-full px-3 border-r cursor-pointer ${isDarkMode ? 'bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700' : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'}`} value={trainingPeriodMode} onChange={e => { setTrainingPeriodMode(e.target.value); setTrainingPeriodValue('1'); }}>
                                            <option value="ano">Anual</option><option value="semestre">Semestral</option><option value="trimestre">Trimestral</option><option value="mes">Mensal</option><option value="custom">Personalizado</option>
                                        </select>
                                        {trainingPeriodMode === 'custom' ? (
                                            <div className={`flex items-center px-2 gap-1 ${isDarkMode ? 'bg-slate-700' : 'bg-white'}`}>
                                                <input type="date" value={trainingCustomStart} onChange={e => setTrainingCustomStart(e.target.value)} className={`h-full text-xs outline-none bg-transparent w-24 ${isDarkMode ? 'text-white invert-calendar' : ''}`}/>
                                                <span className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>até</span>
                                                <input type="date" value={trainingCustomEnd} onChange={e => setTrainingCustomEnd(e.target.value)} className={`h-full text-xs outline-none bg-transparent w-24 ${isDarkMode ? 'text-white invert-calendar' : ''}`}/>
                                            </div>
                                        ) : (
                                            <div className="flex items-center h-full">
                                                {(trainingPeriodMode !== 'ano') && (
                                                    <select className={`h-full text-xs outline-none px-2 min-w-[80px] border-r cursor-pointer ${isDarkMode ? 'bg-slate-700 text-white border-slate-600' : 'bg-white border-gray-200'}`} value={trainingPeriodValue} onChange={e => setTrainingPeriodValue(e.target.value)}>
                                                        {trainingPeriodMode === 'semestre' && <><option value="1">1º Sem</option><option value="2">2º Sem</option></>}
                                                        {trainingPeriodMode === 'trimestre' && <><option value="1">1º Trim</option><option value="2">2º Trim</option><option value="3">3º Trim</option><option value="4">4º Trim</option></>}
                                                        {trainingPeriodMode === 'mes' && ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'].map((m,i)=><option key={i} value={i+1}>{m}</option>)}
                                                    </select>
                                                )}
                                                <input type="number" value={trainingFilterYear} onChange={e => setTrainingFilterYear(e.target.value)} className={`w-16 h-full text-xs text-center outline-none font-bold ${isDarkMode ? 'bg-slate-700 text-white placeholder-slate-500' : 'bg-white placeholder-gray-400'}`} placeholder="Ano"/>
                                            </div>
                                        )}
                                    </div>
                                    <select className={`h-9 text-xs border rounded-lg px-3 w-full md:w-auto shadow-sm focus:border-blue-500 outline-none ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={trainingFilterDept} onChange={e => setTrainingFilterDept(e.target.value)}>
                                        <option value="">Todos Deptos</option>{data.departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                                    </select>
                                    <div className="relative w-full xl:w-64 h-9">
                                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} size={14}/>
                                        <input placeholder="Buscar..." value={trainingFilterType} onChange={e => setTrainingFilterType(e.target.value)} className={`w-full h-full text-xs pl-9 border rounded-lg focus:ring-2 focus:border-blue-500 outline-none shadow-sm transition-all ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white focus:ring-slate-500 placeholder-slate-400' : 'bg-white border-gray-300 focus:ring-blue-100'}`}/>
                                    </div>
                                </div>
                            </div>
                            
                            <div className={`flex justify-end pt-2 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                <button onClick={() => { setSyncYearInput(new Date().getFullYear().toString()); setIsSyncModalOpen(true); }} className="text-xs px-3 py-1.5 h-8 bg-green-600 hover:bg-green-700 text-white rounded font-bold flex items-center gap-2 shadow-sm whitespace-nowrap transition-colors"><RefreshCw size={14}/> Sincronizar Treinamentos</button>
                            </div>

                            {/* ... (Lógica da Tabela de Treinamentos mantida igual) ... */}
                            {(() => {
                                const isDateInPeriod = (itemDateStr) => { 
                                    if (!itemDateStr) return false; 
                                    const parts = itemDateStr.includes('/') ? itemDateStr.split('/') : itemDateStr.split('-');
                                    if (parts.length !== 3) return false; 
                                    let d, m, y;
                                    if(parts[0].length === 4) { y = parseInt(parts[0], 10); m = parseInt(parts[1], 10); d = parseInt(parts[2], 10); } 
                                    else { d = parseInt(parts[0], 10); m = parseInt(parts[1], 10); y = parseInt(parts[2], 10); }
                                    const itemDate = new Date(y, m - 1, d);
                                    if (trainingPeriodMode === 'custom') { 
                                        if (!trainingCustomStart || !trainingCustomEnd) return true;
                                        const start = new Date(trainingCustomStart + 'T00:00:00');
                                        const end = new Date(trainingCustomEnd + 'T23:59:59');
                                        return itemDate >= start && itemDate <= end;
                                    } 
                                    if (trainingFilterYear && String(y) !== String(trainingFilterYear)) return false;
                                    if (trainingPeriodMode === 'ano') return true; 
                                    if (trainingPeriodMode === 'semestre') { const sem = parseInt(trainingPeriodValue); return sem === 1 ? (m >= 1 && m <= 6) : (m >= 7 && m <= 12); } 
                                    if (trainingPeriodMode === 'trimestre') { const q = parseInt(trainingPeriodValue); const qStartMonth = (q - 1) * 3 + 1; const qEndMonth = qStartMonth + 2; return m >= qStartMonth && m <= qEndMonth; } 
                                    if (trainingPeriodMode === 'mes') return m === parseInt(trainingPeriodValue);
                                    return true; 
                                };
                                
                                const filteredTrainings = (data.trainingData || []).filter(t => { 
                                    if (!isDateInPeriod(t.date)) return false; 
                                    if (trainingFilterDept && t.departmentName !== trainingFilterDept) return false; 
                                    if (trainingFilterType) { 
                                        const s = trainingFilterType.toLowerCase(); 
                                        const cName = data.collaborators.find(c => c.email?.toLowerCase() === t.email?.toLowerCase())?.name?.toLowerCase() || ''; 
                                        return cName.includes(s) || t.email.toLowerCase().includes(s) || t.description.toLowerCase().includes(s) || t.type.toLowerCase().includes(s); 
                                    } 
                                    return true; 
                                });

                                if (trainingView === 'consolidado') { 
                                    const consolidatedData = Object.values(filteredTrainings.reduce((acc, curr) => { 
                                        const emailKey = curr.email ? curr.email.toLowerCase() : 'sem_email'; 
                                        if (!acc[emailKey]) { 
                                            const collab = data.collaborators.find(c => c.email?.toLowerCase() === emailKey); 
                                            const deptName = collab ? (data.departments.find(d => d.id === collab.departmentId)?.name || 'N/D') : (curr.departmentName || 'N/D');
                                            acc[emailKey] = { 
                                                id: emailKey, 
                                                email: curr.email, 
                                                name: collab ? collab.name : (curr.email || 'Desconhecido'), 
                                                departmentName: deptName, 
                                                personType: collab ? (collab.personType || 'Colaborador') : 'N/D', 
                                                totalHours: 0, 
                                                d1: 0, 
                                                d2: 0, 
                                                count: 0 
                                            }; 
                                        } 
                                        const h = parseFloat(curr.hours) || 0; 
                                        acc[emailKey].totalHours += h; 
                                        acc[emailKey].count += 1; 
                                        const tType = (curr.type || '').toLowerCase(); 
                                        const isD1 = tType.includes('1') || tType.includes('atuação') || tType.includes('atuacao'); 
                                        if (isD1) acc[emailKey].d1 += h; else acc[emailKey].d2 += h; 
                                        return acc;
                                    }, {}));

                                    return ( 
                                        <div className={`max-h-[650px] overflow-y-auto border rounded-lg shadow-sm w-full ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                            <table className={`w-full text-xs text-left border-collapse ${isDarkMode ? 'text-slate-300' : ''}`}>
                                                <thead className={`sticky top-0 z-10 uppercase ${isDarkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-50 font-bold text-purple-900'}`}>
                                                    <tr>
                                                        <th className="p-3">Colaborador</th>
                                                        <th className="p-3">Departamento</th>
                                                        <th className="p-3 text-center">Tipo Pessoa</th>
                                                        <th className="p-3 text-center">Qtd. Cursos</th>
                                                        <th className="p-3 text-center">D1 (Atuação)</th>
                                                        <th className="p-3 text-center">D2 (Outros)</th>
                                                        <th className="p-3 text-center">Total Horas</th>
                                                    </tr>
                                                </thead>
                                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700 bg-slate-800' : 'divide-gray-200 bg-white'}`}>
                                                    {consolidatedData.sort((a,b) => b.totalHours - a.totalHours).map(item => (
                                                        <tr key={item.id} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-purple-50'}>
                                                            <td className={`p-3 font-bold ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}><div>{item.name}</div><div className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>{item.email}</div></td>
                                                            <td className={`p-3 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{item.departmentName}</td>
                                                            <td className={`p-3 text-center ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                                                                <span className={`px-2 py-0.5 rounded border text-[10px] font-bold ${item.personType === 'Gestão' ? (isDarkMode ? 'bg-purple-900/50 text-purple-300 border-purple-800' : 'bg-purple-50 text-purple-700 border-purple-200') : (isDarkMode ? 'bg-slate-700 text-slate-300 border-slate-600' : 'bg-gray-100 text-gray-600 border-gray-200')}`}>
                                                                    {item.personType}
                                                                </span>
                                                            </td>
                                                            <td className={`p-3 text-center ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>{item.count}</td>
                                                            <td className={`p-3 text-center font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{item.d1.toFixed(2)}h</td>
                                                            <td className={`p-3 text-center font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{item.d2.toFixed(2)}h</td>
                                                            <td className={`p-3 text-center font-bold text-sm ${isDarkMode ? 'text-purple-300 bg-purple-900/20' : 'text-purple-700 bg-purple-50/30'}`}>{item.totalHours.toFixed(2)}h</td>
                                                        </tr>
                                                    ))}
                                                    {consolidatedData.length === 0 && <tr><td colSpan="7" className={`p-8 text-center ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Nenhum registro encontrado.</td></tr>}
                                                </tbody>
                                            </table>
                                        </div> 
                                    );
                                }
                                
                                return ( 
                                    <div className={`max-h-[650px] overflow-y-auto border rounded-lg shadow-sm w-full ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                        <table className={`w-full text-xs text-left border-collapse ${isDarkMode ? 'text-slate-300' : ''}`}>
                                            <thead className={`sticky top-0 z-10 uppercase tracking-wider shadow-sm ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 font-bold text-gray-600'}`}>
                                                <tr>
                                                    <th className="p-3 w-10 text-center"><input type="checkbox" onChange={() => toggleSelectAllTraining(filteredTrainings)} /></th>
                                                    <th className="p-3 w-48">Colaborador</th>
                                                    <th className="p-3 w-24">Depto</th>
                                                    <th className="p-3 w-24 text-center">Tipo Pessoa</th>
                                                    <th className="p-3 text-center w-24">Data</th>
                                                    <th className="p-3">Descrição</th>
                                                    <th className="p-3 text-center min-w-[140px]">Tipo</th>
                                                    <th className="p-3 text-center w-20">Horas</th>
                                                    <th className="p-3 text-center w-24">Resumo</th>
                                                    <th className="p-3 text-center w-24">Certificado</th>
                                                    <th className="p-3 text-center w-24">Ações</th>
                                                </tr>
                                            </thead>
                                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700 bg-slate-800' : 'divide-gray-100 bg-white'}`}>
                                                {filteredTrainings.map((t, index) => { 
                                                    const rowClass = index % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');
                                                    const isAtuacao = t.type.includes('1') || t.type.includes('Atuação') || t.type.includes('atuacao');
                                                    const badgeClass = isAtuacao ? (isDarkMode ? 'bg-blue-900 text-blue-200 border-blue-800' : 'bg-blue-600 text-white border-blue-600') : (isDarkMode ? 'bg-emerald-900 text-emerald-200 border-emerald-800' : 'bg-emerald-600 text-white border-emerald-600');
                                                    const collab = data.collaborators.find(c => c.email?.toLowerCase() === t.email?.toLowerCase());
                                                    const displayDeptName = collab ? (data.departments.find(d => d.id === collab.departmentId)?.name || 'N/D') : t.departmentName;
                                                    const personType = collab ? (collab.personType || 'Colaborador') : 'N/D';

                                                return (
                                                        <tr key={t.id} className={`${rowClass} transition-colors ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-blue-50'}`}>
                                                            <td className="p-3 text-center align-middle"><input type="checkbox" checked={selectedTrainingIds.includes(t.id)} onChange={() => toggleSelectTraining(t.id)}/></td>
                                                            <td className="p-3 align-middle"><div className={`font-bold truncate w-40 ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`} title={collab?.name || ''}>{collab?.name || 'N/D'}</div><div className="group relative w-fit"><div className={`text-[10px] truncate max-w-[20ch] cursor-help ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>{t.email}</div></div></td>
                                                            <td className={`p-3 align-middle text-[11px] ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{displayDeptName}</td>
                                                            <td className={`p-3 align-middle text-center ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                                                                <span className={`px-2 py-0.5 rounded border text-[9px] font-bold ${personType === 'Gestão' ? (isDarkMode ? 'bg-purple-900/50 text-purple-300 border-purple-800' : 'bg-purple-50 text-purple-700 border-purple-200') : (isDarkMode ? 'bg-slate-700 text-slate-300 border-slate-600' : 'bg-gray-100 text-gray-600 border-gray-200')}`}>
                                                                    {personType}
                                                                </span>
                                                            </td>
                                                            <td className={`p-3 text-center align-middle ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>{t.date}</td>
                                                            <td className={`p-3 font-bold align-middle ${isDarkMode ? 'text-slate-300' : 'text-gray-800'}`}>{t.description}</td>
                                                            <td className="p-3 text-center align-middle"><span className={`px-3 py-1 rounded-md text-[10px] font-bold border whitespace-nowrap shadow-sm ${badgeClass}`}>{t.type}</span></td>
                                                            <td className="p-3 text-center font-bold text-purple-600 align-middle">{formatDecimalToTime(t.hours)}h</td>
                                                            
                                                            <td className="p-3 text-center align-middle">
                                                                {t.summaryLink ? (
                                                                    <a href={t.summaryLink} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 hover:underline flex items-center gap-1">
                                                                        <FileText size={12}/> Resumo
                                                                    </a>
                                                                ) : <span className="text-gray-300">-</span>}
                                                            </td>
                                                            <td className="p-3 text-center align-middle">
                                                                {t.certLink ? (
                                                                    <a href={t.certLink} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-800 hover:underline flex items-center gap-1">
                                                                        <Award size={12}/> Certificado
                                                                    </a>
                                                                ) : <span className="text-gray-300">-</span>}
                                                            </td>

                                                            <td className="p-3 text-center align-middle">
                                                                <div className="flex items-center justify-center gap-2">
                                                                    <button onClick={() => { 
                                                                        setForm(t); 
                                                                        setTab('training'); 
                                                                        setIsCadModalOpen(true); 
                                                                    }} className={`p-1.5 rounded transition-colors ${isDarkMode ? 'text-blue-400 hover:bg-slate-600' : 'text-blue-500 hover:bg-blue-100 hover:text-blue-700'}`} title="Editar"><Pencil size={14}/></button>
                                                                    <button onClick={() => {
                                                                        setConfirmModal({
                                                                            isOpen: true,
                                                                            title: "Excluir Curso",
                                                                            message: "Deseja realmente excluir este curso permanentemente?",
                                                                            onConfirm: () => {
                                                                                actions.genericDelete('training', t.id);
                                                                                setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
                                                                            }
                                                                        });
                                                                    }} className={`p-1.5 rounded transition-colors ${isDarkMode ? 'text-red-400 hover:bg-slate-600' : 'text-red-400 hover:bg-red-100 hover:text-red-600'}`} title="Excluir">
                                                                        <Trash2 size={14}/>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })} 
                                                {filteredTrainings.length === 0 && <tr><td colSpan="11" className={`p-8 text-center ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Nenhum registro encontrado.</td></tr>}
                                            </tbody>
                                        </table>
                                    </div> 
                                );
                            })()}
                        </div>
                    )}

                    {/* --- 3. CONTEÚDO: PERCEPÇÃO EQUIPE (Item 2 e 3: Botão Config Adicionado e Cores Distintas) --- */}
                    {externalSubTab === 'percepcao' && canViewPerceptionList && (
                        <div className="p-4 space-y-4">
                            <div className={`flex justify-between items-center mb-2 p-3 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                <div className="flex items-center gap-2">
                                    <h3 className={`font-bold flex items-center gap-2 mr-4 ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                                        <Sparkles size={18}/> Gestão de Resultados (Percepção)
                                    </h3>
                                    {selectedPerceptionIds.length > 0 && (
                                        <button onClick={handleBulkDeletePerception} className="px-3 py-1.5 rounded-md text-xs font-bold bg-red-600 text-white hover:bg-red-700 flex items-center gap-2 shadow-sm transition-all"><Trash2 size={14}/> Excluir ({selectedPerceptionIds.length})</button>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    {/* ITEM 2: BOTÃO CONFIGURAÇÃO (MOVIDO DA ÁREA COLABORADOR) */}
                                    {hasPerm('access_sub_collab_area_config') && (
                                        <Button 
                                            onClick={() => setIsPerceptionConfigOpen(true)} 
                                            className="text-xs bg-purple-600 hover:bg-purple-700 text-white shadow-md border-0" 
                                            icon={Settings}
                                            title="Configurar Prazos e Perguntas"
                                        >
                                            Configuração
                                        </Button>
                                    )}

                                    {/* ITEM 3: CORES DISTINTAS PARA CADA BOTÃO */}
                                    <Button 
                                        onClick={() => { setColumnForm({ description: '', notes: '', startDate: '', endDate: '', order: (data.perceptionCycles || []).length + 1, linkedField: '', linkedQuestionId: '' }); setIsColumnModalOpen(true); }} 
                                        className="text-xs bg-blue-600 hover:bg-blue-700 text-white shadow-md border-0" 
                                        icon={LayoutDashboard}
                                    >
                                        Criar Coluna
                                    </Button>
                                    
                                    <Button 
                                        onClick={() => setIsPerceptionSyncModalOpen(true)} 
                                        className="text-xs bg-amber-600 hover:bg-amber-700 text-white shadow-md border-0"
                                    >
                                        Sincronizar (Legado)
                                    </Button>
                                    
                                    <Button 
                                        onClick={() => { setPercepForm({ id: Date.now().toString(), email: '', date: new Date().toLocaleDateString('pt-BR'), nota: '' }); setIsPercepEditModalOpen(true); }} 
                                        icon={Plus} 
                                        className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white shadow-md border-0"
                                    >
                                        Lançamento Manual
                                    </Button>
                                </div>
                            </div>

                            {/* TABELA DE PERCEPÇÃO (MANTIDA IGUAL) */}
                            <div className={`max-h-[600px] overflow-y-auto border rounded-lg shadow-sm w-full ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                <table className={`w-full text-xs text-left border-collapse ${isDarkMode ? 'text-slate-300' : ''}`}>
                                    <thead className={`sticky top-0 z-10 uppercase ${isDarkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-50 font-bold text-purple-900'}`}>
                                        <tr>
                                            {/* AÇÕES (PRIMEIRA) */}
                                            <th className="p-4 text-center border-b border-gray-200 dark:border-slate-700 w-24">Ações</th>
                                            
                                            {/* 1. CHECKBOX */}
                                            <th className="p-4 w-10 text-center border-b border-gray-200 dark:border-slate-700">
                                                <input type="checkbox" onChange={toggleSelectAllPerception} checked={(data.teamPerception || []).length > 0 && selectedPerceptionIds.length === (data.teamPerception || []).length} />
                                            </th>
                                            
                                            {/* 2. COLUNAS ORIGINAIS (Email, Gestor, Data, Nota) */}
                                            <th className="p-4 border-b border-gray-200 dark:border-slate-700">Email do Colaborador</th>
                                            <th className="p-4 border-b border-gray-200 dark:border-slate-700">Email do Gestor</th>
                                            <th className="p-4 text-center border-b border-gray-200 dark:border-slate-700">Data</th>
                                            <th className="p-4 text-center border-b border-gray-200 dark:border-slate-700">Nota</th>

                                            {/* 3. COLUNAS DINÂMICAS */}
                                            {(data.perceptionCycles || [])
                                                .sort((a,b) => (a.order || 0) - (b.order || 0))
                                                .map(col => (
                                                    <th key={col.id} className="p-2 text-center w-48 min-w-[12rem] max-w-[12rem] border-b border-l border-gray-200 dark:border-slate-700 bg-black/5 dark:bg-white/5 relative group/header vertical-align-top">
                                                        <div className="flex flex-col items-center justify-start w-full h-full py-2 cursor-help px-2 gap-1.5">
                                                            <span className="font-bold text-xs text-center leading-tight line-clamp-3 break-words w-full" title={col.description}>
                                                                {col.description}
                                                            </span>
                                                            {col.linkedField && (
                                                                <span className="text-[9px] font-mono font-bold bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-2 py-0.5 rounded border border-blue-200 dark:border-blue-800 shadow-sm">
                                                                    {col.linkedField.replace('col_', 'Col ')}
                                                                </span>
                                                            )}
                                                        </div>
                                                        
                                                        {/* POPUP DE OBSERVAÇÕES */}
                                                        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-slate-800 text-white text-xs rounded-lg shadow-2xl opacity-0 invisible group-hover/header:opacity-100 group-hover/header:visible transition-all duration-200 z-[999] pointer-events-auto text-left border border-slate-600 flex flex-col">
                                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 -mb-1 w-3 h-3 bg-slate-800 border-t border-l border-slate-600 rotate-45"></div>
                                                            <div className="p-3 border-b border-slate-600 bg-slate-900/50 rounded-t-lg flex justify-between items-center shrink-0">
                                                                <span className="font-bold text-blue-300 truncate max-w-[70%]">{col.description}</span>
                                                                <span className="text-[9px] bg-slate-700 px-1.5 py-0.5 rounded text-slate-300 font-mono">
                                                                    {col.linkedField?.replace('col_', 'Col ')}
                                                                </span>
                                                            </div>
                                                            <div className="p-3 max-h-60 overflow-y-auto custom-scrollbar">
                                                                <div className="opacity-90 whitespace-pre-wrap leading-relaxed">
                                                                    {col.notes ? col.notes : <i className="text-slate-500">Nenhuma observação registrada.</i>}
                                                                </div>
                                                            </div>
                                                            <div className="p-2 border-t border-slate-700 text-[9px] text-slate-400 text-right bg-slate-900/30 rounded-b-lg">
                                                                Vigência: {new Date(col.startDate).toLocaleDateString('pt-BR')}
                                                            </div>
                                                        </div>

                                                        {/* BOTÕES DE AÇÃO */}
                                                        <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover/header:opacity-100 transition-opacity bg-white dark:bg-slate-800 rounded shadow-sm p-0.5 border border-gray-100 dark:border-slate-600 z-10">
                                                            <button onClick={() => handleEditColumn(col)} className="p-1 text-blue-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-slate-700 rounded" title="Editar Coluna"><Pencil size={10}/></button>
                                                            <button onClick={() => {if(window.confirm('Excluir esta coluna e todos os dados vinculados?')) actions.deletePerceptionCycle(col.id)}} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-slate-700 rounded" title="Excluir Coluna"><Trash2 size={10}/></button>
                                                        </div>
                                                    </th>
                                                ))}
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-200'}`}>
                                        {(data.teamPerception || []).map(item => {
                                            const checkCycleMatch = (cycle) => {
                                                if(!item.date) return false;
                                                try {
                                                    const parts = item.date.split('/'); 
                                                    if(parts.length !== 3) return false;
                                                    const recDate = new Date(parts[2], parts[1]-1, parts[0]);
                                                    const start = new Date(cycle.startDate); start.setHours(0,0,0,0);
                                                    const end = cycle.endDate ? new Date(cycle.endDate) : new Date('2100-01-01'); end.setHours(23,59,59,999);
                                                    return recDate >= start && recDate <= end;
                                                } catch(e) { return false; }
                                            };
                                            const isSelected = selectedPerceptionIds.includes(item.id);
                                            return (
                                                <tr key={item.id} className={`transition-colors ${isSelected ? (isDarkMode ? 'bg-purple-900/20' : 'bg-purple-50') : (isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50')}`}>
                                                    {/* AÇÕES (PRIMEIRA) */}
                                                    <td className="p-4 text-center align-middle">
                                                        <div className="flex justify-center gap-2">
                                                            <button onClick={() => handleEditPerception(item)} className={`p-1.5 rounded transition-colors ${isDarkMode ? 'text-blue-400 hover:text-blue-300 hover:bg-slate-600' : 'text-blue-500 hover:text-blue-700 hover:bg-blue-100'}`} title="Editar"><Pencil size={16}/></button>
                                                            <button onClick={() => actions.genericDelete('teamPerception', item.id)} className={`p-1.5 rounded transition-colors ${isDarkMode ? 'text-red-400 hover:text-red-300 hover:bg-slate-600' : 'text-red-500 hover:text-red-700 hover:bg-red-100'}`} title="Excluir"><Trash2 size={16}/></button>
                                                        </div>
                                                    </td>

                                                    <td className="p-4 text-center align-middle">
                                                        <input type="checkbox" checked={isSelected} onChange={() => toggleSelectPerception(item.id)} />
                                                    </td>
                                                    <td className={`p-4 font-bold align-middle ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>{item.email}</td>
                                                    <td className={`p-4 text-xs align-middle ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{item.manager_email || '-'}</td>
                                                    <td className={`p-4 text-center align-middle ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{item.date}</td>
                                                    <td className="p-4 text-center align-middle">
                                                        <span className={`px-2 py-1 rounded font-bold text-white shadow-sm ${item.nota >= 4 ? 'bg-green-500' : item.nota >= 3 ? 'bg-blue-500' : 'bg-red-500'}`}>
                                                            {item.nota !== null ? String(item.nota).replace('.', ',') : '-'}
                                                        </span>
                                                    </td>
                                                    {(data.perceptionCycles || []).sort((a,b) => (a.order || 0) - (b.order || 0)).map(col => {
                                                        return (
                                                            <td key={col.id} className="p-4 text-center align-middle border-l border-gray-100 dark:border-slate-700">
                                                                {(() => {
                                                                    const isMatch = checkCycleMatch(col);
                                                                    if (isMatch) {
                                                                        const fieldKey = col.linkedField || 'nota';
                                                                        let rawValue = item[fieldKey];
                                                                        if (rawValue === undefined || rawValue === null || rawValue === '') {
                                                                            if (fieldKey === 'nota') return <span className="text-red-300 text-[10px]">S/ Nota</span>;
                                                                            return <span className="text-gray-300 text-xl">-</span>;
                                                                        }
                                                                        if (typeof rawValue === 'object') rawValue = JSON.stringify(rawValue); 
                                                                        
                                                                        return <div className={`text-xs whitespace-pre-wrap max-h-16 overflow-y-auto ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`} title={String(rawValue)}>{String(rawValue)}</div>;
                                                                    }
                                                                    return <span className="text-gray-300/30 text-[10px]">-</span>;
                                                                })()}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                        {(!data.teamPerception || data.teamPerception.length === 0) && (
                                            <tr><td colSpan={15} className={`p-8 text-center ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Nenhum dado de percepção registrado.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* MODAL CRIAR COLUNA (MANTIDO) */}
                            {isColumnModalOpen && (
                                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setIsColumnModalOpen(false)}>
                                    <div className={`p-6 rounded-xl w-full max-w-md shadow-2xl space-y-4 ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                        <div className={`flex justify-between items-center border-b pb-2 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                        <h3 className="font-bold text-lg flex items-center gap-2"><LayoutDashboard size={20} className="text-blue-500"/> {columnForm.id ? 'Editar Coluna' : 'Nova Coluna'}</h3>
                                        <button onClick={() => setIsColumnModalOpen(false)} className={`hover:text-gray-500 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}><X size={20}/></button>
                                        </div>
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-4 gap-4">
                                                <div className="col-span-1">
                                                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Ordem</label>
                                                    <Input type="number" className={`text-center ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`} value={columnForm.order} onChange={e => setColumnForm({...columnForm, order: e.target.value})} />
                                                </div>
                                                <div className="col-span-3">
                                                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Vincular Coluna (Planilha)</label>
                                                    <select className={`w-full h-[42px] px-2 border rounded text-xs outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-200'}`} value={columnForm.linkedField} onChange={e => setColumnForm({...columnForm, linkedField: e.target.value})}>
                                                        {!columnForm.linkedField && <option value="">Selecione...</option>}
                                                        {["E","F","G","H","I","J","K","L","M","N","O","P","Q","R","S"].filter(l => {
                                                                const fieldName = `col_${l}`;
                                                                const isUsed = (data.perceptionCycles || []).some(cycle => cycle.linkedField === fieldName && cycle.id !== columnForm.id);
                                                                return !isUsed;
                                                            }).map(l => (<option key={l} value={`col_${l}`}>Coluna {l}</option>))
                                                        }
                                                    </select>
                                                </div>
                                                
                                                {/* SELETOR DE PERGUNTA */}
                                                <div className="col-span-4">
                                                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Vincular Pergunta da Avaliação</label>
                                                    <select className={`w-full p-2 border rounded text-xs outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-200'}`} value={columnForm.linkedQuestionId || ''} onChange={e => setColumnForm({...columnForm, linkedQuestionId: e.target.value})}>
                                                        <option value="">(Opcional) Selecione a pergunta...</option>
                                                        {(perceptionQuestions || []).sort((a,b) => a.order - b.order).map(q => (
                                                            <option key={q.id} value={q.id}>#{q.order} - {q.text.substring(0, 50)}...</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="col-span-4">
                                                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Descrição (Título da Coluna) *</label>
                                                    <Input className={isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''} placeholder="Ex: Pergunta 1 - Q4 2025" value={columnForm.description} onChange={e => setColumnForm({...columnForm, description: e.target.value})} />
                                                </div>
                                            </div>
                                            <div>
                                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Observações</label>
                                                <textarea className={`w-full p-2.5 border rounded text-sm outline-none focus:ring-2 focus:ring-blue-500 resize-none h-24 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-gray-300 text-gray-800'}`} placeholder="Detalhes..." value={columnForm.notes} onChange={e => setColumnForm({...columnForm, notes: e.target.value})} />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div><label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Início *</label><Input type="date" className={isDarkMode ? 'bg-slate-900 border-slate-600 text-white invert-calendar' : ''} value={columnForm.startDate} onChange={e => setColumnForm({...columnForm, startDate: e.target.value})} /></div>
                                                <div><label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Fim (Opcional)</label><Input type="date" className={isDarkMode ? 'bg-slate-900 border-slate-600 text-white invert-calendar' : ''} value={columnForm.endDate} onChange={e => setColumnForm({...columnForm, endDate: e.target.value})} /></div>
                                            </div>
                                        </div>
                                        <div className={`flex justify-end gap-2 pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                            <Button variant="secondary" onClick={() => setIsColumnModalOpen(false)} className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}>Cancelar</Button>
                                            <Button onClick={handleSaveColumn} className="bg-blue-600 hover:bg-blue-700 text-white shadow-md">Salvar</Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                        {/* MODAL EDITAR PERCEPÇÃO (MANUAL) (MANTIDO) */}
                            {isPercepEditModalOpen && (
                                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setIsPercepEditModalOpen(false)}>
                                    <div className={`p-6 rounded-xl w-96 shadow-2xl animate-fade-in space-y-4 ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
                                        <h3 className={`font-bold text-lg flex items-center gap-2 border-b pb-2 ${isDarkMode ? 'text-slate-100 border-slate-700' : 'text-gray-800 border-gray-200'}`}>{percepForm.id ? <Edit3 size={18}/> : <Plus size={18}/>} {percepForm.id ? 'Editar Registro' : 'Novo Registro'}</h3>
                                        <div><label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Email</label><Input className={isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''} value={percepForm.email} onChange={e => setPercepForm({...percepForm, email: e.target.value})} placeholder="email@empresa.com" /></div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div><label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Data (Texto)</label><Input className={isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''} value={percepForm.date} onChange={e => setPercepForm({...percepForm, date: e.target.value})} placeholder="DD/MM/AAAA" /></div>
                                            <div><label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Nota (1-5)</label><Input className={isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''} type="number" step="0.1" value={percepForm.nota} onChange={e => setPercepForm({...percepForm, nota: e.target.value})} /></div>
                                        </div>
                                        {(data.perceptionCycles || []).length > 0 && (
                                            <div className={`mt-4 pt-3 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                                <label className={`text-xs font-bold block mb-2 uppercase tracking-wide ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>Dados Adicionais (Colunas Vinculadas)</label>
                                                <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto custom-scrollbar pr-1">
                                                    {(data.perceptionCycles || []).sort((a,b) => (a.order || 0) - (b.order || 0)).map(col => {
                                                        const fieldKey = col.linkedField || 'nota';
                                                        if(fieldKey === 'nota') return null;
                                                        return (
                                                            <div key={col.id} className="col-span-2">
                                                                <label className={`text-[10px] font-bold block mb-1 truncate ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`} title={col.description}>{col.description} <span className="opacity-50">({fieldKey.replace('col_', '')})</span></label>
                                                                <textarea className={`w-full p-2 border rounded text-xs outline-none focus:ring-2 focus:ring-purple-500 custom-scrollbar resize-y min-h-[80px] ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-gray-300 text-gray-800'}`} placeholder="Insira os dados..." value={percepForm[fieldKey] || ''} onChange={e => setPercepForm({...percepForm, [fieldKey]: e.target.value})} />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        <div className={`flex justify-end gap-2 pt-2 border-t mt-4 ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                            <Button variant="secondary" onClick={() => setIsPercepEditModalOpen(false)} className={isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600' : ''}>Cancelar</Button>
                                            <Button onClick={handleSavePerception} className="bg-purple-600 hover:bg-purple-700 text-white">Salvar</Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        };
        
    
        // ========================================================================
        // 6. RENDERIZAÇÃO PRINCIPAL (MAIN RENDER)
        // ========================================================================
        return (
            <div className="space-y-6">
                {tab === 'users' ? renderUsuariosTab() : (
                <div className="grid grid-cols-1 gap-6">
                    
                    {/* --- HEADER DA ABA --- */}
                                {tab !== 'training' && tab !== 'pcs' && tab !== 'ponto' && ( 
                    
                                <Card className={`lg:col-span-3 flex justify-between items-center ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white'}`} darkMode={isDarkMode}>
                                <div className="flex gap-2 items-center">
                                <h3 className={`font-bold text-lg capitalize flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                                    {tab === 'dept' && 'Departamentos'}
                                    {tab === 'manager' && 'Gestores'}
                                    {tab === 'collab' && 'Pessoas'}
                                    {tab === 'criteria' && 'Critérios da Avaliação'}
                                    {tab === 'types' && 'Tipos de Avaliação'}
                                    {tab === 'gestao' && 'Parâmetros de Gestão'}
                                    {tab === 'config' && 'Parâmetros de Avaliação'}
                                    {tab === 'prazos' && 'Prazos'}
                                    {tab === 'backup' && 'Backup e Restauração de Dados'}

                                </h3>
                                {tab === 'gestao' && <Button onClick={() => handleNewClick()} icon={Plus} className="ml-4">Novo Parâmetro</Button>}
                            </div>
                            
                                <div className="flex items-center gap-3">
                                {tab !== 'config' && tab !== 'backup' && tab !== 'pcs' && (
                                    <div className="relative w-64">
                                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} size={16} />
                                        <Input 
                                            className={`w-full pl-9 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-gray-200'}`} 
                                            placeholder="Buscar..." 
                                            value={search} 
                                            onChange={e=>setSearch(e.target.value)}
                                        />
                                    </div>
                                )}

                                {tab === 'collab' && (
                                        <Button 
                                            onClick={() => {
                                                // Reseta filtros e abre modal
                                                setBulkCollabFilters({ dept: '', type: 'Todos', search: '' });
                                                setBulkCollabSelectedIds([]);
                                                setBulkCollabForm({ personType: '', departmentId: '', companyName: '', companyCNPJ: '', evalTypeId: '', startDate: '', endDate: '', workRuleId: '' });
                                                setIsBulkCollabModalOpen(true); // <--- ISSO É O MAIS IMPORTANTE
                                            }} 
                                            className="ml-2 bg-amber-200 text-amber-800 hover:bg-amber-300 border-amber-300" // Ajuste suas cores aqui
                                            icon={Edit3}
                                        >
                                            Edição em Bloco
                                        </Button>
                                    )}
                                    {tab !== 'gestao' && tab !== 'training' && tab !== 'config' && tab !== 'prazos' && tab !== 'backup' && tab !== 'pcs' && (
                                        <Button onClick={() => handleNewClick()} icon={Plus} className="ml-0">Novo Registro</Button>
                                    )}
                            </div>
                        </Card>
                    )}

                    {/* --- CONTEÚDO PRINCIPAL --- */}
                    <Card className="p-0 overflow-hidden min-h-[400px]" darkMode={isDarkMode}>
                        
                        {/* CASO ESPECIAL: TREINAMENTOS */}
                        {tab === 'training' ? (
                            renderTrainingTab()
                            ) : tab === 'backup' ? (
                            <div className="p-6 h-full overflow-y-auto animate-fade-in">
                                <div className={`max-w-4xl mx-auto rounded-xl shadow-sm border p-8 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                    
                                    <div className="text-center mb-10">
                                        <div className="w-20 h-20 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Database size={40}/>
                                        </div>
                                        <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Central de Segurança de Dados</h3>
                                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                            Gerencie a exportação (backup) e importação (restauração) de todos os dados do sistema.
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        {/* CARD EXPORTAR */}
                                        <div className={`p-6 rounded-xl border flex flex-col items-center text-center gap-4 transition-all hover:shadow-lg ${isDarkMode ? 'bg-slate-900 border-slate-700 hover:border-blue-500' : 'bg-slate-50 border-gray-200 hover:border-blue-400'}`}>
                                            <div className={`p-4 rounded-full ${isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                                                <FileDown size={32}/>
                                            </div>
                                            <div>
                                                <h4 className={`font-bold text-lg ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>Exportar Backup</h4>
                                                <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                                    Gera um arquivo <b>.JSON</b> completo contendo cadastros, avaliações, histórico e configurações atuais.
                                                </p>
                                            </div>
                                            <button 
                                                onClick={handleExportBackup}
                                                className="mt-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-sm shadow-md transition-colors w-full flex items-center justify-center gap-2"
                                            >
                                                <FileDown size={16}/> Baixar Arquivo
                                            </button>
                                        </div>

                                        {/* CARD IMPORTAR */}
                                        <div className={`p-6 rounded-xl border flex flex-col items-center text-center gap-4 transition-all hover:shadow-lg ${isDarkMode ? 'bg-slate-900 border-slate-700 hover:border-green-500' : 'bg-slate-50 border-gray-200 hover:border-green-400'}`}>
                                            <div className={`p-4 rounded-full ${isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-600'}`}>
                                                {importing ? <Loader2 size={32} className="animate-spin"/> : <Upload size={32}/>}
                                            </div>
                                            <div>
                                                <h4 className={`font-bold text-lg ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>Restaurar Dados</h4>
                                                <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                                    Carrega um arquivo de backup e recria os registros no banco de dados.
                                                </p>
                                            </div>
                                            <label className={`mt-auto px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold text-sm shadow-md transition-colors w-full cursor-pointer flex justify-center items-center gap-2 ${importing ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                                <input type="file" accept=".json" className="hidden" onChange={handleImportBackup} disabled={importing} />
                                                {importing ? <Loader2 size={16} className="animate-spin"/> : <Upload size={16}/>}
                                                {importing ? 'Processando...' : 'Selecionar Arquivo'}
                                            </label>
                                        </div>
                                    </div>

                                    <div className={`mt-8 p-4 rounded-lg border flex gap-3 items-start ${isDarkMode ? 'bg-yellow-900/10 border-yellow-800/50 text-yellow-200' : 'bg-yellow-50 border-yellow-200 text-yellow-800'}`}>
                                        <AlertTriangle size={20} className="shrink-0 mt-0.5"/>
                                        <div className="text-xs space-y-1">
                                            <p className="font-bold">Atenção ao Restaurar:</p>
                                            <p>Esta ação irá adicionar os dados do arquivo ao sistema. Se existirem registros com o mesmo ID, eles serão <b>sobrescritos</b>. Recomendamos realizar este procedimento preferencialmente em um ambiente limpo ou novo.</p>
                                        </div>
                                    </div>

                                    {/* NOVO: BLOCO DE LIMPEZA DE ARTEFATOS (LIXO) */}
                                    <div className={`mt-8 p-6 rounded-xl border flex flex-col gap-4 ${isDarkMode ? 'bg-red-900/10 border-red-800' : 'bg-red-50 border-red-200'}`}>
                                        <div className="flex items-center gap-3 text-red-600 dark:text-red-500">
                                            <Trash2 size={24}/>
                                            <h4 className="font-bold text-lg">Limpeza de Artefatos Antigos (Avançado)</h4>
                                        </div>
                                        <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-red-200' : 'text-red-800'}`}>
                                            Utilize esta ferramenta para excluir permanentemente documentos residuais salvos na raiz do sistema (ex: <i>c_69e7d6eb0fc55...</i> ou variações como <i>-2, -970</i>). 
                                            Basta inserir a parte principal do nome do arquivo.
                                        </p>
                                        <div className="flex flex-col md:flex-row gap-3">
                                            <input 
                                                type="text" 
                                                value={artifactPrefix}
                                                onChange={(e) => setArtifactPrefix(e.target.value)}
                                                placeholder="Cole aqui o nome ou prefixo do documento..." 
                                                className={`flex-1 p-3 rounded-lg border outline-none focus:ring-2 focus:ring-red-500 ${isDarkMode ? 'bg-slate-900 border-red-800 text-white placeholder-red-900' : 'bg-white border-red-300 text-gray-800'}`}
                                            />
                                            <button 
                                                onClick={handleCleanArtifacts}
                                                disabled={isCleaning}
                                                className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-3 rounded-lg shadow-md transition-colors flex justify-center items-center gap-2 disabled:opacity-50 shrink-0"
                                            >
                                                {isCleaning ? <Loader2 size={18} className="animate-spin"/> : <Trash2 size={18}/>}
                                                Buscar e Excluir
                                            </button>
                                        </div>
                                    </div>

                                </div>
                            </div>
                            
                        ) : tab === 'config' ? (
                            <div className={`flex flex-col h-full ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                                
                                {/* BARRA DE NAVEGAÇÃO SUPERIOR + BOTÃO DE BACKUP */}
                                <div className={`flex items-center border-b shrink-0 px-2 gap-2 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                    
                                    {/* Lista de Sub-abas */}
                                    <div className="flex overflow-x-auto flex-1 no-scrollbar">
                                        {[{ id: 'metricas', label: 'Métricas Globais', icon: TrendingUp, perm: 'access_sub_metricas' }, 
                                        { id: 'deadlines', label: 'Prazos', icon: Clock, perm: 'access_sub_deadlines' }, 
                                        { id: 'messages', label: 'Mensagens PDF', icon: FileText, perm: 'access_sub_messages' }, 
                                        { id: 'competencias', label: 'Competências', icon: Award, perm: 'access_sub_competencias' }, 
                                        { id: 'minWage', label: 'Salário Mínimo', icon: BadgeDollarSign, perm: 'access_sub_minwage' }, 
                                        { id: 'logo', label: 'Logomarca', icon: Sparkles, perm: 'access_sub_logo' }
                                        ].map(sub => {
                                            if (!hasPerm(sub.perm)) return null;
                                            const isActive = configSubTab === sub.id;
                                            return (
                                                <button 
                                                    key={sub.id} 
                                                    onClick={() => { setConfigSubTab(sub.id); setSearch(''); }} 
                                                    className={`px-4 py-3 text-xs font-bold flex items-center gap-2 transition-colors whitespace-nowrap border-b-2 ${isActive ? (isDarkMode ? 'bg-slate-800 text-blue-400 border-blue-500' : 'bg-white text-blue-600 border-blue-600') : (isDarkMode ? 'text-slate-400 hover:bg-slate-800 border-transparent' : 'text-gray-500 hover:bg-gray-200 border-transparent')}`}
                                                >
                                                    <sub.icon size={14}/> {sub.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="flex-1 overflow-y-auto max-h-[600px] custom-scrollbar">
                                    {/* ... (O conteúdo das sub-abas permanece o mesmo, o código abaixo apenas reconecta a lógica existente) ... */}
                                    {configSubTab === 'metricas' && (
                                        <>
                                            {/* HEADER / BUSCA / BOTÃO */}
                                            <div className={`flex justify-between items-center p-3 border-b sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                <div className="relative w-64">
                                                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} size={14} />
                                                    <Input className={`w-full pl-9 h-9 text-xs ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white'}`} placeholder="Buscar métrica..." value={search} onChange={e=>setSearch(e.target.value)}/>
                                                </div>
                                                <Button onClick={handleNewClick} icon={Plus} className="h-9 text-xs">Nova Métrica</Button>
                                            </div>
                                            {/* TABELA MÉTRICAS (Simplificada para caber na resposta, use o conteúdo original do seu arquivo para o corpo das tabelas) */}
                                            <div className="max-h-[600px] overflow-y-auto">
                                                {/* (Reutilize a tabela de métricas original aqui) */}
                                                <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                    <thead className={`uppercase text-xs font-bold sticky top-0 z-10 border-b ${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                                        <tr>
                                                            <th className="p-4">Descrição</th>
                                                            <th className="p-4">Departamento</th>
                                                            <th className="p-4 text-center">Tipo</th>
                                                            <th className="p-4 text-center">Data Início</th>
                                                            <th className="p-4 text-center">Status</th>
                                                            <th className="p-4 text-center text-blue-500" title="Média Qualitativa para Promoção">Média Promo.</th>
                                                            <th className="p-4 text-center text-indigo-500" title="Média Qualitativa para Premiação">Média Prem.</th>
                                                            <th className="p-4 text-center text-red-500" title="Limite de Pontos Quantitativos">Max. Perda</th>
                                                            <th className="p-4 text-center">Ações</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                        {filterList(data.globalConfigs).map((item, i) => {
                                                            const isActive = !item.endDate;
                                                            const rowClass = i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');
                                                            const hoverClass = isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-blue-50';

                                                            return (
                                                                <tr key={item.id} className={`${rowClass} ${hoverClass} transition-colors border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                                                    <td className={`p-4 font-bold align-middle ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{item.name}</td>
                                                                    <td className={`p-4 align-middle text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Geral (Todos)</td>
                                                                    <td className="p-4 text-center align-middle"><span className={`px-2 py-1 rounded text-[10px] font-bold uppercase border ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-200'}`}>Global</span></td>
                                                                    <td className={`p-4 text-center align-middle text-xs font-mono ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{item.startDate ? new Date(item.startDate).toLocaleDateString('pt-BR') : '-'}</td>
                                                                    <td className="p-4 text-center align-middle">
                                                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border ${isActive ? (isDarkMode ? 'bg-green-900/30 text-green-400 border-green-800' : 'bg-green-100 text-green-700 border-green-200') : (isDarkMode ? 'bg-slate-700 text-slate-400 border-slate-600' : 'bg-gray-100 text-gray-500 border-gray-200')}`}>
                                                                            {isActive ? <CheckCircle size={10}/> : <X size={10}/>} {isActive ? 'ATIVO' : 'INATIVO'}
                                                                        </span>
                                                                    </td>
                                                                    <td className="p-4 text-center align-middle"><div className={`font-bold text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{item.mediaPromocao ? parseFloat(item.mediaPromocao).toFixed(1) : '-'}</div></td>
                                                                    <td className="p-4 text-center align-middle"><div className={`font-bold text-sm ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{item.mediaPremiacao ? parseFloat(item.mediaPremiacao).toFixed(1) : '-'}</div></td>
                                                                    <td className="p-4 text-center align-middle"><div className={`font-bold text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{item.maxPointsLost !== undefined && item.maxPointsLost !== null ? parseFloat(item.maxPointsLost).toFixed(1) : '-'}</div></td>
                                                                    <td className="p-4 text-center align-middle">
                                                                        <div className="flex justify-center gap-2">
                                                                            <button onClick={()=>handleEdit(item)} className={`p-1.5 rounded transition ${isDarkMode ? 'text-blue-400 hover:bg-slate-600' : 'text-blue-500 hover:bg-blue-50'}`}><Pencil size={16}/></button>
                                                                            <button onClick={()=>actions.deleteConfig(item.id)} className={`p-1.5 rounded transition ${isDarkMode ? 'text-red-400 hover:bg-slate-600' : 'text-red-500 hover:bg-red-50'}`}><Trash2 size={16}/></button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    )}

                                    {configSubTab === 'deadlines' && (
                                        <>
                                            <div className={`flex justify-between items-center p-3 border-b sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                <div className="relative w-64">
                                                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} size={14} />
                                                    <Input className={`w-full pl-9 h-9 text-xs ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white'}`} placeholder="Buscar prazo..." value={search} onChange={e=>setSearch(e.target.value)}/>
                                                </div>
                                                <Button onClick={handleNewClick} icon={Plus} className="h-9 text-xs">Novo Prazo</Button>
                                            </div>
                                            <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                <thead className={`uppercase text-xs font-bold border-b ${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                                                    <tr>
                                                        <th className="p-3 pl-6">Regra Aplicada</th>
                                                        <th className="p-3 text-center">T1</th><th className="p-3 text-center">T2</th><th className="p-3 text-center">T3</th><th className="p-3 text-center">T4</th>
                                                        <th className="p-3 text-center">Ações</th>
                                                    </tr>
                                                </thead>
                                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                    {filterList(data.deadlines).map(d => (
                                                        <tr key={d.id} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}>
                                                            <td className="p-3 pl-6">
                                                                <div className={`font-bold ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{d.name || "Regra Sem Nome"}</div>
                                                                <div className={`text-[10px] uppercase flex gap-2 mt-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                                                                    <span>Depto: {d.departmentId ? data.departments.find(dep=>dep.id===d.departmentId)?.name : 'Geral'}</span>
                                                                    <span className="text-blue-500 font-bold border-l pl-2 border-slate-600">{d.personType}</span>
                                                                </div>
                                                            </td>
                                                            <td className={`p-3 text-center font-mono ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{d.q1}</td>
                                                            <td className={`p-3 text-center font-mono ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{d.q2}</td>
                                                            <td className={`p-3 text-center font-mono ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{d.q3}</td>
                                                            <td className={`p-3 text-center font-mono ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{d.q4}</td>
                                                            <td className="p-3 text-center flex justify-center gap-2">
                                                                <button onClick={()=>handleEdit(d)} className={`p-1.5 rounded transition ${isDarkMode ? 'text-blue-400 hover:bg-slate-600' : 'text-blue-500 hover:bg-blue-50'}`}><Pencil size={14}/></button>
                                                                <button onClick={()=>actions.deleteDeadline(d.id)} className={`p-1.5 rounded transition ${isDarkMode ? 'text-red-400 hover:bg-slate-600' : 'text-red-500 hover:bg-red-50'}`}><Trash2 size={14}/></button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </>
                                    )}

                                    {/* ... (Mantenha o código das outras sub-abas: messages, competencias, minWage, logo) ... */}
                                {configSubTab === 'messages' && (
                                        <>
                                            {/* HEADER LOCAL: BUSCA E NOVO */}
                                            <div className={`flex justify-between items-center p-3 border-b sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                <div className="relative w-64">
                                                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} size={14} />
                                                    <Input className={`w-full pl-9 h-9 text-xs ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white'}`} placeholder="Buscar mensagem..." value={search} onChange={e=>setSearch(e.target.value)}/>
                                                </div>
                                                <Button onClick={handleNewClick} icon={Plus} className="h-9 text-xs">Nova Mensagem</Button>
                                            </div>

                                            {/* TABELA DE MENSAGENS */}
                                            <div className="max-h-[600px] overflow-y-auto">
                                                <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                    <thead className={`uppercase text-xs font-bold sticky top-0 z-10 border-b ${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                                        <tr>
                                                            <th className="p-3 pl-6">Tipo</th>
                                                            <th className="p-3 text-center">Público</th>
                                                            <th className="p-3">Dpto</th> {/* ALTERADO PARA DPTO */}
                                                            <th className="p-3 w-[25%]">Título</th> {/* AUMENTADA LARGURA */}
                                                            <th className="p-3 w-[30%]">Mensagem</th>
                                                            <th className="p-3 text-center">Cores</th>
                                                            <th className="p-3 text-center">Ações</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                        {filterList(data.pdfMessages).map((msg, i) => (
                                                            <tr key={msg.id} className={`${i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50')} ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-blue-50'} transition-colors border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                                                
                                                                <td className={`p-3 pl-6 font-bold align-middle ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{msg.type}</td>
                                                                
                                                                <td className="p-3 text-center align-middle">
                                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-200'}`}>{msg.personType}</span>
                                                                </td>
                                                                
                                                                <td className="p-3 align-middle text-xs">
                                                                    {msg.departmentId === 'all' ? 'Todos' : data.departments.find(d=>d.id===msg.departmentId)?.name || '-'}
                                                                </td>
                                                                
                                                                {/* TÍTULO SEM QUEBRA DE LINHA E COM TOOLTIP */}
                                                                <td className="p-3 align-middle font-medium text-xs">
                                                                    <div className="truncate max-w-[250px]" title={msg.title}>
                                                                        {msg.title}
                                                                    </div>
                                                                </td>
                                                                
                                                                <td className="p-3 align-middle">
                                                                    <div className="truncate max-w-[250px] text-xs opacity-80 cursor-help" title={msg.body}>
                                                                        {msg.body}
                                                                    </div>
                                                                </td>
                                                                
                                                                <td className="p-3 text-center align-middle">
                                                                    <div className="flex justify-center gap-2">
                                                                        <div className="w-5 h-5 rounded border shadow-sm" style={{backgroundColor: msg.bgColor}} title={`Fundo: ${msg.bgColor}`}></div>
                                                                        <div className="w-5 h-5 rounded border shadow-sm flex items-center justify-center font-bold text-[10px]" style={{backgroundColor: '#fff', color: msg.textColor}} title={`Texto: ${msg.textColor}`}>T</div>
                                                                    </div>
                                                                </td>
                                                                
                                                                <td className="p-3 text-center align-middle">
                                                                    <div className="flex justify-center gap-2">
                                                                        <button onClick={()=>handleEdit(msg)} className={`p-1.5 rounded transition ${isDarkMode ? 'text-blue-400 hover:bg-slate-600' : 'text-blue-500 hover:bg-blue-50'}`}><Pencil size={14}/></button>
                                                                        <button onClick={() => actions.genericDelete('messages', msg.id)} className={`p-1.5 rounded transition ${isDarkMode ? 'text-red-400 hover:bg-slate-600' : 'text-red-500 hover:bg-red-50'}`}><Trash2 size={14}/></button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        {filterList(data.pdfMessages).length === 0 && <tr><td colSpan="7" className="p-8 text-center text-gray-400">Nenhuma mensagem configurada.</td></tr>}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    )}

                                        {configSubTab === 'competencias' && (
                                        <>
                                            {/* HEADER LOCAL: BUSCA E NOVO */}
                                            <div className={`flex justify-between items-center p-3 border-b sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                <div className="relative w-64">
                                                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} size={14} />
                                                    <Input className={`w-full pl-9 h-9 text-xs ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white'}`} placeholder="Buscar competência..." value={search} onChange={e=>setSearch(e.target.value)}/>
                                                </div>
                                                <Button onClick={handleNewClick} icon={Plus} className="h-9 text-xs">Nova Competência</Button>
                                            </div>

                                            <div className="max-h-[600px] overflow-y-auto">
                                                <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                    <thead className={`uppercase text-xs font-bold border-b ${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                                                        <tr>
                                                            <th className="p-3 pl-6">Competência</th><th className="p-3">Depto</th><th className="p-3 text-center">Tipo</th><th className="p-3 text-center">Vigência</th><th className="p-3 text-center">Cargos</th><th className="p-3 text-center">Ações</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                        {filterList(data.competencias || []).map(c => (
                                                            <tr key={c.id} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}>
                                                                <td className={`p-3 pl-6 font-bold ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>{c.name}</td>
                                                                <td className={`p-3 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{data.departments.find(d=>d.id===c.departmentId)?.name || 'Todos'}</td>
                                                                <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600'}`}>{c.personType}</span></td>
                                                                <td className={`p-3 text-center text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>{formatMonthYear(c.startDate)} {c.endDate ? `- ${formatMonthYear(c.endDate)}` : ''}</td>
                                                                <td className="p-3 text-center"><span className={`text-xs font-bold px-2 py-1 rounded cursor-help ${isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-600'}`} title={(c.roleIds || []).join(', ')}>{(c.roleIds || []).length} Cargos</span></td>
                                                                <td className="p-3 text-center flex justify-center gap-2">
                                                                    <button onClick={()=>handleEdit(c)} className={`p-1.5 rounded transition ${isDarkMode ? 'text-blue-400 hover:bg-slate-600' : 'text-blue-500 hover:bg-blue-50'}`}><Pencil size={14}/></button>
                                                                    <button onClick={()=>actions.deleteCompetencia(c.id)} className={`p-1.5 rounded transition ${isDarkMode ? 'text-red-400 hover:bg-slate-600' : 'text-red-500 hover:bg-red-50'}`}><Trash2 size={14}/></button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    )}

                                        {configSubTab === 'minWage' && (
                                        <>
                                            {/* HEADER LOCAL: BUSCA E NOVO */}
                                            <div className={`flex justify-between items-center p-3 border-b sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                <div className="relative w-64">
                                                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} size={14} />
                                                    <Input className={`w-full pl-9 h-9 text-xs ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white'}`} placeholder="Buscar histórico..." value={search} onChange={e=>setSearch(e.target.value)}/>
                                                </div>
                                                <Button onClick={handleNewClick} icon={Plus} className="h-9 text-xs">Novo Salário</Button>
                                            </div>

                                            <div className="max-h-[600px] overflow-y-auto">
                                                <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                    <thead className={`uppercase text-xs font-bold border-b ${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                                                        <tr><th className="p-3 pl-6">Valor Base</th><th className="p-3 text-center">Início Vigência</th><th className="p-3 text-center">Ações</th></tr>
                                                    </thead>
                                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                        {filterList(data.minWageHistory || []).sort((a,b)=>new Date(b.validFrom)-new Date(a.validFrom)).map(mw => (
                                                            <tr key={mw.id} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}>
                                                                <td className={`p-3 pl-6 font-bold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>{formatCurrency(mw.value)}</td>
                                                                <td className={`p-3 text-center ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{formatMonthYear(mw.validFrom)}</td>
                                                                <td className="p-3 text-center flex justify-center gap-2">
                                                                    <button onClick={()=>{ setForm(mw); setIsCadModalOpen(true); }} className={`p-1.5 rounded transition ${isDarkMode ? 'text-blue-400 hover:bg-slate-600' : 'text-blue-500 hover:bg-blue-50'}`}><Pencil size={14}/></button>
                                                                    <button onClick={()=>actions.deleteMinWageHistory(mw.id)} className={`p-1.5 rounded transition ${isDarkMode ? 'text-red-400 hover:bg-slate-600' : 'text-red-500 hover:bg-red-50'}`}><Trash2 size={14}/></button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    )}
                                    {configSubTab === 'logo' && (
                                        <div className="p-6 flex flex-col items-center gap-6">
                                            <div className="text-center">
                                                <h3 className={`font-bold text-lg ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>Logomarca dos Relatórios</h3>
                                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Esta imagem aparecerá no cabeçalho dos PDFs gerados.</p>
                                            </div>
                                            <div className={`w-48 h-48 border-2 border-dashed rounded-xl flex items-center justify-center relative overflow-hidden group ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-300'}`}>
                                                {(data.globalConfigs?.[0]?.logoUrl || logoBase64) ?
                                                    (<img src={logoBase64 || data.globalConfigs[0].logoUrl} alt="Logo" className="w-full h-full object-contain p-2" />) : 
                                                    (<div className={`flex flex-col items-center ${isDarkMode ? 'text-slate-600' : 'text-gray-400'}`}><Upload size={32}/><span className="text-xs mt-2">Sem Logo</span></div>)
                                                }
                                                <label className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer text-white font-bold text-xs">
                                                    Alterar Imagem
                                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if (file) {
                                                            const reader = new FileReader();
                                                            reader.onloadend = () => {
                                                                setLogoBase64(reader.result);
                                                                const currentConfig = data.globalConfigs?.[0] || { id: 'def', name: 'Padrão' };
                                                                actions.saveConfig({ ...currentConfig, logoUrl: reader.result });
                                                            };
                                                            reader.readAsDataURL(file);
                                                        }
                                                    }}/>
                                                </label>
                                            </div>
                                            {data.globalConfigs?.[0]?.logoUrl && (
                                                <Button variant="danger" onClick={() => { const conf = data.globalConfigs[0]; actions.saveConfig({ ...conf, logoUrl: null }); setLogoBase64(null); }}>Remover Logo</Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                        ) : tab === 'pcs' ? (
                    <div className="space-y-4 animate-fade-in">
                        <Card className={`flex flex-col md:flex-row justify-between items-center gap-4 p-4 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`} darkMode={isDarkMode}>
                            <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto">
                                <div className={`flex items-center gap-2 border rounded px-2 py-1 ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-50'}`}>
                                    <span className={`text-xs font-bold hidden sm:block ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{selectedFile ? selectedFile.name.substring(0, 15)+"..." : "PDF:"}</span>
                                    <label className={`cursor-pointer border px-2 py-1 rounded text-xs font-bold flex items-center gap-1 shadow-sm ${isDarkMode ? 'bg-slate-600 hover:bg-slate-500 text-slate-200 border-slate-500' : 'bg-white hover:bg-gray-100 text-gray-700'}`}>
                                        <input type="file" accept=".pdf" onChange={handleFileSelect} className="hidden" />
                                        <FileCheck size={14}/> <span className="hidden sm:inline">Selecionar</span>
                                    </label>
                                    <Button onClick={() => { setImportConfig({ targetDept: '', ignoreTerm: '' }); setImportModalOpen(true); }} disabled={!selectedFile || isProcessing} className={`text-xs px-2 py-1 h-7 ${!selectedFile ? 'bg-gray-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}>
                                        {isProcessing ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
                                    </Button>
                                </div>
                                <Button variant="warning" onClick={()=>setModal('readjust')} className="text-xs px-3 h-9 whitespace-nowrap"><Coins size={14} className="mr-1"/> Reajuste Dissídio</Button>
                                <Button onClick={() => { setForm({}); setIsPCSModalOpen(true); }} className="text-xs px-3 h-9 bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"><Plus size={14} className="mr-1"/> Novo Cargo</Button>
                            </div>
                            <div className="relative w-full md:w-64">
                                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} size={16} />
                                <Input className={`w-full pl-9 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white'}`} placeholder="Buscar cargo, nível..." value={pcsSearch} onChange={e=>setPcsSearch(e.target.value)}/>
                            </div>
                        </Card>

                        <Card className="p-0 overflow-hidden shadow-md" darkMode={isDarkMode}>
                            <div className="max-h-[600px] overflow-y-auto">
                                <table className={`w-full text-sm text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                                    <thead className={`uppercase text-xs font-bold sticky top-0 border-b z-10 ${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                        <tr>
                                            <th className="p-4 w-10 text-center"><input type="checkbox" onChange={toggleSelectAllPcs} checked={filteredPcsList.length > 0 && selectedPcsIds.length >= filteredPcsList.length} /></th>
                                            <th className="p-4">Cargo / Função</th>
                                            <th className="p-4 text-center">Senioridade</th>
                                            <th className="p-4 text-center">Nível</th>
                                            <th className="p-4">Departamento</th>
                                            <th className="p-4 text-right">Salário Base</th>
                                            <th className="p-4 text-center">Vigência</th>
                                            <th className="p-4 text-center">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                        {filteredPcsList.length > 0 ? (
                                            filteredPcsList.map((p, i) => (
                                                <tr key={p.id} className={`${selectedPcsIds.includes(p.id) ? (isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50') : (i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50'))} ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-blue-50'} transition-colors`}>
                                                    <td className="p-4 text-center"><input type="checkbox" checked={selectedPcsIds.includes(p.id)} onChange={() => toggleSelectPcs(p.id)} /></td>
                                                    <td className={`p-4 font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{p.role}</td>
                                                    <td className="p-4 text-center"><span className={`px-2 py-0.5 rounded text-xs font-bold border ${isDarkMode ? 'bg-blue-900/50 text-blue-300 border-blue-800' : 'bg-blue-50 text-blue-700 border-blue-100'}`}>{p.seniority || '-'}</span></td>
                                                    <td className={`p-4 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{p.level}</td>
                                                    <td className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>{p.department}</td>
                                                    <td className="p-4 text-right font-bold text-green-600 text-base">{formatCurrency(p.salary)}</td>
                                                    <td className={`p-4 text-center text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>{formatMonthYear(p.validFrom)}</td>
                                                    <td className="p-4 text-center">
                                                        <div className="flex justify-center gap-2">
                                                            <button onClick={()=>{ setForm(p); setIsPCSModalOpen(true); }} className="text-blue-500 hover:text-blue-400 p-1.5 rounded hover:bg-blue-500/10 transition"><Pencil size={16}/></button>
                                                            <button onClick={()=>actions.deletePCS(p.id)} className="text-red-500 hover:text-red-400 p-1.5 rounded hover:bg-red-500/10 transition"><Trash2 size={16}/></button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr><td colSpan="8" className="p-8 text-center text-gray-400">Nenhum cargo visível.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card>

                        {/* Barra de Ação em Bloco */}
                        {selectedPcsIds.length > 0 && (
                            <div className={`border rounded-lg p-2 flex items-center justify-between mb-2 animate-fade-in shadow-sm ${isDarkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                                <span className={`text-sm font-bold flex items-center gap-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-800'}`}><CheckSquare size={16}/> {selectedPcsIds.length} cargos selecionados</span>
                                <div className="flex gap-2">
                                    <button onClick={() => { setBulkPcsForm({}); setIsBulkEditPcsOpen(true); }} className={`border px-3 py-1 rounded text-xs font-bold transition-colors flex items-center gap-1 ${isDarkMode ? 'bg-slate-700 text-blue-300 border-slate-600 hover:bg-slate-600' : 'bg-white hover:bg-blue-100 text-blue-700 border-blue-200'}`}><Edit3 size={14}/> Editar em Bloco</button>
                                    <button onClick={handleBulkDeletePcs} className={`border px-3 py-1 rounded text-xs font-bold transition-colors flex items-center gap-1 ${isDarkMode ? 'bg-slate-700 text-red-400 border-slate-600 hover:bg-slate-600' : 'bg-white hover:bg-red-100 text-red-600 border-red-200'}`}><Trash2 size={14}/> Excluir</button>
                                </div>
                            </div>
                        )}

                        {/* MODAL EDITAR EM BLOCO */}
                        {isBulkEditPcsOpen && (
                            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                                <div className={`p-6 rounded-xl w-full max-w-lg shadow-2xl animate-fade-in space-y-4 ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white'}`}>
                                    <div className={`flex justify-between items-center border-b pb-2 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                        <h3 className="font-bold text-lg flex items-center gap-2"><Edit3 size={18}/> Editar em Bloco ({selectedPcsIds.length})</h3>
                                        <button onClick={() => setIsBulkEditPcsOpen(false)}><X size={20} className="text-gray-400 hover:text-gray-600"/></button>
                                    </div>
                                    <div className={`p-3 rounded border text-xs mb-2 ${isDarkMode ? 'bg-yellow-900/20 text-yellow-300 border-yellow-800' : 'bg-yellow-50 text-yellow-800 border-yellow-200'}`}>Preencha <b>apenas</b> os campos que deseja alterar. Campos vazios manterão o valor original.</div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="col-span-2"><label className="text-xs font-bold block mb-1">Título do Cargo</label><Input value={bulkPcsForm.role||''} onChange={e=>setBulkPcsForm({...bulkPcsForm, role:e.target.value})} placeholder="Manter original..." className={isDarkMode ? 'bg-slate-700 border-slate-600' : ''}/></div>
                                        <div><label className="text-xs font-bold block mb-1">Senioridade</label><select className={`w-full p-2.5 border rounded text-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white'}`} value={bulkPcsForm.seniority||''} onChange={e=>setBulkPcsForm({...bulkPcsForm, seniority:e.target.value})}><option value="">(Manter original)</option><option>JR</option><option>PL</option><option>SR</option><option>N/A</option></select></div>
                                        <div><label className="text-xs font-bold block mb-1">Nível</label><select className={`w-full p-2.5 border rounded text-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white'}`} value={bulkPcsForm.level||''} onChange={e=>setBulkPcsForm({...bulkPcsForm, level:e.target.value})}><option value="">(Manter original)</option><option>I</option><option>II</option><option>III</option><option>IV</option></select></div>
                                        <div><label className="text-xs font-bold block mb-1">Salário Base</label><Input type="number" value={bulkPcsForm.salary||''} onChange={e=>setBulkPcsForm({...bulkPcsForm, salary:e.target.value})} placeholder="Manter..." className={isDarkMode ? 'bg-slate-700 border-slate-600' : ''}/></div>
                                        <div><label className="text-xs font-bold block mb-1">Depto Associado</label><select className={`w-full p-2.5 border rounded text-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white'}`} value={bulkPcsForm.department||''} onChange={e=>setBulkPcsForm({...bulkPcsForm, department:e.target.value})}><option value="">(Manter original)</option><option value="Todos">Todos</option>{data.departments.map(d=><option key={d.id} value={d.name}>{d.name}</option>)}</select></div>
                                        <div><label className="text-xs font-bold block mb-1">Nova Vigência</label><Input type="date" value={bulkPcsForm.validFrom||''} onChange={e=>setBulkPcsForm({...bulkPcsForm, validFrom:e.target.value})} className={isDarkMode ? 'bg-slate-700 border-slate-600' : ''}/></div>
                                    </div>
                                    <div className={`flex justify-end gap-2 pt-2 border-t mt-4 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                        <Button variant="secondary" onClick={() => setIsBulkEditPcsOpen(false)}>Cancelar</Button>
                                        <Button onClick={handleBulkEditSave}>Aplicar Alterações</Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* MODAL DE REAJUSTE COLETIVO */}
                        {modal === 'readjust' && (
                            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
                                <div className={`p-6 rounded-xl w-80 space-y-4 shadow-2xl ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800'}`}>
                                    <h3 className="font-bold text-lg border-b pb-2 mb-2 flex items-center gap-2"><Coins size={18} className="text-yellow-500"/> Reajuste Coletivo</h3>
                                    <div><label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Percentual (%)</label><Input type="number" value={readjustPercent} onChange={e=>setReadjustPercent(e.target.value)} className={`text-center font-bold text-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`} placeholder="0.0"/></div>
                                    <div><label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Nova Vigência</label><Input type="date" value={readjustDate} onChange={e=>setReadjustDate(e.target.value)} className={isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}/></div>
                                    <div className="flex gap-2 pt-2"><Button variant="secondary" onClick={()=>setModal(null)} className={`w-full ${isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600' : ''}`}>Cancelar</Button><Button onClick={handleMassReadjust} className="w-full bg-blue-600 hover:bg-blue-700 text-white">Aplicar</Button></div>
                                </div>
                            </div>
                        )}
                        
                        {/* MODAL IMPORTAÇÃO */}
                        {importModalOpen && (
                            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                                <div className="bg-white p-6 rounded-xl w-96 shadow-2xl animate-fade-in space-y-4">
                                    <h3 className="font-bold text-lg text-gray-800 flex items-center gap-2"><Settings size={18}/> Configurar Importação</h3>
                                    <div><label className="text-xs font-bold text-gray-600 block mb-1">Departamento Destino</label><select className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white text-sm" value={importConfig.targetDept} onChange={e => setImportConfig({...importConfig, targetDept: e.target.value})}><option value="">Manter Original</option><option value="Todos">Todos</option>{data.departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}</select></div>
                                    <div><label className="text-xs font-bold text-gray-600 block mb-1">Vigência Inicial</label><Input type="date" value={importDate} onChange={e=>setImportDate(e.target.value)} className="text-sm" /></div>
                                    <div><label className="text-xs font-bold text-gray-600 block mb-1">Desconsiderar Palavra (Filtro)</label><div className="relative"><Input placeholder="Ex: Contábil" value={importConfig.ignoreTerm} onChange={e => setImportConfig({...importConfig, ignoreTerm: e.target.value})} className="pr-8"/><Eraser size={14} className="absolute right-3 top-3 text-gray-400"/></div></div>
                                    <div className="flex justify-end gap-2 pt-2"><Button variant="secondary" onClick={()=>setImportModalOpen(false)}>Cancelar</Button><Button onClick={confirmImport}>Importar Agora</Button></div>
                                </div>
                            </div>
                        )}

                        {isPCSModalOpen && (
                            <CadastroModal isOpen={isPCSModalOpen} onClose={() => setIsPCSModalOpen(false)} tab="pcs" form={form} setForm={setForm} data={data} handleSave={handleSavePCSForm} currentUser={currentUser} isDarkMode={isDarkMode} />
                        )}

            
                    </div>

                        ) : tab === 'criteria' ? (
                            (() => {
                                // Verifica permissões usando a nova função hasPerm
                                const canViewCriteria = hasPerm('access_sub_criteria_list');
                                const canViewConference = hasPerm('access_sub_conference_types');

                                // Se não tiver nenhuma permissão, exibe mensagem de bloqueio
                                if (!canViewCriteria && !canViewConference) return <div className="p-8 text-center text-gray-400">Acesso negado a este módulo.</div>;

                                return (
                                    <div className={`flex flex-col h-full min-h-[500px] ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                                        <div className={`flex border-b shrink-0 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                            
                                            {canViewCriteria && (
                                                <button 
                                                    onClick={() => { setCriteriaSubTab('criterios'); setSearch(''); }} 
                                                    className={`px-6 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${criteriaSubTab === 'criterios' ? (isDarkMode ? 'border-blue-500 text-blue-400 bg-slate-800' : 'border-blue-600 text-blue-600 bg-white') : (isDarkMode ? 'border-transparent text-slate-400 hover:bg-slate-800' : 'border-transparent text-gray-500 hover:bg-gray-100')}`}
                                                >
                                                    <List size={14}/> Critérios de Avaliação
                                                </button>
                                            )}
                                            
                                            {canViewConference && (
                                                <button 
                                                    onClick={() => { setCriteriaSubTab('conferencia'); setSearch(''); }} 
                                                    className={`px-6 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${criteriaSubTab === 'conferencia' ? (isDarkMode ? 'border-indigo-500 text-indigo-400 bg-slate-800' : 'border-indigo-600 text-indigo-600 bg-white') : (isDarkMode ? 'border-transparent text-slate-400 hover:bg-slate-800' : 'border-transparent text-gray-500 hover:bg-gray-100')}`}
                                                >
                                                    <ClipboardCheck size={14}/> Tipos de Conferência
                                                </button>
                                            )}
                                        </div>

                                        {/* CONTEÚDO: CRITÉRIOS DE AVALIAÇÃO */}
                                        {criteriaSubTab === 'criterios' && canViewCriteria && (
                                            <div className="max-h-[600px] overflow-y-auto">
                                                <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                    <thead className={`uppercase text-xs font-bold sticky top-0 z-10 border-b ${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                                        <tr>
                                                            <th className="p-4 w-10 text-center">
                                                                <input type="checkbox" onChange={toggleSelectAllCriteria} checked={getFilteredCriteria().length > 0 && selectedCriteriaIds.length >= getFilteredCriteria().length} />
                                                            </th>
                                                            <th className="p-4">Critério</th><th className="p-4 text-center">Tipo</th><th className="p-4 text-center">Dimensão</th><th className="p-4 text-center">Público</th><th className="p-4 text-center">Peso</th><th className="p-4 text-center">Ações</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                        {getFilteredCriteria().map((item, i) => {
                                                            const isQuali = item.dim !== undefined && item.dim !== null;
                                                            const isSelected = selectedCriteriaIds.includes(item.id);
                                                            const rowClass = i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');
                                                            const activeClass = isSelected ? (isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50') : rowClass;
                                                            
                                                            return (
                                                                <tr key={item.id} className={`${activeClass} ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-blue-50'} transition-colors border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                                                    <td className="p-4 text-center"><input type="checkbox" checked={isSelected} onChange={() => toggleSelectCriteria(item.id)} /></td>
                                                                    <td className={`p-4 font-bold align-middle ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{item.name}</td>
                                                                    <td className="p-4 text-center align-middle">
                                                                        <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase border ${isQuali ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 border-blue-800' : 'bg-blue-50 text-blue-700 border-blue-100') : (isDarkMode ? 'bg-orange-900/50 text-orange-300 border-orange-800' : 'bg-orange-50 text-orange-700 border-orange-100')}`}>{isQuali ? 'Qualitativa' : 'Quantitativa'}</span>
                                                                    </td>
                                                                    <td className={`p-4 text-center align-middle ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{item.dim || '-'}</td>
                                                                    <td className="p-4 text-center align-middle">
                                                                        <span className={`px-2 py-0.5 rounded text-xs font-bold border ${isDarkMode ? 'bg-slate-700 text-slate-300 border-slate-600' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>{item.personType || 'Colaborador'}</span>
                                                                    </td>
                                                                    <td className={`p-4 text-center font-bold align-middle ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>{isQuali ? '-' : (item.peso !== undefined ? item.peso : '-')}</td>
                                                                    <td className="p-4 text-center align-middle">
                                                                        <div className="flex justify-center gap-2">
                                                                            <button onClick={() => handleEdit(item)} className={`p-1.5 rounded transition ${isDarkMode ? 'text-blue-400 hover:text-blue-300 hover:bg-slate-600' : 'text-blue-500 hover:text-blue-700 hover:bg-blue-50'}`}><Pencil size={16} /></button>
                                                                            <button onClick={() => actions.genericDelete('criteria', item.id)} className={`p-1.5 rounded transition ${isDarkMode ? 'text-red-400 hover:text-red-300 hover:bg-slate-600' : 'text-red-500 hover:text-red-700 hover:bg-red-50'}`}><Trash2 size={16} /></button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                        {getFilteredCriteria().length === 0 && (<tr><td colSpan="7" className={`p-8 text-center ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Nenhum critério encontrado.</td></tr>)}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        {/* CONTEÚDO: TIPOS DE CONFERÊNCIA */}
                                        {criteriaSubTab === 'conferencia' && canViewConference && (
                                            <div className="p-4 space-y-4">
                                                <div className={`flex justify-between items-center p-3 border-b sticky top-0 z-10 rounded-lg ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                                                    <div className="relative w-64">
                                                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} size={14} />
                                                        <Input className={`w-full pl-9 h-9 text-xs ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white'}`} placeholder="Buscar conferência..." value={search} onChange={e=>setSearch(e.target.value)}/>
                                                    </div>
                                                    <Button onClick={() => { setForm({ departmentId: '', personType: 'Colaborador', startDate: '', endDate: '' }); setIsCadModalOpen(true); }} icon={Plus} className="h-9 text-xs">Novo Tipo</Button>
                                                </div>
                                                <div className="max-h-[600px] overflow-y-auto">
                                                    <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                        <thead className={`uppercase text-xs font-bold sticky top-0 z-10 border-b ${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                                            <tr><th className="p-4">Descrição</th><th className="p-4">Departamento</th><th className="p-4 text-center">Tipo</th><th className="p-4 text-center">Vigência</th><th className="p-4 text-center">Ações</th></tr>
                                                        </thead>
                                                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                            {filterList(data.conferenciaTypes || []).map((conf, i) => (
                                                                <tr key={conf.id} className={`${i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50')} ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-blue-50'} transition-colors border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                                                    <td className={`p-4 font-bold align-middle ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{conf.name}</td>
                                                                    <td className={`p-4 align-middle ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{conf.departmentId === 'all' ? 'Todos' : data.departments.find(d=>d.id===conf.departmentId)?.name || '-'}</td>
                                                                    <td className="p-4 text-center align-middle">
                                                                        <span className={`px-2 py-0.5 rounded text-xs font-bold border ${isDarkMode ? 'bg-indigo-900/50 text-indigo-300 border-indigo-800' : 'bg-indigo-50 text-indigo-700 border-indigo-100'}`}>{conf.personType}</span>
                                                                    </td>
                                                                    <td className={`p-4 text-center align-middle text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>{formatMonthYear(conf.startDate)} {conf.endDate ? `- ${formatMonthYear(conf.endDate)}` : ''}</td>
                                                                    <td className="p-4 text-center align-middle">
                                                                        <div className="flex justify-center gap-2">
                                                                            <button onClick={() => { setForm(conf); setIsCadModalOpen(true); }} className={`p-1.5 rounded transition ${isDarkMode ? 'text-blue-400 hover:text-blue-300 hover:bg-slate-600' : 'text-blue-500 hover:text-blue-700 hover:bg-blue-50'}`} title="Editar"><Pencil size={16} /></button>
                                                                            <button onClick={() => actions.genericDelete('conferencia', conf.id)} className={`p-1.5 rounded transition ${isDarkMode ? 'text-red-400 hover:text-red-300 hover:bg-slate-600' : 'text-red-500 hover:text-red-700 hover:bg-red-50'}`} title="Excluir"><Trash2 size={16} /></button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                            {(!data.conferenciaTypes || data.conferenciaTypes.length === 0) && (<tr><td colSpan="5" className={`p-8 text-center ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Nenhum tipo de conferência encontrado.</td></tr>)}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })() ) : tab === 'manager' ? (
                            <Card className="p-0 overflow-hidden shadow-md" darkMode={isDarkMode}>
                                <div className="max-h-[600px] overflow-y-auto">
                                    <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                        <thead className={`uppercase text-xs font-bold sticky top-0 z-10 border-b ${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                            <tr>
                                                <th className="p-4">Nome do Gestor</th>
                                                <th className="p-4">Email Corporativo</th>
                                                <th className="p-4">Departamento Principal</th>
                                                <th className="p-4">Rateio de Custos</th>
                                                <th className="p-4 text-center">Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                            {filterList(data.managers)
                                                .filter(m => {
                                                    if (currentUser.role === 'SuperAdm') return true;
                                                    const isMainDept = m.departmentId === currentUser.departmentId;
                                                    const isSharedDept = m.deptShare?.some(ds => ds.deptId === currentUser.departmentId);
                                                    return isMainDept || isSharedDept;
                                                })
                                                .map((m, i) => {
                                                    const rowClass = i % 2 === 0 
                                                        ? (isDarkMode ? 'bg-slate-800' : 'bg-white') 
                                                        : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');
                                                    
                                                    const hoverClass = isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-blue-50';

                                                    return (
                                                        <tr key={m.id} className={`${rowClass} ${hoverClass} transition-colors border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                                            <td className={`p-4 font-bold align-middle ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                                                {m.name}
                                                            </td>
                                                            <td className={`p-4 align-middle ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                                                                {m.email}
                                                            </td>
                                                            <td className={`p-4 align-middle ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                                                                {data.departments.find(d => d.id === m.departmentId)?.name || '-'}
                                                            </td>
                                                            <td className="p-4 align-middle">
                                                                {m.deptShare && m.deptShare.length > 0 ? (
                                                                    <div className="flex flex-col gap-1">
                                                                        {m.deptShare.map((share, idx) => {
                                                                            const deptName = data.departments.find(d => d.id === share.deptId)?.name || 'N/D';
                                                                            return (
                                                                                <div key={idx} className={`text-xs px-2 py-1 rounded border flex justify-between items-center w-40 ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-200'}`}>
                                                                                    <span className={`font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>{deptName}</span>
                                                                                    <span className="font-bold text-orange-500">{share.share}%</span>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-gray-400 text-xs italic">Sem rateio (100% Depto Principal)</span>
                                                                )}
                                                            </td>
                                                            <td className="p-4 text-center align-middle">
                                                                <div className="flex justify-center gap-2">
                                                                    <button onClick={() => handleEdit(m)} className={`p-1.5 rounded transition ${isDarkMode ? 'text-blue-400 hover:text-blue-300 hover:bg-slate-600' : 'text-blue-500 hover:text-blue-700 hover:bg-blue-100'}`} title="Editar">
                                                                        <Pencil size={16} />
                                                                    </button>
                                                                    <button onClick={() => actions.genericDelete('manager', m.id)} className={`p-1.5 rounded transition ${isDarkMode ? 'text-red-400 hover:text-red-300 hover:bg-slate-600' : 'text-red-500 hover:text-red-700 hover:bg-red-100'}`} title="Excluir">
                                                                        <Trash2 size={16} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            {filterList(data.managers).length === 0 && (
                                                <tr><td colSpan="5" className={`p-8 text-center ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Nenhum gestor encontrado.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        ) : tab === 'ponto' ? (
                            <div className="flex flex-col h-full min-h-[500px]">
                            {/* NAVEGAÇÃO INTERNA DO PONTO */}
                                <div className={`flex border-b shrink-0 mb-4 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                    {hasPerm('access_sub_ponto_rules') && (
                                        <button onClick={() => setPontoTab('config')} className={`px-6 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${pontoTab === 'config' ? (isDarkMode ? 'border-blue-500 text-blue-400 bg-slate-800' : 'border-blue-600 text-blue-600 bg-white') : (isDarkMode ? 'border-transparent text-slate-400 hover:bg-slate-800' : 'border-transparent text-gray-500 hover:bg-gray-100')}`}>
                                            <Briefcase size={14}/> Regras Gerais
                                        </button>
                                    )}
                                    {hasPerm('access_sub_ponto_sits') && (
                                        <button onClick={() => setPontoTab('situations')} className={`px-6 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${pontoTab === 'situations' ? (isDarkMode ? 'border-indigo-500 text-indigo-400 bg-slate-800' : 'border-indigo-600 text-indigo-600 bg-white') : (isDarkMode ? 'border-transparent text-slate-400 hover:bg-slate-800' : 'border-transparent text-gray-500 hover:bg-gray-100')}`}>
                                            <FileUp size={14}/> Situações (Justificativas)
                                        </button>
                                    )}
                                    {hasPerm('access_sub_ponto_settings') && (
                                        <button onClick={() => setPontoTab('notificacoes')} className={`px-6 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${pontoTab === 'notificacoes' ? (isDarkMode ? 'border-orange-500 text-orange-400 bg-slate-800' : 'border-orange-600 text-orange-600 bg-white') : (isDarkMode ? 'border-transparent text-slate-400 hover:bg-slate-800' : 'border-transparent text-gray-500 hover:bg-gray-100')}`}>
                                            <Settings size={14}/> Configurações
                                        </button>
                                    )}
                                </div>

                                {/* NOVA CONTEÚDO: CONFIGURAÇÕES DE APROVAÇÃO/NOTIFICAÇÃO */}
                                {pontoTab === 'notificacoes' && (
                                    <div className="space-y-4 animate-fade-in">
                                        <div className={`flex justify-between items-center p-3 border-b sticky top-0 z-10 rounded-lg ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                                            <h3 className={`font-bold text-sm ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>Notificações e Aprovações de Ponto</h3>
                                            <Button onClick={() => { actions.saveConfig(pontoSettings); alert("Configurações salvas!"); }} icon={Save} className="h-8 text-xs bg-orange-600 hover:bg-orange-700 border-0">Salvar Alterações</Button>
                                        </div>
                                        
                                        {/* ========================================== */}
                                        {/* NOVO MOTOR DE EVENTOS E GATILHOS (V3)      */}
                                        {/* ========================================== */}
                                        <Card darkMode={isDarkMode} className="p-0 overflow-hidden shadow-md border-0">
                                            {(() => {
                                                const builtInRules = [
                                                    { id: 'delay10', label: 'Atrasos/Adiantos (Até 10 min)', hasLimit: false },
                                                    { id: 'delayX', label: 'Atrasos/Adiantos (Acima limite)', hasLimit: true },
                                                    { id: 'manualAll', label: 'Marcações Manuais (Todas)', hasLimit: false },
                                                    { id: 'manualX', label: 'Marcações Manuais (Acima limite)', hasLimit: true }
                                                ];
                                                
                                                // Puxa as justificativas dinamicamente do cadastro de Situações
                                                const dynamicRules = (data.pontoSituations || []).map(sit => ({
                                                    id: `sit_${sit.id}`,
                                                    label: sit.name,
                                                    hasLimit: false
                                                }));

                                                const handlePontoRuleChange = (ruleId, field, value) => {
                                                    setPontoSettings(prev => ({
                                                        ...prev,
                                                        rules: {
                                                            ...prev.rules,
                                                            [ruleId]: {
                                                                ...(prev.rules[ruleId] || {}),
                                                                [field]: value
                                                            }
                                                        }
                                                    }));
                                                };

                                                // Componente interno para gerar o grupo de 4 checkboxes repetidos
                                                const CheckboxGroup = ({ ruleId, prefix, ruleData, colorClass }) => (
                                                    <div className="flex items-center justify-center gap-3">
                                                        {['Gestor', 'RH', 'DIR', 'FNC'].map(role => (
                                                            <label key={role} className="flex items-center gap-1 cursor-pointer text-[10px] font-bold">
                                                                <input 
                                                                    type="checkbox" 
                                                                    className={`w-3.5 h-3.5 rounded focus:ring-2 cursor-pointer ${colorClass}`}
                                                                    checked={ruleData[`${prefix}${role}`] || false}
                                                                    onChange={e => handlePontoRuleChange(ruleId, `${prefix}${role}`, e.target.checked)}
                                                                />
                                                                {role}
                                                            </label>
                                                        ))}
                                                    </div>
                                                );

                                                // Componente interno para desenhar as 2 linhas (Colab/Gestão) de um evento
                                                const renderEventRows = (ruleConf) => {
                                                    const r = pontoSettings.rules?.[ruleConf.id] || {};
                                                    
                                                    // Fundo e borda base para destacar a separação de cada bloco de evento
                                                    const leftColsBg = isDarkMode ? 'bg-slate-800' : 'bg-white';
                                                    const topBorderStyle = isDarkMode ? 'border-t-2 border-slate-600' : 'border-t-2 border-gray-300';
                                                    const innerBorder = isDarkMode ? 'border-b border-slate-700/50' : 'border-b border-gray-100';

                                                    // Cores específicas de fundo para as linhas (Azul = Colaborador / Roxo = Gestão)
                                                    const colabBg = isDarkMode ? 'bg-blue-900/10' : 'bg-blue-50/50';
                                                    const gestaoBg = isDarkMode ? 'bg-purple-900/10' : 'bg-purple-50/50';

                                                    return (
                                                        <React.Fragment key={ruleConf.id}>
                                                            {/* LINHA 1: COLABORADOR */}
                                                            <tr className={topBorderStyle}>
                                                                {/* Células mescladas (Esquerda - Nome e Limite) */}
                                                                <td rowSpan={2} className={`p-3 font-bold border-r dark:border-slate-700 align-middle ${leftColsBg}`}>
                                                                    {ruleConf.label}
                                                                </td>
                                                                <td rowSpan={2} className={`p-3 text-center border-r dark:border-slate-700 align-middle ${leftColsBg}`}>
                                                                    {ruleConf.hasLimit ? (
                                                                        <div className="flex items-center justify-center gap-1">
                                                                            <input 
                                                                                type="number" 
                                                                                className={`w-14 p-1.5 border rounded text-xs text-center font-bold outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} 
                                                                                placeholder="Min." 
                                                                                value={r.limit||''} 
                                                                                onChange={e=>handlePontoRuleChange(ruleConf.id, 'limit', parseInt(e.target.value)||0)} 
                                                                            />
                                                                            <span className="text-[9px] opacity-70">m</span>
                                                                        </div>
                                                                    ) : <span className="text-gray-400">-</span>}
                                                                </td>
                                                                
                                                                {/* Células da Direita (Colaborador) */}
                                                                <td className={`p-2 text-center text-[11px] font-black uppercase tracking-wider border-r dark:border-slate-700 ${innerBorder} ${colabBg}`}>
                                                                    <span className={isDarkMode ? 'text-blue-400' : 'text-blue-700'}>Colaborador</span>
                                                                </td>
                                                                <td className={`p-2 border-r dark:border-slate-700 ${innerBorder} ${colabBg}`}>
                                                                    <CheckboxGroup ruleId={ruleConf.id} prefix="colNot_" ruleData={r} colorClass="text-blue-600 focus:ring-blue-500" />
                                                                </td>
                                                                <td className={`p-2 ${innerBorder} ${colabBg}`}>
                                                                    <CheckboxGroup ruleId={ruleConf.id} prefix="colApp_" ruleData={r} colorClass="text-orange-500 focus:ring-orange-500" />
                                                                </td>
                                                            </tr>

                                                            {/* LINHA 2: GESTÃO */}
                                                            <tr>
                                                                {/* Células da Direita (Gestão) */}
                                                                <td className={`p-2 text-center text-[11px] font-black uppercase tracking-wider border-r dark:border-slate-700 ${gestaoBg}`}>
                                                                    <span className={isDarkMode ? 'text-purple-400' : 'text-purple-700'}>Gestão</span>
                                                                </td>
                                                                <td className={`p-2 border-r dark:border-slate-700 ${gestaoBg}`}>
                                                                    {/* Checkboxes de notificação da gestão agora em tom Roxo para combinar */}
                                                                    <CheckboxGroup ruleId={ruleConf.id} prefix="gesNot_" ruleData={r} colorClass="text-purple-600 focus:ring-purple-500" />
                                                                </td>
                                                                <td className={`p-2 ${gestaoBg}`}>
                                                                    <CheckboxGroup ruleId={ruleConf.id} prefix="gesApp_" ruleData={r} colorClass="text-orange-500 focus:ring-orange-500" />
                                                                </td>
                                                            </tr>
                                                        </React.Fragment>
                                                    );
                                                };

                                                return (
                                                    <div className="overflow-x-auto custom-scrollbar">
                                                        <table className={`w-full text-xs text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                            <thead className={`text-[10px] uppercase font-bold sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>
                                                                <tr>
                                                                    <th className="p-3 w-1/4 border-b dark:border-slate-700">Descrição do Evento</th>
                                                                    <th className="p-3 text-center border-b dark:border-slate-700 w-24">Limite</th>
                                                                    <th className="p-3 text-center border-b dark:border-slate-700 w-28">Alvo</th>
                                                                    <th className="p-3 text-center border-b dark:border-slate-700">Notificação (Apenas Cópia)</th>
                                                                    <th className="p-3 text-center border-b dark:border-slate-700 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400">Aprovação (Responsável)</th>
                                                                </tr>
                                                            </thead>
                                                            
                                                            <tbody>
                                                                {/* GRUPO 1: EVENTOS FIXOS */}
                                                                <tr className={`${isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50/50'}`}>
                                                                    <td colSpan="5" className={`p-2 px-3 font-bold text-[10px] uppercase tracking-wider border-b dark:border-slate-700 ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                                                                        Atrasos, Adiantos e Marcações Manuais
                                                                    </td>
                                                                </tr>
                                                                {builtInRules.map(f => renderEventRows(f))}

                                                                {/* GRUPO 2: SITUAÇÕES CADASTRAIS (DINÂMICAS) */}
                                                                <tr className={`${isDarkMode ? 'bg-purple-900/20' : 'bg-purple-50/50'}`}>
                                                                    <td colSpan="5" className={`p-2 px-3 font-bold text-[10px] uppercase tracking-wider border-y dark:border-slate-700 ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                                                                        Situações e Justificativas Cadastradas
                                                                    </td>
                                                                </tr>
                                                                {dynamicRules.length === 0 && (
                                                                    <tr><td colSpan="5" className="p-6 text-center text-gray-400 italic text-xs">Nenhuma situação/justificativa cadastrada no sistema.</td></tr>
                                                                )}
                                                                {dynamicRules.map(f => renderEventRows(f))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                );
                                            })()}
                                        </Card>
                                    </div>
                                )}

                                {/* CONTEÚDO: REGRAS GERAIS */}
                                {pontoTab === 'config' && (
                                    <div className="space-y-4">
                                        <div className={`flex justify-between items-center p-3 border-b sticky top-0 z-10 rounded-lg ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                                            <h3 className={`font-bold text-sm ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>Regras de Jornada</h3>
                                            <Button onClick={() => { setForm({ id: null, entryTime: '08:00', lunchStart: '12:00', lunchEnd: '13:00', exitTime: '17:30', coffeeBreak: '15:15 às 15:30', workHours: '08:00', tolerance: 10, deadlineDays: 30, pdfTemplate: 'BASE: {entrada} ÀS {saida} | ALMOÇO: {inicio_almoco} ÀS {fim_almoco} | CAFÉ: {cafe} | TOL: {tolerancia}m' }); setIsRuleModalOpen(true); }} icon={Plus} className="h-8 text-xs">Nova Regra</Button>
                                        </div>
                                    
                                        <div className="max-h-[500px] overflow-y-auto">
                                            <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                <thead className={`uppercase text-xs font-bold ${isDarkMode ? 'bg-slate-900' : 'bg-gray-100'}`}>
                                                    <tr><th className="p-3">Nome</th><th className="p-3 text-center">Jornada</th><th className="p-3 text-center">Vigência</th><th className="p-3 text-center">Ações</th></tr>
                                                </thead>
                                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                    {(Array.isArray(data.pontoConfig) ? data.pontoConfig : []).map((rule, idx) => (
                                                        <tr key={rule.id || idx} className={`${idx % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50')} hover:opacity-90`}>
                                                            <td className="p-3 font-bold">{rule.name}</td>
                                                            <td className="p-3 text-center">{rule.entryTime} - {rule.exitTime} ({rule.workHours})</td>
                                                            <td className="p-3 text-center text-xs">{new Date(rule.validFrom).toLocaleDateString('pt-BR')} {rule.validTo ? `a ${new Date(rule.validTo).toLocaleDateString('pt-BR')}` : '(Atual)'}</td>
                                                            <td className="p-3 text-center">
                                                                <div className="flex justify-center gap-2">
                                                                    <button onClick={() => { setForm(rule); setIsRuleModalOpen(true); }} className="text-blue-500 p-1"><Edit3 size={16}/></button>
                                                                    <button onClick={() => { if(window.confirm('Excluir regra?')) actions.genericDelete('pontoConfig', rule.id); }} className="text-red-500 p-1"><Trash2 size={16}/></button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        
                                        {/* MODAL REGRA NO CADASTROS SYSTEM (TELA REAL, LAYOUT FLEXBOX FORÇADO) */}
                                        {isRuleModalOpen && (
                                            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
                                                {/* Container largo (max-w-5xl) para caber todos os itens lado a lado perfeitamente */}
                                                <div className={`p-5 rounded-xl w-full max-w-5xl shadow-2xl flex flex-col max-h-[95vh] ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`}>
                                                    <h3 className="font-bold text-lg mb-3 flex items-center gap-2 shrink-0"><Settings size={18}/> {form.id ? 'Editar Regra' : 'Nova Regra'}</h3>
                                                    
                                                    <div className="overflow-y-auto overflow-x-hidden custom-scrollbar flex-1 pr-2 space-y-4">
                                                        
                                                        {/* LINHA 1: Nome e Validades (Garante a mesma linha com flex) */}
                                                        <div className="flex gap-3 w-full">
                                                            <div className="flex-[2]">
                                                                <PontoInput label="Nome da Regra" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} darkMode={isDarkMode} placeholder="Ex: Jornada Padrão 2025" />
                                                            </div>
                                                            <div className="flex-1">
                                                                <label className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Início</label>
                                                                <input type="date" value={form.validFrom || ''} onChange={e=>setForm({...form, validFrom: e.target.value})} className={`w-full p-2.5 rounded-lg border text-xs h-[42px] outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white focus:border-blue-500' : 'bg-white border-gray-300 focus:border-blue-500'}`} />
                                                            </div>
                                                            <div className="flex-1">
                                                                <label className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Fim (Opcional)</label>
                                                                <input type="date" value={form.validTo || ''} onChange={e=>setForm({...form, validTo: e.target.value})} className={`w-full p-2.5 rounded-lg border text-xs h-[42px] outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white focus:border-blue-500' : 'bg-white border-gray-300 focus:border-blue-500'}`} />
                                                            </div>
                                                        </div>

                                                        {/* LINHA 2: Horários Base (4 campos na mesma linha) */}
                                                        <div className="flex gap-3 w-full">
                                                            <div className="flex-1"><PontoInput label="Entrada" type="time" value={form.entryTime} onChange={e=>setForm({...form, entryTime: e.target.value})} darkMode={isDarkMode} /></div>
                                                            <div className="flex-1"><PontoInput label="Saída" type="time" value={form.exitTime} onChange={e=>setForm({...form, exitTime: e.target.value})} darkMode={isDarkMode} /></div>
                                                            <div className="flex-1"><PontoInput label="Início Almoço" type="time" value={form.lunchStart} onChange={e=>setForm({...form, lunchStart: e.target.value})} darkMode={isDarkMode} /></div>
                                                            <div className="flex-1"><PontoInput label="Fim Almoço" type="time" value={form.lunchEnd} onChange={e=>setForm({...form, lunchEnd: e.target.value})} darkMode={isDarkMode} /></div>
                                                        </div>

                                                        {/* LINHA 3: Carga, Tolerância e Café na mesma linha */}
                                                        <div className="flex gap-3 w-full">
                                                            <div className="flex-1"><PontoInput label="Carga Diária" type="time" value={form.workHours} onChange={e=>setForm({...form, workHours: e.target.value})} darkMode={isDarkMode} /></div>
                                                            <div className="flex-1"><PontoInput label="Tolerância (min)" type="number" value={form.tolerance} onChange={e=>setForm({...form, tolerance: e.target.value})} darkMode={isDarkMode} /></div>
                                                            <div className="flex-[2]"><PontoInput label="Intervalo do Café (Ex: 15:15 às 15:30)" value={form.coffeeBreak} onChange={e=>setForm({...form, coffeeBreak: e.target.value})} darkMode={isDarkMode} /></div>
                                                        </div>

                                                        {/* LINHA 4: Configuração e Previews do PDF (Lado a Lado) */}
                                                        <div className="flex gap-4 mt-2 w-full">
                                                            
                                                            {/* Lado Esquerdo: Editor do Template */}
                                                            <div className={`flex-1 p-4 border rounded-xl shadow-inner flex flex-col ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                                <label className={`text-[10px] font-bold uppercase mb-1 block ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>Formato do Texto no PDF</label>
                                                                <p className="text-[9px] opacity-70 mb-3">Edite o texto abaixo. Use as tags para inserir os horários automaticamente.</p>
                                                                
                                                                <textarea 
                                                                    value={form.pdfTemplate !== undefined ? form.pdfTemplate : 'BASE: {entrada} ÀS {saida} | ALMOÇO: {inicio_almoco} ÀS {fim_almoco} | CAFÉ: {cafe} | TOL: {tolerancia}m'} 
                                                                    onChange={e => setForm({...form, pdfTemplate: e.target.value})} 
                                                                    className={`w-full p-2 border rounded-lg text-xs mb-3 outline-none resize-none flex-1 focus:ring-1 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                                                                    rows="3"
                                                                />
                                                                
                                                                <div className="flex gap-1.5 text-[9px] font-mono text-gray-500 flex-wrap">
                                                                    <span className="bg-black/5 dark:bg-white/10 px-1.5 py-0.5 rounded">{`{entrada}`}</span>
                                                                    <span className="bg-black/5 dark:bg-white/10 px-1.5 py-0.5 rounded">{`{saida}`}</span>
                                                                    <span className="bg-black/5 dark:bg-white/10 px-1.5 py-0.5 rounded">{`{inicio_almoco}`}</span>
                                                                    <span className="bg-black/5 dark:bg-white/10 px-1.5 py-0.5 rounded">{`{fim_almoco}`}</span>
                                                                    <span className="bg-black/5 dark:bg-white/10 px-1.5 py-0.5 rounded">{`{cafe}`}</span>
                                                                    <span className="bg-black/5 dark:bg-white/10 px-1.5 py-0.5 rounded">{`{tolerancia}`}</span>
                                                                </div>
                                                            </div>

                                                            {/* Lado Direito: Previews Gerados */}
                                                            <div className="flex-1 flex flex-col gap-3 justify-center">
                                                                <div className={`p-3 border rounded-xl shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                                                    <label className={`text-[9px] font-bold uppercase mb-1 block ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Visualização Padrão</label>
                                                                    <div className={`text-xs font-mono font-bold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                                        BASE: {form.entryTime || '00:00'} ÀS {form.exitTime || '00:00'} | ALMOÇO: {form.lunchStart || '12:00'} ÀS {form.lunchEnd || '13:00'} | CAFÉ: {form.coffeeBreak || '-'} | TOL: {form.tolerance || 0}m
                                                                    </div>
                                                                </div>

                                                                <div className={`p-3 border rounded-xl shadow-sm flex-1 flex flex-col justify-center ${isDarkMode ? 'bg-emerald-900/20 border-emerald-800/50' : 'bg-emerald-50 border-emerald-200'}`}>
                                                                    <label className={`text-[9px] font-bold uppercase mb-1 block ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>Preview Dinâmico (Como vai aparecer no PDF)</label>
                                                                    <div className={`text-xs font-mono font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                                                                        {(() => {
                                                                            const template = form.pdfTemplate !== undefined ? form.pdfTemplate : 'BASE: {entrada} ÀS {saida} | ALMOÇO: {inicio_almoco} ÀS {fim_almoco} | CAFÉ: {cafe} | TOL: {tolerancia}m';
                                                                            return template
                                                                                .replace(/\{entrada\}/g, form.entryTime || '00:00')
                                                                                .replace(/\{saida\}/g, form.exitTime || '00:00')
                                                                                .replace(/\{inicio_almoco\}/g, form.lunchStart || '12:00')
                                                                                .replace(/\{fim_almoco\}/g, form.lunchEnd || '13:00')
                                                                                .replace(/\{cafe\}/g, form.coffeeBreak || '-')
                                                                                .replace(/\{tolerancia\}/g, form.tolerance || 0);
                                                                        })()}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                        </div>
                                                    </div>

                                                    {/* RODAPÉ DO MODAL (Fixo na parte inferior) */}
                                                    <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-slate-700 shrink-0">
                                                        <Button variant="secondary" onClick={() => setIsRuleModalOpen(false)} className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}>Cancelar</Button>
                                                        <Button onClick={() => {
                                                            const currentTemplate = form.pdfTemplate !== undefined ? form.pdfTemplate : 'BASE: {entrada} ÀS {saida} | ALMOÇO: {inicio_almoco} ÀS {fim_almoco} | CAFÉ: {cafe} | TOL: {tolerancia}m';
                                                            const pdfString = currentTemplate
                                                                .replace(/\{entrada\}/g, form.entryTime || '00:00')
                                                                .replace(/\{saida\}/g, form.exitTime || '00:00')
                                                                .replace(/\{inicio_almoco\}/g, form.lunchStart || '12:00')
                                                                .replace(/\{fim_almoco\}/g, form.lunchEnd || '13:00')
                                                                .replace(/\{cafe\}/g, form.coffeeBreak || '-')
                                                                .replace(/\{tolerancia\}/g, form.tolerance || 0);

                                                            const itemToSave = { ...form, id: form.id || Date.now().toString(), pdfTemplate: currentTemplate, pdfPrintString: pdfString };
                                                            
                                                            if (actions.saveItem) {
                                                                actions.saveItem('pontoConfig', itemToSave);
                                                            } else if (actions.savePontoConfig) {
                                                                actions.savePontoConfig(itemToSave);
                                                            }
                                                            setIsRuleModalOpen(false);
                                                        }}>Salvar Regra</Button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* CONTEÚDO: SITUAÇÕES */}
                                {pontoTab === 'situations' && (
                                    <div className="space-y-4 animate-fade-in">
                                        <div className={`flex justify-between items-center p-3 border-b sticky top-0 z-10 rounded-lg ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                                            <h3 className={`font-bold text-sm ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>Tipos de Justificativa</h3>
                                            <Button onClick={() => { setForm({ id: null, name: '', type: 'Abono', color: '#10b981', departmentId: 'all', personType: 'Todos', requireAttachment: false }); setIsSitModalOpen(true); }} icon={Plus} className="h-8 text-xs">Nova Situação</Button>
                                        </div>
                                        <div className="max-h-[500px] overflow-y-auto custom-scrollbar">
                                            <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                <thead className={`text-xs uppercase font-bold sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900' : 'bg-gray-100'}`}>
                                                    <tr>
                                                        <th className="p-3 w-10 border-b dark:border-slate-700">Cor</th>
                                                        <th className="p-3 border-b dark:border-slate-700">Nome da Situação</th>
                                                        <th className="p-3 w-32 text-center border-b dark:border-slate-700">Tipo Base</th>
                                                        <th className="p-3 w-24 text-center border-b dark:border-slate-700">Ações</th>
                                                    </tr>
                                                </thead>
                                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                    {(data.pontoSituations || []).map((s, idx) => (
                                                        <tr key={s.id || idx} className={`${idx % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50')} hover:opacity-90`}>
                                                            <td className="p-3 text-center align-middle">
                                                                <div className="w-4 h-4 rounded-full shadow-sm mx-auto" style={{ backgroundColor: s.color || '#ccc' }}></div>
                                                            </td>
                                                            <td className="p-3 font-bold align-middle">
                                                                <div className="flex items-center gap-2">
                                                                    <span>{s.name}</span>
                                                                    {s.requireAttachment && <span className={`text-[9px] px-1.5 py-0.5 rounded border flex items-center gap-1 shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-300' : 'bg-gray-100 border-gray-300 text-gray-600'}`} title="Exige Anexo"><Paperclip size={10}/> Anexo Obrig.</span>}
                                                                </div>
                                                                <div className="flex items-center gap-2 text-[9px] opacity-60 font-normal mt-0.5 uppercase tracking-wider">
                                                                    <span>{s.personType || 'Todos'}</span> • <span>{s.departmentId === 'all' || !s.departmentId ? 'Geral (Todos Deptos)' : data.departments.find(d=>d.id===s.departmentId)?.name || 'Depto Excluído'}</span>
                                                                </div>
                                                            </td>
                                                            <td className="p-3 text-center align-middle">
                                                                <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold border ${s.type === 'Abono' ? (isDarkMode ? 'border-green-800 text-green-400 bg-green-900/30' : 'border-green-200 text-green-700 bg-green-50') : (s.type === 'Desconto' ? (isDarkMode ? 'border-red-800 text-red-400 bg-red-900/30' : 'border-red-200 text-red-700 bg-red-50') : (isDarkMode ? 'border-yellow-800 text-yellow-400 bg-yellow-900/30' : 'border-yellow-300 text-yellow-700 bg-yellow-50'))}`}>
                                                                    {s.type}
                                                                </span>
                                                            </td>
                                                            <td className="p-3 text-center align-middle">
                                                                <div className="flex justify-center gap-2">
                                                                    <button onClick={() => { setForm(s); setIsSitModalOpen(true); }} className="text-blue-500 p-1.5 hover:bg-blue-100 rounded transition-colors"><Edit3 size={16}/></button>
                                                                    <button onClick={() => { if(window.confirm('Deseja excluir esta situação? Ela não aparecerá mais para os colaboradores.')) actions.savePontoSituations(data.pontoSituations.filter(x => x.id !== s.id)); }} className="text-red-500 p-1.5 hover:bg-red-100 rounded transition-colors"><Trash2 size={16}/></button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {(data.pontoSituations || []).length === 0 && <tr><td colSpan="4" className="p-8 text-center text-gray-400 italic">Nenhuma situação cadastrada.</td></tr>}
                                                </tbody>
                                            </table>
                                        </div>
                                        {/* MODAL SITUAÇÃO */}
                                        {isSitModalOpen && (
                                            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
                                                <div className={`p-6 rounded-xl w-full max-w-md shadow-2xl flex flex-col max-h-[95vh] ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`}>
                                                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2 shrink-0"><Settings size={18}/> {form.id ? 'Editar Situação' : 'Nova Situação'}</h3>
                                                    
                                                    <div className="space-y-4 flex-1 overflow-y-auto pr-2 custom-scrollbar">
                                                        <PontoInput label="Nome da Situação / Motivo" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} darkMode={isDarkMode} placeholder="Ex: Atestado Médico" />
                                                        
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <div>
                                                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Tipo Base</label>
                                                                <select className={`w-full p-2.5 rounded-lg border text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={form.type} onChange={e=>setForm({...form, type: e.target.value})}>
                                                                    <option value="Abono">Abono</option>
                                                                    <option value="Desconto">Desconto</option>
                                                                    <option value="Neutro">Neutro (Observação)</option>
                                                                </select>
                                                            </div>
                                                            <div>
                                                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Público Alvo</label>
                                                                <select className={`w-full p-2.5 rounded-lg border text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={form.personType || 'Todos'} onChange={e=>setForm({...form, personType: e.target.value})}>
                                                                    <option value="Todos">Todos</option>
                                                                    <option value="Colaborador">Apenas Colaborador</option>
                                                                    <option value="Gestão">Apenas Gestão</option>
                                                                </select>
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Departamento Restrito</label>
                                                            <select className={`w-full p-2.5 rounded-lg border text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={form.departmentId || 'all'} onChange={e=>setForm({...form, departmentId: e.target.value})}>
                                                                <option value="all">Geral (Todos os Departamentos)</option>
                                                                {data.departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                                            </select>
                                                        </div>

                                                        <div className="grid grid-cols-2 gap-3 items-end">
                                                            <div>
                                                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Cor da Tag</label>
                                                                <div className="flex gap-2">
                                                                    <input type="color" className="h-10 w-12 cursor-pointer rounded border-0 p-0" value={form.color || '#10b981'} onChange={e=>setForm({...form, color: e.target.value})} />
                                                                    <input type="text" className={`flex-1 p-2.5 rounded-lg border text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={form.color || '#10b981'} onChange={e=>setForm({...form, color: e.target.value})} />
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer font-bold text-xs transition-colors shadow-sm ${form.requireAttachment ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300' : 'bg-gray-50 border-gray-200 text-gray-600 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                                                    <input type="checkbox" className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer" checked={form.requireAttachment || false} onChange={e=>setForm({...form, requireAttachment: e.target.checked})} />
                                                                    Exige Anexo?
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-200 dark:border-slate-700 shrink-0">
                                                        <Button variant="secondary" onClick={() => setIsSitModalOpen(false)}>Cancelar</Button>
                                                        <Button onClick={() => {
                                                            if (!form.name) return alert("O Nome é obrigatório.");
                                                            
                                                            let safeColor = form.color;
                                                            if (!safeColor) {
                                                                const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6'];
                                                                safeColor = colors[Math.floor(Math.random() * colors.length)];
                                                            }
                                                            
                                                            const itemToSave = {
                                                                ...form,
                                                                color: safeColor,
                                                                id: form.id || Date.now().toString(),
                                                                departmentId: form.departmentId || 'all',
                                                                personType: form.personType || 'Todos',
                                                                requireAttachment: form.requireAttachment || false
                                                            };

                                                            // Fallback seguro
                                                            if (actions.saveItem) {
                                                                actions.saveItem('pontoSituations', itemToSave);
                                                            } else if (actions.savePontoSituations) {
                                                                actions.savePontoSituations(itemToSave);
                                                            }
                                                            setIsSitModalOpen(false);
                                                        }}>Salvar Situação</Button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : tab === 'dept' ? (
                            <Card className="p-0 overflow-hidden shadow-md" darkMode={isDarkMode}>
                                <div className="max-h-[600px] overflow-y-auto">
                                    <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                        <thead className={`uppercase text-xs font-bold sticky top-0 z-10 border-b ${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                            <tr>
                                                <th className="p-4">Departamento</th>
                                                <th className="p-4">Email</th>
                                                <th className="p-4">Gestor(es) Vinculado(s)</th>
                                                <th className="p-4">Rateio</th>
                                                <th className="p-4 text-center">Ações</th>
                                            </tr>
                                        </thead>

                                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                            {filterList(data.departments).map((d, i) => {
                                                const rowClass = i % 2 === 0
                                                    ? (isDarkMode ? 'bg-slate-800' : 'bg-white')
                                                    : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');

                                                const hoverClass = isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-blue-50';

                                                const linkedManagers = (data.managers || []).filter(m =>
                                                    m.departmentId === d.id ||
                                                    (m.deptShare || []).some(ds => ds.deptId === d.id)
                                                );

                                                return (
                                                    <tr
                                                        key={d.id}
                                                        className={`${rowClass} ${hoverClass} transition-colors border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}
                                                    >
                                                        <td className={`p-4 font-bold ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                                            {d.name}
                                                        </td>

                                                        <td className="p-4">
                                                            {d.email ? (
                                                                <span className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>
                                                                    {d.email}
                                                                </span>
                                                            ) : (
                                                                <span className="text-gray-400 italic">Não informado</span>
                                                            )}
                                                        </td>

                                                        <td className="p-4">
                                                            {linkedManagers.length > 0 ? (
                                                                <div className="flex flex-wrap gap-2">
                                                                    {linkedManagers.map(m => (
                                                                        <span
                                                                            key={m.id}
                                                                            className={`px-2 py-1 rounded text-xs font-bold border ${
                                                                                isDarkMode
                                                                                    ? 'bg-blue-900/40 text-blue-300 border-blue-800'
                                                                                    : 'bg-blue-50 text-blue-700 border-blue-200'
                                                                            }`}
                                                                        >
                                                                            {m.name}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-400 italic">Sem gestor vinculado</span>
                                                            )}
                                                        </td>

                                                        <td className="p-4">
                                                            {linkedManagers.length > 0 ? (
                                                                <div className="flex flex-col gap-1">
                                                                    {linkedManagers.map(m => {
                                                                        const shareItem = (m.deptShare || []).find(ds => ds.deptId === d.id);

                                                                        let percentual = null;
                                                                        let origem = '';

                                                                        if (shareItem) {
                                                                            percentual = shareItem.share;
                                                                            origem = 'rateio';
                                                                        } else if (m.departmentId === d.id) {
                                                                            percentual = 100;
                                                                            origem = 'principal';
                                                                        }

                                                                        return (
                                                                            <div
                                                                                key={`${d.id}_${m.id}`}
                                                                                className={`text-xs px-2 py-1 rounded border ${
                                                                                    isDarkMode
                                                                                        ? 'bg-slate-900 border-slate-700 text-slate-300'
                                                                                        : 'bg-white border-gray-200 text-gray-700'
                                                                                }`}
                                                                            >
                                                                                <span className="font-bold">{m.name}</span>
                                                                                {' - '}
                                                                                <span className="text-orange-500 font-bold">{percentual}%</span>
                                                                                {origem === 'principal' && (
                                                                                    <span className="opacity-70"> (principal)</span>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-400 italic">Sem rateio</span>
                                                            )}
                                                        </td>

                                                        <td className="p-4 text-center align-middle">
                                                            <div className="flex justify-center gap-2">
                                                                <button
                                                                    onClick={() => handleEdit(d)}
                                                                    className={`p-1.5 rounded transition ${
                                                                        isDarkMode
                                                                            ? 'text-blue-400 hover:text-blue-300 hover:bg-slate-600'
                                                                            : 'text-blue-500 hover:text-blue-700 hover:bg-blue-100'
                                                                    }`}
                                                                    title="Editar"
                                                                >
                                                                    <Pencil size={16} />
                                                                </button>

                                                                <button
                                                                    onClick={() => handleSafeDelete('dept', d)}
                                                                    className={`p-1.5 rounded transition ${
                                                                        isDarkMode
                                                                            ? 'text-red-400 hover:text-red-300 hover:bg-slate-600'
                                                                            : 'text-red-500 hover:text-red-700 hover:bg-red-100'
                                                                    }`}
                                                                    title="Excluir"
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}

                                            {filterList(data.departments).length === 0 && (
                                                <tr>
                                                    <td colSpan="5" className={`p-8 text-center ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                                                        Nenhum departamento encontrado.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        ) : tab === 'types' ? (
                            <Card className="p-0 overflow-hidden shadow-md" darkMode={isDarkMode}>
                                <div className="max-h-[600px] overflow-y-auto">
                                    <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                        <thead className={`uppercase text-xs font-bold sticky top-0 z-10 border-b ${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                            <tr>
                                                <th className="p-4">Descrição / Nome</th><th className="p-4 text-center">Vigência</th><th className="p-4 text-center">Público</th><th className="p-4 text-center">Departamento</th><th className="p-4 text-center text-blue-500">Média Promoção</th><th className="p-4 text-center text-indigo-500">Média Premiação</th><th className="p-4 text-center text-red-500">Max. Pts Perdidos</th><th className="p-4 text-center">Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                        {filterList(data.evaluationTypes || []).sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((type, i) => {                                            const deptName = type.departmentId ? (data.departments.find(d => d.id === type.departmentId)?.name || 'Geral (Todos)') : 'Geral (Todos)';
                                                const rowClass = i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');
                                                const hoverClass = isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-blue-50';
                                                return (
                                                    <tr key={type.id} className={`${rowClass} ${hoverClass} transition-colors border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                                        <td className={`p-4 font-bold align-middle ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{type.name}</td>
                                                        <td className={`p-4 text-center align-middle text-xs whitespace-nowrap ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{formatMonthYear(type.startDate)} - {type.endDate ? formatMonthYear(type.endDate) : 'Indefinido'}</td>
                                                        <td className="p-4 text-center align-middle">
                                                            <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase border ${type.personType === 'Gestão' ? (isDarkMode ? 'bg-purple-900/50 text-purple-300 border-purple-800' : 'bg-purple-50 text-purple-700 border-purple-100') : (isDarkMode ? 'bg-blue-900/50 text-blue-300 border-blue-800' : 'bg-blue-50 text-blue-700 border-blue-100')}`}>{type.personType || 'Colaborador'}</span>
                                                        </td>
                                                        <td className={`p-4 text-center align-middle text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{deptName}</td>
                                                        <td className="p-4 text-center align-middle"><div className={`font-bold px-2 py-1 rounded border inline-block min-w-[50px] ${isDarkMode ? 'bg-blue-900/30 text-blue-400 border-blue-800' : 'text-blue-600 bg-blue-50 border-blue-100'}`}>{type.mediaPromocao ? parseFloat(type.mediaPromocao).toFixed(1) : '-'}</div></td>
                                                        <td className="p-4 text-center align-middle"><div className={`font-bold px-2 py-1 rounded border inline-block min-w-[50px] ${isDarkMode ? 'bg-indigo-900/30 text-indigo-400 border-indigo-800' : 'text-indigo-600 bg-indigo-50 border-indigo-100'}`}>{type.mediaPremiacao ? parseFloat(type.mediaPremiacao).toFixed(1) : '-'}</div></td>
                                                        <td className="p-4 text-center align-middle"><div className={`font-bold px-2 py-1 rounded border inline-block min-w-[50px] ${isDarkMode ? 'bg-red-900/30 text-red-400 border-red-800' : 'text-red-600 bg-red-50 border-red-100'}`}>{type.maxPointsLost !== undefined && type.maxPointsLost !== null ? parseFloat(type.maxPointsLost).toFixed(1) : '-'}</div></td>
                                                        <td className="p-4 text-center align-middle">
                                                            <div className="flex justify-center gap-2">
                                                                <button onClick={() => handleEdit(type)} className={`p-1.5 rounded transition ${isDarkMode ? 'text-blue-400 hover:text-blue-300 hover:bg-slate-600' : 'text-blue-500 hover:text-blue-700 hover:bg-blue-50'}`} title="Editar"><Pencil size={16} /></button>
                                                                <button onClick={() => actions.genericDelete('types', type.id)} className={`p-1.5 rounded transition ${isDarkMode ? 'text-red-400 hover:text-red-300 hover:bg-slate-600' : 'text-red-500 hover:text-red-700 hover:bg-red-50'}`} title="Excluir"><Trash2 size={16} /></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {filterList(data.evaluationTypes).length === 0 && (<tr><td colSpan="8" className={`p-8 text-center ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Nenhum tipo de avaliação cadastrado.</td></tr>)}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        ) : tab === 'collab' ? (
                            <Card className="p-0 overflow-hidden shadow-md" darkMode={isDarkMode}>
                                <div className="max-h-[600px] overflow-y-auto">
                                    <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                        <thead className={`uppercase text-xs font-bold sticky top-0 z-10 border-b ${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                            <tr><th className="p-4">Nome</th><th className="p-4">Email</th><th className="p-4">Departamento</th><th className="p-4 text-center">Tipo</th><th className="p-4 text-center">Cargo Completo</th><th className="p-4 text-center">Ações</th></tr>
                                        </thead>
                                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                            {filterList(visibleCollaborators).map((c, i) => {
                                                const rowClass = i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');
                                                const hoverClass = isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-blue-50';
                                                return (
                                                    <tr key={c.id} className={`${rowClass} ${hoverClass} transition-colors border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                                        <td className={`p-4 font-bold align-middle ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{c.name}</td>
                                                        <td className={`p-4 align-middle ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{c.email}</td>
                                                        <td className={`p-4 align-middle ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{data.departments.find(d => d.id === c.departmentId)?.name || '-'}</td>
                                                        <td className="p-4 text-center align-middle">
                                                            <span className={`px-2 py-1 rounded text-xs font-bold border ${c.personType === 'Gestão' ? (isDarkMode ? 'bg-purple-900/50 text-purple-300 border-purple-800' : 'bg-purple-50 text-purple-700 border-purple-100') : (isDarkMode ? 'bg-indigo-900/50 text-indigo-300 border-indigo-800' : 'bg-indigo-50 text-indigo-700 border-indigo-100')}`}>
                                                                {c.personType || 'Colaborador'}
                                                            </span>
                                                        </td>
                                                        <td className={`p-4 text-center align-middle font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>{`${c.role || ''} ${c.seniority || ''} ${c.level || ''}`}</td>
                                                        <td className="p-4 text-center align-middle">
                                                            <div className="flex justify-center gap-2">
                                                                <button onClick={() => handleEdit(c)} className={`p-1.5 rounded transition ${isDarkMode ? 'text-blue-400 hover:text-blue-300 hover:bg-slate-600' : 'text-blue-500 hover:text-blue-700 hover:bg-blue-50'}`} title="Editar"><Pencil size={16} /></button>
                                                                <button onClick={() => handleSafeDelete('collab', c)} className={`p-1.5 rounded transition ${isDarkMode ? 'text-red-400 hover:text-red-300 hover:bg-slate-600' : 'text-red-500 hover:text-red-700 hover:bg-red-50'}`} title="Excluir"><Trash2 size={16} /></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {filterList(visibleCollaborators).length === 0 && (<tr><td colSpan="6" className={`p-8 text-center ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Nenhum colaborador encontrado.</td></tr>)}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        ) : (
                            // Fallback para outras abas genéricas (Gestores, etc)
                            <div className="max-h-[500px] overflow-y-auto divide-y text-sm bg-white">
                                {filterList(tab==='pcs'?data.pcs:tab==='gestao'?data.gestaoMetasPercepcao:data.managers).map(i => (
                                    <div key={i.id} className="py-2 px-6 flex justify-between items-center hover:bg-gray-50">
                                        <div className="flex items-center gap-2">
                                            <div><div className="font-bold">{i.name || i.title}</div><div className="text-xs text-gray-500">{i.email}</div></div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={()=>handleEdit(i)} className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50"><Pencil size={16}/></button>
                                            <button onClick={()=>actions.genericDelete(tab, i.id)} className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"><Trash2 size={16}/></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>
                </div>
                )}

                {/* --- MODAIS DE CADASTRO E AÇÕES --- */}
                
                {/* 1. MODAL GERAL */}
                {isCadModalOpen && (
                    <CadastroModal 
                        isOpen={isCadModalOpen} 
                        onClose={() => setIsCadModalOpen(false)} 
                        tab={tab === 'config' ? (configSubTab === 'metricas' ? 'config' : configSubTab === 'deadlines' ? 'prazos' : configSubTab) : (tab === 'criteria' && criteriaSubTab === 'conferencia' ? 'conferencia' : tab)} 
                        form={form} 
                        setForm={setForm} 
                        data={data} 
                        handleSave={() => {
                            if (tab === 'criteria' && criteriaSubTab === 'conferencia') {
                                // CORREÇÃO: Usa o id que já está no form se for edição
                                actions.saveConferenciaType({ ...form, id: form.id || Date.now().toString() });
                                setIsCadModalOpen(false);
                                setForm({});
                            } else {
                                handleSave();
                            }
                        }} 
                        criteriaType={criteriaType} 
                        setCriteriaType={setCriteriaType} 
                        currentUser={currentUser} 
                        isDarkMode={isDarkMode} 
                    />
                )}

                {/* 2. MODAL DE USUÁRIOS */}
                {isUserModalOpen && (
                    <CadastroModal 
                        isOpen={isUserModalOpen} 
                        onClose={()=>setIsUserModalOpen(false)} 
                        tab="users" 
                        form={userForm} 
                        setForm={setUserForm} 
                        data={data} 
                        handleSaveUser={handleSaveUser} 
                        currentUser={currentUser} 
                        isDarkMode={isDarkMode}
                    />
                )}

                {/* 3. MODAL DE PERMISSÕES (INLINE) */}
                {isPermModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                        <div className={`rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                            <div className={`p-6 flex justify-between items-center shrink-0 text-white ${isDarkMode ? 'bg-gradient-to-r from-slate-900 to-slate-800' : 'bg-gradient-to-r from-slate-800 to-slate-900'}`}>
                                <div>
                                    <h3 className="font-bold text-xl flex items-center gap-3">
                                        <div className="p-2 bg-white/10 rounded-lg"><Shield size={24} className="text-blue-400"/></div>
                                        Gerenciar Permissões
                                    </h3>
                                    <p className="text-slate-400 text-sm mt-1 ml-12">Editando acessos de: <span className="text-white font-bold">{editingUserName}</span></p>
                                </div>
                                <button onClick={()=>setIsPermModalOpen(false)} className="text-white/50 hover:text-white hover:bg-white/10 p-2 rounded-full transition-all"><X size={24}/></button>
                            </div>
                            
                            <div className={`p-6 overflow-y-auto custom-scrollbar space-y-8 ${isDarkMode ? 'bg-slate-900' : 'bg-slate-50'}`}>
                                
                                {/* Permissão Especial Solta */}
                                <div className={`p-4 rounded-xl border-l-4 shadow-sm flex items-center justify-between ${isDarkMode ? 'bg-slate-800 border-red-500' : 'bg-white border-red-500'}`}>
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600'}`}><Lock size={20}/></div>
                                        <div>
                                            <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Acesso a Dados Sensíveis (Gestão)</h4>
                                            <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Permite visualizar salários e notas de gestores.</p>
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input type="checkbox" className="sr-only peer" checked={permissionForm.viewGestao === true} onChange={e => setPermissionForm({...permissionForm, viewGestao: e.target.checked})} />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                                    </label>
                                </div>

        {/* Catálogo dinâmico no mesmo padrão visual do HUB */}
        {dynamicPeoplePermissionStructure.map((project) => {
            const collectKeys = (node, acc=[]) => { if (node?.key) acc.push(node.key); (node?.children || []).forEach(ch => collectKeys(ch, acc)); return acc; };
            const projectKeys = collectKeys(project, []);
            const grantedCount = projectKeys.filter(k => permissionForm?.[k] === true).length;
            const isRootChecked = !!permissionForm?.[project.key];
            const isExpanded = expandedPermGroups[`user_${project.id}`] !== false;
            return (
                <div key={project.id} className={`gp-hub-permission-shell rounded-xl border overflow-hidden ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-[#fffaf2] border-[#e5cdb1]'}`}>
                    <div className={`px-4 py-3 border-b flex items-center justify-between gap-3 ${isDarkMode ? 'border-slate-700' : 'border-[#e5cdb1]'}`}>
                        <div className="flex items-center gap-2 min-w-0">
                            <button type="button" onClick={() => setExpandedPermGroups(prev => ({...prev, [`user_${project.id}`]: !isExpanded}))} className="w-5 h-5 inline-flex items-center justify-center text-slate-500">
                                {isExpanded ? <ChevronDown size={13}/> : <ChevronRight size={13}/>} 
                            </button>
                            <input type="checkbox" className="w-4 h-4 accent-teal-600" checked={isRootChecked} onChange={e => setPermissionForm(handlePermissionChange(project.key, e.target.checked, permissionForm || {}, dynamicPeoplePermissionStructure))}/>
                            <span className={`gp-permission-module-title ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>MÓDULO: {project.label || PEOPLE_PERMISSION_APP_NAME}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <span className={`px-2 py-1 rounded-full border text-[9px] font-black ${isDarkMode ? 'border-slate-600 text-slate-300' : 'border-[#dec7aa] text-slate-700 bg-white'}`}>{grantedCount} / {projectKeys.length} concedidas</span>
                            <button type="button" onClick={() => { const next={...(permissionForm||{})}; projectKeys.forEach(k=>next[k]=true); setPermissionForm(next); }} className="px-2.5 py-1.5 rounded-md bg-teal-600 text-white text-[9px] font-black inline-flex items-center gap-1"><Check size={11}/>Tudo</button>
                            <button type="button" onClick={() => { const next={...(permissionForm||{})}; projectKeys.forEach(k=>next[k]=false); setPermissionForm(next); }} className={`px-2 py-1.5 text-[9px] font-black ${isDarkMode?'text-slate-300':'text-slate-700'}`}><RefreshCw size={10} className="inline mr-1"/>Limpar</button>
                        </div>
                    </div>
                    {isExpanded && <div className="p-3">{renderPermissionTree(project.children, permissionForm, setPermissionForm, 0, dynamicPeoplePermissionStructure)}</div>}
                </div>
            );
        })}
                            </div>
                            <div className={`p-4 border-t flex justify-end gap-3 shrink-0 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                <Button variant="secondary" onClick={()=>setIsPermModalOpen(false)} className={isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600' : ''}>Cancelar</Button>
                                <Button onClick={handleSavePermissions} icon={Save} className="bg-slate-800 hover:bg-slate-900">Salvar Alterações</Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 4. MODAL DE RESET DE SENHA */}
                {isResetModalOpen && userToReset && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] backdrop-blur-sm" onClick={() => setIsResetModalOpen(false)}>
                        <div className={`p-6 rounded-xl w-96 shadow-2xl animate-fade-in ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
                            <div className={`flex justify-between items-center mb-4 border-b pb-2 ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                <h3 className="font-bold text-lg flex items-center gap-2 text-orange-600"><Key size={20}/> Resetar Senha</h3>
                                <button onClick={() => setIsResetModalOpen(false)} className={`hover:text-gray-600 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}><X size={20}/></button>
                            </div>
                            <div className="space-y-4">
                                <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Definindo nova senha para: <b>{userToReset.name}</b></p>
                                <div>
                                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Nova Senha Padrão</label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-2.5 text-gray-400" size={16}/>
                                        <input type="text" className={`w-full pl-9 p-2 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none font-mono text-sm ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`} value={resetPasswordValue} onChange={(e) => setResetPasswordValue(e.target.value)}/>
                                    </div>
                                    <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>* O usuário será solicitado a trocar esta senha no próximo login.</p>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 mt-6">
                                <Button variant="secondary" onClick={() => setIsResetModalOpen(false)} className={isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600' : ''}>Cancelar</Button>
                                <Button onClick={handleConfirmReset} className="bg-orange-600 hover:bg-orange-700 text-white">Confirmar Reset</Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 5. MODAL DE SYNC (CAPACITAÇÕES - GERAL) */}
                {isSyncModalOpen && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className={`p-6 rounded-xl w-96 shadow-2xl animate-fade-in space-y-4 ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white'}`}>
                            <h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}><RefreshCw size={18}/> Sincronizar Treinamentos</h3>
                            <div>
                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Ano Base (C1)</label>
                                <Input type="number" value={syncYearInput} onChange={e => setSyncYearInput(e.target.value)} className={`text-center text-lg tracking-widest font-bold ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`} maxLength={4}/>
                                <p className={`text-[10px] mt-2 ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>O sistema atualizará a planilha e importará os dados após 2 segundos.</p>
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <Button variant="secondary" onClick={() => setIsSyncModalOpen(false)} disabled={isUpdatingSheet} className={isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600' : ''}>Cancelar</Button>
                                <Button onClick={handleConfirmSync} disabled={isUpdatingSheet} className="bg-green-600 hover:bg-green-700 text-white">
                                    {isUpdatingSheet ? <><Loader2 size={16} className="animate-spin mr-2"/> Aguarde...</> : "Confirmar"}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 6. MODAL DE SYNC PERCEPÇÃO */}
                {isPerceptionSyncModalOpen && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className={`p-6 rounded-xl w-96 shadow-2xl animate-fade-in space-y-4 ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white'}`}>
                            <h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}><RefreshCw size={18}/> Sincronizar Percepção da Equipe</h3>
                            <div>
                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Ano Base (Célula C1)</label>
                                <Input type="number" value={perceptionYearInput} onChange={e => setPerceptionYearInput(e.target.value)} className={`text-center text-lg tracking-widest font-bold ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`} maxLength={4}/>
                                <p className={`text-[10px] mt-2 ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>O sistema atualizará a planilha e importará os dados.</p>
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <Button variant="secondary" onClick={() => setIsPerceptionSyncModalOpen(false)} disabled={isUpdatingSheet} className={isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600' : ''}>Cancelar</Button>
                                <Button onClick={handleConfirmPerceptionSync} disabled={isUpdatingSheet} className="bg-blue-600 hover:bg-blue-700 text-white">
                                    {isUpdatingSheet ? <><Loader2 size={16} className="animate-spin mr-2"/> Processando...</> : "Confirmar"}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

            {/* MODAL DE PERMISSÕES EM BLOCO (ATUALIZADO) */}
                {isBulkPermModalOpen && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                        <div className={`rounded-2xl w-full max-w-6xl shadow-2xl flex flex-col max-h-[95vh] overflow-hidden ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                            
                            {/* HEADER */}
                            <div className="p-5 border-b flex justify-between items-center shrink-0">
                                <div>
                                    <h3 className={`font-bold text-xl ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Gerenciar Permissões em Bloco</h3>
                                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Aplique regras de acesso para grupos ou usuários específicos.</p>
                                </div>
                                <button onClick={() => setIsBulkPermModalOpen(false)}><X size={24} className="text-gray-400 hover:text-red-500"/></button>
                            </div>
                            
                            <div className="flex flex-1 overflow-hidden">
                                
                                {/* COLUNA ESQUERDA: SELEÇÃO DE ALVO (30% LARGURA) */}
                                <div className={`w-[30%] border-r flex flex-col ${isDarkMode ? 'border-slate-700 bg-slate-900/50' : 'border-gray-200 bg-gray-50'}`}>
                                    <div className="p-4 border-b border-inherit">
                                        <label className={`text-xs font-bold block mb-2 uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>1. Definição do Público</label>
                                        
                                        {/* Botões de Alternância de Modo */}
                                        <div className="flex gap-2 mb-4">
                                            <button 
                                                onClick={() => setBulkTargetMode('selection')}
                                                className={`flex-1 py-2 text-xs rounded border font-bold transition-colors ${bulkTargetMode === 'selection' ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : (isDarkMode ? 'bg-slate-800 border-slate-600 text-slate-400 hover:bg-slate-700' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-100')}`}
                                            >
                                                Seleção Individual
                                            </button>
                                            <button 
                                                onClick={() => setBulkTargetMode('role')}
                                                className={`flex-1 py-2 text-xs rounded border font-bold transition-colors ${bulkTargetMode === 'role' ? 'bg-purple-600 text-white border-purple-600 shadow-sm' : (isDarkMode ? 'bg-slate-800 border-slate-600 text-slate-400 hover:bg-slate-700' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-100')}`}
                                            >
                                                Por Tipo (Cargo)
                                            </button>
                                        </div>

                                        {/* CONTEÚDO CONDICIONAL: MODO TIPO */}
                                        {bulkTargetMode === 'role' && (
                                            <div className="animate-fade-in space-y-3">
                                                <div>
                                                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Tipo de Usuário Alvo</label>
                                                    <select 
                                                        value={bulkTargetRole} 
                                                        onChange={(e) => setBulkTargetRole(e.target.value)}
                                                        className={`w-full p-2.5 text-sm rounded border outline-none focus:ring-2 focus:ring-purple-500 ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                                                    >
                                                        <option value="ADM">Todos os ADMs</option>
                                                        <option value="Gestor">Todos os Gestores</option>
                                                        <option value="Colaborador">Todos os Colaboradores</option>
                                                        <option value="SuperAdm">Todos os SuperAdms</option>
                                                        <option value="Solicitante">Solicitantes (Pendentes)</option>
                                                    </select>
                                                </div>
                                                <div className={`text-xs p-3 rounded border flex items-center gap-2 ${isDarkMode ? 'bg-purple-900/20 text-purple-300 border-purple-800' : 'bg-purple-50 text-purple-700 border-purple-100'}`}>
                                                    <Users size={14}/>
                                                    <span>Afetará <b>{data.users.filter(u => u.role === bulkTargetRole).length}</b> usuários cadastrados.</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* CONTEÚDO CONDICIONAL: MODO MANUAL */}
                                        {bulkTargetMode === 'selection' && (
                                            <div className="animate-fade-in flex flex-col h-[350px]">
                                                <div className="relative mb-2">
                                                    <Search className={`absolute left-2.5 top-2.5 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} size={14}/>
                                                    <input 
                                                        type="text" 
                                                        placeholder="Buscar usuário..." 
                                                        value={manualSearch}
                                                        onChange={e => setManualSearch(e.target.value)}
                                                        className={`w-full pl-8 p-2 text-xs rounded border outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                                                    />
                                                </div>
                                                
                                                <div className={`flex-1 border rounded overflow-hidden flex flex-col ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-300 bg-white'}`}>
                                                    <div className={`p-2 border-b flex justify-between items-center text-[10px] ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-100 border-gray-200'}`}>
                                                        <span className="font-bold">{manualSelectedIds.length} selecionados</span>
                                                        <button 
                                                            onClick={() => {
                                                                const visibleUsers = data.users.filter(u => u.name.toLowerCase().includes(manualSearch.toLowerCase()));
                                                                const allIds = visibleUsers.map(u => u.id);
                                                                const allSelected = allIds.every(id => manualSelectedIds.includes(id));
                                                                if (allSelected) setManualSelectedIds(prev => prev.filter(id => !allIds.includes(id)));
                                                                else setManualSelectedIds(prev => [...new Set([...prev, ...allIds])]);
                                                            }}
                                                            className="text-blue-500 hover:underline font-bold"
                                                        >
                                                            Alternar Todos
                                                        </button>
                                                    </div>
                                                    <div className="overflow-y-auto custom-scrollbar p-1 space-y-0.5 flex-1">
                                                        {data.users
                                                            .filter(u => u.name.toLowerCase().includes(manualSearch.toLowerCase()))
                                                            .map(u => (
                                                                <label key={u.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${manualSelectedIds.includes(u.id) ? (isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50') : (isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50')}`}>
                                                                    <input 
                                                                        type="checkbox" 
                                                                        checked={manualSelectedIds.includes(u.id)}
                                                                        onChange={() => {
                                                                            if(manualSelectedIds.includes(u.id)) setManualSelectedIds(prev => prev.filter(id => id !== u.id));
                                                                            else setManualSelectedIds(prev => [...prev, u.id]);
                                                                        }}
                                                                        className="rounded text-blue-600 focus:ring-blue-500"
                                                                    />
                                                                    <div className="overflow-hidden">
                                                                        <div className={`text-xs font-medium truncate ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>{u.name}</div>
                                                                        <div className={`text-[10px] truncate ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>{u.role} • {data.departments.find(d => d.id === u.departmentId)?.name}</div>
                                                                    </div>
                                                                </label>
                                                            ))
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* SELETOR DE AÇÃO (ADD/REMOVE) */}
                                    <div className="p-4 mt-auto">
                                        <label className={`text-xs font-bold block mb-2 uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>2. Tipo de Alteração</label>
                                        <div className="grid grid-cols-1 gap-2">
                                            <label className={`flex items-center gap-3 cursor-pointer p-3 border rounded transition-all ${bulkActionType === 'add' ? 'bg-green-50 border-green-400 ring-1 ring-green-400 dark:bg-green-900/20 dark:border-green-700' : 'border-gray-200 dark:border-slate-700 opacity-70 hover:opacity-100'}`}>
                                                <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${bulkActionType === 'add' ? 'border-green-500 bg-green-500' : 'border-gray-400'}`}>
                                                    {bulkActionType === 'add' && <div className="w-1.5 h-1.5 bg-white rounded-full"></div>}
                                                </div>
                                                <input type="radio" name="bulkType" checked={bulkActionType === 'add'} onChange={() => setBulkActionType('add')} className="hidden"/>
                                                <span className={`text-xs font-bold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>Adicionar / Conceder</span>
                                            </label>
                                            <label className={`flex items-center gap-3 cursor-pointer p-3 border rounded transition-all ${bulkActionType === 'remove' ? 'bg-red-50 border-red-400 ring-1 ring-red-400 dark:bg-red-900/20 dark:border-red-700' : 'border-gray-200 dark:border-slate-700 opacity-70 hover:opacity-100'}`}>
                                                <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${bulkActionType === 'remove' ? 'border-red-500 bg-red-500' : 'border-gray-400'}`}>
                                                    {bulkActionType === 'remove' && <div className="w-1.5 h-1.5 bg-white rounded-full"></div>}
                                                </div>
                                                <input type="radio" name="bulkType" checked={bulkActionType === 'remove'} onChange={() => setBulkActionType('remove')} className="hidden"/>
                                                <span className={`text-xs font-bold ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>Remover / Revogar</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* COLUNA DIREITA: LISTA DE PERMISSÕES HIERÁRQUICA (70% LARGURA) */}
                                <div className="flex-1 flex flex-col overflow-hidden">
                                    <div className={`p-4 border-b text-xs font-bold uppercase tracking-wide flex justify-between items-center ${isDarkMode ? 'bg-slate-900 border-slate-700 text-slate-400' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                                        <span>3. Selecione as Permissões</span>
                                        <span className="text-[10px] font-normal normal-case opacity-70">Apenas as marcadas serão alteradas</span>
                                    </div>
                                    
                                    {/* CORREÇÃO: Fundo branco no modo claro */}
                                    <div className={`flex-1 overflow-y-auto p-6 custom-scrollbar ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                                        
                                        {/* Renderização dos Projetos */}
                                        {PERMISSION_STRUCTURE.map((project) => {
                                            // Verifica se o projeto está habilitado nas permissões
                                            const isProjectEnabled = !!permissionForm[project.key];
                                            // Verifica se está visualmente expandido (Requisito 3)
                                            // Se undefined, consideramos expandido por padrão se estiver habilitado
                                            const isProjectExpanded = expandedPermGroups[project.id] !== false; 

                                            return (
                                                <div key={project.id} className={`mb-6 border rounded-xl overflow-hidden shadow-sm ${isDarkMode ? 'border-slate-700 bg-slate-900/30' : 'border-gray-200 bg-white'}`}>
                                                    
                                                    {/* Header do Projeto */}
                                                    <div className={`p-3 flex items-center justify-between border-b ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-100'}`}>
                                                        
                                                        <div className="flex items-center gap-3">
                                                            {/* Checkbox Mestre do Projeto (Com Cascata) */}
                                                            <input 
                                                                type="checkbox" 
                                                                checked={isProjectEnabled} 
                                                                onChange={e => {
                                                                    const newVal = e.target.checked;
                                                                    // Aplica cascata também ao ligar/desligar o projeto inteiro
                                                                    const newPermissions = handlePermissionChange(project.key, newVal, permissionForm, PERMISSION_STRUCTURE);
                                                                    
                                                                    // Se habilitar, garante que expande visualmente
                                                                    if (newVal) {
                                                                        setExpandedPermGroups(prev => ({...prev, [project.id]: true}));
                                                                    }
                                                                    setPermissionForm(newPermissions);
                                                                }} 
                                                                className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                                                            />
                                                            <span className={`font-bold text-sm ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                                                {project.label}
                                                            </span>
                                                        </div>

                                                        {/* BOTÃO + / - (REQUISITO 3) */}
                                                        {/* Só aparece se o projeto estiver habilitado */}
                                                        {isProjectEnabled && (
                                                            <button 
                                                                onClick={() => setExpandedPermGroups(prev => ({...prev, [project.id]: !isProjectExpanded}))}
                                                                className={`p-1.5 rounded transition-colors flex items-center gap-1 text-xs font-bold border ${isDarkMode ? 'border-slate-600 hover:bg-slate-700 text-slate-300' : 'border-gray-300 hover:bg-gray-200 text-gray-600'}`}
                                                                title={isProjectExpanded ? "Recolher Projeto" : "Expandir Projeto"}
                                                            >
                                                                {isProjectExpanded ? <Minus size={14}/> : <Plus size={14}/>}
                                                            </button>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Corpo: Lista Recursiva */}
                                                    {/* Exibe se: Projeto Habilitado E Visualmente Expandido */}
                                                    {isProjectEnabled && isProjectExpanded && (
                                                        <div className={`p-2 ${isDarkMode ? '' : 'bg-white'}`}>
                                                            {renderPermissionTree(project.children, permissionForm, setPermissionForm)}
                                                        </div>
                                                    )}
                                                    
                                                    {/* Mensagem se projeto desabilitado */}
                                                    {!isProjectEnabled && (
                                                        <div className={`p-8 text-center text-sm italic ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                                                            O acesso a este projeto está desabilitado.
                                                        </div>
                                                    )}
                                                    
                                                    {/* Mensagem se projeto habilitado mas recolhido */}
                                                    {isProjectEnabled && !isProjectExpanded && (
                                                        <div className={`p-2 text-center text-xs text-blue-500 font-bold bg-blue-50/50 dark:bg-blue-900/10 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/20`} onClick={() => setExpandedPermGroups(prev => ({...prev, [project.id]: true}))}>
                                                            ... Permissões ocultas (Clique para expandir) ...
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* FOOTER */}
                            <div className={`p-4 border-t flex justify-between items-center gap-2 shrink-0 ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-gray-50'}`}>
                                <div className={`text-xs italic ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                                    * Esta ação aplicará as permissões selecionadas imediatamente.
                                </div>
                                <div className="flex gap-2">
                                    <Button variant="secondary" onClick={() => setIsBulkPermModalOpen(false)}>Cancelar</Button>
                                    <Button onClick={handleSaveBulkPermissions} className={bulkActionType === 'remove' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}>
                                        Confirmar {bulkActionType === 'add' ? 'Concessão' : 'Revogação'} Massiva
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}            

                {/* MODAL DE PERFIS (CORRIGIDO: SELEÇÃO E EDICAO) */}
                {isProfileModalOpen && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                        <div className={`gp-profile-modal rounded-xl w-full max-w-6xl shadow-2xl flex h-[85vh] overflow-hidden ${isDarkMode ? 'bg-slate-800' : 'bg-[#fffaf2]'}`}>
                            
                            {/* Lado Esquerdo: Lista de Perfis */}
                            <div className={`w-1/4 border-r flex flex-col ${isDarkMode ? 'border-slate-700 bg-slate-900' : 'border-gray-200 bg-gray-50'}`}>
                                <div className={`p-4 border-b font-bold flex items-center gap-2 ${isDarkMode ? 'text-white border-slate-700' : 'text-gray-800 border-gray-200'}`}>
                                    <ShieldCheck size={18} className="text-teal-500"/> Perfis de Acesso
                                </div>
                                <div className="flex-1 overflow-y-auto custom-scrollbar">
                                    {data.permissionProfiles.map(p => (
                                        <div 
                                            key={p.id}
                                            onClick={() => setProfileForm({ ...p, permissions: p.permissions || {} })}
                                            className={`m-2 p-3 rounded-xl border flex flex-col gap-2 transition-colors group cursor-pointer ${profileForm.id === p.id ? (isDarkMode ? 'bg-teal-900/30 border-teal-500' : 'bg-white border-teal-400 shadow-sm') : (isDarkMode ? 'hover:bg-slate-800 border-slate-700 text-slate-300' : 'hover:bg-white border-[#e5cdb1] text-gray-800 bg-[#fffaf2]')}`} 
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-sm font-black truncate flex-1">{p.name}</span>
                                                <div className="flex gap-1 opacity-100 transition-opacity">
                                                {/* BOTÃO EDITAR EXPLÍCITO */}
                                                <button 
                                                    onClick={(e) => { 
                                                        e.stopPropagation(); 
                                                        setProfileForm({ ...p, permissions: p.permissions || {} }); 
                                                    }} 
                                                    className="text-blue-500 hover:text-blue-700 p-1.5 rounded hover:bg-blue-100/50"
                                                    title="Editar Perfil"
                                                >
                                                    <Edit3 size={14}/>
                                                </button>
                                                
                                                <button 
                                                    onClick={(e) => { 
                                                        e.stopPropagation(); 
                                                        handleDeleteProfileWithLog(p); 
                                                    }} 
                                                    className="text-red-400 hover:text-red-600 p-1.5 rounded hover:bg-red-100/50"
                                                    title="Excluir Perfil"
                                                >
                                                    <Trash2 size={14}/>
                                                </button>
                                            </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); setProfileMembersFilter('unlinked'); setProfileMembersModal(p); }}
                                                className="self-start inline-flex items-center gap-1.5 rounded-full bg-teal-700 hover:bg-teal-800 text-white px-3 py-1 text-[10px] font-black"
                                            >
                                                <Users size={12}/>
                                                {data.users.filter(u => (u.profileId || u._profileId) === p.id).length} colaborador{data.users.filter(u => (u.profileId || u._profileId) === p.id).length === 1 ? '' : 'es'}
                                            </button>
                                        </div>
                                    ))}
                                    <button 
                                        onClick={() => setProfileForm({ id: null, name: '', permissions: {} })} 
                                        className="w-full p-4 text-center text-sm text-blue-500 font-bold hover:bg-blue-50/10 border-t border-transparent hover:border-blue-500/20 transition-all flex items-center justify-center gap-2"
                                    >
                                        <Plus size={16}/> Criar Novo Perfil
                                    </button>
                                </div>
                            </div>

                            {/* Lado Direito: Editor Hierárquico */}
                            <div className="flex-1 flex flex-col overflow-hidden">
                                {/* Toolbar do Editor */}
                                <div className={`p-4 border-b flex justify-between items-center gap-4 ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-white'}`}>
                                    <div className="flex-1">
                                        <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Nome do Perfil</label>
                                        <Input 
                                            placeholder="Ex: Gerente Comercial" 
                                            value={profileForm.name} 
                                            onChange={e => setProfileForm({...profileForm, name: e.target.value})} 
                                            className={isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}
                                        />
                                    </div>
                                    <div className="gp-linked-role w-64">
                                        <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>Vincular Automaticamente a:</label>
                                        <select 
                                            className={`w-full p-2.5 border rounded text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                                            value={profileForm.linkedRole || ''}
                                            onChange={e => setProfileForm({...profileForm, linkedRole: e.target.value})}
                                        >
                                            <option value="">(Sem vínculo automático)</option>
                                            <option value="ADM">ADM</option>
                                            <option value="Gestor">Gestor</option>
                                            <option value="Colaborador">Colaborador</option>
                                            <option value="Solicitante">Solicitante</option>
                                        </select>
                                    </div>
                                    <div className="flex gap-2 items-end">
                                        <Button variant="secondary" onClick={() => setIsProfileModalOpen(false)}>Fechar</Button>
                                        <Button onClick={handleSaveProfile} icon={Save}>Salvar Perfil</Button>
                                    </div>
                                </div>

                                {/* Catálogo de permissões - busca e aplicação */}
                                <div className={`mx-5 mt-4 p-3 rounded-xl border flex items-center gap-3 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-[#fffaf2] border-[#e5cdb1]'}`}>
                                    <div className="relative flex-1">
                                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                                        <input
                                            value={profilePermissionSearch}
                                            onChange={e => setProfilePermissionSearch(e.target.value)}
                                            placeholder="Buscar por permissão, módulo ou chave..."
                                            className={`w-full pl-9 pr-3 py-2 rounded-lg border text-xs outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-[#dec7aa] text-slate-800'}`}
                                        />
                                    </div>
                                    <Filter size={15} className="text-slate-400"/>
                                    <select className={`px-3 py-2 rounded-lg border text-xs font-bold ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-[#dec7aa]'}`} value={PEOPLE_PERMISSION_APP_ID} disabled>
                                        <option value={PEOPLE_PERMISSION_APP_ID}>Gestão de Pessoas</option>
                                    </select>
                                    <label className={`inline-flex items-center gap-2 text-xs font-black whitespace-nowrap ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                        <input type="checkbox" checked={profileShowOnlyNew} onChange={e => setProfileShowOnlyNew(e.target.checked)} className="w-4 h-4 rounded text-amber-600"/>
                                        Novas
                                    </label>
                                </div>

                                {/* Lista de Permissões — catálogo dinâmico desta aplicação */}
                                <div className="px-5 pt-2 flex items-center gap-2 text-[10px] font-bold text-slate-500">
                                    <span>{peoplePermissionCatalogLoading ? 'Sincronizando catálogo...' : `${peoplePermissionCatalog.length || localPeoplePermissionCatalog.length} permissões publicadas`}</span>
                                    {peoplePermissionCatalogError && <span className="text-amber-600">• catálogo local em uso</span>}
                                </div>
                                <div className="flex-1 overflow-y-auto p-5 custom-scrollbar bg-opacity-50">
                                    {filterPeoplePermissionTree(dynamicPeoplePermissionStructure, profilePermissionSearch, profileShowOnlyNew).map((project) => {
                                        const collectKeys = (node, acc=[]) => { if (node?.key) acc.push(node.key); (node?.children || []).forEach(ch => collectKeys(ch, acc)); return acc; };
                                        const projectKeys = collectKeys(project, []);
                                        const grantedCount = projectKeys.filter(k => profileForm.permissions?.[k] === true).length;
                                        const isRootChecked = !!profileForm.permissions?.[project.key];
                                        const isExpanded = expandedPermGroups[`profile_${project.id}`] !== false;
                                        return (
                                            <div key={project.id} className={`gp-hub-permission-shell mb-4 rounded-xl border overflow-hidden ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-[#fffaf2] border-[#e5cdb1]'}`}>
                                                <div className={`px-4 py-3 border-b flex items-center justify-between gap-3 ${isDarkMode ? 'border-slate-700' : 'border-[#e5cdb1]'}`}>
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <button type="button" onClick={() => setExpandedPermGroups(prev => ({...prev, [`profile_${project.id}`]: !isExpanded}))} className="w-5 h-5 inline-flex items-center justify-center text-slate-500">
                                                            {isExpanded ? <ChevronDown size={13}/> : <ChevronRight size={13}/>} 
                                                        </button>
                                                        <input type="checkbox" className="w-4 h-4 accent-teal-600" checked={isRootChecked} onChange={e => setProfileForm(prev => ({...prev, permissions: handlePermissionChange(project.key, e.target.checked, prev.permissions || {}, dynamicPeoplePermissionStructure)}))}/>
                                                        <span className={`gp-permission-module-title ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>MÓDULO: {project.label || PEOPLE_PERMISSION_APP_NAME}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <span className={`px-2 py-1 rounded-full border text-[9px] font-black ${isDarkMode ? 'border-slate-600 text-slate-300' : 'border-[#dec7aa] text-slate-700 bg-white'}`}>{grantedCount} / {projectKeys.length} concedidas</span>
                                                        <button type="button" onClick={() => { const next={...(profileForm.permissions||{})}; projectKeys.forEach(k=>next[k]=true); setProfileForm(prev=>({...prev,permissions:next})); }} className="px-2.5 py-1.5 rounded-md bg-teal-600 text-white text-[9px] font-black inline-flex items-center gap-1"><Check size={11}/>Tudo</button>
                                                        <button type="button" onClick={() => { const next={...(profileForm.permissions||{})}; projectKeys.forEach(k=>next[k]=false); setProfileForm(prev=>({...prev,permissions:next})); }} className={`px-2 py-1.5 text-[9px] font-black ${isDarkMode?'text-slate-300':'text-slate-700'}`}><RefreshCw size={10} className="inline mr-1"/>Limpar</button>
                                                    </div>
                                                </div>
                                                {isExpanded && (
                                                    <div className="p-3">
                                                        {renderPermissionTree(project.children, profileForm.permissions || {}, (newPerms) => setProfileForm(prev => ({...prev, permissions:newPerms})), 0, dynamicPeoplePermissionStructure)}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                )}


                {/* MODAL DE COLABORADORES VINCULADOS AO PERFIL */}
                {profileMembersModal && (
                    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setProfileMembersModal(null)}>
                        <div className={`w-full max-w-md max-h-[80vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-[#fffaf2] text-slate-900'}`} onClick={e => e.stopPropagation()}>
                            <div className="bg-teal-600 text-white px-5 py-4 flex items-center justify-between">
                                <div className="font-black flex items-center gap-2"><Users size={18}/> Perfil: {profileMembersModal.name}</div>
                                <button onClick={() => setProfileMembersModal(null)}><X size={18}/></button>
                            </div>
                            <div className="p-4 overflow-y-auto space-y-2">
                                <div className="text-[10px] font-black uppercase text-slate-500 mb-2">Colaboradores vinculados:</div>
                                {data.users.filter(u => (u.profileId || u._profileId) === profileMembersModal.id).map(u => (
                                    <div key={u.id} className={`p-3 rounded-xl border flex items-center justify-between gap-3 ${isDarkMode ? 'border-slate-600 bg-slate-900' : 'border-[#e5cdb1] bg-white'}`}>
                                        <div className="min-w-0">
                                            <div className="font-black text-xs truncate">{u.name}</div>
                                            <div className="text-[10px] text-slate-500 truncate">{u.email}</div>
                                        </div>
                                        <button title="Desvincular" onClick={async () => { await actions.saveUser({ ...u, profileId: '', _profileId: '' }); }} className="text-red-500 hover:bg-red-50 rounded p-1"><X size={14}/></button>
                                    </div>
                                ))}
                                {data.users.filter(u => (u.profileId || u._profileId) === profileMembersModal.id).length === 0 && <div className="text-xs text-slate-500 italic p-3">Nenhum colaborador vinculado.</div>}
                            </div>
                            <div className={`border-t p-4 ${isDarkMode ? 'border-slate-700' : 'border-[#e5cdb1]'}`}>
                                <div className="flex items-center justify-between gap-3 mb-2">
                                    <div className="text-[10px] font-black uppercase">Vincular colaborador a este perfil:</div>
                                    <div className={`inline-flex rounded-lg p-0.5 ${isDarkMode ? 'bg-slate-900' : 'bg-[#fff7eb]'}`}>
                                        <button
                                            type="button"
                                            onClick={() => setProfileMembersFilter('unlinked')}
                                            className={`px-3 py-1 rounded-md text-[9px] font-black transition ${profileMembersFilter === 'unlinked' ? 'bg-teal-700 text-white shadow-sm' : (isDarkMode ? 'text-slate-300' : 'text-slate-700')}`}
                                        >
                                            Não Vinculados
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setProfileMembersFilter('all')}
                                            className={`px-3 py-1 rounded-md text-[9px] font-black transition ${profileMembersFilter === 'all' ? 'bg-teal-700 text-white shadow-sm' : (isDarkMode ? 'text-slate-300' : 'text-slate-700')}`}
                                        >
                                            Todos
                                        </button>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <select id="gp-profile-member-select" className={`flex-1 p-2 rounded-lg border text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-[#dec7aa]'}`}>
                                        <option value="">Selecione um colaborador...</option>
                                        {data.users
                                            .filter(u => profileMembersFilter === 'all' || (u.profileId || u._profileId) !== profileMembersModal.id)
                                            .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR'))
                                            .map(u => {
                                                const alreadyLinked = (u.profileId || u._profileId) === profileMembersModal.id;
                                                return <option key={u.id} value={u.id}>{u.name}{profileMembersFilter === 'all' && alreadyLinked ? ' (vinculado)' : ''}</option>;
                                            })}
                                    </select>
                                    <button onClick={async () => {
                                        const sel = document.getElementById('gp-profile-member-select');
                                        const uid = sel?.value;
                                        if (!uid) return;
                                        const u = data.users.find(x => x.id === uid);
                                        if (!u) return;
                                        if ((u.profileId || u._profileId) === profileMembersModal.id) {
                                            if (sel) sel.value = '';
                                            return;
                                        }
                                        await actions.saveUser({ ...u, profileId: profileMembersModal.id, _profileId: profileMembersModal.id, permissionOverrides: {}, permissions: { ...(profileMembersModal.permissions || {}) } });
                                        if (sel) sel.value = '';
                                    }} className="px-3 py-2 rounded-lg border border-teal-600 text-teal-700 font-black text-xs hover:bg-teal-50">+ Vincular</button>
                                </div>
                            </div>
                            <div className="p-4 flex justify-end"><button onClick={() => setProfileMembersModal(null)} className="px-4 py-2 rounded-lg bg-white border border-[#dec7aa] text-slate-900 font-black text-xs">Concluído</button></div>
                        </div>
                    </div>
                )}

                {/* MODAL DE MIGRAÇÃO DE DADOS */}
                {isMigrationModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => !importing && setIsMigrationModalOpen(false)}>
                        <div className={`p-8 rounded-xl w-[500px] shadow-2xl border flex flex-col gap-6 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            
                            <div className="text-center">
                                <div className="w-16 h-16 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Database size={32}/>
                                </div>
                                <h3 className="font-bold text-xl">Migração de Dados</h3>
                                <p className={`text-sm mt-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                    Exporte os dados deste projeto para salvar um backup ou importar em um novo ambiente.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                {/* BOTÃO EXPORTAR */}
                                <button 
                                    onClick={handleExportBackup}
                                    disabled={importing}
                                    className={`p-4 rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-3 transition-all ${isDarkMode ? 'border-slate-600 hover:border-blue-500 hover:bg-slate-700' : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'}`}
                                >
                                    <FileDown size={24} className="text-blue-500"/>
                                    <div className="text-center">
                                        <span className="block font-bold text-sm">Exportar (Backup)</span>
                                        <span className="text-[10px] opacity-70">Baixar arquivo JSON</span>
                                    </div>
                                </button>

                                {/* BOTÃO IMPORTAR */}
                                <label className={`cursor-pointer p-4 rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-3 transition-all ${isDarkMode ? 'border-slate-600 hover:border-green-500 hover:bg-slate-700' : 'border-gray-300 hover:border-green-500 hover:bg-green-50'}`}>
                                    <input type="file" accept=".json" className="hidden" onChange={handleImportBackup} disabled={importing} />
                                    {importing ? <Loader2 size={24} className="animate-spin text-green-500"/> : <Upload size={24} className="text-green-500"/>}
                                    <div className="text-center">
                                        <span className="block font-bold text-sm">{importing ? 'Importando...' : 'Importar (Restaurar)'}</span>
                                        <span className="text-[10px] opacity-70">Carregar do JSON</span>
                                    </div>
                                </label>
                            </div>

                            <div className={`text-[10px] p-3 rounded text-center ${isDarkMode ? 'bg-yellow-900/20 text-yellow-500' : 'bg-yellow-50 text-yellow-700'}`}>
                                <AlertTriangle size={12} className="inline mr-1 mb-0.5"/>
                                <b>Cuidado:</b> A importação deve ser feita preferencialmente em projetos vazios para evitar duplicação de IDs.
                            </div>

                            <button onClick={() => setIsMigrationModalOpen(false)} disabled={importing} className="w-full py-2 rounded-lg text-sm font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                                Fechar
                            </button>
                        </div>
                    </div>
                )}

                                {/* MODAL GLOBAL DE ALERTA (DINÂMICO: SUCESSO vs ERRO) */}
                        {validationAlert && (
                            <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setValidationAlert(null)}>
                                <div className={`p-6 rounded-xl w-96 shadow-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()}>
                                    
                                    {(() => {
                                        // Verifica se é Sucesso pelo título
                                        const isSuccess = validationAlert.title === 'Sucesso!' || validationAlert.title === 'Sucesso';
                                        const isAISuggestion = validationAlert.type === 'aiSuggestion';
                                        
                                        return (
                                            <>
                                                <div className={`flex items-center gap-3 mb-4 ${isSuccess ? 'text-green-500' : isAISuggestion ? 'text-purple-500' : 'text-red-500'}`}>
                                                    <div className={`p-2 rounded-full ${isSuccess ? 'bg-green-100' : isAISuggestion ? 'bg-purple-100' : 'bg-red-100'}`}>
                                                        {isSuccess ? <CheckCircle size={24} /> : isAISuggestion ? <Sparkles size={24} /> : <AlertTriangle size={24} />}
                                                    </div>
                                                    <h3 className="font-bold text-lg">{validationAlert.title || 'Atenção'}</h3>
                                                </div>
                                                
                                                <p className={`text-sm mb-6 whitespace-pre-wrap leading-relaxed max-h-[45vh] overflow-y-auto custom-scrollbar ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                    {validationAlert.message || (typeof validationAlert === 'string' ? validationAlert : 'Erro desconhecido')}
                                                </p>
                                                
                                                <div className="flex justify-end gap-2">
                                                    <Button 
                                                        variant="secondary"
                                                        onClick={() => setValidationAlert(null)} 
                                                        className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}
                                                    >
                                                        {validationAlert.cancelText || (isSuccess ? 'Continuar' : 'Entendi')}
                                                    </Button>
                                                    {validationAlert.onConfirm && (
                                                        <Button
                                                            onClick={validationAlert.onConfirm}
                                                            className={`${isAISuggestion ? 'bg-purple-600 hover:bg-purple-700' : 'bg-red-600 hover:bg-red-700'} text-white shadow-md`}
                                                        >
                                                            {validationAlert.confirmText || 'Confirmar'}
                                                        </Button>
                                                    )}
                                                </div>
                                            </>
                                        );
                                    })()}
                                    
                                </div>
                            </div>
                        )}

                    {/* MODAL DE AVALIAÇÃO DE GESTÃO */}
                                <PerceptionFormModal 
                                    isOpen={isEvaluationModalOpen}
                                    onClose={() => setIsEvaluationModalOpen(false)}
                                    data={data}
                                    questions={perceptionQuestions} // Agora a variável será encontrada!
                                    currentUser={currentUser}
                                    isDarkMode={isDarkMode}
                                    onSave={(newEntry) => {
                                        actions.saveTeamPerception(newEntry);
                                        alert("Avaliação registrada com sucesso!");
                                    }}
                                />

                                {/* MODAL DE CONFIGURAÇÃO DE PRAZOS E PERGUNTAS */}
                                    <PerceptionConfigModal 
                                        isOpen={isPerceptionConfigOpen}
                                        onClose={() => setIsPerceptionConfigOpen(false)}
                                        config={perceptionConfig}
                                        questions={perceptionQuestions} 
                                        
                                        // CORREÇÃO: Salva no banco de dados com ID fixo 'perception_dates'
                                        onSaveConfig={(newDates) => {
                                            actions.saveConfig({ 
                                                id: 'perception_dates', // ID fixo para recuperar depois
                                                name: 'Configuração de Prazos Gestão', // Nome para identificação
                                                ...newDates 
                                            });
                                        }}
                                        
                                        onSaveQuestions={(newQuestionsList) => {
                                            newQuestionsList.forEach(q => savePerceptionQuestion(q));
                                        }}
                                        isDarkMode={isDarkMode}
                                    />
                                {/* --- MODAL GENÉRICO DE SYNC (CORRIGIDO) --- */}
                                            {syncModalConfig.isOpen && (
                                                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                                                    <div className={`p-6 rounded-xl w-96 shadow-2xl animate-fade-in space-y-4 ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white'}`}>
                                                        <h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                                            <RefreshCw size={18}/> {syncModalConfig.title}
                                                        </h3>
                                                        <div>
                                                            <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                                                                Ano Referência
                                                            </label>
                                                            <Input 
                                                                type="number" 
                                                                value={batchYearInput} 
                                                                onChange={e => setBatchYearInput(e.target.value)} 
                                                                className={`text-center text-lg tracking-widest font-bold ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`} 
                                                                maxLength={4}
                                                            />
                                                        </div>
                                                        <div className="flex justify-end gap-2 pt-2">
                                                            <Button variant="secondary" onClick={() => setSyncModalConfig(prev => ({...prev, isOpen: false}))} disabled={isProcessingBatch} className={isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600' : ''}>Cancelar</Button>
                                                            <Button onClick={() => syncModalConfig.callback(batchYearInput)} disabled={isProcessingBatch} className="bg-blue-600 hover:bg-blue-700 text-white">
                                                                {isProcessingBatch ? <><Loader2 size={16} className="animate-spin mr-2"/> Processando...</> : "Confirmar"}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                {/* MODAL DE CONFIRMAÇÃO CUSTOMIZADO */}
                {confirmModal.isOpen && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setConfirmModal({ isOpen: false })}>
                        <div className={`p-6 rounded-xl w-96 shadow-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex items-center gap-3 mb-4 text-blue-600">
                                <div className={`p-2 rounded-full ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                                    <CheckCircle size={24}/>
                                </div>
                                <h3 className="font-bold text-lg">{confirmModal.title}</h3>
                            </div>
                            <p className={`text-sm mb-6 whitespace-pre-wrap leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                {confirmModal.message}
                            </p>
                            <div className="flex justify-end gap-2">
                                <Button variant="secondary" onClick={() => setConfirmModal({ isOpen: false })} className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}>Cancelar</Button>
                                <Button onClick={confirmModal.onConfirm} className="bg-blue-600 hover:bg-blue-700 text-white shadow-md border-0">Confirmar</Button>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* --- MODAL DE EDIÇÃO EM BLOCO DE PESSOAS (COLE ISTO NO FINAL) --- */}
                {isBulkCollabModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                        <div className={`rounded-xl w-full max-w-6xl shadow-2xl flex flex-col h-[90vh] overflow-hidden ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`}>
                            
                            {/* HEADER */}
                            <div className={`p-5 border-b flex justify-between items-center shrink-0 ${isDarkMode ? 'border-slate-700 bg-slate-900' : 'border-gray-200 bg-gray-50'}`}>
                                <div>
                                    <h3 className="font-bold text-xl flex items-center gap-2"><Users size={24} className="text-orange-500"/> Edição em Bloco de Pessoas</h3>
                                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Selecione os colaboradores na lista e defina os novos valores ao lado.</p>
                                </div>
                                <button onClick={() => setIsBulkCollabModalOpen(false)} className="p-2 hover:bg-black/10 rounded-full transition-colors"><X size={24}/></button>
                            </div>

                            <div className="flex flex-1 overflow-hidden">
                                
                                {/* COLUNA ESQUERDA: LISTA (40%) */}
                                <div className={`w-[40%] border-r flex flex-col ${isDarkMode ? 'border-slate-700 bg-slate-900/50' : 'border-gray-200 bg-gray-50'}`}>
                                    <div className="p-4 border-b border-inherit space-y-3">
                                        <div className="flex justify-between items-center">
                                            <h4 className="font-bold text-sm uppercase text-blue-500">1. Seleção</h4>
                                            <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{bulkCollabSelectedIds.length} selecionados</span>
                                        </div>
                                        
                                        <div className="grid grid-cols-2 gap-2">
                                            <select className={`p-2 text-xs rounded border ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white'}`} value={bulkCollabFilters.dept} onChange={e=>setBulkCollabFilters({...bulkCollabFilters, dept: e.target.value})}>
                                                <option value="">Todos Deptos</option>
                                                {data.departments.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                                            </select>
                                            <select className={`p-2 text-xs rounded border ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white'}`} value={bulkCollabFilters.type} onChange={e=>setBulkCollabFilters({...bulkCollabFilters, type: e.target.value})}>
                                                <option value="Todos">Todos Tipos</option>
                                                <option value="Colaborador">Colaborador</option>
                                                <option value="Gestão">Gestão</option>
                                            </select>
                                        </div>
                                        <input type="text" placeholder="Buscar por nome..." className={`w-full p-2 text-xs rounded border ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`} value={bulkCollabFilters.search} onChange={e=>setBulkCollabFilters({...bulkCollabFilters, search: e.target.value})} />
                                        
                                        <button onClick={() => {
                                            if (bulkCollabSelectedIds.length === filteredCollabsForBulk.length && filteredCollabsForBulk.length > 0) {
                                                setBulkCollabSelectedIds([]);
                                            } else {
                                                setBulkCollabSelectedIds(filteredCollabsForBulk.map(c => c.id));
                                            }
                                        }} className="text-xs font-bold text-blue-500 hover:underline w-full text-left">
                                            {(bulkCollabSelectedIds.length === filteredCollabsForBulk.length && filteredCollabsForBulk.length > 0) ? "Desmarcar Todos" : "Selecionar Todos da Lista"}
                                        </button>
                                    </div>

                                    <div className="flex-1 overflow-y-auto p-2 custom-scrollbar space-y-1">
                                        {filteredCollabsForBulk.map(c => (
                                            <label key={c.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${bulkCollabSelectedIds.includes(c.id) ? (isDarkMode ? 'bg-blue-900/40 border border-blue-800' : 'bg-blue-50 border border-blue-200') : (isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-white')}`}>
                                                <input type="checkbox" checked={bulkCollabSelectedIds.includes(c.id)} onChange={() => toggleBulkCollabSelect(c.id)} className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"/>
                                                <div className="overflow-hidden">
                                                    <div className="text-xs font-bold truncate">{c.name}</div>
                                                    <div className={`text-[10px] truncate ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{c.personType} • {data.departments.find(d=>d.id===c.departmentId)?.name || '-'}</div>
                                                </div>
                                            </label>
                                        ))}
                                        {filteredCollabsForBulk.length === 0 && <p className="text-center text-xs opacity-50 p-4">Nenhum colaborador encontrado.</p>}
                                    </div>
                                </div>

                                {/* COLUNA DIREITA: FORMULÁRIO (60%) */}
                                <div className="flex-1 flex flex-col overflow-y-auto custom-scrollbar p-6 bg-opacity-50">
                                    <h4 className="font-bold text-sm uppercase text-orange-500 mb-4 border-b pb-2">2. Dados para Alteração</h4>
                                    <div className={`p-3 rounded border mb-6 text-xs flex items-start gap-2 ${isDarkMode ? 'bg-yellow-900/20 border-yellow-800 text-yellow-200' : 'bg-yellow-50 border-yellow-200 text-yellow-800'}`}>
                                        <AlertTriangle size={16} className="shrink-0 mt-0.5"/>
                                        <span><b>Atenção:</b> Campos vazios ou mantidos como "Manter Original" NÃO alterarão os dados atuais dos colaboradores.</span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-6">
                                        <div className="col-span-2">
                                            <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Departamento</label>
                                            <select className={`w-full p-2.5 rounded border text-sm ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`} value={bulkCollabForm.departmentId} onChange={e=>setBulkCollabForm({...bulkCollabForm, departmentId: e.target.value})}>
                                                <option value="">(Manter Original)</option>
                                                {data.departments.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Tipo de Pessoa</label>
                                            <select className={`w-full p-2.5 rounded border text-sm ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`} value={bulkCollabForm.personType} onChange={e=>setBulkCollabForm({...bulkCollabForm, personType: e.target.value})}>
                                                <option value="">(Manter Original)</option>
                                                <option value="Colaborador">Colaborador</option>
                                                <option value="Gestão">Gestão</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Empresa Vinculada</label>
                                            <input type="text" placeholder="(Manter Original)" className={`w-full p-2.5 rounded border text-sm ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`} value={bulkCollabForm.companyName} onChange={e=>setBulkCollabForm({...bulkCollabForm, companyName: e.target.value})}/>
                                        </div>
                                        <div>
                                            <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>CNPJ Empresa</label>
                                            <input type="text" placeholder="(Manter Original)" className={`w-full p-2.5 rounded border text-sm ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`} value={bulkCollabForm.companyCNPJ} onChange={e=>setBulkCollabForm({...bulkCollabForm, companyCNPJ: e.target.value})}/>
                                        </div>
                                        <div className="col-span-2 border-t pt-4 mt-2 dark:border-slate-700">
                                            <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Regra de Avaliação</label>
                                            <select className={`w-full p-2.5 rounded border text-sm ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`} value={bulkCollabForm.evalTypeId} onChange={e=>setBulkCollabForm({...bulkCollabForm, evalTypeId: e.target.value})}>
                                                <option value="">(Manter Original)</option>
                                                {data.evaluationTypes.map(t=><option key={t.id} value={t.id}>{t.name} ({t.personType})</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Início Avaliação</label>
                                            <input type="date" className={`w-full p-2.5 rounded border text-sm ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white invert-calendar' : 'bg-white'}`} value={bulkCollabForm.startDate} onChange={e=>setBulkCollabForm({...bulkCollabForm, startDate: e.target.value})}/>
                                        </div>
                                        <div>
                                            <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Fim Avaliação</label>
                                            <input type="date" className={`w-full p-2.5 rounded border text-sm ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white invert-calendar' : 'bg-white'}`} value={bulkCollabForm.endDate} onChange={e=>setBulkCollabForm({...bulkCollabForm, endDate: e.target.value})}/>
                                        </div>
                                        <div className="col-span-2 border-t pt-4 mt-2 dark:border-slate-700">
                                            <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Regra de Jornada</label>
                                            <select className={`w-full p-2.5 rounded border text-sm ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={bulkCollabForm.workRuleId} onChange={e=>setBulkCollabForm({...bulkCollabForm, workRuleId: e.target.value})}>
                                                <option value="">(Manter Original)</option>
                                                <option value="padrao">Jornada Padrão</option>
                                                {(Array.isArray(data.pontoConfig) ? data.pontoConfig : []).map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
                                            </select>
                                        </div>
                                        
                                        {/* NOVOS CAMPOS: EXCEÇÕES E PRIVILÉGIOS DE PONTO */}
                                        <div className="col-span-2">
                                            <h4 className={`text-xs font-bold mb-3 mt-2 uppercase tracking-wider ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>Exceções e Privilégios (Ponto)</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div>
                                                    <label className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Aprovação Manual</label>
                                                    <select className={`w-full p-2.5 rounded border text-xs outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={bulkCollabForm.skipPunchApproval} onChange={e=>setBulkCollabForm({...bulkCollabForm, skipPunchApproval: e.target.value})}>
                                                        <option value="">(Manter Original)</option>
                                                        <option value="true">Dispensar Aprovação</option>
                                                        <option value="false">Exigir Aprovação (Padrão)</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Notificar Justificativas</label>
                                                    <select className={`w-full p-2.5 rounded border text-xs outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={bulkCollabForm.skipJustificationNotify} onChange={e=>setBulkCollabForm({...bulkCollabForm, skipJustificationNotify: e.target.value})}>
                                                        <option value="">(Manter Original)</option>
                                                        <option value="true">Não Notificar RH/Gestor</option>
                                                        <option value="false">Notificar (Padrão)</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Crítica de Atrasos</label>
                                                    <select className={`w-full p-2.5 rounded border text-xs outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={bulkCollabForm.skipDelayCritique} onChange={e=>setBulkCollabForm({...bulkCollabForm, skipDelayCritique: e.target.value})}>
                                                        <option value="">(Manter Original)</option>
                                                        <option value="true">Ignorar Tolerâncias</option>
                                                        <option value="false">Criticar Atrasos (Padrão)</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                
                            {/* --- BLOCO ACADCOINS NA EDIÇÃO EM MASSA --- */}
                                    <div className={`col-span-2 border-t pt-4 mt-2 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                        <label className="flex items-center gap-2 font-bold text-sm cursor-pointer mb-2">
                                            <input type="checkbox" checked={editAcadCoins} onChange={e => setEditAcadCoins(e.target.checked)} className="w-4 h-4" />
                                            Alterar Vínculo AcadCoins
                                        </label>
                                        {editAcadCoins && (
                                            <div className="mt-4 space-y-4 pl-6 border-l-2 border-blue-500">
                                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                                    <input type="checkbox" checked={bulkAcadCoins.participates} onChange={e => setBulkAcadCoins({...bulkAcadCoins, participates: e.target.checked})} className="w-4 h-4 accent-blue-600" />
                                                    Tornar Elegível ao Programa
                                                </label>
                                                {bulkAcadCoins.participates && (
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="text-xs font-bold block mb-1">Nova Data de Início</label>
                                                            <input type="date" value={bulkAcadCoins.startDate} onChange={e => setBulkAcadCoins({...bulkAcadCoins, startDate: e.target.value})} className={`w-full p-2.5 rounded border text-sm ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white invert-calendar' : 'bg-white border-gray-300'}`} />
                                                        </div>
                                                        <div>
                                                            <label className="text-xs font-bold block mb-1">Nova Data Final</label>
                                                            <input type="date" value={bulkAcadCoins.endDate} onChange={e => setBulkAcadCoins({...bulkAcadCoins, endDate: e.target.value})} className={`w-full p-2.5 rounded border text-sm ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white invert-calendar' : 'bg-white border-gray-300'}`} />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div> 
                                </div>

                            {/* FOOTER */}
                            <div className={`p-4 border-t flex justify-between items-center gap-4 shrink-0 ${isDarkMode ? 'border-slate-700 bg-slate-900' : 'border-gray-200 bg-gray-50'}`}>
                                <div className="text-xs opacity-60">* Verifique a seleção antes de aplicar.</div>
                                <div className="flex gap-2">
                                    <Button variant="secondary" onClick={() => setIsBulkCollabModalOpen(false)}>Cancelar</Button>
                                    <Button onClick={executeBulkCollabUpdate} className="bg-orange-600 hover:bg-orange-700 text-white shadow-md">Aplicar Alterações</Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                </div>
                );
    };

    /**
     * ============================================================================
     * COMPONENTE: APONTAMENTOS SYSTEM (LAYOUT AJUSTADO: LINHA ÚNICA + CARDS FIXOS)
     * ============================================================================
     */


    /* ===============================================================================
     * [END_MOD_CADASTROS_SYSTEM]
     * FIM DO BLOCO: CadastrosSystem
     * [START_MOD_APONTAMENTOS_SYSTEM]
     * INICIO DO BLOCO: ApontamentosSystem
     * =============================================================================== */
    const ApontamentosSystem = ({ 
        data, 
        actions, 
        permissions, 
        currentUser, 
        isDarkMode 
    }) => {
        
        
        // Preferências Salvas
        const prefs = currentUser?.preferences?.apt || {};

    // ========================================================================
        // 1. ESTADOS E PERSISTÊNCIA DUPLA (CACHE + BANCO)
        // ========================================================================
        
        // Função inteligente que lê do Cache PRIMEIRO, depois do Banco, depois Default
        const getInitialDate = (cacheKey, dbKey, defaultFunc) => {
            const cached = localStorage.getItem(cacheKey);
            if (cached) return cached;
            const dbPref = currentUser?.preferences?.apt?.[dbKey];
            if (dbPref) return dbPref;
            return defaultFunc();
        };

        const [searchTerm, setSearchTerm] = useState('');
        
        const [filterStartDate, setFilterStartDate] = useState(() => 
            getInitialDate('@acad_apt_start', 'filterStartDate', () => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0])
        );
        
        const [filterEndDate, setFilterEndDate] = useState(() => 
            getInitialDate('@acad_apt_end', 'filterEndDate', () => new Date().toISOString().split('T')[0])
        );

        const [filterCollabId, setFilterCollabId] = useState('');

        // Efeito: Salva as datas simultaneamente no navegador e no banco
        React.useEffect(() => {
            // 1. Salva imediatamente no Cache Local (Garante que a conta Teste preserve a data ao mudar de aba)
            if (filterStartDate) localStorage.setItem('@acad_apt_start', filterStartDate);
            if (filterEndDate) localStorage.setItem('@acad_apt_end', filterEndDate);

            // 2. Salva no Banco de Dados (Para contas reais acessarem de outros PCs)
            const timer = setTimeout(() => {
                if (currentUser && actions.saveUserPreferences) {
                    const newAptPrefs = { filterStartDate, filterEndDate };
                    if (JSON.stringify(currentUser?.preferences?.apt) !== JSON.stringify(newAptPrefs)) {
                        actions.saveUserPreferences({ apt: newAptPrefs });
                    }
                }
            }, 1000);
            
            return () => clearTimeout(timer);
        }, [filterStartDate, filterEndDate, currentUser, actions]);
        
        // Filtros Solicitados (D)
        const [filterType, setFilterType] = useState(''); // Classificação (Nome da Ocorrência)
        const [filterWeight, setFilterWeight] = useState(''); // Peso
        const [filterConfType, setFilterConfType] = useState(''); // Tipo Conferência

        const [filteredQuanti, setFilteredQuanti] = useState(data.quantiCriteria);
        const [enhancing, setEnhancing] = useState(false);
        
        const [editingId, setEditingId] = useState(null);
        const [editForm, setEditForm] = useState({});
        
        const [selectedIds, setSelectedIds] = useState([]);
        const [modalConfig, setModalConfig] = useState(null); 

        // Estados para Qtde e Duplicação
        const [isBulkMode, setIsBulkMode] = useState(false); 
        const [bulkQty, setBulkQty] = useState(2); 
        const [dupModalOpen, setDupModalOpen] = useState(false); 
        const [dupTargetId, setDupTargetId] = useState(null); 
        const [dupCount, setDupCount] = useState(2); 

        const [newEntry, setNewEntry] = useState({ 
            collaboratorId: '', 
            date: new Date().toISOString().split('T')[0], 
            type: '', 
            conferenciaType: '', 
            weight: 0, 
            description: '',
            qtde: 1 
        });

        // Paleta de Cores para os Cards
        const TYPE_COLORS = [
            'from-blue-500 to-cyan-500', 
            'from-purple-500 to-fuchsia-500', 
            'from-emerald-500 to-teal-400',
            'from-orange-500 to-amber-400', 
            'from-pink-500 to-rose-400', 
            'from-indigo-500 to-violet-500',
            'from-red-500 to-orange-500', 
            'from-teal-500 to-emerald-400'
        ];

        // ========================================================================
        // 2. MEMOIZATION E EFEITOS
        // ========================================================================

        const visibleCollaborators = useMemo(() => {
            return data.collaborators.filter(c => 
                canViewCollab(c, currentUser) && c.personType === 'Colaborador'
            );
        }, [data.collaborators, currentUser]);

        // Atualiza lista de critérios disponíveis no dropdown de cadastro
        useEffect(() => {
            const targetId = editingId ? editForm.collaboratorId : newEntry.collaboratorId;
            if(!targetId) { 
                setFilteredQuanti(data.quantiCriteria.filter(c => c.personType !== 'Gestão')); 
                return; 
            }
            const c = data.collaborators.find(x => x.id === targetId);
            if(c && c.evalTypeId) {
                const et = data.evaluationTypes.find(t => t.id === c.evalTypeId);
                if(et && et.selectedQuantiIds?.length > 0) { 
                    setFilteredQuanti(data.quantiCriteria.filter(q => et.selectedQuantiIds.includes(q.id))); 
                    return; 
                }
            }
            setFilteredQuanti(data.quantiCriteria.filter(c => c.personType !== 'Gestão'));
        }, [newEntry.collaboratorId, editingId, editForm.collaboratorId, data.quantiCriteria, data.collaborators, data.evaluationTypes]);

        const getFilteredConfTypes = (collabId, dateStr) => {
            if (!collabId || !dateStr) return [];
            const targetCollab = data.collaborators.find(c => c.id === collabId);
            if (!targetCollab) return [];
            const targetPersonType = targetCollab.personType || 'Colaborador';
            const entryDate = new Date(dateStr); 
            return (data.conferenciaTypes || []).filter(t => {
                const deptMatch = t.departmentId === 'all' || t.departmentId === currentUser.departmentId;
                const personMatch = t.personType === targetPersonType;
                let dateValid = true;
                if (t.startDate && new Date(t.startDate) > entryDate) dateValid = false;
                if (t.endDate && new Date(t.endDate) < entryDate) dateValid = false;
                return deptMatch && personMatch && dateValid;
            });
        };

        // Lógica de Filtragem da Tabela
        const filteredHistory = useMemo(() => {
            return data.apontamentos.slice().sort((a, b) => new Date(b.date) - new Date(a.date)).filter(ap => {
                const c = data.collaborators.find(x => x.id === ap.collaboratorId);
                if (!canViewCollab(c, currentUser) || c?.personType !== 'Colaborador') return false;
                
                // Filtros de Data
                if (filterStartDate && ap.date < filterStartDate) return false;
                if (filterEndDate && ap.date > filterEndDate) return false;
                
                // Filtros Específicos
                if (filterCollabId && ap.collaboratorId !== filterCollabId) return false;
                if (filterType && ap.type !== filterType) return false; // Classificação
                if (filterWeight && String(ap.weight) !== String(filterWeight)) return false; // Peso
                if (filterConfType && (ap.conferenciaType || '') !== filterConfType) return false; // Tipo Conf.

                // Busca Livre
                const s = searchTerm.toLowerCase();
                const cName = (c?.name || '').toLowerCase();
                return cName.includes(s) || ap.description.toLowerCase().includes(s) || ap.type.toLowerCase().includes(s) || (ap.conferenciaType || '').toLowerCase().includes(s);
            });
        }, [data.apontamentos, data.collaborators, searchTerm, currentUser, filterStartDate, filterEndDate, filterCollabId, filterType, filterWeight, filterConfType]);

        // Estatísticas para os Cards do Topo
        const cardsStats = useMemo(() => {
            const stats = data.quantiCriteria.filter(q => q.personType !== 'Gestão').map((crit, idx) => {
                // Conta no histórico filtrado pelas datas e pessoas (mas ignora o filtro de Tipo do card para mostrar todos os disponíveis)
                const baseMatches = data.apontamentos.filter(ap => {
                    const c = data.collaborators.find(x => x.id === ap.collaboratorId);
                    if (!canViewCollab(c, currentUser) || c?.personType !== 'Colaborador') return false;
                    if (filterStartDate && ap.date < filterStartDate) return false;
                    if (filterEndDate && ap.date > filterEndDate) return false;
                    if (filterCollabId && ap.collaboratorId !== filterCollabId) return false;
                    
                    // Aplica filtros de Peso e Conf, mas NÃO o de Tipo (pois os cards SÃO o filtro de tipo)
                    if (filterWeight && String(ap.weight) !== String(filterWeight)) return false;
                    if (filterConfType && (ap.conferenciaType || '') !== filterConfType) return false;
                    
                    return ap.type === crit.name;
                });

                const count = baseMatches.reduce((acc, curr) => acc + (parseInt(curr.qtde) || 1), 0);
                const points = baseMatches.reduce((acc, curr) => acc + (parseFloat(curr.weight) * (parseInt(curr.qtde) || 1)), 0);
                
                return {
                    id: crit.id,
                    name: crit.name,
                    count,
                    points,
                    color: TYPE_COLORS[idx % TYPE_COLORS.length]
                };
            });

            // REGRA 2.a: Só exibir cards com registro > 0
            return stats.filter(s => s.count > 0);

        }, [data.quantiCriteria, data.apontamentos, filterStartDate, filterEndDate, filterCollabId, filterWeight, filterConfType, currentUser]);

        // Helpers de Bloqueio
        const checkLockedEvaluation = (apontamentoId) => {
            const ap = data.apontamentos.find(a => a.id === apontamentoId);
            if (!ap) return null;
            return checkLockedEvaluationByDate(ap.collaboratorId, ap.date);
        };

        const checkLockedEvaluationByDate = (collabId, dateStr) => {
            const dateParts = dateStr.split('-');
            const year = dateParts[0];
            const month = parseInt(dateParts[1]);
            let quarter = month <= 3 ? 'q1' : month <= 6 ? 'q2' : month <= 9 ? 'q3' : 'q4';

            const ev = data.evaluations.find(e => e.collaboratorId === collabId && e.periodYear === year);
            if (!ev) return null;

            if (ev.status === 'Finalizada' || ev.status === 'T4-Concluido') return `Avaliação ${year} finalizada.`;
            
            const closedMap = { 'T1-Concluido': 1, 'T2-Concluido': 2, 'T3-Concluido': 3 };
            const closedLevel = closedMap[ev.status] || 0;
            const currentQLevel = parseInt(quarter.replace('q', ''));

            if (closedLevel >= currentQLevel) return `Trimestre ${quarter.toUpperCase()} fechado.`;
            return null;
        };

        // ========================================================================
        // 4. HANDLERS E SINCRONIZAÇÃO ABSOLUTA
        // ========================================================================

        // --- MOTOR DE SINCRONIZAÇÃO AUTOMÁTICA ---
        // Recalcula todos os erros do zero sempre que houver qualquer alteração
        const syncEvalQuantitativo = async (collabId, year, nextAps) => {
            const ev = data.evaluations.find(e => e.collaboratorId === collabId && e.periodYear === String(year));
            if (!ev) return;
            
            const updEval = JSON.parse(JSON.stringify(ev));
            
            if (updEval.quantitative && updEval.quantitative.errors) {
                updEval.quantitative.errors = getSyncedQuantitativeErrors(updEval, nextAps, data.quantiCriteria, data.collaborators, data.evaluationTypes);
                const finalEv = recalculateFinalAverage(updEval);
                await actions.saveEvaluation(finalEv);
            }
        };

        const handleCriteriaChange = (e, isEdit) => {
            const tName = e.target.value;
            const t = filteredQuanti.find(q => q.name === tName);
            const weight = t ? t.peso : 0;
            if(isEdit) setEditForm({...editForm, type: tName, weight});
            else setNewEntry({...newEntry, type: tName, weight});
        };

        const handleEnhance = async (text, isEdit) => {
            if (!text || text.length < 3) return alert("Digite o texto.");
            setEnhancing(true);
            try {
                const systemPrompt = `Você é um Consultor de RH. Reescreva a ocorrência de forma objetiva, impessoal e formal.`;
                const res = await callGeminiAI(text, systemPrompt, 'revision');

                if (!isUsableAIText(res)) {
                    alert(`${res || 'A IA não retornou conteúdo válido.'}

O texto informado foi preservado.`);
                    return;
                }

                setModalConfig({
                    type: 'aiSuggestion',
                    title: 'Sugestão da IA',
                    message: res,
                    cancelText: 'Manter o que escrevi',
                    confirmText: 'Inserir avaliação da IA',
                    onConfirm: () => {
                        if (isEdit) setEditForm(prev => ({ ...prev, description: res }));
                        else setNewEntry(prev => ({ ...prev, description: res }));
                        setModalConfig(null);
                    }
                });
            } catch (error) {
                console.error('Erro ao melhorar ocorrência com IA:', error);
                alert('Falha ao acionar a IA. O texto informado foi preservado.');
            } finally {
                setEnhancing(false);
            }
        };

        const handleSaveNew = async () => {
            if (!newEntry.collaboratorId) return alert("Selecione um Colaborador.");
            if (!newEntry.date) return alert("Informe a Data.");
            if (!newEntry.type) return alert("Selecione o Fato.");

            const lockMsg = checkLockedEvaluationByDate(newEntry.collaboratorId, newEntry.date);
            if (lockMsg) {
                setModalConfig({ type: 'alert', title: 'Ação Bloqueada', message: `${lockMsg}\n\nPara incluir, reabra o trimestre na avaliação.` });
                return;
            }

            const qtdRegistros = isBulkMode ? parseInt(bulkQty) : 1;
            const qtdErroPorRegistro = parseInt(newEntry.qtde) || 1;

            const entriesToSave = [];
            for (let i = 0; i < qtdRegistros; i++) {
                entriesToSave.push({
                    ...newEntry,
                    qtde: qtdErroPorRegistro,
                    id: Date.now().toString() + Math.random().toString(36).substr(2, 5)
                });
            }

            entriesToSave.forEach(entry => actions.addApontamento(entry));

            // NOVA LÓGICA DE SYNC
            const year = newEntry.date.split('-')[0];
            const nextAps = [...data.apontamentos, ...entriesToSave];
            await syncEvalQuantitativo(newEntry.collaboratorId, year, nextAps);

            setNewEntry({ collaboratorId: '', date: new Date().toISOString().split('T')[0], type: '', conferenciaType: '', weight: 0, description: '', qtde: 1 });
            setIsBulkMode(false);
            setBulkQty(2);
        };

        const handleSaveEdit = async () => {
            if(!editForm.collaboratorId || !editForm.date || !editForm.type) return;

            // Verifica se a avaliação original estava bloqueada (antes de editar data/nome)
            const oldAp = data.apontamentos.find(a => a.id === editForm.id);
            if (oldAp) {
                const lockOld = checkLockedEvaluationByDate(oldAp.collaboratorId, oldAp.date);
                if (lockOld) return setModalConfig({ type: 'alert', title: 'Edição Bloqueada', message: `O período original da ocorrência está fechado.\n\n${lockOld}` });
            }

            // Verifica se o novo período selecionado está bloqueado
            const lockNew = checkLockedEvaluationByDate(editForm.collaboratorId, editForm.date);
            if (lockNew) return setModalConfig({ type: 'alert', title: 'Edição Bloqueada', message: `O novo período de destino está fechado.\n\n${lockNew}` });

            actions.addApontamento(editForm);

            // NOVA LÓGICA DE SYNC: Simula a lista após a edição
            const nextAps = data.apontamentos.map(a => a.id === editForm.id ? editForm : a);
            const newYear = editForm.date.split('-')[0];
            
            await syncEvalQuantitativo(editForm.collaboratorId, newYear, nextAps);

            // Se o RH alterou o colaborador ou mudou o ano da ocorrência, limpa a avaliação antiga também
            if (oldAp && (oldAp.collaboratorId !== editForm.collaboratorId || oldAp.date.split('-')[0] !== newYear)) {
                const oldYear = oldAp.date.split('-')[0];
                await syncEvalQuantitativo(oldAp.collaboratorId, oldYear, nextAps);
            }

            setEditingId(null);
        };

        const startEdit = (ap) => { 
            const lockMsg = checkLockedEvaluation(ap.id);
            if (lockMsg) {
                setModalConfig({ type: 'alert', title: 'Edição Bloqueada', message: `${lockMsg}\n\nPara editar, a avaliação deve ser reaberta.` });
                return;
            }
            setEditingId(ap.id); 
            setEditForm(ap); 
        };
        
        const cancelEdit = () => { setEditingId(null); setEditForm({}); };

        const toggleSelect = (id) => {
            if (selectedIds.includes(id)) setSelectedIds(prev => prev.filter(i => i !== id));
            else setSelectedIds(prev => [...prev, id]);
        };

        const toggleSelectAll = () => {
            const visibleIds = filteredHistory.map(h => h.id);
            const allSelected = visibleIds.every(id => selectedIds.includes(id));
            if (allSelected) setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
            else setSelectedIds(prev => [...new Set([...prev, ...visibleIds])]);
        };

        const confirmBulkDelete = () => {
            const lockedItems = [];
            selectedIds.forEach(id => {
                const msg = checkLockedEvaluation(id);
                if (msg) lockedItems.push(msg);
            });

            if (lockedItems.length > 0) {
                const uniqueMsgs = [...new Set(lockedItems)];
                setModalConfig({ type: 'alert', title: 'Exclusão Bloqueada', message: `Existem itens em períodos fechados:\n\n${uniqueMsgs.map(m => "• " + m).join('\n')}` });
                return;
            }

            setModalConfig({
                type: 'confirm',
                title: 'Confirmar Exclusão',
                message: `Deseja excluir ${selectedIds.length} ocorrências permanentemente?`,
                onConfirm: async () => {
                    const nextAps = data.apontamentos.filter(a => !selectedIds.includes(a.id));
                    const collabsToSync = new Set();
                    
                    selectedIds.forEach(id => {
                        const ap = data.apontamentos.find(a => a.id === id);
                        if (ap) collabsToSync.add(`${ap.collaboratorId}|${ap.date.split('-')[0]}`);
                        actions.delApontamento(id);
                    });

                    if (actions.logAction) actions.logAction('EXCLUSAO_EM_BLOCO_OCORRENCIAS', { qtd: selectedIds.length });
                    
                    // Sincroniza e recalcula as avaliações de todos os afetados
                    for (const item of Array.from(collabsToSync)) {
                        const [cId, yr] = item.split('|');
                        await syncEvalQuantitativo(cId, yr, nextAps);
                    }

                    setSelectedIds([]);
                    setModalConfig(null);
                }
            });
        };
        
        const handleConfirmDuplication = async () => {
            if (!dupTargetId) return;
            const original = data.apontamentos.find(a => a.id === dupTargetId);
            if (!original) return;
            const copies = parseInt(dupCount);
            if (isNaN(copies) || copies < 1) return alert("Valor inválido.");
            const lockMsg = checkLockedEvaluationByDate(original.collaboratorId, original.date);
            if (lockMsg) {
                setDupModalOpen(false);
                setModalConfig({ type: 'alert', title: 'Ação Bloqueada', message: lockMsg });
                return;
            }

            const entriesToSave = [];
            for (let i = 0; i < copies; i++) {
                entriesToSave.push({ ...original, id: Date.now().toString() + Math.random().toString(36).substr(2, 5) });
            }
            
            entriesToSave.forEach(e => actions.addApontamento(e));

            // NOVA LÓGICA DE SYNC
            const nextAps = [...data.apontamentos, ...entriesToSave];
            const year = original.date.split('-')[0];
            await syncEvalQuantitativo(original.collaboratorId, year, nextAps);
            
            setDupModalOpen(false); 
            setDupTargetId(null);
        };

        const handleDeleteSingle = (id) => {
            const lockMsg = checkLockedEvaluation(id);
            if (lockMsg) {
                setModalConfig({ type: 'alert', title: 'Bloqueado', message: lockMsg });
                return;
            }
            setModalConfig({
                type: 'confirm', title: 'Excluir', message: 'Excluir esta ocorrência permanentemente?',
                onConfirm: async () => {
                    const ap = data.apontamentos.find(a => a.id === id);
                    actions.delApontamento(id);
                    
                    if (ap) {
                        // NOVA LÓGICA DE SYNC
                        const nextAps = data.apontamentos.filter(a => a.id !== id);
                        const year = ap.date.split('-')[0];
                        await syncEvalQuantitativo(ap.collaboratorId, year, nextAps);
                    }
                    setModalConfig(null);
                }
            });
        };

        const getWeightColor = (w) => {
            const val = parseFloat(w);
            if (val >= 5) return isDarkMode ? 'bg-red-900/40 text-red-300 border-red-800' : 'bg-red-100 text-red-700 border-red-200';
            if (val >= 3) return isDarkMode ? 'bg-orange-900/40 text-orange-300 border-orange-800' : 'bg-orange-100 text-orange-700 border-orange-200';
            return isDarkMode ? 'bg-emerald-900/40 text-emerald-300 border-emerald-800' : 'bg-emerald-100 text-emerald-700 border-emerald-200';
        };

        // ========================================================================
        // 5. RENDERIZAÇÃO
        // ========================================================================
        return (
            <div className="flex flex-col h-[calc(100vh-100px)] animate-fade-in relative space-y-6">

                {/* --- 1. CARDS DE TOPO (REGRA 1: Layout Flex Wrap + Tamanho Fixo + Sem rolagem) --- */}
                {cardsStats.length > 0 && (
                    <div className="flex flex-wrap gap-2 pb-2 shrink-0">
                        {cardsStats.map((stat, idx) => {
                            const isSelected = filterType === stat.name;
                            return (
                                <div 
                                    key={idx} 
                                    onClick={() => setFilterType(isSelected ? '' : stat.name)} 
                                    className={`
                                        w-36 h-24 p-2 rounded-xl shadow-lg relative overflow-hidden text-white group transition-all cursor-pointer border-2
                                        ${isSelected ? 'ring-4 ring-offset-2 ring-blue-500 scale-105 z-10' : 'hover:-translate-y-1 hover:shadow-xl'}
                                        bg-gradient-to-br ${stat.color}
                                        border-transparent flex flex-col justify-between
                                    `}
                                >
                                    <div className="relative z-10">
                                        <h4 className="font-bold text-[10px] uppercase tracking-wide opacity-90 truncate leading-tight mb-1" title={stat.name}>{stat.name}</h4>
                                        <div className="text-2xl font-black">{stat.count}</div>
                                    </div>
                                    <div className="flex items-center gap-1 text-[9px] font-medium bg-white/20 px-1.5 py-0.5 rounded-full w-fit backdrop-blur-sm relative z-10">
                                        <AlertTriangle size={8} className="fill-white"/> {stat.points.toFixed(1)} pts
                                    </div>
                                    
                                    {/* Ícone Decorativo */}
                                    <ClipboardCheck size={48} className="absolute -bottom-3 -right-3 opacity-10 rotate-12 group-hover:scale-110 transition-transform pointer-events-none"/>
                                    {isSelected && <div className="absolute top-1 right-1 bg-white text-blue-600 rounded-full p-0.5"><Check size={10} strokeWidth={4}/></div>}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* --- 2. PAINEL DE INCLUSÃO (REGRA 2: LINHA ÚNICA) --- */}
                <div className={`p-1 rounded-2xl shadow-lg border relative overflow-visible shrink-0 z-20 transition-colors ${isDarkMode ? 'bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700' : 'bg-gradient-to-br from-white to-gray-50 border-blue-100'}`}>
                    <div className={`absolute -top-3 left-6 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border shadow-sm ${isDarkMode ? 'bg-slate-900 text-blue-400 border-blue-500' : 'bg-blue-600 text-white border-blue-700'}`}>
                        <Plus size={10} className="inline mr-1"/> Novo Lançamento
                    </div>

                    <div className="flex flex-wrap items-end gap-2 p-3 pt-5">
                        
                        {/* Colaborador */}
                        <div className="w-[200px]">
                            <label className={`text-[9px] font-bold uppercase block mb-1 ${isDarkMode?'text-slate-400':'text-gray-500'}`}>Colaborador</label>
                            <select 
                                className={`w-full h-8 px-2 border rounded-lg text-xs font-medium outline-none transition-all ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white focus:border-blue-500' : 'bg-gray-50 border-gray-200 text-gray-700 focus:border-blue-500 focus:bg-white'}`}
                                value={newEntry.collaboratorId} 
                                onChange={e=>setNewEntry({...newEntry, collaboratorId:e.target.value})}
                            >
                                <option value="">Selecione...</option>
                                {visibleCollaborators.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>

                        {/* Data */}
                        <div className="w-[110px]">
                            <label className={`text-[9px] font-bold uppercase block mb-1 ${isDarkMode?'text-slate-400':'text-gray-500'}`}>Data</label>
                            <input type="date" className={`w-full h-8 px-2 border rounded-lg text-xs outline-none transition-all ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white invert-calendar' : 'bg-gray-50 border-gray-200 text-gray-700'}`} value={newEntry.date} onChange={e=>setNewEntry({...newEntry, date:e.target.value})} />
                        </div>

                        {/* Qtde */}
                        <div className="w-[50px]">
                            <label className={`text-[9px] font-bold uppercase block mb-1 ${isDarkMode?'text-slate-400':'text-gray-500'}`}>Qtd</label>
                            <input type="number" min="1" max="100" className={`w-full h-8 px-1 text-center border rounded-lg text-xs outline-none font-bold transition-all ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-purple-700'}`} value={newEntry.qtde} onChange={e => setNewEntry({...newEntry, qtde: e.target.value})}/>
                        </div>

                        {/* Classificação */}
                        <div className="w-[200px]">
                            <label className={`text-[9px] font-bold uppercase block mb-1 ${isDarkMode?'text-slate-400':'text-gray-500'}`}>Classificação (Ocorrência)</label>
                            <select className={`w-full h-8 px-2 border rounded-lg text-xs outline-none transition-all ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-700'}`} value={newEntry.type} onChange={(e)=>handleCriteriaChange(e, false)}>
                                <option value="">Selecione...</option>
                                {filteredQuanti.map(q=><option key={q.id} value={q.name}>{formatOccurrenceTerm(q.name)}</option>)}
                            </select>
                        </div>

                        {/* Tipo Conf */}
                        <div className="w-[140px]">
                            <label className={`text-[9px] font-bold uppercase block mb-1 ${isDarkMode?'text-slate-400':'text-gray-500'}`}>Tipo Conf.</label>
                            <input list="conferencia-options" className={`w-full h-8 px-2 border rounded-lg text-xs outline-none transition-all ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-500 focus:border-teal-500' : 'bg-gray-50 border-gray-200 text-gray-700 focus:border-teal-500'}`} placeholder="Ex: Entrada..." value={newEntry.conferenciaType} onChange={e => setNewEntry({...newEntry, conferenciaType: e.target.value})}/>
                            <datalist id="conferencia-options">{getFilteredConfTypes(newEntry.collaboratorId, newEntry.date).map(conf => (<option key={conf.id} value={conf.name} />))}</datalist>
                        </div>

                        {/* Descrição (Flex para ocupar o resto) */}
                        <div className="flex-1 min-w-[200px] relative">
                            <label className={`text-[9px] font-bold uppercase block mb-1 ${isDarkMode?'text-slate-400':'text-gray-500'}`}>Descrição</label>
                            <input className={`w-full h-8 pl-2 pr-8 border rounded-lg text-xs outline-none transition-all ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-700'}`} placeholder="Detalhes..." value={newEntry.description} onChange={e=>setNewEntry({...newEntry, description:e.target.value})}/>
                            <button onClick={()=>handleEnhance(newEntry.description, false)} className="absolute right-1 top-6 text-purple-500 hover:text-purple-700 disabled:opacity-50 p-1 transition-colors" disabled={enhancing} title="Melhorar texto com IA">{enhancing ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}</button>
                        </div>

                        {/* Checkbox Multi */}
                        <div className="flex items-center h-8 px-2 rounded border border-dashed border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-800/50">
                            <input type="checkbox" id="chkBulk" checked={isBulkMode} onChange={e => setIsBulkMode(e.target.checked)} className="cursor-pointer w-3 h-3 rounded text-purple-600"/>
                            <label htmlFor="chkBulk" className={`text-[10px] font-bold cursor-pointer select-none ml-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>x</label>
                            {isBulkMode && (<input type="number" min="2" max="50" value={bulkQty} onChange={e => setBulkQty(e.target.value)} className={`w-8 h-6 text-center text-[10px] font-bold rounded border ml-1 outline-none ${isDarkMode ? 'bg-slate-900 border-purple-500 text-white' : 'bg-white border-purple-300 text-purple-800'}`}/>)}
                        </div>

                        {/* Botão Salvar */}
                        <button onClick={handleSaveNew} className="h-8 px-4 bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-blue-500/30 rounded-lg flex items-center justify-center text-white transition-all transform hover:scale-105 font-bold text-xs">
                            <Save size={14} className="mr-1"/> Gravar
                        </button>
                    </div>
                </div>

                {/* --- 3. BARRA DE FILTROS E AÇÕES EM MASSA --- */}
                <div className={`p-4 rounded-xl shadow-sm border flex flex-col xl:flex-row gap-4 items-end ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                    
                    {/* Grupo de Filtros */}
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3 w-full">
                        {/* Filtros Solicitados (D) */}
                        <div className="md:col-span-2">
                            <label className={`text-[10px] font-bold uppercase block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Período</label>
                            <div className="flex gap-2">
                                <input type="date" className={`w-full p-2 rounded-lg border text-xs outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white invert-calendar' : 'bg-gray-50 border-gray-200'}`} value={filterStartDate} onChange={e=>setFilterStartDate(e.target.value)} />
                                <input type="date" className={`w-full p-2 rounded-lg border text-xs outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white invert-calendar' : 'bg-gray-50 border-gray-200'}`} value={filterEndDate} onChange={e=>setFilterEndDate(e.target.value)} />
                            </div>
                        </div>
                        <div>
                            <label className={`text-[10px] font-bold uppercase block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Colaborador</label>
                            <select className={`w-full p-2 rounded-lg border text-xs outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-gray-50 border-gray-200'}`} value={filterCollabId} onChange={e=>setFilterCollabId(e.target.value)}>
                                <option value="">Todos</option>
                                {visibleCollaborators.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        
                        {/* Filtro Classificação (Nome da Ocorrência) */}
                        <div>
                            <label className={`text-[10px] font-bold uppercase block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Classificação</label>
                            <select className={`w-full p-2 rounded-lg border text-xs outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-gray-50 border-gray-200'}`} value={filterType} onChange={e=>setFilterType(e.target.value)}>
                                <option value="">Todas</option>
                                {data.quantiCriteria.map(q=><option key={q.id} value={q.name}>{formatOccurrenceTerm(q.name)}</option>)}
                            </select>
                        </div>

                        {/* Filtro Peso (Novo) */}
                        <div>
                            <label className={`text-[10px] font-bold uppercase block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Peso</label>
                            <select className={`w-full p-2 rounded-lg border text-xs outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-gray-50 border-gray-200'}`} value={filterWeight} onChange={e=>setFilterWeight(e.target.value)}>
                                <option value="">Todos</option>
                                <option value="0.5">0.5</option>
                                <option value="1">1.0</option>
                                <option value="3">3.0</option>
                                <option value="5">5.0</option>
                                <option value="9">9.0</option>
                                <option value="11">11.0</option>
                            </select>
                        </div>

                        {/* Filtro Tipo Conferência (Novo) */}
                        <div>
                            <label className={`text-[10px] font-bold uppercase block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Tipo Conf.</label>
                            <select className={`w-full p-2 rounded-lg border text-xs outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-gray-50 border-gray-200'}`} value={filterConfType} onChange={e=>setFilterConfType(e.target.value)}>
                                <option value="">Todos</option>
                                {data.conferenciaTypes && data.conferenciaTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="w-full md:w-64 flex flex-col justify-end gap-2">
                        <div className="relative">
                            <label className={`text-[10px] font-bold uppercase block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Busca Livre</label>
                            <Search className={`absolute left-3 top-8 text-gray-400`} size={14} />
                            <input 
                                placeholder="Buscar..." 
                                value={searchTerm} 
                                onChange={e => setSearchTerm(e.target.value)} 
                                className={`pl-9 w-full p-2 rounded-lg border text-xs outline-none focus:ring-1 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white placeholder-slate-500' : 'bg-gray-50 border-gray-200'}`}
                            />
                        </div>
                    </div>

                    {/* Ação em Massa */}
                    {selectedIds.length > 0 && (
                        <div className="flex items-center gap-3 animate-fade-in bg-red-50 dark:bg-red-900/20 px-4 py-2 rounded-xl border border-red-100 dark:border-red-800 w-full xl:w-auto justify-between xl:justify-start h-[58px]">
                            <span className={`text-xs font-bold ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                                {selectedIds.length} selecionados
                            </span>
                            <button onClick={confirmBulkDelete} className="flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-lg shadow-sm transition-colors bg-red-600 hover:bg-red-700 text-white">
                                <Trash2 size={14}/> Excluir
                            </button>
                        </div>
                    )}
                </div>

            {/* --- TABELA DE HISTÓRICO (REGRAS DE LAYOUT ESTÁTICO) --- */}
                <div className={`flex-1 rounded-2xl shadow-xl border overflow-hidden flex flex-col ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                    {/* overflow-x-hidden e table-fixed evitam a barra de rolagem horizontal */}
                    <div className="overflow-y-auto overflow-x-hidden flex-1 custom-scrollbar">
                        <table className={`w-full table-fixed text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : ''}`}>
                        <thead className={`sticky top-0 z-20 shadow-sm border-b uppercase text-[9px] tracking-wider font-black ${isDarkMode ? 'bg-slate-950 border-slate-700 text-slate-400' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                                <tr>
                                    <th className="p-2 w-[3%] text-center">
                                        <input type="checkbox" checked={filteredHistory.length > 0 && selectedIds.length >= filteredHistory.length} onChange={toggleSelectAll} className="cursor-pointer rounded w-3 h-3 text-blue-600 focus:ring-blue-500"/>
                                    </th>
                                    <th className="p-2 w-[13%]">Colaborador</th>
                                    <th className="p-2 w-[11%]">Gestor</th>
                                    <th className="p-2 w-[5%]">Depto</th>
                                    <th className="p-2 w-[6%] text-center">Data</th>
                                    <th className="p-2 w-[3%] text-center">Qtd</th>
                                    <th className="p-2 w-[14%]">Classificação</th>                  
                                    <th className="p-2 w-[9%]">Tipo Conf.</th>       
                                    <th className="p-2 w-[5%] text-center">Peso</th>
                                    <th className="p-2 w-[24%]">Descrição</th>
                                    <th className="p-2 w-[7%] text-center">Ações</th>
                                </tr>
                            </thead>

                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700/50 bg-slate-900' : 'divide-gray-100 bg-white'}`}>
                                {filteredHistory.map((ap, idx) => {
                                    const isEditing = editingId === ap.id;
                                    const collab = data.collaborators.find(c=>c.id===ap.collaboratorId);
                                    const cName = collab?.name || 'N/D';
                                    
                                    // REQUISITO 1: Apenas 1º e 2º Nome
                                    const cNameParts = cName.split(' ');
                                    const cFirstTwoNames = cNameParts.length > 1 ? cNameParts[0] + ' ' + cNameParts[1] : cNameParts[0];

                                    const managerList = data.managers.filter(m => collab?.managerIds?.includes(m.id));
                                    const deptName = collab ? data.departments.find(d => d.id === collab.departmentId)?.name || 'N/D' : 'N/D';
                                    const cInitial = cName.charAt(0);
                                    const avatarColor = isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-blue-100 text-blue-700';
                                    
                                    const weightClass = getWeightColor(parseFloat(ap.weight) * (parseInt(ap.qtde) || 1));
                                    const rowClass = idx % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50/50');
                                    const isSelected = selectedIds.includes(ap.id);

                                    // REQUISITO b: Depto e Data (Max 10 chars)
                                    const deptTrunc = deptName.length > 10 ? deptName.substring(0, 10) + '...' : deptName;
                                    const dataFormatada = new Date(ap.date + 'T00:00:00').toLocaleDateString('pt-BR'); // Já tem exatos 10 caracteres

                                    // REQUISITO c: Qtde (Max 4 chars)
                                    const qtdeStr = String(ap.qtde || 1);
                                    const qtdeTrunc = qtdeStr.length > 4 ? qtdeStr.substring(0, 4) : qtdeStr;

                                    // REQUISITO d: Classificação (Max 30 chars)
                                    const classifText = formatOccurrenceTerm(ap.type || '');
                                    const classifTrunc = classifText.length > 30 ? classifText.substring(0, 30) + '...' : classifText;

                                    const confText = ap.conferenciaType || '-';
                                    const confTrunc = confText.length > 15 ? confText.substring(0, 15) + '...' : confText;

                                    if (isEditing) {
                                        return (
                                            <tr key={ap.id} className={`${isDarkMode ? 'bg-blue-900/20 border-l-4 border-blue-500' : 'bg-blue-50 border-l-4 border-blue-500'}`}>
                                                <td className="p-2 text-center"><input type="checkbox" disabled /></td>
                                                <td colSpan="10" className="p-2">
                                                    <div className="flex flex-wrap items-center gap-2 p-2">
                                                        <select className={`text-xs p-2.5 rounded-lg border outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-blue-300'}`} value={editForm.collaboratorId || ''} onChange={e=>setEditForm({...editForm, collaboratorId:e.target.value})}>
                                                            {visibleCollaborators.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                                                        </select>
                                                        <input type="date" className={`text-xs p-2.5 rounded-lg border outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white invert-calendar' : 'bg-white border-blue-300'}`} value={editForm.date || ''} onChange={e=>setEditForm({...editForm, date:e.target.value})}/>
                                                        <input type="number" className={`text-xs p-2.5 w-20 text-center rounded-lg border outline-none font-bold ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-blue-300'}`} value={editForm.qtde || 1} onChange={e=>setEditForm({...editForm, qtde: e.target.value})}/>
                                                        <select className={`text-xs p-2.5 rounded-lg border outline-none w-48 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-blue-300'}`} value={editForm.type || ''} onChange={(e)=>handleCriteriaChange(e, true)}>
                                                            {filteredQuanti.map(q=><option key={q.id} value={q.name}>{formatOccurrenceTerm(q.name)}</option>)}
                                                        </select>
                                                        <input className={`text-xs p-2.5 rounded-lg border outline-none w-32 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-blue-300'}`} value={editForm.conferenciaType || ''} onChange={e => setEditForm({...editForm, conferenciaType: e.target.value})} placeholder="Tipo Conf." />
                                                        <textarea className={`text-xs p-2.5 border rounded-lg resize-y outline-none flex-1 min-w-[200px] ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-blue-300'}`} value={editForm.description || ''} onChange={e=>setEditForm({...editForm, description:e.target.value})} rows={1} />
                                                        <div className="flex gap-1">
                                                            <button onClick={handleSaveEdit} className="bg-green-600 text-white p-2 rounded-lg hover:bg-green-700 shadow-sm"><Check size={16}/></button>
                                                            <button onClick={cancelEdit} className="bg-red-500 text-white p-2 rounded-lg hover:bg-red-600 shadow-sm"><X size={16}/></button>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }

                                    return (
                                        <tr key={ap.id} className={`${rowClass} ${isSelected ? (isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50') : ''} hover:brightness-95 transition-all group border-b ${isDarkMode ? 'border-slate-800' : 'border-gray-100'}`}>
                                            <td className="p-2 text-center align-middle truncate">
                                                <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(ap.id)} className="cursor-pointer rounded text-blue-600 focus:ring-blue-500 w-3 h-3"/>
                                            </td>
                                            
                                            <td className="p-2 align-middle truncate" title={cName}>
                                                <div className="flex items-center gap-1.5">
                                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0 ${avatarColor}`}>
                                                        {cInitial}
                                                    </div>
                                                    <span className={`font-bold text-[11px] truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                                        {cFirstTwoNames}
                                                    </span>
                                                </div>
                                            </td>
                                            
                                            <td className="p-2 align-middle text-[9px] opacity-80 truncate">
                                                {managerList.length > 0 ? (
                                                    <div className="flex flex-col gap-0.5">
                                                        {managerList.map(m => {
                                                            const mTrunc = m.name.length > 25 ? m.name.substring(0, 25) + '...' : m.name;
                                                            return <span key={m.id} title={m.name} className="truncate block leading-tight">{mTrunc}</span>;
                                                        })}
                                                    </div>
                                                ) : 'N/D'}
                                            </td>

                                            <td className="p-2 align-middle text-[9px] opacity-70 font-bold uppercase truncate" title={deptName}>
                                                {deptTrunc}
                                            </td>

                                            <td className="p-2 text-center align-middle truncate">
                                                <span className={`font-mono text-[10px] px-1 py-0.5 rounded ${isDarkMode ? 'bg-black/20 text-slate-300' : 'bg-gray-100 text-gray-600'}`}>
                                                    {dataFormatada}
                                                </span>
                                            </td>
                                            
                                            <td className="p-2 text-center align-middle truncate" title={qtdeStr}>
                                                <span className={`font-bold text-xs ${parseInt(ap.qtde) > 1 ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400'}`}>
                                                    {qtdeTrunc}
                                                </span>
                                            </td>

                                            <td className="p-2 align-middle truncate" title={classifText}>
                                                <span className={`font-bold text-[10px] truncate block ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>{classifTrunc}</span>
                                            </td>
                                            
                                            <td className="p-2 align-middle truncate" title={confText}>
                                                <span className={`text-[9px] px-1 py-0.5 rounded border truncate block ${isDarkMode ? 'bg-slate-800 border-slate-700 text-blue-300' : 'bg-white border-gray-200 text-blue-600'}`}>
                                                    {confTrunc}
                                                </span>
                                            </td>
                                            
                                            <td className="p-2 text-center align-middle truncate">
                                                <span className={`inline-flex items-center justify-center px-1 py-0.5 rounded text-[9px] font-black uppercase tracking-wider border shadow-sm ${weightClass}`}>
                                                    {(parseFloat(ap.weight) * (parseInt(ap.qtde) || 1)).toFixed(1)}p
                                                </span>
                                            </td>
                                            
                                            <td className="p-2 align-middle">
                                                {/* REQUISITO E: Descrição expandida, usa break-words para quebrar linha se precisar e manter a tela segura */}
                                                <div className={`text-[10px] line-clamp-3 leading-tight ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`} title={ap.description}>
                                                    {ap.description}
                                                </div>
                                            </td>
                                            
                                            <td className="p-2 text-center align-middle truncate">
                                                <div className="flex justify-center gap-1 transition-opacity">
                                                    <button onClick={() => { setDupTargetId(ap.id); setDupModalOpen(true); }} className={`p-1 rounded-lg transition ${isDarkMode ? 'text-purple-400 hover:bg-slate-700' : 'text-purple-500 hover:bg-purple-50'}`} title="Duplicar">
                                                        <Copy size={12}/>
                                                    </button>
                                                    <button onClick={()=>startEdit(ap)} className={`p-1 rounded-lg transition ${isDarkMode ? 'text-blue-400 hover:bg-slate-700' : 'text-blue-500 hover:bg-blue-50'}`} title="Editar">
                                                        <Edit3 size={12}/>
                                                    </button>
                                                    <button onClick={()=>handleDeleteSingle(ap.id)} className={`p-1 rounded-lg transition ${isDarkMode ? 'text-red-400 hover:bg-slate-700' : 'text-red-500 hover:bg-red-50'}`} title="Excluir">
                                                        <Trash2 size={12}/>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filteredHistory.length === 0 && (
                                    <tr>
                                        <td colSpan="11" className="p-12 text-center">
                                            <div className="flex flex-col items-center justify-center opacity-40">
                                                <AlertCircle size={48} className="mb-2"/>
                                                <p className="text-sm">Nenhum registro encontrado com os filtros atuais.</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* MODAL DUPLICAÇÃO */}
                {dupModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
                        <div className={`p-6 rounded-2xl w-80 shadow-2xl space-y-4 ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800'}`}>
                            <h3 className="font-bold text-lg flex items-center gap-2"><div className="p-1 bg-purple-100 rounded text-purple-600"><Copy size={18}/></div> Duplicar Registro</h3>
                            <div>
                                <label className={`text-xs font-bold block mb-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Quantas cópias?</label>
                                <input 
                                    type="number" 
                                    min="1" 
                                    max="50" 
                                    value={dupCount} 
                                    onChange={e => setDupCount(e.target.value)} 
                                    className={`w-full text-center text-3xl font-black py-2 rounded-xl border-2 outline-none focus:ring-4 focus:ring-purple-500/20 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white focus:border-purple-500' : 'bg-white border-gray-200 focus:border-purple-500'}`}
                                />
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <button onClick={() => setDupModalOpen(false)} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${isDarkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>Cancelar</button>
                                <button onClick={handleConfirmDuplication} className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 text-sm font-bold shadow-lg shadow-purple-500/30 transition-colors">Confirmar</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL CUSTOMIZADO (ALERT/CONFIRM) */}
                {modalConfig && (
                    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => !modalConfig.onConfirm && setModalConfig(null)}>
                        <div className={`p-6 rounded-2xl w-96 shadow-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex items-center gap-3 mb-4">
                                {modalConfig.type === 'alert' ? 
                                    <div className="p-3 bg-red-100 rounded-full text-red-600"><Lock size={24}/></div> : 
                                    modalConfig.type === 'aiSuggestion' ?
                                    <div className="p-3 bg-purple-100 rounded-full text-purple-600"><Sparkles size={24}/></div> :
                                    <div className="p-3 bg-yellow-100 rounded-full text-yellow-600"><AlertTriangle size={24}/></div>
                                }
                                <h3 className="font-bold text-lg leading-tight">{modalConfig.title}</h3>
                            </div>
                            <p className={`text-sm mb-6 whitespace-pre-wrap leading-relaxed max-h-[45vh] overflow-y-auto custom-scrollbar ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>{modalConfig.message}</p>
                            <div className="flex justify-end gap-2">
                                <button 
                                    onClick={() => setModalConfig(null)} 
                                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${isDarkMode ?
                                    'bg-slate-700 hover:bg-slate-600 text-slate-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                                >
                                    {modalConfig.cancelText || (modalConfig.type === 'alert' ? 'Entendi' : 'Cancelar')}
                                </button>
                                {modalConfig.onConfirm && (
                                    <button 
                                        onClick={modalConfig.onConfirm} 
                                        className={`px-4 py-2 rounded-lg text-white text-sm font-bold shadow-lg transition-colors ${modalConfig.type === 'aiSuggestion' ? 'bg-purple-600 hover:bg-purple-700 shadow-purple-500/30' : 'bg-red-600 hover:bg-red-700 shadow-red-500/30'}`}
                                    >
                                        {modalConfig.confirmText || 'Confirmar'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    /**
     * ============================================================================
     * COMPONENTE: AVALIAÇÃO SYSTEM (CORRIGIDO: HELPERS REINSERIDOS)
     * ============================================================================
     */


    /* ===============================================================================
     * [END_MOD_APONTAMENTOS_SYSTEM]
     * FIM DO BLOCO: ApontamentosSystem
     * [START_MOD_AVALIACAO_SYSTEM]
     * INICIO DO BLOCO: AvaliacaoSystem
     * =============================================================================== */
    const AvaliacaoSystem = ({ 
        data, 
        actions, 
        openEvalId, 
        permissions, 
        currentUser, 
        isDarkMode 
    }) => {
        
        // ========================================================================
        // 1. ESTADOS (STATE)
        // ========================================================================
        
        // Navegação e Edição
        const [view, setView] = useState('list');
        const [curr, setCurr] = useState(null);
        const [activeTab, setActiveTab] = useState('resumo'); 
        const qualiExpansionStorageKey = `acad_quali_dimensions_${currentUser?.id || currentUser?.email || 'anon'}`;
        const [expandedQualiDimensions, setExpandedQualiDimensions] = useState(() => {
            try {
                const local = JSON.parse(localStorage.getItem(qualiExpansionStorageKey) || '{}');
                return { ...(currentUser?.preferences?.qualitativeExpandedDimensions || {}), ...local };
            } catch {
                return currentUser?.preferences?.qualitativeExpandedDimensions || {};
            }
        });
        const setQualiDimensionExpanded = (dimension, isOpen) => {
            setExpandedQualiDimensions(prev => {
                const next = { ...prev, [dimension]: !!isOpen };
                try { localStorage.setItem(qualiExpansionStorageKey, JSON.stringify(next)); } catch {}
                return next;
            });
        };
        useEffect(() => {
            const timer = setTimeout(() => {
                if (currentUser && actions.saveUserPreferences) {
                    actions.saveUserPreferences({ qualitativeExpandedDimensions: expandedQualiDimensions });
                }
            }, 600);
            return () => clearTimeout(timer);
        }, [expandedQualiDimensions]);
        const [forceEditMode, setForceEditMode] = useState(false);
        
        // UI Auxiliar
        const [tooltipState, setTooltipState] = useState(null);
        const [searchTerm, setSearchTerm] = useState('');
        const [validationAlert, setValidationAlert] = useState(null);
        const evalWorkStateKey = openEvalId ? `acad_eval_work_state_${openEvalId}` : null;

        // Preserva a avaliação aberta quando o usuário navega por outras abas superiores.
        useEffect(() => {
            if (!evalWorkStateKey || !curr) return;
            try {
                sessionStorage.setItem(evalWorkStateKey, JSON.stringify({ curr, activeTab, forceEditMode }));
            } catch (e) {
                console.warn('Não foi possível salvar o estado temporário da avaliação:', e);
            }
        }, [evalWorkStateKey, curr, activeTab, forceEditMode]);

        // --- NOVO: FECHAR TOOLTIP AO CLICAR FORA ---
        useEffect(() => {
            const handleClickOutside = (e) => {
                // Se o popup estiver aberto E o clique NÃO for dentro do popup (classe .float-popup)
                if (tooltipState && !e.target.closest('.float-popup')) {
                    setTooltipState(null);
                }
            };
            // Adiciona o ouvinte
            document.addEventListener('mousedown', handleClickOutside);
            // Remove quando desmontar ou mudar o estado
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, [tooltipState]);
    
            // --- ESTADO QUE FALTAVA ---
        const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

        
        // --- ESTADO DO FEEDBACK (ATUALIZADO COM NATUREZA E ACOMPANHAMENTO) ---
        const [feedbackForm, setFeedbackForm] = useState({ 
            id: null, 
            type: 'Gestão', 
            text: '',
            date: new Date().toISOString().split('T')[0],
            actionTag: '',
            nature: 'Positivo', // Novo: Positivo ou Corretivo
            followUpDays: '90', // Novo: Padrão 90 dias
            followUpDate: ''    // Novo: Data calculada
        });

        // Função auxiliar para calcular data futura
        const calculateFollowUp = (baseDate, days) => {
            if (!baseDate || !days || days === 'custom') return '';
            const d = new Date(baseDate);
            d.setDate(d.getDate() + parseInt(days));
            return d.toISOString().split('T')[0];
        };

       // --- NOVA FUNÇÃO: PREPARAR EDIÇÃO ---
            const handleEditFeedback = (f) => {
                if (!canModifyEvaluationFeedback(f)) {
                    setValidationAlert({
                        title: "Edição Bloqueada",
                        message: "Este feedback foi inserido pelo avaliado e só pode ser editado pelo próprio usuário que o criou."
                    });
                    return;
                }

                const dateStr = new Date(f.timestamp).toISOString().split('T')[0];
                
                setFeedbackForm({
                    id: f.id,
                    type: f.type,
                    text: f.text,
                    date: dateStr,
                    actionTag: f.actionTag || '',
                    nature: f.nature || 'Positivo',
                    followUpDays: f.followUpDays || '90',
                    followUpDate: f.followUpDate || ''
                });

                setIsFeedbackModalOpen(true);
            };
        
        const [isGeneratingFeedback, setIsGeneratingFeedback] = useState(false);

        // --- FUNÇÃO DE IA PARA FEEDBACK (MINERAÇÃO DE DADOS COMPLETA) ---
        const handleEnhanceFeedback = async () => {
            if (!curr) return;
            setIsGeneratingFeedback(true);
            
            try {
                const userText = feedbackForm.text ? feedbackForm.text.trim() : "";
                const hasText = userText.length > 5; 
                
                let systemInstruction = "";
                let promptContext = "";

                // 1. MINERAÇÃO: Histórico (Ano Anterior)
                const currentYearInt = parseInt(curr.periodYear);
                const historyEvals = data.evaluations.filter(e => 
                    e.collaboratorId === curr.collaboratorId && 
                    parseInt(e.periodYear) === currentYearInt - 1
                );
                const historySummary = historyEvals.length > 0 
                    ? `Ano ${historyEvals[0].periodYear}: Nota ${historyEvals[0].personType === 'Gestão' ? historyEvals[0].finalScoreGestao : historyEvals[0].qualitative?.finalAverage}` 
                    : "Sem histórico anterior.";

                // 2. MINERAÇÃO: Pontos Fortes e Fracos (Qualitativa)
                let strengths = [], weaknesses = [];
                if (curr.qualitative?.criteria) {
                    curr.qualitative.criteria.forEach(c => {
                        const vals = ['q1','q2','q3','q4'].map(k => parseFloat(c[k])).filter(v => !isNaN(v));
                        if (vals.length > 0) {
                            const avg = vals.reduce((a,b)=>a+b,0) / vals.length;
                            if (avg >= 3.5) strengths.push(`${c.name}`);
                            if (avg <= 2.5) weaknesses.push(`${c.name}`);
                        }
                    });
                }

                // 3. MINERAÇÃO: Ocorrências (Quantitativa)
                const errors = data.apontamentos.filter(ap => 
                    ap.collaboratorId === curr.collaboratorId && 
                    ap.date.startsWith(curr.periodYear)
                ).map(ap => `${formatOccurrenceTerm(ap.type)} (${ap.date})`);

                // 4. MINERAÇÃO: Anotações do Gestor
                const notes = curr.personalNotes ? Object.values(curr.personalNotes).map(n => n.text).join('; ') : "";

                if (hasText) {
                    // CENÁRIO A: TEM TEXTO -> REESCREVER
                    systemInstruction = `ATUE COMO: Especialista em RH e Compliance. 
                    OBJETIVO: Reescrever o texto do gestor para remover agressividade e garantir profissionalismo, mantendo a mensagem central.`;
                    promptContext = `TEXTO ORIGINAL: "${userText}"`;
                } else {
                    // CENÁRIO B: VAZIO -> CRIAR DO ZERO BASEADO NOS DADOS
                    systemInstruction = `ATUE COMO: Gestor Sênior. 
                    OBJETIVO: Escrever um feedback de AEC - Acompanhamento de Evolução Continuada baseando-se ESTRITAMENTE nos dados fornecidos.
                    ESTRUTURA:
                    1. Comece citando o nome do colaborador (${curr.collaboratorName}).
                    2. Elogie os pontos fortes: ${strengths.join(', ') || 'pontualidade e dedicação'}.
                    3. Aponte melhorias necessárias (se houver): ${weaknesses.join(', ') || 'Nenhum ponto crítico'}.
                    4. Cite as ocorrências (se houver): ${errors.map(formatOccurrenceTerm).join(', ') || 'Sem ocorrências registradas'}.
                    5. Use as anotações do gestor como contexto: "${notes}".
                    6. Termine com um plano de ação motivador.`;
                    
                    promptContext = `DADOS DO COLABORADOR:
                    Nome: ${curr.collaboratorName}
                    Cargo: ${curr.role}
                    Histórico: ${historySummary}`;
                }

                // Chamada à API
                const result = await callGeminiAI(promptContext, systemInstruction, 'feedback');

                if (!isUsableAIText(result)) {
                    setValidationAlert({
                        title: "IA sem retorno válido",
                        message: `${result || 'A IA não retornou conteúdo válido.'}

O texto informado pelo gestor foi preservado.`
                    });
                    return;
                }
                
                // Mostra a sugestão em popup. Não altera o texto digitado sem confirmação do gestor.
                setValidationAlert({
                    title: 'Sugestão da IA',
                    message: result,
                    type: 'aiSuggestion',
                    cancelText: 'Manter o que escrevi',
                    confirmText: 'Inserir avaliação da IA',
                    onConfirm: () => {
                        setFeedbackForm(prev => ({ ...prev, text: result }));
                        setValidationAlert(null);
                    }
                });

            } catch (error) {
                console.error("Erro IA:", error);
                setValidationAlert({ title: "Erro", message: "Falha na geração do feedback. O texto informado foi preservado." });
            } finally {
                setIsGeneratingFeedback(false);
            }
        };

            // --- HELPER DE SEGURANÇA: CHECAR ASSINATURA ---
                const checkSignatureLock = (quarterKey) => {
                    if (!curr || !curr.managerSignatures) return false;
                    
                    // Mapeia 'q1' -> 'T1', etc.
                    const quarterMap = { 'q1': 'T1', 'q2': 'T2', 'q3': 'T3', 'q4': 'T4' };
                    const tag = quarterMap[quarterKey];
                    
                    if (!tag) return false;

                    // Verifica se existe assinatura para este trimestre
                    const isSigned = curr.managerSignatures.some(sig => 
                        sig.statusRef && sig.statusRef.includes(tag)
                    );

                    if (isSigned) {
                        setValidationAlert({
                            title: "Edição Bloqueada",
                            message: `O trimestre ${tag} já foi ASSINADO pelo gestor.\n\nPara incluir, editar ou excluir dados deste período, você deve primeiro remover a assinatura no painel principal.`
                        });
                        return true; // Retorna true indicando que ESTÁ bloqueado
                    }
                    return false;
                };

        // Estados para Sincronização Avançada (Menus e Modais)
        const [syncMenuOpen, setSyncMenuOpen] = useState(null); 
        const [syncModalConfig, setSyncModalConfig] = useState({ 
            isOpen: false, title: '', type: '', source: '', callback: null 
        });
        const [batchYearInput, setBatchYearInput] = useState(new Date().getFullYear().toString());
        const [isProcessingBatch, setIsProcessingBatch] = useState(false);

        // Estado para SuperAdm visualizar todos na criação
        const [showAllOverride, setShowAllOverride] = useState(false);

        // --- ESTADOS PARA ABA ANOTAÇÕES ---
        const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
            const [noteForm, setNoteForm] = useState({ 
                id: null, 
                text: '', 
                date: new Date().toISOString().split('T')[0],
                tipo: 'OBS',        // Padrão: OBS
                prioridade: 'Baixa' // Padrão: Baixa
            });    
        // Estado para controlar a exclusão de anotação (Substitui window.confirm)
        const [noteToDelete, setNoteToDelete] = useState(null);

        // Estado para controlar o Tooltip Inteligente das Anotações
        const [hoveredNote, setHoveredNote] = useState(null); // { id: string, position: 'top' | 'bottom' }
        
        // Paleta de cores para post-its/anotações (rotativa ou aleatória)
        const NOTE_COLORS = [
            { bg: 'bg-yellow-100', border: 'border-yellow-200', text: 'text-yellow-900', darkBg: 'bg-yellow-900/20', darkBorder: 'border-yellow-700', darkText: 'text-yellow-100' },
            { bg: 'bg-blue-100', border: 'border-blue-200', text: 'text-blue-900', darkBg: 'bg-blue-900/20', darkBorder: 'border-blue-700', darkText: 'text-blue-100' },
            { bg: 'bg-green-100', border: 'border-green-200', text: 'text-green-900', darkBg: 'bg-green-900/20', darkBorder: 'border-green-700', darkText: 'text-green-100' },
            { bg: 'bg-pink-100', border: 'border-pink-200', text: 'text-pink-900', darkBg: 'bg-pink-900/20', darkBorder: 'border-pink-700', darkText: 'text-pink-100' },
            { bg: 'bg-purple-100', border: 'border-purple-200', text: 'text-purple-900', darkBg: 'bg-purple-900/20', darkBorder: 'border-purple-700', darkText: 'text-purple-100' }
        ];

        // ========================================================================
        // 2. MEMOIZATION E PROPRIEDADES COMPUTADAS
        // ========================================================================
            const collaboratorTrainings = useMemo(() => {
            if (!curr) return [];
            const targetCollab = data.collaborators.find(c => c.id === curr.collaboratorId);
            const targetEmail = targetCollab ? targetCollab.email?.toLowerCase() : '';
            if (!targetEmail) return [];

            // 1. Pega todos os cursos do colaborador naquele ano
            const baseTrainings = data.trainingData.filter(t => 
                t.email?.toLowerCase() === targetEmail && String(t.year) === String(curr.periodYear)
            );

            // 2. Mapeia para descobrir quais chegaram DEPOIS da assinatura/fechamento
            return baseTrainings.map(t => {
                const month = parseInt((t.date || '').split('/')[1] || (t.date || '').split('-')[1] || '1');
                const qNum = month <= 3 ? 1 : month <= 6 ? 2 : month <= 9 ? 3 : 4;
                const qTag = `T${qNum}`;
                
                let isLate = false;
                
                // Procura assinatura do trimestre ou se a avaliação foi finalizada
                const sig = curr.managerSignatures?.find(s => s.statusRef?.includes(qTag) || s.statusRef?.includes('FINALIZADA') || s.statusRef?.includes('ASSINADA'));
                
                if (sig) {
                    const courseTime = t.timestamp || (t.createdAt ? new Date(t.createdAt).getTime() : (parseInt(t.id) > 1000000000000 ? parseInt(t.id) : null));
                    // Se o carimbo de tempo do curso for maior que o da assinatura, ele está atrasado
                    if (courseTime && courseTime > sig.timestamp) {
                        isLate = true; 
                    }
                }
                
                return { ...t, isLate }; // Adiciona a flag isLate no curso
                
            }).sort((a,b) => {
                try {
                    const partsA = a.date.split('/');
                    const partsB = b.date.split('/');
                    const dateA = new Date(partsA[2], partsA[1]-1, partsA[0]);
                    const dateB = new Date(partsB[2], partsB[1]-1, partsB[0]);
                    return dateB - dateA;
                } catch (e) { return 0; }
            });
        }, [curr, data.collaborators, data.trainingData]);

        const trainingStatsDisplay = useMemo(() => {
            let d1 = 0, d2 = 0;
            collaboratorTrainings.forEach(t => {
                if (t.isLate) return; // TRAVA VISUAL: Não soma se chegou atrasado!
                
                const typeLower = (t.type || '').toLowerCase();
                const h = parseFloat(t.hours) || 0;
                if (typeLower.includes('1') || typeLower.includes('atuação') || typeLower.includes('atuacao')) {
                    d1 += h;
                } else {
                    d2 += h;
                }
            });
            return { d1, d2, total: d1 + d2 };
        }, [collaboratorTrainings]);

        const collabBeingEvaluated = useMemo(() => {
            return data.collaborators.find(c => c.id === curr?.collaboratorId) || null;
        }, [curr, data.collaborators]);
        
        const isGestao = collabBeingEvaluated?.personType === 'Gestão';
        const isColaborador = collabBeingEvaluated?.personType === 'Colaborador';
        
        const headerInfo = useMemo(() => {
            if (!collabBeingEvaluated) return { manager: '-', role: '-' };
            const linkedManagers = data.managers.filter(m => 
                collabBeingEvaluated.managerIds && collabBeingEvaluated.managerIds.includes(m.id)
            );
            const managerName = linkedManagers.length > 0 
                ? linkedManagers.map(m => m.name).join(' / ') 
                : '-';
            const fullRole = `${collabBeingEvaluated.role || ''} ${collabBeingEvaluated.seniority || ''} ${collabBeingEvaluated.level || ''}`.trim();
            return { manager: managerName, role: fullRole };
        }, [collabBeingEvaluated, data.managers]);

        const feedbacksList = useMemo(() => {
            const history = [];
            if (!curr || !curr.feedbacksHistory) return history;
            for (const [key, value] of Object.entries(curr.feedbacksHistory)) { 
                history.push({ id: key, ...value }); 
            }
            return history.sort((a, b) => b.timestamp - a.timestamp);
        }, [curr]);

            const visibleCollaboratorsList = useMemo(() => {
            const userDept = data.departments.find(d => d.id === currentUser?.departmentId);
            const isUserDir = userDept && ['DIR', 'DIRETORIA', 'DIR.'].some(term => userDept.name.toUpperCase().includes(term));
            const isSuperAdm = currentUser?.role === 'SuperAdm';
            const hasGestaoPerm = currentUser?.permissions?.viewGestao;
            const hasGestaoCadastrosPerm = currentUser?.permissions?.viewGestaoCadastros;

            return data.collaborators.filter(c => {
                // 1. SuperAdm + Checkbox = Tudo
                if (isSuperAdm && showAllOverride) return true;

                // 2. SuperAdm (DIR) = Só Gestão (Global)
                if (isSuperAdm && isUserDir) return c.personType === 'Gestão';

                // 3. SuperAdm (Outros) = Tudo do Depto
                if (isSuperAdm) return c.departmentId === currentUser.departmentId;

                // Regras ADM (Sempre Depto Local)
                if (c.departmentId !== currentUser.departmentId) return false;

                // 4. ADM + Permissão global + Checkbox = Tudo do Depto
                if (hasGestaoPerm && showAllOverride) return true;

                // 5. NOVA REGRA: permissão específica para Cadastros
                if (hasGestaoCadastrosPerm) return true;

                // 6. ADM Padrão = Só Colaborador
                return c.personType === 'Colaborador';
            });
        }, [data.collaborators, currentUser, data.departments, showAllOverride]);

        // Cálculo Dinâmico de Resultado (Promoção/Premiação) para exibição no Card
        const dynamicResultLabels = useMemo(() => {
            if (!curr || !data.evaluations) return [];
            const otherEvals = data.evaluations.filter(e => 
                e.periodYear === curr.periodYear && 
                e.id !== curr.id && 
                e.collaboratorId !== curr.collaboratorId
            );
            const allCandidates = [
                recalculateFinalAverage(curr),
                ...otherEvals.map(e => recalculateFinalAverage(e))
            ];
            
            const resolveTieLocal = (a, b) => {
                const scoreA = a.personType === 'Gestão' ? (a.finalScoreGestao || 0) : (a.qualitative.finalAverage || 0);
                const scoreB = b.personType === 'Gestão' ? (b.finalScoreGestao || 0) : (b.qualitative.finalAverage || 0);
                if (Math.abs(scoreB - scoreA) > 0.0001) return scoreB - scoreA;
                if (a.personType === 'Colaborador') {
                    const lostA = a.quantitative?.meanPoints || 0;
                    const lostB = b.quantitative?.meanPoints || 0;
                    if (lostA !== lostB) return lostA - lostB;
                }
                return (a.collaboratorName || '').localeCompare(b.collaboratorName || '');
            };
            
            let isWinner = false;
            if (curr.personType === 'Gestão') {
                const allGestao = allCandidates.filter(e => e.personType === 'Gestão');
                allGestao.sort(resolveTieLocal);
                if (allGestao.length > 0 && allGestao[0].id === curr.id) isWinner = true;
            } else {
                const deptCollabs = allCandidates.filter(e => e.personType === 'Colaborador' && e.department === curr.department);
                deptCollabs.sort(resolveTieLocal);
                if (deptCollabs.length > 0 && deptCollabs[0].id === curr.id) isWinner = true;
            }
            
            const evWithScores = recalculateFinalAverage(curr);
            let config = data.evaluationTypes.find(et => et.id === collabBeingEvaluated?.evalTypeId) || data.globalConfigs[0] || {};
            
            let score = 0;
            if (evWithScores.personType === 'Gestão') {
                let realMP = 0;
                if (evWithScores.gestaoMetasPercepcao) {
                    evWithScores.gestaoMetasPercepcao.forEach(item => {
                        ['q1', 'q2', 'q3', 'q4'].forEach((q, idx) => {
                            if ((idx + 1) >= (evWithScores.activeFromQuarter || 1)) {
                                realMP += (parseFloat(item.scores?.[q]) || 0);
                            }
                        });
                    });
                }
                score = (evWithScores.qualitative?.finalScoreGestaoQuali || 0) + realMP + (evWithScores.atualizacao?.total || 0);
            } else {
                score = evWithScores.qualitative?.finalAverage || 0;
            }

            const lost = evWithScores.personType === 'Colaborador' ? (evWithScores.quantitative.meanPoints || 0) : 0;
            
            const mediaPromo = config.mediaPromocao || 8.0;
            const mediaPremiacao = config.mediaPremiacao || 7.0;
            const maxLost = config.maxPointsLost || 10.0;
            
            const promoThreshold = evWithScores.personType === 'Gestão' ? (mediaPromo / 10.0) * 100.0 : mediaPromo;
            const premioThreshold = evWithScores.personType === 'Gestão' ? (mediaPremiacao / 10.0) * 100.0 : mediaPremiacao;
            const canWin = evWithScores.personType === 'Colaborador' ? (lost <= maxLost) : true;
            
            const eligiblePromo = score >= promoThreshold && canWin;
            const eligiblePremio = score >= premioThreshold && canWin;
            
            let calculatedLabels = [];
            if (eligiblePromo && isWinner) calculatedLabels.push({ text: 'Promoção', color: isDarkMode ? 'bg-green-900/50 text-green-300 border border-green-700' : 'bg-green-100 text-green-700', details: { score, needed: promoThreshold, lost, maxLost, type: 'PROMOÇÃO' } });
            if (eligiblePremio) calculatedLabels.push({ text: 'Premiação', color: isDarkMode ? 'bg-blue-900/50 text-blue-300 border border-blue-700' : 'bg-blue-100 text-blue-700', details: { score, needed: premioThreshold, lost, maxLost, type: 'PREMIAÇÃO' } });
            if (calculatedLabels.length === 0) calculatedLabels.push({ text: '-', color: 'text-gray-400' });
            
            return calculatedLabels;
        }, [curr, data.evaluations, data.collaborators, data.evaluationTypes, data.globalConfigs, collabBeingEvaluated, isDarkMode]);

        // ========================================================================
        // 3. EFEITOS E SINCRONIZAÇÃO AUTOMÁTICA
        // ========================================================================

        const getIntegrationData = useCallback(() => {
            if (!curr) return { d1: 0, d2: 0, perception: { q1: null, q2: null, q3: null, q4: null } };
            
            const targetCollab = data.collaborators.find(c => c.id === curr.collaboratorId);
            const targetEmail = targetCollab ? targetCollab.email?.toLowerCase() : '';
            const evalYear = String(curr.periodYear);

            const relevantTrainings = data.trainingData.filter(t => {
                if (t.email?.toLowerCase() !== targetEmail || String(t.year) !== evalYear) return false;

                // --- TRAVA DE SEGURANÇA NA MATEMÁTICA ---
                const month = parseInt((t.date || '').split('/')[1] || (t.date || '').split('-')[1] || '1');
                const qNum = month <= 3 ? 1 : month <= 6 ? 2 : month <= 9 ? 3 : 4;
                const qTag = `T${qNum}`;
                
                const sig = curr.managerSignatures?.find(s => s.statusRef?.includes(qTag) || s.statusRef?.includes('FINALIZADA') || s.statusRef?.includes('ASSINADA'));
                
                if (sig) {
                    const courseTime = t.timestamp || (t.createdAt ? new Date(t.createdAt).getTime() : (parseInt(t.id) > 1000000000000 ? parseInt(t.id) : null));
                    if (courseTime && courseTime > sig.timestamp) {
                        return false; // Rejeita e expulsa o curso da soma das horas
                    }
                }
                return true;
            });
            
            let d1 = 0, d2 = 0;
            relevantTrainings.forEach(t => {
                const typeLower = t.type ? t.type.toLowerCase() : '';
                const isD1 = typeLower.includes('1') || typeLower.includes('atuação') || typeLower.includes('atuacao');
                if (isD1) d1 += (t.hours || 0); else d2 += (t.hours || 0);
            });

            // ... MANTENHA O RESTANTE DA FUNÇÃO DA PERCEPÇÃO COMO ESTAVA ...
            //const evalYear = curr?.periodYear || new Date().getFullYear().toString();

            const relevantPerceptions = data.teamPerception.filter(p => {
                const managerEmail = (
                    p.manager_email ||
                    p.managerEmail ||
                    p.emailGestor ||
                    p.gestorEmail ||
                    p.manager ||
                    ''
                ).toLowerCase().trim();

                if (managerEmail !== targetEmail) return false;

                return p.date && p.date.includes(evalYear);
            });
            
            const quarterScores = { q1: [], q2: [], q3: [], q4: [] };
            relevantPerceptions.forEach(p => {
                const parts = p.date.split('/'); 
                if (parts.length === 3) {
                    const month = parseInt(parts[1], 10);
                    const val = parseFloat(p.nota);
                    if (!isNaN(val)) {
                        if (month <= 3) quarterScores.q1.push(val);
                        else if (month <= 6) quarterScores.q2.push(val);
                        else if (month <= 9) quarterScores.q3.push(val);
                        else quarterScores.q4.push(val);
                    }
                }
            });
            
            const finalQ = { q1: null, q2: null, q3: null, q4: null };
            Object.keys(quarterScores).forEach(q => {
                if (quarterScores[q].length > 0) finalQ[q] = quarterScores[q].reduce((a, b) => a + b, 0) / quarterScores[q].length;
            });
            
            return { d1, d2, perception: finalQ };
        }, [curr?.collaboratorId, curr?.periodYear, curr?.managerSignatures, data.trainingData, data.teamPerception, data.collaborators]);

        useEffect(() => {
            if (!curr) return;
            
            // --- NOVA TRAVA DE SEGURANÇA INTELIGENTE (Sincronização Automática) ---
            // 1. Impede se a avaliação estiver totalmente fechada/arquivada.
            const isClosed = curr.status === 'Finalizada' || curr.status === 'T4-Concluido' || curr.status === 'Concluída Financeiro';
            
            // 2. Descobre o trimestre atual (em tempo real) para verificar se ele já foi assinado
            const today = new Date();
            const currentMonth = today.getMonth() + 1;
            const currentYear = today.getFullYear();
            const evalYear = parseInt(curr.periodYear);
            
            let currentQTag = '';
            if (currentYear > evalYear) currentQTag = 'T4'; // Se for ano passado, olha para a última assinatura (T4)
            else if (currentYear < evalYear) currentQTag = 'T1';
            else currentQTag = currentMonth <= 3 ? 'T1' : currentMonth <= 6 ? 'T2' : currentMonth <= 9 ? 'T3' : 'T4';

            const isCurrentQuarterSigned = curr.managerSignatures && curr.managerSignatures.some(sig => 
                sig.statusRef && (sig.statusRef.includes(currentQTag) || sig.statusRef.includes('FINALIZADA') || sig.statusRef.includes('ASSINADA'))
            );

            // Se o período estiver fechado OU o trimestre atual já estiver assinado, bloqueia o AUTO-SYNC.
            // Neste caso, novos cursos só entrarão se o gestor clicar no botão "Sincronizar -> Atualizar (Banco)"
            if (isClosed || isCurrentQuarterSigned) return;

            const integration = getIntegrationData();
            let hasChanges = false;
            const upd = JSON.parse(JSON.stringify(curr));

            if (!upd.atualizacao) upd.atualizacao = {};
    
            // 1. ATUALIZAÇÃO DE CURSOS (D1 e D2)
            if (Math.abs((upd.atualizacao.D1_hours || 0) - integration.d1) > 0.01) { 
                upd.atualizacao.D1_hours = integration.d1;
                upd.atualizacao.manual_D1_hours = false; 
                hasChanges = true; 
            }

            if (Math.abs((upd.atualizacao.D2_hours || 0) - integration.d2) > 0.01) { 
                upd.atualizacao.D2_hours = integration.d2;
                upd.atualizacao.manual_D2_hours = false; 
                hasChanges = true; 
            }

            // 2. ATUALIZAÇÃO DE PERCEPÇÃO (360º) - Exclusivo Gestão
            if (upd.personType === 'Gestão' && upd.gestaoMetasPercepcao) {
                const pIdx = upd.gestaoMetasPercepcao.findIndex(i => i.type === 'Percepção' || i.name.toLowerCase().includes('percepção'));
                if (pIdx !== -1) {
                    const item = upd.gestaoMetasPercepcao[pIdx];
                    if (!item.overrides) item.overrides = {}; 
                    
                    ['q1', 'q2', 'q3', 'q4'].forEach(q => {
                        const quarterMap = { 'q1': 'T1', 'q2': 'T2', 'q3': 'T3', 'q4': 'T4' };
                        const tag = quarterMap[q];
                        const isQuarterSigned = upd.managerSignatures && upd.managerSignatures.some(sig => sig.statusRef && sig.statusRef.includes(tag));
                        
                        if (!item.overrides[q] && !isQuarterSigned) {
                            const newVal = integration.perception[q];
                            const oldVal = item.scores[q];
                            const isDiff = (newVal === null && oldVal !== null) || (newVal !== null && oldVal === null) || (newVal !== null && Math.abs(newVal - oldVal) > 0.001);
                            if (isDiff) { 
                                item.scores[q] = newVal; 
                                hasChanges = true; 
                            }
                        }
                    });
                }
            }

            // 3. SE HOUVE MUDANÇA, RECALCULA E SALVA NA HORA
            if (hasChanges) {
                const D1_max = 15.00, D2_max = 5.00, D1_min = 36, D2_min = 18;
                const d1_h = upd.atualizacao.D1_hours || 0;
                const d2_h = upd.atualizacao.D2_hours || 0;
                const d1_score = d1_h >= D1_min ? D1_max : (d1_h / D1_min) * D1_max;
                const d2_score = d2_h >= D2_min ? D2_max : (d2_h / D2_min) * D2_max;
                upd.atualizacao.D1_nota = Math.min(D1_max, d1_score);
                upd.atualizacao.D2_nota = Math.min(D2_max, d2_score);
                upd.atualizacao.total = upd.atualizacao.D1_nota + upd.atualizacao.D2_nota;
                const finalEv = recalculateFinalAverage(upd);
                setCurr(finalEv);
                actions.saveEvaluation(finalEv);
            }
        }, [getIntegrationData, curr?.id, curr?.periodYear, curr?.collaboratorId, data.trainingData, data.teamPerception]);

        // ========================================================================
        // 4. LÓGICA DE PROCESSAMENTO EM LOTE (SYNC MANUAL)
        // ========================================================================

        const handleExternalSync = async (type, year) => {
            setIsProcessingBatch(true);
            setSyncModalConfig(prev => ({...prev, isOpen: false}));
            try {
                let result;
                if (type === 'perception') {
                    result = await actions.syncTeamPerceptionGlobal(year);
                } else {
                    result = await actions.syncTrainingsGlobal(year);
                }
                alert(`Sincronização externa concluída! ${result.count} registros importados.`);
            } catch (error) {
                console.error(error);
                alert("Erro na sincronização: " + error.message);
            } finally {
                setIsProcessingBatch(false);
            }
        };

        const handleInternalBatchUpdatePerception = async (year) => {
            setIsProcessingBatch(true);
            setSyncModalConfig(prev => ({...prev, isOpen: false}));
            try {
                const targetEvals = data.evaluations.filter(e => 
                    e.periodYear === year && 
                    e.personType === 'Gestão' && 
                    e.status !== 'Finalizada' && e.status !== 'T4-Concluido'
                );
                let updateCount = 0;
                for (const ev of targetEvals) {
                    const targetCollab = data.collaborators.find(c => c.id === ev.collaboratorId);
                    const targetEmail = targetCollab ? targetCollab.email?.toLowerCase() : '';
                    const relevantPerceptions = data.teamPerception.filter(p => {
                        const managerEmail = (
                            p.manager_email ||
                            p.managerEmail ||
                            p.emailGestor ||
                            p.gestorEmail ||
                            ''
                        ).toLowerCase().trim();

                        if (managerEmail !== targetEmail) return false;

                        return p.date && p.date.includes(year);
                    });
                    const quarterScores = { q1: [], q2: [], q3: [], q4: [] };
                    relevantPerceptions.forEach(p => {
                        const parts = p.date.split('/'); 
                        if (parts.length === 3) {
                            const month = parseInt(parts[1], 10);
                            const val = parseFloat(p.nota);
                            if (!isNaN(val)) {
                                if (month <= 3) quarterScores.q1.push(val);
                                else if (month <= 6) quarterScores.q2.push(val);
                                else if (month <= 9) quarterScores.q3.push(val);
                                else quarterScores.q4.push(val);
                            }
                        }
                    });
                    const upd = JSON.parse(JSON.stringify(ev));
                    if (upd.gestaoMetasPercepcao) {
                        const pIdx = upd.gestaoMetasPercepcao.findIndex(i => i.type === 'Percepção' || i.name.toLowerCase().includes('percepção'));
                        if (pIdx !== -1) {
                            const item = upd.gestaoMetasPercepcao[pIdx];
                            const overrides = item.overrides || {};
                            ['q1','q2','q3','q4'].forEach(q => {
                                if (!overrides[q] && quarterScores[q].length > 0) {
                                    const avg = quarterScores[q].reduce((a,b)=>a+b,0) / quarterScores[q].length;
                                    item.scores[q] = avg;
                                }
                            });
                            const finalEv = recalculateFinalAverage(upd);
                            await actions.saveEvaluation(finalEv);
                            updateCount++;
                        }
                    }
                }
                alert(`Atualização interna de Percepção concluída! ${updateCount} avaliações processadas.`);
            } catch (e) {
                console.error(e);
                alert("Erro na atualização interna: " + e.message);
            } finally {
                setIsProcessingBatch(false);
            }
        };

        const handleInternalBatchUpdateTraining = async (year) => {
            setIsProcessingBatch(true);
            setSyncModalConfig(prev => ({...prev, isOpen: false}));
            try {
                const targetEvals = data.evaluations.filter(e => 
                    e.periodYear === year && 
                    e.status !== 'Finalizada' && e.status !== 'T4-Concluido'
                );
                let updateCount = 0;
                for (const ev of targetEvals) {
                    const targetCollab = data.collaborators.find(c => c.id === ev.collaboratorId);
                    const targetEmail = targetCollab ? targetCollab.email?.toLowerCase() : '';
                    const currentYear = ev.periodYear || new Date().getFullYear().toString();

                    const relevantTrainings = data.trainingData.filter(t => 
                        t.email?.toLowerCase() === targetEmail && String(t.year) === String(currentYear)
                    );
                    let d1 = 0, d2 = 0;
                    relevantTrainings.forEach(t => {
                        const typeLower = t.type ? t.type.toLowerCase() : '';
                        const isD1 = typeLower.includes('1') || typeLower.includes('atuação') || typeLower.includes('atuacao');
                        if (isD1) d1 += (t.hours || 0); else d2 += (t.hours || 0);
                    });
                    const upd = JSON.parse(JSON.stringify(ev));
                    if (!upd.atualizacao) upd.atualizacao = {};
                    if (!upd.atualizacao.manual_D1_hours) upd.atualizacao.D1_hours = d1;
                    if (!upd.atualizacao.manual_D2_hours) upd.atualizacao.D2_hours = d2;
                    const D1_max = 15.00, D2_max = 5.00, D1_min = 36, D2_min = 18;
                    const d1_h = upd.atualizacao.D1_hours || 0;
                    const d2_h = upd.atualizacao.D2_hours || 0;
                    const d1_score = d1_h >= D1_min ? D1_max : (d1_h / D1_min) * D1_max;
                    const d2_score = d2_h >= D2_min ? D2_max : (d2_h / D2_min) * D2_max;
                    upd.atualizacao.D1_nota = Math.min(D1_max, d1_score);
                    upd.atualizacao.D2_nota = Math.min(D2_max, d2_score);
                    upd.atualizacao.total = upd.atualizacao.D1_nota + upd.atualizacao.D2_nota;
                    const finalEv = recalculateFinalAverage(upd);
                    await actions.saveEvaluation(finalEv);
                    updateCount++;
                }
                alert(`Atualização interna de Treinamentos concluída! ${updateCount} avaliações processadas.`);
            } catch (e) {
                console.error(e);
                alert("Erro na atualização interna: " + e.message);
            } finally {
                setIsProcessingBatch(false);
            }
        };

        const openSyncModal = (type, source) => {
            setSyncMenuOpen(null); 
            setBatchYearInput(new Date().getFullYear().toString());
            setSyncModalConfig({
                isOpen: true,
                title: source === 'external' ? 'Sincronizar (Importar Planilha)' : 'Atualizar (Dados do Banco)',
                type,
                source,
                callback: source === 'external' ? 
                    (year) => handleExternalSync(type, year) : 
                    (type === 'perception' ? handleInternalBatchUpdatePerception : handleInternalBatchUpdateTraining)
            });
        };

        // ========================================================================
        // 5. HANDLERS DE AÇÃO E HELPERS DE CÁLCULO VISUAL
        // ========================================================================

                    // Função auxiliar para verificar se está editando algo fechado
        const checkAndLogClosedEdit = (fieldChanged, oldValue, newValue) => {
            const isClosed = curr.status === 'Finalizada' || curr.status === 'T4-Concluido';
            if (isClosed) {
                actions.logAction('EDICAO_PERIODO_FECHADO', {
                    entity: 'AVALIACOES',
                    recordId: curr.id,
                    details: [
                        {
                            campo: fieldChanged,
                            de: oldValue,
                            para: newValue
                        },
                        {
                            campo: 'motivo',
                            de: '',
                            para: 'Edição forçada manual'
                        }
                    ]
                });
            }
        };

        const updateGestaoScore = async (id, q, val) => { 
            // --- NOVO: BLOQUEIO POR ASSINATURA ---
            // Impede edição se o trimestre já foi assinado
            if (checkSignatureLock(q)) return;
            // -------------------------------------

            const upd = JSON.parse(JSON.stringify(curr));
            if (!upd.gestaoMetasPercepcao) return;
            
            const item = upd.gestaoMetasPercepcao.find(i => i.id === id);
            if (!item) return;
            
            const oldVal = item.scores[q]; // Captura valor antigo para log
            
            const isPercepcao = item.type === 'Percepção' || item.name.toLowerCase().includes('percepção');
            if (!item.overrides) item.overrides = {};
            
            let newVal = val !== '' ? parseFloat(val) : null;
            if (newVal !== null) { 
                if (newVal > 5) newVal = 5; 
                if (newVal < 1) newVal = 1; 
            }
            
            if (isPercepcao) {
                if (val === '') { 
                    item.overrides[q] = false; 
                    const integration = getIntegrationData(); 
                    newVal = integration.perception[q]; 
                } else { 
                    item.overrides[q] = true; 
                }
            }
            
            item.scores = { ...item.scores, [q]: newVal };
            
            // Log se estiver fechado administrativamente
            checkAndLogClosedEdit(`Gestão ${item.name} (${q})`, oldVal, newVal);

            const newCurr = recalculateFinalAverage(upd);
            setCurr(newCurr); 
            await actions.saveEvaluation(newCurr); 
        };

        const updateAtualizacao = async (field, val) => { 
            const upd = JSON.parse(JSON.stringify(curr));
            if (!upd.atualizacao) upd.atualizacao = {};
            
            const oldVal = upd.atualizacao[field]; // Valor antigo

            const isIntegrable = field === 'D1_hours' || field === 'D2_hours';
            let newVal = parseFloat(val) || 0;

            if (isIntegrable) {
                const overrideKey = `manual_${field}`;
                if (val === '') { 
                    upd.atualizacao[overrideKey] = false; 
                    const integration = getIntegrationData(); 
                    newVal = field === 'D1_hours' ? integration.d1 : integration.d2;
                    upd.atualizacao[field] = newVal;
                } else { 
                    upd.atualizacao[overrideKey] = true;
                    upd.atualizacao[field] = newVal; 
                }
            } else {
                upd.atualizacao[field] = newVal;
            }
            
            // Log se estiver fechado
            checkAndLogClosedEdit(`Atualização ${field}`, oldVal, newVal);

            const D1_max = 15.00, D2_max = 5.00, D1_min = 36, D2_min = 18;
            const D1_h = upd.atualizacao.D1_hours || 0;
            const D2_h = upd.atualizacao.D2_hours || 0;
            upd.atualizacao.D1_nota = Math.min(D1_max, D1_h >= D1_min ? D1_max : (D1_h / D1_min) * D1_max);
            upd.atualizacao.D2_nota = Math.min(D2_max, D2_h >= D2_min ? D2_max : (D2_h / D2_min) * D2_max); 
            upd.atualizacao.total = upd.atualizacao.D1_nota + upd.atualizacao.D2_nota;
            const newCurr = recalculateFinalAverage(upd); setCurr(newCurr); await actions.saveEvaluation(newCurr); 
        };

            const updateScore = async (type, id, q, val) => { 
                    // --- NOVO: BLOQUEIO POR ASSINATURA ---
                    if (checkSignatureLock(q)) return;
                    // -------------------------------------

                    const upd = JSON.parse(JSON.stringify(curr));
                    // ... (resto do código original da função updateScore)
                    let finalVal = null;
                    let itemName = '';
                    let oldVal = '';
                    if (type === 'quali') { 
                        const parsed = parseInt(val);
                        finalVal = !isNaN(parsed) ? Math.min(4, Math.max(1, parsed)) : null;
                        const it = upd.qualitative.criteria.find(i => i.id === id); 
                        itemName = it.name; oldVal = it[q]; it[q] = finalVal;
                    } else { 
                        const numVal = parseInt(val) || 0; 
                        finalVal = Math.max(0, numVal);
                        const it = upd.quantitative.errors.find(i => i.id === id);
                        itemName = it.name; oldVal = it[q]; it[q] = finalVal;
                    } 
                    if (oldVal !== finalVal) {
                        checkAndLogClosedEdit(`${type === 'quali' ? 'Quali' : 'Quanti'} - ${itemName} (${q})`, oldVal, finalVal);
                    }
                    const newCurr = recalculateFinalAverage(upd); setCurr(newCurr); await actions.saveEvaluation(newCurr); 
                };

                    const initEval = async (collabId, year) => { 
                    const c = data.collaborators.find(x => x.id === collabId);
                    if (!c) {
                        alert("Erro: Colaborador não encontrado.");
                        return;
                    }
                    
                    // --- 1. DEFINE O TIPO EXATAMENTE PELO CADASTRO (Sem buscar por cargo) ---
                    // Lê estritamente o campo "Tipo de Pessoa" do cadastro. 
                    // Se estiver "Gestor", padroniza para "Gestão". Se for vazio, assume "Colaborador".
                    const personType = c.personType === 'Gestor' ? 'Gestão' : (c.personType || 'Colaborador'); 
                    
                    const et = data.evaluationTypes.find(t => t.id === c.evalTypeId);
                    const departmentName = data.departments.find(d => d.id === c.departmentId)?.name ?? ''; 

                    // --- LÓGICA DE TRIMESTRES ATIVOS ---
                    let activeFromQuarter = 1;
                    if (c.startDate) {
                        const start = new Date(c.startDate + "T00:00:00");
                        const startYear = start.getFullYear();
                        const evalYear = parseInt(year);

                        if (startYear > evalYear) {
                            return alert("Erro: Data de admissão posterior ao ano da avaliação.");
                        }

                        if (startYear === evalYear) {
                            const month = start.getMonth() + 1;
                            if (month <= 3) activeFromQuarter = 1;      
                            else if (month <= 6) activeFromQuarter = 2;
                            else if (month <= 9) activeFromQuarter = 3; 
                            else activeFromQuarter = 4;
                        }
                    }

                    // --- 2. LÓGICA DE CARREGAMENTO DE CRITÉRIOS (CORRIGIDA) ---
                    // Agora ele compara estritamente o "Tipo de Pessoa" do Critério com o "Tipo de Pessoa" do Colaborador.
                    // Critérios em branco assumem o comportamento exclusivo de "Colaborador".
                    const activeQuali = data.qualiCriteria.filter(q => {
                        if (et?.selectedQualiIds?.length > 0) {
                            return et.selectedQualiIds.includes(q.id);
                        }
                        const qType = q.personType === 'Gestor' ? 'Gestão' : (q.personType || 'Colaborador');
                        return qType === personType || qType === 'Ambos';
                    });
                    
                    const activeQuanti = data.quantiCriteria.filter(q => {
                        if (et?.selectedQuantiIds?.length > 0) {
                            return et.selectedQuantiIds.includes(q.id);
                        }
                        const qType = q.personType === 'Gestor' ? 'Gestão' : (q.personType || 'Colaborador');
                        return qType === personType || qType === 'Ambos';
                    });

                    // --- LÓGICA DE HERANÇA DE PDI PENDENTE ---
                    const inheritedHistory = {};
                    const prevYear = (parseInt(year) - 1).toString();
                    const prevEval = data.evaluations.find(e => e.collaboratorId === collabId && e.periodYear === prevYear);
                    if (prevEval && prevEval.feedbacksHistory) {
                        Object.values(prevEval.feedbacksHistory).forEach(f => {
                            if (f.type === 'PDI' && f.status !== 'Concluído') {
                                const newId = Date.now().toString() + Math.random().toString(36).substr(2, 5);
                                inheritedHistory[newId] = { 
                                    ...f, 
                                    id: newId, 
                                    quarter: 'q1', 
                                    text: `[HERDADO DE ${prevYear}]\n${f.text}` 
                                };
                            }
                        });
                    }

                    const newEval = { 
                        id: Date.now().toString(), status: 'Em Andamento', 
                        collaboratorId: c.id, collaboratorName: c.name, periodYear: year, department: departmentName, 
                        role: c.role, seniority: c.seniority, level: c.level, 
                        evalTypeName: et?.name || 'Padrão', 
                        personType: personType, 
                        activeFromQuarter: activeFromQuarter,
                        qualitative: { criteria: activeQuali.map(cr => ({...cr, q1:null, q2:null, q3:null, q4:null})), finalAverage: 0, finalScoreGestaoQuali: 0 },
                        quantitative: { errors: activeQuanti.map(er => ({...er, q1:0, q2:0, q3:0, q4:0})), meanPoints: 0 }, 
                        feedbacks: { q1:'', q2:'', q3:'', q4:'' }, 
                        feedbacksHistory: inheritedHistory, 
                        pdi: { knowledge: '', plan: '' }, 
                        atualizacao: { D1_hours: 0, D2_hours: 0, D1_certs: 0, D2_certs: 0, D1_nota: 0, D2_nota: 0, total: 0 }, 
                        gestaoMetasPercepcao: personType === 'Gestão' ? data.gestaoMetasPercepcao.map(item => ({...item, scores: item.scores || { q1: null, q2: null, q3: null, q4: null } })) : undefined, 
                        totalGestaoMetasPercepcao: 0, totalGestaoMetas: 0, totalGestaoPercepcao: 0, finalScoreGestao: 0, 
                    };

                    newEval.quantitative.errors = getSyncedQuantitativeErrors(newEval, data.apontamentos, data.quantiCriteria, data.collaborators, data.evaluationTypes);

                    const evWithScores = recalculateFinalAverage(newEval); 
                    await actions.saveEvaluation(evWithScores); 
                    
                    if (actions.setOpenId) {
                        actions.setOpenId(evWithScores.id);
                    }
                    
                    setCurr(evWithScores); 
                    setView('edit'); 
                    setForceEditMode(false); 
                };

            const isFeedbackOwner = (feedback) => {
                    if (!feedback || !currentUser) return false;

                    const currentUserId = String(currentUser.id || '');
                    const currentUserEmail = String(currentUser.email || '').trim().toLowerCase();

                    const createdBy = String(feedback.createdBy || '');
                    const createdByEmail = String(feedback.createdByEmail || '').trim().toLowerCase();

                    if (currentUserId && createdBy && currentUserId === createdBy) return true;
                    if (currentUserEmail && createdByEmail && currentUserEmail === createdByEmail) return true;

                    return false;
                };

                const canModifyEvaluationFeedback = (feedback) => {
                    if (!feedback) return true;

                    // Feedback/consideração do avaliado não pode ser editado/excluído
                    // por gestor, RH, diretoria ou qualquer outro usuário.
                    if (feedback.type === 'Colaborador') {
                        return isFeedbackOwner(feedback);
                    }

                    return true;
                };
            // Localize a função handleSaveFeedback dentro de AvaliacaoSystem e substitua por esta:

            const handleSaveFeedback = async () => { 
                // 1. Validação de Texto
                if (!curr || !feedbackForm.text) {
                    return setValidationAlert({ title: "Campo Obrigatório", message: "Por favor, digite o texto." });
                }
                
                // Validação de Data
                const dateStr = feedbackForm.date || new Date().toISOString().split('T')[0];
                const year = parseInt(curr.periodYear);
                const inputYear = parseInt(dateStr.split('-')[0]);

                if (inputYear !== year) {
                    return setValidationAlert({ title: "Data Inválida", message: `A data informada (${inputYear}) deve estar dentro do ano da avaliação (${year}).\n\nPor favor, ajuste a data.` });
                }
                
                // --- BLOQUEIO POR ASSINATURA ---
                const month = parseInt(dateStr.split('-')[1], 10);
                const calculatedQuarter = month <= 3 ? 'q1' : month <= 6 ? 'q2' : month <= 9 ? 'q3' : 'q4';
                if (checkSignatureLock(calculatedQuarter)) return;
                // -------------------------------------

                const timestamp = new Date(dateStr).getTime();
                let savedColor = '#64748b'; 
                if (feedbackForm.type === 'Gestão') savedColor = '#3b82f6';
                else if (feedbackForm.type === 'Colaborador') savedColor = '#10b981';
                else if (feedbackForm.type === 'AvDes') savedColor = '#ef4444';
                else if (feedbackForm.type === 'PDI') savedColor = '#f97316'; // Laranja PDI

                const idToSave = feedbackForm.id || Date.now().toString();

                const previousFeedback = feedbackForm.id
                    ? curr.feedbacksHistory?.[feedbackForm.id]
                    : null;

                if (previousFeedback && !canModifyEvaluationFeedback(previousFeedback)) {
                    alert("Este feedback foi inserido pelo avaliado e só pode ser editado pelo próprio usuário que o criou.");
                    return;
                }

                // Lógica para salvar Tags e Prazos baseada no Tipo
                const isPDI = feedbackForm.type === 'PDI';
                let finalActionTag = '';
                
                if (isPDI) {
                    finalActionTag = 'Meta';
                } else if (feedbackForm.type === 'Gestão' || feedbackForm.type === 'AvDes') {
                    finalActionTag = feedbackForm.actionTag;
                }

                const needsFollowUp = isPDI || finalActionTag === 'Reveja' || finalActionTag === 'Pare';

                const previousFeedbackData = curr.feedbacksHistory?.[idToSave] || {};

                const newFeedbackEntry = { 
                    ...previousFeedbackData,
                    id: idToSave, 
                    type: feedbackForm.type,
                    quarter: calculatedQuarter,
                    text: feedbackForm.text,
                    timestamp: timestamp,
                    color: savedColor, 
                    actionTag: finalActionTag,
                    nature: isPDI ? 'Desenvolvimento' : feedbackForm.nature, 
                    followUpDays: needsFollowUp ? feedbackForm.followUpDays : null,
                    followUpDate: needsFollowUp ? feedbackForm.followUpDate : null,
                    status: feedbackForm.status || previousFeedbackData.status || 'Pendente',

                    createdAt: previousFeedbackData.createdAt || new Date().toISOString(),
                    createdBy: previousFeedbackData.createdBy || currentUser?.id || '',
                    createdByName: previousFeedbackData.createdByName || currentUser?.name || '',
                    createdByEmail: previousFeedbackData.createdByEmail || currentUser?.email || '',

                    updatedAt: feedbackForm.id ? new Date().toISOString() : '',
                    updatedBy: feedbackForm.id ? currentUser?.id || '' : '',
                    updatedByName: feedbackForm.id ? currentUser?.name || '' : '',
                    updatedByEmail: feedbackForm.id ? currentUser?.email || '' : ''
                };

                const updatedFeedbacksHistory = { ...curr.feedbacksHistory, [idToSave]: newFeedbackEntry };
                const updatedEval = { ...curr, feedbacksHistory: updatedFeedbacksHistory }; 
                
                await actions.saveEvaluation(updatedEval); 
                
                setCurr(updatedEval); 
                setIsFeedbackModalOpen(false); 
                
                const currentYear = new Date().getFullYear();
                const defaultDate = parseInt(curr.periodYear) === currentYear ? new Date().toISOString().split('T')[0] : `${curr.periodYear}-12-31`;

                setFeedbackForm({ 
                    id: null, type: 'Gestão', text: '', date: defaultDate, actionTag: '', nature: 'Positivo', followUpDays: '90', followUpDate: ''
                });
            };    
            const handleDeleteFeedback = async (id) => { 
                    if (!curr || !curr.feedbacksHistory) return;
                    
                    // --- NOVO: BLOQUEIO POR ASSINATURA ---
                    const target = curr.feedbacksHistory[id];

                    if (!canModifyEvaluationFeedback(target)) {
                        alert("Este feedback foi inserido pelo avaliado e só pode ser excluído pelo próprio usuário que o criou.");
                        return;
                    }

                    if (target && target.quarter) {
                        // Verifica se o trimestre deste feedback já foi assinado
                        if (checkSignatureLock(target.quarter)) return;
                    }
                    // -------------------------------------

                    const updatedFeedbacksHistory = { ...curr.feedbacksHistory };
                    delete updatedFeedbacksHistory[id]; 
                    
                    const updatedEval = { ...curr, feedbacksHistory: updatedFeedbacksHistory }; 
                    await actions.saveEvaluation(updatedEval); 
                    setCurr(updatedEval); 
                };

        // --- HELPERS VISUAIS (CORRIGIDO: REINSERIDOS) ---
        const getQualiColAvg = (q) => { 
            if(!curr) return '-'; 
            let sum=0, count=0;
            curr.qualitative.criteria?.forEach(c => { if(c[q] !== null && c[q] !== undefined && c[q] !== ''){ sum+=parseFloat(c[q]); count++; } });
            const avg = count > 0 ? (sum/count) : 0;
            return avg > 0 ? (avg * (10/4)).toFixed(4) : '-';
        };

        const getRowAvg = (c) => { 
            let sum=0, count=0;
            ['q1','q2','q3','q4'].forEach(q=>{ if(c[q] !== null && c[q] !== undefined && c[q] !== ''){ sum+=parseFloat(c[q]); count++; } });
            return count > 0 ? (sum/count).toFixed(4) : '-'; 
        };

        const getQuantiColTotal = (q) => { 
            if(!curr) return 0;
            let sum=0;
            curr.quantitative.errors?.forEach(e => { if(e[q]) sum += (parseFloat(e[q]) * e.peso); }); 
            return sum.toFixed(4); 
        };

        const getQuantiRowTotal = (e) => { 
            let sum=0;
            ['q1','q2','q3','q4'].forEach(q=>{ if(e[q]) sum += (parseFloat(e[q]) * e.peso); }); 
            return sum.toFixed(4); 
        };

        const handleSaveNote = async () => {
            if (!curr) return;
            
            // 1. Validação de Tipo
            if (noteForm.tipo === 'Assinatura' && !noteForm.id) {
                setValidationAlert({
                    title: "Ação Não Permitida",
                    message: "O tipo 'Assinatura' é reservado para registros automáticos do sistema."
                });
                return;
            }

            // 2. Validação: DATA FORA DO ANO DA AVALIAÇÃO
            const noteDate = new Date(noteForm.date + 'T00:00:00');
            const noteYear = noteDate.getFullYear();
            const evalYear = parseInt(curr.periodYear);
            
            if (noteYear !== evalYear) {
                setValidationAlert({
                    title: "Data Inválida",
                    message: `Esta avaliação é referente ao ano de **${evalYear}**.\nVocê não pode inserir uma anotação com data de **${noteYear}**.`
                });
                return;
            }

            // 3. Cálculo do Trimestre da Nota
            const month = noteDate.getMonth() + 1; // 1 a 12
            let targetQuarter = 1;
            if (month > 3) targetQuarter = 2;
            if (month > 6) targetQuarter = 3;
            if (month > 9) targetQuarter = 4;
            const qKey = `q${targetQuarter}`;

            // --- NOVO: BLOQUEIO POR ASSINATURA ---
            // Verifica se este trimestre já foi assinado pelo gestor
            if (checkSignatureLock(qKey)) return;
            // -------------------------------------

            // 4. Validação: TRIMESTRE FECHADO (Status antigo)
            const statusMap = { 
                'Em Andamento': 0, 'T1-Concluido': 1, 'T2-Concluido': 2, 
                'T3-Concluido': 3, 'T4-Concluido': 4, 'Finalizada': 4 
            };
            const currentLockLevel = statusMap[curr.status] || 0;

            if (currentLockLevel >= targetQuarter) {
                setValidationAlert({
                    title: "Período Fechado",
                    message: `O **${targetQuarter}º Trimestre** já foi concluído nesta avaliação (Status: ${curr.status}).\nNão é permitido inserir ou editar anotações em períodos retroativos já encerrados.`
                });
                return;
            }

            // --- SALVAMENTO ---
            const noteId = noteForm.id || Date.now().toString();
            const colorIndex = parseInt(noteId.slice(-1)) % NOTE_COLORS.length; 

            const newNote = {
                id: noteId,
                text: noteForm.text,
                date: noteForm.date,
                tipo: noteForm.tipo || 'OBS',
                prioridade: noteForm.prioridade || 'Baixa',
                status: noteForm.status || 'Aberta',
                timestamp: Date.now(),
                colorIndex: colorIndex 
            };

            const currentNotes = curr.personalNotes || {};
            const updatedNotes = { ...currentNotes, [noteId]: newNote };
            
            const updatedEval = { ...curr, personalNotes: updatedNotes };
            await actions.saveEvaluation(updatedEval);
            
            setCurr(updatedEval);
            setIsNotesModalOpen(false);
            setNoteForm({ id: null, text: '', date: new Date().toISOString().split('T')[0], tipo: 'OBS', prioridade: 'Baixa' });
        };

            // Abre o modal de confirmação
        const handleDeleteNote = (id) => {
            setNoteToDelete(id);
        };

        const confirmDeleteNote = async () => {
            if (!noteToDelete) return;
            
            // --- NOVO: BLOQUEIO POR ASSINATURA ---
            // Recupera a nota original para saber a data
            const targetNote = curr.personalNotes ? curr.personalNotes[noteToDelete] : null;
            
            if (targetNote) {
                const m = parseInt(targetNote.date.split('-')[1]);
                const qKey = m <= 3 ? 'q1' : m <= 6 ? 'q2' : m <= 9 ? 'q3' : 'q4';
                
                // Se o trimestre da nota estiver assinado, bloqueia exclusão
                if (checkSignatureLock(qKey)) {
                    setNoteToDelete(null); // Fecha o modal
                    return;
                }
            }
            // -------------------------------------
            
            const currentNotes = { ...(curr.personalNotes || {}) };
            delete currentNotes[noteToDelete];
            
            const updatedEval = { ...curr, personalNotes: currentNotes };
            await actions.saveEvaluation(updatedEval);
            
            setCurr(updatedEval);
            setNoteToDelete(null);
        };

        const handleEditNote = (note) => {
            setNoteForm({
                id: note.id,
                text: note.text,
                date: note.date
            });
            setIsNotesModalOpen(true);
        };

        const onHoverQuantiCell = (e, errorName, quarter) => { 
            if(!curr) return;
            
            const qMonths = { q1:['01','02','03'], q2:['04','05','06'], q3:['07','08','09'], q4:['10','11','12'] };
            const targetMonths = qMonths[quarter]; 
            
            const incidents = data.apontamentos.filter(ap => 
                ap.collaboratorId === curr.collaboratorId && 
                ap.type === errorName && 
                targetMonths.includes(ap.date.split('-')[1]) && 
                ap.date.startsWith(curr.periodYear)
            );
            
            if (incidents.length === 0) return; 
            
            const rect = e.currentTarget.getBoundingClientRect();
            
            // CORREÇÃO: Usando setTooltipState (conforme definido no useState)
            setTooltipState({ 
                visible: true, 
                type: 'incident', 
                x: rect.left + rect.width/2, 
                y: rect.top, 
                incidents: incidents 
            });
        };

        const handleKeyDown = (e, type, idx, q, totalRows) => { 
            if (e.key === 'Enter' || e.key === 'Tab') { 
                e.preventDefault(); 
                let nextIdx = idx + 1; let nextQ = q; const qs = ['q1', 'q2', 'q3', 'q4']; 
                if (nextIdx >= totalRows) { 
                    nextIdx = 0; 
                    const currQIdx = qs.indexOf(q); 
                    if (currQIdx < qs.length - 1) nextQ = qs[currQIdx + 1]; else return; 
                } 
                const nextInput = document.querySelector(`input[data-nav="${type}-${nextIdx}-${nextQ}"]`); 
                if(nextInput) nextInput.focus(); 
            } 
        };

        const getScoreClass = (val) => { 
            if(!val) return isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-800';
            const v = parseFloat(val);
            if(v <= 1.5) return '!bg-red-500 !text-white !border-red-600';
            if(v <= 2.5) return '!bg-orange-500 !text-white !border-orange-600'; 
            if(v <= 3.5) return '!bg-green-500 !text-white !border-green-600';
            if(v <= 4.5) return '!bg-blue-500 !text-white !border-blue-600'; 
            return '!bg-indigo-600 !text-white !border-indigo-700';
        };

        // ========================================================================
        // 6. RENDERIZAÇÃO
        // ========================================================================

        useEffect(() => {
            if (openEvalId) {
                if (!curr || curr.id !== openEvalId) {
                    const evOriginal = data.evaluations.find(e => e.id === openEvalId);
if (evOriginal) {
    const ev = JSON.parse(JSON.stringify(evOriginal));
    // Uso de ?. para evitar quebra se o colaborador (c) não for encontrado
    const c = data.collaborators.find(col => col.id === ev?.collaboratorId);
    ev.personType = ev?.personType || c?.personType || 'Colaborador';
                        ev.feedbacksHistory = ev.feedbacksHistory || {};

                        ev.qualitative = ev.qualitative || {};
                        ev.qualitative.criteria = Array.isArray(ev.qualitative.criteria)
                            ? ev.qualitative.criteria
                            : [];

                        ev.quantitative = ev.quantitative || {};
                        ev.quantitative.errors = Array.isArray(ev.quantitative.errors)
                            ? ev.quantitative.errors
                            : [];

                        ev.atualizacao = ev.atualizacao || {
                            D1_hours: 0,
                            D2_hours: 0,
                            D1_nota: 0,
                            D2_nota: 0,
                            total: 0
                        };

                        if (ev.personType === 'Gestão') {
                            const baseGestaoMetas = Array.isArray(data.gestaoMetasPercepcao)
                                ? data.gestaoMetasPercepcao
                                : [];

                            ev.gestaoMetasPercepcao = Array.isArray(ev.gestaoMetasPercepcao) && ev.gestaoMetasPercepcao.length > 0
                                ? ev.gestaoMetasPercepcao
                                : baseGestaoMetas.map(item => ({
                                    ...item,
                                    scores: item.scores || {
                                        q1: null,
                                        q2: null,
                                        q3: null,
                                        q4: null
                                    }
                                }));
                        }

                        let evWithScores = recalculateFinalAverage(ev);
                        let restoredState = null;
                        try {
                            const rawSavedState = sessionStorage.getItem(`acad_eval_work_state_${openEvalId}`);
                            if (rawSavedState) restoredState = JSON.parse(rawSavedState);
                        } catch (e) {
                            restoredState = null;
                        }

                        if (restoredState?.curr?.id === openEvalId) {
                            evWithScores = recalculateFinalAverage(restoredState.curr);
                            setActiveTab(restoredState.activeTab || 'resumo');
                            setForceEditMode(!!restoredState.forceEditMode);
                        } else {
                            setActiveTab('resumo');
                            setForceEditMode(false);
                        }

                        setCurr(evWithScores);
                        setView('edit');
                    } else {
                        actions.setOpenId(null);
                        setView('list');
                    }
                }
            } else {
                setCurr(null);
            }
        }, [openEvalId, data.evaluations, data.collaborators, data.gestaoMetasPercepcao, data.apontamentos]);
        // Limpamos dependências excessivas para evitar re-renderizações desnecessárias

        // --- CORREÇÃO: SINCRONIZAÇÃO EM TEMPO REAL E MANUAL DE OCORRÊNCIAS ---
        useEffect(() => {
            if (openEvalId && curr && curr.id === openEvalId) {
                const syncedErrors = getSyncedQuantitativeErrors(
                    curr, 
                    data.apontamentos, 
                    data.quantiCriteria, 
                    data.collaborators, 
                    data.evaluationTypes
                );

                // Compara as listas para evitar atualizações infinitas de estado
                let hasChanged = syncedErrors.length !== (curr.quantitative.errors || []).length;
                if (!hasChanged) {
                    for (let i = 0; i < syncedErrors.length; i++) {
                        const s = syncedErrors[i];
                        const c = curr.quantitative.errors[i];
                        if (
                            !c ||
                            s.id !== c.id ||
                            s.name !== c.name ||
                            s.peso !== c.peso ||
                            s.q1 !== c.q1 ||
                            s.q2 !== c.q2 ||
                            s.q3 !== c.q3 ||
                            s.q4 !== c.q4
                        ) {
                            hasChanged = true;
                            break;
                        }
                    }
                }

                if (hasChanged) {
                    setCurr(prev => {
                        if (!prev) return prev;
                        const nextCurr = {
                            ...prev,
                            quantitative: {
                                ...prev.quantitative,
                                errors: syncedErrors
                            }
                        };
                        return recalculateFinalAverage(nextCurr);
                    });
                }
            }
        }, [data.apontamentos, data.quantiCriteria, data.collaborators, data.evaluationTypes, openEvalId, curr]);

        const manualSyncOcorrencias = () => {
            if (!curr) return;
            const syncedErrors = getSyncedQuantitativeErrors(
                curr, 
                data.apontamentos, 
                data.quantiCriteria, 
                data.collaborators, 
                data.evaluationTypes
            );
            const nextCurr = {
                ...curr,
                quantitative: {
                    ...curr.quantitative,
                    errors: syncedErrors
                }
            };
            const updatedCurr = recalculateFinalAverage(nextCurr);
            setCurr(updatedCurr);
            alert("Ocorrências sincronizadas com sucesso!");
        };

        if (view === 'list') {
            const visibleEvaluations = data.evaluations.filter(ev => {
                const c = data.collaborators.find(x => x.id === ev.collaboratorId);

                if (ev.personType === 'Gestão' && currentUser?.permissions?.viewGestaoDashEval !== true) {
                    return false;
                }

                if (!canViewCollab(c, currentUser)) return false;

                if (searchTerm) {
                    const searchLower = searchTerm.toLowerCase();
                    return ev.collaboratorName.toLowerCase().includes(searchLower) || ev.periodYear.includes(searchLower) || (ev.status && ev.status.toLowerCase().includes(searchLower));
                }

                return true;
            }).sort((a, b) => Number(b.periodYear) - Number(a.periodYear));
            
            return (
                <div className="space-y-4">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
                        <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Avaliações</h2>
                        <div className="flex flex-1 justify-end items-center gap-2 w-full md:w-auto">
                            <div className="relative w-full md:w-64">
                                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} size={16} />
                                <Input placeholder="Buscar nome, ano..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={`pl-9 mb-0 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-gray-200'}`}/>
                            </div>
                            <Button onClick={()=>setView('new')} className="h-[42px]"><Plus size={16}/> Nova</Button>
                        </div>
                    </div>
                    <Card className="overflow-hidden shadow-md p-0" darkMode={isDarkMode}>
                        <div className="max-h-[600px] overflow-y-auto">
                            <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : ''}`}>
                                <thead className={`text-xs uppercase font-bold sticky top-0 z-10 border-b ${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                    <tr><th className="p-4 text-left">Nome</th><th className="p-4 text-center">Ano</th><th className="p-4 text-center">Departamento</th><th className="p-4 text-left">Gestor</th><th className="p-4 text-center">Tipo Avaliação</th><th className="p-4 text-center w-32">Ações</th></tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700 bg-slate-800' : 'divide-gray-100 bg-white'}`}>
                                    {visibleEvaluations.map((ev, i) => {
                                        const collab = data.collaborators.find(c => c.id === ev.collaboratorId);
                                        const linkedManagers = data.managers.filter(m => collab?.managerIds && collab.managerIds.includes(m.id));
                                        const managerNames = linkedManagers.length > 0 ? linkedManagers.map(m => m.name).join(' / ') : '-';
                                        const rowClass = i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');
                                        const hoverClass = isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-blue-50';
                                        return (
                                            <tr key={ev.id} className={`${rowClass} ${hoverClass} transition-colors group border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                            <td className={`p-4 font-bold align-middle ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                                <div className="flex items-center gap-2">
                                                    
                                                    {/* 1. BLOCO DE PENDÊNCIAS (VERMELHO) - JÁ EXISTENTE */}
                                                    {(() => {
                                                        const openPendencies = Object.values(ev.personalNotes || {}).filter(n => (n.tipo === 'Pendênca' || n.tipo === 'Pendência') && n.status !== 'Concluída');
                                                        if (openPendencies.length > 0) {
                                                            return (
                                                                <div className="relative group/evalpnd">
                                                                    <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center text-white animate-pulse cursor-help border border-white shadow-md">
                                                                        <AlertCircle size={14} strokeWidth={3} />
                                                                    </div>
                                                                    <div className="absolute left-0 bottom-full mb-2 w-72 bg-slate-900 text-white p-2 rounded-lg shadow-2xl opacity-0 invisible group-hover/evalpnd:opacity-100 group-hover/evalpnd:visible transition-all z-[100] border border-red-600 text-[10px]">
                                                                        <p className="font-bold uppercase text-red-400 mb-1 border-b border-red-600/30 pb-1">Pendências ({openPendencies.length})</p>
                                                                        <ul className="space-y-1 max-h-32 overflow-y-auto custom-scrollbar">
                                                                            {openPendencies.map(p => (
                                                                                <li key={p.id} className="leading-tight opacity-90 flex gap-1 border-b border-slate-800 pb-1 mb-1 last:border-0">
                                                                                    <span className="font-mono font-bold text-red-300 whitespace-nowrap">[{new Date(p.date + 'T00:00:00').toLocaleDateString('pt-BR').substring(0, 5)}]:</span>
                                                                                    <span className="truncate">{p.text.substring(0, 50)}...</span>
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    })()}

                                                    {/* 2. BLOCO DE OBSERVAÇÕES (AMARELO) - NOVO */}
                                                    {(() => {
                                                        const openObs = Object.values(ev.personalNotes || {}).filter(n => n.tipo === 'OBS' && n.status !== 'Concluída');
                                                        if (openObs.length > 0) {
                                                            return (
                                                                <div className="relative group/evalobs">
                                                                    <div className="w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center text-white cursor-help border border-white shadow-md">
                                                                        <span className="font-bold text-[10px]">!</span>
                                                                    </div>
                                                                    <div className="absolute left-0 bottom-full mb-2 w-72 bg-slate-800 text-white p-2 rounded-lg shadow-2xl opacity-0 invisible group-hover/evalobs:opacity-100 group-hover/evalobs:visible transition-all z-[100] border border-yellow-500 text-[10px]">
                                                                        <p className="font-bold uppercase text-yellow-400 mb-1 border-b border-yellow-500/30 pb-1">Observações ({openObs.length})</p>
                                                                        <ul className="space-y-1 max-h-32 overflow-y-auto custom-scrollbar">
                                                                            {openObs.map(p => (
                                                                                <li key={p.id} className="leading-tight opacity-90 flex gap-1 border-b border-slate-700 pb-1 mb-1 last:border-0">
                                                                                    <span className="font-mono font-bold text-yellow-300 whitespace-nowrap">[{new Date(p.date + 'T00:00:00').toLocaleDateString('pt-BR').substring(0, 5)}]:</span>
                                                                                    <span className="truncate">{p.text.substring(0, 50)}...</span>
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    })()}

                                                    <span>{ev.collaboratorName}</span>
                                                </div>
                                            </td>                                            <td className={`p-4 text-center align-middle ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}><span className={`px-2 py-1 rounded text-xs font-bold border ${isDarkMode ? 'bg-slate-900 text-slate-300 border-slate-600' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>{ev.periodYear}</span></td>
                                                <td className={`p-4 text-center align-middle ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{ev.department}</td>
                                                <td className={`p-4 text-left align-middle text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{managerNames}</td>
                                                <td className="p-4 text-center align-middle"><span className={`px-3 py-1 rounded-full text-xs font-bold border shadow-sm ${isDarkMode ? 'bg-indigo-900/30 text-indigo-300 border-indigo-800' : 'bg-indigo-50 text-indigo-700 border-indigo-100'}`}>{ev.evalTypeName || 'Padrão'}</span></td>
                                                <td className="p-4 text-center align-middle"><div className="flex justify-center gap-3 opacity-80 group-hover:opacity-100 transition-opacity"><button onClick={() => actions.edit(ev)} className={`p-2 rounded-lg transition-all ${isDarkMode ? 'text-blue-400 hover:text-blue-300 hover:bg-slate-600' : 'text-blue-600 hover:text-blue-800 hover:bg-blue-100'}`} title="Abrir Avaliação"><Eye size={18}/></button><button onClick={() => actions.delete(ev.id)} className={`p-2 rounded-lg transition-all ${isDarkMode ? 'text-red-400 hover:text-red-300 hover:bg-slate-600' : 'text-red-500 hover:text-red-700 hover:bg-red-100'}`} title="Excluir Avaliação"><Trash2 size={18}/></button></div></td>
                                            </tr>
                                        );
                                    })}
                                    {visibleEvaluations.length === 0 && (<tr><td colSpan={6} className={`p-8 text-center italic ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Nenhuma avaliação encontrada.</td></tr>)}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </div>
            );
        }

            if (view === 'new') return (
            <Card className="max-w-md mx-auto mt-10" darkMode={isDarkMode}>
                <h3 className={`font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Iniciar Avaliação</h3>
                
                <div className="space-y-4">
                {/* --- CHECKBOX DE VISIBILIDADE (DINÂMICO) --- */}
                { (currentUser?.role === 'SuperAdm' || currentUser?.permissions?.viewGestao || currentUser?.permissions?.viewGestaoCadastros) && (
                    <div className={`flex items-center gap-2 p-2 rounded border mb-2 ${isDarkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-100'}`}>
                        <input 
                            type="checkbox" 
                            id="overrideChk"
                            checked={showAllOverride}
                            onChange={(e) => setShowAllOverride(e.target.checked)}
                            className="rounded cursor-pointer text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="overrideChk" className={`text-xs font-bold cursor-pointer select-none ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                            {currentUser.role === 'SuperAdm'
                                ? "Exibir TODOS (Ignorar regras de Depto/Tipo)"
                                : "Incluir Gestão (Do meu departamento)"}
                        </label>
                    </div>
                )}

                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Colaborador</label>
                    <select 
                        id="sc" 
                        className={`w-full p-2 border rounded outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                    >
                        <option value="">Selecione...</option>
                        {visibleCollaboratorsList.map(c => (
                            <option key={c.id} value={c.id}>
                                {c.name} ({c.personType})
                            </option>
                        ))}
                    </select>

                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Ano de Referência</label>
                    <Input 
                        id="sy" 
                        type="number" 
                        defaultValue={new Date().getFullYear()} 
                        className={`font-bold text-center ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`} 
                    />
                    
                    <div className="flex gap-2 pt-2">
                        <Button 
                            variant="secondary" 
                            onClick={() => { setView('list'); setShowAllOverride(false); }} 
                            className={`w-full ${isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600' : ''}`}
                        >
                            Cancelar
                        </Button>
                        <Button 
                            onClick={() => initEval(document.getElementById('sc').value, document.getElementById('sy').value)} 
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            Iniciar
                        </Button>
                    </div>
                </div>
            </Card>
        );

        if (!curr) return <div className={`text-center p-8 ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>Carregando avaliação...</div>;
        
        const currWithScores = recalculateFinalAverage(curr);
        const isFinalized = curr.status === 'Finalizada' || curr.status === 'T4-Concluido';
        const isLocked = isFinalized && !forceEditMode;

        return (
            <div className="space-y-6 relative">
                
                {/* ================================================================================= */}
                {/* 1. MODAL DE FEEDBACK (CORRIGIDO COM NOVOS CAMPOS: CLASSIFICAÇÃO E ACOMPANHAMENTO) */}
                {/* ================================================================================= */}
                {isFeedbackModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setIsFeedbackModalOpen(false)}>
                        <div className={`p-6 rounded-xl w-full max-w-2xl shadow-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            
                            {/* Header do Modal */}
                            <div className={`flex justify-between items-center mb-4 border-b pb-2 ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                <h3 className="font-bold text-lg flex items-center gap-2">
                                    {feedbackForm.id ? <Edit3 size={18}/> : <Plus size={18}/>} 
                                    {feedbackForm.type === 'PDI' ? (feedbackForm.id ? 'Editar Meta PDI' : 'Nova Meta PDI') : (feedbackForm.id ? 'Editar Feedback' : 'Novo Feedback')}
                                </h3>
                                <button onClick={() => setIsFeedbackModalOpen(false)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                            </div>

                            {/* --- VISUALIZAÇÃO EXCLUSIVA PARA PDI --- */}
                            {feedbackForm.type === 'PDI' ? (
                                <div className="space-y-4 animate-fade-in">
                                    <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-orange-900/10 border-orange-800' : 'bg-orange-50 border-orange-200'}`}>
                                        <label className={`text-xs font-bold block mb-2 uppercase tracking-wide ${isDarkMode ? 'text-orange-400' : 'text-orange-700'}`}>Ação Prática / O que será feito?</label>
                                        <textarea 
                                            className={`w-full p-3 border rounded-lg h-32 resize-none outline-none focus:ring-2 focus:ring-orange-500 custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-gray-300'}`}
                                            placeholder="Descreva a meta específica. Ex: Liderar o próximo projeto; Realizar o curso avançado de Power BI..."
                                            value={feedbackForm.text} 
                                            onChange={e=>setFeedbackForm({...feedbackForm, text:e.target.value})}
                                        ></textarea>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Data de Criação</label>
                                            <input type="date" value={feedbackForm.date} onChange={e => setFeedbackForm({...feedbackForm, date: e.target.value})} className={`w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-orange-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white invert-calendar' : 'bg-white border-gray-300'}`}/>
                                        </div>
                                        <div>
                                            <label className={`text-xs font-bold block mb-1 flex items-center gap-1 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}><Clock size={12}/> Prazo Alvo (Conclusão)</label>
                                            <input type="date" value={feedbackForm.followUpDate} onChange={e => setFeedbackForm({...feedbackForm, followUpDate: e.target.value})} className={`w-full p-2.5 border rounded-lg outline-none font-bold focus:ring-2 focus:ring-orange-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white invert-calendar' : 'bg-white border-gray-300'}`}/>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* --- VISUALIZAÇÃO PADRÃO PARA FEEDBACKS --- */
                                <div className="space-y-4 animate-fade-in">
                                    <div className={`p-3 rounded border ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                                        <label className={`text-xs font-bold block mb-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Classificação do Feedback</label>
                                        <div className="flex gap-4">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="radio" name="nature" checked={feedbackForm.nature === 'Positivo'} onChange={() => setFeedbackForm({...feedbackForm, nature: 'Positivo', actionTag: 'Avance', followUpDate: ''})} className="text-blue-600 focus:ring-blue-500"/>
                                                <span className={`text-sm font-bold ${feedbackForm.nature === 'Positivo' ? 'text-blue-600' : (isDarkMode ? 'text-slate-500' : 'text-gray-500')}`}>Positivo (Elogio/Reconhecimento)</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="radio" name="nature" checked={feedbackForm.nature === 'Corretivo'} onChange={() => setFeedbackForm({...feedbackForm, nature: 'Corretivo', actionTag: '', followUpDate: ''})} className="text-red-600 focus:ring-red-500"/>
                                                <span className={`text-sm font-bold ${feedbackForm.nature === 'Corretivo' ? 'text-red-600' : (isDarkMode ? 'text-slate-500' : 'text-gray-500')}`}>Corretivo (Ajuste de Conduta)</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Origem/Tipo</label>
                                            <select className={`w-full p-2 border rounded text-sm outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={feedbackForm.type} onChange={e => setFeedbackForm({...feedbackForm, type: e.target.value})}>
                                                <option value="Gestão">Gestão</option>
                                                <option value="Colaborador">Colaborador</option>
                                                <option value="AvDes">AvDes (RH)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Data do Fato</label>
                                            <input type="date" className={`w-full p-2 border rounded text-sm outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white invert-calendar' : 'bg-white border-gray-300'}`} value={feedbackForm.date} min={`${curr.periodYear}-01-01`} max={`${curr.periodYear}-12-31`} onChange={e => { const newDate = e.target.value; setFeedbackForm({...feedbackForm, date: newDate, followUpDate: calculateFollowUp(newDate, feedbackForm.followUpDays)}); }}/>
                                        </div>

                                        {(feedbackForm.type === 'Gestão' || feedbackForm.type === 'AvDes') && (
                                            <div className="animate-fade-in">
                                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>Direcionamento</label>
                                                <select className={`w-full p-2 border-2 rounded text-sm font-bold outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-blue-900 text-white' : 'bg-blue-50 border-blue-200 text-gray-800'}`} value={feedbackForm.actionTag} onChange={e => { const tag = e.target.value; let updates = { actionTag: tag }; if ((tag === 'Reveja' || tag === 'Pare') && !feedbackForm.followUpDate) { updates.followUpDays = '90'; updates.followUpDate = calculateFollowUp(feedbackForm.date, '90'); } setFeedbackForm({...feedbackForm, ...updates}); }}>
                                                    <option value="">Selecione...</option>
                                                    {feedbackForm.nature === 'Positivo' && <option value="Avance">🔵 AVANCE</option>}
                                                    {feedbackForm.nature === 'Corretivo' && <><option value="Reveja">🟠 REVEJA</option><option value="Pare">🔴 PARE</option></>}
                                                </select>
                                            </div>
                                        )}
                                    </div>

                                    {(feedbackForm.actionTag === 'Reveja' || feedbackForm.actionTag === 'Pare') && (
                                        <div className={`p-3 rounded border animate-fade-in ${isDarkMode ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200'}`}>
                                            <div className="flex items-center gap-2 mb-2 text-yellow-600 dark:text-yellow-400 font-bold text-xs uppercase"><Clock size={14}/> Plano de Acompanhamento</div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Revisar em:</label>
                                                    <select className={`w-full p-2 border rounded text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={feedbackForm.followUpDays} onChange={(e) => { const days = e.target.value; setFeedbackForm({...feedbackForm, followUpDays: days, followUpDate: days === 'custom' ? '' : calculateFollowUp(feedbackForm.date, days)}); }}>
                                                        <option value="30">30 dias</option><option value="60">60 dias</option><option value="90">90 dias (Padrão)</option><option value="120">120 dias</option><option value="180">180 dias</option><option value="360">360 dias</option><option value="custom">Data Personalizada</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Data Limite</label>
                                                    <input type="date" className={`w-full p-2 border rounded text-sm font-bold ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white invert-calendar' : 'bg-white border-gray-300'}`} value={feedbackForm.followUpDate} disabled={feedbackForm.followUpDays !== 'custom'} onChange={(e) => setFeedbackForm({...feedbackForm, followUpDate: e.target.value})}/>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="relative">
                                        <textarea className={`w-full p-3 border rounded-lg h-32 resize-none text-sm outline-none focus:ring-2 focus:ring-blue-500 custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-gray-300'}`} placeholder="Descreva o feedback, ponto de atenção ou elogio..." value={feedbackForm.text} onChange={e=>setFeedbackForm({...feedbackForm, text:e.target.value})}></textarea>
                                        <button onClick={handleEnhanceFeedback} disabled={isGeneratingFeedback} className="absolute bottom-3 right-3 bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 shadow-md transition-all disabled:opacity-50 z-10" title="Gerar feedback com IA">
                                            {isGeneratingFeedback ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>} Melhorar com IA
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className={`flex justify-end gap-2 mt-6 pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                <Button variant="secondary" onClick={()=>setIsFeedbackModalOpen(false)} className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}>Cancelar</Button>
                                <Button onClick={handleSaveFeedback} className="bg-blue-600 hover:bg-blue-700 text-white shadow-md">Salvar Registro</Button>
                            </div>
                        </div>
                    </div>
                )}


                {/* MODAL DE ANOTAÇÕES COMPLETO COM TIPO E PRIORIDADE */}
                {isNotesModalOpen && (
                    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setIsNotesModalOpen(false)}>
                        <div className={`p-6 rounded-xl w-full max-w-xl shadow-2xl space-y-4 ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            
                            {/* Header do Modal */}
                            <div className={`flex justify-between items-center border-b pb-2 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                <h3 className="font-bold text-lg flex items-center gap-2">
                                    {noteForm.id ? <Edit3 size={18} className="text-blue-500"/> : <Plus size={18} className="text-teal-500"/>}
                                    {noteForm.id ? 'Editar Anotação' : 'Nova Anotação'}
                                </h3>
                                <button onClick={() => setIsNotesModalOpen(false)} className={`hover:text-gray-500 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}><X size={20}/></button>
                            </div>
                            
                            {/* Grade de Configuração (Data, Tipo e Prioridade) */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Campo: Data */}
                                <div>
                                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Data</label>
                                    <Input 
                                        type="date" 
                                        value={noteForm.date} 
                                        onChange={e => setNoteForm({...noteForm, date: e.target.value})}
                                        className={isDarkMode ? 'bg-slate-900 border-slate-600 text-white invert-calendar' : ''}
                                    />
                                </div>

                                {/* Campo: Tipo */}
                                <div>
                                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Tipo</label>
                                    <select 
                                        className={`w-full p-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                                        value={noteForm.tipo || 'OBS'}
                                        onChange={e => setNoteForm({...noteForm, tipo: e.target.value})}
                                        // Se estiver editando uma nota que JÁ É assinatura (gerada pelo sistema), desabilita a edição do tipo
                                        disabled={noteForm.tipo === 'Assinatura'}
                                    >
                                        <option value="OBS">OBS</option>
                                        <option value="Pendênca">Pendência</option>
                                        
                                        {/* Só mostra a opção Assinatura se a nota JÁ FOR desse tipo (para visualização), 
                                            caso contrário, esconde para criação manual */}
                                        {noteForm.tipo === 'Assinatura' && (
                                            <option value="Assinatura">Registro de Assinatura</option>
                                        )}
                                    </select>
                                </div>

                                {/* Campo: Prioridade (Não obrigatório, padrão 'Baixa' na gravação) */}
                                <div>
                                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Prioridade</label>
                                    <select 
                                        className={`w-full p-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                                        value={noteForm.prioridade || 'Baixa'}
                                        onChange={e => setNoteForm({...noteForm, prioridade: e.target.value})}
                                    >
                                        <option value="Baixa">Baixa</option>
                                        <option value="Média">Média</option>
                                        <option value="Alta">Alta</option>
                                        <option value="Urgente">Urgente</option>
                                    </select>
                                </div>
                            </div>

                            {/* Campo: Conteúdo da Anotação */}
                            <div>
                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Descrição Detalhada</label>
                                <textarea 
                                    className={`w-full p-3 border rounded-lg h-40 resize-none text-sm outline-none focus:ring-2 focus:ring-teal-500 custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-gray-300 text-gray-800'}`}
                                    placeholder="Descreva aqui os detalhes da observação ou pendência..."
                                    value={noteForm.text} 
                                    onChange={e => setNoteForm({...noteForm, text: e.target.value})}
                                ></textarea>
                            </div>

                            {/* Rodapé e Ações */}
                            <div className={`flex justify-end gap-2 pt-2 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                <Button 
                                    variant="secondary" 
                                    onClick={() => setIsNotesModalOpen(false)} 
                                    className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}
                                >
                                    Cancelar
                                </Button>
                                <Button 
                                    onClick={handleSaveNote} 
                                    className={isDarkMode ? 'bg-teal-600 hover:bg-teal-500 text-white' : 'bg-teal-600 hover:bg-teal-700 text-white'}
                                    icon={Save}
                                >
                                    Salvar Anotação
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

        {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO (ANOTAÇÃO) */}
                {noteToDelete && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setNoteToDelete(null)}>
                        <div className={`p-6 rounded-xl w-96 shadow-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex items-center gap-3 mb-4 text-red-500">
                                <div className={`p-2 rounded-full ${isDarkMode ? 'bg-red-900/30' : 'bg-red-100'}`}>
                                    <Trash2 size={24}/>
                                </div>
                                <h3 className="font-bold text-lg">Excluir Anotação?</h3>
                            </div>
                            
                            <p className={`text-sm mb-6 leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                Tem certeza que deseja remover esta anotação permanentemente? Esta ação não pode ser desfeita.
                            </p>

                            <div className="flex justify-end gap-2">
                                <Button 
                                    variant="secondary" 
                                    onClick={() => setNoteToDelete(null)} 
                                    className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}
                                >
                                    Cancelar
                                </Button>
                                <Button 
                                    onClick={confirmDeleteNote} 
                                    className="bg-red-600 hover:bg-red-700 text-white shadow-md border-0"
                                >
                                    Sim, Excluir
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- MODAL GENÉRICO DE SINCRONIZAÇÃO (INPUT DE ANO) --- */}
                {syncModalConfig.isOpen && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className={`p-6 rounded-xl w-96 shadow-2xl animate-fade-in space-y-4 ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white'}`}>
                            <h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                <RefreshCw size={18}/> {syncModalConfig.title}
                            </h3>
                            <div>
                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                                    Ano Referência
                                </label>
                                <Input type="number" value={batchYearInput} onChange={e => setBatchYearInput(e.target.value)} className={`text-center text-lg tracking-widest font-bold ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`} maxLength={4}/>
                                <p className={`text-[10px] mt-2 ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                                    A ação será executada para o ano informado.
                                </p>
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <Button variant="secondary" onClick={() => setSyncModalConfig(prev => ({...prev, isOpen: false}))} disabled={isProcessingBatch} className={isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600' : ''}>Cancelar</Button>
                                <Button onClick={() => syncModalConfig.callback(batchYearInput)} disabled={isProcessingBatch} className="bg-blue-600 hover:bg-blue-700 text-white">
                                    {isProcessingBatch ? <><Loader2 size={16} className="animate-spin mr-2"/> Processando...</> : "Confirmar"}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

            {/* Card Principal (CABEÇALHO REORGANIZADO: Notas na linha superior direita) */}
                <Card className={`rounded-xl shadow-sm border p-5 flex flex-col md:flex-row justify-between items-center gap-4 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`} darkMode={isDarkMode}>
                    
                    {/* LADO ESQUERDO: Nome e Dados Cadastrais com Alertas */}
                    <div className="flex flex-col gap-1 w-full md:w-auto">
                        <div className="flex items-center gap-3">
                            
                            {/* ALERTA VERMELHO: PENDÊNCIAS */}
                            {(() => {
                                const openPendencies = Object.values(curr.personalNotes || {}).filter(n => (n.tipo === 'Pendênca' || n.tipo === 'Pendência') && n.status !== 'Concluída');
                                if (openPendencies.length > 0) {
                                    return (
                                        <div className="relative group/pnd">
                                            <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white animate-pulse cursor-help border-2 border-white shadow-lg">
                                                <AlertCircle size={20} strokeWidth={3} />
                                            </div>
                                            <div className="absolute left-0 top-full mt-2 w-72 bg-slate-800 text-white p-3 rounded-lg shadow-2xl opacity-0 invisible group-hover/pnd:opacity-100 group-hover/pnd:visible transition-all z-[100] border border-red-500 text-[10px]">
                                                <p className="font-bold uppercase text-red-400 mb-2 border-b border-red-500/30 pb-1">Pendências Críticas ({openPendencies.length})</p>
                                                <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
                                                    {openPendencies.map(p => (
                                                        <div key={p.id} className="text-[11px] leading-tight border-b border-slate-700 pb-1 last:border-0 flex gap-1">
                                                            <span className="font-bold text-red-300 whitespace-nowrap">[{new Date(p.date + 'T00:00:00').toLocaleDateString('pt-BR').substring(0, 5)}]</span> 
                                                            <span className="truncate">{p.text.substring(0, 100)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            })()}

                            {/* ALERTA AMARELO: OBSERVAÇÕES (NOVO) */}
                            {(() => {
                                const openObs = Object.values(curr.personalNotes || {}).filter(n => n.tipo === 'OBS' && n.status !== 'Concluída');
                                if (openObs.length > 0) {
                                    return (
                                        <div className="relative group/obs">
                                            <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center text-white cursor-help border-2 border-white shadow-lg">
                                                <span className="font-black text-lg">!</span>
                                            </div>
                                            <div className="absolute left-0 top-full mt-2 w-72 bg-slate-800 text-white p-3 rounded-lg shadow-2xl opacity-0 invisible group-hover/obs:opacity-100 group-hover/obs:visible transition-all z-[100] border border-yellow-500 text-[10px]">
                                                <p className="font-bold uppercase text-yellow-400 mb-2 border-b border-yellow-500/30 pb-1">Observações em Aberto ({openObs.length})</p>
                                                <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
                                                    {openObs.map(p => (
                                                        <div key={p.id} className="text-[11px] leading-tight border-b border-slate-700 pb-1 last:border-0 flex gap-1">
                                                            <span className="font-bold text-yellow-300 whitespace-nowrap">[{new Date(p.date + 'T00:00:00').toLocaleDateString('pt-BR').substring(0, 5)}]</span>
                                                            <span className="truncate">{p.text.substring(0, 100)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            })()}

                            <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{curr.collaboratorName}</h2>
                        </div>
                        
                        <div className={`flex flex-wrap items-center gap-x-6 gap-y-1 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">ANO:</span>
                                <span className="font-medium">{curr.periodYear}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">DEPTO:</span>
                                <span className="font-medium">{curr.department}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">FUNÇÃO:</span>
                                <span className="font-medium">{headerInfo.role}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">GESTOR:</span>
                                <span className="font-medium">{headerInfo.manager}</span>
                            </div>
                        </div>
                    </div>

                    {/* LADO DIREITO: Notas + Separador + Botões */}
                    <div className="flex items-center gap-4 shrink-0">
                        
                        {/* BLOCO DE NOTAS (BADGES) */}
                        <div className="flex items-center gap-3">
                            {isGestao ? (
                                <div className={`flex items-center gap-2 px-3 py-1 rounded-lg border shadow-sm ${isDarkMode ? 'bg-green-900/20 border-green-800 text-green-300' : 'bg-green-50 border-green-100 text-green-700'}`}>
                                    <Trophy size={16} className="shrink-0"/>
                                    <div className="flex flex-col leading-none">
                                        <span className="text-[9px] font-bold uppercase opacity-70">Nota Gestão</span>
                                        {(() => {
                                            let realMetasPercepcao = 0;
                                            if (currWithScores.gestaoMetasPercepcao) {
                                                currWithScores.gestaoMetasPercepcao.forEach(item => {
                                                    ['q1', 'q2', 'q3', 'q4'].forEach((q, idx) => {
                                                        if ((idx + 1) >= (currWithScores.activeFromQuarter || 1)) {
                                                            realMetasPercepcao += (parseFloat(item.scores?.[q]) || 0);
                                                        }
                                                    });
                                                });
                                            }
                                            const realNotaGestao = (currWithScores.qualitative?.finalScoreGestaoQuali || 0) + realMetasPercepcao + (currWithScores.atualizacao?.total || 0);
                                            return <span className="text-base font-black">{realNotaGestao.toFixed(4)}</span>;
                                        })()}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className={`flex items-center gap-2 px-3 py-1 rounded-lg border shadow-sm ${isDarkMode ? 'bg-blue-900/20 border-blue-800 text-blue-300' : 'bg-blue-50 border-blue-100 text-blue-700'}`}>
                                        <Award size={16} className="shrink-0"/>
                                        <div className="flex flex-col leading-none">
                                            <span className="text-[9px] font-bold uppercase opacity-70">Qualitativa</span>
                                            <span className="text-base font-black">{currWithScores.qualitative.finalAverage?.toFixed(4)}</span>
                                        </div>
                                    </div>
                                    <div className={`flex items-center gap-2 px-3 py-1 rounded-lg border shadow-sm ${isDarkMode ? 'bg-red-900/20 border-red-800 text-red-300' : 'bg-red-50 border-red-100 text-red-700'}`}>
                                        <AlertCircle size={16} className="shrink-0"/>
                                        <div className="flex flex-col leading-none">
                                            <span className="text-[9px] font-bold uppercase opacity-70">Quantitativa</span>
                                            <span className="text-base font-black">{currWithScores.quantitative.meanPoints?.toFixed(4)}</span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* SEPARADOR VERTICAL */}
                        <div className={`h-8 w-px ${isDarkMode ? 'bg-slate-600' : 'bg-gray-200'}`}></div>

                        {/* BOTÕES DE AÇÃO */}
                        <div className="flex gap-2">
                            <Button variant="secondary" onClick={()=>setView('list')} title="Voltar" className={`px-3 h-10 ${isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600' : ''}`}><ArrowLeft size={18}/></Button>
                            {isLocked ? 
                                (<Button variant="warning" onClick={() => setForceEditMode(true)} title="Habilitar Edição" className={`px-3 h-10 ${isDarkMode ? 'bg-yellow-900/30 text-yellow-200 hover:bg-yellow-900/50 border-yellow-700' : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 border-yellow-300'}`}><Unlock size={18}/></Button>) 
                                : 
                                (<Button onClick={()=>{actions.saveEvaluation(currWithScores); setView('list');}} title="Salvar Alterações" className="px-4 h-10 font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-sm"><Save size={18} className="mr-2"/> Salvar</Button>)
                            }
                        </div>
                    </div>
                </Card>

                        {/* Abas - Ordem Corrigida: Resumo, Quali, Quanti, Extras, Feedback, Anotações */}
                        <div className={`flex gap-2 sticky top-[80px] z-30 py-2 shadow-sm border-b backdrop-blur-sm bg-opacity-95 ${isDarkMode ? 'bg-slate-900/90 border-slate-700' : 'bg-slate-50 border-gray-200/50'}`}>
                            
                            {/* 1. RESUMO */}
                            <button onClick={() => setActiveTab('resumo')} 
                                className={`px-6 py-2 rounded-t-lg font-bold text-sm transition-colors ${activeTab === 'resumo' ? (isDarkMode ? 'bg-slate-800 text-blue-400 border-t-2 border-blue-500 shadow-sm' : 'bg-white text-blue-600 border-t-2 border-blue-600 shadow-sm') : (isDarkMode ? 'bg-slate-900 text-slate-500 hover:bg-slate-800' : 'bg-gray-200 text-gray-500 hover:bg-gray-300')}`}>
                                RESUMO
                            </button>

                            {/* 2. QUALITATIVA */}
                            <button onClick={() => setActiveTab('qualitativa')} 
                                className={`px-6 py-2 rounded-t-lg font-bold text-sm transition-colors ${activeTab === 'qualitativa' ? (isDarkMode ? 'bg-slate-800 text-blue-400 border-t-2 border-blue-500 shadow-sm' : 'bg-white text-blue-600 border-t-2 border-blue-600 shadow-sm') : (isDarkMode ? 'bg-slate-900 text-slate-500 hover:bg-slate-800' : 'bg-gray-200 text-gray-500 hover:bg-gray-300')}`}>
                                QUALITATIVA
                            </button>

                            {/* 3. QUANTITATIVA (Oculta para Gestão) */}
                            {isColaborador && (
                                <button onClick={() => setActiveTab('quantitativa')} 
                                    className={`px-6 py-2 rounded-t-lg font-bold text-sm transition-colors ${activeTab === 'quantitativa' ? (isDarkMode ? 'bg-slate-800 text-blue-400 border-t-2 border-blue-500 shadow-sm' : 'bg-white text-blue-600 border-t-2 border-blue-600 shadow-sm') : (isDarkMode ? 'bg-slate-900 text-slate-500 hover:bg-slate-800' : 'bg-gray-200 text-gray-500 hover:bg-gray-300')}`}>
                                    QUANTITATIVA
                                </button>
                            )}

                            {/* 4. EXTRAS */}
                            <button onClick={() => setActiveTab('extras')} 
                                className={`px-6 py-2 rounded-t-lg font-bold text-sm transition-colors ${activeTab === 'extras' ? (isDarkMode ? 'bg-slate-800 text-purple-400 border-t-2 border-purple-500 shadow-sm' : 'bg-white text-purple-600 border-t-2 border-purple-600 shadow-sm') : (isDarkMode ? 'bg-slate-900 text-slate-500 hover:bg-slate-800' : 'bg-gray-200 text-gray-500 hover:bg-gray-300')}`}>
                                EXTRAS
                            </button>

                            {/* 5. FEEDBACKS */}
                            <button onClick={() => setActiveTab('feedbacks')} 
                                className={`px-6 py-2 rounded-t-lg font-bold text-sm transition-colors ${activeTab === 'feedbacks' ? (isDarkMode ? 'bg-slate-800 text-blue-400 border-t-2 border-blue-500 shadow-sm' : 'bg-white text-blue-600 border-t-2 border-blue-600 shadow-sm') : (isDarkMode ? 'bg-slate-900 text-slate-500 hover:bg-slate-800' : 'bg-gray-200 text-gray-500 hover:bg-gray-300')}`}>
                                FEEDBACKS
                            </button>

                            {/* 6. ANOTAÇÕES */}
                            <button onClick={() => setActiveTab('anotacoes')} 
                                className={`px-6 py-2 rounded-t-lg font-bold text-sm transition-colors ${activeTab === 'anotacoes' ? (isDarkMode ? 'bg-slate-800 text-teal-400 border-t-2 border-teal-500 shadow-sm' : 'bg-white text-teal-600 border-t-2 border-teal-600 shadow-sm') : (isDarkMode ? 'bg-slate-900 text-slate-500 hover:bg-slate-800' : 'bg-gray-200 text-gray-500 hover:bg-gray-300')}`}>
                                ANOTAÇÕES
                            </button>
                            {/* 7. PDI */}
                            <button onClick={() => setActiveTab('pdi')} 
                                className={`px-6 py-2 rounded-t-lg font-bold text-sm transition-colors ${activeTab === 'pdi' ? (isDarkMode ? 'bg-slate-800 text-orange-400 border-t-2 border-orange-500 shadow-sm' : 'bg-white text-orange-600 border-t-2 border-orange-600 shadow-sm') : (isDarkMode ? 'bg-slate-900 text-slate-500 hover:bg-slate-800' : 'bg-gray-200 text-gray-500 hover:bg-gray-300')}`}>
                                PDI (METAS)
                            </button>
                        </div>
                <Card className="rounded-t-none shadow-sm p-6 min-h-[400px]" darkMode={isDarkMode}>
                
                        {activeTab === 'resumo' && (
                        <div className="space-y-8 animate-fade-in">
                            
                            {/* --- SEÇÃO SUPERIOR: COMPETÊNCIAS EXIGIDAS --- */}
                            {(() => {
                
                                // 1. Lógica de Filtragem de Competências
                                const targetDeptId = collabBeingEvaluated?.departmentId;
                                const targetRole = curr.role; 
                                const targetPersonType = curr.personType;

                                const relevantCompetencies = (data.competencias || []).filter(comp => {
                                    // Filtro de Departamento
                                    const matchDept = !comp.departmentId || comp.departmentId === 'all' || comp.departmentId === targetDeptId;
                                    // Filtro de Tipo de Pessoa
                                    const matchType = !comp.personType || comp.personType === 'Todos' || comp.personType === targetPersonType;
                                    // Filtro de Cargo
                                    const matchRole = !comp.roleIds || comp.roleIds.length === 0 || comp.roleIds.includes(targetRole);

                                    return matchDept && matchType && matchRole;
                                });

                                // 2. Paleta de Cores completa (Mais de 10 variações com suporte a Dark Mode)
                                const compColors = [
                                    { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200', darkBg: 'bg-blue-500/20', darkText: 'text-blue-300', darkBorder: 'border-blue-500/30' },
                                    { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-200', darkBg: 'bg-emerald-500/20', darkText: 'text-emerald-300', darkBorder: 'border-emerald-500/30' },
                                    { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200', darkBg: 'bg-yellow-500/20', darkText: 'text-yellow-200', darkBorder: 'border-yellow-500/30' },
                                    { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', darkBg: 'bg-red-500/20', darkText: 'text-red-300', darkBorder: 'border-red-500/30' },
                                    { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200', darkBg: 'bg-purple-500/20', darkText: 'text-purple-300', darkBorder: 'border-purple-500/30' },
                                    { bg: 'bg-slate-200', text: 'text-slate-700', border: 'border-slate-300', darkBg: 'bg-slate-600/30', darkText: 'text-slate-300', darkBorder: 'border-slate-500/30' },
                                    { bg: 'bg-stone-200', text: 'text-stone-700', border: 'border-stone-300', darkBg: 'bg-stone-500/20', darkText: 'text-stone-300', darkBorder: 'border-stone-500/30' },
                                    { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200', darkBg: 'bg-orange-500/20', darkText: 'text-orange-300', darkBorder: 'border-orange-500/30' },
                                    { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-200', darkBg: 'bg-indigo-500/20', darkText: 'text-indigo-300', darkBorder: 'border-indigo-500/30' },
                                    { bg: 'bg-fuchsia-100', text: 'text-fuchsia-800', border: 'border-fuchsia-200', darkBg: 'bg-fuchsia-500/20', darkText: 'text-fuchsia-300', darkBorder: 'border-fuchsia-500/30' },
                                    { bg: 'bg-lime-100', text: 'text-lime-800', border: 'border-lime-200', darkBg: 'bg-lime-500/20', darkText: 'text-lime-200', darkBorder: 'border-lime-500/30' },
                                    { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-200', darkBg: 'bg-cyan-500/20', darkText: 'text-cyan-300', darkBorder: 'border-cyan-500/30' },
                                    { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200', darkBg: 'bg-amber-500/20', darkText: 'text-amber-300', darkBorder: 'border-amber-500/30' },
                                    { bg: 'bg-gray-200', text: 'text-gray-700', border: 'border-gray-300', darkBg: 'bg-gray-600/30', darkText: 'text-gray-300', darkBorder: 'border-gray-500/30' },
                                    { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-200', darkBg: 'bg-teal-500/20', darkText: 'text-teal-300', darkBorder: 'border-teal-500/30' },
                                    { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-200', darkBg: 'bg-pink-500/20', darkText: 'text-pink-300', darkBorder: 'border-pink-500/30' },
                                ];

                                return (
                                    <div className="mb-2">
                                        <h4 className={`font-bold border-b pb-2 mb-3 flex items-center gap-2 ${isDarkMode ? 'text-slate-200 border-slate-700' : 'text-gray-700 border-gray-200'}`}>
                                            <Award size={18}/> Competências do Cargo
                                        </h4>
                                        <div className="flex flex-wrap gap-2">
                                            {relevantCompetencies.length > 0 ? (
                                                relevantCompetencies.map((comp, idx) => {
                                                    const style = compColors[idx % compColors.length];
                                                    return (
                                                        <div 
                                                            key={comp.id} 
                                                            className={`px-3 py-2 rounded-lg border text-xs font-bold uppercase tracking-wide shadow-sm select-none transition-transform hover:scale-105 cursor-default
                                                                ${isDarkMode 
                                                                    ? `${style.darkBg} ${style.darkBorder} ${style.darkText}` 
                                                                    : `${style.bg} ${style.border} ${style.text}`
                                                                }
                                                            `}
                                                            title="Competência Cadastrada"
                                                            >
                                                            {comp.name}
                                                        </div>
                                                );
                                                })
                                            ) : (
                                                <div className={`text-sm italic p-2 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                                                    Nenhuma competência específica vinculada a este perfil (Depto/Cargo).
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* --- SEÇÃO CENTRAL: PONTUAÇÃO E RESULTADOS LADO A LADO --- */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                
                                {/* COLUNA ESQUERDA: DETALHAMENTO DA PONTUAÇÃO */}
                                <div className="space-y-4">
                                    <h4 className={`font-bold border-b pb-2 flex items-center gap-2 ${isDarkMode ? 'text-slate-200 border-slate-700' : 'text-gray-700 border-gray-200'}`}><LayoutDashboard size={18}/> Detalhamento da Pontuação</h4>
                                    
                                    {/* 1. Avaliação Qualitativa */}
                                    <div className={`p-4 rounded-lg flex justify-between items-center border ${isDarkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-100'}`}>
                                        <div><div className={`font-bold ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>Avaliação Qualitativa</div><div className={`text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>Competências e Comportamento</div></div>
                                        <div className={`text-2xl font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>{isGestao ? currWithScores.qualitative.finalScoreGestaoQuali.toFixed(4) : currWithScores.qualitative.finalAverage.toFixed(4)}<span className="text-xs ml-1 opacity-70">/ {isGestao ? '40' : '10'}</span></div>
                                    </div>

                                    {isColaborador && (
                                        <>
                                            {/* 2. Pontos Perdidos */}
                                            <div className={`p-4 rounded-lg flex justify-between items-center border ${isDarkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-100'}`}>
                                                <div><div className={`font-bold ${isDarkMode ? 'text-red-300' : 'text-red-800'}`}>Avaliação Quantitativa</div><div className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>Ocorrências</div></div>
                                                <div className={`text-2xl font-bold ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>{currWithScores.quantitative.meanPoints.toFixed(4)}<span className="text-xs ml-1 opacity-70">pts</span></div>
                                            </div>

                                            {/* 3. CARD: HORAS DE CURSOS (EXTRAS) CORRIGIDO */}
                                            <div className={`p-4 rounded-lg flex justify-between items-center border ${isDarkMode ? 'bg-purple-900/20 border-purple-800' : 'bg-purple-50 border-purple-100'}`}>
                                                <div>
                                                    <div className={`font-bold ${isDarkMode ? 'text-purple-300' : 'text-purple-800'}`}>Cursos e Capacitações</div>
                                                    <div className={`text-[10px] ${isDarkMode ? 'text-purple-400' : 'text-purple-600'} mt-1 flex gap-2`}>
                                                        <span>D1 (Atuação): <b>{formatDecimalToTime(currWithScores.atualizacao?.D1_hours || 0)}h</b></span>
                                                        <span>•</span>
                                                        <span>D2 (Outros): <b>{formatDecimalToTime(currWithScores.atualizacao?.D2_hours || 0)}h</b></span>
                                                    </div>
                                                </div>
                                                <div className={`text-2xl font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                                                    {formatDecimalToTime((currWithScores.atualizacao?.D1_hours || 0) + (currWithScores.atualizacao?.D2_hours || 0))}
                                                    <span className="text-xs ml-1 opacity-70">h</span>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    
                                    {isGestao && (() => {
                                        // Calcula a SOMA REAL dos pontos inseridos para Metas e Percepção (sem projeção)
                                        let realTotalMetas = 0;
                                        let realTotalPercepcao = 0;
                                        
                                        if (currWithScores.gestaoMetasPercepcao) {
                                            currWithScores.gestaoMetasPercepcao.forEach(item => {
                                                const isPercepcao = item.type === 'Percepção' || item.name.toLowerCase().includes('percepção');
                                                
                                                // Soma os 4 trimestres da mesma forma que a aba Extras
                                                const sum = ['q1', 'q2', 'q3', 'q4'].reduce((acc, q, idx) => {
                                                    if ((idx + 1) < (currWithScores.activeFromQuarter || 1)) return acc;
                                                    return acc + (parseFloat(item.scores?.[q]) || 0);
                                                }, 0);
                                                
                                                if (isPercepcao) {
                                                    realTotalPercepcao += sum;
                                                } else {
                                                    realTotalMetas += sum;
                                                }
                                            });
                                        }

                                        return (
                                            <>
                                                <div className={`p-4 rounded-lg flex justify-between items-center border ${isDarkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-100'}`}>
                                                    <div><div className={`font-bold ${isDarkMode ? 'text-red-300' : 'text-red-800'}`}>Planejamento Estratégico</div><div className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>Cumprimento de Metas</div></div>
                                                    <div className={`text-2xl font-bold ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>{realTotalMetas.toFixed(4)}<span className="text-xs ml-1 opacity-70">/ 20</span></div>
                                                </div>
                                                <div className={`p-4 rounded-lg flex justify-between items-center border ${isDarkMode ? 'bg-orange-900/20 border-orange-800' : 'bg-orange-50 border-orange-100'}`}>
                                                    <div><div className={`font-bold ${isDarkMode ? 'text-orange-300' : 'text-orange-800'}`}>Percepção da Equipe</div><div className={`text-xs ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>Avaliação 360º / Clima</div></div>
                                                    <div className={`text-2xl font-bold ${isDarkMode ? 'text-orange-400' : 'text-orange-700'}`}>{realTotalPercepcao.toFixed(4)}<span className="text-xs ml-1 opacity-70">/ 20</span></div>
                                                </div>
                                                <div className={`p-4 rounded-lg flex justify-between items-center border ${isDarkMode ? 'bg-purple-900/20 border-purple-800' : 'bg-purple-50 border-purple-100'}`}>
                                                    <div><div className={`font-bold ${isDarkMode ? 'text-purple-300' : 'text-purple-800'}`}>Extras (Atualização)</div><div className={`text-xs ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>Cursos e Certificações</div></div>
                                                    <div className={`text-2xl font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>{currWithScores.atualizacao?.total?.toFixed(4)}<span className="text-xs ml-1 opacity-70">/ 20</span></div>
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>

                                {/* COLUNA DIREITA: STATUS & RESULTADO PREVISTO */}
                                <div className="space-y-4">
                                    <div className={`flex justify-between items-center border-b pb-2 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                        <h4 className={`font-bold flex items-center gap-2 ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}><Award size={18}/> Status & Resultado Previsto</h4>
                                        <span className={`text-[10px] px-2 py-0.5 rounded border animate-pulse ${isDarkMode ? 'bg-yellow-900/30 text-yellow-300 border-yellow-800' : 'bg-yellow-100 text-yellow-700 border-yellow-200'}`}>Tempo Real</span>
                                    </div>
                                    
                                    {dynamicResultLabels && dynamicResultLabels.length > 0 ? (
                                        <div className="space-y-3">
                                            {dynamicResultLabels.map((l, idx) => { 
                                                if(l.text === '-') return <div key={idx} className={`p-4 rounded text-center border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-500' : 'bg-gray-100 border-gray-200 text-gray-500'}`}>Sem resultado atingido no momento.</div>;
                                                const isPromo = l.text === 'Promoção';
                                                const needed = l.details?.needed || 0; 
                                                const score = l.details?.score || 0;
                                                const lost = l.details?.lost || 0; 
                                                const maxLost = l.details?.maxLost || 0; 
                                                return (
                                                    <div key={idx} className={`border p-4 rounded-lg shadow-sm transition-all duration-300 ${isPromo ? (isDarkMode ? 'bg-green-900/20 border-green-800 ring-1 ring-green-800' : 'bg-green-50 border-green-200 ring-1 ring-green-300') : (isDarkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200')}`}>
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <div className={`p-2 rounded-full ${isPromo 
                                                            ? (isDarkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-200 text-green-700') : (isDarkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-200 text-blue-700')}`}>{isPromo ? <TrendingUp size={24}/> : <Award size={24}/>}</div>
                                                            <div><h5 className={`font-bold text-lg ${isPromo ? (isDarkMode ? 'text-green-300' : 'text-green-800') : (isDarkMode ? 'text-blue-300' : 'text-blue-800')}`}>{l.text.toUpperCase()}</h5><span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Resultado Projetado (Ranking Dinâmico)</span></div>
                                                        </div>
                                                        <div className={`grid grid-cols-2 gap-2 text-sm mt-3 p-2 rounded ${isDarkMode ? 'bg-slate-900/50 text-slate-300' : 'bg-white/60 text-gray-800'}`}>
                                                            <div>Nota Atual: <b>{score.toFixed(4)}</b></div>
                                                            <div>Meta: <b>{needed.toFixed(2)}</b></div>
                                                            {isColaborador && (
                                                                <>
                                                                    <div className={`${lost > maxLost ? 'text-red-500 font-bold' : ''}`}>Perdidos: <b>{lost.toFixed(4)}</b></div>
                                                                    <div>Limite: <b>{maxLost.toFixed(2)}</b></div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                    ) : <div className={`p-6 border rounded-lg text-center ${isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-500' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>Calculando...</div>}
                                    {recalculateFinalAverage(curr).pendingIssues && recalculateFinalAverage(curr).pendingIssues.length > 0 && (<div className={`border p-4 rounded-lg mt-4 ${isDarkMode ? 'bg-orange-900/20 border-orange-800' : 'bg-orange-50 border-orange-200'}`}><div className={`flex items-center gap-2 font-bold mb-1 ${isDarkMode ? 'text-orange-400' : 'text-orange-700'}`}><AlertTriangle size={18}/> Atenção: Pendências</div><p className={`text-xs ${isDarkMode ? 'text-orange-300' : 'text-orange-600'}`}>Existem notas não preenchidas nos trimestres: <b>{recalculateFinalAverage(curr).pendingIssues.join(', ')}</b>.</p></div>)}
                                </div>
                            </div>

                            {/* --- QUADROS DE ORIENTAÇÃO (RESULTADOS) --- */}
                            {isColaborador && (
                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 pt-6 border-t border-dashed border-gray-300 dark:border-slate-700 animate-fade-in">
                                        
                                        {/* 1. Matriz Qualitativa */}
                                        <div className={`border rounded-xl overflow-hidden shadow-sm ${isDarkMode ? 'border-slate-700' : 'border-blue-200'}`}>
                                            <div className={`p-3 font-bold text-xs uppercase text-center tracking-wider ${isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-600 text-white'}`}>
                                                Matriz de Desempenho Qualitativo (Base: Nota Final)
                                            </div>
                                            <div className={`divide-y ${isDarkMode ? 'divide-slate-700 bg-slate-800' : 'divide-blue-100 bg-white'}`}>
                                                {[
                                                    { min: 8.000, max: 10.000, title: "EXCELENTE CONTRIBUIDOR", desc: "Na maioria das vezes, supera o esperado. Seu desempenho é reconhecido por todos com destaque." },
                                                    { min: 6.000, max: 7.999, title: "BOM CONTRIBUIDOR", desc: "Em geral, alcança os objetivos de forma adequada contribuindo para os resultados de sua Área." },
                                                    { min: 4.000, max: 5.999, title: "CONTRIBUIDOR PARCIAL", desc: "Em geral, contribui de forma parcial para os resultados de sua Área." },
                                                    { min: 0.000, max: 3.999, title: "MENOR CONTRIBUIDOR", desc: "Na maioria das vezes, contribui pouco para os resultados de sua Área." }
                                                ].map((row, idx) => {
                                                    const score = currWithScores.qualitative.finalAverage || 0;
                                                    const isActive = score >= row.min && score <= row.max;
                                                    return (
                                                        <div key={idx} className={`flex text-xs transition-colors ${isActive ? (isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50') : ''}`}>
                                                            <div className={`w-12 shrink-0 flex items-center justify-center font-bold border-r ${isDarkMode ? 'border-slate-700 text-blue-400' : 'border-blue-100 text-blue-700'} ${isActive ? 'text-lg' : ''}`}>
                                                                {isActive ? '( X )' : '( )'}
                                                            </div>
                                                            <div className={`w-32 shrink-0 flex items-center justify-center border-r font-mono text-[10px] ${isDarkMode ? 'border-slate-700 text-slate-400' : 'border-blue-100 text-gray-500'}`}>
                                                                {row.min.toFixed(3)} Até {row.max.toFixed(3)}
                                                            </div>
                                                            <div className="p-2 flex-1">
                                                                <div className={`font-bold mb-0.5 ${isActive ? (isDarkMode ? 'text-blue-300' : 'text-blue-800') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}>{row.title}</div>
                                                                <div className={`leading-tight ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>{row.desc}</div>
                                                            </div>
                                                        </div>
                                                );
                                                })}
                                            </div>
                                        </div>

                                        {/* 2. Matriz Quantitativa */}
                                        <div className={`border rounded-xl overflow-hidden shadow-sm ${isDarkMode ? 'border-slate-700' : 'border-teal-200'}`}>
                                            <div className={`p-3 font-bold text-xs uppercase text-center tracking-wider ${isDarkMode ? 'bg-teal-900/40 text-teal-300' : 'bg-teal-600 text-white'}`}>
                                                Orientações sobre Avaliação Quantitativa
                                            </div>
                                            <div className={`divide-y ${isDarkMode ? 'divide-slate-700 bg-slate-800' : 'divide-teal-100 bg-white'}`}>
                                                {[
                                                    { min: 0.000, max: 10.000, title: "EXCELENTE ASSERTIVIDADE", desc: "Os acertos do colaborador são muito maiores que as ocorrências, de maneira que as ocorrências eventualmente registradas não comprometem seu excelente desempenho." },
                                                    { min: 10.001, max: 20.000, title: "BOA ASSERTIVIDADE", desc: "Os acertos do colaborador são relativamente maiores que as ocorrências. Apesar das ocorrências registradas, o desempenho do colaborador é bom." },
                                                    { min: 20.001, max: 30.000, title: "ASSERTIVIDADE MÉDIA", desc: "O colaborador precisa acertar mais. As ocorrências registradas estão interferindo no desempenho do colaborador." },
                                                    { min: 30.001, max: 40.000, title: "ASSERTIVIDADE RAZOAVELMENTE COMPROMETIDA", desc: "As ocorrências do colaborador estão comprometendo seu desempenho." },
                                                    { min: 40.001, max: 9999.00, title: "ASSERTIVIDADE CONSIDERAVELMENTE COMPROMETIDA", desc: "As ocorrências do colaborador estão comprometendo de forma considerável seu desempenho." }
                                                ].map((row, idx) => {
                                                    const lost = currWithScores.quantitative.meanPoints || 0;
                                                    const isActive = lost >= row.min && lost <= row.max;
                                                    return (
                                                        <div key={idx} className={`flex text-xs transition-colors ${isActive ? (isDarkMode ? 'bg-teal-900/20' : 'bg-teal-50') : ''}`}>
                                                            <div className={`w-12 shrink-0 flex items-center justify-center font-bold border-r ${isDarkMode ? 'border-slate-700 text-teal-400' : 'border-teal-100 text-teal-700'} ${isActive ? 'text-lg' : ''}`}>
                                                                {isActive ? '( X )' : '( )'}
                                                            </div>
                                                            <div className={`w-32 shrink-0 flex items-center justify-center border-r font-mono text-[10px] ${isDarkMode ? 'border-slate-700 text-slate-400' : 'border-teal-100 text-gray-500'}`}>
                                                                {row.min.toFixed(3)} Até {row.max > 1000 ? '...' : row.max.toFixed(3)}
                                                            </div>
                                                            <div className="p-2 flex-1">
                                                                <div className={`font-bold mb-0.5 ${isActive ? (isDarkMode ? 'text-teal-300' : 'text-teal-800') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}>{row.title}</div>
                                                                <div className={`leading-tight ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>{row.desc}</div>
                                                            </div>
                                                        </div>
                                                );
                                                })}
                                            </div>
                                        </div>
                                </div>
                            )}
                        </div>
                    )}

            {activeTab === 'qualitativa' && (
                        <div className="space-y-4">
                            <div className={`flex flex-wrap items-center gap-4 p-3 border rounded-lg ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-100'}`}>
                                <span className="text-xs font-bold text-gray-500 uppercase mr-2">Legenda:</span>
                                <div className="flex items-center gap-2"><div className="w-6 h-6 rounded flex items-center justify-center bg-red-500 text-white text-xs font-bold shadow-sm">1</div><span className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Abaixo do Esperado</span></div>
                                <div className="flex items-center gap-2"><div className="w-6 h-6 rounded flex items-center justify-center bg-orange-500 text-white text-xs font-bold shadow-sm">2</div><span className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Parcialmente do Esperado</span></div>
                                <div className="flex items-center gap-2"><div className="w-6 h-6 rounded flex items-center justify-center bg-green-500 text-white text-xs font-bold shadow-sm">3</div><span className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Atinge o Esperado</span></div>
                                <div className="flex items-center gap-2"><div className="w-6 h-6 rounded flex items-center justify-center bg-blue-500 text-white text-xs font-bold shadow-sm">4</div><span className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Acima do Esperado</span></div>
                            </div>
                            <div className="overflow-x-auto pb-6">
                                <div className="min-w-[700px]">
                                    {/* HEADER GERAL EM GRID (Espaçamento reduzido de pb-4/mb-4 para pb-2/mb-2) */}
                                    <div className={`grid grid-cols-[minmax(250px,1fr)_repeat(4,70px)_90px] gap-2 border-b pb-2 mb-2 font-bold items-end ${isDarkMode ? 'text-slate-500 border-slate-700' : 'text-gray-500'}`}>
                                        <div className="pl-4">Critério (Agrupado por Dimensão)</div>
                                        {['q1','q2','q3','q4'].map((q,i) => (
                                            <div key={q} className="text-center">
                                                <div className={(i + 1) < (curr.activeFromQuarter || 1) ? 'opacity-30' : ''}>{i+1}º Trim</div>
                                                <div className={`text-[9px] text-blue-500 font-normal ${(i + 1) < (curr.activeFromQuarter || 1) ? 'hidden' : ''}`}>Méd: {getQualiColAvg(q)}</div>
                                            </div>
                                        ))}
                                        <div className={`text-center font-bold ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Média</div>
                                    </div>

                                    {/* CORPO COM AGRUPAMENTO COLAPSÁVEL NATIVO */}
                                    {(() => {
                                        // 1. Agrupa os critérios pelo campo Dimensão
                                        const grouped = (curr.qualitative.criteria || []).reduce((acc, c) => {
                                            const dim = c.dim || 'Outros (Sem Dimensão)';
                                            if (!acc[dim]) acc[dim] = [];
                                            acc[dim].push(c);
                                            return acc;
                                        }, {});
                                        
                                        // 2. Paleta de Cores Rotativa para os Grupos
                                        const colors = [
                                            { bg: isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50', border: isDarkMode ? 'border-blue-800' : 'border-blue-200', text: isDarkMode ? 'text-blue-400' : 'text-blue-800' },
                                            { bg: isDarkMode ? 'bg-emerald-900/20' : 'bg-emerald-50', border: isDarkMode ? 'border-emerald-800' : 'border-emerald-200', text: isDarkMode ? 'text-emerald-400' : 'text-emerald-800' },
                                            { bg: isDarkMode ? 'bg-purple-900/20' : 'bg-purple-50', border: isDarkMode ? 'border-purple-800' : 'border-purple-200', text: isDarkMode ? 'text-purple-400' : 'text-purple-800' },
                                            { bg: isDarkMode ? 'bg-orange-900/20' : 'bg-orange-50', border: isDarkMode ? 'border-orange-800' : 'border-orange-200', text: isDarkMode ? 'text-orange-400' : 'text-orange-800' },
                                            { bg: isDarkMode ? 'bg-pink-900/20' : 'bg-pink-50', border: isDarkMode ? 'border-pink-800' : 'border-pink-200', text: isDarkMode ? 'text-pink-400' : 'text-pink-800' }
                                        ];

                                        return Object.entries(grouped).map(([dim, crits], gIdx) => {
                                            const theme = colors[gIdx % colors.length];
                                            return (
                                                <details key={dim} open={!!expandedQualiDimensions[dim]} onToggle={(e) => setQualiDimensionExpanded(dim, e.currentTarget.open)} className={`mb-4 rounded-xl border shadow-sm group/dim ${theme.bg} ${theme.border}`}>
                                                    
                                                    {/* Header da Dimensão (Nível 1) - ALTURA REDUZIDA: padding mudou de p-4 para py-2.5 px-4 */}
                                                    <summary className={`py-2.5 px-4 font-bold uppercase tracking-wider cursor-pointer select-none list-none flex justify-between items-center [&::-webkit-details-marker]:hidden ${theme.text}`}>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xl leading-none group-open/dim:hidden">+</span>
                                                            <span className="text-xl leading-none hidden group-open/dim:inline">-</span>
                                                            {dim} 
                                                            <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-white/50 dark:bg-black/20 text-current">{crits.length} itens</span>
                                                        </div>
                                                    </summary>
                                                    
                                                    {/* Lista de Critérios da Dimensão (Nível 2) */}
                                                    <div className={`p-2 space-y-1 ${isDarkMode ? 'bg-slate-900' : 'bg-white'} rounded-b-xl`}>
                                                        {crits.map((c) => {
                                                            const globalIdx = curr.qualitative.criteria.findIndex(x => x.id === c.id);

                                                            return (
                                                                // ALTURA DA LINHA REDUZIDA: padding mudou de 'pb-4 pt-3' para 'py-2'
                                                                <div key={c.id} className={`border-b last:border-0 py-2 px-2 transition-colors ${isDarkMode ? 'border-slate-700 hover:bg-slate-800' : 'border-gray-100 hover:bg-gray-50'}`}>
                                                                    <div className="grid grid-cols-[minmax(250px,1fr)_repeat(4,70px)_90px] gap-2 items-start w-full">
                                                                        
                                                                        {/* Nome e Descrição Colapsável */}
                                                                        <div className="pr-4">
                                                                            <details className="group/crit cursor-pointer">
                                                                                <summary className={`font-bold text-xs uppercase list-none flex items-start gap-2 select-none [&::-webkit-details-marker]:hidden ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                                                                    <span className={`text-lg leading-none shrink-0 ${theme.text} group-open/crit:hidden`}>+</span>
                                                                                    <span className={`text-lg leading-none shrink-0 ${theme.text} hidden group-open/crit:inline`}>-</span>
                                                                                    <span className="mt-0.5 leading-snug">{c.name}</span>
                                                                                </summary>
                                                                                <div className={`text-xs mt-1.5 pl-4 border-l-2 ${theme.border} ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                                                                    {c.description}
                                                                                </div>
                                                                            </details>
                                                                        </div>
                                                                        
                                                                        {/* Inputs Trimestrais - ALTURA REDUZIDA: h-10 mudou para h-8 (40px para 32px) */}
                                                                        {['q1','q2','q3','q4'].map((q, qIdx) => {
                                                                            const isQuarterInactive = (qIdx + 1) < (curr.activeFromQuarter || 1);
                                                                            return (
                                                                                <div key={q} className={`flex justify-center ${isQuarterInactive ? (isDarkMode ? 'opacity-50' : 'opacity-50') : ''}`}>
                                                                                    <input 
                                                                                        type="number" min="1" max="4" step="1" 
                                                                                        onKeyPress={(e) => { if (e.key === '.' || e.key === ',') e.preventDefault(); }} 
                                                                                        data-nav={`quali-${globalIdx}-${q}`} 
                                                                                        disabled={isLocked || isQuarterInactive} 
                                                                                        className={`w-14 h-8 text-center rounded border font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${getScoreClass(c[q])} ${isLocked || isQuarterInactive ? 'opacity-60 cursor-not-allowed' : ''} ${isQuarterInactive ? '!bg-transparent !border-dashed !text-gray-400' : ''}`} 
                                                                                        value={isQuarterInactive ? '' : (c[q]||'')} 
                                                                                        placeholder={isQuarterInactive ? '-' : ''}
                                                                                        onChange={e=>updateScore('quali', c.id, q, e.target.value)} 
                                                                                        onKeyDown={(e) => handleKeyDown(e, 'quali', globalIdx, q, curr.qualitative.criteria?.length)} 
                                                                                    />
                                                                                </div>
                                                                            );
                                                                        })}
                                                                        
                                                                        {/* Média - ALTURA REDUZIDA: h-10 mudou para h-8 para alinhar com o input */}
                                                                        <div className={`flex items-center justify-center h-8 font-bold text-sm ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                                                            {getRowAvg(c)}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </details>
                                            );
                                        });
                                    })()}
                                </div>
                            </div>
                        </div>
                    )}
                    
                                {activeTab === 'quantitativa' && isColaborador && (
                            <div>
                                {/* Aviso Informativo */}
                                <div className={`p-3 mb-4 rounded border text-xs flex items-center justify-between gap-4 ${isDarkMode ? 'bg-blue-900/20 border-blue-800 text-blue-300' : 'bg-blue-50 border-blue-100 text-blue-700'}`}>
                                    <div className="flex items-center gap-2">
                                        <Info size={14} className="shrink-0" />
                                        <span>
                                            <b>Atenção:</b> Os valores abaixo são calculados automaticamente através do módulo de <b>Ocorrências</b>. 
                                            Para corrigir um valor, ajuste o registro na aba "Ocorrências".
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={manualSyncOcorrencias}
                                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-[11px] font-bold transition-all shadow-sm shrink-0 whitespace-nowrap"
                                    >
                                        Sincronizar Ocorrências
                                    </button>
                                </div>

                                <table className={`w-full text-sm ${isDarkMode ? 'text-slate-300' : ''}`}>
                                    <thead>
                                        <tr className={`border-b text-xs uppercase font-bold ${isDarkMode ? 'text-slate-500 border-slate-700' : 'text-gray-500'}`}>
                                            <th className="text-left pb-3 w-1/3">Ocorrência</th>
                                            <th className="text-center pb-3 w-16">Peso</th>
                                            {['1º','2º','3º','4º'].map((t,i) => (
                                                <th key={i} className="text-center pb-3">
                                                    <div className={(i + 1) < (curr.activeFromQuarter || 1) ? 'opacity-30' : ''}>{t} Trim</div>
                                                    <div className={`text-[10px] font-normal text-gray-400 ${(i + 1) < (curr.activeFromQuarter || 1) ? 'hidden' : ''}`}>Total: {getQuantiColTotal(`q${i+1}`)}</div>
                                                </th>
                                            ))}
                                            <th className={`text-center pb-3 w-24 ${isDarkMode ? 'text-red-400 bg-red-900/20' : 'text-red-600 bg-red-50'}`}>Total Pontos</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                        {curr.quantitative.errors?.map((e, idx) => { 
                                            return (
                                                <tr key={e.id} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}>
                                                    <td className={`py-3 pr-4 font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>{formatOccurrenceTerm(e.name)}</td>
                                                    <td className="text-center font-bold text-red-500">{e.peso}</td>
                                                    {['q1','q2','q3','q4'].map((q, qIdx) => {
                                                        const isQuarterInactive = (qIdx + 1) < (curr.activeFromQuarter || 1);
                                                        const val = parseInt(e[q]) || 0;
                                                        
                                                        return (
                                                            <td 
                                                                key={q} 
                                                                className={`px-2 ${isQuarterInactive ? (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-100/50') : 'cursor-help'}`} 
                                                                onMouseEnter={(ev) => !isQuarterInactive && onHoverQuantiCell(ev, e.name, q)}
                                                                title={!isQuarterInactive ? "Passe o mouse para ver detalhes" : ""}
                                                            >
                                                                <div className="flex items-center justify-center">
                                                                    <input 
                                                                        type="text" 
                                                                        disabled={true} // BLOQUEADO PERMANENTEMENTE
                                                                        className={`w-16 h-8 text-center border rounded font-bold 
                                                                            ${isDarkMode 
                                                                                ? 'bg-slate-900 border-slate-700 text-slate-400' 
                                                                                : 'bg-gray-100 border-gray-200 text-gray-500'
                                                                            } 
                                                                            ${val > 0 && !isQuarterInactive ? '!text-red-500 !bg-red-50 !border-red-100' : ''}
                                                                            ${isQuarterInactive ? '!border-dashed opacity-30' : ''}
                                                                        `} 
                                                                        value={isQuarterInactive ? '' : val} 
                                                                        placeholder={isQuarterInactive ? '-' : '0'}
                                                                    />
                                                                </div>
                                                            </td>
                                                        );
                                                    })}
                                                    <th className={`text-center font-bold ${isDarkMode ? 'text-red-400 bg-red-900/20' : 'text-red-600 bg-red-50'}`}>{getQuantiRowTotal(e)}</th>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr className={`font-bold border-t ${isDarkMode ? 'bg-red-900/20 text-red-300 border-red-800' : 'bg-red-50 text-red-800 border-red-100'}`}>
                                            <td colSpan="2" className="py-3 px-4">Total Pontos Perdidos por Trimestre</td>
                                            <td className="text-center">{getQuantiColTotal('q1')}</td>
                                            <td className="text-center">{getQuantiColTotal('q2')}</td>
                                            <td className="text-center">{getQuantiColTotal('q3')}</td>
                                            <td className="text-center">{getQuantiColTotal('q4')}</td>
                                            <td className="text-center">-</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
            
            {activeTab === 'feedbacks' && (
                        <div className="space-y-4">
                            <Button onClick={() => setIsFeedbackModalOpen(true)} disabled={isLocked} icon={Plus}>Novo Feedback</Button>
                            
                            <div className="space-y-4 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
                                {(() => {
                                    const nonPdiFeedbacks = feedbacksList.filter(f => f.type !== 'PDI');
                                    if (nonPdiFeedbacks.length === 0) {
                                        return <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>Nenhum feedback registrado ainda.</p>;
                                    }
                                    return nonPdiFeedbacks.map(f => {
                                        let containerClass = '';
                                        let titleColor = '';
                                        let borderColor = '';
                                        let badgeClass = '';

                                        if (f.type === 'AvDes' || f.type === 'AvPDF') {
                                            containerClass = isDarkMode ? 'bg-purple-900/20' : 'bg-purple-50';
                                            borderColor = isDarkMode ? 'border-purple-800' : 'border-purple-200';
                                            titleColor = isDarkMode ? 'text-purple-400' : 'text-purple-700';
                                            badgeClass = isDarkMode ? 'bg-purple-900/50 text-purple-300 border-purple-700' : 'bg-purple-100 text-purple-700 border-purple-200';
                                        } else if (f.type === 'Colaborador') {
                                            containerClass = isDarkMode ? 'bg-emerald-900/20' : 'bg-emerald-50';
                                            borderColor = isDarkMode ? 'border-emerald-800' : 'border-emerald-200';
                                            titleColor = isDarkMode ? 'text-emerald-400' : 'text-emerald-700';
                                            badgeClass = isDarkMode ? 'bg-emerald-900/50 text-emerald-300 border-emerald-700' : 'bg-emerald-100 text-emerald-700 border-emerald-200';
                                        } else {
                                            if (f.nature === 'Corretivo' && f.actionTag === 'Pare') {
                                                containerClass = isDarkMode ? 'bg-red-900/20' : 'bg-red-50';
                                                borderColor = isDarkMode ? 'border-red-800' : 'border-red-200';
                                                titleColor = isDarkMode ? 'text-red-400' : 'text-red-700';
                                                badgeClass = isDarkMode ? 'bg-red-900/50 text-red-300 border-red-700' : 'bg-red-100 text-red-700 border-red-200';
                                            } else if (f.nature === 'Corretivo') {
                                                containerClass = isDarkMode ? 'bg-amber-900/20' : 'bg-amber-50';
                                                borderColor = isDarkMode ? 'border-amber-800' : 'border-amber-200';
                                                titleColor = isDarkMode ? 'text-amber-400' : 'text-amber-700';
                                                badgeClass = isDarkMode ? 'bg-amber-900/50 text-amber-300 border-amber-700' : 'bg-amber-100 text-amber-800 border-amber-200';
                                            } else {
                                                containerClass = isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50';
                                                borderColor = isDarkMode ? 'border-blue-800' : 'border-blue-200';
                                                titleColor = isDarkMode ? 'text-blue-400' : 'text-blue-700';
                                                badgeClass = isDarkMode ? 'bg-blue-900/50 text-blue-300 border-blue-700' : 'bg-blue-100 text-blue-700 border-blue-200';
                                            }
                                        }

                                        return (
                                            <div key={f.id} className={`p-4 rounded-lg border shadow-sm ${containerClass} ${borderColor}`}>
                                                <div className={`flex justify-between items-start border-b pb-2 mb-2 ${isDarkMode ? 'border-white/10' : 'border-black/5'}`}>
                                                    <div className="flex flex-col gap-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`text-[10px] px-2 py-0.5 rounded border uppercase font-bold ${badgeClass}`}>
                                                                {f.nature || (f.type === 'AvDes' ? 'RH' : 'Padrão')}
                                                            </span>
                                                            <span className={`text-xs font-bold uppercase ${titleColor}`}>
                                                                {f.type} - {f.quarter ? f.quarter.toUpperCase() : '-'}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                                                                Data: {new Date(f.timestamp).toLocaleDateString('pt-BR')}
                                                            </span>
                                                            {f.actionTag && (
                                                                <>
                                                                    <span className="text-gray-300">|</span>
                                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider border shadow-sm 
                                                                        ${f.actionTag === 'Pare' ? 'bg-red-600 text-white border-red-700' : 
                                                                        f.actionTag === 'Reveja' ? 'bg-amber-500 text-white border-amber-600' : 
                                                                        'bg-blue-600 text-white border-blue-700'}`}>
                                                                        {f.actionTag.toUpperCase()}
                                                                    </span>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        {f.followUpDate && (
                                                            <div className={`flex flex-col items-end mr-2 px-2 py-1 rounded border ${isDarkMode ? 'bg-yellow-900/30 border-yellow-800 text-yellow-500' : 'bg-yellow-50 border-yellow-200 text-yellow-700'}`}>
                                                                <span className="text-[9px] font-bold uppercase flex items-center gap-1"><Clock size={10}/> Acompanhar:</span>
                                                                <span className="text-xs font-bold">{new Date(f.followUpDate).toLocaleDateString('pt-BR')}</span>
                                                            </div>
                                                        )}
                                                        <button
                                                                onClick={() => handleEditFeedback(f)}
                                                                disabled={isLocked || !canModifyEvaluationFeedback(f)}
                                                                className={`p-1 rounded transition-colors ${
                                                                    isLocked || !canModifyEvaluationFeedback(f)
                                                                        ? 'text-gray-500 opacity-40 cursor-not-allowed'
                                                                        : isDarkMode
                                                                            ? 'text-blue-400 hover:bg-slate-700'
                                                                            : 'text-blue-500 hover:bg-white/50'
                                                                }`}
                                                                title={
                                                                    !canModifyEvaluationFeedback(f)
                                                                        ? 'Bloqueado: feedback inserido pelo avaliado.'
                                                                        : 'Editar'
                                                                }
                                                            >
                                                                <Edit3 size={14}/>
                                                            </button>

                                                            <button
                                                                onClick={() => {
                                                                    if (!canModifyEvaluationFeedback(f)) {
                                                                        setValidationAlert({
                                                                            title: "Exclusão Bloqueada",
                                                                            message: "Este feedback foi inserido pelo avaliado e só pode ser excluído pelo próprio usuário que o criou."
                                                                        });
                                                                        return;
                                                                    }

                                                                    handleDeleteFeedback(f.id);
                                                                }}
                                                                disabled={isLocked || !canModifyEvaluationFeedback(f)}
                                                                className={`p-1 rounded transition-colors ${
                                                                    isLocked || !canModifyEvaluationFeedback(f)
                                                                        ? 'text-gray-500 opacity-40 cursor-not-allowed'
                                                                        : 'text-red-500 hover:text-red-700 hover:bg-red-100'
                                                                }`}
                                                                title={
                                                                    !canModifyEvaluationFeedback(f)
                                                                        ? 'Bloqueado: feedback inserido pelo avaliado.'
                                                                        : 'Excluir'
                                                                }
                                                            >
                                                                <Trash2 size={14}/>
                                                            </button>
                                                    </div>
                                                </div>
                                                <p className={`text-sm whitespace-pre-wrap leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-800'}`}>
                                                    {f.text}
                                                </p>
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </div>
                    )}

                {/* --- ABA ANOTAÇÕES ATUALIZADA COM CONTROLE DE PENDÊNCIAS --- */}
                    {activeTab === 'anotacoes' && (
                        <div className="space-y-4 animate-fade-in pt-2">
                            <div className="flex justify-between items-center mb-4 border-b pb-2">
                                <h4 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-teal-400' : 'text-teal-700'}`}>
                                    <ClipboardCheck size={20}/> Anotações Pessoais
                                </h4>
                                <Button onClick={() => { 
                                    setNoteForm({ id: null, text: '', date: new Date().toISOString().split('T')[0], tipo: 'OBS', prioridade: 'Baixa' }); 
                                    setIsNotesModalOpen(true); 
                                }} icon={Plus} className={isDarkMode ? 'bg-teal-600 hover:bg-teal-500 text-white' : 'bg-teal-600 hover:bg-teal-700 text-white'}>
                                    Nova Anotação
                                </Button>
                            </div>

                            <div className={`border rounded-lg overflow-hidden shadow-sm ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                    <thead className={`uppercase text-xs font-bold ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-600'}`}>
                                        <tr>
                                            <th className="p-4 w-32 text-center">Data</th>
                                            <th className="p-4 w-28 text-center">Tipo</th>
                                            <th className="p-4 w-28 text-center">Prioridade</th>
                                            <th className="p-4">Descrição (Resumo)</th>
                                            <th className="p-4 w-28 text-center">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700 bg-slate-800' : 'divide-gray-100 bg-white'}`}>
                                        {!curr.personalNotes || Object.values(curr.personalNotes).length === 0 ? (
                                            <tr><td colSpan="5" className="p-8 text-center italic opacity-50">Nenhuma anotação registrada.</td></tr>
                                        ) : (
                                            Object.values(curr.personalNotes)
                                                .sort((a, b) => {
                                                    const prioridadePeso = { 'Urgente': 4, 'Alta': 3, 'Média': 2, 'Baixa': 1 };
                                                    const pesoA = prioridadePeso[a.prioridade] || 1;
                                                    const pesoB = prioridadePeso[b.prioridade] || 1;
                                                    if (pesoB !== pesoA) return pesoB - pesoA;
                                                    return new Date(b.date) - new Date(a.date);
                                                })
                                                .map((note) => {
                                                    const style = NOTE_COLORS[note.colorIndex !== undefined ? note.colorIndex : 0];
                                                    const firstLine = note.text.split('\n')[0].substring(0, 50) + (note.text.length > 50 ? '...' : '');
                                                    
                                                    // ATUALIZAÇÃO: AGORA PERMITE CONCLUIR PENDÊNCIA E OBS
                                                    const canBeResolved = note.tipo === 'Pendênca' || note.tipo === 'Pendência' || note.tipo === 'OBS';
                                                    const isPending = (note.tipo === 'Pendênca' || note.tipo === 'Pendência');
                                                    const isResolved = note.status === 'Concluída';
                                                    
                                                    const prioColor = {
                                                        'Urgente': 'bg-red-500 text-white',
                                                        'Alta': 'bg-orange-500 text-white',
                                                        'Média': 'bg-yellow-500 text-black',
                                                        'Baixa': 'bg-gray-400 text-white'
                                                    }[note.prioridade] || 'bg-gray-400 text-white';

                                                return (
                                                        <tr key={note.id} className={`transition-colors ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'} ${isResolved ? 'opacity-50' : ''}`}>
                                                            <td className={`p-4 text-center font-mono text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                                                {new Date(note.date + 'T00:00:00').toLocaleDateString('pt-BR')}
                                                            </td>
                                                            <td className="p-4 text-center">
                                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${
                                                                    note.tipo === 'Assinatura' ? 'border-purple-500 text-purple-500 bg-purple-50' :
                                                                    isPending ? 'border-red-500 text-red-500' : 
                                                                    (note.tipo === 'OBS' ? 'border-yellow-500 text-yellow-600' : 'border-blue-500 text-blue-500')
                                                                }`}>
                                                                    {note.tipo.toUpperCase()}
                                                                </span>
                                                            </td>
                                                            <td className="p-4 text-center">
                                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold shadow-sm ${isResolved || note.tipo === 'Assinatura' ? 'bg-green-600 text-white' : prioColor}`}>
                                                                    {note.tipo === 'Assinatura' ? 'REGISTRADA' : (isResolved ? 'CONCLUÍDA' : note.prioridade)}
                                                                </span>
                                                            </td>
                                                            <td className="p-4 relative align-middle"
                                                                onMouseEnter={(e) => {
                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                    const spaceBelow = window.innerHeight - rect.bottom;
                                                                    const position = spaceBelow < 250 ? 'top' : 'bottom';
                                                                    setHoveredNote({ id: note.id, position });
                                                                }}
                                                                onMouseLeave={() => setHoveredNote(null)}
                                                            >
                                                                <div className={`inline-block px-3 py-1.5 rounded-lg text-xs font-medium cursor-help border ${isResolved ? 'line-through opacity-50' : ''} ${isDarkMode ? `${style.darkBg} ${style.darkText} ${style.darkBorder}` : `${style.bg} ${style.text} ${style.border}`}`}>
                                                                    {firstLine}
                                                                </div>

                                                                {hoveredNote && hoveredNote.id === note.id && (
                                                                    <div className={`absolute z-[999] p-3 rounded-lg shadow-2xl w-96 left-10 border animate-fade-in ${hoveredNote.position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} ${isDarkMode ? 'bg-slate-800 text-slate-200 border-slate-600' : 'bg-white text-gray-700 border-gray-200'}`}>
                                                                        <div className={`text-[10px] font-bold uppercase mb-2 border-b pb-1 flex justify-between ${isDarkMode ? 'border-slate-600 text-slate-400' : 'border-gray-100 text-gray-400'}`}>
                                                                            <span>Conteúdo Completo</span>
                                                                        </div>
                                                                        <div className="max-h-48 overflow-y-auto custom-scrollbar whitespace-pre-wrap break-words leading-relaxed text-xs">
                                                                            {note.text}
                                                                        </div>
                                                                        <div className={`absolute left-4 w-3 h-3 border-t border-l rotate-45 ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'} ${hoveredNote.position === 'top' ? '-bottom-1.5 border-t-0 border-l-0 border-b border-r transform rotate-45' : '-top-1.5'}`}></div>
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="p-4 text-center">
                                                                <div className="flex justify-center gap-2">
                                                                    
                                                                    {/* BOTÕES DE AÇÃO - ESCONDIDOS SE FOR ASSINATURA */}
                                                                    {note.tipo !== 'Assinatura' ? (
                                                                        <>
                                                                            {canBeResolved && (
                                                                                <button 
                                                                                    onClick={async () => {
                                                                                        const newStatus = isResolved ? 'Aberta' : 'Concluída';
                                                                                        const updatedNotes = { ...curr.personalNotes, [note.id]: { ...note, status: newStatus } };
                                                                                        const upd = { ...curr, personalNotes: updatedNotes };
                                                                                        await actions.saveEvaluation(upd);
                                                                                        setCurr(upd);
                                                                                    }}
                                                                                    className={`p-1.5 rounded transition-colors ${isResolved ? 'text-orange-500 hover:bg-orange-100' : 'text-green-500 hover:bg-green-100'}`}
                                                                                    title={isResolved ? "Reabrir Item" : "Marcar como Concluído"}
                                                                                >
                                                                                    {isResolved ? <RefreshCw size={16}/> : <CheckCircle size={16}/>}
                                                                                </button>
                                                                            )}
                                                                            
                                                                            <button onClick={() => handleEditNote(note)} className={`p-1.5 rounded transition-colors ${isDarkMode ? 'text-blue-400 hover:bg-slate-600' : 'text-blue-500 hover:bg-blue-50'}`} title="Editar"><Pencil size={16}/></button>
                                                                            <button onClick={() => handleDeleteNote(note.id)} className={`p-1.5 rounded transition-colors ${isDarkMode ? 'text-red-400 hover:bg-slate-600' : 'text-red-500 hover:bg-red-50'}`} title="Excluir"><Trash2 size={16}/></button>
                                                                        </>
                                                                    ) : (
                                                                        <span className="text-[9px] text-gray-400 italic">Protegido</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* --- ABA PDI (PLANO DE DESENVOLVIMENTO INDIVIDUAL) --- */}
                    {activeTab === 'pdi' && (
                        <div className="space-y-6 animate-fade-in pt-2">
                            <div className="flex justify-between items-center mb-4 border-b pb-2">
                                <h4 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-orange-400' : 'text-orange-700'}`}>
                                    <TrendingUp size={20}/> PDI - Plano de Desenvolvimento Individual
                                </h4>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                
                                {/* QUADRO 1: FOCO GERAL (TEXTO) */}
                                <div className={`col-span-1 p-5 rounded-xl border shadow-sm flex flex-col ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-orange-50/30 border-orange-100'}`}>
                                    <label className={`font-bold text-sm block mb-1 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                        Foco de Desenvolvimento (Resumo)
                                    </label>
                                    <p className={`text-[10px] mb-3 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                        Use este espaço para descrever habilidades gerais a focar neste ciclo.
                                    </p>
                                    <textarea 
                                        disabled={isLocked}
                                        className={`w-full p-4 flex-1 rounded-lg border outline-none focus:ring-2 focus:ring-orange-500 min-h-[200px] resize-none custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-gray-300'}`}
                                        placeholder="Ex: Focar em liderança de equipes e comunicação não-violenta..."
                                        value={curr.pdi?.knowledge || ''}
                                        onChange={(e) => setCurr({...curr, pdi: {...curr.pdi, knowledge: e.target.value}})}
                                    />
                                    <div className="mt-3 flex justify-end">
                                        <Button size="sm" variant="secondary" onClick={() => actions.saveEvaluation(curr)} disabled={isLocked} icon={Save}>
                                            Salvar Resumo
                                        </Button>
                                    </div>
                                </div>

                                {/* QUADRO 2: AÇÕES PRÁTICAS (LISTA) */}
                                <div className={`col-span-1 lg:col-span-2 p-5 rounded-xl border shadow-sm flex flex-col ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-blue-50/30 border-blue-100'}`}>
                                    <div className="flex justify-between items-center mb-4">
                                        <div>
                                            <label className={`font-bold text-sm flex items-center gap-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                                                <List size={16}/> Ações Práticas e Metas
                                            </label>
                                            <p className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Defina as tarefas e os prazos de acompanhamento.</p>
                                        </div>
                                        <Button 
                                            onClick={() => {
                                                setFeedbackForm({ 
                                                    id: null, type: 'PDI', text: '', date: new Date().toISOString().split('T')[0],
                                                    actionTag: 'Meta', nature: 'Desenvolvimento', followUpDays: '90',
                                                    followUpDate: calculateFollowUp(new Date().toISOString().split('T')[0], '90'),
                                                    status: 'Pendente'
                                                });
                                                setIsFeedbackModalOpen(true);
                                            }} 
                                            disabled={isLocked}
                                            icon={Plus} size="sm" className="bg-orange-600 hover:bg-orange-700 text-white border-0 shadow-md">
                                            Nova Meta de PDI
                                        </Button>
                                    </div>
                                    
                                    <div className="space-y-3 flex-1 overflow-y-auto custom-scrollbar pr-2 min-h-[200px]">
                                        {feedbacksList.filter(f => f.type === 'PDI').length === 0 ? (
                                            <div className="text-center p-8 h-full flex flex-col items-center justify-center border-2 border-dashed rounded-xl border-gray-300 dark:border-slate-600 text-gray-400">
                                                <TrendingUp size={32} className="mb-2 opacity-30"/>
                                                Nenhuma ação de PDI cadastrada.<br/>Clique no botão acima para adicionar a primeira meta.
                                            </div>
                                        ) : (
                                            feedbacksList.filter(f => f.type === 'PDI').map(pdi => (
                                                <div key={pdi.id} className={`p-4 rounded-lg border flex flex-col gap-2 ${pdi.status === 'Concluído' ? 'opacity-60 bg-gray-100 dark:bg-slate-900' : 'bg-white dark:bg-slate-800 border-l-4 border-l-orange-500 shadow-sm'}`}>
                                                    <div className="flex justify-between items-start">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${pdi.status === 'Concluído' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                                                                {pdi.status || 'Pendente'}
                                                            </span>
                                                            {pdi.followUpDate && (
                                                                <span className={`flex items-center gap-1 text-[10px] font-bold ${new Date(pdi.followUpDate) < new Date() && pdi.status !== 'Concluído' ? 'text-red-500 animate-pulse' : 'text-blue-500'}`}>
                                                                    <Clock size={12}/> Limite: {new Date(pdi.followUpDate).toLocaleDateString('pt-BR')}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex gap-1">
                                                            <button onClick={() => handleEditFeedback(pdi)} disabled={isLocked} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded" title="Editar"><Edit3 size={14}/></button>
                                                            <button onClick={() => handleDeleteFeedback(pdi.id)} disabled={isLocked} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Excluir"><Trash2 size={14}/></button>
                                                        </div>
                                                    </div>
                                                    <p className="text-sm whitespace-pre-wrap">{pdi.text}</p>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'extras' && (
                        <div className="space-y-8 animate-fade-in">
                            {isGestao && (
                                <>
                                    <div className="flex justify-between items-center border-b pb-2 mb-2 relative">
                                        <h4 className="font-bold text-lg text-red-700 flex items-center gap-2">
                                            <Bot size={20}/> AVAL. RESULTADOS E PERCEPÇÃO DA EQUIPE 
                                            <span className="text-xs font-normal text-gray-500 ml-4">(MÁXIMO 40 PONTOS)</span>
                                        </h4>
                                        
                                        {/* MENU A: SINCRONIZAR PERCEPÇÃO */}
                                        <div className="relative">
                                            <button 
                                                className="bg-red-100 text-red-700 hover:bg-red-200 px-3 py-1.5 rounded text-xs font-bold flex items-center gap-2 transition-colors border border-red-200 shadow-sm" 
                                                onClick={() => setSyncMenuOpen(syncMenuOpen === 'perception' ? null : 'perception')}
                                            >
                                                <RefreshCw size={14}/> Sincronizar
                                            </button>
                                            
                                            {syncMenuOpen === 'perception' && (
                                                <div className={`absolute top-full right-0 mt-2 w-48 rounded-lg shadow-xl border z-50 overflow-hidden animate-fade-in ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'}`}>
                                                    <button onClick={() => openSyncModal('perception', 'external')} className={`w-full text-left px-4 py-2 text-xs hover:bg-gray-100 transition-colors ${isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-gray-700'}`}>
                                                        1) Sincronizar dados (Google)
                                                    </button>
                                                    <button onClick={() => openSyncModal('perception', 'internal')} className={`w-full text-left px-4 py-2 text-xs hover:bg-gray-100 transition-colors border-t ${isDarkMode ? 'text-slate-300 hover:bg-slate-700 border-slate-700' : 'text-gray-700 border-gray-100'}`}>
                                                        2) Atualizar dados (Banco)
                                                    </button>
                                                </div>
                                            )}
                                            {syncMenuOpen === 'perception' && <div className="fixed inset-0 z-40" onClick={() => setSyncMenuOpen(null)}></div>}
                                        </div>
                                    </div>
                                    <table className={`w-full text-sm ${isDarkMode ? 'text-slate-300' : ''}`}>
                                        <thead className={`uppercase text-xs font-bold ${isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-800'}`}>
                                            <tr>
                                                <th className="p-3 w-1/3 text-left">Parâmetros</th>
                                                {['1º Trim', '2º Trim', '3º Trim', '4º Trim'].map((t, i) => (
                                                    <th key={i} className={`p-3 text-center ${(i + 1) < (curr.activeFromQuarter || 1) ? 'opacity-40' : ''}`}>{t}</th>
                                                ))}
                                                <th className="p-3 text-center">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                            {(curr.gestaoMetasPercepcao || []).map(item => { 
                                                // Soma apenas trimestres ativos para exibição correta do total neste contexto visual
                                                // OBS: O cálculo real final é feito no recalculateFinalAverage
                                                const totalScore = ['q1', 'q2', 'q3', 'q4'].reduce((sum, q, idx) => {
                                                    if ((idx + 1) < (curr.activeFromQuarter || 1)) return sum; // Pula inativos
                                                    return sum + parseFloat(item.scores?.[q] || 0);
                                                }, 0);
                                                
                                                // Para visualização, se for menor que 1 ano, multiplicamos por 4? 
                                                // No cálculo final (recalculateFinalAverage) já fazemos a projeção. 
                                                // Aqui mostramos a soma dos inputs ou a projeção? 
                                                // Geralmente tabelas mostram os inputs. Vamos manter a soma simples dos inputs aqui.
                                                
                                                return (
                                                    <tr key={item.id} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-red-50/50'}>
                                                        <td className="p-3 font-medium">
                                                            <div className={`font-bold text-sm ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{item.name}</div>
                                                            <div className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>{item.description}</div>
                                                        </td>
                                                        {['q1', 'q2', 'q3', 'q4'].map((q, qIdx) => {
                                                            const isQuarterInactive = (qIdx + 1) < (curr.activeFromQuarter || 1);
                                                            return (
                                                                <td key={q} className={`p-3 text-center ${isQuarterInactive ? (isDarkMode ? 'bg-slate-800/30' : 'bg-gray-50/50') : ''}`}>
                                                                    <input 
                                                                        type="number" min="1" max="5" step="0.5" 
                                                                        disabled={isLocked || isQuarterInactive} 
                                                                        className={`w-16 p-1 border rounded text-center font-bold ${getScoreClass(item.scores[q])} ${isLocked || isQuarterInactive ? 'opacity-60 cursor-not-allowed' : ''} ${isQuarterInactive ? '!bg-transparent !border-dashed !text-gray-400' : ''}`} 
                                                                        value={isQuarterInactive ? '' : (item.scores[q] || '')} 
                                                                        placeholder={isQuarterInactive ? '-' : ''}
                                                                        onChange={e => updateGestaoScore(item.id, q, e.target.value)}
                                                                    />
                                                                </td>
                                                            );
                                                        })}
                                                        <td className={`p-3 text-center font-bold ${isDarkMode ? 'bg-red-900/40 text-red-200' : 'bg-red-200'}`}>
                                                            {/* Exibe o total somado. A projeção final vai para o cabeçalho/card principal */}
                                                            {totalScore.toFixed(2)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </>
                            )}

                            <div className={`pt-6 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="font-bold text-lg text-purple-700">
                                        {isGestao ? "Extras: Atualização e Reciclagem (20 pontos por ano)" : "D) Extras: Atualização e Reciclagem"}
                                    </h4>

                                    {/* MENU B: SINCRONIZAR EXTRAS (TREINAMENTO) */}
                                    <div className="relative">
                                        <button 
                                            className="bg-purple-100 text-purple-700 hover:bg-purple-200 px-3 py-1.5 rounded text-xs font-bold flex items-center gap-2 transition-colors border border-purple-200 shadow-sm" 
                                            onClick={() => setSyncMenuOpen(syncMenuOpen === 'training' ? null : 'training')}
                                        >
                                            <RefreshCw size={14}/> Sincronizar
                                        </button>
                                        
                                        {syncMenuOpen === 'training' && (
                                            <div className={`absolute top-full right-0 mt-2 w-48 rounded-lg shadow-xl border z-50 overflow-hidden animate-fade-in ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'}`}>
                                                <button onClick={() => openSyncModal('training', 'external')} className={`w-full text-left px-4 py-2 text-xs hover:bg-gray-100 transition-colors ${isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-gray-700'}`}>
                                                    1) Sincronizar dados (Google)
                                                </button>
                                                <button onClick={() => openSyncModal('training', 'internal')} className={`w-full text-left px-4 py-2 text-xs hover:bg-gray-100 transition-colors border-t ${isDarkMode ? 'text-slate-300 hover:bg-slate-700 border-slate-700' : 'text-gray-700 border-gray-100'}`}>
                                                    2) Atualizar dados (Banco)
                                                </button>
                                            </div>
                                        )}
                                        {syncMenuOpen === 'training' && <div className="fixed inset-0 z-40" onClick={() => setSyncMenuOpen(null)}></div>}
                                    </div>
                                </div>

                                <table className={`w-full text-sm ${isDarkMode ? 'text-slate-300' : ''}`}>
                                    <thead className={`uppercase text-xs font-bold ${isDarkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-50 text-purple-800'}`}>
                                        <tr><th className="p-3 w-1/3">Área de Atualização</th><th className="p-3 text-center">Horas</th><th className="p-3 text-center">Pontos Máx</th><th className="p-3 text-center">Nota</th></tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                        <tr className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}>
                                            <td className="p-3 font-medium">Na Área: Cursos de graduação, especialização ou mestre (Min 36h)</td>
                                            <td className="p-3 text-center flex items-center justify-center gap-2">
                                                <input type="text" disabled={isLocked} value={formatDecimalToTime(curr.atualizacao?.D1_hours)} onChange={e => updateAtualizacao('D1_hours', formatTimeToDecimal(e.target.value))} className={`w-20 p-1 border rounded text-center font-mono font-bold ${isLocked ? 'bg-gray-100 text-gray-600' : 'bg-white'}`} placeholder="00:00"/>
                                                <span className="text-gray-500 text-xs">(36h)</span>
                                            </td>
                                            <td className="p-3 text-center font-bold text-purple-500">15.00</td>
                                            <td className="p-3 text-center font-bold">{curr.atualizacao?.D1_nota?.toFixed(4) || '0.0000'}</td>
                                        </tr>
                                        <tr className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}>
                                            <td className="p-3 font-medium">Outras Áreas: Cursos livres, graduação (Min 18h)</td>
                                            <td className="p-3 text-center flex items-center justify-center gap-2">
                                                <input type="text" disabled={isLocked} value={formatDecimalToTime(curr.atualizacao?.D2_hours)} onChange={e => updateAtualizacao('D2_hours', formatTimeToDecimal(e.target.value))} className={`w-20 p-1 border rounded text-center font-mono font-bold ${isLocked ? 'bg-gray-100 text-gray-600' : 'bg-white'}`} placeholder="00:00"/>
                                                <span className="text-gray-500 text-xs">(18h)</span>
                                            </td>
                                            <td className="p-3 text-center font-bold text-purple-500">5.00</td>
                                            <td className="p-3 text-center font-bold">{curr.atualizacao?.D2_nota?.toFixed(4) || '0.0000'}</td>
                                        </tr>
                                    </tbody>
                                </table>
                {/* --- LISTAGEM DETALHADA DE CURSOS (SEM OS TOTAIS) --- */}
                            <div className="mt-8 pt-6 border-t border-dashed border-gray-300 dark:border-slate-700">
                                <h4 className={`font-bold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                                    <List size={20}/> Detalhamento dos Cursos Realizados
                                </h4>

                                {(() => {
                                    const getTrainingQuarter = (dateValue) => {
                                        if (!dateValue) return '-';
                                        const raw = String(dateValue);
                                        let d;
                                        if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
                                            const [day, month, year] = raw.split('/').map(Number);
                                            d = new Date(year, month - 1, day);
                                        } else {
                                            d = new Date(raw.includes('T') ? raw : `${raw}T00:00:00`);
                                        }
                                        if (Number.isNaN(d.getTime())) return '-';
                                        return `${Math.floor(d.getMonth() / 3) + 1}º Trim`;
                                    };
                                    const groupedTrainings = collaboratorTrainings.reduce((acc, training) => {
                                        const quarter = getTrainingQuarter(training.date);
                                        if (!acc[quarter]) acc[quarter] = [];
                                        acc[quarter].push(training);
                                        return acc;
                                    }, {});
                                    const quarterOrder = ['1º Trim', '2º Trim', '3º Trim', '4º Trim', '-'];
                                    const orderedGroups = Object.entries(groupedTrainings).sort((a, b) => quarterOrder.indexOf(a[0]) - quarterOrder.indexOf(b[0]));
                                    return (
                                <>
                                {/* TABELA DE CURSOS - AJUSTADA PARA NÃO QUEBRAR TEXTO NO TIPO */}
                                <div className={`border rounded-lg overflow-hidden shadow-sm ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                    <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                        <thead className={`uppercase text-xs font-bold ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-600'}`}>
                                            <tr>
                                                <th className="p-3 w-24 text-center">Data</th>
                                                <th className="p-3 w-24 text-center">Trimestre</th>
                                                <th className="p-3">Curso / Descrição</th>
                                                {/* Aumentada a largura de w-32 para w-44 para acomodar o texto em linha única */}
                                                <th className="p-3 w-44 text-center">Tipo</th>
                                                <th className="p-3 w-20 text-center">Horas</th>
                                                <th className="p-3 w-40 text-center">Comprovantes</th>
                                            </tr>
                                        </thead>
                                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700 bg-slate-800' : 'divide-gray-100 bg-white'}`}>
                                            {collaboratorTrainings.length > 0 ? (
                                                orderedGroups.flatMap(([quarter, items]) => [
                                                    <tr key={`group-${quarter}`} className={isDarkMode ? 'bg-slate-900/80' : 'bg-purple-50/70'}>
                                                        <td colSpan={6} className={`px-3 py-2 text-xs font-black uppercase tracking-wider ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                                                            {quarter} — {items.length} curso(s) — {formatDecimalToTime(items.reduce((sum, item) => sum + (parseFloat(item.hours) || 0), 0))}h
                                                        </td>
                                                    </tr>,
                                                    ...items.map(t => {
                                                    const typeLower = (t.type || '').toLowerCase();
                                                    const isD1 = typeLower.includes('1') || typeLower.includes('atuação') || typeLower.includes('atuacao');
                                                    
                                                    return (
                                                        <tr key={t.id} className={`transition-colors ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'} ${t.isLate ? 'opacity-80 bg-red-50/30 dark:bg-red-900/10' : ''}`}>
                                                            <td className={`p-3 text-center text-xs font-mono ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{t.date}</td>
                                                            <td className="p-3 text-center"><span className={`px-2 py-1 rounded text-[10px] font-bold ${isDarkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>{quarter}</span></td>
                                                            <td className="p-3 font-medium">
                                                                <div className="flex flex-col gap-1">
                                                                    <span>{t.description}</span>
                                                                    {/* ALERTA DE CURSO ATRASADO */}
                                                                    {t.isLate && (
                                                                        <span className={`w-fit px-2 py-0.5 rounded text-[10px] font-bold ${isDarkMode ? 'bg-red-900/40 text-red-300 border border-red-800' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                                                                            ⚠ Enviado após fechamento (Não Contabilizado)
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="p-3 text-center align-middle">
                                                                <span className={`px-3 py-1 rounded-full text-[10px] font-bold border whitespace-nowrap shadow-sm ${t.isLate ? 'bg-gray-100 text-gray-500 border-gray-300 dark:bg-slate-800 dark:text-gray-400 dark:border-slate-600' : (isD1 ? (isDarkMode ? 'bg-blue-900/40 text-blue-300 border-blue-800' : 'bg-blue-50 text-blue-700 border-blue-200') : (isDarkMode ? 'bg-green-900/40 text-green-300 border-green-800' : 'bg-green-50 text-green-700 border-green-200'))}`}>
                                                                    {t.type}
                                                                </span>
                                                            </td>
                                                            {/* HORAS RISCADAS EM VERMELHO SE ATRASADO */}
                                                            <td className={`p-3 text-center font-bold font-mono ${t.isLate ? 'line-through text-red-500 dark:text-red-400' : (isDarkMode ? 'text-purple-400' : 'text-purple-600')}`}>
                                                                {formatDecimalToTime(t.hours)}h
                                                            </td>
                                                            <td className="p-3 text-center">
                                                                <div className="flex items-center justify-center gap-2 text-xs">
                                                                    {t.summaryLink ? (
                                                                        <a href={t.summaryLink} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 hover:underline flex items-center gap-1">
                                                                            <FileText size={12}/> Resumo
                                                                        </a>
                                                                    ) : <span className="text-gray-300">-</span>}
                                                                    {t.certLink ? (
                                                                        <a href={t.certLink} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-800 hover:underline flex items-center gap-1">
                                                                            <Award size={12}/> Certificado
                                                                        </a>
                                                                    ) : <span className="text-gray-300">-</span>}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                    })
                                                ])
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="p-8 text-center text-gray-400 italic">Nenhum curso registrado para este período.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                </>
                                    );
                                })()}
                            </div>             
                        </div>
                        </div>
                    )}
                </Card>

                
                {/* POPUP DE TOOLTIP GERAL (CORRIGIDO: tooltipState) */}
                {tooltipState && (
                    <div 
                        className="fixed float-popup w-80 z-[9999]" 
                        style={{ 
                            top: tooltipState.y, 
                            left: tooltipState.x, 
                            transform: tooltipState.type === 'incident' ? 'translate(-50%, -100%)' : 'translateX(-100%)', 
                            pointerEvents: 'auto', 
                            marginTop: tooltipState.type === 'incident' ? '-10px' : '0',
                            marginLeft: tooltipState.type === 'incident' ? '0' : '-10px'
                        }} 
                                    >
                        <div className={`p-3 text-gray-700 shadow-2xl border rounded-lg overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-white border-gray-200'}`}>
                            
                            {/* HEADER DO TOOLTIP */}
                            <div className={`font-bold mb-2 border-b pb-1 text-xs uppercase flex justify-between items-center ${isDarkMode ? 'text-emerald-400 border-slate-600' : 'text-emerald-600 border-gray-100'}`}>
                                <span>
                                    {tooltipState.type === 'incident' ? 'Detalhes das Ocorrências' : 
                                    tooltipState.type === 'annual_detail' ? 'Memória de Cálculo Anual' : 
                                    tooltipState.type === 'monthly_detail' ? 'Memória de Cálculo Mensal' :
                                    tooltipState.type === 'feedbacks' ? `Histórico de Feedbacks (${tooltipState.data?.year})` : 'Detalhes'}
                                </span>
                                {tooltipState.type === 'incident' && (
                                    <span className="bg-red-100 text-red-600 px-1.5 rounded text-[9px] border border-red-200">{tooltipState.incidents?.length} item(s)</span>
                                )}
                            </div>
                            
                            {/* CORPO: OCORRÊNCIAS (QUANTITATIVA) */}
                            {tooltipState.type === 'incident' && tooltipState.incidents && (
                                <div className="max-h-60 overflow-y-auto custom-scrollbar space-y-2">
                                    {tooltipState.incidents.map((inc, idx) => (
                                        <div key={idx} className={`text-xs p-2 rounded border shadow-sm ${isDarkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-100'}`}>
                                            <div className="flex justify-between items-start mb-1 border-b pb-1 border-dashed border-gray-300 dark:border-slate-500">
                                                <div className="flex flex-col">
                                                    <span className={`font-bold ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                                                        {new Date(inc.date).toLocaleDateString('pt-BR')}
                                                    </span>
                                                    <span className="text-[9px] opacity-70">{inc.conferenciaType || 'Geral'}</span>
                                                </div>
                                                <span className="font-bold text-red-500 bg-red-100 dark:bg-red-900/30 px-1.5 rounded border border-red-200 dark:border-red-800">
                                                    Peso: {inc.weight}
                                                </span>
                                            </div>
                                            
                                            <div className={`text-[11px] leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                                                {(() => {
                                                    const urlRegex = /(https?:\/\/[^\s]+)/g;
                                                    const parts = (inc.description || '').split(urlRegex);
                                                    return parts.map((part, i) => 
                                                        urlRegex.test(part) ? (
                                                            <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 underline pointer-events-auto" onClick={(e) => e.stopPropagation()}>{part}</a>
                                                        ) : part
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* OUTROS TIPOS DE TOOLTIP */}
                            {(tooltipState.type === 'annual_detail' || tooltipState.type === 'monthly_detail') && tooltipState.data && (
                                <div className="space-y-1">
                                    <div className="flex justify-between text-xs"><span>Aumento Salarial:</span> <span className="font-bold text-blue-600">{formatCurrency(tooltipState.data.promoMensal)}</span></div>
                                    <div className="flex justify-between text-xs"><span>Premiação:</span> <span className="font-bold text-purple-600">{formatCurrency(tooltipState.data.premiacao)}</span></div>
                                    <div className="border-t pt-1 mt-1 border-gray-200"></div>
                                    <div className="flex justify-between text-xs"><span>FGTS (8%):</span> <span className="font-bold text-red-600">{formatCurrency(tooltipState.data.fgts)}</span></div>
                                    <div className="border-t pt-1 mt-1 font-bold text-sm text-emerald-700 flex justify-between border-gray-200">
                                        <span>TOTAL:</span> 
                                        <span>{formatCurrency(tooltipState.data.promoMensal + tooltipState.data.premiacao + tooltipState.data.decimoTerceiro + tooltipState.data.tercoFerias + tooltipState.data.dsr + tooltipState.data.reflexoFerias13 + tooltipState.data.fgts)}</span>
                                    </div>
                                </div>
                            )}

                            {tooltipState.type === 'feedbacks' && tooltipState.data?.feedbacks && (
                                <div className="max-h-60 overflow-y-auto custom-scrollbar space-y-2">
                                    {Object.values(tooltipState.data.feedbacks).length === 0 ? (
                                        <p className="text-xs italic text-gray-400">Nenhum feedback registrado.</p>
                                    ) : (
                                        Object.values(tooltipState.data.feedbacks).sort((a, b) => b.timestamp - a.timestamp).map((f, idx) => (
                                            <div key={idx} className={`text-xs p-2 mb-2 rounded border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-100'}`}>
                                                <div className="flex justify-between items-center mb-1 border-b pb-1 border-gray-200 dark:border-slate-600">
                                                    <span className="font-bold uppercase text-[10px] text-blue-500">{f.type} - {f.quarter?.toUpperCase()}</span>
                                                    <span className="text-[9px] opacity-70">{new Date(f.timestamp).toLocaleDateString()}</span>
                                                </div>
                                                <p className="whitespace-pre-wrap leading-relaxed opacity-90">{f.text}</p>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}                                 
                                </div> {/* Fecha a div principal do AvaliacaoSystem */}
                            <div className={`absolute bottom-[-6px] left-1/2 -ml-2 w-3 h-3 rotate-45 border-b border-r ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} ${tooltipState.type !== 'incident' ? 'hidden' : ''}`}></div>
                    </div>
                )}
                {/* MODAL GLOBAL DE ALERTA (DINÂMICO: SUCESSO vs ERRO) */}
                        {validationAlert && (
                            <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setValidationAlert(null)}>
                                <div className={`p-6 rounded-xl w-96 shadow-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()}>
                                    
                                    {(() => {
                                        // Verifica se é Sucesso pelo título
                                        const isSuccess = validationAlert.title === 'Sucesso!' || validationAlert.title === 'Sucesso';
                                        const isAISuggestion = validationAlert.type === 'aiSuggestion';
                                        
                                        return (
                                            <>
                                                <div className={`flex items-center gap-3 mb-4 ${isSuccess ? 'text-green-500' : isAISuggestion ? 'text-purple-500' : 'text-red-500'}`}>
                                                    <div className={`p-2 rounded-full ${isSuccess ? 'bg-green-100' : isAISuggestion ? 'bg-purple-100' : 'bg-red-100'}`}>
                                                        {isSuccess ? <CheckCircle size={24} /> : isAISuggestion ? <Sparkles size={24} /> : <AlertTriangle size={24} />}
                                                    </div>
                                                    <h3 className="font-bold text-lg">{validationAlert.title || 'Atenção'}</h3>
                                                </div>
                                                
                                                <p className={`text-sm mb-6 whitespace-pre-wrap leading-relaxed max-h-[45vh] overflow-y-auto custom-scrollbar ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                    {validationAlert.message || (typeof validationAlert === 'string' ? validationAlert : 'Erro desconhecido')}
                                                </p>
                                                
                                                <div className="flex justify-end gap-2">
                                                    <Button 
                                                        variant="secondary"
                                                        onClick={() => setValidationAlert(null)} 
                                                        className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}
                                                    >
                                                        {validationAlert.cancelText || (isSuccess ? 'Continuar' : 'Entendi')}
                                                    </Button>
                                                    {validationAlert.onConfirm && (
                                                        <Button
                                                            onClick={validationAlert.onConfirm}
                                                            className={`${isAISuggestion ? 'bg-purple-600 hover:bg-purple-700' : 'bg-red-600 hover:bg-red-700'} text-white shadow-md`}
                                                        >
                                                            {validationAlert.confirmText || 'Confirmar'}
                                                        </Button>
                                                    )}
                                                </div>
                                            </>
                                        );
                                    })()}
                                    
                                </div>
                            </div>
                        )}
            </div>
        );
            };

        /**
         * ============================================================================
         * SUB-COMPONENTES DO FINANCEIRO (DASHBOARD WIDGETS)
         * ============================================================================
         * Ajustados para responsividade real: ocupam 100% do container pai e
         * reagem ao redimensionamento.
         */

        // --- 1. TABELA DE ANÁLISE DE FUNCIONÁRIOS (RESPONSIVA) ---
        const EmployeeAnalysisTable = ({ data, totalEmployees, isDarkMode }) => {
            return (
            <div className="flex flex-col h-full w-full min-h-0">
                {/* Header fixo visualmente dentro do card */}
                <div className={`shrink-0 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                    <table className={`w-full text-sm text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                        <thead className={`text-xs uppercase font-bold ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-600'}`}>
                            <tr>
                                <th className="p-3 w-1/2">Departamento</th>
                                <th className="p-3 text-center">Qtde.</th>
                                <th className="p-3 text-center">% Total</th>
                            </tr>
                        </thead>
                    </table>
                </div>
                
                {/* Corpo com scroll */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <table className={`w-full text-sm text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                            {data.deptAnalysis.map((d, i) => (
                                <tr key={d.id || i} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}>
                                    <td className={`p-3 w-1/2 font-medium flex items-center gap-2 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                        <div className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: d.color }}></div>
                                        <span className="truncate" title={d.name}>{d.name}</span>
                                    </td>
                                    <td className="p-3 text-center">{d.restricted ? '-' : d.count}</td>
                                    <td className="p-3 text-center font-bold text-blue-500">
                                        {d.restricted ? '-' : ((d.count / totalEmployees) * 100).toFixed(1) + '%'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                
                {/* Footer fixo */}
                <div className={`shrink-0 border-t-2 ${isDarkMode ? 'bg-slate-800 border-blue-900 text-blue-400' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
                    <table className="w-full text-sm font-bold">
                        <tfoot>
                            <tr>
                                <td className="p-3 w-1/2">Total Geral:</td>
                                <td className="p-3 text-center">{totalEmployees}</td>
                                <td className="p-3 text-center">100%</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        );
        };

        // --- 2. TABELA DE FOLHA DE PAGAMENTO (RESPONSIVA) ---
        const PayrollAnalysisTable = ({ data, totalPayroll, totalEmployees, isDarkMode }) => {
        return (
            <div className="flex flex-col h-full w-full min-h-0">
                <div className={`shrink-0 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                    <table className={`w-full text-sm text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                        <thead className={`text-xs uppercase font-bold ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-600'}`}>
                            <tr>
                                <th className="p-3 w-1/2">Departamento</th>
                                <th className="p-3 text-right">Folha</th>
                                <th className="p-3 text-center">%</th>
                                <th className="p-3 text-right">Média</th>
                            </tr>
                        </thead>
                    </table>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <table className={`w-full text-sm text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                            {data.deptAnalysis.map((d, i) => (
                                <tr key={d.id || i} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}>
                                    <td className={`p-3 w-1/2 font-medium flex items-center gap-2 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                        <div className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: d.color }}></div>
                                        <span className="truncate" title={d.name}>{d.name}</span>
                                    </td>
                                    <td className="p-3 text-right font-bold text-green-500">
                                        {d.restricted ? '-' : formatCurrency(d.payroll)}
                                    </td>
                                    <td className="p-3 text-center text-xs">
                                        {d.restricted ? '-' : ((d.payroll / totalPayroll) * 100).toFixed(1) + '%'}
                                    </td>
                                    <td className={`p-3 text-right text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                        {d.restricted ? '-' : formatCurrency(d.payroll / d.count)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className={`shrink-0 border-t-2 ${isDarkMode ? 'bg-slate-800 border-green-900 text-green-400' : 'bg-green-50 border-green-200 text-green-800'}`}>
                    <table className="w-full text-sm font-bold">
                        <tfoot>
                            <tr>
                                <td className="p-3 w-1/2">Total:</td>
                                <td className="p-3 text-right">{formatCurrency(totalPayroll)}</td>
                                <td className="p-3 text-center">100%</td>
                                <td className="p-3 text-right">
                                    {totalEmployees > 0 ? formatCurrency(totalPayroll / totalEmployees) : formatCurrency(0)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        );
        };

        // --- 1. TABELA UNIFICADA: DISTRIBUIÇÃO POR DEPARTAMENTO (LAYOUT CORRIGIDO) ---
        const DepartmentDistributionTable = ({ data, totalEmployees, totalPayroll, isDarkMode }) => {
        const [tooltip, setTooltip] = React.useState(null);

        const formatCurrencyAbs = (val) => {
            const num = Math.abs(parseFloat(val) || 0);
            return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
        };

        // Mantivemos as larguras originais conforme solicitado,
        // mas o ajuste de HTML abaixo fará o texto preencher o espaço corretamente.
        const widthClasses = {
            col1: "w-[35%]", // Departamento
            col2: "w-[10%]", // Qtde
            col3: "w-[10%]", // %
            col4: "w-[20%]", // Total Folha
            col5: "w-[10%]", // %
            col6: "w-[15%]"  // Média
        };
        
        const list = Array.isArray(data) ? data : []; 

        return (
            <div className="flex flex-col h-full w-full min-h-0 relative">
                {/* Header fixo */}
                <div className={`shrink-0 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                    <table className={`w-full text-sm text-left table-fixed ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                        <thead className={`text-xs uppercase font-bold ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-600'}`}>
                            <tr>
                                <th className={`p-3 text-left truncate ${widthClasses.col1}`}>Departamento</th>
                                <th className={`p-3 text-center truncate ${widthClasses.col2}`}>Qtde.</th>
                                <th className={`p-3 text-center truncate ${widthClasses.col3}`}>%</th>
                                <th className={`p-3 text-right truncate ${widthClasses.col4}`}>Total Folha</th>
                                <th className={`p-3 text-center truncate ${widthClasses.col5}`}>%</th>
                                <th className={`p-3 text-right truncate ${widthClasses.col6}`}>Média</th>
                            </tr>
                        </thead>
                    </table>
                </div>

                {/* Corpo com scroll */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <table className={`w-full text-sm text-left table-fixed ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                            {list.map((d, i) => (
                                <tr key={d.id || i} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}>
                                    {/* CORREÇÃO AQUI: Removemos o flex do TD e colocamos num DIV interno */}
                                    <td 
                                        className={`p-3 cursor-help ${widthClasses.col1} ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}
                                        onMouseEnter={(e) => {
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            setTooltip({
                                                x: rect.left + 20,
                                                y: rect.bottom - 5,
                                                title: d.name,
                                                people: d.people || []
                                            });
                                        }}
                                        onMouseLeave={() => setTooltip(null)}
                                    >
                                        <div className="flex items-center gap-2 w-full">
                                            <div className="w-3 h-3 rounded-full shadow-sm shrink-0" style={{ backgroundColor: d.color }}></div>
                                            {/* min-w-0 é essencial para o truncate funcionar dentro de flexbox */}
                                            <span className="truncate min-w-0 font-medium" title="Passe o mouse para ver colaboradores">
                                                {d.name}
                                            </span>
                                        </div>
                                    </td>

                                    <td className={`p-3 text-center font-bold truncate ${widthClasses.col2}`}>
                                        {d.restricted ? '-' : d.count}
                                    </td>
                                    <td className={`p-3 text-center truncate ${widthClasses.col3}`}>
                                        <div className="flex flex-col items-center justify-center w-full px-1">
                                            <span className="text-xs font-bold text-blue-500">
                                                {d.restricted ? '-' : ((d.count / totalEmployees) * 100).toFixed(1) + '%'}
                                            </span>
                                            {!d.restricted && totalEmployees > 0 && (
                                                <div className={`w-full h-1.5 rounded-full mt-1 overflow-hidden ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                                                    <div 
                                                        className="h-full rounded-full bg-blue-500 transition-all duration-500" 
                                                        style={{ width: `${(d.count / totalEmployees) * 100}%` }}
                                                    ></div>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className={`p-3 text-right font-bold text-green-500 truncate ${widthClasses.col4}`}>
                                        {d.restricted ? '-' : formatCurrencyAbs(d.payroll)}
                                    </td>
                                    <td className={`p-3 text-center text-xs truncate ${widthClasses.col5}`}>
                                        {d.restricted ? '-' : ((d.payroll / totalPayroll) * 100).toFixed(1) + '%'}
                                    </td>
                                    <td className={`p-3 text-right text-xs font-mono truncate ${widthClasses.col6} ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                        {d.restricted ? '-' : formatCurrencyAbs(d.payroll / d.count)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Footer fixo com Totais */}
                <div className={`shrink-0 border-t-2 ${isDarkMode ? 'bg-slate-800 border-indigo-900 text-indigo-400' : 'bg-indigo-50 border-indigo-200 text-indigo-800'}`}>
                    <table className="w-full text-sm font-bold table-fixed">
                        <tfoot>
                            <tr>
                                <td className={`p-3 truncate ${widthClasses.col1}`}>Total Geral:</td>
                                <td className={`p-3 text-center truncate ${widthClasses.col2}`}>{totalEmployees}</td>
                                <td className={`p-3 text-center truncate ${widthClasses.col3}`}>100%</td>
                                <td className={`p-3 text-right truncate ${widthClasses.col4}`}>{formatCurrencyAbs(totalPayroll)}</td>
                                <td className={`p-3 text-center truncate ${widthClasses.col5}`}>100%</td>
                                <td className={`p-3 text-right truncate ${widthClasses.col6}`}>
                                    {totalEmployees > 0 ? formatCurrencyAbs(totalPayroll / totalEmployees) : formatCurrencyAbs(0)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* POPUP DE COLABORADORES */}
                {tooltip && (
                    <div 
                        className={`fixed z-[9999] w-64 max-h-60 overflow-y-auto rounded-lg shadow-2xl border p-2 animate-fade-in ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'bg-white border-gray-200 text-gray-700'}`}
                        style={{ top: tooltip.y, left: tooltip.x }}
                        onMouseLeave={() => setTooltip(null)}
                    >
                        <div className={`text-xs font-bold uppercase border-b pb-1 mb-1 ${isDarkMode ? 'border-slate-700 text-blue-400' : 'border-gray-100 text-blue-600'}`}>
                            {tooltip.title} ({tooltip.people.length})
                        </div>
                        <ul className="space-y-1">
                            {tooltip.people.map((p, idx) => (
                                <li key={idx} className={`text-[10px] flex flex-col p-1 rounded ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-50'}`}>
                                    <span className="font-bold flex justify-between">
                                        {p.name}
                                        {p.share && <span className="text-orange-500 text-[9px] ml-1">({p.share})</span>}
                                    </span>
                                    <span className={`truncate ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>{p.role}</span>
                                </li>
                            ))}
                            {tooltip.people.length === 0 && <li className="text-[10px] italic opacity-50">Nenhum dado.</li>}
                        </ul>
                    </div>
                )}
            </div>
        );
        };

        // --- 2. GRÁFICO DE PIZZA 3D (RESPONSIVO) ---
        const DepartmentPieChart = ({ data, isDarkMode }) => {
        const list = Array.isArray(data) ? data : [];
        const total = list.reduce((acc, item) => acc + item.count, 0);
        let cumulativePercent = 0;
        
        const getCoordinatesForPercent = (percent) => { 
            const x = Math.cos(2 * Math.PI * percent);
            const y = Math.sin(2 * Math.PI * percent); 
            return [x, y]; 
        };

        const slices = list.map(item => {
            const startPercent = cumulativePercent; 
            const slicePercent = total > 0 ? item.count / total : 0; 
            cumulativePercent += slicePercent; 
            const endPercent = cumulativePercent;
            
            const [startX, startY] = getCoordinatesForPercent(startPercent); 
            const [endX, endY] = getCoordinatesForPercent(endPercent);
            const largeArcFlag = slicePercent > 0.5 ? 1 : 0;
            
            return { 
                ...item, 
                pathData: `M 0 0 L ${startX} ${startY} A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY} L 0 0` 
            };
        });

        return (
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 h-full w-full p-4 overflow-hidden">
                {/* Gráfico SVG Escalável */}
                <div className="relative flex-1 h-full w-full min-h-[150px] flex items-center justify-center">
                    <div className="relative h-full aspect-square max-h-[95%]">
                        <svg viewBox="-1.2 -1.2 2.4 2.4" className="w-full h-full drop-shadow-xl overflow-visible" style={{ transform: 'rotate(-90deg)' }}>
                            {slices.map((slice, i) => (
                                <g key={i}>
                                    <path d={slice.pathData} fill={slice.color} style={{ filter: 'brightness(0.8)' }} transform="translate(0.02, 0.02)" />
                                    <path d={slice.pathData} fill={slice.color} stroke={isDarkMode ? '#1e293b' : 'white'} strokeWidth="0.02" className="hover:opacity-90 cursor-pointer transition-opacity">
                                        <title>{slice.name}: {slice.count}</title>
                                    </path>
                                </g>
                            ))}
                            {/* Donut Hole */}
                            <circle cx="0" cy="0" r="0.45" fill={isDarkMode ? '#1e293b' : 'white'} />
                        </svg>
                        {/* Texto Central */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                            <span className={`text-2xl md:text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>{total}</span>
                            <span className={`text-[9px] uppercase font-bold tracking-widest ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`}>Colab.</span>
                        </div>
                    </div>
                </div>
                
                {/* Legenda Lateral com Scroll */}
                <div className="flex-1 w-full md:w-auto h-full overflow-hidden flex flex-col md:max-w-[50%]">
                    <h4 className={`font-bold mb-2 border-b pb-2 text-sm uppercase flex justify-between shrink-0 ${isDarkMode ? 'text-slate-200 border-slate-700' : 'text-slate-700 border-gray-200'}`}>
                        <span>Departamento</span><span>Qtd.</span>
                    </h4>
                    <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-1">
                        {list.map((item, idx) => (
                            <div key={idx} className={`flex items-center justify-between p-1.5 rounded transition-colors ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}`}>
                                <div className="flex items-center gap-2 overflow-hidden">
                                    <div className="w-2.5 h-2.5 rounded-full shadow-sm shrink-0" style={{ backgroundColor: item.color }}></div>
                                    <span className={`text-xs font-bold truncate ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{item.name}</span>
                                </div>
                                <div className="flex items-center gap-2 pl-2">
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-100 text-slate-700'}`}>{item.count}</span>
                                    <span className="text-[10px] text-slate-400 w-8 text-right">{total > 0 ? ((item.count / total) * 100).toFixed(0) + '%' : '0%'}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
        };

        // --- 3. ANÁLISE DE CUSTO POR CARGO (HIERÁRQUICO - COM FILTROS CORRIGIDOS) ---
        const RoleCostAnalysis = ({ data, currentUser, analiseFilters, isDarkMode }) => {
        const [expandedCats, setExpandedCats] = useState({});
        const toggleCat = (catName) => { 
            setExpandedCats(prev => ({ ...prev, [catName]: !prev[catName] }));
        };

        const { categories, maxCatCost, totalCost } = useMemo(() => {
            const cats = {}; 
            let grandTotal = 0; 
            let maxVal = 0;
            
            const getCategory = (roleName) => { 
                const r = roleName ? roleName.toUpperCase().trim() : ''; 
                if (r.includes('GERENTE') || r.includes('DIRETOR') || r.includes('HEAD')) return 'GERENTE/DIRETORIA'; 
                if (r.includes('COORDENADOR') || r.includes('SUPERVISOR') || r.includes('LIDER') || r.includes('ENCARREGADO')) return 'ENCARREGADO/COORDENAÇÃO'; 
                if (r.includes('ANALISTA')) return 'ANALISTA'; 
                if (r.includes('ASSISTENTE')) return 'ASSISTENTE'; 
                if (r.includes('AUXILIAR')) return 'AUXILIAR'; 
                if (r.includes('ESTAGIARIO') || r.includes('ESTAGIÁRIO')) return 'ESTAGIÁRIO'; 
                return 'OUTROS CARGOS'; 
            };

            data.collaborators.forEach(c => {
                // 1. APLICAÇÃO DOS FILTROS DE TELA (PRIORIDADE)
                // Se houver filtro de Depto e não bater, ignora
                if (analiseFilters.dept && c.departmentId !== analiseFilters.dept) return;
                // Se houver filtro de Pessoa e não bater, ignora
                if (analiseFilters.personType !== 'Todos' && c.personType !== analiseFilters.personType) return;

                // 2. VERIFICAÇÃO DE PERMISSÕES DE SEGURANÇA
                let canView = true;

                if (currentUser.role === 'SuperAdm') {
                    canView = true;
                } else {
                    // Regra ADM: Só vê departamento dele
                    if (c.departmentId !== currentUser.departmentId) {
                        canView = false;
                    } else {
                        // Mesmo departamento. Checa sigilo de Gestão.
                        if (c.personType === 'Gestão') {
                            if (currentUser.permissions?.viewGestao) {
                                canView = true;
                            } else {
                                const rUpper = (c.role || '').toUpperCase();
                                if (rUpper.includes('GERENTE') || rUpper.includes('DIRETOR') || rUpper.includes('HEAD')) {
                                    canView = false;
                                } else {
                                    canView = true;
                                }
                            }
                        } else {
                            canView = true;
                        }
                    }
                }

                if (!canView) return;

                // Lógica de Agrupamento
                const cDeptName = data.departments.find(d => d.id === c.departmentId)?.name || 'N/D';
                const currentPcs = data.pcs.find(p => p.role === c.role && p.seniority === c.seniority && p.level === c.level);
                const salary = currentPcs ? parseFloat(currentPcs.salary) : parseFloat(c.salary || 0);
                
                const catKey = getCategory(c.role);
                const fullRoleLabel = `${c.role} ${c.seniority || ''} ${c.level || ''}`.trim();
                
                if (!cats[catKey]) cats[catKey] = { name: catKey, count: 0, cost: 0, depts: {} };
                cats[catKey].count++;
                cats[catKey].cost += salary;
                
                if (!cats[catKey].depts[cDeptName]) cats[catKey].depts[cDeptName] = { name: cDeptName, count: 0, cost: 0, roles: {} };
                const deptNode = cats[catKey].depts[cDeptName];
                deptNode.count++; 
                deptNode.cost += salary;
                
                if (!deptNode.roles[fullRoleLabel]) deptNode.roles[fullRoleLabel] = { name: fullRoleLabel, count: 0, cost: 0 };
                deptNode.roles[fullRoleLabel].count++; 
                deptNode.roles[fullRoleLabel].cost += salary;
                
                grandTotal += salary;
            });

            const sortedCats = Object.values(cats).map(cat => {
                const sortedDepts = Object.values(cat.depts).map(dept => {
                    const sortedRoles = Object.values(dept.roles).sort((a,b) => b.cost - a.cost);
                    return { ...dept, roles: sortedRoles };
                }).sort((a,b) => b.cost - a.cost);
                return { ...cat, depts: sortedDepts };
            }).sort((a, b) => b.cost - a.cost);
            
            if (sortedCats.length > 0) maxVal = sortedCats[0].cost;
            
            return { categories: sortedCats, maxCatCost: maxVal, totalCost: grandTotal };
        }, [data, currentUser, analiseFilters]);

        return (
            <div className="flex flex-col h-full min-h-0">
                <div className={`p-4 border-b flex justify-between items-center shrink-0 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-slate-50 border-gray-100'}`}>
                    <div><h3 className={`font-bold flex items-center gap-2 text-sm ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}><BadgeDollarSign size={16} className="text-emerald-600"/> Custo & Pessoas por Nível</h3></div>
                    <div className="text-right">
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Seleção</div>
                        <div className="text-xs font-bold text-emerald-600">{formatCurrency(totalCost)}</div>
                    </div>
                </div>
                
                <div className={`overflow-y-auto custom-scrollbar flex-1 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                    {categories.map((cat, idx) => (
                        <div key={idx} className={`border-b last:border-0 ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                            <div className={`p-3 cursor-pointer transition-colors group ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}`} onClick={() => toggleCat(cat.name)}>
                                <div className="flex justify-between items-end mb-1">
                                    <div className="flex items-center gap-2">
                                        <div className={`text-gray-400 transition-transform duration-200 ${expandedCats[cat.name] ? 'rotate-90' : ''}`}><ChevronRight size={14}/></div>
                                        <span className={`text-xs font-bold ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>{cat.name}</span>
                                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border flex items-center gap-1 ${isDarkMode ? 'bg-blue-900/50 text-blue-300 border-blue-800' : 'bg-blue-50 text-blue-700 border-blue-100'}`}><Users size={8} /> {cat.count}</span>
                                    </div>
                                    <span className="text-xs font-bold text-emerald-600">{formatCurrency(cat.cost)}</span>
                                </div>
                                <div className={`w-full rounded-full h-1.5 overflow-hidden relative ml-5 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                                    <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400" style={{ width: `${(cat.cost / maxCatCost) * 100}%` }}></div>
                                </div>
                            </div>

                            {expandedCats[cat.name] && (
                                <div className={`${isDarkMode ? 'bg-slate-900/50' : 'bg-gray-50/50'} inner-shadow`}>
                                    {cat.depts.map((dept, dIdx) => (
                                        <div key={dIdx} className={`pl-8 pr-4 py-2 border-t border-dashed ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                            <div className="flex justify-between items-center mb-2">
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-[10px] font-bold uppercase tracking-wide px-1 rounded border ${isDarkMode ? 'bg-slate-800 text-slate-400 border-slate-600' : 'bg-white text-gray-500 border-gray-200'}`}>{dept.name}</span>
                                                    <span className="text-[9px] text-gray-400">({dept.count})</span>
                                                </div>
                                                <span className={`text-[10px] font-bold ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{formatCurrency(dept.cost)}</span>
                                            </div>
                                            <div className={`space-y-1 pl-2 border-l-2 ml-1 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                                {dept.roles.map((role, rIdx) => (
                                                    <div key={rIdx} className={`flex justify-between items-center text-[10px] p-1 rounded transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-white text-gray-600'}`}>
                                                        <span className="font-medium truncate w-2/3" title={role.name}>{role.name}</span>
                                                        <div className="flex items-center gap-3">
                                                            <span className={`px-1 rounded ${isDarkMode ? 'bg-slate-700 text-slate-500' : 'bg-gray-100 text-gray-400'}`}>{role.count}</span>
                                                            <span className="font-mono text-emerald-600 font-bold">{formatCurrency(role.cost)}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
        };

        // --- 4. EVOLUÇÃO TRIMESTRAL (CORRIGIDO: Oculta dados sem assinatura) ---
        const QuarterlyEvolutionTable = ({ data, currentUser, analiseFilters, isDarkMode, hideInternalFilter, showSelfOnly = false }) => {
        const [tooltip, setTooltip] = useState(null);
        const [viewMode, setViewMode] = useState('table'); // 'table' | 'chart'
        
        const yearToUse = analiseFilters?.endDate ? parseInt(analiseFilters.endDate.split('-')[0]) : new Date().getFullYear();
        const [internalYear, setInternalYear] = useState('');
        const displayYear = internalYear ? parseInt(internalYear) : yearToUse;

        const timeline = useMemo(() => {
            const quarters = [];
            const today = new Date();
            const currentRealYear = today.getFullYear();
            const currentMonth = today.getMonth() + 1;

            // 1. Define o último trimestre EFETIVAMENTE FECHADO do ano corrente
            let maxClosedQuarter = 0;
            if (currentMonth > 3) maxClosedQuarter = 1; // Abr(4) em diante -> T1 fechado
            if (currentMonth > 6) maxClosedQuarter = 2; // Jul(7) em diante -> T2 fechado
            if (currentMonth > 9) maxClosedQuarter = 3; // Out(10) em diante -> T3 fechado
            // Se estamos em Jan/Fev/Mar, maxClosedQuarter = 0 (Nenhum do ano atual é exibido)

            // 2. Configura o loop para gerar as colunas (Retroativo)
            let loopYear = displayYear;
            let loopQuarter = 4; // Começa sempre do Q4 do ano selecionado e vai voltando

            // Gera até 8 trimestres para trás
            for (let i = 0; i < 8; i++) {
                
                // Regras de Bloqueio:
                const isFutureYear = loopYear > currentRealYear;
                const isCurrentYearFutureQuarter = (loopYear === currentRealYear && loopQuarter > maxClosedQuarter);

                // Só adiciona na lista se NÃO for futuro e NÃO for trimestre aberto do ano atual
                if (!isFutureYear && !isCurrentYearFutureQuarter) {
                    quarters.push({ 
                        id: `${loopYear}-q${loopQuarter}`, 
                        label: `${loopYear} (${loopQuarter}ºT)`, 
                        year: String(loopYear), 
                        qKey: `q${loopQuarter}` 
                    });
                }

                // Decrementa para o próximo passo do loop (volta no tempo)
                loopQuarter--;
                if (loopQuarter < 1) {
                    loopQuarter = 4;
                    loopYear--;
                }
            }
            return quarters;
        }, [displayYear]);

        const rows = useMemo(() => {
            if (!currentUser) return [];

            const myEmail = (currentUser.email || '').trim().toLowerCase();

            const relevantCollabs = data.collaborators.filter(c => {
                if (showSelfOnly) {
                    const cEmail = (c.email || '').trim().toLowerCase();
                    return cEmail === myEmail;
                }
                if (analiseFilters.dept && c.departmentId !== analiseFilters.dept) return false;
                if (analiseFilters.personType !== 'Todos' && c.personType !== analiseFilters.personType) return false;
                if (c.personType === 'Gestão' && currentUser.role !== 'SuperAdm' && !currentUser.permissions?.viewGestao) return false;
                if (c.email === currentUser.email && currentUser.role !== 'SuperAdm') return false; 
                if (currentUser.role === 'SuperAdm') return true;
                return c.departmentId === currentUser.departmentId;
            });

            const mappedRows = relevantCollabs.map(collab => {
                const rowData = { 
                    id: collab.id, name: collab.name, role: collab.role, 
                    dept: data.departments.find(d => d.id === collab.departmentId)?.name, 
                    quarters: {}, hasEvalInPeriod: false 
                };
                
                let prevQuali = null; let prevQuanti = null;
                
                // Dentro de QuarterlyEvolutionTable -> useMemo (rows) -> timeline.reverse().forEach...

                [...timeline].reverse().forEach(t => {
                const ev = data.evaluations.find(e => e.collaboratorId === collab.id && e.periodYear === t.year);
                let qualiScore = null; let quantiScore = null; let feedbackData = null;
                let isSignedForQuarter = false; 

                if (ev) {
                    // REGRA BLINDADA: Só mostra se estiver Arquivada (Financeiro) OU se tiver Assinatura válida no banco.
                    // O status 'Finalizada' ou 'T4-Concluido' sozinho NÃO libera mais a visualização.
                    const isArchived = ev.status === 'Concluída Financeiro';
                    const signatures = ev.managerSignatures || [];
                    const qTag = t.qKey.replace('q', 'T'); 
                    
                    const hasSpecificSig = signatures.some(s => {
                        const ref = (s.statusRef || '').toUpperCase();
                        // Procura por T1, T2, T3, T4 ou tags globais
                        return ref.includes(qTag) || ref.includes('FINALIZADA') || ref.includes('ASSINADA');
                    });

                    if (isArchived || hasSpecificSig) {
                        isSignedForQuarter = true;
                    }
                }

        // Só carrega os dados SE estiver assinado para aquele trimestre
        if (ev && isSignedForQuarter) {
            rowData.hasEvalInPeriod = true; 
            
            if (ev.feedbacksHistory) {
                const historyItems = Object.values(ev.feedbacksHistory)
                    .filter(f => f.quarter === t.qKey && (f.type === 'AvDes' || f.type === 'AvPDF')) 
                    .sort((a,b) => b.timestamp - a.timestamp);
                if (historyItems.length > 0) feedbackData = historyItems[0];
            }

            if (ev.qualitative?.criteria) {
                let sum = 0, count = 0;
                ev.qualitative.criteria.forEach(c => { if (c[t.qKey]) { sum += parseFloat(c[t.qKey]); count++; } });
                if (count > 0) qualiScore = (sum / count) * 2.5; 
            }
            if (collab.personType === 'Colaborador' && ev.quantitative?.errors) {
                let sumPts = 0;
                ev.quantitative.errors.forEach(err => { sumPts += (parseInt(err[t.qKey] || 0) * err.peso); });
                quantiScore = sumPts;
            }
        }
        
        // ... restante do código de tendência ...
                    
                    let trendQuali = 'neutral', trendQuanti = 'neutral';
                    if (prevQuali !== null && qualiScore !== null) { trendQuali = qualiScore > prevQuali ? 'up' : qualiScore < prevQuali ? 'down' : 'stable'; }
                    if (prevQuanti !== null && quantiScore !== null) { trendQuanti = quantiScore < prevQuanti ? 'up' : quantiScore > prevQuanti ? 'down' : 'stable'; }
                    
                    rowData.quarters[t.id] = { 
                        quali: qualiScore, quanti: quantiScore, trendQuali, trendQuanti, feedback: feedbackData, 
                        hasData: (qualiScore !== null || quantiScore !== null),
                        isGestao: collab.personType === 'Gestão'
                    };
                    
                    if (qualiScore !== null) prevQuali = qualiScore; 
                    if (quantiScore !== null) prevQuanti = quantiScore;
                });
                return rowData;
            });

            if (showSelfOnly) return mappedRows;
            return mappedRows.filter(r => r.hasEvalInPeriod);

        }, [data, timeline, currentUser, analiseFilters, showSelfOnly]);

        const chartData = useMemo(() => {
            if (!showSelfOnly || !rows || rows.length === 0) return [];

            const row = rows[0];

            return [...timeline].reverse().map((t) => {
                const cell = row.quarters?.[t.id];

                return {
                id: t.id,
                quarter: t.label,
                qualitativa: cell?.quali !== null && cell?.quali !== undefined
                    ? Number(cell.quali.toFixed(4))
                    : null,
                quantitativa: cell?.quanti !== null && cell?.quanti !== undefined
                    ? Number(cell.quanti.toFixed(4))
                    : null,
                hasData: !!cell?.hasData,
                feedback: cell?.feedback || null
                };
            });
            }, [rows, timeline, showSelfOnly]);

            // Média anual atualizada: considera somente trimestres do ano de referência
            // que já passaram pela regra de assinatura/liberação acima.
            const annualSummary = useMemo(() => {
                if (!showSelfOnly || !rows?.length) return { qualitative: 0, quantitative: 0, hasQualitative: false, hasQuantitative: false };
                const row = rows[0];
                const yearPrefix = `${displayYear}-`;
                const yearCells = Object.entries(row?.quarters || {})
                    .filter(([id, cell]) => id.startsWith(yearPrefix) && cell?.hasData)
                    .map(([, cell]) => cell);

                const qualiValues = yearCells
                    .map(cell => cell?.quali)
                    .filter(v => v !== null && v !== undefined && !Number.isNaN(Number(v)))
                    .map(Number);
                const quantiValues = yearCells
                    .map(cell => cell?.quanti)
                    .filter(v => v !== null && v !== undefined && !Number.isNaN(Number(v)))
                    .map(Number);

                return {
                    qualitative: qualiValues.length ? qualiValues.reduce((a, b) => a + b, 0) / qualiValues.length : 0,
                    quantitative: quantiValues.length ? quantiValues.reduce((a, b) => a + b, 0) / quantiValues.length : 0,
                    hasQualitative: qualiValues.length > 0,
                    hasQuantitative: quantiValues.length > 0
                };
            }, [rows, displayYear, showSelfOnly]);

            const hasQualiChartData = chartData.some(d => d.qualitativa !== null);
            const hasQuantiChartData = chartData.some(d => d.quantitativa !== null);
            const hasChartData = hasQualiChartData || hasQuantiChartData;

            const formatChartLabel = (value, decimals = 2) => {
            if (value === null || value === undefined || Number.isNaN(Number(value))) return '';
            return Number(value).toFixed(decimals);
            };
        const getQuarterCell = (row, t) => {
    const cell = row?.quarters?.[t.id] || {};

    const hasData =
        cell.hasData === true ||
        cell.quali !== null && cell.quali !== undefined ||
        cell.quanti !== null && cell.quanti !== undefined;

    return {
        quali: cell.quali ?? null,
        quanti: cell.quanti ?? null,
        trendQuali: cell.trendQuali || null,
        trendQuanti: cell.trendQuanti || null,
        feedback: cell.feedback || null,
        hasData
    };
};

const renderChartBadgeLabel = ({
    x,
    y,
    value,
    decimals = 2,
    strokeColor = '#2563eb',
    fillColor = '#ffffff',
    textColor = '#1d4ed8',
    placement = 'top'
}) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return null;

    const label = Number(value).toFixed(decimals);
    const charWidth = 7;
    const paddingX = 8;
    const badgeHeight = 20;
    const badgeWidth = Math.max(34, label.length * charWidth + paddingX * 2);

    // posição vertical do badge
    let badgeY = y - 30;

    if (placement === 'bottom') {
        badgeY = y + 12;
    }

    if (placement === 'auto-bottom') {
        // se estiver muito perto da base, sobe o label
        badgeY = Number(value) <= 0.3 ? y - 30 : y + 12;
    }

    const badgeX = x - badgeWidth / 2;

    return (
        <g>
            <rect
                x={badgeX}
                y={badgeY}
                rx={6}
                ry={6}
                width={badgeWidth}
                height={badgeHeight}
                fill={fillColor}
                stroke={strokeColor}
                strokeWidth={1.5}
                opacity={0.98}
            />
            <text
                x={x}
                y={badgeY + badgeHeight / 2 + 4}
                textAnchor="middle"
                fontSize="11"
                fontWeight="700"
                fill={textColor}
            >
                {label}
            </text>
        </g>
    );
};

const renderQualiLabel = (props) =>
    renderChartBadgeLabel({
        ...props,
        decimals: 4,
        strokeColor: '#2563eb',
        fillColor: isDarkMode ? '#0f172a' : '#ffffff',
        textColor: isDarkMode ? '#bfdbfe' : '#1d4ed8',
        placement: 'top'
    });

const renderQuantiLabel = (props) =>
    renderChartBadgeLabel({
        ...props,
        decimals: 4,
        strokeColor: '#dc2626',
        fillColor: isDarkMode ? '#0f172a' : '#ffffff',
        textColor: isDarkMode ? '#fecaca' : '#b91c1c',
        placement: 'auto-bottom'
    });
        return (
            <div className="flex flex-col h-full min-h-0 relative">
                <div className="flex justify-between items-center mb-4 px-1 shrink-0">
                    <div className="flex items-center gap-4">
                        <div><h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}><TrendingUp size={20} className="text-blue-600"/> Evolução Trimestral</h3>
                            <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Histórico (8 Trimestres). Ref: {displayYear}</p></div>
                            {!hideInternalFilter && (
                                <div className={`flex items-center gap-2 px-2 py-1 rounded border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'}`}>
                                    <span className={`text-xs font-bold ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Ano Ref:</span>
                                    <input type="number" className={`w-16 text-center text-xs font-bold bg-transparent outline-none ${isDarkMode ? 'text-white' : 'text-gray-800'}`} placeholder={new Date().getFullYear().toString()} value={internalYear} onChange={(e) => setInternalYear(e.target.value)}/>
                                </div>
                            )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        {showSelfOnly && (
                            <div className="flex items-center gap-2 mr-8">
                                <div className={`min-w-[105px] px-3 py-2 rounded-lg border shadow-sm text-center ${isDarkMode ? 'bg-blue-950/40 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
                                    <div className={`text-[9px] uppercase font-black tracking-wide ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>Qualitativa</div>
                                    <div className={`text-base font-black leading-tight ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                                        {annualSummary.hasQualitative ? annualSummary.qualitative.toFixed(4) : '0.0000'}
                                    </div>
                                </div>
                                <div className={`min-w-[105px] px-3 py-2 rounded-lg border shadow-sm text-center ${isDarkMode ? 'bg-red-950/30 border-red-700' : 'bg-red-50 border-red-200'}`}>
                                    <div className={`text-[9px] uppercase font-black tracking-wide ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>Quantitativa</div>
                                    <div className={`text-base font-black leading-tight ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                                        {annualSummary.hasQuantitative ? annualSummary.quantitative.toFixed(4) : '0.0000'}
                                    </div>
                                </div>
                            </div>
                        )}
                        {showSelfOnly && (
                            <div className={`flex p-1 rounded-lg border shadow-sm ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
                            <button
                                type="button"
                                onClick={() => setViewMode('table')}
                                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
                                viewMode === 'table'
                                    ? 'bg-blue-600 text-white'
                                    : isDarkMode
                                    ? 'text-slate-400 hover:bg-slate-800'
                                    : 'text-gray-500 hover:bg-gray-100'
                                }`}
                            >
                                Tabela
                            </button>

                            <button
                                type="button"
                                onClick={() => setViewMode('chart')}
                                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
                                viewMode === 'chart'
                                    ? 'bg-blue-600 text-white'
                                    : isDarkMode
                                    ? 'text-slate-400 hover:bg-slate-800'
                                    : 'text-gray-500 hover:bg-gray-100'
                                }`}
                            >
                                Gráfico
                            </button>
                            </div>
                        )}

                        <div className={`flex gap-4 text-xs font-medium px-3 py-2 rounded-lg border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-white border-gray-200'}`}>
                            <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded bg-blue-600 shadow-sm"></div> Qualitativa
                            </div>
                            <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded bg-red-600 shadow-sm"></div> Quantitativa (Pts)
                            </div>
                        </div>
                        </div>
                </div>
                <div className={`rounded-xl shadow-sm border overflow-hidden flex-1 flex flex-col ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                    {viewMode === 'chart' && showSelfOnly ? (
                        <div className="p-4 h-[360px] min-h-[320px]">
                        {hasChartData ? (
                            <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={chartData}
                                margin={{ top: 40, right: 40, left: 10, bottom: 28 }}
                            >
                                <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke={isDarkMode ? '#334155' : '#e5e7eb'}
                                />

                                <XAxis
                                dataKey="quarter"
                                tick={{ fontSize: 11, fill: isDarkMode ? '#cbd5e1' : '#475569' }}
                                axisLine={{ stroke: isDarkMode ? '#475569' : '#cbd5e1' }}
                                tickLine={false}
                                />

                                {hasQualiChartData && (
                                <YAxis
                                    yAxisId="left"
                                    domain={[0, 10]}
                                    tick={{ fontSize: 11, fill: isDarkMode ? '#93c5fd' : '#2563eb' }}
                                    axisLine={{ stroke: isDarkMode ? '#475569' : '#cbd5e1' }}
                                    tickLine={false}
                                />
                                )}

                                {hasQuantiChartData && (
                                <YAxis
                                    yAxisId="right"
                                    orientation="right"
                                    allowDecimals={false}
                                    tick={{ fontSize: 11, fill: isDarkMode ? '#fca5a5' : '#dc2626' }}
                                    axisLine={{ stroke: isDarkMode ? '#475569' : '#cbd5e1' }}
                                    tickLine={false}
                                />
                                )}

                                <RechartsTooltip
                                contentStyle={{
                                    backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
                                    borderColor: isDarkMode ? '#334155' : '#e5e7eb',
                                    borderRadius: 12,
                                    fontSize: 12,
                                    color: isDarkMode ? '#e2e8f0' : '#334155'
                                }}
                                labelStyle={{
                                    color: isDarkMode ? '#e2e8f0' : '#334155',
                                    fontWeight: 700
                                }}
                                formatter={(value, name) => {
                                    if (name === 'Qualitativa') return [formatChartLabel(value, 4), 'Qualitativa'];
                                    if (name === 'Quantitativa (Pts)') return [formatChartLabel(value, 4), 'Quantitativa (Pts)'];
                                    return [value, name];
                                }}
                                />

                                <Legend wrapperStyle={{ fontSize: 12 }} />

                                {hasQualiChartData && (
                                <Line
                                    yAxisId="left"
                                    type="monotone"
                                    dataKey="qualitativa"
                                    name="Qualitativa"
                                    stroke="#2563eb"
                                    strokeWidth={3}
                                    dot={{ r: 6, strokeWidth: 3, fill: '#ffffff', stroke: '#2563eb' }}
                                    activeDot={{ r: 8, strokeWidth: 3, fill: '#ffffff', stroke: '#2563eb' }}
                                    connectNulls={false}
                                >
                                    <LabelList
                                        dataKey="qualitativa"
                                        content={renderQualiLabel}
                                    />
                                </Line>
                                )}

                                {hasQuantiChartData && (
                                <Line
                                    yAxisId="right"
                                    type="monotone"
                                    dataKey="quantitativa"
                                    name="Quantitativa (Pts)"
                                    stroke="#dc2626"
                                    strokeWidth={3}
                                    dot={{ r: 6, strokeWidth: 3, fill: '#ffffff', stroke: '#dc2626' }}
                                    activeDot={{ r: 8, strokeWidth: 3, fill: '#ffffff', stroke: '#dc2626' }}
                                    connectNulls={false}
                                >
                                    <LabelList
                                        dataKey="quantitativa"
                                        content={renderQuantiLabel}
                                    />
                                </Line>
                                )}
                            </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className={`h-full flex flex-col items-center justify-center text-center p-8 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                            <TrendingUp size={42} className="mb-3 opacity-40" />
                            <p className="text-sm font-bold">Nenhuma nota liberada para gráfico.</p>
                            <p className="text-xs mt-1">As notas aparecerão após assinatura/liberação do trimestre.</p>
                            </div>
                        )}
                        </div>
                                        ) : (
                        <div className="overflow-auto custom-scrollbar flex-1">
                            <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : ''}`}>
                                <thead className={`text-xs uppercase tracking-wider border-b z-10 sticky top-0 ${
                                    isDarkMode
                                        ? 'bg-slate-900 text-slate-400 border-slate-700'
                                        : 'bg-gray-50 text-gray-600 font-bold border-gray-200'
                                }`}>
                                    <tr>
                                        <th className={`p-4 w-64 sticky left-0 z-20 border-r shadow-sm ${
                                            isDarkMode
                                                ? 'bg-slate-900 border-slate-700'
                                                : 'bg-gray-50 border-gray-200'
                                        }`}>
                                            Colaborador
                                        </th>

                                        {timeline.map(t => (
                                            <th
                                                key={t.id}
                                                className={`p-1.5 text-center min-w-[85px] text-[10px] border-r last:border-0 ${
                                                    isDarkMode
                                                        ? 'bg-slate-900 border-slate-700 text-slate-300'
                                                        : 'bg-gray-50 border-gray-100 text-gray-800'
                                                }`}
                                            >
                                                {t.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                    {rows.length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan={timeline.length + 1}
                                                className={`p-8 text-center text-sm ${
                                                    isDarkMode ? 'text-slate-500' : 'text-gray-400'
                                                }`}
                                            >
                                                Nenhum dado encontrado para o período selecionado.
                                            </td>
                                        </tr>
                                    ) : (
                                        rows.map((row, idx) => (
                                            <tr
                                                key={row.id}
                                                className={`transition-colors ${
                                                    isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-blue-50/30'
                                                } ${
                                                    idx % 2 === 0
                                                        ? (isDarkMode ? 'bg-slate-800' : 'bg-white')
                                                        : (isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50/40')
                                                }`}
                                            >
                                                <td className={`p-3 border-r bg-transparent sticky left-0 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] ${
                                                    isDarkMode
                                                        ? 'bg-inherit border-slate-700'
                                                        : 'bg-inherit border-gray-200'
                                                }`}>
                                                    <div
                                                        className={`font-bold truncate w-56 ${
                                                            isDarkMode ? 'text-slate-200' : 'text-gray-800'
                                                        }`}
                                                        title={row.name}
                                                    >
                                                        {row.name}
                                                    </div>

                                                    <div className={`text-[10px] truncate font-medium ${
                                                        isDarkMode ? 'text-slate-500' : 'text-gray-500'
                                                    }`}>
                                                        {row.role} • {row.dept}
                                                    </div>
                                                </td>

                                                {timeline.map(t => {
                                                    const cell = getQuarterCell(row, t);

                                                    if (!cell.hasData) {
                                                        return (
                                                            <td
                                                                key={t.id}
                                                                className={`p-3 text-center border-r ${
                                                                    isDarkMode
                                                                        ? 'border-slate-700 text-slate-600'
                                                                        : 'border-gray-100 text-gray-300'
                                                                }`}
                                                            >
                                                                -
                                                            </td>
                                                        );
                                                    }

                                                    return (
                                                        <td
                                                            key={t.id}
                                                            className={`p-2 border-r align-top ${
                                                                isDarkMode ? 'border-slate-700' : 'border-gray-100'
                                                            }`}
                                                        >
                                                            <div className="flex flex-col gap-2">
                                                                {cell.quali !== null && cell.quali !== undefined && (
                                                                    <div
                                                                        className={`flex justify-between items-center px-2 py-1.5 rounded border cursor-help relative group shadow-sm transition-colors ${
                                                                            isDarkMode
                                                                                ? 'bg-blue-500/10 border-blue-500/20 text-blue-300'
                                                                                : 'bg-blue-50 border-blue-200 text-blue-700'
                                                                        }`}
                                                                        onMouseEnter={(e) => {
                                                                            if (cell.feedback) {
                                                                                const rect = e.currentTarget.getBoundingClientRect();
                                                                                setTooltip({
                                                                                    data: cell.feedback,
                                                                                    x: rect.left + rect.width / 2,
                                                                                    y: rect.top
                                                                                });
                                                                            }
                                                                        }}
                                                                        onMouseLeave={() => setTooltip(null)}
                                                                    >
                                                                        <span className="font-bold text-sm">
                                                                            {Number(cell.quali).toFixed(4)}
                                                                        </span>

                                                                        {cell.trendQuali === 'up' && (
                                                                            <TrendingUp size={14} className="text-emerald-500" />
                                                                        )}

                                                                        {cell.trendQuali === 'down' && (
                                                                            <TrendingDown size={14} className="text-red-500" />
                                                                        )}

                                                                        {cell.trendQuali === 'stable' && (
                                                                            <Minus size={14} className="text-gray-400" />
                                                                        )}

                                                                        {cell.feedback && (
                                                                            <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border-2 border-white"></div>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {cell.quanti !== null && cell.quanti !== undefined && (
                                                                    <div className={`flex justify-between items-center px-2 py-1.5 rounded border shadow-sm ${
                                                                        isDarkMode
                                                                            ? 'bg-red-500/10 border-red-500/20 text-red-300'
                                                                            : 'bg-red-50 border-red-200 text-red-700'
                                                                    }`}>
                                                                        <span className="font-bold text-sm">
                                                                            {Number(cell.quanti).toFixed(4)}
                                                                        </span>

                                                                        {cell.trendQuanti === 'up' && (
                                                                            <TrendingUp size={14} className="text-emerald-500" />
                                                                        )}

                                                                        {cell.trendQuanti === 'down' && (
                                                                            <TrendingDown size={14} className="text-red-500" />
                                                                        )}

                                                                        {cell.trendQuanti === 'stable' && (
                                                                            <Minus size={14} className="text-gray-400" />
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    </div>
                {tooltip && <div className={`fixed z-[9999] w-72 p-0 rounded-lg shadow-2xl pointer-events-none animate-fade-in border overflow-hidden ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`} style={{ top: tooltip.y - 10, left: tooltip.x, transform: 'translate(-50%, -100%)' }}><div className={`p-2 border-b flex justify-between items-center ${isDarkMode ? 'bg-slate-950 border-slate-800' : 'bg-gray-50 border-gray-100'}`}><span className={`text-[10px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{tooltip.data.type || 'Feedback'}</span><span className={`text-[9px] ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>{new Date(tooltip.data.timestamp).toLocaleDateString()}</span></div><div className={`p-3 text-xs italic leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>"{tooltip.data.text}"</div></div>}
            </div>
        );
    };


        /**
         * ============================================================================
         * COMPONENTE: RASTREAMENTO DE FEEDBACKS (PERSISTÊNCIA + FILTROS INTELIGENTES)
         * ============================================================================
         */
        const FeedbackTrackingTab = ({ data, actions, currentUser, isDarkMode }) => {
            
        // 1. CARREGAR PREFERÊNCIAS OU DEFINIR PADRÕES (PRIMEIRO ACESSO: ANO EM BRANCO)
        const savedPrefs = currentUser?.preferences?.feedbackTracking || {};
        
        const [filters, setFilters] = useState({
            dept: savedPrefs.dept || '',
            classification: savedPrefs.classification || '', 
            direction: savedPrefs.direction || '',      
            status: savedPrefs.status || 'Pendentes', 
            periodMode: savedPrefs.periodMode || 'ano',
            // Se houver preferência salva, usa ela. Se não (primeiro acesso), usa string vazia (Todos)
            year: savedPrefs.year !== undefined ? savedPrefs.year : '', 
            startDate: savedPrefs.startDate || '',
            endDate: savedPrefs.endDate || ''
        });

        const [confirmModal, setConfirmModal] = useState({ 
        isOpen: false, 
        title: '', 
        message: '', 
        onConfirm: null 
        });
        
        const [searchTerm, setSearchTerm] = useState('');

        // --- EFEITO: SALVAR PREFERÊNCIAS (AUTO-SAVE) ---
        useEffect(() => {
            const timer = setTimeout(() => {
                // Só salva se houver alterações reais e usuário logado
                if (currentUser && actions.saveUserPreferences) {
                    actions.saveUserPreferences({ feedbackTracking: filters });
                }
            }, 1000); // Debounce de 1 segundo
            return () => clearTimeout(timer);
        }, [filters, currentUser, actions]);

        // --- ESTADOS DE MODAIS ---
        const [editDateModal, setEditDateModal] = useState({ isOpen: false, feedbackId: null, evalId: null, currentDate: '' });
        const [obsModal, setObsModal] = useState({ isOpen: false, feedbackId: null, evalId: null, text: '' });
        const [detailModal, setDetailModal] = useState(null); 
        const [continuityModal, setContinuityModal] = useState({ isOpen: false, source: null, targetKey: '' });
        const [isSavingContinuity, setIsSavingContinuity] = useState(false);
        const getShortName = (fullName) => {
            const clean = String(fullName || '').trim();

            if (!clean) return '-';

            const parts = clean.split(/\s+/).filter(Boolean);

            if (parts.length === 1) return parts[0];

            return `${parts[0]} ${parts[1]}`;
        };

        const getQuarterRefLabel = (quarter, year) => {
            const q = String(quarter || '').toLowerCase();
            const y = String(year || '').trim();

            const labels = {
                q1: `1ºTrim/${y}`,
                q2: `2ºTrim/${y}`,
                q3: `3ºTrim/${y}`,
                q4: `4ºTrim/${y}`
            };

            return labels[q] || '-';
        };


        const getCompleteContinuityHistory = (feedbackItem) => {
            if (!feedbackItem) return [];

            const inherited = Array.isArray(feedbackItem.inheritedHistory)
                ? feedbackItem.inheritedHistory
                : [];

            const normalizedInherited = inherited.map((entry, index) => {
                const snapshot = entry?.snapshot && typeof entry.snapshot === 'object'
                    ? entry.snapshot
                    : entry;

                return {
                    ...snapshot,
                    evalId: entry?.evalId || snapshot?.evalId || '',
                    feedbackId: entry?.feedbackId || snapshot?.feedbackId || snapshot?.id || `historico-${index}`,
                    periodYear: entry?.periodYear || snapshot?.periodYear || '',
                    quarter: entry?.quarter || snapshot?.quarter || '',
                    quarterDisplay: entry?.quarterDisplay || getQuarterRefLabel(entry?.quarter || snapshot?.quarter, entry?.periodYear || snapshot?.periodYear),
                    isInherited: true
                };
            });

            return normalizedInherited.sort((a, b) => Number(a.timestamp || 0) - Number(b.timestamp || 0));
        };

        // Une históricos de várias origens sem perder registros já vinculados ao destino.
        // A chave composta avaliação + feedback permite que um mesmo feedback ativo
        // concentre diversos acompanhamentos anteriores, inclusive de anos diferentes.
        const mergeContinuityHistories = (...historyLists) => {
            const merged = new Map();

            historyLists.flat().filter(Boolean).forEach((entry, index) => {
                const snapshot = entry?.snapshot && typeof entry.snapshot === 'object'
                    ? entry.snapshot
                    : entry;
                const evalId = String(entry?.evalId || snapshot?.evalId || '');
                const feedbackId = String(entry?.feedbackId || snapshot?.feedbackId || snapshot?.id || `historico-${index}`);
                const key = `${evalId}::${feedbackId}`;

                const normalized = {
                    ...entry,
                    evalId,
                    feedbackId,
                    periodYear: String(entry?.periodYear || snapshot?.periodYear || ''),
                    quarter: entry?.quarter || snapshot?.quarter || '',
                    quarterDisplay: entry?.quarterDisplay || getQuarterRefLabel(entry?.quarter || snapshot?.quarter, entry?.periodYear || snapshot?.periodYear),
                    snapshot: JSON.parse(JSON.stringify(snapshot || {}))
                };

                // Em caso de repetição, mantém a versão mais completa/recente.
                merged.set(key, { ...(merged.get(key) || {}), ...normalized });
            });

            return Array.from(merged.values()).sort((a, b) => {
                const aTime = Number(a?.snapshot?.timestamp || a?.timestamp || 0);
                const bTime = Number(b?.snapshot?.timestamp || b?.timestamp || 0);
                return aTime - bTime;
            });
        };

        // --- 1. PROCESSAMENTO DOS DADOS ---
        const processedFeedbacks = React.useMemo(() => {
            const result = [];
            const isSuperAdm = currentUser.role === 'SuperAdm';

            data.evaluations.forEach(ev => {
                const collab = data.collaborators.find(c => c.id === ev.collaboratorId);
                if (!collab) return;

                // --- REGRAS DE ACESSO ---
                let canView = false;
                if (isSuperAdm) canView = true;
                else if (collab.departmentId === currentUser.departmentId) {
                    if (collab.personType === 'Gestão' && !currentUser.permissions?.viewGestao) canView = false;
                    else canView = true;
                }
                
                if (!canView) return;

                // --- ITERA SOBRE OS FEEDBACKS ---
                if (ev.feedbacksHistory) {
                    Object.values(ev.feedbacksHistory).forEach(fb => {
                        result.push({
                                ...fb,
                                evalId: ev.id,
                                collabName: ev.collaboratorName,
                                collabShortName: getShortName(ev.collaboratorName),
                                collabId: ev.collaboratorId,
                                managerName: ev.managerName || 'N/D',
                                department: ev.department,
                                periodYear: ev.periodYear,
                                isConcluded: fb.status === 'Concluído' || fb.status === 'Encaminhado',
                                isForwarded: fb.status === 'Encaminhado',
                                quarterDisplay: getQuarterRefLabel(fb.quarter, ev.periodYear)
                            });
                    });
                }
            });

            // --- FILTRAGEM ---
            return result.filter(item => {
                // 1. Filtro Texto
                if (searchTerm) {
                    const s = searchTerm.toLowerCase();
                    const textMatch = 
                    String(item.collabName || '').toLowerCase().includes(s) ||
                    String(item.collabShortName || '').toLowerCase().includes(s) ||
                    String(item.text || '').toLowerCase().includes(s) ||
                    String(item.department || '').toLowerCase().includes(s);
                    if (!textMatch) return false;
                }

                // 2. Filtros Específicos
                if (filters.dept && item.department !== filters.dept) return false;
                if (filters.classification && item.nature !== filters.classification) return false;
                if (filters.direction && item.actionTag !== filters.direction) return false;

                // 3. Filtro de Status
                if (filters.status === 'Pendentes') {
                    if (item.isConcluded) return false;
                } else if (filters.status === 'Concluídos') {
                    if (!item.isConcluded) return false;
                }

                // 4. Filtro de Período (LÓGICA AJUSTADA)
                const itemDate = new Date(item.timestamp);
                
                if (filters.periodMode === 'ano') {
                    // Se o ano estiver em branco, MOSTRA TUDO (ignora filtro de data)
                    if (filters.year && filters.year.trim() !== '') {
                        if (itemDate.getFullYear().toString() !== filters.year) return false;
                    }
                } else if (filters.periodMode === 'custom') {
                    if (filters.startDate && itemDate < new Date(filters.startDate)) return false;
                    if (filters.endDate && itemDate > new Date(filters.endDate + 'T23:59:59')) return false;
                }

                return true;
            }).sort((a,b) => {
                if (a.followUpDate && b.followUpDate) return new Date(a.followUpDate) - new Date(b.followUpDate);
                return b.timestamp - a.timestamp;
            });

        }, [data.evaluations, data.collaborators, currentUser, filters, searchTerm]);


    const cleanFeedbackForSave = (item) => {
            const clean = { ...item };
            ['evalId','collabName','collabShortName','collabId','quarterDisplay','isConcluded','isForwarded','managerName','department','periodYear'].forEach(k => delete clean[k]);
            return clean;
        };

        const getContinuityCandidates = (source) => {
            if (!source) return [];
            const sourceDate = new Date(source.timestamp || 0).getTime();
            const candidates = [];
            data.evaluations.forEach(ev => {
                if (ev.collaboratorId !== source.collabId || !ev.feedbacksHistory) return;
                Object.values(ev.feedbacksHistory).forEach(fb => {
                    if (fb.id === source.id && ev.id === source.evalId) return;
                    const fbDate = new Date(fb.timestamp || 0).getTime();
                    if (fbDate < sourceDate) return;
                    if ((fb.nature || '') !== (source.nature || '')) return;
                    if (fb.status === 'Concluído' || fb.status === 'Encaminhado') return;
                    candidates.push({ ...fb, evalId: ev.id, periodYear: ev.periodYear, quarterDisplay: getQuarterRefLabel(fb.quarter, ev.periodYear) });
                });
            });
            return candidates.sort((a,b) => new Date(a.timestamp) - new Date(b.timestamp));
        };

        const concludeDefinitely = async (fbItem) => {
            const targetEval = data.evaluations.find(e => e.id === fbItem.evalId);
            if (!targetEval) return;
            const updatedHistory = { ...targetEval.feedbacksHistory };
            updatedHistory[fbItem.id] = cleanFeedbackForSave({ ...fbItem, status: 'Concluído', concludedAt: new Date().toISOString(), conclusionType: 'resolved' });
            await actions.saveEvaluation({ ...targetEval, feedbacksHistory: updatedHistory });
        };

        const linkToNextFeedback = async () => {
            if (isSavingContinuity) return;

            const source = continuityModal.source;
            const [targetEvalId, targetFeedbackId] = String(continuityModal.targetKey || '').split('::');
            if (!source || !targetEvalId || !targetFeedbackId) {
                alert('Selecione o feedback seguinte.');
                return;
            }

            const sourceEval = data.evaluations.find(e => String(e.id) === String(source.evalId));
            const targetEval = data.evaluations.find(e => String(e.id) === String(targetEvalId));
            if (!sourceEval || !targetEval) {
                alert('Não foi possível localizar uma das avaliações. Atualize a tela e tente novamente.');
                return;
            }

            const sourceFeedback = sourceEval.feedbacksHistory?.[source.id];
            const targetFeedback = targetEval.feedbacksHistory?.[targetFeedbackId];
            if (!sourceFeedback || !targetFeedback) {
                alert('O feedback de origem ou destino não está mais disponível. Atualize a tela e tente novamente.');
                return;
            }

            setIsSavingContinuity(true);
            try {
                const now = new Date().toISOString();
                const chainId = sourceFeedback.continuity?.chainId || targetFeedback.continuity?.chainId || `fb-chain-${Date.now()}`;

                const targetQuarterDisplay = getQuarterRefLabel(targetFeedback.quarter, targetEval.periodYear);
                const linkageMessage = `Encerrado por vínculo e encaminhado para acompanhamento no feedback ${targetQuarterDisplay}.`;

                const sourceSaved = cleanFeedbackForSave({
                    ...sourceFeedback,
                    status: 'Encaminhado',
                    concludedAt: now,
                    conclusionType: 'continued',
                    conclusionNote: linkageMessage,
                    linkedTo: {
                        evalId: targetEval.id,
                        feedbackId: targetFeedback.id,
                        periodYear: String(targetEval.periodYear || ''),
                        quarter: targetFeedback.quarter || '',
                        quarterDisplay: targetQuarterDisplay,
                        text: targetFeedback.text || '',
                        linkedAt: now
                    },
                    continuity: {
                        ...(sourceFeedback.continuity || {}),
                        chainId,
                        nextEvalId: targetEval.id,
                        nextFeedbackId: targetFeedback.id,
                        nextPeriodYear: String(targetEval.periodYear || ''),
                        nextQuarter: targetFeedback.quarter || '',
                        nextQuarterDisplay: targetQuarterDisplay,
                        linkedAt: now
                    }
                });

                // Leva para o próximo registro uma fotografia COMPLETA do feedback anterior.
                // O snapshot preserva texto, classificação, direcionamento, datas, autores,
                // observações/comentários e quaisquer campos futuros do feedback original.
                const sourceSnapshot = JSON.parse(JSON.stringify(cleanFeedbackForSave(sourceFeedback)));
                const sourceHistoryEntry = {
                    evalId: sourceEval.id,
                    feedbackId: sourceFeedback.id,
                    periodYear: String(sourceEval.periodYear || ''),
                    quarter: sourceFeedback.quarter || '',
                    quarterDisplay: getQuarterRefLabel(sourceFeedback.quarter, sourceEval.periodYear),
                    snapshot: sourceSnapshot
                };

                // IMPORTANTE: soma o histórico que o destino já possuía, o histórico
                // herdado pela origem e o próprio feedback de origem. Assim, diversos
                // feedbacks podem convergir para um único acompanhamento ativo.
                const inherited = mergeContinuityHistories(
                    Array.isArray(targetFeedback.inheritedHistory) ? targetFeedback.inheritedHistory : [],
                    Array.isArray(sourceFeedback.inheritedHistory) ? sourceFeedback.inheritedHistory : [],
                    [sourceHistoryEntry]
                );

                const existingPreviousLinks = Array.isArray(targetFeedback.continuity?.previousLinks)
                    ? targetFeedback.continuity.previousLinks
                    : [];
                const newPreviousLink = {
                    evalId: sourceEval.id,
                    feedbackId: sourceFeedback.id,
                    periodYear: String(sourceEval.periodYear || ''),
                    quarter: sourceFeedback.quarter || '',
                    quarterDisplay: getQuarterRefLabel(sourceFeedback.quarter, sourceEval.periodYear),
                    linkedAt: now
                };
                const previousLinksMap = new Map(
                    [...existingPreviousLinks, newPreviousLink].map(link => [`${link.evalId}::${link.feedbackId}`, link])
                );

                const targetSaved = cleanFeedbackForSave({
                    ...targetFeedback,
                    inheritedHistory: inherited,
                    continuity: {
                        ...(targetFeedback.continuity || {}),
                        chainId,
                        // Mantidos para compatibilidade com registros antigos.
                        previousEvalId: sourceEval.id,
                        previousFeedbackId: sourceFeedback.id,
                        previousLinks: Array.from(previousLinksMap.values())
                    }
                });

                // Quando origem e destino pertencem à mesma avaliação, é obrigatório
                // salvar os dois feedbacks em uma única gravação. Duas gravações separadas
                // fariam a segunda sobrescrever a primeira com um histórico antigo.
                if (String(sourceEval.id) === String(targetEval.id)) {
                    const combinedHistory = {
                        ...(sourceEval.feedbacksHistory || {}),
                        [sourceFeedback.id]: sourceSaved,
                        [targetFeedback.id]: targetSaved
                    };
                    await actions.saveEvaluation({ ...sourceEval, feedbacksHistory: combinedHistory });
                } else {
                    await Promise.all([
                        actions.saveEvaluation({
                            ...sourceEval,
                            feedbacksHistory: { ...(sourceEval.feedbacksHistory || {}), [sourceFeedback.id]: sourceSaved }
                        }),
                        actions.saveEvaluation({
                            ...targetEval,
                            feedbacksHistory: { ...(targetEval.feedbacksHistory || {}), [targetFeedback.id]: targetSaved }
                        })
                    ]);
                }

                setContinuityModal({ isOpen: false, source: null, targetKey: '' });
            } catch (error) {
                console.error('Erro ao vincular feedbacks:', error);
                alert('Não foi possível concluir o vínculo. Verifique sua conexão e tente novamente.');
            } finally {
                setIsSavingContinuity(false);
            }
        };

        const undoContinuityLink = async (fbItem) => {
            const sourceEval = data.evaluations.find(e => String(e.id) === String(fbItem.evalId));
            const nextEvalId = fbItem.continuity?.nextEvalId || fbItem.linkedTo?.evalId;
            const nextFeedbackId = fbItem.continuity?.nextFeedbackId || fbItem.linkedTo?.feedbackId;
            const targetEval = data.evaluations.find(e => String(e.id) === String(nextEvalId));

            if (!sourceEval || !targetEval || !nextFeedbackId) {
                throw new Error('Não foi possível localizar o feedback de destino do vínculo.');
            }

            // Sempre usa o registro mais atual existente na avaliação, evitando desfazer
            // com a cópia processada/antiga exibida na tabela.
            const sourceFeedback = sourceEval.feedbacksHistory?.[fbItem.id];
            const targetFeedback = targetEval.feedbacksHistory?.[nextFeedbackId];
            if (!sourceFeedback || !targetFeedback) {
                throw new Error('O feedback de origem ou de destino não está mais disponível.');
            }

            const sourceSaved = cleanFeedbackForSave({
                ...sourceFeedback,
                status: 'Pendente',
                concludedAt: null,
                conclusionType: null,
                conclusionNote: null,
                linkedTo: null,
                continuity: {
                    ...(sourceFeedback.continuity || {}),
                    nextEvalId: null,
                    nextFeedbackId: null,
                    nextPeriodYear: null,
                    nextQuarter: null,
                    nextQuarterDisplay: null,
                    linkedAt: null
                }
            });

            const remainingPreviousLinks = (targetFeedback.continuity?.previousLinks || []).filter(link =>
                !(String(link.evalId) === String(sourceEval.id) && String(link.feedbackId) === String(sourceFeedback.id))
            );
            const legacyPointsToSource =
                String(targetFeedback.continuity?.previousEvalId || '') === String(sourceEval.id) &&
                String(targetFeedback.continuity?.previousFeedbackId || '') === String(sourceFeedback.id);
            const lastRemainingLink = remainingPreviousLinks.length > 0
                ? remainingPreviousLinks[remainingPreviousLinks.length - 1]
                : null;

            const targetSaved = cleanFeedbackForSave({
                ...targetFeedback,
                inheritedHistory: (targetFeedback.inheritedHistory || []).filter(h =>
                    !(String(h.evalId) === String(sourceEval.id) && String(h.feedbackId) === String(sourceFeedback.id))
                ),
                continuity: {
                    ...(targetFeedback.continuity || {}),
                    previousEvalId: legacyPointsToSource ? (lastRemainingLink?.evalId || null) : targetFeedback.continuity?.previousEvalId,
                    previousFeedbackId: legacyPointsToSource ? (lastRemainingLink?.feedbackId || null) : targetFeedback.continuity?.previousFeedbackId,
                    previousLinks: remainingPreviousLinks
                }
            });

            // Mesma proteção usada ao criar o vínculo: quando os dois registros estão
            // na mesma avaliação, deve haver UMA ÚNICA gravação. Caso contrário, a
            // segunda gravação repõe o estado antigo e faz parecer que nada aconteceu.
            if (String(sourceEval.id) === String(targetEval.id)) {
                const combinedHistory = {
                    ...(sourceEval.feedbacksHistory || {}),
                    [sourceFeedback.id]: sourceSaved,
                    [targetFeedback.id]: targetSaved
                };
                await actions.saveEvaluation({ ...sourceEval, feedbacksHistory: combinedHistory });
            } else {
                await Promise.all([
                    actions.saveEvaluation({
                        ...sourceEval,
                        feedbacksHistory: { ...(sourceEval.feedbacksHistory || {}), [sourceFeedback.id]: sourceSaved }
                    }),
                    actions.saveEvaluation({
                        ...targetEval,
                        feedbacksHistory: { ...(targetEval.feedbacksHistory || {}), [targetFeedback.id]: targetSaved }
                    })
                ]);
            }
        };

        const handleConclude = (fbItem) => {
            if (fbItem.status === 'Encaminhado') {
                setConfirmModal({
                    isOpen: true,
                    title: 'Desfazer vínculo?',
                    message: 'O feedback voltará para Pendente e somente o histórico deste vínculo será removido do feedback seguinte.',
                    onConfirm: async () => {
                        try {
                            await undoContinuityLink(fbItem);
                            setConfirmModal({ isOpen:false,title:'',message:'',onConfirm:null });
                        } catch (error) {
                            console.error('Erro ao desfazer vínculo:', error);
                            alert(error?.message || 'Não foi possível desfazer o vínculo. Atualize a tela e tente novamente.');
                        }
                    }
                });
                return;
            }
            if (fbItem.status === 'Concluído') {
                setConfirmModal({ isOpen: true, title: 'Reabrir Feedback?', message: "O feedback voltará para o status de 'Pendente'. Deseja continuar?", onConfirm: async () => {
                    const targetEval = data.evaluations.find(e => e.id === fbItem.evalId);
                    const updatedHistory = { ...targetEval.feedbacksHistory, [fbItem.id]: cleanFeedbackForSave({ ...fbItem, status:'Pendente', concludedAt:null, conclusionType:null }) };
                    await actions.saveEvaluation({ ...targetEval, feedbacksHistory: updatedHistory });
                    setConfirmModal({ isOpen:false,title:'',message:'',onConfirm:null });
                }});
                return;
            }
            setContinuityModal({ isOpen: true, source: fbItem, targetKey: '' });
        };

        const handleUpdateDate = async () => {
            if (!editDateModal.currentDate) return alert("Selecione uma data.");
            const targetEval = data.evaluations.find(e => e.id === editDateModal.evalId);
            const fbItem = targetEval.feedbacksHistory[editDateModal.feedbackId];
            const updatedHistory = { ...targetEval.feedbacksHistory };
            updatedHistory[editDateModal.feedbackId] = { ...fbItem, followUpDate: editDateModal.currentDate };
            await actions.saveEvaluation({ ...targetEval, feedbacksHistory: updatedHistory });
            setEditDateModal({ isOpen: false, feedbackId: null, evalId: null, currentDate: '' });
        };

        const handleAddObs = async () => {
            if (!obsModal.text.trim()) return alert("Digite a observação.");
            const targetEval = data.evaluations.find(e => e.id === obsModal.evalId);
            const fbItem = targetEval.feedbacksHistory[obsModal.feedbackId];
            const newObs = { id: Date.now().toString(), text: obsModal.text, date: new Date().toISOString(), author: currentUser.name };
            const updatedHistory = { ...targetEval.feedbacksHistory };
            const currentObsList = fbItem.observations || [];
            updatedHistory[obsModal.feedbackId] = { ...fbItem, observations: [...currentObsList, newObs] };
            await actions.saveEvaluation({ ...targetEval, feedbacksHistory: updatedHistory });
            setObsModal({ isOpen: false, feedbackId: null, evalId: null, text: '' });
        };

        const handleOpenEvaluation = (evalId) => {
            if (actions.edit) {
                const ev = data.evaluations.find(e => e.id === evalId);
                if (ev) actions.edit(ev);
            }
        };

        // --- RENDER ---
        return (
            <div className="space-y-4 h-full flex flex-col">
                
                {/* 1. BARRA DE FILTROS */}
                <Card className={`p-3 shrink-0 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`} darkMode={isDarkMode}>
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                            <h3 className={`font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                <MessageSquare size={20} className="text-purple-500"/> Acompanhamento de Feedbacks
                            </h3>
                            <div className="relative w-64">
                                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} size={14} />
                                <Input placeholder="Pesquisar..." value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} className={`pl-9 h-9 text-xs ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white'}`}/>
                            </div>
                        </div>

                        <div className="grid grid-cols-6 gap-2">
                            <select className={`h-8 text-xs rounded border px-2 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200'}`} value={filters.dept} onChange={e=>setFilters({...filters, dept:e.target.value})}>
                                <option value="">Todos Deptos</option>
                                {data.departments.map(d=><option key={d.id} value={d.name}>{d.name}</option>)}
                            </select>
                            <select className={`h-8 text-xs rounded border px-2 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200'}`} value={filters.classification} onChange={e=>setFilters({...filters, classification:e.target.value})}>
                                <option value="">Todas Classificações</option>
                                <option value="Positivo">Positivo</option>
                                <option value="Corretivo">Corretivo</option>
                            </select>
                            <select className={`h-8 text-xs rounded border px-2 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200'}`} value={filters.direction} onChange={e=>setFilters({...filters, direction:e.target.value})}>
                                <option value="">Todos Direcionamentos</option>
                                <option value="Avance">Avance</option>
                                <option value="Reveja">Reveja</option>
                                <option value="Pare">Pare</option>
                            </select>
                            <select className={`h-8 text-xs rounded border px-2 font-bold ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-700'}`} value={filters.status} onChange={e=>setFilters({...filters, status:e.target.value})}>
                                <option value="Pendentes">Pendentes (Aberto)</option>
                                <option value="Concluídos">Concluídos</option>
                                <option value="Todos">Todos</option>
                            </select>
                            <div className={`flex rounded border h-8 overflow-hidden col-span-2 ${isDarkMode ? 'border-slate-600' : 'border-gray-200'}`}>
                                <select className={`h-full text-xs px-2 outline-none border-r ${isDarkMode ? 'bg-slate-700 text-white border-slate-600' : 'bg-gray-100 border-gray-200'}`} value={filters.periodMode} onChange={e=>setFilters({...filters, periodMode:e.target.value})}>
                                    <option value="ano">Ano</option>
                                    <option value="custom">Personalizado</option>
                                </select>
                                {filters.periodMode === 'ano' ? (
                                    <input 
                                        type="number" 
                                        className={`flex-1 text-center text-xs outline-none bg-transparent ${isDarkMode?'text-white':''}`} 
                                        value={filters.year} 
                                        onChange={e=>setFilters({...filters, year:e.target.value})}
                                        placeholder="Todos" // Placeholder indicando que vazio = todos
                                    />
                                ) : (
                                    <div className="flex flex-1 items-center">
                                        <input type="date" className={`w-1/2 text-[10px] outline-none bg-transparent ${isDarkMode?'text-white invert-calendar':''}`} value={filters.startDate} onChange={e=>setFilters({...filters, startDate:e.target.value})}/>
                                        <input type="date" className={`w-1/2 text-[10px] outline-none bg-transparent ${isDarkMode?'text-white invert-calendar':''}`} value={filters.endDate} onChange={e=>setFilters({...filters, endDate:e.target.value})}/>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Card>

                {/* 2. TABELA DE FEEDBACKS */}
                <div className={`flex-1 rounded-xl shadow-sm border overflow-hidden flex flex-col ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                    <div className="overflow-auto custom-scrollbar flex-1">
                        <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                        <thead className={`text-xs uppercase font-bold sticky top-0 z-10 border-b ${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                                <tr>
                                    <th className="p-3 w-48">Colaborador</th>
                                    <th className="p-3 w-20 text-center">Ref.</th>
                                    <th className="p-3 w-28 text-center">Origem</th>
                                    <th className="p-3 w-16 text-center">Dpto</th> 
                                    <th className="p-3 w-28 text-center">Natureza</th>
                                    <th className="p-3 w-24 text-center">Ação</th>
                                    <th className="p-3 w-28 text-center">Prazo</th>
                                    <th className="p-3">Resumo</th>
                                    <th className="p-3 w-40 text-center">Ações</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                {processedFeedbacks.length === 0 ? (
                                    <tr><td colSpan={9} className="p-8 text-center opacity-50">Nenhum registro encontrado.</td></tr>
                                ) : (
                                    processedFeedbacks.map((fb, idx) => {
                                        const isLate = fb.followUpDate && new Date(fb.followUpDate) < new Date() && !fb.isConcluded;
                                        const rowClass = idx % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');
                                        const hasObs = fb.observations && fb.observations.length > 0;
                                        
                                        return (
                                            <tr key={idx} className={`transition-colors ${rowClass} ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-blue-50'} ${fb.isConcluded ? 'opacity-60 grayscale' : ''}`}>
                                                {/* 1. COLABORADOR */}
                                                <td className="p-3">
                                                    <div
                                                        className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}
                                                        title={fb.collabName}
                                                    >
                                                        {fb.collabShortName || getShortName(fb.collabName)}
                                                    </div>
                                                </td>
                                                
                                                {/* 2. REFERÊNCIA TRIMESTRE */}
                                                <td className="p-3 text-center text-[10px] font-bold text-slate-500 whitespace-nowrap">
                                                    {fb.quarterDisplay}
                                                </td>
                                                
                                                {/* 3. ORIGEM (PDI OU FEEDBACK) */}
                                                <td className="p-3 text-center">
                                                    {fb.type === 'PDI' ? (
                                                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-700 border border-orange-300 shadow-sm flex items-center justify-center gap-1 w-fit mx-auto">
                                                            <TrendingUp size={10}/> PDI                                                   </span>
                                                    ) : (
                                                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 border border-blue-300 flex items-center justify-center gap-1 w-fit mx-auto">
                                                            <MessageSquare size={10}/> Feedback
                                                        </span>
                                                    )}
                                                </td>
                                                
                                                {/* 4. DEPARTAMENTO */}
                                                <td className="p-3 text-center text-xs">{fb.department.substring(0, 3).toUpperCase()}</td>
                                                
                                                {/* 5. NATUREZA */}
                                                <td className="p-3 text-center">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${fb.nature === 'Corretivo' ? 'bg-red-100 text-red-700 border-red-200' : fb.type === 'PDI' ? 'bg-indigo-100 text-indigo-700 border-indigo-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`}>
                                                        {fb.nature || 'Padrão'}
                                                    </span>
                                                </td>
                                                
                                                {/* 6. AÇÃO (DIRECIONAMENTO) */}
                                                <td className="p-3 text-center">
                                                    {fb.actionTag ? (
                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider border shadow-sm 
                                                            ${fb.actionTag === 'Pare' ? 'bg-red-500 text-white' : 
                                                            fb.actionTag === 'Reveja' ? 'bg-amber-500 text-white' : 
                                                            'bg-blue-500 text-white'}`}>
                                                            {fb.actionTag.toUpperCase()}
                                                        </span>
                                                    ) : '-'}
                                                </td>
                                                
                                                {/* 7. PRAZO */}
                                                <td className="p-3 text-center">
                                                    {fb.followUpDate ? (
                                                        <div className={`font-mono text-xs font-bold ${isLate ? 'text-red-500 animate-pulse' : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}>
                                                            {new Date(fb.followUpDate).toLocaleDateString('pt-BR')}
                                                            {isLate && <div className="text-[9px] uppercase">Atrasado</div>}
                                                        </div>
                                                    ) : '-'}
                                                </td>
                                                
                                                {/* 8. RESUMO TEXTO */}
                                                <td className="p-3 relative group">
                                                    <div className="truncate max-w-[200px] text-xs cursor-help">{fb.text}</div>
                                                    <div className={`absolute left-0 top-full mt-1 w-80 p-3 rounded shadow-xl z-50 invisible group-hover:visible whitespace-pre-wrap text-xs border ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-200 text-gray-800'}`}>
                                                        {fb.text}
                                                    </div>
                                                </td>
                                                
                                                {/* 9. BOTÕES DE AÇÃO */}
                                                <td className="p-3 text-center">
                                                    <div className="flex justify-center gap-1">
                                                        {/* BOTÃO TOGGLE: CONCLUIR / REABRIR */}
                                                        <button 
                                                            onClick={() => handleConclude(fb)} 
                                                            className={`p-1.5 rounded transition-colors ${
                                                                fb.isConcluded 
                                                                    ? 'bg-orange-100 text-orange-600 hover:bg-orange-200' 
                                                                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                                                            }`} 
                                                            title={fb.isForwarded ? "Desfazer vínculo com o próximo feedback" : (fb.isConcluded ? "Reabrir Registro" : "Concluir ou vincular ao próximo")}
                                                        >
                                                            {fb.isForwarded ? <Reply size={14}/> : (fb.isConcluded ? <RefreshCw size={14}/> : <CheckCircle size={14}/>)}
                                                        </button>

                                                        {/* BOTÃO EDITAR DATA */}
                                                        {!fb.isConcluded && (
                                                            <button 
                                                                onClick={() => setEditDateModal({ isOpen: true, feedbackId: fb.id, evalId: fb.evalId, currentDate: fb.followUpDate })} 
                                                                className="p-1.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200" 
                                                                title="Editar Prazo"
                                                            >
                                                                <Calendar size={14}/>
                                                            </button>
                                                        )}
                                                        
                                                        {/* BOTÃO OBSERVAÇÕES */}
                                                        <div className="relative group/obs">
                                                            <button 
                                                                onClick={() => setObsModal({ isOpen: true, feedbackId: fb.id, evalId: fb.evalId, text: '' })} 
                                                                className={`p-1.5 rounded hover:opacity-80 transition-colors ${hasObs ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-700'}`} 
                                                                title="Adicionar/Ver Observações"
                                                            >
                                                                <MessageSquare size={14}/>
                                                            </button>
                                                        </div>

                                                        <button onClick={() => setDetailModal(fb)} className="p-1.5 rounded bg-gray-100 text-gray-700 hover:bg-gray-200" title="Ver Detalhes"><Eye size={14}/></button>
                                                        
                                                        <button onClick={() => handleOpenEvaluation(fb.evalId)} className="p-1.5 rounded bg-orange-100 text-orange-700 hover:bg-orange-200" title="Abrir Avaliação">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* --- MODAIS DE AÇÃO --- */}
                
                {editDateModal.isOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setEditDateModal({ ...editDateModal, isOpen: false })}>
                        <div className={`p-6 rounded-xl w-80 shadow-xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <h3 className="font-bold mb-4">Reprogramar Prazo</h3>
                            <label className="text-xs block mb-1">Nova Data</label>
                            <input type="date" className={`w-full p-2 border rounded mb-4 ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar' : ''}`} value={editDateModal.currentDate || ''} onChange={e => setEditDateModal({...editDateModal, currentDate: e.target.value})} />
                            <div className="flex justify-end gap-2">
                                <Button variant="secondary" onClick={() => setEditDateModal({ ...editDateModal, isOpen: false })}>Cancelar</Button>
                                <Button onClick={handleUpdateDate}>Salvar</Button>
                            </div>
                        </div>
                    </div>
                )}

                {obsModal.isOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setObsModal({ ...obsModal, isOpen: false })}>
                        <div className={`p-6 rounded-xl w-96 shadow-xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <h3 className="font-bold mb-4 flex items-center gap-2"><MessageSquare size={18}/> Nova Observação</h3>
                            <textarea className={`w-full p-2 border rounded mb-4 h-24 text-sm ${isDarkMode ? 'bg-slate-900 border-slate-600' : ''}`} placeholder="Digite a observação sobre este feedback..." value={obsModal.text} onChange={e => setObsModal({...obsModal, text: e.target.value})} autoFocus></textarea>
                            <div className="flex justify-end gap-2">
                                <Button variant="secondary" onClick={() => setObsModal({ ...obsModal, isOpen: false })}>Cancelar</Button>
                                <Button onClick={handleAddObs}>Registrar</Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* NOVO MODAL DE CONFIRMAÇÃO (Substitui window.confirm) */}
                                {/* MODAL DE CONCLUSÃO / CONTINUIDADE */}
                {continuityModal.isOpen && continuityModal.source && (
                    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                        <div className={`w-full max-w-3xl rounded-2xl border shadow-2xl p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`}>
                            <h3 className="text-lg font-black mb-2">Como deseja concluir este feedback?</h3>
                            <p className="text-sm opacity-70 mb-5">Conclua definitivamente quando o problema não se repetiu, ou vincule ao próximo registro para manter um histórico contínuo, inclusive entre anos.</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
                                <button onClick={async () => { await concludeDefinitely(continuityModal.source); setContinuityModal({isOpen:false,source:null,targetKey:''}); }} className="p-4 rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 text-left">
                                    <div className="font-black flex items-center gap-2"><CheckCircle size={18}/> Concluir definitivamente</div>
                                    <div className="text-xs mt-1">O comportamento foi corrigido e o acompanhamento termina aqui.</div>
                                </button>
                                <div className={`p-4 rounded-xl border ${isDarkMode ? 'border-purple-700 bg-purple-900/20' : 'border-purple-300 bg-purple-50'}`}>
                                    <div className="font-black flex items-center gap-2 text-purple-600"><Reply size={18}/> Vincular ao próximo</div>
                                    <div className={`mt-3 max-h-72 overflow-y-auto rounded-lg border p-2 space-y-2 ${isDarkMode ? 'bg-slate-900/70 border-slate-600' : 'bg-white border-gray-300'}`}>
                                        {getContinuityCandidates(continuityModal.source).length === 0 ? (
                                            <div className="p-3 text-xs text-center opacity-60">Nenhum feedback posterior disponível para vínculo.</div>
                                        ) : getContinuityCandidates(continuityModal.source).map(candidate => {
                                            const candidateKey = `${candidate.evalId}::${candidate.id}`;
                                            const selected = continuityModal.targetKey === candidateKey;
                                            const createdDate = candidate.timestamp ? new Date(candidate.timestamp).toLocaleDateString('pt-BR') : '-';
                                            const followDate = candidate.followUpDate ? new Date(`${candidate.followUpDate}T00:00:00`).toLocaleDateString('pt-BR') : '-';
                                            return (
                                                <button
                                                    type="button"
                                                    key={candidateKey}
                                                    onClick={() => setContinuityModal(prev => ({ ...prev, targetKey: candidateKey }))}
                                                    className={`w-full text-left rounded-lg border p-3 transition-all ${selected ? 'border-purple-500 ring-2 ring-purple-500/30 bg-purple-500/10' : isDarkMode ? 'border-slate-700 hover:border-purple-600 bg-slate-800' : 'border-gray-200 hover:border-purple-400 bg-white'}`}
                                                >
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="font-black text-xs text-purple-500">{candidate.quarterDisplay}</div>
                                                        <div className={`shrink-0 w-4 h-4 rounded-full border flex items-center justify-center ${selected ? 'border-purple-500 bg-purple-600' : 'border-gray-400'}`}>
                                                            {selected && <Check size={11} className="text-white" />}
                                                        </div>
                                                    </div>
                                                    <div className="mt-2 text-sm font-semibold whitespace-normal break-words">{candidate.text || 'Sem descrição'}</div>
                                                    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] opacity-75">
                                                        <span><b>Natureza:</b> {candidate.nature || '-'}</span>
                                                        <span><b>Ação:</b> {candidate.actionTag || '-'}</span>
                                                        <span><b>Registrado:</b> {createdDate}</span>
                                                        <span><b>Prazo:</b> {followDate}</span>
                                                        <span className="col-span-2"><b>Observações:</b> {(candidate.observations || []).length}</span>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <button disabled={!continuityModal.targetKey || isSavingContinuity} onClick={linkToNextFeedback} className="w-full mt-3 py-2 rounded bg-purple-600 disabled:opacity-40 text-white text-xs font-black flex items-center justify-center gap-2">
                                        {isSavingContinuity && <Loader2 size={14} className="animate-spin" />}
                                        {isSavingContinuity ? 'Salvando vínculo...' : 'Confirmar vínculo'}
                                    </button>
                                </div>
                            </div>
                            <button onClick={()=>setContinuityModal({isOpen:false,source:null,targetKey:''})} className={`w-full py-2 rounded-lg text-sm font-bold ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>Cancelar</button>
                        </div>
                    </div>
                )}

{confirmModal.isOpen && (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })}>
                        <div className={`p-6 rounded-xl w-96 shadow-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            
                            <div className="flex items-center gap-3 mb-4 text-blue-600">
                                <div className={`p-2 rounded-full ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                                    <CheckCircle size={24}/>
                                </div>
                                <h3 className="font-bold text-lg">{confirmModal.title}</h3>
                            </div>
                            
                            <p className={`text-sm mb-6 leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                {confirmModal.message}
                            </p>

                            <div className="flex justify-end gap-2">
                                <Button 
                                    variant="secondary" 
                                    onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })} 
                                    className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}
                                >
                                    Cancelar
                                </Button>
                                
                                <Button 
                                    onClick={confirmModal.onConfirm} 
                                    className="bg-blue-600 hover:bg-blue-700 text-white shadow-md border-0"
                                >
                                    Confirmar
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {detailModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setDetailModal(null)}>
                        <div className={`p-6 rounded-xl w-full max-w-lg shadow-xl border overflow-hidden flex flex-col max-h-[90vh] ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center border-b pb-2 mb-4">
                                <h3 className="font-bold text-lg">Detalhes do Feedback</h3>
                                <button onClick={() => setDetailModal(null)}><X size={20}/></button>
                            </div>
                            <div className="overflow-y-auto custom-scrollbar space-y-4 text-sm">
                                <div className="grid grid-cols-2 gap-4">
                                    <div><span className="block text-xs opacity-50 uppercase font-bold">Colaborador</span>{detailModal.collabName}</div>
                                    <div><span className="block text-xs opacity-50 uppercase font-bold">Ref.</span>{detailModal.quarterDisplay}</div>
                                    
                                    <div>
                                        <span className="block text-xs opacity-50 uppercase font-bold mb-1">Classificação</span>
                                        <span className={`px-2 py-0.5 rounded text-xs font-bold border ${detailModal.nature === 'Corretivo' ? 'bg-red-100 text-red-700 border-red-200' : 'bg-blue-100 text-blue-700 border-blue-200'}`}>
                                            {detailModal.nature || 'Padrão'}
                                        </span>
                                    </div>
                                    
                                    <div>
                                        <span className="block text-xs opacity-50 uppercase font-bold mb-1">Direcionamento</span>
                                        {detailModal.actionTag ? (
                                            <span className={`px-2 py-0.5 rounded text-xs font-bold border ${
                                                detailModal.actionTag === 'Pare' ? 'bg-red-500 text-white' : 
                                                detailModal.actionTag === 'Reveja' ? 'bg-amber-500 text-white' : 
                                                'bg-blue-500 text-white'
                                            }`}>
                                                {detailModal.actionTag.toUpperCase()}
                                            </span>
                                        ) : '-'}
                                    </div>
                                </div>

                                {detailModal.status === 'Encaminhado' && (detailModal.linkedTo || detailModal.continuity?.nextFeedbackId) && (
                                    <div className={`p-3 rounded-lg border-l-4 border-purple-500 ${isDarkMode ? 'bg-purple-950/30 border-y border-r border-purple-900' : 'bg-purple-50 border-y border-r border-purple-200'}`}>
                                        <div className="font-black text-purple-500 flex items-center gap-2 mb-1">
                                            <Reply size={16}/> Encerrado por vínculo
                                        </div>
                                        <p className="text-sm">
                                            {detailModal.conclusionNote || `Este feedback foi encerrado e vinculado ao feedback ${detailModal.linkedTo?.quarterDisplay || detailModal.continuity?.nextQuarterDisplay || 'seguinte'} para continuidade do acompanhamento.`}
                                        </p>
                                        <div className="text-xs opacity-60 mt-2">
                                            Destino: {detailModal.linkedTo?.quarterDisplay || detailModal.continuity?.nextQuarterDisplay || '-'}
                                            {detailModal.linkedTo?.text ? ` — ${detailModal.linkedTo.text}` : ''}
                                        </div>
                                    </div>
                                )}

                                {getCompleteContinuityHistory(detailModal).length > 0 && (
                                    <div>
                                        <span className="block text-xs opacity-50 uppercase font-bold mb-2">Histórico vinculado completo</span>
                                        <div className="space-y-3">
                                            {getCompleteContinuityHistory(detailModal).map((historyItem, historyIndex) => (
                                                <div key={`${historyItem.feedbackId || historyIndex}`} className={`rounded-lg border overflow-hidden ${isDarkMode ? 'border-purple-800 bg-slate-900/60' : 'border-purple-200 bg-purple-50/60'}`}>
                                                    <div className={`px-3 py-2 flex flex-wrap items-center justify-between gap-2 border-b ${isDarkMode ? 'border-purple-800 bg-purple-950/30' : 'border-purple-200 bg-purple-100/70'}`}>
                                                        <div className="font-black text-purple-500">{historyItem.quarterDisplay || getQuarterRefLabel(historyItem.quarter, historyItem.periodYear)}</div>
                                                        <div className="text-[10px] opacity-60">
                                                            Registro original: {historyItem.timestamp ? new Date(historyItem.timestamp).toLocaleDateString('pt-BR') : '-'}
                                                        </div>
                                                    </div>
                                                    <div className="p-3 space-y-3">
                                                        <div className="flex flex-wrap gap-2 text-[10px] font-bold">
                                                            <span className="px-2 py-1 rounded bg-red-100 text-red-700">{historyItem.nature || 'Sem classificação'}</span>
                                                            <span className="px-2 py-1 rounded bg-amber-100 text-amber-700">{historyItem.actionTag || 'Sem direcionamento'}</span>
                                                            <span className="px-2 py-1 rounded bg-slate-200 text-slate-700">{historyItem.type || 'Feedback'}</span>
                                                        </div>
                                                        <div>
                                                            <div className="text-[10px] uppercase font-bold opacity-50 mb-1">Feedback original</div>
                                                            <p className="whitespace-pre-wrap">{historyItem.text || '-'}</p>
                                                        </div>
                                                        <div>
                                                            <div className="text-[10px] uppercase font-bold opacity-50 mb-1">Anotações e comentários — {historyItem.quarterDisplay || getQuarterRefLabel(historyItem.quarter, historyItem.periodYear)}</div>
                                                            {Array.isArray(historyItem.observations) && historyItem.observations.length > 0 ? (
                                                                <div className="space-y-2">
                                                                    {historyItem.observations.map((obs, obsIndex) => (
                                                                        <div key={obs.id || obsIndex} className={`p-2 rounded text-xs border-l-2 border-purple-500 ${isDarkMode ? 'bg-slate-950' : 'bg-white'}`}>
                                                                            <div className="flex justify-between gap-2 font-bold mb-1">
                                                                                <span>{obs.author || 'Sem identificação'} · {historyItem.quarterDisplay || getQuarterRefLabel(historyItem.quarter, historyItem.periodYear)}</span>
                                                                                <span className="opacity-50">{obs.date ? new Date(obs.date).toLocaleDateString('pt-BR') : '-'}</span>
                                                                            </div>
                                                                            <p className="whitespace-pre-wrap">{obs.text}</p>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : <p className="text-xs italic opacity-50">Nenhuma anotação neste trimestre.</p>}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className={`p-3 rounded border ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                    <span className="block text-xs opacity-50 uppercase font-bold mb-1">Feedback atual — {detailModal.quarterDisplay}</span>
                                    <p className="whitespace-pre-wrap">{detailModal.text}</p>
                                </div>
                                
                                <div>
                                    <span className="block text-xs opacity-50 uppercase font-bold mb-2">Anotações e comentários — {detailModal.quarterDisplay}</span>
                                    <div className="space-y-2">
                                        {detailModal.observations && detailModal.observations.length > 0 ? (
                                            detailModal.observations.map((obs, i) => (
                                                <div key={obs.id || i} className={`p-2 rounded text-xs border-l-2 border-purple-500 ${isDarkMode ? 'bg-slate-900' : 'bg-purple-50'}`}>
                                                    <div className="flex justify-between gap-2 font-bold mb-1">
                                                        <span>{obs.author || 'Sem identificação'} · {detailModal.quarterDisplay}</span>
                                                        <span className="opacity-50">{obs.date ? new Date(obs.date).toLocaleDateString('pt-BR') : '-'}</span>
                                                    </div>
                                                    <p className="whitespace-pre-wrap">{obs.text}</p>
                                                </div>
                                            ))
                                        ) : <p className="text-xs italic opacity-50">Nenhuma observação extra neste trimestre.</p>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    /**
     * ============================================================================
     * COMPONENTE: FINANCEIRO SYSTEM (VERSÃO FINAL INTEGRAL)
     * ============================================================================
     */


    /* ===============================================================================
     * [END_MOD_AVALIACAO_SYSTEM]
     * FIM DO BLOCO: AvaliacaoSystem
     * [START_MOD_FINANCEIRO_SYSTEM]
     * INICIO DO BLOCO: FinanceiroSystem
     * =============================================================================== */
    const FinanceiroSystem = ({
        data,
        actions,
        permissions,
        currentUser,
        initialTab,
        isDarkMode
    }) => {
        // --- Blindagem de Dados ---
        const evaluations = data?.evaluations || [];
        const collaborators = data?.collaborators || [];
        const departments = data?.departments || [];
        const pcs = data?.pcs || [];
        const globalConfigs = data?.globalConfigs || [];
        const evaluationTypes = data?.evaluationTypes || [];
        const minWageHistory = data?.minWageHistory || [];

        // ========================================================================
        // 1. ESTADOS (STATE MANAGEMENT) - MIGRADO PARA BANCO DE DADOS
        // ========================================================================

        const [tab, setTab] = useState(initialTab || 'pcs');
        const [form, setForm] = useState({});
        const [modal, setModal] = useState(null);
        const [notification, setNotification] = useState(null);
        const [isUpdatingSheet, setIsUpdatingSheet] = useState(false);

        // 1. Ler preferências salvas no perfil do usuário (se houver)
        const prefs = currentUser?.preferences?.fin || {};

        // --- Filtros de Projeção (Inicia com valor do Banco ou Padrão) ---
        const [projYear, setProjYear] = useState(prefs.projYear || new Date().getFullYear().toString());
        const [projDept, setProjDept] = useState(prefs.projDept || '');
        const [projRes, setProjRes] = useState(prefs.projRes || 'Todos');
        const [projPersonType, setProjPersonType] = useState(prefs.projPersonType || 'Todos');
        const [projGroup, setProjGroup] = useState(prefs.projGroup || 'Departamento');

        // --- Estados para Comparação (Também persistidos) ---
        const [isComparing, setIsComparing] = useState(prefs.isComparing || false);
        const [unarchiveModal, setUnarchiveModal] = useState(null);
        const [compScope, setCompScope] = useState(prefs.compScope || 'total'); 
        const [compDeptId, setCompDeptId] = useState(prefs.compDeptId || '');
        const [compYears, setCompYears] = useState(prefs.compYears || []); 

        // ADICIONE ISTO SE NÃO TIVER:
        const [selectedEval, setSelectedEval] = useState(null); 
        const [isGenerating, setIsGenerating] = useState(false);
        

        const toggleCompYear = (year) => {
            if (compYears.includes(year)) {
                setCompYears(prev => prev.filter(y => y !== year));
            } else {
                if (compYears.length >= 4) return alert("Máximo de 4 anos para comparação.");
                setCompYears(prev => [...prev, year].sort((a,b) => b - a));
            }
        };

        // --- Filtros de Análise (Inicia com valor do Banco ou Padrão) ---
        const [analiseFilters, setAnaliseFilters] = useState(prefs.analiseFilters || {
            startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
            endDate: new Date().toISOString().split('T')[0],
            dept: '',
            personType: 'Todos',
            showTotalSalary: false
        });

        // --- Estados de Modais e Dados Temporários (Não persistidos) ---
        const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
        const [syncYearInput, setSyncYearInput] = useState(new Date().getFullYear().toString());
        const [isPCSModalOpen, setIsPCSModalOpen] = useState(false);
        const [isBulkEditPcsOpen, setIsBulkEditPcsOpen] = useState(false);
        const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);
        const [importModalOpen, setImportModalOpen] = useState(false);

        const [bulkPcsForm, setBulkPcsForm] = useState({});
        const [promoCandidates, setPromoCandidates] = useState([]);
        const [promoEffectiveDate, setPromoEffectiveDate] = useState('');
        const [selectedPcsIds, setSelectedPcsIds] = useState([]);
        const [readjustPercent, setReadjustPercent] = useState(0);
        const [readjustDate, setReadjustDate] = useState('');
        const [levelOverrides, setLevelOverrides] = useState({});
        const [importConfig, setImportConfig] = useState({ targetDept: '', ignoreTerm: '' });
        const [importDate, setImportDate] = useState(new Date().toISOString().split('T')[0]);
        const [selectedFile, setSelectedFile] = useState(null);
        const [isProcessing, setIsProcessing] = useState(false);
        
        // --- Estados de UI ---
        const [pcsSearch, setPcsSearch] = useState('');
        const [undoTargetId, setUndoTargetId] = useState(null);
        const [expandedDepts, setExpandedDepts] = useState({});
        const [tooltip, setTooltip] = useState(null);

        // --- DASHBOARD LAYOUT & RESIZING (VIA BANCO) ---
        const [showWidgetMenu, setShowWidgetMenu] = useState(false);

        // --- ESTADOS DOS FILTROS DA CENTRAL (ATUALIZADO) ---
        const [finFilterYear, setFinFilterYear] = useState(new Date().getFullYear().toString());
        const [finFilterDept, setFinFilterDept] = useState('');
        const [finFilterStatus, setFinFilterStatus] = useState('');
        const [finFilterPersonType, setFinFilterPersonType] = useState('Todos'); // <--- NOVO ESTADO
        const [finSearch, setFinSearch] = useState('');

        const [signConfirmModal, setSignConfirmModal] = useState({ 
            isOpen: false, 
            type: '', // 'diretoria' ou 'rh'
            action: '', // 'sign' ou 'undo'
            evalData: null 
        });

    const filteredEvaluations = useMemo(() => {
        return evaluations.filter(ev => {
            // 1. Filtro de Ano (Segurança: converte para string para evitar erro se for null)
            if (finFilterYear && String(ev.periodYear) !== String(finFilterYear)) return false;

            // 2. Filtro de Departamento (CORRIGIDO)
            if (finFilterDept) {
                // Tenta pelo ID direto
                if (ev.departmentId === finFilterDept) {
                    // ok, match
                } else {
                    // Fallback: Busca o nome do depto pelo ID do filtro e compara com o nome na avaliação
                    const deptObj = departments.find(d => d.id === finFilterDept);
                    if (!deptObj || ev.department !== deptObj.name) return false;
                }
            }

            // 3. Filtro de Tipo
            if (finFilterPersonType !== 'Todos' && ev.personType !== finFilterPersonType) return false;

            // 4. Filtro de Pesquisa (CORRIGIDO: Tela Branca)
            if (finSearch) {
                const s = finSearch.toLowerCase();
                const cName = (ev.collaboratorName || '').toLowerCase(); // Proteção contra null
                const mName = (ev.managerName || '').toLowerCase();      // Proteção contra null
                if (!cName.includes(s) && !mName.includes(s)) return false;
            }

            // 5. Filtro de Status Inteligente (CORRIGIDO: Removido bloqueio indevido)
            if (finFilterStatus) {
                const isArchived = ev.status === 'Concluída Financeiro';
                const hasManagerSig = ev.managerSignature || (ev.managerSignatures && ev.managerSignatures.length > 0);
                const hasCollabSig = ev.collabSignature || (ev.collabSignatures && Object.keys(ev.collabSignatures).length > 0);
                const isFullySigned = hasManagerSig && hasCollabSig;

                if (finFilterStatus === 'Arquivada' && !isArchived) return false;
                if (finFilterStatus === 'Concluída' && (!isFullySigned || isArchived)) return false; // Pronta mas não arquivada
                
                // Lógicas estritas para os pendentes
                if (finFilterStatus === 'Pendente Gestor' && (!isArchived && !hasManagerSig)) return true;
                if (finFilterStatus === 'Pendente Colab' && (!isArchived && hasManagerSig && !hasCollabSig)) return true;
                
                // Se selecionou um status específico e não retornou true acima, então é false
                if (finFilterStatus === 'Pendente Gestor' || finFilterStatus === 'Pendente Colab') return false; 
                
                // Pendente Geral (Qualquer um que não esteja pronto)
                if (finFilterStatus === 'Pendente' && (isFullySigned || isArchived)) return false;
            }

            return true;
        });
    }, [evaluations, departments, finFilterYear, finFilterDept, finFilterStatus, finFilterPersonType, finSearch]);

        const defaultWidgets = [
            { id: 'financialSummary', label: 'Resumo Financeiro Promoção|Premiação', visible: true, w: 2, h: 500, order: 0 },
            { id: 'distribution', label: 'Distribuição por Departamento', visible: true, w: 2, h: 400, order: 1 },
            { id: 'pieChart', label: 'Gráfico Pizza 3D', visible: true, w: 1, h: 400, order: 2 },
            { id: 'roleCost', label: 'Custo por Cargo', visible: true, w: 1, h: 400, order: 3 },
            { id: 'history5y', label: 'Histórico 5 Anos', visible: true, w: 2, h: 450, order: 4 },
            { id: 'history8q', label: 'Evolução Trimestral', visible: true, w: 2, h: 450, order: 5 }
        ];

        // Carrega layout do banco ou usa o padrão
        const [dashboardItems, setDashboardItems] = useState(() => {
            const savedLayout = prefs.layout;
            if (savedLayout && Array.isArray(savedLayout)) {
                // Mescla o salvo com o padrão (para garantir que novos widgets apareçam)
                const merged = defaultWidgets.map((def) => {
                    const savedItem = savedLayout.find((p) => p.id === def.id);
                    return savedItem ? { ...def, ...savedItem } : def;
                });
                return merged.sort((a, b) => (a.order || 99) - (b.order || 99));
            }
            return defaultWidgets;
        });
        
        const [draggedIdx, setDraggedIdx] = useState(null);

        // --- EFEITOS DE PERSISTÊNCIA ---

        useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);

        useEffect(() => {
            if (notification) {
                const timer = setTimeout(() => setNotification(null), 3000);
                return () => clearTimeout(timer);
            }
        }, [notification]);

        // SALVAMENTO AUTOMÁTICO NO BANCO (Unificado)
        useEffect(() => {
            const timer = setTimeout(() => {
                if (currentUser && currentUser.id && actions.saveUserPreferences) {
                    const newFinPreferences = {
                        projYear, projDept, projRes, projPersonType, projGroup, // Filtros Projeção
                        isComparing, compScope, compDeptId, compYears,          // Filtros Comparação
                        analiseFilters,                                         // Filtros Análise
                        layout: dashboardItems.map(i => ({                      // Layout Dashboard
                            id: i.id, visible: i.visible, w: i.w, h: i.h, order: i.order 
                        }))
                    };

                    // Compara com o que já está salvo na memória
                    const currentSaved = currentUser.preferences?.fin || {};
                    if (JSON.stringify(currentSaved) !== JSON.stringify(newFinPreferences)) {
                        actions.saveUserPreferences({ fin: newFinPreferences });
                    }
                }
            }, 2000); // Debounce de 2 segundos

            return () => clearTimeout(timer);
        }, [
            projYear, projDept, projRes, projPersonType, projGroup, 
            isComparing, compScope, compDeptId, compYears,
            analiseFilters, dashboardItems, 
            currentUser, actions
        ]);

        // ========================================================================
        // 3. LÓGICA DE NEGÓCIO E CALCULOS
        // ========================================================================
        const notify = (type, text) => setNotification({ type, text });
        
        const toggleDept = (dept) => {
            setExpandedDepts((prev) => ({ ...prev, [dept]: !prev[dept] }));
        };

        // Helper: Formata moeda sempre positivo para exibição
        const formatCurrencyAbs = (val) => {
            const num = Math.abs(parseFloat(val) || 0);
            return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
        };

        const calculateUnifiedImpacts = (monthlyPromo, annualAward) => {
            const P = Math.abs(monthlyPromo || 0); 
            const R = Math.abs(annualAward || 0);
            const an_Salario = P * 12;
            const an_Premio = R;
            const an_Decimo = P;
            const an_Ferias = P / 3;
            const an_DSR_Premio = (R / 21) * 5;
            const media_Var_Mensal = (R + an_DSR_Premio) / 12;
            const an_Reflexos = (media_Var_Mensal * 2) + (media_Var_Mensal / 3);
            
            // Subtotal sem encargos
            const subtotal_Sem_Encargos = an_Salario + an_Premio + an_Decimo + an_Ferias + an_DSR_Premio + an_Reflexos;
            
            const an_FGTS = subtotal_Sem_Encargos * 0.08;
            
            // Total Final
            const totalAnnual = subtotal_Sem_Encargos + an_FGTS;
            const totalMonthly = totalAnnual / 12;
            
            return {
                totalAnnual,
                totalMonthly,
                subtotalSemEncargos: subtotal_Sem_Encargos,
                details: {
                    promoMensal: an_Salario,
                    premiacao: an_Premio,
                    decimoTerceiro: an_Decimo,
                    tercoFerias: an_Ferias,
                    dsr: an_DSR_Premio,
                    reflexoFerias13: an_Reflexos,
                    fgts: an_FGTS
                }
            };
        };

        const calculateComplexImpact = (monthlyPromo, annualAward) => {
            const unified = calculateUnifiedImpacts(monthlyPromo, annualAward);
            return { totalMensal: unified.totalMonthly, totalAnnual: unified.totalAnnual };
        };

        const getPcsRank = (role, seniority, level) => {
            let score = 0;
            const r = (role || '').toUpperCase().trim();
            if (r.includes('ESTAGI')) score += 10000;
            else if (r.includes('AUXILIAR')) score += 20000;
            else if (r.includes('ASSISTENTE')) score += 30000;
            else if (r.includes('ANALISTA')) score += 40000;
            else if (r.includes('ENCARREGADO')) score += 50000;
            else if (r.includes('COORDENADOR')) score += 55000;
            else if (r.includes('GERENTE')) score += 60000;
            else if (r.includes('DIRETOR')) score += 70000;
            else score += 1000;

            const s = (seniority || '').toUpperCase().trim();
            if (s.includes('JR')) score += 100;
            else if (s.includes('PL')) score += 200;
            else if (s.includes('SR')) score += 300;

            const l = (level || '').toUpperCase().trim();
            if (l === 'I' || l === '1') score += 1;
            else if (l === 'II' || l === '2') score += 2;
            else if (l === 'III' || l === '3') score += 3;
            else if (l === 'IV' || l === '4') score += 4;

            return score;
        };
        
        const hasRecentPromotion = (collabId) => {
            const c = collaborators.find(x => x.id === collabId);
            if (!c || !c.roleHistory || c.roleHistory.length < 2) return false;
            const history = [...c.roleHistory].sort((a, b) => {
                const dA = a.validFrom || '';
                const dB = b.validFrom || '';
                return dB.localeCompare(dA);
            });
            const lastMoveDate = new Date(history[0].validFrom);
            const projectYearInt = parseInt(projYear);
            return lastMoveDate.getFullYear() >= projectYearInt;
        };
        
        const getPcsSortRank = (item) => getPcsRank(item.role, item.seniority, item.level);

        const filteredPcsList = useMemo(() => {
            return pcs.filter((p) => {
                if (currentUser.role !== 'SuperAdm') {
                    const userDeptName = departments.find(d => d.id === currentUser.departmentId)?.name;
                    if (p.department !== 'Todos' && p.department !== 'Geral' && p.department !== userDeptName) return false;
                }
                const s = pcsSearch.toLowerCase();
                return !pcsSearch || p.role.toLowerCase().includes(s) || p.department.toLowerCase().includes(s);
            }).sort((a, b) => getPcsSortRank(a) - getPcsSortRank(b));
        }, [pcs, pcsSearch, currentUser, departments]);

        // --- CÁLCULO DE DADOS DE ANÁLISE (CORRIGIDO: Percentuais sobre o Todo Global) ---
        const analiseData = useMemo(() => {
            if (!currentUser) return { totalEmployees: 0, totalPayroll: 0, deptAnalysis: [] };
            
            // 1. Filtra colaboradores apenas por Permissão e Tipo de Pessoa (IGNORA filtro de Depto para calcular o Total Geral)
            const globalScopeCollabs = collaborators.filter((c) => {
                // A. Permissões de Segurança
                if (currentUser.role !== 'SuperAdm') {
                    if (c.departmentId !== currentUser.departmentId) return false;
                    if (c.personType === 'Gestão') {
                        if (!currentUser.permissions?.viewGestao) {
                            const rUpper = (c.role || '').toUpperCase();
                            if (rUpper.includes('GERENTE') || rUpper.includes('DIRETOR') || rUpper.includes('HEAD')) return false;
                        }
                    }
                }
                // B. Filtro de Tipo de Pessoa (ex: ver só Gestão ou só Colaborador)
                if (analiseFilters.personType !== 'Todos' && c.personType !== analiseFilters.personType) return false;
                
                return true;
            });

            // 2. Calcula os Totais GERAIS da empresa (Denominador fixo para a porcentagem)
            let totalEmployees = 0;
            let totalPayroll = 0;

            // 3. Estrutura para montar as linhas (aplica o filtro de Depto aqui)
            const deptStats = {};

            globalScopeCollabs.forEach(collab => {
                // Cálculos básicos do salário
                const currentPcs = pcs.find(p => p.role === collab.role && p.seniority === collab.seniority && p.level === collab.level);
                const salary = currentPcs ? parseFloat(currentPcs.salary) : parseFloat(collab.salary || 0);
                const fullRole = `${collab.role || ''} ${collab.seniority || ''} ${collab.level || ''}`.trim();
                const managerRecord = data.managers.find(m => m.email && collab.email && m.email.toLowerCase() === collab.email.toLowerCase());

                // Acumula no Total Geral
                totalEmployees++;
                totalPayroll += salary;

                // Helper para adicionar dados se passar no filtro de departamento
                const addToDeptStats = (dId, dName, partialSal, isPartial) => {
                    // *** AQUI É O PULO DO GATO: O filtro de departamento só afeta a exibição das linhas, não o total geral ***
                    if (analiseFilters.dept && dId !== analiseFilters.dept) return;

                    if (!deptStats[dName]) {
                        const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#e879f9', '#6d28d9', '#f472b6'];
                        deptStats[dName] = {
                            count: 0,
                            payroll: 0,
                            color: colors[Object.keys(deptStats).length % colors.length],
                            people: []
                        };
                    }
                    deptStats[dName].payroll += partialSal;
                    deptStats[dName].count += 1;
                    deptStats[dName].people.push({ 
                        name: collab.name, 
                        role: fullRole, 
                        share: isPartial ? `${isPartial}%` : null 
                    });
                };

                // Lógica de distribuição (Gestores com Rateio vs Colaboradores Padrão)
                if (managerRecord && managerRecord.deptShare && managerRecord.deptShare.length > 0) {
                    managerRecord.deptShare.forEach((ds) => {
                        const dName = departments.find(d => d.id === ds.deptId)?.name || 'N/D';
                        const partialSalary = salary * (ds.share / 100);
                        addToDeptStats(ds.deptId, dName, partialSalary, ds.share);
                    });
                } else {
                    const deptName = departments.find(d => d.id === collab.departmentId)?.name || 'N/D';
                    addToDeptStats(collab.departmentId, deptName, salary, null);
                }
            });

            return {
                totalEmployees, // Total Global da empresa (respeitando apenas filtros de tipo/permissão)
                totalPayroll,   // Folha Global da empresa
                deptAnalysis: Object.keys(deptStats).map(d => ({ name: d, ...deptStats[d] })).sort((a, b) => b.payroll - a.payroll)
            };
        }, [analiseFilters, collaborators, departments, pcs, currentUser, data.managers]);    

        // --- CÁLCULO COMPARATIVO 5 ANOS ---
        const comparativeData = useMemo(() => {
            if (!currentUser) return { years: [], rows: [] };
            const currentYear = new Date().getFullYear();
            const years = Array.from({ length: 5 }, (_, i) => (currentYear - i).toString());

            const resolveTieLocal = (a, b) => {
                const scoreA = a.personType === 'Gestão' ? (a.finalScoreGestao || 0) : (a.qualitative.finalAverage || 0);
                const scoreB = b.personType === 'Gestão' ? (b.finalScoreGestao || 0) : (b.qualitative.finalAverage || 0);
                if (scoreB !== scoreA) return scoreB - scoreA;
                if (a.quantitative?.meanPoints !== b.quantitative?.meanPoints) return a.quantitative.meanPoints - b.quantitative.meanPoints;
                return (a.collaboratorName || '').localeCompare(b.collaboratorName || '');
            };

            const winnersByYearAndDept = {};
            years.forEach((year) => {
                const allEvalsYear = evaluations.filter(e => e.periodYear === year && (e.status === 'Finalizada' || e.status === 'T4-Concluido'));
                const calculatedEvals = allEvalsYear.map(ev => { try { return recalculateFinalAverage(ev); } catch (e) { return ev; } });
                const byDept = calculatedEvals.reduce((acc, ev) => {
                    if (!acc[ev.department]) acc[ev.department] = [];
                    acc[ev.department].push(ev);
                    return acc;
                }, {});
                Object.keys(byDept).forEach((dept) => {
                    const candidates = byDept[dept].filter((ev) => {
                        const collab = collaborators.find(c => c.id === ev.collaboratorId);
                        if (!collab) return false;
                        let config = evaluationTypes.find(et => et.id === collab.evalTypeId) || globalConfigs.find(gc => year >= new Date(gc.startDate).getFullYear()) || globalConfigs[0] || {};
                        const score = ev.personType === 'Gestão' ? (ev.finalScoreGestao || 0) : (ev.qualitative.finalAverage || 0);
                        const lost = ev.personType === 'Colaborador' ? (ev.quantitative.meanPoints || 0) : 0;
                        const mediaPromo = config.mediaPromocao || 8.0;
                        const maxLost = config.maxPointsLost || 10.0;
                        const promoThreshold = ev.personType === 'Gestão' ? (mediaPromo / 10.0) * 100.0 : mediaPromo;
                        const canWin = ev.personType === 'Colaborador' ? (lost <= maxLost) : true;
                        return score >= promoThreshold && canWin;
                    });
                    if (candidates.length > 0) {
                        candidates.sort(resolveTieLocal);
                        winnersByYearAndDept[`${year}-${dept}`] = candidates[0].collaboratorId;
                    }
                });
            });
            const rows = collaborators.filter((c) => {
                if (currentUser.role === 'SuperAdm') return true;
                if (c.departmentId !== currentUser.departmentId) return false;
                if (c.personType === 'Gestão') return false;
                if (c.email === currentUser.email) return false;
                if (analiseFilters.dept && c.departmentId !== analiseFilters.dept) return false;
                if (analiseFilters.personType !== 'Todos' && analiseFilters.personType && c.personType !== analiseFilters.personType) return false;
                return true;
            }).map((c) => {
                const row = {
                    id: c.id,
                    name: c.name,
                    dept: departments.find(d => d.id === c.departmentId)?.name || '-',
                    yearsData: {}
                };
        years.forEach((year) => {
                    const ev = evaluations.find(e => e.collaboratorId === c.id && e.periodYear === year && (e.status === 'Finalizada' || e.status === 'T4-Concluido'));
                    
                    if (ev && ev.qualitative) {
                        try {
                            const evCalc = recalculateFinalAverage(ev);
                            const isGestao = evCalc.personType === 'Gestão';
                            const noteQuali = isGestao ? evCalc.finalScoreGestao : evCalc.qualitative.finalAverage;
                            const noteQuanti = isGestao ? (evCalc.atualizacao?.total || 0) : evCalc.quantitative.meanPoints;
                            
                            // --- CORREÇÃO INÍCIO: Recálculo das Regras para Legenda ---
                            
                            // 1. Busca a configuração do ano específico ou padrão
                            let config = evaluationTypes.find(et => et.id === c.evalTypeId) || 
                                        globalConfigs.find(gc => year >= new Date(gc.startDate).getFullYear()) || 
                                        globalConfigs[0] || {};

                            const mediaPremiacao = parseFloat(config.mediaPremiacao) || 7.0;
                            const maxPointsLost = parseFloat(config.maxPointsLost) || 10.0;

                            // 2. Define o alvo (Threshold)
                            // Se for gestão, converte para base 100. Se colab, mantém base 10.
                            const premioThreshold = isGestao ? (mediaPremiacao / 10.0) * 100.0 : mediaPremiacao;
                            
                            // 3. Verifica critérios
                            const scoreToCheck = isGestao ? (evCalc.finalScoreGestao || 0) : (evCalc.qualitative.finalAverage || 0);
                            const lostPoints = isGestao ? 0 : (evCalc.quantitative.meanPoints || 0);
                            const canWin = isGestao ? true : (lostPoints <= maxPointsLost);

                            // 4. Define a etiqueta
                            const isWinner = winnersByYearAndDept[`${year}-${ev.department}`] === c.id;
                            let calculatedLabel = null;

                            if (isWinner) {
                                calculatedLabel = 'Promoção';
                            } else if (scoreToCheck >= premioThreshold && canWin) {
                                calculatedLabel = 'Premiação';
                            }
                            // --- CORREÇÃO FIM ---

                            row.yearsData[year] = { 
                                quali: noteQuali, 
                                quanti: noteQuanti, 
                                label: calculatedLabel, 
                                isGestao, 
                                feedbacks: ev.feedbacksHistory || {} 
                            };
                        } catch (e) { console.warn("Erro comp", c.name); }
                    }
                });
                return row;
            }).filter(r => Object.keys(r.yearsData).length > 0).sort((a, b) => a.name.localeCompare(b.name));

            return { years, rows };
        }, [collaborators, evaluations, analiseFilters, departments, currentUser, evaluationTypes, globalConfigs, data.users]);

        // Este bloco processa todos os impactos de promoção e premiação para o ano selecionado.
        const calculatedProjectionData = useMemo(() => {
            // Validação de segurança para evitar erros de renderização se os dados não estiverem prontos
            if (!evaluations || !collaborators || !pcs || !currentUser) {
                return { 
                    rows: [], 
                    totalMonthly: 0, 
                    totalAnnual: 0, 
                    detalhesAnual: {}, 
                    detalhesMensal: {} 
                };
            }

            /**
             * Critério de Desempate Local para o Ranking de Promoção
             * 1. Nota Final (Base 100 para Gestão, Base 10 para Colaborador)
             * 2. Menor Pontuação Perdida (Apenas Colaboradores)
             * 3. Ordem Alfabética
             */
            const resolveTieLocal = (a, b) => {
                const scoreA = a.personType === 'Gestão' ? (a.finalScoreGestao || 0) : (a.qualitative.finalAverage || 0);
                const scoreB = b.personType === 'Gestão' ? (b.finalScoreGestao || 0) : (b.qualitative.finalAverage || 0);
                
                if (Math.abs(scoreB - scoreA) > 0.0001) {
                    return scoreB - scoreA;
                }
                
                if (a.personType === 'Colaborador') {
                    const lostA = a.quantitative?.meanPoints || 0;
                    const lostB = b.quantitative?.meanPoints || 0;
                    if (lostA !== lostB) {
                        return lostA - lostB; 
                    }
                }
                return (a.collaboratorName || '').localeCompare(b.collaboratorName || '');
            };

            // Ordena a tabela salarial (PCS) para calcular o próximo nível de promoção corretamente
            const sortedPcs = [...pcs].sort((a, b) => {
                return getPcsRank(a.role, a.seniority, a.level) - getPcsRank(b.role, b.seniority, b.level);
            });
            
            // DECLARAÇÃO ESSENCIAL: Variável para armazenar candidatos elegíveis processados
            let allCandidates = []; 

            // Recupera o Salário Mínimo mais atual para cálculos de premiação proporcional
            const currentMinWage = minWageHistory.length > 0 
                ? [...minWageHistory].sort((a, b) => new Date(b.validFrom) - new Date(a.validFrom))[0] 
                : { value: 0 };
            const minWageValue = parseFloat(currentMinWage.value || 0);
            
            /**
             * Recupera o cargo e salário do colaborador no ano da projeção
             * Verifica o histórico para garantir que o cálculo use o salário da época.
             */
            const getSnapshotByYear = (collab, year) => {
                const targetYear = parseInt(year);
                if (!collab.roleHistory || collab.roleHistory.length === 0) {
                    return { 
                        role: collab.role, 
                        seniority: collab.seniority, 
                        level: collab.level, 
                        salary: parseFloat(collab.salary || 0) 
                    };
                }

                const sortedHistory = [...collab.roleHistory].sort((a, b) => {
                    const dateA = a.validFrom ? new Date(a.validFrom).getTime() : 0;
                    const dateB = b.validFrom ? new Date(b.validFrom).getTime() : 0;
                    return dateB - dateA;
                });

                const snapshot = sortedHistory.find(h => {
                    if (!h.validFrom) return false;
                    const hYear = parseInt(String(h.validFrom).split('-')[0]);
                    return !isNaN(hYear) && hYear <= targetYear;
                });

                if (snapshot) {
                    return { 
                        role: snapshot.role, 
                        seniority: snapshot.seniority, 
                        level: snapshot.level, 
                        salary: parseFloat(snapshot.salary || 0) 
                    };
                }

                const oldest = sortedHistory[sortedHistory.length - 1];
                return { 
                    role: oldest.role, 
                    seniority: oldest.seniority, 
                    level: oldest.level, 
                    salary: parseFloat(oldest.salary || 0) 
                };
            };

            // --- FASE 1: FILTRAGEM E APLICAÇÃO DE REGRAS DE ACESSO ---
            evaluations.forEach((ev) => {
                const collab = collaborators.find(c => c.id === ev.collaboratorId);
                if (!collab || !ev.qualitative) return; 
                
                // Filtro por ano selecionado na interface
                if (ev.periodYear !== projYear) return;

                // Lógica de Permissão e Anonimização Rígida (Filtro por Usuário)
                let isVisible = false;
                let isBlind = false;

                const isSuperAdmin = currentUser.role === 'SuperAdm';
                const hasViewGestaoPerm = currentUser.permissions?.viewGestao;
                const hasBlindGestaoPerm = currentUser.permissions?.access_sub_fin_blind_gestao;

                if (collab.personType === 'Gestão') {
                    // ADM NÃO PODE VER GESTÃO DE FORMA ALGUMA NESTA TELA (Nem mesmo anonimizado)
                    if (currentUser.role === 'ADM') {
                        isVisible = false;
                    } 
                    // PRIORIDADE MÁXIMA: Se a trava de anonimização estiver ativa, ela afeta a todos permitidos
                    else if (hasBlindGestaoPerm) {
                        isVisible = true;
                        isBlind = true; // Oculta nomes e salários base
                    } 
                    // Se não houver trava de anonimato, SuperAdm e RH Senior veem tudo
                    else if (isSuperAdmin || hasViewGestaoPerm) {
                        isVisible = true;
                        isBlind = false;
                    }
                } else {
                    // Colaboradores: Vê se for SuperAdm ou se pertencer ao mesmo depto
                    if (isSuperAdmin || collab.departmentId === currentUser.departmentId) {
                        isVisible = true;
                    }
                }

                if (!isVisible) return;

                // Filtros de busca da tela (Departamento e Tipo)
                if (projDept && !isBlind && collab.departmentId !== projDept) return;
                if (projPersonType !== 'Todos' && collab.personType !== projPersonType) return;

                try {
                    // Adiciona o candidato elegível para a lista de processamento de ranking
                    allCandidates.push({ 
                        ...recalculateFinalAverage(ev), 
                        originalCollab: collab, 
                        isBlind 
                    });
                } catch (err) {
                    console.error("Erro ao processar candidato:", ev.collaboratorName, err);
                }
            });

            // --- FASE 2: DEFINIÇÃO DE VENCEDORES (RANKING POR DEPTO) ---
            const gestaoCandidates = allCandidates.filter(c => c.personType === 'Gestão');
            const collabCandidates = allCandidates.filter(c => c.personType === 'Colaborador');
            const promotionWinnersIds = new Set();

            // 1 Vaga de Promoção para a Gestão Global
            if (gestaoCandidates.length > 0) {
                gestaoCandidates.sort(resolveTieLocal);
                promotionWinnersIds.add(gestaoCandidates[0].id);
            }

            // 1 Vaga de Promoção por Departamento (Padrão para todos)
            const collabsByDept = collabCandidates.reduce((acc, c) => {
                const groupKey = c.department; // Usa o departamento real, sem verificar RLC

                if (!acc[groupKey]) acc[groupKey] = [];
                acc[groupKey].push(c);
                return acc;
            }, {});

            Object.values(collabsByDept).forEach((deptList) => {
                deptList.sort(resolveTieLocal);
                if (deptList.length > 0) {
                    promotionWinnersIds.add(deptList[0].id);
                }
            });

            // --- FASE 3: CÁLCULO DOS IMPACTOS FINANCEIROS INDIVIDUAIS ---
            let totalMonthlyPromo = 0;
            let totalAnnualAward = 0;
            const rows = [];

            allCandidates.forEach((ev) => {
                const collab = ev.originalCollab;
                const isBlind = ev.isBlind; 
                
                // Gestores vinculados
                const linkedManagers = data.managers.filter(m => collab?.managerIds && collab.managerIds.includes(m.id));
                const managersList = linkedManagers.length > 0 ? linkedManagers.map(m => m.name) : ['N/D'];

                // Busca as metas (thresholds) da regra de avaliação do colaborador
                let config = evaluationTypes.find(et => et.id === collab?.evalTypeId) || globalConfigs[0] || {};
                let score = ev.personType === 'Gestão' ? (ev.finalScoreGestao || 0) : (ev.qualitative.finalAverage || 0);
                const lost = ev.quantitative.meanPoints || 0;
                
                const mediaPromo = config.mediaPromocao || 8.0;
                const mediaPremiacao = config.mediaPremiacao || 7.0;
                const maxLost = config.maxPointsLost || 10.0;
                
                // Ajuste de escala para Gestão (Base 100) vs Colaborador (Base 10)
                const promoThreshold = ev.personType === 'Gestão' ? (mediaPromo / 10.0) * 100.0 : mediaPromo;
                const premioThreshold = ev.personType === 'Gestão' ? (mediaPremiacao / 10.0) * 100.0 : mediaPremiacao;
                const canWin = ev.personType === 'Colaborador' ? (lost <= maxLost) : true;
                
                // Verifica elegibilidade final
                const isWinner = promotionWinnersIds.has(ev.id);
                const isPromo = (score >= promoThreshold && canWin && isWinner);
                const isPremio = (score >= premioThreshold && canWin);
                
                // Aplica filtro de visualização de resultado da tela
                if (projRes === 'Promoção' && !isPromo) return;
                if (projRes === 'Premiação' && !isPremio) return;
                if (projRes === 'Promoção|Premiação' && !isPromo && !isPremio) return;

                const snapshot = getSnapshotByYear(collab, projYear);
                const currentSalary = snapshot.salary;
                const currentRoleStr = `${snapshot.role} ${snapshot.seniority || ''} ${snapshot.level || ''}`.trim();
                
                let newSalary = currentSalary;
                let promoValue = 0;
                let awardValue = 0;
                let newRoleStr = currentRoleStr;
                let newRoleParts = { role: snapshot.role, seniority: snapshot.seniority, level: snapshot.level };
                
                // Recupera o 'pulo' de nível (ex: promovido 2 níveis acima)
                const jump = levelOverrides[ev.collaboratorId] || 1;

                // Se for promovido, busca o próximo degrau na tabela PCS
                if (isPromo) {
                    const currentRank = getPcsRank(snapshot.role, snapshot.seniority, snapshot.level);
                    const potentialUpgrades = sortedPcs.filter(p => {
                        const pSal = parseFloat(p.salary);
                        return getPcsRank(p.role, p.seniority, p.level) > currentRank && pSal > (currentSalary + 0.01);
                    });

                    const targetIndex = Math.min(jump - 1, potentialUpgrades.length - 1);
                    if (potentialUpgrades.length > 0 && targetIndex >= 0) {
                        const targetPcs = potentialUpgrades[targetIndex];
                        newSalary = parseFloat(targetPcs.salary);
                        promoValue = newSalary - currentSalary;
                        newRoleStr = `${targetPcs.role} ${targetPcs.seniority} ${targetPcs.level}`;
                        newRoleParts = { 
                            role: targetPcs.role, 
                            seniority: targetPcs.seniority, 
                            level: targetPcs.level 
                        };
                    }
                }

                // Se for premiado, calcula 5% do salário ou 10% do SM (o que for maior)
                if (isPremio) {
                    const baseForPremio = newSalary > 0 ? newSalary : currentSalary;
                    const v1 = baseForPremio * 0.05;
                    const v2 = minWageValue * 0.10;
                    awardValue = Math.max(v1, v2);
                }

                // Acumuladores para o dashboard do topo
                totalMonthlyPromo += promoValue;
                totalAnnualAward += awardValue;

                // Monta o objeto de linha para a tabela de projeção
                rows.push({
                    id: ev.collaboratorId,
                    name: isBlind ? 'CONFIDENCIAL (Gestão)' : ev.collaboratorName,
                    dept: collab.personType === 'Gestão' ? 'DIRETORIA' : ev.department, 
                    managerList: isBlind ? ['-'] : managersList,
                    year: ev.periodYear,
                    role: isBlind ? '-' : currentRoleStr,
                    newRole: isBlind ? '-' : newRoleStr,
                    newRoleParts,
                    salary: isBlind ? 0 : currentSalary,
                    newSalary: isBlind ? 0 : newSalary,
                    promoDiff: promoValue,
                    award: awardValue,
                    jump: jump,
                    isBlind: isBlind
                });
            });

            // Ordenação Final: Por Departamento e depois pelo valor do custo (maior para menor)
            rows.sort((a, b) => {
                const diffDept = a.dept.localeCompare(b.dept);
                if (diffDept !== 0) return diffDept;
                return b.promoDiff - a.promoDiff;
            });

            // Calcula os impactos fiscais e reflexos (13º, Férias, FGTS) baseado nas somas
            const impacts = calculateUnifiedImpacts(totalMonthlyPromo, totalAnnualAward);

            return {
                rows,
                totalMonthly: impacts.totalMonthly,
                totalAnnual: impacts.totalAnnual,
                detalhesAnual: impacts.details,
                detalhesMensal: {
                    promoMensal: impacts.details.promoMensal / 12,
                    premiacao: impacts.details.premiacao / 12,
                    decimoTerceiro: impacts.details.decimoTerceiro / 12,
                    tercoFerias: impacts.details.tercoFerias / 12,
                    dsr: impacts.details.dsr / 12,
                    reflexoFerias13: impacts.details.reflexoFerias13 / 12,
                    fgts: impacts.details.fgts / 12
                }
            };
        }, [evaluations, pcs, minWageHistory, projYear, projDept, projRes, projPersonType, globalConfigs, evaluationTypes, collaborators, levelOverrides, permissions, currentUser, data.managers]);
        
        // ========================================================================
        // 4. HANDLERS (AÇÕES E EVENTOS)
        // ========================================================================

        // --- Drag & Drop e Resizing dos Widgets ---
        const handleDragStart = (e, index) => {
            setDraggedIdx(index);
            e.dataTransfer.effectAllowed = "move";
        };

        const handleDragEnter = (e, index) => {
            e.preventDefault();
            if (draggedIdx === null || draggedIdx === index) return;
            const newItems = [...dashboardItems];
            const draggedItem = newItems[draggedIdx];
            newItems.splice(draggedIdx, 1);
            newItems.splice(index, 0, draggedItem);
            newItems.forEach((item, i) => { item.order = i + 1; });
            setDraggedIdx(index);
            setDashboardItems(newItems);
        };
        const handleDragEnd = () => setDraggedIdx(null);

        const toggleWidgetVisibility = (id) => {
            setDashboardItems((prev) => prev.map(item => item.id === id ? { ...item, visible: !item.visible } : item));
        };

        const toggleWidgetWidth = (id) => {
            setDashboardItems((prev) => prev.map(item => item.id === id ? { ...item, w: item.w === 1 ? 2 : 1 } : item));
        };

        const handleOrderChange = (id, newOrder) => {
            const orderNum = parseInt(newOrder, 10);
            if (isNaN(orderNum)) return;
            setDashboardItems((prev) => {
                const newItems = prev.map(item => item.id === id ? { ...item, order: orderNum } : item);
                return newItems.sort((a, b) => (a.order || 99) - (b.order || 99));
            });
        };

        // --- Resizing ---
        const resizingRef = React.useRef(null);
        const stopResize = () => {
            resizingRef.current = null;
            document.removeEventListener('mousemove', handleResizeMove);
            document.removeEventListener('mouseup', stopResize);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        const handleResizeMove = (e) => {
            if (!resizingRef.current) return;
            const { id, startX, startY, startH, startW } = resizingRef.current;
            const deltaX = e.clientX - startX;
            const deltaY = e.clientY - startY;

            const newHeight = Math.max(200, startH + deltaY);
            let newWidth = startW;
            const threshold = 100;

            if (startW === 1 && deltaX > threshold) newWidth = 2;
            if (startW === 2 && deltaX < -threshold) newWidth = 1;
            setDashboardItems((prev) => prev.map(item =>
                item.id === id ? { ...item, h: newHeight, w: newWidth } : item
            ));
        };

        const startResize = (e, item) => {
            e.preventDefault();
            e.stopPropagation();
            resizingRef.current = {
                id: item.id,
                startX: e.clientX,
                startY: e.clientY,
                startH: item.h,
                startW: item.w
            };
            document.addEventListener('mousemove', handleResizeMove);
            document.addEventListener('mouseup', stopResize);
            document.body.style.cursor = 'nwse-resize';
            document.body.style.userSelect = 'none';
        };
        
        // --- Outros Handlers ---
        const handleSavePCSForm = () => {
            if (!form.role || !form.salary) return notify('error', "Preencha Cargo e Salário.");
            actions.savePCS({ ...form, id: form.id || Date.now().toString() });
            actions.logAction('EDICAO_PCS_MANUAL', {
                acao: form.id ? 'Atualização' : 'Novo Cargo',
                cargo: form.role,
                nivel: form.level,
                salario: form.salary
            });
            setIsPCSModalOpen(false);
            setForm({});
        };

        const toggleSelectPcs = (id) => {
            if (selectedPcsIds.includes(id)) {
                setSelectedPcsIds((prev) => prev.filter(i => i !== id));
            } else {
                setSelectedPcsIds((prev) => [...prev, id]);
            }
        };

        const toggleSelectAllPcs = () => {
            const allIds = filteredPcsList.map(p => p.id);
            const allSelected = allIds.every(id => selectedPcsIds.includes(id));
            if (allSelected) {
                setSelectedPcsIds((prev) => prev.filter(id => !allIds.includes(id)));
            } else {
                setSelectedPcsIds((prev) => [...new Set([...prev, ...allIds])]);
            }
        };

        const handleBulkDeletePcs = () => {
            if (selectedPcsIds.length === 0) return;
            selectedPcsIds.forEach(id => {
                actions.deletePCS(id);
            });
            actions.logAction('EXCLUSAO_EM_BLOCO_PCS', { qtd: selectedPcsIds.length, ids: selectedPcsIds });
            setSelectedPcsIds([]);
            notify('success', `${selectedPcsIds.length} cargos excluídos.`);
        };
        const handleBulkEditPcsOpen = () => {
            if (selectedPcsIds.length === 0) return;
            setBulkPcsForm({});
            setIsBulkEditPcsOpen(true);
        };

        const handleNewPCS = () => {
            setForm({});
            setIsPCSModalOpen(true);
        };
        const handleEditPCS = (item) => {
            setForm(item);
            setIsPCSModalOpen(true);
        };
        const handleMassReadjust = () => {
            if (!readjustPercent || !readjustDate) return;
            const newRecords = pcs.map((p) => ({
                ...p,
                id: Date.now() + Math.random().toString(),
                salary: parseFloat(p.salary) * (1 + parseFloat(readjustPercent) / 100),
                validFrom: readjustDate
            }));
            actions.importPCSBatch(newRecords);
            setModal(null);
        };

        const handleFileSelect = (e) => {
            if (e.target.files && e.target.files[0]) setSelectedFile(e.target.files[0]);
        };

        const handleProcessImportClick = () => {
            if (!selectedFile) return;
            setImportConfig({ targetDept: '', ignoreTerm: '' });
            setImportModalOpen(true);
        };

        const confirmImport = async () => {
            setImportModalOpen(false);
            setIsProcessing(true);
            try {
                await loadImportLibs();
                const importedData = await readImportFile(selectedFile);
                if (!importedData || importedData.length === 0) throw new Error("Arquivo vazio ou inválido.");
                const newPCS = mapImportedDataToPCS(importedData, importConfig, importDate);
                if (newPCS.length === 0) throw new Error("Nenhum cargo identificado.");
                actions.importPCSBatch(newPCS);
                setSelectedFile(null);
            } catch (e) {
                console.error(e);
                notify('error', "Erro na importação: " + e.message);
            } finally {
                setIsProcessing(false);
            }
        };

        const handleBatchPromotionClick = () => {
            const hasFuture = (id) => {
                const c = collaborators.find(x => x.id === id);
                if (!c || !c.roleHistory) return false;
                const targetYear = parseInt(projYear);
                return c.roleHistory.some((h) => {
                    if (!h.validFrom) return false;
                    const hYear = parseInt(h.validFrom.split('-')[0]);
                    return hYear > targetYear;
                });
            };
            const validCandidates = calculatedProjectionData.rows.filter(r => r.promoDiff > 0 && !hasFuture(r.id));
            if (validCandidates.length === 0) return notify('error', "Nenhuma promoção pendente.");
            const listWithSelection = validCandidates.map(c => ({ ...c, checked: true }));
            setPromoCandidates(listWithSelection);
            const nextYear = parseInt(projYear) + 1;
            setPromoEffectiveDate(`${nextYear}-01-01`);
            setIsPromoModalOpen(true);
        };
        const handleModalJumpChange = (candidateId, newJumpValue) => {
            let jump = parseInt(newJumpValue);
            if (isNaN(jump) || jump < 1) jump = 1;
            const sortedPcs = [...pcs].sort((a, b) => getPcsRank(a.role, a.seniority, a.level) - getPcsRank(b.role, b.seniority, b.level));
            setPromoCandidates((prev) => prev.map((c) => {
                if (c.id !== candidateId) return c;
                const potentialUpgrades = sortedPcs.filter((p) => {
                    const pSalary = parseFloat(p.salary);
                    const cSalary = parseFloat(c.salary);
                    return pSalary > (cSalary + 0.01);
                });
                let actualJump = jump;
                if (potentialUpgrades.length === 0) {
                    return { ...c, jump: 0, newRole: c.role, newSalary: c.salary, promoDiff: 0 };
                }
                if (jump > potentialUpgrades.length) {
                    actualJump = potentialUpgrades.length;
                }
                const targetIndex = actualJump - 1;
                const targetPcs = potentialUpgrades[targetIndex];
                return {
                    ...c,
                    jump: actualJump,
                    newSalary: parseFloat(targetPcs.salary),
                    newRole: `${targetPcs.role} ${targetPcs.seniority} ${targetPcs.level}`,
                    promoDiff: parseFloat(targetPcs.salary) - c.salary,
                    newRoleParts: { role: targetPcs.role, seniority: targetPcs.seniority, level: targetPcs.level }
                };
            }));
        };

        const toggleCandidate = (id) => {
            setPromoCandidates((prev) => prev.map(c => c.id === id ? { ...c, checked: !c.checked } : c));
        };

        const executeBatchPromotion = async () => {
            const selected = promoCandidates.filter(c => c.checked);
            if (selected.length === 0) return notify('error', "Selecione ao menos um.");
            if (!promoEffectiveDate) return notify('error', "Informe a data.");
            try {
                let processedCount = 0;
                const [y, m, d] = promoEffectiveDate.split('-');
                const dateObj = new Date(y, m - 1, d);
                dateObj.setDate(dateObj.getDate() - 1);
                const endDateOld = dateObj.toISOString().split('T')[0];

                for (const p of selected) {
                    const collab = collaborators.find(c => c.id === p.id);
                    if (!collab) continue;
                    let history = collab.roleHistory ? [...collab.roleHistory] : [];
                    if (history.length === 0) {
                        history.push({
                            id: Date.now().toString() + "_old",
                            role: collab.role,
                            seniority: collab.seniority,
                            level: collab.level,
                            salary: parseFloat(collab.salary),
                            validFrom: collab.startDate || '2024-01-01',
                            endDate: endDateOld
                        });
                    } else {
                        history.sort((a, b) => {
                            const dA = a.validFrom || '';
                            const dB = b.validFrom || '';
                            return dB.localeCompare(dA);
                        });
                        if (history[0]) {
                            history[0].endDate = endDateOld;
                        }
                    }
                    const roleParts = p.newRoleParts || { role: p.newRole.split(' ')[0], seniority: 'N/A', level: 'I' };
                    const newEntry = {
                        id: Date.now().toString() + "_" + Math.random().toString(36).substr(2, 5),
                        role: roleParts.role,
                        seniority: roleParts.seniority,
                        level: roleParts.level,
                        salary: parseFloat(p.newSalary),
                        validFrom: promoEffectiveDate,
                        endDate: ''
                    };
                    history.push(newEntry);
                    const updatedCollab = {
                        ...collab,
                        role: roleParts.role,
                        seniority: roleParts.seniority,
                        level: roleParts.level,
                        salary: parseFloat(p.newSalary),
                        roleHistory: history
                    };
                    await actions.saveCollab(updatedCollab);
                    actions.logAction('PROMOCAO_APLICADA', {
                        colaborador: collab.name,
                        de: collab.role,
                        para: roleParts.role,
                        novoSalario: p.newSalary,
                        vigencia: promoEffectiveDate
                    });
                    processedCount++;
                }
                setIsPromoModalOpen(false);
                notify('success', `Sucesso! ${processedCount} promoções.`);
            } catch (error) {
                console.error(error);
                notify('error', "Erro: " + error.message);
            }
        };
        const handleUndoClick = (collabId) => {
            setUndoTargetId(collabId);
        };
        const processUndo = async () => {
            if (!undoTargetId) return;
            const collab = collaborators.find(c => c.id === undoTargetId);
            if (!collab) return;
            try {
                const rawHistory = collab.roleHistory || [];
                const sortedHistory = [...rawHistory].sort((a, b) => {
                    const dA = a.validFrom || '';
                    const dB = b.validFrom || '';
                    return dB.localeCompare(dA);
                });
                if (sortedHistory.length < 2) {
                    notify('error', "Histórico insuficiente.");
                    setUndoTargetId(null);
                    return;
                }
                const recordToRestore = sortedHistory[1];
                const newHistory = sortedHistory.slice(1);
                if (newHistory[0]) {
                    newHistory[0].endDate = '';
                }
                const updatedCollab = {
                    ...collab,
                    role: recordToRestore.role,
                    seniority: recordToRestore.seniority,
                    level: recordToRestore.level,
                    salary: parseFloat(recordToRestore.salary),
                    roleHistory: newHistory
                };
                await actions.saveCollab(updatedCollab);
                actions.logAction('PROMOCAO_DESFEITA', { colaborador: collab.name, retornouPara: recordToRestore.role });
                notify('success', "Desfeito com sucesso.");
            } catch (error) {
                console.error(error);
                notify('error', error.message);
            } finally {
                setUndoTargetId(null);
            }
        };

        // --- NOVA FUNÇÃO DE EXPORTAÇÃO PDF COM QUADRO DETALHADO (CORRIGIDA: CENTRALIZAÇÃO E VALORES ABSOLUTOS) ---
        const exportEstimativaPDF = async () => {
            try {
                if (typeof loadPdfLibs !== 'undefined') await loadPdfLibs();
                const PDFConstructor = (window.jspdf && window.jspdf.jsPDF) ? window.jspdf.jsPDF : window.jsPDF;
                if (!PDFConstructor) throw new Error("Biblioteca PDF não disponível.");
                
                const doc = new PDFConstructor('l', 'mm', 'a4');
                const logoUrl = globalConfigs?.[0]?.logoUrl;
                let startY = 20;

                // --- 1. CABEÇALHO ---
                if (logoUrl) {
                    try {
                        doc.addImage(logoUrl, 'PNG', 14, 10, 25, 25);
                        doc.setFontSize(16);
                        doc.setFont("helvetica", "bold");
                        doc.text("Estimativa Financeira - 2025", 45, 25);
                        startY = 45;
                    } catch (e) {
                        doc.text("Estimativa Financeira - 2025", 14, 20);
                    }
                } else {
                    doc.setFontSize(16);
                    doc.setFont("helvetica", "bold");
                    doc.text("Estimativa Financeira - 2025", 14, 20);
                    startY = 35;
                }

                // Prepara os grupos de dados
                const groups = calculatedProjectionData.rows.reduce((acc, curr) => {
                    if (!acc[curr.dept]) acc[curr.dept] = [];
                    acc[curr.dept].push(curr);
                    return acc;
                }, {});
                
                const visibleDepts = Object.keys(groups);

                // --- 2. QUADRO RESUMO (DETALHAMENTO DE CUSTOS) ---
                if (visibleDepts.length > 0) {
                    doc.setFontSize(14);
                    doc.text("Detalhamento de Custos por Departamento", 14, startY);
                    startY += 8;

                    const detailHeaders = [["Item"]];
                    const detailData = [
                        ["Aumento Salarial"], 
                        ["Premiação"], 
                        ["13º Salário"], 
                        ["1/3 Férias"], 
                        ["DSR"], 
                        ["Reflexo Fér/13º"], 
                        ["SUBTOTAL (Sem Encargos)"], // NOVA LINHA
                        ["ENCARGOS (FGTS 8%)"],      // NOVA LINHA
                        ["CUSTO TOTAL (Com Encargos)"] // NOVA LINHA DE TOTAL FINAL
                    ];

                    // Totalizadores Gerais
                    let grandTotalMonthly = 0;
                    let grandTotalAnnual = 0;

                    // Prepara colunas dinâmicas para cada departamento
                    visibleDepts.forEach(dept => {
                        // Cabeçalho do Depto
                        detailHeaders[0].push({ content: dept, colSpan: 2, styles: { halign: 'center', fontStyle: 'bold', fillColor: [52, 73, 94], textColor: [255, 255, 255] } });
                        
                        // Calcula totais do departamento
                        const itens = groups[dept];
                        const sumPromo = itens.reduce((acc, i) => acc + i.promoDiff, 0);
                        const sumAward = itens.reduce((acc, i) => acc + i.award, 0);
                        const impacts = calculateUnifiedImpacts(sumPromo, sumAward);

                        grandTotalMonthly += impacts.totalMonthly;
                        grandTotalAnnual += impacts.totalAnnual;
                        
                        // Adiciona valores nas linhas (USANDO formatCurrencyAbs PARA REMOVER NEGATIVOS)
                        detailData[0].push(formatCurrencyAbs(impacts.details.promoMensal), formatCurrencyAbs(impacts.details.promoMensal/12));
                        detailData[1].push(formatCurrencyAbs(impacts.details.premiacao), formatCurrencyAbs(impacts.details.premiacao/12));
                        detailData[2].push(formatCurrencyAbs(impacts.details.decimoTerceiro), formatCurrencyAbs(impacts.details.decimoTerceiro/12));
                        detailData[3].push(formatCurrencyAbs(impacts.details.tercoFerias), formatCurrencyAbs(impacts.details.tercoFerias/12));
                        detailData[4].push(formatCurrencyAbs(impacts.details.dsr), formatCurrencyAbs(impacts.details.dsr/12));
                        detailData[5].push(formatCurrencyAbs(impacts.details.reflexoFerias13), formatCurrencyAbs(impacts.details.reflexoFerias13/12));
                        
                        // SUBTOTAL
                        detailData[6].push(formatCurrencyAbs(impacts.subtotalSemEncargos), formatCurrencyAbs(impacts.subtotalSemEncargos/12));

                        // ENCARGOS
                        detailData[7].push(formatCurrencyAbs(impacts.details.fgts), formatCurrencyAbs(impacts.details.fgts/12));

                        // TOTAL FINAL
                        detailData[8].push(formatCurrencyAbs(impacts.totalAnnual), formatCurrencyAbs(impacts.totalMonthly));
                    });

                    // Coluna de Total Geral (Empresa)
                    if (visibleDepts.length > 1) {
                        detailHeaders[0].push({ content: "TOTAL GERAL", colSpan: 2, styles: { halign: 'center', fontStyle: 'bold', fillColor: [44, 62, 80], textColor: [255, 255, 255] } });
                        
                        // Recalcula tudo para o total geral
                        const totalPromo = visibleDepts.reduce((acc, d) => acc + groups[d].reduce((s, i) => s + i.promoDiff, 0), 0);
                        const totalAward = visibleDepts.reduce((acc, d) => acc + groups[d].reduce((s, i) => s + i.award, 0), 0);
                        const totalImpacts = calculateUnifiedImpacts(totalPromo, totalAward);

                        const pushTotal = (rowIdx, valAnual, valMensal) => {
                            detailData[rowIdx].push(formatCurrencyAbs(valAnual), formatCurrencyAbs(valMensal));
                        };

                        pushTotal(0, totalImpacts.details.promoMensal, totalImpacts.details.promoMensal/12);
                        pushTotal(1, totalImpacts.details.premiacao, totalImpacts.details.premiacao/12);
                        pushTotal(2, totalImpacts.details.decimoTerceiro, totalImpacts.details.decimoTerceiro/12);
                        pushTotal(3, totalImpacts.details.tercoFerias, totalImpacts.details.tercoFerias/12);
                        pushTotal(4, totalImpacts.details.dsr, totalImpacts.details.dsr/12);
                        pushTotal(5, totalImpacts.details.reflexoFerias13, totalImpacts.details.reflexoFerias13/12);
                        pushTotal(6, totalImpacts.subtotalSemEncargos, totalImpacts.subtotalSemEncargos/12);
                        pushTotal(7, totalImpacts.details.fgts, totalImpacts.details.fgts/12);
                        pushTotal(8, totalImpacts.totalAnnual, totalImpacts.totalMonthly);
                    }

                    // Cria sub-headers (Anual / Mensal)
                    const subHeaders = [""];
                    for(let i=0; i < (visibleDepts.length + (visibleDepts.length > 1 ? 1 : 0)); i++) {
                        subHeaders.push("Anual", "Mensal");
                    }
                    
                    // Renderiza tabela complexa
                    doc.autoTable({
                        startY: startY,
                        head: detailHeaders.concat([subHeaders]),
                        body: detailData,
                        theme: 'grid',
                        // AQUI: Força o alinhamento central para todas as células do corpo
                        styles: { fontSize: 7, halign: 'center', cellPadding: 1.5 },
                        // AQUI: Mantém a primeira coluna (labels) alinhada à esquerda
                        columnStyles: { 0: { halign: 'left', fontStyle: 'bold', cellWidth: 35, fillColor: [240, 240, 240] } },
                        headStyles: { fillColor: [52, 152, 219], halign: 'center', valign: 'middle' },
                        didParseCell: (data) => {
                            // Estilo Zebra (Linhas Alternadas)
                            if (data.section === 'body' && data.row.index % 2 === 0) {
                                data.cell.styles.fillColor = [249, 250, 251];
                            }
                            
                            // Destaque Subtotal
                            if (data.row.index === 6) {
                                data.cell.styles.fontStyle = 'bold';
                                data.cell.styles.fillColor = [230, 230, 230];
                            }
                            // Destaque Encargos
                            if (data.row.index === 7) {
                                data.cell.styles.fontStyle = 'italic';
                                data.cell.styles.textColor = [100, 100, 100];
                            }
                            // Destaque Total Final
                            if (data.row.index === 8) {
                                data.cell.styles.fontStyle = 'bold';
                                data.cell.styles.fillColor = [220, 252, 231]; // Verde claro
                                data.cell.styles.textColor = [21, 128, 61]; // Verde escuro
                            }
                            
                            // Destaque Coluna Mensal (Visual Block)
                            if (data.column.index > 0 && data.column.index % 2 === 0) {
                                if (data.section === 'head' && data.row.index === 1) {
                                    data.cell.styles.fillColor = [229, 231, 235]; 
                                    data.cell.styles.textColor = [0,0,0];
                                }
                            }
                        }
                    });

                    startY = doc.lastAutoTable.finalY + 15;
                }

                // --- 3. TABELA DETALHADA DE COLABORADORES ---
                doc.addPage(); // Quebra de página para separar o resumo da lista
                doc.setFontSize(14);
                doc.text("Listagem de Colaboradores (Projeção)", 14, 20);
                startY = 25;

                const tableBody = [];
                Object.entries(groups).forEach(([dept, itens]) => {
                    tableBody.push([{ content: dept, colSpan: 6, styles: { fillColor: [240, 240, 240], fontStyle: 'bold' } }]);
                    itens.forEach((row) => {
                        const managerStr = row.managerList ? row.managerList.join(' / ') : '';
                        tableBody.push([row.name, row.role, formatCurrency(row.salary), formatCurrency(row.newSalary), formatCurrency(row.promoDiff), formatCurrency(row.award)]);
                    });
                    const sumPromo = itens.reduce((acc, i) => acc + i.promoDiff, 0);
                    const sumAward = itens.reduce((acc, i) => acc + i.award, 0);
                    const groupImpacts = calculateComplexImpact(sumPromo, sumAward);
                    tableBody.push([{ content: `Subtotal ${dept}`, colSpan: 4, styles: { halign: 'right', fontStyle: 'bold' } }, formatCurrency(sumPromo), formatCurrency(sumAward)]);
                    tableBody.push([{ content: `TOTAL ${dept} - IMPACTO ANUAL:`, colSpan: 4, styles: { halign: 'right', fontStyle: 'bold', fillColor: [220, 252, 231], textColor: [21, 128, 61] } }, { content: formatCurrency(groupImpacts.totalAnnual), colSpan: 2, styles: { halign: 'center', fontStyle: 'bold', fillColor: [220, 252, 231], textColor: [21, 128, 61] } }]);
                    tableBody.push([{ content: `TOTAL ${dept} - IMPACTO MENSAL:`, colSpan: 4, styles: { halign: 'right', fontStyle: 'bold', fillColor: [219, 234, 254], textColor: [30, 64, 175] } }, { content: formatCurrency(groupImpacts.totalMensal), colSpan: 2, styles: { halign: 'center', fontStyle: 'bold', fillColor: [219, 234, 254], textColor: [30, 64, 175] } }]);
                });
                doc.autoTable({ head: [['Colaborador', 'Cargo', 'Salário Atual', 'Novo Salário', 'Custo Mensal', 'Custo Anual']], body: tableBody, startY: startY, theme: 'grid', styles: { fontSize: 8 }, headStyles: { fillColor: [41, 128, 185] } });
                
                doc.save(`Estimativa_${projYear}.pdf`);
            } catch (e) {
                console.error(e);
                notify('error', "Erro ao exportar PDF.");
            }
        };

        const exportEstimativaExcel = async () => {
            try {
                if (typeof loadImportLibs !== 'undefined') await loadImportLibs();
                if (!window.XLSX) throw new Error("Biblioteca XLSX não disponível.");
                const dataExport = [];
                const groups = calculatedProjectionData.rows.reduce((acc, curr) => {
                    if (!acc[curr.dept]) acc[curr.dept] = [];
                    acc[curr.dept].push(curr);
                    return acc;
                }, {});
                Object.entries(groups).forEach(([dept, itens]) => {
                    itens.forEach((item) => {
                        dataExport.push({
                            Departamento: dept,
                            Nome: item.name,
                            Cargo: item.role,
                            'Salário Novo': item.newSalary,
                            'Aumento Mensal': item.promoDiff,
                            'Prêmio Anual': item.award
                        });
                    });
                    const sumPromo = itens.reduce((acc, i) => acc + i.promoDiff, 0);
                    const sumAward = itens.reduce((acc, i) => acc + i.award, 0);
                    const groupImpacts = calculateComplexImpact(sumPromo, sumAward);
                    dataExport.push({
                        Departamento: `TOTAL ${dept}`,
                        Nome: '',
                        Cargo: '',
                        'Salário Novo': '',
                        'Aumento Mensal': sumPromo,
                        'Prêmio Anual': sumAward
                    });
                    dataExport.push({
                        Departamento: `TOTAL ${dept} - IMPACTO ANUAL (12m + Prêmios)`,
                        Nome: '',
                        Cargo: '',
                        'Salário Novo': '',
                        'Aumento Mensal': groupImpacts.totalAnnual,
                        'Prêmio Anual': ''
                    });
                    dataExport.push({
                        Departamento: `TOTAL ${dept} - IMPACTO MENSAL (Folha)`,
                        Nome: '',
                        Cargo: '',
                        'Salário Novo': '',
                        'Aumento Mensal': groupImpacts.totalMensal,
                        'Prêmio Anual': ''
                    });
                    dataExport.push({});
                });
                const ws = window.XLSX.utils.json_to_sheet(dataExport);
                const wb = window.XLSX.utils.book_new();
                window.XLSX.utils.book_append_sheet(wb, ws, "Estimativa");
                window.XLSX.writeFile(wb, `Estimativa_Financeira_${projYear}.xlsx`);
            } catch (e) {
                console.error(e);
                notify('error', "Erro ao exportar Excel.");
            }
        };
        const handleConfirmSync = async () => {
            if (!syncYearInput || syncYearInput.length !== 4) return notify('error', "Ano inválido.");
            setIsUpdatingSheet(true);
            try {
                const result = await actions.syncTrainingsGlobal(syncYearInput);
                setIsUpdatingSheet(false);
                setIsSyncModalOpen(false);
                notify('success', `Sucesso! ${result.count} registros importados.`);
            } catch (error) {
                console.error(error);
                notify('error', "Erro: " + error.message);
                setIsUpdatingSheet(false);
            }
        };

        // --- CORREÇÃO: FUNÇÃO DE CÁLCULO PARA O WIDGET DE COMPARAÇÃO ---
        const getFinancialsForContext = useCallback((targetYear, targetDeptId) => {
            // 1. Filtra avaliações do ano solicitado
            const targetEvals = evaluations.filter(ev => ev.periodYear === targetYear);
            
            let totalPromoMensal = 0;
            let totalPremioAnual = 0;

            // Ordenação de PCS para cálculo de promoção
            const sortedPcs = [...pcs].sort((a, b) => getPcsRank(a.role, a.seniority, a.level) - getPcsRank(b.role, b.seniority, b.level));

            targetEvals.forEach(ev => {
                const collab = collaborators.find(c => c.id === ev.collaboratorId);
                if (!collab) return;

                // Filtros de Escopo (Departamento)
                if (targetDeptId) {
                    if (targetDeptId === 'gestao_virtual') {
                        if (collab.personType !== 'Gestão') return;
                    } else {
                        if (collab.departmentId !== targetDeptId) return;
                    }
                }
                
                // Respeita filtro de tipo global se não for 'Todos'
                if (projPersonType !== 'Todos' && collab.personType !== projPersonType) return;

                // Recalcula nota
                const evCalc = recalculateFinalAverage(ev);
                
                // Regras (Config)
                let config = evaluationTypes.find(et => et.id === collab.evalTypeId) || globalConfigs.find(gc => gc.startDate.startsWith(targetYear)) || globalConfigs[0] || {};
                
                const isGestao = evCalc.personType === 'Gestão';
                let score = isGestao ? (evCalc.finalScoreGestao || 0) : (evCalc.qualitative.finalAverage || 0);
                const lost = isGestao ? 0 : (evCalc.quantitative.meanPoints || 0);
                
                const mediaPromo = config.mediaPromocao || 8.0;
                const mediaPremiacao = config.mediaPremiacao || 7.0;
                const maxLost = config.maxPointsLost || 10.0;

                const promoThreshold = isGestao ? (mediaPromo / 10.0) * 100.0 : mediaPromo;
                const premioThreshold = isGestao ? (mediaPremiacao / 10.0) * 100.0 : mediaPremiacao;
                const canWin = isGestao ? true : (lost <= maxLost);

                const isPromo = score >= promoThreshold && canWin;
                const isPremio = score >= premioThreshold && canWin;

                // Tenta achar salário histórico ou usa atual
                let baseSalary = parseFloat(collab.salary || 0);
                if (collab.roleHistory && collab.roleHistory.length > 0) {
                    const historyForYear = collab.roleHistory
                        .sort((a,b) => new Date(b.validFrom) - new Date(a.validFrom))
                        .find(h => parseInt(h.validFrom.split('-')[0]) <= parseInt(targetYear));
                    if (historyForYear) baseSalary = parseFloat(historyForYear.salary);
                }

                // Cálculo Promoção
                let promoVal = 0;
                if (isPromo) {
                    const currentRank = getPcsRank(collab.role, collab.seniority, collab.level);
                    const nextPcs = sortedPcs.find(p => getPcsRank(p.role, p.seniority, p.level) > currentRank);
                    if (nextPcs) {
                        const nextSal = parseFloat(nextPcs.salary);
                        if (nextSal > baseSalary) promoVal = nextSal - baseSalary;
                    }
                }

                // Cálculo Premiação
                let awardVal = 0;
                if (isPremio) {
                    const salarioRef = baseSalary + promoVal;
                    const mwEpoch = minWageHistory
                        .sort((a,b) => new Date(b.validFrom) - new Date(a.validFrom))
                        .find(m => parseInt(m.validFrom.split('-')[0]) <= parseInt(targetYear))?.value || 1412;
                    
                    const v1 = salarioRef * 0.05; 
                    const v2 = parseFloat(mwEpoch) * 0.10;
                    awardVal = Math.max(v1, v2);
                }

                totalPromoMensal += promoVal;
                totalPremioAnual += awardVal;
            });

            return calculateUnifiedImpacts(totalPromoMensal, totalPremioAnual);

        }, [evaluations, collaborators, pcs, evaluationTypes, globalConfigs, minWageHistory, projPersonType]);

        // --- Widgets Map ---
        const availableWidgets = {    
        financialSummary: (
                <div className={`h-full w-full overflow-hidden flex flex-col rounded-xl border shadow-sm ${isDarkMode ? '!bg-slate-900 !border-slate-700' : 'bg-white border-gray-200'}`}>
                    {/* Header */}
                    <div className={`p-3 border-b flex flex-col gap-3 shrink-0 ${isDarkMode ? '!bg-slate-900 !border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex justify-between items-center">
                            <h3 className={`font-bold flex items-center gap-2 ${isDarkMode ? '!text-slate-100' : 'text-gray-800'}`}>
                                <BadgeDollarSign size={18} className="text-emerald-500"/> Resumo Financeiro Detalhado
                            </h3>
                            <div className="flex items-center gap-2">
                                <span className={`text-xs font-bold ${isDarkMode ? '!text-slate-400' : 'text-gray-500'}`}>Ano Base:</span>
                                <input type="number" value={projYear} onChange={(e) => setProjYear(e.target.value)} className={`w-16 h-7 text-center text-xs rounded border outline-none focus:ring-1 focus:ring-blue-500 ${isDarkMode ? '!bg-slate-800 !border-slate-600 !text-white' : 'bg-white border-gray-300 text-gray-700'}`} />
                            </div>
                        </div>
                        {/* Controles de Comparação */}
                        <div className={`flex items-center flex-wrap gap-4 text-xs p-2 rounded border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input type="checkbox" checked={isComparing} onChange={(e) => setIsComparing(e.target.checked)} className="rounded text-blue-600 focus:ring-blue-500" />
                                <span className={`font-bold ${isComparing ? 'text-blue-500' : (isDarkMode ? 'text-slate-400' : 'text-gray-600')}`}>Comparar Períodos</span>
                            </label>
                            {isComparing && (
                                <>
                                    <div className={`w-px h-4 ${isDarkMode ? 'bg-slate-600' : 'bg-gray-300'}`}></div>
                                    <div className="flex items-center gap-2">
                                        <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Escopo:</span>
                                        <select value={compScope} onChange={e => setCompScope(e.target.value)} className={`p-1 rounded border outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-700'}`}>
                                            <option value="total">Total (Empresa)</option>
                                            <option value="dept">Departamento</option>
                                        </select>
                                    </div>
                                    {compScope === 'dept' && (
                                        <select value={compDeptId} onChange={e => setCompDeptId(e.target.value)} className={`p-1 rounded border outline-none max-w-[150px] ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-700'}`}>
                                            <option value="">Selecione...</option>
                                            <option value="gestao_virtual">Gestão (Todos)</option>
                                            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                        </select>
                                    )}
                                    <div className={`w-px h-4 ${isDarkMode ? 'bg-slate-600' : 'bg-gray-300'}`}></div>
                                    <div className="flex items-center gap-2">
                                        <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Anos:</span>
                                        <div className="flex gap-1">
                                            {Array.from({length: 5}, (_, i) => String(parseInt(projYear) - (i + 1))).map(y => (
                                                <button key={y} onClick={() => toggleCompYear(y)} disabled={!compYears.includes(y) && compYears.length >= 4} className={`px-2 py-0.5 rounded border transition-colors ${compYears.includes(y) ? (isDarkMode ? 'bg-blue-600 text-white border-blue-600' : 'bg-blue-600 text-white border-blue-600') : (isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-600 hover:bg-slate-700' : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200')} disabled:opacity-30 disabled:cursor-not-allowed`}>{y}</button>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Tabela */}
                    <div className="flex-1 overflow-auto custom-scrollbar">
                        <table className={`w-full text-xs text-right border-collapse ${isDarkMode ? '!text-slate-300' : 'text-gray-700'}`}>
                            {isComparing ? (
                                /* --- MODO COMPARAÇÃO --- */
                                <>
                                    <thead className={`sticky top-0 z-20 font-bold uppercase shadow-md ${isDarkMode ? '!bg-indigo-950 !text-indigo-100' : 'bg-indigo-600 text-white'}`}>
                                        <tr>
                                            <th className={`p-3 text-left w-48 border-r ${isDarkMode ? '!border-indigo-900' : 'border-indigo-500'}`}>Item de Custo</th>
                                            <th className={`p-2 text-center border-l bg-opacity-20 bg-white`}>{projYear} <span className="text-[9px] opacity-70 block">(Base)</span></th>
                                            {compYears.sort((a,b) => b-a).map(year => <th key={year} className={`p-2 text-center border-l ${isDarkMode ? '!border-indigo-900' : 'border-indigo-500'}`}>{year}</th>)}
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? '!divide-slate-800' : 'divide-gray-200'}`}>
                                        {(() => {
                                            // Filtra os dados base respeitando o filtro da tela "Análise Gerencial"
                                            const filterDeptName = analiseFilters.dept ? departments.find(d => d.id === analiseFilters.dept)?.name : null;
                                            
                                            // Recalcula Base se houver filtros ativos na tela
                                            let baseDataToUse = calculatedProjectionData;
                                            if (filterDeptName || analiseFilters.personType !== 'Todos') {
                                                const filteredRows = calculatedProjectionData.rows.filter(r => {
                                                    if (filterDeptName && r.dept !== filterDeptName) return false;
                                                    const c = collaborators.find(col => col.id === r.id);
                                                    if (analiseFilters.personType !== 'Todos' && c && c.personType !== analiseFilters.personType) return false;
                                                    return true;
                                                });
                                                // Recalcula totais dos filtrados
                                                const sumPromo = filteredRows.reduce((acc, i) => acc + i.promoDiff, 0);
                                                const sumAward = filteredRows.reduce((acc, i) => acc + i.award, 0);
                                                const impacts = calculateUnifiedImpacts(sumPromo, sumAward);
                                                baseDataToUse = { detalhesAnual: impacts.details, subtotalSemEncargos: impacts.subtotalSemEncargos, totalAnnual: impacts.totalAnnual };
                                            }

                                            const baseData = (compScope === 'total' || compDeptId === '') 
                                                ? (baseDataToUse.detalhesAnual || baseDataToUse) // Fallback seguro
                                                : getFinancialsForContext(projYear, compDeptId)?.details;

                                            const safeBase = baseData || { promoMensal: 0, premiacao: 0, decimoTerceiro: 0, tercoFerias: 0, dsr: 0, reflexoFerias13: 0, fgts: 0 };
                                            
                                            // Se baseDataToUse for o objeto completo com totais, usa eles, senão calcula
                                            const baseSub = baseDataToUse.subtotalSemEncargos !== undefined ? baseDataToUse.subtotalSemEncargos : (safeBase.promoMensal + safeBase.premiacao + safeBase.decimoTerceiro + safeBase.tercoFerias + safeBase.dsr + safeBase.reflexoFerias13);
                                            const baseTotal = baseDataToUse.totalAnnual !== undefined ? baseDataToUse.totalAnnual : (baseSub + safeBase.fgts);
                                            const baseWrapper = { ...safeBase, subtotal: baseSub, total: baseTotal };

                                            const compDataMap = {};
                                            compYears.forEach(y => {
                                                // Aplica filtros também na comparação se estiverem ativos na tela
                                                let targetD = compScope === 'dept' ? compDeptId : (analiseFilters.dept || null);
                                                const raw = getFinancialsForContext(y, targetD);
                                                compDataMap[y] = raw ? { ...raw.details, subtotal: raw.subtotalSemEncargos, total: raw.totalAnnual } : null;
                                            });

                                            const rowsData = [
                                                { label: "Aumento Salarial", key: "promoMensal" }, { label: "Premiação", key: "premiacao" },
                                                { label: "13º Salário", key: "decimoTerceiro" }, { label: "1/3 Férias", key: "tercoFerias" },
                                                { label: "DSR", key: "dsr" }, { label: "Reflexo Fér/13º", key: "reflexoFerias13" },
                                                { label: "SUBTOTAL", key: "subtotal", isSubtotal: true },
                                                { label: "FGTS (8%)", key: "fgts" }, { label: "TOTAL CUSTO ANUAL", key: "total", isTotal: true }
                                            ];

                                            return rowsData.map((rowItem, rIdx) => {
                                                let rowBgClass = isDarkMode ? '!bg-slate-900' : 'bg-white';
                                                let textClass = isDarkMode ? '!text-slate-300' : 'text-gray-700';
                                                if (rowItem.isTotal) { rowBgClass = isDarkMode ? '!bg-emerald-950' : 'bg-green-100'; textClass = isDarkMode ? '!text-emerald-400 font-black' : 'text-green-800 font-black'; }
                                                else if (rowItem.isSubtotal) { rowBgClass = isDarkMode ? '!bg-slate-800' : 'bg-gray-100'; textClass = isDarkMode ? '!text-white font-bold' : 'text-gray-900 font-bold'; }

                                                return (
                                                    <tr key={rIdx} className={`${rowBgClass} transition-colors hover:brightness-110`}>
                                                        <td className={`p-3 text-left border-r ${isDarkMode ? '!border-slate-800' : 'border-gray-200'} ${textClass}`}>{rowItem.label}</td>
                                                        <td className={`p-2 border-l font-bold ${isDarkMode ? '!border-slate-800' : 'border-gray-200'} ${textClass}`}>{formatCurrencyAbs(baseWrapper[rowItem.key])}</td>
                                                        {compYears.sort((a,b) => b-a).map(year => {
                                                            const val = compDataMap[year] ? compDataMap[year][rowItem.key] : 0;
                                                            const diff = (baseWrapper[rowItem.key] || 0) - val;
                                                            const colorDiff = diff > 0 ? 'text-red-500' : diff < 0 ? 'text-green-500' : 'text-gray-400';
                                                            return (
                                                                <td key={year} className={`p-2 border-l ${isDarkMode ? '!border-slate-800' : 'border-gray-200'} ${textClass}`}>
                                                                    <div>{formatCurrencyAbs(val)}</div>
                                                                    {val > 0 && <div className={`text-[9px] ${colorDiff}`}>Dif: {diff > 0 ? '+' : ''}{formatCurrencyAbs(diff)}</div>}
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                );
                                            });
                                        })()}
                                    </tbody>
                                </>
                            ) : (
                                /* --- MODO PADRÃO (Sem comparação) - CORRIGIDO PARA FILTROS --- */
                                <>
                                    <thead className={`sticky top-0 z-20 font-bold uppercase shadow-md ${isDarkMode ? '!bg-blue-950 !text-blue-100' : 'bg-blue-600 text-white'}`}>
                                        <tr>
                                            <th className={`p-3 text-left w-48 border-r ${isDarkMode ? '!border-blue-900 !bg-blue-950' : 'border-blue-500'}`}>Item</th>
                                            {(() => {
                                                // LÓGICA DE FILTRO APLICADA AQUI
                                                const filterDeptName = analiseFilters.dept ? departments.find(d => d.id === analiseFilters.dept)?.name : null;
                                                
                                                const groups = calculatedProjectionData.rows.reduce((acc, curr) => {
                                                    // Aplica filtro de Departamento
                                                    if (filterDeptName && curr.dept !== filterDeptName) return acc;
                                                    // Aplica filtro de Tipo Pessoa
                                                    if (analiseFilters.personType !== 'Todos') {
                                                        const c = collaborators.find(col => col.id === curr.id);
                                                        if (c && c.personType !== analiseFilters.personType) return acc;
                                                    }
                                                    
                                                    if (!acc[curr.dept]) acc[curr.dept] = []; acc[curr.dept].push(curr); return acc;
                                                }, {});
                                                const visibleDepts = Object.keys(groups).sort();
                                                const deptHeaders = visibleDepts.map(dept => <th key={dept} colSpan={2} className={`p-2 text-center border-l ${isDarkMode ? '!border-blue-900 !bg-blue-950' : 'border-blue-400'}`}>{dept}</th>);
                                                if (visibleDepts.length > 1) deptHeaders.push(<th key="total-geral" colSpan={2} className={`p-2 text-center border-l ${isDarkMode ? '!border-blue-900 !bg-blue-900 !text-emerald-300' : 'border-blue-400 bg-blue-800'}`}>TOTAL GERAL</th>);
                                                return deptHeaders;
                                            })()}
                                        </tr>
                                        <tr className={`text-[10px] ${isDarkMode ? '!bg-slate-800 !text-slate-400' : 'bg-blue-700 text-blue-100'}`}>
                                            <th className={`p-1 border-r ${isDarkMode ? '!border-slate-700 !bg-slate-800' : 'border-blue-500'}`}></th>
                                            {(() => {
                                                // REPETIÇÃO DA LÓGICA DE FILTRO PARA A SEGUNDA LINHA DO CABEÇALHO
                                                const filterDeptName = analiseFilters.dept ? departments.find(d => d.id === analiseFilters.dept)?.name : null;
                                                
                                                const groups = calculatedProjectionData.rows.reduce((acc, curr) => {
                                                    if (filterDeptName && curr.dept !== filterDeptName) return acc;
                                                    if (analiseFilters.personType !== 'Todos') {
                                                        const c = collaborators.find(col => col.id === curr.id);
                                                        if (c && c.personType !== analiseFilters.personType) return acc;
                                                    }
                                                    if (!acc[curr.dept]) acc[curr.dept] = []; acc[curr.dept].push(curr); return acc;
                                                }, {});
                                                const visibleDepts = Object.keys(groups).sort();
                                                const headers = [];
                                                visibleDepts.forEach(dept => { headers.push(<th key={`${dept}-anual`} className={`p-1 text-center border-l min-w-[90px] ${isDarkMode ? '!border-slate-700 !bg-slate-800' : 'border-blue-500'}`}>Anual</th>); headers.push(<th key={`${dept}-mensal`} className={`p-1 text-center border-l min-w-[90px] ${isDarkMode ? '!border-slate-700 !bg-slate-800' : 'border-blue-500'}`}>Mensal</th>); });
                                                if (visibleDepts.length > 1) { headers.push(<th key="total-anual" className={`p-1 text-center border-l min-w-[90px] ${isDarkMode ? '!border-slate-700 !bg-slate-800/80' : 'border-blue-500 bg-blue-800'}`}>Anual</th>); headers.push(<th key="total-mensal" className={`p-1 text-center border-l min-w-[90px] ${isDarkMode ? '!border-slate-700 !bg-slate-800/80' : 'border-blue-500 bg-blue-800'}`}>Mensal</th>); }
                                                return headers;
                                            })()}
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? '!divide-slate-800' : 'divide-gray-200'}`}>
                                        {(() => {
                                            // REPETIÇÃO DA LÓGICA DE FILTRO PARA O CORPO DA TABELA
                                            const filterDeptName = analiseFilters.dept ? departments.find(d => d.id === analiseFilters.dept)?.name : null;

                                            const groups = calculatedProjectionData.rows.reduce((acc, curr) => {
                                                if (filterDeptName && curr.dept !== filterDeptName) return acc;
                                                if (analiseFilters.personType !== 'Todos') {
                                                    const c = collaborators.find(col => col.id === curr.id);
                                                    if (c && c.personType !== analiseFilters.personType) return acc;
                                                }
                                                if (!acc[curr.dept]) acc[curr.dept] = []; acc[curr.dept].push(curr); return acc;
                                            }, {});
                                            const visibleDepts = Object.keys(groups).sort();
                                            
                                            if (visibleDepts.length === 0) return <tr><td colSpan={20} className="p-8 text-center text-gray-400">Nenhum dado disponível com os filtros selecionados.</td></tr>;

                                            const rowsData = [ { label: "Aumento Salarial", key: "promoMensal" }, { label: "Premiação", key: "premiacao" }, { label: "13º Salário", key: "decimoTerceiro" }, { label: "1/3 Férias", key: "tercoFerias" }, { label: "DSR", key: "dsr" }, { label: "Reflexo Fér/13º", key: "reflexoFerias13" }, { label: "SUBTOTAL", key: "subtotal", isSubtotal: true }, { label: "FGTS (8%)", key: "fgts" }, { label: "TOTAL CUSTO", key: "total", isTotal: true } ];
                                            
                                            // Recalcula totais gerais baseado apenas no visível
                                            let totalPromo = 0;
                                            let totalAward = 0;
                                            if (visibleDepts.length > 1) {
                                                totalPromo = visibleDepts.reduce((acc, d) => acc + groups[d].reduce((s, i) => s + i.promoDiff, 0), 0);
                                                totalAward = visibleDepts.reduce((acc, d) => acc + groups[d].reduce((s, i) => s + i.award, 0), 0);
                                            }
                                            const totalImpacts = calculateUnifiedImpacts(totalPromo, totalAward);

                                            return rowsData.map((rowItem, rIdx) => {
                                                let rowBgClass = isDarkMode ? '!bg-slate-900' : 'bg-white';
                                                let textClass = isDarkMode ? '!text-slate-300' : 'text-gray-700';
                                                if (rowItem.isTotal) { rowBgClass = isDarkMode ? '!bg-emerald-950' : 'bg-green-100'; textClass = isDarkMode ? '!text-emerald-400 font-black' : 'text-green-800 font-black'; }
                                                else if (rowItem.isSubtotal) { rowBgClass = isDarkMode ? '!bg-slate-800' : 'bg-gray-100'; textClass = isDarkMode ? '!text-white font-bold' : 'text-gray-900 font-bold'; }
                                                else if (rIdx % 2 !== 0) { rowBgClass = isDarkMode ? '!bg-slate-800/50' : 'bg-gray-50'; }

                                                return (
                                                    <tr key={rIdx} className={`${rowBgClass} transition-colors hover:brightness-110`}>
                                                        <td className={`p-3 text-left border-r ${isDarkMode ? '!border-slate-800' : 'border-gray-200'} ${textClass}`}>{rowItem.label}</td>
                                                        {visibleDepts.map(dept => {
                                                            const itens = groups[dept];
                                                            const sumPromo = itens.reduce((acc, i) => acc + i.promoDiff, 0);
                                                            const sumAward = itens.reduce((acc, i) => acc + i.award, 0);
                                                            const impacts = calculateUnifiedImpacts(sumPromo, sumAward);
                                                            let valAnual = 0, valMensal = 0;
                                                            if (rowItem.key === 'subtotal') { valAnual = impacts.subtotalSemEncargos; valMensal = impacts.subtotalSemEncargos / 12; }
                                                            else if (rowItem.key === 'total') { valAnual = impacts.totalAnnual; valMensal = impacts.totalMonthly; }
                                                            else { valAnual = impacts.details[rowItem.key]; valMensal = impacts.details[rowItem.key] / 12; }
                                                            return (<React.Fragment key={dept}><td className={`p-2 border-l ${isDarkMode ? '!border-slate-800' : 'border-gray-200'} ${textClass}`}>{formatCurrencyAbs(valAnual)}</td><td className={`p-2 border-l ${isDarkMode ? '!border-slate-800' : 'border-gray-200'} ${textClass}`}>{formatCurrencyAbs(valMensal)}</td></React.Fragment>);
                                                        })}
                                                        {visibleDepts.length > 1 && (
                                                            <><td className={`p-2 border-l font-bold ${isDarkMode ? '!border-slate-800 !bg-slate-800/30' : 'border-gray-200 bg-gray-50 text-gray-900'} ${rowItem.isTotal ? (isDarkMode ? '!text-emerald-400' : '!text-green-800') : (isDarkMode ? '!text-white' : '')}`}>{formatCurrencyAbs(rowItem.key === 'subtotal' ? totalImpacts.subtotalSemEncargos : rowItem.key === 'total' ? totalImpacts.totalAnnual : totalImpacts.details[rowItem.key])}</td>
                                                            <td className={`p-2 border-l font-bold ${isDarkMode ? '!border-slate-800 !bg-slate-800/30' : 'border-gray-200 bg-gray-50 text-gray-900'} ${rowItem.isTotal ? (isDarkMode ? '!text-emerald-400' : '!text-green-800') : (isDarkMode ? '!text-white' : '')}`}>{formatCurrencyAbs(rowItem.key === 'subtotal' ? totalImpacts.subtotalSemEncargos / 12 : rowItem.key === 'total' ? totalImpacts.totalMonthly : totalImpacts.details[rowItem.key] / 12)}</td></>
                                                        )}
                                                    </tr>
                                                );
                                            });
                                        })()}
                                    </tbody>
                                </>
                            )}
                        </table>
                    </div>
                </div>
            ),

            distribution: (
                <div className="h-full w-full flex flex-col overflow-hidden">
                    <DepartmentDistributionTable 
                        data={analiseData.deptAnalysis} 
                        totalEmployees={analiseData.totalEmployees} 
                        totalPayroll={analiseData.totalPayroll} 
                        isDarkMode={isDarkMode} 
                    />
                </div>
            ),
            pieChart: (
                <div className={`h-full w-full rounded-xl shadow-sm border overflow-hidden flex flex-col ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                    <h3 className={`font-bold text-lg p-4 border-b flex items-center gap-2 shrink-0 ${isDarkMode ? 'bg-slate-900 border-slate-700 text-slate-200' : 'bg-slate-50 border-gray-100 text-gray-800'}`}>
                        <Users size={18} /> Distribuição por Departamento
                    </h3>
                    
                    <div className="flex-1 min-h-0 relative">
                        <DepartmentPieChart data={analiseData.deptAnalysis} isDarkMode={isDarkMode} />
                    </div>
                </div>
            ),
            roleCost: <RoleCostAnalysis data={data} currentUser={currentUser} analiseFilters={analiseFilters} isDarkMode={isDarkMode} />,
            history5y: (
                <Card className="overflow-hidden h-full flex flex-col" darkMode={isDarkMode}>
                    <div className={`flex justify-between items-center mb-4 px-4 pt-4 shrink-0 ${isDarkMode ?
                        'text-slate-200' : 'text-gray-800'}`}>
                        <h3 className="font-bold text-lg flex items-center gap-2">
                            <History size={18} /> Comparativo de Desempenho (5 Anos)
                        </h3>
                        <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>
                            Legenda: <span className="text-blue-600 font-bold">Qualitativa</span> |
                            <span className="text-green-600 font-bold">Quantitativa</span>
                        </span>
                    </div>
                    <div className="flex-1 overflow-y-auto px-4 pb-4">
                        <table className={`w-full text-sm text-left border-collapse ${isDarkMode ?
                            'text-slate-300' : ''}`}>
                            <thead className={`font-bold sticky top-0 z-10 shadow-sm ${isDarkMode ?
                                'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-600'}`}>
                                <tr>
                                    <th className={`p-3 border-r w-64 min-w-[200px] ${isDarkMode ?
                                        'border-slate-700' : 'border-gray-200'}`}>Colaborador / Depto</th>
                                    {comparativeData.years.map((year) => (
                                        <th key={year} className={`p-3 text-center min-w-[140px] border-r last:border-0 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>{year}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDarkMode ?
                                'divide-slate-700' : 'divide-gray-200'}`}>
                                {comparativeData.rows.map((row, i) => (
                                    <tr key={row.id} className={`${i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50/40')} ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-blue-50/30'} transition-colors`}>
                                        <td className={`p-3 border-r bg-transparent sticky left-0 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] ${isDarkMode ? 'bg-inherit border-slate-700' : 'bg-inherit border-gray-200'}`}>
                                            <div className={`font-bold truncate w-56 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{row.name}</div>
                                            <div className={`text-[10px] truncate font-medium ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>{row.role} • {row.dept}</div>
                                        </td>
                                        {comparativeData.years.map((year) => {
                                            const data = row.yearsData[year];
                                            if (!data) return <td key={year} className={`p-3 text-center border-r ${isDarkMode ? 'border-slate-700 text-slate-600' : 'border-gray-200 text-gray-300'}`}>-</td>;
                                            return (
                                                <td key={year} className={`p-2 text-center border-r align-top ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                                    <div className="flex flex-col gap-2">
                                                        <div className="h-6 flex items-center justify-center cursor-help"
                                                            onMouseEnter={(e) => {
                                                                if (data.feedbacks && Object.keys(data.feedbacks).length > 0) {
                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                    setTooltip({ 
                                                                        type: 'feedbacks', 
                                                                        data: { year, feedbacks: data.feedbacks }, 
                                                                        x: rect.left + rect.width / 2, 
                                                                        y: rect.top 
                                                                    });
                                                                }
                                                            }}
                                                            onMouseLeave={() => setTooltip(null)}
                                                        >
                                                            {data.label ?
                                                            (
                                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide shadow-sm border ${data.label === 'Promoção' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 border-blue-700' : 'bg-blue-100 text-blue-700 border-blue-200') : (isDarkMode ? 'bg-green-900/50 text-green-300 border-green-700' : 'bg-green-100 text-green-700 border-green-200')}`}>
                                                                    {data.label}
                                                                </span>
                                                            ) : (
                                                                <span className={`text-[9px] ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`}>-</span>
                                                            )}
                                                        </div>
                                                        
                                                        <div className={`rounded p-1.5 border space-y-1 ${isDarkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-white/50 border-gray-200'}`}>
                                                            <div className="flex justify-between items-center text-xs">
                                                                <span className={`text-[9px] font-bold ${isDarkMode ?
                                                                    'text-slate-400' : 'text-gray-400'}`}>Qualitativa</span>
                                                                <div className={`flex items-center gap-1 px-1.5 rounded border min-w-[65px] justify-between ${isDarkMode ?
                                                                    'bg-slate-800 border-slate-600' : 'bg-white border-gray-100'}`}>
                                                                    <span className="font-bold text-blue-600">{data.quali.toFixed(4)}</span>
                                                                    <div className="w-3 flex justify-center">
                                                                        {data.trendQuali === 'up' && <TrendingUp size={12} className="text-green-500" />}
                                                                        {data.trendQuali === 'down' && <TrendingDown size={12} className="text-red-500" />}
                                                                        {data.trendQuali === 'stable' && <Minus size={12} className="text-yellow-500" />}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="flex justify-between items-center text-xs">
                                                                <span className={`text-[9px] font-bold ${isDarkMode ?
                                                                    'text-slate-400' : 'text-gray-400'}`}>Quantitativa</span>
                                                                <div className={`flex items-center gap-1 px-1.5 rounded border min-w-[65px] justify-between ${isDarkMode ?
                                                                    'bg-slate-800 border-slate-600' : 'bg-white border-gray-100'}`}>
                                                                    <span className="font-bold text-green-600">{data.quanti.toFixed(4)}</span>
                                                                    <div className="w-3 flex justify-center">
                                                                        {data.trendQuanti === 'up' && <TrendingUp size={12} className={!data.isGestao ?
                                                                        "text-red-500" : "text-green-500"} />}
                                                                        {data.trendQuanti === 'down' && <TrendingDown size={12} className={!data.isGestao ?
                                                                        "text-green-500" : "text-red-500"} />}
                                                                        {data.trendQuanti === 'stable' && <Minus size={12} className="text-yellow-500" />}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            ),
            history8q: (
                <div className="h-full overflow-hidden flex flex-col">
                    <QuarterlyEvolutionTable data={data} currentUser={currentUser} analiseFilters={analiseFilters} isDarkMode={isDarkMode} />
                </div>
            )
        };

        // --- 1. HELPERS DE TOOLTIP (ADICIONAR) ---
        const calculateTooltipPos = (rect) => {
            const popupHeight = 250; 
            const spaceBelow = window.innerHeight - rect.bottom;
            let yPos;
            if (spaceBelow < popupHeight + 10) {
                yPos = rect.top - 10;
                return { x: rect.left + rect.width / 2, y: yPos, position: 'top' };
            } else {
                yPos = rect.bottom + 5;
                return { x: rect.left + rect.width / 2, y: yPos, position: 'bottom' };
            }
        };

        const onHoverCardDetails = (e, cardType, data) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const pos = calculateTooltipPos(rect);
            
            let xPos = pos.x;
            // Ajuste para não sair da tela à direita
            if (xPos + 150 > window.innerWidth) xPos = window.innerWidth - 160;
            
            setTooltip({ 
                x: xPos, 
                y: pos.y, 
                placement: pos.position,
                type: 'details', 
                data: { ...data, type: cardType } 
            });
        };


        // --- 2. MÉTRICAS DETALHADAS POR TRIMESTRE (REFORMULADO: 5 CARDS) ---
        const financeMetrics = useMemo(() => {
            const stats = { 
                total: { q1: 0, q2: 0, q3: 0, q4: 0, details: { q1: [], q2: [], q3: [], q4: [], type: 'total' } }, 
                pendConclusion: { q1: 0, q2: 0, q3: 0, q4: 0, details: { q1: [], q2: [], q3: [], q4: [], type: 'pendConclusion' } }, // 2) Vermelho
                pendGestor: { q1: 0, q2: 0, q3: 0, q4: 0, details: { q1: [], q2: [], q3: [], q4: [], type: 'pendGestor' } },         // 3) Laranja
                pendColab: { q1: 0, q2: 0, q3: 0, q4: 0, details: { q1: [], q2: [], q3: [], q4: [], type: 'pendColab' } },           // 4) Verde
                ready: { q1: 0, q2: 0, q3: 0, q4: 0, details: { q1: [], q2: [], q3: [], q4: [], type: 'ready' } }                    // 5) Teal (Prontas)
            };

            filteredEvaluations.forEach(ev => {
                // Definições de Estado
                const isArchived = ev.status === 'Concluída Financeiro';
                // "Concluída no Dashboard" = T4 Fechado, Finalizada ou já Arquivada no Financeiro
                const isDashboardFinished = ev.status === 'T4-Concluido' || ev.status === 'Finalizada' || isArchived;
                
                const hasManagerSig = ev.managerSignature || (ev.managerSignatures && ev.managerSignatures.length > 0);
                const hasCollabSig = ev.collabSignature || (ev.collabSignatures && Object.keys(ev.collabSignatures).length > 0);

                ['q1', 'q2', 'q3', 'q4'].forEach((q) => {
                    // 1) TOTAL AVALIAÇÕES (Independente de status)
                    stats.total[q]++;
                    stats.total.details[q].push({ name: ev.collaboratorName, status: ev.status });

                    // 2) PENDENTE CONCLUSÃO (Vermelho)
                    // Ainda não foi finalizada pelo gestor no Dashboard (está em T1, T2, T3 ou Em Andamento)
                    if (!isDashboardFinished) {
                        stats.pendConclusion[q]++;
                        stats.pendConclusion.details[q].push({ name: ev.collaboratorName, status: ev.status });
                    }

                    // 3) PENDENTE GESTOR (Laranja Escuro)
                    // Concluída no Dashboard, mas SEM assinatura do gestor
                    if (isDashboardFinished && !hasManagerSig) {
                        stats.pendGestor[q]++;
                        stats.pendGestor.details[q].push({ name: ev.collaboratorName, status: 'Falta Assinatura Gestor' });
                    }

                    // 4) PENDENTE COLABORADOR (Verde)
                    // Concluída no Dashboard, COM assinatura gestor, SEM assinatura colaborador
                    if (isDashboardFinished && hasManagerSig && !hasCollabSig) {
                        stats.pendColab[q]++;
                        stats.pendColab.details[q].push({ name: ev.collaboratorName, status: 'Falta Assinatura Colab.' });
                    }

                    // 5) PRONTAS ARQUIVADAS (Teal)
                    // Concluída, Assinada por AMBOS (Pronta para arquivar ou já arquivada)
                    if (isDashboardFinished && hasManagerSig && hasCollabSig) {
                        stats.ready[q]++;
                        stats.ready.details[q].push({ name: ev.collaboratorName, status: isArchived ? 'Arquivada' : 'Pronta p/ Arquivar' });
                    }
                });
            });

            return stats;
        }, [filteredEvaluations]);


                // --- MAPA DE CORES PARA OS CARDS ---
                    const colorClasses = { 
                        blue: { bg: 'bg-blue-600', border: 'border-blue-500' }, 
                        orange: { bg: 'bg-orange-600', border: 'border-orange-500' }, 
                        green: { bg: 'bg-green-600', border: 'border-green-500' }, 
                        teal: { bg: 'bg-teal-600', border: 'border-teal-500' }, 
                        red: { bg: 'bg-red-600', border: 'border-red-500' },
                        indigo: { bg: 'bg-indigo-600', border: 'border-indigo-500' },
                        yellow: { bg: 'bg-yellow-500', border: 'border-yellow-400', text: 'text-black' }
                    };

    // --- GERAÇÃO DE PDF COMPLETA (VERSÃO FINAL EXPANDIDA) ---
        const generatePDF = useCallback(async (evParam, typeParam) => {
            // 1. Definição do Alvo
            const evRaw = evParam || selectedEval;
            const type = typeParam || 'Completo';

            // Validação inicial
            if (!evRaw) {
                return;
            }

            setIsGenerating(true);

            try {
                // ===========================================================
                // 1. LÓGICA DE FILTRAGEM DE DADOS (SEGURANÇA / HIGIENE)
                // ===========================================================
                
                // Cria uma cópia profunda para não alterar a visualização na tela
                const ev = JSON.parse(JSON.stringify(evRaw));

                // Determina até qual trimestre está OFICIALMENTE LIBERADO no PDF.
                // Regra de segurança: trimestre com dado lançado, mas ainda sem assinatura, NÃO aparece.
                // Isso evita que ocorrências/notas do trimestre seguinte vazem antes do fechamento.
                const mgrSigs = Array.isArray(ev.managerSignatures)
                    ? ev.managerSignatures
                    : (ev.managerSignature ? [ev.managerSignature] : []);

                const getSignedQuarterNumber = (sig) => {
                    const ref = String(sig?.statusRef || sig?.quarter || sig?.status || '').toUpperCase();
                    if (ref.includes('T4') || ref.includes('4º') || ref.includes('FINALIZADA') || ref.includes('ASSINADA')) return 4;
                    if (ref.includes('T3') || ref.includes('3º')) return 3;
                    if (ref.includes('T2') || ref.includes('2º')) return 2;
                    if (ref.includes('T1') || ref.includes('1º')) return 1;
                    return 0;
                };

                let maxAllowedQ = mgrSigs.reduce((maxQ, sig) => Math.max(maxQ, getSignedQuarterNumber(sig)), 0);

                // Arquivamento financeiro pressupõe ciclo integralmente concluído.
                if (ev.status === 'Concluída Financeiro') maxAllowedQ = 4;

                const getQuarterNumberFromValue = (quarterValue, dateValue, timestampValue) => {
                    const raw = String(quarterValue || '').toUpperCase();
                    if (raw.includes('Q4') || raw.includes('T4') || raw.includes('4º')) return 4;
                    if (raw.includes('Q3') || raw.includes('T3') || raw.includes('3º')) return 3;
                    if (raw.includes('Q2') || raw.includes('T2') || raw.includes('2º')) return 2;
                    if (raw.includes('Q1') || raw.includes('T1') || raw.includes('1º')) return 1;

                    let d = null;
                    if (dateValue) d = new Date(String(dateValue).length === 10 ? `${dateValue}T12:00:00` : dateValue);
                    else if (timestampValue) d = new Date(timestampValue);
                    if (!d || Number.isNaN(d.getTime())) return 0;
                    return Math.floor(d.getMonth() / 3) + 1;
                };

                const formatPdfGrade = (value) => {
                    if (value === null || value === undefined || value === '') return '-';

                    const num = Number(value);
                    if (!isNaN(num)) return num.toFixed(1);

                    return String(value);
                };

                const formatPdfCount = (value) => {
                    if (value === null || value === undefined || value === '') return '-';

                    const num = Number(value);
                    if (!isNaN(num)) return String(num);

                    return String(value);
                };

                const getQuarterWeightedPoints = (error, q) => {
                    const qtd = Number(error?.[q] || 0);
                    const peso = Number(error?.peso || 0);
                    return qtd * peso;
                };

                // Aplica a limpeza dos dados futuros (não assinados)
                if (maxAllowedQ < 4) {
                    // Limpa Critérios Qualitativos
                    if (ev.qualitative && ev.qualitative.criteria) {
                        ev.qualitative.criteria.forEach(c => {
                            if (maxAllowedQ < 1) {
                                c.q1 = null;
                            }
                            if (maxAllowedQ < 2) {
                                c.q2 = null;
                            }
                            if (maxAllowedQ < 3) {
                                c.q3 = null;
                            }
                            if (maxAllowedQ < 4) {
                                c.q4 = null;
                            }
                        });
                    }
                    
                    // Limpa Erros Quantitativos
                    if (ev.quantitative && ev.quantitative.errors) {
                        ev.quantitative.errors.forEach(e => {
                            if (maxAllowedQ < 1) {
                                e.q1 = 0;
                            }
                            if (maxAllowedQ < 2) {
                                e.q2 = 0;
                            }
                            if (maxAllowedQ < 3) {
                                e.q3 = 0;
                            }
                            if (maxAllowedQ < 4) {
                                e.q4 = 0;
                            }
                        });
                    }
                    
                    // Limpa Metas de Gestão
                    if (ev.gestaoMetasPercepcao) {
                        ev.gestaoMetasPercepcao.forEach(item => {
                            if (item.scores) {
                                if (maxAllowedQ < 1) {
                                    item.scores.q1 = null;
                                }
                                if (maxAllowedQ < 2) {
                                    item.scores.q2 = null;
                                }
                                if (maxAllowedQ < 3) {
                                    item.scores.q3 = null;
                                }
                                if (maxAllowedQ < 4) {
                                    item.scores.q4 = null;
                                }
                            }
                        });
                    }
                }

                // Recalcula as notas finais baseadas APENAS nos dados filtrados
                const evWithScores = recalculateFinalAverage(ev);
                const isGestao = ev.personType === 'Gestão';

                // ===========================================================
                // 2. INICIALIZAÇÃO DO PDF
                // ===========================================================

                if (typeof loadPdfLibs !== 'undefined') {
                    await loadPdfLibs();
                } else if (!window.jspdf) {
                    throw new Error("Biblioteca PDF não carregada. Verifique sua conexão.");
                }

                const PDFConstructor = (window.jspdf && window.jspdf.jsPDF) ? window.jspdf.jsPDF : window.jsPDF;
                const doc = new PDFConstructor('p', 'mm', 'a4');
                
                const margin = 14;
                let y = 20;

                // Dados Auxiliares para Cabeçalho
                const collabData = data.collaborators.find(c => c.id === ev.collaboratorId);
                
                let realDeptName = ev.department;
                if (collabData) {
                    const deptObj = data.departments.find(d => d.id === collabData.departmentId);
                    if (deptObj) {
                        realDeptName = deptObj.name;
                    }
                }

                const linkedManagers = data.managers.filter(m => {
                    return collabData && collabData.managerIds && collabData.managerIds.includes(m.id);
                });
                
                const managerNameFallback = linkedManagers.length > 0 
                    ? linkedManagers.map(m => m.name).join(' / ') 
                    : 'Gestor(a) Responsável';

                // ===========================================================
                // 3. HELPERS DE DESENHO (FUNÇÕES INTERNAS)
                // ===========================================================

                // Helper: Tabela de Percepção (Página 2)
                const drawPerceptionSection = (startYPosition) => {
                    const perceptionCols = (data.perceptionCycles || []).sort((a, b) => {
                        return (a.order || 0) - (b.order || 0);
                    });
                    
                    if (perceptionCols.length === 0) {
                        return startYPosition;
                    }

                    const targetEmail = collabData ? collabData.email.toLowerCase() : '';
                    
                    const rawRecords = (data.teamPerception || []).filter(p => {
                        const pManagerEmail = p.manager_email ? p.manager_email.toLowerCase() : '';
                        const hasDate = p.date && p.date.endsWith(ev.periodYear);
                        return pManagerEmail === targetEmail && hasDate;
                    });

                    if (rawRecords.length === 0) {
                        return startYPosition;
                    }

                    const tableBody = rawRecords.map(rec => {
                        const row = [rec.date, String(rec.nota || '-')];
                        perceptionCols.forEach(col => {
                            const val = rec[col.linkedField || 'nota'] || '-';
                            row.push(String(val));
                        });
                        return row;
                    });

                    if (startYPosition + 40 > doc.internal.pageSize.getHeight()) { 
                        doc.addPage('a4', 'l'); 
                        startYPosition = 20; 
                    }

                    doc.setFontSize(12); 
                    doc.setTextColor(230, 81, 0); 
                    doc.setFont('helvetica', 'bold');
                    doc.text("DETALHAMENTO: PERCEPÇÃO DA EQUIPE", 14, startYPosition);
                    startYPosition += 6;

                    const headers = ["DATA", "NOTA", ...perceptionCols.map(c => c.description)];

                    doc.autoTable({
                    startY: startYPosition,
                    head: [headers],
                    body: tableBody,
                    theme: 'grid',
                    styles: { 
                        fontSize: 7, 
                        cellPadding: 2, 
                        overflow: 'linebreak',
                        valign: 'middle' // Garante que o texto fique no meio verticalmente
                    },
                    headStyles: { 
                        fillColor: [239, 108, 0], 
                        textColor: [255, 255, 255], 
                        halign: 'center', 
                        fontStyle: 'bold' 
                    },
                    columnStyles: {
                        1: { halign: 'center', fontStyle: 'bold' }, // Coluna 1 (NOTA) centralizada
                    },
                    didParseCell: (data) => {
                        // Aplicamos a lógica em todas as colunas de dados da tabela (seção body)
                        if (data.section === 'body') {
                            const cellValue = String(data.cell.raw).trim();

                            // Mapeamento completo de cores para garantir que todos os campos pintem
                            if (cellValue.includes('Insatisfatório')) {
                                data.cell.styles.fillColor = [244, 67, 54]; // Vermelho
                                data.cell.styles.textColor = [255, 255, 255]; // Letra Branca
                            } else if (cellValue.includes('Regular')) {
                                data.cell.styles.fillColor = [230, 81, 0]; // Laranja
                                data.cell.styles.textColor = [255, 255, 255];
                            } else if (cellValue.includes('Bom') && !cellValue.includes('Muito Bom')) {
                                data.cell.styles.fillColor = [245, 127, 23]; // Amarelo Escuro
                                data.cell.styles.textColor = [255, 255, 255];
                            } else if (cellValue.includes('Muito Bom')) {
                                data.cell.styles.fillColor = [46, 125, 50]; // Verde
                                data.cell.styles.textColor = [255, 255, 255];
                            } else if (cellValue.includes('Excelente')) {
                                data.cell.styles.fillColor = [21, 101, 192]; // Azul
                                data.cell.styles.textColor = [255, 255, 255];
                            }

                            // Centralizar apenas a coluna da NOTA (índice 1)
                            if (data.column.index === 1) {
                                data.cell.styles.halign = 'center';
                            }
                        }
                    }
                });
                    return doc.lastAutoTable.finalY + 10;
                };

            // Helper: Quadro de Assinaturas (CORRIGIDO: INVALID DATE + LINHAS EXTRAS)
                const drawSignatureTable = (startYPosition) => {
                    const year = evRaw.periodYear; 
                    
                    const headers = [
                        "TIPO ASSINATURA", 
                        `1º TRIM/${year}`, 
                        `2º TRIM/${year}`, 
                        `3º TRIM/${year}`, 
                        `4º TRIM/${year}`
                    ];

                    const formatSigCell = (sigData) => {
                        if (!sigData) {
                            return "Aguardando assinatura...";
                        }
                        
                        let dateStr = '-';
                        
                        // --- CORREÇÃO DE DATA (INVALID DATE) ---
                        // 1. Se já vier como string formatada BR (ex: "25/10/2023, 14:00")
                        if (sigData.date && typeof sigData.date === 'string' && sigData.date.includes('/')) {
                            // Pega apenas a parte da data, ignorando a hora após a vírgula ou espaço
                            dateStr = sigData.date.split(',')[0].split(' ')[0].trim();
                        } 
                        // 2. Se for ISO (YYYY-MM-DD) ou Timestamp numérico
                        else if (sigData.date) {
                            try {
                                const d = new Date(sigData.date);
                                // Verifica se a data é válida
                                if (!isNaN(d.getTime())) {
                                    dateStr = d.toLocaleDateString('pt-BR');
                                } else {
                                    dateStr = String(sigData.date); // Fallback: mostra como está
                                }
                            } catch (e) {
                                dateStr = '-';
                            }
                        }

                        return `Assinado em: ${dateStr}\n\n${sigData.name}\nID: ${sigData.id || 'N/D'}`;
                    };

                    // 1. Linha GESTOR
                    const mgrRow = ["ASSINATURA\nGESTOR"];
                    let mgrSigs = [];
                    if (evRaw.managerSignatures) {
                        mgrSigs = evRaw.managerSignatures;
                    } else if (evRaw.managerSignature) {
                        mgrSigs = [evRaw.managerSignature];
                    }
                    
                    const quartersMap = [['T1', '1º'], ['T2', '2º'], ['T3', '3º'], ['T4', '4º', 'FINALIZADA', 'ASSINADA']];

                    quartersMap.forEach(qTags => {
                        const signaturesFound = mgrSigs.filter(s => {
                            const ref = (s.statusRef || '').toUpperCase();
                            return qTags.some(tag => ref.includes(tag));
                        });

                        if (signaturesFound.length > 0) {
                            const textContent = signaturesFound.map(sig => 
                                // Passa o objeto completo para o formatador corrigido
                                formatSigCell(sig).replace('Assinado em: ', '') // Pequeno ajuste para evitar duplicar texto se necessário, ou usar direto
                            ).join('\n----------------\n');
                            
                            // Reconstrói a string formatada usando o helper (mais seguro)
                            // Como pode haver múltiplas, pegamos a mais recente ou formatamos individualmente
                            const formattedSigs = signaturesFound.map(sig => {
                                // Extração manual da data corrigida para este loop específico
                                let dStr = '-';
                                if (sig.date && typeof sig.date === 'string' && sig.date.includes('/')) dStr = sig.date.split(',')[0].split(' ')[0].trim();
                                else if (sig.date) try { dStr = new Date(sig.date).toLocaleDateString('pt-BR'); } catch(e){}
                                
                                return `Assinado em: ${dStr}\n${sig.gestorName || sig.name}\n(ID: ${sig.gestorId || sig.id})`;
                            }).join('\n----\n');

                            mgrRow.push(formattedSigs);
                        } else {
                            mgrRow.push("Aguardando assinatura\nGestor");
                        }
                    });

                    // 2. Linha COLABORADOR
                    const colRow = ["ASSINATURA\nCOLABORADOR"];
                    const colSigs = evRaw.collabSignatures || {};
                    
                    ['q1', 'q2', 'q3', 'q4'].forEach(qKey => {
                        const sig = colSigs[qKey];
                        if (sig) {
                            // Garante email
                            let emailDisplay = sig.email;
                            if (!emailDisplay) {
                                const cRegister = data.collaborators.find(c => c.id === ev.collaboratorId);
                                if (cRegister) emailDisplay = cRegister.email;
                            }
                            colRow.push(formatSigCell({
                                date: sig.date,
                                name: sig.name || ev.collaboratorName, 
                                id: sig.id
                            }));
                        } else {
                            colRow.push("Aguardando assinatura\nColaborador");
                        }
                    });

                    // --- 3. Linha DIRETORIA ---
                    const dirSig = evRaw.diretoriaSignature;
                    const dirRow = [
                        "ASSINATURA\nDIRETORIA",
                        "-", "-", "-", // T1-T3
                        dirSig ? formatSigCell(dirSig) : "Aguardando Validação" // T4
                    ];

                    // --- 4. Linha RH ---
                    const rhSig = evRaw.rhSignature;
                    const rhRow = [
                        "ASSINATURA\nRH",
                        "-", "-", "-", // T1-T3
                        rhSig ? formatSigCell(rhSig) : "Aguardando Validação" // T4
                    ];

                    // Montagem do Corpo
                    const tableBody = [mgrRow, colRow];
                    if (evRaw.personType !== 'Gestão') {
                        tableBody.push(dirRow);
                    }
                    tableBody.push(rhRow);

                    if (startYPosition + 70 > doc.internal.pageSize.getHeight()) {
                        doc.addPage();
                        startYPosition = 20;
                    }

                    doc.autoTable({
                        startY: startYPosition,
                        head: [headers],
                        body: tableBody,
                        theme: 'grid',
                        styles: { 
                            fontSize: 6, 
                            cellPadding: 2, 
                            valign: 'middle', 
                            halign: 'center', 
                            lineWidth: 0.1, 
                            lineColor: [200, 200, 200],
                            overflow: 'linebreak' 
                        },
                        headStyles: { 
                            fillColor: [30, 58, 138], 
                            textColor: [255, 255, 255], 
                            fontStyle: 'bold', 
                            halign: 'center',
                            minCellHeight: 8
                        },
                        columnStyles: {
                            0: { fontStyle: 'bold', fillColor: [243, 244, 246], cellWidth: 30, valign: 'middle' }, 
                            1: { cellWidth: 40 }, 
                            2: { cellWidth: 40 }, 
                            3: { cellWidth: 40 }, 
                            4: { cellWidth: 40 }
                        },
                        didParseCell: (data) => {
                            if (data.section === 'body') {
                                if (data.column.index > 0 && typeof data.cell.raw === 'string' && data.cell.raw.includes("Aguardando")) {
                                    data.cell.styles.textColor = [150, 150, 150];
                                    data.cell.styles.fontStyle = 'italic';
                                }
                                if (data.row.index >= 2 && data.column.index === 0) { 
                                    data.cell.styles.fillColor = [230, 230, 230];
                                }
                            }
                        }
                    });

                    return doc.lastAutoTable.finalY + 10;
                };

                // ===========================================================
                // 4. CONSTRUÇÃO DO CORPO DO PDF
                // ===========================================================

                // Cabeçalho e Logo
                const logoUrl = data.globalConfigs && data.globalConfigs[0] ? data.globalConfigs[0].logoUrl : null;
                
                if (logoUrl) {
                    try {
                        doc.addImage(logoUrl, 'PNG', 14, 10, 25, 25);
                        doc.setFontSize(11); 
                        doc.setFont("helvetica", "bold"); 
                        doc.text("AEC - ACOMPANHAMENTO DE EVOLUÇÃO CONTINUADA", 105, 25, { align: "center" });
                        y = 45;
                    } catch (e) {
                        doc.setFillColor(18, 169, 149); 
                        doc.rect(0, 0, 210, 30, 'F'); 
                        doc.setTextColor(255, 255, 255); 
                        doc.setFontSize(11); 
                        doc.setFont("helvetica", "bold"); 
                        doc.text("AEC - ACOMPANHAMENTO DE EVOLUÇÃO CONTINUADA", margin, 20);
                        y = 40;
                    }
                } else {
                    doc.setFillColor(18, 169, 149); 
                    doc.rect(0, 0, 210, 30, 'F'); 
                    doc.setTextColor(255, 255, 255); 
                    doc.setFontSize(11); 
                    doc.setFont("helvetica", "bold"); 
                    doc.text("AEC - ACOMPANHAMENTO DE EVOLUÇÃO CONTINUADA", margin, 20);
                    y = 40;
                }

                // Tabela de Identificação
                const identificationData = [
                    ["Colaborador:", ev.collaboratorName, "Cargo:", ev.role || '-'],
                    ["Departamento:", realDeptName, "Gestor(es):", managerNameFallback],
                    ["Ano Avaliado:", ev.periodYear, "Tipo:", ev.evalTypeName || 'Padrão']
                ];

                doc.autoTable({
                    startY: y, 
                    body: identificationData, 
                    theme: 'grid',
                    styles: { 
                        fontSize: 9, 
                        cellPadding: 1.5, 
                        lineColor: [200, 200, 200], 
                        textColor: [0,0,0] 
                    },
                    columnStyles: { 
                        0: { fontStyle: 'bold', fillColor: [240, 240, 240], cellWidth: 30 }, 
                        1: { cellWidth: 70 }, 
                        2: { fontStyle: 'bold', fillColor: [240, 240, 240], cellWidth: 25 }, 
                        3: { cellWidth: 'auto' } 
                    }
                });
                
                y = doc.lastAutoTable.finalY + 10;

                // ===========================================================
                // 5. CONTEÚDO BASEADO NO TIPO (SIMPLES / COMPLETO)
                // ===========================================================
                
                // CASO A: SIMPLIFICADO GESTÃO
                if (type === 'Simplificado Gestão' && isGestao) {
                    doc.setFontSize(12); 
                    doc.setTextColor(0, 0, 0); 
                    doc.setFont('helvetica', 'bold'); 
                    doc.text("QUADRO RESUMO DE PONTUAÇÃO (GESTÃO)", margin, y); 
                    y += 6;

                    // Funções de resumo
                    const getQualiQ = (q) => { 
                        let sum = 0, count = 0; 
                        if (evWithScores.qualitative && evWithScores.qualitative.criteria) {
                            evWithScores.qualitative.criteria.forEach(c => {
                                if (c[q] !== null && c[q] !== '' && c[q] !== undefined) { 
                                    sum += parseFloat(c[q]); 
                                    count++; 
                                }
                            }); 
                        }
                        if (count === 0) return 0; 
                        return (sum / count) * 2.5; 
                    };

                    const getGestaoItemQ = (typeFilter, q) => { 
                        if (!evWithScores.gestaoMetasPercepcao) return 0; 
                        const items = evWithScores.gestaoMetasPercepcao.filter(i => {
                            if (typeFilter === 'Metas') {
                                return (i.type === 'Metas');
                            } else {
                                return (i.type === 'Percepção' || i.name.toLowerCase().includes('percepção'));
                            }
                        }); 
                        return items.reduce((acc, item) => {
                            const val = item.scores && item.scores[q] ? parseFloat(item.scores[q]) : 0;
                            return acc + val;
                        }, 0); 
                    };

                    const summaryData = [
                        ["1. Qualitativa (Máx: 40)", getQualiQ('q1').toFixed(2), getQualiQ('q2').toFixed(2), getQualiQ('q3').toFixed(2), getQualiQ('q4').toFixed(2), evWithScores.qualitative.finalScoreGestaoQuali?.toFixed(2), '#0b5394'],
                        ["2. Plan. Estratégico (Máx: 20)", getGestaoItemQ('Metas', 'q1').toFixed(2), getGestaoItemQ('Metas', 'q2').toFixed(2), getGestaoItemQ('Metas', 'q3').toFixed(2), getGestaoItemQ('Metas', 'q4').toFixed(2), evWithScores.totalGestaoMetas?.toFixed(2), '#ef4444'],
                        ["3. Percepção Equipe (Máx: 20)", getGestaoItemQ('Percepção', 'q1').toFixed(2), getGestaoItemQ('Percepção', 'q2').toFixed(2), getGestaoItemQ('Percepção', 'q3').toFixed(2), getGestaoItemQ('Percepção', 'q4').toFixed(2), evWithScores.totalGestaoPercepcao?.toFixed(2), '#f97316'],
                        ["4. Capacitações (Máx: 20)", "-", "-", "-", "-", evWithScores.atualizacao?.total?.toFixed(2), '#9333ea'],
                        [`Total Final`, "", "", "", "", evWithScores.finalScoreGestao?.toFixed(2), "TOTAL_ROW"]
                    ];

                    doc.autoTable({
                        startY: y, 
                        head: [['Componente', 'Trim 1', 'Trim 2', 'Trim 3', 'Trim 4', 'Total Obtido']],
                        body: summaryData.map(row => row.slice(0, 6)),
                        theme: 'grid',
                        headStyles: { halign: 'center', fillColor: [220, 220, 220], textColor: [0, 0, 0], fontStyle: 'bold' },
                        columnStyles: { 
                            0: { cellWidth: 60, fontStyle: 'bold' }, 
                            1: { halign: 'center' }, 
                            2: { halign: 'center' }, 
                            3: { halign: 'center' }, 
                            4: { halign: 'center' }, 
                            5: { halign: 'center', fontStyle: 'bold' } 
                        },
                        didParseCell: (data) => {
                            if (data.section === 'body' && data.row.index < 4) {
                                const color = summaryData[data.row.index][6];
                                const rgb = hexToRgb(color);
                                if (data.column.index === 5 && rgb) {
                                    data.cell.styles.fillColor = rgb;
                                    data.cell.styles.textColor = [255, 255, 255];
                                }
                            }
                        },
                        willDrawCell: (data) => {
                            if (data.section === 'body' && data.row.index === 4) { 
                                data.row.height = data.row.height * 1.1; 
                            }
                        },
                        didDrawCell: (data) => {
                            if (data.section === 'body' && data.row.index === 4 && data.column.index === 5) {
                                const score = evWithScores.finalScoreGestao?.toFixed(4) || '0.0000';
                                const total = " / 100.00";
                                const fullText = score + total;
                                
                                doc.setFontSize(11); 
                                doc.setFont('helvetica', 'bold'); 
                                doc.setTextColor(16, 185, 129);
                                
                                const textWidth = doc.getTextWidth(fullText);
                                const centerX = data.cell.x + (data.cell.width / 2) - (textWidth / 2);
                                const centerY = data.cell.y + (data.cell.height / 2) + 1.5;
                                
                                doc.text(fullText, centerX, centerY);
                                
                                doc.setFontSize(10); 
                                doc.setTextColor(0, 0, 0); 
                                doc.setFont('helvetica', 'normal');
                            }
                        }
                    });
                    y = doc.lastAutoTable.finalY + 3;

                // CASO B: SIMPLIFICADO COLABORADOR
                } else if (type === 'Simplificado Colaborador' && !isGestao) {
                    doc.setFontSize(12); 
                    doc.setTextColor(0, 0, 0); 
                    doc.setFont(undefined, 'bold'); 
                    doc.text("1. AVALIAÇÃO QUALITATIVA", margin, y); 
                    y += 5;
                    
                    const qualiRows = (evWithScores.qualitative?.criteria || []).map(c => [
                        c.name || '-',
                        c.dim || '-',
                        formatPdfGrade(c.q1),
                        formatPdfGrade(c.q2),
                        formatPdfGrade(c.q3),
                        formatPdfGrade(c.q4)
                    ]);
                    doc.autoTable({ 
                        startY: y, 
                        head: [['Critério', 'Dimensão', 'Q1', 'Q2', 'Q3', 'Q4']], 
                        body: qualiRows, 
                        theme: 'grid', 
                        headStyles: { fillColor: [11, 83, 148], fontSize: 8, halign: 'center', textColor: [255, 255, 255] }, 
                        bodyStyles: { fontSize: 7 },
                        columnStyles: { 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' } }
                    });
                    y = doc.lastAutoTable.finalY + 8;
                    
                    doc.setFillColor(219, 234, 254);
                    doc.setDrawColor(96, 165, 250);
                    doc.roundedRect(125, y - 4, 70, 8, 2, 2, 'FD');
                    doc.setFontSize(9);
                    doc.setTextColor(30, 64, 175);
                    doc.setFont('helvetica', 'bold');
                    doc.text(`Média Qualitativa: ${evWithScores.qualitative.finalAverage.toFixed(4)}`, 192, y + 1, { align: 'right' });
                    doc.setTextColor(0, 0, 0);
                    y += 11;

                    doc.setFontSize(12); 
                    doc.setFont(undefined, 'bold'); 
                    doc.text("2. AVALIAÇÃO QUANTITATIVA", margin, y); 
                    y += 5;
                    
                    // CORREÇÃO AQUI: Ordenação Numérica Natural e Alinhamento
                    const sortedQuantiErrors = [...(evWithScores.quantitative?.errors || [])].sort((a, b) =>
                        (a.name || '').localeCompare((b.name || ''), undefined, { numeric: true })
                    );

                    const quantiRows = sortedQuantiErrors.map(e => {
                        const totalRow = ['q1', 'q2', 'q3', 'q4'].reduce(
                            (sum, q) => sum + getQuarterWeightedPoints(e, q),
                            0
                        );

                        return [
                            formatOccurrenceTerm(e.name) || '-',
                            Number(e.peso || 0).toFixed(1),
                            formatPdfCount(e.q1),
                            formatPdfCount(e.q2),
                            formatPdfCount(e.q3),
                            formatPdfCount(e.q4),
                            totalRow.toFixed(1)
                        ];
                    });
                    
                    doc.autoTable({ 
                        startY: y, 
                        head: [['Tipo de Ocorrência', 'Peso', 'Q1', 'Q2', 'Q3', 'Q4', 'Total Pts']], 
                        body: quantiRows, 
                        theme: 'grid', 
                        headStyles: { fillColor: [192, 57, 43], fontSize: 8, halign: 'center', textColor: [255, 255, 255] }, 
                        bodyStyles: { fontSize: 7 },
                        columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' }, 6: { halign: 'center' } }
                    });
                    y = doc.lastAutoTable.finalY + 8; 
                    
                    doc.setFillColor(254, 215, 170);
                    doc.setDrawColor(251, 146, 60);
                    doc.roundedRect(125, y - 4, 70, 8, 2, 2, 'FD');
                    doc.setFontSize(9);
                    doc.setTextColor(154, 52, 18);
                    doc.setFont('helvetica', 'bold');
                    doc.text(`Média Quantitativa: ${evWithScores.quantitative.meanPoints.toFixed(4)}`, 192, y + 1, { align: 'right' });
                    doc.setTextColor(0, 0, 0);
                    y += 11;

                // CASO C: COMPLETO (AMBOS)
                } else if (type === 'Completo') {
                    doc.setFontSize(12); 
                    doc.setTextColor(0, 0, 0); 
                    doc.setFont(undefined, 'bold'); 
                    doc.text("1. AVALIAÇÃO QUALITATIVA", margin, y);

                    const qualiRows = (evWithScores.qualitative?.criteria || []).map(c => [
                            c.name || '-',
                            c.dim || '-',
                            formatPdfGrade(c.q1),
                            formatPdfGrade(c.q2),
                            formatPdfGrade(c.q3),
                            formatPdfGrade(c.q4)
                        ]);
                    doc.autoTable({
                        startY: y + 5,
                        head: [['Critério', 'Dimensão', 'Q1', 'Q2', 'Q3', 'Q4']],
                        body: qualiRows,
                        theme: 'grid',
                        headStyles: { fillColor: [11, 83, 148] },
                        // CORREÇÃO: Alinhamento Qualitativo
                        columnStyles: { 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' } },
                        foot: [[
                            { content: isGestao ? 'Pontuação Qualitativa Total (0-40):' : 'Média Qualitativa Final (0-10):', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold', fillColor: [239, 246, 255], textColor: [30, 64, 175] } },
                            { content: isGestao ? evWithScores.qualitative.finalScoreGestaoQuali?.toFixed(4) : evWithScores.qualitative.finalAverage?.toFixed(4), styles: { fontStyle: 'bold', fillColor: [219, 234, 254], textColor: [30, 64, 175], halign: 'center', lineColor: [96, 165, 250], lineWidth: 0.4 } }
                        ]]
                    });
                    y = doc.lastAutoTable.finalY + 10;

                    if (!isGestao) {
                        doc.setFontSize(12);
                        doc.text("2. AVALIAÇÃO QUANTITATIVA", margin, y);
                        
                        // CORREÇÃO AQUI: Ordenação Numérica Natural e Alinhamento
                        const sortedQuantiErrors = [...(evWithScores.quantitative?.errors || [])].sort((a, b) =>
                            (a.name || '').localeCompare((b.name || ''), undefined, { numeric: true })
                        );

                        const quantiRows = sortedQuantiErrors.map(e => [
                            formatOccurrenceTerm(e.name) || '-',
                            Number(e.peso || 0).toFixed(1),
                            formatPdfCount(e.q1),
                            formatPdfCount(e.q2),
                            formatPdfCount(e.q3),
                            formatPdfCount(e.q4)
                        ]);
                        
                        doc.autoTable({
                            startY: y + 5,
                            head: [['Ocorrência', 'Peso', 'Q1', 'Q2', 'Q3', 'Q4']],
                            body: quantiRows,
                            theme: 'grid',
                            headStyles: { fillColor: [192, 57, 43] },
                            columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' } },
                            foot: [[
                                { content: 'Média de Pontos Perdidos:', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 247, 237], textColor: [154, 52, 18] } },
                                { content: evWithScores.quantitative.meanPoints?.toFixed(4), styles: { fontStyle: 'bold', fillColor: [254, 215, 170], textColor: [154, 52, 18], halign: 'center', lineColor: [251, 146, 60], lineWidth: 0.4 } }
                            ]]
                        });
                        y = doc.lastAutoTable.finalY + 10;
                    } else {
                        if (y + 60 > doc.internal.pageSize.getHeight()) { doc.addPage(); y = 40; }
                        doc.setFontSize(12);
                        doc.text("2. RESULTADOS (METAS E PERCEPÇÃO)", margin, y);
                        const metasRows = (evWithScores.gestaoMetasPercepcao || []).map(item => {
                            const totalItem = ['q1', 'q2', 'q3', 'q4'].reduce((acc, q) => acc + parseFloat(item.scores?.[q] || 0), 0);
                            return [item.name, item.scores.q1 || '-', item.scores.q2 || '-', item.scores.q3 || '-', item.scores.q4 || '-', totalItem.toFixed(1)];
                        });
                        doc.autoTable({
                            startY: y + 5,
                            head: [['Item', 'Q1', 'Q2', 'Q3', 'Q4', 'Total']],
                            body: metasRows,
                            theme: 'grid',
                            foot: [[
                                { content: 'Total Metas + Percepção (0-40):', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold' } },
                                { content: evWithScores.totalGestaoMetasPercepcao?.toFixed(2), styles: { fontStyle: 'bold', fillColor: [255, 237, 213] } }
                            ]]
                        });
                        y = doc.lastAutoTable.finalY + 10;

                        if (y + 40 > doc.internal.pageSize.getHeight()) { doc.addPage(); y = 40; }
                        doc.setFontSize(12);
                        doc.setTextColor(0, 0, 0);
                        doc.text("3. EXTRAS: ATUALIZAÇÃO E RECICLAGEM", margin, y);

                        const updateRows = [
                            ["Na Área (D1) - Min 36h", (evWithScores.atualizacao?.D1_hours || 0) + "h", (evWithScores.atualizacao?.D1_nota || 0).toFixed(2)],
                            ["Outras Áreas (D2) - Min 18h", (evWithScores.atualizacao?.D2_hours || 0) + "h", (evWithScores.atualizacao?.D2_nota || 0).toFixed(2)]
                        ];

                        doc.autoTable({
                            startY: y + 5,
                            head: [['Categoria', 'Horas Registradas', 'Pontos Obtidos']],
                            body: updateRows,
                            theme: 'grid',
                            headStyles: { fillColor: [147, 51, 234] },
                            foot: [[
                                { content: 'Total Pontos Extras (0-20):', colSpan: 2, styles: { halign: 'right', fontStyle: 'bold' } },
                                { content: evWithScores.atualizacao?.total?.toFixed(2), styles: { fontStyle: 'bold', fillColor: [243, 232, 255] } }
                            ]]
                        });
                        y = doc.lastAutoTable.finalY + 10;
                    }
                }

                // =================================================================
                // 4. FEEDBACKS E MENSAGENS FINAIS
                // =================================================================
                
                // PONTOS DE ATENÇÃO (RH)
                if (type.startsWith('Simplificado')) {
                    const avDesFeedbacks = Object.values(ev.feedbacksHistory || {})
                        .filter(f => f.type === 'AvDes' || f.type === 'AvPDF')
                        .sort((a, b) => b.timestamp - a.timestamp);

                    if (avDesFeedbacks.length > 0) {
                        if (y + 40 > doc.internal.pageSize.getHeight()) { doc.addPage(); y = 40; }
                        y += 5;
                        doc.setFontSize(12); 
                        doc.setTextColor(185, 28, 28); 
                        doc.setFont(undefined, 'bold');
                        doc.text("FEEDBACKS", margin, y);
                        y += 4;
                        
                        const feedbackRows = avDesFeedbacks.map(f => [
                            new Date(f.timestamp).toLocaleDateString('pt-BR'),
                            f.quarter ? f.quarter.toUpperCase() : '-',
                            f.text
                        ]);
                        
                        doc.autoTable({
                            startY: y,
                            head: [['Data', 'Ref.', 'Descrição']],
                            body: feedbackRows,
                            theme: 'grid',
                            headStyles: { fillColor: [220, 38, 38], textColor: [255, 255, 255] },
                            columnStyles: { 0: { cellWidth: 25 }, 1: { cellWidth: 20 }, 2: { cellWidth: 'auto' } },
                            styles: { fontSize: 9 }
                        });
                        y = doc.lastAutoTable.finalY + 15;
                    }
                } 
                // HISTÓRICO DE FEEDBACKS - COMPLETO
                else if (type === 'Completo') {
                    if (y + 40 > doc.internal.pageSize.getHeight()) { doc.addPage(); y = 40; }
                    doc.setFontSize(12); 
                    doc.setTextColor(0, 0, 0); 
                    doc.setFont(undefined, 'bold');
                    doc.text(isGestao ? "4. HISTÓRICO COMPLETO DE FEEDBACKS" : "3. HISTÓRICO COMPLETO DE FEEDBACKS", margin, y);

                    const historyList = Object.values(ev.feedbacksHistory || {}).sort((a, b) => b.timestamp - a.timestamp);
                    if (historyList.length > 0) {
                        const feedbackRows = historyList.map(f => [new Date(f.timestamp).toLocaleDateString('pt-BR'), f.type, f.quarter?.toUpperCase() || '-', f.text]);
                        doc.autoTable({ startY: y + 5, head: [['Data', 'Tipo', 'Trim', 'Descrição']], body: feedbackRows, theme: 'grid', headStyles: { fillColor: [243, 156, 18], textColor: [255, 255, 255] }, columnStyles: { 3: { cellWidth: 90 } } });
                        y = doc.lastAutoTable.finalY + 10;
                    } else {
                        doc.setFontSize(10); doc.setFont(undefined, 'normal'); doc.text("Nenhum feedback registrado.", margin, y + 10); y += 20;
                    }

                    // OCORRÊNCIAS - COMPLETO
                    if (y + 40 > doc.internal.pageSize.getHeight()) { doc.addPage(); y = 40; }
                    y += 5;
                    doc.setFontSize(12); 
                    doc.setTextColor(0, 0, 0); 
                    doc.setFont(undefined, 'bold');
                    const sectionNum = isGestao ? "5." : "4.";
                    doc.text(`${sectionNum} RELAÇÃO DE CONFERÊNCIAS (OCORRÊNCIAS)`, margin, y);

                    const occurrences = (data.apontamentos || []).filter(ap => {
                        if (ap.collaboratorId !== ev.collaboratorId) return false;
                        if (!String(ap.date || '').startsWith(String(ev.periodYear))) return false;
                        const qNum = getQuarterNumberFromValue('', ap.date, ap.timestamp);
                        return qNum > 0 && qNum <= maxAllowedQ;
                    }).sort((a, b) => new Date(b.date) - new Date(a.date));

                    if (occurrences.length > 0) {
                        const occRows = occurrences.map(ap => [
                            new Date(ap.date + 'T00:00:00').toLocaleDateString('pt-BR'),
                            ap.weight || ap.peso || 0,
                            ap.type,
                            ap.description
                        ]);
                        doc.autoTable({
                            startY: y + 5,
                            head: [['Data', 'Peso', 'Tipo', 'Descrição']],
                            body: occRows,
                            theme: 'grid',
                            headStyles: { fillColor: [192, 57, 43], textColor: [255, 255, 255], halign: 'center' },
                            columnStyles: { 0: { cellWidth: 25 }, 1: { cellWidth: 15 }, 2: { cellWidth: 50 }, 3: { cellWidth: 'auto' } },
                            styles: { cellPadding: 2 }
                        });
                        y = doc.lastAutoTable.finalY + 10;
                    } else {
                        doc.setFontSize(10); doc.setFont(undefined, 'normal');
                        doc.text("Nenhuma ocorrência registrada neste período.", margin, y + 10);
                        y += 20;
                    }
                }

                // MENSAGEM FINAL (PROMOÇÃO/PREMIAÇÃO)
                const resultLabel = ev.labels?.find(l => l.text === 'Promoção' || l.text === 'Premiação');
                const isConcluded = ev.status === 'Finalizada' || ev.status === 'T4-Concluido' || ev.status === 'Concluída Financeiro';

                if (type.startsWith('Simplificado') && resultLabel && isConcluded) {
                    const msg = data.pdfMessages.find(m => m.type === resultLabel.text && m.personType === ev.personType);
                    if (msg) {
                        const nextYear = (parseInt(ev.periodYear) + 1).toString();
                        const replaceVars = (text) => text ? text.replace(/{AnoAtual}/g, ev.periodYear).replace(/{AnoSeg}/g, nextYear).replace(/{colaborador}/g, ev.collaboratorName).replace(/{gestor}/g, managerNameFallback) : "";
                        const finalTitle = replaceVars(msg.title);
                        const finalBody = replaceVars(msg.body);
                        const rgbBg = hexToRgb(msg.bgColor); const rgbText = hexToRgb(msg.textColor);

                        if (y + 50 > doc.internal.pageSize.getHeight()) { doc.addPage(); y = 40; }
                        if (rgbBg) doc.setFillColor(rgbBg[0], rgbBg[1], rgbBg[2]);
                        doc.roundedRect(margin, y, 182, 40, 5, 5, 'F');
                        if (rgbText) doc.setTextColor(rgbText[0], rgbText[1], rgbText[2]);
                        doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.text(finalTitle, 105, y + 15, { align: 'center' });
                        doc.setFontSize(11); doc.setFont("helvetica", "normal"); doc.text(doc.splitTextToSize(finalBody, 170), 105, y + 25, { align: 'center' });
                        y += 50;
                    }
                }

                // =================================================================
                // 5. FINALIZAÇÃO: QUADRO DE ASSINATURAS E PÁGINA 2
                // =================================================================

                // Verifica espaço para assinaturas. Se não couber, nova página.
                const estimatedSignatureHeight = 60;
                if (y + estimatedSignatureHeight > doc.internal.pageSize.getHeight()) {
                    doc.addPage();
                    y = 20;
                } else {
                    y += 10;
                }

                // 2. Desenha o Quadro de Assinaturas
                y = drawSignatureTable(y);

                // 3. Página 2 (Percepção) - Lógica ajustada para filtrar por Gestor Alvo e Trimestre
                const targetEmail = collabData ? collabData.email.toLowerCase() : '';

                const hasPerception = (data.teamPerception || []).some(p => {
                    const pManagerEmail = p.manager_email ? p.manager_email.toLowerCase() : '';
                    const pDate = p.date || ''; // Ex: "25/10/2023"
                    
                    // Verifica se o destinatário é a Joice (targetEmail)
                    const isToThisManager = pManagerEmail === targetEmail;
                    // Verifica se pertence ao ano correto
                    const isCorrectYear = pDate.endsWith(ev.periodYear);
                    
                    // IMPORTANTE: Aqui filtramos para que a página só exista se houver dados do TRIMESTRE atual
                    // A função drawPerceptionSection já filtra os registros, aqui validamos a existência
                    return isToThisManager && isCorrectYear;
                });

                // SÓ ADICIONA A PÁGINA SE: 
                // 1. O avaliado for do tipo 'Gestão'
                // 2. Existirem percepções cujo manager_email seja o da Joice (exemplo) no ano da avaliação
                if (ev.personType === 'Gestão' && hasPerception) {
                    // Verificamos novamente dentro de drawPerceptionSection se o filtro de trimestre retornará linhas
                    // Para garantir que não saia uma página com cabeçalho mas sem dados.
                    doc.addPage('a4', 'l'); 
                    drawPerceptionSection(20);
                }

                // 4. Salvar/Abrir
                const cleanType = type.includes('Simplificado') ? 'Simplificado' : 'Completo';
                const safeFileName = `${ev.collaboratorName}-${realDeptName}-${ev.evalTypeName || 'Padrao'}-${cleanType}-${ev.periodYear}`.replace(/[^a-z0-9\-]/gi, '_');

                doc.setProperties({ title: safeFileName });
                const pdfUrl = doc.output('bloburl');
                window.open(pdfUrl, '_blank');

            } catch (e) {
                console.error("Erro PDF:", e);
                alert("Erro ao gerar PDF: " + e.message);
            }
            setIsGenerating(false);
            setModal(null);
        }, [selectedEval, data, recalculateFinalAverage]);

        // --- HANDLER: ASSINATURAS DIRETORIA E RH ---
        const handleToggleSignature = (ev, type) => {
            const isSigning = type === 'diretoria' ? !ev.diretoriaSignature : !ev.rhSignature;
            
            setSignConfirmModal({
                isOpen: true,
                type: type,
                action: isSigning ? 'sign' : 'undo',
                evalData: ev
            });
        };

        const executeSignatureAction = async () => {
            const { type, action, evalData } = signConfirmModal;
            if (!evalData) return;

            try {
                const updatedEval = { ...evalData };
                const fieldName = type === 'diretoria' ? 'diretoriaSignature' : 'rhSignature';
                
                // ID consistente para a nota (permite criar e apagar a mesma nota)
                const noteId = `SIG_${type.toUpperCase()}_${evalData.id}`;
                const currentNotes = { ...(updatedEval.personalNotes || {}) };

                if (action === 'sign') {
                    // 1. Cria o objeto de assinatura
                    updatedEval[fieldName] = {
                        name: currentUser.name,
                        id: currentUser.id,
                        date: new Date().toISOString(),
                        role: type === 'diretoria' ? 'Diretoria' : 'RH'
                    };

                    // 2. Cria o registro na aba de Anotações (Regra A)
                    currentNotes[noteId] = {
                        id: noteId,
                        text: `Validação Final realizada pelo ${type === 'diretoria' ? 'Diretoria' : 'RH'} (${currentUser.name}).`,
                        date: new Date().toISOString().split('T')[0],
                        tipo: 'Assinatura',
                        prioridade: 'Alta',
                        status: 'Concluída',
                        timestamp: Date.now(),
                        colorIndex: 4 // Cor diferenciada (Roxo/Escuro)
                    };

                } else {
                    // 1. Remove a assinatura
                    updatedEval[fieldName] = null;
                    
                    // 2. Remove a anotação correspondente
                    if (currentNotes[noteId]) {
                        delete currentNotes[noteId];
                    }

                    // Reverte status se necessário
                    if (updatedEval.status === 'Concluída Financeiro') {
                        updatedEval.status = 'Finalizada'; 
                    }
                }

                // Atualiza o objeto com as notas novas
                updatedEval.personalNotes = currentNotes;

                await actions.saveEvaluation(updatedEval);
                
                // Log de Auditoria
                const actionLabel = type === 'diretoria' ? 'Diretoria' : 'RH';
                const logType = action === 'sign' ? `ASSINATURA_${actionLabel.toUpperCase()}` : `REMOCAO_ASSINATURA_${actionLabel.toUpperCase()}`;
                if(actions.logAction) actions.logAction(logType, { colaborador: evalData.collaboratorName, ano: evalData.periodYear });

                notify('success', `Assinatura de ${actionLabel} ${action === 'sign' ? 'realizada' : 'removida'} com sucesso.`);
            } catch (e) {
                console.error(e);
                notify('error', "Erro ao processar assinatura.");
            } finally {
                setSignConfirmModal({ isOpen: false, type: '', action: '', evalData: null });
            }
        };

                    return (
                        <div className="space-y-6 relative h-full flex flex-col">
                            {notification && (
                                <div className={`fixed top-6 right-6 z-[100] px-4 py-3 rounded-lg shadow-xl text-white text-sm font-bold flex items-center gap-2 animate-fade-in ${notification.type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}>
                                    {notification.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
                                    {notification.text}
                                </div>
                            )}
                            
                            {undoTargetId && (
                                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
                                    <div className="bg-white p-6 rounded-xl w-96 shadow-2xl space-y-4">
                                        <div className="flex items-center gap-3 text-red-600">
                                            <div className="p-2 bg-red-50 rounded-full"><History size={24} /></div>
                                            <h3 className="font-bold text-lg text-gray-800">Cancelar Promoção?</h3>
                                        </div>
                                        <p className="text-sm text-gray-600 leading-relaxed">Esta ação reverterá o cadastro para o cargo e salário anteriores.
                                        O histórico atual será excluído.</p>
                                        <div className="flex justify-end gap-2 pt-2">
                                            <button onClick={() => setUndoTargetId(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium transition-colors">Cancelar</button>
                                            <button onClick={processUndo} className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm font-bold shadow-md transition-colors">Sim, Desfazer</button>
                                        </div>
                                    </div>
                                </div>
                            )}
                
                {tab === 'analise' && (
                    <div className="flex flex-col h-full space-y-6 animate-fade-in overflow-hidden">
                        <Card className={`flex flex-col md:flex-row justify-between items-center gap-4 shadow-md shrink-0 z-30 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-yellow-50 border-yellow-200'}`} darkMode={isDarkMode}>
                            {/* ... Filtros de Análise ... */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 flex-1 w-full">
                                <div><label className={`text-xs font-bold block mb-1 ${isDarkMode ?
                                    'text-slate-400' : 'text-gray-600'}`}>Período Inicial</label><Input type="date" value={analiseFilters.startDate} onChange={e=>setAnaliseFilters({...analiseFilters, startDate:e.target.value})} className={isDarkMode ?
                                    'bg-slate-700 border-slate-600 text-white invert-calendar' : 'bg-white'}/></div>
                                <div><label className={`text-xs font-bold block mb-1 ${isDarkMode ?
                                    'text-slate-400' : 'text-gray-600'}`}>Período Final</label><Input type="date" value={analiseFilters.endDate} onChange={e=>setAnaliseFilters({...analiseFilters, endDate:e.target.value})} className={isDarkMode ?
                                    'bg-slate-700 border-slate-600 text-white invert-calendar' : 'bg-white'}/></div>
                                <div><label className={`text-xs font-bold block mb-1 ${isDarkMode ?
                                    'text-slate-400' : 'text-gray-600'}`}>Departamento</label><select className={`w-full p-2.5 rounded-lg border outline-none ${isDarkMode ?
                                    'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={analiseFilters.dept} onChange={e=>setAnaliseFilters({...analiseFilters, dept:e.target.value})}><option value="">Todos</option>{departments.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}</select></div>
                                <div><label className={`text-xs font-bold block mb-1 ${isDarkMode ?
                                    'text-slate-400' : 'text-gray-600'}`}>Tipo Pessoa</label><select className={`w-full p-2.5 rounded-lg border outline-none ${isDarkMode ?
                                    'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={analiseFilters.personType} onChange={e=>setAnaliseFilters({...analiseFilters, personType:e.target.value})}><option value="Todos">Todos</option><option value="Colaborador">Colaborador</option><option value="Gestão">Gestão</option></select></div>
                            </div>
                            {/* ... Menu de Widgets ... */}
                            <div className="relative">
                                <button onClick={() => setShowWidgetMenu(!showWidgetMenu)} className={`p-2 border rounded-lg flex items-center gap-2 text-xs font-bold 
                                shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600' : 'bg-white border-gray-300 hover:bg-gray-100 text-gray-600'}`}><Settings size={16}/> Visualização</button>
                                {showWidgetMenu && (
                                    <div className={`absolute top-full right-0 mt-2 w-72 rounded-xl shadow-2xl border z-50 animate-fade-in p-2 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                        <div className={`grid grid-cols-[1fr_60px] gap-2 p-2 border-b mb-2 font-bold text-xs uppercase ${isDarkMode ? 'text-slate-400 border-slate-700' : 'text-gray-500 border-gray-200'}`}>
                                            <span>Widget</span>
                                            <span className="text-center">Ordem</span>
                                        </div>
                                        <div className="space-y-1 max-h-60 overflow-y-auto custom-scrollbar">
                                            {dashboardItems.map(item => (
                                                <div key={item.id} className={`grid grid-cols-[1fr_60px] gap-2 items-center p-2 rounded ${isDarkMode ?
                                                    'hover:bg-slate-700' : 'hover:bg-gray-50'}`}>
                                                    <label className={`flex items-center gap-2 cursor-pointer ${isDarkMode ?
                                                        'text-slate-300' : 'text-gray-700'}`}>
                                                        <input type="checkbox" checked={item.visible} onChange={() => toggleWidgetVisibility(item.id)} className="rounded text-blue-600 focus:ring-blue-500"/>
                                                        <span className="text-xs truncate" title={item.label}>{item.label}</span>
                                                    </label>
                                                    <input type="number" min="1" max="99" value={item.order ||
                                                    ''} onChange={(e) => handleOrderChange(item.id, e.target.value)} className={`w-full p-1 text-center text-xs border rounded ${isDarkMode ?
                                                    'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} title="Ordem de exibição"/>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {showWidgetMenu && <div className="fixed inset-0 z-40" onClick={() => setShowWidgetMenu(false)}></div>}
                            </div>
                        </Card>
                        
                        <div className="flex-1 overflow-y-auto custom-scrollbar p-1">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 transition-all duration-300 pb-20">
                                {dashboardItems.filter(i => i.visible).map((item, index) => (
                                    <div 
                                        key={item.id} 
                                        id={`widget-${item.id}`} 
                                        className={`flex flex-col rounded-xl shadow-sm border transition-all duration-200 relative group/widget ${item.w === 2 ?
                                        'lg:col-span-2' : 'lg:col-span-1'} ${draggedIdx === index ? 'opacity-50 scale-[0.98] ring-2 ring-blue-400 border-dashed' : ''} ${isDarkMode ?
                                        'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`} 
                                        style={{ height: item.h }}
                                        draggable="true" 
                                        onDragStart={(e) => handleDragStart(e, index)} 
                                        onDragEnter={(e) => handleDragEnter(e, index)} 
                                        onDragEnd={handleDragEnd} 
                                        onDragOver={(e) => e.preventDefault()}
                                    >
                                        {/* ... Header do Widget ... */}
                                        <div className={`h-8 border-b flex justify-between items-center px-2 shrink-0 cursor-move group-hover/widget:bg-opacity-80 select-none rounded-t-xl ${isDarkMode ?
                                            'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-100'}`} title="Arraste para mover">
                                            <div className="flex items-center gap-2 text-gray-400 group-hover:text-blue-500">
                                                <div className="flex gap-0.5"><div className="w-1 h-3 bg-current rounded-full"></div><div className="w-1 h-3 bg-current rounded-full"></div></div>
                                                <span className="text-[10px] font-bold uppercase tracking-wider">{item.label}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className={`text-[9px] font-mono ${isDarkMode ?
                                                    'text-slate-600' : 'text-gray-300'}`}>#{item.order}</span>
                                                <button onClick={() => toggleWidgetWidth(item.id)} className={`p-1 rounded transition-colors ${isDarkMode ?
                                                    'hover:bg-slate-700 text-slate-500 hover:text-blue-400' : 'hover:bg-gray-200 text-gray-500 hover:text-blue-600'}`} title={item.w === 1 ? "Expandir Largura" : "Reduzir Largura"}>{item.w === 1 ?
                                                    <ToggleLeft size={16}/> : <ToggleRight size={16}/>}</button>
                                            </div>
                                        </div>
                                        <div className="flex-1 overflow-hidden min-h-0 relative">
                                            {availableWidgets[item.id]}
                                        </div>
                                        {/* ... Resizer ... */}
                                        <div className={`h-4 border-t flex items-center justify-end px-1 transition-colors shrink-0 rounded-b-xl relative ${isDarkMode ?
                                            'bg-slate-900 border-slate-700 hover:bg-slate-800' : 'bg-gray-50 border-gray-100 hover:bg-blue-50'}`}>
                                            <div className="absolute bottom-0 right-0 w-6 h-6 cursor-nwse-resize z-20 flex items-end justify-end p-1 opacity-50 hover:opacity-100 text-blue-500" onMouseDown={(e) => startResize(e, item)} title="Redimensionar">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 2 13.5 10.5"/><polyline points="10 2 2 10"/><polyline points="2 22 10.5 13.5"/><polyline points="13.5 22 22 13.5"/><circle cx="12" cy="12" r="1"/></svg>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                
                        {tab === 'central' && (
                    <div className="space-y-6 animate-fade-in">
                        
                        {/* --- CARDS DE STATUS (5 CARDS - REFORMULADO) --- */}
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3 shrink-0">
                            {[
                                { label: 'Total Avaliações', icon: Users, color: 'blue', typeKey: 'total', data: financeMetrics.total },
                                { label: 'Pendente Conclusão', icon: AlertTriangle, color: 'red', typeKey: 'pendConclusion', data: financeMetrics.pendConclusion },
                                { label: 'Pendente Gestor', icon: UserCheck, color: 'orange', typeKey: 'pendGestor', data: financeMetrics.pendGestor },
                                { label: 'Pendente Colab.', icon: Clock, color: 'green', typeKey: 'pendColab', data: financeMetrics.pendColab },
                                { label: 'Prontas / Arquivadas', icon: CheckCircle, color: 'teal', typeKey: 'ready', data: financeMetrics.ready }
                            ].map((card, i) => (
                                <div 
                                    key={i} 
                                    className={`${colorClasses[card.color]?.bg || 'bg-gray-600'} text-white rounded-xl p-3 shadow-md relative group cursor-pointer transition-transform hover:scale-[1.02] flex flex-col justify-start gap-3 h-auto z-0`} 
                                    onMouseEnter={(e) => onHoverCardDetails(e, card.typeKey, card.data.details)} 
                                    onMouseLeave={() => setTooltip(null)}
                                >
                                    <div className="absolute top-2 right-2 p-1.5 rounded-full bg-white/20 hover:bg-white/40 transition-all cursor-help z-10 shadow-sm"><Info size={16} className="text-white"/></div>
                                    <div className="flex items-center gap-2 mb-0 pr-6"><card.icon size={18} /><h3 className="font-bold text-xs">{card.label}</h3></div>
                                    
                                    {/* DETALHAMENTO POR TRIMESTRE */}
                                    <div className="space-y-0 text-[10px]">
                                        {['q1','q2','q3','q4'].map((q, idx) => (
                                            <div key={q} className={`flex justify-between border-b ${colorClasses[card.color]?.border || 'border-white/20'} py-0.5 last:border-0`}>
                                                <span className="opacity-90">{idx+1}º Trim</span>
                                                <span className="font-bold text-xs">{card.data[q]}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

            {/* 2. BARRA DE FILTROS (ATUALIZADA) */}
                        <Card className={`py-2 px-3 shadow-sm min-h-[50px] shrink-0 overflow-x-auto ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`} darkMode={isDarkMode}>
                            <div className="flex items-center gap-3 w-full">
                                
                                {/* Filtro Ano */}
                                <div className={`flex items-center gap-2 border rounded-lg px-2 h-8 w-28 shrink-0 ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                                    <span className={`text-[10px] font-bold ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Ano:</span>
                                    <input type="number" value={finFilterYear} onChange={e => setFinFilterYear(e.target.value)} className={`bg-transparent border-0 h-full text-xs w-full text-center focus:ring-0 px-0 outline-none ${isDarkMode ? 'text-white' : 'text-gray-800'}`} />
                                </div>

                                {/* Filtro Depto */}
                                <div className={`flex items-center gap-2 border rounded-lg px-2 h-8 w-40 shrink-0 ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                                    <span className={`text-[10px] font-bold ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Depto:</span>
                                    <select value={finFilterDept} onChange={e => setFinFilterDept(e.target.value)} className={`bg-transparent border-0 h-full text-xs w-full focus:ring-0 px-0 outline-none cursor-pointer ${isDarkMode ? 'text-white bg-slate-700' : 'text-gray-800'}`}>
                                        <option value="">Todos</option>
                                        {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                    </select>
                                </div>

                                {/* Filtro Tipo (NOVO) */}
                                <div className={`flex items-center gap-2 border rounded-lg px-2 h-8 w-36 shrink-0 ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                                    <span className={`text-[10px] font-bold ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Tipo:</span>
                                    <select value={finFilterPersonType} onChange={e => setFinFilterPersonType(e.target.value)} className={`bg-transparent border-0 h-full text-xs w-full focus:ring-0 px-0 outline-none cursor-pointer ${isDarkMode ? 'text-white bg-slate-700' : 'text-gray-800'}`}>
                                        <option value="Todos">Todos</option>
                                        <option value="Colaborador">Colaborador</option>
                                        <option value="Gestão">Gestão</option>
                                    </select>
                                </div>

                                {/* Filtro Status */}
                                <div className={`flex items-center gap-2 border rounded-lg px-2 h-8 w-44 shrink-0 ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                                    <span className={`text-[10px] font-bold ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Status:</span>
                                    <select value={finFilterStatus} onChange={e => setFinFilterStatus(e.target.value)} className={`bg-transparent border-0 h-full text-xs w-full focus:ring-0 px-0 outline-none cursor-pointer ${isDarkMode ? 'text-white bg-slate-700' : 'text-gray-800'}`}>
                                        <option value="">Todos</option>
                                        <option value="Pendente">Pendentes (Geral)</option>
                                        <option value="Pendente Gestor">Aguardando Gestor</option>
                                        <option value="Pendente Colab">Aguardando Colab.</option>
                                        <option value="Concluída">Prontas p/ Arquivar</option>
                                        <option value="Arquivada">Arquivadas</option>
                                    </select>
                                </div>

                                {/* Campo de Pesquisa */}
                                <div className="relative flex-1 min-w-[150px]">
                                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} size={14} />
                                    <input 
                                        type="text" 
                                        placeholder="Buscar..." 
                                        value={finSearch} 
                                        onChange={e => setFinSearch(e.target.value)}
                                        className={`w-full h-8 pl-9 pr-3 rounded-lg border text-xs outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-gray-50 border-gray-200 text-gray-700 placeholder-gray-400'}`}
                                    />
                                </div>
                            </div>
                        </Card>

                        {/* 3. TABELA DE DADOS (AGORA USA filteredEvaluations) */}
                        <Card className="p-0 overflow-hidden shadow-md border-0" darkMode={isDarkMode}>
                            <div className="overflow-x-auto">
                                <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                    <thead className={`uppercase text-xs font-bold sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 text-slate-400 border-b border-slate-700' : 'bg-gray-100 text-gray-600 border-b border-gray-200'}`}>
                                        <tr>
                                            <th className="p-4 w-1/4">Nome</th>
                                            <th className="p-4 w-24">Ano</th>
                                            <th className="p-4 w-32">Depto</th>
                                            <th className="p-4 w-1/4">Gestor</th>
                                            <th className="p-4 w-40">Status</th>
                                            <th className="p-4 text-center w-32">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700 bg-slate-800' : 'divide-gray-100 bg-white'}`}>
                                        {filteredEvaluations.length === 0 ? (
                                            <tr>
                                                <td colSpan="6" className={`p-8 text-center italic ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                                                    Nenhum registro encontrado com os filtros atuais.
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredEvaluations.map((ev, idx) => {
                                                // 1. Verificações de Assinaturas Base
                                                const hasManagerSig = ev.managerSignature || (ev.managerSignatures && ev.managerSignatures.length > 0);
                                                const hasCollabSig = ev.collabSignature || (ev.collabSignatures && Object.keys(ev.collabSignatures).length > 0);
                                                const isBaseSigned = hasManagerSig && hasCollabSig; // Regra A: Só libera se base assinada

                                                // 2. Verificações Novas
                                                const isGestaoType = ev.personType === 'Gestão';
                                                const hasDiretoriaSig = !!ev.diretoriaSignature;
                                                const hasRHSig = !!ev.rhSignature;

                                                // 3. Regra de Arquivamento (Regra E)
                                                // Se for Gestão: Precisa de RH (Diretoria ignora)
                                                // Se for Colaborador: Precisa de RH + Diretoria
                                                const readyToArchive = isBaseSigned && hasRHSig && (isGestaoType || hasDiretoriaSig);

                                                const isArchived = ev.status === 'Concluída Financeiro';
                                                
                                                // Definição de Status Visual (Mantida lógica anterior + novos status)
                                                let statusLabel = 'AGUARDANDO GESTOR';
                                                let statusColor = isDarkMode ? 'bg-orange-900/30 text-orange-300 border border-orange-800' : 'bg-orange-100 text-orange-700 border border-orange-100';

                                                if (isArchived) {
                                                    statusLabel = 'ARQUIVADA';
                                                    statusColor = isDarkMode ? 'bg-indigo-900/30 text-indigo-300 border border-indigo-800' : 'bg-indigo-100 text-indigo-700 border border-indigo-100';
                                                } else if (readyToArchive) {
                                                    statusLabel = 'PRONTA P/ ARQUIVAR';
                                                    statusColor = isDarkMode ? 'bg-green-900/30 text-green-300 border border-green-800' : 'bg-green-100 text-green-700 border border-green-100';
                                                } else if (isBaseSigned) {
                                                    statusLabel = 'AGUARDANDO VALIDAÇÃO (DIR/RH)';
                                                    statusColor = isDarkMode ? 'bg-purple-900/30 text-purple-300 border border-purple-800' : 'bg-purple-100 text-purple-700 border border-purple-100';
                                                } else if (hasManagerSig && !hasCollabSig) {
                                                    statusLabel = 'AGUARDANDO COLABORADOR';
                                                    statusColor = isDarkMode ? 'bg-yellow-900/30 text-yellow-300 border border-yellow-800' : 'bg-yellow-100 text-yellow-800 border border-yellow-100';
                                                }

                                                const rowClass = idx % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');

                                                return (
                                                    <tr key={ev.id} className={`${rowClass} transition-colors ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-blue-50'}`}>
                                                        <td className={`p-4 font-bold ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{ev.collaboratorName}</td>
                                                        <td className="p-4">{ev.periodYear}</td>
                                                        <td className="p-4">{ev.department}</td>
                                                        <td className="p-4">{ev.managerName}</td>
                                                        <td className="p-4">
                                                            <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${statusColor}`}>
                                                                {statusLabel}
                                                            </span>
                                                        </td>
                                                        
                                                        <td className="p-4 text-center">
                                                            <div className="flex justify-center gap-2">
                                                                
                                                                {/* 1. ASSINATURA DIRETORIA (Regra B: some se Gestão) */}
                                                                {!isGestaoType && (
                                                                    <button 
                                                                        onClick={() => handleToggleSignature(ev, 'diretoria')}
                                                                        disabled={!isBaseSigned} // Regra A
                                                                        className={`p-2 rounded transition-colors ${
                                                                            !isBaseSigned ? 'text-gray-300 cursor-not-allowed opacity-30' : 
                                                                            hasDiretoriaSig 
                                                                                ? (isDarkMode ? 'bg-green-900/50 text-green-400 border border-green-700' : 'bg-green-100 text-green-700 border border-green-200') 
                                                                                : (isDarkMode ? 'text-slate-400 hover:bg-slate-700 border border-slate-700' : 'text-gray-400 hover:bg-gray-100 border border-gray-200')
                                                                        }`} 
                                                                        title={!isBaseSigned ? "Aguardando assinaturas da base" : hasDiretoriaSig ? "Diretoria Assinou (Clique para desfazer)" : "Assinar como Diretoria"}
                                                                    >
                                                                        <Award size={18}/>
                                                                    </button>
                                                                )}

                                                                {/* 2. ASSINATURA RH */}
                                                                <button 
                                                                    onClick={() => handleToggleSignature(ev, 'rh')}
                                                                    disabled={!isBaseSigned} // Regra A
                                                                    className={`p-2 rounded transition-colors ${
                                                                        !isBaseSigned ? 'text-gray-300 cursor-not-allowed opacity-30' : 
                                                                        hasRHSig 
                                                                            ? (isDarkMode ? 'bg-green-900/50 text-green-400 border border-green-700' : 'bg-green-100 text-green-700 border border-green-200') 
                                                                            : (isDarkMode ? 'text-slate-400 hover:bg-slate-700 border border-slate-700' : 'text-gray-400 hover:bg-gray-100 border border-gray-200')
                                                                    }`} 
                                                                    title={!isBaseSigned ? "Aguardando assinaturas da base" : hasRHSig ? "RH Assinou (Clique para desfazer)" : "Assinar como RH"}
                                                                >
                                                                    <Users size={18}/>
                                                                </button>
                                                                
                                                                <div className={`w-px h-6 mx-1 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}></div>

                                                                {/* 3. IMPRIMIR */}
                                                                <button 
                                                                    onClick={() => { setSelectedEval(ev); setModal('pdf'); }}
                                                                    disabled={!isBaseSigned} 
                                                                    className={`p-2 rounded transition-colors ${!isBaseSigned ? 'text-gray-300 cursor-not-allowed opacity-30' : (isDarkMode ? 'text-blue-400 hover:bg-slate-600' : 'text-blue-600 hover:bg-blue-100')}`} 
                                                                    title="Imprimir Relatório"
                                                                >
                                                                    <Printer size={18}/>
                                                                </button>

                                                                {/* 4. ARQUIVAR (Regra E) */}
                                                                <button 
                                                                    onClick={() => {
                                                                        if (isArchived) {
                                                                            setUnarchiveModal(ev);
                                                                        } else {
                                                                            actions.saveEvaluation({...ev, status: 'Concluída Financeiro'});
                                                                            notify('success', 'Avaliação arquivada com sucesso.');
                                                                        }
                                                                    }} 
                                                                    disabled={!isArchived && !readyToArchive} // Bloqueia se não estiver pronta E não estiver já arquivada (para desarquivar)
                                                                    className={`p-2 rounded transition-colors ${
                                                                        isArchived 
                                                                        ? (isDarkMode ? 'text-green-400 hover:bg-slate-600' : 'text-green-600 hover:bg-green-100') 
                                                                        : (!readyToArchive ? 'text-gray-300 cursor-not-allowed opacity-30' : (isDarkMode ? 'text-orange-400 hover:bg-slate-600' : 'text-orange-600 hover:bg-orange-100'))
                                                                    }`}
                                                                    title={isArchived ? "Desarquivar" : readyToArchive ? "Arquivar Financeiro" : "Aguardando validações de Dir/RH"}
                                                                >
                                                                    {isArchived ? <RefreshCw size={18}/> : <Archive size={18}/>}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </div>
                )}

                {/* --- NOVA ABA: TRACKING DE FEEDBACKS --- */}
                {tab === 'feedbacks' && (
                    <div className="h-full overflow-hidden animate-fade-in">
                        <FeedbackTrackingTab 
                            data={data} 
                            actions={actions} 
                            currentUser={currentUser} 
                            isDarkMode={isDarkMode} 
                        />
                    </div>
                )}

                {/* --- ABA PROJEÇÃO FINANCEIRA (PROJ) --- */}
                {tab === 'proj' && (
                    <div className="space-y-6 animate-fade-in flex flex-col h-full overflow-hidden">
                        <Card className={`mb-6 p-4 shrink-0 ${isDarkMode ?
                            'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`} darkMode={isDarkMode}>
                            <div className="flex flex-col xl:flex-row gap-4 items-center justify-between">
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 w-full xl:w-auto xl:flex-1">
                                    <div><label className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Ano</label><Input type="number" value={projYear} onChange={e=>setProjYear(e.target.value)} className={`h-8 text-xs ${isDarkMode ?
                                        'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/></div>
                                    <div><label className={`text-[10px] font-bold block mb-1 ${isDarkMode ?
                                        'text-slate-400' : 'text-gray-600'}`}>Departamento</label><select className={`w-full h-8 text-xs p-1 rounded border outline-none focus:ring-1 focus:ring-blue-500 ${isDarkMode ?
                                        'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`} onChange={e=>setProjDept(e.target.value)} value={projDept}><option value="">Todos</option>{departments.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}</select></div>
                                    <div><label className={`text-[10px] font-bold block mb-1 ${isDarkMode ?
                                        'text-slate-400' : 'text-gray-600'}`}>Tipo Pessoa</label><select className={`w-full h-8 text-xs p-1 rounded border outline-none focus:ring-1 focus:ring-blue-500 ${isDarkMode ?
                                        'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={projPersonType} onChange={e=>setProjPersonType(e.target.value)}><option value="Todos">Todos</option><option value="Colaborador">Colaborador</option><option value="Gestão">Gestão</option></select></div>
                                    <div><label className={`text-[10px] font-bold block mb-1 ${isDarkMode ?
                                        'text-slate-400' : 'text-gray-600'}`}>Resultado</label><select className={`w-full h-8 text-xs p-1 rounded border outline-none focus:ring-1 focus:ring-blue-500 ${isDarkMode ?
                                        'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`} onChange={e=>setProjRes(e.target.value)} value={projRes}><option value="Todos">Tudo</option><option value="Promoção">Promoção</option><option value="Premiação">Premiação</option><option value="Promoção|Premiação">Promoção |
                                        Premiação</option></select></div>
                                    <div><label className={`text-[10px] font-bold block mb-1 ${isDarkMode ?
                                        'text-slate-400' : 'text-gray-600'}`}>Agrupamento</label><select className={`w-full h-8 text-xs p-1 rounded border outline-none focus:ring-1 focus:ring-blue-500 ${isDarkMode ?
                                        'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={projGroup} onChange={e=>setProjGroup(e.target.value)}><option value="Departamento">Departamento</option><option value="Promoção">Promoção</option><option value="Premiação">Premiação</option></select></div>
                                </div>
                                <div className="flex items-center gap-3 shrink-0 flex-wrap justify-center md:justify-end">
                                    <div className={`rounded-lg px-3 py-2 text-center min-w-[140px] relative flex flex-col justify-center shadow-sm h-[50px] border ${isDarkMode ?
                                        'bg-emerald-900/20 border-emerald-800' : 'bg-emerald-50 border-emerald-100'}`}><div className={`text-[9px] font-bold uppercase leading-tight ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>Impacto Anual</div><div className={`text-sm font-bold ${isDarkMode ?
                                        'text-emerald-300' : 'text-emerald-600'}`}>{formatCurrency(calculatedProjectionData.totalAnnual)}</div><div className="absolute top-1 right-1 group cursor-help" onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, type: 'annual_detail', data: calculatedProjectionData.detalhesAnual })} onMouseLeave={() => setTooltip(null)}><Info size={12} className={isDarkMode ?
                                        'text-emerald-600' : 'text-emerald-500'} /></div></div>
                                    <div className={`rounded-lg px-3 py-2 text-center min-w-[140px] relative flex flex-col justify-center shadow-sm h-[50px] border ${isDarkMode ?
                                        'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-100'}`}><div className={`text-[9px] font-bold uppercase leading-tight ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>Impacto Mensal (Folha)</div><div className={`text-sm font-bold ${isDarkMode ?
                                        'text-blue-300' : 'text-blue-600'}`}>{formatCurrency(calculatedProjectionData.totalMonthly)}</div><div className="absolute top-1 right-1 group cursor-help" onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, type: 'monthly_detail', data: calculatedProjectionData.detalhesMensal })} onMouseLeave={() => setTooltip(null)}><Info size={12} className={isDarkMode ?
                                        'text-blue-600' : 'text-blue-500'} /></div></div>
                                    {/* CORREÇÃO: Adicionei a chave '{' no início para o React entender que é código */}
                                    {(permissions?.access_sub_execute_promo === true) && (
                                        <Button onClick={handleBatchPromotionClick} className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white h-[50px] px-4 font-bold shadow-md flex items-center gap-2 border-0" title="Aplicar promoções nos cadastros">
                                            <TrendingUp size={18}/> 
                                            <div className="flex flex-col items-start leading-none">
                                                <span className="text-xs">Aplicar</span>
                                                <span className="text-sm">Promover</span>
                                            </div>
                                        </Button>
                                    )}
                                        <div className="flex flex-col gap-1 h-[50px] justify-between"><Button onClick={exportEstimativaPDF} className="bg-red-600 hover:bg-red-700 h-[22px] px-2 text-[9px] whitespace-nowrap w-20 flex items-center justify-center border-0"><FileText size={10} className="mr-1"/> PDF</Button><Button onClick={exportEstimativaExcel} className="bg-green-600 hover:bg-green-700 h-[22px] px-2 text-[9px] whitespace-nowrap w-20 flex items-center justify-center border-0"><FileSpreadsheet size={10} className="mr-1"/> Excel</Button></div>
                                </div>
                            </div>
                        </Card>
                        
                        <div className={`rounded-xl shadow-md overflow-hidden flex flex-col flex-1 border ${isDarkMode ?
                            'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                            <div className={`shrink-0 border-b z-20 ${isDarkMode ?
                                'bg-slate-900 border-slate-700' : 'bg-gray-100 border-gray-200'}`}>
                                <table className="w-full text-sm text-left">
                                    <thead className="text-xs uppercase font-bold">
                                        <tr>
                                            <th className={`p-3 text-left w-[20%] ${isDarkMode ?
                                                'text-slate-400' : 'text-gray-600'}`}>Colaborador</th>
                                            <th className={`p-3 text-center w-[6%] ${isDarkMode ?
                                                'text-slate-400' : 'text-gray-600'}`}>Ano Aval.</th>
                                            {projGroup !== 'Departamento' && (<th className={`p-3 text-left w-[10%] ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Departamento</th>)}
                                            <th className={`p-3 text-left w-[10%] ${isDarkMode 
                                                ? 'text-slate-400' : 'text-gray-600'}`}>Gestor</th>
                                            <th className={`p-3 text-left w-[12%] ${isDarkMode ?
                                                'text-slate-400' : 'text-gray-600'}`}>Cargo</th>
                                            <th className={`p-3 text-center w-[5%] ${isDarkMode ?
                                                'text-slate-400' : 'text-gray-600'}`}>Níveis</th>
                                            <th className={`p-3 text-center w-[8%] ${isDarkMode ?
                                                'text-slate-400' : 'text-gray-600'}`}>Salário Atual</th>
                                            <th className={`p-3 text-center w-[8%] ${isDarkMode ?
                                                'text-slate-400' : 'text-gray-600'}`}>Novo Salário</th>
                                            <th className={`p-3 text-center w-[8%] text-blue-600`}>Custo Mensal Promoção</th>
                                            <th className={`p-3 text-center w-[8%] text-purple-600`}>Custo Anual Premiação</th>
                                            <th className={`p-3 text-center w-[5%] text-red-600 border-l ${isDarkMode ?
                                                'border-slate-700' : ''}`}>Ações</th>
                                        </tr>
                                    </thead>
                                </table>
                            </div>
                            <div className="flex-1 overflow-y-auto custom-scrollbar">
                                <table className="w-full text-sm text-left">
                                    <tbody className="divide-y">
                                        {(() => { 
                                            const groups = calculatedProjectionData.rows.reduce((acc, curr) => { 
                                                let groupKey = curr.dept; 
                                                if (projGroup === 'Promoção') { 
                                                    if (curr.promoDiff > 0) groupKey = 'LISTAGEM: PROMOÇÕES'; else return acc; 
                                                } 
                                                else if (projGroup === 'Premiação') { 
                                                    if (curr.award > 0) groupKey = 'LISTAGEM: PREMIAÇÕES';
                                                    else return acc; 
                                                } 
                                                if (!acc[groupKey]) acc[groupKey] = [];
                                                acc[groupKey].push(curr); 
                                                return acc; 
                                            }, {}); 
                                            const footerColSpan = projGroup !== 'Departamento' ? 8 : 7;
                                            const totalCols = footerColSpan + 3; 
                                            return Object.entries(groups).map(([dept, itens]) => { 
                                                const sumPromo = itens.reduce((acc, i) => acc + i.promoDiff, 0); 
                                                const sumAward = itens.reduce((acc, i) => acc + i.award, 0); 
                                                const groupImpacts = calculateUnifiedImpacts(sumPromo, sumAward); 
                                                
                                                // Correção: Grupos iniciam fechados (FALSE por padrão)
                                                const isExpanded = expandedDepts[dept] === true; 
                                                
                                                return (
                                                    <React.Fragment key={dept}>
                                                        <tr className={`${isDarkMode ? 'bg-slate-600 border-slate-500 hover:bg-slate-500' : 'bg-slate-200 border-b border-t border-slate-300 hover:bg-slate-300'} transition-colors shadow-sm`}>
                                                            <td colSpan={totalCols} className="px-4 py-2">
                                                                <div className="flex items-center gap-2">
                                                                    <button onClick={() => toggleDept(dept)} className={`p-1 rounded-full transition-all focus:outline-none ${isDarkMode ?
                                                                        'hover:bg-slate-500 text-slate-200' : 'hover:bg-slate-100 text-slate-600'}`}>{isExpanded ? <Minus size={16} /> : <Plus size={16} />}</button>
                                                                    <span className={`font-bold uppercase text-xs cursor-pointer select-none ${isDarkMode ?
                                                                        'text-white' : 'text-slate-800'}`} onClick={() => toggleDept(dept)}>{dept} <span className={`${isDarkMode ? 'text-slate-300' : 'text-slate-500'} font-normal ml-2`}>({itens.length} pessoas)</span></span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        {isExpanded && itens.map((r, i) => (
                                                            <tr key={r.id + i} className={`${isDarkMode 
                                                                ? 'bg-slate-800 hover:bg-slate-700' : 'bg-white hover:bg-blue-50'} transition-colors animate-fade-in`}>
                                                                <td className="p-3 w-[20%] font-bold align-middle">{r.name}</td>
                                                                <td className="p-3 w-[6%] text-center align-middle text-xs opacity-70">{r.year}</td>
                                                                {projGroup !== 'Departamento' 
                                                                && (<td className="p-3 w-[10%] align-middle text-xs opacity-80 uppercase font-medium">{r.dept}</td>)}
                                                                <td className="p-3 w-[10%] text-left align-middle text-xs opacity-70">
                                                                    {r.managerList && r.managerList.length > 0 ? (
                                                                        <div className="flex flex-col gap-1">
                                                                            {r.managerList.map(name => <span key={name} className="block whitespace-nowrap">{name}</span>)}
                                                                        </div>
                                                                    ) : 'N/D'}
                                                                </td>
                                                                <td className="p-3 w-[12%] align-middle">
                                                                    {/* Se estiver anonimizado (isBlind), não mostra nada ou apenas um traço */}
                                                                    {r.isBlind ? (
                                                                        <span className="text-gray-400">-</span>
                                                                    ) : (
                                                                        <>
                                                                            <div className="font-medium text-xs">
                                                                                <span className="text-xs mr-1 text-blue-600 font-bold">(A)</span>
                                                                                <span className="text-blue-600 font-medium">{r.role}</span>
                                                                            </div>
                                                                            {/* Só mostra o cargo novo (N) se houver promoção real e não for anonimizado */}
                                                                            {r.newRole !== r.role && (
                                                                                <div className="text-green-600 font-medium text-xs mt-0.5">
                                                                                    <span className="text-green-600 mr-1 font-bold">(N)</span>
                                                                                    <span className="text-green-600 font-medium">{r.newRole}</span>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </td>
                                                                <td className="p-3 w-[5%] text-center align-middle"><input type="number" min="1" max="3" className={`w-12 text-center border rounded p-1 text-xs font-bold focus:ring-2 focus:ring-yellow-400 outline-none ${isDarkMode ?
                                                                    'bg-slate-700 border-slate-600 text-white' : 'bg-yellow-50 border-gray-200'}`} value={r.jump} onChange={(e) => setLevelOverrides(prev => ({...prev, [r.id]: parseInt(e.target.value) || 1}))} /></td>
                                                {/* Salário Atual (Oculto se Blind) */}
                                                <td className="p-3 w-[8%] text-center align-middle">
                                                    {r.isBlind ? <span className="text-gray-400">-</span> : formatCurrency(r.salary)}
                                                </td>
                                                
                                                {/* Novo Salário (Oculto se Blind) */}
                                                <td className="p-3 w-[8%] text-center align-middle">
                                                    {r.isBlind ? <span className="text-gray-400">-</span> : formatCurrency(r.newSalary)}
                                                </td>
                                                
                                                {/* Custo Promoção (Sempre Visível) */}
                                                <td className="p-3 w-[8%] text-center font-bold text-blue-600 align-middle">
                                                    {r.promoDiff > 0 ? `+ ${formatCurrency(r.promoDiff)}` : '-'}
                                                </td>

                                                {/* Custo Premiação (Sempre Visível) */}
                                                <td className="p-3 w-[8%] text-center font-bold text-purple-600 align-middle">
                                                    {r.award > 0 ? formatCurrency(r.award) : '-'}
                                                </td>

                                                {/* Ações (Bloqueado se Blind) */}
                                                <td className={`p-3 w-[5%] text-center align-middle border-l ${isDarkMode ? 'border-slate-700' : ''}`}>
                                                    {!r.isBlind && hasRecentPromotion(r.id) ? (
                                                        <button onClick={() => handleUndoClick(r.id)} className="flex items-center gap-1 bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:text-red-700 px-2 py-1 rounded text-[10px] font-bold transition-all whitespace-nowrap shadow-sm"><History size={12}/> Desfazer</button>
                                                    ) : (
                                                        <span className="text-gray-300 text-[10px]">-</span>
                                                    )}
                                                </td>                                                        </tr>
                                                        ))}
                                                        {/* LINHAS DE SUBTOTAL E TOTAIS (CORRIGIDO: ALTO CONTRASTE DARK MODE) */}
                                                        {isExpanded && (
                                                            <>
                                                                <tr className={`${isDarkMode ? 'bg-slate-700 border-t border-slate-600' : 'bg-gray-100 border-t border-gray-300'} text-xs font-black`}>
                                                                    <td colSpan={footerColSpan} className={`px-4 py-2 text-right uppercase tracking-wider ${isDarkMode ?
                                                                        'text-slate-300' : 'text-gray-600'}`}>Subtotal {dept} (Soma):</td>
                                                                    <td className={`px-4 py-2 text-center ${isDarkMode ?
                                                                        'text-blue-300' : 'text-blue-600'}`}>{formatCurrency(sumPromo)}</td>
                                                                    <td className={`px-4 py-2 text-center ${isDarkMode ?
                                                                        'text-purple-300' : 'text-purple-600'}`}>{formatCurrency(sumAward)}</td>
                                                                    <td className={`border-l ${isDarkMode ?
                                                                        'bg-slate-700 border-slate-600' : 'bg-gray-100'}`}></td>
                                                                </tr>
                                                                <tr className={`font-bold text-sm border-t ${isDarkMode ?
                                                                    'bg-green-900 text-white border-green-800' : 'bg-green-100 border-green-200 text-green-800'}`}>
                                                                    <td colSpan={footerColSpan} className={`px-4 py-2 text-right uppercase text-xs tracking-wider ${isDarkMode ?
                                                                        'text-green-300' : 'text-green-800'}`}>TOTAL {dept} - IMPACTO ANUAL 12 MESES + PRÊMIOS:</td>
                                                                    <td colSpan="2" className={`px-4 py-2 text-center text-base border-l font-black ${isDarkMode ?
                                                                        'border-green-800 text-green-300 bg-green-950' : 'border-green-300 text-green-700 bg-green-200'}`}>{formatCurrency(groupImpacts.totalAnnual)}</td>
                                                                    <td className={`border-l ${isDarkMode ?
                                                                        'bg-green-900 border-green-800' : 'bg-green-100'}`}></td>
                                                                </tr>
                                                                <tr className={`font-bold text-sm border-t ${isDarkMode ? 'bg-blue-900 text-white border-blue-800' : 'bg-blue-100 border-blue-200 text-blue-800'}`}>
                                                                <td colSpan={footerColSpan} className={`px-4 py-2 text-right uppercase text-xs tracking-wider ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>TOTAL {dept} - IMPACTO MENSAL (FOLHA):</td>
                                                                {/* CORREÇÃO: Alterado para .totalMonthly */}
                                                                <td colSpan="2" className={`px-4 py-2 text-center text-base border-l font-black ${isDarkMode ? 'border-blue-800 text-blue-300 bg-blue-950' : 'border-blue-300 text-blue-700 bg-blue-200'}`}>{formatCurrency(groupImpacts.totalMonthly)}</td>
                                                                <td className={`border-l ${isDarkMode ? 'bg-blue-900 border-blue-800' : 'bg-blue-100'}`}></td>
                                                            </tr>
                                                                <tr><td colSpan={totalCols} className="h-4 border-none bg-transparent"></td></tr>
                                                            </>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            }) 
                                        })()}</tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL DE REAJUSTE COLETIVO */}
                {modal === 'readjust' && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
                        <div className={`p-6 rounded-xl w-80 space-y-4 shadow-2xl ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800'}`}>
                            <h3 className="font-bold text-lg border-b pb-2 mb-2 flex items-center gap-2"><Coins size={18} className="text-yellow-500"/> Reajuste Coletivo</h3>
                            <div>
                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Percentual (%)</label>
                                <Input type="number" value={readjustPercent} onChange={e=>setReadjustPercent(e.target.value)} className={`text-center font-bold text-lg ${isDarkMode ?
                                    'bg-slate-900 border-slate-600 text-white' : ''}`} placeholder="0.0"/>
                            </div>
                            <div>
                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ?
                                    'text-slate-400' : 'text-gray-600'}`}>Nova Vigência</label>
                                <Input type="date" value={readjustDate} onChange={e=>setReadjustDate(e.target.value)} className={isDarkMode ?
                                    'bg-slate-900 border-slate-600 text-white' : ''}/>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <Button variant="secondary" onClick={()=>setModal(null)} className={`w-full ${isDarkMode ?
                                    'bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600' : ''}`}>Cancelar</Button>
                                <Button onClick={handleMassReadjust} className="w-full bg-blue-600 hover:bg-blue-700 text-white">Aplicar</Button>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* MODAL DE REVISÃO DE PROMOÇÃO */}
                {isPromoModalOpen && (
                    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                        <div className={`rounded-xl w-full max-w-6xl shadow-2xl flex flex-col max-h-[90vh] animate-fade-in overflow-hidden ${isDarkMode ? 'bg-slate-800 text-slate-200' : 'bg-white text-gray-800'}`}>
                            {/* Header do Modal */}
                            <div className={`p-5 border-b flex justify-between items-center ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-indigo-50 border-gray-100'}`}>
                                <div>
                                    <h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ?
                                        'text-indigo-400' : 'text-indigo-900'}`}><TrendingUp size={20}/> Revisão de Promoções</h3>
                                    <p className={`text-xs ${isDarkMode ?
                                        'text-indigo-300' : 'text-indigo-600'}`}>Confirme quem será efetivado, ajuste os níveis se necessário e defina a data.</p>
                                </div>
                                <div className="flex flex-col items-end">
                                    <label className={`text-[10px] font-bold uppercase mb-1 ${isDarkMode ?
                                        'text-slate-400' : 'text-gray-500'}`}>Vigência da Promoção</label>
                                    <Input type="date" value={promoEffectiveDate} onChange={e => setPromoEffectiveDate(e.target.value)} className={`h-9 text-sm font-bold ${isDarkMode ?
                                        'bg-slate-800 border-slate-600 text-white focus:ring-indigo-500' : 'bg-white border-indigo-200 text-indigo-700 focus:ring-indigo-500'}`}/>
                                </div>
                            </div>

                            {/* Tabela */}
                            <div className="flex-1 overflow-y-auto p-0">
                                <table className="w-full text-sm text-left border-collapse">
                                    <thead className={`text-xs uppercase font-bold sticky top-0 z-10 shadow-sm ${isDarkMode ?
                                        'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-600'}`}>
                                        <tr>
                                            <th className={`p-3 w-10 text-center border-b ${isDarkMode ?
                                                'bg-slate-900 border-slate-700' : 'bg-gray-100 border-gray-200'}`}><div title="Promoção Confirmada?">Promo?</div></th>
                                            <th className={`p-3 border-b ${isDarkMode ?
                                                'bg-slate-900 border-slate-700' : 'bg-gray-100 border-gray-200'}`}>Colaborador</th>
                                            <th className={`p-3 border-b ${isDarkMode ?
                                                'bg-slate-900 border-slate-700' : 'bg-gray-100 border-gray-200'}`}>Função Antiga</th>
                                            <th className={`p-3 border-b text-center w-24 ${isDarkMode ?
                                                'bg-slate-900 border-slate-700' : 'bg-gray-100 border-gray-200'}`}>Níveis (+Up)</th>
                                            <th className={`p-3 border-b ${isDarkMode ?
                                                'bg-slate-900 border-slate-700' : 'bg-gray-100 border-gray-200'}`}>Função Nova (Prevista)</th>
                                            <th className={`p-3 border-b text-right ${isDarkMode ?
                                                'bg-slate-900 border-slate-700' : 'bg-gray-100 border-gray-200'}`}>Salário Antigo</th>
                                            <th className={`p-3 border-b text-right text-green-600 ${isDarkMode ?
                                                'bg-slate-900 border-slate-700' : 'bg-gray-100 border-gray-200'}`}>Salário Novo</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                        {promoCandidates.map((c, idx) => (
                                            <tr key={c.id} className={`${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-indigo-50/50'} transition-colors ${!c.checked ? 
                                                (isDarkMode ? 'opacity-50 grayscale bg-slate-900' : 'opacity-50 grayscale bg-gray-50') : ''}`}>
                                                <td className="p-3 text-center align-middle"><input type="checkbox" checked={c.checked} onChange={() => toggleCandidate(c.id)} className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"/></td>
                                                <td className={`p-3 font-bold align-middle ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>{c.name}<div className={`text-[10px] font-normal ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>{c.dept}</div></td>
                                                <td className={`p-3 text-xs align-middle ${isDarkMode ?
                                                    'text-slate-400' : 'text-gray-500'}`}>{c.role}</td>
                                                <td className="p-3 text-center align-middle">
                                                    <input type="number" min="1" max="5" value={c.jump} onChange={(e) => handleModalJumpChange(c.id, e.target.value)} disabled={!c.checked} className={`w-14 text-center border rounded p-1.5 text-xs font-bold focus:ring-2 focus:ring-indigo-400 outline-none ${!c.checked ?
                                                        (isDarkMode ? 'bg-slate-900 text-slate-600 border-slate-700' : 'bg-gray-100 text-gray-400') : (isDarkMode ? 'bg-yellow-900/30 text-yellow-200 border-yellow-700' : 'bg-yellow-50 text-yellow-800 border-yellow-300')}`} title="Ajustar Níveis de Aumento"/>
                                                </td>
                                                <td className="p-3 align-middle"><span className={`text-xs font-bold px-2 py-1 rounded ${c.newRole !== c.role ?
                                                    (isDarkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-700') : (isDarkMode ? 'text-slate-500' : 'text-gray-400')}`}>{c.newRole}</span></td>
                                                <td className={`p-3 text-right text-xs align-middle decoration-slice ${isDarkMode ?
                                                    'text-slate-400' : 'text-gray-500'}`}>{formatCurrency(c.salary)}</td>
                                                <td className={`p-3 text-right font-bold align-middle text-sm ${isDarkMode ?
                                                    'text-green-400' : 'text-green-600'}`}>{formatCurrency(c.newSalary)}{c.promoDiff > 0 && (<div className={`text-[10px] font-normal ${isDarkMode ? 'text-green-500' : 'text-green-500'}`}>Dif: +{formatCurrency(c.promoDiff)}</div>)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Footer do Modal */}
                            <div className={`p-4 border-t flex justify-between items-center shrink-0 ${isDarkMode ?
                                'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                <div className={`text-xs ${isDarkMode ?
                                    'text-slate-400' : 'text-gray-500'}`}><b>{promoCandidates.filter(c => c.checked).length}</b> colaboradores selecionados.</div>
                                <div className="flex gap-3">
                                    <button onClick={() => setIsPromoModalOpen(false)} className={`px-4 py-2 rounded-lg border font-medium text-sm transition-colors ${isDarkMode ?
                                        'border-slate-600 text-slate-300 bg-slate-800 hover:bg-slate-700' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-100'}`}>Cancelar</button>
                                    <button onClick={executeBatchPromotion} className="px-6 py-2 rounded-lg bg-green-600 text-white font-bold text-sm hover:bg-green-700 shadow-md flex items-center gap-2 transition-colors"><CheckCircle size={18}/> Confirmar e Aplicar Mudanças</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                
                {importModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white p-6 rounded-xl w-96 shadow-2xl animate-fade-in space-y-4">
                            <h3 className="font-bold text-lg text-gray-800 flex items-center gap-2"><Settings size={18}/> Configurar Importação</h3>
                            <div><label className="text-xs font-bold text-gray-600 block mb-1">Departamento Destino</label><select className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white text-sm" value={importConfig.targetDept} onChange={e => setImportConfig({...importConfig, targetDept: e.target.value})}><option value="">Manter Original</option><option value="Todos">Todos</option>{departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}</select></div>
                            <div><label className="text-xs font-bold text-gray-600 block mb-1">Vigência Inicial</label><Input type="date" value={importDate} onChange={e=>setImportDate(e.target.value)} className="text-sm" /></div>
                            <div><label className="text-xs font-bold text-gray-600 block mb-1">Desconsiderar Palavra (Filtro)</label><div className="relative"><Input placeholder="Ex: Contábil" value={importConfig.ignoreTerm} onChange={e => setImportConfig({...importConfig, ignoreTerm: e.target.value})} className="pr-8"/><Eraser size={14} className="absolute right-3 top-3 text-gray-400"/></div></div>
                            <div className="flex justify-end gap-2 pt-2"><Button variant="secondary" onClick={()=>setImportModalOpen(false)}>Cancelar</Button><Button onClick={confirmImport}>Importar Agora</Button></div>
                        </div>
                    </div>
                )}

                    {/* MODAL DE PDF (No FinanceiroSystem) */}
                        {modal === 'pdf' && selectedEval && (
                            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] backdrop-blur-sm" onClick={() => setModal(null)}>
                                <div className={`p-6 rounded-lg w-80 shadow-2xl animate-fade-in ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                    <h3 className="font-bold mb-4 flex items-center gap-2"><Printer size={20} /> Gerar Relatório PDF</h3>
                                    <p className="text-xs text-gray-500 mb-4">Selecione o modelo de impressão:</p>
                                    
                                    <Button className="w-full mb-2" onClick={() => generatePDF(selectedEval, 'Completo')}>
                                        Completo
                                    </Button>
                                    
                                    <Button variant="secondary" className="w-full mb-2" onClick={() => generatePDF(selectedEval, selectedEval.personType === 'Gestão' ? 'Simplificado Gestão' : 'Simplificado Colaborador')}>
                                        Simplificado ({selectedEval.personType})
                                    </Button>
                                    
                                    <Button variant="danger" className="w-full" onClick={() => setModal(null)}>
                                        Cancelar
                                    </Button>
                                </div>
                            </div>
                        )}

                {isSyncModalOpen && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white p-6 rounded-xl w-96 shadow-2xl animate-fade-in space-y-4">
                            <h3 className="font-bold text-lg text-gray-800 flex items-center gap-2"><RefreshCw size={18}/> Sincronizar Treinamentos</h3>
                            <div>
                                <label className="text-xs font-bold text-gray-600 block mb-1">Ano Base (C1)</label>
                                <Input type="number" value={syncYearInput} onChange={e => setSyncYearInput(e.target.value)} className="text-center text-lg tracking-widest font-bold" maxLength={4}/>
                                <p className="text-[10px] text-gray-500 mt-2">O sistema atualizará a planilha e importará os dados após 2 segundos.</p>
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <Button variant="secondary" onClick={() => setIsSyncModalOpen(false)} disabled={isUpdatingSheet}>Cancelar</Button>
                                <Button onClick={handleConfirmSync} disabled={isUpdatingSheet} className="bg-green-600 hover:bg-green-700 text-white">
                                    {isUpdatingSheet ? <><Loader2 size={16} className="animate-spin mr-2"/> Processando...</> : "Confirmar"}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL CONFIRMAÇÃO DE ASSINATURA DIR/RH */}
                {signConfirmModal.isOpen && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setSignConfirmModal({ ...signConfirmModal, isOpen: false })}>
                        <div className={`p-6 rounded-xl w-96 shadow-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            
                            <div className={`flex items-center gap-3 mb-4 ${signConfirmModal.action === 'sign' ? 'text-green-600' : 'text-red-600'}`}>
                                <div className={`p-3 rounded-full ${signConfirmModal.action === 'sign' ? (isDarkMode ? 'bg-green-900/30' : 'bg-green-100') : (isDarkMode ? 'bg-red-900/30' : 'bg-red-100')}`}>
                                    {signConfirmModal.type === 'diretoria' ? <Award size={24}/> : <Users size={24}/>}
                                </div>
                                <h3 className="font-bold text-lg">
                                    {signConfirmModal.action === 'sign' ? 'Confirmar Assinatura?' : 'Desfazer Assinatura?'}
                                </h3>
                            </div>

                            <p className={`text-sm mb-6 leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                {signConfirmModal.action === 'sign' 
                                    ? `Você está prestes a validar esta avaliação como ${signConfirmModal.type === 'diretoria' ? 'DIRETORIA' : 'RH'}.`
                                    : `Tem certeza que deseja remover a assinatura de ${signConfirmModal.type === 'diretoria' ? 'DIRETORIA' : 'RH'}?`
                                }
                            </p>

                            <div className="flex justify-end gap-2">
                                <Button 
                                    variant="secondary" 
                                    onClick={() => setSignConfirmModal({ ...signConfirmModal, isOpen: false })} 
                                    className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}
                                >
                                    Cancelar
                                </Button>
                                
                                <Button 
                                    onClick={executeSignatureAction} 
                                    className={`text-white shadow-md border-0 ${signConfirmModal.action === 'sign' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                                >
                                    {signConfirmModal.action === 'sign' ? 'Confirmar' : 'Sim, Remover'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* POPUP DE TOOLTIP GERAL (INCLUI RESTAURAÇÃO DE FEEDBACKS COM CORES) */}
                {tooltip && (
                    <div className="fixed float-popup w-72 z-[9999]" style={{ top: tooltip.y, left: tooltip.x, transform: 'translateX(-100%)', pointerEvents: 'auto', marginLeft: '-10px' }} onMouseLeave={() => setTooltip(null)} >
                        <div className={`p-3 text-gray-700 shadow-2xl border rounded-lg ${isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-white border-gray-200'}`}>
                            {/* HEADER DO TOOLTIP */}
                            <div className={`font-bold mb-2 border-b pb-1 text-xs uppercase ${isDarkMode ? 'text-emerald-400 border-slate-600' : 'text-emerald-600 border-gray-100'}`}>
                                {tooltip.type === 'annual_detail' ?
                                    'Memória de Cálculo Anual' : 
                                tooltip.type === 'monthly_detail' ?
                                    'Memória de Cálculo Mensal' :
                                tooltip.type === 'feedbacks' ?
                                    `Histórico de Feedbacks (${tooltip.data.year})` : 'Detalhes'}
                            </div>
                            
                            {/* BODY DO TOOLTIP: CÁLCULOS FINANCEIROS */}
                            {(tooltip.type === 'annual_detail' || tooltip.type === 'monthly_detail') && (
                                <div className="space-y-1">
                                    <div className="flex justify-between text-xs"><span>Aumento Salarial:</span> <span className="font-bold text-blue-600">{formatCurrency(tooltip.data.promoMensal)}</span></div>
                                    <div className="flex justify-between text-xs"><span>Premiação:</span> <span className="font-bold text-purple-600">{formatCurrency(tooltip.data.premiacao)}</span></div>
                                    <div className="flex justify-between text-xs"><span>13º Salário:</span> <span className="font-bold">{formatCurrency(tooltip.data.decimoTerceiro)}</span></div>
                                    <div className="flex justify-between text-xs"><span>1/3 Férias:</span> <span className="font-bold">{formatCurrency(tooltip.data.tercoFerias)}</span></div>
                                    <div className="flex justify-between text-xs"><span>DSR:</span> <span className="font-bold">{formatCurrency(tooltip.data.dsr)}</span></div>
                                    <div className="flex justify-between text-xs"><span>Reflexo Fér/13º:</span> <span className="font-bold">{formatCurrency(tooltip.data.reflexoFerias13)}</span></div>
                                    <div className="border-t pt-1 mt-1 border-gray-200"></div>
                                    <div className="flex justify-between text-xs"><span>FGTS (8%):</span> <span className="font-bold text-red-600">{formatCurrency(tooltip.data.fgts)}</span></div>
                                    <div className="border-t pt-1 mt-1 font-bold text-sm text-emerald-700 flex justify-between border-gray-200">
                                        <span>TOTAL:</span> 
                                        <span>{formatCurrency(tooltip.data.promoMensal + tooltip.data.premiacao + 
                                        tooltip.data.decimoTerceiro + tooltip.data.tercoFerias + tooltip.data.dsr + tooltip.data.reflexoFerias13 + tooltip.data.fgts)}</span>
                                    </div>
                                </div>
                            )}

                            {/* BODY DO TOOLTIP: FEEDBACKS (RESTAURADO COM CORES) */}
                            {tooltip.type === 'feedbacks' && (
                                <div className="max-h-60 overflow-y-auto custom-scrollbar space-y-2">
                                    {Object.values(tooltip.data.feedbacks).length === 0 ?
                                    (
                                        <p className="text-xs italic text-gray-400">Nenhum feedback registrado.</p>
                                    ) : (
                                        Object.values(tooltip.data.feedbacks)
                                            .sort((a, b) => b.timestamp - a.timestamp)
                                            .map((f, idx) => {
                                                const isGestao = f.type === 'Gestão' || f.type === 'Avaliador';
                                                const isPDF = f.type === 'AvPDF';
                                                
                                                // Define cores baseado no tipo (consistente com o sistema)
                                                const bgClass = isGestao 
                                                    ? (isDarkMode ? 'bg-blue-900/30 border-blue-800 text-blue-100' : 'bg-blue-50 border-blue-200 text-gray-800')
                                                    : isPDF 
                                                        ? (isDarkMode ? 'bg-red-900/30 border-red-800 text-red-100' : 'bg-red-50 border-red-200 text-gray-800')
                                                        : (isDarkMode ? 'bg-yellow-900/30 border-yellow-800 text-yellow-100' : 'bg-yellow-50 border-yellow-200 text-gray-800');
                                                const titleColor = isGestao 
                                                    ? (isDarkMode ? 'text-blue-400' : 'text-blue-600') 
                                                    : isPDF 
                                                        ? (isDarkMode ? 'text-red-400' : 'text-red-600') 
                                                        : (isDarkMode ? 'text-yellow-400' : 'text-yellow-600');
                                                return (
                                                    <div key={idx} className={`text-xs p-2 mb-2 rounded border shadow-sm ${bgClass}`}>
                                                        <div className={`flex justify-between items-center mb-1 border-b pb-1 ${isDarkMode ? 'border-white/10' : 'border-black/5'}`}>
                                                            <span className={`font-bold uppercase text-[10px] ${titleColor}`}>
                                                                {f.type} - {f.quarter.toUpperCase()}
                                                            </span>
                                                            <span className="text-[9px] opacity-70">
                                                                {new Date(f.timestamp).toLocaleDateString()}
                                                            </span>
                                                        </div>
                                                        <p className="whitespace-pre-wrap leading-relaxed opacity-90">
                                                            {f.text}
                                                        </p>
                                                    </div>
                                                );
                                            })
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                                                                    {/* MODAL DE DESARQUIVAMENTO */}
                                                        {unarchiveModal && (
                                                            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setUnarchiveModal(null)}>
                                                                <div className={`p-6 rounded-xl w-96 shadow-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()}>
                                                                    <div className="flex items-center gap-3 mb-4 text-orange-500">
                                                                        <div className={`p-2 rounded-full ${isDarkMode ? 'bg-orange-900/30' : 'bg-orange-100'}`}>
                                                                            <History size={24}/>
                                                                        </div>
                                                                        <h3 className="font-bold text-lg">Desarquivar Avaliação?</h3>
                                                                    </div>
                                                                    
                                                                    <p className={`text-sm mb-6 leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                                        A avaliação de <b>{unarchiveModal.collaboratorName}</b> voltará para o status <b>"Finalizada"</b> e sairá do arquivo morto do financeiro.
                                                                    </p>

                                                                    <div className="flex justify-end gap-2">
                                                                        <Button 
                                                                            variant="secondary" 
                                                                            onClick={() => setUnarchiveModal(null)} 
                                                                            className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}
                                                                        >
                                                                            Cancelar
                                                                        </Button>
                                                                        
                                                                        <Button 
                                                                            onClick={() => {
                                                                                // Reverte para Finalizada
                                                                                actions.saveEvaluation({ ...unarchiveModal, status: 'Finalizada' });
                                                                                notify('success', 'Avaliação desarquivada com sucesso.');
                                                                                setUnarchiveModal(null);
                                                                            }} 
                                                                            className="bg-orange-600 hover:bg-orange-700 text-white shadow-md border-0"
                                                                        >
                                                                            Sim, Desarquivar
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

            {/* TOOLTIP FLUTUANTE (FINANCEIRO - PADRÃO RESTAURADO) */}
                {tooltip && tooltip.type === 'details' && (
                    <div 
                        className="fixed float-popup shadow-2xl border border-gray-200 bg-white rounded-lg pointer-events-none z-[9999]" 
                        style={{ 
                            top: tooltip.y, 
                            left: tooltip.x, 
                            transform: 'translate(-50%, 0)', 
                            marginTop: '15px'
                        }}
                    >
                        <div className="w-72 bg-white rounded-lg shadow-2xl border border-slate-200 flex flex-col">
                            <div className="font-bold border-b border-gray-100 p-3 bg-gray-50 rounded-t-lg text-sm text-blue-600 truncate flex justify-between items-center">
                                <span>
                                    {tooltip.data.type === 'total' ? "Total Cadastrado" : 
                                    tooltip.data.type === 'pending' ? "Pendências Gerais" :
                                    tooltip.data.type === 'pendConclusion' ? "Pendente Conclusão" :
                                    tooltip.data.type === 'pendGestor' ? "Falta Assin. Gestor" : 
                                    tooltip.data.type === 'pendColab' ? "Falta Assin. Colab" : 
                                    tooltip.data.type === 'ready' ? "Prontas / Arquivadas" : "Detalhes"}
                                </span>
                            </div>
                            <div className="overflow-y-auto custom-scrollbar p-2" style={{ maxHeight: '250px' }}>
                                <ul className="text-xs space-y-1">
                                    {(() => {
                                        // Mesma lógica de consolidação do Dashboard
                                        const consolidated = {};
                                        const quartersMap = { q1: 'T1', q2: 'T2', q3: 'T3', q4: 'T4' };
                                        const colorsMap = { 
                                            q1: 'text-blue-600', 
                                            q2: 'text-amber-500', 
                                            q3: 'text-red-500', 
                                            q4: 'text-emerald-500' 
                                        };
                                        
                                        ['q1', 'q2', 'q3', 'q4'].forEach(q => {
                                            (tooltip.data[q] || []).forEach(item => {
                                                if (!consolidated[item.name]) consolidated[item.name] = { name: item.name, badges: [] };
                                                consolidated[item.name].badges.push({ label: quartersMap[q], color: colorsMap[q] });
                                            });
                                        });
                                        
                                        const list = Object.values(consolidated).sort((a, b) => a.name.localeCompare(b.name));
                                        
                                        if (list.length === 0) return <li className="text-center text-gray-400 p-2">Nenhum registro.</li>;

                                        return list.map((item, i) => (
                                            <li key={i} className="flex justify-between items-center text-gray-700 border-b border-gray-50 pb-1 mb-1 last:border-0 last:pb-0 last:mb-0">
                                                <span className="font-bold truncate w-2/3" title={item.name}>{item.name}</span>
                                                <span className="flex gap-1 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">
                                                    {item.badges.map((b, bi) => (
                                                        <span key={bi} className={`font-mono text-[9px] font-bold ${b.color}`}>{b.label}</span>
                                                    ))}
                                                </span>
                                            </li>
                                        ));
                                    })()}
                                </ul>
                            </div>
                        </div>
                        {/* Seta do Tooltip */}
                        <div className="absolute left-1/2 -ml-2 -top-1 w-4 h-4 bg-white border-t border-l border-gray-200 transform rotate-45"></div>
                    </div>
                )}

            </div>
        );
    };

    // Converta Decimal para Horas (ex: 10.5 -> 10:30) - SEM SEGUNDOS
    const formatDecimalToTime = (decimal) => {
        if (!decimal && decimal !== 0) return '';
        const hours = Math.floor(decimal);
        const minutesDecimal = (decimal - hours) * 60;
        const minutes = Math.round(minutesDecimal); // Arredonda os minutos
        return `${hours}:${minutes.toString().padStart(2, '0')}`;
    };

    // Converta Horas para Decimal (ex: 10:30:00 -> 10.5)
    const formatTimeToDecimal = (timeStr) => {
        if (!timeStr) return 0;
        if (!timeStr.includes(':')) return parseFloat(timeStr) || 0;
        const parts = timeStr.split(':').map(Number);
        const h = parts[0] || 0;
        const m = parts[1] || 0;
        const s = parts[2] || 0;
        return h + (m / 60) + (s / 3600);
    };


    // ============================================================================
    // LÓGICA DO SISTEMA DE PONTO (MIGRAÇÃO)
    // ============================================================================

    // Utilitários de Tempo
    const formatTimePonto = (date) => date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    const minutesToTimePonto = (totalMinutes) => {
        if (isNaN(totalMinutes) || totalMinutes === null) return "00:00";
        const sign = totalMinutes < 0 ? "-" : "";
        const m = Math.abs(totalMinutes);
        const hours = Math.floor(m / 60);
        const mins = Math.round(m % 60);
        return `${sign}${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
    };
    const timeToMinutesPonto = (timeStr) => {
        if (!timeStr || typeof timeStr !== 'string') return 0;
        const parts = timeStr.split(':');
        return (parseInt(parts[0]) * 60) + parseInt(parts[1]);
    };

    // Componente de Input específico para o Ponto (com Label)


    /* ===============================================================================
     * [END_MOD_FINANCEIRO_SYSTEM]
     * FIM DO BLOCO: FinanceiroSystem
     * [START_MOD_PONTO_INPUT]
     * INICIO DO BLOCO: PontoInput
     * =============================================================================== */
    const PontoInput = ({ label, darkMode, ...props }) => (
        <div className="w-full">
            {label && <label className={`text-xs font-bold block mb-1 ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>{label}</label>}
            <input {...props} className={`w-full rounded-lg border p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${darkMode ? 'bg-slate-900 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-gray-300 text-gray-800'}`} />
        </div>
    );

    // Função auxiliar de conversão para usar no ManagementModule
    const parseBalanceInput = (val) => {
        if (!val) return 0;
        const isNegative = String(val).startsWith('-');
        const cleanVal = String(val).replace('-', '');
        
        // Formato HH:MM
        if (cleanVal.includes(':')) {
            const parts = cleanVal.split(':');
            const hours = parseInt(parts[0]) || 0;
            const mins = parseInt(parts[1]) || 0;
            const totalMins = (hours * 60) + mins;
            return isNegative ? -totalMins : totalMins;
        } 
        
        // CORREÇÃO: Formato numérico simples (assume MINUTOS diretamente)
        const mins = parseInt(cleanVal) || 0;
        return isNegative ? -mins : mins;
    };

    // --- MÓDULO CADASTROS DO PONTO (MANAGEMENT) - FINAL AJUSTADO ---


    /* ===============================================================================
     * [END_MOD_PONTO_INPUT]
     * FIM DO BLOCO: PontoInput
     * [START_MOD_PONTO_MANAGEMENT]
     * INICIO DO BLOCO: ManagementModule
     * =============================================================================== */
    const ManagementModule = ({ isDarkMode, safeCollaborators, safeDepartments, employeesMeta, setEmployeesMeta, config, setConfig, situations, setSituations, onUpdateCollab, actions }) => {
        const [subTab, setSubTab] = React.useState('people');
        const [searchTerm, setSearchTerm] = React.useState('');
        
        // Estados Pessoas
        const [editingPerson, setEditingPerson] = React.useState(null);

        // Estados Regras Gerais
        const [isRuleModalOpen, setIsRuleModalOpen] = React.useState(false);
        const [rulesList, setRulesList] = React.useState(Array.isArray(config) ? config : []);
        const [ruleForm, setRuleForm] = React.useState({});

        // Estados Situações (Novo)
        const [isSitModalOpen, setIsSitModalOpen] = React.useState(false);
        const [sitForm, setSitForm] = React.useState({});

        // --- LÓGICA REGRAS ---
        React.useEffect(() => {
            if (!Array.isArray(config) && config.workHours) {
                setRulesList([{ id: 'default', ...config, name: 'Regra Padrão', validFrom: '2024-01-01', validTo: '', deadlineDays: 30 }]);
            } else if (Array.isArray(config)) {
                setRulesList(config);
            }
        }, [config]);

        const saveRulesToGlobal = (newList) => {
            setRulesList(newList);
            setConfig(newList);
        };

        const handleSaveRule = () => {
            if (!ruleForm.name || !ruleForm.workHours || !ruleForm.validFrom) return alert("Preencha os campos obrigatórios.");
            let newList = [...rulesList];
            if (ruleForm.id) newList = newList.map(r => r.id === ruleForm.id ? ruleForm : r);
            else newList.push({ ...ruleForm, id: Date.now().toString() });
            saveRulesToGlobal(newList);
            setIsRuleModalOpen(false);
        };

        const handleDeleteRule = (id) => {
            if (window.confirm("Excluir esta regra?")) {
                saveRulesToGlobal(rulesList.filter(r => r.id !== id));
            }
        };

        // --- LÓGICA SITUAÇÕES ---
        const handleSaveSituation = () => {
            if (!sitForm.name) return alert("Nome obrigatório.");
            
            // Define uma cor baseada no tipo se não tiver
            let color = sitForm.color;
            if (!color) {
                const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6'];
                color = colors[Math.floor(Math.random() * colors.length)];
            }

            const newSit = { 
                id: sitForm.id || Date.now().toString(),
                name: sitForm.name,
                type: sitForm.type || 'Abono',
                color: color,
                departmentId: sitForm.departmentId || 'all',
                personType: sitForm.personType || 'Todos',
                requireAttachment: sitForm.requireAttachment || false
            };

            let newList = [...situations];
            if (sitForm.id) newList = newList.map(s => s.id === sitForm.id ? newSit : s);
            else newList.push(newSit);
            
            setSituations(newList);
            setIsSitModalOpen(false);
        };

        const handleDeleteSituation = (id) => {
            if (window.confirm("Excluir esta situação?")) {
                setSituations(situations.filter(s => s.id !== id));
            }
        };

        // --- LÓGICA PESSOAS ---
        const filteredPeople = safeCollaborators.filter(c => 
            (c.name && c.name.toLowerCase().includes(searchTerm.toLowerCase())) || 
            (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        const handleSaveMeta = (id, data) => {
            const { cpf, registrationNumber, initialBalanceMinutes, initialDate, ...pointData } = data;
            setEmployeesMeta(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...data } }));
            const targetCollab = safeCollaborators.find(c => c.id === id);
            if (targetCollab && onUpdateCollab) {
                onUpdateCollab({
                    ...targetCollab,
                    cpf, registrationNumber, initialBalanceMinutes, initialDate, ...pointData
                });
            }
            setEditingPerson(null);
            alert("Cadastro atualizado com sucesso!");
        };

        return (
            <div className="space-y-6 animate-fade-in">
                <div className={`flex border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                    <button onClick={() => setSubTab('people')} className={`px-6 py-3 font-bold text-sm border-b-2 transition-colors ${subTab === 'people' ? 'border-blue-500 text-blue-500' : 'border-transparent opacity-60'}`}>Pessoas</button>
                    <button onClick={() => setSubTab('situations')} className={`px-6 py-3 font-bold text-sm border-b-2 transition-colors ${subTab === 'situations' ? 'border-blue-500 text-blue-500' : 'border-transparent opacity-60'}`}>Situações</button>
                    <button onClick={() => setSubTab('config')} className={`px-6 py-3 font-bold text-sm border-b-2 transition-colors ${subTab === 'config' ? 'border-blue-500 text-blue-500' : 'border-transparent opacity-60'}`}>Regras Gerais</button>
                </div>

                {/* ABA PESSOAS */}
                {subTab === 'people' && (
                    <>
                        <div className="flex justify-between mb-4">
                            <div className="relative w-72">
                                <Search className={`absolute left-3 top-2.5 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} size={16}/>
                                <input placeholder="Buscar nome ou e-mail..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={`w-full pl-9 p-2 rounded-lg border text-sm outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`} />
                            </div>
                        </div>
                        <div className={`border rounded-lg overflow-hidden ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                            <table className={`w-full text-sm text-left table-fixed ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                <thead className={`text-xs uppercase font-bold ${isDarkMode ? 'bg-slate-900' : 'bg-gray-100'}`}>
                                    <tr>
                                        <th className="p-4 w-1/3">Colaborador</th>
                                        <th className="p-4 w-1/3">E-mail</th>
                                        <th className="p-4 w-1/4">CPF / Matrícula</th>
                                        <th className="p-4 w-20 text-center">Ações</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                    {filteredPeople.map((p, idx) => {
                                        const meta = employeesMeta[p.id] || {};
                                        // ZEBRA STRIPING PARA PESSOAS
                                        const rowClass = idx % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50');
                                        
                                        return (
                                            <tr key={p.id} className={`${rowClass} hover:opacity-90 transition-colors`}>
                                                <td className="p-4 font-bold flex items-center gap-2 truncate">
                                                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${isDarkMode ? 'bg-slate-700' : 'bg-blue-100 text-blue-600'}`}>{p.name.charAt(0)}</div>
                                                    <span className="truncate">{p.name}</span>
                                                </td>
                                                <td className="p-4 font-mono text-xs opacity-80 truncate">{p.email || 'N/D'}</td>
                                                <td className="p-4 font-mono text-xs truncate">{p.cpf || meta.cpf || '-'}</td>
                                                <td className="p-4 text-center"><button onClick={() => setEditingPerson(p)} className="text-blue-500 hover:bg-blue-50 p-2 rounded"><Edit3 size={16}/></button></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {/* MODAL PESSOA */}
                        {editingPerson && (
                            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
                                <div className={`p-6 rounded-xl w-96 shadow-2xl ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`}>
                                    <h3 className="font-bold text-lg mb-4">Dados de Ponto</h3>
                                    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                                        <p className="font-bold opacity-70 border-b pb-2">{editingPerson.name}</p>
                                        <PontoInput label="CPF" id="edit-cpf" defaultValue={editingPerson.cpf || (employeesMeta[editingPerson.id] || {}).cpf} darkMode={isDarkMode} />
                                        <PontoInput label="Matrícula" id="edit-mat" defaultValue={editingPerson.registrationNumber || (employeesMeta[editingPerson.id] || {}).registrationNumber} darkMode={isDarkMode} />
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="col-span-2">
                                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Data Implantação Saldo</label>
                                                <input type="date" id="edit-date" defaultValue={editingPerson.initialDate || (employeesMeta[editingPerson.id] || {}).initialDate} className={`w-full p-2 border rounded text-sm ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white invert-calendar' : ''}`} />
                                            </div>
                                            <div className="col-span-2">
                                                <PontoInput label="Saldo Inicial (HH:MM)" id="edit-balance" placeholder="Ex: 10:30 ou -05:00" defaultValue={minutesToTimePonto(editingPerson.initialBalanceMinutes || (employeesMeta[editingPerson.id] || {}).initialBalanceMinutes)} darkMode={isDarkMode} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-200 dark:border-slate-700">
                                        <Button variant="secondary" onClick={() => setEditingPerson(null)}>Cancelar</Button>
                                        <Button onClick={() => {
                                            const cpf = document.getElementById('edit-cpf').value;
                                            const mat = document.getElementById('edit-mat').value;
                                            const date = document.getElementById('edit-date').value;
                                            const balStr = document.getElementById('edit-balance').value;
                                            const balanceMinutes = parseBalanceInput(balStr);
                                            handleSaveMeta(editingPerson.id, { cpf, registrationNumber: mat, initialDate: date, initialBalanceMinutes: balanceMinutes });
                                        }}>Salvar</Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* ABA SITUAÇÕES (NOVO CRUD) */}
                {subTab === 'situations' && (
                    <div className="space-y-4">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="font-bold flex items-center gap-2"><FileUp size={20}/> Tipos de Justificativa</h3>
                            <Button onClick={() => { setSitForm({ id: null, name: '', type: 'Abono', color: '#10b981', departmentId: 'all', personType: 'Todos', requireAttachment: false }); setIsSitModalOpen(true); }} icon={Plus}>Nova Situação</Button>
                        </div>

                        <div className={`border rounded-lg overflow-hidden ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                            <table className={`w-full text-sm text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                <thead className={`text-xs uppercase font-bold ${isDarkMode ? 'bg-slate-900' : 'bg-gray-100'}`}>
                                    <tr>
                                        <th className="p-3 w-10">Cor</th>
                                        <th className="p-3">Nome da Situação</th>
                                        <th className="p-3 w-32 text-center">Tipo</th>
                                        <th className="p-3 w-24 text-center">Ações</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                    {situations.map((s, idx) => {
                                        const rowClass = idx % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50');
                                        return (
                                            <tr key={s.id || idx} className={`${rowClass} hover:opacity-90`}>
                                                <td className="p-3 text-center">
                                                    <div className="w-4 h-4 rounded-full shadow-sm mx-auto" style={{ backgroundColor: s.color || '#ccc' }}></div>
                                                </td>
                                                <td className="p-3 font-bold">
                                                    <div className="flex items-center gap-2">
                                                        <span>{s.name}</span>
                                                        {s.requireAttachment && <span className={`text-[9px] px-1.5 py-0.5 rounded border flex items-center gap-1 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-300' : 'bg-gray-100 border-gray-200 text-gray-600'}`} title="Exige Anexo"><Paperclip size={10}/> Anexo</span>}
                                                    </div>
                                                    <div className="flex items-center gap-2 text-[9px] opacity-60 font-normal mt-0.5 uppercase tracking-wider">
                                                        <span>{s.personType || 'Todos'}</span> • <span>{s.departmentId === 'all' || !s.departmentId ? 'Geral' : safeDepartments.find(d=>d.id===s.departmentId)?.name || 'Depto'}</span>
                                                    </div>
                                                </td>
                                                <td className="p-3 text-center align-middle">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold border ${s.type === 'Abono' ? (isDarkMode ? 'border-green-800 text-green-400 bg-green-900/30' : 'border-green-200 text-green-700 bg-green-50') : (isDarkMode ? 'border-red-800 text-red-400 bg-red-900/30' : 'border-red-200 text-red-700 bg-red-50')}`}>
                                                        {s.type}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-center align-middle">
                                                    <div className="flex justify-center gap-2">
                                                        <button onClick={() => { setSitForm(s); setIsSitModalOpen(true); }} className="text-blue-500 p-1 hover:bg-blue-500/10 rounded"><Edit3 size={16}/></button>
                                                        <button onClick={() => handleDeleteSituation(s.id)} className="text-red-500 p-1 hover:bg-red-500/10 rounded"><Trash2 size={16}/></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {situations.length === 0 && <tr><td colSpan={4} className="p-6 text-center opacity-50">Nenhuma situação cadastrada.</td></tr>}
                                </tbody>
                            </table>
                        </div>

                        {/* MODAL SITUAÇÃO */}
                        {isSitModalOpen && (
                            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
                                <div className={`p-6 rounded-xl w-full max-w-md shadow-2xl ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`}>
                                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><Settings size={18}/> {sitForm.id ? 'Editar Situação' : 'Nova Situação'}</h3>
                                    
                                    <div className="space-y-4">
                                        <PontoInput label="Nome" value={sitForm.name} onChange={e=>setSitForm({...sitForm, name: e.target.value})} darkMode={isDarkMode} />
                                        
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Tipo Base</label>
                                                <select className={`w-full p-2.5 rounded-lg border text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={sitForm.type} onChange={e=>setSitForm({...sitForm, type: e.target.value})}>
                                                    <option value="Abono">Abono</option>
                                                    <option value="Desconto">Desconto</option>
                                                    <option value="Neutro">Neutro (Observação)</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Público Alvo</label>
                                                <select className={`w-full p-2.5 rounded-lg border text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={sitForm.personType || 'Todos'} onChange={e=>setSitForm({...sitForm, personType: e.target.value})}>
                                                    <option value="Todos">Todos</option>
                                                    <option value="Colaborador">Colaborador</option>
                                                    <option value="Gestão">Gestão</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div>
                                            <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Departamento Restrito</label>
                                            <select className={`w-full p-2.5 rounded-lg border text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={sitForm.departmentId || 'all'} onChange={e=>setSitForm({...sitForm, departmentId: e.target.value})}>
                                                <option value="all">Geral (Todos os Departamentos)</option>
                                                {safeDepartments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                            </select>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3 items-end">
                                            <div>
                                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Cor da Tag</label>
                                                <div className="flex gap-2">
                                                    <input type="color" className="h-10 w-12 cursor-pointer rounded border-0 p-0" value={sitForm.color || '#10b981'} onChange={e=>setSitForm({...sitForm, color: e.target.value})} />
                                                    <input type="text" className={`flex-1 p-2.5 rounded-lg border text-sm ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={sitForm.color || '#10b981'} onChange={e=>setSitForm({...sitForm, color: e.target.value})} />
                                                </div>
                                            </div>
                                            <div>
                                                <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer font-bold text-xs transition-colors ${sitForm.requireAttachment ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-400' : 'bg-gray-50 border-gray-200 text-gray-600 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-400'}`}>
                                                    <input type="checkbox" className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500" checked={sitForm.requireAttachment || false} onChange={e=>setSitForm({...sitForm, requireAttachment: e.target.checked})} />
                                                    Exige Anexo?
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-200 dark:border-slate-700">
                                        <Button variant="secondary" onClick={() => setIsSitModalOpen(false)}>Cancelar</Button>
                                        <Button onClick={handleSaveSituation}>Salvar Situação</Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ABA REGRAS GERAIS (MANTIDA IGUAL MAS COM LISTA ZEBRADA JÁ IMPLEMENTADA) */}
                {subTab === 'config' && (
                    <div className="space-y-4">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="font-bold flex items-center gap-2"><Briefcase size={20}/> Regras de Jornada</h3>
                            <Button onClick={() => { setRuleForm({ id: null, entryTime: '08:00', exitTime: '17:30', breakDescription: '1h', workHours: '08:00', tolerance: 10, deadlineDays: 30 }); setIsRuleModalOpen(true); }} icon={Plus}>Nova Regra</Button>
                        </div>
                        <div className={`border rounded-lg overflow-hidden ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                            <table className={`w-full text-sm text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                <thead className={`text-xs uppercase font-bold ${isDarkMode ? 'bg-slate-900' : 'bg-gray-100'}`}>
                                    <tr><th className="p-3">Nome da Regra</th><th className="p-3 text-center">Jornada</th><th className="p-3 text-center">Vigência</th><th className="p-3 text-center">Prazo (Dias)</th><th className="p-3 text-center">Ações</th></tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                    {rulesList.map((rule, idx) => (
                                        <tr key={rule.id || idx} className={`${idx % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50')} hover:opacity-90`}>
                                            <td className="p-3 font-bold">{rule.name}</td>
                                            <td className="p-3 text-center">{rule.entryTime} - {rule.exitTime} ({rule.workHours})</td>
                                            <td className="p-3 text-center text-xs">{new Date(rule.validFrom).toLocaleDateString('pt-BR')} {rule.validTo ? `a ${new Date(rule.validTo).toLocaleDateString('pt-BR')}` : '(Atual)'}</td>
                                            <td className="p-3 text-center font-mono">{rule.deadlineDays}</td>
                                            <td className="p-3 text-center">
                                                <div className="flex justify-center gap-2">
                                                    <button onClick={() => { setRuleForm(rule); setIsRuleModalOpen(true); }} className="text-blue-500 p-1"><Edit3 size={16}/></button>
                                                    <button onClick={() => handleDeleteRule(rule.id)} className="text-red-500 p-1"><Trash2 size={16}/></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        
                        {/* MODAL REGRA NO MANAGEMENT MODULE (CORREÇÃO DE ERRO NO-UNDEF) */}
                                        {isRuleModalOpen && (
                                            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
                                                <div className={`p-5 rounded-xl w-full max-w-5xl shadow-2xl flex flex-col max-h-[95vh] ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`}>
                                                    <h3 className="font-bold text-lg mb-3 flex items-center gap-2 shrink-0"><Settings size={18}/> {ruleForm.id ? 'Editar Regra' : 'Nova Regra'}</h3>
                                                    
                                                    <div className="overflow-y-auto overflow-x-hidden custom-scrollbar flex-1 pr-2 space-y-4">
                                                        <div className="flex gap-3 w-full">
                                                            <div className="flex-[2]"><PontoInput label="Nome da Regra" value={ruleForm.name} onChange={e=>setRuleForm({...ruleForm, name: e.target.value})} darkMode={isDarkMode} placeholder="Ex: Jornada Padrão 2025" /></div>
                                                            <div className="flex-1"><label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Início</label><input type="date" value={ruleForm.validFrom || ''} onChange={e=>setRuleForm({...ruleForm, validFrom: e.target.value})} className={`w-full p-2.5 rounded-lg border text-sm h-[42px] outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white border-gray-300'}`} /></div>
                                                            <div className="flex-1"><label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Fim (Opcional)</label><input type="date" value={ruleForm.validTo || ''} onChange={e=>setRuleForm({...ruleForm, validTo: e.target.value})} className={`w-full p-2.5 rounded-lg border text-sm h-[42px] outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white border-gray-300'}`} /></div>
                                                        </div>
                                                        <div className="flex gap-3 w-full">
                                                            <div className="flex-1"><PontoInput label="Entrada" type="time" value={ruleForm.entryTime} onChange={e=>setRuleForm({...ruleForm, entryTime: e.target.value})} darkMode={isDarkMode} /></div>
                                                            <div className="flex-1"><PontoInput label="Saída" type="time" value={ruleForm.exitTime} onChange={e=>setRuleForm({...ruleForm, exitTime: e.target.value})} darkMode={isDarkMode} /></div>
                                                            <div className="flex-1"><PontoInput label="Início Almoço" type="time" value={ruleForm.lunchStart} onChange={e=>setRuleForm({...ruleForm, lunchStart: e.target.value})} darkMode={isDarkMode} /></div>
                                                            <div className="flex-1"><PontoInput label="Fim Almoço" type="time" value={ruleForm.lunchEnd} onChange={e=>setRuleForm({...ruleForm, lunchEnd: e.target.value})} darkMode={isDarkMode} /></div>
                                                        </div>
                                                        <div className="flex gap-3 w-full">
                                                            <div className="flex-1"><PontoInput label="Carga Diária" type="time" value={ruleForm.workHours} onChange={e=>setRuleForm({...ruleForm, workHours: e.target.value})} darkMode={isDarkMode} /></div>
                                                            <div className="flex-1"><PontoInput label="Tolerância (min)" type="number" value={ruleForm.tolerance} onChange={e=>setRuleForm({...ruleForm, tolerance: e.target.value})} darkMode={isDarkMode} /></div>
                                                            <div className="flex-[2]"><PontoInput label="Intervalo do Café" value={ruleForm.coffeeBreak} onChange={e=>setRuleForm({...ruleForm, coffeeBreak: e.target.value})} darkMode={isDarkMode} /></div>
                                                        </div>
                                                        <div className="flex gap-4 mt-2 w-full">
                                                            <div className={`flex-1 p-4 border rounded-xl shadow-inner flex flex-col ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                                <label className="text-[10px] font-bold uppercase mb-1 block text-blue-500">Formato do Texto no PDF</label>
                                                                <textarea value={ruleForm.pdfTemplate !== undefined ? ruleForm.pdfTemplate : 'BASE: {entrada} ÀS {saida} | ALMOÇO: {inicio_almoco} ÀS {fim_almoco} | CAFÉ: {cafe} | TOL: {tolerancia}m'} onChange={e => setRuleForm({...ruleForm, pdfTemplate: e.target.value})} className={`w-full p-2 border rounded-lg text-xs mb-3 outline-none resize-none flex-1 ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`} rows="3" />
                                                                <div className="flex gap-1.5 text-[9px] font-mono text-gray-500 flex-wrap"><span className="bg-black/5 dark:bg-white/10 px-1.5 py-0.5 rounded">{`{entrada}`}</span><span className="bg-black/5 dark:bg-white/10 px-1.5 py-0.5 rounded">{`{saida}`}</span><span className="bg-black/5 dark:bg-white/10 px-1.5 py-0.5 rounded">{`{inicio_almoco}`}</span><span className="bg-black/5 dark:bg-white/10 px-1.5 py-0.5 rounded">{`{fim_almoco}`}</span><span className="bg-black/5 dark:bg-white/10 px-1.5 py-0.5 rounded">{`{cafe}`}</span><span className="bg-black/5 dark:bg-white/10 px-1.5 py-0.5 rounded">{`{tolerancia}`}</span></div>
                                                            </div>
                                                            <div className="flex-1 flex flex-col gap-3 justify-center">
                                                                <div className={`p-3 border rounded-xl shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                                                    <label className="text-[9px] font-bold uppercase mb-1 block text-gray-400">Visualização Padrão</label>
                                                                    <div className="text-xs font-mono font-bold text-slate-500">BASE: {ruleForm.entryTime || '00:00'} ÀS {ruleForm.exitTime || '00:00'} | ALMOÇO: {ruleForm.lunchStart || '12:00'} ÀS {ruleForm.lunchEnd || '13:00'} | CAFÉ: {ruleForm.coffeeBreak || '-'} | TOL: {ruleForm.tolerance || 0}m</div>
                                                                </div>
                                                                <div className={`p-3 border rounded-xl shadow-sm flex-1 ${isDarkMode ? 'bg-emerald-900/20 border-emerald-800/50' : 'bg-emerald-50 border-emerald-200'}`}>
                                                                    <label className="text-[9px] font-bold uppercase mb-1 block text-emerald-500">Preview Dinâmico</label>
                                                                    <div className="text-xs font-mono font-bold text-emerald-600">
                                                                        {(() => {
                                                                            const template = ruleForm.pdfTemplate !== undefined ? ruleForm.pdfTemplate : 'BASE: {entrada} ÀS {saida} | ALMOÇO: {inicio_almoco} ÀS {fim_almoco} | CAFÉ: {cafe} | TOL: {tolerancia}m';
                                                                            return template.replace(/\{entrada\}/g, ruleForm.entryTime || '00:00').replace(/\{saida\}/g, ruleForm.exitTime || '00:00').replace(/\{inicio_almoco\}/g, ruleForm.lunchStart || '12:00').replace(/\{fim_almoco\}/g, ruleForm.lunchEnd || '13:00').replace(/\{cafe\}/g, ruleForm.coffeeBreak || '-').replace(/\{tolerancia\}/g, ruleForm.tolerance || 0);
                                                                        })()}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
                                                        <Button variant="secondary" onClick={() => setIsRuleModalOpen(false)}>Cancelar</Button>
                                                        <Button onClick={() => {
                                                            const currentTemplate = ruleForm.pdfTemplate !== undefined ? ruleForm.pdfTemplate : 'BASE: {entrada} ÀS {saida} | ALMOÇO: {inicio_almoco} ÀS {fim_almoco} | CAFÉ: {cafe} | TOL: {tolerancia}m';
                                                            const pdfString = currentTemplate.replace(/\{entrada\}/g, ruleForm.entryTime || '00:00').replace(/\{saida\}/g, ruleForm.exitTime || '00:00').replace(/\{inicio_almoco\}/g, ruleForm.lunchStart || '12:00').replace(/\{fim_almoco\}/g, ruleForm.lunchEnd || '13:00').replace(/\{cafe\}/g, ruleForm.coffeeBreak || '-').replace(/\{tolerancia\}/g, ruleForm.tolerance || 0);
                                                            const itemToSave = { ...ruleForm, id: ruleForm.id || Date.now().toString(), pdfTemplate: currentTemplate, pdfPrintString: pdfString };
                                                            
                                                            if (typeof setConfig === 'function') {
                                                                let newList = Array.isArray(config) ? [...config] : [];
                                                                if (ruleForm.id) newList = newList.map(r => r.id === ruleForm.id ? itemToSave : r);
                                                                else newList.push(itemToSave);
                                                                setConfig(newList);
                                                            }
                                                            setIsRuleModalOpen(false);
                                                        }}>Salvar Regra</Button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                    </div>
                )}
            </div>
        );
    };

    // --- MÓDULO RELÓGIO (ATUALIZADO: PERMISSÕES VINCULADAS + MODAL COMPLETO) ---


    /* ===============================================================================
     * [END_MOD_PONTO_MANAGEMENT]
     * FIM DO BLOCO: ManagementModule
     * [START_MOD_PONTO_CLOCK]
     * INICIO DO BLOCO: ClockModule
     * =============================================================================== */
    const ClockModule = ({ 
        isDarkMode, 
        effectiveUser, 
        db, 
        collaborators,
        isMockMode, 
        mockRecords, 
        setMockRecords, 
        situations, 
        permissions,
        globalConfigs, 
        config
    }) => {
        // --- TRADUTOR DE ID: Transforma o Email do Login no ID do Cadastro ---
        const myCollabId = React.useMemo(() => {
        const emailLogado = (effectiveUser?.email || '').trim().toLowerCase();
        const found = (collaborators || []).find(c => (c.email || '').trim().toLowerCase() === emailLogado);
        // Prioriza o ID do objeto colaborador do banco para bater com os registros de ponto
        return found ? String(found.id) : String(effectiveUser?.id || '');
        }, [collaborators, effectiveUser]);

        const [time, setTime] = useState(new Date());
        const [todayPunches, setTodayPunches] = useState([]);
        const [loading, setLoading] = useState(false);
        
        // Estados de Controle de Modais
        const [showManual, setShowManual] = useState(false);
        const [showJustify, setShowJustify] = useState(false);
        
        // NOVO Estado da Marcação Manual em Lote
        const [manualDate, setManualDate] = useState(new Date().toISOString().split('T')[0]);
        const [manualRecords, setManualRecords] = useState([{ id: Date.now(), type: '', time: '', obs: '' }]);

        // Estado do Formulário de Justificativa (COMPLETO)
        const [justifyData, setJustifyData] = useState({ 
            date: '', 
            endDate: '', 
            isPeriod: false, // Checkbox para indicar se é um intervalo de dias
            timeStart: '', 
            timeEnd: '', 
            situationId: '', 
            obs: '', 
            file: null 
        });

        // --- LÓGICA DE PERMISSÕES (ITEM 2 DO PEDIDO) ---
        const isSuperAdm = effectiveUser.role === 'SuperAdm';
        
        // Verifica explicitamente se a permissão existe e é true. 
        // Se for undefined ou false, bloqueia (a menos que seja SuperAdm).
        const canManual = isSuperAdm || (permissions && permissions.ponto_can_manual === true);
        const canJustify = isSuperAdm || (permissions && permissions.ponto_can_justify === true);

        // Atualiza o relógio a cada segundo
        useEffect(() => {
            const timer = setInterval(() => {
                setTime(new Date());
            }, 1000);
            
            return () => clearInterval(timer);
        }, []);

    // Carrega registros do dia com Blindagem de Cache Local
        useEffect(() => {
            const todayStr = new Date().toISOString().split('T')[0];
            
            // 1. Tenta carregar imediatamente do cache local para não piscar a tela
            const cachedPunches = localStorage.getItem(`punches_${myCollabId}_${todayStr}`);
            if (cachedPunches) {
                setTodayPunches(JSON.parse(cachedPunches));
            }

            if (isMockMode) {
                const todayPunchesList = mockRecords.filter(p => p.date === todayStr && p.userId === myCollabId);
                setTodayPunches(todayPunchesList);
                localStorage.setItem(`punches_${myCollabId}_${todayStr}`, JSON.stringify(todayPunchesList));
            } else if (db) {
                const q = query(collection(db, `${FIRESTORE_PATH_PUBLIC}/time_records`), where('userId', '==', myCollabId), where('date', '==', todayStr));
                const unsubscribe = onSnapshot(q, (snapshot) => {
                    const recs = snapshot.docs.map(d => ({id: d.id, ...d.data()}));
                    const filtered = recs.filter(r => r.type !== 'Justificativa' && r.type !== 'Observation' && r.type !== 'RHNote' && r.type !== 'DayNote').sort((a,b) => (a.time || '').localeCompare(b.time || ''));
                    setTodayPunches(filtered);
                    // 2. Atualiza o cache sempre que o banco atualizar
                    localStorage.setItem(`punches_${myCollabId}_${todayStr}`, JSON.stringify(filtered));
                });
                return () => unsubscribe();
            }
        }, [myCollabId, isMockMode, db]);

        // Função de Salvar Genérica
        const saveRecord = async (record) => {
            if (isMockMode) {
                setMockRecords(prev => [...prev, record]);
            } else if (db) {
                // CORREÇÃO: Usar setDoc para garantir que o Firestore use o ID exato do registro. 
                // Isso previne que as marcações fiquem com IDs aleatórios, permitindo editar/excluir corretamente!
                await setDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/time_records`, record.id), record);
            }
        };

        // Lógica do Rótulo do Botão Principal
        const getNextPunchLabel = (count) => {
            const standard = ['Entrada', 'Saída Almoço', 'Volta Almoço', 'Saída'];
            if (count < 4) {
                return standard[count];
            }
            if (count % 2 === 0) {
                return 'Entrada Extra';
            } else {
                return 'Saída Extra';
            }
        };
        
        const nextLabel = getNextPunchLabel(todayPunches.length);

        // Handler: Bater Ponto Automático (Digital)
        const handlePunch = async () => {
            setLoading(true);
            await new Promise(r => setTimeout(r, 500));
            
            const now = new Date();
            const currentType = getNextPunchLabel(todayPunches.length);
            const timeStr = formatTimePonto(now);
            
            // Aplica Regras de Aprovação (Atrasos/Adiantos)
            const settings = globalConfigs?.find(c => c.id === 'ponto_settings')?.rules || {};
            let needsApproval = false;

            let expectedTime = null;
            if (config) {
                if (currentType === 'Entrada') expectedTime = config.entryTime;
                else if (currentType === 'Saída Almoço') expectedTime = config.lunchStart;
                else if (currentType === 'Volta Almoço') expectedTime = config.lunchEnd;
                else if (currentType === 'Saída') expectedTime = config.exitTime;
            }

            if (expectedTime) {
                const diffMins = Math.abs(timeToMinutesPonto(timeStr) - timeToMinutesPonto(expectedTime));
                if (settings.delayX?.requireApproval && diffMins > (parseInt(settings.delayX.limit) || 15)) {
                    needsApproval = true;
                } else if (settings.delay10?.requireApproval && diffMins > 0 && diffMins <= 10) {
                    needsApproval = true;
                }
            }
            
            const record = { 
                id: Date.now().toString(), 
                userId: myCollabId, 
                date: now.toISOString().split('T')[0], 
                time: timeStr, 
                type: currentType, 
                timestamp: now.toISOString(), 
                source: 'digital',
                status: needsApproval ? 'Pending' : 'Approved',
                managerNotified: false
            };
            
            await saveRecord(record);
            
            // BLINDAGEM: Salva imediatamente no cache local também
            const updatedPunches = [...todayPunches, record].sort((a,b) => (a.time || '').localeCompare(b.time || ''));
            setTodayPunches(updatedPunches);
            const todayStr = now.toISOString().split('T')[0];
            localStorage.setItem(`punches_${myCollabId}_${todayStr}`, JSON.stringify(updatedPunches));

            setLoading(false);

            if (needsApproval) alert(`Batida registrada fora da tolerância. Enviada para aprovação do gestor.`);
        };

        // Handler: Salvar Manual (EM LOTE)
        const handleManualSave = async () => {
            if (!manualDate) return alert("Selecione a data da marcação.");
            if (manualRecords.length === 0) return alert("Adicione pelo menos um registro.");

            // Validação
            for (let rec of manualRecords) {
                if (!rec.type || !rec.time || !rec.obs.trim()) {
                    return alert("Preencha o Tipo, Horário e Motivo para TODOS os registros da lista.");
                }
            }

            const settings = globalConfigs?.find(c => c.id === 'ponto_settings')?.rules || {};
            let someNeedsApproval = false;
            const todayStr = new Date().toISOString().split('T')[0];
            let currentPunches = [...todayPunches];

            try {
                for (let i = 0; i < manualRecords.length; i++) {
                    const rec = manualRecords[i];
                    let needsApproval = false;

                    if (settings.manualAll?.requireApproval) {
                        needsApproval = true;
                    } else if (settings.manualX?.requireApproval) {
                        const limit = parseInt(settings.manualX.limit) || 15;
                        let expectedTime = null;
                        if (config) {
                            if (rec.type === 'Entrada') expectedTime = config.entryTime;
                            else if (rec.type === 'Saída Almoço') expectedTime = config.lunchStart;
                            else if (rec.type === 'Volta Almoço') expectedTime = config.lunchEnd;
                            else if (rec.type === 'Saída') expectedTime = config.exitTime;
                        }
                        if (expectedTime) {
                            const diffMins = Math.abs(timeToMinutesPonto(rec.time) - timeToMinutesPonto(expectedTime));
                            if (diffMins > limit) needsApproval = true;
                        }
                    }
                    
                    if (needsApproval) someNeedsApproval = true;

                    // ID diferente para não sobrepor se gravar no mesmo milissegundo
                    const docId = Date.now().toString() + i; 

                    const record = { 
                        id: docId, 
                        userId: myCollabId, 
                        date: manualDate, 
                        time: rec.time, 
                        type: rec.type, 
                        obs: rec.obs, 
                        timestamp: `${manualDate}T${rec.time}:00.000Z`, 
                        source: 'manual',
                        status: needsApproval ? 'Pending' : 'Approved', 
                        managerNotified: false 
                    };
                    
                    await saveRecord(record);
                    
                    // Atualiza cache em tempo real se for hoje
                    if (manualDate === todayStr) {
                        currentPunches.push(record);
                    }
                }

                if (manualDate === todayStr) {
                    currentPunches.sort((a,b) => (a.time || '').localeCompare(b.time || ''));
                    setTodayPunches(currentPunches);
                    localStorage.setItem(`punches_${myCollabId}_${todayStr}`, JSON.stringify(currentPunches));
                }

                setShowManual(false);
                setManualRecords([{ id: Date.now(), type: '', time: '', obs: '' }]);
                
                if (someNeedsApproval) alert("Alguns pontos manuais exigem aprovação do gestor e foram enviados!");
                else alert("Registros manuais inseridos com sucesso.");

            } catch (e) {
                console.error("Erro ao salvar lote manual:", e);
                alert("Erro ao gravar os registros.");
            }
        };

        // Handler: Salvar Justificativa (RESTAURADO E ATUALIZADO)
        const handleJustifySave = async () => {
            if (!justifyData.situationId) return alert("Selecione o motivo (Situação).");
            if (!justifyData.date) return alert("Informe a data inicial.");

            let intervalText = "";
            if (justifyData.isPeriod) {
                if (!justifyData.endDate) return alert("Informe a data final do período.");
                intervalText = `Período: ${new Date(justifyData.date).toLocaleDateString('pt-BR')} até ${new Date(justifyData.endDate).toLocaleDateString('pt-BR')}`;
            } else {
                intervalText = (justifyData.timeStart || justifyData.timeEnd) 
                    ? `Dia: ${new Date(justifyData.date).toLocaleDateString('pt-BR')} | Horário: ${justifyData.timeStart || '?'} às ${justifyData.timeEnd || '?'}`
                    : `Dia Inteiro: ${new Date(justifyData.date).toLocaleDateString('pt-BR')}`;
            }

            const situationName = situations.find(s => s.id === justifyData.situationId)?.name || 'N/D';

            // Aplica Regra de Aprovação Dinâmica
            const settings = globalConfigs?.find(c => c.id === 'ponto_settings')?.rules || {};
            let needsApproval = true;
            const sitNameLower = situationName.toLowerCase();
            
            if (sitNameLower.includes('atestado') || sitNameLower.includes('médic') || sitNameLower.includes('medic')) {
                needsApproval = settings.certificate?.requireApproval ?? true;
            } else if (sitNameLower.includes('falta') || sitNameLower.includes('ausência') || sitNameLower.includes('ausencia')) {
                needsApproval = settings.absence?.requireApproval ?? true;
            } else {
                needsApproval = settings.otherJust?.requireApproval ?? true;
            }
            
            const statusRecord = needsApproval ? 'Pending' : 'Approved';

            const justifyPayload = {
                id: Date.now().toString(),
                userId: myCollabId,
                date: justifyData.date, 
                time: '00:00', 
                type: 'Justificativa',
                category: situationName,
                details: intervalText,
                obs: justifyData.obs,
                timestamp: new Date().toISOString(),
                source: 'digital',
                status: statusRecord,
                managerNotified: false 
            };

            await saveRecord(justifyPayload);
            
            if (needsApproval) {
                alert(`Justificativa enviada para aprovação com sucesso!\n\n${intervalText}`);
            } else {
                alert(`Justificativa registrada e validada com sucesso!\n\n${intervalText}`);
            }
            setShowJustify(false);
            
            setJustifyData({ date: '', endDate: '', isPeriod: false, timeStart: '', timeEnd: '', situationId: '', obs: '', file: null });
        };

        return (
            <div className="flex flex-col items-center justify-center py-6 animate-fade-in relative">
                {/* Relógio Digital */}
                <div className={`text-6xl md:text-8xl font-black tracking-tighter mb-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                    {formatTimePonto(time)}
                </div>
                <div className={`text-lg uppercase tracking-widest font-bold mb-8 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    {time.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
                </div>
                
                {/* Botões de Ação */}
                <div className="flex flex-wrap items-center justify-center gap-10 w-full max-w-4xl mt-6">
                    {/* 1. BOTÃO PRINCIPAL: BATER PONTO */}
                    <button 
                        onClick={handlePunch} 
                        disabled={loading} 
                        className={`w-36 h-36 rounded-full shadow-2xl border-4 border-white/20 flex flex-col items-center justify-center gap-2 transition-all duration-300 transform hover:scale-105 active:scale-95 z-10 text-white bg-gradient-to-br from-blue-600 to-cyan-500 hover:shadow-cyan-500/40 ${loading ? 'opacity-70 cursor-wait' : ''}`}
                    >
                        <Fingerprint size={48} strokeWidth={1.5} className="drop-shadow-md" />
                        <span className="text-[10px] font-black uppercase tracking-widest leading-none text-center px-1 drop-shadow-md">
                            {loading ? '...' : nextLabel}
                        </span>
                    </button>

                    {/* 2. BOTÃO JUSTIFICAR (SÓ APARECE SE TIVER PERMISSÃO) */}
                    {canJustify && (
                        <button 
                            onClick={() => setShowJustify(true)} 
                            className="w-36 h-36 rounded-full shadow-xl border-4 border-white/20 flex flex-col items-center justify-center gap-2 transition-all duration-300 transform hover:scale-105 active:scale-95 text-white bg-gradient-to-br from-emerald-500 to-teal-400 hover:shadow-teal-500/40"
                        >
                            <FileUp size={32} className="drop-shadow-md" />
                            <span className="text-[10px] font-bold uppercase tracking-wider drop-shadow-md">Justificar</span>
                        </button>
                    )}

                    {/* 3. BOTÃO MANUAL (SÓ APARECE SE TIVER PERMISSÃO) */}
                    {canManual && (
                        <button 
                            onClick={() => {
                                setManualDate(new Date().toISOString().split('T')[0]);
                                setManualRecords([{ id: Date.now(), type: '', time: '', obs: '' }]);
                                setShowManual(true);
                            }} 
                            className="w-36 h-36 rounded-full shadow-xl border-4 border-white/20 flex flex-col items-center justify-center gap-2 transition-all duration-300 transform hover:scale-105 active:scale-95 text-white bg-gradient-to-br from-orange-500 to-amber-400 hover:shadow-orange-500/40"
                        >
                            <Edit3 size={32} className="drop-shadow-md" />
                            <span className="text-[10px] font-bold uppercase tracking-wider drop-shadow-md">Manual</span>
                        </button>
                    )}
                </div>

                {/* Histórico Visual do Dia */}
                <div className="mt-12 flex gap-4 flex-wrap justify-center">
                    {todayPunches.map((p, i) => {
                        let cardColors = isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-slate-800';
                        let typeColor = isDarkMode ? 'text-slate-400' : 'text-slate-500';
                        
                        if (!isDarkMode) {
                            if (i === 0 || i === 2) { cardColors = 'bg-blue-600 border-blue-700 text-white shadow-md'; typeColor = 'text-blue-100'; }
                            else if (i === 1 || i === 3) { cardColors = 'bg-orange-500 border-orange-600 text-white shadow-md'; typeColor = 'text-orange-100'; }
                            else if (i % 2 === 0) { cardColors = 'bg-purple-600 border-purple-700 text-white shadow-md'; typeColor = 'text-purple-100'; }
                            else { cardColors = 'bg-pink-600 border-pink-700 text-white shadow-md'; typeColor = 'text-pink-100'; }
                        }

                        return (
                        <div key={i} className={`flex flex-col items-center p-3 rounded-lg border min-w-[90px] animate-fade-in ${cardColors}`}>
                            <span className={`text-[10px] uppercase font-bold mb-1 ${typeColor}`}>
                                {p.type}
                            </span>
                            <span className="text-lg font-bold font-mono">
                                {p.time}
                            </span>
                            {p.source === 'manual' && (
                                <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold mt-1 ${isDarkMode ? 'text-orange-500' : 'bg-black/20 text-white'}`}>MANUAL</span>
                            )}
                        </div>
                    )})}
                </div>
                
                {/* --- MODAL: REGISTRO MANUAL EM LOTE --- */}
                {showManual && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                        <div className={`p-5 rounded-xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[95vh] ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`}>
                            <h3 className="font-bold text-lg mb-4 flex items-center gap-2 shrink-0"><Clock size={18}/> Registro Manual de Ponto</h3>
                            
                            <div className="overflow-y-auto overflow-x-hidden custom-scrollbar flex-1 pr-2 space-y-4">
                                
                                <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Data (Aplicada a todos os registros abaixo)</label>
                                    <input 
                                        type="date" 
                                        value={manualDate} 
                                        onChange={e => setManualDate(e.target.value)} 
                                        max={new Date().toISOString().split('T')[0]} 
                                        className={`w-48 p-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white invert-calendar focus:border-blue-500' : 'bg-white border-gray-300 focus:border-blue-500'}`} 
                                    />
                                </div>

                                <div className="space-y-3">
                                    {manualRecords.map((rec, index) => {
                                        const punchOptions = ['Entrada', 'Saída Almoço', 'Volta Almoço', 'Saída', 'Entrada Extra', 'Saída Extra'];
                                        
                                        const todayStr = new Date().toISOString().split('T')[0];
                                        const todayUsed = manualDate === todayStr ? todayPunches.map(p => p.type) : [];
                                        const usedTypes = [...todayUsed, ...manualRecords.filter(r => r.id !== rec.id).map(r => r.type)];
                                        const availableTypes = punchOptions.filter(t => !usedTypes.includes(t) || t === rec.type);

                                        const isLast = index === manualRecords.length - 1;

                                        return (
                                            <div key={rec.id} className={`flex flex-wrap md:flex-nowrap items-end gap-2 p-2 rounded-lg border transition-all ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-gray-50'}`}>
                                                <div className="w-full md:w-40">
                                                    <label className={`text-[9px] font-bold uppercase block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Tipo de Batida</label>
                                                    <select 
                                                        value={rec.type} 
                                                        onChange={e => setManualRecords(manualRecords.map(r => r.id === rec.id ? {...r, type: e.target.value} : r))}
                                                        className={`w-full p-2.5 rounded border text-xs outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                                                    >
                                                        <option value="">Selecione...</option>
                                                        {availableTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                                    </select>
                                                </div>
                                                <div className="w-full md:w-32">
                                                    <label className={`text-[9px] font-bold uppercase block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Horário</label>
                                                    <input 
                                                        type="time" 
                                                        value={rec.time} 
                                                        onChange={e => setManualRecords(manualRecords.map(r => r.id === rec.id ? {...r, time: e.target.value} : r))}
                                                        className={`w-full p-2.5 rounded border text-xs font-bold outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                                                    />
                                                </div>
                                                <div className="flex-1 min-w-[200px]">
                                                    <label className={`text-[9px] font-bold uppercase block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Motivo / Justificativa</label>
                                                    <input 
                                                        type="text" 
                                                        value={rec.obs} 
                                                        onChange={e => setManualRecords(manualRecords.map(r => r.id === rec.id ? {...r, obs: e.target.value} : r))}
                                                        placeholder="Descreva o motivo..."
                                                        className={`w-full p-2.5 rounded border text-xs outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                                                    />
                                                </div>
                                                
                                                <div className="flex gap-1 w-full md:w-auto justify-end mt-2 md:mt-0">
                                                    {(manualRecords.length > 1) && (
                                                        <button 
                                                            onClick={() => setManualRecords(manualRecords.filter(r => r.id !== rec.id))} 
                                                            className="p-2 h-[38px] rounded border border-red-200 bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-700 transition-colors"
                                                            title="Excluir Registro"
                                                        >
                                                            <Trash2 size={16}/>
                                                        </button>
                                                    )}
                                                    {isLast && manualRecords.length < 6 && (
                                                        <button 
                                                            onClick={() => setManualRecords([...manualRecords, { id: Date.now(), type: '', time: '', obs: '' }])}
                                                            className="p-2 h-[38px] rounded border border-blue-300 bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-800 transition-colors shadow-sm"
                                                            title="Adicionar Mais"
                                                        >
                                                            <Plus size={18} strokeWidth={3}/>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-slate-700 shrink-0">
                                <Button variant="secondary" onClick={() => setShowManual(false)} className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}>Cancelar</Button>
                                <Button onClick={handleManualSave}>Enviar Registros</Button>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* --- MODAL: JUSTIFICATIVA (RESTAURADO COMPLETO) --- */}
                {showJustify && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
                        <div className={`p-6 rounded-xl w-full max-w-md shadow-2xl overflow-y-auto max-h-[90vh] ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`}>
                            <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><FileUp size={20}/> Justificar Ausência</h3>
                            
                            <div className="space-y-4">
                                {/* Seleção do Motivo */}
                                <div>
                                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Motivo (Situação)</label>
                                    <select className={`w-full p-2.5 rounded-lg border text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={justifyData.situationId} onChange={e=>setJustifyData({...justifyData, situationId: e.target.value})}>
                                        <option value="">Selecione...</option>
                                        {situations.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                    </select>
                                </div>

                                {/* Área de Data e Hora */}
                                <div className={`p-3 rounded border ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                    <div className="flex items-center gap-2 mb-3">
                                        <input type="checkbox" id="chkPeriod" checked={justifyData.isPeriod} onChange={e=>setJustifyData({...justifyData, isPeriod: e.target.checked})} className="rounded text-blue-600"/>
                                        <label htmlFor="chkPeriod" className="text-xs font-bold cursor-pointer select-none">É um período de afastamento (vários dias)?</label>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-[10px] font-bold block mb-1">Início *</label>
                                            <input type="date" className={`w-full p-2 rounded border text-xs ${isDarkMode ? 'bg-slate-800 border-slate-600 invert-calendar' : 'bg-white'}`} value={justifyData.date} onChange={e=>setJustifyData({...justifyData, date: e.target.value})} />
                                        </div>
                                        {justifyData.isPeriod && (
                                            <div>
                                                <label className="text-[10px] font-bold block mb-1">Fim *</label>
                                                <input type="date" className={`w-full p-2 rounded border text-xs ${isDarkMode ? 'bg-slate-800 border-slate-600 invert-calendar' : 'bg-white'}`} value={justifyData.endDate} onChange={e=>setJustifyData({...justifyData, endDate: e.target.value})} />
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Horários (Apenas se não for período de dias) */}
                                    {!justifyData.isPeriod && (
                                        <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-dashed border-gray-300 dark:border-slate-700">
                                            <div>
                                                <label className="text-[10px] font-bold block mb-1">Hora Início (Opcional)</label>
                                                <input type="time" className={`w-full p-2 rounded border text-xs ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white'}`} value={justifyData.timeStart} onChange={e=>setJustifyData({...justifyData, timeStart: e.target.value})} />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-bold block mb-1">Hora Fim (Opcional)</label>
                                                <input type="time" className={`w-full p-2 rounded border text-xs ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white'}`} value={justifyData.timeEnd} onChange={e=>setJustifyData({...justifyData, timeEnd: e.target.value})} />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Observação */}
                                <div>
                                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Observação</label>
                                    <textarea className={`w-full p-2.5 rounded-lg border text-sm outline-none resize-none h-20 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={justifyData.obs} onChange={e=>setJustifyData({...justifyData, obs: e.target.value})} placeholder="Detalhes adicionais..."></textarea>
                                </div>
                                
                                {/* Anexo */}
                                <div>
                                    <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Anexo (Atestado/Comprovante)</label>
                                    <input type="file" className={`w-full text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`} onChange={e=>setJustifyData({...justifyData, file: e.target.files[0]})} />
                                </div>
                            </div>

                            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-200 dark:border-slate-700">
                                <Button variant="secondary" onClick={() => setShowJustify(false)} className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}>Cancelar</Button>
                                <Button variant="success" onClick={handleJustifySave} icon={FileUp}>Enviar Justificativa</Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // --- MÓDULO BANCO DE HORAS (SISTEMA RH EXPERT + APROVAÇÕES E DATA CORRIGIDA) ---


    /* ===============================================================================
     * [END_MOD_PONTO_CLOCK]
     * FIM DO BLOCO: ClockModule
     * [START_MOD_PONTO_BANK]
     * INICIO DO BLOCO: BankModule
     * =============================================================================== */
    const BankModule = ({ 
        isDarkMode, 
        effectiveUser, 
        db, 
        isMockMode, 
        mockRecords, 
        setMockRecords,
        mockSignatures, 
        setMockSignatures, 
        employeesMeta, 
        config, 
        logoUrl, 
        collaborators,
        logAction,
        departments 
    }) => {
        // --- TRADUTOR DE ID ---
        const myCollabId = React.useMemo(() => {
        const emailLogado = (effectiveUser?.email || '').trim().toLowerCase();
        const found = (collaborators || []).find(c => (c.email || '').trim().toLowerCase() === emailLogado);
        // Prioriza o ID do objeto colaborador do banco para bater com os registros de ponto
        return found ? String(found.id) : String(effectiveUser?.id || '');
        }, [collaborators, effectiveUser]);

        // 1. ESTADOS DE FILTRO E USUÁRIO
        const [periodType, setPeriodType] = React.useState('month'); 
        const [refMonth, setRefMonth] = React.useState(new Date().toISOString().slice(0, 7));
        const [customStart, setCustomStart] = React.useState(new Date().toISOString().slice(0, 7) + '-01');
        const [customEnd, setCustomEnd] = React.useState(new Date().toISOString().split('T')[0]);
        const [targetUserId, setTargetUserId] = React.useState(myCollabId);

                // Garante que o ID inicial seja o do colaborador logado, mas permite trocas fluidas pelo Gestor [cite: 1218, 1220]
            React.useEffect(() => {
                if (!targetUserId || targetUserId === 'undefined') {
                    setTargetUserId(myCollabId);
                }
            }, [myCollabId]);

        // 2. ESTADOS DE DADOS
        const [rawPunches, setRawPunches] = React.useState([]); 
        const [isProcessing, setIsProcessing] = React.useState(false);
            
        const [dateFilterMode, setDateFilterMode] = React.useState('month'); // 'month' | 'period'
        const [monthReference, setMonthReference] = React.useState(() => {
            const now = new Date();
            const mm = String(now.getMonth() + 1).padStart(2, '0');
            const yyyy = now.getFullYear();
            return `${mm}/${yyyy}`;
        });
        const [periodStart, setPeriodStart] = React.useState('');
        const [periodEnd, setPeriodEnd] = React.useState('');

        // 3. ESTADOS DE INTERFACE E ASSINATURAS
        const [isSignModalOpen, setIsSignModalOpen] = React.useState(false);
        const [isManagerListOpen, setIsManagerListOpen] = React.useState(false);
        const [pendingSignaturesList, setPendingSignaturesList] = React.useState([]);
        const [signatureData, setSignatureData] = React.useState(null);
        
        // --- NOVOS ESTADOS: ABA DE ASSINATURAS ---
        const [signModalTab, setSignModalTab] = React.useState('revisao');
        const [mySignaturesList, setMySignaturesList] = React.useState([]);

        // Efeito para carregar histórico de assinaturas do usuário
        React.useEffect(() => {
            if (!db || !targetUserId || !isSignModalOpen) return;
            const q = query(collection(db, `${FIRESTORE_PATH_PUBLIC}/point_signatures`), where('userId', '==', targetUserId));
            const unsub = onSnapshot(q, (snap) => {
                setMySignaturesList(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b) => b.month.localeCompare(a.month)));
            });
            return () => unsub();
        }, [db, targetUserId, isSignModalOpen]);
        
        // 4. EDIÇÃO E NOTAS (RH)
        const [editingDayPunches, setEditingDayPunches] = React.useState(null);
        const [dayNoteModal, setDayNoteModal] = React.useState({ isOpen: false, date: '', display: '', text: '', noteId: null, type: 'RHNote' });
        const [localNotification, setLocalNotification] = React.useState(null); 
        const [deleteConfirmation, setDeleteConfirmation] = React.useState(null); 

        // 5. NOVO: WORKFLOW DE APROVAÇÃO DO GESTOR
        const [isApprovalModalOpen, setIsApprovalModalOpen] = React.useState(false);
        const [pendingRequests, setPendingRequests] = React.useState([]);
        const [approvalNote, setApprovalNote] = React.useState('');
        const [approvalFilterDept, setApprovalFilterDept] = React.useState('');
        const [approvalSearch, setApprovalSearch] = React.useState('');
        
        // --- ESTADO: MODAL DE REJEIÇÃO / CORREÇÃO DE INCONSISTÊNCIA ---
        const [rejectConfig, setRejectConfig] = React.useState({ isOpen: false, req: null, action: 'delete', newTime: '', reason: '' });
        
        // 6. INCONSISTÊNCIAS (NOVO)
        const [isInconsistenciesModalOpen, setIsInconsistenciesModalOpen] = React.useState(false);
        const [incFilters, setIncFilters] = React.useState({
            startDate: new Date().toISOString().split('T')[0].slice(0, 8) + '01',
            endDate: new Date().toISOString().split('T')[0],
            dept: '',
            type: 'Todos',
            search: ''
        });
        const [incRecords, setIncRecords] = React.useState([]);

        React.useEffect(() => {
            if (!isInconsistenciesModalOpen || !db) return;
            const q = query(
                collection(db, `${FIRESTORE_PATH_PUBLIC}/time_records`), 
                where('date', '>=', incFilters.startDate), 
                where('date', '<=', incFilters.endDate)
            );
            const unsub = onSnapshot(q, (snap) => {
                const loaded = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                // Pega apenas as inconsistências
                const inc = loaded.filter(r => 
                    r.source === 'manual' || 
                    r.type === 'Justificativa' || 
                    r.status === 'Pending'
                );
                setIncRecords(inc);
            });
            return () => unsub();
        }, [isInconsistenciesModalOpen, incFilters.startDate, incFilters.endDate, db]);

        const processedInconsistencies = React.useMemo(() => {
            return incRecords.filter(r => {
                const collab = collaborators.find(c => c.id === r.userId);
                if (!collab) return false;
                
                // Se for gestor, só vê do próprio departamento (SuperAdm vê tudo)
                if (effectiveUser.role !== 'SuperAdm' && collab.departmentId !== effectiveUser.departmentId) return false;

                if (incFilters.dept && collab.departmentId !== incFilters.dept) return false;

                const isManual = r.source === 'manual';
                const isJustif = r.type === 'Justificativa';
                const isDivergent = r.source === 'digital' && r.status === 'Pending';

                if (incFilters.type === 'Manual' && !isManual) return false;
                if (incFilters.type === 'Justificativa' && !isJustif) return false;
                if (incFilters.type === 'Divergente' && !isDivergent) return false;

                if (incFilters.search) {
                    const s = incFilters.search.toLowerCase();
                    if (
                        !collab.name.toLowerCase().includes(s) &&
                        !(r.obs || '').toLowerCase().includes(s) &&
                        !(r.category || '').toLowerCase().includes(s)
                    ) return false;
                }

                return true;
            }).map(r => {
                const collab = collaborators.find(c => c.id === r.userId);
                const deptName = departments?.find(d => d.id === collab?.departmentId)?.name || 'N/D';
                
                let tipoLabel = '';
                if (r.type === 'Justificativa') tipoLabel = 'Justificativa';
                else if (r.source === 'manual') tipoLabel = `Manual (${r.type})`;
                else tipoLabel = `Divergente (${r.type})`;

                return {
                    ...r,
                    collabName: collab?.name || 'Desconhecido',
                    deptName,
                    tipoLabel
                };
            }).sort((a,b) => new Date(b.timestamp || b.date) - new Date(a.timestamp || a.date));
        }, [incRecords, collaborators, departments, incFilters, effectiveUser]);

        const exportIncPDF = async () => {
            try {
                if (typeof loadPdfLibs !== 'undefined') await loadPdfLibs();
                const PDFConstructor = (window.jspdf && window.jspdf.jsPDF) ? window.jspdf.jsPDF : window.jsPDF;
                const doc = new PDFConstructor('l', 'mm', 'a4');
                
                doc.setFontSize(14);
                doc.text("Relatório de Inconsistências e Justificativas", 14, 20);
                doc.setFontSize(10);
                doc.text(`Período: ${incFilters.startDate.split('-').reverse().join('/')} a ${incFilters.endDate.split('-').reverse().join('/')}`, 14, 28);

                const tableBody = processedInconsistencies.map(r => [
                    new Date(r.date + 'T12:00:00').toLocaleDateString('pt-BR'),
                    r.collabName,
                    r.deptName,
                    r.tipoLabel,
                    r.time || '-',
                    (r.category ? `[${r.category}] ` : '') + (r.obs || r.details || '')
                ]);

                doc.autoTable({
                    startY: 35,
                    head: [['Data', 'Colaborador', 'Departamento', 'Tipo', 'Horário/Detalhe', 'Histórico/Obs']],
                    body: tableBody,
                    theme: 'grid',
                    styles: { fontSize: 8 },
                    headStyles: { fillColor: [220, 38, 38] }
                });

                doc.save(`Inconsistencias_${incFilters.startDate}_a_${incFilters.endDate}.pdf`);
            } catch(e) { alert("Erro ao exportar PDF: " + e.message); }
        };

        const exportIncExcel = async () => {
            try {
                if (typeof loadImportLibs !== 'undefined') await loadImportLibs();
                const dataExport = processedInconsistencies.map(r => ({
                    "Data": new Date(r.date + 'T12:00:00').toLocaleDateString('pt-BR'),
                    "Colaborador": r.collabName,
                    "Departamento": r.deptName,
                    "Tipo Ocorrência": r.tipoLabel,
                    "Horário/Detalhe": r.time || '-',
                    "Categoria (Justif)": r.category || '-',
                    "Observação / Motivo": r.obs || r.details || '-'
                }));

                const ws = window.XLSX.utils.json_to_sheet(dataExport);
                const wb = window.XLSX.utils.book_new();
                window.XLSX.utils.book_append_sheet(wb, ws, "Inconsistencias");
                window.XLSX.writeFile(wb, `Inconsistencias_${incFilters.startDate}_a_${incFilters.endDate}.xlsx`);
            } catch(e) { alert("Erro ao exportar Excel: " + e.message); }
        };

    // --- HELPER LOCAL DE PERMISSÃO ---
        // MOVIDO PARA CIMA: A declaração deve vir antes de isGestor e subordinates
        const hasPermBank = (key) => effectiveUser.role === 'SuperAdm' || effectiveUser.permissions?.[key] === true;

        // Variáveis Derivadas
        const isGestor = effectiveUser.role === 'Gestor' || effectiveUser.role === 'SuperAdm' || effectiveUser.role === 'ADM' || hasPermBank('ponto_btn_requests');
        const isViewingOthers = String(targetUserId) !== String(myCollabId);

        const canViewInconsistencies = effectiveUser.role === 'SuperAdm' || effectiveUser.permissions?.ponto_view_inconsistencies === true;

        const notify = (type, text) => {
            setLocalNotification({ type, text });
            setTimeout(() => setLocalNotification(null), 4000);
        };

        // REGRA DE VISIBILIDADE: Gestão (RH, DIR, SuperAdm ou Permissão Especial)
        const subordinates = React.useMemo(() => {
            const userDeptName = departments?.find(d => d.id === effectiveUser.departmentId)?.name?.toUpperCase() || '';
            const isRH = userDeptName.includes('RH') || userDeptName.includes('RECURSOS HUMANOS');
            const isDIR = userDeptName.includes('DIR') || userDeptName.includes('DIRETORIA');
            const hasGlobalView = hasPermBank('ponto_view_all');
            const isSuper = effectiveUser.role === 'SuperAdm';

            const canViewAll = isSuper || isRH || isDIR || hasGlobalView;

            if (canViewAll) {
                return collaborators.filter(c => c.id !== effectiveUser.id);
            }
            
            // Regra Padrão: Vê o próprio departamento ou subordinados diretos
            return collaborators.filter(c => {
                if (c.id === effectiveUser.id) return false;
                const isSameDept = c.departmentId && c.departmentId === effectiveUser.departmentId;
                const isManagedByMe = c.managerIds && c.managerIds.includes(effectiveUser.id);
                return isSameDept || isManagedByMe;
            });
        }, [collaborators, effectiveUser, departments]);

        // --- CARREGAR SOLICITAÇÕES PENDENTES (NOTIFICAÇÃO DO GESTOR) ---
        React.useEffect(() => {
            if (!isGestor || !db) return;
            
            // Pega APENAS os IDs da equipe (subordinados). NÃO INCLUI O USUÁRIO LOGADO.
            const subIds = subordinates.map(s => String(s.id));
            
            // Se o gestor não tem equipe abaixo dele, não tem o que aprovar
            if (subIds.length === 0) {
                setPendingRequests([]);
                return;
            }

            const q = query(
                collection(db, `${FIRESTORE_PATH_PUBLIC}/time_records`), 
                where('status', '==', 'Pending')
            );
            
            const unsub = onSnapshot(q, (snap) => {
                const reqs = snap.docs.map(d => ({id: d.id, ...d.data()}))
                                    .filter(r => subIds.includes(String(r.userId)));
                setPendingRequests(reqs);
            });
            
            return () => unsub();
        }, [isGestor, subordinates, db]);

        const processedPendingRequests = React.useMemo(() => {
            return pendingRequests.filter(req => {
                let collab = (collaborators || []).find(c => String(c.id) === String(req.userId));
                if (!collab) return false;

                // Filtro de Departamento
                if (approvalFilterDept && collab.departmentId !== approvalFilterDept) return false;

                // Filtro de Busca Livre
                if (approvalSearch) {
                    const s = approvalSearch.toLowerCase();
                    const collabName = collab.name.toLowerCase();
                    const obs = (req.obs || req.details || '').toLowerCase();
                    if (!collabName.includes(s) && !obs.includes(s)) return false;
                }

                return true;
            });
        }, [pendingRequests, collaborators, approvalFilterDept, approvalSearch]);

    // --- CARREGAR MARCAÇÕES DO USUÁRIO ALVO (MODO MOCK / TESTE) ---
        React.useEffect(() => {
            if (isMockMode) {
                setRawPunches(mockRecords.filter(r => String(r.userId) === String(targetUserId)));
            }
        }, [mockRecords, targetUserId, isMockMode]);

        
        // --- ESTADO PARA EDIÇÃO INLINE DE SOLICITAÇÕES ---
        const [editableReqs, setEditableReqs] = React.useState({});

        // --- PROCESSAR REJEIÇÃO / CORREÇÃO DE PONTO (MODAL) ---
        const handleConfirmRejectAction = async () => {
            const req = rejectConfig.req;
            try {
                if (rejectConfig.action === 'delete') {
                    // 1. Deleta a marcação pendente
                    await deleteDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/time_records`, req.id));
                    // 2. Cria a notificação para o colaborador ler na folha
                    const noteRecord = {
                        id: Date.now().toString(),
                        userId: req.userId,
                        date: req.date,
                        type: 'Observation', 
                        obs: `⚠️ Marcação de ${req.type} às ${req.time} não foi considerada. Motivo: ${rejectConfig.reason}. Favor enviar novamente com o horário correto.`,
                        timestamp: new Date().toISOString()
                    };
                    await setDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/time_records`, noteRecord.id), noteRecord);
                    notify('success', 'Marcação excluída e colaborador notificado.');
                } else if (rejectConfig.action === 'adjust') {
                    if (!rejectConfig.newTime) return alert("Preencha o novo horário.");
                    // 1. Atualiza a marcação com o horário novo e deixa o rastro
                    const updatePayload = {
                        status: 'Approved',
                        time: rejectConfig.newTime,
                        obs: (req.obs ? req.obs + ' | ' : '') + `Horário corrigido pelo Gestor (era ${req.time})`,
                        isModified: true,
                        processedBy: effectiveUser.name || effectiveUser.email || 'Sistema',
                        processedAt: new Date().toISOString()
                    };
                    await updateDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/time_records`, req.id), updatePayload);
                    notify('success', 'Horário ajustado e aprovado com sucesso!');
                }
                setRejectConfig({ isOpen: false, req: null, action: 'delete', newTime: '', reason: '' });
            } catch (e) {
                notify('error', 'Erro ao processar: ' + e.message);
            }
        };

        // --- PROCESSAR SOLICITAÇÃO (APROVAR / REJEITAR) COM EDIÇÃO ---
        const handleProcessRequest = async (req, newStatus) => {
            try {
                const finalData = editableReqs[req.id] || {};
                const finalTime = finalData.time !== undefined ? finalData.time : req.time;
                const finalObs = finalData.obs !== undefined ? finalData.obs : req.obs;

                // Cria o objeto apenas com campos válidos (Evita o erro de 'undefined' do Firebase)
                const updatePayload = {
                    status: newStatus,
                    managerNote: approvalNote,
                    processedBy: effectiveUser.name || effectiveUser.email || 'Sistema',
                    processedAt: new Date().toISOString(),
                    isModified: (finalTime !== req.time || finalObs !== req.obs)
                };

                if (finalTime !== undefined) updatePayload.time = finalTime;
                if (finalObs !== undefined) updatePayload.obs = finalObs;

                await updateDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/time_records`, req.id), updatePayload);
                
                setEditableReqs(prev => { const n = {...prev}; delete n[req.id]; return n; });
                notify('success', `Solicitação ${newStatus === 'Approved' ? 'Aprovada' : 'Rejeitada'} com sucesso!`);
                setApprovalNote('');
            } catch (error) {
                notify('error', 'Erro ao processar: ' + error.message);
            }
        };

        const dataInicioReal = React.useMemo(() => {
    let collabRecord = (collaborators || []).find(
        c => String(c.id) === String(targetUserId)
    );

    if (!collabRecord && targetUserId === effectiveUser?.id) {
        collabRecord = (collaborators || []).find(
            c => c.email?.toLowerCase() === effectiveUser?.email?.toLowerCase()
        );
    }

    const officialData = collabRecord || effectiveUser || {};
    const userMeta = employeesMeta?.[officialData.id] || employeesMeta?.[targetUserId] || {};

    const dataImplantacao =
        officialData.initialDate ||
        userMeta.initialDate ||
        '2020-01-01';

    const dataImplantacaoObj = new Date(dataImplantacao + 'T12:00:00');
    dataImplantacaoObj.setDate(dataImplantacaoObj.getDate() + 1);

    return dataImplantacaoObj.toISOString().split('T')[0];
}, [collaborators, targetUserId, effectiveUser, employeesMeta]);

        // --- EFFECT 1: CARREGAR REGISTROS DO COLABORADOR ---
        React.useEffect(() => {
            // 1. Define o ID alvo de forma robusta
            const queryTargetId = String(targetUserId || myCollabId);
            
            if (!queryTargetId || queryTargetId === 'undefined' || queryTargetId === 'null') return;

            // 2. Limpa dados anteriores para evitar "fantasmis de dados" de outro usuário
            setRawPunches([]);

            if (isMockMode) {
                const loaded = mockRecords.filter(r => String(r.userId) === queryTargetId);
                loaded.sort((a, b) => (a.date !== b.date) ? a.date.localeCompare(b.date) : (a.time || '').localeCompare(b.time || ''));
                setRawPunches(loaded);
                return;
            }

            if (!db) return;

            // 3. Removemos a trava de "Data de Implantação" da Query. 
            // Puxamos a partir de 2020 para garantir que nenhuma batida feita no período de testes fique de fora!
            const q = query(
                collection(db, `${FIRESTORE_PATH_PUBLIC}/time_records`),
                where('userId', '==', queryTargetId),
                where('date', '>=', dataInicioReal)
            );

            const unsub = onSnapshot(q, (snapshot) => {
                const loaded = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                loaded.sort((a, b) => {
                    if (a.date !== b.date) return a.date.localeCompare(b.date);
                    return (a.time || '').localeCompare(b.time || '');
                });
                setRawPunches(loaded);
            });

            return () => unsub();
        }, [targetUserId, isMockMode, db]); // Dependências super enxutas e focadas apenas no ID do alvo!


        const bankCalculation = React.useMemo(() => {
            let collabRecord = (collaborators || []).find(c => String(c.id) === String(targetUserId));
            if (!collabRecord && targetUserId === effectiveUser.id) collabRecord = (collaborators || []).find(c => c.email?.toLowerCase() === effectiveUser.email?.toLowerCase());
            const officialData = collabRecord || effectiveUser;
            const userMeta = employeesMeta[officialData.id] || employeesMeta[targetUserId] || {};

            const dataImplantacao = officialData.initialDate || userMeta.initialDate || '2020-01-01';
            const rawBalance = officialData.initialBalanceMinutes || userMeta.initialBalanceMinutes;
            const saldoInicialCadastro = parseInt(rawBalance) || 0;

            const dataImplantacaoObj = new Date(dataImplantacao + 'T12:00:00');
            dataImplantacaoObj.setDate(dataImplantacaoObj.getDate() + 1); 
            const dataInicioReal = dataImplantacaoObj.toISOString().split('T')[0];

            let viewStart = ''; let viewEnd = '';
            const todayStr = new Date().toISOString().split('T')[0];

            if (periodType === 'month') {
                const [y, m] = refMonth.split('-');
                const lastDay = new Date(y, m, 0).getDate();
                viewStart = `${refMonth}-01`; viewEnd = `${refMonth}-${lastDay}`;
            } else if (periodType === 'custom') {
                const firstDayOfMonth = `${todayStr.slice(0, 7)}-01`;
                viewStart = customStart || firstDayOfMonth; 
                viewEnd = customEnd || todayStr;
            } else { 
                viewStart = '1900-01-01'; viewEnd = todayStr;
            }

            // --- NOVA LÓGICA DO SALDO FINAL ---
            let targetFinalDate = viewEnd;
            // Se a data de hoje estiver dentro do período filtrado, o alvo é hoje.
            if (todayStr >= viewStart && todayStr <= viewEnd) {
                targetFinalDate = todayStr;
            }

            let currentDate = new Date(dataInicioReal + 'T12:00:00');
            const endViewDate = new Date(viewEnd + 'T12:00:00');

            let saldoCorrente = saldoInicialCadastro;
            let saldoAnteriorView = saldoInicialCadastro;
            let saldoFinalView = saldoInicialCadastro; // Variável nova
            let capturedAnterior = false;
            const processedRows = [];

            // APLICA REGRA DE JORNADA ESPECÍFICA DO COLABORADOR SE HOUVER
            let currentRule = Array.isArray(config) ? config[0] : config;
            if (officialData.workRuleId && officialData.workRuleId !== 'padrao') {
                const specificRule = (Array.isArray(config) ? config : []).find(r => r.id === officialData.workRuleId);
                if (specificRule) currentRule = specificRule;
            }

            const contractMins = timeToMinutesPonto(currentRule?.workHours || '08:00');
            const tolerance = parseInt(currentRule?.tolerance || 10);

            while (currentDate <= endViewDate) {
                const dateStr = currentDate.toISOString().split('T')[0];
                const isWeekend = currentDate.getDay() === 0 || currentDate.getDay() === 6;
                const dayRecords = rawPunches.filter(p => p.date === dateStr);
                
                // Exclui os tipos de notas para não confundir com batidas de ponto
                const dayPunches = dayRecords.filter(p => p.type !== 'Justificativa' && p.type !== 'DayNote' && p.type !== 'RHNote' && p.type !== 'Observation' && p.status !== 'Rejected');
                const dayJustifications = dayRecords.filter(p => p.type === 'Justificativa' && p.status !== 'Rejected');

                // Separa as anotações do RH das Observações do Dia
                const rhNotes = dayRecords.filter(p => p.type === 'RHNote' || p.type === 'DayNote');
                const obsNotes = dayRecords.filter(p => p.type === 'Observation');

                let workedMins = 0;
                // REGRA CORRIGIDA: Toda batida (manual ou digital) só entra no cálculo se estiver Aprovada
                const validPunches = dayPunches.filter(p => p.status === 'Approved' || !p.status);
                
                for(let i=0; i < validPunches.length - 1; i+=2) {
                    const s = timeToMinutesPonto(validPunches[i].time);
                    const e = timeToMinutesPonto(validPunches[i+1].time);
                    if (e > s) workedMins += (e - s);
                }

                let isFuture = dateStr > todayStr;
                let isToday = dateStr === todayStr;
                let dailyDiff = 0;
                
                // Declarado FORA do IF para que a tabela abaixo consiga ler esta variável sem erro
                const hasApprovedJustification = dayJustifications.some(j => j.status === 'Approved');

                if (isFuture) {
                    dailyDiff = workedMins; 
                } else if (isToday) {
                    // REGRA DO DIA ATUAL: Só consolida saldo negativo se tiver batido a Saída após o almoço (mínimo 4 batidas)
                    // Isso evita que o saldo diário e acumulado fiquem negativos no meio do expediente.
                    if (validPunches.length >= 4) {
                        dailyDiff = isWeekend ? workedMins : (workedMins - contractMins);
                        if (!isWeekend && Math.abs(dailyDiff) <= tolerance) dailyDiff = 0;
                        if (hasApprovedJustification && dailyDiff < 0) dailyDiff = 0;
                    } else {
                        // O dia ainda não acabou. Saldo do dia é 0 para dias úteis (não penaliza).
                        // Mas se for fim de semana, qualquer hora já trabalhada é extra e conta.
                        dailyDiff = isWeekend ? workedMins : 0;
                    }
                } else {
                    // DIAS PASSADOS: Cálculo normal de fechamento
                    dailyDiff = isWeekend ? workedMins : (workedMins - contractMins);
                    if (!isWeekend && Math.abs(dailyDiff) <= tolerance) dailyDiff = 0;
                    if (hasApprovedJustification && dailyDiff < 0) dailyDiff = 0;
                }

                if (dateStr === viewStart) {
                    saldoAnteriorView = saldoCorrente;
                    capturedAnterior = true;
                }

                saldoCorrente += dailyDiff;

                // Captura o Saldo Acumulado na Data Alvo
                if (dateStr === targetFinalDate) {
                    saldoFinalView = saldoCorrente;
                }

                if (dateStr >= viewStart && dateStr <= viewEnd) {
                    const obsList = [];
                    dayJustifications.forEach(j => {
                        let stText = j.status === 'Pending' ? ' [Em Análise]' : j.status === 'Approved' ? ' [Abonado]' : '';
                        obsList.push(`[${j.category}]${stText}${j.obs ? ` - ${j.obs}` : ''}`);
                    });
                    
                    dayPunches.forEach(p => {
                        if (p.source === 'manual') obsList.push(`(Batida Manual às ${p.time}: ${p.status === 'Pending' ? 'Em Análise' : 'Aprovada'})`);
                    });

                    const rhNoteObj = rhNotes.length > 0 ? rhNotes[rhNotes.length - 1] : null;
                    const obsNoteObj = obsNotes.length > 0 ? obsNotes[obsNotes.length - 1] : null;

                    processedRows.push({
                        date: dateStr,
                        display: currentDate.toLocaleDateString('pt-BR'),
                        week: currentDate.toLocaleDateString('pt-BR', { weekday: 'short' }).toUpperCase(),
                        punches: dayPunches, // Passa todas para renderizar (mesmo as manuais pendentes)
                        originalPunches: dayRecords.filter(p => p.type !== 'Justificativa' && p.type !== 'DayNote' && p.type !== 'RHNote' && p.type !== 'Observation'),
                        worked: minutesToTimePonto(workedMins),
                        balance: dailyDiff,
                        accumulated: saldoCorrente,
                        isWeekend,
                        justificationsText: obsList.join(' | '),
                        rhNote: rhNoteObj ? rhNoteObj.obs : '',
                        rhNoteId: rhNoteObj ? rhNoteObj.id : null,
                        obsNote: obsNoteObj ? obsNoteObj.obs : '',
                        obsNoteId: obsNoteObj ? obsNoteObj.id : null,
                        hasApprovedJustification
                    });
                }
                currentDate.setDate(currentDate.getDate() + 1);
            }

            if (!capturedAnterior) {
                saldoAnteriorView = saldoCorrente;
            }

            return { 
                rows: processedRows, 
                initial: saldoAnteriorView, 
                final: saldoFinalView 
            };

        }, [rawPunches, refMonth, periodType, customStart, customEnd, targetUserId, myCollabId, collaborators, employeesMeta, config]);

        // Vincula o resultado do cálculo às variáveis de visualização de forma segura
        const sheetData = React.useMemo(() => bankCalculation?.rows || [], [bankCalculation]);
        const periodBalance = React.useMemo(() => ({ 
            initial: bankCalculation?.initial || 0, 
            final: bankCalculation?.final || 0 
        }), [bankCalculation]);

        // --- EFFECT 3: CARREGAR ASSINATURA ---
        React.useEffect(() => {
            if (!db || !targetUserId || periodType !== 'month') { setSignatureData(null); return; }
            const sigId = `${targetUserId}_${refMonth}`;
            const unsubSig = onSnapshot(doc(db, `${FIRESTORE_PATH_PUBLIC}/point_signatures`, sigId), (docSnap) => {
                if (docSnap.exists()) setSignatureData(docSnap.data());
                else setSignatureData(null);
            });
            return () => unsubSig();
        }, [db, targetUserId, refMonth, periodType]);

        const handleSaveDayNote = async () => {
            if (!db) return;
            try {
                if (dayNoteModal.noteId) {
                    if (dayNoteModal.text.trim() === '') {
                        if (!hasPermBank('ponto_delete_obs')) return notify('error', 'Sem permissão para excluir observações.');
                        await deleteDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/time_records`, dayNoteModal.noteId));
                    } else {
                        if (!hasPermBank('ponto_edit_manual_obs')) return notify('error', 'Sem permissão para editar observações.');
                        await updateDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/time_records`, dayNoteModal.noteId), { obs: dayNoteModal.text });
                    }
                } else {
                    if (!hasPermBank('ponto_edit_manual_obs')) return notify('error', 'Sem permissão para incluir observações.');
                    if (dayNoteModal.text.trim() !== '') {
                        const record = { 
                            id: Date.now().toString(), 
                            userId: targetUserId, 
                            date: dayNoteModal.date, 
                            type: dayNoteModal.type, // Salva como 'RHNote' ou 'Observation'
                            obs: dayNoteModal.text, 
                            timestamp: new Date().toISOString() 
                        };
                        await setDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/time_records`, record.id), record);
                    }
                }
                setDayNoteModal({ isOpen: false, date: '', display: '', text: '', noteId: null, type: 'RHNote' });
                notify('success', 'Anotação gravada com sucesso!');
            } catch (error) { notify('error', 'Erro ao salvar anotação: ' + error.message); }
        };

        const handleDirectUpdate = async (punchId, newTime, oldTime, dateStr) => {
            if (!newTime || !db) return;
            try {
                await updateDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/time_records`, punchId), {
                    time: newTime, history: arrayUnion({ oldTime: oldTime, newTime: newTime, modifiedBy: effectiveUser.name, modifiedAt: new Date().toISOString() }), isModified: true 
                });
                notify('success', 'Registro atualizado.');
                if (editingDayPunches) setEditingDayPunches(prev => ({ ...prev, punches: prev.punches.map(p => p.id === punchId ? { ...p, time: newTime, isModified: true } : p) }));
            } catch (e) { notify('error', "Erro ao atualizar: " + e.message); }
        };

        const confirmDeletePunch = async () => {
            if (!deleteConfirmation) return;
            try {
                if (isMockMode) {
                    setMockRecords(prev => prev.filter(r => r.id !== deleteConfirmation.punchId));
                } else if (db) {
                    await deleteDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/time_records`, deleteConfirmation.punchId));
                }
                
                // CORREÇÃO: Força a remoção imediata visualmente na tabela e no modal
                setRawPunches(prev => prev.filter(p => p.id !== deleteConfirmation.punchId));
                
                if (editingDayPunches) {
                    setEditingDayPunches(prev => {
                        const remaining = prev.punches.filter(p => p.id !== deleteConfirmation.punchId);
                        if (remaining.length === 0) return null; // Fecha o modal se excluir a última
                        return { ...prev, punches: remaining };
                    });
                }
                notify('success', 'Batida excluída com sucesso.');
            } catch (e) { notify('error', "Erro ao excluir: " + e.message); }
            setDeleteConfirmation(null);
        };

        const requestDeletePunch = (punchId, punchDate, punchTime) => { setDeleteConfirmation({ punchId, punchDate, punchTime }); };
        
        const handleOpenSignModal = () => { if (periodType !== 'month') return notify('error', "Apenas períodos mensais fechados podem ser assinados."); setIsSignModalOpen(true); };
        
        const handleConfirmSign = async () => {
            if (!db) return notify('error', "Erro de conexão.");
            if (isProcessing) return; // Evita duplo clique
            setIsProcessing(true); 
            try {
                const isManagerSigning = isViewingOthers && isGestor;
                const sigId = `${targetUserId}_${refMonth}`;
                
                if (isManagerSigning) {
                    if (!signatureData) return notify('error', "O colaborador precisa assinar primeiro.");
                    const managerHash = `MGR-${Date.now().toString(36).toUpperCase()}`;
                    await updateDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/point_signatures`, sigId), {
                        managerSignature: { managerId: effectiveUser.id, managerName: effectiveUser.name, date: new Date().toISOString(), hash: managerHash }
                    });
                    if(logAction) logAction('ASSINATURA_PONTO_GESTOR', { gestor: effectiveUser.name, alvo: targetUserId, mes: refMonth });
                    notify('success', "Assinado pela Gestão com sucesso!");
                    setSignModalTab('assinados');
                } else {
                    const hash = `ACAD-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
                    const targetCollab = collaborators.find(c => c.id === targetUserId) || effectiveUser;
                    
                    // --- GERAÇÃO DO LINK PÚBLICO E QR CODE ---
                    let origin = window.location.origin;
                    if (!origin || origin === 'null' || origin === 'about:blank') origin = 'https://rh.suaempresa.com.br'; 
                    let publicUrl = `${origin}${window.location.pathname}?publicSheet=${sigId}`;
                    publicUrl = publicUrl.replace('https://https://', 'https://').replace('.googhttps', '.goog');
                    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(publicUrl)}`;

                    // --- PREPARA DADOS DA REGRA PARA O PDF CONGELADO ---
                    let ruleString = '';
                    if (targetCollab.workRuleId && targetCollab.workRuleId !== 'padrao') {
                        const foundRule = (Array.isArray(config) ? config : []).find(r => r.id === targetCollab.workRuleId);
                        if (foundRule && foundRule.pdfPrintString) ruleString = foundRule.pdfPrintString;
                    }
                    if (!ruleString) {
                        const defRule = Array.isArray(config) ? config[0] : config;
                        ruleString = defRule?.pdfPrintString || "BASE: 08:00 ÀS 17:30 | ALMOÇO: 12:00 ÀS 13:00 | CAFÉ: 15:15 às 15:30 | TOL: 10m";
                    }

                    const signaturePayload = {
                        id: sigId, 
                        userId: targetUserId, 
                        userName: targetCollab.name, 
                        userCpf: targetCollab.cpf || employeesMeta[targetUserId]?.cpf || 'N/D', 
                        userMatricula: targetCollab.registrationNumber || employeesMeta[targetUserId]?.registrationNumber || 'N/D',
                        userRole: `${targetCollab.role || ''} ${targetCollab.seniority || ''} ${targetCollab.level || ''}`.trim().toUpperCase(),
                        userDept: (targetCollab.department || effectiveUser.departmentId || 'GERAL').toUpperCase(),
                        companyName: (targetCollab.companyName || "ACAD ASSESSORIA CONTABIL").toUpperCase(),
                        companyCNPJ: targetCollab.companyCNPJ || "04.550.717/0001-68",
                        month: refMonth, 
                        date: new Date().toISOString(), 
                        hash: hash, 
                        publicUrl: publicUrl, 
                        qrCodeUrl: qrCodeUrl,
                        summary: { initial: periodBalance.initial, final: periodBalance.final }, 
                        sheetData: sheetData, // CONGELA O ESPELHO
                        ruleString: ruleString, // CONGELA A REGRA VIGENTE
                        managerSignature: null
                    };
                    
                    await setDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/point_signatures`, sigId), signaturePayload);
                    if(logAction) logAction('ASSINATURA_PONTO_COLABORADOR', { colaborador: targetCollab.name, mes: refMonth });
                    notify('success', `Ponto assinado eletronicamente!`);
                    setSignModalTab('assinados'); // Direciona para a aba de assinados imediatamente
                }
            } catch (error) { notify('error', "Erro ao salvar assinatura."); }
            finally { setIsProcessing(false); }
        };

        const handleUndoSign = async () => {
            if (!signatureData) return;
            const sigId = `${targetUserId}_${refMonth}`;
            try {
                if (isViewingOthers && isGestor) {
                    await updateDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/point_signatures`, sigId), { managerSignature: null });
                    if(logAction) logAction('DESFAZER_ASSINATURA_GESTOR', { gestor: effectiveUser.name, alvo: targetUserId, mes: refMonth });
                    notify('success', "Validação removida.");
                } else {
                    if (signatureData.managerSignature) return notify('error', "Gestor já validou. Solicite o cancelamento a ele.");
                    await deleteDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/point_signatures`, sigId));
                    if (logAction) {
                        logAction('DESFAZER_ASSINATURA_COLABORADOR', {
                            entity: 'ASSINATURAS_PONTO',
                            recordId: signatureData?.id || sigId || refMonth,
                            details: [
                                { campo: 'usuario', de: '', para: effectiveUser.name },
                                { campo: 'mes', de: '', para: refMonth }
                            ]
                        });
                    }
                    setSignatureData(null); 
                    notify('success', "Assinatura desfeita.");
                }
            } catch (e) { notify('error', "Erro: " + e.message); }
        };

        const handleMainButtonAction = () => { if (isViewingOthers) { if (signatureData?.managerSignature) handleUndoSign(); else if (signatureData) handleConfirmSign(); } else { if (signatureData) handleUndoSign(); else handleOpenSignModal(); } };
        
        const handleOpenManagerList = async () => {
            if (!isGestor) return;
            try {
                const q = query(collection(db, `${FIRESTORE_PATH_PUBLIC}/point_signatures`), where('month', '==', refMonth));
                const snap = await getDocs(q);
                const list = snap.docs.map(d => d.data()).filter(d => !d.managerSignature && subordinates.some(sub => sub.id === d.userId));
                setPendingSignaturesList(list);
                setIsManagerListOpen(true);
            } catch (e) { notify('error', "Erro ao buscar pendências."); }
        };

        // NOVA FUNÇÃO: DESFAZER ASSINATURA ESPECÍFICA (A partir da Tabela)
        const handleUndoSpecificSign = async (sig) => {
            if (!sig) return;
            if (!window.confirm(`Deseja desfazer a assinatura do mês ${sig.month}?`)) return;
            
            try {
                if (isViewingOthers && isGestor) {
                    await updateDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/point_signatures`, sig.id), { managerSignature: null });
                    if (logAction) {
                        logAction('DESFAZER_ASSINATURA_GESTOR', {
                            entity: 'ASSINATURAS_PONTO',
                            recordId: sig.id,
                            details: [
                                { campo: 'gestor', de: '', para: effectiveUser.name },
                                { campo: 'alvo', de: '', para: targetUserId },
                                { campo: 'mes', de: '', para: sig.month }
                            ]
                        });
                    }
                    notify('success', "Validação do gestor removida.");
                } else {
                    if (sig.managerSignature) return notify('error', "O Gestor já validou esta folha. Solicite o cancelamento a ele primeiro.");
                    await deleteDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/point_signatures`, sig.id));
                    if (logAction) {
                        logAction('DESFAZER_ASSINATURA_COLABORADOR', {
                            entity: 'ASSINATURAS_PONTO',
                            recordId: sig.id,
                            details: [
                                { campo: 'usuario', de: '', para: effectiveUser.name },
                                { campo: 'mes', de: '', para: sig.month }
                            ]
                        });
                    }
                    notify('success', "Assinatura desfeita com sucesso.");
                }
            } catch (e) { notify('error', "Erro: " + e.message); }
        };
        
        // --- GERAÇÃO DE PDF ---
        const handlePrint = async () => {
            if (typeof loadPdfLibs !== 'undefined') await loadPdfLibs();
            if (!window.jspdf) return notify('error', "Biblioteca PDF não carregada.");
            
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF('p', 'mm', 'a4');
            
            const primaryColor = [30, 58, 138];
            const accentColor = [15, 23, 42]; 
            const lightGray = [241, 245, 249];
            const borderColor = [203, 213, 225];
            
            const targetCollab = (collaborators || []).find(c => c.id === targetUserId) || effectiveUser;
            const userName = targetCollab.name.toUpperCase();
            const userCpf = targetCollab.cpf || employeesMeta[targetUserId]?.cpf || 'N/D';
            const userMatricula = targetCollab.registrationNumber || employeesMeta[targetUserId]?.registrationNumber || 'N/D';
            const fullRole = `${targetCollab.role || ''} ${targetCollab.seniority || ''} ${targetCollab.level || ''}`.trim().toUpperCase();
            const userDept = (targetCollab.department || effectiveUser.departmentId || 'GERAL').toUpperCase();
            const companyName = (targetCollab.companyName || "ACAD ASSESSORIA CONTABIL").toUpperCase();
            const companyCNPJ = targetCollab.companyCNPJ || "04.550.717/0001-68";

            let periodoTexto = "";
            if(periodType === 'month') {
                const [ano, mes] = refMonth.split('-');
                const lastDay = new Date(ano, mes, 0).getDate();
                periodoTexto = `01/${mes}/${ano} A ${lastDay}/${mes}/${ano}`;
            } else if(periodType === 'custom') {
                const d1 = customStart ? new Date(customStart).toLocaleDateString('pt-BR') : '?';
                const d2 = customEnd ? new Date(customEnd).toLocaleDateString('pt-BR') : '?';
                periodoTexto = `${d1} A ${d2}`;
            } else {
                periodoTexto = "HISTÓRICO COMPLETO";
            }
            
            doc.setFillColor(...primaryColor); 
            doc.rect(0, 0, 210, 12, 'F'); // Altura retornada para 12
            doc.setFont("helvetica", "bold"); 
            doc.setTextColor(255, 255, 255); 
            doc.setFontSize(12); 
            doc.text("FOLHA DE PONTO INDIVIDUAL", 105, 8, { align: "center" }); 

            if (logoUrl) { 
                try { doc.addImage(logoUrl, 'PNG', 10, 1.5, 9, 9); } catch (e) {} 
            } 

            const startY = 16; 
            const boxHeight = 28; 
            doc.setFillColor(...lightGray); 
            doc.setDrawColor(...borderColor); 
            doc.roundedRect(10, startY, 190, boxHeight, 2, 2, 'FD'); 
            
            doc.setFillColor(...primaryColor); 
            doc.roundedRect(10, startY, 3, boxHeight, 2, 2, 'F'); 
            doc.rect(11, startY, 2, boxHeight, 'F'); 

            const writeLabelVal = (label, val, x, y, isBoldVal = false) => { 
                doc.setFontSize(6); 
                doc.setTextColor(100, 116, 139); 
                doc.setFont("helvetica", "bold");
                doc.text(label, x, y); 
                
                doc.setFontSize(8); 
                doc.setTextColor(...accentColor); 
                doc.setFont("helvetica", isBoldVal ? "bold" : "normal"); 
                doc.text(String(val).substring(0, 40), x, y + 3.5); 
            }; 

            const r1 = startY + 6; 
            writeLabelVal("COLABORADOR:", userName, 18, r1, true); 
            writeLabelVal("CPF:", userCpf, 90, r1); 
            writeLabelVal("MATRÍCULA:", userMatricula, 120, r1); 
            writeLabelVal("FUNÇÃO:", fullRole, 145, r1); 

            const r2 = startY + 16; 
            writeLabelVal("EMPRESA:", companyName, 18, r2); 
            writeLabelVal("CNPJ:", companyCNPJ, 90, r2); 
            
            doc.setFontSize(6);
            doc.setTextColor(100, 116, 139); 
            doc.text("DEPARTAMENTO:", 120, r2); 
            
            doc.setFontSize(9);
            doc.setTextColor(...primaryColor); 
            doc.setFont("helvetica", "bold"); 
            doc.text(userDept, 120, r2 + 3.5); 

            // Reposicionamento do campo PERÍODO para acomodar o QR Code
            doc.setFillColor(...primaryColor); 
            doc.roundedRect(145, startY + 13, 30, 10, 1, 1, 'F'); 
            doc.setTextColor(255); 
            doc.setFontSize(5); 
            doc.text("PERÍODO:", 160, startY + 16.5, { align: "center" }); 
            doc.setFontSize(7); 
            doc.setFont("helvetica", "bold"); 
            doc.text(periodoTexto, 160, startY + 20.5, { align: "center" });

            // --- INJEÇÃO DO QR CODE DENTRO DO BLOCO DE DADOS DO FUNCIONÁRIO ---
            if (signatureData && signatureData.qrCodeUrl) {
                try {
                    // Adiciona o QR Code no canto direito do bloco cinza
                    doc.addImage(signatureData.qrCodeUrl, 'PNG', 178, startY + 4, 18, 18);
                    doc.setFontSize(4.5);
                    doc.setTextColor(100, 116, 139);
                    doc.text("VALIDAÇÃO DIGITAL", 187, startY + 24, { align: "center" });
                } catch (e) {
                    console.log("Erro imagem QR:", e);
                }
            }
            // ------------------------------------------------------------------

            const tableBody = sheetData.map(row => {
                const p = row.punches.map(punch => punch.time);
                let extraEnt = [], extraSai = [];
                for (let i = 4; i < p.length; i += 2) { 
                    if (p[i]) extraEnt.push(p[i]); 
                    if (p[i+1]) extraSai.push(p[i+1]); 
                }
                
                // 1. CONCATENA JUSTIFICATIVAS E OBSERVAÇÕES
                const obsList = [];
                if (row.justificationsText) obsList.push(row.justificationsText);
                if (row.obsNote) obsList.push(`Obs: ${row.obsNote}`);
                const observacoesFinal = obsList.join(' | ');

                return [
                    row.display.substring(0, 5), 
                    row.week.toUpperCase().substring(0, 3), 
                    p[0]||'', p[1]||'', p[2]||'', p[3]||'', 
                    extraEnt.join('\n'), extraSai.join('\n'), 
                    row.worked, 
                    minutesToTimePonto(row.balance),
                    observacoesFinal
                ];
            });

            doc.autoTable({
                startY: startY + boxHeight + 3,
                head: [
                    [{ content: 'DATA', styles: { valign: 'middle' } }, { content: 'DIA', styles: { valign: 'middle' } }, { content: 'JORNADA NORMAL', colSpan: 4, styles: { halign: 'center' } }, { content: 'HE / EXTRAS', colSpan: 2, styles: { halign: 'center' } }, { content: 'TOTAIS', colSpan: 2, styles: { halign: 'center' } }, { content: 'OBSERVAÇÕES', styles: { valign: 'middle', halign: 'center' } }], 
                    ['Data', 'Dia', 'Entrada', 'S. Almoço', 'V. Almoço', 'Saída', 'Ent. Extra', 'Saí. Extra', 'Hrs Trab', 'Saldo', 'Observações']
                ],
                body: tableBody,
                theme: 'grid',
                // Fontes e margens reduzidas para caber 1 coluna a mais e não quebrar página
                styles: { fontSize: 5.5, cellPadding: 0.6, lineColor: [200, 200, 200], lineWidth: 0.1, valign: 'middle', textColor: [50, 50, 50], overflow: 'linebreak' },
                headStyles: { fillColor: primaryColor, textColor: [255], fontStyle: 'bold', halign: 'center', lineWidth: 0.1, minCellHeight: 5 },
                columnStyles: { 
                    0: { cellWidth: 10, halign: 'center' }, 1: { cellWidth: 8, halign: 'center' }, 
                    2: { cellWidth: 11, halign: 'center' }, 3: { cellWidth: 12, halign: 'center' }, 
                    4: { cellWidth: 12, halign: 'center' }, 5: { cellWidth: 11, halign: 'center' }, 
                    6: { cellWidth: 14, halign: 'center', fontStyle: 'bold', textColor: [22, 163, 74] }, 
                    7: { cellWidth: 14, halign: 'center', fontStyle: 'bold', textColor: [220, 38, 38] }, 
                    8: { cellWidth: 12, halign: 'center', fontStyle: 'bold' }, 9: { cellWidth: 12, halign: 'center' },
                    10: { cellWidth: 'auto', halign: 'left' } 
                },
                didParseCell: (data) => { 
                    if (data.section === 'body') { 
                        if (sheetData[data.row.index]?.isWeekend) data.cell.styles.fillColor = [241, 245, 249]; 
                        else if (data.row.index % 2 === 0) data.cell.styles.fillColor = [255, 255, 255]; 
                        if (data.column.index === 9 && data.cell.raw && data.cell.raw.includes('-')) data.cell.styles.textColor = [220, 0, 0]; 
                    } 
                },
                margin: { left: 10, right: 10, bottom: 2 }
            });

            let footerY = doc.lastAutoTable.finalY + 2; 

            doc.setFillColor(248, 250, 252); doc.setDrawColor(200); doc.roundedRect(10, footerY, 190, 7, 2, 2, 'FD');
            doc.setFontSize(6.5); doc.setFont("helvetica", "bold"); doc.setTextColor(100); doc.text("RESUMO BANCO DE HORAS:", 15, footerY + 5);
            doc.setTextColor(0); doc.setFont("helvetica", "normal"); doc.text(`Saldo Anterior:`, 70, footerY + 5); 
            doc.setFont("helvetica", "bold"); doc.text(minutesToTimePonto(periodBalance.initial), 92, footerY + 5);
            doc.setFont("helvetica", "normal"); 
            const resultPeriod = periodBalance.final - periodBalance.initial; 
            doc.text(`Resultado Período:`, 115, footerY + 5);
            if(resultPeriod < 0) doc.setTextColor(220, 38, 38); else doc.setTextColor(22, 163, 74); 
            doc.setFont("helvetica", "bold"); doc.text(minutesToTimePonto(resultPeriod), 142, footerY + 5);
            doc.setTextColor(0); doc.text("SALDO FINAL:", 165, footerY + 5); 
            if(periodBalance.final < 0) doc.setTextColor(220, 38, 38); else doc.setTextColor(22, 163, 74); 
            doc.text(minutesToTimePonto(periodBalance.final), 187, footerY + 5);

            // 2. INSERÇÃO DINÂMICA DA REGRA DE JORNADA
            footerY += 9;
            let ruleString = '';
            if (targetCollab.workRuleId && targetCollab.workRuleId !== 'padrao') {
                const foundRule = (Array.isArray(config) ? config : []).find(r => r.id === targetCollab.workRuleId);
                if (foundRule && foundRule.pdfPrintString) ruleString = foundRule.pdfPrintString;
            }
            if (!ruleString) {
                const defRule = Array.isArray(config) ? config[0] : config;
                ruleString = defRule?.pdfPrintString || "BASE: 08:00 ÀS 17:30 | ALMOÇO: 12:00 ÀS 13:00 | CAFÉ: 15:15 às 15:30 | TOL: 10m";
            }

            doc.setFillColor(248, 250, 252); doc.setDrawColor(200); doc.roundedRect(10, footerY, 190, 7, 2, 2, 'FD');
            doc.setFontSize(6.5); doc.setFont("helvetica", "bold"); doc.setTextColor(100); doc.text("JORNADA DE TRABALHO BASE:", 15, footerY + 5);
            doc.setTextColor(...primaryColor); doc.setFont("helvetica", "bold"); doc.text(ruleString, 60, footerY + 5);

            // 3. ASSINATURAS ELETRÔNICAS
            footerY += 9;
            doc.setFillColor(...primaryColor); doc.rect(10, footerY, 190, 5, 'F'); 
            doc.setTextColor(255); doc.setFontSize(6.5); doc.setFont("helvetica", "bold"); 
            doc.text("ASSINATURAS ELETRÔNICAS", 105, footerY + 3.5, { align: "center" });

            let sigY = footerY + 5;
            const signaturesToPrint = [];
            if (signatureData) signaturesToPrint.push({ role: "COLABORADOR", name: userName.substring(0, 30), date: new Date(signatureData.date).toLocaleDateString('pt-BR'), hash: signatureData.hash });
            else signaturesToPrint.push({ role: "COLABORADOR", name: userName.substring(0, 30), date: "Pendência", hash: "Aguardando assinatura..." });
            
            if (signatureData && signatureData.managerSignature) signaturesToPrint.push({ role: "GESTOR", name: signatureData.managerSignature.managerName.substring(0, 30), date: new Date(signatureData.managerSignature.date).toLocaleDateString('pt-BR'), hash: signatureData.managerSignature.hash });
            else signaturesToPrint.push({ role: "GESTOR", name: "Aguardando", date: "-", hash: "-" });

            doc.autoTable({
                startY: sigY, head: [['PAPEL', 'NOME', 'DATA', 'HASH / ID']], 
                body: signaturesToPrint.map(s => [s.role, s.name, s.date, s.hash]), 
                theme: 'grid', styles: { fontSize: 5.5, cellPadding: 1, textColor: [50], lineColor: [200] }, 
                headStyles: { fillColor: [240], textColor: [50], fontStyle: 'bold', halign: 'left', minCellHeight: 5 }, 
                columnStyles: { 0: { cellWidth: 25, fontStyle: 'bold' }, 1: { cellWidth: 65 }, 2: { cellWidth: 20, halign: 'center' }, 3: { cellWidth: 'auto', fontStyle: 'italic' } }, 
                margin: { left: 10, right: 10, bottom: 2 }
            });

            doc.setTextColor(150); doc.setFontSize(4.5); doc.setFont("helvetica", "italic"); 
            doc.text("Documento gerado eletronicamente pelo Sistema de Gestão de Pessoas (ACAD). Validade jurídica conforme MP 2.200-2/2001.", 105, doc.internal.pageSize.getHeight() - 4, { align: "center" });

            window.open(doc.output('bloburl'), '_blank');
        };


        const formatBalanceWithParentheses = React.useCallback((minutes) => {
        const safeMinutes = Number(minutes || 0);
        const formatted = minutesToTimePonto(Math.abs(safeMinutes));

        if (safeMinutes < 0) {
            return `(${formatted})`;
        }

        return formatted;
        }, [minutesToTimePonto]);

        const parseMonthReference = React.useCallback((value) => {
        if (!value || !/^\d{2}\/\d{4}$/.test(value)) return null;

        const [mm, yyyy] = value.split('/');
        const month = Number(mm);
        const year = Number(yyyy);

        if (month < 1 || month > 12 || year < 2000) return null;

        return { month, year };
        }, []);

            const formatDateToBR = React.useCallback((dateStr) => {
                if (!dateStr) return '';
                const [y, m, d] = dateStr.split('-');
                if (!y || !m || !d) return '';
                return `${d}/${m}/${y}`;
            }, []);

            const formatDateToISO = React.useCallback((brDate) => {
                if (!brDate || !/^\d{2}\/\d{2}\/\d{4}$/.test(brDate)) return '';
                const [d, m, y] = brDate.split('/');
                return `${y}-${m}-${d}`;
            }, []);

            const handleMonthReferenceChange = React.useCallback((value) => {
                let digits = value.replace(/\D/g, '').slice(0, 6);

                if (digits.length >= 3) {
                    digits = `${digits.slice(0, 2)}/${digits.slice(2)}`;
                }

                setMonthReference(digits);
            }, []);

            const handlePeriodDateChange = React.useCallback((setter, value) => {
                let digits = value.replace(/\D/g, '').slice(0, 8);

                if (digits.length >= 3 && digits.length <= 4) {
                    digits = `${digits.slice(0, 2)}/${digits.slice(2)}`;
                } else if (digits.length >= 5) {
                    digits = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
                }

                setter(digits);
            }, []);
        return (
            <div className="space-y-6 animate-fade-in relative">
                
                {localNotification && (
                    <div className={`fixed top-6 right-6 z-[120] px-4 py-3 rounded-lg shadow-xl text-white text-sm font-bold flex items-center gap-2 animate-fade-in ${localNotification.type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}>
                        {localNotification.type === 'error' ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
                        {localNotification.text}
                    </div>
                )}

                <Card className="flex flex-col md:flex-row items-center gap-4 p-3" darkMode={isDarkMode}>
                    <div className="flex flex-wrap items-center gap-4 w-full">
                        {/* Filtro Dropdown */}
                        <div className="flex items-center gap-2 border-r pr-4 border-gray-200 dark:border-slate-700">
                            <select 
                                value={periodType} 
                                onChange={e => setPeriodType(e.target.value)}
                                className={`px-3 py-1.5 text-xs rounded-lg border font-bold shadow-sm transition-all outline-none cursor-pointer focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}
                            >
                                <option value="month">Mês</option>
                                <option value="custom">Período</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-2 flex-1">
                            {periodType === 'month' && (
                                <div className="flex items-center relative w-full md:w-auto">
                                    <PontoInput 
                                        type="month" 
                                        value={refMonth} 
                                        onChange={e => setRefMonth(e.target.value)} 
                                        darkMode={isDarkMode} 
                                        placeholder="MM/AAAA"
                                        title="Formato exigido: MM/AAAA"
                                    />
                                    {!refMonth && (
                                        <span className="absolute left-3 text-xs text-gray-400 pointer-events-none opacity-70">
                                            MM/AAAA
                                        </span>
                                    )}
                                </div>
                            )}
                            {periodType === 'custom' && (
                                <div className="flex items-center gap-2">
                                    <PontoInput 
                                        type="date" 
                                        value={customStart} 
                                        onChange={e => setCustomStart(e.target.value)} 
                                        darkMode={isDarkMode} 
                                        placeholder="dd/mm/aaaa"
                                        title="Formato: dd/mm/aaaa" 
                                    />
                                    <span className="text-xs text-gray-500">até</span>
                                    <PontoInput 
                                        type="date" 
                                        value={customEnd} 
                                        onChange={e => setCustomEnd(e.target.value)} 
                                        darkMode={isDarkMode} 
                                        placeholder="dd/mm/aaaa"
                                        title="Formato: dd/mm/aaaa" 
                                    />
                                </div>
                            )}
                        </div>
                        
                        {isGestor && (
                            <div className="flex items-center gap-2 pl-4 border-l border-gray-200 dark:border-slate-700">
                                <label className="text-[10px] font-bold text-slate-500 uppercase">Visualizar:</label>
                                <select 
                                    className={`h-9 px-2 rounded-lg border text-xs outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                                    value={String(targetUserId)}
                                    onChange={(e) => {
                                        setRawPunches([]);
                                        setTargetUserId(String(e.target.value));
                                    }}
                                >
                                    <option value={String(myCollabId)}>Eu (Meu Ponto)</option>
                                    {subordinates.map(sub => (
                                        <option key={sub.id} value={String(sub.id)}>
                                            {sub.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* SUMÁRIOS DE SALDOS COM DESTAQUE DE COR DE FUNDO */}
                        <div className="flex gap-3 border-l pl-4 border-gray-200 dark:border-slate-700">
                            {/* 1. Card Saldo Anterior */}
                            <div className={`flex flex-col items-center justify-center px-4 py-1.5 rounded-xl shadow-sm border ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-indigo-50 border-indigo-100'}`}>
                                <span className={`text-[9px] uppercase font-bold block mb-0.5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                                    Saldo Anterior
                                </span>
                                <span className={`text-sm font-mono font-black ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                                    {formatBalanceWithParentheses(periodBalance.initial)} h
                                </span>
                            </div>

                            {/* 2. Card Saldo Acumulado */}
                            <div className={`flex flex-col items-center justify-center px-4 py-1.5 rounded-xl shadow-sm border ${periodBalance.final < 0 ? (isDarkMode ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-200') : (isDarkMode ? 'bg-emerald-900/30 border-emerald-700' : 'bg-emerald-50 border-emerald-200')}`}>
                                <span className={`text-[9px] uppercase font-bold block mb-0.5 ${periodBalance.final < 0 ? (isDarkMode ? 'text-red-400' : 'text-red-700') : (isDarkMode ? 'text-emerald-400' : 'text-emerald-700')}`}>
                                    Saldo Acumulado
                                </span>
                                <div className="flex items-center gap-2">
                                    <span className={`text-lg font-mono font-black ${periodBalance.final < 0 ? (isDarkMode ? 'text-red-300' : 'text-red-700') : (isDarkMode ? 'text-emerald-300' : 'text-emerald-700')}`}>
                                        {formatBalanceWithParentheses(periodBalance.final)} h
                                    </span>
                                    {/* Badge Restaurado */}
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide text-white ${periodBalance.final < 0 ? 'bg-red-500' : 'bg-emerald-600'}`}>
                                        {periodBalance.final < 0 ? 'Devedor' : 'Credor'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-2 ml-auto">
                            <Button variant="secondary" icon={Printer} onClick={handlePrint} className="h-10 px-3">PDF</Button>
                            
                            {isGestor && !isViewingOthers && (
                                        <>
                                            {(effectiveUser.role === 'SuperAdm' || effectiveUser.role === 'Gestor' || effectiveUser.role === 'ADM' || hasPermBank('ponto_btn_requests')) && (
                                                <button onClick={() => setIsApprovalModalOpen(true)} style={{ backgroundColor: '#f97316' }} className="flex items-center gap-2 rounded-lg h-10 px-3 hover:opacity-90 text-white border-0 shadow-sm text-xs font-bold relative transition-opacity">
                                                    <Bell size={16} /> Solicitações
                                                    {pendingRequests.length > 0 && <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-black w-5 h-5 flex items-center justify-center rounded-full shadow-lg border border-white animate-bounce">{pendingRequests.length}</span>}
                                                </button>
                                            )}
                                            
                                            {(effectiveUser.role === 'SuperAdm' || effectiveUser.role === 'Gestor' || effectiveUser.role === 'ADM' || hasPermBank('ponto_btn_inconsistencies')) && (
                                                <button onClick={() => setIsInconsistenciesModalOpen(true)} style={{ backgroundColor: '#ef4444' }} className="flex items-center gap-2 rounded-lg h-10 px-3 hover:opacity-90 text-white border-0 shadow-sm text-xs font-bold transition-opacity">
                                                    <AlertTriangle size={16} /> Inconsistências
                                                </button>
                                            )}
                                            
                                            <button onClick={handleOpenManagerList} style={{ backgroundColor: '#9333ea' }} className="flex items-center gap-2 rounded-lg h-10 px-3 hover:opacity-90 text-white border-0 shadow-sm text-xs font-bold transition-opacity">
                                                <Users size={16} /> Assinar Equipe
                                            </button>
                                        </>
                                    )}

                            {periodType === 'month' && (
                                <button 
                                    onClick={() => setIsSignModalOpen(true)}
                                    className="h-10 px-4 rounded-lg font-bold text-xs shadow-sm flex items-center gap-2 transition-all bg-blue-600 text-white hover:bg-blue-700"
                                    title="Abrir Central de Assinaturas"
                                >
                                    <Fingerprint size={16}/> Assinaturas / Histórico
                                </button>
                            )}
                        </div>
                    </div>
                </Card>
                
                {/* TABELA DE ESPELHO DE PONTO */}
                <Card className="p-0 overflow-hidden" darkMode={isDarkMode}>
                    <div className="overflow-x-auto">
                        <table className={`w-full text-sm text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                            <thead className={`text-xs uppercase font-bold ${isDarkMode ? 'bg-slate-900' : 'bg-gray-100'}`}>
                                <tr>
                                    <th className="p-4">Data</th>
                                    {/* 1. Largura Fixa e Maior para a coluna de Batidas */}
                                    <th className="p-4 w-[340px] min-w-[280px]">Batidas</th>
                                    <th className="p-4 text-center">Horas Trab.</th>
                                    <th className="p-4 text-center">Saldo Diário</th>
                                    <th className={`p-4 text-center font-bold border-l border-r ${isDarkMode ? 'bg-slate-800 border-slate-700 text-indigo-300' : 'bg-indigo-50 border-indigo-100 text-indigo-800'}`}>Saldo Acumulado</th>
                                    <th className="p-4">Observações (Sistema)</th>
                                    {(hasPermBank('ponto_edit_manual_obs') || hasPermBank('ponto_can_edit')) && (
                                        <th className="p-4 text-center text-blue-500">Ações / Anotação</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
        {sheetData.map((row, i) => (
            <tr key={`${targetUserId}-${row.date}-${i}`} className={`${row.isWeekend ? (isDarkMode?'bg-slate-700/30':'bg-gray-50') : ''} hover:bg-opacity-80`}>
                                        <td className="p-4 border-r border-dashed dark:border-slate-700 font-bold">{row.display} <span className="text-[10px] opacity-60 uppercase">{row.week}</span></td>
                                        
                                        <td className="p-4 font-mono text-xs">
                                            {/* 2. Grid com 6 colunas (grid-cols-6) para caberem os 6 registros */}
                                            <div className="grid grid-cols-3 xl:grid-cols-6 gap-1.5 w-full">
                                                {row.punches.map((p, pIdx) => {
                                                    let colorClass = p.isModified 
                                                        ? (isDarkMode ? 'bg-yellow-900/40 text-yellow-300 border-yellow-700' : 'bg-yellow-100 text-yellow-800 border-yellow-300')
                                                        : (isDarkMode ? 'bg-slate-800 text-gray-300 border-slate-600' : 'bg-gray-100 text-gray-700 border-gray-300');
                                                    
                                                    if (p.source === 'manual' && p.status === 'Pending') {
                                                        colorClass = isDarkMode ? 'bg-orange-900/30 text-orange-400 border-orange-500 border-dashed' : 'bg-orange-50 text-orange-700 border-orange-300 border-dashed';
                                                    } else if (!p.isModified) {
                                                        if (pIdx === 0 || pIdx === 2) {
                                                            // Entradas (Azul)
                                                            colorClass = isDarkMode ? 'bg-blue-900/40 text-blue-300 border-blue-700' : 'bg-blue-50 text-blue-700 border-blue-200';
                                                        } else if (pIdx === 1 || pIdx === 3) {
                                                            // Saídas (Laranja)
                                                            colorClass = isDarkMode ? 'bg-orange-900/40 text-orange-300 border-orange-700' : 'bg-orange-50 text-orange-700 border-orange-200';
                                                        } else if (pIdx % 2 === 0) {
                                                            // Entradas Extras (Roxo)
                                                            colorClass = isDarkMode ? 'bg-purple-900/40 text-purple-300 border-purple-700' : 'bg-purple-50 text-purple-700 border-purple-200';
                                                        } else {
                                                            // Saídas Extras (Rosa)
                                                            colorClass = isDarkMode ? 'bg-pink-900/40 text-pink-300 border-pink-700' : 'bg-pink-50 text-pink-700 border-pink-200';
                                                        }
                                                    }

                                                    return (
                                                        <span key={pIdx} title={p.source === 'manual' && p.status === 'Pending' ? `Pendente (Motivo: ${p.obs || ''})` : ''} className={`px-1.5 py-1 rounded border text-center font-bold whitespace-nowrap transition-colors cursor-default shadow-sm ${colorClass}`}>
                                                            {p.time}
                                                        </span>
                                                    )
                                                })}
                                            </div>
                                        </td>

                                        <td className="p-4 text-center font-bold">{row.worked}</td>
                                        <td className={`p-4 text-center font-bold ${row.balance >= 0 ? 'text-green-500' : 'text-red-500'} ${row.hasApprovedJustification && row.balance === 0 ? 'text-purple-500 italic' : ''}`}>
                                            {row.hasApprovedJustification && row.balance === 0 ? 'Abonado' : minutesToTimePonto(row.balance)}
                                        </td>
                                        <td className={`p-4 border-l border-r shadow-inner ${isDarkMode ? 'border-slate-700 bg-slate-800/40' : 'border-indigo-100 bg-indigo-50/40'}`}>
                                            <div className={`flex justify-center items-center w-full font-bold font-mono text-sm tracking-wide ${row.accumulated >= 0 ? (isDarkMode ? 'text-blue-400' : 'text-blue-700') : (isDarkMode ? 'text-red-400' : 'text-red-600')}`}>
                                                {minutesToTimePonto(row.accumulated)}
                                            </div>
                                        </td>
                                        
                                        {/* 3. Coluna de Observações APENAS com justificativas e obs manuais. */}
                                        <td className="p-4 text-xs align-middle w-64 max-w-[250px]">
                                            <div className="flex flex-col gap-1 items-start group/obs w-full overflow-hidden">
                                                {/* Justificativas do Sistema (Atestados, etc) */}
                                                {row.justificationsText && (
                                                    <span 
                                                        title={row.justificationsText}
                                                        className={`px-2 py-1 rounded-md border font-medium block w-full truncate cursor-help ${row.justificationsText.includes('Abonado') ? 'bg-green-100 text-green-700 border-green-200' : isDarkMode ? 'bg-orange-900/20 text-orange-300 border-orange-800' : 'bg-orange-50 text-orange-700 border-orange-200'}`}
                                                    >
                                                        {row.justificationsText}
                                                    </span>
                                                )}
                                                
                                                {/* Observação Manual Adicionada */}
                                                {row.obsNote && (
                                                    <div 
                                                        onClick={() => hasPermBank('ponto_edit_manual_obs') && setDayNoteModal({ isOpen: true, date: row.date, display: row.display, text: row.obsNote, noteId: row.obsNoteId, type: 'Observation' })}
                                                        className={`${hasPermBank('ponto_edit_manual_obs') ? 'cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/40' : 'cursor-default'} px-2 py-1 rounded-md border font-medium italic relative transition-colors block w-full truncate ${isDarkMode ? 'bg-blue-900/20 text-blue-300 border-blue-800' : 'bg-blue-50 text-blue-700 border-blue-200'}`}
                                                        title={hasPermBank('ponto_edit_manual_obs') ? `Obs: ${row.obsNote}\n\n(Clique para editar)` : `Obs: ${row.obsNote}`}
                                                    >
                                                        <span className="font-bold mr-1">Obs:</span>{row.obsNote}
                                                        
                                                        {/* Ícone de Edição flutuando à direita (ignora o truncate) */}
                                                        {hasPermBank('ponto_edit_manual_obs') && (
                                                            <div className={`absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover/obs:opacity-100 transition-opacity shadow-sm ${isDarkMode ? 'bg-slate-800 text-blue-400' : 'bg-white text-blue-600'}`}>
                                                                <Edit3 size={12} />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Botão "+" Oculto que aparece ao passar o mouse para adicionar Obs Manual */}
                                                {!row.obsNote && hasPermBank('ponto_edit_manual_obs') && (
                                                    <button 
                                                        onClick={() => setDayNoteModal({ isOpen: true, date: row.date, display: row.display, text: '', noteId: null, type: 'Observation' })}
                                                        className="opacity-0 group-hover/obs:opacity-100 text-[10px] text-blue-500 hover:underline flex items-center gap-1 transition-all mt-0.5"
                                                    >
                                                        <Plus size={10}/> Add Obs. Manual
                                                    </button>
                                                )}

                                                {/* Tracinho se não houver nada e o mouse não estiver em cima */}
                                                {!row.justificationsText && !row.obsNote && (
                                                    <span className="text-gray-300 dark:text-gray-600 group-hover/obs:hidden">-</span>
                                                )}
                                            </div>
                                        </td>

                                        {(hasPermBank('ponto_edit_manual_obs') || hasPermBank('ponto_can_edit')) && (
                                            <td className="p-4 text-center align-middle">
                                                <div className="flex justify-center gap-2">
                                                    
                                                    {/* Botão de Anotação RH */}
                                                    {hasPermBank('ponto_edit_manual_obs') && (
                                                        <button 
                                                            onClick={() => setDayNoteModal({ isOpen: true, date: row.date, display: row.display, text: row.rhNote || '', noteId: row.rhNoteId, type: 'RHNote' })} 
                                                            className={`p-1.5 rounded transition-colors ${row.rhNote ? 'text-white bg-purple-600 shadow-md' : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'}`} 
                                                            title={row.rhNote ? `Anotação RH:\n${row.rhNote}` : "Adicionar anotação interna"}
                                                        >
                                                            <MessageSquare size={16}/>
                                                        </button>
                                                    )}

                                                    {hasPermBank('ponto_can_edit') && (
                                                        <button onClick={() => setEditingDayPunches({ date: row.display, punches: row.originalPunches, dateStr: row.date })} className="p-1.5 rounded text-blue-600 hover:bg-blue-50 transition-colors" title="Ajustar Batidas"><Edit3 size={16}/></button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>

                            {/* NOVO RODAPÉ (TFOOT) DA TABELA */}
                            <tfoot className={`${isDarkMode ? 'bg-slate-800 border-t border-slate-700' : 'bg-gray-50 border-t border-gray-200'}`}>
                                <tr>
                                    {/* Ocupa as 4 primeiras colunas (Data, Batidas, Horas Trab, Saldo Diário) e alinha à direita */}
                                    <td colSpan={4} className={`p-4 text-right text-xs font-bold uppercase ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                        Saldo Final no Período:
                                    </td>
                                    {/* Centraliza perfeitamente e mantém a cor de destaque da coluna */}
                                    <td className={`p-4 border-l border-r ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-indigo-100/60 border-indigo-200'}`}>
                                        <div className={`flex justify-center items-center w-full font-black font-mono text-base ${
                                            bankCalculation.final > 0 ? (isDarkMode ? 'text-blue-400' : 'text-blue-700') : bankCalculation.final < 0 ? (isDarkMode ? 'text-red-400' : 'text-red-600') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')
                                        }`}>
                                            {bankCalculation.final < 0 ? '-' : ''}{minutesToTimePonto(Math.abs(bankCalculation.final))} h
                                        </div>
                                    </td>
                                    {/* Preenche o restante das colunas (Observações e Ações) */}
                                    <td colSpan={(hasPermBank('ponto_edit_manual_obs') || hasPermBank('ponto_can_edit')) ? 2 : 1}></td>
                                </tr>
                            </tfoot>

                        </table>
                    </div>
                </Card>

                {/* MODAL DE SOLICITAÇÕES DO GESTOR */}
                {isApprovalModalOpen && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4" onClick={() => setIsApprovalModalOpen(false)}>
                        <div className={`rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh] relative ${isDarkMode ? 'bg-slate-800 border border-slate-700 text-white' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            <div className={`p-5 border-b shrink-0 ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                <h3 className="font-bold text-lg flex items-center gap-2 text-orange-500"><Bell size={20}/> Aprovação de Ocorrências (Equipe)</h3>
                                <p className="text-xs text-gray-500">Aprove atestados (abona horas) ou libere batidas manuais da sua equipe.</p>
                            </div>

                            {/* NOVOS FILTROS DE SOLICITAÇÕES */}
                            <div className={`p-4 border-b shrink-0 flex gap-3 ${isDarkMode ? 'border-slate-700 bg-slate-900/50' : 'border-gray-200 bg-gray-50'}`}>
                                <div className="w-1/3">
                                    <label className={`text-[10px] font-bold uppercase block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Departamento</label>
                                    <select
                                        className={`w-full p-2 border rounded text-xs outline-none focus:ring-2 focus:ring-orange-500 ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                                        value={approvalFilterDept}
                                        onChange={e => setApprovalFilterDept(e.target.value)}
                                    >
                                        <option value="">Todos</option>
                                        {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                    </select>
                                </div>
                                <div className="flex-1">
                                    <label className={`text-[10px] font-bold uppercase block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Buscar (Nome ou Motivo)</label>
                                    <div className="relative">
                                        <Search size={14} className={`absolute left-2.5 top-2 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}/>
                                        <input
                                            type="text"
                                            placeholder="Pesquisar..."
                                            className={`w-full pl-8 p-2 border rounded text-xs outline-none focus:ring-2 focus:ring-orange-500 ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                                            value={approvalSearch}
                                            onChange={e => setApprovalSearch(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4">
                                {processedPendingRequests.length === 0 ? (
                                    <p className="text-center text-sm text-gray-400 py-8">Nenhuma solicitação pendente encontrada.</p>
                                ) : (
                                    processedPendingRequests.map(req => {
                                    // Busca o dono da solicitação (mesmo que seja o próprio usuário logado)
                                    let collab = (collaborators || []).find(c => String(c.id) === String(req.userId));
                                    if (!collab && String(req.userId) === String(effectiveUser.id)) collab = effectiveUser;
                                    
                                    return (
                                        <div key={req.id} className={`p-4 rounded-xl border shadow-sm flex flex-col md:flex-row items-center gap-4 ${isDarkMode ? 'border-slate-700 bg-slate-900/50 text-white' : 'border-gray-200 bg-white text-gray-800'}`}>
                                                {/* Coluna 1: Colaborador e Data */}
                                                <div className="flex-1 min-w-[150px]">
                                                    <div className="font-black text-sm text-blue-600 dark:text-blue-400">{collab?.name || 'Desconhecido'}</div>
                                                    <div className="text-xs font-bold opacity-70 mt-1">
                                                        {new Date(req.date + 'T12:00:00').toLocaleDateString('pt-BR')} {req.time ? `às ${req.time}` : ''}
                                                    </div>
                                                    <div className={`inline-block mt-2 px-2 py-0.5 rounded text-[9px] font-bold uppercase border ${req.source === 'manual' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' : req.source === 'digital' ? 'bg-red-100 text-red-800 border-red-300' : 'bg-orange-100 text-orange-800 border-orange-300'}`}>
                                                        {req.source === 'manual' ? 'Batida Manual' : req.source === 'digital' ? 'Batida Divergente' : 'Justificativa'}
                                                    </div>
                                                </div>
                                                
                                                {/* Coluna 2: Justificativa */}
                                                <div className={`flex-[2] text-xs p-3 rounded-lg border w-full ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                                                    <span className="font-bold text-[10px] uppercase opacity-70 block mb-1">Justificativa / Motivo:</span>
                                                    <span className="italic">{req.obs || req.details || 'Nenhuma justificativa informada pelo colaborador.'}</span>
                                                </div>
                                                
                                                {/* Coluna 3: Ações (Lado a Lado) */}
                                                <div className="flex gap-2 w-full md:w-auto">
                                                    <button onClick={() => setRejectConfig({ isOpen: true, req: req, action: 'delete', newTime: req.time, reason: '' })} className="flex-1 md:flex-none py-2.5 px-4 rounded-lg font-bold text-xs bg-white dark:bg-slate-800 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-800 transition-colors whitespace-nowrap shadow-sm">
                                                        Tratar Inconsistência
                                                    </button>
                                                    <button onClick={() => handleProcessRequest(req, 'Approved')} className="flex-1 md:flex-none py-2.5 px-4 rounded-lg font-bold text-xs bg-green-600 text-white hover:bg-green-700 shadow-md border-0 transition-colors whitespace-nowrap">
                                                        Aprovar / Abonar
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    })
                                )}
                            </div>
                            
                            <div className="p-4 border-t"><Button variant="secondary" onClick={() => setIsApprovalModalOpen(false)} className="w-full">Fechar Painel</Button></div>
                        </div>
                    </div>
                )}

                {/* MODAL DE REJEIÇÃO / CORREÇÃO (NOVO) */}
                {rejectConfig.isOpen && rejectConfig.req && (
                    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4" onClick={() => setRejectConfig({ ...rejectConfig, isOpen: false })}>
                        <div className={`p-6 rounded-xl w-full max-w-md shadow-2xl border flex flex-col gap-4 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center border-b pb-2 dark:border-slate-700">
                                <h3 className="font-bold text-lg text-red-600 flex items-center gap-2"><AlertTriangle size={20}/> Tratar Inconsistência</h3>
                                <button onClick={() => setRejectConfig({ ...rejectConfig, isOpen: false })} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                            </div>
                            
                            <div className="flex flex-col gap-2">
                                <label className={`p-3 rounded-lg border cursor-pointer transition-colors ${rejectConfig.action === 'delete' ? 'bg-red-50 border-red-300 dark:bg-red-900/20 dark:border-red-700' : 'bg-gray-50 border-gray-200 dark:bg-slate-900 dark:border-slate-700'}`}>
                                    <div className="flex items-center gap-2 font-bold mb-1">
                                        <input type="radio" checked={rejectConfig.action === 'delete'} onChange={() => setRejectConfig({...rejectConfig, action: 'delete'})} className="text-red-600" />
                                        1) Excluir e Notificar
                                    </div>
                                    <p className="text-[10px] ml-6 opacity-70">A marcação será deletada. O colaborador receberá um aviso na folha de ponto.</p>
                                </label>
                                
                                <label className={`p-3 rounded-lg border cursor-pointer transition-colors ${rejectConfig.action === 'adjust' ? 'bg-blue-50 border-blue-300 dark:bg-blue-900/20 dark:border-blue-700' : 'bg-gray-50 border-gray-200 dark:bg-slate-900 dark:border-slate-700'}`}>
                                    <div className="flex items-center gap-2 font-bold mb-1">
                                        <input type="radio" checked={rejectConfig.action === 'adjust'} onChange={() => setRejectConfig({...rejectConfig, action: 'adjust'})} className="text-blue-600" />
                                        2) Ajustar Horário Manualmente
                                    </div>
                                    <p className="text-[10px] ml-6 opacity-70">O horário será ajustado e aprovado. Ficará registrado que o gestor fez a alteração.</p>
                                </label>
                            </div>

                            <div className="mt-2 space-y-3">
                                {rejectConfig.action === 'delete' && (
                                    <div>
                                        <label className="text-xs font-bold block mb-1">Motivo da Exclusão (Para o Colaborador)</label>
                                        <input type="text" placeholder="Ex: Esqueceu de bater na saída para o almoço..." value={rejectConfig.reason} onChange={e=>setRejectConfig({...rejectConfig, reason: e.target.value})} className={`w-full p-2 text-sm border rounded outline-none focus:ring-2 focus:ring-red-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} autoFocus />
                                    </div>
                                )}
                                
                                {rejectConfig.action === 'adjust' && (
                                    <div>
                                        <label className="text-xs font-bold block mb-1">Novo Horário Correto</label>
                                        <input type="time" value={rejectConfig.newTime} onChange={e=>setRejectConfig({...rejectConfig, newTime: e.target.value})} className={`w-full p-2 text-lg font-bold text-center border rounded outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} autoFocus />
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end gap-2 pt-4 border-t dark:border-slate-700">
                                <Button variant="secondary" onClick={() => setRejectConfig({ ...rejectConfig, isOpen: false })}>Cancelar</Button>
                                <Button onClick={handleConfirmRejectAction} className={rejectConfig.action === 'delete' ? 'bg-red-600 hover:bg-red-700 text-white border-0' : 'bg-blue-600 hover:bg-blue-700 text-white border-0'}>Confirmar Ação</Button>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* DEMAIS MODAIS EXISTENTES (DAY NOTE, EDICAO, CONFIRMAR DELECAO) */}
                {dayNoteModal.isOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setDayNoteModal({ ...dayNoteModal, isOpen: false })}>
                        <div className={`rounded-xl w-full max-w-sm shadow-2xl p-6 ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center mb-4 border-b pb-2">
                                <h3 className="font-bold text-lg text-purple-600">{dayNoteModal.type === 'RHNote' ? 'Anotação RH:' : 'Observação Manual:'} {dayNoteModal.display}</h3>
                                <button onClick={() => setDayNoteModal({ ...dayNoteModal, isOpen: false })}><X size={20}/></button>
                            </div>
                            <textarea className={`w-full p-3 border rounded-lg h-32 outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-gray-50 border-gray-300'}`} placeholder="Digite a anotação manual aqui..." value={dayNoteModal.text} onChange={e => setDayNoteModal({...dayNoteModal, text: e.target.value})} autoFocus></textarea>
                            <div className="mt-4 flex justify-end gap-2"><Button variant="secondary" onClick={() => setDayNoteModal({ ...dayNoteModal, isOpen: false })}>Cancelar</Button><Button onClick={handleSaveDayNote} className="bg-purple-600 text-white">Salvar</Button></div>
                        </div>
                    </div>
                )}

                {editingDayPunches && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setEditingDayPunches(null)}>
                        <div className={`rounded-xl w-full max-w-md shadow-2xl p-6 ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center mb-4 border-b pb-2"><h3 className="font-bold text-lg">Gerenciar: {editingDayPunches.date}</h3><button onClick={() => setEditingDayPunches(null)}><X size={20}/></button></div>
                            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                                {editingDayPunches.punches.map(p => (
                                    <div key={p.id} className="flex items-center justify-between p-3 rounded border bg-gray-50 dark:bg-slate-900 dark:border-slate-700">
                                        <input type="time" defaultValue={p.time} id={`input-time-${p.id}`} className="p-1 rounded text-sm font-bold border dark:bg-slate-800 dark:border-slate-600"/>
                                        <div className="flex gap-2">
                                            {hasPermBank('ponto_can_edit') && (
                                                <button onClick={() => handleDirectUpdate(p.id, document.getElementById(`input-time-${p.id}`).value, p.time, editingDayPunches.dateStr)} className="p-2 rounded bg-blue-100 text-blue-600"><Save size={16}/></button>
                                            )}
                                            {hasPermBank('ponto_can_delete') && (
                                                <button onClick={() => requestDeletePunch(p.id, editingDayPunches.dateStr, p.time)} className="p-2 rounded bg-red-100 text-red-600"><Trash2 size={16}/></button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 flex justify-end"><Button onClick={() => setEditingDayPunches(null)}>Fechar</Button></div>
                        </div>
                    </div>
                )}
                
                {/* COMO DEVE FICAR: */}
                {deleteConfirmation && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setDeleteConfirmation(null)}>
                        <div className={`p-6 rounded-xl w-80 shadow-2xl border ${isDarkMode ? 'bg-slate-800 text-white border-slate-700' : 'bg-white text-gray-800 border-gray-200'}`} onClick={e=>e.stopPropagation()}>
                            <h3 className="font-bold text-lg text-red-500 mb-4">Confirmar Exclusão</h3>
                            <div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setDeleteConfirmation(null)}>Cancelar</Button><Button onClick={confirmDeletePunch} className="bg-red-600 text-white border-0">Sim, Excluir</Button></div>
                        </div>
                    </div>
                )}
            {/* MODAL DE ASSINATURA INDIVIDUAL (NOVO COM ABAS E QR CODE) */}
                {isSignModalOpen && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4" onClick={() => setIsSignModalOpen(false)}>
                        <div className={`rounded-xl w-full max-w-3xl shadow-2xl flex flex-col relative max-h-[90vh] overflow-hidden ${isDarkMode ? 'bg-slate-800 border border-slate-700 text-white' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            
                            <div className="flex justify-between items-center p-5 border-b dark:border-slate-700">
                                <h3 className="font-bold text-lg flex items-center gap-2 text-blue-500">
                                    <Fingerprint size={20}/> Central de Assinaturas (Ponto)
                                </h3>
                                <button onClick={() => setIsSignModalOpen(false)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                            </div>

                            <div className={`flex border-b shrink-0 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                <button onClick={() => setSignModalTab('revisao')} className={`flex-1 py-3 text-sm font-bold border-b-2 transition-colors ${signModalTab === 'revisao' ? 'border-blue-500 text-blue-500' : 'border-transparent opacity-60'}`}>1. Revisão e Assinatura</button>
                                <button onClick={() => setSignModalTab('assinados')} className={`flex-1 py-3 text-sm font-bold border-b-2 transition-colors ${signModalTab === 'assinados' ? 'border-blue-500 text-blue-500' : 'border-transparent opacity-60'}`}>2. Documentos Assinados</button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                                {signModalTab === 'revisao' && (
                                    <div className="space-y-4 animate-fade-in flex flex-col h-full">
                                        
                                        {/* Linha 1: Filtro de Mês */}
                                        <div className="flex items-center gap-3 bg-blue-50/50 dark:bg-blue-900/10 p-3 rounded-xl border border-blue-100 dark:border-blue-800">
                                            <label className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>Referência (MM/AAAA):</label>
                                            <input 
                                                type="month" 
                                                value={refMonth} 
                                                onChange={e => {
                                                    setRefMonth(e.target.value);
                                                    setPeriodType('month');
                                                }}
                                                className={`p-2 border rounded-lg text-sm font-bold outline-none focus:ring-2 focus:ring-blue-500 shadow-sm ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white invert-calendar' : 'bg-white border-gray-300 text-gray-800'}`}
                                                title="Selecione o mês e ano para revisar"
                                            />
                                        </div>

                                        {/* Linha 2: Saldos (Cards estilo Print) */}
                                        <div className="grid grid-cols-3 gap-4">
                                            <div className={`p-4 rounded-xl border shadow-sm text-center flex flex-col justify-center ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                <span className="text-[10px] uppercase font-bold opacity-70 block mb-1">Saldo Anterior</span>
                                                <span className={`text-xl font-black ${periodBalance.initial >= 0 ? 'text-blue-500' : 'text-red-500'}`}>{minutesToTimePonto(periodBalance.initial)} h</span>
                                            </div>
                                            <div className={`p-4 rounded-xl border shadow-sm text-center flex flex-col justify-center ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                <span className="text-[10px] uppercase font-bold opacity-70 block mb-1">Resultado do Mês</span>
                                                <span className={`text-xl font-black ${(periodBalance.final - periodBalance.initial) >= 0 ? 'text-green-500' : 'text-red-500'}`}>{minutesToTimePonto(periodBalance.final - periodBalance.initial)} h</span>
                                            </div>
                                            <div className={`p-4 rounded-xl border shadow-sm text-center flex flex-col justify-center ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-blue-50 border-blue-200'}`}>
                                                <span className="text-[10px] uppercase font-bold opacity-70 block mb-1">Saldo Final Acumulado</span>
                                                <span className={`text-2xl font-black ${periodBalance.final >= 0 ? 'text-green-600' : 'text-red-600'}`}>{minutesToTimePonto(periodBalance.final)} h</span>
                                            </div>
                                        </div>

                                        {/* Linha 3: Tabela de Marcações Detalhadas */}
                                        <div className={`flex-1 min-h-[250px] overflow-y-auto border rounded-xl shadow-sm custom-scrollbar ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                            <table className={`w-full text-xs text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                <thead className={`sticky top-0 z-10 uppercase font-bold ${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                                                    <tr>
                                                        <th className="p-3 border-b border-r dark:border-slate-700 w-32">Data</th>
                                                        <th className="p-3 border-b border-r dark:border-slate-700">Batidas / Ocorrências</th>
                                                        <th className="p-3 border-b text-center w-32">Saldo Diário</th>
                                                    </tr>
                                                </thead>
                                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700 bg-slate-800' : 'divide-gray-100 bg-white'}`}>
                                                    {sheetData.map((row, i) => (
                                                        <tr key={i} className={`${row.isWeekend ? (isDarkMode ? 'bg-slate-800/60' : 'bg-gray-50') : ''} hover:brightness-95 transition-colors`}>
                                                            <td className="p-3 border-r dark:border-slate-700 font-bold whitespace-nowrap align-middle">
                                                                {row.display.substring(0,5)} <span className="opacity-50 text-[9px] uppercase ml-1">{row.week}</span>
                                                            </td>
                                                            <td className="p-3 border-r dark:border-slate-700 align-middle">
                                                                <div className="flex flex-wrap gap-1.5 items-center">
                                                                    {row.punches.map((p, idx) => {
                                                                        let colorClass = p.isModified ? 'bg-yellow-400 text-yellow-900 border-yellow-500' : 'bg-gray-200 text-gray-800 border-gray-300 dark:bg-slate-700 dark:border-slate-600';
                                                                        if (p.source === 'manual' && p.status === 'Pending') colorClass = 'bg-orange-200 text-orange-900 border-orange-500 border-dashed dark:bg-orange-900/30 dark:text-orange-400';
                                                                        else if (!p.isModified) {
                                                                            if (idx === 0 || idx === 2) colorClass = 'bg-blue-600 text-white border-blue-700 shadow-sm dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700'; // Entradas
                                                                            else if (idx === 1 || idx === 3) colorClass = 'bg-orange-500 text-white border-orange-600 shadow-sm dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-700'; // Saídas
                                                                            else if (idx % 2 === 0) colorClass = 'bg-purple-600 text-white border-purple-700 shadow-sm dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700'; // Entradas Extra
                                                                            else colorClass = 'bg-pink-600 text-white border-pink-700 shadow-sm dark:bg-pink-900/40 dark:text-pink-300 dark:border-pink-700'; // Saídas Extra
                                                                        }
                                                                        return (
                                                                            <span key={idx} className={`px-2 py-1 rounded border font-mono font-bold text-[11px] shadow-sm ${colorClass}`}>
                                                                                {p.time}
                                                                            </span>
                                                                        );
                                                                    })}
                                                                    {row.punches.length === 0 && <span className="text-gray-400 italic text-[10px]">- Sem marcações -</span>}
                                                                    
                                                                    {row.justificationsText && (
                                                                        <span className={`px-2 py-1 rounded-md border font-medium text-[10px] ml-2 ${row.justificationsText.includes('Abonado') ? 'bg-green-100 text-green-700 border-green-200' : isDarkMode ? 'bg-orange-900/20 text-orange-300 border-orange-800' : 'bg-orange-50 text-orange-700 border-orange-200'}`}>
                                                                            {row.justificationsText}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className={`p-3 text-center font-bold font-mono text-sm align-middle ${row.balance >= 0 ? 'text-green-500' : 'text-red-500'} ${row.hasApprovedJustification && row.balance === 0 ? '!text-purple-500 italic' : ''}`}>
                                                                {row.hasApprovedJustification && row.balance === 0 ? 'Abonado' : minutesToTimePonto(row.balance)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {sheetData.length === 0 && (
                                                        <tr><td colSpan="3" className="p-8 text-center italic text-gray-400">Nenhum registro para o período selecionado.</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Linha 4: Botão de Assinatura */}
                                        <div className="pt-4 border-t dark:border-slate-700 shrink-0">
                                            {signatureData ? (
                                                <div className={`p-4 rounded-xl border border-green-200 bg-green-50 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400 flex items-center justify-center gap-3`}>
                                                    <CheckCircle size={28}/>
                                                    <div>
                                                        <p className="font-bold text-base">Este período já foi assinado eletronicamente.</p>
                                                        <p className="text-xs opacity-80 mt-0.5">Acesse a aba "Documentos Assinados" para ver o histórico e gerar o QR Code.</p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <Button onClick={handleConfirmSign} disabled={isProcessing || sheetData.length === 0} className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-widest shadow-lg border-0 text-sm">
                                                    {isProcessing ? <Loader2 className="animate-spin mx-auto"/> : 'Confirmar e Assinar Folha de Ponto'}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {signModalTab === 'assinados' && (
                                    <div className="space-y-4 animate-fade-in">
                                        <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                            <thead className={`text-xs uppercase font-bold sticky top-0 border-b ${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                                                <tr>
                                                    <th className="p-3">Mês Ref.</th>
                                                    <th className="p-3">Data Assinatura</th>
                                                    <th className="p-3 text-center">Status (Gestor)</th>
                                                    <th className="p-3 text-center">Ações</th>
                                                </tr>
                                            </thead>
                                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                {mySignaturesList.map(sig => (
                                                    <tr key={sig.id} className={isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'}>
                                                        <td className="p-3 font-bold text-lg">{sig.month}</td>
                                                        <td className="p-3 font-mono text-xs">{new Date(sig.date).toLocaleString('pt-BR')}</td>
                                                        <td className="p-3 text-center">
                                                            <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase border ${sig.managerSignature ? 'bg-green-100 text-green-700 border-green-300' : 'bg-yellow-100 text-yellow-700 border-yellow-300'}`}>
                                                                {sig.managerSignature ? 'Validado' : 'Pendente'}
                                                            </span>
                                                        </td>
                                                        <td className="p-3 text-center flex justify-center gap-2">
                                                            <Button 
                                                                onClick={() => {
                                                                    setRefMonth(sig.month);
                                                                    setTimeout(() => handlePrint(), 500);
                                                                }} 
                                                                size="sm" variant="secondary" className="text-[10px] h-8" icon={Printer}
                                                            >
                                                                Imprimir
                                                            </Button>
                                                            {sig.qrCodeUrl && (
                                                                <a href={sig.publicUrl} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors flex items-center justify-center border border-indigo-200" title="Ver Link/QR Code Online">
                                                                    <Globe size={16}/>
                                                                </a>
                                                            )}
                                                            {/* NOVO: BOTÃO DESFAZER ASSINATURA */}
                                                            <button 
                                                                onClick={() => handleUndoSpecificSign(sig)} 
                                                                className="p-1.5 rounded bg-red-100 text-red-700 hover:bg-red-200 transition-colors flex items-center justify-center border border-red-200" 
                                                                title="Desfazer Assinatura / Excluir"
                                                            >
                                                                <Trash2 size={16}/>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {mySignaturesList.length === 0 && <tr><td colSpan="4" className="p-6 text-center italic opacity-50">Nenhum documento assinado.</td></tr>}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL DE ASSINATURA DA EQUIPE */}
                {isManagerListOpen && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4" onClick={() => setIsManagerListOpen(false)}>
                        <div className={`p-6 rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh] relative ${isDarkMode ? 'bg-slate-800 border border-slate-700 text-white' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            <div className={`flex justify-between items-center mb-4 border-b pb-3 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                <h3 className="font-bold text-lg flex items-center gap-2 text-purple-600">
                                    <Users size={20}/> Pendências da Equipe ({refMonth})
                                </h3>
                                <button onClick={() => setIsManagerListOpen(false)} className="text-gray-500 hover:text-red-500 transition-colors"><X size={20}/></button>
                            </div>
                            
                            <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-3">
                                {pendingSignaturesList.length === 0 ? (
                                    <p className="text-center text-sm text-gray-400 py-8">Nenhuma assinatura pendente de validação para este período.</p>
                                ) : (
                                    pendingSignaturesList.map(sig => (
                                        <div key={sig.id} className={`flex items-center justify-between p-3 rounded-lg border ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                            <div>
                                                <div className="font-bold text-sm">{sig.userName}</div>
                                                <div className="text-[10px] text-gray-500 mt-1">Assinado pelo colab em: {new Date(sig.date).toLocaleDateString('pt-BR')}</div>
                                            </div>
                                            <Button 
                                                onClick={() => {
                                                    setTargetUserId(sig.userId);
                                                    setIsManagerListOpen(false);
                                                }} 
                                                className="text-xs py-1.5 h-8 bg-blue-100 text-blue-700 hover:bg-blue-200 border-0"
                                            >
                                                Ver Espelho
                                            </Button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                )} 

            {/* MODAL DE INCONSISTÊNCIAS E JUSTIFICATIVAS */}
                {isInconsistenciesModalOpen && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4" onClick={() => setIsInconsistenciesModalOpen(false)}>
                        <div className={`p-6 rounded-xl w-full max-w-6xl shadow-2xl flex flex-col h-[90vh] relative ${isDarkMode ? 'bg-slate-800 border border-slate-700 text-white' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            
                            <div className={`flex justify-between items-center mb-4 border-b pb-3 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                <div>
                                    <h3 className="font-bold text-xl flex items-center gap-2 text-red-500">
                                        <AlertTriangle size={24}/> Relatório de Inconsistências
                                    </h3>
                                    <p className="text-xs text-gray-500 mt-1">Acompanhe atrasos, justificativas e batidas manuais da equipe.</p>
                                </div>
                                <div className="flex gap-2">
                                    <Button onClick={exportIncPDF} className="bg-red-600 hover:bg-red-700 text-white text-xs py-1.5 h-8 border-0" icon={FileText}>PDF</Button>
                                    <Button onClick={exportIncExcel} className="bg-green-600 hover:bg-green-700 text-white text-xs py-1.5 h-8 border-0" icon={FileSpreadsheet}>Excel</Button>
                                    <div className="w-px h-8 bg-gray-300 mx-2"></div>
                                    <button onClick={() => setIsInconsistenciesModalOpen(false)} className="text-gray-500 hover:text-red-500 transition-colors"><X size={24}/></button>
                                </div>
                            </div>

                            {/* FILTROS */}
                            <div className={`grid grid-cols-1 md:grid-cols-5 gap-3 p-3 rounded-lg mb-4 shrink-0 ${isDarkMode ? 'bg-slate-900 border border-slate-700' : 'bg-gray-50 border border-gray-200'}`}>
                                <div><label className="text-[10px] font-bold uppercase opacity-70">Data Início</label><input type="date" value={incFilters.startDate} onChange={e => setIncFilters({...incFilters, startDate: e.target.value})} className={`w-full p-1.5 border rounded text-xs outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white invert-calendar' : 'bg-white'}`} /></div>
                                <div><label className="text-[10px] font-bold uppercase opacity-70">Data Fim</label><input type="date" value={incFilters.endDate} onChange={e => setIncFilters({...incFilters, endDate: e.target.value})} className={`w-full p-1.5 border rounded text-xs outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white invert-calendar' : 'bg-white'}`} /></div>
                                <div>
                                    <label className="text-[10px] font-bold uppercase opacity-70">Departamento</label>
                                    <select value={incFilters.dept} onChange={e => setIncFilters({...incFilters, dept: e.target.value})} className={`w-full p-1.5 border rounded text-xs outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`}>
                                        <option value="">Todos</option>
                                        {departments?.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold uppercase opacity-70">Tipo</label>
                                    <select value={incFilters.type} onChange={e => setIncFilters({...incFilters, type: e.target.value})} className={`w-full p-1.5 border rounded text-xs outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`}>
                                        <option value="Todos">Todas Inconsistências</option>
                                        <option value="Divergente">Atrasos/Adiantos (Digital)</option>
                                        <option value="Manual">Batidas Manuais</option>
                                        <option value="Justificativa">Justificativas e Atestados</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold uppercase opacity-70">Busca Livre</label>
                                    <input type="text" placeholder="Nome ou observação..." value={incFilters.search} onChange={e => setIncFilters({...incFilters, search: e.target.value})} className={`w-full p-1.5 border rounded text-xs outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`} />
                                </div>
                            </div>
                            
                            {/* TABELA */}
                            <div className="flex-1 overflow-y-auto custom-scrollbar border rounded-lg dark:border-slate-700">
                                <table className="w-full text-xs text-left border-collapse">
                                    <thead className={`sticky top-0 shadow-sm uppercase font-bold ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-600'}`}>
                                        <tr>
                                            <th className="p-3 w-24">Data</th>
                                            <th className="p-3 w-48">Colaborador / Depto</th>
                                            <th className="p-3 w-32 text-center">Tipo Ocorrência</th>
                                            <th className="p-3 w-32 text-center">Horário/Gatilho</th>
                                            <th className="p-3">Histórico / Observações</th>
                                            <th className="p-3 w-20 text-center">Anexo</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                        {processedInconsistencies.length === 0 ? (
                                            <tr><td colSpan={6} className="p-8 text-center text-gray-400 italic">Nenhum registro encontrado no período selecionado.</td></tr>
                                        ) : (
                                            processedInconsistencies.map((inc, i) => (
                                                <tr key={inc.id} className={`${i%2===0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50')} hover:brightness-95 transition-all`}>
                                                    <td className="p-3 font-mono">{new Date(inc.date + 'T12:00:00').toLocaleDateString('pt-BR')}</td>
                                                    <td className="p-3">
                                                        <div className="font-bold">{inc.collabName}</div>
                                                        <div className="text-[9px] opacity-70 truncate">{inc.deptName}</div>
                                                    </td>
                                                    <td className="p-3 text-center">
                                                        <span className={`px-2 py-1 rounded-md text-[9px] font-bold uppercase border ${inc.source === 'manual' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' : inc.type === 'Justificativa' ? 'bg-orange-100 text-orange-800 border-orange-300' : 'bg-red-100 text-red-800 border-red-300'}`}>
                                                            {inc.tipoLabel}
                                                        </span>
                                                    </td>
                                                    <td className="p-3 text-center">
                                                        {inc.time ? (
                                                            <span className="font-mono font-bold text-sm bg-black/5 dark:bg-white/10 px-2 py-0.5 rounded">{inc.time}</span>
                                                        ) : (
                                                            <span className="text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                    <td className="p-3">
                                                        {inc.category && <span className="font-bold text-[10px] text-blue-600 block mb-0.5">{inc.category}</span>}
                                                        <div className="text-[11px] leading-tight opacity-90 whitespace-pre-wrap">{inc.obs || inc.details || <span className="italic opacity-50">Sem observações</span>}</div>
                                                    </td>
                                                    <td className="p-3 text-center">
                                                        {inc.fileUrl ? (
                                                            <a href={inc.fileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 flex justify-center" title="Ver Anexo"><FileText size={16}/></a>
                                                        ) : <span className="text-gray-300">-</span>}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )} 
            </div>
        );
    };

    /**
     * ============================================================================
     * COMPONENTE: SISTEMA DE PESQUISAS (RH / CLIMA) - VERSÃO COMPLETA E EXPANDIDA
     * ============================================================================
     */


    /* ===============================================================================
     * [END_MOD_PONTO_BANK]
     * FIM DO BLOCO: BankModule
     * [START_MOD_SURVEYS_SYSTEM]
     * INICIO DO BLOCO: SurveysSystem
     * =============================================================================== */
    const SurveysSystem = ({ data, actions, currentUser, isDarkMode }) => {
        // ========================================================================
        // 1. ESTADOS E VARIÁVEIS
        // ========================================================================
        
        const hasPerm = (key) => currentUser?.role === 'SuperAdm' || currentUser?.permissions?.[key] === true;
        const [validationAlert, setValidationAlert] = useState(null);

        // Controle de Visualização
        const [viewMode, setViewMode] = useState('list'); 
        const [search, setSearch] = useState('');
        
        // Controle de Modais
        const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
        const [isAnsweringModalOpen, setIsAnsweringModalOpen] = useState(false);
        const [activeConfigTab, setActiveConfigTab] = useState('general');

        const [contributorSearch, setContributorSearch] = useState('');
        const [currentSurvey, setCurrentSurvey] = useState(null); 
        const [answersForm, setAnswersForm] = useState({}); 

        const [surveyFilter, setSurveyFilter] = useState(''); 
        const [dateRange, setDateRange] = useState('30'); 
        const [surveySearch, setSurveySearch] = useState('');
        const [surveyViewModal, setSurveyViewModal] = useState(null);
        const [responseFilterDept, setResponseFilterDept] = useState(''); 
        const [responseFilterType, setResponseFilterType] = useState(''); 

        const [responseFilterDeadline, setResponseFilterDeadline] = useState('all'); 
        const [followUpModal, setFollowUpModal] = useState({
            isOpen: false,
            responseId: null,
            deadline: '',
            reminders: [],
            completed: false
        });
        
        // Estado do Formulário Principal
        const [surveyForm, setSurveyForm] = useState({
            id: null,
            shortCode: '',
            title: '',
            description: '',
            
            isAnonymous: false,
            isActive: true,
            hideAfterResponse: true,
            allowMultiple: false,
            
            collectsDept: false,      
            collectsPersonType: false, 
            
            recurrenceType: 'none',
            customInterval: 0,
            openDuration: 7,
            deadline: '',
            
            isSectorInternal: false,
            targetDepartments: [], 
            targetAudienceType: 'all',
            targetUsers: [],

            sections: [] 
        }); 
        
        const RH_COLORS = [
            { bg: 'bg-gradient-to-br from-purple-600 to-indigo-600', text: 'text-white', icon: 'text-purple-200' },
            { bg: 'bg-gradient-to-br from-pink-500 to-rose-500', text: 'text-white', icon: 'text-pink-200' },
            { bg: 'bg-gradient-to-br from-teal-500 to-emerald-500', text: 'text-white', icon: 'text-teal-200' },
            { bg: 'bg-gradient-to-br from-blue-500 to-cyan-500', text: 'text-white', icon: 'text-blue-200' },
            { bg: 'bg-gradient-to-br from-orange-400 to-amber-500', text: 'text-white', icon: 'text-amber-100' },
        ];

        // ========================================================================
        // 2. FUNÇÕES AUXILIARES (HELPERS)
        // ========================================================================

    const initNewSurvey = () => {
            setSurveyForm({
                id: null,
                shortCode: '',
                title: '',
                description: '',
                isAnonymous: false,
                isActive: true,
                hideAfterResponse: true,
                allowMultiple: false,
                collectsDept: false,
                collectsPersonType: false,
                recurrenceType: 'none',
                customInterval: 0,
                openDuration: 7,
                deadline: '',
                isSectorInternal: false,
                targetDepartments: [], 
                targetAudienceType: 'all',
                targetUsers: [],
                sections: [{ 
                    id: Date.now().toString(), 
                    title: 'Grupo 1', 
                    description: '', 
                    questions: [] 
                }]
            });
            setActiveConfigTab('general');
            setContributorSearch('');
            setIsConfigModalOpen(true);
        };

        const handleReOpenSurvey = async (survey) => {
            const defaultDate = new Date();
            defaultDate.setDate(defaultDate.getDate() + 7);
            
            const newDeadline = prompt(
                "Informe a nova data limite (AAAA-MM-DD):", 
                defaultDate.toISOString().split('T')[0]
            );
            
            if (newDeadline) {
                const updated = { ...survey, isActive: true, deadline: newDeadline };
                if (actions.saveItem) {
                    await actions.saveItem('surveys', updated);
                    alert("Pesquisa reaberta com sucesso!");
                }
            }
        };

        // ========================================================================
        // 3. GERENCIAMENTO DE SEÇÕES E PERGUNTAS (BUILDER)
        // ========================================================================

        const addSection = () => {
            setSurveyForm(prev => ({
                ...prev,
                sections: [
                    ...(prev.sections || []), 
                    { 
                        id: Date.now().toString(), 
                        title: `Novo Grupo ${(prev.sections?.length || 0) + 1}`, 
                        questions: [] 
                    }
                ]
            }));
        };

        const removeSection = (sectionId) => {
            if (window.confirm("Remover este grupo e todas as perguntas?")) {
                setSurveyForm(prev => ({ 
                    ...prev, 
                    sections: prev.sections.filter(s => s.id !== sectionId) 
                }));
            }
        };

        const updateSectionTitle = (sectionId, newVal) => {
            setSurveyForm(prev => ({ 
                ...prev, 
                sections: prev.sections.map(s => s.id === sectionId ? { ...s, title: newVal } : s) 
            }));
        };

        const addQuestionToSection = (sectionId) => {
            const newQ = {
                id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
                text: '', 
                type: 'text', 
                required: true, 
                options: [], 
                allowMultiple: false, 
                hasOther: false, 
                justifyTrigger: []
            };
            setSurveyForm(prev => ({ 
                ...prev, 
                sections: prev.sections.map(s => 
                    s.id !== sectionId ? s : { ...s, questions: [...s.questions, newQ] }
                ) 
            }));
        };

        const addContextToSection = (sectionId) => { 
            const newCtx = { 
                id: Math.random().toString(36).substr(2, 9), 
                type: 'context', 
                text: '', 
                imageUrl: null, 
                referenceInfo: '' 
            }; 
            setSurveyForm(prev => ({ 
                ...prev, 
                sections: prev.sections.map(s => 
                    s.id === sectionId ? { ...s, questions: [...s.questions, newCtx] } : s
                ) 
            })); 
        };

        const duplicateQuestion = (sectionId, question) => {
            const cloned = {
                ...question,
                id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
                text: question.text ? question.text + ' (Cópia)' : '',
                options: [...(question.options || [])],
                justifyTrigger: [...(question.justifyTrigger || [])]
            };
            setSurveyForm(prev => ({
                ...prev,
                sections: prev.sections.map(s => {
                    if (s.id !== sectionId) return s;
                    const idx = s.questions.findIndex(x => x.id === question.id);
                    const newQs = [...s.questions];
                    newQs.splice(idx + 1, 0, cloned);
                    return { ...s, questions: newQs };
                })
            }));
        };

        const updateQuestionInSection = (sectionId, questionId, field, value) => {
            setSurveyForm(prev => ({
                ...prev,
                sections: prev.sections.map(s => {
                    if (s.id !== sectionId) return s;
                    return { 
                        ...s, 
                        questions: s.questions.map(q => q.id === questionId ? { ...q, [field]: value } : q) 
                    };
                })
            }));
        };

        const removeQuestionFromSection = (sectionId, questionId) => {
            setSurveyForm(prev => ({
                ...prev,
                sections: prev.sections.map(s => {
                    if (s.id !== sectionId) return s;
                    return { 
                        ...s, 
                        questions: s.questions.filter(q => q.id !== questionId) 
                    };
                })
            }));
        };

        // ========================================================================
        // 4. GERENCIAMENTO DE OPÇÕES (MÚLTIPLA ESCOLHA) E TABELAS
        // ========================================================================

        const addOptionToQuestion = (sectionId, questionId) => {
            setSurveyForm(prev => ({
                ...prev,
                sections: prev.sections.map(s => 
                    s.id !== sectionId ? s : {
                        ...s, 
                        questions: s.questions.map(q => 
                            q.id !== questionId ? q : { 
                                ...q, 
                                options: [...(q.options || []), `Opção ${(q.options || []).length + 1}`] 
                            }
                        )
                    }
                )
            }));
        };

        const updateOptionText = (sectionId, questionId, optionIndex, newText) => {
            setSurveyForm(prev => ({
                ...prev,
                sections: prev.sections.map(s => 
                    s.id !== sectionId ? s : {
                        ...s, 
                        questions: s.questions.map(q => {
                            if (q.id !== questionId) return q;
                            const oldText = q.options[optionIndex];
                            const newOptions = [...(q.options || [])];
                            newOptions[optionIndex] = newText;
                            
                            let newTriggers = [...(q.justifyTrigger || [])];
                            if (newTriggers.includes(oldText)) {
                                newTriggers = newTriggers.map(t => t === oldText ? newText : t);
                            }
                            
                            return { ...q, options: newOptions, justifyTrigger: newTriggers };
                        })
                    }
                )
            }));
        };

        const removeOption = (sectionId, questionId, optionIndex) => {
            setSurveyForm(prev => ({
                ...prev,
                sections: prev.sections.map(s => 
                    s.id !== sectionId ? s : {
                        ...s, 
                        questions: s.questions.map(q => {
                            if (q.id !== questionId) return q;
                            const oldText = q.options[optionIndex];
                            const newOptions = [...(q.options || [])];
                            newOptions.splice(optionIndex, 1);
                            
                            const newTriggers = (q.justifyTrigger || []).filter(t => t !== oldText);
                            
                            return { ...q, options: newOptions, justifyTrigger: newTriggers };
                        })
                    }
                )
            }));
        };

        const toggleJustifyOption = (sectionId, questionId, text) => {
            setSurveyForm(prev => ({
                ...prev,
                sections: prev.sections.map(s => 
                    s.id !== sectionId ? s : {
                        ...s, 
                        questions: s.questions.map(q => {
                            if (q.id !== questionId) return q;
                            const triggers = q.justifyTrigger || [];
                            const newTriggers = triggers.includes(text) 
                                ? triggers.filter(t => t !== text) 
                                : [...triggers, text];
                            return { ...q, justifyTrigger: newTriggers };
                        })
                    }
                )
            }));
        };

        // ========================================================================
        // 5. AÇÕES DE PERSISTÊNCIA E RESPOSTAS
        // ========================================================================

        const handleSaveSurvey = async () => {
            if (!surveyForm.title) return alert("O título da pesquisa é obrigatório.");
            const hasQuestions = surveyForm.sections?.some(s => s.questions.length > 0);
            if (!hasQuestions) return alert("Adicione ao menos um grupo e uma pergunta.");

            const payload = {
                ...surveyForm,
                id: surveyForm.id || Date.now().toString(),
                createdAt: surveyForm.createdAt || new Date().toISOString(),
                createdBy: currentUser.email
            };

            if (actions.saveItem) {
                await actions.saveItem('surveys', payload);
                setIsConfigModalOpen(false);
                alert("Pesquisa salva com sucesso!");
            }
        };

        const handleOpenAnswer = (survey) => {
            setCurrentSurvey(survey);
            setAnswersForm({});
            setIsAnsweringModalOpen(true);
        };

        const handleSubmitResponse = async () => {
            // Validações
            for (const section of currentSurvey.sections || []) {
                for (const q of section.questions) {
                    if (q.type === 'context') continue;

                    if (q.required) {
                        const response = answersForm[q.id];
                        const hasValue = response?.value && (Array.isArray(response.value) ? response.value.length > 0 : String(response.value).trim().length > 0);
                        const hasOtherContent = response?.isOtherChecked && response?.otherText?.trim().length > 0;
                        
                        let isSpreadsheetEmpty = false;
                        if (q.type === 'spreadsheet') {
                            if (!response?.value || typeof response.value !== 'object' || Object.keys(response.value).length === 0) isSpreadsheetEmpty = true;
                        }

                        if ((!hasValue || isSpreadsheetEmpty) && !hasOtherContent && q.type !== 'enps') {
                            return setValidationAlert({ title: "Pergunta Pendente", message: `A pergunta "${q.text}" é obrigatória.` });
                        }
                        // Validação específica para enps quando required
                        if (q.type === 'enps' && (response?.value === undefined || response?.value === null)) {
                            return setValidationAlert({ title: "Pergunta Pendente", message: `A pergunta "${q.text}" é obrigatória.` });
                        }
                    }
                    
                    if ((q.type === 'multi' || q.type === 'dropdown') && q.justifyTrigger?.length > 0) {
                        const selected = answersForm[q.id]?.value;
                        const checkList = Array.isArray(selected) ? selected : (selected ? [selected] : []);
                        const needsJustify = checkList.some(s => q.justifyTrigger.includes(s));
                        if (needsJustify && (!answersForm[q.id]?.justification || answersForm[q.id].justification.trim().length < 3)) {
                            return setValidationAlert({ title: "Justificativa Obrigatória", message: `Justifique sua resposta na pergunta: "${q.text}".` });
                        }
                    }
                }
            }

            const now = new Date();
            let userIdToSave = null;
            let userEmailToSave = null;
            let deptToSave = null;
            let typeToSave = null;

            const realDeptName = data.departments.find(d => d.id === currentUser.departmentId)?.name || 'N/D';
            const collabProfile = data.collaborators.find(c => c.email === currentUser.email);
            const realPersonType = collabProfile ? collabProfile.personType : (currentUser.role === 'Gestor' || currentUser.role === 'SuperAdm' ? 'Gestão' : 'Colaborador');

            if (currentSurvey.isAnonymous) {
                if (currentSurvey.collectsDept) deptToSave = realDeptName;
                if (currentSurvey.collectsPersonType) typeToSave = realPersonType;
            } else {
                userIdToSave = currentUser.id;
                userEmailToSave = currentUser.email;
                deptToSave = realDeptName;
                typeToSave = realPersonType;
            }

            const payload = {
                id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
                surveyId: currentSurvey.id,
                surveyTitle: currentSurvey.title,
                date: now.toISOString(),
                formattedDate: now.toLocaleDateString('pt-BR'),
                timestamp: now.getTime(),
                userId: userIdToSave,
                userEmail: userEmailToSave,
                userDept: deptToSave,
                userType: typeToSave,
                answers: answersForm
            };

            try {
                if (actions.saveItem) {
                    await actions.saveItem('surveyResponses', payload);
                    setIsAnsweringModalOpen(false);
                    setValidationAlert({ title: "Sucesso!", message: "Sua resposta foi enviada com sucesso." });
                }
            } catch (error) {
                setValidationAlert({ title: "Erro", message: "Não foi possível salvar sua resposta." });
            }
        };

        // ==============================================================================
        // LÓGICA DE FILTRAGEM, EXIBIÇÃO E EXPORTAÇÃO
        // ==============================================================================
        
        const [displayMode, setDisplayMode] = useState('detalhado');
        const [isExporting, setIsExporting] = useState(false);

        const getFilteredResponses = useMemo(() => {
            if (!surveyFilter) return [];
            let cutoffDate = new Date();
            if (dateRange !== 'custom') {
                cutoffDate.setDate(cutoffDate.getDate() - parseInt(dateRange));
            }
            return (data.surveyResponses || []).filter(r => {
                if (r.surveyId !== surveyFilter) return false;
                const rDate = new Date(r.timestamp);
                if (dateRange !== 'custom' && rDate < cutoffDate) return false;
                if (surveySearch) {
                    const s = surveySearch.toLowerCase();
                    const emailStr = r.userEmail || '';
                    if (!emailStr.toLowerCase().includes(s)) return false;
                }
                if (responseFilterDept && r.userDept !== data.departments.find(d => d.id === responseFilterDept)?.name) return false;
                if (responseFilterType && r.userType !== responseFilterType) return false;
                if (responseFilterDeadline === 'with_deadline' && !r.followUpDeadline) return false; 
                if (responseFilterDeadline === 'due_reminders') {
                    if (r.followUpCompleted) return false;
                    const today = new Date().toISOString().split('T')[0];
                    if (!r.followUpReminders || !r.followUpReminders.some(d => d && d <= today)) return false;
                }
                return true;
            }).sort((a, b) => {
                if (responseFilterDeadline === 'with_deadline' || responseFilterDeadline === 'due_reminders') {
                    const dateA = a.followUpDeadline ? new Date(a.followUpDeadline).getTime() : Infinity;
                    const dateB = b.followUpDeadline ? new Date(b.followUpDeadline).getTime() : Infinity;
                    if (dateA === dateB) return b.timestamp - a.timestamp;
                    return dateA - dateB; 
                }
                return b.timestamp - a.timestamp;
            });
        }, [data.surveyResponses, surveyFilter, dateRange, surveySearch, responseFilterDept, responseFilterType, responseFilterDeadline, data.departments]);

        const indicators = useMemo(() => {
            const stats = {};
            (data.surveys || []).forEach(s => {
                const unreadCount = (data.surveyResponses || []).filter(r => r.surveyId === s.id && !r.read).length;
                if (unreadCount > 0) stats[s.id] = { title: s.title, count: unreadCount, id: s.id };
            });
            return Object.values(stats);
        }, [data.surveys, data.surveyResponses]);

        const markAsRead = async (responseId) => {
            if (actions.saveItem) {
                const original = (data.surveyResponses || []).find(r => r.id === responseId);
                if (original) await actions.saveItem('surveyResponses', { ...original, read: true });
            }
        };

        const summarizedData = useMemo(() => {
            if (!surveyFilter || displayMode !== 'totalizado') return null;
            const surveyDef = (data.surveys || []).find(s => s.id === surveyFilter);
            if (!surveyDef) return null;

            const summary = [];
            const totalResponsesCount = getFilteredResponses.length;

            surveyDef.sections?.forEach(sec => {
                sec.questions.forEach(q => {
                    if (q.type === 'context' || q.type === 'spreadsheet') return; 
                    
                    const answersCount = {};
                    let validAnswers = 0;
                    
                    if (q.type === 'enps') {
                        let promoters = 0, detractors = 0, neutrals = 0;
                        getFilteredResponses.forEach(r => {
                            const ans = r.answers?.[q.id];
                            if (ans && ans.value !== undefined) {
                                validAnswers++;
                                const val = parseInt(ans.value);
                                if (val >= 9) promoters++;
                                else if (val >= 7) neutrals++;
                                else detractors++;
                            }
                        });
                        const enpsScore = validAnswers > 0 ? Math.round(((promoters / validAnswers) * 100) - ((detractors / validAnswers) * 100)) : 0;
                        summary.push({ 
                            questionId: q.id, 
                            questionText: q.text, 
                            type: q.type, 
                            validAnswers, 
                            enpsData: { score: enpsScore, promoters, neutrals, detractors }, 
                            results: [] 
                        });
                        return; 
                    }

                    getFilteredResponses.forEach(r => {
                        const ans = r.answers?.[q.id];
                        if (ans && ans.value) {
                            validAnswers++;
                            const vals = Array.isArray(ans.value) ? ans.value : [ans.value];
                            vals.forEach(v => {
                                const key = String(v).trim();
                                if (key) answersCount[key] = (answersCount[key] || 0) + 1;
                            });
                            if (ans.otherText) {
                                const otherKey = `Outros: ${ans.otherText.trim()}`;
                                answersCount[otherKey] = (answersCount[otherKey] || 0) + 1;
                            }
                        }
                    });

                    const sortedAnswers = Object.entries(answersCount)
                        .map(([label, count]) => ({ 
                            label, 
                            count, 
                            percent: totalResponsesCount > 0 ? ((count / totalResponsesCount) * 100).toFixed(1) : 0 
                        }))
                        .sort((a, b) => b.count - a.count);

                    summary.push({ 
                        questionId: q.id, 
                        questionText: q.text, 
                        type: q.type, 
                        validAnswers, 
                        results: sortedAnswers 
                    });
                });
            });

            return summary;
        }, [surveyFilter, displayMode, getFilteredResponses, data.surveys]);

        const exportSurveyExcel = async () => {
            setIsExporting(true);
            try {
                if (typeof loadImportLibs !== 'undefined') await loadImportLibs();
                if (!window.XLSX) throw new Error("Biblioteca XLSX não disponível.");

                const surveyDef = (data.surveys || []).find(s => s.id === surveyFilter);
                const surveyTitle = surveyDef ? surveyDef.title : 'Pesquisa';
                let dataExport = [];

                if (displayMode === 'detalhado') {
                    const header = ["Data", "Participante", "Departamento", "Tipo"];
                    const questions = [];
                    surveyDef?.sections?.forEach(sec => {
                        sec.questions.forEach(q => { 
                            if (q.type !== 'context') {
                                header.push(q.text); questions.push(q); 
                            }
                        });
                    });
                    
                    getFilteredResponses.forEach(r => {
                        const row = { 
                            "Data": new Date(r.timestamp).toLocaleDateString(), 
                            "Participante": r.userEmail || 'Anônimo', 
                            "Departamento": r.userDept || '-', 
                            "Tipo": r.userType || '-' 
                        };
                        questions.forEach(q => {
                            const ans = r.answers?.[q.id];
                            let val = '-';
                            if (ans) {
                                if (q.type === 'spreadsheet') {
                                    val = "Respondido na Planilha";
                                } else {
                                    val = Array.isArray(ans.value) ? ans.value.join(', ') : (ans.value || '');
                                    if (ans.otherText) val += ` (Outro: ${ans.otherText})`;
                                    if (ans.justification) val += ` [Justificativa: ${ans.justification}]`;
                                }
                            }
                            row[q.text] = val;
                        });
                        dataExport.push(row);
                    });
                } else {
                    summarizedData.forEach(qSum => {
                        dataExport.push({ "Pergunta": qSum.questionText, "Resposta": "", "Quantidade": "", "%": "" });
                        
                        if (qSum.type === 'enps' && qSum.enpsData) {
                            dataExport.push({ "Pergunta": "", "Resposta": "eNPS Score", "Quantidade": qSum.enpsData.score, "%": "-" });
                            dataExport.push({ "Pergunta": "", "Resposta": "Promotores (9-10)", "Quantidade": qSum.enpsData.promoters, "%": "-" });
                            dataExport.push({ "Pergunta": "", "Resposta": "Neutros (7-8)", "Quantidade": qSum.enpsData.neutrals, "%": "-" });
                            dataExport.push({ "Pergunta": "", "Resposta": "Detratores (0-6)", "Quantidade": qSum.enpsData.detractors, "%": "-" });
                        } else {
                            qSum.results.forEach(res => {
                                dataExport.push({ "Pergunta": "", "Resposta": res.label, "Quantidade": res.count, "%": `${res.percent}%` });
                            });
                        }
                        dataExport.push({}); 
                    });
                }

                const ws = window.XLSX.utils.json_to_sheet(dataExport);
                const wb = window.XLSX.utils.book_new();
                window.XLSX.utils.book_append_sheet(wb, ws, "Resultados");
                window.XLSX.writeFile(wb, `Resultados_${surveyTitle.replace(/[^a-z0-9]/gi, '_')}.xlsx`);
            } catch (e) {
                console.error(e);
                alert("Erro ao exportar Excel.");
            } finally {
                setIsExporting(false);
            }
        };

        const exportSurveyPDF = async () => {
            setIsExporting(true);
            try {
                if (typeof loadPdfLibs !== 'undefined') await loadPdfLibs();
                const PDFConstructor = (window.jspdf && window.jspdf.jsPDF) ? window.jspdf.jsPDF : window.jsPDF;
                if (!PDFConstructor) throw new Error("Biblioteca PDF não disponível.");
                
                const doc = new PDFConstructor('l', 'mm', 'a4'); 
                let startY = 20;
                const surveyDef = (data.surveys || []).find(s => s.id === surveyFilter);
                const surveyTitle = surveyDef ? surveyDef.title : 'Pesquisa';

                doc.setFontSize(16);
                doc.setFont("helvetica", "bold");
                doc.text(`Resultados: ${surveyTitle}`, 14, startY);
                startY += 10;

                if (displayMode === 'detalhado') {
                    const head = [["Data", "Participante", "Depto"]];
                    const questions = [];
                    surveyDef?.sections?.forEach(sec => {
                        sec.questions.forEach(q => {
                            if (q.type !== 'context') {
                                head[0].push(q.text.substring(0, 30) + (q.text.length > 30 ? '...' : ''));
                                questions.push(q);
                            }
                        });
                    });

                    const body = getFilteredResponses.map(r => {
                        const row = [ new Date(r.timestamp).toLocaleDateString(), r.userEmail || 'Anônimo', r.userDept || '-' ];
                        questions.forEach(q => {
                            const ans = r.answers?.[q.id];
                            let val = '-';
                            if (ans) {
                                if (q.type === 'spreadsheet') {
                                    val = "Respondido (Planilha)";
                                } else {
                                    val = Array.isArray(ans.value) ? ans.value.join(', ') : (ans.value || '');
                                    if (ans.otherText) val += ` (Outros: ${ans.otherText})`;
                                }
                            }
                            row.push(val);
                        });
                        return row;
                    });

                    doc.autoTable({ startY, head, body, theme: 'grid', styles: { fontSize: 7, overflow: 'linebreak' }, headStyles: { fillColor: [88, 28, 135] } });
                } else {
                    summarizedData.forEach(qSum => {
                        if (qSum.type === 'enps' && qSum.enpsData) {
                            const body = [
                                ["eNPS Score", qSum.enpsData.score, "-"],
                                ["Promotores (9-10)", qSum.enpsData.promoters, "-"],
                                ["Neutros (7-8)", qSum.enpsData.neutrals, "-"],
                                ["Detratores (0-6)", qSum.enpsData.detractors, "-"]
                            ];
                            doc.autoTable({
                                startY,
                                head: [[{ content: qSum.questionText + " (eNPS)", colSpan: 3, styles: { fillColor: [243, 244, 246], textColor: [0,0,0] } }], ["Categoria", "Quantidade/Score", ""]],
                                body: body,
                                theme: 'grid',
                                styles: { fontSize: 8 },
                                headStyles: { fillColor: [88, 28, 135] }
                            });
                            startY = doc.lastAutoTable.finalY + 10;
                        } else {
                            const body = qSum.results.map(res => [res.label, res.count, `${res.percent}%`]);
                            doc.autoTable({
                                startY,
                                head: [[{ content: qSum.questionText, colSpan: 3, styles: { fillColor: [243, 244, 246], textColor: [0,0,0] } }], ["Resposta", "Quantidade", "% (do total)"]],
                                body: body,
                                theme: 'grid',
                                styles: { fontSize: 8 },
                                headStyles: { fillColor: [88, 28, 135] }
                            });
                            startY = doc.lastAutoTable.finalY + 10;
                        }
                        if (startY > 180) { doc.addPage(); startY = 20; }
                    });
                }
                doc.save(`Resultados_${surveyTitle.replace(/[^a-z0-9]/gi, '_')}.pdf`);
            } catch (e) {
                console.error(e);
                alert("Erro ao exportar PDF.");
            } finally {
                setIsExporting(false);
            }
        };

        // ========================================================================
        // 6. LÓGICA DE FILTRAGEM (DASHBOARD)
        // ========================================================================
        
        const filteredSurveys = (data.surveys || []).filter(s => {
            const matchesSearch = s.title.toLowerCase().includes(search.toLowerCase());
            
            if (viewMode === 'config') return matchesSearch; 

            if (!s.isActive) return false;
            if (s.deadline) {
                const today = new Date().toISOString().split('T')[0];
                if (s.deadline < today) return false;
            }

            const hasResponded = (data.surveyResponses || []).some(r => r.surveyId === s.id && r.userId === currentUser.id);
            if (s.hideAfterResponse && hasResponded) return false;

            const myDeptId = currentUser.departmentId;
            const isDeptAllowed = (s.targetDepartments || []).length === 0 || (s.targetDepartments || []).includes(myDeptId);
            
            if (!isDeptAllowed) return false; 

            let isTypeAllowed = false;
            const myRole = currentUser.role; 
            const isManagerial = ['Gestor', 'Diretoria', 'RH', 'SuperAdm'].includes(myRole);

            if (s.targetAudienceType === 'all') isTypeAllowed = true;
            else if (s.targetAudienceType === 'management') isTypeAllowed = isManagerial;
            else if (s.targetAudienceType === 'contributors') isTypeAllowed = !isManagerial; 
            else if (s.targetAudienceType === 'custom') isTypeAllowed = (s.targetUsers || []).includes(currentUser.id);

            if (!isTypeAllowed) return false;

            return matchesSearch;
        });

        // ========================================================================
        // 7. RENDERIZAÇÃO (JSX)
        // ========================================================================
        return (
            <div className="space-y-6 animate-fade-in h-full flex flex-col">
                
                {/* HEADER */}
                <Card className={`p-4 shrink-0 flex flex-col md:flex-row justify-between items-center gap-4 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`} darkMode={isDarkMode}>
                    <div className="flex items-center gap-3">
                        <div className={`p-3 rounded-full ${isDarkMode ? 'bg-purple-900/50 text-purple-400' : 'bg-purple-100 text-purple-600'}`}>
                            <ClipboardCheck size={24} />
                        </div>
                        <div>
                            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Pesquisas & Clima</h2>
                            <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Participe e ajude a construir um ambiente melhor.</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 w-full md:w-auto">
                        {(currentUser.role === 'SuperAdm' || hasPerm('access_surveys_config')) && (
                            <button 
                                onClick={() => setViewMode(viewMode === 'list' ? 'config' : 'list')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm shadow-md transition-all ${viewMode === 'list' ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-slate-600 hover:bg-slate-700 text-white'}`}
                            >
                                {viewMode === 'list' ? (
                                    <><Settings size={18}/> Painel de Configuração</>
                                ) : (
                                    <><ArrowLeft size={18}/> Voltar aos Cards</>
                                )}
                            </button>
                        )}
                    </div>
                </Card>

                {/* MODO CONFIGURAÇÃO (ADMIN) - LISTA DE PESQUISAS CADASTRADAS */}
                {viewMode === 'config' && (
                    <div className="flex-1 overflow-hidden flex flex-col space-y-4">
                        <div className="flex justify-between items-center">
                            <div className="flex gap-2 overflow-x-auto pb-2 shrink-0 custom-scrollbar max-w-2xl">
                                <div className="p-2 px-4 rounded-lg bg-blue-100 text-blue-700 font-bold text-xs shadow-sm flex items-center gap-2">
                                    <FileText size={16}/> Total: {data.surveys?.length || 0}
                                </div>
                                <div className="p-2 px-4 rounded-lg bg-green-100 text-green-700 font-bold text-xs shadow-sm flex items-center gap-2">
                                    <CheckCircle size={16}/> Ativas: {(data.surveys || []).filter(s => s.isActive).length}
                                </div>
                            </div>
                            <Button onClick={initNewSurvey} icon={Plus} className="bg-purple-600 hover:bg-purple-700 text-white ml-auto">
                                Nova Pesquisa
                            </Button>
                        </div>

                        <Card className="p-0 overflow-hidden flex-1" darkMode={isDarkMode}>
                            <div className="overflow-y-auto h-full custom-scrollbar">
                                <table className={`w-full text-sm text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                    <thead className={`text-xs uppercase font-bold sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>
                                        <tr>
                                            <th className="p-4">Título / Código</th>
                                            <th className="p-4">Público Alvo</th>
                                            <th className="p-4">Vigência</th>
                                            <th className="p-4 text-center">Status</th>
                                            <th className="p-4 text-center">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                        {(data.surveys || []).map(s => (
                                            <tr key={s.id} className={`transition-colors ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'}`}>
                                                <td className="p-4">
                                                    <div className="font-bold">{s.title}</div>
                                                    {s.shortCode && (
                                                        <div className="text-[10px] bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-300 px-1.5 rounded w-fit mt-1">
                                                            {s.shortCode}
                                                        </div>
                                                    )}
                                                    <div className="text-xs opacity-60 mt-1 truncate max-w-[200px]">{s.description}</div>
                                                </td>
                                                <td className="p-4 text-xs">
                                                    <div className="flex flex-col gap-1">
                                                        <span className="font-bold">
                                                            Tipo: {s.targetAudienceType === 'all' ? 'Todos' : (s.targetAudienceType === 'management' ? 'Gestão' : 'Colaboradores')}
                                                        </span>
                                                        {s.targetDepartments?.length > 0 && (
                                                            <span className="opacity-70">
                                                                Depto: {data.departments.find(d => d.id === s.targetDepartments[0])?.name || 'Vários'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="p-4 text-xs font-mono">
                                                    {s.deadline ? new Date(s.deadline).toLocaleDateString('pt-BR') : 'Sem prazo'}
                                                </td>
                                                <td className="p-4 text-center">
                                                    <span className={`px-2 py-1 rounded text-[10px] uppercase font-bold border ${s.isActive ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200'}`}>
                                                        {s.isActive ? 'Ativa' : 'Fechada'}
                                                    </span>
                                                </td>
                                                <td className="p-4 text-center">
                                                    <div className="flex justify-center gap-2">
                                                        <button 
                                                            onClick={() => { setSurveyForm(s); setActiveConfigTab('general'); setIsConfigModalOpen(true); }} 
                                                            className="p-1.5 rounded text-blue-500 hover:bg-blue-100 transition-colors" 
                                                            title="Editar Configurações"
                                                        >
                                                            <Edit3 size={16}/>
                                                        </button>
                                                        <button 
                                                            onClick={() => handleReOpenSurvey(s)} 
                                                            className="p-1.5 rounded text-teal-600 hover:bg-teal-100 transition-colors" 
                                                            title="Reabrir / Estender Prazo"
                                                        >
                                                            <RefreshCw size={16}/>
                                                        </button>
                                                        <button 
                                                            onClick={() => { if(window.confirm("Tem certeza que deseja excluir esta pesquisa? As respostas vinculadas também serão perdidas.")) actions.genericDelete('surveys', s.id); }} 
                                                            className="p-1.5 rounded text-red-500 hover:bg-red-100 transition-colors" 
                                                            title="Excluir"
                                                        >
                                                            <Trash2 size={16}/>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {(data.surveys || []).length === 0 && (
                                            <tr>
                                                <td colSpan="5" className="p-8 text-center opacity-50 text-sm">
                                                    Nenhuma pesquisa cadastrada.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </div>
                )}

                {/* MODO CARDS (COLABORADOR) */}
                {viewMode === 'list' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 overflow-y-auto p-1">
                        {filteredSurveys.length === 0 ? (
                            <div className={`col-span-full p-10 text-center border-2 border-dashed rounded-xl ${isDarkMode ? 'border-slate-700 text-slate-500' : 'border-gray-300 text-gray-400'}`}>
                                <Sparkles size={40} className="mx-auto mb-4 opacity-30"/>
                                <p>Nenhuma pesquisa disponível para você no momento.</p>
                            </div>
                        ) : (
                            filteredSurveys.map((s, idx) => {
                                const theme = RH_COLORS[idx % RH_COLORS.length];
                                const myResponses = (data.surveyResponses || []).filter(r => r.surveyId === s.id && r.userId === currentUser.id);
                                const hasResponded = myResponses.length > 0;
                                
                                let isRecurringOpen = false;
                                let daysToWait = 0;

                                if (hasResponded && s.recurrenceType !== 'none') {
                                    const lastResponse = myResponses.sort((a,b) => new Date(b.date) - new Date(a.date))[0];
                                    const lastDate = new Date(lastResponse.date);
                                    const interval = s.recurrenceType === 'custom' ? (parseInt(s.customInterval) || 30) : parseInt(s.recurrenceType);
                                    const nextDate = new Date(lastDate);
                                    nextDate.setDate(lastDate.getDate() + interval);
                                    const today = new Date();
                                    if (today >= nextDate) isRecurringOpen = true;
                                    else daysToWait = Math.ceil(Math.abs(nextDate - today) / (1000 * 60 * 60 * 24)); 
                                }

                                let cardStatus = 'open'; 
                                let badgeLabel = 'Nova';
                                let badgeColor = 'green'; 

                                if (!hasResponded) {
                                    if (!s.deadline) { cardStatus = 'open'; badgeLabel = 'Permanente'; badgeColor = 'blue'; } 
                                    else { cardStatus = 'open'; badgeLabel = 'Nova'; badgeColor = 'green'; }
                                } else {
                                    if (s.allowMultiple && s.recurrenceType === 'none') { cardStatus = 'open'; badgeLabel = 'Disponível'; badgeColor = 'blue'; } 
                                    else if (s.recurrenceType !== 'none') {
                                        if (isRecurringOpen) { cardStatus = 'open'; badgeLabel = 'Nova (Recorrência)'; badgeColor = 'green'; } 
                                        else { cardStatus = 'waiting'; badgeLabel = `Aguarde ${daysToWait} dias`; badgeColor = 'orange'; }
                                    } else { cardStatus = 'blocked'; badgeLabel = 'Concluída'; badgeColor = 'gray'; }
                                }

                                const isClickable = cardStatus === 'open';
                                const cardBg = !isClickable ? (isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-100 border-gray-300') : theme.bg;
                                const textColor = !isClickable ? (isDarkMode ? 'text-slate-400' : 'text-gray-500') : 'text-white';

                                return (
                                    <div 
                                        key={s.id} 
                                        onClick={() => isClickable && handleOpenAnswer(s)} 
                                        className={`rounded-xl shadow-lg overflow-hidden flex flex-col transition-transform hover:scale-[1.02] group relative border ${!isClickable ? '' : 'border-transparent'} ${cardBg} ${!isClickable ? 'cursor-default' : 'cursor-pointer'}`}
                                    >
                                        <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
                                            {cardStatus === 'blocked' && (
                                                <span className="bg-gray-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1">
                                                    <CheckCircle size={10}/> Concluída
                                                </span>
                                            )}
                                            {cardStatus === 'waiting' && (
                                                <span className="bg-orange-500 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1">
                                                    <Clock size={10}/> {badgeLabel}
                                                </span>
                                            )}
                                            {cardStatus === 'open' && (
                                                <span className={`${badgeColor === 'green' ? 'bg-emerald-500 animate-pulse' : 'bg-blue-500'} text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1 border border-white/20`}>
                                                    <Sparkles size={10}/> {badgeLabel}
                                                </span>
                                            )}
                                        </div>
                                        <div className="p-6 flex-1 flex flex-col justify-between">
                                            <div>
                                                <div className={`mb-4 opacity-80 ${!isClickable ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : theme.icon}`}>
                                                    <ClipboardCheck size={32}/>
                                                </div>
                                                <h3 className={`text-xl font-bold mb-2 leading-tight ${textColor}`}>{s.title}</h3>
                                                <p className={`text-sm line-clamp-3 ${!isClickable ? 'opacity-60' : 'opacity-90'} ${textColor}`}>{s.description || 'Participe!'}</p>
                                            </div>
                                            <div className="mt-6 flex flex-wrap gap-2">
                                                <span className={`text-[10px] px-2 py-1 rounded backdrop-blur-sm flex items-center gap-1 ${!isClickable ? 'bg-black/10 text-gray-500' : 'bg-white/20 text-white'}`}>
                                                    {(() => {
                                                        const coletados = [];
                                                        if (!s.isAnonymous) coletados.push('E-mail');
                                                        if (s.collectsDept) coletados.push('Dpto');
                                                        if (s.collectsPersonType) coletados.push('Tipo de Pessoa');
                                                        if (coletados.length === 0) return <><Lock size={10}/> Anônima</>;
                                                        else return <><Info size={10}/> Dados Coletados: {coletados.join(', ').replace(/,([^,]*)$/, ' e$1')}</>;
                                                    })()}
                                                </span>
                                                {s.deadline && (
                                                    <span className={`text-[10px] px-2 py-1 rounded backdrop-blur-sm flex items-center gap-1 ${!isClickable ? 'bg-black/10 text-gray-500' : 'bg-white/20 text-white'}`}>
                                                        <Clock size={10}/> Até {new Date(s.deadline).toLocaleDateString('pt-BR')}
                                                    </span>
                                                )}
                                                {s.recurrenceType !== 'none' && (
                                                    <span className={`text-[10px] px-2 py-1 rounded backdrop-blur-sm flex items-center gap-1 ${!isClickable ? 'bg-black/10 text-gray-500' : 'bg-white/20 text-white'}`}>
                                                        <Copy size={10}/> Recorrente
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {isClickable && (
                                            <div className="bg-black/10 p-3 flex justify-between items-center text-white/90 text-xs font-bold uppercase tracking-wider group-hover:bg-black/20 transition-colors">
                                                <span>{hasResponded ? 'Enviar Nova Resposta' : 'Responder Agora'}</span>
                                                <ChevronRight size={16}/>
                                            </div>
                                        )}
                                        {!isClickable && hasResponded && (
                                            <div className={`p-3 text-center text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'bg-slate-900/50 text-slate-500' : 'bg-gray-200 text-gray-500'}`}>
                                                Último envio: {new Date(myResponses.sort((a,b)=>new Date(b.date)-new Date(a.date))[0].date).toLocaleDateString()}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}

                {/* MODAL DE CRIAÇÃO/EDIÇÃO (COM ABAS E TODAS AS FUNÇÕES DE PERGUNTAS) */}
                {isConfigModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                        <div className={`rounded-xl w-full max-w-5xl shadow-2xl flex flex-col max-h-[90vh] ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            
                            <div className="p-5 border-b flex justify-between items-center shrink-0">
                                <h3 className="font-bold text-lg flex items-center gap-2">
                                    <Settings size={20} className="text-blue-500"/> Configurar Pesquisa
                                </h3>
                                <button onClick={() => setIsConfigModalOpen(false)}><X size={20}/></button>
                            </div>

                            <div className={`flex border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                <button 
                                    onClick={() => setActiveConfigTab('general')} 
                                    className={`flex-1 py-3 text-sm font-bold border-b-2 flex items-center justify-center gap-2 ${activeConfigTab === 'general' ? (isDarkMode ? 'border-purple-400 text-purple-400' : 'border-purple-600 text-purple-600') : (isDarkMode ? 'border-transparent text-slate-400' : 'border-transparent text-gray-500')}`}
                                >
                                    <FileText size={16}/> Dados Gerais
                                </button>
                                <button 
                                    onClick={() => setActiveConfigTab('audience')} 
                                    className={`flex-1 py-3 text-sm font-bold border-b-2 flex items-center justify-center gap-2 ${activeConfigTab === 'audience' ? (isDarkMode ? 'border-purple-400 text-purple-400' : 'border-purple-600 text-purple-600') : (isDarkMode ? 'border-transparent text-slate-400' : 'border-transparent text-gray-500')}`}
                                >
                                    <Users size={16}/> Público Alvo
                                </button>
                                <button 
                                    onClick={() => setActiveConfigTab('structure')} 
                                    className={`flex-1 py-3 text-sm font-bold border-b-2 flex items-center justify-center gap-2 ${activeConfigTab === 'structure' ? (isDarkMode ? 'border-purple-400 text-purple-400' : 'border-purple-600 text-purple-600') : (isDarkMode ? 'border-transparent text-slate-400' : 'border-transparent text-gray-500')}`}
                                >
                                    <List size={16}/> Perguntas
                                </button>
                            </div>
                            
                            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar space-y-8">
                                
                                {/* ABA 1: DADOS GERAIS */}
                                {activeConfigTab === 'general' && (
                                    <section className="space-y-4 animate-fade-in">
                                        <div className="flex flex-col md:flex-row gap-4">
                                            <div className="w-full md:w-1/4">
                                                <label className="text-xs font-bold block mb-1">Cód. Reduzido</label>
                                                <Input 
                                                    value={surveyForm.shortCode || ''} 
                                                    onChange={e => setSurveyForm({...surveyForm, shortCode: e.target.value.toUpperCase().replace(/\s/g, '_')})} 
                                                    placeholder="EX: CLIMA_25" 
                                                    className="font-mono uppercase font-bold" 
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <label className="text-xs font-bold block mb-1">Título</label>
                                                <Input 
                                                    value={surveyForm.title} 
                                                    onChange={e => setSurveyForm({...surveyForm, title: e.target.value})} 
                                                    className="font-bold text-lg" 
                                                />
                                            </div>
                                        </div>
                                        <textarea 
                                            className={`w-full p-3 border rounded-lg text-sm ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`} 
                                            rows={2} 
                                            value={surveyForm.description} 
                                            onChange={e => setSurveyForm({...surveyForm, description: e.target.value})} 
                                            placeholder="Instruções..."
                                        ></textarea>
                                        
                                        <div className={`p-5 rounded-xl border mt-4 ${isDarkMode ? 'bg-slate-900/40 border-slate-700' : 'bg-slate-50 border-gray-200'}`}>
                                            <h4 className="text-xs font-bold uppercase mb-4 opacity-70">Regras e Prazos</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                <label className="flex justify-between p-3 rounded border cursor-pointer">
                                                    <span className="text-xs font-bold">Liberada?</span>
                                                    <input type="checkbox" checked={surveyForm.isActive} onChange={e => setSurveyForm({...surveyForm, isActive: e.target.checked})}/>
                                                </label>
                                                <label className="flex justify-between p-3 rounded border cursor-pointer">
                                                    <span className="text-xs font-bold">Anônima?</span>
                                                    <input type="checkbox" checked={surveyForm.isAnonymous} onChange={e => setSurveyForm({...surveyForm, isAnonymous: e.target.checked})}/>
                                                </label>
                                                <label className="flex justify-between p-3 rounded border cursor-pointer">
                                                    <span className="text-xs font-bold">Ocultar ao Enviar?</span>
                                                    <input type="checkbox" checked={surveyForm.hideAfterResponse} onChange={e => setSurveyForm({...surveyForm, hideAfterResponse: e.target.checked})}/>
                                                </label>
                                                <label className="flex justify-between p-3 rounded border cursor-pointer">
                                                    <span className="text-xs font-bold">Múltiplos Envios?</span>
                                                    <input type="checkbox" checked={surveyForm.allowMultiple} onChange={e => setSurveyForm({...surveyForm, allowMultiple: e.target.checked})}/>
                                                </label>
                                                <label className={`flex justify-between p-3 rounded border cursor-pointer border-l-4 ${surveyForm.collectsDept ? 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                                                    <div className="flex flex-col">
                                                        <span className="text-xs font-bold text-blue-700 dark:text-blue-300">Salvar Departamento?</span>
                                                        <span className="text-[9px] opacity-60">Identifica a área</span>
                                                    </div>
                                                    <input type="checkbox" checked={surveyForm.collectsDept} onChange={e => setSurveyForm({...surveyForm, collectsDept: e.target.checked})}/>
                                                </label>
                                                <label className={`flex justify-between p-3 rounded border cursor-pointer border-l-4 ${surveyForm.collectsPersonType ? 'border-l-purple-500 bg-purple-50 dark:bg-purple-900/20' : ''}`}>
                                                    <div className="flex flex-col">
                                                        <span className="text-xs font-bold text-purple-700 dark:text-purple-300">Salvar Tipo Pessoa?</span>
                                                        <span className="text-[9px] opacity-60">Gestão ou Colaborador</span>
                                                    </div>
                                                    <input type="checkbox" checked={surveyForm.collectsPersonType} onChange={e => setSurveyForm({...surveyForm, collectsPersonType: e.target.checked})}/>
                                                </label>
                                                
                                                <div className="flex items-center gap-2 p-3 rounded border">
                                                    <Clock size={16}/>
                                                    <span className="text-xs font-bold whitespace-nowrap">Data Limite:</span>
                                                    <input type="date" value={surveyForm.deadline} onChange={e => setSurveyForm({...surveyForm, deadline: e.target.value})} className={`bg-transparent outline-none text-xs font-bold w-full ${isDarkMode ? 'invert-calendar' : ''}`} />
                                                </div>
                                                
                                                <div className="md:col-span-2 lg:col-span-2 flex flex-col md:flex-row gap-4 items-center p-3 rounded border">
                                                    <div className="flex items-center gap-2">
                                                        <RefreshCw size={16}/>
                                                        <span className="text-xs font-bold">Periodicidade:</span>
                                                    </div>
                                                    <select 
                                                        className={`p-1.5 rounded text-xs border ${isDarkMode ? 'bg-slate-900 border-slate-600' : ''}`} 
                                                        value={surveyForm.recurrenceType} 
                                                        onChange={e => setSurveyForm({...surveyForm, recurrenceType: e.target.value})}
                                                    >
                                                        <option value="none">Não repete</option>
                                                        <option value="30">Mensal</option>
                                                        <option value="90">Trimestral</option>
                                                        <option value="180">Semestral</option>
                                                        <option value="custom">Personalizado</option>
                                                    </select>
                                                    {surveyForm.recurrenceType === 'custom' && (
                                                        <input 
                                                            type="number" 
                                                            className="w-16 p-1 text-xs border rounded" 
                                                            placeholder="Dias" 
                                                            value={surveyForm.customInterval} 
                                                            onChange={e => setSurveyForm({...surveyForm, customInterval: parseInt(e.target.value)||0})} 
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </div>                                
                                    </section>
                                )}

                                {/* ABA 2: PÚBLICO ALVO */}
                                {activeConfigTab === 'audience' && (
                                    <section className="space-y-6 animate-fade-in">
                                        <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 text-xs flex gap-2">
                                            <Sparkles size={16}/>
                                            <p>Defina quem poderá visualizar e responder esta pesquisa.</p>
                                        </div>
                                        
                                        <label className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer border-l-4 transition-all ${surveyForm.isSectorInternal ? 'bg-purple-50 border-purple-500 border-l-purple-600 dark:bg-purple-900/30' : 'border-gray-200 border-l-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800'}`}>
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded-full ${surveyForm.isSectorInternal ? 'bg-purple-200 text-purple-700' : 'bg-gray-200 text-gray-500'}`}>
                                                    <Building size={20}/>
                                                </div>
                                                <div>
                                                    <span className="text-sm font-bold block">Pesquisa Interna do Setor</span>
                                                    <span className="text-[10px] opacity-70">Restringe automaticamente ao departamento do criador ({data.departments.find(d => d.id === currentUser.departmentId)?.name || 'Seu Depto'}).</span>
                                                </div>
                                            </div>
                                            <input 
                                                type="checkbox" 
                                                className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500" 
                                                checked={surveyForm.isSectorInternal} 
                                                onChange={(e) => { 
                                                    const isChecked = e.target.checked; 
                                                    setSurveyForm(prev => ({ 
                                                        ...prev, 
                                                        isSectorInternal: isChecked, 
                                                        targetDepartments: isChecked ? [currentUser.departmentId] : [] 
                                                    })); 
                                                }} 
                                            />
                                        </label>

                                        <div className={surveyForm.isSectorInternal ? 'opacity-50 pointer-events-none grayscale' : ''}>
                                            <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                                                <Building size={16}/> Departamentos Permitidos 
                                                {surveyForm.isSectorInternal && <span className="text-xs text-purple-500 ml-2">(Bloqueado)</span>}
                                            </h4>
                                            <div className={`p-4 rounded-xl border max-h-48 overflow-y-auto custom-scrollbar grid grid-cols-2 md:grid-cols-3 gap-2 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
                                                <label className={`flex items-center gap-2 p-2 rounded cursor-pointer border ${surveyForm.targetDepartments.length === 0 ? 'bg-blue-100 border-blue-300 text-blue-800' : 'border-transparent hover:bg-gray-100 dark:hover:bg-slate-800'}`}>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={surveyForm.targetDepartments.length === 0} 
                                                        onChange={() => setSurveyForm(prev => ({ ...prev, targetDepartments: [] }))} 
                                                    />
                                                    <span className="text-xs font-bold">Todos os Departamentos</span>
                                                </label>
                                                {(data.departments || []).map(dept => (
                                                    <label key={dept.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer border ${surveyForm.targetDepartments.includes(dept.id) ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200' : 'border-transparent hover:bg-gray-50 dark:hover:bg-slate-800'}`}>
                                                        <input 
                                                            type="checkbox" 
                                                            checked={surveyForm.targetDepartments.includes(dept.id)} 
                                                            onChange={(e) => { 
                                                                const isChecked = e.target.checked; 
                                                                setSurveyForm(prev => { 
                                                                    const current = prev.targetDepartments || []; 
                                                                    if (isChecked) return { ...prev, targetDepartments: [...current, dept.id] }; 
                                                                    else return { ...prev, targetDepartments: current.filter(id => id !== dept.id) }; 
                                                                }); 
                                                            }} 
                                                        />
                                                        <span className="text-xs">{dept.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                                                <Users size={16}/> Tipo de Colaborador
                                            </h4>
                                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                {[ 
                                                    { id: 'all', label: 'Todos', desc: 'Qualquer função' }, 
                                                    { id: 'management', label: 'Gestão', desc: 'Gestores, RH, Dir.' }, 
                                                    { id: 'contributors', label: 'Colaboradores', desc: 'Apenas operacionais' }, 
                                                    { id: 'custom', label: 'Personalizado', desc: 'Selecionar nomes' } 
                                                ].map(opt => (
                                                    <div 
                                                        key={opt.id} 
                                                        className={`p-3 rounded-lg border cursor-pointer transition-all ${surveyForm.targetAudienceType === opt.id ? 'bg-purple-50 border-purple-500 ring-1 ring-purple-500 dark:bg-purple-900/30' : 'hover:bg-gray-50 dark:hover:bg-slate-800 border-gray-200 dark:border-slate-700'}`} 
                                                        onClick={() => setSurveyForm(prev => ({ ...prev, targetAudienceType: opt.id }))}
                                                    >
                                                        <div className="flex justify-between items-center mb-1">
                                                            <span className="text-xs font-bold">{opt.label}</span>
                                                            {surveyForm.targetAudienceType === opt.id && <CheckCircle size={14} className="text-purple-600"/>}
                                                        </div>
                                                        <p className="text-[10px] opacity-70">{opt.desc}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {surveyForm.targetAudienceType === 'custom' && (
                                            <div className="animate-fade-in border-t pt-4">
                                                <div className="flex justify-between items-center mb-3">
                                                    <h4 className="text-sm font-bold">Selecione os Colaboradores</h4>
                                                    <div className="relative w-64">
                                                        <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"/>
                                                        <input 
                                                            type="text" 
                                                            placeholder="Buscar nome..." 
                                                            value={contributorSearch} 
                                                            onChange={(e) => setContributorSearch(e.target.value)} 
                                                            className={`w-full pl-8 p-1.5 text-xs rounded border outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-300'}`} 
                                                        />
                                                    </div>
                                                </div>
                                                <div className={`p-4 rounded-xl border max-h-60 overflow-y-auto custom-scrollbar grid grid-cols-1 md:grid-cols-3 gap-2 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
                                                    {(data.collaborators || []).filter(u => { 
                                                        if (surveyForm.targetDepartments.length > 0 && !surveyForm.targetDepartments.includes(u.departmentId)) return false; 
                                                        if (contributorSearch && !u.name.toLowerCase().includes(contributorSearch.toLowerCase())) return false; 
                                                        return true; 
                                                    }).map(user => (
                                                        <label key={user.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer border ${surveyForm.targetUsers.includes(user.id) ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'border-transparent hover:bg-gray-50'}`}>
                                                            <input 
                                                                type="checkbox" 
                                                                checked={surveyForm.targetUsers.includes(user.id)} 
                                                                onChange={(e) => { 
                                                                    const isChecked = e.target.checked; 
                                                                    setSurveyForm(prev => { 
                                                                        const current = prev.targetUsers || []; 
                                                                        if (isChecked) return { ...prev, targetUsers: [...current, user.id] }; 
                                                                        else return { ...prev, targetUsers: current.filter(id => id !== user.id) }; 
                                                                    }); 
                                                                }} 
                                                            />
                                                            <div className="flex flex-col overflow-hidden">
                                                                <span className="text-xs font-bold truncate">{user.name}</span>
                                                                <span className="text-[9px] opacity-60 truncate">{data.departments.find(d=>d.id===user.departmentId)?.name}</span>
                                                            </div>
                                                        </label>
                                                    ))}
                                                    {(data.collaborators || []).length === 0 && <p className="col-span-3 text-center text-xs opacity-50 p-4">Nenhum colaborador encontrado.</p>}
                                                </div>
                                            </div>
                                        )}
                                    </section>
                                )}

                                {/* ABA 3: PERGUNTAS (ESTRUTURA COMPLETA COM NOVOS TIPOS) */}
                                {activeConfigTab === 'structure' && (
                                    <section className="animate-fade-in">
                                        <div className="flex justify-between items-center mb-6">
                                            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-purple-500">
                                                <List size={14}/> Estrutura do Formulário
                                            </div>
                                            <Button onClick={addSection} size="sm" icon={Plus} className="bg-purple-600 text-white">Novo Grupo</Button>
                                        </div>
                                        
                                        <div className="space-y-6">
                                            {(() => {
                                                let globalQCounter = 1;
                                                return (surveyForm.sections || []).map((section, sIdx) => (
                                                    <div key={section.id} className={`p-4 rounded-xl border ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                                                        <div className="flex justify-between items-center mb-4 border-b pb-2 gap-4 border-inherit">
                                                            <input 
                                                                type="text" 
                                                                className={`w-full font-bold bg-transparent outline-none ${isDarkMode ? 'text-white' : 'text-gray-800'}`} 
                                                                value={section.title} 
                                                                onChange={(e) => updateSectionTitle(section.id, e.target.value)} 
                                                                placeholder="Nome do Grupo" 
                                                            />
                                                            <button onClick={() => removeSection(section.id)} className="text-red-400 hover:text-red-600">
                                                                <Trash2 size={16}/>
                                                            </button>
                                                        </div>
                                                        
                                                        <div className="space-y-3">
                                                            {section.questions.map((q, qIdx) => {
                                                                // CONTEXTO BASE
                                                                if (q.type === 'context') {
                                                                    return (
                                                                        <div key={q.id} className={`p-5 rounded-xl border-2 border-dashed relative group mb-4 shadow-sm transition-colors ${isDarkMode ? 'bg-indigo-900/10 border-indigo-800 hover:border-indigo-600' : 'bg-indigo-50/50 border-indigo-300 hover:border-indigo-400'}`}>
                                                                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                                                                                <button onClick={() => duplicateQuestion(section.id, q)} className="text-blue-500 hover:bg-blue-100 p-1.5 rounded" title="Duplicar Bloco"><Copy size={14}/></button>
                                                                                <button onClick={() => removeQuestionFromSection(section.id, q.id)} className="text-red-500 hover:bg-red-100 p-1.5 rounded" title="Excluir Bloco"><Trash2 size={14}/></button>
                                                                            </div>
                                                                            <div className="flex gap-4">
                                                                                <div className="flex flex-col items-center gap-1 mt-1">
                                                                                    <span className="bg-indigo-200 text-indigo-800 w-8 h-8 rounded-full flex items-center justify-center font-black text-xs shadow-sm"><FileText size={16}/></span>
                                                                                </div>
                                                                                <div className="flex-1 space-y-3">
                                                                                    <div>
                                                                                        <label className="text-[10px] font-bold uppercase text-indigo-600 dark:text-indigo-400 mb-1 block">Rótulo / Orientação</label>
                                                                                        <Input 
                                                                                            value={q.referenceInfo || ''} 
                                                                                            onChange={e => updateQuestionInSection(section.id, q.id, 'referenceInfo', e.target.value)} 
                                                                                            placeholder="Ex: 'Utilize o texto e a imagem abaixo para responder às questões'" 
                                                                                            className={`font-bold text-sm ${isDarkMode ? 'bg-slate-900 border-slate-700 text-indigo-300' : 'text-indigo-800 border-indigo-200 bg-white'}`} 
                                                                                        />
                                                                                    </div>
                                                                                    <div>
                                                                                        <label className="text-[10px] font-bold uppercase text-gray-500 mb-1 block">Texto Base (Opcional se usar imagem)</label>
                                                                                        <textarea 
                                                                                            value={q.text || ''} 
                                                                                            onChange={e => updateQuestionInSection(section.id, q.id, 'text', e.target.value)} 
                                                                                            className={`w-full p-3 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500 resize-y min-h-[100px] shadow-inner custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-gray-300 text-gray-800'}`} 
                                                                                            placeholder="Cole o texto longo, artigo ou instruções aqui..." 
                                                                                        />
                                                                                    </div>
                                                                                    <div className={`p-3 rounded border ${isDarkMode ? 'bg-black/20 border-slate-700' : 'bg-white/50 border-gray-200'}`}>
                                                                                        <label className="text-[10px] font-bold uppercase text-gray-600 dark:text-gray-400 flex items-center gap-1 mb-2">
                                                                                            <Paperclip size={12}/> Imagem / Gráfico Auxiliar (Opcional)
                                                                                        </label>
                                                                                        <input 
                                                                                            type="file" 
                                                                                            accept="image/*" 
                                                                                            className="text-xs w-full" 
                                                                                            onChange={(e) => { 
                                                                                                const file = e.target.files[0]; 
                                                                                                if (file) { 
                                                                                                    const reader = new FileReader(); 
                                                                                                    reader.onload = () => updateQuestionInSection(section.id, q.id, 'imageUrl', reader.result); 
                                                                                                    reader.readAsDataURL(file); 
                                                                                                } 
                                                                                            }} 
                                                                                        />
                                                                                        {q.imageUrl && ( 
                                                                                            <div className="mt-3 relative inline-block"> 
                                                                                                <img src={q.imageUrl} alt="Contexto Base" className="max-h-40 rounded-lg border border-gray-300 shadow-md" /> 
                                                                                                <button onClick={() => updateQuestionInSection(section.id, q.id, 'imageUrl', null)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-lg hover:bg-red-600"><X size={14}/></button> 
                                                                                            </div> 
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                }

                                                                // PERGUNTA NORMAL
                                                                const currentQNum = globalQCounter++;

                                                                return (
                                                                    <div key={q.id} className={`p-3 rounded border relative group ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-gray-50'}`}>
                                                                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1 z-10">
                                                                            <button onClick={() => duplicateQuestion(section.id, q)} className="text-blue-500 hover:bg-blue-100 p-1.5 rounded" title="Duplicar"><Copy size={14}/></button>
                                                                            <button onClick={() => removeQuestionFromSection(section.id, q.id)} className="text-red-500 hover:bg-red-100 p-1.5 rounded" title="Excluir"><Trash2 size={14}/></button>
                                                                        </div>

                                                                        <div className="flex gap-3 mb-2 pr-16 items-start">
                                                                            <span className="bg-purple-100 text-purple-700 w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs shrink-0 mt-1">{currentQNum}</span>
                                                                            
                                                                            <div className="flex-1 space-y-2">
                                                                                <div className="flex gap-2 items-start">
                                                                                    <textarea 
                                                                                        value={q.text} 
                                                                                        onChange={e => updateQuestionInSection(section.id, q.id, 'text', e.target.value)} 
                                                                                        className={`flex-1 p-2 rounded border outline-none focus:ring-2 focus:ring-indigo-400 resize-y min-h-[40px] text-sm ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-gray-300'}`} 
                                                                                        placeholder="Enunciado da pergunta..."
                                                                                        rows={1}
                                                                                    />
                                                                                    <select 
                                                                                        className={`p-1.5 rounded border text-xs h-[40px] font-bold text-indigo-700 bg-indigo-50 outline-none cursor-pointer ${isDarkMode ? 'bg-indigo-900/30 border-indigo-800 text-indigo-300' : 'border-indigo-200'}`} 
                                                                                        value={q.type} 
                                                                                        onChange={e => {
                                                                                            const val = e.target.value;
                                                                                            updateQuestionInSection(section.id, q.id, 'type', val);
                                                                                            if (val === 'spreadsheet' && !q.tableConfig) {
                                                                                                updateQuestionInSection(section.id, q.id, 'tableConfig', {
                                                                                                    rows: 2, cols: 2,
                                                                                                    cells: { 
                                                                                                        '0-0': { type: 'label', value: 'Cabeçalho 1', isBold: true }, 
                                                                                                        '0-1': { type: 'label', value: 'Cabeçalho 2', isBold: true } 
                                                                                                    }
                                                                                                });
                                                                                            }
                                                                                            if (val === 'true_false' && (!q.options || q.options.length === 0)) {
                                                                                                updateQuestionInSection(section.id, q.id, 'options', ['Afirmação 1']);
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <option value="text">Texto / Discursiva</option>
                                                                                        <option value="multi">Múltipla Escolha</option>
                                                                                        <option value="dropdown">Lista Suspensa</option>
                                                                                        <option value="true_false">Verdadeiro ou Falso</option>
                                                                                        <option value="fill_blank">Completar Frase</option>
                                                                                        <option value="spreadsheet">Planilha / Tabela</option>
                                                                                        <option value="enps">eNPS (Escala 0 a 10)</option>
                                                                                    </select>
                                                                                </div>

                                                                                <div className="mb-2"> 
                                                                                    <label className="text-[10px] font-bold uppercase text-gray-500 flex items-center gap-1 mb-1"> 
                                                                                        <Paperclip size={12}/> Adicionar Imagem para a Questão (Opcional) 
                                                                                    </label> 
                                                                                    <input 
                                                                                        type="file" 
                                                                                        accept="image/*" 
                                                                                        className="text-xs w-full" 
                                                                                        onChange={(e) => { 
                                                                                            const file = e.target.files[0]; 
                                                                                            if (file) { 
                                                                                                const reader = new FileReader(); 
                                                                                                reader.onload = () => updateQuestionInSection(section.id, q.id, 'imageUrl', reader.result); 
                                                                                                reader.readAsDataURL(file); 
                                                                                            } 
                                                                                        }} 
                                                                                    /> 
                                                                                    {q.imageUrl && ( 
                                                                                        <div className="mt-2 relative inline-block"> 
                                                                                            <img src={q.imageUrl} alt="Imagem da Questão" className="max-h-32 rounded border border-gray-300 shadow-sm" /> 
                                                                                            <button onClick={() => updateQuestionInSection(section.id, q.id, 'imageUrl', null)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600"><X size={12}/></button> 
                                                                                        </div> 
                                                                                    )} 
                                                                                </div>

                                                                                {/* DICA: COMPLETAR FRASE */}
                                                                                {q.type === 'fill_blank' && (
                                                                                    <div className={`mt-2 mb-3 text-[10px] p-2 rounded border ${isDarkMode ? 'bg-orange-900/20 border-orange-800 text-orange-300' : 'bg-orange-50 border-orange-200 text-orange-700'}`}>
                                                                                        <b className="uppercase">Dica:</b> No enunciado da pergunta acima, use chaves <b>{`{ }`}</b> para criar os espaços em branco que o respondente deverá preencher. <br/>
                                                                                        Exemplo: <i>"Se não fosse contador eu gostaria de ser {`{profissão}`}, mas não poderia porque {`{motivo}`}."</i>
                                                                                    </div>
                                                                                )}

                                                                                {/* OPÇÃO DE PLANILHA EXPANDIDA */}
                                                                                {q.type === 'spreadsheet' && ( 
                                                                                    <div className={`mt-2 mb-3 p-4 rounded-xl border shadow-inner ${isDarkMode ? 'bg-slate-900/80 border-slate-700' : 'bg-gray-50 border-gray-200'}`}> 
                                                                                        <div className="mb-4"> 
                                                                                            <label className="text-[10px] font-bold uppercase text-gray-500 block mb-1">
                                                                                                Orientações de Preenchimento (Opcional)
                                                                                            </label> 
                                                                                            <textarea 
                                                                                                className={`w-full p-2 text-sm rounded-lg border outline-none focus:ring-1 focus:ring-indigo-500 resize-y min-h-[50px] ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`} 
                                                                                                placeholder="Ex: Preencha apenas os valores na coluna total..." 
                                                                                                value={q.instructions || ''} 
                                                                                                onChange={e => updateQuestionInSection(section.id, q.id, 'instructions', e.target.value)} 
                                                                                            /> 
                                                                                        </div> 
                                                                                        
                                                                                        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-300 dark:border-slate-600"> 
                                                                                            <span className={`text-[10px] font-bold uppercase mr-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                                                                Dimensões da Tabela:
                                                                                            </span> 
                                                                                            <button onClick={() => updateQuestionInSection(section.id, q.id, 'tableConfig', { ...q.tableConfig, rows: (q.tableConfig?.rows || 2) + 1 })} className="text-[10px] bg-indigo-600 text-white px-2 py-1.5 rounded font-bold hover:bg-indigo-700 transition-colors">+ Linha</button> 
                                                                                            <button onClick={() => updateQuestionInSection(section.id, q.id, 'tableConfig', { ...q.tableConfig, rows: Math.max(1, (q.tableConfig?.rows || 2) - 1) })} className="text-[10px] bg-red-600 text-white px-2 py-1.5 rounded font-bold hover:bg-red-700 transition-colors">- Linha</button> 
                                                                                            <span className="text-gray-300 mx-1">|</span> 
                                                                                            <button onClick={() => updateQuestionInSection(section.id, q.id, 'tableConfig', { ...q.tableConfig, cols: (q.tableConfig?.cols || 2) + 1 })} className="text-[10px] bg-indigo-600 text-white px-2 py-1.5 rounded font-bold hover:bg-indigo-700 transition-colors">+ Coluna</button> 
                                                                                            <button onClick={() => updateQuestionInSection(section.id, q.id, 'tableConfig', { ...q.tableConfig, cols: Math.max(1, (q.tableConfig?.cols || 2) - 1) })} className="text-[10px] bg-red-600 text-white px-2 py-1.5 rounded font-bold hover:bg-red-700 transition-colors">- Coluna</button> 
                                                                                        </div> 
                                                                                        
                                                                                        <div className="overflow-x-auto custom-scrollbar pb-2"> 
                                                                                            <table className="w-full border-collapse"> 
                                                                                                <tbody> 
                                                                                                    {Array.from({length: q.tableConfig?.rows || 2}).map((_, r) => ( 
                                                                                                        <tr key={r}> 
                                                                                                            {Array.from({length: q.tableConfig?.cols || 2}).map((_, c) => { 
                                                                                                                const cellId = `${r}-${c}`; 
                                                                                                                const cell = q.tableConfig?.cells?.[cellId] || { type: 'label', value: '', isBold: false }; 
                                                                                                                const updateCell = (field, val) => { 
                                                                                                                    const newCells = { ...(q.tableConfig?.cells || {}) }; 
                                                                                                                    newCells[cellId] = { ...cell, [field]: val }; 
                                                                                                                    updateQuestionInSection(section.id, q.id, 'tableConfig', { ...q.tableConfig, cells: newCells }); 
                                                                                                                }; 
                                                                                                                return ( 
                                                                                                                    <td key={c} className={`p-2 border min-w-[220px] ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}> 
                                                                                                                        <div className="flex flex-col gap-1.5"> 
                                                                                                                            <div className="flex justify-between items-start bg-black/5 dark:bg-white/5 p-1 rounded"> 
                                                                                                                                <div className="flex flex-col gap-1 w-full mr-2"> 
                                                                                                                                    <select className={`text-[9px] p-0.5 rounded border outline-none font-bold uppercase w-full ${cell.type === 'input' ? 'bg-orange-100 text-orange-800 border-orange-300' : 'bg-gray-100 text-gray-700 border-gray-300'}`} value={cell.type} onChange={e => updateCell('type', e.target.value)}> 
                                                                                                                                        <option value="label">Rótulo / Fixo</option> 
                                                                                                                                        <option value="input">Respondente Preenche</option> 
                                                                                                                                    </select> 
                                                                                                                                    {cell.type === 'input' && ( 
                                                                                                                                        <select className="text-[9px] p-0.5 rounded border outline-none font-bold uppercase bg-blue-50 text-blue-700 border-blue-200 w-full" value={cell.format || 'text'} onChange={e => updateCell('format', e.target.value)}> 
                                                                                                                                            <option value="text">Texto Livre</option> 
                                                                                                                                            <option value="number">Núm (1.000,00)</option> 
                                                                                                                                            <option value="time">Hora (00:00:00)</option> 
                                                                                                                                        </select> 
                                                                                                                                    )} 
                                                                                                                                </div> 
                                                                                                                                {cell.type === 'label' && ( 
                                                                                                                                    <label className="text-[9px] font-bold flex items-center gap-1 cursor-pointer text-gray-500 mt-1 shrink-0" title="Exibir em Negrito"> 
                                                                                                                                        <input type="checkbox" checked={cell.isBold} onChange={e => updateCell('isBold', e.target.checked)} className="rounded"/> B 
                                                                                                                                    </label> 
                                                                                                                                )} 
                                                                                                                            </div> 
                                                                                                                            {cell.type === 'label' && ( 
                                                                                                                                <input 
                                                                                                                                    type="text" 
                                                                                                                                    placeholder="Texto fixo exibido..." 
                                                                                                                                    value={cell.value || ''} 
                                                                                                                                    onChange={e => updateCell('value', e.target.value)} 
                                                                                                                                    className={`w-full p-2 text-xs rounded border outline-none focus:border-indigo-400 ${cell.isBold ? 'font-bold bg-white dark:bg-slate-800' : 'bg-white dark:bg-slate-900'} ${isDarkMode ? 'border-slate-600 text-white' : 'border-gray-200'}`} 
                                                                                                                                /> 
                                                                                                                            )} 
                                                                                                                        </div> 
                                                                                                                    </td> 
                                                                                                                ); 
                                                                                                            })} 
                                                                                                        </tr> 
                                                                                                    ))} 
                                                                                                </tbody> 
                                                                                            </table> 
                                                                                        </div> 
                                                                                    </div> 
                                                                                )}
                                                                                
                                                                                {/* Configurações Extra de Campo */}
                                                                                <div className="flex flex-wrap items-center gap-4 text-xs p-2 rounded bg-black/5 dark:bg-white/5 mb-2 mt-2">
                                                                                    <label className="flex items-center gap-2 cursor-pointer select-none font-bold text-red-500">
                                                                                        <input type="checkbox" className="rounded text-red-600 focus:ring-red-500" checked={q.required} onChange={e => updateQuestionInSection(section.id, q.id, 'required', e.target.checked)}/> Obrigatória?
                                                                                    </label>
                                                                                    
                                                                                    {(q.type === 'multi' || q.type === 'dropdown') && (
                                                                                        <>
                                                                                            <div className="w-px h-3 bg-gray-400"></div>
                                                                                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                                                                                <input type="checkbox" className="rounded text-purple-600 focus:ring-purple-500" checked={q.allowMultiple} onChange={e => updateQuestionInSection(section.id, q.id, 'allowMultiple', e.target.checked)}/> <span className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Múltiplas Respostas</span>
                                                                                            </label>
                                                                                            <div className="w-px h-3 bg-gray-400"></div>
                                                                                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                                                                                <input type="checkbox" className="rounded text-purple-600 focus:ring-purple-500" checked={q.hasOther} onChange={e => updateQuestionInSection(section.id, q.id, 'hasOther', e.target.checked)}/> <span className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Campo Livre (Outros)</span>
                                                                                            </label>
                                                                                        </>
                                                                                    )}
                                                                                </div>

                                                                                {/* Definição de Opções */}
                                                                                {(q.type === 'multi' || q.type === 'dropdown' || q.type === 'true_false') && (
                                                                                    <div className="space-y-2 mt-2 p-3 rounded-xl border border-dashed border-gray-300 dark:border-slate-600">
                                                                                        <span className="text-[10px] font-bold uppercase text-gray-500 block mb-2">Definição das Opções</span>
                                                                                        {(q.options||[]).map((opt, i) => (
                                                                                            <div key={i} className="flex items-start gap-2 text-xs">
                                                                                                <div className={`w-3 h-3 mt-2 ${q.allowMultiple ? 'rounded' : 'rounded-full'} border border-gray-400 bg-white shrink-0`}></div>
                                                                                                <textarea 
                                                                                                    className={`flex-1 p-2 rounded border outline-none resize-y min-h-[40px] custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-gray-200'}`} 
                                                                                                    value={opt} 
                                                                                                    onChange={e => updateOptionText(section.id, q.id, i, e.target.value)}
                                                                                                    placeholder={`Opção ${i + 1}`}
                                                                                                    rows={1}
                                                                                                />
                                                                                                <label className={`mt-1 flex items-center gap-1 text-[9px] px-2 py-1.5 rounded border cursor-pointer select-none transition-colors shrink-0 ${ (q.justifyTrigger || []).includes(opt) ? 'bg-orange-100 text-orange-700 border-orange-300 font-bold' : 'bg-gray-100 text-gray-500 hover:bg-gray-200 border-gray-200' }`}>
                                                                                                    <input type="checkbox" className="hidden" checked={(q.justifyTrigger || []).includes(opt)} onChange={() => toggleJustifyOption(section.id, q.id, opt)}/> 
                                                                                                    {(q.justifyTrigger||[]).includes(opt) ? <Check size={10}/> : null} Exigir Justificativa
                                                                                                </label>
                                                                                                <button onClick={() => removeOption(section.id, q.id, i)} className="mt-1 text-red-400 ml-1 hover:text-red-600 bg-red-50 p-1.5 rounded shrink-0" title="Excluir Opção"><X size={14}/></button>
                                                                                            </div>
                                                                                        ))}
                                                                                        <button onClick={() => addOptionToQuestion(section.id, q.id)} className="text-[10px] text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded font-bold hover:bg-indigo-100 transition-colors border border-indigo-200 mt-2">
                                                                                            + Adicionar Opção
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                            
                                                            {/* BOTOES DE INSERIR NO FIM DO GRUPO */} 
                                                            <div className={`flex justify-center gap-4 py-6 rounded-xl border-2 border-dashed mt-4 ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-300'}`}> 
                                                                <button onClick={() => addContextToSection(section.id)} className={`text-xs font-bold flex items-center gap-2 px-5 py-2.5 rounded-lg transition-all shadow-sm ${isDarkMode ? 'bg-indigo-900/40 text-indigo-400 hover:bg-indigo-800' : 'bg-indigo-100/50 text-indigo-600 hover:bg-indigo-100 hover:text-indigo-800'}`}> 
                                                                    <FileText size={16}/> Inserir Bloco Base (Texto/Imagem) 
                                                                </button> 
                                                                <button onClick={() => addQuestionToSection(section.id)} className={`text-xs font-bold flex items-center gap-2 px-5 py-2.5 rounded-lg transition-all shadow-sm ${isDarkMode ? 'bg-teal-900/40 text-teal-400 hover:bg-teal-800' : 'bg-teal-100/50 text-teal-600 hover:bg-teal-100 hover:text-teal-800'}`}> 
                                                                    <Plus size={16}/> Inserir Pergunta 
                                                                </button> 
                                                            </div> 
                                                        </div>
                                                    </div>
                                                ));
                                            })()}
                                        </div>
                                    </section>
                                )}

                            </div>
                            <div className="p-5 border-t flex justify-end gap-3 shrink-0">
                                <Button variant="secondary" onClick={() => setIsConfigModalOpen(false)}>Cancelar</Button>
                                <Button onClick={handleSaveSurvey} className="bg-green-600 text-white">Salvar e Liberar</Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL RESPONDER (VISUAL MODERNIZADO COM TODOS OS TIPOS DE QUESTÕES) */}
                {isAnsweringModalOpen && currentSurvey && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                        <div className={`rounded-xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh] ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            
                            <div className={`p-8 border-b shrink-0 bg-gradient-to-r ${isDarkMode ? 'from-slate-900 to-slate-800 border-slate-700' : 'from-indigo-600 to-purple-600 text-white'}`}>
                                <h3 className="font-bold text-3xl mb-2">{currentSurvey.title}</h3>
                                <p className="opacity-90 text-sm leading-relaxed max-w-2xl">{currentSurvey.description}</p>
                                {currentSurvey.isAnonymous && <span className="mt-4 inline-flex items-center gap-1 bg-white/20 text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wide backdrop-blur-md"><Lock size={12}/> Resposta Anônima</span>}
                            </div>

                            <div className={`flex-1 overflow-y-auto p-8 custom-scrollbar space-y-10 ${isDarkMode ? 'bg-slate-800' : 'bg-gray-50'}`}>
                                {(currentSurvey.sections||[]).map((section, sIdx) => {
                                    let qCounter = 1;
                                    return (
                                    <div key={section.id} className="animate-fade-in">
                                        <div className="flex items-center gap-3 mb-6">
                                            <span className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${isDarkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-700'}`}>{sIdx+1}</span>
                                            <h4 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{section.title}</h4>
                                        </div>
                                        
                                        <div className="space-y-6 pl-0 md:pl-11">
                                            {section.questions.map((q) => {
                                                
                                                // 1. RENDERIZAÇÃO DO CONTEXTO
                                                if (q.type === 'context') {
                                                    return (
                                                        <div key={q.id} className={`p-6 rounded-xl border-2 border-dashed shadow-md mb-8 mt-4 break-inside-avoid ${isDarkMode ? 'border-indigo-800 bg-indigo-950/30' : 'border-indigo-300 bg-indigo-50/50'}`}>
                                                            {q.referenceInfo && <h4 className={`font-bold uppercase tracking-wider mb-3 text-sm ${isDarkMode ? 'text-indigo-400' : 'text-indigo-700'}`}>{q.referenceInfo}</h4>}
                                                            {q.text && <div className={`whitespace-pre-wrap leading-relaxed text-base mb-4 ${isDarkMode ? 'text-slate-300' : 'text-gray-800'}`}>{q.text}</div>}
                                                            {q.imageUrl && <img src={q.imageUrl} alt="Contexto Base" className={`max-w-full rounded-lg shadow border mt-2 mx-auto ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`} />}
                                                        </div>
                                                    );
                                                }

                                                // 2. RENDERIZAÇÃO DA PERGUNTA NORMAL
                                                const currentQNum = qCounter++;
                                                const ansData = answersForm[q.id];
                                                const ansVal = ansData?.value !== undefined ? ansData.value : ansData;

                                                return (
                                                <div key={q.id} className={`p-6 rounded-xl border-l-4 shadow-sm transition-all hover:shadow-md ${isDarkMode ? 'bg-slate-900 border-slate-700 border-l-purple-500' : 'bg-white border-gray-200 border-l-purple-500'}`}>
                                                    
                                                    <div className="flex justify-between items-start mb-4">
                                                        <label className={`font-bold text-base flex gap-2 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                                            <span className="shrink-0">{currentQNum})</span>
                                                            {q.type !== 'fill_blank' ? (
                                                                <span>{q.text}</span>
                                                            ) : (
                                                                <div className="leading-relaxed">
                                                                    {(q.text || '').split(new RegExp("(\\{[^}]+\\})")).map((part, i) => {
                                                                        if (part.startsWith('{') && part.endsWith('}')) {
                                                                            const ph = part.slice(1, -1);
                                                                            return (
                                                                                <input 
                                                                                    key={i} 
                                                                                    type="text" 
                                                                                    placeholder={ph} 
                                                                                    className={`border-b-4 outline-none px-2 mx-1 text-center font-black min-w-[150px] transition-colors ${isDarkMode ? 'border-purple-500 bg-slate-800 text-purple-300 placeholder:text-slate-600 focus:bg-slate-700' : 'border-purple-500 bg-purple-100 text-purple-800 placeholder:text-purple-300 focus:bg-purple-200'}`} 
                                                                                    value={(ansVal || {})[i] || ''} 
                                                                                    onChange={e => setAnswersForm(prev => ({ ...prev, [q.id]: { ...prev[q.id], value: { ...(ansVal || {}), [i]: e.target.value } } }))} 
                                                                                />
                                                                            );
                                                                        }
                                                                        return <span key={i}>{part}</span>;
                                                                    })}
                                                                </div>
                                                            )}
                                                        </label>
                                                        {q.required && (
                                                            <span className="flex items-center gap-1 text-[10px] uppercase font-bold text-red-500 bg-red-50 px-2 py-1 rounded border border-red-100 ml-3 shrink-0">
                                                                <AlertCircle size={10}/> Obrigatória
                                                            </span>
                                                        )}
                                                    </div>

                                                    {q.imageUrl && (
                                                        <div className="mb-6 border-l-4 border-purple-400 pl-4 py-2">
                                                            <img src={q.imageUrl} alt="Ilustração da Questão" className={`max-w-full max-h-80 rounded-lg shadow-md border ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`} />
                                                        </div>
                                                    )}

                                                    {/* CORPO DA PERGUNTA: ENPS */}
                                                    {q.type === 'enps' ? (
                                                        <div className="flex flex-col items-center gap-4 mt-4 mb-2">
                                                            <div className="flex justify-between w-full max-w-2xl px-2 text-[10px] uppercase font-bold text-gray-400">
                                                                <span>Nada Provável (0)</span>
                                                                <span>Com Certeza (10)</span>
                                                            </div>
                                                            <div className="flex flex-wrap justify-center gap-2">
                                                                {[0,1,2,3,4,5,6,7,8,9,10].map(score => {
                                                                    const isSelected = ansVal === score;
                                                                    let colorClass = isDarkMode ? "bg-slate-800 text-red-400 border-slate-600 hover:bg-slate-700" : "bg-gray-50 text-red-600 border-gray-200 hover:bg-gray-100";
                                                                    if (score >= 7 && score <= 8) colorClass = isDarkMode ? "bg-slate-800 text-yellow-400 border-slate-600 hover:bg-slate-700" : "bg-gray-50 text-yellow-600 border-gray-200 hover:bg-gray-100";
                                                                    if (score >= 9) colorClass = isDarkMode ? "bg-slate-800 text-green-400 border-slate-600 hover:bg-slate-700" : "bg-gray-50 text-green-600 border-gray-200 hover:bg-gray-100";
                                                                    
                                                                    return (
                                                                        <button
                                                                            key={score}
                                                                            type="button"
                                                                            onClick={() => setAnswersForm(prev => ({ ...prev, [q.id]: { ...prev[q.id], value: score } }))}
                                                                            className={`w-10 h-10 md:w-12 md:h-12 rounded-lg border-2 font-black text-lg transition-all ${isSelected ? 'ring-2 ring-offset-2 ring-purple-500 scale-110 shadow-lg ' + colorClass.replace('bg-gray-50', '').replace('bg-slate-800', '') + ' bg-purple-600 !text-white !border-purple-600' : colorClass}`}
                                                                        >
                                                                            {score}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    ) : q.type === 'text' ? (
                                                        <textarea 
                                                            className={`w-full p-4 rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 transition-all resize-y min-h-[100px] ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-500' : 'bg-gray-50 border-gray-300 focus:bg-white'}`} 
                                                            placeholder="Digite sua resposta aqui..."
                                                            value={ansVal || ''} 
                                                            onChange={e => setAnswersForm(prev => ({ ...prev, [q.id]: { ...prev[q.id], value: e.target.value } }))}
                                                        ></textarea>
                                                    ) : q.type === 'true_false' ? (
                                                        <div className="grid grid-cols-1 gap-3 max-w-3xl">
                                                            {(q.options || []).map((opt, oIdx) => {
                                                                const currentArr = Array.isArray(ansVal) ? ansVal : [];
                                                                const optVal = currentArr[oIdx];
                                                                return (
                                                                    <div key={oIdx} className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl border-2 transition-all shadow-sm ${optVal ? (isDarkMode ? 'border-purple-700 bg-purple-900/20' : 'border-purple-400 bg-purple-50') : (isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-white')}`}>
                                                                        <span className={`text-base font-medium flex-1 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{opt}</span>
                                                                        <div className="flex gap-2 shrink-0 bg-black/5 dark:bg-white/5 p-1.5 rounded-lg">
                                                                            <label className={`px-5 py-2.5 rounded-md border-2 cursor-pointer font-bold text-sm transition-colors flex items-center justify-center gap-2 ${optVal === 'V' ? 'bg-green-600 text-white border-green-600 shadow-md' : (isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-600 hover:border-green-500 hover:text-green-400' : 'bg-white text-gray-600 border-gray-300 hover:border-green-500 hover:text-green-600')}`}>
                                                                                <input 
                                                                                    type="radio" 
                                                                                    className="hidden" 
                                                                                    checked={optVal === 'V'} 
                                                                                    onChange={() => {
                                                                                        const newVal = [...currentArr]; 
                                                                                        newVal[oIdx] = 'V';
                                                                                        setAnswersForm(prev => ({ ...prev, [q.id]: { ...prev[q.id], value: newVal } }));
                                                                                    }} 
                                                                                />
                                                                                {optVal === 'V' && <Check size={16}/>} Verdadeiro
                                                                            </label>
                                                                            <label className={`px-5 py-2.5 rounded-md border-2 cursor-pointer font-bold text-sm transition-colors flex items-center justify-center gap-2 ${optVal === 'F' ? 'bg-red-600 text-white border-red-600 shadow-md' : (isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-600 hover:border-red-500 hover:text-red-400' : 'bg-white text-gray-600 border-gray-300 hover:border-red-500 hover:text-red-600')}`}>
                                                                                <input 
                                                                                    type="radio" 
                                                                                    className="hidden" 
                                                                                    checked={optVal === 'F'} 
                                                                                    onChange={() => {
                                                                                        const newVal = [...currentArr]; 
                                                                                        newVal[oIdx] = 'F';
                                                                                        setAnswersForm(prev => ({ ...prev, [q.id]: { ...prev[q.id], value: newVal } }));
                                                                                    }} 
                                                                                />
                                                                                {optVal === 'F' && <X size={16}/>} Falso
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : q.type === 'spreadsheet' ? (
                                                        <div className={`overflow-x-auto custom-scrollbar border-2 rounded-2xl shadow-inner mt-4 ${isDarkMode ? 'bg-slate-950 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                            <table className="w-full border-collapse">
                                                                <tbody>
                                                                    {Array.from({length: q.tableConfig?.rows || 1}).map((_, r) => (
                                                                        <tr key={r}>
                                                                            {Array.from({length: q.tableConfig?.cols || 1}).map((_, c) => {
                                                                                const cellId = `${r}-${c}`;
                                                                                const cell = q.tableConfig?.cells?.[cellId] || { type: 'label', value: '' };
                                                                                return (
                                                                                    <td key={c} className={`p-4 border align-middle ${isDarkMode ? 'border-slate-700' : 'border-gray-200'} ${cell.type === 'label' ? (isDarkMode ? 'bg-slate-900' : 'bg-white') : 'bg-transparent'}`}>
                                                                                        {cell.type === 'label' ? (
                                                                                            <div className={`text-sm px-2 ${cell.isBold ? 'font-black uppercase tracking-wider' : 'font-medium'} ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                                                                                {cell.value}
                                                                                            </div>
                                                                                        ) : (
                                                                                            (() => {
                                                                                                const cellValue = ansVal?.[cellId] || '';
                                                                                                const isEmpty = cellValue.trim() === '';
                                                                                                const formatType = cell.format || 'text';
                                                                                                const placeholderText = formatType === 'number' ? '0,00' : formatType === 'time' ? '00:00' : '____';
                                                                                                const bgClasses = isEmpty 
                                                                                                    ? (isDarkMode ? 'bg-yellow-900/20 border-yellow-700/50 placeholder-yellow-700/50' : 'bg-yellow-50 border-yellow-400 placeholder-yellow-600/50') 
                                                                                                    : (isDarkMode ? 'bg-slate-800 border-purple-500 text-white shadow-[0_0_10px_rgba(168,85,247,0.2)]' : 'bg-white border-purple-400 text-purple-900 shadow-[0_0_10px_rgba(168,85,247,0.2)]');

                                                                                                return (
                                                                                                    <input 
                                                                                                        type="text" 
                                                                                                        className={`w-full min-w-[140px] p-3 rounded-lg border-2 outline-none focus:ring-4 focus:ring-purple-500/30 text-center font-bold transition-all duration-300 ${bgClasses}`}
                                                                                                        value={cellValue}
                                                                                                        onChange={e => {
                                                                                                            const newVal = { ...(ansVal || {}) };
                                                                                                            newVal[cellId] = e.target.value;
                                                                                                            setAnswersForm(prev => ({ ...prev, [q.id]: { ...prev[q.id], value: newVal } }));
                                                                                                        }} 
                                                                                                        placeholder={placeholderText} 
                                                                                                    />
                                                                                                );
                                                                                            })()
                                                                                        )}
                                                                                    </td>
                                                                                );
                                                                            })}
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    ) : (
                                                        <div className="space-y-3">
                                                                {q.type === 'dropdown' ? (
                                                                    <div className="max-w-md">
                                                                        <div className="relative">
                                                                            <select 
                                                                                className={`w-full p-4 pr-10 rounded-xl border-2 appearance-none outline-none focus:ring-2 focus:ring-purple-500 font-medium transition-colors ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                                                                                value={ansVal || ''}
                                                                                onChange={(e) => setAnswersForm(prev => ({ ...prev, [q.id]: { ...prev[q.id], value: e.target.value } }))}
                                                                            >
                                                                                <option value="">Selecione uma opção...</option>
                                                                                {(q.options||[]).map((opt, oIdx) => <option key={oIdx} value={opt}>{opt}</option>)}
                                                                            </select>
                                                                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 opacity-50 pointer-events-none" size={20}/>
                                                                        </div>
                                                                        
                                                                        {/* JUSTIFICATIVA CONDICIONAL PARA DROPDOWN */}
                                                                        {ansVal && (q.justifyTrigger || []).includes(ansVal) && (
                                                                            <div className="mt-3 ml-2 animate-fade-in">
                                                                                <label className="text-[10px] uppercase font-bold text-orange-600 mb-1 block">Por favor, justifique:</label>
                                                                                <input 
                                                                                    type="text" 
                                                                                    className={`w-full p-2 text-sm rounded border focus:ring-1 focus:ring-orange-500 outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-orange-200'}`} 
                                                                                    placeholder="Detalhe o motivo..." 
                                                                                    value={ansData?.justification || ''} 
                                                                                    onChange={e => setAnswersForm(prev => ({ ...prev, [q.id]: { ...prev[q.id], justification: e.target.value } }))}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                (q.options||[]).map(opt => {
                                                                    const isChecked = q.allowMultiple ? (ansVal || []).includes(opt) : ansVal === opt;
                                                                    return (
                                                                        <div key={opt} className={`group p-3 rounded-lg border cursor-pointer transition-all ${isChecked ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-gray-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-800'}`}>
                                                                            <label className="flex items-center gap-3 cursor-pointer w-full">
                                                                                <div className={`w-5 h-5 rounded flex items-center justify-center border transition-colors ${isChecked ? 'bg-purple-600 border-purple-600' : 'border-gray-400 bg-white'}`}>
                                                                                    {isChecked && <CheckCircle size={12} className="text-white"/>}
                                                                                </div>
                                                                                <input 
                                                                                    type={q.allowMultiple ? "checkbox" : "radio"} 
                                                                                    name={`q-${q.id}`} 
                                                                                    className="hidden"
                                                                                    checked={isChecked} 
                                                                                    onChange={() => { 
                                                                                        let val;
                                                                                        if (q.allowMultiple) {
                                                                                            val = isChecked ? (ansVal||[]).filter(x=>x!==opt) : [...(ansVal||[]), opt];
                                                                                        } else {
                                                                                            val = opt;
                                                                                        }
                                                                                        setAnswersForm(prev => ({ ...prev, [q.id]: { ...prev[q.id], value: val } })); 
                                                                                    }} 
                                                                                />
                                                                                <span className={`text-sm font-medium ${isChecked ? 'text-purple-900 dark:text-purple-200' : 'text-gray-700 dark:text-gray-300'}`}>{opt}</span>
                                                                            </label>
                                                                            {isChecked && (q.justifyTrigger||[]).includes(opt) && (
                                                                                <div className="mt-3 ml-8 animate-fade-in">
                                                                                    <label className="text-[10px] uppercase font-bold text-orange-600 mb-1 block">Por favor, justifique:</label>
                                                                                    <input 
                                                                                        type="text" 
                                                                                        className={`w-full p-2 text-sm rounded border focus:ring-1 focus:ring-orange-500 outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-orange-200'}`} 
                                                                                        placeholder="Detalhe o motivo..." 
                                                                                        value={ansData?.justification || ''} 
                                                                                        onChange={e => setAnswersForm(prev => ({ ...prev, [q.id]: { ...prev[q.id], justification: e.target.value } }))}
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })
                                                            )}

                                                            {q.hasOther && (
                                                                <div className={`group p-3 rounded-lg border cursor-pointer transition-all ${ansData?.isOtherChecked ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-gray-200 dark:border-slate-700 hover:border-purple-300'}`}>
                                                                    <label className="flex items-center gap-3 cursor-pointer w-full">
                                                                        <div className={`w-5 h-5 rounded flex items-center justify-center border transition-colors ${ansData?.isOtherChecked ? 'bg-purple-600 border-purple-600' : 'border-gray-400 bg-white'}`}>
                                                                            {ansData?.isOtherChecked && <CheckCircle size={12} className="text-white"/>}
                                                                        </div>
                                                                        <input 
                                                                            type="checkbox" 
                                                                            className="hidden"
                                                                            checked={!!ansData?.isOtherChecked}
                                                                            onChange={(e) => {
                                                                                const checked = e.target.checked;
                                                                                setAnswersForm(prev => ({ 
                                                                                    ...prev, 
                                                                                    [q.id]: { 
                                                                                        ...prev[q.id], 
                                                                                        isOtherChecked: checked, 
                                                                                        otherText: checked ? (prev[q.id]?.otherText || '') : '' 
                                                                                    } 
                                                                                }));
                                                                            }}
                                                                        />
                                                                        <span className={`text-sm font-medium ${ansData?.isOtherChecked ? 'text-purple-900 dark:text-purple-200' : 'text-gray-700 dark:text-gray-300'}`}>Campo Livre</span>
                                                                    </label>
                                                                    {ansData?.isOtherChecked && (
                                                                        <div className="mt-3 ml-8 animate-fade-in">
                                                                            <label className="text-[10px] uppercase font-bold text-purple-600 mb-1 block">Digite sua resposta:</label>
                                                                            <input 
                                                                                type="text" 
                                                                                autoFocus
                                                                                className={`w-full p-2 text-sm rounded border focus:ring-1 focus:ring-purple-500 outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-purple-200'}`} 
                                                                                placeholder="Digite aqui..." 
                                                                                value={ansData?.otherText || ''} 
                                                                                onChange={e => setAnswersForm(prev => ({ ...prev, [q.id]: { ...prev[q.id], otherText: e.target.value } }))}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>

                            <div className={`p-6 border-t flex justify-end gap-3 shrink-0 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                <Button variant="secondary" onClick={() => setIsAnsweringModalOpen(false)}>Cancelar</Button>
                                <Button onClick={handleSubmitResponse} className="bg-purple-600 hover:bg-purple-700 text-white px-8 shadow-lg font-bold">Enviar Respostas</Button>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* MODAL DETALHES DA RESPOSTA (ATUALIZADO COM TODOS OS FORMATOS DE PERGUNTA) */}
                {surveyViewModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setSurveyViewModal(null)}>
                        <div className={`p-6 rounded-xl w-full max-w-2xl shadow-2xl overflow-y-auto max-h-[90vh] ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            
                            <div className={`flex justify-between items-center mb-6 border-b pb-4 ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                <div>
                                    <h3 className="font-bold text-xl">Detalhes da Resposta</h3>
                                    <div className="flex flex-col text-xs mt-1 opacity-70">
                                        <span><b>Participante:</b> {surveyViewModal.userEmail || 'Anônimo'}</span>
                                        <span><b>Data:</b> {new Date(surveyViewModal.timestamp).toLocaleString('pt-BR')}</span>
                                    </div>
                                </div>
                                <button onClick={() => setSurveyViewModal(null)} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors"><X size={24}/></button>
                            </div>

                            <div className="space-y-6">
                                {(() => {
                                    const surveyDef = (data.surveys || []).find(s => s.id === surveyViewModal.surveyId);
                                    if (!surveyDef) return <p className="text-center italic opacity-50">Estrutura da pesquisa original não encontrada.</p>;

                                    return (surveyDef.sections || []).map((section) => (
                                        <div key={section.id} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                            <h4 className={`font-bold text-sm uppercase mb-4 pb-2 border-b ${isDarkMode ? 'text-purple-400 border-slate-700' : 'text-purple-700 border-gray-200'}`}>
                                                {section.title}
                                            </h4>
                                            
                                            <div className="space-y-5">
                                                {section.questions.map((q) => {
                                                    if (q.type === 'context') return null; // Pula os blocos de leitura
                                                    
                                                    const answer = surveyViewModal.answers?.[q.id];
                                                    let displayValue = <span className="text-gray-400 italic text-xs">Não respondido</span>;
                                                    
                                                    if (answer) {
                                                        const rawVal = answer.value;
                                                        const otherText = answer.otherText ? ` (Outros: ${answer.otherText})` : '';
                                                        const justify = answer.justification ? `\nJustificativa: ${answer.justification}` : '';
                                                        
                                                        let textContent = '';
                                                        
                                                        if (q.type === 'true_false' && Array.isArray(q.options)) {
                                                            const userArr = Array.isArray(rawVal) ? rawVal : [];
                                                            textContent = q.options.map((opt, i) => `[${userArr[i] || '-'}] ${opt}`).join('\n');
                                                        } else if (q.type === 'spreadsheet') {
                                                            const cells = q.tableConfig?.cells || {};
                                                            let userArr = [];
                                                            const sheetData = typeof rawVal === 'object' && rawVal !== null ? rawVal : {};
                                                            
                                                            const sortedKeys = Object.keys(cells).sort((a, b) => {
                                                                const [rA, cA] = a.split('-').map(Number);
                                                                const [rB, cB] = b.split('-').map(Number);
                                                                if (rA !== rB) return rA - rB; return cA - cB;
                                                            });

                                                            sortedKeys.forEach(cellId => {
                                                                if (cells[cellId].type === 'input') {
                                                                    const parts = cellId.split('-');
                                                                    const cellName = `L${parseInt(parts[0])+1}, C${parseInt(parts[1])+1}`;
                                                                    userArr.push(`[${cellName}] ➜ Preenchido: ${sheetData[cellId] || '-'}`);
                                                                }
                                                            });
                                                            textContent = userArr.join('\n');
                                                        } else if (Array.isArray(rawVal)) {
                                                            textContent = rawVal.join(', ') + otherText;
                                                        } else {
                                                            textContent = (rawVal || '') + otherText;
                                                        }

                                                        if (textContent || justify) {
                                                            displayValue = (
                                                                <div className={`text-sm mt-1 p-2 rounded whitespace-pre-wrap font-medium ${isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'}`}>
                                                                    <span className={isDarkMode ? 'text-slate-200' : 'text-gray-800'}>{textContent || '-'}</span>
                                                                    {justify && (
                                                                        <div className={`mt-2 text-xs border-t pt-2 ${isDarkMode ? 'border-slate-700 text-orange-300' : 'border-gray-200 text-orange-600'}`}>
                                                                            <span className="font-bold">Nota/Justificativa:</span> {answer.justification}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        }
                                                    }

                                                    return (
                                                        <div key={q.id}>
                                                            <p className={`text-xs font-bold mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                                                                {q.text}
                                                            </p>
                                                            {displayValue}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ));
                                })()}
                            </div>
                            
                            <div className={`mt-6 pt-4 border-t flex justify-end ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                <Button onClick={() => setSurveyViewModal(null)}>Fechar</Button>
                            </div>
                        </div>
                    </div>
                )}
                        
            </div>
        );
    };

    /**
     * ============================================================================
     * COMPONENTE: RH SYSTEM (ATUALIZADO - CORES E FUNCIONALIDADES)
     * ============================================================================
     */


    /* ===============================================================================
     * [END_MOD_SURVEYS_SYSTEM]
     * FIM DO BLOCO: SurveysSystem
     * [START_MOD_RH_SYSTEM]
     * INICIO DO BLOCO: RHSystem
     * =============================================================================== */
    const RHSystem = ({ data, actions, currentUser, isDarkMode, initialTab }) => {
        const activeTab = initialTab || 'talents'; 
        const [subTab, setSubTab] = useState('resumes'); 
        const [validationAlert, setValidationAlert] = useState(null);
        
        // --- ESTADOS DE UI: AVALIAÇÕES (RH) ---
        const [expandedTestsRH, setExpandedTestsRH] = useState({});
        const [expandedSectionsRH, setExpandedSectionsRH] = useState({});
        const [isReprocessing, setIsReprocessing] = useState(false);

        const SECTION_GRADIENTS = [
            'from-blue-600 to-cyan-500', 'from-purple-600 to-fuchsia-500', 'from-emerald-500 to-teal-400',
            'from-orange-500 to-amber-400', 'from-pink-600 to-rose-400', 'from-indigo-600 to-blue-500',
            'from-red-600 to-orange-500', 'from-teal-600 to-emerald-400'
        ];

        // --- FUNÇÃO: REPROCESSAR NOTA ---
        const handleReprocessTest = async (resume, attemptIndex, originalTest) => {
            if (!window.confirm("Isso fará com que a Inteligência Artificial e o Motor Matemático reavaliem TODAS as respostas desta tentativa com as regras mais recentes. Deseja continuar?")) return;
            
            setIsReprocessing(true);
            try {
                const tResult = resume.testsTaken[attemptIndex];
                let totalScore = 0;
                let finalTotalPossible = 0;
                let newDetails = {};

                for (const sec of originalTest.sections) {
                    for (const q of sec.questions) {
                        // --- INÍCIO DO BLOCO SUBSTITUÍDO (DENTRO DO LOOP DE QUESTÕES) ---
                    if (q.type === 'context') continue;

                    // 1. Definição do Objeto de Resposta
                    const answerObj = (answeringTest && answeringTest.answers) 
                        ? answeringTest.answers[q.id] 
                        : (tResult?.details?.[q.id] || {});

                    const rawUserAnswer = answerObj?.value !== undefined ? answerObj.value : (answerObj?.userAnswer !== undefined ? answerObj.userAnswer : answerObj);
                    const userAnswer = rawUserAnswer;
                    const maxPts = parseFloat(q.points !== undefined ? q.points : 10) || 0;
                    
                    let qScore = 0;
                    let aiFeedback = null;
                    
                    // Verifica se está vazio
                    let isEmpty = false;
                    if (userAnswer === undefined || userAnswer === null || userAnswer === '') isEmpty = true;
                    if (Array.isArray(userAnswer) && userAnswer.length === 0) isEmpty = true;
                    if (typeof userAnswer === 'object' && userAnswer !== null && !Array.isArray(userAnswer) && q.type !== 'file') {
                    if (Object.keys(userAnswer).length === 0) isEmpty = true;
                    else if (Object.values(userAnswer).every(v => v === null || v === '' || String(v).trim() === '')) isEmpty = true;
                    }
                    const isOtherChecked = answerObj?.isOtherChecked || false;

                    if (isEmpty && !isOtherChecked) {
                        qScore = 0;
                        aiFeedback = "Questão não respondida.";
                    }
                    
                    // ====================================================
                    // GRUPO 1: CORREÇÃO EXATA (SEM GASTO DE IA)
                    // ====================================================

                    // A) Múltipla Escolha e Dropdown
                    else if (q.type === 'multi' || q.type === 'dropdown') {
                        if (q.allowMultiple) {
                            const expected = Array.isArray(q.correctAnswer) ? q.correctAnswer : [];
                            const userArr = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
                            if (expected.length > 0) {
                                let correctHits = 0;
                                userArr.forEach(ans => { if (expected.includes(ans)) correctHits++; });
                                // Cálculo proporcional simples (sem penalidade de erro para não negativar)
                                qScore = (correctHits / expected.length) * maxPts;
                                aiFeedback = `Seleção Múltipla: ${correctHits} de ${expected.length} opções corretas encontradas.`;
                            } else {
                                qScore = maxPts; // Sem gabarito = 100%
                            }
                        } else {
                            const valUser = String(userAnswer).trim();
                            const valGab = String(q.correctOption || q.correctAnswer || '').trim();
                            if (valUser === valGab) {
                                qScore = maxPts;
                                aiFeedback = "Resposta correta.";
                            } else {
                                qScore = 0;
                                aiFeedback = "Resposta incorreta.";
                            }
                        }
                    }

                    // B) Verdadeiro ou Falso
                    else if (q.type === 'true_false') {
                        const expected = Array.isArray(q.correctAnswer) ? q.correctAnswer : [];
                        const userArr = Array.isArray(userAnswer) ? userAnswer : [];
                        if (expected.length > 0) {
                            let hits = 0;
                            expected.forEach((gabItem, idx) => { if (userArr[idx] === gabItem) hits++; });
                            qScore = (hits / expected.length) * maxPts;
                            aiFeedback = `V/F: ${hits} acertos de ${expected.length} itens.`;
                        } else {
                            qScore = 0;
                            aiFeedback = "Gabarito não configurado.";
                        }
                    }

                    // C) Arquivo (Anexo)
                    else if (q.type === 'file') {
                        qScore = maxPts;
                        aiFeedback = "Arquivo anexado (Correção manual recomendada).";
                    }

                    // ====================================================
                    // GRUPO 2: CORREÇÃO VIA IA (Discursiva, Lacunas, Planilha Texto)
                    // ====================================================
                    
                    else {
                        // Prepara os dados para o Prompt
                        let promptContext = "";
                        let isSpreadsheet = q.type === 'spreadsheet';
                        let isFillBlank = q.type === 'fill_blank';
                        
                        // Tratamento especial para Planilha (Texto vs Número)
                        if (isSpreadsheet) {
                            // Se a planilha só tem números, corrige localmente para economizar IA
                            const cells = q.tableConfig?.cells || {};
                            const hasTextCell = Object.values(cells).some(c => c.type === 'input' && c.format !== 'number' && c.format !== 'time');
                            
                            if (!hasTextCell) {
                                // CORREÇÃO LOCAL (NUMÉRICA)
                                let totalInputs = 0, correctInputs = 0;
                                Object.keys(cells).forEach(cellId => {
                                    const cell = cells[cellId];
                                    if (cell.type === 'input') {
                                        totalInputs++;
                                        const uVal = userAnswer ? String(userAnswer[cellId] || '').replace(',','.').trim() : '';
                                        const gVal = String(cell.expected || '').replace(',','.').trim();
                                        if(parseFloat(uVal) === parseFloat(gVal)) correctInputs++;
                                    }
                                });
                                qScore = totalInputs > 0 ? (correctInputs / totalInputs) * maxPts : maxPts;
                                aiFeedback = `Planilha Numérica: ${correctInputs} de ${totalInputs} acertos exatos.`;
                                // Pula a chamada de IA abaixo
                                promptContext = null; 
                            } else {
                                // PLANILHA DE TEXTO -> VAI PARA IA
                                const userSheet = typeof userAnswer === 'object' ? JSON.stringify(userAnswer) : String(userAnswer);
                                const gabSheet = JSON.stringify(cells);
                                promptContext = `TIPO: Planilha de Texto.\nCONFIGURAÇÃO E GABARITO (JSON): ${gabSheet}\nRESPOSTAS DO CANDIDATO (JSON): ${userSheet}\nINSTRUÇÃO: Compare cada célula preenchida com o esperado ('expected'). Considere sinônimos e contexto.`;
                            }
                        }
                        
                        // Tratamento para Complete as Frases
                        else if (isFillBlank) {
                            const gabaritoObj = (typeof q.correctAnswer === 'object') ? JSON.stringify(q.correctAnswer) : q.correctAnswer;
                            const userObj = (typeof userAnswer === 'object') ? JSON.stringify(userAnswer) : userAnswer;
                            promptContext = `TIPO: Completar Lacunas.\nFRASE: "${q.text}"\nGABARITO (Lista de lacunas): ${gabaritoObj}\nRESPOSTAS DO CANDIDATO: ${userObj}\nINSTRUÇÃO: Avalie se as palavras preenchidas correspondem ao gabarito. Aceite variações gramaticais leves.`;
                        }
                        
                        // Tratamento para Texto Livre
                        else {
                            const expectedGabarito = String(q.correctAnswer || 'Livre').trim();
                            promptContext = `TIPO: Discursiva.\nPERGUNTA: "${q.text}"\nGABARITO ESPERADO: "${expectedGabarito}"\nRESPOSTA: "${userAnswer}"`;
                        }

                                                        if (promptContext) {
                                                        try {
                                                            // DELAY OBRIGATÓRIO (Rate Limit)
                                                            await new Promise(r => setTimeout(r, 4000));

                                                            // --- CORREÇÃO: USANDO A CONSTANTE GLOBAL DE PROMPT ---
                                                            const aiRes = await callGeminiAI(promptContext, SYSTEM_CORRECTION_PROMPT, 'evaluation');
                                                            
                                                            let cleanRes = String(aiRes).replace(/```json/gi, '').replace(/```/g, '').trim();
                                                            const jsonMatch = cleanRes.match(/\{[\s\S]*\}/);
                                                            
                                                            if (jsonMatch) {
                                                                const parsed = JSON.parse(jsonMatch[0]);
                                                                // Garante que o percentual seja um número entre 0 e 1
                                                                const percent = Math.max(0, Math.min(1, parseFloat(parsed.percentual) || 0));
                                                                qScore = maxPts * percent;
                                                                aiFeedback = `[IA] ${parsed.analise}`;
                                                            } else {
                                                                throw new Error("Retorno IA sem JSON válido");
                                                            }
                                                        } catch (err) {
                                                            console.error("Erro IA Preview:", err);
                                                            qScore = 0;
                                                            aiFeedback = "⚠️ Falha na validação automática (IA). Gestor deve validar.";
                                                        }
                                                    }
                        
                    }

                    totalScore += qScore;
                    
                    // Preserva justificativas anteriores se existirem
                    const previousDetail = tResult.details?.[q.id] || {};
                    newDetails[q.id] = { 
                        userAnswer, 
                        justifications: previousDetail.justifications || [], 
                        score: parseFloat(qScore.toFixed(2)), 
                        maxPoints: maxPts, 
                        aiFeedback 
                    };

                    // --- FIM DO BLOCO SUBSTITUÍDO ---
                    }
                }

                const updatedTestsTaken = [...resume.testsTaken];
                updatedTestsTaken[attemptIndex] = {
                    ...tResult,
                    score: parseFloat(totalScore.toFixed(2)),
                    details: newDetails
                };

                const updatedResume = { ...resume, testScore: updatedTestsTaken[updatedTestsTaken.length - 1].score, testsTaken: updatedTestsTaken };
                await actions.saveItem('resumes', updatedResume);
                setViewingResumeTests(updatedResume);
                setValidationAlert({ title: "Sucesso!", message: `Reprocessamento concluído.\nNova Nota: ${totalScore.toFixed(2)} pts.` });

            } catch (e) {
                setValidationAlert({ title: "Erro", message: "Erro ao reprocessar: " + e.message });
            } finally {
                setIsReprocessing(false);
            }
        };

        // --- NOVOS ESTADOS (ANOTAÇÕES E IA) ---
        const [resumeNotesModal, setResumeNotesModal] = useState({ isOpen: false, resume: null, newNote: '' });
        const [generatingSummaryId, setGeneratingSummaryId] = useState(null); // Controla qual currículo está gerando

        const STATUS_OPTIONS = [
            "Recebido", "Em análise", "Aguardando retorno", "Não retornou", 
            "Não quer participar", "Não apareceu", "Concluir teste", "Entrevista", 
            "Reprovado ou Proposta", "Retorno do Candidato", "Contratado", "Arquivado"
        ];
        const SITUACAO_OPTIONS = ["Pendente", "Não tem perfil Acad", "Perfil Acad"];

        // --- ESTADOS: GESTÃO DE PESQUISA ---
        const [surveyFilter, setSurveyFilter] = useState(''); 

        // --- LÓGICA PARA FINALIZAR TESTES EXPIRADOS (CANDIDATO DESCONECTADO) ---
        useEffect(() => {
            const autoFinalizeExpired = async () => {
                const today = new Date();
                (data.resumes || []).forEach(async (resume) => {
                    // Verifica se iniciou um teste mas não concluiu
                    if (resume.draftStartTime && (!resume.testsTaken || resume.testsTaken.length === 0)) {
                        const start = new Date(resume.draftStartTime);
                        // Busca a duração definida no teste original
                        const testDef = (data.recruitmentTests || []).find(t => resume.drafts && resume.drafts[t.id]);
                        const duration = testDef?.durationMinutes || 60; // Padrão 60 min se não achar
                        
                        const limit = new Date(start.getTime() + (duration * 60000));
                        
                        // Se o tempo acabou e ele não enviou, o RH limpa o rascunho ou marca como feito
                        if (today > limit) {
                            console.log("Sistema: Tempo esgotado para o candidato", resume.name);
                            // Aqui você pode adicionar uma lógica para mover o rascunho para 'testsTaken' 
                            // com nota zero ou com o que foi salvo até então.
                        }
                    }
                });
            };
            autoFinalizeExpired();
        }, [data.resumes, data.recruitmentTests]);
        
        const [dateRange, setDateRange] = useState('30'); 
        const [customDates, setCustomDates] = useState({ start: '', end: '' });
        const [surveySearch, setSurveySearch] = useState('');
        const [surveyViewModal, setSurveyViewModal] = useState(null);
        const [responseFilterDept, setResponseFilterDept] = useState(''); 
        const [responseFilterType, setResponseFilterType] = useState('');
        const [responseFilterDeadline, setResponseFilterDeadline] = useState('all');
        
        const [followUpModal, setFollowUpModal] = useState({
            isOpen: false,
            responseId: null,
            deadline: '',
            reminders: [],
            completed: false
        });

        // --- ESTADOS: GESTÃO DE TALENTOS ---
        const resumesList = data.resumes || []; 
        const [isTestModalOpen, setIsTestModalOpen] = useState(false);
        const [testToEdit, setTestToEdit] = useState(null); 

            // --- NOVAS FUNÇÕES: GERENCIAMENTO DE TENTATIVAS DE TESTES ---
        const handleLiberateTest = async (resume, testId) => {
            const targetTestId = String(testId);
            const updatedAllowedRetakes = [...(resume.allowedRetakes || [])];
            
            // Adiciona a permissão para refazer
            if (!updatedAllowedRetakes.includes(targetTestId)) {
                updatedAllowedRetakes.push(targetTestId);
            }

            // Limpa rascunhos e cronômetros antigos para o candidato começar do zero
            const updatedDrafts = { ...resume.drafts };
            delete updatedDrafts[targetTestId];

            const updatedDraftStartTimes = { ...resume.draftStartTimes };
            delete updatedDraftStartTimes[targetTestId];

            const updatedResume = {
                ...resume,
                allowedRetakes: updatedAllowedRetakes,
                drafts: updatedDrafts,
                draftStartTimes: updatedDraftStartTimes
            };

            if (actions.saveItem) {
                await actions.saveItem('resumes', updatedResume);
                setViewingResumeTests(updatedResume); // Atualiza o modal na hora
                setValidationAlert({ title: "Sucesso", message: "Teste liberado! O candidato pode acessar o link e realizar o teste novamente." });
            }
        };

        const handleDeleteAttempt = async (resume, attemptIndex) => {
            if (!window.confirm("Deseja realmente excluir esta tentativa? A nota será apagada.")) return;

            const newTestsTaken = [...(resume.testsTaken || [])];
            newTestsTaken.splice(attemptIndex, 1); // Remove a tentativa específica

            // Recalcula a nota principal (Pega a nota da última tentativa restante, ou 0)
            const newMainScore = newTestsTaken.length > 0 ? newTestsTaken[newTestsTaken.length - 1].score : 0;

            const updatedResume = {
                ...resume,
                testsTaken: newTestsTaken,
                testScore: newMainScore
            };

            if (actions.saveItem) {
                await actions.saveItem('resumes', updatedResume);
                setViewingResumeTests(updatedResume); // Atualiza o modal na hora
            }
        };

        
        // --- REQUISITO 3: PALETA DE CORES (Baseada nos Favoritos) ---
        const TEST_CARD_STYLES = [
            { gradient: "from-blue-600 to-cyan-500" },
            { gradient: "from-purple-600 to-fuchsia-500" },
            { gradient: "from-emerald-600 to-teal-400" },
            { gradient: "from-orange-500 to-amber-400" },
            { gradient: "from-pink-600 to-rose-400" },
            { gradient: "from-indigo-600 to-blue-500" },
            { gradient: "from-red-600 to-orange-500" },
            { gradient: "from-teal-600 to-emerald-400" },
            { gradient: "from-sky-500 to-blue-400" },
            { gradient: "from-violet-600 to-purple-500" }
        ];

        // --- FUNÇÃO DE EXPORTAÇÃO (PDF COM LAYOUT DA TELA E PLANILHA) ---
        const exportTestPDF = async (test, includeAnswers = false) => {
            try {
                if (typeof loadPdfLibs !== 'undefined') await loadPdfLibs();
                const PDFConstructor = (window.jspdf && window.jspdf.jsPDF) ? window.jspdf.jsPDF : window.jsPDF;
                const doc = new PDFConstructor('p', 'mm', 'a4');
                let y = 15;

                const primaryColor = [79, 70, 229]; // Indigo 600
                const secondaryColor = [243, 244, 246]; // Gray 100
                const textColor = [31, 41, 55]; // Gray 800
                const successColor = [22, 163, 74]; // Verde para o Gabarito

                // 1. CABEÇALHO DO TESTE
                const logoUrl = data.globalConfigs?.[0]?.logoUrl;
                if (logoUrl) {
                    try {
                        doc.addImage(logoUrl, 'PNG', 14, y, 30, 30, undefined, 'FAST');
                        doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.setTextColor(...primaryColor);
                        doc.text(test.title + (includeAnswers ? " (GABARITO)" : ""), 50, y + 18);
                        y += 35;
                    } catch (e) {
                        doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.setTextColor(...primaryColor);
                        doc.text(test.title + (includeAnswers ? " (GABARITO)" : ""), 14, y + 10);
                        y += 25;
                    }
                } else {
                    doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.setTextColor(...primaryColor);
                    doc.text(test.title + (includeAnswers ? " (GABARITO)" : ""), 14, y + 10);
                    y += 25;
                }

                // 2. BLOCO DE INFORMAÇÕES DO CANDIDATO
                doc.setFillColor(...secondaryColor);
                doc.setDrawColor(209, 213, 219); 
                doc.roundedRect(14, y, 182, 22, 3, 3, 'FD');
                doc.setFontSize(10); doc.setTextColor(...textColor); doc.setFont("helvetica", "bold");
                
                doc.text("Nome do Candidato:", 18, y + 8); doc.line(55, y + 8, 140, y + 8); 
                doc.text("Data:", 145, y + 8); doc.line(155, y + 8, 190, y + 8); 
                doc.text("Área / Vaga:", 18, y + 16); doc.setFont("helvetica", "normal");
                doc.text(test.departmentName || "Geral", 42, y + 16);
                y += 32;

                if (test.description) {
                    doc.setFontSize(9); doc.setFont("helvetica", "italic"); doc.setTextColor(107, 114, 128); 
                    const descLines = doc.splitTextToSize(test.description, 182);
                    doc.text(descLines, 14, y);
                    y += (descLines.length * 5) + 6;
                }

                // 3. RENDERIZAÇÃO DE GRUPOS E PERGUNTAS
                (test.sections || []).forEach((sec, sIdx) => {
                    if (y > 260) { doc.addPage(); y = 20; }
                    
                    // Cabeçalho da Seção
                    doc.setFillColor(...primaryColor);
                    doc.rect(14, y, 182, 8, 'F');
                    doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255);
                    doc.text(`${sIdx + 1}. ${sec.title.toUpperCase()}`, 18, y + 5.5);
                    y += 14;

                    let qCounter = 1;
                    (sec.questions || []).forEach((q) => {
                        if (y > 260) { doc.addPage(); y = 20; }
                        
                        // --- BLOCO DE CONTEXTO ---
                        if (q.type === 'context') {
                            doc.setFontSize(10); doc.setTextColor(37, 99, 235); doc.setFont("helvetica", "bold");
                            if (q.referenceInfo) { doc.text(q.referenceInfo, 18, y); y += 6; }
                            doc.setTextColor(100, 116, 139); doc.setFont("helvetica", "normal");
                            if (q.text) {
                                const lines = doc.splitTextToSize(q.text, 175);
                                doc.text(lines, 18, y);
                                y += (lines.length * 5) + 4;
                            }
                            return; 
                        }

                        // --- PERGUNTAS NORMAIS ---
                        const startQY = y - 4; // Marca o início da pergunta para desenhar a borda lateral

                        doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(...textColor);
                        
                        let qText = `${qCounter++}) ${q.text}`;
                        if (q.type === 'fill_blank') qText = qText.replace(new RegExp('\\{.*?\\}', 'g'), '________________');
                        
                        const lines = doc.splitTextToSize(qText, 170);
                        doc.text(lines, 20, y);
                        y += (lines.length * 5) + 2;

                        doc.setFont("helvetica", "normal");
                        
                        // TIPOS DE RESPOSTAS
                        if (q.type === 'multi') {
                            (q.options || []).forEach(opt => {
                                if (y > 280) { doc.addPage(); y = 20; }
                                const isCorrect = includeAnswers && (Array.isArray(q.correctAnswer) ? q.correctAnswer.includes(opt) : q.correctAnswer === opt);
                                doc.setDrawColor(...textColor);
                                if (q.allowMultiple) doc.rect(24, y - 3, 3, 3); else doc.circle(25.5, y - 1.5, 1.5);
                                
                                if (isCorrect) {
                                    doc.setTextColor(...successColor); doc.setFont("helvetica", "bold");
                                    doc.text("X", q.allowMultiple ? 24.5 : 24.5, y);
                                    doc.text(`${opt}`, 30, y);
                                    doc.setTextColor(...textColor); doc.setFont("helvetica", "normal");
                                } else {
                                    doc.text(`${opt}`, 30, y);
                                }
                                y += 6;
                            });
                            if (q.hasOther) {
                                if (y > 280) { doc.addPage(); y = 20; }
                                if (q.allowMultiple) doc.rect(24, y - 3, 3, 3); else doc.circle(25.5, y - 1.5, 1.5);
                                doc.text(`Outros: _______________________________________`, 30, y);
                                y += 6;
                            }
                        } else if (q.type === 'text') {
                            if (includeAnswers && q.correctAnswer) {
                                doc.setTextColor(...successColor);
                                const ansLines = doc.splitTextToSize(`Gabarito Esperado: ${q.correctAnswer}`, 170);
                                doc.text(ansLines, 20, y + 4);
                                y += (ansLines.length * 5) + 6;
                                doc.setTextColor(...textColor);
                            } else {
                                y += 6; doc.setDrawColor(209, 213, 219);
                                doc.line(20, y, 196, y); doc.line(20, y + 8, 196, y + 8); doc.line(20, y + 16, 196, y + 16);
                                y += 22;
                            }
                        } else if (q.type === 'fill_blank') {
                            if (includeAnswers && q.correctAnswer) {
                                doc.setTextColor(...successColor); doc.text(`Gabarito: ${q.correctAnswer}`, 20, y + 4); doc.setTextColor(...textColor);
                            }
                            y += 8; 
                        } else if (q.type === 'true_false') {
                            (q.options || []).forEach((opt, oIdx) => {
                                if (y > 280) { doc.addPage(); y = 20; }
                                doc.circle(25.5, y - 1.5, 1.5); // Bolinha V
                                doc.circle(31.5, y - 1.5, 1.5); // Bolinha F
                                doc.text(`V     F     ${opt}`, 24, y);
                                y += 6;
                                if (includeAnswers && Array.isArray(q.correctAnswer) && q.correctAnswer[oIdx]) {
                                    doc.setTextColor(...successColor);
                                    doc.text(`-> Correto: ${q.correctAnswer[oIdx]}`, 36, y);
                                    doc.setTextColor(...textColor);
                                    y += 6;
                                }
                            });
                        } else if (q.type === 'spreadsheet') {
                            if (q.instructions) {
                                doc.setFontSize(8); doc.setTextColor(100, 116, 139);
                                doc.text(`Orientações: ${q.instructions}`, 20, y);
                                y += 6; doc.setTextColor(...textColor); doc.setFontSize(10);
                            }
                            
                            // RENDERIZAÇÃO REAL DA PLANILHA NO PDF
                            const tableBody = [];
                            const rows = q.tableConfig?.rows || 1;
                            const cols = q.tableConfig?.cols || 1;

                            for (let r = 0; r < rows; r++) {
                                const rowData = [];
                                for (let c = 0; c < cols; c++) {
                                    const cellId = `${r}-${c}`;
                                    const cell = q.tableConfig?.cells?.[cellId] || { type: 'label', value: '' };
                                    
                                    if (cell.type === 'label') {
                                        rowData.push({ 
                                            content: cell.value, 
                                            styles: { fontStyle: cell.isBold ? 'bold' : 'normal', fillColor: [243, 244, 246], halign: 'center', valign: 'middle' } 
                                        });
                                    } else {
                                        if (includeAnswers && cell.expected) {
                                            rowData.push({ content: cell.expected, styles: { textColor: successColor, fontStyle: 'bold', halign: 'center', valign: 'middle' } });
                                        } else {
                                            rowData.push(""); // Célula em branco para preenchimento
                                        }
                                    }
                                }
                                tableBody.push(rowData);
                            }

                            doc.autoTable({
                                startY: y,
                                body: tableBody,
                                theme: 'grid',
                                styles: { fontSize: 8, cellPadding: 3, lineColor: [209, 213, 219], lineWidth: 0.2 },
                                margin: { left: 20, right: 14 }
                            });
                            y = doc.lastAutoTable.finalY + 6;
                        }

                        // Desenha a Borda Lateral da Pergunta (Estilo igual à tela)
                        doc.setDrawColor(...primaryColor);
                        doc.setLineWidth(1.5);
                        doc.line(15, startQY, 15, y - 2);
                        doc.setLineWidth(0.1); // Reseta a largura da linha
                        
                        y += 4; // Espaçamento para a próxima pergunta
                    });
                });

                doc.save(`Teste_${test.title.replace(/[^a-z0-9]/gi, '_')}${includeAnswers ? '_Gabarito' : ''}.pdf`);
            } catch (e) {
                console.error(e);
                alert("Erro ao gerar o PDF do Teste.");
            }
        };

        const [answeringTest, setAnsweringTest] = useState(null); 
        const [viewingResumeTests, setViewingResumeTests] = useState(null);
        const [isEvaluatingTest, setIsEvaluatingTest] = useState(false);
        const [exportMenuOpen, setExportMenuOpen] = useState(null);
        const [shareTest, setShareTest] = useState(null); // Guarda os dados do teste a ser compartilhado
        const [allowedEmails, setAllowedEmails] = useState([]);
        const [shareFilterDept, setShareFilterDept] = useState('');
        const [shareFilterStatus, setShareFilterStatus] = useState('');
        const [shareSearch, setShareSearch] = useState('');
        const [isLinkGenerated, setIsLinkGenerated] = useState(false);

        // --- ESTADOS PARA NOVO CURRÍCULO (ATUALIZADO COM ANEXO) ---
        const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
        const [resumeFile, setResumeFile] = useState(null); // Estado para o arquivo físico
        const [isUploadingResume, setIsUploadingResume] = useState(false);
        const [resumeForm, setResumeForm] = useState({
            name: '', email: '', deptInterest: '', link: '', 
            status: 'Recebido', // Novo padrão
            classification: 'Em Análise',
            date: new Date().toISOString().split('T')[0] // Data Recebimento
        });

        // --- ESTADOS: FILTROS DE CURRÍCULOS (Filtro por Depto padrão) ---
        const [resumeFilters, setResumeFilters] = useState({
            dateRange: 'all', 
            status: '', 
            // Se não for SuperAdm, trava o filtro inicial no depto do usuário
            dept: currentUser?.role === 'SuperAdm' ? '' : currentUser?.departmentId || '', 
            classification: '', 
            search: ''
        });
        
        const [iaSummaryModal, setIaSummaryModal] = useState({ isOpen: false, resume: null, text: '' });

        // --- NOVOS ESTADOS PARA TOOLTIPS FLUTUANTES ---
        const [scoreTooltip, setScoreTooltip] = useState(null);
        const [testHoverTooltip, setTestHoverTooltip] = useState(null);
        const [summaryHover, setSummaryHover] = useState(null);

        // --- Lógica de Filtragem e Ordenação de Currículos (ATUALIZADA) ---
        const filteredResumes = useMemo(() => {
            return (data.resumes || [])
                .filter(r => {
                    const rDate = new Date(r.date || r.createdAt || new Date());
                    
                    // Filtro de Data
                    if (resumeFilters.dateRange !== 'all') {
                        let cutoff = new Date();
                        cutoff.setDate(cutoff.getDate() - parseInt(resumeFilters.dateRange));
                        if (rDate < cutoff) return false;
                    }
                    
                    // Filtros de Select
                    if (resumeFilters.status && r.status !== resumeFilters.status) return false;
                    if (resumeFilters.dept && r.deptInterest !== resumeFilters.dept) return false;
                    if (resumeFilters.classification && r.classification !== resumeFilters.classification) return false;
                    
                    // Filtro de Busca (Nome/Email)
                    if (resumeFilters.search) {
                        const s = resumeFilters.search.toLowerCase();
                        return (r.name || '').toLowerCase().includes(s) || (r.email || '').toLowerCase().includes(s);
                    }
                    return true;
                })
                // ORDENAÇÃO: Sempre do mais recente para o mais antigo pela data de recebimento
                .sort((a, b) => {
                    const dateA = new Date(a.date || a.createdAt || 0);
                    const dateB = new Date(b.date || b.createdAt || 0);
                    return dateB - dateA; // Decrescente
                });
                    }, [data.resumes, resumeFilters]);

                    // --- FUNÇÕES DE ANOTAÇÕES E IA (CURRÍCULOS) ---
                    const handleSaveResumeNote = async () => {
                        if (!resumeNotesModal.newNote.trim() || !resumeNotesModal.resume) return;
                        const targetResume = resumeNotesModal.resume;
                        const newNoteObj = { date: new Date().toISOString(), text: resumeNotesModal.newNote, author: currentUser?.name || 'Sistema' };
                        const updatedNotes = [...(targetResume.notes || []), newNoteObj];
                        await actions.saveItem('resumes', { ...targetResume, notes: updatedNotes });
                        setResumeNotesModal({ ...resumeNotesModal, resume: { ...targetResume, notes: updatedNotes }, newNote: '' });
                    };

                    const handleGenerateIASummary = async (resume, extractedText = null) => {
                        setGeneratingSummaryId(resume.id);
                        try {
                            let textContext = extractedText;
                            if (!textContext) {
                                textContext = `Link do currículo: ${resume.link || 'Não informado'} (Tente extrair os dados pelo link se possível).`;
                            }

                            const promptStr = `DADOS DO CURRÍCULO:\n${textContext}\n\nCom base nos dados acima, extraia em formato de tópicos curtos: Nome, Telefone, Endereço, Formação, Últimos Empregos (Local e Data) e Principais Atividades.`;
                            const sysPrompt = "Você é um assistente de RH. Extraia os dados solicitados de forma clara e direta. Se não conseguir ler os dados, informe que a leitura falhou.";
                            
                            const summary = await callGeminiAI(promptStr, sysPrompt, 'general');
                            await actions.saveItem('resumes', { ...resume, iaSummary: summary });
                        } catch (e) {
                            console.error(e);
                        } finally {
                            setGeneratingSummaryId(null);
                        }
                    };

        // --- NOVO HANDLER: SALVAR CURRÍCULO COM ANEXO E IA AUTOMÁTICA ---
        const handleSaveResume = async () => {
            if (!resumeForm.name || !resumeForm.email || !resumeForm.deptInterest) {
                return setValidationAlert({ title: "Atenção", message: "Nome, Email e Departamento são obrigatórios." });
            }
            
            setIsUploadingResume(true);
            try {
                let finalLink = resumeForm.link;
                let extractedText = "";

                // Se o usuário anexou um PDF, lê o texto antes de fazer o upload
                // 1. Tenta ler o arquivo PDF se foi anexado fisicamente
                if (resumeFile) {
                    if (resumeFile.name.toLowerCase().endsWith('.pdf')) {
                        try {
                            if (typeof loadImportLibs !== 'undefined') await loadImportLibs();
                            const pdfRows = await readImportFile(resumeFile);
                            if (pdfRows && pdfRows.length > 0) extractedText = pdfRows.join('\n');
                        } catch (err) {
                            console.warn("Falha ao ler texto do PDF físico:", err);
                        }
                    }
                    // (Aqui entraria sua função de upload se estiver usando)
                    finalLink = "Link_Simulado_Do_Storage_ou_Drive"; 
                } 
                // 2. Se não anexou arquivo, mas colou um link do Google Drive
                else if (resumeForm.link && resumeForm.link.includes('drive.google.com')) {
                    try {
                        // Extrai o ID do arquivo do link do Drive
                        const idMatch = resumeForm.link.match(/[-\w]{25,}/);
                        if (idMatch) {
                            const fileId = idMatch[0];
                            // URL DO SEU SCRIPT (A mesma usada para deletar)
                            const SCRIPT_URL = "https://script.google.com/macros/s/AKfycbySpFDKhRT_DiNaKsG5G86izYnTvbtU6nbZQoQngFbi2BvCWBCzuw_hIMRDoHN1ug_A/exec";
                            
                            const response = await fetch(SCRIPT_URL, {
                                method: 'POST',
                                body: JSON.stringify({ action: 'read', id: fileId }),
                                headers: { "Content-Type": "text/plain;charset=utf-8" }
                            });
                            
                            const result = await response.json();
                            if (result.status === 'success' && result.text) {
                                extractedText = result.text;
                            }
                        }
                    } catch (err) {
                        console.warn("Falha ao ler texto via link do Drive:", err);
                    }
                }

                const isNew = !resumeForm.id;
                const newResume = {
                    ...resumeForm,
                    link: finalLink,
                    id: resumeForm.id || Date.now().toString(),
                    testScore: resumeForm.testScore || 0 
                };

                if (actions.saveItem) {
                    await actions.saveItem('resumes', newResume);
                    setIsResumeModalOpen(false);
                    setResumeForm({ name: '', email: '', deptInterest: '', link: '', status: 'Recebido', classification: 'Em Análise', date: new Date().toISOString().split('T')[0] });
                    setResumeFile(null);
                    setValidationAlert({ title: "Sucesso", message: "Currículo cadastrado com sucesso!" });
                    
                    // GERA O RESUMO IA AUTOMATICAMENTE: Usa o texto lido do PDF, ou tenta pelo link
                    if (isNew || extractedText) {
                        handleGenerateIASummary(newResume, extractedText);
                    }
                }
            } catch (e) {
                setValidationAlert({ title: "Erro", message: "Erro ao salvar currículo: " + e.message });
            } finally {
                setIsUploadingResume(false);
            }
        };

        // --- FUNÇÃO AUXILIAR: MUDANÇA RÁPIDA DE STATUS ---
        const quickStatusChange = async (resume, newStatus, newClassification) => {
            if (actions.saveItem) {
                await actions.saveItem('resumes', { 
                    ...resume, 
                    status: newStatus, 
                    classification: newClassification || resume.classification 
                });
            }
        };

        // Handler para Editar Teste
        const handleEditTest = (test) => {
            setTestToEdit(test);
            setIsTestModalOpen(true);
        };

        // --- NOVA FUNÇÃO: CLONAR TESTE ---
        const handleCloneTest = async (testToClone) => {
            if (!window.confirm(`Deseja criar uma cópia do teste "${testToClone.title}"?`)) return;
            
            try {
                // Cria uma cópia profunda do objeto
                const clonedTest = JSON.parse(JSON.stringify(testToClone));
                
                // Renova IDs e limpa dados da cópia
                clonedTest.id = Date.now().toString() + Math.random().toString(36).substr(2, 5);
                clonedTest.title = `${clonedTest.title} (Cópia)`;
                clonedTest.createdAt = new Date().toISOString();
                clonedTest.allowedEmails = []; // Limpa os acessos anteriores
                
                // Renova os IDs das sessões e perguntas para evitar conflitos no banco
                clonedTest.sections = (clonedTest.sections || []).map(sec => ({
                    ...sec,
                    id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
                    questions: (sec.questions || []).map(q => ({
                        ...q,
                        id: Math.random().toString(36).substr(2, 9)
                    }))
                }));

                if (actions.saveItem) {
                    await actions.saveItem('recruitmentTests', clonedTest);
                    setValidationAlert({ title: "Sucesso!", message: "Teste clonado com sucesso. Você já pode editá-lo." });
                }
            } catch (e) {
                console.error(e);
                setValidationAlert({ title: "Erro", message: "Erro ao clonar teste: " + e.message });
            }
        };

        // Lógica de Filtragem de Respostas de Pesquisa (ATUALIZADA)
        const getFilteredResponses = useMemo(() => {
            if (!surveyFilter) return [];
            let cutoffDate = new Date();
            if (dateRange !== 'custom') {
                cutoffDate.setDate(cutoffDate.getDate() - parseInt(dateRange));
            }
            return (data.surveyResponses || []).filter(r => {
                if (r.surveyId !== surveyFilter) return false;
                
                const rDate = new Date(r.timestamp);
                if (dateRange !== 'custom' && rDate < cutoffDate) return false;
                
                if (surveySearch) {
                    const s = surveySearch.toLowerCase();
                    const emailStr = r.userEmail || '';
                    if (!emailStr.toLowerCase().includes(s)) return false;
                }

                if (responseFilterDept) {
                    const deptName = data.departments.find(d => d.id === responseFilterDept)?.name;
                    if (r.userDept !== deptName) return false;
                }

                if (responseFilterType) {
                    if (r.userType !== responseFilterType) return false;
                }

                // --- NOVO: FILTROS DE ACOMPANHAMENTO ---
                if (responseFilterDeadline === 'with_deadline') {
                    if (!r.followUpDeadline) return false; // Exige que tenha prazo
                } else if (responseFilterDeadline === 'due_reminders') {
                    if (r.followUpCompleted) return false;
                    const today = new Date().toISOString().split('T')[0];
                    // Verifica se tem algum lembrete vencido/hoje
                    if (!r.followUpReminders || !r.followUpReminders.some(d => d && d <= today)) return false;
                }
                // --------------------------------------

                return true;
            }).sort((a, b) => {
                // REQUISITO: ORDENAÇÃO PELO PRAZO MAIS CURTO QUANDO FILTRADO
                if (responseFilterDeadline === 'with_deadline' || responseFilterDeadline === 'due_reminders') {
                    const dateA = a.followUpDeadline ? new Date(a.followUpDeadline).getTime() : Infinity;
                    const dateB = b.followUpDeadline ? new Date(b.followUpDeadline).getTime() : Infinity;
                    
                    // Se os dois tiverem a mesma data, usa a data do envio original
                    if (dateA === dateB) return b.timestamp - a.timestamp;
                    return dateA - dateB; // Crescente (Mais perto de vencer primeiro)
                }
                // Padrão: Mais recentes primeiro
                return b.timestamp - a.timestamp;
            });
        }, [data.surveyResponses, surveyFilter, dateRange, surveySearch, responseFilterDept, responseFilterType, responseFilterDeadline, data.departments]);

        const indicators = useMemo(() => {
            const stats = {};
            (data.surveys || []).forEach(s => {
                const unreadCount = (data.surveyResponses || []).filter(r => r.surveyId === s.id && !r.read).length;
                if (unreadCount > 0) stats[s.id] = { title: s.title, count: unreadCount, id: s.id };
            });
            return Object.values(stats);
        }, [data.surveys, data.surveyResponses]);

        const markAsRead = async (responseId) => {
            if (actions.saveItem) {
                const original = (data.surveyResponses || []).find(r => r.id === responseId);
                if (original) await actions.saveItem('surveyResponses', { ...original, read: true });
            }
        };

        // ==============================================================================
        // INÍCIO: ESTADOS E FUNÇÕES DE EXIBIÇÃO E EXPORTAÇÃO (MOVIDOS PARA CÁ)
        // ==============================================================================
        const [displayMode, setDisplayMode] = useState('detalhado');
        const [isExporting, setIsExporting] = useState(false);

        // Lógica para agrupar as respostas (Visão Totalizada)
        const summarizedData = useMemo(() => {
            if (!surveyFilter || displayMode !== 'totalizado') return null;
            const surveyDef = (data.surveys || []).find(s => s.id === surveyFilter);
            if (!surveyDef) return null;

            const summary = [];
            const totalResponsesCount = getFilteredResponses.length;

            surveyDef.sections?.forEach(sec => {
                sec.questions.forEach(q => {
                    const answersCount = {};
                    let validAnswers = 0;
                    
                    // --- LÓGICA EXCLUSIVA PARA ENPS ---
                    if (q.type === 'enps') {
                        let promoters = 0, detractors = 0, neutrals = 0;
                        getFilteredResponses.forEach(r => {
                            const ans = r.answers?.[q.id];
                            if (ans && ans.value !== undefined) {
                                validAnswers++;
                                const val = parseInt(ans.value);
                                if (val >= 9) promoters++;
                                else if (val >= 7) neutrals++;
                                else detractors++;
                            }
                        });
                        // Cálculo oficial do eNPS (-100 a +100)
                        const enpsScore = validAnswers > 0 ? Math.round(((promoters / validAnswers) * 100) - ((detractors / validAnswers) * 100)) : 0;
                        
                        summary.push({
                            questionId: q.id, questionText: q.text, type: q.type, validAnswers,
                            enpsData: { score: enpsScore, promoters, neutrals, detractors },
                            results: [] 
                        });
                        return; // Pula o resto do loop para esta pergunta
                    }
                    // ----------------------------------

                    getFilteredResponses.forEach(r => {
                        // ... (MANTENHA A LÓGICA ORIGINAL QUE JÁ ESTÁ AÍ PARA AS OUTRAS PERGUNTAS) ...
                        const ans = r.answers?.[q.id];
                        if (ans && ans.value) {
                            validAnswers++;
                            const vals = Array.isArray(ans.value) ? ans.value : [ans.value];
                            vals.forEach(v => {
                                const key = String(v).trim();
                                if (key) {
                                    answersCount[key] = (answersCount[key] || 0) + 1;
                                }
                            });
                            if (ans.otherText) {
                                const otherKey = `Outro: ${ans.otherText.trim()}`;
                                answersCount[otherKey] = (answersCount[otherKey] || 0) + 1;
                            }
                        }
                    });

                    const sortedAnswers = Object.entries(answersCount)
                        .map(([label, count]) => ({ 
                            label, 
                            count, 
                            percent: totalResponsesCount > 0 ? ((count / totalResponsesCount) * 100).toFixed(1) : 0 
                        }))
                        .sort((a, b) => b.count - a.count);

                    summary.push({
                        questionId: q.id,
                        questionText: q.text,
                        type: q.type,
                        validAnswers,
                        results: sortedAnswers
                    });
                });
            });

            return summary;
        }, [surveyFilter, displayMode, getFilteredResponses, data.surveys]);

        // Função Exportar Excel
        const exportSurveyExcel = async () => {
            setIsExporting(true);
            try {
                if (typeof loadImportLibs !== 'undefined') await loadImportLibs();
                if (!window.XLSX) throw new Error("Biblioteca XLSX não disponível.");

                const surveyDef = (data.surveys || []).find(s => s.id === surveyFilter);
                const surveyTitle = surveyDef ? surveyDef.title : 'Pesquisa';
                let dataExport = [];

                if (displayMode === 'detalhado') {
                    const header = ["Data", "Participante", "Departamento", "Tipo"];
                    const questions = [];
                    surveyDef?.sections?.forEach(sec => {
                        sec.questions.forEach(q => { header.push(q.text); questions.push(q); });
                    });
                    
                    getFilteredResponses.forEach(r => {
                        const row = { "Data": new Date(r.timestamp).toLocaleDateString(), "Participante": r.userEmail || 'Anônimo', "Departamento": r.userDept || '-', "Tipo": r.userType || '-' };
                        questions.forEach(q => {
                            const ans = r.answers?.[q.id];
                            let val = '-';
                            if (ans) {
                                val = Array.isArray(ans.value) ? ans.value.join(', ') : (ans.value || '');
                                if (ans.otherText) val += ` (Outro: ${ans.otherText})`;
                                if (ans.justification) val += ` [Justificativa: ${ans.justification}]`;
                            }
                            row[q.text] = val;
                        });
                        dataExport.push(row);
                    });
                } else {
                    summarizedData.forEach(qSum => {
                        dataExport.push({ "Pergunta": qSum.questionText, "Resposta": "", "Quantidade": "", "%": "" });
                        qSum.results.forEach(res => {
                            dataExport.push({ "Pergunta": "", "Resposta": res.label, "Quantidade": res.count, "%": `${res.percent}%` });
                        });
                        dataExport.push({}); 
                    });
                }

                const ws = window.XLSX.utils.json_to_sheet(dataExport);
                const wb = window.XLSX.utils.book_new();
                window.XLSX.utils.book_append_sheet(wb, ws, "Resultados");
                window.XLSX.writeFile(wb, `Resultados_${surveyTitle.replace(/[^a-z0-9]/gi, '_')}.xlsx`);
            } catch (e) {
                console.error(e);
                alert("Erro ao exportar Excel.");
            } finally {
                setIsExporting(false);
            }
        };

        // Função Exportar PDF
        const exportSurveyPDF = async () => {
            setIsExporting(true);
            try {
                if (typeof loadPdfLibs !== 'undefined') await loadPdfLibs();
                const PDFConstructor = (window.jspdf && window.jspdf.jsPDF) ? window.jspdf.jsPDF : window.jsPDF;
                if (!PDFConstructor) throw new Error("Biblioteca PDF não disponível.");
                
                const doc = new PDFConstructor('l', 'mm', 'a4'); 
                let startY = 20;
                const surveyDef = (data.surveys || []).find(s => s.id === surveyFilter);
                const surveyTitle = surveyDef ? surveyDef.title : 'Pesquisa';

                doc.setFontSize(16);
                doc.setFont("helvetica", "bold");
                doc.text(`Resultados: ${surveyTitle}`, 14, startY);
                startY += 10;

                if (displayMode === 'detalhado') {
                    const head = [["Data", "Participante", "Depto"]];
                    const questions = [];
                    surveyDef?.sections?.forEach(sec => {
                        sec.questions.forEach(q => {
                            head[0].push(q.text.substring(0, 30) + (q.text.length > 30 ? '...' : ''));
                            questions.push(q);
                        });
                    });

                    const body = getFilteredResponses.map(r => {
                        const row = [ new Date(r.timestamp).toLocaleDateString(), r.userEmail || 'Anônimo', r.userDept || '-' ];
                        questions.forEach(q => {
                            const ans = r.answers?.[q.id];
                            let val = '-';
                            if (ans) {
                                val = Array.isArray(ans.value) ? ans.value.join(', ') : (ans.value || '');
                                if (ans.otherText) val += ` (Outros: ${ans.otherText})`;
                            }
                            row.push(val);
                        });
                        return row;
                    });

                    doc.autoTable({ startY, head, body, theme: 'grid', styles: { fontSize: 7, overflow: 'linebreak' }, headStyles: { fillColor: [88, 28, 135] } });
                } else {
                    summarizedData.forEach(qSum => {
                        const body = qSum.results.map(res => [res.label, res.count, `${res.percent}%`]);
                        doc.autoTable({
                            startY,
                            head: [[{ content: qSum.questionText, colSpan: 3, styles: { fillColor: [243, 244, 246], textColor: [0,0,0] } }], ["Resposta", "Quantidade", "% (do total)"]],
                            body: body,
                            theme: 'grid',
                            styles: { fontSize: 8 },
                            headStyles: { fillColor: [88, 28, 135] }
                        });
                        startY = doc.lastAutoTable.finalY + 10;
                        if (startY > 180) { doc.addPage(); startY = 20; }
                    });
                }
                doc.save(`Resultados_${surveyTitle.replace(/[^a-z0-9]/gi, '_')}.pdf`);
            } catch (e) {
                console.error(e);
                alert("Erro ao exportar PDF.");
            } finally {
                setIsExporting(false);
            }
        };
        // ==============================================================================
        // FIM DAS NOVAS FUNÇÕES
        // ==============================================================================
        
        // --- RENDERIZAÇÃO ---
        if (activeTab === 'surveys') {
            // TELA A: GESTÃO DE PESQUISA (Código Mantido)
            return (
                <div className="flex-1 flex flex-col space-y-6 overflow-hidden animate-fade-in h-full">
                    {indicators.length > 0 && (
                        <div className="flex gap-4 overflow-x-auto pb-2 shrink-0 custom-scrollbar">
                            {indicators.map(ind => (
                                <div key={ind.id} onClick={() => setSurveyFilter(ind.id)} className="min-w-[200px] p-4 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 text-white shadow-lg cursor-pointer transform hover:scale-105 transition-all">
                                    <div className="flex justify-between items-start"><h4 className="font-bold text-sm truncate w-32">{ind.title}</h4><span className="bg-white/20 px-2 py-1 rounded text-xs font-bold">{ind.count} novas</span></div>
                                </div>
                            ))}
                        </div>
                    )}
                {/* BARRA DE FILTROS DE RESULTADOS (ATUALIZADA) */}
                    <Card className={`p-4 grid grid-cols-1 md:grid-cols-7 gap-3 shrink-0 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`} darkMode={isDarkMode}>
                        <div className="md:col-span-2"><label className="text-xs font-bold block mb-1">Pesquisa (Resultados)</label><select className={`w-full p-2 rounded border text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={surveyFilter} onChange={(e) => setSurveyFilter(e.target.value)}><option value="">Selecione...</option>{(data.surveys || []).map(s => <option key={s.id} value={s.id}>{s.title}</option>)}</select></div>
                        
                        <div><label className="text-xs font-bold block mb-1">Período</label><select className={`w-full p-2 rounded border text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={dateRange} onChange={(e) => setDateRange(e.target.value)}><option value="30">Últimos 30 dias</option><option value="90">Últimos 90 dias</option><option value="360">Último Ano</option></select></div>
                        
                        <div><label className="text-xs font-bold block mb-1 text-blue-600">Depto</label><select className={`w-full p-2 rounded border text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={responseFilterDept} onChange={(e) => setResponseFilterDept(e.target.value)}><option value="">Todos</option>{data.departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}</select></div>
                        
                        <div><label className="text-xs font-bold block mb-1 text-blue-600">Tipo</label><select className={`w-full p-2 rounded border text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={responseFilterType} onChange={(e) => setResponseFilterType(e.target.value)}><option value="">Todos</option><option value="Colaborador">Colaborador</option><option value="Gestão">Gestão</option></select></div>
                        
                    {/* NOVO FILTRO: EXIBIÇÃO */}
                        <div>
                            <label className="text-xs font-bold block mb-1 text-purple-600">Exibição</label>
                            <select className={`w-full p-2 rounded border text-xs font-bold ${isDarkMode ? 'bg-slate-900 border-purple-500 text-purple-300' : 'bg-purple-50 border-purple-300 text-purple-700'}`} value={displayMode} onChange={(e) => setDisplayMode(e.target.value)}>
                                <option value="detalhado">Detalhado (Lista)</option>
                                <option value="totalizado">Totalizado (Resumo)</option>
                            </select>
                        </div>

                        {/* --- NOVO FILTRO: ACOMPANHAMENTO --- */}
                        <div>
                            <label className="text-xs font-bold block mb-1 text-orange-600">Acompanhamento</label>
                            <select className={`w-full p-2 rounded border text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={responseFilterDeadline} onChange={(e) => setResponseFilterDeadline(e.target.value)}>
                                <option value="all">Todos</option>
                                <option value="with_deadline">Com Prazo Definido</option>
                                <option value="due_reminders">Lembretes Pendentes</option>
                            </select>
                        </div>

                        <div className="relative"><label className="text-xs font-bold block mb-1">Busca Livre</label><Search className="absolute left-2 top-8 text-gray-400" size={14}/><input className={`w-full pl-8 p-2 rounded border text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} placeholder="Buscar..." value={surveySearch} onChange={e => setSurveySearch(e.target.value)}/></div>
                    </Card>

                    <div className={`flex-1 rounded-xl border shadow-sm overflow-hidden flex flex-col ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                        
                        {/* BARRA DE EXPORTAÇÃO (SÓ APARECE SE TIVER PESQUISA SELECIONADA) */}
                        {surveyFilter && (
                            <div className={`flex justify-between items-center p-3 border-b shrink-0 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                <div className={`text-xs font-bold ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                    Respostas Filtradas: {getFilteredResponses.length}
                                </div>
                                <div className="flex gap-2">
                                    <Button onClick={exportSurveyPDF} disabled={isExporting} className="bg-red-600 hover:bg-red-700 text-white text-xs h-8 px-3" icon={FileText}>
                                        {isExporting ? 'Aguarde...' : 'Baixar PDF'}
                                    </Button>
                                    <Button onClick={exportSurveyExcel} disabled={isExporting} className="bg-green-600 hover:bg-green-700 text-white text-xs h-8 px-3" icon={FileSpreadsheet}>
                                        {isExporting ? 'Aguarde...' : 'Baixar Excel'}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {surveyFilter ? (
                            <>
                                {displayMode === 'totalizado' ? (
                                    /* --- MODO: TOTALIZADO (GRÁFICOS E BARRAS) --- */
                                    <div className="flex-1 overflow-auto custom-scrollbar p-6 space-y-6">
                                        {summarizedData?.map(qSum => (
                                            <div key={qSum.questionId} className={`p-4 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-gray-200'}`}>
                                                <h4 className={`font-bold mb-4 text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>{qSum.questionText}</h4>
                                                <div className="space-y-3">
                                                    {qSum.results.map((res, i) => (
                                                        <div key={i} className="flex items-center gap-3 text-xs">
                                                            <span className={`w-1/2 truncate font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`} title={res.label}>{res.label}</span>
                                                            <div className="flex-1 h-3 rounded-full bg-gray-100 dark:bg-slate-700 overflow-hidden flex shadow-inner">
                                                                <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-1000" style={{ width: `${res.percent}%` }}></div>
                                                            </div>
                                                            <span className={`w-12 text-right font-bold ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{res.count}</span>
                                                            <span className="w-12 text-right opacity-50 font-mono">{res.percent}%</span>
                                                        </div>
                                                    ))}
                                                    {qSum.results.length === 0 && <p className="text-xs italic opacity-50 text-center py-2">Nenhuma resposta válida para este item.</p>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    /* --- MODO: DETALHADO (TABELA ORIGINAL) --- */
                                    <div className="flex-1 overflow-auto custom-scrollbar">
                                        <table className={`w-full text-xs text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                            <thead className={`uppercase font-bold sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-700'}`}>
                                                <tr>
                                                    <th className="p-3 w-28 border-b text-center">Ações</th>
                                                    <th className="p-3 w-24 border-b">Data</th>
                                                    <th className="p-3 border-b min-w-[150px]">Participante</th>
                                                    {/* NOVA COLUNA AQUI */}
                                                    <th className="p-3 border-b min-w-[120px] text-center text-orange-600">Prazo HR</th>
                                                    
                                                    {(() => { const surveyDef = (data.surveys || []).find(s => s.id === surveyFilter); const questions = []; surveyDef?.sections?.forEach(sec => questions.push(...sec.questions)); return questions.map(q => (<th key={q.id} className="p-3 border-b min-w-[150px]"><div className="truncate w-32" title={q.text}>{q.text}</div></th>)); })()}
                                                </tr>
                                            </thead>
                                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                            {getFilteredResponses.map((r, idx) => (
                                                <tr key={r.id} className={`${idx%2===0?(isDarkMode?'bg-slate-800':'bg-white'):(isDarkMode?'bg-slate-800/50':'bg-gray-50')} ${!r.read?'font-bold border-l-4 border-l-pink-500':''}`}>
                                                    <td className="p-3 flex gap-2 justify-center">
                                                            <button onClick={() => { setSurveyViewModal(r); markAsRead(r.id); }} className={`p-1.5 rounded transition-colors ${isDarkMode ? 'bg-slate-700 text-blue-400 hover:bg-slate-600' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'}`} title="Visualizar Resposta"><Eye size={14}/></button>
                                                            
                                                            {/* --- NOVO BOTÃO DE ACOMPANHAMENTO --- */}
                                                        <button onClick={() => {
                                                            setFollowUpModal({ isOpen: true, responseId: r.id, deadline: r.followUpDeadline || '', reminders: r.followUpReminders || [], completed: r.followUpCompleted || false });
                                                        }} className="p-1.5 rounded text-orange-500 hover:bg-orange-100 transition-colors" title="Definir Prazo / Acompanhamento">
                                                            <Clock size={16}/>
                                                        </button>
                                                            
                                                            <button onClick={async () => { if (window.confirm("ATENÇÃO: Deseja excluir este registro?")) { if (actions.genericDelete) { await actions.genericDelete('surveyResponses', r.id); } } }} className={`p-1.5 rounded transition-colors ${isDarkMode ? 'bg-slate-700 text-red-400 hover:bg-slate-600' : 'bg-red-100 text-red-600 hover:bg-red-200'}`} title="Excluir Registro"><Trash2 size={14}/></button>
                                                        </td>
                                                        <td className="p-3 font-mono">{new Date(r.timestamp).toLocaleDateString()}</td>
                                                        <td className="p-3">
                                                        {r.userEmail ? (
                                                            <div>
                                                                <div className="font-bold">{r.userEmail}</div>
                                                                {(r.userDept || r.userType) && (
                                                                    <div className="text-[9px] opacity-60 flex gap-2 mt-0.5">
                                                                        {r.userDept && <span className="bg-gray-100 dark:bg-slate-700 px-1 rounded">{r.userDept}</span>}
                                                                        {r.userType && <span className="bg-gray-100 dark:bg-slate-700 px-1 rounded">{r.userType}</span>}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (<span className="italic opacity-50">Anônimo</span>)}
                                                    </td>

                                                    {/* --- NOVA CÉLULA: PRAZO HR --- */}
                                                    <td className="p-3 text-center align-middle border-l dark:border-slate-700">
                                                        {r.followUpDeadline ? (
                                                            <div className={`flex flex-col items-center justify-center p-1 rounded shadow-sm border ${r.followUpCompleted ? 'bg-gray-100 border-gray-200 dark:bg-slate-800 dark:border-slate-700 opacity-60 grayscale' : 'bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800'}`}>
                                                                <span className={`font-bold text-[11px] ${r.followUpCompleted ? 'line-through text-gray-500' : 'text-orange-600 dark:text-orange-400'}`}>
                                                                    {new Date(r.followUpDeadline + 'T00:00:00').toLocaleDateString('pt-BR')}
                                                                </span>
                                                                {r.followUpReminders?.length > 0 && !r.followUpCompleted && (
                                                                    <span className="text-[9px] text-orange-500 font-medium flex items-center gap-1 mt-0.5" title="Lembretes agendados">
                                                                        <Clock size={8}/> {r.followUpReminders.length} lembrete(s)
                                                                    </span>
                                                                )}
                                                                {r.followUpCompleted && <span className="text-[9px] text-green-600 font-bold mt-0.5 uppercase tracking-widest">Concluído</span>}
                                                            </div>
                                                        ) : <span className="text-gray-300 dark:text-slate-600">-</span>}
                                                    </td>
                                                    {/* --------------------------- */}
                                                        {(() => { const surveyDef = (data.surveys || []).find(s => s.id === surveyFilter); const questions = []; surveyDef?.sections?.forEach(sec => questions.push(...sec.questions)); return questions.map(q => { const ans = r.answers?.[q.id]; let display = '-'; if (ans) { if (ans.value) display = Array.isArray(ans.value) ? ans.value.join(', ') : ans.value; if (ans.otherText) display += ` (Outro: ${ans.otherText})`; } return <td key={q.id} className="p-3 border-l relative group"><div className="truncate w-32">{display}</div></td>; }); })()}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        ) : <div className="flex-1 flex flex-col items-center justify-center text-gray-400"><ClipboardCheck size={48} className="mb-4 opacity-20"/><p>Selecione uma pesquisa para ver os resultados.</p></div>}
                    </div>
                    
                    {surveyViewModal && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setSurveyViewModal(null)}>
                            <div className={`p-6 rounded-xl w-full max-w-2xl shadow-2xl overflow-y-auto max-h-[90vh] ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                
                                {/* Header do Modal */}
                                <div className={`flex justify-between items-center mb-6 border-b pb-4 ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                    <div>
                                        <h3 className="font-bold text-xl">Detalhes da Resposta</h3>
                                        <div className="flex flex-col text-xs mt-1 opacity-70">
                                            <span><b>Participante:</b> {surveyViewModal.userEmail || 'Anônimo'}</span>
                                            <span><b>Data:</b> {new Date(surveyViewModal.timestamp).toLocaleString('pt-BR')}</span>
                                        </div>
                                    </div>
                                    <button onClick={() => setSurveyViewModal(null)} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors"><X size={24}/></button>
                                </div>

                                {/* Corpo das Respostas */}
                                <div className="space-y-6">
                                    {(() => {
                                        // 1. Encontra a estrutura original da pesquisa para pegar os títulos das perguntas
                                        const surveyDef = (data.surveys || []).find(s => s.id === surveyViewModal.surveyId);
                                        
                                        if (!surveyDef) return <p className="text-center italic opacity-50">Estrutura da pesquisa original não encontrada (pode ter sido excluída).</p>;

                                        return (surveyDef.sections || []).map((section) => (
                                            <div key={section.id} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                <h4 className={`font-bold text-sm uppercase mb-4 pb-2 border-b ${isDarkMode ? 'text-purple-400 border-slate-700' : 'text-purple-700 border-gray-200'}`}>
                                                    {section.title}
                                                </h4>
                                                
                                                <div className="space-y-5">
                                                    {section.questions.map((q) => {
                                                        const answer = surveyViewModal.answers?.[q.id];
                                                        
                                                        // Formata a resposta para exibição
                                                        let displayValue = <span className="text-gray-400 italic text-xs">Não respondido</span>;
                                                        
                                                        if (answer) {
                                                            const rawVal = answer.value;
                                                            const otherText = answer.otherText ? ` (Outro: ${answer.otherText})` : '';
                                                            const justify = answer.justification ? `\nJustificativa: ${answer.justification}` : '';
                                                            
                                                            let textContent = '';
                                                            if (Array.isArray(rawVal)) {
                                                                textContent = rawVal.join(', ') + otherText;
                                                            } else {
                                                                textContent = (rawVal || '') + otherText;
                                                            }

                                                            if (textContent || justify) {
                                                                displayValue = (
                                                                    <div className={`text-sm mt-1 p-2 rounded ${isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'}`}>
                                                                        <span className={isDarkMode ? 'text-slate-200' : 'text-gray-800'}>{textContent || '-'}</span>
                                                                        {justify && (
                                                                            <div className={`mt-2 text-xs border-t pt-1 ${isDarkMode ? 'border-slate-700 text-orange-300' : 'border-gray-200 text-orange-600'}`}>
                                                                                <span className="font-bold">Nota:</span> {answer.justification}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            }
                                                        }

                                                        return (
                                                            <div key={q.id}>
                                                                <p className={`text-xs font-bold mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                                                                    {q.text}
                                                                </p>
                                                                {displayValue}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ));
                                    })()}
                                </div>
                                
                                {/* Footer */}
                                <div className={`mt-6 pt-4 border-t flex justify-end ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                    <Button onClick={() => setSurveyViewModal(null)}>Fechar</Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        // --- TELA B: GESTÃO DE TALENTOS (ATUALIZADA) ---
        return (
            <div className="space-y-6 animate-fade-in flex flex-col h-full">
                <div className={`flex gap-2 p-1 rounded-lg w-fit shrink-0 ${isDarkMode ? 'bg-slate-900' : 'bg-gray-200'}`}>
                    <button 
                        onClick={() => setSubTab('resumes')} 
                        className={`px-4 py-2 text-xs font-bold rounded-md transition-all ${subTab === 'resumes' ? (isDarkMode ? 'bg-slate-800 shadow text-blue-400' : 'bg-white shadow text-blue-600') : (isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-600 hover:text-gray-800')}`}
                    >
                        Currículos
                    </button>
                    <button 
                        onClick={() => setSubTab('process')} 
                        className={`px-4 py-2 text-xs font-bold rounded-md transition-all ${subTab === 'process' ? (isDarkMode ? 'bg-slate-800 shadow text-indigo-400' : 'bg-white shadow text-indigo-600') : (isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-600 hover:text-gray-800')}`}
                    >
                        Processo Seletivo (Testes)
                    </button>
                </div>

                {subTab === 'resumes' && (
                    <div className="space-y-4 flex flex-col flex-1 overflow-hidden animate-fade-in relative">
                        
                        {/* BOTÃO NOVO REGISTRO */}
                        <div className="flex justify-end">
                            <Button onClick={() => setIsResumeModalOpen(true)} icon={UserPlus} className="bg-blue-600 text-white shadow-md">Novo Currículo</Button>
                        </div>

                        {/* BARRA DE FILTROS AVANÇADOS */}
                        <Card className="p-3 grid grid-cols-1 md:grid-cols-5 gap-3 shrink-0" darkMode={isDarkMode}>
                            <div>
                                <label className="text-[10px] font-bold uppercase opacity-60">Período</label>
                                <select className={`w-full p-1.5 border rounded text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600' : ''}`} value={resumeFilters.dateRange} onChange={e => setResumeFilters({...resumeFilters, dateRange: e.target.value})}>
                                    <option value="all">Todo Histórico</option>
                                    <option value="30">Últimos 30 dias</option>
                                    <option value="90">Últimos 90 dias</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold uppercase opacity-60">Departamento</label>
                                <select className={`w-full p-1.5 border rounded text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600' : ''}`} value={resumeFilters.dept} onChange={e => setResumeFilters({...resumeFilters, dept: e.target.value})}>
                                    <option value="">Todos os Departamentos</option>
                                    {data.departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold uppercase opacity-60">Status</label>
                                <select className={`w-full p-1.5 border rounded text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600' : ''}`} value={resumeFilters.status} onChange={e => setResumeFilters({...resumeFilters, status: e.target.value})}>
                                    <option value="">Todos</option>
                                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold uppercase opacity-60">Situação</label>
                                <select className={`w-full p-1.5 border rounded text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600' : ''}`} value={resumeFilters.classification} onChange={e => setResumeFilters({...resumeFilters, classification: e.target.value})}>
                                    <option value="">Todas</option>
                                    {SITUACAO_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div className="relative">
                                <label className="text-[10px] font-bold uppercase opacity-60">Busca</label>
                                <Input className={`pl-2 h-8 text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600' : ''}`} value={resumeFilters.search} onChange={e => setResumeFilters({...resumeFilters, search: e.target.value})} placeholder="Nome/Email..." />
                            </div>
                        </Card>

                        <Card className="p-0 flex-1 overflow-hidden" darkMode={isDarkMode}>
                            <div className="overflow-auto h-full custom-scrollbar pb-32">
                                <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                                    <thead className={`sticky top-0 z-10 text-xs uppercase font-bold ${isDarkMode ? 'bg-slate-900 border-b border-slate-700' : 'bg-gray-100 border-b border-gray-200'}`}>
                                        <tr>
                                            <th className="p-3 w-40">Candidato</th>
                                            <th className="p-3 w-32">E-mail</th>
                                            <th className="p-3 w-24 text-center">Data Receb.</th>
                                            {/* 1. Nome alterado para Dpto e largura reduzida (de w-32 para w-24) */}
                                            <th className="p-3 w-24">Dpto</th>
                                            <th className="p-3 w-32">Gestor</th>
                                            {/* 3. Largura do Status aumentada (de w-32 para w-44) */}
                                            <th className="p-3 w-44 text-center">Status</th>
                                            <th className="p-3 w-32 text-center">Situação</th>
                                            <th className="p-3 w-24 text-center">Links (Testes)</th>
                                            <th className="p-3 w-24 text-center">Nota (Testes)</th>
                                            {/* 2. Coluna Resumo IA reduzida para virar botão/link */}
                                            <th className="p-3 w-24 text-center">Resumo IA</th>
                                            <th className="p-3 w-40 text-center">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                        {filteredResumes.map((r, idx) => {
                                            const rowClass = idx % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');
                                            
                                            // ITEM 7: Cores exclusivas para o Status
                                            let statusColor = 'bg-gray-100 text-gray-700 border-gray-200';
                                            if (r.status === 'Recebido') statusColor = 'bg-blue-100 text-blue-800 border-blue-300';
                                            if (r.status === 'Em análise') statusColor = 'bg-purple-100 text-purple-800 border-purple-300';
                                            if (r.status === 'Aguardando retorno') statusColor = 'bg-yellow-100 text-yellow-800 border-yellow-300';
                                            if (r.status === 'Não retornou' || r.status === 'Não apareceu' || r.status === 'Não quer participar') statusColor = 'bg-red-50 text-red-600 border-red-200';
                                            if (r.status === 'Concluir teste') statusColor = 'bg-indigo-100 text-indigo-800 border-indigo-300';
                                            if (r.status === 'Entrevista') statusColor = 'bg-orange-100 text-orange-800 border-orange-300';
                                            if (r.status === 'Retorno do Candidato') statusColor = 'bg-teal-100 text-teal-800 border-teal-300';
                                            if (r.status === 'Contratado') statusColor = 'bg-green-100 text-green-800 border-green-400';
                                            if (r.status === 'Reprovado ou Proposta') statusColor = 'bg-red-100 text-red-800 border-red-300';
                                            if (r.status === 'Arquivado') statusColor = 'bg-slate-200 text-slate-600 border-slate-300';

                                            const isHired = r.status === 'Contratado';
                                            const lastTest = r.testsTaken && r.testsTaken.length > 0 ? r.testsTaken[r.testsTaken.length - 1] : null;

                                            return (
                                            <tr key={r.id} className={`${rowClass} ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-blue-50'} transition-colors align-middle`}>
                                                <td className="p-3 font-bold truncate max-w-[150px]" title={r.name}>{r.name}</td>
                                                <td className="p-3 text-[10px] opacity-80 truncate max-w-[120px]" title={r.email}>{r.email}</td>
                                                <td className="p-3 text-center text-xs font-mono">{new Date(r.date || r.createdAt).toLocaleDateString('pt-BR')}</td>
                                                
                                                <td className="p-3">
                                                    <select 
                                                        className={`w-full p-1 text-xs border rounded bg-transparent ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}
                                                        value={r.deptInterest || ''}
                                                        onChange={e => actions.saveItem('resumes', { ...r, deptInterest: e.target.value })}
                                                    >
                                                        <option value="">Sem Depto</option>
                                                        {data.departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                                    </select>
                                                </td>
                                                
                                                <td className="p-3 text-[10px] font-bold text-blue-600 truncate max-w-[120px]" title={r.gestorName}>
                                                    {r.gestorName || '-'}
                                                </td>

                                                <td className="p-3 text-center">
                                                    <select 
                                                        className={`w-full p-1 text-[10px] border rounded font-bold uppercase tracking-wider ${isDarkMode ? statusColor.replace('bg-', 'bg-opacity-20 bg-') : statusColor}`}
                                                        value={r.status || 'Recebido'}
                                                        disabled={r.status === 'Recebido'} 
                                                        onChange={e => {
                                                            const newStatus = e.target.value;
                                                            const updates = { status: newStatus };
                                                            if (currentUser && currentUser.email !== r.gestorEmail) {
                                                                updates.gestorName = currentUser.name;
                                                                updates.gestorEmail = currentUser.email;
                                                            }
                                                            actions.saveItem('resumes', { ...r, ...updates });
                                                        }}
                                                    >
                                                        <option value="Recebido" disabled>Recebido</option>
                                                        {STATUS_OPTIONS.filter(s => s !== 'Recebido').map(s => <option key={s} value={s}>{s}</option>)}
                                                    </select>
                                                </td>

                                                <td className="p-3 text-center">
                                                    <select 
                                                        className={`w-full p-1 text-[10px] border rounded ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-300' : 'bg-white border-gray-200 text-gray-600'}`}
                                                        value={r.classification || 'Pendente'}
                                                        onChange={e => actions.saveItem('resumes', { ...r, classification: e.target.value })}
                                                    >
                                                        {SITUACAO_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                                                    </select>
                                                </td>

                                                <td className="p-3 text-center">
                                                    <div className="flex flex-col gap-1 max-h-16 overflow-y-auto custom-scrollbar">
                                                        {r.testLinks && r.testLinks.length > 0 ? (
                                                            r.testLinks.map((linkObj, idx) => (
                                                                <a key={idx} href={linkObj.url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 hover:underline bg-blue-50 dark:bg-blue-900/20 px-1 py-0.5 rounded border border-blue-100 dark:border-blue-800">
                                                                    Teste {idx + 1}
                                                                </a>
                                                            ))
                                                        ) : <span className="text-[10px] text-gray-400">-</span>}
                                                    </div>
                                                </td>

                                                <td className="p-3 text-center relative">
                                                    {lastTest ? (
                                                        <div className="flex flex-col items-center gap-1 group">
                                                            <div 
                                                                className={`font-black text-xs px-2 py-1 rounded cursor-help border ${lastTest.score >= 70 ? 'bg-green-100 text-green-700 border-green-200' : 'bg-orange-100 text-orange-700 border-orange-200'}`}
                                                                onMouseEnter={(e) => {
                                                                    const originalTest = data.recruitmentTests.find(t => t.id === lastTest.testId);
                                                                    let groups = {};
                                                                    if (originalTest && lastTest.details) {
                                                                        originalTest.sections.forEach(sec => {
                                                                            let secScore = 0;
                                                                            sec.questions.forEach(q => {
                                                                                if (lastTest.details[q.id]) secScore += lastTest.details[q.id].score;
                                                                            });
                                                                            groups[sec.title] = secScore;
                                                                        });
                                                                    }
                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                    setScoreTooltip({ x: rect.left, y: rect.bottom, total: lastTest.score, groups });
                                                                }}
                                                                onMouseLeave={() => setScoreTooltip(null)}
                                                            >
                                                                {lastTest.score} pts
                                                            </div>
                                                        </div>
                                                    ) : <span className="text-gray-400 text-xs">-</span>}
                                                </td>

                                                {/* 2. COLUNA RESUMO IA (Botão com Popup) */}
                                                <td className="p-3 text-center align-middle">
                                                    {r.iaSummary ? (
                                                        <button 
                                                            className="text-[10px] text-purple-600 font-bold cursor-pointer flex items-center justify-center gap-1 bg-purple-50 dark:bg-purple-900/20 py-1.5 rounded-lg border border-purple-200 dark:border-purple-800 transition-colors hover:bg-purple-100 w-full"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const rect = e.currentTarget.getBoundingClientRect();
                                                                setSummaryHover({ text: r.iaSummary, x: rect.left, y: rect.top + (rect.height / 2) });
                                                            }}
                                                        >
                                                            <Sparkles size={12}/> Ler Resumo
                                                        </button>
                                                    ) : (
                                                        <button 
                                                            className="text-[10px] text-gray-500 font-medium cursor-pointer hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 flex items-center justify-center gap-1 mx-auto py-1 px-2 rounded transition-colors disabled:opacity-50" 
                                                            onClick={() => handleGenerateIASummary(r)}
                                                            disabled={generatingSummaryId === r.id}
                                                        >
                                                            {generatingSummaryId === r.id ? <Loader2 size={12} className="animate-spin"/> : <Bot size={12}/>}
                                                            Gerar
                                                        </button>
                                                    )}
                                                </td>

                                                {/* AÇÕES EM LINHA ÚNICA (flex-nowrap) */}
                                                <td className="p-3 text-center align-middle">
                                                    <div className="flex justify-center items-center gap-1.5 flex-nowrap">
                                                        <button onClick={() => { setResumeForm(r); setIsResumeModalOpen(true); }} className="p-1.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100" title="Ver/Editar Cadastro"><Eye size={14}/></button>
                                                        
                                                        {/* Botão IA Modificado (Abre Modal de Texto) */}
                                                        <button onClick={() => setIaSummaryModal({ isOpen: true, resume: r, text: '' })} disabled={generatingSummaryId === r.id} className="p-1.5 rounded bg-purple-50 text-purple-600 hover:bg-purple-100 disabled:opacity-50" title="Gerar Resumo IA (Colar Texto)">
                                                            {generatingSummaryId === r.id ? <Loader2 size={14} className="animate-spin"/> : <Bot size={14}/>}
                                                        </button>
                                                        
                                                        {/* Botão Anotações com Tooltip Flutuante */}
                                                        <div className="relative group/notes">
                                                            <button onClick={() => setResumeNotesModal({isOpen: true, resume: r, newNote: ''})} className={`p-1.5 rounded transition-colors ${r.notes?.length > 0 ? 'bg-yellow-400 text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`} title="Anotações / Histórico">
                                                                <MessageSquare size={14}/>
                                                            </button>
                                                            {r.notes?.length > 0 && (
                                                                <div className="absolute right-0 bottom-full mb-2 w-64 bg-slate-800 text-white text-xs p-3 rounded-lg shadow-xl invisible group-hover/notes:visible z-[100] text-left border border-slate-600">
                                                                    <p className="font-bold mb-2 border-b border-slate-600 pb-1 text-yellow-400">Últimas Anotações:</p>
                                                                    <div className="max-h-32 overflow-y-auto custom-scrollbar space-y-2">
                                                                        {[...r.notes].reverse().map((n, i) => (
                                                                            <div key={i} className="pb-2 border-b border-slate-700 border-dashed last:border-0 last:pb-0">
                                                                                <div className="flex justify-between text-[9px] text-gray-400 mb-0.5">
                                                                                    <span>{new Date(n.date).toLocaleDateString('pt-BR')}</span>
                                                                                    <span>{n.author}</span>
                                                                                </div>
                                                                                <div className="leading-tight text-[11px]">{n.text}</div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* BOTÃO ÚNICO DE GERENCIAMENTO DE TESTES */}
                                                        {(r.testsTaken?.length > 0 || r.testLinks?.length > 0) && (
                                                            <button 
                                                                onClick={() => setViewingResumeTests(r)}
                                                                className="p-1.5 rounded bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors" 
                                                                title="Gerenciar Testes e Notas"
                                                            >
                                                                <FileText size={14}/>
                                                            </button>
                                                        )}

                                                        <button onClick={() => { if(window.confirm("Excluir este currículo permanentemente?")) actions.genericDelete('resumes', r.id); }} className="p-1.5 rounded bg-red-50 text-red-600 hover:bg-red-100" title="Excluir Currículo">
                                                            <Trash2 size={14}/>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )})}
                                        {filteredResumes.length === 0 && (
                                            <tr><td colSpan="11" className="p-8 text-center text-gray-400 italic">Nenhum currículo encontrado.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                        
                        {/* --- MODAL DA IA PARA RESUMO DO CURRÍCULO --- */}
                        {iaSummaryModal.isOpen && (
                            <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                                <div className={`p-6 rounded-xl w-full max-w-2xl shadow-2xl ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800'}`}>
                                    <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                                        <Bot size={20} className="text-purple-500"/>
                                        Resumir Currículo: {iaSummaryModal.resume?.name}
                                    </h3>
                                    <p className={`text-xs mb-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                        Cole o texto do currículo do candidato abaixo. A IA fará a leitura do texto e extrairá os pontos principais.
                                    </p>
                                    
                                    <textarea 
                                        className={`w-full p-3 rounded-lg border h-48 resize-none outline-none focus:ring-2 focus:ring-purple-500 custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-300'}`}
                                        placeholder="Cole aqui o texto do currículo..."
                                        value={iaSummaryModal.text}
                                        onChange={e => setIaSummaryModal({...iaSummaryModal, text: e.target.value})}
                                    ></textarea>

                                    <div className="flex justify-end gap-2 mt-4">
                                        <Button variant="secondary" onClick={() => setIaSummaryModal({isOpen:false, resume:null, text:''})}>Cancelar</Button>
                                        <Button 
                                            onClick={async () => {
                                                if (!iaSummaryModal.text.trim()) return alert("Cole o texto do currículo.");
                                                setGeneratingSummaryId(iaSummaryModal.resume.id);
                                                setIaSummaryModal({isOpen:false, resume:null, text:''}); // Fecha modal
                                                try {
                                                    const promptStr = `Extraia os dados em formato de tópicos curtos: Nome, Telefone, Endereço, Formação, Últimos Empregos (Local e Data) e Principais Atividades.\nTEXTO:\n${iaSummaryModal.text}`;
                                                    const sysPrompt = "Você é um assistente de RH. Resuma o currículo fornecido em tópicos.";
                                                    const summary = await callGeminiAI(promptStr, sysPrompt, 'general');
                                                    await actions.saveItem('resumes', { ...iaSummaryModal.resume, iaSummary: summary });
                                                } catch (e) { alert("Erro ao gerar resumo."); } 
                                                finally { setGeneratingSummaryId(null); }
                                            }} 
                                            className="bg-purple-600 hover:bg-purple-700 text-white shadow-sm"
                                        >
                                            Analisar e Gerar Resumo
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* --- MODAL DE ANOTAÇÕES DO CURRÍCULO (ITEM 3) --- */}
                        {resumeNotesModal.isOpen && (
                            <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                                <div className={`p-6 rounded-xl w-full max-w-md shadow-2xl ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800'}`}>
                                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                                        <MessageSquare size={20} className="text-yellow-500"/>
                                        Anotações: {resumeNotesModal.resume?.name}
                                    </h3>
                                    
                                    <textarea 
                                        className={`w-full p-3 rounded-lg border h-24 resize-none outline-none focus:ring-2 focus:ring-yellow-400 ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-300'}`}
                                        placeholder="Digite uma nova anotação sobre o candidato..."
                                        value={resumeNotesModal.newNote}
                                        onChange={e => setResumeNotesModal({...resumeNotesModal, newNote: e.target.value})}
                                    ></textarea>

                                    <div className="flex justify-end gap-2 mt-4">
                                        <Button variant="secondary" onClick={() => setResumeNotesModal({isOpen:false, resume:null, newNote:''})} className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}>Fechar</Button>
                                        <Button onClick={handleSaveResumeNote} className="bg-yellow-500 hover:bg-yellow-600 text-white shadow-sm">Gravar Anotação</Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* --- TOOLTIPS FLUTUANTES (NOTAS E TESTES E RESUMO IA) --- */}
                        
                        {/* NOVO: TOOLTIP DO RESUMO IA (CORRIGIDO PARA CLIQUE E CORES) */}
                        {summaryHover && (
                            <>
                                {/* Camada invisível para detectar o clique fora e fechar */}
                                <div className="fixed inset-0 z-[9998]" onClick={() => setSummaryHover(null)}></div>
                                
                                <div 
                                    className={`fixed z-[9999] p-4 rounded-xl shadow-2xl text-xs border w-80 transform -translate-x-full -translate-y-1/2 animate-fade-in ${isDarkMode ? 'bg-slate-800 text-white border-slate-600' : 'bg-white text-gray-800 border-gray-200'}`} 
                                    style={{ top: summaryHover.y, left: summaryHover.x - 10 }}
                                >
                                    <div className={`font-bold mb-2 border-b pb-1 flex items-center gap-2 ${isDarkMode ? 'text-purple-400 border-slate-600' : 'text-purple-600 border-gray-200'}`}>
                                        <Bot size={14}/> Resumo Extraído:
                                    </div>
                                    <div className="leading-relaxed opacity-90 max-h-60 overflow-y-auto custom-scrollbar pr-2 whitespace-pre-wrap pointer-events-auto">
                                        {summaryHover.text}
                                    </div>
                                    {/* Setinha apontando para o botão (direita) */}
                                    <div className={`absolute right-[-6px] top-1/2 -translate-y-1/2 w-3 h-3 rotate-45 border-t border-r ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'}`}></div>
                                </div>
                            </>
                        )}
                        
                        {scoreTooltip && (
                            <div className="fixed z-[9999] bg-slate-800 text-white p-3 rounded-lg shadow-2xl text-xs border border-slate-600 pointer-events-none" style={{ top: scoreTooltip.y + 5, left: scoreTooltip.x - 50 }}>
                                <div className="font-bold border-b border-slate-600 pb-1 mb-2 text-blue-300 uppercase tracking-wider">
                                    Detalhamento por Grupo
                                </div>
                                <div className="space-y-1">
                                    {Object.keys(scoreTooltip.groups).length > 0 ? (
                                        Object.entries(scoreTooltip.groups).map(([group, score]) => (
                                            <div key={group} className="flex justify-between gap-4">
                                                <span>{group}:</span>
                                                <span className="font-bold text-green-400">{score.toFixed(1)} pts</span>
                                            </div>
                                        ))
                                    ) : <div className="italic text-slate-400">Detalhes indisponíveis</div>}
                                </div>
                                <div className="border-t border-slate-600 mt-2 pt-1 flex justify-between font-bold text-sm">
                                    <span>Total:</span> <span>{scoreTooltip.total}</span>
                                </div>
                            </div>
                        )}

                        {testHoverTooltip && (
                            <div className="fixed z-[9999] bg-white dark:bg-slate-800 text-gray-800 dark:text-white p-3 rounded-lg shadow-2xl text-xs border border-gray-300 dark:border-slate-600" style={{ top: testHoverTooltip.y + 5, left: testHoverTooltip.x - 80 }}>
                                <div className="font-bold border-b border-gray-200 dark:border-slate-600 pb-1 mb-2 text-indigo-600 dark:text-indigo-400">
                                    {testHoverTooltip.testName}
                                </div>
                                <p className="mb-3 opacity-80">Clique no botão para abrir o espelho completo da prova do candidato.</p>
                                {testHoverTooltip.testObj && (
                                    <button 
                                        onClick={() => exportTestPDF(testHoverTooltip.testObj)}
                                        className="w-full bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 px-3 py-1.5 rounded flex items-center justify-center gap-2 font-bold transition-colors"
                                    >
                                        <FileText size={14}/> Gerar PDF da Prova (Branco)
                                    </button>
                                )}
                            </div>
                        )}

                        {/* MODAL NOVO/EDITAR CURRÍCULO (ATUALIZADO COM ANEXO E NOVOS CAMPOS) */}
                        {isResumeModalOpen && (
                            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => !isUploadingResume && setIsResumeModalOpen(false)}>
                                <div className={`p-6 rounded-xl w-full max-w-2xl shadow-2xl ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800 border border-gray-200'}`} onClick={e => e.stopPropagation()}>
                                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2 border-b pb-2 dark:border-slate-700">
                                        <UserPlus size={20} className="text-blue-500"/> 
                                        {resumeForm.id ? 'Ficha do Candidato' : 'Cadastrar Novo Currículo'}
                                    </h3>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Lado Esquerdo: Dados Básicos */}
                                        <div className="space-y-3">
                                            <div>
                                                <label className="text-[10px] font-bold uppercase opacity-70 block mb-1">Data Recebimento</label>
                                                <input type="date" className={`w-full p-2 border rounded text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar' : ''}`} value={resumeForm.date} onChange={e => setResumeForm({...resumeForm, date: e.target.value})} />
                                            </div>
                                            <Input placeholder="Nome Completo *" value={resumeForm.name} onChange={e => setResumeForm({...resumeForm, name: e.target.value})} className={isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''} />
                                            <Input placeholder="E-mail *" type="email" value={resumeForm.email} onChange={e => setResumeForm({...resumeForm, email: e.target.value})} className={isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''} />
                                            
                                            <div>
                                                <label className="text-[10px] font-bold uppercase opacity-70 block mb-1">Departamento Desejado *</label>
                                                <select className={`w-full p-2 rounded border text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`} value={resumeForm.deptInterest} onChange={e => setResumeForm({...resumeForm, deptInterest: e.target.value})}>
                                                    <option value="">Selecione...</option>
                                                    {data.departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                                </select>
                                            </div>
                                        </div>

                                        {/* Lado Direito: Anexos e Status */}
                                        <div className="space-y-3">
                                            <div className={`p-3 rounded border ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                                                <label className="text-[10px] font-bold uppercase text-blue-500 block mb-2">Acompanhamento RH</label>
                                                
                                                <label className="text-xs font-bold block mb-1">Status Atual</label>
                                                <select className={`w-full p-2 rounded border text-sm outline-none mb-3 ${isDarkMode ? 'bg-slate-800 border-slate-500 text-white' : 'bg-white'}`} value={resumeForm.status} onChange={e => setResumeForm({...resumeForm, status: e.target.value})}>
                                                    <option value="Recebido">Recebido</option>
                                                    <option value="Em Análise">Em Análise</option>
                                                    <option value="Processo Seletivo">Processo Seletivo</option>
                                                    <option value="Aprovado">Aprovado</option>
                                                    <option value="Reprovado">Reprovado</option>
                                                    <option value="Arquivado">Arquivado</option>
                                                </select>

                                                <label className="text-xs font-bold block mb-1">Situação / Classificação</label>
                                                <select className={`w-full p-2 rounded border text-sm outline-none ${isDarkMode ? 'bg-slate-800 border-slate-500 text-white' : 'bg-white'}`} value={resumeForm.classification} onChange={e => setResumeForm({...resumeForm, classification: e.target.value})}>
                                                    <option value="Em Análise">Em Análise</option>
                                                    <option value="Contratar">Contratar</option>
                                                    <option value="Possibilidade Futura">Possibilidade Futura</option>
                                                    <option value="Não Chamar Mais">Não Chamar Mais</option>
                                                </select>
                                            </div>

                                            {/* Anexo de Arquivo */}
                                            <div className={`p-3 rounded border border-dashed ${isDarkMode ? 'border-slate-500' : 'border-gray-400'}`}>
                                                <label className="text-[10px] font-bold uppercase opacity-70 block mb-1 flex items-center gap-1"><Paperclip size={12}/> Currículo Físico (PDF/Word)</label>
                                                <input type="file" accept=".pdf,.doc,.docx" onChange={e => setResumeFile(e.target.files[0])} className={`w-full text-xs mb-2 ${isDarkMode ? 'text-slate-300' : ''}`} />
                                                
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-bold opacity-50">OU</span>
                                                </div>

                                                <label className="text-[10px] font-bold uppercase opacity-70 block mt-2 mb-1">Link Externo (LinkedIn, Portfólio)</label>
                                                <Input placeholder="https://..." value={resumeForm.link} onChange={e => setResumeForm({...resumeForm, link: e.target.value})} className={`h-8 text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`} />
                                                
                                                {resumeForm.id && resumeForm.link && (
                                                    <a href={resumeForm.link} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline mt-2 inline-block">🔗 Acessar Currículo Atual</a>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-end gap-2 mt-6 pt-4 border-t dark:border-slate-700">
                                        <Button variant="secondary" onClick={() => setIsResumeModalOpen(false)} disabled={isUploadingResume} className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}>Cancelar</Button>
                                        <Button onClick={handleSaveResume} disabled={isUploadingResume} className="bg-blue-600 hover:bg-blue-700 text-white">
                                            {isUploadingResume ? <><Loader2 size={16} className="animate-spin mr-2"/> Salvando...</> : "Salvar Candidato"}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {subTab === 'process' && (
                    <div className="flex-1 space-y-4">
                        <div className="flex justify-between items-center">
                            <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Banco de Provas e Testes</h3>
                            <Button onClick={() => { setTestToEdit(null); setIsTestModalOpen(true); }} icon={Plus} className="bg-indigo-600 text-white shadow-md">Criar Novo Teste</Button>
                        </div>
                        
                        {/* REQUISITO 3: CARDS ESTILO "FAVORITOS" */}
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-5">
                            {(data.recruitmentTests || []).map((test, index) => {
                                const style = TEST_CARD_STYLES[index % TEST_CARD_STYLES.length];
                                
                                return (
                                    <div key={test.id} className={`p-5 rounded-2xl border-0 flex flex-col justify-between transition-all duration-300 hover:-translate-y-1 hover:shadow-xl text-white overflow-hidden relative shadow-md bg-gradient-to-br ${style.gradient}`}>
                                        
                                        <FileText size={80} className="absolute -bottom-4 -right-4 opacity-10 rotate-12 pointer-events-none"/>
                                        
                                        <div className="relative z-10">
                                            <div className="flex justify-between items-start mb-3 gap-2">
                                                <h4 className="font-bold text-lg leading-tight drop-shadow-sm line-clamp-2">{test.title}</h4>
                                                
                                                {/* BOTÃO DE INICIAR/TESTAR (Novo Ícone e Bypass) */}
                                                <button 
                                                    className="w-10 h-10 rounded-full transition-all flex items-center justify-center shrink-0 bg-white/20 hover:bg-white/40 text-white shadow-sm backdrop-blur-md border border-white/10 hover:scale-110"
                                                    onClick={() => setAnsweringTest({ test, step: 'questions', email: 'rh@preview.com', answers: {}, isPreview: true })} 
                                                    title="Visualizar e Testar Prova"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                                                </button>
                                            </div>

                                            <div className="flex flex-wrap gap-2 mt-2">
                                                <span className="text-[9px] font-bold uppercase px-2 py-1 rounded bg-black/20 text-white/90 backdrop-blur-sm border border-white/10 shadow-inner truncate max-w-full">
                                                    {test.departmentName || 'Geral'}
                                                </span>
                                                <span className="text-[9px] font-bold uppercase px-2 py-1 rounded bg-black/20 text-white/90 backdrop-blur-sm border border-white/10 shadow-inner">
                                                    {test.personType || 'Todos'}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className="flex justify-end items-center mt-8 pt-3 border-t border-white/20 relative z-10">
                                            <div className="flex gap-1.5 relative">
                                                
                                                {/* BOTÃO DE EXPORTAR (Apenas Ícone) */}
                                                <button 
                                                    className="text-white bg-white/10 p-2 hover:bg-white/30 rounded-lg transition-colors flex items-center justify-center border border-white/20 shadow-sm" 
                                                    onClick={() => setExportMenuOpen(exportMenuOpen === test.id ? null : test.id)} 
                                                    title="Exportar Prova/Gabarito"
                                                >
                                                    <Download size={14}/> 
                                                </button>

                                                {/* BOTÃO DE COMPARTILHAR (Apenas Ícone) */}
                                                <button 
                                                    className="text-white bg-white/10 p-2 hover:bg-white/30 rounded-lg transition-colors flex items-center justify-center border border-white/20 shadow-sm mr-2" 
                                                    onClick={() => {
                                                        setShareTest(test);
                                                        setAllowedEmails(test.allowedEmails || []);
                                                        setShareFilterDept(''); setShareFilterStatus(''); setShareSearch(''); setIsLinkGenerated(false);
                                                    }} 
                                                    title="Compartilhar Teste com Candidatos (Link / QR Code)"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
                                                </button>
                                                
                                                {/* DROPDOWN DE EXPORTAÇÃO (Com as duas opções de PDF) */}
                                                {exportMenuOpen === test.id && (
                                                    <div className="absolute bottom-full right-16 mb-2 w-48 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl z-50 overflow-hidden flex flex-col animate-fade-in">
                                                        <button onClick={() => { exportTestPDF(test, false); setExportMenuOpen(null); }} className="px-4 py-3 text-xs font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 text-left flex items-center gap-2"><FileText size={14}/> Prova em Branco (PDF)</button>
                                                        <button onClick={() => { exportTestPDF(test, true); setExportMenuOpen(null); }} className="px-4 py-3 text-xs font-bold text-green-600 hover:bg-gray-50 dark:hover:bg-slate-700 text-left flex items-center gap-2 border-t border-gray-100 dark:border-slate-700"><CheckCircle size={14}/> Gabarito (PDF)</button>
                                                    </div>
                                                )}
                                                {exportMenuOpen === test.id && <div className="fixed inset-0 z-40" onClick={() => setExportMenuOpen(null)}></div>}

                                                {/* BOTÃO DE CLONAR */}
                                                <button className="text-white bg-white/10 p-2 hover:bg-white/30 rounded-lg transition-colors border border-white/20 shadow-sm" onClick={() => handleCloneTest(test)} title="Duplicar / Clonar Teste">
                                                    <Copy size={14}/>
                                                </button>

                                                <button className="text-white bg-white/10 p-2 hover:bg-white/30 rounded-lg transition-colors border border-white/20 shadow-sm" onClick={() => handleEditTest(test)} title="Editar Teste">
                                                    <Edit3 size={14}/>
                                                </button>
                                                
                                                <button className="text-white bg-white/10 p-2 hover:bg-red-500/80 rounded-lg transition-colors border border-white/20 shadow-sm" onClick={() => { if(window.confirm("Excluir este teste permanentemente?")) actions.genericDelete('recruitmentTests', test.id); }} title="Excluir Teste">
                                                    <Trash2 size={14}/>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* MODAL NOVO CURRÍCULO */}
                {isResumeModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
                        <div className={`p-6 rounded-xl w-full max-w-md shadow-2xl ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`}>
                            <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><UserPlus size={20}/> Cadastrar Candidato</h3>
                            <div className="space-y-3">
                                <Input placeholder="Nome Completo *" value={resumeForm.name} onChange={e => setResumeForm({...resumeForm, name: e.target.value})} className={isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''} />
                                <Input placeholder="E-mail *" type="email" value={resumeForm.email} onChange={e => setResumeForm({...resumeForm, email: e.target.value})} className={isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''} />
                                
                                <div>
                                    <label className="text-xs font-bold block mb-1 opacity-70">Área de Interesse</label>
                                    <select className={`w-full p-2 rounded border text-sm ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`} value={resumeForm.deptInterest} onChange={e => setResumeForm({...resumeForm, deptInterest: e.target.value})}>
                                        <option value="">Selecione...</option>
                                        {data.departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                                    </select>
                                </div>
                                
                                <Input placeholder="Link LinkedIn / Portfólio" value={resumeForm.link} onChange={e => setResumeForm({...resumeForm, link: e.target.value})} className={isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''} />
                            </div>
                            <div className="flex justify-end gap-2 mt-6">
                                <Button variant="secondary" onClick={() => setIsResumeModalOpen(false)} disabled={isUploadingResume} className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}>Cancelar</Button>
                                <Button onClick={handleSaveResume}>Salvar</Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- MODAL PARA GERAR LINK E DEFINIR ACESSOS (ATUALIZADO) --- */}
                {shareTest && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setShareTest(null)}>
                        <div className={`p-8 rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col md:flex-row gap-8 max-h-[90vh] ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            
                            {/* COLUNA ESQUERDA: Link e QR Code */}
                            <div className="w-full md:w-1/2 flex flex-col items-center text-center">
                                <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-4">
                                    <Users size={32} />
                                </div>
                                
                                <h3 className="font-bold text-xl mb-1 truncate w-full" title={shareTest.title}>{shareTest.title}</h3>
                                <p className={`text-sm mb-6 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Compartilhe este teste com os candidatos.</p>

                                {!isLinkGenerated ? (
                                    // --- MENSAGEM DE BLOQUEIO (ANTES DE SALVAR) ---
                                    <div className={`flex flex-col items-center justify-center flex-1 p-6 w-full rounded-xl border-2 border-dashed ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-slate-50 border-gray-300'}`}>
                                        <Lock size={48} className={`mb-4 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
                                        <p className={`font-bold text-sm mb-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                            Acesso Bloqueado
                                        </p>
                                        <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                                            Selecione os candidatos autorizados na lista ao lado e clique em <b>"Salvar Acessos e Gerar Link"</b> para liberar o QR Code e a URL da prova.
                                        </p>
                                    </div>
                                ) : (
                                    // --- QR CODE E LINK (DEPOIS DE SALVAR) ---
                                    (() => {
                                        let origin = window.location.origin;
                                        if (!origin || origin === 'null' || origin === 'about:blank') origin = 'https://rh.suaempresa.com.br'; 
                                        let publicUrl = `${origin}${window.location.pathname}?publicTest=${shareTest.id}`;
                                        publicUrl = publicUrl.replace('https://https://', 'https://').replace('.googhttps', '.goog');
                                        
                                        const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(publicUrl)}`;

                                        const handleSafeCopy = async () => {
                                            try {
                                                if (navigator.clipboard && window.isSecureContext) {
                                                    await navigator.clipboard.writeText(publicUrl);
                                                    setValidationAlert({ title: "Copiado!", message: "Link copiado para a área de transferência." });
                                                } else { throw new Error("Fallback"); }
                                            } catch (err) {
                                                try {
                                                    const textArea = document.createElement("textarea");
                                                    textArea.value = publicUrl; textArea.style.position = "fixed"; textArea.style.left = "-999999px";
                                                    document.body.appendChild(textArea); textArea.focus(); textArea.select();
                                                    document.execCommand('copy'); textArea.remove();
                                                    setValidationAlert({ title: "Copiado!", message: "Link copiado (Modo de Compatibilidade)." });
                                                } catch (e) {
                                                    setValidationAlert({ title: "Aviso", message: "Por favor, selecione o link e pressione Ctrl+C." });
                                                }
                                            }
                                        };

                                        return (
                                            <div className="w-full flex flex-col items-center animate-fade-in">
                                                <div className="p-4 bg-white rounded-xl shadow-inner border border-gray-200 mb-6">
                                                    <img src={qrCodeUrl} alt="QR Code" className="w-40 h-40" />
                                                </div>
                                                <div className="w-full text-left mb-2">
                                                    <label className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Link Público</label>
                                                </div>
                                                <div className="flex w-full gap-2 mb-4">
                                                    <input 
                                                        type="text" readOnly value={publicUrl} onClick={(e) => e.target.select()}
                                                        className={`flex-1 p-3 rounded-lg border font-mono text-xs outline-none cursor-copy ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-300' : 'bg-gray-50 border-gray-200 text-gray-600'}`}
                                                    />
                                                    <button onClick={handleSafeCopy} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 rounded-lg font-bold transition-colors shadow-md flex items-center gap-2">
                                                        <Copy size={16}/> Copiar
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })()
                                )}
                                
                                <Button variant="secondary" onClick={() => setShareTest(null)} className={`w-full mt-auto ${isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}`}>Fechar Painel</Button>
                            </div>

                            {/* COLUNA DIREITA: Controle de Acesso */}
                            <div className={`w-full md:w-1/2 flex flex-col h-full pl-0 md:pl-8 border-t md:border-t-0 md:border-l pt-6 md:pt-0 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className={`text-sm font-bold uppercase tracking-wider ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                        <Lock size={16} className="inline mr-1 mb-0.5"/> Controle de Acesso
                                    </h4>
                                </div>
                                
                                <p className="text-[11px] mb-3 opacity-70 leading-relaxed">
                                    Selecione quem pode acessar esta prova. <b>Se ninguém for selecionado, a prova será pública</b>.
                                </p>

                                {/* FILTROS */}
                                <div className="flex flex-col gap-2 mb-3 bg-black/5 dark:bg-white/5 p-2 rounded-lg border border-transparent dark:border-slate-700">
                                    <div className="relative">
                                        <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} size={14}/>
                                        <input type="text" placeholder="Buscar candidato por nome ou email..." value={shareSearch} onChange={(e) => setShareSearch(e.target.value)} className={`w-full pl-8 p-1.5 text-xs rounded border outline-none transition-colors ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <select value={shareFilterDept} onChange={(e) => setShareFilterDept(e.target.value)} className={`w-full p-1.5 text-xs rounded border outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}>
                                            <option value="">Todos Deptos</option>
                                            {data.departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                            </select>
                                        <select value={shareFilterStatus} onChange={(e) => setShareFilterStatus(e.target.value)} className={`w-full p-1.5 text-xs rounded border outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}>
                                            <option value="">Todos Status</option>
                                            <option value="Recebido">Recebido</option>
                                            <option value="Em Análise">Em Análise</option>
                                            <option value="Processo Seletivo">Processo Seletivo</option>
                                        </select>
                                    </div>
                                </div>
                                
                                {/* LISTA DE CANDIDATOS */}
                                <div className={`flex-1 min-h-[200px] overflow-y-auto custom-scrollbar border rounded-lg p-2 ${isDarkMode ? 'border-slate-600 bg-slate-900/50' : 'border-gray-200 bg-gray-50'}`}>
                                    {(() => {
                                        const filteredShareResumes = data.resumes.filter(r => {
                                            if (shareFilterDept && r.deptInterest !== shareFilterDept) return false;
                                            if (shareFilterStatus && r.status !== shareFilterStatus) return false;
                                            if (shareSearch) {
                                                const s = shareSearch.toLowerCase();
                                                return (r.name || '').toLowerCase().includes(s) || (r.email || '').toLowerCase().includes(s);
                                            }
                                            return true;
                                        });

                                        if (data.resumes.length === 0) return <p className="text-xs italic opacity-50 p-4 text-center">Nenhum currículo cadastrado.</p>;
                                        if (filteredShareResumes.length === 0) return <p className="text-xs italic opacity-50 p-4 text-center">Nenhum candidato atende aos filtros atuais.</p>;

                                        return filteredShareResumes.map(r => {
                                            const emailLower = r.email.toLowerCase();
                                            const isAllowed = allowedEmails.includes(emailLower);
                                            return (
                                                <label key={r.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors border mb-1 last:mb-0 ${isAllowed ? 'bg-indigo-50 border-indigo-200 dark:bg-indigo-900/30 dark:border-indigo-800 text-indigo-900 dark:text-indigo-200' : 'border-transparent hover:bg-slate-100 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-200'}`}>
                                                    <input
                                                        type="checkbox" 
                                                        className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4 cursor-pointer"
                                                        checked={isAllowed}
                                                        onChange={(e) => {
                                                            if (e.target.checked) setAllowedEmails(prev => [...prev, emailLower]);
                                                            else setAllowedEmails(prev => prev.filter(x => x !== emailLower));
                                                        }}
                                                    />
                                                    <div className="flex flex-col flex-1 overflow-hidden">
                                                        <span className={`text-sm font-bold truncate ${isAllowed ? 'text-indigo-800 dark:text-indigo-300' : ''}`}>{r.name}</span>
                                                        <div className="flex items-center gap-2 text-[10px] opacity-70">
                                                            <span className="truncate max-w-[120px]">{r.email}</span>
                                                            <span>•</span>
                                                            <span className="truncate">{r.status || 'Recebido'}</span>
                                                        </div>
                                                    </div>
                                                </label>
                                            )
                                        });
                                    })()}
                                </div>

                                {/* BOTÃO DE SALVAR */}
                                <div className="mt-4 pt-4 flex justify-between items-center border-t border-gray-200 dark:border-slate-700">
                                    <span className="text-xs font-bold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300 px-3 py-1.5 rounded shrink-0">
                                        {allowedEmails.length} Autorizados
                                    </span>
                                    {/* CORREÇÃO: Adicionado whitespace-nowrap e padding */}
                                    <Button onClick={async () => {
                                        // 1. Salva a liberação no Teste (como já fazia)
                                        await actions.saveItem('recruitmentTests', { ...shareTest, allowedEmails });
                                        setShareTest({ ...shareTest, allowedEmails }); 
                                        setIsLinkGenerated(true); 

                                        // 2. ITEM 5: Adiciona o link ao array do candidato no banco de Resumos
                                        let origin = window.location.origin;
                                        if (!origin || origin === 'null' || origin === 'about:blank') origin = 'https://rh.suaempresa.com.br'; 
                                        let publicUrl = `${origin}${window.location.pathname}?publicTest=${shareTest.id}`;
                                        publicUrl = publicUrl.replace('https://https://', 'https://').replace('.googhttps', '.goog');

                                        for (const email of allowedEmails) {
                                            const candidate = data.resumes.find(r => r.email.toLowerCase() === email.toLowerCase());
                                            if (candidate) {
                                                const currentLinks = candidate.testLinks || [];
                                                // Adiciona se não existir um link idêntico já mapeado
                                                if (!currentLinks.find(l => l.url === publicUrl)) {
                                                    const newLinks = [...currentLinks, { 
                                                        testId: shareTest.id, // <--- ADICIONADO AQUI
                                                        testName: shareTest.title, 
                                                        url: publicUrl, 
                                                        generatedAt: new Date().toISOString() 
                                                    }];
                                                    await actions.saveItem('resumes', { ...candidate, testLinks: newLinks });
                                                }
                                            }
                                        }

                                        setValidationAlert({ title: "Sucesso", message: "Regras salvas! O link foi anexado aos currículos autorizados e está disponível para cópia." });
                                    }} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-md whitespace-nowrap px-6 py-2">
                                        Salvar Acessos e Enviar
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- MODAL PARA VISUALIZAR O TESTE (CANDIDATO OU PREVIEW DO RH) --- */}
                {answeringTest && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setAnsweringTest(null)}>
                        <div className={`rounded-xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh] ${isDarkMode ? 'bg-slate-950 text-slate-200 border border-slate-700' : 'bg-gray-100 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            
                            {/* HEADER IDÊNTICO AO DO CANDIDATO */}
                            <div className={`sticky top-0 z-[100] w-full shadow-xl border-b-4 rounded-t-xl shrink-0 ${isDarkMode ? 'bg-slate-900 border-indigo-500' : 'bg-white border-indigo-600'} flex flex-col transition-colors`}>
                                <div className="flex flex-col md:flex-row justify-between items-center gap-4 p-5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-t-lg">
                                    <div className="flex-1 text-center md:text-left w-full">
                                        <h1 className="text-xl md:text-2xl font-black leading-tight truncate">{answeringTest.test.title}</h1>
                                        <p className="text-xs opacity-90 truncate">{answeringTest.test.description}</p>
                                    </div>
                                    {/* Exibe o badge de PREVIEW se for o RH testando */}
                                    {answeringTest.isPreview && (
                                        <div className="bg-yellow-400 text-yellow-900 text-[10px] font-black uppercase px-3 py-1.5 rounded-full tracking-widest shadow-md">
                                            Modo Visualização
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto p-4 md:p-8 custom-scrollbar">
                                {/* PASSO 1: IDENTIFICAÇÃO (SÓ APARECE SE NÃO FOR PREVIEW) */}
                                {answeringTest.step === 'email' && !answeringTest.isPreview && (
                                    <div className={`max-w-sm mx-auto mt-10 space-y-4 text-center p-8 rounded-2xl shadow-sm border ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
                                        <div className="w-16 h-16 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4"><Users size={32}/></div>
                                        <h4 className="font-bold text-lg">Identificação do Candidato</h4>
                                        <p className="text-xs opacity-70 mb-4">Informe o e-mail cadastrado no seu currículo para iniciar o teste.</p>
                                        <Input 
                                            type="email" 
                                            placeholder="seu@email.com" 
                                            className={`text-center ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}`}
                                            value={answeringTest.email} 
                                            onChange={e => setAnsweringTest({...answeringTest, email: e.target.value})}
                                        />
                                        <div className="flex gap-2 justify-center mt-6">
                                            <Button variant="secondary" onClick={() => setAnsweringTest(null)}>Cancelar</Button>
                                            <Button onClick={() => {
                                                const resume = data.resumes.find(r => r.email.toLowerCase() === answeringTest.email.toLowerCase());
                                                if (!resume) return setValidationAlert({ title: "Atenção", message: "E-mail não encontrado! Por favor, cadastre o currículo primeiro." });
                                                const draftAnswers = resume.drafts?.[answeringTest.test.id] || {};
                                                setAnsweringTest({ ...answeringTest, step: 'questions', answers: draftAnswers, resumeId: resume.id });
                                            }}>Iniciar Teste</Button>
                                        </div>
                                    </div>
                                )}

                                {/* PASSO 2: PERGUNTAS */}
                                {answeringTest.step === 'questions' && (
                                    <div className="space-y-6">
                                        {(answeringTest.test.sections || []).map((sec, sIdx) => {
                                            const groupColorClass = sIdx % 2 === 0 
                                                ? (isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-blue-50 border-blue-200') 
                                                : (isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-emerald-50 border-emerald-200');
                                            const textColorClass = sIdx % 2 === 0 
                                                ? (isDarkMode ? 'text-blue-400' : 'text-blue-800') 
                                                : (isDarkMode ? 'text-emerald-400' : 'text-emerald-800');
                                            const contentBgClass = isDarkMode ? 'bg-slate-950' : 'bg-white';

                                            let qCounter = 1;

                                            return (
                                                <div key={sec.id || sIdx} className={`rounded-xl border-2 shadow-sm overflow-hidden ${groupColorClass}`}>
                                                    <div className="p-4 flex justify-between items-center">
                                                        <h4 className={`text-lg font-black ${textColorClass}`}>{sIdx + 1}. {sec.title}</h4>
                                                    </div>

                                                    <div className={`p-4 md:p-8 space-y-8 border-t-2 border-inherit ${contentBgClass}`}>
                                                        {(sec.questions || []).map((q) => {
                                                            
                                                            // CONTEXTO
                                                            if (q.type === 'context') {
                                                                return (
                                                                    <div key={q.id} className={`p-6 rounded-xl border-2 border-dashed shadow-md mb-8 mt-4 break-inside-avoid ${isDarkMode ? 'border-indigo-800 bg-indigo-950/30' : 'border-indigo-300 bg-indigo-50/50'}`}>
                                                                        {q.referenceInfo && <h4 className={`font-bold uppercase tracking-wider mb-3 text-sm ${isDarkMode ? 'text-indigo-400' : 'text-indigo-700'}`}>{q.referenceInfo}</h4>}
                                                                        {q.text && <div className={`whitespace-pre-wrap leading-relaxed text-base mb-4 ${isDarkMode ? 'text-slate-300' : 'text-gray-800'}`}>{q.text}</div>}
                                                                        {q.imageUrl && <img src={q.imageUrl} alt="Contexto Base" className={`max-w-full rounded-lg shadow border mt-2 mx-auto ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`} />}
                                                                    </div>
                                                                );
                                                            }

                                                            // PERGUNTA NORMAL
                                                            const currentQNum = qCounter++;
                                                            const ans = answeringTest.answers[q.id];
                                                            const isMissing = q.required && !ans?.value && !answeringTest.isPreview;

                                                            return (
                                                                <div key={q.id} className={`p-6 rounded-2xl border-2 transition-all shadow-sm ${isMissing ? (isDarkMode ? 'border-red-800 bg-red-900/20' : 'border-red-300 bg-red-50') : (isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-gray-100 bg-gray-50')}`}>
                                                                    <div className={`font-bold text-lg mb-4 flex gap-2 whitespace-pre-wrap leading-relaxed ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                                                        <span>{currentQNum})</span>
                                                                        {q.type !== 'fill_blank' ? (
                                                                            <span>{q.text} {q.required && <span className="text-red-500 ml-1">*</span>}</span>
                                                                        ) : (
                                                                            <div className="leading-relaxed">
                                                                                {(q.text || '').split(new RegExp("(\\{[^}]+\\})")).map((part, i) => {
                                                                                    if (part.startsWith('{') && part.endsWith('}')) {
                                                                                        const ph = part.slice(1, -1);
                                                                                        return (
                                                                                            <input key={i} type="text" placeholder={ph} 
                                                                                                className={`border-b-4 outline-none px-2 mx-1 text-center font-black min-w-[150px] transition-colors ${isDarkMode ? 'border-indigo-500 bg-slate-800 text-indigo-300 placeholder:text-slate-600 focus:bg-slate-700' : 'border-indigo-500 bg-indigo-100 text-indigo-800 placeholder:text-indigo-300 focus:bg-indigo-200'}`} 
                                                                                                value={(answeringTest.answers[q.id]?.value || {})[i] || ''} 
                                                                                                onChange={e => setAnsweringTest(prev => ({ ...prev, answers: { ...prev.answers, [q.id]: { ...prev.answers[q.id], value: { ...prev.answers[q.id]?.value, [i]: e.target.value } } } }))} 
                                                                                            />
                                                                                        );
                                                                                    }
                                                                                    return <span key={i}>{part}</span>;
                                                                                })}
                                                                                {q.required && <span className="text-red-500 ml-1">*</span>}
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    {q.imageUrl && (
                                                                        <div className="mb-6 border-l-4 border-indigo-400 pl-4 py-2">
                                                                            <img src={q.imageUrl} alt="Ilustração da Questão" className={`max-w-full max-h-80 rounded-lg shadow-md border ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`} />
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {/* DISCURSIVA */}
                                                                    {q.type === 'text' && (
                                                                        <textarea className={`w-full p-4 rounded-xl border-2 outline-none focus:border-indigo-500 shadow-inner transition-colors ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500 focus:bg-slate-900' : 'bg-white border-gray-200 text-gray-800'}`} rows={4} placeholder="Digite sua resposta detalhada..." value={answeringTest.answers[q.id]?.value || ''} onChange={e => setAnsweringTest(prev => ({ ...prev, answers: { ...prev.answers, [q.id]: { ...prev.answers[q.id], value: e.target.value } } }))}></textarea>
                                                                    )}

                                                                    {/* MULTIPLA ESCOLHA */}
                                                                    {q.type === 'multi' && (
                                                                        <div className="grid grid-cols-1 gap-3">
                                                                            {(q.options || []).map((opt, oIdx) => {
                                                                                const isChecked = q.allowMultiple ? (answeringTest.answers[q.id]?.value || []).includes(opt) : answeringTest.answers[q.id]?.value === opt;
                                                                                return (
                                                                                    <label key={oIdx} className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${isChecked ? (isDarkMode ? 'bg-indigo-900/30 border-indigo-500 ring-1 ring-indigo-500 shadow-md' : 'bg-indigo-50 border-indigo-500 ring-1 ring-indigo-500 shadow-md') : (isDarkMode ? 'bg-slate-800 border-slate-700 hover:border-indigo-500' : 'bg-white border-gray-200 hover:border-indigo-200')}`}>
                                                                                        <input type={q.allowMultiple ? "checkbox" : "radio"} className="hidden" checked={isChecked} onChange={() => {
                                                                                            let val = q.allowMultiple ? (isChecked ? (answeringTest.answers[q.id]?.value || []).filter(x => x !== opt) : [...(answeringTest.answers[q.id]?.value || []), opt]) : opt;
                                                                                            setAnsweringTest(prev => ({ ...prev, answers: { ...prev.answers, [q.id]: { ...prev.answers[q.id], value: val } } }));
                                                                                        }} />
                                                                                        <div className={`w-6 h-6 flex items-center justify-center border-2 ${q.allowMultiple ? 'rounded-lg' : 'rounded-full'} ${isChecked ? 'bg-indigo-600 border-indigo-600 text-white' : (isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-100 border-gray-300')}`}>
                                                                                            {isChecked && (q.allowMultiple ? <Check size={14} strokeWidth={3}/> : <div className={`w-2.5 h-2.5 rounded-full ${isDarkMode ? 'bg-white' : 'bg-white'}`}></div>)}
                                                                                        </div>
                                                                                        <span className={`text-base font-medium ${isChecked ? (isDarkMode ? 'text-indigo-300' : 'text-indigo-900') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}>{opt}</span>
                                                                                    </label>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}

                                                                    {/* V/F */}
                                                                    {q.type === 'true_false' && (
                                                                        <div className="space-y-3">
                                                                            {(q.options || []).map((opt, oIdx) => {
                                                                                const currentArr = Array.isArray(answeringTest.answers[q.id]?.value) ? answeringTest.answers[q.id].value : [];
                                                                                const ansVal = currentArr[oIdx];
                                                                                return (
                                                                                    <div key={oIdx} className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl border transition-all ${ansVal ? (isDarkMode ? 'border-indigo-700 bg-indigo-900/20' : 'border-indigo-300 bg-indigo-50/30') : (isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-white')}`}>
                                                                                        <span className={`text-sm font-medium flex-1 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{opt}</span>
                                                                                        <div className="flex gap-2 shrink-0">
                                                                                            <label className={`px-4 py-2 rounded-lg border cursor-pointer font-bold text-xs transition-colors flex items-center gap-1 ${ansVal === 'V' ? (isDarkMode ? 'bg-green-900/30 text-green-400 border-green-600' : 'bg-green-100 text-green-700 border-green-500') : (isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-600 hover:bg-slate-700' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100')}`}>
                                                                                                <input type="radio" className="hidden" checked={ansVal === 'V'} onChange={() => {
                                                                                                    const newVal = [...currentArr]; newVal[oIdx] = 'V';
                                                                                                    setAnsweringTest(prev => ({ ...prev, answers: { ...prev.answers, [q.id]: { ...prev.answers[q.id], value: newVal } } }));
                                                                                                }} />
                                                                                                Verdadeiro
                                                                                            </label>
                                                                                            <label className={`px-4 py-2 rounded-lg border cursor-pointer font-bold text-xs transition-colors flex items-center gap-1 ${ansVal === 'F' ? (isDarkMode ? 'bg-red-900/30 text-red-400 border-red-600' : 'bg-red-100 text-red-700 border-red-500') : (isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-600 hover:bg-slate-700' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100')}`}>
                                                                                                <input type="radio" className="hidden" checked={ansVal === 'F'} onChange={() => {
                                                                                                    const newVal = [...currentArr]; newVal[oIdx] = 'F';
                                                                                                    setAnsweringTest(prev => ({ ...prev, answers: { ...prev.answers, [q.id]: { ...prev.answers[q.id], value: newVal } } }));
                                                                                                }} />
                                                                                                Falso
                                                                                            </label>
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}

                                                                    {/* PLANILHA */}
                                                                    {q.type === 'spreadsheet' && (
                                                                        <div className="overflow-x-auto custom-scrollbar border rounded-xl shadow-sm mt-3">
                                                                            <table className="w-full border-collapse">
                                                                                <tbody>
                                                                                    {Array.from({length: q.tableConfig?.rows || 1}).map((_, r) => (
                                                                                        <tr key={r}>
                                                                                            {Array.from({length: q.tableConfig?.cols || 1}).map((_, c) => {
                                                                                                const cellId = `${r}-${c}`;
                                                                                                const cell = q.tableConfig?.cells?.[cellId] || { type: 'label', value: '' };
                                                                                                return (
                                                                                                    <td key={c} className={`p-3 border align-middle ${isDarkMode ? 'border-slate-600' : 'border-gray-200'} ${cell.type === 'label' ? (isDarkMode ? 'bg-slate-800' : 'bg-gray-50') : 'bg-white dark:bg-slate-900'}`}>
                                                                                                        {cell.type === 'label' ? (
                                                                                                            <div className={`text-sm px-2 ${cell.isBold ? 'font-black' : 'font-medium'} ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{cell.value}</div>
                                                                                                        ) : (
                                                                                                            <input type="text" className={`w-full min-w-[120px] p-2.5 rounded border-2 outline-none focus:border-indigo-500 text-center shadow-inner ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                                                                                                                value={answeringTest.answers[q.id]?.value?.[cellId] || ''}
                                                                                                                onChange={e => {
                                                                                                                    const newVal = { ...(answeringTest.answers[q.id]?.value || {}) };
                                                                                                                    newVal[cellId] = e.target.value;
                                                                                                                    setAnsweringTest(prev => ({ ...prev, answers: { ...prev.answers, [q.id]: { ...prev.answers[q.id], value: newVal } } }));
                                                                                                                }} placeholder="____" />
                                                                                                        )}
                                                                                                    </td>
                                                                                                );
                                                                                            })}
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
    {/* PASSO 3: RESULTADOS DO PREVIEW (VALIDAÇÃO DE IA) */}
                                {answeringTest.step === 'preview_results' && (
                                    <div className="space-y-6 animate-fade-in p-2 md:p-6">
                                        <div className={`p-6 rounded-2xl border-2 text-center shadow-lg ${isDarkMode ? 'bg-slate-900 border-indigo-500' : 'bg-indigo-50 border-indigo-400'}`}>
                                            <div className="w-16 h-16 bg-indigo-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md"><Bot size={32}/></div>
                                            <h3 className="text-2xl font-black mb-2 text-indigo-600 dark:text-indigo-400">Resultado da Validação IA</h3>
                                            <p className="text-sm opacity-80 mb-6">Esta é uma simulação exata de como o sistema corrigirá as respostas do candidato real baseada no seu gabarito.</p>
                                            <div className="inline-flex flex-col items-center justify-center px-8 py-4 bg-white dark:bg-slate-800 rounded-xl shadow-inner border dark:border-slate-700">
                                                <span className="text-[10px] uppercase font-bold text-gray-500">Nota Final Simulada</span>
                                                <span className="text-4xl font-black text-green-600">{answeringTest.previewData.score} <span className="text-lg text-gray-400">/ {answeringTest.previewData.total}</span></span>
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <h4 className="font-bold text-lg border-b pb-2 dark:border-slate-700">Análise Detalhada por Questão</h4>
                                            {Object.values(answeringTest.previewData.details).map((res, i) => (
                                                <div key={i} className={`p-4 rounded-xl border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                                    <div className="flex justify-between items-start mb-3 gap-4">
                                                        <span className="font-bold text-sm flex-1">{res.questionText}</span>
                                                        <span className={`px-2 py-1 rounded text-xs font-black shrink-0 border ${res.score >= res.maxPoints ? 'bg-green-100 text-green-700 border-green-200' : res.score > 0 ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-red-100 text-red-700 border-red-200'}`}>
                                                            {res.score} / {res.maxPoints} pts
                                                        </span>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                                        <div className="p-3 bg-gray-50 dark:bg-slate-900 rounded border dark:border-slate-700">
                                                            <span className="block text-[10px] uppercase font-bold text-gray-500 mb-1">O que você respondeu:</span>
                                                            <span className="font-medium whitespace-pre-wrap">{typeof res.userAnswer === 'object' ? JSON.stringify(res.userAnswer) : String(res.userAnswer || 'Vazio')}</span>
                                                        </div>
                                                        <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded border border-indigo-100 dark:border-indigo-800/50">
                                                            <span className="block text-[10px] uppercase font-bold text-indigo-500 mb-1 flex items-center gap-1"><Sparkles size={12}/> Feedback da IA / Sistema:</span>
                                                            <span className="font-medium whitespace-pre-wrap leading-relaxed">{res.aiFeedback}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                            {/* FOOTER DO MODAL DE RESPOSTA */}
                            <div className={`p-5 border-t flex justify-end gap-3 shrink-0 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                <Button variant="secondary" onClick={() => setAnsweringTest(null)} disabled={isEvaluatingTest}>Fechar</Button>
                                
                                {answeringTest.step === 'questions' && (
                                    <>
                                        <Button 
                                            variant="secondary"
                                            disabled={isEvaluatingTest}
                                            className="border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                            onClick={async () => {
                                                setIsEvaluatingTest(true);
                                                try {
                                                    const resume = data.resumes.find(r => r.id === answeringTest.resumeId);
                                                    const updatedDrafts = { ...(resume.drafts || {}), [answeringTest.test.id]: answeringTest.answers };
                                                    const updatedResume = { ...resume, drafts: updatedDrafts };
                                                    
                                                    await actions.saveItem('resumes', updatedResume);
                                                    setValidationAlert({ title: "Salvo com sucesso!", message: "Seu progresso foi salvo. Você pode fechar e continuar mais tarde." });
                                                } catch (e) {
                                                    setValidationAlert({ title: "Erro", message: "Falha ao salvar rascunho." });
                                                } finally {
                                                    setIsEvaluatingTest(false);
                                                }
                                            }}
                                        >
                                            <Save size={16} className="mr-2"/> Salvar Progresso
                                        </Button>

                                        <Button 
                                            disabled={isEvaluatingTest}
                                            className="bg-indigo-600 text-white hover:bg-indigo-700 px-6 flex items-center gap-2 border-0 shadow-md"
                                            onClick={async () => {
                                                setIsEvaluatingTest(true);
                                                try {
                                                    let finalScore = 0;
                                                    let finalTotalPossible = 0;
                                                    let detailedResults = {};

                                                    // MOTOR DE CORREÇÃO (SIMULAÇÃO COM REGRAS REAIS)
                                                    for (const sec of answeringTest.test.sections) {
                                                        for (const q of sec.questions) {
                                                            if (q.type === 'context') continue;

                                                            const answerObj = answeringTest.answers[q.id];
                                                            const userAnswer = answerObj?.value !== undefined ? answerObj.value : answerObj;
                                                            const maxPts = parseFloat(q.points !== undefined ? q.points : 10) || 0;
                                                            let qScore = 0;
                                                            let aiFeedback = null;

                                                            finalTotalPossible += maxPts;

                                                            let isEmpty = false;
                                                            if (userAnswer === undefined || userAnswer === null || userAnswer === '') isEmpty = true;
                                                            if (Array.isArray(userAnswer) && userAnswer.length === 0) isEmpty = true;
                                                            if (typeof userAnswer === 'object' && userAnswer !== null && !Array.isArray(userAnswer) && q.type !== 'file') {
                                                                if (Object.keys(userAnswer).length === 0) isEmpty = true;
                                                                else if (Object.values(userAnswer).every(v => v === null || v === '' || String(v).trim() === '')) isEmpty = true;
                                                            }

                                                            const expectedGabarito = String(q.correctAnswer || '').trim();
                                                            const isLivre = expectedGabarito === '' || expectedGabarito.toLowerCase() === 'livre' || expectedGabarito.toLowerCase() === 'pessoal';

                                                            if (isEmpty && !answerObj?.isOtherChecked) {
                                                                qScore = 0;
                                                                aiFeedback = "Questão não respondida.";
                                                            } 
                                                            else if (isLivre && q.type !== 'multi' && q.type !== 'true_false' && q.type !== 'file' && q.type !== 'text') {
                                                                qScore = maxPts;
                                                                aiFeedback = "Aprovado: Sem gabarito restrito.";
                                                            }
                                                            else if (q.type === 'multi') {
                                                                if (q.allowMultiple) {
                                                                    const expected = Array.isArray(q.correctAnswer) ? q.correctAnswer : [];
                                                                    const userArr = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
                                                                    if (expected.length > 0) {
                                                                        let correctHits = 0, wrongHits = 0;
                                                                        userArr.forEach(ans => { if (expected.includes(ans)) correctHits++; else wrongHits++; });
                                                                        qScore = (Math.max(0, correctHits - wrongHits) / expected.length) * maxPts;
                                                                        aiFeedback = `Múltipla Escolha: Acertou ${correctHits} e errou ${wrongHits}.`;
                                                                    }
                                                                } else {
                                                                    if (String(userAnswer).trim() === String(q.correctOption || q.correctAnswer).trim()) {
                                                                        qScore = maxPts;
                                                                        aiFeedback = "Resposta exata.";
                                                                    } else {
                                                                        qScore = 0;
                                                                        aiFeedback = "Resposta incorreta.";
                                                                    }
                                                                }
                                                            } 
                                                            else if (q.type === 'true_false') {
                                                                const expected = Array.isArray(q.correctAnswer) ? q.correctAnswer : [];
                                                                const userArr = Array.isArray(userAnswer) ? userAnswer : [];
                                                                if (expected.length > 0) {
                                                                    let hits = 0;
                                                                    expected.forEach((ans, idx) => { if (userArr[idx] === ans) hits++; });
                                                                    qScore = (hits / expected.length) * maxPts;
                                                                    aiFeedback = `V/F: Acertou ${hits} de ${expected.length}.`;
                                                                }
                                                            } 
                                                            else if (q.type === 'spreadsheet' || q.type === 'text' || q.type === 'fill_blank') {
                                                                const parseNum = (str) => {
                                                                    if (!str) return null;
                                                                    let clean = String(str).replace(/[^\d.,-]/g, '').trim();
                                                                    if (clean === '') return null;
                                                                    if (clean.includes(',') && clean.includes('.')) clean = clean.lastIndexOf(',') > clean.lastIndexOf('.') ? clean.replace(/\./g, '').replace(',', '.') : clean.replace(/,/g, '');
                                                                    else if (clean.includes(',')) clean = clean.replace(',', '.');
                                                                    const num = parseFloat(clean);
                                                                    return isNaN(num) ? null : num;
                                                                };

                                                                const normalizeText = (str) => String(str).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

                                                                const isFlexibleMatch = (answeredRaw, expectedRaw, format = 'text') => {
                                                                    if (!expectedRaw || expectedRaw.toLowerCase() === 'livre') return String(answeredRaw).trim() !== ''; 
                                                                    if (format === 'number') {
                                                                        const nAns = parseNum(answeredRaw); const nExp = parseNum(expectedRaw);
                                                                        if (nAns !== null && nExp !== null && nAns === nExp) return true;
                                                                    }
                                                                    const ansNorm = normalizeText(answeredRaw); 
                                                                    const expNorm = normalizeText(expectedRaw);
                                                                    if (ansNorm === expNorm) return true;
                                                                    
                                                                    const numAns = ansNorm.replace(/\D/g, '');
                                                                    const numExp = expNorm.replace(/\D/g, '');
                                                                    if (numAns !== '' && numExp !== '' && numAns === numExp && ansNorm.length < 30 && format !== 'text') return true;

                                                                    const options = expNorm.split(/\s+ou\s+|\/|\||,|;/).map(s => s.trim()).filter(Boolean);
                                                                    return options.some(opt => ansNorm === opt);
                                                                };

                                                                if (q.type === 'spreadsheet') {
                                                                    const cells = q.tableConfig?.cells || {};
                                                                    let totalInputs = 0, correctInputs = 0;
                                                                    Object.keys(cells).forEach(cellId => {
                                                                        const cell = cells[cellId];
                                                                        if (cell.type === 'input') {
                                                                            totalInputs++;
                                                                            if (isFlexibleMatch(userAnswer ? userAnswer[cellId] : '', cell.expected, cell.format)) correctInputs++;
                                                                        }
                                                                    });
                                                                    qScore = totalInputs > 0 ? (correctInputs / totalInputs) * maxPts : maxPts;
                                                                    aiFeedback = totalInputs > 0 ? `Planilha: ${correctInputs} de ${totalInputs} corretas.` : "Sem gabarito estrito.";
                                                                } else {
                                                                    let userPlainStr = q.type === 'fill_blank' 
                                                                        ? String(q.text).replace(/(\{.*?\})/g, (() => { let i = 0; return () => userAnswer?.[i++] || ''; })()) + (typeof userAnswer === 'object' ? " " + Object.values(userAnswer).join(" ") : "")
                                                                        : String(userAnswer);
                                                                    
                                                                    if (answerObj?.justification) userPlainStr += `\nJustificativa: ${answerObj.justification}`;

                                                                    if (isLivre && q.type === 'text') {
                                                                        const systemPrompt = `Você é um Avaliador implacável. A questão NÃO possui gabarito fixo. Avalie se a RESPOSTA atende à PERGUNTA. Dê nota parcial se incompleta. RETORNE JSON: {"percentual": numero_de_0_a_1, "analise": "análise curta"}`;
                                                                        try {
                                                                            const aiRes = await callGeminiAI(`PERGUNTA: "${q.text}"\nRESPOSTA DO CANDIDATO: "${userPlainStr}"`, systemPrompt, 'evaluation');
                                                                            let cleanRes = String(aiRes).replace(/```json/gi, '').replace(/```/g, '').trim();
                                                                            const parsed = JSON.parse(cleanRes.match(/\{[\s\S]*\}/)[0]);
                                                                            qScore = maxPts * Math.max(0, Math.min(1, parseFloat(parsed.percentual) || 0));
                                                                            aiFeedback = parsed.analise || "Avaliado pela IA.";
                                                                        } catch (err) { qScore = maxPts; aiFeedback = "Aprovado por preenchimento."; }
                                                                    }
                                                                    else if (isFlexibleMatch(userPlainStr, expectedGabarito, 'text')) {
                                                                        qScore = maxPts;
                                                                        aiFeedback = "Match Automático (Exato).";
                                                                    } else {
                                                                        const blanksMatch = q.type === 'fill_blank' ? (String(q.text).match(/\{.*?\}/g) || []) : [];
                                                                        const totalBlanks = blanksMatch.length || 1;
                                                                        
                                                                        const systemPrompt = q.type === 'fill_blank' 
                                                                        ? `Você é um Avaliador rigoroso. A questão possui ${totalBlanks} lacuna(s). Avalie CADA lacuna com o GABARITO. Ignore plural/singular. CÁLCULO PROPORCIONAL: Acertou 1 de 2 = 0.5. RETORNE JSON: {"percentual": numero_de_0_a_1, "analise": "análise"}`
                                                                        : `Você é um Avaliador de RH implacável. Corrija a RESPOSTA baseada no GABARITO. FALSOS POSITIVOS: Se o sentido for incorreto, a nota é 0.0. PARCIALIDADE: Se abordar metade, nota 0.5. RETORNE JSON: {"percentual": numero_de_0_a_1, "analise": "Justificativa"}`;

                                                                        try {
                                                                            // --- CORREÇÃO: DELAY DE 4 SEGUNDOS PARA EVITAR ERRO 429 ---
                                                                            await new Promise(resolve => setTimeout(resolve, 4000));

                                                                            const aiRes = await callGeminiAI(`PERGUNTA: "${q.text}"\nGABARITO: "${expectedGabarito}"\nRESPOSTA: "${userPlainStr}"`, systemPrompt, 'evaluation');
                                                                            let cleanRes = String(aiRes).replace(/```json/gi, '').replace(/```/g, '').trim();
                                                                            const parsed = JSON.parse(cleanRes.match(/\{[\s\S]*\}/)[0]);
                                                                            const percent = Math.max(0, Math.min(1, parseFloat(parsed.percentual) || 0));
                                                                            qScore = maxPts * percent;
                                                                            aiFeedback = parsed.analise;
                                                                        } catch (err) {
                                                                            console.error("Erro Crítico IA:", err);
                                                                            
                                                                            // REGRA DE SEGURANÇA: Se a IA falhar, a nota é 0 e marca para revisão.
                                                                            qScore = 0;
                                                                            
                                                                            aiFeedback = "⚠️ FALHA DE PROCESSAMENTO IA: O sistema não conseguiu validar esta resposta automaticamente. REVISÃO MANUAL OBRIGATÓRIA PELO GESTOR.";
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                            else if (q.type === 'file') { qScore = maxPts; aiFeedback = "Arquivo anexado."; }

                                                            finalScore += qScore;
                                                            detailedResults[q.id] = { 
                                                                userAnswer,
                                                                score: parseFloat(qScore.toFixed(2)), 
                                                                maxPoints: maxPts, 
                                                                aiFeedback,
                                                                questionText: q.text
                                                            };
                                                        }
                                                    }

                                                    // EXIBE RESULTADOS NA TELA EM VEZ DE SALVAR
                                                    setAnsweringTest(prev => ({
                                                        ...prev,
                                                        step: 'preview_results',
                                                        previewData: {
                                                            score: parseFloat(finalScore.toFixed(2)),
                                                            total: finalTotalPossible,
                                                            details: detailedResults
                                                        }
                                                    }));

                                                } catch(e) {
                                                    setValidationAlert({ title: "Erro na Validação", message: "Falha: " + e.message });
                                                } finally {
                                                    setIsEvaluatingTest(false);
                                                }
                                            }} 
                                        >
                                            {isEvaluatingTest ? <><Loader2 size={16} className="animate-spin"/> Validando IA...</> : <><Sparkles size={16}/> Finalizar e Corrigir</>}
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* --- MODAL PARA VISUALIZAR OS TESTES FEITOS NO CURRÍCULO --- */}
                {viewingResumeTests && (
                    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setViewingResumeTests(null)}>
                        <div className={`rounded-xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh] ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            
                            <div className={`p-5 border-b flex justify-between items-center shrink-0 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                <h3 className="font-bold text-lg flex items-center gap-2"><FileText size={20} className="text-indigo-600"/> Avaliações de: {viewingResumeTests.name}</h3>
                                <button onClick={() => setViewingResumeTests(null)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
                                
                                {/* SEÇÃO 1: ACESSOS E LINKS LIBERADOS */}
                                {viewingResumeTests.testLinks?.length > 0 && (
                                    <div className="space-y-3">
                                        <h4 className={`font-bold text-sm uppercase tracking-wider border-b pb-2 ${isDarkMode ? 'border-slate-700 text-slate-400' : 'border-gray-200 text-gray-500'}`}>
                                            Testes Vinculados ao Candidato
                                        </h4>
                                        <div className="grid grid-cols-1 gap-2">
                                            {viewingResumeTests.testLinks.map((link, idx) => {
                                                const extractTestId = link.testId || (link.url ? link.url.split('publicTest=')[1] : null);
                                                
                                                return (
                                                    <div key={idx} className={`flex items-center justify-between p-3 rounded-lg border ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                        <div className="flex flex-col">
                                                            <span className={`font-bold text-sm ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{link.testName}</span>
                                                            <a href={link.url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-500 hover:underline truncate max-w-[200px]">{link.url}</a>
                                                        </div>
                                                        <Button 
                                                            onClick={() => handleLiberateTest(viewingResumeTests, extractTestId)}
                                                            size="sm"
                                                            icon={Unlock}
                                                            className="bg-blue-600 hover:bg-blue-700 text-white border-0 text-[10px] uppercase font-bold shadow-md transition-colors"
                                                        >
                                                            Liberar Nova Tentativa
                                                        </Button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* SEÇÃO 2: TENTATIVAS REALIZADAS */}
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center border-b pb-2 dark:border-slate-700">
                                        <h4 className={`font-bold text-sm uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                            Tentativas Finalizadas
                                        </h4>
                                        <span className="text-[10px] text-gray-400">Clique na tentativa para expandir</span>
                                    </div>
                                    
                                    {(!viewingResumeTests.testsTaken || viewingResumeTests.testsTaken.length === 0) ? (
                                        <div className="text-center p-6 text-gray-400 border border-dashed rounded-lg dark:border-slate-700">Nenhum teste finalizado por este candidato.</div>
                                    ) : (
                                        viewingResumeTests.testsTaken.map((tResult, idx) => {
                                            const originalTest = data.recruitmentTests.find(t => t.id === tResult.testId);
                                            const isTestExpanded = expandedTestsRH[idx] !== false; // Expansivo por padrão

                                            return (
                                                <div key={idx} className={`rounded-xl border shadow-sm overflow-hidden transition-all ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
                                                    
                                                    {/* HEADER DA TENTATIVA (CLICÁVEL PARA EXPANDIR) */}
                                                    <div 
                                                        className={`p-4 flex justify-between items-center cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-50'}`}
                                                        onClick={() => setExpandedTestsRH(prev => ({ ...prev, [idx]: !isTestExpanded }))}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className="p-1 rounded-full bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                                                                {isTestExpanded ? <ChevronDown size={18}/> : <ChevronRight size={18}/>}
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="bg-indigo-600 text-white text-[10px] font-black px-2 py-0.5 rounded">Tentativa {idx + 1}</span>
                                                                    <h4 className="font-bold text-base text-indigo-600 dark:text-indigo-400">{tResult.testTitle}</h4>
                                                                </div>
                                                                <p className="text-xs opacity-60 flex items-center gap-1 mt-1"><Clock size={12}/> {new Date(tResult.date).toLocaleString('pt-BR')}</p>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="flex gap-4 items-center">
                                                            <div className="bg-green-100 text-green-700 font-black px-4 py-1.5 rounded-lg border border-green-200 flex flex-col items-center">
                                                                <span className="text-[9px] uppercase">Nota</span>
                                                                <span className="text-lg leading-none mt-1">{tResult.score} pts</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* CORPO EXPANSÍVEL DA TENTATIVA */}
                                                    {isTestExpanded && (
                                                        <div className="p-4 border-t border-gray-100 dark:border-slate-700 bg-black/5 dark:bg-white/5">
                                                            
                                                            {/* BARRA DE AÇÕES DA TENTATIVA */}
                                                            <div className="flex justify-end gap-2 mb-4">
                                                                <button 
                                                                    onClick={(e) => { e.stopPropagation(); handleReprocessTest(viewingResumeTests, idx, originalTest); }} 
                                                                    disabled={isReprocessing || !originalTest}
                                                                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors text-xs font-bold shadow-md disabled:opacity-50"
                                                                >
                                                                    {isReprocessing ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>} 
                                                                    Reprocessar Correção
                                                                </button>
                                                                <button 
                                                                    onClick={(e) => { e.stopPropagation(); handleDeleteAttempt(viewingResumeTests, idx); }} 
                                                                    className="flex items-center gap-1 text-red-600 hover:text-red-700 bg-white dark:bg-slate-800 border border-red-200 dark:border-red-800 px-3 py-1.5 rounded-lg transition-colors text-xs font-bold shadow-sm"
                                                                >
                                                                    <Trash2 size={14}/> Excluir
                                                                </button>
                                                            </div>

                                                            {originalTest && tResult.details ? (
                                                                <div className="space-y-4">
                                                                    {originalTest.sections.map((sec, sIdx) => {
                                                                        const secKey = `${idx}-${sec.id}`;
                                                                        const isSecExpanded = expandedSectionsRH[secKey] !== false; // Sections expandidas por padrão
                                                                        const gradient = SECTION_GRADIENTS[sIdx % SECTION_GRADIENTS.length];

                                                                        return (
                                                                        <div key={sec.id} className="rounded-xl overflow-hidden border border-gray-200 dark:border-slate-700 shadow-sm bg-white dark:bg-slate-900">
                                                                            
                                                                            {/* HEADER DO GRUPO (COLORIDO E CLICÁVEL) */}
                                                                            <div 
                                                                                className={`p-4 bg-gradient-to-r ${gradient} text-white cursor-pointer flex justify-between items-center transition-opacity hover:opacity-90`}
                                                                                onClick={() => setExpandedSectionsRH(prev => ({ ...prev, [secKey]: !isSecExpanded }))}
                                                                            >
                                                                                <h5 className="font-bold text-sm uppercase flex items-center gap-2">
                                                                                    {isSecExpanded ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                                                                                    {sIdx + 1}. {sec.title}
                                                                                </h5>
                                                                            </div>
                                                                            
                                                                            {isSecExpanded && (
                                                                                <div className="p-4 space-y-6">
                                                                                    {sec.questions.map((q, qIdx) => {
                                                                                        if (q.type === 'context') return null;

                                                                                        const result = tResult.details ? tResult.details[q.id] : null;
                                                                                        if (!result) return null;

                                                                                        const isFull = result.score >= result.maxPoints;
                                                                                        const isPartial = result.score > 0 && result.score < result.maxPoints;
                                                                                        const statusColor = isFull ? 'text-green-700 bg-green-100 border-green-300' : isPartial ? 'text-orange-700 bg-orange-100 border-orange-300' : 'text-red-700 bg-red-100 border-red-300';
                                                                                        const statusLabel = isFull ? 'Correta' : isPartial ? 'Parcial' : 'Incorreta';
                                                                                        
                                                                                        // Renderização de Respostas Aninhadas
                                                                                        let expectedDisplay = Array.isArray(q.correctAnswer) ? q.correctAnswer.join(', ') : (q.correctAnswer || 'Livre');
                                                                                        const actualAnswerObj = (typeof result.userAnswer === 'object' && result.userAnswer !== null && result.userAnswer.value !== undefined) ? result.userAnswer.value : result.userAnswer;
                                                                                        let displayAnswer = actualAnswerObj;

                                                                                        if (q.type === 'fill_blank') {
                                                                                            if (typeof actualAnswerObj === 'object' && actualAnswerObj !== null) displayAnswer = Object.entries(actualAnswerObj).map(([k, v]) => `Lacuna ${parseInt(k)+1}: ${v}`).join(' | ');
                                                                                            else displayAnswer = String(actualAnswerObj || '-');
                                                                                        } else if (q.type === 'multi' && Array.isArray(actualAnswerObj)) {
                                                                                            displayAnswer = actualAnswerObj.join(', ');
                                                                                        } else if (q.type === 'true_false' && Array.isArray(q.options)) {
                                                                                            expectedDisplay = q.options.map((opt, i) => `[${q.correctAnswer?.[i] || '-'}] ${opt}` + (q.correctJustification?.[i] ? ` \n↳ Just. (Gabarito): ${q.correctJustification[i]}` : '')).join('\n\n');
                                                                                            displayAnswer = q.options.map((opt, i) => `[${actualAnswerObj?.[i] || '-'}] ${opt}` + (result.justifications?.[i] ? ` \n↳ Just. (Candidato): ${result.justifications[i]}` : '')).join('\n\n');
                                                                                        } else if (q.type === 'spreadsheet') {
                                                                                            const cells = q.tableConfig?.cells || {};
                                                                                            let expectedArr = []; let userArr = [];
                                                                                            const sheetData = typeof actualAnswerObj === 'object' && actualAnswerObj !== null ? actualAnswerObj : {};
                                                                                            
                                                                                            const sortedKeys = Object.keys(cells).sort((a, b) => {
                                                                                                const [rA, cA] = a.split('-').map(Number);
                                                                                                const [rB, cB] = b.split('-').map(Number);
                                                                                                if (rA !== rB) return rA - rB; return cA - cB;
                                                                                            });

                                                                                            sortedKeys.forEach(cellId => {
                                                                                                if (cells[cellId].type === 'input') {
                                                                                                    const parts = cellId.split('-');
                                                                                                    const cellName = `L${parseInt(parts[0])+1}, C${parseInt(parts[1])+1}`;
                                                                                                    expectedArr.push(`[${cellName}] ➜ Esperado: ${cells[cellId].expected || 'Livre'}`);
                                                                                                    userArr.push(`[${cellName}] ➜ Preenchido: ${sheetData[cellId] || '-'}`);
                                                                                                }
                                                                                            });
                                                                                            expectedDisplay = expectedArr.join('\n'); displayAnswer = userArr.join('\n');
                                                                                        } else if (q.type === 'file') {
                                                                                            if (actualAnswerObj && actualAnswerObj.name) displayAnswer = `📎 Arquivo Anexado: ${actualAnswerObj.name}`;
                                                                                            else displayAnswer = 'Nenhum arquivo válido anexado.';
                                                                                        }

                                                                                        if (typeof displayAnswer === 'object' && displayAnswer !== null && !React.isValidElement(displayAnswer)) {
                                                                                            try { displayAnswer = JSON.stringify(displayAnswer, null, 2); } catch (e) { displayAnswer = 'Formato de resposta incompatível.'; }
                                                                                        }

                                                                                        return (
                                                                                            <div key={q.id} className={`p-4 rounded-xl border-2 transition-all shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                                                                <div className="flex justify-between items-start gap-4">
                                                                                                    <div className="font-bold flex-1 flex flex-wrap items-center gap-2">
                                                                                                        <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs shrink-0 ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-white border text-gray-500'}`}>{qIdx+1}</span>
                                                                                                        <span className={isDarkMode ? 'text-slate-200' : 'text-gray-800'}>{q.text}</span>
                                                                                                        <span className={`text-[9px] px-2 py-0.5 rounded border uppercase tracking-wider font-black ${statusColor}`}>
                                                                                                            {statusLabel}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                    
                                                                                                    {/* EDIÇÃO DA NOTA DO TESTE */}
                                                                                                    <div className="flex flex-col items-end gap-1 shrink-0">
                                                                                                        <div className="flex items-center gap-1">
                                                                                                            <input 
                                                                                                                type="number" min="0" max={result.maxPoints} step="0.1"
                                                                                                                value={result.score}
                                                                                                                onChange={(e) => {
                                                                                                                    const newScore = parseFloat(e.target.value) || 0;
                                                                                                                    setViewingResumeTests(prev => {
                                                                                                                        const updated = { ...prev };
                                                                                                                        const testTarget = { ...updated.testsTaken[idx] };
                                                                                                                        testTarget.details[q.id].score = newScore;
                                                                                                                        let newTotal = 0;
                                                                                                                        Object.values(testTarget.details).forEach(det => newTotal += det.score);
                                                                                                                        testTarget.score = parseFloat(newTotal.toFixed(2));
                                                                                                                        updated.testsTaken[idx] = testTarget;
                                                                                                                        return updated;
                                                                                                                    });
                                                                                                                }}
                                                                                                                className={`w-16 p-1 text-sm font-bold text-center border rounded outline-none focus:ring-2 focus:ring-indigo-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                                                                                                            />
                                                                                                            <span className="text-xs font-bold text-gray-500">/ {result.maxPoints} pts</span>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </div>
                                                                                                
                                                                                                <div className="mt-4 flex flex-col gap-3 text-xs">
        
                                                                                                {/* BOX DA RESPOSTA DO CANDIDATO */}
                                                                                                <div className={`p-4 rounded-lg border-2 shadow-sm ${
                                                                                                    isFull ? 'bg-green-50 border-green-300 dark:bg-green-900/20 dark:border-green-800' : 
                                                                                                    isPartial ? 'bg-orange-50 border-orange-300 dark:bg-orange-900/20 dark:border-orange-800' : 
                                                                                                    'bg-red-50 border-red-300 dark:bg-red-900/20 dark:border-red-800'
                                                                                                }`}>
                                                                                                    <span className={`text-[10px] font-bold uppercase tracking-wider block mb-2 flex items-center gap-1 ${
                                                                                                        isFull ? 'text-green-700 dark:text-green-400' : 
                                                                                                        isPartial ? 'text-orange-700 dark:text-orange-400' : 
                                                                                                        'text-red-700 dark:text-red-400'
                                                                                                    }`}>
                                                                                                        Resposta do Candidato ({statusLabel})
                                                                                                    </span>
                                                                                                    <div className={`text-sm whitespace-pre-wrap font-medium ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                                                                                        {displayAnswer || <i className="opacity-50 font-normal">Deixou em branco</i>}
                                                                                                    </div>
                                                                                                </div>

                                                                                                {/* BOX DO GABARITO OFICIAL */}
                                                                                                <div className="p-4 rounded-lg border-2 bg-blue-50 border-blue-300 dark:bg-blue-900/20 dark:border-blue-800 shadow-sm relative overflow-hidden">
                                                                                                    <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
                                                                                                    <span className="text-[10px] font-bold text-blue-700 dark:text-blue-400 uppercase tracking-wider block mb-2 pl-2">
                                                                                                        Gabarito Sistema (Esperado)
                                                                                                    </span>
                                                                                                    <div className={`text-sm whitespace-pre-wrap font-medium pl-2 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                                                                                        {expectedDisplay}
                                                                                                    </div>
                                                                                                </div>

                                                                                            </div>

                                                                                                {result.aiFeedback && (
                                                                                                    <div className={`mt-3 p-3 rounded-lg text-xs border-l-4 flex flex-col gap-1 ${isDarkMode ? 'bg-indigo-900/20 border-indigo-500 text-indigo-200' : 'bg-indigo-50 border-indigo-400 text-indigo-900'}`}>
                                                                                                        <div className="flex items-center gap-1 font-bold text-indigo-600 dark:text-indigo-400 mb-1">
                                                                                                            <Sparkles size={14} className="shrink-0"/>
                                                                                                            Análise:
                                                                                                        </div>
                                                                                                        <span className="leading-relaxed">{result.aiFeedback}</span>
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : (
                                                                <div className="text-xs font-mono bg-black/5 dark:bg-black/30 p-4 rounded-lg whitespace-pre-wrap break-words">
                                                                    {JSON.stringify(tResult.details || tResult.answers, null, 2)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                            
                            {/* Botão para Salvar a Edição das Notas */}
                            {viewingResumeTests.testsTaken?.length > 0 && (
                                <div className={`p-4 border-t flex justify-end gap-2 ${isDarkMode ? 'border-slate-700 bg-slate-900' : 'border-gray-200 bg-gray-50'}`}>
                                    <Button variant="secondary" onClick={() => setViewingResumeTests(null)}>Fechar</Button>
                                    <Button onClick={async () => {
                                        await actions.saveItem('resumes', viewingResumeTests);
                                        setValidationAlert({ title: "Sucesso", message: "Notas atualizadas e salvas com sucesso!" });
                                    }} className="bg-green-600 hover:bg-green-700 text-white shadow-md">
                                        Salvar Correção de Notas
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* MODAL GLOBAL DE ALERTA DO RH */}
                {validationAlert && (
                    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setValidationAlert(null)}>
                        <div className={`p-6 rounded-xl w-96 shadow-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            <div className={`flex items-center gap-3 mb-4 ${validationAlert.title?.includes('Sucesso') ? 'text-green-500' : 'text-red-500'}`}>
                                <div className={`p-2 rounded-full ${validationAlert.title?.includes('Sucesso') ? 'bg-green-100' : 'bg-red-100'}`}>
                                    {validationAlert.title?.includes('Sucesso') ? <CheckCircle size={24} /> : <AlertTriangle size={24} />}
                                </div>
                                <h3 className="font-bold text-lg">{validationAlert.title || 'Atenção'}</h3>
                            </div>
                            <p className={`text-sm mb-6 whitespace-pre-wrap leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                {validationAlert.message}
                            </p>
                            <div className="flex justify-end">
                                <Button onClick={() => setValidationAlert(null)} className={`${validationAlert.title?.includes('Sucesso') ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white shadow-md border-0`}>
                                    {validationAlert.title?.includes('Sucesso') ? 'Continuar' : 'Entendi'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL CONSTRUTOR DE TESTES TÉCNICOS */}
                {isTestModalOpen && (
                    <RecruitmentTestBuilder 
                        isOpen={isTestModalOpen} 
                        onClose={() => { setIsTestModalOpen(false); setTestToEdit(null); }} 
                        data={data} 
                        actions={actions} 
                        isDarkMode={isDarkMode}
                        testToEdit={testToEdit} 
                    />
                )}
            </div>
        );
    };


    /**
     * COMPONENTE: CONSTRUTOR DE TESTES TÉCNICOS E PESQUISAS
     * (ATUALIZADO COM DRAG & DROP, TEXTAREAS EXPANSÍVEIS, ALINHAMENTO CORRIGIDO E FUNÇÕES RESTAURADAS)
     */ 


    /* ===============================================================================
     * [END_MOD_RH_SYSTEM]
     * FIM DO BLOCO: RHSystem
     * [START_MOD_RECRUITMENT_TEST_BUILDER]
     * INICIO DO BLOCO: RecruitmentTestBuilder
     * =============================================================================== */
    const RecruitmentTestBuilder = ({ isOpen, onClose, data, actions, isDarkMode, testToEdit }) => { 
        const [activeTab, setActiveTab] = useState('general'); 
        const [isSaving, setIsSaving] = useState(false); 
        
        // Controle de expansão de grupos (Toggles +/-)
        const [expandedGroups, setExpandedGroups] = useState({}); 

        // --- ESTADOS: CONTROLE DE DRAG & DROP DOS GRUPOS ---
        const [draggedSectionIdx, setDraggedSectionIdx] = useState(null);

        const handleSectionDragStart = (e, index) => {
            setDraggedSectionIdx(index);
            e.dataTransfer.effectAllowed = "move";
        };

        const handleSectionDragEnter = (e, index) => {
            e.preventDefault();
            if (draggedSectionIdx === null || draggedSectionIdx === index) return;
            
            // Cria cópia das seções e as reorganiza em tempo real
            const newSections = [...testForm.sections];
            const draggedSection = newSections[draggedSectionIdx];
            newSections.splice(draggedSectionIdx, 1);
            newSections.splice(index, 0, draggedSection);
            
            setTestForm(prev => ({ ...prev, sections: newSections }));
            setDraggedSectionIdx(index);
        };

        const handleSectionDragEnd = () => {
            setDraggedSectionIdx(null);
        };

        const toggleGroup = (id) => { 
            setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] })); 
        }; 
        
        // Estado inicial 
        const [testForm, setTestForm] = useState({ 
            id: null, 
            title: '', 
            description: '', 
            durationMinutes: 60, 
            departmentId: '', 
            departmentName: '', 
            personType: 'Todos', 
            sections: [ 
                { id: Date.now().toString(), title: 'Grupo 1', questions: [] } 
            ] 
        });

        // Carrega dados se for edição 
        useEffect(() => { 
            if (testToEdit) { setTestForm(testToEdit); } 
        }, [testToEdit]);

        const addSection = () => { 
            setTestForm(prev => ({ ...prev, sections: [...prev.sections, { id: Date.now().toString(), title: `Grupo ${prev.sections.length + 1}`, questions: [] }] })); 
        }; 
        
        const removeSection = (sectionId) => { 
            if (window.confirm("Remover este grupo?")) { 
                setTestForm(prev => ({ ...prev, sections: prev.sections.filter(s => s.id !== sectionId) })); 
            } 
        }; 

        const updateSectionTitle = (sectionId, newVal) => {
            setTestForm(prev => ({
                ...prev,
                sections: prev.sections.map(s => 
                    s.id === sectionId ? { ...s, title: newVal } : s
                )
            }));
        };

        const addQuestionToSection = (sectionId) => { 
            const newQ = { 
                id: Math.random().toString(36).substr(2, 9), 
                text: '', type: 'text', required: true, options: [], correctAnswer: '', points: 1, allowMultiple: false, hasOther: false, justifyTrigger: [] 
            }; 
            setTestForm(prev => ({ ...prev, sections: prev.sections.map(s => s.id === sectionId ? { ...s, questions: [...s.questions, newQ] } : s) })); 
        };

        const addContextToSection = (sectionId) => { 
            const newCtx = { 
                id: Math.random().toString(36).substr(2, 9), 
                type: 'context', text: '', imageUrl: null, referenceInfo: '' 
            }; 
            setTestForm(prev => ({ ...prev, sections: prev.sections.map(s => s.id === sectionId ? { ...s, questions: [...s.questions, newCtx] } : s) })); 
        };

        const updateQuestion = (sectionId, qId, field, value) => { 
            setTestForm(prev => ({ ...prev, sections: prev.sections.map(s => s.id === sectionId ? { ...s, questions: s.questions.map(q => q.id === qId ? { ...q, [field]: value } : q) } : s) })); 
        }; 
        
        const duplicateQuestion = (sectionId, question) => { 
            setTestForm(prev => ({ ...prev, sections: prev.sections.map(s => { 
                if (s.id !== sectionId) return s; 
                const idx = s.questions.findIndex(x => x.id === question.id); 
                const cloned = { ...question, id: Math.random().toString(36).substr(2, 9), text: question.text ? question.text + ' (Cópia)' : '', options: [...(question.options || [])], justifyTrigger: [...(question.justifyTrigger || [])] }; 
                const newQs = [...s.questions]; 
                newQs.splice(idx + 1, 0, cloned); 
                return { ...s, questions: newQs }; 
            }) })); 
        };

        const removeQuestionFromSection = (sectionId, qId) => { 
            setTestForm(prev => ({ ...prev, sections: prev.sections.map(s => s.id === sectionId ? { ...s, questions: s.questions.filter(q => q.id !== qId) } : s) })); 
        };

        const addOption = (sectionId, qId) => { 
            setTestForm(prev => ({ ...prev, sections: prev.sections.map(s => s.id === sectionId ? { ...s, questions: s.questions.map(q => q.id === qId ? { ...q, options: [...(q.options || []), `Opção ${(q.options?.length||0)+1}`] } : q) } : s) })); 
        }; 
        
        // --- CORREÇÃO: FUNÇÃO RESTAURADA ---
        const handleOptionChange = (sectionId, qId, oIdx, val) => {
            setTestForm(prev => ({
                ...prev,
                sections: prev.sections.map(s => s.id === sectionId ? {
                    ...s, questions: s.questions.map(q => {
                        if (q.id !== qId) return q;
                        const oldVal = q.options[oIdx]; 
                        
                        const newOpts = [...q.options];
                        newOpts[oIdx] = val;
                        
                        let newTriggers = [...(q.justifyTrigger || [])];
                        if (newTriggers.includes(oldVal)) {
                            newTriggers = newTriggers.map(t => t === oldVal ? val : t);
                        }

                        return { ...q, options: newOpts, justifyTrigger: newTriggers };
                    })
                } : s)
            }));
        };

        const removeOption = (sectionId, qId, oIdx) => { 
            setTestForm(prev => ({ ...prev, sections: prev.sections.map(s => s.id === sectionId ? { ...s, questions: s.questions.map(q => { 
                if (q.id !== qId) return q; 
                const valToRemove = q.options[oIdx];
                const newOpts = [...q.options]; 
                newOpts.splice(oIdx, 1); 
                
                const newTriggers = (q.justifyTrigger || []).filter(t => t !== valToRemove);

                return { ...q, options: newOpts, justifyTrigger: newTriggers }; 
            }) } : s) })); 
        };

        const toggleJustifyOption = (sectionId, qId, optionText) => { 
            setTestForm(prev => ({ ...prev, sections: prev.sections.map(s => s.id === sectionId ? { ...s, questions: s.questions.map(q => { 
                if (q.id !== qId) return q; 
                const currentTriggers = q.justifyTrigger || []; 
                const newTriggers = currentTriggers.includes(optionText) ? currentTriggers.filter(t => t !== optionText) : [...currentTriggers, optionText]; 
                return { ...q, justifyTrigger: newTriggers }; 
            }) } : s) })); 
        }; 
        
        const handleSave = async () => { 
            if (!testForm.title) return alert("Título obrigatório."); 
            if (isSaving) return; 
            setIsSaving(true); 
            try { 
                const deptName = data.departments.find(d => d.id === testForm.departmentId)?.name || 'Geral'; 
                const payloadId = testForm.id || (Date.now().toString() + Math.random().toString(36).substr(2, 5)); 
                const payload = { ...testForm, departmentName: deptName, id: payloadId }; 
                await actions.saveItem('recruitmentTests', payload); 
                alert("Teste salvo com sucesso!"); 
                onClose(); 
            } catch (error) { 
                alert("Erro ao salvar: " + error.message); 
            } finally { 
                setIsSaving(false); 
            } 
        };

        if (!isOpen) return null;

        return ( 
            <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"> 
                <div className={`rounded-xl w-full max-w-5xl shadow-2xl flex flex-col max-h-[95vh] ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`}> 
                    
                    {/* HEADER */} 
                    <div className="p-5 border-b flex justify-between items-center shrink-0"> 
                        <h3 className="font-bold text-lg flex items-center gap-2"> 
                            <PenTool size={20} className="text-indigo-500"/> {testForm.id ? 'Editar Teste' : 'Novo Teste'} 
                        </h3> 
                        <button onClick={onClose}><X size={20}/></button> 
                    </div> 

                    {/* ABAS DE NAVEGAÇÃO */} 
                    <div className={`flex border-b shrink-0 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}> 
                        <button onClick={() => setActiveTab('general')} className={`flex-1 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'general' ? 'border-indigo-500 text-indigo-500' : 'border-transparent opacity-60'}`}>Dados Gerais e Público</button> 
                        <button onClick={() => setActiveTab('structure')} className={`flex-1 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'structure' ? 'border-indigo-500 text-indigo-500' : 'border-transparent opacity-60'}`}>Perguntas & Gabarito</button> 
                    </div> 
                    
                    <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar"> 
                        
                        {/* ABA 1: DADOS GERAIS + PÚBLICO */} 
                        {activeTab === 'general' && ( 
                            <div className="space-y-4 animate-fade-in"> 
                                <div className="grid grid-cols-2 gap-4"> 
                                    <div className="col-span-2"> 
                                        <label className="text-xs font-bold block mb-1">Título do Teste</label> 
                                        <Input placeholder="Ex: Avaliação de Lógica - Nível 1" value={testForm.title} onChange={e => setTestForm({...testForm, title: e.target.value})} className="font-bold text-lg"/> 
                                    </div> 
                                    <div> 
                                        <label className="text-xs font-bold block mb-1">Departamento</label> 
                                        <select className={`w-full p-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={testForm.departmentId || ''} onChange={e => setTestForm({...testForm, departmentId: e.target.value})}> 
                                            <option value="">Todos (Geral)</option> 
                                            {data.departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)} 
                                        </select> 
                                    </div> 
                                    <div> 
                                        <label className="text-xs font-bold block mb-1">Público Alvo (Tipo)</label> 
                                        <select className={`w-full p-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={testForm.personType || 'Todos'} onChange={e => setTestForm({...testForm, personType: e.target.value})}> 
                                            <option value="Todos">Todos</option> 
                                            <option value="Colaborador">Colaborador</option> 
                                            <option value="Gestão">Gestão</option> 
                                        </select> 
                                    </div> 
                                    <div> 
                                        <label className="text-xs font-bold block mb-1">Tempo de Prova (Minutos)</label> 
                                        <input type="number" className={`w-full p-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={testForm.durationMinutes || ''} onChange={e => setTestForm({...testForm, durationMinutes: parseInt(e.target.value) || 0})} /> 
                                    </div> 
                                    <div className="col-span-2"> 
                                        <label className="text-xs font-bold block mb-1">Instruções / Descrição</label> 
                                        <textarea className={`w-full p-3 border rounded-lg text-sm h-32 outline-none focus:ring-2 focus:ring-indigo-500 custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={testForm.description} onChange={e => setTestForm({...testForm, description: e.target.value})} placeholder="Instruções para o candidato..."></textarea> 
                                    </div> 
                                </div> 
                            </div> 
                        )} 

                        {/* ABA 2: PERGUNTAS (ESTRUTURA) COM DRAG & DROP E ++/-- */ } 
                        {activeTab === 'structure' && ( 
                            <div className="space-y-6 animate-fade-in"> 
                                <div className="flex justify-end"> 
                                    <Button onClick={addSection} size="sm" icon={Plus} className="bg-indigo-600 text-white hover:bg-indigo-700">Novo Grupo</Button> 
                                </div> 

                                {(() => {
                                    // --- CONTADOR GLOBAL DE PERGUNTAS ---
                                    let globalQCounter = 1;

                                    return testForm.sections.map((section, sIdx) => ( 
                                        <div 
                                            key={section.id} 
                                            draggable={true}
                                            onDragStart={(e) => handleSectionDragStart(e, sIdx)}
                                            onDragEnter={(e) => handleSectionDragEnter(e, sIdx)}
                                            onDragEnd={handleSectionDragEnd}
                                            onDragOver={(e) => e.preventDefault()}
                                            className={`p-4 rounded-xl border shadow-sm transition-all duration-300 ${draggedSectionIdx === sIdx ? 'opacity-50 ring-2 ring-indigo-500 scale-[0.98]' : ''} ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}
                                        > 
                                            {/* Cabeçalho do Grupo */} 
                                            <div className={`flex justify-between items-center mb-4 border-b pb-2 gap-4 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}> 
                                                
                                                {/* ÍCONE DE ARRASTAR */}
                                                <div className="cursor-move text-gray-400 hover:text-indigo-500 p-1" title="Clique e arraste para reordenar este grupo">
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
                                                </div>

                                                {/* BOTÃO EXPANDIR / MINIMIZAR */}
                                                <button 
                                                    onClick={() => toggleGroup(section.id)} 
                                                    className={`p-1.5 rounded transition-colors ${isDarkMode ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                                    title={expandedGroups[section.id] ? "Expandir Grupo" : "Recolher Grupo"}
                                                >
                                                    {expandedGroups[section.id] ? <Plus size={16}/> : <Minus size={16}/>}
                                                </button>

                                                <input 
                                                    type="text" 
                                                    className={`w-full font-bold bg-transparent outline-none text-lg focus:text-indigo-600 transition-colors ${isDarkMode ? 'text-white focus:text-indigo-400' : 'text-gray-800'}`} 
                                                    value={section.title} 
                                                    onChange={(e) => updateSectionTitle(section.id, e.target.value)} 
                                                    placeholder="Nome do Grupo" 
                                                /> 
                                                
                                                <button 
                                                    onClick={() => removeSection(section.id)} 
                                                    className={`p-1 rounded-full transition-colors ${isDarkMode ? 'text-red-400 hover:bg-red-900/30 hover:text-red-300' : 'text-red-500 hover:bg-red-50 hover:text-red-600'}`}
                                                    title="Excluir Grupo"
                                                >
                                                    <Trash2 size={18}/>
                                                </button> 
                                            </div> 

                                            {/* Lista de Perguntas e Contextos - Fica invisível se o grupo for minimizado */} 
                                            {!expandedGroups[section.id] && (
                                                <div className="space-y-4 animate-fade-in"> 
                                                    {section.questions.map((q) => { 
                                                        
                                                        // ========================================== 
                                                        // RENDERIZAÇÃO DO BLOCO DE CONTEXTO BASE
                                                        // ========================================== 
                                                        if (q.type === 'context') { 
                                                            return ( 
                                                                <div key={q.id} className={`p-5 rounded-xl border-2 border-dashed relative group mb-4 shadow-sm transition-colors ${isDarkMode ? 'bg-indigo-900/10 border-indigo-800 hover:border-indigo-600' : 'bg-indigo-50/50 border-indigo-300 hover:border-indigo-400'}`}> 
                                                                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity"> 
                                                                        <button onClick={() => duplicateQuestion(section.id, q)} className="text-blue-500 hover:bg-blue-100 p-1.5 rounded" title="Duplicar Bloco"><Copy size={14}/></button> 
                                                                        <button onClick={() => setTestForm(prev => ({...prev, sections: prev.sections.map(s => s.id === section.id ? {...s, questions: s.questions.filter(xq => xq.id !== q.id)} : s)}))} className="text-red-500 hover:bg-red-100 p-1.5 rounded" title="Excluir Bloco"><Trash2 size={14}/></button> 
                                                                    </div> 
                                                                    <div className="flex gap-4"> 
                                                                        <div className="flex flex-col items-center gap-1 mt-1"> 
                                                                            <span className="bg-indigo-200 text-indigo-800 w-8 h-8 rounded-full flex items-center justify-center font-black text-xs shadow-sm"><FileText size={16}/></span> 
                                                                        </div> 
                                                                        <div className="flex-1 space-y-3"> 
                                                                            <div> 
                                                                                <label className="text-[10px] font-bold uppercase text-indigo-600 dark:text-indigo-400 mb-1 block">Rótulo / Orientação</label> 
                                                                                <Input value={q.referenceInfo || ''} onChange={e => updateQuestion(section.id, q.id, 'referenceInfo', e.target.value)} placeholder="Ex: 'Utilize o texto e a imagem abaixo para responder às questões de 1 a 5'" className={`font-bold text-sm ${isDarkMode ? 'bg-slate-900 border-slate-700 text-indigo-300' : 'text-indigo-800 border-indigo-200 bg-white'}`} /> 
                                                                            </div> 
                                                                            <div> 
                                                                                <label className="text-[10px] font-bold uppercase text-gray-500 mb-1 block">Texto Base (Opcional se usar imagem)</label> 
                                                                                <textarea value={q.text || ''} onChange={e => updateQuestion(section.id, q.id, 'text', e.target.value)} className={`w-full p-3 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500 resize-y min-h-[100px] shadow-inner custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-gray-300 text-gray-800'}`} placeholder="Cole o texto longo, artigo ou instruções aqui..." /> 
                                                                            </div> 
                                                                            <div className={`p-3 rounded border ${isDarkMode ? 'bg-black/20 border-slate-700' : 'bg-white/50 border-gray-200'}`}> 
                                                                                <label className="text-[10px] font-bold uppercase text-gray-600 dark:text-gray-400 flex items-center gap-1 mb-2"><Paperclip size={12}/> Imagem / Gráfico Auxiliar (Opcional)</label> 
                                                                                <input type="file" accept="image/*" className="text-xs w-full" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onload = () => updateQuestion(section.id, q.id, 'imageUrl', reader.result); reader.readAsDataURL(file); } }} /> 
                                                                                {q.imageUrl && ( <div className="mt-3 relative inline-block"> <img src={q.imageUrl} alt="Contexto Base" className="max-h-40 rounded-lg border border-gray-300 shadow-md" /> <button onClick={() => updateQuestion(section.id, q.id, 'imageUrl', null)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-lg hover:bg-red-600"><X size={14}/></button> </div> )} 
                                                                            </div> 
                                                                        </div> 
                                                                    </div> 
                                                                </div> 
                                                            ); 
                                                        } 

                                                        // ========================================== 
                                                        // RENDERIZAÇÃO DA PERGUNTA NORMAL
                                                        // ========================================== 
                                                        const currentQNum = globalQCounter++; 

                                                        return ( 
                                                            <div key={q.id} className={`p-4 rounded-xl border-2 relative group shadow-sm transition-all ${isDarkMode ? 'bg-slate-900 border-slate-700 hover:border-slate-500' : 'bg-white border-gray-200 hover:border-indigo-200'}`}> 
                                                                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1 z-10 bg-white dark:bg-slate-900 p-1 rounded-lg shadow-sm border dark:border-slate-700"> 
                                                                    <button onClick={() => duplicateQuestion(section.id, q)} className="text-blue-500 hover:bg-blue-100 p-1.5 rounded" title="Duplicar"> <Copy size={14}/> </button> 
                                                                    <button onClick={() => removeQuestionFromSection(section.id, q.id)} className="text-red-500 hover:bg-red-100 p-1.5 rounded" title="Excluir"> <Trash2 size={14}/> </button> 
                                                                </div> 
                                                                
                                                                <div className="flex gap-3 mb-4 pr-16 items-start"> 
                                                                    <span className="bg-indigo-100 text-indigo-700 w-8 h-8 rounded-full flex items-center justify-center font-black text-sm shrink-0 border border-indigo-200 shadow-sm mt-1">{currentQNum}</span> 
                                                                    
                                                                    <div className="flex-1 flex gap-2 items-start w-full"> 
                                                                        <textarea 
                                                                            value={q.text} 
                                                                            onChange={e => updateQuestion(section.id, q.id, 'text', e.target.value)} 
                                                                            className={`flex-1 p-3 font-bold text-sm rounded-lg border outline-none focus:ring-2 focus:ring-indigo-400 resize-y min-h-[42px] shadow-inner ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-gray-50 border-gray-300'}`} 
                                                                            placeholder="Digite o enunciado da questão..." 
                                                                            rows={2}
                                                                        /> 
                                                                        <select 
                                                                            className={`w-48 shrink-0 h-[42px] p-1.5 rounded-lg border text-xs font-bold text-indigo-700 bg-indigo-50 outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer shadow-sm ${isDarkMode ? 'bg-indigo-900/30 border-indigo-800 text-indigo-300' : 'border-indigo-200'}`} 
                                                                            value={q.type} 
                                                                            onChange={e => { 
                                                                                const val = e.target.value;
                                                                                updateQuestion(section.id, q.id, 'type', val); 
                                                                                // Reseta o gabarito. Dropdown e Multi usam Array; Fill_blank usa Objeto; Outros usam String
                                                                                updateQuestion(section.id, q.id, 'correctAnswer', (val === 'multi' || val === 'dropdown') ? [] : (val === 'fill_blank' ? {} : '')); 
                                                                                if (val === 'spreadsheet' && !q.tableConfig) {
                                                                                    updateQuestion(section.id, q.id, 'tableConfig', {
                                                                                        rows: 2, cols: 2,
                                                                                        cells: { '0-0': { type: 'label', value: 'Cabeçalho 1', isBold: true }, '0-1': { type: 'label', value: 'Cabeçalho 2', isBold: true } }
                                                                                    });
                                                                                }
                                                                            }}
                                                                        > 
                                                                            <option value="text">Discursiva / Texto Livre</option> 
                                                                            <option value="multi">Múltipla Escolha</option> 
                                                                            <option value="dropdown">Lista Suspensa</option>
                                                                            <option value="true_false">Verdadeiro ou Falso</option> 
                                                                            <option value="fill_blank">Completar Frase</option> 
                                                                            <option value="spreadsheet">Planilha / Tabela</option> 
                                                                        </select> 
                                                                    </div> 
                                                                </div> 

                                                                <div className="mb-4 pl-11"> 
                                                                    <label className="text-[10px] font-bold uppercase text-gray-500 flex items-center gap-1 mb-1"> <Paperclip size={12}/> Adicionar Imagem para a Questão (Opcional) </label> 
                                                                    <input type="file" accept="image/*" className="text-xs w-full" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onload = () => updateQuestion(section.id, q.id, 'imageUrl', reader.result); reader.readAsDataURL(file); } }} /> 
                                                                    {q.imageUrl && ( <div className="mt-2 relative inline-block"> <img src={q.imageUrl} alt="Imagem da Questão" className="max-h-32 rounded border border-gray-300 shadow-sm" /> <button onClick={() => updateQuestion(section.id, q.id, 'imageUrl', null)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600"><X size={12}/></button> </div> )} 
                                                                </div> 
                                                                
                                                                {q.type === 'fill_blank' && ( 
                                                                    <div className={`mt-2 mb-3 ml-11 text-[10px] p-2 rounded border ${isDarkMode ? 'bg-orange-900/20 border-orange-800 text-orange-300' : 'bg-orange-50 border-orange-200 text-orange-700'}`}> 
                                                                        <b className="uppercase">Dica:</b> No enunciado da pergunta acima, use chaves <b>{`{ }`}</b> para indicar os espaços em branco que o candidato deve preencher. <br/> Exemplo: <i>{"O framework {nome do framework} foi criado pelo Facebook."}</i> 
                                                                    </div> 
                                                                )} 
                                                                
                                                                {q.type === 'spreadsheet' && ( 
                                                                    <div className={`mt-2 mb-3 ml-11 p-4 rounded-xl border shadow-inner ${isDarkMode ? 'bg-slate-900/80 border-slate-700' : 'bg-gray-50 border-gray-200'}`}> 
                                                                        <div className="mb-4"> 
                                                                            <label className="text-[10px] font-bold uppercase text-gray-500 block mb-1">Orientações de Preenchimento (Opcional)</label> 
                                                                            <textarea className={`w-full p-2 text-sm rounded-lg border outline-none focus:ring-1 focus:ring-indigo-500 resize-y min-h-[50px] ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`} placeholder="Ex: Preencha apenas os valores na coluna total..." value={q.instructions || ''} onChange={e => updateQuestion(section.id, q.id, 'instructions', e.target.value)} /> 
                                                                        </div> 
                                                                        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-300 dark:border-slate-600"> 
                                                                            <span className={`text-[10px] font-bold uppercase mr-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Dimensões da Tabela:</span> 
                                                                            <button onClick={() => updateQuestion(section.id, q.id, 'tableConfig', { ...q.tableConfig, rows: (q.tableConfig?.rows || 2) + 1 })} className="text-[10px] bg-indigo-600 text-white px-2 py-1.5 rounded font-bold hover:bg-indigo-700 transition-colors">+ Linha</button> 
                                                                            <button onClick={() => updateQuestion(section.id, q.id, 'tableConfig', { ...q.tableConfig, rows: Math.max(1, (q.tableConfig?.rows || 2) - 1) })} className="text-[10px] bg-red-600 text-white px-2 py-1.5 rounded font-bold hover:bg-red-700 transition-colors">- Linha</button> 
                                                                            <span className="text-gray-300 mx-1">|</span> 
                                                                            <button onClick={() => updateQuestion(section.id, q.id, 'tableConfig', { ...q.tableConfig, cols: (q.tableConfig?.cols || 2) + 1 })} className="text-[10px] bg-indigo-600 text-white px-2 py-1.5 rounded font-bold hover:bg-indigo-700 transition-colors">+ Coluna</button> 
                                                                            <button onClick={() => updateQuestion(section.id, q.id, 'tableConfig', { ...q.tableConfig, cols: Math.max(1, (q.tableConfig?.cols || 2) - 1) })} className="text-[10px] bg-red-600 text-white px-2 py-1.5 rounded font-bold hover:bg-red-700 transition-colors">- Coluna</button> 
                                                                        </div> 
                                                                        <div className="overflow-x-auto custom-scrollbar pb-2"> 
                                                                            <table className="w-full border-collapse"> 
                                                                                <tbody> 
                                                                                    {Array.from({length: q.tableConfig?.rows || 2}).map((_, r) => ( 
                                                                                        <tr key={r}> 
                                                                                            {Array.from({length: q.tableConfig?.cols || 2}).map((_, c) => { 
                                                                                                const cellId = `${r}-${c}`; 
                                                                                                const cell = q.tableConfig?.cells?.[cellId] || { type: 'label', value: '', expected: '', isBold: false }; 
                                                                                                const updateCell = (field, val) => { const newCells = { ...(q.tableConfig?.cells || {}) }; newCells[cellId] = { ...cell, [field]: val }; updateQuestion(section.id, q.id, 'tableConfig', { ...q.tableConfig, cells: newCells }); }; 
                                                                                                return ( 
                                                                                                    <td key={c} className={`p-2 border min-w-[220px] ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}> 
                                                                                                        <div className="flex flex-col gap-1.5"> 
                                                                                                            <div className="flex justify-between items-start bg-black/5 dark:bg-white/5 p-1 rounded"> 
                                                                                                                <div className="flex flex-col gap-1 w-full mr-2"> 
                                                                                                                    <select className={`text-[9px] p-0.5 rounded border outline-none font-bold uppercase w-full ${cell.type === 'input' ? 'bg-orange-100 text-orange-800 border-orange-300' : 'bg-gray-100 text-gray-700 border-gray-300'}`} value={cell.type} onChange={e => updateCell('type', e.target.value)}> 
                                                                                                                        <option value="label">Rótulo / Fixo</option> 
                                                                                                                        <option value="input">Candidato Preenche</option> 
                                                                                                                    </select> 
                                                                                                                    {cell.type === 'input' && ( 
                                                                                                                        <select className="text-[9px] p-0.5 rounded border outline-none font-bold uppercase bg-blue-50 text-blue-700 border-blue-200 w-full" value={cell.format || 'text'} onChange={e => updateCell('format', e.target.value)}> 
                                                                                                                            <option value="text">Texto Livre</option> 
                                                                                                                            <option value="number">Núm (1.000,00)</option> 
                                                                                                                            <option value="time">Hora (00:00:00)</option> 
                                                                                                                        </select> 
                                                                                                                    )} 
                                                                                                                </div> 
                                                                                                                {cell.type === 'label' && ( <label className="text-[9px] font-bold flex items-center gap-1 cursor-pointer text-gray-500 mt-1 shrink-0" title="Exibir em Negrito (Usado para Totalizadores ou Cabeçalhos)"> <input type="checkbox" checked={cell.isBold} onChange={e => updateCell('isBold', e.target.checked)} className="rounded"/> B </label> )} 
                                                                                                            </div> 
                                                                                                            {cell.type === 'label' ? ( 
                                                                                                                <input type="text" placeholder="Texto fixo exibido..." value={cell.value || ''} onChange={e => updateCell('value', e.target.value)} className={`w-full p-2 text-xs rounded border outline-none focus:border-indigo-400 ${cell.isBold ? 'font-bold bg-white dark:bg-slate-800' : 'bg-white dark:bg-slate-900'} ${isDarkMode ? 'border-slate-600 text-white' : 'border-gray-200'}`} /> 
                                                                                                            ) : ( 
                                                                                                                <input type="text" placeholder="Gabarito esperado (Ex: 1500)..." value={cell.expected || ''} onChange={e => updateCell('expected', e.target.value)} className={`w-full p-2 text-xs rounded border outline-none focus:border-orange-500 font-bold bg-orange-50 text-orange-900 border-orange-300 placeholder-orange-400/70`} /> 
                                                                                                            )} 
                                                                                                        </div> 
                                                                                                    </td> 
                                                                                                ); 
                                                                                            })} 
                                                                                        </tr> 
                                                                                    ))} 
                                                                                </tbody> 
                                                                            </table> 
                                                                        </div> 
                                                                    </div> 
                                                                )} 

                                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-4 border-t border-dashed border-gray-300 dark:border-slate-600"> 

                                                                    {/* LADO ESQUERDO: GABARITO E OPÇÕES */} 
                                                                    <div className="flex flex-col gap-4"> 
                                                                        
                                                                        {/* SEÇÃO DE OPÇÕES (Multi, Dropdown, V/F) */}
                                                                        {(q.type === 'multi' || q.type === 'true_false' || q.type === 'dropdown') && ( 
                                                                            <div className="space-y-2"> 
                                                                                <span className="text-[10px] font-bold uppercase text-gray-500 block">Definição das Opções</span>
                                                                                {q.options.map((opt, i) => ( 
                                                                                    <div key={i} className="flex items-start gap-2 text-xs"> 
                                                                                        <div className={`w-3 h-3 mt-3 ${q.allowMultiple ? 'rounded' : 'rounded-full'} border border-gray-400 bg-white shrink-0`}></div> 
                                                                                        <textarea 
                                                                                            className={`flex-1 p-2 rounded border outline-none resize-y min-h-[40px] custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-gray-200'}`} 
                                                                                            value={opt} 
                                                                                            onChange={e => handleOptionChange(section.id, q.id, i, e.target.value)} 
                                                                                            placeholder={`Opção ${i + 1}`}
                                                                                            rows={1}
                                                                                        /> 
                                                                                        <label className={`mt-1 flex items-center gap-1 text-[9px] px-2 py-1.5 rounded border cursor-pointer select-none transition-colors shrink-0 ${(q.justifyTrigger||[]).includes(opt) ? 'bg-orange-100 text-orange-700 border-orange-300 font-bold' : 'bg-gray-100 text-gray-500 hover:bg-gray-200 border-gray-200'}`}> 
                                                                                            <input type="checkbox" className="hidden" checked={(q.justifyTrigger||[]).includes(opt)} onChange={() => toggleJustifyOption(section.id, q.id, opt)}/> 
                                                                                            {(q.justifyTrigger||[]).includes(opt) ? <Check size={10}/> : null} Exigir Justificativa 
                                                                                        </label> 
                                                                                        <button onClick={() => removeOption(section.id, q.id, i)} className="mt-1 text-red-400 ml-1 hover:text-red-600 bg-red-50 p-1.5 rounded shrink-0" title="Excluir Opção"><X size={14}/></button> 
                                                                                    </div> 
                                                                                ))} 
                                                                                <button onClick={() => addOption(section.id, q.id)} className="text-[10px] text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded font-bold hover:bg-indigo-100 transition-colors border border-indigo-200 mt-2">+ Adicionar Opção</button> 
                                                                            </div> 
                                                                        )}

                                                                        {/* SEÇÃO DE GABARITO DO SISTEMA */}
                                                                        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-green-900/10 border-green-800' : 'bg-green-50 border-green-200'}`}> 
                                                                            <label className={`text-[10px] font-bold uppercase block mb-3 flex items-center gap-1 ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}> <CheckCircle size={14}/> Gabarito do Sistema </label> 
                                                                            
                                                                            {q.type === 'fill_blank' ? (
                                                                                <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar pr-1">
                                                                                    {(() => {
                                                                                        const blanks = (q.text || '').match(/\{.*?\}/g) || [];
                                                                                        if (blanks.length === 0) return <span className="text-xs text-gray-400 italic">Digite o texto com chaves {`{ }`} acima para gerar as lacunas.</span>;
                                                                                        return blanks.map((blankLabel, idx) => (
                                                                                            <div key={idx} className="flex items-center gap-2">
                                                                                                <span className={`text-xs font-bold w-24 truncate ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{idx + 1}. {blankLabel}:</span>
                                                                                                <input type="text" className={`flex-1 p-2 text-xs rounded border outline-none ${isDarkMode ? 'bg-slate-900 border-green-800 text-green-300' : 'bg-white border-green-300 text-gray-800'}`}
                                                                                                    placeholder={`Resposta correta`}
                                                                                                    value={(q.correctAnswer && q.correctAnswer[idx]) || ''}
                                                                                                    onChange={e => {
                                                                                                        const newCorrect = { ...(q.correctAnswer || {}) };
                                                                                                        newCorrect[idx] = e.target.value;
                                                                                                        updateQuestion(section.id, q.id, 'correctAnswer', newCorrect);
                                                                                                    }}
                                                                                                />
                                                                                            </div>
                                                                                        ));
                                                                                    })()}
                                                                                </div>
                                                                            ) : q.type === 'true_false' ? ( 
                                                                                <div className="space-y-3 max-h-60 overflow-y-auto custom-scrollbar pr-1"> 
                                                                                    <span className={`text-[9px] italic block mb-1 ${isDarkMode ? 'text-green-500' : 'text-green-700'}`}>Defina V/F e a Justificativa (se exigida):</span> 
                                                                                    {(q.options || []).map((o, idx) => { 
                                                                                        const requiresJust = (q.justifyTrigger || []).includes(o); 
                                                                                        return ( 
                                                                                            <div key={idx} className={`p-2 rounded border flex flex-col gap-2 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-green-200'}`}> 
                                                                                                <div className="flex items-start gap-2 mb-2"> 
                                                                                                    <select className={`text-xs p-1 rounded font-bold outline-none border cursor-pointer mt-1 ${(q.correctAnswer?.[idx] === 'V') ? 'bg-green-100 text-green-700 border-green-300' : (q.correctAnswer?.[idx] === 'F') ? 'bg-red-100 text-red-700 border-red-300' : 'bg-gray-100 text-gray-400 border-gray-300'}`} value={q.correctAnswer?.[idx] || ''} onChange={e => { const newArr = [...(Array.isArray(q.correctAnswer) ? q.correctAnswer : [])]; newArr[idx] = e.target.value; updateQuestion(section.id, q.id, 'correctAnswer', newArr); }}> 
                                                                                                        <option value="">?</option><option value="V">V</option><option value="F">F</option> 
                                                                                                    </select> 
                                                                                                    <span className={`text-xs whitespace-pre-wrap flex-1 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>{o || `Item ${idx+1}`}</span> 
                                                                                                </div> 
                                                                                                {requiresJust && ( 
                                                                                                    <textarea className={`w-full text-xs p-1.5 rounded border outline-none focus:border-green-500 resize-y min-h-[30px] custom-scrollbar ${isDarkMode ? 'bg-slate-800 border-slate-600 text-green-300 placeholder-green-800' : 'bg-green-50/50 border-green-200 text-green-800 placeholder-green-600/40'}`} placeholder="Justificativa esperada..." value={q.correctJustification?.[idx] || ''} onChange={e => { const newArr = [...(Array.isArray(q.correctJustification) ? q.correctJustification : [])]; newArr[idx] = e.target.value; updateQuestion(section.id, q.id, 'correctJustification', newArr); }} /> 
                                                                                                )} 
                                                                                            </div> 
                                                                                        ); 
                                                                                    })} 
                                                                                </div> 
                                                                            ) : (q.type === 'multi' || q.type === 'dropdown') ? ( 
                                                                                q.allowMultiple ? ( 
                                                                                    <div className="space-y-1"> 
                                                                                        <span className="text-[9px] text-green-700 dark:text-green-500 mb-1 block">Quais opções estão corretas?</span> 
                                                                                        {q.options.map((o, idx) => ( 
                                                                                            <label key={idx} className="flex items-start gap-2 text-xs p-1.5 rounded border transition-colors cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800"> 
                                                                                                <input type="checkbox" className="rounded mt-1" checked={(q.correctAnswer || []).includes(o)} onChange={e => { const isChecked = e.target.checked; const current = q.correctAnswer || []; updateQuestion(section.id, q.id, 'correctAnswer', isChecked ? [...current, o] : current.filter(x => x !== o)); }}/> 
                                                                                                <span className={`whitespace-pre-wrap flex-1 ${Array.isArray(q.correctAnswer) && q.correctAnswer.includes(o) ? 'font-bold text-green-700 dark:text-green-400' : (isDarkMode ? 'text-slate-300' : 'text-gray-800')}`}>{o || `Opção ${idx+1}`}</span> 
                                                                                            </label> 
                                                                                        ))} 
                                                                                    </div> 
                                                                                ) : ( 
                                                                                    <select className={`w-full p-2.5 text-xs rounded-lg border outline-none font-medium ${isDarkMode ? 'bg-slate-900 border-green-800 text-green-300' : 'bg-white border-green-300 text-gray-800'}`} value={typeof q.correctAnswer === 'string' ? q.correctAnswer : ''} onChange={e => updateQuestion(section.id, q.id, 'correctAnswer', e.target.value)}> 
                                                                                        <option value="">Selecione a opção correta...</option> 
                                                                                        {q.options.map((o, idx) => <option key={idx} value={o}>{o || `Opção ${idx+1}`}</option>)} 
                                                                                    </select> 
                                                                                ) 
                                                                            ) : ( 
                                                                                <textarea 
                                                                                    placeholder="Palavras-chave ou texto esperado para a IA corrigir..." 
                                                                                    value={q.correctAnswer || ''} 
                                                                                    onChange={e => updateQuestion(section.id, q.id, 'correctAnswer', e.target.value)} 
                                                                                    className={`w-full p-2 text-xs rounded border outline-none focus:ring-1 focus:ring-green-500 resize-y min-h-[60px] custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-green-800 text-green-100' : 'border-green-300 bg-white text-gray-800'}`}
                                                                                /> 
                                                                            )} 
                                                                        </div>
                                                                    </div>

                                                                    {/* LADO DIREITO: Configurações e Valores */} 
                                                                    <div className={`p-4 rounded-xl border flex flex-col gap-3 justify-center ${isDarkMode ? 'bg-slate-900/40 border-slate-700' : 'bg-gray-50 border-gray-200'}`}> 
                                                                        <div className="flex justify-between items-center border-b border-gray-200 dark:border-slate-600 pb-3"> 
                                                                            <label className="text-[10px] font-bold uppercase text-gray-700 dark:text-gray-300">Valor da Questão:</label> 
                                                                            <div className="flex items-center gap-1"> 
                                                                                <input type="number" min="0" step="0.5" value={q.points !== undefined ? q.points : 10} onChange={e => updateQuestion(section.id, q.id, 'points', parseFloat(e.target.value) || 0)} className={`w-16 p-1.5 text-sm text-center border rounded font-bold text-indigo-700 outline-none focus:ring-2 focus:ring-indigo-500 ${isDarkMode ? 'bg-slate-800 border-slate-600 text-indigo-300' : 'bg-white border-gray-300'}`} /> 
                                                                                <span className="text-[10px] font-bold text-gray-500">Pts</span> 
                                                                            </div> 
                                                                        </div> 
                                                                        <div className="flex flex-col gap-2"> 
                                                                            <label className={`flex items-center gap-3 cursor-pointer text-xs font-bold p-2 rounded-lg border transition-colors ${q.required ? 'bg-red-50 border-red-200 text-red-600 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400' : 'border-transparent text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800'}`}> 
                                                                                <input type="checkbox" checked={q.required} onChange={e => updateQuestion(section.id, q.id, 'required', e.target.checked)} className="text-red-500 focus:ring-red-500 w-4 h-4 rounded"/> Pergunta Obrigatória 
                                                                            </label> 
                                                                            {(q.type === 'multi' || q.type === 'dropdown') && ( 
                                                                                <> 
                                                                                    {q.type === 'multi' && (
                                                                                        <label className={`flex items-center gap-3 cursor-pointer text-xs font-bold p-2 rounded-lg border transition-colors ${q.hasOther ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400' : 'border-transparent text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800'}`}> 
                                                                                            <input type="checkbox" checked={q.hasOther || false} onChange={e => updateQuestion(section.id, q.id, 'hasOther', e.target.checked)} className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"/> Incluir Campo "Outros"
                                                                                        </label> 
                                                                                    )}
                                                                                    <label className={`flex items-center gap-3 cursor-pointer text-xs font-bold p-2 rounded-lg border transition-colors ${q.allowMultiple ? 'bg-purple-50 border-purple-200 text-purple-700 dark:bg-purple-900/20 dark:border-purple-800 dark:text-purple-400' : 'border-transparent text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800'}`}> 
                                                                                        <input type="checkbox" checked={q.allowMultiple || false} onChange={e => { 
                                                                                            updateQuestion(section.id, q.id, 'allowMultiple', e.target.checked); 
                                                                                            // Reseta gabarito se mudar o tipo de seleção
                                                                                            updateQuestion(section.id, q.id, 'correctAnswer', e.target.checked ? [] : '');
                                                                                        }} className="rounded text-purple-600 focus:ring-purple-500 w-4 h-4" /> Permitir Múltiplas Respostas 
                                                                                    </label> 
                                                                                </> 
                                                                            )} 
                                                                        </div> 
                                                                    </div> 
                                                                </div> 
                                                            </div> 
                                                        ); 
                                                    })} 

                                                    {/* BOTOES DE INSERIR NO FIM DO GRUPO */} 
                                                    <div className={`flex justify-center gap-4 py-6 rounded-xl border-2 border-dashed ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-300'}`}> 
                                                        <button onClick={() => addContextToSection(section.id)} className={`text-xs font-bold flex items-center gap-2 px-5 py-2.5 rounded-lg transition-all shadow-sm ${isDarkMode ? 'bg-indigo-900/40 text-indigo-400 hover:bg-indigo-800' : 'bg-indigo-100/50 text-indigo-600 hover:bg-indigo-100 hover:text-indigo-800'}`}> 
                                                            <FileText size={16}/> Inserir Bloco Base (Texto/Imagem) 
                                                        </button> 
                                                        <button onClick={() => addQuestionToSection(section.id)} className={`text-xs font-bold flex items-center gap-2 px-5 py-2.5 rounded-lg transition-all shadow-sm ${isDarkMode ? 'bg-teal-900/40 text-teal-400 hover:bg-teal-800' : 'bg-teal-100/50 text-teal-600 hover:bg-teal-100 hover:text-teal-800'}`}> 
                                                            <Plus size={16}/> Inserir Pergunta 
                                                        </button> 
                                                    </div> 
                                                </div> 
                                            )} 
                                        </div> 
                                    )); 
                                })()} 
                            </div> 
                        )} 

                    </div> 
                    {/* FOOTER */} 
                    <div className={`p-5 border-t flex justify-end gap-3 shrink-0 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}> 
                        <Button variant="secondary" onClick={onClose} className={isDarkMode ? 'bg-slate-800 text-slate-300 border-slate-600' : ''}>Descartar Alterações</Button> 
                        <Button onClick={handleSave} className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 shadow-md" disabled={isSaving}> 
                            {isSaving ? <Loader2 size={16} className="animate-spin" /> : "Salvar Teste"} 
                        </Button> 
                    </div> 
                </div> 
            </div> 
        ); 
    };

    /**
     * ============================================================================
     * COMPONENTE: PORTAL PÚBLICO DO CANDIDATO (DASHBOARD UNIFICADO)
     * ============================================================================
     */


    /* ===============================================================================
     * [END_MOD_RECRUITMENT_TEST_BUILDER]
     * FIM DO BLOCO: RecruitmentTestBuilder
     * [START_MOD_PUBLIC_TEST_RUNNER]
     * INICIO DO BLOCO: PublicTestRunner
     * =============================================================================== */
    const PublicTestRunner = ({ testId }) => {
        const [isDarkMode, setIsDarkMode] = useState(false);

        const [step, setStep] = useState('loading');
        
        // --- NOVOS ESTADOS DO DASHBOARD UNIFICADO ---
        const [hasStarted, setHasStarted] = useState(false);
        const [activeSectionIdx, setActiveSectionIdx] = useState(null); // null = Tela Principal, número = Sessão Aberta
        
        const [testData, setTestData] = useState(null);
        const [email, setEmail] = useState('');
        const [candidateName, setCandidateName] = useState('');
        const [resumeData, setResumeData] = useState(null);
        const [answers, setAnswers] = useState({});
        const [errorMsg, setErrorMsg] = useState('');
        const [isEvaluating, setIsEvaluating] = useState(false);
        const [isSavingDraft, setIsSavingDraft] = useState(false);

        // CONTROLES DE TRAVA E TEMPO
        const [timeLeft, setTimeLeft] = useState(null);
        const [isLocked, setIsLocked] = useState(false); 
        const [showTimeOverModal, setShowTimeOverModal] = useState(false);
        const [lockCount, setLockCount] = useState(0); 

        // MODAIS DE ENVIO E VALIDAÇÃO
        const [confirmSubmitModal, setConfirmSubmitModal] = useState(false);
        const [showUnansweredModal, setShowUnansweredModal] = useState(false);
        const [unansweredList, setUnansweredList] = useState([]);
        const [isFinished, setIsFinished] = useState(false);

        // CORES DOS CARDS (GRADIENTES VIBRANTES)
        const SECTION_GRADIENTS = [
            'from-blue-600 to-cyan-500', 'from-purple-600 to-fuchsia-500', 'from-emerald-500 to-teal-400',
            'from-orange-500 to-amber-400', 'from-pink-600 to-rose-400', 'from-indigo-600 to-blue-500',
            'from-red-600 to-orange-500', 'from-teal-600 to-emerald-400'
        ];

        // 1. CARREGA OS DADOS DO TESTE
        useEffect(() => {
            const loadTest = async () => {
                try {
                    let appInstance;
                    try { appInstance = getApp(); } catch (e) { appInstance = initializeApp(firebaseConfig); }
                    const db = getFirestore(appInstance);
                    const cleanTestId = testId ? String(testId).trim().replace(/\/+$/, '') : '';
                    
                    const testDocRef = doc(db, `${FIRESTORE_PATH_PUBLIC}/recruitmentTests`, cleanTestId);
                    const testDocSnap = await getDoc(testDocRef);
                    
                    if (testDocSnap.exists()) {
                        const data = testDocSnap.data();
                        setTestData({ id: testDocSnap.id, ...data });
                        if (data.timeLimit || data.durationMinutes) {
                            setTimeLeft((data.timeLimit || data.durationMinutes) * 60);
                        }
                        setStep('login');
                    } else {
                        setErrorMsg('Teste não encontrado ou link inválido.');
                        setStep('error');
                    }
                } catch (error) {
                    console.error("Erro ao carregar teste:", error);
                    setErrorMsg('Falha de conexão: ' + error.message);
                    setStep('error');
                }
            };
            if (testId) loadTest();
        }, [testId]);

        // PROTEÇÃO ANTI-COLA (SÓ ATIVA DEPOIS DE INICIAR O TESTE)
        useEffect(() => {
            if (step !== 'dashboard' || !hasStarted || isFinished) return;

            const handleVisibilityChange = () => {
                if (document.hidden) { setIsLocked(true); setLockCount(prev => prev + 1); }
            };
            const handleWindowBlur = () => {
                setIsLocked(true); setLockCount(prev => prev + 1);
            };

            document.addEventListener('visibilitychange', handleVisibilityChange);
            window.addEventListener('blur', handleWindowBlur);

            return () => {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                window.removeEventListener('blur', handleWindowBlur);
            };
        }, [step, hasStarted, isFinished]);

        // TIMER
        useEffect(() => {
            let timerId;
            if (hasStarted && !isFinished && timeLeft !== null && timeLeft > 0 && !isLocked) {
                timerId = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
            } else if (timeLeft === 0 && hasStarted && !isFinished) {
                setShowTimeOverModal(true);
                handleSubmitTest();
            }
            return () => { if (timerId) clearInterval(timerId); }
        }, [timeLeft, hasStarted, isLocked, isFinished]);

        // RASCUNHO AUTO-SAVE
        useEffect(() => {
            if (step === 'dashboard' && hasStarted && email && !isFinished) {
                const interval = setInterval(() => {
                    saveDraftToDB();
                    saveDraftLocal();
                }, 30000); 
                return () => clearInterval(interval);
            }
        }, [answers, step, hasStarted, timeLeft, resumeData, isFinished, email]);

        const saveDraftToDB = async () => {
            if (!resumeData?.id) return;
            setIsSavingDraft(true);
            try {
                const db = getFirestore();
                const resumeRef = doc(db, `${FIRESTORE_PATH_PUBLIC}/resumes`, resumeData.id);
                await updateDoc(resumeRef, { [`drafts.${testId}`]: answers });
            } catch (e) {
                console.error("Erro ao salvar rascunho no DB:", e);
            } finally {
                setTimeout(() => setIsSavingDraft(false), 1000);
            }
        };

        const saveDraftLocal = () => {
            if (!email) return;
            const normalizedEmail = email.trim().toLowerCase();
            const draftKey = `test_draft_${testId}_${normalizedEmail}`;
            localStorage.setItem(draftKey, JSON.stringify({ answers, timeLeft, lastSaved: new Date().toISOString() }));
        };

        const loadDraftLocal = (candidateEmail) => {
            const draftKey = `test_draft_${testId}_${candidateEmail}`;
            const draftStr = localStorage.getItem(draftKey);
            if (draftStr) {
                try {
                    const parsed = JSON.parse(draftStr);
                    setAnswers(parsed.answers || {});
                } catch (e) { console.error("Erro ao carregar rascunho", e); }
            }
        };

        const handleManualSaveDraft = async () => {
            await saveDraftToDB();
            saveDraftLocal();
            alert("Rascunho salvo com sucesso! Se você fechar a página, o tempo continuará contando ao fundo.");
        };

        const clearDrafts = () => {
            if (email) localStorage.removeItem(`test_draft_${testId}_${email.trim().toLowerCase()}`);
        };

        // 2. VALIDA O LOGIN
        const handleLogin = async (e) => {
            e.preventDefault();
            setErrorMsg('');
            const normalizedEmail = email.trim().toLowerCase();
            if (!normalizedEmail || !normalizedEmail.includes('@')) return alert("Por favor, insira um e-mail válido.");

            try {
                const db = getFirestore(); 
                const resultsRef = collection(db, `${FIRESTORE_PATH_PUBLIC}/test_results`);
                const q = query(resultsRef, where('testId', '==', testId), where('candidateEmail', '==', normalizedEmail));
                const snap = await getDocs(q);

                const resumesRef = collection(db, `${FIRESTORE_PATH_PUBLIC}/resumes`);
                const qRes = query(resumesRef, where('email', '==', normalizedEmail));
                const querySnapshot = await getDocs(qRes);

                if (!querySnapshot.empty) {
                    const resumeDoc = querySnapshot.docs[0];
                    const rData = resumeDoc.data();
                    
                    const testsTaken = rData.testsTaken || [];
                    const alreadyTakenInResume = testsTaken.some(t => t.testId === testId);
                    const allowedRetakes = rData.allowedRetakes || [];

                    if ((!snap.empty || alreadyTakenInResume) && !allowedRetakes.includes(testId)) {
                        setErrorMsg("Você já realizou este teste! O sistema não permite refazê-lo automaticamente.");
                        return;
                    }

                    setResumeData({ id: resumeDoc.id, ...rData });
                    setCandidateName(rData.name);

                    let calculatedTimeLeft = (testData.timeLimit || testData.durationMinutes || 60) * 60;
                    
                    if (rData.draftStartTimes && rData.draftStartTimes[testId]) {
                        setHasStarted(true); // Já havia iniciado antes
                        const startTime = new Date(rData.draftStartTimes[testId]).getTime();
                        const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
                        calculatedTimeLeft = Math.max(0, calculatedTimeLeft - elapsedSeconds);
                        
                        if (calculatedTimeLeft <= 0) {
                            setErrorMsg("O tempo limite para este teste já se esgotou (O cronômetro continua rodando mesmo com a tela fechada).");
                            return;
                        }
                    }
                    setTimeLeft(calculatedTimeLeft);

                    if (rData.drafts && rData.drafts[testId]) setAnswers(rData.drafts[testId]);
                    else loadDraftLocal(normalizedEmail);
                    
                    setStep('dashboard'); 
                } else {
                    setErrorMsg('E-mail não encontrado na base de currículos. Certifique-se de que cadastrou seu currículo conosco primeiro.');
                }
            } catch (error) {
                console.error("Erro ao verificar e-mail:", error);
                setErrorMsg('Erro ao validar acesso. Tente novamente.');
            }
        };

        // 3. INICIA O TESTE
        const handleStartTest = async () => {
            setHasStarted(true);
            if (document.documentElement.requestFullscreen) {
                document.documentElement.requestFullscreen().catch(err => console.log(err));
            }
            if (resumeData && resumeData.id) {
                try {
                    if (!resumeData.draftStartTimes || !resumeData.draftStartTimes[testId]) {
                        const db = getFirestore();
                        const resumeRef = doc(db, `${FIRESTORE_PATH_PUBLIC}/resumes`, resumeData.id);
                        await updateDoc(resumeRef, { [`draftStartTimes.${testId}`]: new Date().toISOString() });
                    }
                } catch (e) { console.warn("Aviso ao registrar início:", e); }
            }
        };

        const handleAnswerChange = (qId, value) => setAnswers(prev => ({ ...prev, [qId]: value }));

        const evaluateTextAnswersWithGemini = async (answersObj) => {
            const textEvaluations = {};
            const textQuestions = [];
            testData.sections.forEach(sec => {
                sec.questions.forEach(q => { if (q.type === 'text' && q.evaluationCriteria) textQuestions.push(q); });
            });
            if (textQuestions.length === 0) return textEvaluations;

            try {
                for (const q of textQuestions) {
                    const candidateAnswer = answersObj[q.id]?.value || answersObj[q.id]; 
                    if (!candidateAnswer || String(candidateAnswer).trim() === '') {
                        textEvaluations[q.id] = { score: 0, feedback: "Em branco." }; continue;
                    }
                    // Encontre a montagem do prompt de correção no seu código e substitua/atualize as instruções para estas:

    const prompt = `
    Você é um corretor automático de provas extremamente rigoroso e analítico.
    Sua tarefa é analisar as respostas de um candidato comparando-as com o gabarito oficial e retornar um JSON estrito.

    REGRAS DE CORREÇÃO (Siga rigidamente):
    1. QUESTÕES COM MÚLTIPLAS LACUNAS/ITENS (Completar, Tabelas, etc):
    - Avalie PROPORCIONALMENTE. Se o candidato respondeu apenas parte da questão (ex: deixou itens em branco), ele NÃO PODE receber a nota máxima. Dê nota parcial (ex: 0.5) ou 0 se estiver muito incompleto.

    2. COMPARAÇÃO EXATA E FALSOS NEGATIVOS:
    - Se a resposta do candidato for igual ao gabarito, IGNORANDO espaços em branco extras, quebras de linha ou diferenças entre maiúsculas e minúsculas, você DEVE considerar CORRETA (nota máxima).
    - Se o candidato respondeu corretamente a essência da palavra (ex: pequenos erros de digitação em questões não-críticas), considere o contexto, mas seja rigoroso em questões técnicas.

    3. ATENÇÃO AO GABARITO:
    - Leia todo o JSON da prova. Se a propriedade "gabarito" ou "correctAnswer" existir na questão, ELA É A VERDADE ABSOLUTA. Nunca diga que não há gabarito se ele estiver no JSON enviado.

    4. FALSOS POSITIVOS:
    - Não seja excessivamente bonzinho. Se a questão for objetiva ou de preenchimento e a resposta do candidato for fundamentalmente diferente do gabarito, a nota é 0.

    Retorne EXCLUSIVAMENTE um objeto JSON estruturado da seguinte forma:
    {
    "pontuacaoTotal": numero,
    "correcoes": [
        {
        "questaoId": "string",
        "notaAtribuida": numero,
        "status": "correta" | "incorreta" | "parcial",
        "justificativa": "Sua explicação detalhada do motivo da nota, apontando erros específicos ou acertos parciais."
        }
    ]
    }
    `;
                    if (typeof window.getGeminiResponse === 'function') {
                        const aiResponse = await window.getGeminiResponse(prompt); 
                        if (aiResponse) {
                            const jsonStr = aiResponse.replace(/```json/g, '').replace(/```/g, '').trim();
                            const parsed = JSON.parse(jsonStr);
                            textEvaluations[q.id] = { score: parsed.score || 0, feedback: parsed.feedback || "Avaliado pela IA." };
                        }
                    }
                }
            } catch (error) { console.error("Erro IA:", error); }
            return textEvaluations;
        };

        const handlePreSubmit = () => {
            const missing = [];
            testData.sections.forEach((sec, sIdx) => {
                sec.questions.forEach((q, qIdx) => {
                    if (q.type === 'context' || !q.required) return;
                    const ansData = answers[q.id];
                    const val = ansData?.value !== undefined ? ansData.value : ansData;
                    const isEmpty = val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0);
                    let isSpreadsheetEmpty = false;
                    if (q.type === 'spreadsheet' && !isEmpty) {
                        if (typeof val === 'object' && Object.keys(val).length === 0) isSpreadsheetEmpty = true;
                    }
                    if (isEmpty || isSpreadsheetEmpty) missing.push(`${sec.title} - Questão ${qIdx + 1}`);
                });
            });

            if (missing.length > 0) {
                setUnansweredList(missing);
                setShowUnansweredModal(true);
            } else {
                setConfirmSubmitModal(true);
            }
        };

        const handleSubmitTest = async () => {
            setIsEvaluating(true);
            try {
                const db = getFirestore();
                let finalTotalPossible = 0; 
                let detailedResults = {};
                let finalScore = 0; // <--- Adicionado para evitar erro de variável

                // LOOP DE CAPTURA RÁPIDA (SEM IA - APENAS SALVA AS RESPOSTAS)
                for (const sec of testData.sections) {
                    for (const q of sec.questions) {
                        if (q.type === 'context') continue;

                    // 1. Definição do Objeto de Resposta (Ajustado para o Candidato)
                    const answerObj = answers[q.id] || {};

                    const rawUserAnswer = answerObj?.value !== undefined ? answerObj.value : answerObj;
                    const userAnswer = rawUserAnswer;
                    const maxPts = parseFloat(q.points !== undefined ? q.points : 10) || 0;
                    
                    finalTotalPossible += maxPts; // Acumula o máximo possível
                    
                    let qScore = 0;
                    let aiFeedback = null;
                    
                    // Verifica se está vazio
                    let isEmpty = false;
                    if (userAnswer === undefined || userAnswer === null || userAnswer === '') isEmpty = true;
                    if (Array.isArray(userAnswer) && userAnswer.length === 0) isEmpty = true;
                    if (typeof userAnswer === 'object' && userAnswer !== null && !Array.isArray(userAnswer) && q.type !== 'file') {
                    if (Object.keys(userAnswer).length === 0) isEmpty = true;
                    else if (Object.values(userAnswer).every(v => v === null || v === '' || String(v).trim() === '')) isEmpty = true;
                    }
                    const isOtherChecked = answerObj?.isOtherChecked || false;

                    if (isEmpty && !isOtherChecked) {
                        qScore = 0;
                        aiFeedback = "Questão não respondida.";
                    }
                    
                    // ====================================================
                    // GRUPO 1: CORREÇÃO EXATA (SEM GASTO DE IA)
                    // ====================================================

                    // A) Múltipla Escolha e Dropdown
                    else if (q.type === 'multi' || q.type === 'dropdown') {
                        if (q.allowMultiple) {
                            const expected = Array.isArray(q.correctAnswer) ? q.correctAnswer : [];
                            const userArr = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
                            if (expected.length > 0) {
                                let correctHits = 0;
                                userArr.forEach(ans => { if (expected.includes(ans)) correctHits++; });
                                // Cálculo proporcional simples (sem penalidade de erro para não negativar)
                                qScore = (correctHits / expected.length) * maxPts;
                                aiFeedback = `Seleção Múltipla: ${correctHits} de ${expected.length} opções corretas encontradas.`;
                            } else {
                                qScore = maxPts; // Sem gabarito = 100%
                            }
                        } else {
                            const valUser = String(userAnswer).trim();
                            const valGab = String(q.correctOption || q.correctAnswer || '').trim();
                            if (valUser === valGab) {
                                qScore = maxPts;
                                aiFeedback = "Resposta correta.";
                            } else {
                                qScore = 0;
                                aiFeedback = "Resposta incorreta.";
                            }
                        }
                    }

                    // B) Verdadeiro ou Falso
                    else if (q.type === 'true_false') {
                        const expected = Array.isArray(q.correctAnswer) ? q.correctAnswer : [];
                        const userArr = Array.isArray(userAnswer) ? userAnswer : [];
                        if (expected.length > 0) {
                            let hits = 0;
                            expected.forEach((gabItem, idx) => { if (userArr[idx] === gabItem) hits++; });
                            qScore = (hits / expected.length) * maxPts;
                            aiFeedback = `V/F: ${hits} acertos de ${expected.length} itens.`;
                        } else {
                            qScore = 0;
                            aiFeedback = "Gabarito não configurado.";
                        }
                    }

                    // C) Arquivo (Anexo)
                    else if (q.type === 'file') {
                        qScore = maxPts;
                        aiFeedback = "Arquivo anexado (Correção manual recomendada).";
                    }

                    // ====================================================
                    // GRUPO 2: CORREÇÃO VIA IA (Discursiva, Lacunas, Planilha Texto)
                    // ====================================================
                    
                    else {
                        // Prepara os dados para o Prompt
                        let promptContext = "";
                        let isSpreadsheet = q.type === 'spreadsheet';
                        let isFillBlank = q.type === 'fill_blank';
                        
                        // Tratamento especial para Planilha (Texto vs Número)
                        if (isSpreadsheet) {
                            // Se a planilha só tem números, corrige localmente para economizar IA
                            const cells = q.tableConfig?.cells || {};
                            const hasTextCell = Object.values(cells).some(c => c.type === 'input' && c.format !== 'number' && c.format !== 'time');
                            
                            if (!hasTextCell) {
                                // CORREÇÃO LOCAL (NUMÉRICA)
                                let totalInputs = 0, correctInputs = 0;
                                Object.keys(cells).forEach(cellId => {
                                    const cell = cells[cellId];
                                    if (cell.type === 'input') {
                                        totalInputs++;
                                        const uVal = userAnswer ? String(userAnswer[cellId] || '').replace(',','.').trim() : '';
                                        const gVal = String(cell.expected || '').replace(',','.').trim();
                                        if(parseFloat(uVal) === parseFloat(gVal)) correctInputs++;
                                    }
                                });
                                qScore = totalInputs > 0 ? (correctInputs / totalInputs) * maxPts : maxPts;
                                aiFeedback = `Planilha Numérica: ${correctInputs} de ${totalInputs} acertos exatos.`;
                                // Pula a chamada de IA abaixo
                                promptContext = null; 
                            } else {
                                // PLANILHA DE TEXTO -> VAI PARA IA
                                const userSheet = typeof userAnswer === 'object' ? JSON.stringify(userAnswer) : String(userAnswer);
                                const gabSheet = JSON.stringify(cells);
                                promptContext = `TIPO: Planilha de Texto.\nCONFIGURAÇÃO E GABARITO (JSON): ${gabSheet}\nRESPOSTAS DO CANDIDATO (JSON): ${userSheet}\nINSTRUÇÃO: Compare cada célula preenchida com o esperado ('expected'). Considere sinônimos e contexto.`;
                            }
                        }
                        
                        // Tratamento para Complete as Frases
                        else if (isFillBlank) {
                            const gabaritoObj = (typeof q.correctAnswer === 'object') ? JSON.stringify(q.correctAnswer) : q.correctAnswer;
                            const userObj = (typeof userAnswer === 'object') ? JSON.stringify(userAnswer) : userAnswer;
                            promptContext = `TIPO: Completar Lacunas.\nFRASE: "${q.text}"\nGABARITO (Lista de lacunas): ${gabaritoObj}\nRESPOSTAS DO CANDIDATO: ${userObj}\nINSTRUÇÃO: Avalie se as palavras preenchidas correspondem ao gabarito. Aceite variações gramaticais leves.`;
                        }
                        
                        // Tratamento para Texto Livre
                        else {
                            const expectedGabarito = String(q.correctAnswer || 'Livre').trim();
                            promptContext = `TIPO: Discursiva.\nPERGUNTA: "${q.text}"\nGABARITO ESPERADO: "${expectedGabarito}"\nRESPOSTA: "${userAnswer}"`;
                        }

                        // CHAMADA À IA (Se necessário)
                        // CHAMADA À IA (Se necessário)
                        if (promptContext) {
                            try {
                                // Delay de segurança (Rate Limit)
                                await new Promise(r => setTimeout(r, 4000));

                                // --- CORREÇÃO: USANDO A CONSTANTE GLOBAL DE PROMPT ---
                                const aiRes = await callGeminiAI(promptContext, SYSTEM_CORRECTION_PROMPT, 'evaluation');
                                
                                let cleanRes = String(aiRes).replace(/```json/gi, '').replace(/```/g, '').trim();
                                const jsonMatch = cleanRes.match(/\{[\s\S]*\}/);
                                
                                if (jsonMatch) {
                                    const parsed = JSON.parse(jsonMatch[0]);
                                    const percent = Math.max(0, Math.min(1, parseFloat(parsed.percentual) || 0));
                                    qScore = maxPts * percent;
                                    aiFeedback = `[IA] ${parsed.analise}`;
                                } else {
                                    throw new Error("JSON inválido");
                                }
                            } catch (err) {
                                console.error("Erro IA:", err);
                                qScore = 0; // Zera nota em caso de falha para forçar revisão
                                aiFeedback = "⚠️ Falha na validação automática. Necessita revisão manual.";
                            }
                        }
                    }

                    finalScore += qScore;
                    
                    // Salva os detalhes da resposta atual
                    detailedResults[q.id] = { 
                        userAnswer, 
                        justifications: answerObj?.justification ? [answerObj.justification] : [], 
                        score: parseFloat(qScore.toFixed(2)), 
                        maxPoints: maxPts, 
                        aiFeedback 
                    };
                    }
                }

                const rawResultToSave = {
                    testId: testData.id, 
                    testTitle: testData.title, 
                    candidateEmail: email.trim().toLowerCase(),
                    candidateName: resumeData.name, 
                    candidateCpf: resumeData.cpf || '', 
                    answers, 
                    score: 0, // Inicia zerado, aguardando o RH
                    totalPossibleScore: finalTotalPossible, 
                    percentage: 0,
                    submittedAt: new Date().toISOString(), 
                    timeSpentSeconds: ((testData.timeLimit || testData.durationMinutes || 60) * 60) - timeLeft,
                    lockViolations: lockCount, 
                    autoSubmitted: timeLeft === 0, 
                    details: detailedResults
                };

                const resultToSave = JSON.parse(JSON.stringify(rawResultToSave));
                const resultsRef = collection(db, `${FIRESTORE_PATH_PUBLIC}/test_results`);
                await addDoc(resultsRef, resultToSave);

                // ATUALIZA O CURRÍCULO DO CANDIDATO
                if (resumeData && resumeData.id) {
                    const resumeRef = doc(db, `${FIRESTORE_PATH_PUBLIC}/resumes`, resumeData.id);
                    await updateDoc(resumeRef, {
                        testScore: 0, // Inicia zerado
                        testsTaken: arrayUnion({ 
                            testId: testData.id, 
                            testTitle: testData.title, 
                            date: new Date().toISOString(), 
                            score: 0, 
                            details: resultToSave.details 
                        }),
                        [`drafts.${testData.id}`]: deleteField(), 
                        [`draftStartTimes.${testData.id}`]: deleteField()
                    });
                }

                clearDrafts(); 
                setIsFinished(true); 
                setConfirmSubmitModal(false); 
                setShowUnansweredModal(false); 
                setStep('finished');
                if (document.fullscreenElement) document.exitFullscreen().catch(err => console.log(err));

            } catch (error) {
                console.error("Erro ao enviar:", error);
                setErrorMsg("Erro ao enviar respostas. Tente novamente.");
            } finally { 
                setIsEvaluating(false); 
            }
        };

        const formatTimeObj = (seconds) => {
            if (seconds === null) return '--:--';
            const m = Math.floor(seconds / 60); const s = seconds % 60;
            return `${m}:${s < 10 ? '0' : ''}${s}`;
        };

        const handleFileUpload = async (qId, e) => {
            const file = e.target.files[0];
            if (!file) return;
            if (file.size > 5 * 1024 * 1024) return alert('Máximo 5MB.');
            try {
                const reader = new FileReader(); reader.readAsDataURL(file);
                reader.onload = () => { handleAnswerChange(qId, { name: file.name, data: reader.result, type: file.type }); };
            } catch (error) { alert("Falha ao processar arquivo."); }
        };

        // --- RENDERIZAÇÕES DE TELA ---
        if (step === 'loading') return <div className={`min-h-screen flex items-center justify-center font-bold animate-pulse ${isDarkMode ? 'bg-slate-950 text-white' : 'bg-slate-50 text-slate-800'}`}>Acessando ambiente seguro...</div>;

        if (step === 'error') return (
            <div className={`min-h-screen flex items-center justify-center p-4 ${isDarkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
                <div className={`p-8 rounded-2xl text-center max-w-md w-full shadow-lg ${isDarkMode ? 'bg-slate-900 border border-slate-800' : 'bg-white'}`}>
                    <AlertTriangle size={48} className="text-red-500 mx-auto mb-4" />
                    <h2 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Acesso Negado</h2>
                    <p className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>{errorMsg}</p>
                </div>
            </div>
        );

        if (step === 'login') return (
            <div className={`min-h-screen flex items-center justify-center p-4 transition-colors duration-300 ${isDarkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
                <button onClick={() => setIsDarkMode(!isDarkMode)} className={`absolute top-4 right-4 p-3 rounded-full shadow-lg transition-transform hover:scale-110 ${isDarkMode ? 'bg-slate-800 text-yellow-400' : 'bg-white text-slate-700'}`}>
                    {isDarkMode ? <Sun size={20}/> : <Moon size={20}/>}
                </button>
                <div className={`rounded-3xl shadow-2xl p-8 sm:p-12 max-w-md w-full border animate-fade-in ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-100'}`}>
                    <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform rotate-3 ${isDarkMode ? 'bg-blue-600/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}><FileText size={40} /></div>
                    <h2 className={`text-3xl font-black text-center tracking-tight mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{testData.title}</h2>
                    <p className={`text-center text-sm mb-8 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Portal do Candidato</p>
                    {errorMsg && <div className={`mb-6 p-4 rounded-xl text-sm font-bold flex items-center gap-3 ${isDarkMode ? 'bg-red-900/30 text-red-400 border border-red-900/50' : 'bg-red-50 text-red-600 border border-red-100'}`}><AlertCircle size={18} className="shrink-0"/> {errorMsg}</div>}
                    <form onSubmit={handleLogin} className="space-y-6">
                        <div>
                            <label className={`text-xs font-bold uppercase tracking-wider block mb-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Seu E-mail Cadastrado</label>
                            <div className="relative">
                                <Mail className={`absolute left-4 top-3.5 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} size={20}/>
                                <input type="email" required placeholder="email@exemplo.com" className={`w-full rounded-xl py-3.5 pl-12 pr-4 focus:ring-2 focus:ring-blue-500 outline-none transition-all font-medium ${isDarkMode ? 'bg-slate-950 border border-slate-800 text-white placeholder-slate-600' : 'bg-gray-50 border border-gray-200 text-slate-900 placeholder-gray-400'}`} value={email} onChange={(e) => setEmail(e.target.value)} />
                            </div>
                        </div>
                        <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl transition-all shadow-lg flex justify-center items-center gap-2">Acessar Avaliação <ChevronRight size={18}/></button>
                    </form>
                </div>
            </div>
        );

        if (step === 'locked') return (
            <div className={`min-h-screen flex items-center justify-center p-4 ${isDarkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
                <div className={`p-8 rounded-3xl text-center max-w-lg w-full shadow-2xl border-t-8 border-red-500 animate-fade-in ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                    <div className="w-24 h-24 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6"><Lock size={48}/></div>
                    <h2 className={`text-2xl font-black mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Aviso de Segurança</h2>
                    <p className={`mb-6 leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Detectamos que você saiu da tela do teste ou trocou de aba. Isso configura uma violação. <b>({lockCount}º aviso)</b>.</p>
                    <button onClick={() => setIsLocked(false)} className="w-full bg-slate-800 hover:bg-slate-700 text-white font-bold py-4 rounded-xl transition-all">Compreendo, retornar ao teste</button>
                </div>
            </div>
        );

        if (step === 'finished') return (
            <div className={`min-h-screen flex items-center justify-center p-4 ${isDarkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
                <div className={`p-10 rounded-3xl text-center max-w-md w-full shadow-2xl border-t-8 border-green-500 animate-fade-in ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                    <div className="w-24 h-24 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle size={48}/></div>
                    <h2 className={`text-3xl font-black tracking-tight mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Sucesso!</h2>
                    <p className={`mb-8 leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Suas respostas foram gravadas. O RH entrará em contato com os próximos passos.</p>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest border-t border-slate-200 dark:border-slate-800 pt-6">Você já pode fechar esta página.</p>
                </div>
            </div>
        );

        // ============================================================================
        // RENDER: DASHBOARD UNIFICADO (GRID DE CARDS OU PERGUNTAS ABERTAS)
        // ============================================================================
        
        // Calcula Progresso Global
        const totalQuestionsCount = testData.sections.reduce((acc, sec) => acc + sec.questions.filter(q => q.type !== 'context').length, 0);
        const answeredCount = Object.keys(answers).filter(k => {
            const val = answers[k]?.value || answers[k];
            return val && (Array.isArray(val) ? val.length > 0 : String(val).trim().length > 0);
        }).length;
        const progressPercent = totalQuestionsCount === 0 ? 0 : (answeredCount / totalQuestionsCount) * 100;

        return (
            <div className={`min-h-screen pb-24 transition-colors duration-300 ${isDarkMode ? 'bg-slate-950 text-slate-300' : 'bg-slate-50 text-slate-800'}`}>
                
                {/* HEADER FIXO TOPO */}
                <div className={`sticky top-0 z-40 border-b shadow-sm backdrop-blur-md ${isDarkMode ? 'bg-slate-900/90 border-slate-800' : 'bg-white/90 border-gray-200'}`}>
                    <div className="h-1.5 w-full bg-gray-200 dark:bg-slate-800">
                        <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${progressPercent}%` }}></div>
                    </div>
                    <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
                        <div>
                            <h1 className={`text-lg sm:text-xl font-black tracking-tight truncate max-w-xs sm:max-w-none ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{testData.title}</h1>
                        </div>
                        
                        {/* INDICADOR GLOBAL DE PERGUNTAS + TEMA (Sempre visível) */}
                        <div className="flex items-center gap-4">
                            <div className="hidden sm:block text-xs font-bold text-slate-500">{answeredCount} de {totalQuestionsCount} respondidas</div>
                            <button onClick={() => setIsDarkMode(!isDarkMode)} className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-slate-800 text-yellow-400' : 'hover:bg-gray-100 text-slate-600'}`} title="Alternar Tema">
                                {isDarkMode ? <Sun size={20}/> : <Moon size={20}/>}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="max-w-5xl mx-auto px-4 mt-8">
                    
                    {/* VISÃO 1: DASHBOARD (CARDS)
                    Aparece se activeSectionIdx for null 
                    */}
                    {activeSectionIdx === null ? (
                        <div className="space-y-6 animate-fade-in">
                            
                            {/* QUADRO DE SAUDAÇÃO E INÍCIO/CRONÔMETRO */}
                            <div className={`p-6 md:p-8 rounded-2xl shadow-lg border relative overflow-hidden flex flex-col md:flex-row justify-between items-center gap-6 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                                <div className="text-center md:text-left">
                                    <h3 className={`text-xl ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                        Prezado(a) <span className={`font-black text-2xl block sm:inline mt-1 sm:mt-0 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{resumeData?.name || candidateName || 'Candidato(a)'}</span>,
                                    </h3>
                                    <p className={`mt-2 text-sm max-w-xl ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                        {hasStarted 
                                            ? 'O teste está em andamento. Clique nos blocos abaixo para abrir e responder às questões. Lembre-se de conferir o cronômetro.' 
                                            : 'Acesse as áreas de conhecimento clicando nos blocos abaixo. Ao clicar em "Iniciar Teste", o cronômetro e as questões serão liberados.'}
                                    </p>
                                </div>
                                
                                <div className="shrink-0 flex flex-col w-full md:w-auto">
                                    {!hasStarted ? (
                                        // BOTAO INICIAR
                                        <button onClick={handleStartTest} className="w-full md:w-auto bg-emerald-500 hover:bg-emerald-600 text-white font-black uppercase tracking-widest py-4 px-8 rounded-xl shadow-[0_0_20px_rgba(16,185,129,0.3)] flex justify-center items-center gap-3 transition-transform hover:scale-105 border-0">
                                            <Play size={22} className="fill-white"/> Iniciar Teste
                                        </button>
                                    ) : (
                                        // CRONÔMETRO E BOTÃO DE ENVIO (Lado a Lado)
                                        <div className="flex flex-col sm:flex-row items-center gap-3 w-full animate-fade-in">
                                            <div className={`flex flex-col items-center justify-center px-6 py-2 rounded-xl border-2 shadow-inner w-full sm:w-auto ${timeLeft < 300 ? 'bg-red-50 text-red-600 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/50 animate-pulse' : (isDarkMode ? 'bg-slate-800 text-blue-400 border-slate-700' : 'bg-blue-50 text-blue-700 border-blue-200')}`}>
                                                <span className="text-[9px] font-bold uppercase tracking-widest opacity-70 mb-0.5">Tempo Restante</span>
                                                <div className="font-mono text-2xl font-black flex items-center gap-2 leading-none">
                                                    <Clock size={20} /> {formatTimeObj(timeLeft)}
                                                </div>
                                            </div>
                                            <button onClick={handlePreSubmit} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-widest py-4 px-6 rounded-xl shadow-lg transition-transform hover:scale-105 flex justify-center items-center gap-2 border-0">
                                                <CheckCircle size={20}/> Finalizar
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* GRID DE CARDS (GRUPOS/SEÇÕES) */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                                {testData.sections.map((sec, sIdx) => {
                                    // Design do Card inspirado nos "Favoritos" do seu sistema
                                    const gradient = SECTION_GRADIENTS[sIdx % SECTION_GRADIENTS.length];
                                    
                                    // Estatísticas da Seção
                                    const secQIds = sec.questions.filter(q => q.type !== 'context').map(q => q.id);
                                    const totalSecQ = secQIds.length;
                                    const ansSecCount = secQIds.filter(id => {
                                        const val = answers[id]?.value || answers[id];
                                        return val && (Array.isArray(val) ? val.length > 0 : String(val).trim().length > 0);
                                    }).length;
                                    const missingCount = totalSecQ - ansSecCount;
                                    const isComplete = totalSecQ > 0 && missingCount === 0;

                                    return (
                                        <div 
                                            key={sIdx} 
                                            onClick={() => {
                                                if (hasStarted) setActiveSectionIdx(sIdx);
                                                else alert("Por favor, clique em 'Iniciar Teste' no botão superior para liberar as questões.");
                                            }}
                                            className={`p-5 rounded-2xl border-0 flex flex-col justify-between transition-all duration-300 relative shadow-lg bg-gradient-to-br ${gradient} text-white overflow-hidden ${hasStarted ? 'cursor-pointer hover:-translate-y-1 hover:shadow-xl' : 'opacity-80 cursor-not-allowed grayscale'}`}
                                            title={hasStarted ? "Clique para abrir as perguntas" : "Inicie o teste primeiro"}
                                        >
                                            <div className="absolute top-0 right-0 h-full w-1/2 bg-white/10 skew-x-12 pointer-events-none"></div>
                                            
                                            {/* Badge Concluído */}
                                            {isComplete && hasStarted && (
                                                <div className="absolute top-3 right-3 bg-white/20 p-1.5 rounded-full backdrop-blur-sm border border-white/20 shadow-sm animate-fade-in">
                                                    <CheckCircle size={16} className="text-white"/>
                                                </div>
                                            )}
                                            
                                            <div className="relative z-10 mb-6">
                                                <div className="p-2.5 bg-white/20 rounded-lg w-fit shadow-inner mb-3 border border-white/10">
                                                    <List size={24} className="text-white drop-shadow-md"/>
                                                </div>
                                                <h4 className="font-bold text-lg leading-tight drop-shadow-md line-clamp-2">{sec.title}</h4>
                                            </div>

                                            <div className="relative z-10 flex justify-between items-end border-t border-white/20 pt-3">
                                                <div className="flex flex-col">
                                                    <span className="text-[9px] uppercase font-bold text-white/80 tracking-wider">Perguntas</span>
                                                    <span className="text-xl font-black drop-shadow-sm">{totalSecQ}</span>
                                                </div>
                                                <div className="flex flex-col items-end">
                                                    <span className="text-[9px] uppercase font-bold text-white/80 tracking-wider">Sem resposta</span>
                                                    <span className={`text-xl font-black drop-shadow-sm ${missingCount === 0 && totalSecQ > 0 ? 'opacity-50' : ''}`}>{missingCount}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>

                            {/* Rascunho Indicador Footer */}
                            {isSavingDraft && (
                                <div className="flex justify-center mt-8">
                                    <span className={`text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 opacity-50 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                        <RefreshCw size={10} className="animate-spin"/> Salvando rascunho automático...
                                    </span>
                                </div>
                            )}
                        </div>
                    ) : (
                        // 
                        // VISÃO 2: RESPONDENDO QUESTÕES DE UMA SEÇÃO (EXPANDIDO)
                        //
                        <div className="space-y-6 animate-fade-in pb-12">
                            
                            {/* BARRA DE NAVEGAÇÃO DA SEÇÃO */}
                            <div className={`sticky top-[70px] z-30 flex flex-col md:flex-row justify-between items-center gap-4 p-4 rounded-2xl shadow-md border backdrop-blur-md ${isDarkMode ? 'bg-slate-900/95 border-slate-700' : 'bg-white/95 border-gray-200'}`}>
                                
                                <div className="flex gap-2 w-full md:w-auto">
                                    {/* Botão Voltar Destacado */}
                                    <button 
                                        onClick={() => setActiveSectionIdx(null)} 
                                        className={`px-4 py-2 font-black text-xs uppercase tracking-wider rounded-lg flex items-center justify-center gap-2 transition-transform hover:scale-105 shadow-sm border ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600' : 'bg-white border-gray-300 text-gray-800 hover:bg-gray-50'}`}
                                    >
                                        <ArrowLeft size={16}/> Menu Principal
                                    </button>

                                    {/* Botão Rascunho Destacado */}
                                    <button 
                                        onClick={handleManualSaveDraft} 
                                        className={`px-4 py-2 font-black text-xs uppercase tracking-wider rounded-lg flex items-center justify-center gap-2 transition-transform hover:scale-105 shadow-md border-0 ${isDarkMode ? 'bg-teal-600 text-white hover:bg-teal-500' : 'bg-teal-500 text-white hover:bg-teal-600'}`}
                                        title="Salvar progresso para não perder os dados"
                                    >
                                        <Save size={16}/> Salvar
                                    </button>
                                </div>
                                
                                <div className="text-center flex-1 w-full md:w-auto">
                                    <span className={`text-[10px] font-bold uppercase tracking-widest block mb-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Você está na área:</span>
                                    <h2 className={`font-black text-lg md:text-xl truncate bg-clip-text text-transparent bg-gradient-to-r ${SECTION_GRADIENTS[activeSectionIdx % SECTION_GRADIENTS.length]}`}>
                                        {testData.sections[activeSectionIdx].title}
                                    </h2>
                                </div>

                                {/* Cronômetro Remanescente */}
                                <div className="flex justify-center w-full md:w-auto shrink-0">
                                    <div className={`flex items-center gap-2 px-4 py-1.5 rounded-lg border-2 shadow-inner ${timeLeft < 300 ? 'bg-red-50 text-red-600 border-red-200 dark:bg-red-900/20 dark:text-red-400' : (isDarkMode ? 'bg-slate-800 text-white border-slate-700' : 'bg-gray-100 text-gray-800 border-gray-200')}`}>
                                        <Clock size={16} /> 
                                        <span className="font-mono font-black text-lg leading-none">{formatTimeObj(timeLeft)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* LISTA DE PERGUNTAS DESTA SEÇÃO */}
                            <div className="space-y-8">
                                {testData.sections[activeSectionIdx].questions.map((q, qIdx) => {
                                    
                                    // Renderização de Contexto
                                    if (q.type === 'context') {
                                        return (
                                            <div key={q.id} className={`p-6 rounded-xl border-2 border-dashed shadow-md mb-8 mt-4 break-inside-avoid ${isDarkMode ? 'border-indigo-800 bg-indigo-950/30' : 'border-indigo-300 bg-indigo-50/50'}`}>
                                                {q.referenceInfo && <h4 className={`font-bold uppercase tracking-wider mb-3 text-sm ${isDarkMode ? 'text-indigo-400' : 'text-indigo-700'}`}>{q.referenceInfo}</h4>}
                                                {q.text && <div className={`whitespace-pre-wrap leading-relaxed text-base mb-4 ${isDarkMode ? 'text-slate-300' : 'text-gray-800'}`}>{q.text}</div>}
                                                {q.imageUrl && <img src={q.imageUrl} alt="Contexto Base" className={`max-w-full rounded-lg shadow border mt-2 mx-auto ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`} />}
                                            </div>
                                        );
                                    }

                                    const ansData = answers[q.id];
                                    const ansVal = ansData?.value !== undefined ? ansData.value : ansData;
                                    const hasAnswer = ansVal !== undefined && ansVal !== '' && (!Array.isArray(ansVal) || ansVal.length > 0);

                                    return (
                                        <div key={q.id} className={`p-6 sm:p-8 rounded-3xl border-2 transition-all shadow-sm relative overflow-hidden ${hasAnswer ? (isDarkMode ? 'border-blue-800 bg-slate-900' : 'border-blue-200 bg-white') : (isDarkMode ? 'border-slate-800 bg-slate-900/50' : 'border-gray-100 bg-gray-50/50')}`}>
                                            
                                            {/* ENUNCIADO (TÍTULO) - DESTAQUE */}
                                            <div className="mb-6">
                                                <div className={`font-black text-lg md:text-xl flex flex-col sm:flex-row items-start gap-2 sm:gap-3 leading-relaxed ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                                                    <div className={`flex items-center justify-center shrink-0 w-8 h-8 rounded-full text-sm font-bold border-2 ${hasAnswer ? 'bg-blue-600 text-white border-blue-600' : (isDarkMode ? 'bg-slate-800 border-slate-600 text-slate-400' : 'bg-gray-200 border-gray-300 text-gray-500')}`}>
                                                        {qIdx + 1}
                                                    </div> 
                                                    
                                                    <div className="flex-1 mt-0.5">
                                                        {q.type !== 'fill_blank' ? (
                                                            <span>{q.text} {q.required && <span className="text-red-500 ml-1" title="Obrigatória">*</span>}</span>
                                                        ) : (
                                                            <div className="leading-relaxed">
                                                                {(q.text || '').split(new RegExp("(\\{[^}]+\\})")).map((part, i) => {
                                                                    if (part.startsWith('{') && part.endsWith('}')) {
                                                                        const ph = part.slice(1, -1);
                                                                        return (
                                                                            <input key={i} type="text" placeholder={ph} 
                                                                                className={`border-b-4 outline-none px-2 mx-1 text-center font-black min-w-[150px] transition-colors ${isDarkMode ? 'border-indigo-500 bg-slate-800 text-indigo-300 placeholder:text-slate-600 focus:bg-slate-700' : 'border-indigo-500 bg-indigo-100 text-indigo-800 placeholder:text-indigo-300 focus:bg-indigo-200'}`} 
                                                                                value={(ansVal || {})[i] || ''} 
                                                                                onChange={e => handleAnswerChange(q.id, { ...ansData, value: { ...(ansVal || {}), [i]: e.target.value } })} 
                                                                            />
                                                                        );
                                                                    }
                                                                    return <span key={i}>{part}</span>;
                                                                })}
                                                                {q.required && <span className="text-red-500 ml-1" title="Obrigatória">*</span>}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {q.imageUrl && (
                                                <div className="mb-6 border-l-4 border-indigo-400 pl-4 py-2 sm:ml-11">
                                                    <img src={q.imageUrl} alt="Ilustração da Questão" className={`max-w-full max-h-80 rounded-lg shadow-md border ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`} />
                                                </div>
                                            )}

                                            {/* ÁREA DE RESPOSTA */}
                                            <div className="sm:ml-11">
                                                
                                                {/* TIPO: LISTA SUSPENSA (DROPDOWN) */}
                                                {q.type === 'dropdown' && (
                                                    <div className="max-w-md">
                                                        {q.allowMultiple ? (
                                                            <div className={`p-3 rounded-xl border ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                                                                <p className="text-[10px] font-bold uppercase mb-2 opacity-60">Selecione uma ou mais opções:</p>
                                                                <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar">
                                                                    {q.options.map((opt, oIdx) => {
                                                                        const isSelected = (ansVal || []).includes(opt);
                                                                        return (
                                                                            <label key={oIdx} className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${isSelected ? (isDarkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-50 text-indigo-800') : 'hover:bg-black/5 dark:hover:bg-white/5'}`}>
                                                                                <input 
                                                                                    type="checkbox" 
                                                                                    className="rounded text-indigo-600 focus:ring-indigo-500"
                                                                                    checked={isSelected}
                                                                                    onChange={() => {
                                                                                        const current = ansVal || [];
                                                                                        const newVal = isSelected ? current.filter(x => x !== opt) : [...current, opt];
                                                                                        handleAnswerChange(q.id, { ...ansData, value: newVal });
                                                                                    }}
                                                                                />
                                                                                <span className="text-sm">{opt}</span>
                                                                            </label>
                                                                        )
                                                                    })}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="relative">
                                                                <select 
                                                                    className={`w-full p-4 pr-10 rounded-xl border-2 appearance-none outline-none focus:ring-2 focus:ring-indigo-500 font-medium transition-colors ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                                                                    value={ansVal || ''}
                                                                    onChange={(e) => handleAnswerChange(q.id, { ...ansData, value: e.target.value })}
                                                                >
                                                                    <option value="">Selecione uma opção...</option>
                                                                    {q.options.map((opt, oIdx) => (
                                                                        <option key={oIdx} value={opt}>{opt}</option>
                                                                    ))}
                                                                </select>
                                                                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 opacity-50 pointer-events-none" size={20}/>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* TIPO: MÚLTIPLA ESCOLHA */}
                                                {(q.type === 'multiple' || q.type === 'multi') && (
                                                    <div className="grid grid-cols-1 gap-3">
                                                        {q.options.map((opt, oIdx) => {
                                                            const isSelected = q.allowMultiple ? (ansVal || []).includes(opt) : ansVal === opt;
                                                            return (
                                                                <label 
                                                                    key={oIdx} 
                                                                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all shadow-sm ${isSelected ? (isDarkMode ? 'bg-indigo-900/30 border-indigo-500 ring-1 ring-indigo-500' : 'bg-indigo-50 border-indigo-500 ring-1 ring-indigo-500') : (isDarkMode ? 'bg-slate-800 border-slate-700 hover:border-indigo-500 hover:bg-slate-800/80' : 'bg-white border-gray-200 hover:border-indigo-300 hover:bg-gray-50')}`}
                                                                >
                                                                    <div className={`w-6 h-6 shrink-0 flex items-center justify-center border-2 transition-colors ${q.allowMultiple ? 'rounded-md' : 'rounded-full'} ${isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : (isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-100 border-gray-300')}`}>
                                                                        {isSelected && (q.allowMultiple ? <Check size={14} strokeWidth={3}/> : <div className="w-2.5 h-2.5 shrink-0 rounded-full bg-white"></div>)}
                                                                    </div>
                                                                    <input 
                                                                        type={q.allowMultiple ? "checkbox" : "radio"} 
                                                                        name={`q-${q.id}`} 
                                                                        className="hidden"
                                                                        checked={isSelected}
                                                                        onChange={() => {
                                                                            let valToSet = opt;
                                                                            if (q.allowMultiple) {
                                                                                const currentArr = Array.isArray(ansVal) ? ansVal : [];
                                                                                valToSet = isSelected ? currentArr.filter(x => x !== opt) : [...currentArr, opt];
                                                                            }
                                                                            handleAnswerChange(q.id, { ...ansData, value: valToSet });
                                                                        }}
                                                                    />
                                                                    <span className={`text-base font-medium ${isSelected ? (isDarkMode ? 'text-indigo-300' : 'text-indigo-900') : (isDarkMode ? 'text-slate-300' : 'text-slate-700')}`}>{opt}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                {/* TIPO: TEXTO LIVRE */}
                                                {q.type === 'text' && (
                                                    <textarea 
                                                        className={`w-full p-5 rounded-2xl border-2 shadow-inner focus:ring-2 focus:ring-indigo-500 outline-none resize-y min-h-[150px] text-base transition-colors ${isDarkMode ? 'bg-slate-950 border-slate-700 text-slate-200 placeholder-slate-600' : 'bg-white border-gray-200 text-slate-800 placeholder-gray-400'}`}
                                                        placeholder="Digite sua resposta detalhada aqui..."
                                                        value={ansVal || ''}
                                                        onChange={(e) => handleAnswerChange(q.id, { ...ansData, value: e.target.value })}
                                                    />
                                                )}

                                                {/* TIPO: ESCALA 1 A 5 */}
                                                {q.type === 'scale' && (
                                                    <div className={`flex justify-between items-center gap-2 p-4 sm:p-6 rounded-2xl border-2 shadow-inner max-w-2xl ${isDarkMode ? 'bg-slate-950 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                        <span className={`text-[10px] sm:text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Discordo</span>
                                                        <div className="flex gap-2 sm:gap-4">
                                                            {[1, 2, 3, 4, 5].map(val => {
                                                                const isSelected = ansVal === val;
                                                                return (
                                                                    <label key={val} className="cursor-pointer group relative">
                                                                        <input type="radio" name={`q-${q.id}`} className="hidden" checked={isSelected} onChange={() => handleAnswerChange(q.id, { ...ansData, value: val })} />
                                                                        <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-full flex items-center justify-center font-black text-lg transition-all duration-300 ${isSelected ? 'bg-indigo-600 text-white scale-110 shadow-lg ring-4 ring-indigo-600/30' : (isDarkMode ? 'bg-slate-800 border-2 border-slate-600 text-slate-400 group-hover:border-indigo-400 group-hover:text-indigo-300' : 'bg-white border-2 border-gray-300 text-gray-500 group-hover:border-indigo-400 group-hover:text-indigo-500')}`}>
                                                                            {val}
                                                                        </div>
                                                                    </label>
                                                                )
                                                            })}
                                                        </div>
                                                        <span className={`text-[10px] sm:text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Concordo</span>
                                                    </div>
                                                )}

                                                {/* TIPO: ARQUIVO */}
                                                {q.type === 'file' && (
                                                    <div className="max-w-lg">
                                                        {ansVal ? (
                                                            <div className={`flex items-center justify-between p-5 rounded-2xl border-2 shadow-sm ${isDarkMode ? 'bg-slate-800 border-indigo-700' : 'bg-indigo-50 border-indigo-200'}`}>
                                                                <div className="flex items-center gap-4 overflow-hidden">
                                                                    <div className="p-3 bg-indigo-600 rounded-lg text-white"><FileText size={24}/></div>
                                                                    <span className={`font-bold truncate ${isDarkMode ? 'text-indigo-200' : 'text-indigo-900'}`}>{ansVal.name || 'Arquivo Anexado'}</span>
                                                                </div>
                                                                <button 
                                                                    onClick={() => { const newAns = {...answers}; delete newAns[q.id]; setAnswers(newAns); }}
                                                                    className="p-2 text-red-500 hover:bg-red-500 hover:text-white dark:hover:bg-red-900/50 rounded-lg transition-colors border border-transparent hover:border-red-500"
                                                                >
                                                                    <Trash2 size={20}/>
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <label className={`flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-2xl cursor-pointer transition-colors ${isDarkMode ? 'border-slate-600 bg-slate-900 hover:bg-slate-800 hover:border-indigo-500' : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-indigo-400'}`}>
                                                                <div className="flex flex-col items-center justify-center pt-5 pb-6 text-center">
                                                                    <UploadCloud className={`mb-3 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} size={36} />
                                                                    <p className={`font-bold ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Clique ou arraste para enviar seu arquivo</p>
                                                                    <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>PDF, DOCX, JPG ou PNG (Máximo 5MB)</p>
                                                                </div>
                                                                <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload(q.id, e)} />
                                                            </label>
                                                        )}
                                                    </div>
                                                )}

                                                {/* TIPO: VERDADEIRO OU FALSO */}
                                                {q.type === 'true_false' && (
                                                    <div className="grid grid-cols-1 gap-3 max-w-3xl">
                                                        {(q.options || []).map((opt, oIdx) => {
                                                            const currentArr = Array.isArray(ansVal) ? ansVal : [];
                                                            const optVal = currentArr[oIdx];
                                                            return (
                                                                <div key={oIdx} className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl border-2 transition-all shadow-sm ${optVal ? (isDarkMode ? 'border-indigo-700 bg-indigo-900/20' : 'border-indigo-400 bg-indigo-50') : (isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-white')}`}>
                                                                    <span className={`text-base font-medium flex-1 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{opt}</span>
                                                                    <div className="flex gap-2 shrink-0 bg-black/5 dark:bg-white/5 p-1.5 rounded-lg">
                                                                        <label className={`px-5 py-2.5 rounded-md border-2 cursor-pointer font-bold text-sm transition-colors flex items-center justify-center gap-2 ${optVal === 'V' ? 'bg-green-600 text-white border-green-600 shadow-md' : (isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-600 hover:border-green-500 hover:text-green-400' : 'bg-white text-gray-600 border-gray-300 hover:border-green-500 hover:text-green-600')}`}>
                                                                            <input type="radio" className="hidden" checked={optVal === 'V'} onChange={() => {
                                                                                const newVal = [...currentArr]; newVal[oIdx] = 'V';
                                                                                handleAnswerChange(q.id, { ...ansData, value: newVal });
                                                                            }} />
                                                                            {optVal === 'V' && <Check size={16}/>} Verdadeiro
                                                                        </label>
                                                                        <label className={`px-5 py-2.5 rounded-md border-2 cursor-pointer font-bold text-sm transition-colors flex items-center justify-center gap-2 ${optVal === 'F' ? 'bg-red-600 text-white border-red-600 shadow-md' : (isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-600 hover:border-red-500 hover:text-red-400' : 'bg-white text-gray-600 border-gray-300 hover:border-red-500 hover:text-red-600')}`}>
                                                                            <input type="radio" className="hidden" checked={optVal === 'F'} onChange={() => {
                                                                                const newVal = [...currentArr]; newVal[oIdx] = 'F';
                                                                                handleAnswerChange(q.id, { ...ansData, value: newVal });
                                                                            }} />
                                                                            {optVal === 'F' && <X size={16}/>} Falso
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                {/* TIPO: PLANILHA */}
                                                {q.type === 'spreadsheet' && (
                                                    <div className={`overflow-x-auto custom-scrollbar border-2 rounded-2xl shadow-inner mt-4 ${isDarkMode ? 'bg-slate-950 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                        <table className="w-full border-collapse">
                                                            <tbody>
                                                                {Array.from({length: q.tableConfig?.rows || 1}).map((_, r) => (
                                                                    <tr key={r}>
                                                                        {Array.from({length: q.tableConfig?.cols || 1}).map((_, c) => {
                                                                            const cellId = `${r}-${c}`;
                                                                            const cell = q.tableConfig?.cells?.[cellId] || { type: 'label', value: '' };
                                                                            return (
                                                                                <td key={c} className={`p-4 border align-middle ${isDarkMode ? 'border-slate-700' : 'border-gray-200'} ${cell.type === 'label' ? (isDarkMode ? 'bg-slate-900' : 'bg-white') : 'bg-transparent'}`}>
                                                                                    {cell.type === 'label' ? (
                                                                                        <div className={`text-sm px-2 ${cell.isBold ? 'font-black uppercase tracking-wider' : 'font-medium'} ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{cell.value}</div>
                                                                                    ) : (
                                                                                        (() => {
                                                                                            const cellValue = ansVal?.[cellId] || '';
                                                                                            const isEmpty = cellValue.trim() === '';
                                                                                            const formatType = cell.format || 'text';
                                                                                            
                                                                                            // 1) Placeholder Inteligente
                                                                                            const placeholderText = formatType === 'number' ? '0,00' : formatType === 'time' ? '00:00' : '____';
                                                                                            
                                                                                            // 2) Cor de Fundo Dinâmica (Destaque se vazio)
                                                                                            const bgClasses = isEmpty 
                                                                                                ? (isDarkMode ? 'bg-yellow-900/20 border-yellow-700/50 placeholder-yellow-700/50' : 'bg-yellow-50 border-yellow-400 placeholder-yellow-600/50') 
                                                                                                : (isDarkMode ? 'bg-slate-800 border-indigo-500 text-white shadow-[0_0_10px_rgba(99,102,241,0.2)]' : 'bg-white border-indigo-400 text-indigo-900 shadow-[0_0_10px_rgba(99,102,241,0.2)]');

                                                                                            return (
                                                                                                <input 
                                                                                                    type="text" 
                                                                                                    className={`w-full min-w-[140px] p-3 rounded-lg border-2 outline-none focus:ring-4 focus:ring-indigo-500/30 text-center font-bold transition-all duration-300 ${bgClasses}`}
                                                                                                    value={cellValue}
                                                                                                    onChange={e => {
                                                                                                        const newVal = { ...(ansVal || {}) };
                                                                                                        newVal[cellId] = e.target.value;
                                                                                                        handleAnswerChange(q.id, { ...ansData, value: newVal });
                                                                                                    }} 
                                                                                                    placeholder={placeholderText} 
                                                                                                />
                                                                                            );
                                                                                        })()
                                                                                    )}
                                                                                </td>
                                                                            );
                                                                        })}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}

                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* BOTTOM NAVEGAÇÃO */}
                            <div className="flex flex-col-reverse sm:flex-row justify-between items-center mt-12 pt-6 border-t dark:border-slate-800 gap-4">
                                <button 
                                    onClick={() => setActiveSectionIdx(null)} 
                                    className={`w-full sm:w-auto px-6 py-3 font-black uppercase tracking-wider rounded-xl transition-transform hover:scale-105 shadow-sm border ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600' : 'bg-white border-gray-300 text-gray-800 hover:bg-gray-50'}`}
                                >
                                    ← Voltar ao Menu Principal
                                </button>
                                
                                {activeSectionIdx < testData.sections.length - 1 ? (
                                    <button 
                                        onClick={() => {
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                            setActiveSectionIdx(activeSectionIdx + 1);
                                        }} 
                                        className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
                                    >
                                        Próximo Grupo <ChevronRight size={18}/>
                                    </button>
                                ) : (
                                    <button 
                                        onClick={() => setActiveSectionIdx(null)} 
                                        className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-black rounded-xl flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
                                    >
                                        <CheckCircle size={18}/> Ir para Finalização
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                </div>
                
                {/* TIME OVER MODAL */}
                {showTimeOverModal && (
                    <div className="fixed inset-0 z-[9999] bg-red-900/90 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className={`rounded-3xl p-10 max-w-md w-full text-center shadow-2xl animate-fade-in ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                            <AlertTriangle size={64} className="text-red-500 mx-auto mb-4 animate-bounce" />
                            <h2 className={`text-3xl font-black mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Tempo Esgotado!</h2>
                            <p className={`mb-8 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                                O cronômetro chegou a zero. Seu teste está sendo salvo e enviado automaticamente neste exato momento.
                            </p>
                            {isEvaluating && (
                                <div className="flex justify-center text-red-500"><Loader2 className="animate-spin" size={32}/></div>
                            )}
                        </div>
                    </div>
                )}

                {/* CONFIRMAR ENVIO */}
                {confirmSubmitModal && (
                    <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4">
                        <div className={`rounded-2xl p-8 max-w-md w-full shadow-2xl border-t-8 border-green-500 text-center ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${isDarkMode ? 'bg-green-900/30' : 'bg-green-100'}`}><CheckCircle size={40} className="text-green-500"/></div>
                            <h3 className="text-green-600 font-black text-2xl mb-2">Confirmar Envio?</h3>
                            <p className={`mb-8 leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Após o envio, suas respostas serão registradas e não poderão mais ser alteradas.</p>
                            <div className="flex gap-4">
                                <button className={`flex-1 py-3 rounded-xl font-bold border-2 ${isDarkMode ? 'bg-slate-800 text-white border-slate-700 hover:bg-slate-700' : 'hover:bg-gray-50 border-gray-200'}`} onClick={() => setConfirmSubmitModal(false)}>Ainda não</button>
                                <button className={`flex-1 py-3 rounded-xl font-black flex items-center justify-center gap-2 ${isDarkMode ? 'bg-green-600 hover:bg-green-500 text-white border-0' : 'bg-green-500 hover:bg-green-600 text-white'}`} onClick={handleSubmitTest} disabled={isEvaluating}>
                                    {isEvaluating ? <><Loader2 size={18} className="animate-spin"/> Enviando...</> : 'Sim, Enviar!'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL DE PERGUNTAS OBRIGATÓRIAS NÃO RESPONDIDAS */}
                {showUnansweredModal && (
                    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className={`rounded-3xl p-8 max-w-md w-full shadow-2xl border-t-8 border-orange-500 ${isDarkMode ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'}`}>
                            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-orange-900/30' : 'bg-orange-100'}`}>
                                <AlertTriangle size={40} className="text-orange-500"/>
                            </div>
                            <h3 className="text-orange-500 font-black text-2xl mb-2 text-center">Atenção!</h3>
                            <p className="text-sm text-center mb-6 opacity-80">Você esqueceu de responder as seguintes perguntas obrigatórias:</p>
                            
                            <div className={`max-h-48 overflow-y-auto p-4 rounded-xl mb-8 border-2 text-sm font-medium ${isDarkMode ? 'bg-slate-950 border-slate-700 text-slate-300' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                                <ul className="list-disc pl-5 space-y-2">
                                    {unansweredList.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                            </div>

                            <div className="flex flex-col gap-3">
                                <button className={`w-full py-4 rounded-xl font-black transition-colors border-2 flex justify-center items-center gap-2 ${isDarkMode ? 'border-slate-700 bg-slate-800 hover:bg-slate-700 text-white' : 'border-gray-300 hover:bg-gray-100 text-gray-800'}`} onClick={() => setShowUnansweredModal(false)}>
                                    <ArrowLeft size={18}/> Voltar e Responder
                                </button>
                                <button className="w-full py-4 rounded-xl font-bold bg-orange-500 hover:bg-orange-600 text-white shadow-lg transition-transform hover:scale-105" onClick={() => {
                                    setShowUnansweredModal(false);
                                    setConfirmSubmitModal(true);
                                }}>
                                    Enviar Assim Mesmo
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    /**
     * ============================================================================
     * COMPONENTE: RECONHECIMENTO (ELOGIOS E CELEBRAÇÕES)
     * ============================================================================
     */


    /* ===============================================================================
     * [END_MOD_PUBLIC_TEST_RUNNER]
     * FIM DO BLOCO: PublicTestRunner
     * [START_MOD_RECOGNITION_TAB]
     * INICIO DO BLOCO: RecognitionTab
     * =============================================================================== */
    const RecognitionTab = ({ data, actions, currentUser, isDarkMode }) => {
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [isAnalyzing, setIsAnalyzing] = useState(false);
        const [validationAlert, setValidationAlert] = useState(null);
        const [showArchived, setShowArchived] = useState(false); // NOVO ESTADO: Controle de Arquivados

        // --- EFEITO: CHUVA DE CONFETES SE HOUVER ELOGIO PARA MIM ---
        useEffect(() => {
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

            // Verifica se há um elogio recente direcionado a este usuário
            const myRecentElogios = (data.recognitions || []).filter(rec => {
                if (rec.archived) return false;
                if (new Date(rec.createdAt) < sevenDaysAgo) return false;
                const tIds = rec.targetIds || [rec.targetId];
                return tIds.includes(currentUser.id);
            });

            if (myRecentElogios.length > 0) {
                const latestId = myRecentElogios[0].id;
                // Verifica no cache do navegador se já comemorou este elogio nesta sessão
                const celebrated = sessionStorage.getItem('celebrated_elogio');

                if (celebrated !== latestId) {
                    const fireConfetti = () => {
                        const duration = 3000; // 3 segundos de chuva
                        const end = Date.now() + duration;

                        const frame = () => {
                            window.confetti({
                                particleCount: 5,
                                angle: 60,
                                spread: 55,
                                origin: { x: 0, y: 0.8 },
                                colors: ['#26ccff', '#a25afd', '#ff5e7e', '#88ff5a', '#fcff42', '#ffa62d', '#ff36ff']
                            });
                            window.confetti({
                                particleCount: 5,
                                angle: 120,
                                spread: 55,
                                origin: { x: 1, y: 0.8 },
                                colors: ['#26ccff', '#a25afd', '#ff5e7e', '#88ff5a', '#fcff42', '#ffa62d', '#ff36ff']
                            });

                            if (Date.now() < end) {
                                requestAnimationFrame(frame);
                            }
                        };
                        frame();
                    };

                    // Carrega a biblioteca de confetes dinamicamente se não existir
                    if (!window.confetti) {
                        const script = document.createElement('script');
                        script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.6.0/dist/confetti.browser.min.js';
                        script.onload = fireConfetti;
                        document.head.appendChild(script);
                    } else {
                        fireConfetti();
                    }

                    sessionStorage.setItem('celebrated_elogio', latestId);
                }
            }
        }, [data.recognitions, currentUser.id]);
        // -------------------------------------------------------------

        const [modalSearch, setModalSearch] = useState(''); // NOVO: Campo de busca dentro do modal
        const [form, setForm] = useState({
            targetType: 'individual',
            targetDeptId: '',
            targetIds: [],
            visibility: 'public', 
            viewers: [],
            message: '',
            emoji: '', // REGRA 4: Começa vazio
            isAnonymous: false 
        });

        const EMOJI_OPTIONS = ['🌟', '👏', '🚀', '💡', '🏆', '🙌', '🎯', '❤️', '🎉', '💪', '🧠', '🤝'];

        // REGRA 1: Mais cores vibrantes adicionadas
        const CARD_COLORS = [
            'from-red-500 to-pink-500', 'from-orange-400 to-red-500', 'from-amber-400 to-orange-500', 
            'from-yellow-400 to-amber-500', 'from-lime-400 to-green-500', 'from-green-400 to-emerald-500', 
            'from-emerald-400 to-teal-500', 'from-teal-400 to-cyan-500', 'from-cyan-500 to-blue-500', 
            'from-sky-400 to-indigo-500', 'from-blue-500 to-indigo-600', 'from-indigo-400 to-violet-500', 
            'from-violet-500 to-purple-500', 'from-purple-500 to-fuchsia-500', 'from-fuchsia-500 to-pink-500', 
            'from-pink-400 to-rose-400', 'from-rose-500 to-red-500', 'from-slate-700 to-slate-900', 
            'from-blue-800 to-indigo-900', 'from-teal-700 to-emerald-900',
            'from-rose-600 to-fuchsia-600', 'from-indigo-500 to-cyan-400', 'from-emerald-500 to-lime-500',
            'from-amber-500 to-pink-500', 'from-violet-600 to-rose-500', 'from-cyan-600 to-purple-600',
            'from-fuchsia-600 to-orange-500', 'from-sky-500 to-emerald-400', 'from-red-600 to-yellow-500'
        ];

        const activeRecognitions = useMemo(() => {
            return (data.recognitions || []).filter(rec => {
                const daysOld = (Date.now() - new Date(rec.createdAt).getTime()) / (1000 * 60 * 60 * 24);
                const isVisuallyArchived = rec.archived || daysOld > 30;

                // Filtra entre a aba principal e a aba de arquivados
                if (showArchived) {
                    if (!isVisuallyArchived) return false;
                } else {
                    if (isVisuallyArchived) return false;
                }

                // Compatibilidade com registros antigos (targetId) e novos (targetIds array)
                const tIds = rec.targetIds || [rec.targetId];

                if (rec.visibility === 'public') return true;
                if (rec.visibility === 'private') {
                    return currentUser.id === rec.authorId || tIds.includes(currentUser.id);
                }
                if (rec.visibility === 'custom') {
                    return currentUser.id === rec.authorId || tIds.includes(currentUser.id) || (rec.viewers || []).includes(currentUser.id);
                }
                return false;
            }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        }, [data.recognitions, currentUser, showArchived]);

        const handlePublish = async () => {
            if (form.targetType === 'individual' && form.targetIds.length === 0) return setValidationAlert({ title: "Atenção", message: "Selecione o colega que deseja elogiar." });
            if (form.targetType === 'department' && !form.targetDeptId) return setValidationAlert({ title: "Atenção", message: "Selecione o departamento." });
            if (!form.message.trim() || form.message.length < 10) return setValidationAlert({ title: "Atenção", message: "Escreva uma mensagem de elogio válida (mínimo 10 caracteres)." });

            setIsAnalyzing(true);
            try {
                const systemPrompt = `Você é um moderador de cultura organizacional. Seu objetivo é analisar se a mensagem a seguir é GENUINAMENTE um elogio, parabenização ou reconhecimento profissional.
                Se a mensagem contiver sarcasmo, ironia, ofensas, cobranças disfarçadas ou não for um elogio, bloqueie.
                Retorne APENAS um JSON estrito no formato: {"isPraise": boolean, "reason": "motivo curto explicando a decisão"}`;
                
                const promptStr = `TEXTO DO ELOGIO: "${form.message}"`;
                
                const aiResponse = await callGeminiAI(promptStr, systemPrompt, 'general');
                const cleanJson = String(aiResponse).replace(/```json/gi, '').replace(/```/g, '').trim();
                const result = JSON.parse(cleanJson);

                if (!result.isPraise) {
                    setValidationAlert({ title: "Mensagem Recusada pela IA", message: `Sua mensagem não foi identificada como um elogio corporativo adequado.\n\nMotivo: ${result.reason}\n\nPor favor, revise o texto e tente novamente.` });
                    setIsAnalyzing(false);
                    return;
                }

                // --- RESOLUÇÃO DOS ALVOS E NOMES ---
                let finalTargetIds = [];
                let finalTargetName = "";

                if (form.targetType === 'individual') {
                    finalTargetIds = form.targetIds;
                    if (finalTargetIds.length === 0) finalTargetName = 'Colegas';
                    else if (finalTargetIds.length === 1) finalTargetName = data.users.find(u => u.id === finalTargetIds[0])?.name || 'Colega';
                    else finalTargetName = `${finalTargetIds.length} Colegas`;
                } 
                else if (form.targetType === 'department') {
                    const deptUsers = data.users.filter(u => u.departmentId === form.targetDeptId);
                    finalTargetIds = form.targetIds.length > 0 ? form.targetIds : deptUsers.map(u => u.id);
                    const deptName = data.departments.find(d => d.id === form.targetDeptId)?.name || 'Depto';
                    
                    if (form.targetIds.length === 0) finalTargetName = `Todos da Equipe (${deptName})`;
                    else if (form.targetIds.length === 1) finalTargetName = data.users.find(u => u.id === form.targetIds[0])?.name || 'Colega';
                    else finalTargetName = `Parte da Equipe (${deptName})`;
                } 
                else if (form.targetType === 'management') {
                    const gestoresEmails = (data.managers || [])
                        .filter(m => m.email)
                        .map(m => m.email.trim().toLowerCase());
                        
                    const managers = (data.users || []).filter(u => {
                        const uEmail = (u.email || '').trim().toLowerCase();
                        return gestoresEmails.includes(uEmail);
                    });
                    
                    finalTargetIds = form.targetIds.length > 0 ? form.targetIds : managers.map(u => u.id);
                    
                    if (form.targetIds.length === 0) finalTargetName = `Toda a Gestão`;
                    else if (form.targetIds.length === 1) finalTargetName = data.users.find(u => u.id === form.targetIds[0])?.name || 'Gestor';
                    else finalTargetName = `Equipe de Liderança`;
                }

                // Garante que não enviou vazio
                if (finalTargetIds.length === 0) {
                    setValidationAlert({ title: "Erro", message: "Nenhum usuário encontrado para a seleção informada." });
                    setIsAnalyzing(false);
                    return;
                }

                const payload = {
                    id: Date.now().toString(),
                    authorId: form.isAnonymous ? 'anonymous' : currentUser.id,
                    realAuthorId: currentUser.id,
                    authorName: form.isAnonymous ? 'Colega Anônimo' : currentUser.name,
                    isAnonymous: form.isAnonymous,
                    
                    // Novos campos
                    targetType: form.targetType,
                    targetIds: finalTargetIds,
                    targetName: finalTargetName,
                    
                    visibility: form.visibility,
                    viewers: form.viewers,
                    message: form.message.trim(),
                    emoji: form.emoji,
                    createdAt: new Date().toISOString(),
                    archived: false,
                    colorIndex: Math.floor(Math.random() * CARD_COLORS.length)
                };

                await actions.saveItem('recognitions', payload);
                if (actions.logAction) {
                        actions.logAction('NOVO_ELOGIO', {
                            entity: 'RECOGNITIONS',
                            recordId: payload.id,
                            details: [
                                { campo: 'autor', de: '', para: form.isAnonymous ? 'Anônimo' : currentUser.name },
                                { campo: 'tipo', de: '', para: form.targetType }
                            ]
                        });
                    }
                
                setIsModalOpen(false);
                setModalSearch(''); // Limpa a busca ao fechar
                setForm({ targetType: 'individual', targetDeptId: '', targetIds: [], visibility: 'public', viewers: [], message: '', emoji: '', isAnonymous: false });
                setValidationAlert({ title: "Sucesso!", message: "Elogio publicado! 🎉" });
                
            } catch (e) {
                console.error("Erro na IA:", e);
                setValidationAlert({ title: "Erro no Sistema", message: "Não foi possível validar seu elogio no momento. Tente novamente." });
            } finally {
                setIsAnalyzing(false);
            }
        };

        const handleArchiveToggle = async (rec) => {
            const isArchiving = !rec.archived;
            const msg = isArchiving ? "Deseja arquivar este elogio? Ele sairá do mural principal." : "Deseja desarquivar este elogio? Ele voltará para o mural principal.";
            if (window.confirm(msg)) {
                await actions.saveItem('recognitions', { ...rec, archived: isArchiving, archivedAt: isArchiving ? new Date().toISOString() : null });
            }
        };

        return (
            <div className="space-y-6 animate-fade-in h-full flex flex-col">
                {/* CABEÇALHO */}
                <div className={`p-6 rounded-xl shadow-lg border relative overflow-hidden shrink-0 ${isDarkMode ? 'bg-gradient-to-r from-slate-900 to-slate-800 border-slate-700' : 'bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 border-transparent text-white'}`}>
                    <div className="absolute right-0 top-0 h-full w-1/3 bg-white/10 skew-x-12 pointer-events-none"></div>
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4 relative z-10">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-white/20 rounded-full shadow-inner text-white">
                                <Award size={28} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-black text-white drop-shadow-sm">Mural de Reconhecimentos</h2>
                                <p className="text-sm font-medium text-white/90 drop-shadow-sm">Celebre conquistas, agradeça e inspire sua equipe!</p>
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <button onClick={() => setShowArchived(!showArchived)} className="bg-black/20 hover:bg-black/30 text-white shadow-xl font-bold px-4 py-3 rounded-xl border-0 transition-transform hover:scale-105 flex items-center justify-center tracking-wide backdrop-blur-sm">
                                {showArchived ? <><Star size={18} className="mr-2"/> Ver Ativos</> : <><Archive size={18} className="mr-2"/> Ver Arquivados</>}
                            </button>
                            <button onClick={() => setIsModalOpen(true)} className="!bg-white !text-orange-600 hover:!bg-orange-50 shadow-xl font-black px-6 py-3 rounded-xl border-0 transition-transform hover:scale-105 flex items-center justify-center tracking-wide">
                                <Sparkles size={18} className="mr-2"/> Escrever Novo Elogio
                            </button>
                        </div>
                    </div>
                </div>

                {/* MURAL (GRID DE CARDS) */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
                    {activeRecognitions.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full opacity-50 p-10">
                            <Star size={64} className="mb-4 text-gray-400" />
                            <h3 className="text-lg font-bold">O mural está vazio</h3>
                            <p className="text-sm">Seja o primeiro a reconhecer um colega hoje!</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-20">
                            {activeRecognitions.map(rec => {
                                // 1. Identifica se o usuário logado está entre os recebedores
                                const tIds = rec.targetIds || [rec.targetId];
                                const isRecipient = tIds.includes(currentUser.id);
                                const isAuthor = currentUser.id === rec.authorId || currentUser.id === rec.realAuthorId;
                                const isManager = ['Gestor', 'SuperAdm'].includes(currentUser.role) || currentUser.permissions?.viewGestao;

                                // Arquivar visível para Gestão, Quem Enviou e Quem Recebeu
                                const canArchive = isAuthor || isRecipient || isManager;
                                const gradientClass = CARD_COLORS[rec.colorIndex % CARD_COLORS.length];

                                // --- NOVA LÓGICA DE NOMES E MÚLTIPLOS USUÁRIOS ---
                                const targetUsers = (data.users || []).filter(u => tIds.includes(u.id));
                                const allTargetNames = targetUsers.map(u => u.name);

                                // Helper: Primeiro Nome (Adiciona Sobrenome se houver duplicidade no grupo)
                                const formatSmartName = (fullName) => {
                                    if (!fullName) return '';
                                    const parts = fullName.split(' ');
                                    const firstName = parts[0];
                                    const sameFirstNames = allTargetNames.filter(n => n.split(' ')[0] === firstName);
                                    if (sameFirstNames.length > 1 && parts.length > 1) {
                                        return `${firstName} ${parts[1]}`;
                                    }
                                    return firstName;
                                };

                                let displayName = rec.targetName;
                                let subTextDisplay = null;
                                let tooltipNames = null;

                                if (tIds.length > 1) {
                                    // Popup mostra TODOS os envolvidos com nome formatado inteligente
                                    tooltipNames = targetUsers.map(u => formatSmartName(u.name)).join('\n');

                                    if (isRecipient) {
                                        // Mostra nome e subtexto de +X colegas
                                        displayName = formatSmartName(currentUser.name);
                                        const othersCount = tIds.length - 1;
                                        subTextDisplay = `+ ${othersCount} colega${othersCount > 1 ? 's' : ''}`;
                                    } else {
                                        // Se eu não estou, mostra o DPTO/Gestão inteiro
                                        displayName = rec.targetName;
                                    }
                                } else if (tIds.length === 1) {
                                    // Apenas 1 pessoa
                                    const singleName = targetUsers[0] ? formatSmartName(targetUsers[0].name) : rec.targetName;
                                    displayName = isRecipient ? `${singleName} (Você)` : singleName;
                                }

                                return (
                                    <div key={rec.id} className={`rounded-2xl shadow-lg border-0 overflow-hidden flex flex-col transition-all hover:-translate-y-1 hover:shadow-xl bg-gradient-to-br ${gradientClass} text-white relative group`}>
                                        
                                        {/* Botões de Ação Ocultos (Hover) */}
                                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                                            {canArchive && (
                                                <button onClick={() => handleArchiveToggle(rec)} className="bg-black/20 hover:bg-black/40 text-white p-2 rounded-full transition-colors" title={rec.archived ? "Desarquivar Elogio" : "Arquivar Elogio (Ocultar do Mural)"}>
                                                    {rec.archived ? <RefreshCw size={14}/> : <Archive size={14}/>}
                                                </button>
                                            )}
                                            {isManager && (
                                                <button onClick={() => { if(window.confirm('Tem certeza que deseja EXCLUIR DEFINITIVAMENTE este elogio?')) actions.genericDelete('recognitions', rec.id); }} className="bg-red-500/80 hover:bg-red-600 text-white p-2 rounded-full transition-colors" title="Excluir Permanentemente (Apenas Gestão)">
                                                    <Trash2 size={14}/>
                                                </button>
                                            )}
                                        </div>

                                        {/* Efeito de Fundo */}
                                        {rec.emoji && (
                                            <div className="absolute -bottom-6 -right-6 text-9xl opacity-10 pointer-events-none transform -rotate-12">
                                                {rec.emoji}
                                            </div>
                                        )}

                                        <div className="p-5 flex-1 flex flex-col relative z-10">
                                            <div className="flex justify-between items-start mb-4 gap-2">
                                                <div className="text-4xl drop-shadow-md shrink-0">
                                                    {rec.emoji || <Sparkles className="opacity-30" size={32}/>}
                                                </div>
                                                
                                                <div className="flex flex-col items-end text-right min-w-0">
                                                    
                                                    {/* DESTAQUE DE NOME COM TOOLTIP E SUBTEXTO */}
                                                    <div className="relative group/name inline-block max-w-full">
                                                        <div className="bg-white/20 px-3 py-1.5 rounded-lg backdrop-blur-md border border-white/40 shadow-lg flex flex-col items-end truncate">
                                                            <span className={`text-[12px] md:text-[14px] font-black tracking-wide text-white drop-shadow-md uppercase ${tooltipNames ? 'cursor-help' : ''}`}>
                                                                {!isRecipient && rec.targetType === 'department' ? 'DPTO - ' : (!isRecipient && rec.targetType === 'management' ? 'GST - ' : '')}
                                                                {displayName}
                                                            </span>
                                                            {subTextDisplay && (
                                                                <span className="text-[9px] font-bold text-white/90 uppercase tracking-widest mt-0.5">
                                                                    {subTextDisplay}
                                                                </span>
                                                            )}
                                                        </div>
                                                        
                                                        {/* POPUP DE MÚLTIPLOS USUÁRIOS (HOVER) */}
                                                        {tooltipNames && (
                                                            <div className={`absolute top-full right-0 mt-2 w-max max-w-[250px] text-[10px] p-2 rounded-lg shadow-xl opacity-0 invisible group-hover/name:opacity-100 group-hover/name:visible transition-all z-50 text-left border whitespace-pre-wrap ${isDarkMode ? 'bg-slate-800 text-white border-slate-600' : 'bg-white text-gray-800 border-gray-200'}`}>
                                                                <div className={`font-bold mb-1 border-b pb-1 ${isDarkMode ? 'text-yellow-400 border-slate-600' : 'text-orange-600 border-gray-200'}`}>
                                                                    Todos os Pessoas Mencionadas:
                                                                </div>
                                                                <div className="max-h-32 overflow-y-auto custom-scrollbar leading-relaxed font-medium">
                                                                    {tooltipNames}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <span className="text-[9px] mt-1 opacity-80 font-medium">
                                                        {new Date(rec.createdAt).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            </div>

                                            <p className="text-sm font-medium leading-relaxed mb-6 flex-1 text-white/95 whitespace-pre-wrap">
                                                "{rec.message}"
                                            </p>

                                            <div className="mt-auto border-t border-white/20 pt-3 flex justify-between items-center">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold border border-white/30">
                                                        {rec.isAnonymous ? '?' : rec.authorName.charAt(0)}
                                                    </div>
                                                    <span className="text-[10px] font-bold opacity-90">
                                                        De: {rec.authorName} 
                                                        {rec.isAnonymous && currentUser.id === rec.realAuthorId && " (Você)"}
                                                    </span>
                                                </div>
                                                
                                                {/* Tag de Privacidade */}
                                                {rec.visibility !== 'public' && (
                                                    <Lock size={12} className="opacity-60" title="Elogio Restrito"/>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* MODAL NOVO ELOGIO */}
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => !isAnalyzing && setIsModalOpen(false)}>
                        <div className={`p-6 rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[95vh] ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            
                            {/* HEADER DO MODAL */}
                            <div className="flex justify-between items-center border-b pb-3 mb-4 shrink-0 dark:border-slate-700">
                                <h3 className="font-bold text-xl flex items-center gap-2 text-orange-500">
                                    <Sparkles size={24}/> Enviar Reconhecimento
                                </h3>
                                {!isAnalyzing && <button onClick={() => { setIsModalOpen(false); setModalSearch(''); }} className="text-gray-400 hover:text-red-500"><X size={20}/></button>}
                            </div>

                            {/* CORPO DO MODAL (Grid 2 colunas para não esticar) */}
                            <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                                
                                {/* COLUNA ESQUERDA: SELEÇÃO DE ALVOS */}
                                <div className={`p-4 rounded-xl border flex flex-col h-full min-h-[350px] ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                    <label className={`text-xs font-bold block mb-3 shrink-0 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>1. Tipo de Destinatário</label>
                                    <div className="flex gap-2 mb-4 shrink-0">
                                        <button onClick={() => { setForm({...form, targetType: 'individual', targetIds: [], targetDeptId: ''}); setModalSearch(''); }} className={`flex-1 py-2 text-xs font-bold rounded-lg border transition-all ${form.targetType === 'individual' ? (isDarkMode ? 'bg-orange-900/40 text-orange-300 border-orange-500 shadow-sm' : 'bg-orange-100 text-orange-700 border-orange-500 shadow-sm') : (isDarkMode ? 'bg-slate-800 text-slate-400 border-slate-600' : 'bg-white text-gray-500 border-gray-200')}`}>Colega(s)</button>
                                        <button onClick={() => { setForm({...form, targetType: 'department', targetIds: [], targetDeptId: ''}); setModalSearch(''); }} className={`flex-1 py-2 text-xs font-bold rounded-lg border transition-all ${form.targetType === 'department' ? (isDarkMode ? 'bg-orange-900/40 text-orange-300 border-orange-500 shadow-sm' : 'bg-orange-100 text-orange-700 border-orange-500 shadow-sm') : (isDarkMode ? 'bg-slate-800 text-slate-400 border-slate-600' : 'bg-white text-gray-500 border-gray-200')}`}>Departamento</button>
                                        <button onClick={() => { setForm({...form, targetType: 'management', targetIds: [], targetDeptId: ''}); setModalSearch(''); }} className={`flex-1 py-2 text-xs font-bold rounded-lg border transition-all ${form.targetType === 'management' ? (isDarkMode ? 'bg-orange-900/40 text-orange-300 border-orange-500 shadow-sm' : 'bg-orange-100 text-orange-700 border-orange-500 shadow-sm') : (isDarkMode ? 'bg-slate-800 text-slate-400 border-slate-600' : 'bg-white text-gray-500 border-gray-200')}`}>Gestão</button>
                                    </div>

                                    {/* OPÇÃO 1: COLABORADOR INDIVIDUAL */}
                                    {form.targetType === 'individual' && (
                                        <div className="flex flex-col flex-1 animate-fade-in min-h-0">
                                            <label className={`text-[10px] uppercase font-bold block mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Quais colegas? (Selecione um ou mais)</label>
                                            <div className="relative mb-2 shrink-0">
                                                <Search size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                                                <input 
                                                    type="text" 
                                                    placeholder="Pesquisar colega..." 
                                                    value={modalSearch} 
                                                    onChange={(e) => setModalSearch(e.target.value)} 
                                                    className={`w-full pl-8 p-1.5 text-xs rounded border outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white focus:border-orange-500' : 'bg-white border-gray-300 focus:border-orange-400 text-gray-800'}`}
                                                />
                                            </div>
                                            <div className={`flex-1 overflow-y-auto custom-scrollbar space-y-1 border rounded-lg p-2 ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-white'}`}>
                                                {(data.users || [])
                                                    .filter(u => u.id !== currentUser.id && u.name.toLowerCase().includes(modalSearch.toLowerCase()))
                                                    .sort((a,b) => a.name.localeCompare(b.name))
                                                    .map(u => (
                                                    <label key={u.id} className={`flex items-center gap-2 text-xs p-1.5 rounded cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-200' : 'hover:bg-gray-100 text-gray-700'}`}>
                                                        <input type="checkbox" checked={form.targetIds.includes(u.id)} onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            setForm(prev => ({ ...prev, targetIds: checked ? [...prev.targetIds, u.id] : prev.targetIds.filter(id => id !== u.id) }));
                                                        }} className="rounded text-orange-500 focus:ring-orange-500"/>
                                                        {u.name} <span className={`text-[9px] opacity-60 ml-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>({data.departments.find(d => d.id === u.departmentId)?.name || 'Geral'})</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* OPÇÃO 2: DEPARTAMENTO */}
                                    {form.targetType === 'department' && (
                                        <div className="flex flex-col flex-1 animate-fade-in min-h-0 space-y-3">
                                            <select 
                                                className={`w-full p-2.5 rounded-lg border text-sm outline-none shrink-0 focus:ring-2 focus:ring-orange-500 ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                                                value={form.targetDeptId} 
                                                onChange={e => { setForm({...form, targetDeptId: e.target.value, targetIds: []}); setModalSearch(''); }}
                                            >
                                                <option value="">Selecione o Departamento...</option>
                                                {(data.departments || []).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                            </select>
                                            
                                            {form.targetDeptId && (
                                                <div className={`flex flex-col flex-1 min-h-0 pt-2 border-t animate-fade-in ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                                    <label className={`text-[10px] uppercase font-bold block mb-2 shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Quem do departamento? (Vazio = TODOS)</label>
                                                    
                                                    <div className="relative mb-2 shrink-0">
                                                        <Search size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                                                        <input 
                                                            type="text" 
                                                            placeholder="Pesquisar colega..." 
                                                            value={modalSearch} 
                                                            onChange={(e) => setModalSearch(e.target.value)} 
                                                            className={`w-full pl-8 p-1.5 text-xs rounded border outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white focus:border-orange-500' : 'bg-white border-gray-300 focus:border-orange-400 text-gray-800'}`}
                                                        />
                                                    </div>

                                                    <div className={`flex-1 overflow-y-auto custom-scrollbar space-y-1 border rounded-lg p-2 ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-white'}`}>
                                                        {(data.users || [])
                                                            .filter(u => u.departmentId === form.targetDeptId && u.id !== currentUser.id && u.name.toLowerCase().includes(modalSearch.toLowerCase()))
                                                            .map(u => (
                                                            <label key={u.id} className={`flex items-center gap-2 text-xs p-1.5 rounded cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-200' : 'hover:bg-gray-100 text-gray-700'}`}>
                                                                <input type="checkbox" checked={form.targetIds.includes(u.id)} onChange={(e) => {
                                                                    const checked = e.target.checked;
                                                                    setForm(prev => ({ ...prev, targetIds: checked ? [...prev.targetIds, u.id] : prev.targetIds.filter(id => id !== u.id) }));
                                                                }} className="rounded text-orange-500 focus:ring-orange-500"/>
                                                                {u.name}
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* OPÇÃO 3: GESTÃO */}
                                    {form.targetType === 'management' && (
                                        <div className="flex flex-col flex-1 animate-fade-in min-h-0">
                                            <label className={`text-[10px] uppercase font-bold block mb-2 shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Quais gestores? (Vazio = Gestão Inteira)</label>
                                            
                                            <div className="relative mb-2 shrink-0">
                                                <Search size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                                                <input 
                                                    type="text" 
                                                    placeholder="Pesquisar gestor..." 
                                                    value={modalSearch} 
                                                    onChange={(e) => setModalSearch(e.target.value)} 
                                                    className={`w-full pl-8 p-1.5 text-xs rounded border outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white focus:border-orange-500' : 'bg-white border-gray-300 focus:border-orange-400 text-gray-800'}`}
                                                />
                                            </div>

                                            <div className={`flex-1 overflow-y-auto custom-scrollbar space-y-1 border rounded-lg p-2 ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-white'}`}>
                                                {(() => {
                                                    const gestoresEmails = (data.managers || []).filter(m => m.email).map(m => m.email.trim().toLowerCase());
                                                    const managersList = (data.users || []).filter(u => {
                                                        if (u.id === currentUser.id) return false;
                                                        const uEmail = (u.email || '').trim().toLowerCase();
                                                        const matchesSearch = u.name.toLowerCase().includes(modalSearch.toLowerCase());
                                                        return gestoresEmails.includes(uEmail) && matchesSearch;
                                                    });
                                                    
                                                    if (managersList.length === 0) return <p className="text-xs italic p-2 text-gray-400">Nenhum gestor encontrado com esse nome.</p>;

                                                    return managersList.map(u => (
                                                        <label key={u.id} className={`flex items-center gap-2 text-xs p-1.5 rounded cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-200' : 'hover:bg-gray-100 text-gray-700'}`}>
                                                            <input type="checkbox" checked={form.targetIds.includes(u.id)} onChange={(e) => {
                                                                const checked = e.target.checked;
                                                                setForm(prev => ({ ...prev, targetIds: checked ? [...prev.targetIds, u.id] : prev.targetIds.filter(id => id !== u.id) }));
                                                            }} className="rounded text-orange-500 focus:ring-orange-500"/>
                                                            {u.name} <span className={`text-[9px] opacity-60 ml-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>({data.departments.find(d => d.id === u.departmentId)?.name || 'Geral'})</span>
                                                        </label>
                                                    ));
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                {/* COLUNA DIREITA: OPÇÕES E MENSAGEM */}
                                <div className="flex flex-col h-full space-y-4">
                                    
                                    {/* Anônimo e Visibilidade Lado a Lado (AGORA AUMENTADO NA LARGURA TOTAL) */}
                                    <div className="grid grid-cols-2 gap-3 shrink-0">
                                        <label className={`flex flex-col justify-center p-2 rounded-lg border cursor-pointer transition-colors ${form.isAnonymous ? (isDarkMode ? 'bg-orange-900/20 border-orange-800' : 'bg-orange-50 border-orange-200') : (isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-200')}`}>
                                            <div className="flex justify-between items-center mb-1">
                                                <span className={`text-xs font-bold flex items-center gap-1 ${form.isAnonymous ? 'text-orange-500' : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}>
                                                    <Lock size={12}/> Anônimo?
                                                </span>
                                                <input 
                                                    type="checkbox" 
                                                    checked={form.isAnonymous} 
                                                    onChange={e => setForm({...form, isAnonymous: e.target.checked})} 
                                                    className="w-4 h-4 rounded text-orange-500 focus:ring-orange-500"
                                                />
                                            </div>
                                            <span className={`text-[9px] leading-tight ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                                                Seu nome ficará oculto.
                                            </span>
                                        </label>

                                        <div className={`p-2 rounded-lg border flex flex-col justify-center ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                                            <label className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Visibilidade</label>
                                            <select 
                                                className={`w-full p-1.5 rounded border text-xs outline-none focus:ring-2 focus:ring-orange-500 ${isDarkMode ? 'bg-slate-800 border-slate-500 text-white' : 'bg-white border-gray-300'}`}
                                                value={form.visibility} 
                                                onChange={e => setForm({...form, visibility: e.target.value, viewers: []})}
                                            >
                                                <option value="public">Público (Mural)</option>
                                                <option value="private">Particular</option>
                                                <option value="custom">Personalizado</option>
                                            </select>
                                        </div>
                                    </div>

                                    {form.visibility === 'custom' && (
                                        <div className="p-3 rounded border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 animate-fade-in shrink-0">
                                            <label className={`text-[10px] font-bold uppercase block mb-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>Quem mais pode ver?</label>
                                            <div className="max-h-24 overflow-y-auto custom-scrollbar space-y-1">
                                                {(data.users || []).filter(u => u.id !== currentUser.id && !form.targetIds.includes(u.id)).map(u => (
                                                    <label key={u.id} className={`flex items-center gap-2 text-xs p-1 rounded cursor-pointer ${form.viewers.includes(u.id) ? 'bg-blue-100 text-blue-800 font-bold dark:bg-blue-800/50 dark:text-blue-200' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}>
                                                        <input type="checkbox" checked={form.viewers.includes(u.id)} onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            setForm(prev => ({ ...prev, viewers: checked ? [...prev.viewers, u.id] : prev.viewers.filter(id => id !== u.id) }));
                                                        }} />
                                                        {u.name}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="shrink-0">
                                        <label className={`text-[10px] uppercase font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Emoji Destaque (Opcional)</label>
                                        <div className="flex flex-wrap gap-1.5">
                                            {EMOJI_OPTIONS.map(em => {
                                                const isSelected = form.emoji === em;
                                                let btnClass = 'w-8 h-8 text-lg flex items-center justify-center rounded-lg border transition-all ';
                                                if (isSelected) {
                                                    btnClass += isDarkMode ? 'bg-orange-900/40 border-orange-500 scale-110 shadow-md' : 'bg-orange-100 border-orange-500 scale-110 shadow-md';
                                                } else {
                                                    btnClass += isDarkMode ? 'bg-slate-800 border-slate-700 hover:bg-slate-700' : 'bg-white border-gray-200 hover:bg-gray-100'; 
                                                }
                                                return (
                                                    <button key={em} onClick={() => setForm({...form, emoji: isSelected ? '' : em})} className={btnClass}>
                                                        {em}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="flex-1 flex flex-col min-h-[120px]">
                                        <label className={`text-[10px] uppercase font-bold block mb-1 flex items-center gap-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                                            Sua Mensagem <Sparkles size={12} className="text-orange-500"/>
                                        </label>
                                        <textarea 
                                            className={`w-full flex-1 p-3 rounded-lg border text-sm outline-none resize-none focus:ring-2 focus:ring-orange-500 custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-gray-300'}`}
                                            placeholder="Escreva algo inspirador... O sistema analisará o texto para garantir que seja um elogio construtivo!"
                                            value={form.message}
                                            onChange={e => setForm({...form, message: e.target.value})}
                                        ></textarea>
                                        <p className="text-[9px] text-gray-400 mt-1">* Mensagens com cobranças ou ironias serão bloqueadas pela moderação IA.</p>
                                    </div>
                                </div>
                            </div>

                            {/* FOOTER DO MODAL */}
                            <div className={`flex justify-end gap-3 mt-4 pt-4 border-t shrink-0 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                <button 
                                    onClick={() => { setIsModalOpen(false); setModalSearch(''); }} 
                                    disabled={isAnalyzing} 
                                    className={isDarkMode ? "!bg-slate-700 hover:!bg-slate-600 !text-white px-5 py-2.5 rounded-lg font-bold text-sm border !border-slate-600 transition-colors" : "!bg-gray-200 hover:!bg-gray-300 !text-gray-800 px-5 py-2.5 rounded-lg font-bold text-sm border !border-gray-300 transition-colors"}
                                >
                                    Cancelar
                                </button>
                                <button 
                                    onClick={handlePublish} 
                                    disabled={isAnalyzing} 
                                    className="!bg-orange-500 hover:!bg-orange-600 !text-white px-6 py-2.5 rounded-lg font-bold text-sm shadow-md border-0 flex items-center transition-colors disabled:opacity-50"
                                >
                                    {isAnalyzing ? <><Loader2 size={16} className="animate-spin mr-2"/> Analisando...</> : "Publicar Elogio"}
                                </button>
                            </div>

                        </div>
                    </div>
                )}

                {/* MODAL GLOBAL DE ALERTA */}
                {validationAlert && (
                    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setValidationAlert(null)}>
                        <div className={`p-6 rounded-xl w-96 shadow-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            <div className={`flex items-center gap-3 mb-4 ${validationAlert.title?.includes('Sucesso') ? 'text-green-500' : 'text-red-500'}`}>
                                <div className={`p-2 rounded-full ${validationAlert.title?.includes('Sucesso') ? 'bg-green-100' : 'bg-red-100'}`}>
                                    {validationAlert.title?.includes('Sucesso') ? <CheckCircle size={24} /> : <AlertTriangle size={24} />}
                                </div>
                                <h3 className="font-bold text-lg">{validationAlert.title}</h3>
                            </div>
                            <p className="text-sm mb-6 whitespace-pre-wrap leading-relaxed opacity-90">
                                {validationAlert.message}
                            </p>
                            <div className="flex justify-end">
                                <Button onClick={() => setValidationAlert(null)} className={`${validationAlert.title?.includes('Sucesso') ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white shadow-md border-0`}>
                                    Entendi
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    /**
     * ============================================================================
     * COMPONENTE: ACADCOINS SYSTEM (MOEDA VIRTUAL E ENGAJAMENTO)
     * ============================================================================
     */


    /* ===============================================================================
     * [END_MOD_RECOGNITION_TAB]
     * FIM DO BLOCO: RecognitionTab
     * [START_MOD_ACAD_COINS_SYSTEM]
     * INICIO DO BLOCO: AcadCoinsSystem
     * =============================================================================== */
    const AcadCoinsSystem = ({ data, actions, currentUser, isDarkMode }) => {
        const isAdmin = currentUser.role === 'SuperAdm' || currentUser.permissions?.acad_coins_admin === true;
        const [activeTab, setActiveTab] = useState('minha_carteira'); 
        const [adminSubTab, setAdminSubTab] = useState('caixa'); 

        const [coinRuleForm, setCoinRuleForm] = useState({ value: '', validFrom: '' });

        const acadCoinsEnabled =
            !!currentUser?.permissions?.collab_tab_acad_coins ||
            currentUser?.role === 'SuperAdm';

        const { data: defaultCoinHistory, saveItem: saveDefaultCoin, deleteItem: deleteDefaultCoin } =
            useFirestoreData('defaultCoinHistory', [], true, currentUser, [], acadCoinsEnabled);

        const { data: caixaDB, saveItem: saveCaixa, deleteItem: delCaixa } =
            useFirestoreData('acadCoinsCaixa', [], true, currentUser, [], acadCoinsEnabled);
        const { data: distDB, saveItem: saveDist, deleteItem: delDist } = useFirestoreData('acadCoinsDist', [], true, currentUser, [], acadCoinsEnabled);
        const { data: prizesDB, saveItem: savePrize, deleteItem: delPrize } = useFirestoreData('acadCoinsPrizes', [], true, currentUser, [], acadCoinsEnabled);
        const { data: redemptionsDB, saveItem: saveRedemption, deleteItem: delRedemption } = useFirestoreData('acadCoinsRedemptions', [], true, currentUser, [], acadCoinsEnabled);
        const { data: projectsDB, saveItem: saveProject, deleteItem: delProject } = useFirestoreData('acadCoinsProjects', [], true, currentUser, [], acadCoinsEnabled);
        const { data: newsDB, saveItem: saveNews, deleteItem: delNews } = useFirestoreData('acadCoinsNews', [], true, currentUser, [], acadCoinsEnabled);
        const { data: rafflesDB, saveItem: saveRaffle, deleteItem: delRaffle } = useFirestoreData('acadCoinsRaffles', [], true, currentUser, [], acadCoinsEnabled);
        const { data: badgesDB, saveItem: saveBadge, deleteItem: delBadge } = useFirestoreData('acadCoinsBadges', [], true, currentUser, [], acadCoinsEnabled);
        const { data: ideasDB, saveItem: saveIdea, deleteItem: delIdea } = useFirestoreData('acadCoinsIdeas', [], true, currentUser, [], acadCoinsEnabled);

        // CORREÇÃO: Removemos a semente de dados de teste (SEED) para não forçar uma injeção falsa no banco
        const { data: coinConfigDB, saveItem: saveCoinConfig } = useFirestoreData('acadCoinsConfig', [], true, currentUser);
        const acadCoinsConfigDB = coinConfigDB;

        const [culturaRewards, setCulturaRewards] = useState({ reward: '', penalty: '' });

        // Fallback apenas visual (Se o banco estiver 100% vazio)
        const config = coinConfigDB[0] ||
        { 
            id: 'global', startDate: '2024-01-01', formulaQuali: 'AvQl * 5', formulaQuanti: '(40 - AvQt) * 2', 
            formulaTrainingD1: 'HrD1 * 5', formulaTrainingD2: 'HrD2 * 2', maxTrainingHoursD1: 40, maxTrainingHoursD2: 20, 
            paymentPeriod: 'mes', paymentDaysOffset: 5, percProportional: 80, percCompany: 10, percEqual: 10,
            redemptionStartDay: 5, redemptionEndDay: 10, redemptionStartTime: '10:00', redemptionEndTime: '10:00',
            currencyName: 'AcadCoins',
            coinsNoAbsences: 0, coinsNoDelays: 0, tolDelays: 0, 
            coinsNoManual: 0, tolManual: 0, coinsNoOvertime: 0, tolOvertime: 0,
            daysToProcessPoints: 5, cutoffDays: '',
            enableRecognition: false, minIndications: 3, fractionOutsideTeam: '2/3', coinsPerRecognition: 10, 
            recognitionReasons: 'Compartilhar Conhecimento, Atitudes Positivas, Inovação e sugestão de melhoria, Auxílio Intersetorial', 
            maxIndicationsPerUser: 2,
            enableGifting: false, giftCoinsAllowance: 5, releaseDateGift: 1, giftPeriodicity: 3,
            // NOVAS CONFIGURAÇÕES DE PROJETOS
            enableProjects: false, coinsPerProject: 100, daysToCreditProject: 5,
            // NOVAS CONFIGURAÇÕES GAMIFICAÇÃO E SOCIAL (ACADNEWS)
            enableAcadNews: false, enableSocialWall: false, enableBadges: false, enableRaffles: false, enableLeaderboard: false
        };

        const currencyName = config.currencyName || 'AcadCoins';
        const [localConfig, setLocalConfig] = useState(config);

        const safeText = (value, fallback = '') => {
                if (value === null || value === undefined) return fallback;

                if (React.isValidElement(value)) return fallback;

                if (typeof value === 'string') return value;
                if (typeof value === 'number') return String(value);
                if (typeof value === 'boolean') return value ? 'Sim' : 'Não';

                if (typeof value === 'object') {
                    const candidates = [
                        value.name,
                        value.title,
                        value.label,
                        value.description,
                        value.text,
                        value.value
                    ];

                    for (const candidate of candidates) {
                        if (
                            candidate !== null &&
                            candidate !== undefined &&
                            !React.isValidElement(candidate) &&
                            typeof candidate !== 'object'
                        ) {
                            return String(candidate);
                        }
                    }

                    return fallback;
                }

                return String(value);
            };

        const isCoinAvailable = (d) => {
            return !d.blocked &&
                d.status !== 'blocked' &&
                d.approvalStatus !== 'pending' &&
                d.approvalStatus !== 'rejected';
        };

        // --- HELPER E ESTADOS: HISTÓRICO DE CONFIGURAÇÕES ---
        const getActiveRule = useCallback((historyArray, targetDate) => {
        if (!historyArray || !historyArray.length) return {};

        const dateStr = typeof targetDate === 'string'
            ? targetDate
            : new Date().toISOString().split('T')[0];

        const sorted = [...historyArray].sort((a, b) =>
            String(b.validFrom || '').localeCompare(String(a.validFrom || ''))
        );

        return sorted.find(r =>
            (!r.validFrom || r.validFrom <= dateStr) &&
            (!r.validTo || r.validTo >= dateStr)
        ) || sorted[0] || {};
    }, []);

        const [ruleForms, setRuleForms] = useState({
            apuracao: { id: null, validFrom: new Date().toISOString().split('T')[0], validTo: '', paymentPeriod: 'mes', cutoffDays: '', paymentDaysOffset: '', redemptionStartDay: '', redemptionStartTime: '10:00', redemptionEndDay: '', redemptionEndTime: '10:00' },
            caixinha: { id: null, validFrom: new Date().toISOString().split('T')[0], validTo: '', percProportional: '', percEqual: '', percCompany: '' },
            treinamentos: {
            id: null,
            validFrom: new Date().toISOString().split('T')[0],
            validTo: '',
            formulaTrainingD1: '',
            formulaTrainingD2: '',
            maxTrainingHoursD1: '',
            maxTrainingHoursD2: '',
            requireTrainingApproval: false
        },
            absenteismo: { id: null, validFrom: new Date().toISOString().split('T')[0], validTo: '', daysToProcessPoints: '', coinsNoAbsences: '', coinsNoDelays: '', tolDelays: '', coinsNoManual: '', tolManual: '', coinsNoOvertime: '', tolOvertime: '' },
            reconhecimento: { id: null, validFrom: new Date().toISOString().split('T')[0], validTo: '', enableRecognition: false, minIndications: '', fractionOutsideTeam: '', coinsPerRecognition: '', maxIndicationsPerUser: '', recognitionReasons: '' },
            presentear: { id: null, validFrom: new Date().toISOString().split('T')[0], validTo: '', enableGifting: false, giftCoinsAllowance: '', releaseDateGift: '', giftPeriodicity: '' },
            acadnews: { id: null, validFrom: new Date().toISOString().split('T')[0], validTo: '', enableAcadNews: false, enableSocialWall: false, enableBadges: false, enableRaffles: false, enableLeaderboard: false, archiveDays: 7 },
            ideias: { id: null, validFrom: new Date().toISOString().split('T')[0], validTo: '', enableIdeas: false, ideasRouting: 'Todos os gestores', ideasAllowShare: false, ideasMaxCoins: '' },
            niveisCaixa: { id: null, validFrom: new Date().toISOString().split('T')[0], validTo: '', tipoAviso: 'meta_saldo', tipoValor: 'fixo', valor: '', mensagem: 'Chegamos a {valor} de saldo na caixinha!' },
            cultura: { id: null, validFrom: new Date().toISOString().split('T')[0], validTo: '', reward: '', penalty: '' }
        });
        const [showInactiveRulesMap, setShowInactiveRulesMap] = useState({});

        const handleSaveRule = async (moduleKey) => {
                const form = ruleForms[moduleKey];

                if (!form.validFrom) {
                    try {
                        alert("A Data de Início é obrigatória.");
                    } catch (e) {
                        console.error("A Data de Início é obrigatória.");
                    }
                    return;
                }

                const newRule = {
                    ...form,
                    id: form.id || Date.now().toString()
                };

                const historyKey = `${moduleKey}History`;

                const currentHistory = Array.isArray(localConfig[historyKey])
                    ? localConfig[historyKey]
                    : [];

                const newHistory = form.id
                    ? currentHistory.map(r => String(r.id) === String(form.id) ? newRule : r)
                    : [...currentHistory, newRule];

                const updatedConfig = {
                    ...localConfig,
                    id: localConfig.id || config.id || 'global',
                    [historyKey]: newHistory
                };

                setLocalConfig(updatedConfig);

                setRuleForms(prev => ({
                    ...prev,
                    [moduleKey]: {
                        ...prev[moduleKey],
                        id: null,
                        validFrom: new Date().toISOString().split('T')[0],
                        validTo: ''
                    }
                }));

                try {
                    await saveCoinConfig(sanitizeForFirebase(updatedConfig));
                    alert("Regra gravada com sucesso.");
                    console.log(`Regra ${moduleKey} gravada com sucesso.`);
                } catch (err) {
                    console.error(`Erro ao gravar regra ${moduleKey}:`, err);
                    try {
                        alert(`Erro ao gravar regra: ${err.message}`);
                    } catch (e) {}
                }
            };
        
        // NOVOS ESTADOS PARA OS MODAIS DE INTERAÇÃO
        const [isRecognizeModalOpen, setIsRecognizeModalOpen] = useState(false);
        const [recognizeForm, setRecognizeForm] = useState({ targetId: '', reason: '' });
        
        const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);
        const [giftForm, setGiftForm] = useState({ targets: [], coinsPerTarget: 1 });
        const [giftSearch, setGiftSearch] = useState('');
        const [auditReport, setAuditReport] = useState(null);

        // NOVOS ESTADOS PARA PROJETOS
        const [isProjectWalletModalOpen, setIsProjectWalletModalOpen] = useState(false);
        const [activeProjectView, setActiveProjectView] = useState(null); // NOVO: Controla qual card foi clicado
        const [projectForm, setProjectForm] = useState({ id: null, emoji: '🚀', name: '', managerId: '', participants: [], startDate: '', endDate: '' });
        const [projectSearch, setProjectSearch] = useState('');
        const [showEmojiPicker, setShowEmojiPicker] = useState(false); // Pode receber 'project' ou 'badge' para saber qual atualizar
        
        // NOVO ESTADO PARA CADASTRO DE SELOS (BADGES)
        const [badgeForm, setBadgeForm] = useState({ id: null, name: '', emoji: '⭐', description: '', triggerType: 'received_indications', triggerValue: '' });
        
        // NOVOS ESTADOS PARA O ACADNEWS E IDEIAS
            const [isNewsModalOpen, setIsNewsModalOpen] = useState(false);
            const [newsForm, setNewsForm] = useState({ id: null, type: 'Rotineira', title: '', content: '' });
            const [isIdeaModalOpen, setIsIdeaModalOpen] = useState(false);
            const [ideaForm, setIdeaForm] = useState({ id: null, title: '', description: '', defense: '', visibility: 'public' });
            const [ideaFilterMode, setIdeaFilterMode] = useState('minhas');
            const [evalIdeaData, setEvalIdeaData] = useState(null); // Armazena a ideia sendo avaliada
            const [showArchivedIdeas, setShowArchivedIdeas] = useState(false); // NOVO: Controle de Ideias Arquivadas
            const [customEvalSearch, setCustomEvalSearch] = useState(''); // Busca na config de gestores
            const [shareIdeaSearch, setShareIdeaSearch] = useState(''); // Busca no modal de compartilhar ideia

            // --- SINCRONIZA AS CONFIGURAÇÕES DO BANCO COM A TELA ---
            
           
            // 1. Criamos a trava AQUI
        const [formInitialized, setFormInitialized] = React.useState(false);

        // 2. O useEffect sincroniza o banco apenas se a trava estiver desativada (!formInitialized)
        React.useEffect(() => { 
            if (coinConfigDB && coinConfigDB.length > 0 && !formInitialized) { 
                setLocalConfig(coinConfigDB[0]); 
                setFormInitialized(true); // Ativa a trava! Agora ele não sobreescreve mais sua digitação
            } 
        }, [coinConfigDB, formInitialized]);

        // 3. (Opcional, mas recomendado) Se a sua tela fechar/desmontar, a trava reseta
        React.useEffect(() => {
            // Se existir uma variável de controle da aba/modal, você pode resetar.
            // Exemplo genérico para quando o componente for desmontado:
            return () => setFormInitialized(false);
        }, []);

        // --- ESTADOS DE FORMULÁRIOS E FILTROS ---
        const [caixaForm, setCaixaForm] = useState({ id: null, type: 'Realizado', date: new Date().toISOString().split('T')[0], amount: '', description: '' });
    
            // Estados antigos (se precisar manter para fallback)
            const [efetivarCaixaModal, setEfetivarCaixaModal] = useState({ isOpen: false, item: null, amount: '', date: '' });
            
            // --- NOVOS ESTADOS PARA A AÇÃO DE CANCELAR/EFETIVAR DA CAIXINHA ---
            const [actionBoxItem, setActionBoxItem] = useState(null); 
            const [cancelReason, setCancelReason] = useState(""); 
            
            const [showPrevisaoModal, setShowPrevisaoModal] = useState(false);

        const [showIndicacoesModal, setShowIndicacoesModal] = useState(false); // NOVO ESTADO
        const [distForm, setDistForm] = useState({ id: null, date: new Date().toISOString().split('T')[0], collabId: '', quantity: '', reason: '' });
        
        // ATUALIZADO: prizeForm agora usa targetPeriod ao invés de datas e inclui coinsValue
        const [prizeForm, setPrizeForm] = useState({ id: null, name: '', value: '', coinsValue: '', quantity: '', targetPeriod: '', isRaffle: false, maxTicketsPerUser: 1 });

        const [evalYear, setEvalYear] = useState(new Date().getFullYear().toString());
        const [evalQuarter, setEvalQuarter] = useState('q1'); 

        const [totDept, setTotDept] = useState('');
        const [totPeriod, setTotPeriod] = useState('');
        const [totSearch, setTotSearch] = useState('');
        const [totReportType, setTotReportType] = useState('saldos'); // NOVO: Controle de abas da totalização

        const [walletPeriod, setWalletPeriod] = useState('');
        const [walletSearch, setWalletSearch] = useState('');

        // --- MODAIS ---
        const [showInactiveRules, setShowInactiveRules] = useState(false);
        const [isFazerOBemModalOpen, setIsFazerOBemModalOpen] = useState(false);
        const [fobForm, setFobForm] = useState({ type: 'colleague', coins: '', isAnonymous: false, targets: [], socialProgram: 'Campanha Solidária Acad' });
        const [fobSearch, setFobSearch] = useState(''); // NOVO: Busca na doação
        const [activeConfigScreen, setActiveConfigScreen] = useState(null);
        const [isConfigModalOpen, setIsConfigModalOpen] = useState(false); // NOVO: Controla os popups de configuração
        
        // MUDANÇA: Agora inicia lendo a memória do navegador, se não existir, usa 'none' (Linha do Tempo)
        const [newsGroupMode, setNewsGroupMode] = useState(() => {
            const saved = localStorage.getItem('acadCoins_newsGroupMode');
            return saved || 'none';
        });
    
        // --- NOVOS ESTADOS PARA RELATÓRIOS E CAIXINHA ---
        const [isReportModalOpen, setIsReportModalOpen] = useState(false);
        // Adicionado collabId padrão 'todos'
        const [reportParams, setReportParams] = useState({ format: 'pdf', type: 'geral', periodMode: 'trimestre', year: new Date().getFullYear().toString(), quarter: 'q1', startDate: '', endDate: '', collabId: 'todos' });
        const [isGeneratingReport, setIsGeneratingReport] = useState(false);
        const [isCaixaPopupOpen, setIsCaixaPopupOpen] = useState(false);
        const [isCotacaoPopupOpen, setIsCotacaoPopupOpen] = useState(false); // NOVO
        const [isBonusPopupOpen, setIsBonusPopupOpen] = useState(false); // NOVO
        const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);
        const [isRaffleModalOpen, setIsRaffleModalOpen] = useState(false);
        const [activeDrawRaffle, setActiveDrawRaffle] = useState(null); // NOVO: Controle do Sorteio ao Vivo
        const [caixaHover, setCaixaHover] = useState(null);
        
        // 1. PRIMEIRO: Descobrir quem é o usuário logado (RESTAURADO)
        const loggedCollabId = useMemo(() => {
            if (!currentUser || !data.collaborators) return null;
            const userEmail = (currentUser.email || '').trim().toLowerCase();
            const found = data.collaborators.find(c => c.email?.trim().toLowerCase() === userEmail);
            return found ? found.id : null;
        }, [currentUser, data.collaborators]);

        // 2. DEPOIS: Usar o ID do usuário para carregar a memória de notificações
        const [lastSeenNewsTime, setLastSeenNewsTime] = useState(Date.now());
        const [sessionHighlightTime, setSessionHighlightTime] = useState(Date.now());

        React.useEffect(() => {
            if (loggedCollabId) {
                const savedTime = parseInt(localStorage.getItem(`acadCoins_lastSeen_${loggedCollabId}`) || '0');
                setLastSeenNewsTime(savedTime);
                setSessionHighlightTime(savedTime);
            }
        }, [loggedCollabId]);

        React.useEffect(() => {
            if (activeTab === 'acadnews' && loggedCollabId) {
                const now = Date.now();
                setLastSeenNewsTime(now); 
                localStorage.setItem(`acadCoins_lastSeen_${loggedCollabId}`, now.toString());
            } else if (activeTab !== 'acadnews' && loggedCollabId) {
                setSessionHighlightTime(Date.now()); // Garante que a memória da sessão atualize ao sair da tela
            }
        }, [activeTab, loggedCollabId]); // Removido lastSeenNewsTime da dependência para evitar travamentos

        
        // --- FUNÇÃO: PDF DETALHADO DA CAIXINHA (COM COTAÇÃO) ---
        const generateCaixinhaPDF = async () => {
            try {
                if (typeof loadPdfLibs !== 'undefined') await loadPdfLibs();
                const PDFConstructor = (window.jspdf && window.jspdf.jsPDF) ? window.jspdf.jsPDF : window.jsPDF;
                const doc = new PDFConstructor('p', 'mm', 'a4');

                const dStart = config.startDate;
                const dEnd = new Date().toISOString().split('T')[0];

                // 1. Calcula Saldo Inicial
                const beforeRecords = caixaDB.filter(c => c.date < dStart && c.type !== 'Previsto');
                let saldoInicial = beforeRecords.reduce((acc, c) => acc + parseFloat(c.amount || 0), 0);
                // 2. Registros do Período
                const periodRecords = caixaDB.filter(c => c.date >= dStart && c.date <= dEnd && c.type !== 'Previsto').sort((a,b) => new Date(a.date) - new Date(b.date));

                let entradas = 0;
                let saidas = 0;
                let runningBalance = saldoInicial;

                const tableBody = periodRecords.map(c => {
                    const val = parseFloat(c.amount || 0);
                    if (val >= 0) entradas += val;
                    else saidas += Math.abs(val);
                    runningBalance += val;

                    return [
                        new Date(c.date + 'T12:00:00').toLocaleDateString('pt-BR'),
                        c.description,
                        val >= 0 ? formatMoney(val) : `-${formatMoney(Math.abs(val))}`,
                        formatMoney(runningBalance)
                    ];
                });

                // Busca Cotação do Período
                const repStats = getStatsAtDate(dEnd);
                const cotacao = repStats.uValue;

                // 3. Renderiza Cabeçalho Moderno
                doc.setFillColor(245, 158, 11); doc.rect(0, 0, 210, 20, 'F');
                doc.setTextColor(255); doc.setFontSize(18); doc.setFont("helvetica", "bold");
                doc.text("Extrato Detalhado da Caixinha", 14, 13);
                
                doc.setTextColor(255, 255, 255); doc.setFontSize(10); doc.setFont("helvetica", "normal");
                doc.text(`Período: ${new Date(dStart + 'T12:00:00').toLocaleDateString('pt-BR')} a ${new Date(dEnd + 'T12:00:00').toLocaleDateString('pt-BR')}`, 130, 13);
                
                let y = 28;

                // Ajuste dos tamanhos para caberem 5 cards lado a lado
                const drawCard = (x, y, w, h, title, value, bgColor, titleColor, valColor) => {
                    doc.setFillColor(...bgColor);
                    doc.roundedRect(x, y, w, h, 2, 2, 'F');
                    doc.setTextColor(...titleColor);
                    doc.setFontSize(7);
                    doc.setFont("helvetica", "bold");
                    doc.text(title, x + 2, y + 6);
                    doc.setTextColor(...valColor);
                    doc.setFontSize(10);
                    doc.text(value, x + 2, y + 14);
                };

                const cardW = 34; // Mais estreito
                const gap = 3;    // Menos espaçamento
                
                drawCard(14, y, cardW, 20, "SALDO INICIAL", formatMoney(saldoInicial), [243, 244, 246], [107, 114, 128], [31, 41, 55]);
                drawCard(14 + (cardW + gap)*1, y, cardW, 20, "ENTRADAS", `+${formatMoney(entradas)}`, [220, 252, 231], [22, 101, 52], [21, 128, 61]);
                drawCard(14 + (cardW + gap)*2, y, cardW, 20, "SAÍDAS", `-${formatMoney(saidas)}`, [254, 226, 226], [153, 27, 27], [220, 38, 38]);
                drawCard(14 + (cardW + gap)*3, y, cardW, 20, "SALDO FINAL", formatMoney(runningBalance), [254, 243, 199], [133, 77, 14], [217, 119, 6]);
                drawCard(14 + (cardW + gap)*4, y, cardW, 20, "COTAÇÃO 1 COIN", formatMoney(cotacao), [238, 242, 255], [67, 56, 202], [49, 46, 129]);

                y += 28;

                // 4. Renderiza Tabela
                doc.autoTable({
                    startY: y,
                    head: [['Data', 'Descrição da Movimentação', 'Valor (R$)', 'Saldo Acumulado']],
                    body: tableBody,
                    theme: 'grid',
                    headStyles: { fillColor: [245, 158, 11] },
                    columnStyles: { 0: { cellWidth: 25 }, 1: { cellWidth: 'auto' }, 2: { cellWidth: 35, halign: 'right' }, 3: { cellWidth: 35, halign: 'right', fontStyle: 'bold' } },
                    didParseCell: (data) => {
                        if (data.section === 'body' && data.column.index === 2) {
                            if (data.cell.raw.includes('-')) data.cell.styles.textColor = [220, 38, 38];
                            else data.cell.styles.textColor = [22, 163, 74];
                        }
                    }
                });

                window.open(doc.output('bloburl'), '_blank');
            } catch (e) {
                console.error("Erro PDF Caixinha:", e);
                alert("Falha ao gerar o PDF.");
            }
        };
        
        const todayStr = new Date().toISOString().split('T')[0];

        const sanitizeForFirebase = (obj) => {
            return JSON.parse(JSON.stringify(obj, (key, value) => {
                if (value === undefined) return null;
                if (typeof value === 'number' && isNaN(value)) return 0;
                return value;
            }));
        };

        // --- MOTOR DINÂMICO: CONTAGEM REGRESSIVA PARA RESGATE ---
        const [redemptionStatus, setRedemptionStatus] = useState({ isOpen: false, text: 'Calculando...' });
        React.useEffect(() => {
            const calculateRedemption = () => {
                const rule = getActiveRule(localConfig.apuracaoHistory, todayStr);
                const now = new Date();
                const currentMonth = now.getMonth();
                const currentYear = now.getFullYear();

                const sDay = parseInt((rule.redemptionStartDay !== undefined && rule.redemptionStartDay !== '') ? rule.redemptionStartDay : localConfig.redemptionStartDay) || 5;
                const eDay = parseInt((rule.redemptionEndDay !== undefined && rule.redemptionEndDay !== '') ? rule.redemptionEndDay : localConfig.redemptionEndDay) || 10;
                const sTime = (rule.redemptionStartTime !== undefined && rule.redemptionStartTime !== '') ? rule.redemptionStartTime : (localConfig.redemptionStartTime || '10:00');
                const eTime = (rule.redemptionEndTime !== undefined && rule.redemptionEndTime !== '') ? rule.redemptionEndTime : (localConfig.redemptionEndTime || '10:00');

                const [sHour, sMin] = sTime.split(':').map(Number);
                const [eHour, eMin] = eTime.split(':').map(Number);

                // Monta as datas de Início e Fim para o mês atual
                const startDateThisMonth = new Date(currentYear, currentMonth, sDay, sHour, sMin, 0);
                const endDateThisMonth = new Date(currentYear, currentMonth, eDay, eHour, eMin, 0);

                // Caso 1: A Janela está ABERTA AGORA
                if (now >= startDateThisMonth && now <= endDateThisMonth) {
                    return { isOpen: true, text: 'Resgatar Prêmios' };
                }

                // Caso 2: A Janela está FECHADA (Descobre qual é a próxima abertura)
                let nextStartDate;
                if (now < startDateThisMonth) {
                    // Ainda não chegou o dia neste mês
                    nextStartDate = startDateThisMonth;
                } else {
                    // Já passou a data deste mês, a próxima é mês que vem
                    nextStartDate = new Date(currentYear, currentMonth + 1, sDay, sHour, sMin, 0);
                }

                const diffMs = nextStartDate - now;
                const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                const diffHours = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
                const diffMins = Math.floor((diffMs / 1000 / 60) % 60);

                let text = '';
                if (diffDays > 0) {
                    text = `Abre em ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
                } else {
                    text = `Abre em ${String(diffHours).padStart(2, '0')}h ${String(diffMins).padStart(2, '0')}m`;
                }

                return { isOpen: false, text, isImminent: diffDays === 0 };
            };

            setRedemptionStatus(calculateRedemption());
            const interval = setInterval(() => { setRedemptionStatus(calculateRedemption()); }, 10000);
            return () => clearInterval(interval);
        }, [localConfig, getActiveRule, todayStr]);


        const getPeriodStr = useCallback((dateStr) => {
            if (!dateStr) return 'N/D';
            const d = new Date(dateStr + 'T12:00:00');
            const y = d.getFullYear();
            const m = d.getMonth() + 1; 
            if (config.paymentPeriod === 'mes') return `${y}-M${String(m).padStart(2, '0')}`;
            if (config.paymentPeriod === 'trimestre') return `${y}-Q${Math.ceil(m / 3)}`;
            if (config.paymentPeriod === 'semestre') return `${y}-S${Math.ceil(m / 6)}`;
            return `${y}`;
        }, [config.paymentPeriod]);

        const getEndOfPeriodDate = useCallback((periodStr) => {
            if (periodStr === getPeriodStr(todayStr)) return todayStr; 
            const [y, p] = periodStr.split('-');
            let month = 12, day = 31;
            if (p) {
                if (p.startsWith('M')) { month = parseInt(p.replace('M', '')); day = new Date(y, month, 0).getDate(); }
                if (p.startsWith('Q')) { month = parseInt(p.replace('Q', '')) * 3; day = new Date(y, month, 0).getDate(); }
                if (p.startsWith('S')) { month = parseInt(p.replace('S', '')) * 6; day = new Date(y, month, 0).getDate(); }
            }
            return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }, [getPeriodStr, todayStr]);

        // LISTA DE PERÍODOS POSSÍVEIS (Para Cadastro de Prêmios)
        const availablePeriodsList = useMemo(() => {
            const periods = [];
            const year = new Date().getFullYear();
            const pType = config.paymentPeriod || 'mes';
            for (let y = year - 1; y <= year + 1; y++) {
                if (pType === 'mes') {
                    for (let m = 1; m <= 12; m++) periods.push(`${y}-M${String(m).padStart(2, '0')}`);
                } else if (pType === 'trimestre') {
                    for (let q = 1; q <= 4; q++) periods.push(`${y}-Q${q}`);
                } else if (pType === 'semestre') {
                    for (let s = 1; s <= 2; s++) periods.push(`${y}-S${s}`);
                } else {
                    periods.push(`${y}`);
                }
            }
            return periods;
        }, [config.paymentPeriod]);

        // MOTOR DE CÁLCULO FINANCEIRO
        const getStatsAtDate = useCallback((targetDate) => {
            const validCaixa = caixaDB.filter(c => c.date >= config.startDate && c.date <= targetDate && c.type !== 'Previsto');
            const tCaixaFull = validCaixa.reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0);

            const activeCollabs = data.collaborators.filter(c => {
                if (c.participatesAcadCoins === false) return false;
                const start = c.acadCoinsStartDate;
                if (!start || start > targetDate) return false; 
                const end = c.acadCoinsEndDate;
                if (end && end < targetDate) return false; 
                return true;
            });
            const activeCount = activeCollabs.length || 1; 

            // INCLUA ESTAS 4 LINHAS AQUI PARA PUXAR A REGRA DA DATA:
            const cxRule = getActiveRule(config.caixinhaHistory, targetDate);
            const percProp = cxRule.percProportional !== undefined ? parseFloat(cxRule.percProportional) : (parseFloat(config.percProportional) || 0);
            const percComp = cxRule.percCompany !== undefined ? parseFloat(cxRule.percCompany) : (parseFloat(config.percCompany) || 0);
            const percEq = cxRule.percEqual !== undefined ? parseFloat(cxRule.percEqual) : (parseFloat(config.percEqual) || 0);

            // SUBSTITUA as 3 variáveis originais por estas:
            const poolProportional = tCaixaFull * (percProp / 100);
            const poolCompany = tCaixaFull * (percComp / 100);
            const poolEqual = tCaixaFull * (percEq / 100);

            const validDist = distDB.filter(d => {
                if (d.date < config.startDate || d.date > targetDate) return false;
                const c = data.collaborators.find(col => col.id === d.collabId);
                if (!c || c.participatesAcadCoins === false) return false;
                const start = c.acadCoinsStartDate;
                if (!start || start > targetDate) return false;
                const end = c.acadCoinsEndDate;
                if (end && targetDate > end) return false;
                return true;
            });
            const tDist = validDist.reduce((acc, curr) => acc + parseFloat(curr.quantity || 0), 0);
            
            const uValue = tDist > 0 ? (poolProportional / tDist) : 0;
            const equalBonusPerPerson = poolEqual / activeCount;

            return { tCaixa: tCaixaFull, tDist, uValue, poolProportional, poolCompany, poolEqual, equalBonusPerPerson, activeCount };
        }, [caixaDB, distDB, data.collaborators, config]);

        const formatMoney = (val) => {
            if (isNaN(val) || val === undefined || val === null) return 'R$ 0,00';
            return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
        };

        const formatQuote = (val) => {
            if (isNaN(val) || val === undefined || val === null) return 'R$ 0,0000';
            return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 4, maximumFractionDigits: 4 });
        };

        const formatCoins = (val) => {
            if (isNaN(val) || val === undefined || val === null) return '0,0000';
            return Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
        };

        const currentStats = getStatsAtDate(todayStr);
        const unitValue = currentStats.uValue;
        const currentPeriodStr = getPeriodStr(todayStr);

        // CÁLCULO DA DATA DE FECHAMENTO (CORTE) DO PERÍODO ATUAL
        const cutoffDateDisplay = useMemo(() => {
    const rule = getActiveRule(localConfig.apuracaoHistory, todayStr);

    const cutoff = (rule.cutoffDays !== undefined && rule.cutoffDays !== '')
        ? parseInt(rule.cutoffDays, 10)
        : parseInt(localConfig.cutoffDays || 0, 10);

    if (isNaN(cutoff)) return null;

    const validFrom = rule.validFrom || localConfig.startDate || todayStr;
    const periodBase = (
        rule.paymentPeriod !== undefined && rule.paymentPeriod !== ''
            ? rule.paymentPeriod
            : localConfig.paymentPeriod || 'mes'
    ).toString().toLowerCase();

    const startDate = new Date(`${validFrom}T00:00:00`);
    const today = new Date(`${todayStr}T00:00:00`);

    if (isNaN(startDate.getTime()) || isNaN(today.getTime())) return null;

    let monthsPerPeriod = 1;
    if (periodBase === 'trimestre') monthsPerPeriod = 3;
    if (periodBase === 'semestre' || periodBase === 'semestral') monthsPerPeriod = 6;
    if (periodBase === 'ano' || periodBase === 'anual') monthsPerPeriod = 12;

    const diffMonths =
        (today.getFullYear() - startDate.getFullYear()) * 12 +
        (today.getMonth() - startDate.getMonth());

    const periodIndex = Math.max(0, Math.floor(diffMonths / monthsPerPeriod));

    const periodStart = new Date(startDate.getFullYear(), startDate.getMonth() + (periodIndex * monthsPerPeriod), 1, 12, 0, 0);
    const periodEnd = new Date(periodStart.getFullYear(), periodStart.getMonth() + monthsPerPeriod, 0, 12, 0, 0);

    periodEnd.setDate(periodEnd.getDate() + cutoff);

    return periodEnd.toLocaleDateString('pt-BR');
}, [localConfig, getActiveRule, todayStr]);

        // AVALIAÇÃO DE DESLIGAMENTO
        const myCollabRecord = data.collaborators.find(c => c.id === loggedCollabId);
        let isDisconnected = false;
        let disconnectedReason = '';
        
        if (myCollabRecord) {
            if (myCollabRecord.participatesAcadCoins === false) {
                isDisconnected = true;
                disconnectedReason = 'Você não está configurado para participar do programa AcadCoins.';
            } else {
                const start = myCollabRecord.acadCoinsStartDate;
                if (!start || start > todayStr) {
                    isDisconnected = true;
                    disconnectedReason = 'Sua participação no programa AcadCoins ainda não iniciou (Contate o RH).';
                } else {
                    const end = myCollabRecord.acadCoinsEndDate;
                    if (end && todayStr > end) {
                        isDisconnected = true;
                        disconnectedReason = 'Seu período de elegibilidade no programa AcadCoins expirou.';
                    }
                }
            }
        } else {
            isDisconnected = true;
        }

        const myDistributions = distDB.filter(d => d.collabId === loggedCollabId && d.date >= config.startDate && d.date <= todayStr);
        // Filtramos 'allowance_presente' para não somar no saldo real do colaborador.
        const myTotalCoins = isDisconnected ? 0 : myDistributions.filter(d => d.type !== 'allowance_presente').reduce((acc, curr) => acc + parseFloat(curr.quantity || 0), 0);
        
        // Calcula quantas moedas de presente a pessoa já gastou neste período (Mês, Trimestre, etc.)
        const myGiftsSentThisPeriod = distDB.filter(d => 
            d.fromId === loggedCollabId && 
            d.type === 'presente_enviado' && 
            getPeriodStr(d.date) === getPeriodStr(todayStr)
        ).reduce((acc, curr) => acc + (parseFloat(curr.usedAllowance) || 1), 0);
        
        // INCLUA A BUSCA DE REGRA E SUBSTITUA O myGiftAllowanceLeft:
        const giftRule = getActiveRule(config.presentearHistory, todayStr);
        const allowGift = parseInt(giftRule.giftCoinsAllowance ?? config.giftCoinsAllowance) || 0;
        const myGiftAllowanceLeft = isDisconnected ? 0 : Math.max(0, allowGift - myGiftsSentThisPeriod);

        const myValFromCoins = myTotalCoins * unitValue;
        const myValFromEqual = isDisconnected ? 0 : currentStats.equalBonusPerPerson;
        const myRealValue = myValFromCoins + myValFromEqual;

        const nextPaymentDate = useMemo(() => {
            const rule = getActiveRule(localConfig.apuracaoHistory, todayStr);
            const p = (rule.paymentPeriod !== undefined && rule.paymentPeriod !== '') ? rule.paymentPeriod : (localConfig.paymentPeriod || 'mes');
            const offset = parseInt((rule.paymentDaysOffset !== undefined && rule.paymentDaysOffset !== '') ? rule.paymentDaysOffset : localConfig.paymentDaysOffset) || 1;

            const today = new Date();
            let y = today.getFullYear();
            let m = today.getMonth(); 
            let nextM = 0;

            if (p === 'mes') nextM = m + 1;
            else if (p === 'trimestre') { if (m < 3) nextM = 3; else if (m < 6) nextM = 6; else if (m < 9) nextM = 9; else { nextM = 0; y++; } }
            else if (p === 'semestre') { if (m < 6) nextM = 6; else { nextM = 0; y++; } }
            else if (p === 'ano') { nextM = 0; y++; }

            return new Date(y, nextM, offset).toLocaleDateString('pt-BR');
        }, [localConfig, getActiveRule, todayStr]);

        const myWalletData = useMemo(() => {
            const availablePeriods = new Set();
            let rows = myDistributions.map(d => {
                const periodStr = getPeriodStr(d.date);
                availablePeriods.add(periodStr);
                const stats = getStatsAtDate(d.date);
                const totalReceber = parseFloat(d.quantity || 0) * stats.uValue;

                return {
                    ...d,
                    periodStr,
                    unitValue: stats.uValue,
                    totalValue: totalReceber
                };
            });

            // --- INJEÇÃO DE MOEDAS VIRTUAIS PARA PRESENTEAR ---
            const giftRule = getActiveRule(config.presentearHistory, todayStr);
            const isGiftingEnabled = giftRule.enableGifting !== undefined ? giftRule.enableGifting : config.enableGifting;
            const allowance = parseInt(giftRule.giftCoinsAllowance ?? config.giftCoinsAllowance) || 0;
            const relDate = parseInt(giftRule.releaseDateGift ?? config.releaseDateGift) || 1;

            if (isGiftingEnabled && allowance > 0) {
                const today = new Date();
                const maxDaysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
                const releaseDay = Math.min(relDate, maxDaysInMonth);
                
                // Só libera os créditos na carteira se o dia atual for >= ao Dia de Liberação configurado
                if (today.getDate() >= releaseDay && myGiftAllowanceLeft > 0) {
                    availablePeriods.add(getPeriodStr(todayStr));
                    rows.push({
                        id: 'virtual_gift_allowance',
                        date: todayStr,
                        periodStr: getPeriodStr(todayStr),
                        quantity: myGiftAllowanceLeft,
                        reason: 'Créditos Disponíveis para Presentear Colegas',
                        type: 'virtual_presente',
                        unitValue: 0,
                        totalValue: 0
                    });
                }
            }

            if (walletPeriod) rows = rows.filter(r => r.periodStr === walletPeriod);
            if (walletSearch) {
                const s = walletSearch.toLowerCase();
                rows = rows.filter(r => r.date.includes(s) || (r.reason || '').toLowerCase().includes(s) || String(r.quantity).includes(s) || formatMoney(r.unitValue).toLowerCase().includes(s) || formatMoney(r.totalValue).toLowerCase().includes(s) );
            }

            return { rows: rows.sort((a,b) => new Date(b.date) - new Date(a.date)), periods: Array.from(availablePeriods).sort().reverse() };
        }, [myDistributions, getPeriodStr, getStatsAtDate, walletPeriod, walletSearch]);


        // --- RESGATE DE PRÊMIOS ---
        const handleRedeemPrize = async (prize) => {
            try {
                if (!redemptionStatus.isOpen) return alert("Fora do período de resgate.");
                
                const isCoinPrize = !!prize.coinsValue;
                const prizeCostCoins = isCoinPrize ? Number(prize.coinsValue) : 0;
                const prizeCostR = !isCoinPrize ? Number(prize.value) : 0;

                if (isCoinPrize) {
                    if (myTotalCoins < prizeCostCoins) return alert("Saldo de moedas insuficiente para resgatar este item.");
                } else {
                    if (myRealValue < prizeCostR) return alert("Saldo em R$ insuficiente para resgatar este item.");
                    if (unitValue <= 0) return alert("A cotação do AcadCoin ainda não foi apurada para permitir o resgate em R$.");
                }

                if (Number(prize.quantity) <= 0) return alert("Infelizmente este prêmio esgotou.");

                const confirmMsg = `Confirma o resgate de "${prize.name}" por ${isCoinPrize ? `${prizeCostCoins} AcadCoins` : formatMoney(prizeCostR)}?\n\nSerão deduzidos os AcadCoins correspondentes da sua carteira.`;
                if (window.confirm(confirmMsg)) {
                    
                    const coinsToDeduct = isCoinPrize ? prizeCostCoins : (prizeCostR / Number(unitValue));
                    if (isNaN(coinsToDeduct) || coinsToDeduct <= 0) {
                        return alert("Erro: Cálculo de moedas inválido.");
                    }
                    
                    const uniqueIdBase = Date.now().toString() + Math.random().toString(36).substr(2, 5);
                    const distId = `dist_${uniqueIdBase}`;

                    // 2. Cria o registro negativo no Extrato
                    const payloadDist = sanitizeForFirebase({
                        id: distId,
                        date: todayStr,
                        collabId: loggedCollabId,
                        quantity: -Math.abs(coinsToDeduct),
                        reason: `Resgate de Prêmio: ${prize.name}`,
                        sourceId: `redeem_${uniqueIdBase}`,
                        type: 'resgate'
                    });
                    await saveDist(payloadDist);

                    // 3. Salva a solicitação para o RH (Guarda o distId para facilitar estorno)
                    const payloadRedemp = sanitizeForFirebase({
                        id: `red_${uniqueIdBase}`,
                        distId: distId,
                        date: new Date().toISOString(),
                        collabId: loggedCollabId,
                        collabName: myCollabRecord?.name || 'Colaborador', 
                        prizeId: prize.id,
                        prizeName: prize.name,
                        value: prizeCostR || 0,
                        coinsValue: prizeCostCoins || 0,
                        status: 'Solicitado',
                        valueCoinsAtRedemption: Number(myValFromCoins) || 0,
                        valueEqualAtRedemption: Number(myValFromEqual) || 0
                    });
                    await saveRedemption(payloadRedemp);

                    // 4. Reduz a quantidade no estoque do Prêmio
                    const payloadPrize = sanitizeForFirebase({
                        ...prize,
                        quantity: Number(prize.quantity) - 1
                    });
                    await savePrize(payloadPrize);
                    
                    alert("Resgate realizado com sucesso! O RH foi notificado.");
                    setIsRedeemModalOpen(false);
                }
            } catch (error) {
                console.error("Erro CRÍTICO no resgate:", error);
                alert("Ocorreu um erro no sistema ao processar seu resgate: " + error.message);
            }
        };

        // --- COMPRA DE BILHETES DE RIFA ---
        const handleBuyTicket = async (prize) => {
            try {
                const isCoinPrize = !!prize.coinsValue;
                const prizeCostCoins = isCoinPrize ? Number(prize.coinsValue) : 0;
                const prizeCostR = !isCoinPrize ? Number(prize.value) : 0;

                if (isCoinPrize) {
                    if (myTotalCoins < prizeCostCoins) return alert("Saldo de moedas insuficiente para comprar este bilhete.");
                } else {
                    if (myRealValue < prizeCostR) return alert("Saldo em R$ insuficiente para comprar este bilhete.");
                    if (unitValue <= 0) return alert("A cotação do AcadCoin ainda não foi apurada.");
                }

                // Validação do limite de bilhetes por pessoa
                const myTickets = rafflesDB.filter(r => r.prizeId === prize.id && r.collabId === loggedCollabId).length;
                const limit = parseInt(prize.maxTicketsPerUser) || 1;
                if (myTickets >= limit) return alert(`Você já atingiu o limite máximo de ${limit} bilhete(s) para este sorteio.`);

                let confirmed = true;
                try { confirmed = window.confirm(`Confirma a compra de 1 bilhete para o sorteio de "${prize.name}" por ${isCoinPrize ? `${prizeCostCoins} AcadCoins` : formatMoney(prizeCostR)}?\n\nLimite: Você tem ${myTickets}/${limit} bilhetes comprados.`);
                } catch(e) { confirmed = true; }
                
                if (confirmed) {
                    const coinsToDeduct = isCoinPrize ? prizeCostCoins : (prizeCostR / Number(unitValue));
                    if (isNaN(coinsToDeduct) || coinsToDeduct <= 0) return alert("Erro no cálculo da cotação.");
                    
                    const uniqueIdBase = Date.now().toString() + Math.random().toString(36).substr(2, 5);
                    await saveDist(sanitizeForFirebase({
                        id: `dist_tkt_${uniqueIdBase}`, date: todayStr, collabId: loggedCollabId,
                        quantity: -Math.abs(coinsToDeduct), reason: `Bilhete de Rifa: ${prize.name}`, sourceId: `ticket_${uniqueIdBase}`, type: 'rifa'
                    }));
                    await saveRaffle(sanitizeForFirebase({
                        id: `tkt_${uniqueIdBase}`, prizeId: prize.id, prizeName: prize.name,
                        collabId: loggedCollabId, collabName: myCollabRecord?.name || 'Colaborador', date: new Date().toISOString()
                    }));
                    try { alert(`🎟️ Bilhete comprado com sucesso! Você tem ${myTickets + 1} de ${limit} bilhete(s).`); } catch(e) { console.log("Bilhete comprado!");
                    }
                }
            } catch (error) { alert("Erro ao processar compra: " + error.message);
            }
        };

        // --- ALGORITMO DE SORTEIO E PUBLICAÇÃO AUTOMÁTICA ---
        const handleDrawRaffle = async (prize, tickets) => {
            if (tickets.length === 0) return alert("Nenhum bilhete vendido para este sorteio.");
            let confirmed = true;
            try { confirmed = window.confirm(`Realizar o sorteio auditável para "${prize.name}" agora?`); } catch(e) { confirmed = true; }
            if (!confirmed) return;
            
            // Sorteio Aleatório Simples
            const winner = tickets[Math.floor(Math.random() * tickets.length)];
            const drawId = Date.now();
            
            // 1. Reduzir quantidade para fechar a rifa e salvar quem ganhou
            await savePrize(sanitizeForFirebase({ ...prize, quantity: 0, lastWinnerId: winner.collabId, lastDrawId: drawId }));

            // 2. Publicar Notícia Urgente (com prizeId para permitir desfazer depois)
            await saveNews(sanitizeForFirebase({
                id: `news_raffle_${drawId}`, prizeId: prize.id, date: new Date().toISOString(), type: 'Urgente',
                title: `🎉 RESULTADO DO SORTEIO: ${prize.name}!`,
                content: `O grande vencedor da Rifa/Sorteio "${prize.name}" foi:\n\n🏆 **${winner.collabName}** 🏆\n\nParabéns! O RH entrará em contato para a entrega do prêmio.\n\n(Transparência: Sorteio automático e auditável realizado entre ${tickets.length} bilhetes validados pelo sistema).`
            }));

            // 3. Criar registro (Valor zero) no Extrato e Distribuição Manual
            await saveDist(sanitizeForFirebase({
                id: `dist_raffle_win_${drawId}`, date: todayStr, collabId: winner.collabId,
                quantity: 0, reason: `Ganhador do Sorteio: ${prize.name}`, sourceId: `raffle_${prize.id}`, type: 'rifa_ganha'
            }));
            
            try { alert(`Sorteio concluído! O vencedor é ${winner.collabName}. A notícia foi postada no AcadNews!`); } catch(e) {}
        };

        // --- DESFAZER SORTEIO ---
        const handleUndoRaffle = async (prize) => {
            let confirmed = true;
            try { confirmed = window.confirm(`Tem certeza que deseja DESFAZER o sorteio de "${prize.name}"?\n\nIsso apagará a notícia, removerá o histórico do ganhador e reabrirá a rifa.`); } catch(e) { confirmed = true; }
            if (!confirmed) return;

            try {
                // 1. Reabrir a rifa (Quantidade volta para 1)
                await savePrize(sanitizeForFirebase({ ...prize, quantity: 1, lastWinnerId: null, lastDrawId: null }));

                // 2. Apagar a notícia do AcadNews
                const relatedNews = newsDB.find(n => n.id.startsWith('news_raffle_') && n.prizeId === prize.id);
                if (relatedNews) await delNews(relatedNews.id);

                // 3. Apagar do Extrato e Distribuição Manual
                const relatedDist = distDB.find(d => d.type === 'rifa_ganha' && d.sourceId === `raffle_${prize.id}`);
                if (relatedDist) await delDist(relatedDist.id);

                try { alert("Sorteio desfeito com sucesso! A Rifa está aberta novamente para novos compradores."); } catch(e) {}
            } catch(e) { alert("Erro ao desfazer: " + e.message); }
        };

        // --- SORTEAR DE NOVO ---
        const handleRedrawRaffle = async (prize, tickets) => {
            if (tickets.length === 0) return alert("Nenhum bilhete vendido.");
            let confirmed = true;
            try { confirmed = window.confirm(`Tem certeza que deseja SORTEAR NOVAMENTE "${prize.name}"?\n\nO ganhador anterior será cancelado (notícia apagada) e um novo sorteio será feito agora mesmo.`); } catch(e) { confirmed = true; }
            if (!confirmed) return;

            try {
                // 1. Limpeza dos rastros do sorteio anterior
                const relatedNews = newsDB.find(n => n.id.startsWith('news_raffle_') && n.prizeId === prize.id);
                if (relatedNews) await delNews(relatedNews.id);

                const relatedDist = distDB.find(d => d.type === 'rifa_ganha' && d.sourceId === `raffle_${prize.id}`);
                if (relatedDist) await delDist(relatedDist.id);

                // 2. Sorteia um novo vencedor
                const winner = tickets[Math.floor(Math.random() * tickets.length)];
                const drawId = Date.now();

                await savePrize(sanitizeForFirebase({ ...prize, quantity: 0, lastWinnerId: winner.collabId, lastDrawId: drawId }));

                await saveNews(sanitizeForFirebase({
                    id: `news_raffle_${drawId}`, prizeId: prize.id, date: new Date().toISOString(), type: 'Urgente',
                    title: `🎉 RE-SORTEIO: ${prize.name}!`,
                    content: `O NOVO grande vencedor da Rifa/Sorteio "${prize.name}" foi:\n\n🏆 **${winner.collabName}** 🏆\n\nParabéns! O RH entrará em contato para a entrega do prêmio.\n\n(Transparência: Re-sorteio automático e auditável realizado entre ${tickets.length} bilhetes).`
                }));

                await saveDist(sanitizeForFirebase({
                    id: `dist_raffle_win_${drawId}`, date: todayStr, collabId: winner.collabId,
                    quantity: 0, reason: `Ganhador do Re-sorteio: ${prize.name}`, sourceId: `raffle_${prize.id}`, type: 'rifa_ganha'
                }));

                try { alert(`Re-sorteio concluído! O NOVO vencedor é ${winner.collabName}. A notícia foi atualizada!`); } catch(e) {}
            } catch(e) { alert("Erro no re-sorteio: " + e.message); }
        };

        // --- CANCELAMENTO DE RESGATE (ADMIN) ---
        const handleUpdateRedemptionStatus = async (r, newStatus) => {
            if (newStatus === 'Cancelado' && r.status !== 'Cancelado') {
                if (!window.confirm("Atenção: Ao cancelar, os AcadCoins serão devolvidos ao colaborador e o prêmio voltará para o estoque. Continuar?")) return;
                
                try {
                    // 1. Estorna os Coins (Apaga o registro negativo)
                    if (r.distId) {
                        await delDist(r.distId);
                    } else {
                        // Fallback para legados
                        const originalDist = distDB.find(d => d.reason.includes(r.prizeName) && d.collabId === r.collabId && d.quantity < 0);
                        if (originalDist) await delDist(originalDist.id);
                    }

                    // 2. Devolve ao Estoque
                    const prize = prizesDB.find(p => p.id === r.prizeId);
                    if (prize) {
                        await savePrize({ ...prize, quantity: Number(prize.quantity) + 1 });
                    }

                    // 3. Atualiza o Status
                    await saveRedemption({ ...r, status: 'Cancelado' });
                    alert("Resgate cancelado. Saldo e estoque estornados com sucesso!");

                } catch (error) {
                    alert("Erro ao estornar: " + error.message);
                }
            } else {
                // Atualização de status normal
                await saveRedemption({ ...r, status: newStatus });
            }
        };

        // --- FAZER O BEM (TRANSFERÊNCIA) BLINDADA ---
        const handleFazerOBemSubmit = async () => {
            const coinsToSend = parseFloat(fobForm.coins);
            if (isNaN(coinsToSend) || coinsToSend <= 0) return alert("Informe uma quantidade válida de Coins.");
            if (myTotalCoins < coinsToSend) return alert("Você não possui saldo de Coins suficiente para esta doação. Obs: O Bônus Igualitário não pode ser transferido.");

            try {
                const uniqueIdBase = Date.now().toString() + Math.random().toString(36).substr(2, 5);

                if (fobForm.type === 'social') {
                    if (!fobForm.socialProgram) return alert("Escolha o programa social.");
                    
                    const socialPayload = sanitizeForFirebase({
                        id: `soc_${uniqueIdBase}`,
                        date: todayStr,
                        collabId: loggedCollabId,
                        quantity: -coinsToSend,
                        reason: `Coins doados p/ causa Social: ${fobForm.socialProgram}`,
                        sourceId: `donate_social_${uniqueIdBase}`,
                        type: 'doacao'
                    });
                    await saveDist(socialPayload);
                    alert(`Sucesso! Você doou ${coinsToSend} Coins para a causa "${fobForm.socialProgram}". Obrigado por Fazer o Bem! ❤️`);
                    
                } else {
                    if (fobForm.targets.length === 0) return alert("Selecione pelo menos um colega para receber.");
                    const totalDebit = coinsToSend * fobForm.targets.length;
                    if (myTotalCoins < totalDebit) return alert(`Saldo insuficiente! Para doar para ${fobForm.targets.length} colegas você precisa de ${totalDebit} Coins.`);

                    let senderNameDisplay = "Um(a) Colega (Anônimo)";
                    if (!fobForm.isAnonymous && myCollabRecord) {
                        const parts = myCollabRecord.name.trim().split(' ');
                        const firstName = parts[0];
                        const hasDuplicate = data.collaborators.some(c => c.id !== loggedCollabId && c.name.split(' ')[0] === firstName);
                        senderNameDisplay = (hasDuplicate && parts.length > 1) ? `${firstName} ${parts[parts.length - 1]}` : firstName;
                    }

                    const outPayload = sanitizeForFirebase({
                        id: `out_${uniqueIdBase}`,
                        date: todayStr,
                        collabId: loggedCollabId,
                        quantity: -totalDebit,
                        reason: `Doação para ${fobForm.targets.length} colega(s)`,
                        sourceId: `donate_out_${uniqueIdBase}`,
                        type: 'doacao'
                    });
                    await saveDist(outPayload);

                    const receiveReason = fobForm.isAnonymous ? "Recebido doação de Coins (Anônimo)" : `Recebido doação de Coins de ${senderNameDisplay}`;
                    
                    for (let i = 0; i < fobForm.targets.length; i++) {
                        const targetId = fobForm.targets[i];
                        const inPayload = sanitizeForFirebase({
                            id: `in_${uniqueIdBase}_${i}`,
                            date: todayStr,
                            collabId: targetId,
                            quantity: coinsToSend,
                            reason: receiveReason,
                            sourceId: `donate_in_${uniqueIdBase}`,
                            type: 'doacao'
                        });
                        await saveDist(inPayload);
                    }

                    alert(`Sucesso! Você doou para ${fobForm.targets.length} colega(s). Obrigado por espalhar o bem! 🌟`);
                }

                setFobForm({ type: 'colleague', coins: '', isAnonymous: false, targets: [], socialProgram: 'Campanha Solidária Acad' });
                setFobSearch('');
                setIsFazerOBemModalOpen(false);

            } catch (error) {
                console.error("Erro na doação:", error);
                alert("Ocorreu um erro ao processar a doação: " + error.message);
            }
        };


        const exportRedemptionsExcel = async () => {
            try {
                if (typeof loadImportLibs !== 'undefined') await loadImportLibs();
                if (!window.XLSX) throw new Error("Biblioteca XLSX não disponível.");

                const dataExport = redemptionsDB.map(r => ({
                    Data: new Date(r.date).toLocaleDateString('pt-BR'),
                    Colaborador: r.collabName,
                    "Prêmio Solicitado": r.prizeName,
                    "Valor do Prêmio": r.value,
                    "Status": r.status,
                    "Valor em AcadCoins (Momento)": r.valueCoinsAtRedemption || 0,
                    "Bônus Igualitário (Momento)": r.valueEqualAtRedemption || 0
                }));

                const ws = window.XLSX.utils.json_to_sheet(dataExport);
                const wb = window.XLSX.utils.book_new();
                window.XLSX.utils.book_append_sheet(wb, ws, "Resgates");
                window.XLSX.writeFile(wb, `Resgates_AcadCoins_${new Date().toISOString().split('T')[0]}.xlsx`);
            } catch (e) {
                alert("Erro ao exportar: " + e.message);
            }
        };

        const evaluateMath = (formula, avQl, avQt) => {
            if (!formula) return 0;
            try {
                let safeFormula = formula.toUpperCase().replace(/AVQL/g, avQl).replace(/AVQT/g, avQt).replace(/[^0-9+\-*/(). ]/g, ''); 
                const result = new Function('return ' + safeFormula)();
                return isNaN(result) || result < 0 ? 0 : result;
            } catch (e) { return 0; }
        };

        // BLOQUEIO POR DATA DE CORTE
        const isPeriodLocked = (dateStr) => {
            const currentConfig = coinConfigDB[0] || localConfig;
            if (!currentConfig.cutoffDays) return false;
            
            const period = getPeriodStr(dateStr);
            const [y, p] = period.split('-');
            let month = 12;
            let year = parseInt(y) || new Date().getFullYear();
            
            if (p) {
                if (p.startsWith('M')) month = parseInt(p.replace('M', ''));
                if (p.startsWith('Q')) month = parseInt(p.replace('Q', '')) * 3;
                if (p.startsWith('S')) month = parseInt(p.replace('S', '')) * 6;
            }
            
            // Busca o último dia real do período selecionado
            const trueEndOfPeriod = new Date(year, month, 0, 12, 0, 0);
            
            // Adiciona a tolerância de dias de corte configurada
            trueEndOfPeriod.setDate(trueEndOfPeriod.getDate() + parseInt(currentConfig.cutoffDays));
            
            // Bloqueia se a data de hoje já ultrapassou a data de fechamento daquele período
            return new Date() > trueEndOfPeriod;
        };

        const handleAddCaixa = () => {
            if (!caixaForm.amount || !caixaForm.description) return alert("Preencha valor e descrição.");
            saveCaixa(sanitizeForFirebase({ id: caixaForm.id || Date.now().toString(), type: caixaForm.type, date: caixaForm.date, amount: parseFloat(caixaForm.amount), description: caixaForm.description }));
            setCaixaForm({ id: null, type: 'Realizado', date: new Date().toISOString().split('T')[0], amount: '', description: '' });
        };
        const handleEditCaixa = (c) => setCaixaForm({ id: c.id, type: c.type || 'Realizado', date: c.date, amount: c.amount, description: c.description });
        const handleCancelCaixa = () => setCaixaForm({ id: null, type: 'Realizado', date: new Date().toISOString().split('T')[0], amount: '', description: '' });

        const handleConfirmarEfetivacao = () => {
            if (!efetivarCaixaModal.amount || !efetivarCaixaModal.date) return alert("Preencha data e valor.");
            saveCaixa(sanitizeForFirebase({ ...efetivarCaixaModal.item, type: 'Realizado', date: efetivarCaixaModal.date, amount: parseFloat(efetivarCaixaModal.amount) }));
            setEfetivarCaixaModal({ isOpen: false, item: null, amount: '', date: '' });
            alert("Lançamento efetivado com sucesso!");
        };

        const handleAddDist = () => {
            if (!distForm.collabId || !distForm.quantity) return alert("Preencha colaborador e quantidade.");
            if (isPeriodLocked(distForm.date)) return alert("Operação bloqueada: A data de corte para este período já expirou.");
            saveDist(sanitizeForFirebase({ id: distForm.id || Date.now().toString(), date: distForm.date, collabId: distForm.collabId, quantity: parseFloat(distForm.quantity), reason: distForm.reason || 'Distribuição Manual', type: 'manual' }));
            setDistForm({ id: null, date: new Date().toISOString().split('T')[0], collabId: '', quantity: '', reason: '' });
        };
        const handleEditDist = (d) => {
            if (isPeriodLocked(d.date)) return alert("Operação bloqueada: A data de corte para este período já expirou.");
            setDistForm({ id: d.id, date: d.date, collabId: d.collabId, quantity: d.quantity, reason: d.reason });
        };
        const handleCancelDist = () => setDistForm({ id: null, date: new Date().toISOString().split('T')[0], collabId: '', quantity: '', reason: '' });

        // --- FUNÇÃO CENTRAL DE CÁLCULO DE NOTAS E FAIXAS ---
        const calculateEvalCoins = useCallback((ev, evalQuarter, currentConfig) => {
        try {
            if (evalQuarter === 'anual') {
                return {
                    totalCoins: 0,
                    detailReason: '',
                    calcMemory: 'Distribuição anual de AcadCoins desativada. A geração deve ocorrer somente por fechamento trimestral.'
                };
            }
            const evCalc = typeof recalculateFinalAverage === 'function' ? recalculateFinalAverage(ev) : ev;
            const isGestor = ev.personType === 'Gestão';
            const hoje = new Date().toISOString().split('T')[0];
            let avQl = 0, avQt = 0;

            // Extração de notas (Qualitativa e Quantitativa) mantida conforme original do sistema
            if (evalQuarter === 'anual') {
                if (isGestor) { avQl = evCalc.finalScoreGestao || 0; avQt = 0; } 
                else { avQl = evCalc.qualitative?.finalAverage || 0; avQt = evCalc.quantitative?.meanPoints || 0; }
            } else {
                if (isGestor) {
                    let qSum = 0, qCount = 0; ev.qualitative?.criteria?.forEach(c => { const val = parseFloat(c[evalQuarter]); if (!isNaN(val)) { qSum += val; qCount++; } });
                    avQl = qCount > 0 ? (qSum / qCount) * 10 : 0; 
                    let metasScore = 0, percScore = 0;
                    ev.gestaoMetasPercepcao?.forEach(item => {
                        const val = parseFloat(item.scores?.[evalQuarter] || 0);
                        const proj = val > 0 ? (val / 5) * 20 : 0; 
                        if (item.type === 'Percepção' || (item.name || '').toLowerCase().includes('percepção')) percScore += proj;
                        else metasScore += proj;
                    });
                    avQl += metasScore + percScore; 
                } else {
                    let sumQl = 0, countQl = 0; ev.qualitative?.criteria?.forEach(c => { const val = parseFloat(c[evalQuarter]); if (!isNaN(val)) { sumQl += val; countQl++; } });
                    avQl = countQl > 0 ? (sumQl / countQl) * 2.5 : 0; 
                    let sumQt = 0; if (ev.quantitative && ev.quantitative.errors) { ev.quantitative.errors.forEach(err => { const errVal = parseInt(err[evalQuarter]); if (!isNaN(errVal)) sumQt += (errVal * (parseFloat(err.peso) || 0)); }); }
                    avQt = sumQt;
                }
            }

            const safeAvQl = parseFloat(avQl.toFixed(2));
            const safeAvQt = parseFloat(avQt.toFixed(2));

            const evaluateMathLocal = (formula, vQl, vQt) => {
                if (!formula) return 0;
                try {
                    let parsed = formula.toUpperCase().replace(/AVQL/g, vQl).replace(/AVQT/g, vQt);
                    parsed = parsed.replace(/,/g, '.').replace(/[^0-9+\-*/(). ]/g, '');
                    return new Function('return ' + parsed)();
                } catch (e) { return 0; }
            };

            const quarterLabelMap = {
                q1: 'Q1 / 1º Trimestre',
                q2: 'Q2 / 2º Trimestre',
                q3: 'Q3 / 3º Trimestre',
                q4: 'Q4 / 4º Trimestre'
            };

            let qualiMemory = 'Qualitativa: nenhuma regra aplicável encontrada.';
            let quantiMemory = 'Quantitativa: nenhuma regra aplicável encontrada.';

        // --- CÁLCULO DAS REGRAS QUALITATIVAS ---
            let coinsQuali = 0;

            if (currentConfig.qualiRules && currentConfig.qualiRules.length > 0) {
                const rule = currentConfig.qualiRules.find(r => {
                    const matchNota = safeAvQl >= (parseFloat(r.min) || 0) && safeAvQl <= (parseFloat(r.max) || 100);
                    const matchTipo = r.tipo === 'Ambos' || (r.tipo === 'Gestão' && isGestor) || (r.tipo === 'Colaborador' && !isGestor);
                    const matchData = (!r.dataIni || hoje >= r.dataIni) && (!r.dataFim || hoje <= r.dataFim);
                    return matchNota && matchTipo && matchData;
                });

                if (rule) {
                    const formulaToUse = (rule.tipo === 'Ambos' && !isGestor) ? rule.formulaColab : rule.formula;
                    coinsQuali = evaluateMathLocal(formulaToUse, safeAvQl, safeAvQt);

                    qualiMemory =
                        `Qualitativa: AvQl ${safeAvQl.toFixed(2)} enquadrada na faixa ${rule.min} a ${rule.max}. ` +
                        `Fórmula: ${formulaToUse || 'não informada'}. ` +
                        `Resultado: ${parseFloat(coinsQuali || 0).toFixed(2)} coins.`;
                }
            }

            // --- CÁLCULO DAS REGRAS QUANTITATIVAS (ERROS) ---
                let coinsQuanti = 0;

                if (currentConfig.quantiRules && currentConfig.quantiRules.length > 0) {
                    const ruleQt = currentConfig.quantiRules.find(r => {
                        const matchErros = safeAvQt >= (parseFloat(r.min) || 0) && safeAvQt <= (parseFloat(r.max) || 999);
                        const matchTipo = r.tipo === 'Ambos' || (r.tipo === 'Gestão' && isGestor) || (r.tipo === 'Colaborador' && !isGestor);
                        const matchData = (!r.dataIni || hoje >= r.dataIni) && (!r.dataFim || hoje <= r.dataFim);
                        return matchErros && matchTipo && matchData;
                    });

                    if (ruleQt) {
                        const formulaToUse = (ruleQt.tipo === 'Ambos' && !isGestor) ? ruleQt.formulaColab : ruleQt.formula;
                        coinsQuanti = evaluateMathLocal(formulaToUse, safeAvQl, safeAvQt);

                        quantiMemory =
                            `Quantitativa: AvQt ${safeAvQt.toFixed(2)} pontos perdidos enquadrada na faixa ${ruleQt.min} a ${ruleQt.max}. ` +
                            `Fórmula: ${formulaToUse || 'não informada'}. ` +
                            `Resultado: ${parseFloat(coinsQuanti || 0).toFixed(2)} coins.`;
                    }
                }

            const totalCoins = coinsQuali + coinsQuanti;

                return {
                    totalCoins,
                    detailReason: `AEC ${ev.periodYear} (${evalQuarter.toUpperCase()}) | Perfil: ${ev.personType}`,
                    calcMemory: [
                        `Memória de cálculo - AEC - Acompanhamento de Evolução Continuada`,
                        `Colaborador: ${ev.collaboratorName || '-'}`,
                        `Ano: ${ev.periodYear}`,
                        `Período: ${quarterLabelMap[evalQuarter] || evalQuarter.toUpperCase()}`,
                        `Perfil: ${ev.personType || '-'}`,
                        `Nota qualitativa usada (AvQl): ${safeAvQl.toFixed(2)}`,
                        `Pontos quantitativos usados (AvQt): ${safeAvQt.toFixed(2)}`,
                        qualiMemory,
                        quantiMemory,
                        `Total: ${parseFloat(coinsQuali || 0).toFixed(2)} + ${parseFloat(coinsQuanti || 0).toFixed(2)} = ${parseFloat(totalCoins || 0).toFixed(2)} coins`
                    ].join('\n')
                };
        } catch (e) { return { totalCoins: 0, detailReason: '' }; }
        }, []);

        // --- GERADOR MANUAL (BOTÃO PROCESSAR LOTE) ---
        const handleGenerateFromEvals = async () => {
            let count = 0;
            const currentConfig = coinConfigDB[0] || localConfig;

            if (evalQuarter === 'anual') {
                alert("A distribuição anual de AcadCoins foi desativada. Utilize apenas Q1, Q2, Q3 ou Q4.");
                return;
            }
            
            // Verifica se a data de corte do período avaliado já expirou
            const mapQuarterMonth = { q1: '03-31', q2: '06-30', q3: '09-30', q4: '12-31' };
            const proxyDate = `${evalYear}-${mapQuarterMonth[evalQuarter] || '12-31'}`;
            if (isPeriodLocked(proxyDate)) return alert("O período selecionado já passou da data de corte. O cálculo não pode mais ser processado.");

            for (const ev of data.evaluations) {
                if (ev.periodYear !== evalYear) continue;
                const collab = data.collaborators.find(c => c.id === ev.collaboratorId);
                if (!collab || collab.participatesAcadCoins === false) continue;
                if (collab.acadCoinsStartDate && collab.acadCoinsStartDate > todayStr) continue;
                if (collab.acadCoinsEndDate && collab.acadCoinsEndDate < todayStr) continue;

                const statusMap = { 'Em Andamento': 0, 'T1-Concluido': 1, 'T2-Concluido': 2, 'T3-Concluido': 3, 'T4-Concluido': 4, 'Finalizada': 4, 'Concluída Financeiro': 4 };
                const evStatusLevel = statusMap[ev.status] || 0;
                let requiredLevel = 4; let qTag = ''; 
                if (evalQuarter === 'q1') { requiredLevel = 1; qTag = 'T1'; } if (evalQuarter === 'q2') { requiredLevel = 2; qTag = 'T2'; }
                if (evalQuarter === 'q3') { requiredLevel = 3; qTag = 'T3'; } if (evalQuarter === 'q4') { requiredLevel = 4; qTag = 'T4'; }
                
                if (evStatusLevel < requiredLevel) continue; 

                let hasManagerSigned = false;
                const mgrSigs = ev.managerSignatures || [];
                if (evalQuarter === 'anual') hasManagerSigned = mgrSigs.some(s => s.statusRef?.toUpperCase().includes('T4') || s.statusRef?.toUpperCase().includes('FINALIZADA'));
                else hasManagerSigned = mgrSigs.some(s => s.statusRef?.toUpperCase().includes(qTag) || s.statusRef?.toUpperCase().includes('FINALIZADA') || s.statusRef?.toUpperCase().includes('ASSINADA'));
                
                if (!hasManagerSigned) continue; 

                const { totalCoins, detailReason, calcMemory } = calculateEvalCoins(ev, evalQuarter, currentConfig);
                const sourceId = `${ev.id}_${evalQuarter}`;
                
                if (!distDB.some(d => d.sourceId === sourceId) && totalCoins > 0) {
                    await saveDist(sanitizeForFirebase({
                    id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
                    date: new Date().toISOString().split('T')[0],
                    collabId: ev.collaboratorId,
                    quantity: parseFloat(totalCoins.toFixed(2)),
                    reason: detailReason,
                    sourceId: sourceId,
                    type: 'av_desempenho',
                    calcMemory: calcMemory || ''
                }));
                    count++;
                }
            }
            
            if (count > 0) alert(`Sucesso! Foram gerados AcadCoins para ${count} avaliações.`);
            else alert(`Nenhuma avaliação elegível para processar manualmente.`);
        };

        // --- SINCRONIZADOR AUTOMÁTICO (RODA EM BACKGROUND) ---
        React.useEffect(() => {
            const currentConfig = coinConfigDB[0] || localConfig;
            if (!currentConfig.autoGenerateCoinsOnSignature) return;

            const timer = setTimeout(async () => {
                const trRule = getActiveRule(currentConfig.treinamentosHistory, todayStr);
                
                // CORREÇÃO: Captura os fatores numéricos da regra ativa no histórico
                const factorD1 = parseFloat(trRule.formulaTrainingD1 !== undefined ? trRule.formulaTrainingD1 : currentConfig.formulaTrainingD1) || 0;
                const factorD2 = parseFloat(trRule.formulaTrainingD2 !== undefined ? trRule.formulaTrainingD2 : currentConfig.formulaTrainingD2) || 0;
                
                // Captura os limites de horas da regra ativa
                const maxH1 = parseFloat(trRule.maxTrainingHoursD1 !== undefined ? trRule.maxTrainingHoursD1 : currentConfig.maxTrainingHoursD1) || 0;
                const maxH2 = parseFloat(trRule.maxTrainingHoursD2 !== undefined ? trRule.maxTrainingHoursD2 : currentConfig.maxTrainingHoursD2) || 0;

                const quarters = ['q1', 'q2', 'q3', 'q4'];

                for (const ev of data.evaluations) {
                    const collab = data.collaborators.find(c => c.id === ev.collaboratorId);
                    if (!collab || collab.participatesAcadCoins === false) continue;
                    if (collab.acadCoinsStartDate && collab.acadCoinsStartDate > todayStr) continue;
                    if (collab.acadCoinsEndDate && collab.acadCoinsEndDate < todayStr) continue;

                    for (const q of quarters) {
                        const sourceId = `${ev.id}_${q}`;
                        let qTag = '';
                        if (q === 'q1') qTag = 'T1'; if (q === 'q2') qTag = 'T2'; if (q === 'q3') qTag = 'T3'; if (q === 'q4') qTag = 'T4';
                        
                        const mgrSigs = ev.managerSignatures || [];
                        let isSigned = false;
                        if (q === 'anual') isSigned = mgrSigs.some(s => s.statusRef?.toUpperCase().includes('T4') || s.statusRef?.toUpperCase().includes('FINALIZADA'));
                        else isSigned = mgrSigs.some(s => s.statusRef?.toUpperCase().includes(qTag) || s.statusRef?.toUpperCase().includes('FINALIZADA') || s.statusRef?.toUpperCase().includes('ASSINADA'));

                        const existingCoin = distDB.find(d => d.sourceId === sourceId);

                        // REGRA 1: Assinou e não tem moeda? Gera automaticamente.
                        if (isSigned && !existingCoin) {
                            const { totalCoins, detailReason, calcMemory } = calculateEvalCoins(ev, q, currentConfig);
                            if (totalCoins > 0) {
                                await await saveDist(sanitizeForFirebase({
                                    id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
                                    date: new Date().toISOString().split('T')[0],
                                    collabId: ev.collaboratorId,
                                    quantity: parseFloat(totalCoins.toFixed(2)),
                                    reason: detailReason,
                                    sourceId: sourceId,
                                    type: 'av_desempenho',
                                    calcMemory: calcMemory || ''
                                }));
                            }
                        } 
                        // REGRA 2: Removeu assinatura e a moeda existe? Deleta e gera Log Auditoria.
                        else if (!isSigned && existingCoin) {
                            if (actions.logAction) {
                            actions.logAction('Exclusao_AcadCoin_Auto', { 
                                details: `AcadCoin excluído automaticamente porque a assinatura do gestor foi desfeita na Avaliação ${ev.id} (${q.toUpperCase()}).` 
                            });
                        }
                        }
                    }
                }
            }, 2000); 

            return () => clearTimeout(timer);
        }, [data.evaluations, distDB, coinConfigDB, localConfig.autoGenerateCoinsOnSignature, calculateEvalCoins, saveDist, delDist, actions, currentUser]);

         // --- LIMPEZA DE REGISTROS ANUAIS DE AEC - ACOMPANHAMENTO DE EVOLUÇÃO CONTINUADA ---
        // Regra atual: AcadCoins de avaliação só podem ser gerados por fechamento trimestral.
        React.useEffect(() => {
            if (!distDB || distDB.length === 0) return;

            const annualEvalCoins = distDB.filter(d =>
                d.type === 'av_desempenho' &&
                (
                    String(d.sourceId || '').toLowerCase().endsWith('_anual') ||
                    String(d.reason || '').toUpperCase().includes('(ANUAL)')
                )
            );

            annualEvalCoins.forEach(d => {
                if (actions.logAction) {
                    actions.logAction('Exclusao_AcadCoin_Anual', {
                        details: `AcadCoin anual removido por regra de negócio: avaliações geram moedas somente por trimestre. Registro: ${d.reason || d.id}`
                    });
                }

                delDist(d.id);
            });
        }, [distDB, delDist, actions]);

        // --- NOVO: MEMÓRIA DE CÁLCULO E APURAÇÃO DAS INDICAÇÕES ---
        const recognitionReportData = useMemo(() => {
            const currentConfig = coinConfigDB[0] || localConfig;
            const minInd = parseInt(currentConfig.minIndications) || 0;
            let fracNum = 0, fracDen = 1;
            
            if (currentConfig.fractionOutsideTeam) {
                const parts = currentConfig.fractionOutsideTeam.split('/');
                if (parts.length === 2) { fracNum = parseInt(parts[0]); fracDen = parseInt(parts[1]); }
            }
            const requiredFraction = fracNum / fracDen;

            const groups = {};
            
            // Agrupa todas as indicações do banco
            distDB.filter(d => d.type === 'indicacao').forEach(ind => {
                const per = getPeriodStr(ind.date);
                if (totPeriod && per !== totPeriod) return; 

                const key = `${per}_${ind.collabId}`;
                if (!groups[key]) {
                    const collab = data.collaborators.find(c => c.id === ind.collabId);
                    groups[key] = {
                        period: per,
                        collabId: ind.collabId,
                        collabName: collab?.name || 'Colaborador Excluído',
                        collabDept: collab?.departmentId,
                        indications: [],
                        total: 0,
                        outsideTeamCount: 0,
                        status: '',
                        justification: '',
                        receivedCoins: false,
                        coinsValue: parseFloat(currentConfig.coinsPerRecognition) || 0
                    };
                }

                const sender = data.collaborators.find(c => c.id === ind.fromId);
                const isOutside = sender && sender.departmentId !== groups[key].collabDept;
                
                groups[key].indications.push({
                    senderName: sender?.name || 'Desconhecido',
                    reason: (ind.reason || '').split('| Motivo:')[1]?.trim() || ind.reason,
                    isOutside
                });
                
                groups[key].total += 1;
                if (isOutside) groups[key].outsideTeamCount += 1;
            });

            // Aplica as regras de negócio
            return Object.values(groups).map(g => {
                const hasCoinsPaid = distDB.some(d => d.type === 'premio_reconhecimento' && d.collabId === g.collabId && getPeriodStr(d.date) === g.period);
                
                if (hasCoinsPaid) {
                    g.receivedCoins = true;
                    g.status = 'SIM';
                    g.justification = '-';
                } else {
                    let failed = false;
                    let reasons = [];
                    if (g.total < minInd) {
                        failed = true;
                        reasons.push(`Não atingiu ${minInd} indicações (Teve ${g.total}).`);
                    }
                    if (g.total > 0 && (g.outsideTeamCount / g.total) < requiredFraction) {
                        failed = true;
                        reasons.push(`Fora do setor: Exigido ${currentConfig.fractionOutsideTeam}, teve ${g.outsideTeamCount}/${g.total}.`);
                    }
                    
                    if (!failed) {
                        g.receivedCoins = true; 
                        g.status = 'SIM (Pendente)';
                        g.justification = 'Elegível. Aguardando data de fechamento para crédito.';
                    } else {
                        g.receivedCoins = false;
                        g.status = 'NÃO';
                        g.justification = reasons.join(' ');
                    }
                }

                // Filtro de busca na tabela
                if (totSearch) {
                    const s = totSearch.toLowerCase();
                    if (!g.collabName.toLowerCase().includes(s) && !g.justification.toLowerCase().includes(s)) return null;
                }

                return g;
            }).filter(Boolean).sort((a, b) => {
                if (a.receivedCoins === b.receivedCoins) {
                    if (b.coinsValue === a.coinsValue) return a.collabName.localeCompare(b.collabName);
                    return b.coinsValue - a.coinsValue;
                }
                return a.receivedCoins ? -1 : 1;
            });
        }, [distDB, data.collaborators, config, totPeriod, totSearch, localConfig, getPeriodStr]);

        // --- MOTOR AUTOMÁTICO: CREDITA MOEDAS 1 DIA APÓS O FECHAMENTO ---
        React.useEffect(() => {
            if (!isAdmin) return;
            const currentConfig = coinConfigDB[0] || localConfig;
            if (!currentConfig.enableRecognition) return;

            const pendings = recognitionReportData.filter(g => g.status === 'SIM (Pendente)');
            if (pendings.length === 0) return;

            const processPendings = async () => {
                for (const g of pendings) {
                    const periodEnd = getEndOfPeriodDate(g.period);
                    const [y, m] = periodEnd.split('-');
                    
                    // Calcula o dia de liberação (Último dia do mês + Dias de Corte + 1 dia seguinte)
                    const releaseDate = new Date(parseInt(y), parseInt(m), 0, 12, 0, 0); 
                    releaseDate.setDate(releaseDate.getDate() + parseInt(currentConfig.cutoffDays || 0) + 1);
                    
                    if (new Date() >= releaseDate) {
                        const rewardId = `rec_rew_${g.collabId}_${g.period}`;
                        const exists = distDB.some(d => d.id === rewardId);
                        
                        if (!exists) {
                            try {
                                await saveDist(sanitizeForFirebase({
                                    id: rewardId,
                                    date: new Date().toISOString().split('T')[0],
                                    collabId: g.collabId,
                                    quantity: g.coinsValue,
                                    reason: `Prêmio por Reconhecimento de Colegas (${g.period})`,
                                    type: 'premio_reconhecimento'
                                }));
                            } catch (e) { console.error("Erro apuração automática:", e); }
                        }
                    }
                }
            };
            processPendings();
        }, [recognitionReportData, isAdmin, config, distDB, saveDist, localConfig, getEndOfPeriodDate]);

        const totalizacaoData = useMemo(() => {
            const groups = {};
            const availablePeriods = new Set();

            distDB.forEach(d => {
                if (d.date >= config.startDate) availablePeriods.add(getPeriodStr(d.date));
            });
            caixaDB.forEach(c => {
            if (c.date >= config.startDate && c.type !== 'Previsto') availablePeriods.add(getPeriodStr(c.date));
            });
            availablePeriods.add(getPeriodStr(todayStr)); 

            const periodStats = {};
            availablePeriods.forEach(p => {
                const endDate = getEndOfPeriodDate(p);
                const targetQueryDate = endDate > todayStr ? todayStr : endDate;
                periodStats[p] = {
                    stats: getStatsAtDate(targetQueryDate),
                    targetDate: targetQueryDate
                };
            });

            data.collaborators.forEach(collab => {
                if (collab.participatesAcadCoins === false) return; 
                
                const start = collab.acadCoinsStartDate; 
                if (!start) return; 

                availablePeriods.forEach(periodStr => {
                    const { targetDate } = periodStats[periodStr];
                    
                    if (start > targetDate) return; 
                    const end = collab.acadCoinsEndDate;
                    if (end && end < targetDate) return; 

                    const groupKey = `${collab.id}_${periodStr}`;
                    const deptName = data.departments.find(dep => dep.id === collab.departmentId)?.name || 'N/D';

                    groups[groupKey] = {
                        collabId: collab.id,
                        name: collab.name,
                        email: collab.email || 'N/D',
                        deptName: deptName,
                        period: periodStr,
                        totalCoins: 0
                    };
                });
            });

            distDB.forEach(d => {
                if (d.date < config.startDate) return;
                const periodStr = getPeriodStr(d.date);
                const groupKey = `${d.collabId}_${periodStr}`;
                if (groups[groupKey] && isCoinAvailable(d)) {
                groups[groupKey].totalCoins += parseFloat(d.quantity || 0);
            }
            });

            let rows = Object.values(groups).map(g => {
                const stats = periodStats[g.period].stats;
                const valProportional = g.totalCoins * stats.uValue;
                
                const collab = data.collaborators.find(c => c.id === g.collabId);
                let wasActive = false;
                if (collab && collab.participatesAcadCoins !== false) {
                    const start = collab.acadCoinsStartDate;
                    const end = collab.acadCoinsEndDate;
                    if (start && start <= periodStats[g.period].targetDate) {
                        if (!end || end >= periodStats[g.period].targetDate) {
                            wasActive = true;
                        }
                    }
                }

                const valEqual = wasActive ? stats.equalBonusPerPerson : 0;

                return {
                    ...g,
                    unitValue: stats.uValue,
                    valProportional,
                    valEqual,
                    totalValue: valProportional + valEqual
                };
            });

            rows = rows.filter(r => r.totalValue > 0);

            if (totDept) rows = rows.filter(r => r.deptName === totDept);
            if (totPeriod) rows = rows.filter(r => r.period === totPeriod);
            if (totSearch) {
                const s = totSearch.toLowerCase();
                rows = rows.filter(r => r.name.toLowerCase().includes(s) || (r.email||'').toLowerCase().includes(s));
            }

            return { 
                rows: rows.sort((a,b) => b.period.localeCompare(a.period) || b.totalValue - a.totalValue),
                periods: Array.from(availablePeriods).sort().reverse()
            };
        }, [distDB, caixaDB, data.collaborators, data.departments, getPeriodStr, getEndOfPeriodDate, getStatsAtDate, todayStr, totDept, totPeriod, totSearch, config.startDate]);


            // --- RENDERIZADOR UNIVERSAL DE TABELAS DE HISTÓRICO DE CONFIGURAÇÃO ---
            const renderConfigHistoryTable = (moduleKey, columns, renderRowCells) => {
            const historyKey = `${moduleKey}History`;
            const history = localConfig[historyKey] || [];
            const showInactive = showInactiveRulesMap[moduleKey];
            const filtered = history.filter(r => showInactive || !r.validTo || r.validTo >= todayStr);
            
            return (
                <div className={`mt-6 border rounded-xl overflow-hidden shadow-sm ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                    <div className={`p-3 flex justify-between items-center border-b ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-100 border-gray-200'}`}>
                        <span className="text-xs font-bold uppercase opacity-70 flex items-center gap-2"><Clock size={14}/> Histórico de Regras ({filtered.length})</span>
                        
                        {/* CORREÇÃO: Ícones removidos para evitar o ReferenceError */}
                        <Button size="sm" variant="secondary" onClick={() => setShowInactiveRulesMap({...showInactiveRulesMap, [moduleKey]: !showInactive})} className="h-7 text-[10px]">
                            {showInactive ? 'Ocultar Inativos' : 'Ver Inativos'}
                        </Button>

                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs text-left">
                            <thead className={`font-bold uppercase opacity-70 ${isDarkMode ? 'bg-slate-900' : 'bg-gray-50'}`}>
                                <tr>
                                    <th className="p-3 w-24">Início</th><th className="p-3 w-24">Fim</th>
                                    {columns.map(c => <th key={c} className="p-3">{c}</th>)}
                                    <th className="p-3 text-center w-24">Ações</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                {filtered.sort((a,b)=>b.validFrom.localeCompare(a.validFrom)).map(row => (
                                    <tr key={row.id} className={`${row.validTo && row.validTo < todayStr ? 'opacity-50' : ''} ${isDarkMode ? 'hover:bg-slate-800/50' : 'hover:bg-gray-50'}`}>
                                        <td className="p-3 font-mono">{new Date(row.validFrom+'T12:00:00').toLocaleDateString('pt-BR')}</td>
                                        <td className="p-3 font-mono">{row.validTo ? new Date(row.validTo+'T12:00:00').toLocaleDateString('pt-BR') : 'Ativo'}</td>
                                        {renderRowCells(row)}
                                        <td className="p-3 text-center flex justify-center gap-1">
                                            <button onClick={() => { setRuleForms({...ruleForms, [moduleKey]: row}); setIsConfigModalOpen(true); }} className="text-blue-500 p-1.5 hover:bg-blue-100 rounded"><Edit3 size={14}/></button>
                                            <button onClick={() => { 
                                                let confirmed = true;
                                                try { confirmed = window.confirm('Excluir regra do histórico?'); } catch(e) { confirmed = true; } // Bypassa o erro do sandbox
                                                if(confirmed) setLocalConfig({...localConfig, [historyKey]: history.filter(r => r.id !== row.id)}); 
                                            }} className="text-red-500 p-1.5 hover:bg-red-100 rounded"><Trash2 size={14}/></button>
                                        </td>
                                    </tr>
                                ))}
                                {filtered.length === 0 && <tr><td colSpan={columns.length+3} className="p-6 text-center italic opacity-50">Nenhum histórico configurado.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        };

        // --- VERIFICAÇÃO DE REGRAS ATIVAS PARA OS BOTÕES DA TELA ---
        const ruleAcadNews = getActiveRule(config.acadnewsHistory, todayStr);
        const isAcadNewsEnabled = ruleAcadNews.enableAcadNews !== undefined ? ruleAcadNews.enableAcadNews : config.enableAcadNews;

        const ruleRec = getActiveRule(config.reconhecimentoHistory, todayStr);
        const isRecEnabled = ruleRec.enableRecognition !== undefined ? ruleRec.enableRecognition : config.enableRecognition;

        const ruleGift = getActiveRule(config.presentearHistory, todayStr);
        const isGiftEnabled = ruleGift.enableGifting !== undefined ? ruleGift.enableGifting : config.enableGifting;
        
        const isProjEnabled = config.enableProjects;

        const ruleIdeias = getActiveRule(config.ideiasHistory, todayStr);
        const isIdeiasEnabled = ruleIdeias.enableIdeas !== undefined ? ruleIdeias.enableIdeas : (config.enableIdeas || false);

        const [showArchivedNews, setShowArchivedNews] = useState(false);
        const { feed: acadNewsFeed, raffleWinners, hasNewItems } = useMemo(() => {
            let tempFeed = [];
            let winners = [];
            let hasNew = false;
            const now = Date.now();
            
            // Garante que o mural social e os dias de arquivamento sejam lidos corretamente
            const ruleAcadNews = getActiveRule(localConfig.acadnewsHistory, todayStr);
            const isSocialEnabled = ruleAcadNews.enableSocialWall !== undefined ? ruleAcadNews.enableSocialWall : (localConfig.enableSocialWall !== false);
            const archiveDays = parseInt(ruleAcadNews.archiveDays) || 7;
            const archiveMs = archiveDays * 24 * 60 * 60 * 1000;

            // 1. Notícias do Admin
            (newsDB || []).forEach(n => {
                const ts = new Date(n.date).getTime();
                const isAutoArchived = (now - ts) > archiveMs;
                const isArchived = n.archived || isAutoArchived;

                if (n.id.startsWith('news_raffle_')) {
                    const isNewToUser = ts > sessionHighlightTime;
                    winners.push({ ...n, timestamp: ts, isNewToUser });
                    if (ts > lastSeenNewsTime) hasNew = true;
                } else {
                    const isNewToUser = !isArchived && ts > sessionHighlightTime;
                    tempFeed.push({ ...n, isNews: true, timestamp: ts, isArchived, isNewToUser });
                    if (!isArchived && ts > lastSeenNewsTime) hasNew = true;
                }
            });

            // 1.5. Ideias Aprovadas
            (ideasDB || []).forEach(i => {
                if (i.status === 'Aprovada') {
                    const ts = new Date(i.date).getTime();
                    const isAutoArchived = (now - ts) > archiveMs;
                    const isArchived = i.archived || isAutoArchived;
                    const isNewToUser = !isArchived && ts > sessionHighlightTime;
                    tempFeed.push({ ...i, isNews: false, isIdea: true, timestamp: ts, isArchived, isNewToUser });
                    if (!isArchived && ts > lastSeenNewsTime) hasNew = true;
                }
            });
            
            // 1.6. Entradas Previstas na Caixinha (Incentivo / Novo Cliente)
            (caixaDB || []).filter(c => c.type === 'Previsto').forEach(c => {
                const eventTs = new Date(c.date + 'T00:00:00').getTime(); 
                let notifyTs = eventTs; 
                
                if (eventTs > now) {
                    const idMatch = String(c.id).match(/\d{13}/);
                    notifyTs = idMatch ? parseInt(idMatch[0]) : (now - 1000);
                }

                const isAutoArchived = (now - eventTs) > archiveMs; 
                const isArchived = c.archived || isAutoArchived;
                const isNewToUser = !isArchived && notifyTs > sessionHighlightTime;
                
                tempFeed.push({ ...c, isNews: false, isPrevisto: true, timestamp: eventTs, isArchived, isNewToUser });
                if (!isArchived && notifyTs > lastSeenNewsTime) hasNew = true;
            });

            // 2. Interações Sociais e Projetos
            if (isSocialEnabled) {
                const projGroups = {};
                
                (distDB || []).forEach(d => {
                    const eventTs = new Date(d.date + 'T00:00:00').getTime();
                    const isAutoArchived = (now - eventTs) > archiveMs;
                    const isArchived = d.archived || isAutoArchived;

                    if (d.type === 'indicacao' || d.type === 'presente_recebido') {
                        const isNewToUser = !isArchived && eventTs > sessionHighlightTime && d.fromId !== loggedCollabId;
                        tempFeed.push({ ...d, isNews: false, timestamp: eventTs, isArchived, isNewToUser });
                        if (!isArchived && eventTs > lastSeenNewsTime && d.fromId !== loggedCollabId) hasNew = true;
                    } 
                    else if (d.type === 'doacao' && parseFloat(d.quantity) < 0) {
                        const isNewToUser = !isArchived && eventTs > sessionHighlightTime && d.collabId !== loggedCollabId;
                        tempFeed.push({ ...d, isNews: false, isDoacao: true, timestamp: eventTs, isArchived, isNewToUser });
                        if (!isArchived && eventTs > lastSeenNewsTime && d.collabId !== loggedCollabId) hasNew = true;
                    }
                    else if (d.type === 'projeto') {
                        let notifyTs = eventTs;
                        if (eventTs > now) {
                            const idMatch = String(d.id).match(/\d{13}/);
                            notifyTs = idMatch ? parseInt(idMatch[0]) : (now - 1000);
                        }

                        const k = d.sourceId + '_' + d.date;
                        if (!projGroups[k]) projGroups[k] = { ...d, participants: [], totalCoins: 0, ts: eventTs, notifyTs, isArchived: d.archived || ((now - eventTs) > archiveMs) };
                        
                        const cName = data.collaborators.find(c => c.id === d.collabId)?.name || 'Colega';
                        if (!projGroups[k].participants.includes(cName)) projGroups[k].participants.push(cName);
                        projGroups[k].totalCoins += parseFloat(d.quantity || 0);
                    }
                });
                
                Object.values(projGroups).forEach(p => {
                    const isNewToUser = !p.isArchived && p.notifyTs > sessionHighlightTime;
                    tempFeed.push({ ...p, isNews: false, isProjeto: true, timestamp: p.ts, isArchived: p.isArchived, isNewToUser });
                    if (!p.isArchived && p.notifyTs > lastSeenNewsTime) hasNew = true;
                });
            }
            
            // 3. Avaliação dos Níveis da Caixinha (Notificações de Metas e Alertas)
            const cxHistory = localConfig.niveisCaixaHistory || [];
            const activeCxRules = cxHistory.filter(r => r.validFrom <= todayStr && (!r.validTo || r.validTo >= todayStr));
            
            if (activeCxRules.length > 0) {
                const currentPeriod = getPeriodStr(todayStr);
                const periodCaixa = (caixaDB || []).filter(c => c.type !== 'Previsto' && getPeriodStr(c.date) === currentPeriod);
                const saldoPeriodo = periodCaixa.reduce((acc, c) => acc + parseFloat(c.amount||0), 0);
                const entradasPeriodo = periodCaixa.filter(c => parseFloat(c.amount)>0).reduce((acc, c) => acc + parseFloat(c.amount||0), 0);
                const saidasPeriodo = Math.abs(periodCaixa.filter(c => parseFloat(c.amount)<0).reduce((acc, c) => acc + parseFloat(c.amount||0), 0));
                
                activeCxRules.forEach(rule => {
                    let trigger = false;
                    let displayVal = 0;
                    const val = parseFloat(rule.valor);
                    
                    if (rule.tipoAviso === 'meta_saldo') {
                        if (rule.tipoValor === 'fixo' && saldoPeriodo >= val) { trigger = true; displayVal = val; }
                    } else if (rule.tipoAviso === 'alerta_reducao') {
                        if (rule.tipoValor === 'fixo' && saidasPeriodo >= val) { trigger = true; displayVal = saidasPeriodo; }
                        else if (rule.tipoValor === 'percentual' && entradasPeriodo > 0 && (saidasPeriodo/entradasPeriodo)*100 >= val) { trigger = true; displayVal = (saidasPeriodo/entradasPeriodo)*100; }
                    }
                    
                    if (trigger) {
                        const formattedVal = rule.tipoValor === 'percentual' ? `${displayVal.toFixed(1)}%` : formatMoney(displayVal);
                        const msg = rule.mensagem.replace(/{valor}/g, formattedVal);
                        const ts = new Date(todayStr + 'T00:00:00').getTime(); 
                        const isNewToUser = ts > sessionHighlightTime;
                        tempFeed.push({
                            id: `nivel_${rule.id}_${currentPeriod}`,
                            isNews: false, isNivelCaixa: true,
                            tipoAviso: rule.tipoAviso,
                            title: rule.tipoAviso === 'meta_saldo' ? '🎉 Meta da Caixinha Alcançada!' : '⚠️ Alerta de Redução na Caixinha',
                            content: msg,
                            timestamp: ts,
                            isArchived: false,
                            isNewToUser
                        });
                        if (ts > lastSeenNewsTime) hasNew = true;
                    }
                });
            }
            
            return { 
                feed: tempFeed.sort((a, b) => b.timestamp - a.timestamp), 
                raffleWinners: winners.sort((a, b) => b.timestamp - a.timestamp),
                hasNewItems: hasNew 
            };
        }, [newsDB, distDB, caixaDB, localConfig, lastSeenNewsTime, sessionHighlightTime, loggedCollabId, getPeriodStr, todayStr, data.collaborators]);

        const leaderboardStats = useMemo(() => {
            if (!ruleAcadNews.enableLeaderboard) return [];
            const tally = {};

            const initUser = (id) => {
                if (!tally[id]) {
                    tally[id] = { 
                        id: id, 
                        name: data.collaborators.find(c => c.id === id)?.name || 'Colega', 
                        count: 0,
                        indicacoes: 0,
                        presentes: 0,
                        projetos: 0,
                        doacoes: 0
                    };
                }
            };

            (distDB || []).forEach(d => {
                // 1. Reconhecimentos feitos (fromId)
                if (d.type === 'indicacao' && d.fromId) {
                    initUser(d.fromId);
                    tally[d.fromId].indicacoes += 1;
                    tally[d.fromId].count += 1;
                }
                // 2. Presentes dados (contamos o fromId em 'presente_recebido')
                else if (d.type === 'presente_recebido' && d.fromId) {
                    initUser(d.fromId);
                    tally[d.fromId].presentes += 1;
                    tally[d.fromId].count += 1;
                }
                // 3. Participações em projetos concluídos (collabId)
                else if (d.type === 'projeto' && d.collabId) {
                    initUser(d.collabId);
                    tally[d.collabId].projetos += 1;
                    tally[d.collabId].count += 1;
                }
                // 4. Doações feitas para colegas ou campanhas sociais (quantity negativa e type doacao)
                else if (d.type === 'doacao' && d.collabId && parseFloat(d.quantity) < 0) {
                    initUser(d.collabId);
                    tally[d.collabId].doacoes += 1;
                    tally[d.collabId].count += 1;
                }
            });

            return Object.values(tally).sort((a,b) => b.count - a.count).slice(0, 5);
        }, [distDB, data.collaborators, ruleAcadNews.enableLeaderboard]);

        const handleArchiveItem = async (item) => {
            if (!window.confirm("Arquivar este item do mural?")) return;
            try {
                if (item.isNews) await saveNews({ ...item, archived: true });
                else await saveDist({ ...item, archived: true });
            } catch(e) { console.error("Erro ao arquivar", e); }
        };

        const getDistributionCalcMemory = (d) => {
            if (!d) return '';

            if (d.calcMemory) return d.calcMemory;
            if (d.calculationMemory) return d.calculationMemory;
            if (d.memoriaCalculo) return d.memoriaCalculo;

            if (d.type === 'av_desempenho') {
                return [
                    'Memória de cálculo não gravada neste lançamento antigo.',
                    `Origem: AEC - Acompanhamento de Evolução Continuada`,
                    `Motivo: ${safeText(d.reason, '-')}`,
                    `Quantidade: ${parseFloat(d.quantity || 0).toFixed(2)} coins`,
                    '',
                    'Novos lançamentos passarão a gravar a memória completa automaticamente.'
                ].join('\n');
            }

            if (d.type === 'treinamento') {
                return [
                    'Memória de cálculo não gravada neste lançamento antigo.',
                    `Origem: Curso/Treinamento`,
                    `Motivo: ${safeText(d.reason, '-')}`,
                    `Quantidade: ${parseFloat(d.quantity || 0).toFixed(2)} coins`,
                    '',
                    'Novos lançamentos passarão a gravar a memória completa automaticamente.'
                ].join('\n');
            }

            return safeText(d.reason, '');
        };

        const hasDistributionCalcMemory = (d) => {
            return d?.type === 'av_desempenho' || d?.type === 'treinamento';
        };
        
        
const pendingTrainingApprovals = useMemo(() => {
    const myCollab = data.collaborators?.find(c =>
        String(c.email || '').trim().toLowerCase() ===
        String(currentUser?.email || '').trim().toLowerCase()
    );

    const myManagerRecord = data.managers?.find(m =>
        String(m.email || '').trim().toLowerCase() ===
        String(currentUser?.email || '').trim().toLowerCase()
    );

    return (distDB || []).filter(d => {
        if (d?.type !== 'treinamento') return false;
        if (d?.approvalStatus !== 'pending') return false;

        const targetCollab = data.collaborators?.find(c =>
            String(c.id) === String(d.collabId)
        );

        if (!targetCollab) return false;

        if (currentUser?.role === 'SuperAdm') return true;
        if (currentUser?.permissions?.acad_coins_admin === true) return true;

        const managerIds = Array.isArray(targetCollab.managerIds)
            ? targetCollab.managerIds.map(String)
            : [];

        return (
            myCollab && managerIds.includes(String(myCollab.id))
        ) || (
            myManagerRecord && managerIds.includes(String(myManagerRecord.id))
        );
    });
}, [distDB, data.collaborators, data.managers, currentUser]);

const pendingTrainingApprovalsGrouped = useMemo(() => {
    const grouped = {};

    (pendingTrainingApprovals || []).forEach(coin => {
        const collab = data.collaborators?.find(c =>
            String(c.id) === String(coin.collabId)
        );

        const training = (data.trainingData || []).find(t =>
            String(t.id) === String(coin.sourceId)
        );

        const collabId = String(coin.collabId || 'sem-colab');
        const collabName = safeText(collab?.name, 'Colaborador');

        if (!grouped[collabId]) {
            grouped[collabId] = {
                collabId,
                collabName,
                totalCoins: 0,
                items: []
            };
        }

        const links = [
            (training?.certLink || coin.trainingCertLink) && {
                label: 'Certificado / Print',
                url: training?.certLink || coin.trainingCertLink
            },
            (training?.summaryLink || coin.trainingSummaryLink) && {
                label: 'Resumo',
                url: training?.summaryLink || coin.trainingSummaryLink
            }
        ].filter(Boolean);

        grouped[collabId].items.push({
            ...coin,
            trainingTitle: safeText(training?.description || training?.title || coin.trainingTitle, 'Curso sem título'),
            reasonText: safeText(coin.reason, 'Curso pendente de aprovação'),
            links
        });

        grouped[collabId].totalCoins += parseFloat(coin.quantity || 0);
    });

    return Object.values(grouped).sort((a, b) =>
        String(a.collabName || '').localeCompare(String(b.collabName || ''))
    );
}, [pendingTrainingApprovals, data.collaborators, data.trainingData]);

        const approveTrainingCoins = async (coin, effectiveMode = 'course_date') => {
            if (!coin) return;

            const finalEffectiveDate =
                effectiveMode === 'approval_date'
                    ? new Date().toISOString().split('T')[0]
                    : (coin.originalCourseDate || coin.effectiveDate || coin.date);

            await saveDist({
                ...coin,
                status: 'approved',
                blocked: false,
                pendingApproval: false,
                approvalStatus: 'approved',
                approvedAt: new Date().toISOString(),
                approvedBy: currentUser?.id || '',
                approvedByName: currentUser?.name || '',
                effectiveDate: finalEffectiveDate,
                date: finalEffectiveDate,
                reason: String(coin.reason || '').replace('Curso pendente de aprovação:', 'Curso:'),
                calcMemory: [
                    coin.calcMemory || '',
                    '',
                    `Aprovação: aprovada por ${currentUser?.name || '-'} em ${new Date().toLocaleString('pt-BR')}.`,
                    `Data de efetivação escolhida: ${effectiveMode === 'approval_date' ? 'data da aprovação' : 'data de envio/conclusão do curso'} (${finalEffectiveDate}).`
                ].join('\n')
            });
        };

        const rejectTrainingCoins = async (coin) => {
            if (!coin) return;

            await saveDist({
                ...coin,
                status: 'rejected',
                blocked: true,
                pendingApproval: false,
                approvalStatus: 'rejected',
                rejectedAt: new Date().toISOString(),
                rejectedBy: currentUser?.id || '',
                rejectedByName: currentUser?.name || '',
                calcMemory: [
                    coin.calcMemory || '',
                    '',
                    `Aprovação: rejeitada por ${currentUser?.name || '-'} em ${new Date().toLocaleString('pt-BR')}.`
                ].join('\n')
            });
        };

        const [selectedTrainingApprovalGroup, setSelectedTrainingApprovalGroup] = useState(null);

        const getShortPersonName = (fullName) => {
            const clean = safeText(fullName, '').trim();
            if (!clean) return 'Colaborador';

            const parts = clean.split(/\s+/).filter(Boolean);
            if (parts.length === 1) return parts[0];

            return `${parts[0]} ${parts[1]}`;
        };

        return (
            <div className="space-y-3 animate-fade-in flex flex-col h-full px-2 pb-2 pt-0 sm:px-4 sm:pb-4 sm:pt-0">
                <style>{`
                :root { --app-accent:#0891b2; --app-accent-2:#4f46e5; --app-bg:#020617; --app-panel:#0f172a; --app-card:#1e293b; --app-line:#334155; --app-text:#f8fafc; --app-muted:#94a3b8; }
                .facil-theme-acad { --app-accent:#00a889; --app-accent-2:#0f766e; --app-bg:#061f1c; --app-panel:#0b2f2a; --app-card:#0f3d35; --app-line:#1e6b5c; --app-text:#ecfffa; --app-muted:#99d9cb; }
                .facil-theme-light { --app-accent:#0f7c72; --app-accent-2:#d97706; --app-bg:#f7f4ee; --app-panel:#fffaf2; --app-card:#ffffff; --app-line:#dfcdb4; --app-text:#102a2b; --app-muted:#64748b; }
                .app-modern-sidebar { background:linear-gradient(180deg,var(--app-panel),var(--app-bg)); border-color:var(--app-line); }
                .facil-theme-light .app-modern-sidebar { color:#123438; background:#fffaf2; }
                .facil-theme-light .app-modern-sidebar [class*="text-slate-"] { color:#49666b !important; }
                .app-menu-icon-box { width:34px;height:34px;border-radius:10px;display:inline-flex;align-items:center;justify-content:center;border:1px solid color-mix(in srgb,var(--app-accent) 50%,transparent);background:color-mix(in srgb,var(--app-accent) 12%,transparent); }
                .app-menu-number { min-width:34px;height:26px;border-radius:9px;display:inline-flex;align-items:center;justify-content:center;font-size:11px;font-weight:900;background:color-mix(in srgb,var(--app-accent) 9%,white);color:var(--app-text);border:1px solid color-mix(in srgb,var(--app-accent) 35%,transparent); }
                .facil-theme-light .app-menu-number { color:#123438; }
                .app-topbar { min-height:58px;display:flex;align-items:stretch;background:color-mix(in srgb,var(--app-panel) 92%,transparent);border-color:var(--app-line); }
                .app-modern-tab { min-width:145px;max-width:240px;height:42px;padding:0 12px;border:1px solid var(--app-line);border-bottom:none;border-radius:12px 12px 0 0;display:flex;align-items:center;gap:8px;font-size:12px;font-weight:800;color:var(--app-muted);background:color-mix(in srgb,var(--app-card) 85%,transparent); }
                .app-modern-tab.is-active { color:var(--app-text);border-color:var(--app-accent);box-shadow:inset 0 3px 0 var(--app-accent);background:var(--app-card); }
                .app-tab-dot { color:var(--app-accent); }.app-tab-close{margin-left:auto;padding:3px;border-radius:999px}.app-tab-close:hover{background:rgba(148,163,184,.2)}
                .app-circle-action { width:38px;height:38px;border-radius:999px;display:inline-flex;align-items:center;justify-content:center;border:1px solid var(--app-line);color:var(--app-accent);background:var(--app-card);position:relative; }.app-circle-action:hover{transform:translateY(-1px);box-shadow:0 5px 16px rgba(0,0,0,.15)}
                .app-logout-action{color:#fb7185}.app-notification-badge{position:absolute;right:-4px;top:-5px;background:#ef4444;color:white;border-radius:999px;min-width:18px;height:18px;padding:0 4px;font-size:9px;font-weight:900;display:flex;align-items:center;justify-content:center}
                .app-zoom-control { height:38px;border:1px solid var(--app-line);border-radius:12px;display:flex;align-items:center;overflow:hidden;background:var(--app-card);color:var(--app-text)}.app-zoom-control button{height:100%;padding:0 10px;font-weight:900}.app-zoom-control strong{padding:0 10px;color:#2563eb;font-size:12px}
                .app-user-chip{height:40px;display:flex;align-items:center;gap:8px;padding:3px 10px 3px 4px;border-left:1px solid var(--app-line);color:var(--app-text)}.app-user-chip img,.app-user-chip>span{width:34px;height:34px;border-radius:999px;object-fit:cover;background:var(--app-accent);color:white;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:900}.app-user-chip div{display:flex;flex-direction:column;line-height:1.1}.app-user-chip b{font-size:11px;white-space:nowrap}.app-user-chip small{font-size:9px;color:var(--app-muted)}
                .app-dialog{background:var(--app-card);border-color:var(--app-line);color:var(--app-text)}.app-dialog-header{padding:18px 22px;border-bottom:1px solid var(--app-line);display:flex;align-items:center;gap:12px}.app-dialog-header>svg{color:var(--app-accent)}.app-dialog-header h3{font-size:18px;font-weight:900}.app-dialog-header p{font-size:11px;color:var(--app-muted);margin-top:2px}.app-dialog-header>button,.app-close-btn{margin-left:auto}.app-dialog-footer{padding:14px 20px;border-top:1px solid var(--app-line);display:flex;justify-content:flex-end;gap:10px}
                .app-cache-option{display:flex;gap:12px;align-items:flex-start;border:1px solid var(--app-line);border-radius:14px;padding:14px;cursor:pointer}.app-cache-option.is-selected{border-color:var(--app-accent);background:color-mix(in srgb,var(--app-accent) 10%,transparent)}.app-cache-option b{display:block}.app-cache-option small{display:block;color:var(--app-muted);margin-top:3px}.app-primary-btn,.app-secondary-btn{height:40px;border-radius:10px;padding:0 16px;display:inline-flex;align-items:center;justify-content:center;gap:7px;font-weight:900;font-size:12px}.app-primary-btn{background:linear-gradient(135deg,var(--app-accent),var(--app-accent-2));color:white}.app-secondary-btn{border:1px solid var(--app-line);color:var(--app-text)}
                .app-tab-button{height:38px;padding:0 14px;border:1px solid var(--app-line);border-radius:10px;display:inline-flex;align-items:center;gap:7px;font-size:12px;font-weight:900}.app-tab-button.active{background:var(--app-accent);color:white;border-color:var(--app-accent)}
                .app-help-search{display:flex;gap:12px;align-items:end;border:1px solid var(--app-line);border-radius:16px;padding:15px;background:color-mix(in srgb,var(--app-panel) 55%,transparent)}.app-help-search input,.app-help-search select,.app-suggestion-form input,.app-suggestion-form select,.app-suggestion-form textarea{width:100%;border:1px solid var(--app-line);border-radius:10px;background:var(--app-panel);color:var(--app-text);padding:10px 12px;outline:none}.app-help-search input{padding-left:36px}.app-help-search label{font-size:10px;font-weight:900;color:var(--app-muted)}
                .manual-layout{margin-top:16px}.manual-cards{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}.manual-vitrine{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px}.manual-lista{display:flex;flex-direction:column;gap:8px}.manual-item{position:relative;border:1px solid var(--app-line);border-radius:18px;padding:16px;background:linear-gradient(135deg,color-mix(in srgb,var(--app-accent) 9%,var(--app-card)),var(--app-card));display:flex;gap:12px;align-items:flex-start;min-height:165px}.manual-lista .manual-item{min-height:auto;align-items:center}.manual-vitrine .manual-item{min-height:210px}.manual-icon{width:42px;height:42px;border-radius:13px;background:var(--app-accent);color:white;display:flex;align-items:center;justify-content:center;flex:none}.manual-content{flex:1}.manual-content small{font-size:9px;text-transform:uppercase;font-weight:900;color:var(--app-muted)}.manual-content h4{font-size:14px;font-weight:900;margin-top:4px}.manual-content p{font-size:11px;color:var(--app-muted);margin-top:8px;line-height:1.5}.manual-open{position:absolute;left:16px;right:16px;bottom:14px;background:var(--app-accent);color:white;border-radius:9px;padding:8px;font-size:11px;font-weight:900}.manual-lista .manual-open{position:static;width:auto;margin-left:auto}.app-suggestion-form{border:1px solid var(--app-line);border-radius:18px;padding:22px;background:var(--app-panel);display:flex;flex-direction:column;gap:14px}.app-suggestion-form h3{font-size:20px;font-weight:900}.app-suggestion-form p{font-size:12px;color:var(--app-muted)}.app-suggestion-form label{font-size:11px;font-weight:900}.app-suggestion-form textarea{min-height:220px;margin-top:6px}.app-suggestion-form input,.app-suggestion-form select{margin-top:6px}
                .facil-theme-light .app-topbar,.facil-theme-light .app-dialog,.facil-theme-light .app-modern-tab,.facil-theme-light .app-circle-action,.facil-theme-light .app-zoom-control{box-shadow:0 4px 18px rgba(89,65,35,.07)}
                .app-modern-sidebar.is-collapsed{width:74px!important;min-width:74px!important}.app-modern-sidebar.is-collapsed .app-sidebar-main-item{width:54px!important;margin-left:auto!important;margin-right:auto!important;padding:6px!important;justify-content:center!important}.app-modern-sidebar.is-collapsed .app-sidebar-main-item>div:first-child{justify-content:center!important;gap:0!important}.app-modern-sidebar.is-collapsed .app-menu-icon-box{margin:0!important}.app-modern-sidebar.is-collapsed .app-menu-favorite,.app-modern-sidebar.is-collapsed .app-sidebar-main-item>div:last-child,.app-modern-sidebar.is-collapsed .ml-4.mt-1{display:none!important}.app-modern-sidebar.is-collapsed .app-sidebar-close{margin:auto!important}.app-modern-sidebar.is-collapsed .flex-1{padding-left:4px!important;padding-right:4px!important}
                .app-sidebar-brand{display:flex!important;align-items:center!important;gap:8px!important;white-space:nowrap}.app-sidebar-brand span{font-size:9px!important;letter-spacing:.22em!important;font-weight:900!important;color:var(--app-accent)!important}.app-sidebar-brand strong{font-size:23px!important;line-height:1!important;color:var(--app-text)!important}.app-sidebar-main-item .font-semibold{font-size:12px!important}.app-sidebar-sub-item{font-size:11px!important}
                .app-modern-tab:nth-child(6n+1){--tab-accent:#06b6d4}.app-modern-tab:nth-child(6n+2){--tab-accent:#a855f7}.app-modern-tab:nth-child(6n+3){--tab-accent:#3b82f6}.app-modern-tab:nth-child(6n+4){--tab-accent:#10b981}.app-modern-tab:nth-child(6n+5){--tab-accent:#f97316}.app-modern-tab:nth-child(6n+6){--tab-accent:#ec4899}.app-modern-tab{border-color:color-mix(in srgb,var(--tab-accent,var(--app-accent)) 55%,var(--app-line))!important}.app-modern-tab .app-tab-dot{color:var(--tab-accent,var(--app-accent))!important}.app-modern-tab.is-active{box-shadow:inset 0 3px 0 var(--tab-accent,var(--app-accent))!important;border-color:var(--tab-accent,var(--app-accent))!important}
                .app-dialog,.app-suggestion-popup,.app-config-popup{border-radius:18px!important;overflow:hidden!important}.app-dialog section{border-radius:16px!important}.suggestion-actions button{border-radius:9px!important}.suggestion-actions .is-complement{color:#d97706!important;border-color:#f59e0b!important}
                .app-notification-panel{position:fixed;right:12px;top:48px;width:min(390px,calc(100vw - 24px));max-height:72vh;z-index:9999;border:1px solid var(--app-line);border-radius:16px;background:var(--app-card);color:var(--app-text);box-shadow:0 18px 55px rgba(0,0,0,.35);overflow:hidden}.app-notification-head{padding:12px 14px;display:flex;justify-content:space-between;border-bottom:1px solid var(--app-line)}.app-notification-tabs{display:flex;border-bottom:1px solid var(--app-line)}.app-notification-tabs button{flex:1;padding:9px;font-size:11px;font-weight:900}.app-notification-tabs button.active{background:var(--app-accent);color:white}.app-notification-list{max-height:55vh;overflow:auto}.app-notification-item{display:flex;align-items:center;gap:8px;padding:11px 13px;border-bottom:1px solid var(--app-line)}.app-notification-item b{display:block;font-size:12px}.app-notification-item small{display:block;font-size:10px;color:var(--app-muted);margin-top:3px}
                .facil-theme-acad .app-main-content,.facil-theme-acad main,.facil-theme-acad .app-dialog,.facil-theme-acad .app-suggestion-form,.facil-theme-acad .app-help-search,.facil-theme-acad .app-manual-body,.facil-theme-acad .app-manual-aside,.facil-theme-acad .app-suggestion-table-wrap,.facil-theme-acad .app-config-popup section{background:#062b26!important;color:#ecfffa!important;border-color:#1e6b5c!important}.facil-theme-acad .app-main-content>div,.facil-theme-acad .rounded-xl,.facil-theme-acad .rounded-2xl,.facil-theme-acad .rounded-lg,.facil-theme-acad table,.facil-theme-acad thead,.facil-theme-acad tbody,.facil-theme-acad tr,.facil-theme-acad td,.facil-theme-acad th{border-color:#1e6b5c!important}.facil-theme-acad input,.facil-theme-acad select,.facil-theme-acad textarea{background:#0a302b!important;color:#ecfffa!important;border-color:#287b6c!important}.facil-theme-acad .bg-slate-900,.facil-theme-acad .bg-slate-950,.facil-theme-acad .bg-slate-800,.facil-theme-acad .bg-gray-900,.facil-theme-acad .bg-gray-800{background-color:#06231f!important}.facil-theme-acad .bg-slate-700,.facil-theme-acad .bg-gray-700{background-color:#0d4038!important}.facil-theme-acad .text-slate-400,.facil-theme-acad .text-gray-400,.facil-theme-acad .text-slate-500,.facil-theme-acad .text-gray-500{color:#9bd8cc!important}.facil-theme-acad .manual-item{background:#0b463d!important;border-color:#217d6d!important}.facil-theme-acad .app-suggestion-table thead{background:#07594d!important}.facil-theme-acad .app-suggestion-table tbody tr{background:#0a352f!important}

                @media(max-width:1100px){.app-user-chip div{display:none}.manual-cards{grid-template-columns:repeat(2,minmax(0,1fr))}.app-topbar-actions{overflow-x:auto}.app-zoom-control{display:none}}@media(max-width:760px){.manual-cards,.manual-vitrine{grid-template-columns:1fr}.app-user-chip{display:none}.app-modern-tab{min-width:120px}.app-help-search{flex-direction:column;align-items:stretch}}


                /* v63_340 - refinamentos de barra, menu e central de ajuda */
                .app-topbar{min-height:41px!important;height:41px!important}.app-topbar-tabs{padding-top:3px!important}.app-modern-tab{height:30px!important;min-width:130px!important;border-radius:9px 9px 0 0!important;font-size:11px!important}.app-topbar-actions{padding:3px 8px!important;gap:5px!important;overflow:visible!important}.app-circle-action{width:29px!important;height:29px!important}.app-zoom-control{height:29px!important;border-radius:9px!important}.app-zoom-control button{padding:0 8px!important}.app-zoom-control strong{padding:0 8px!important;font-size:11px!important}.app-user-chip{height:31px!important;min-width:168px!important;padding-right:8px!important;overflow:visible!important}.app-user-chip img,.app-user-chip>span{width:27px!important;height:27px!important}.app-user-chip div{display:flex!important;min-width:120px!important}.app-user-chip b{display:block!important;font-size:10px!important;max-width:150px;overflow:hidden;text-overflow:ellipsis}.app-user-chip small{font-size:8px!important}
                .app-sidebar-compact-header{height:82px;padding:14px 16px;border-bottom:1px solid var(--app-line);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;position:relative;background:color-mix(in srgb,var(--app-panel) 82%,transparent)}.app-sidebar-close{position:absolute;right:12px;top:12px;width:34px;height:34px;border-radius:999px;border:1px solid var(--app-line);display:flex;align-items:center;justify-content:center;color:var(--app-accent);background:var(--app-card)}.app-sidebar-brand{display:flex;align-items:baseline;gap:7px}.app-sidebar-brand span{font-size:11px;font-weight:900;letter-spacing:.18em;color:var(--app-accent)}.app-sidebar-brand strong{font-size:22px;font-weight:900;color:var(--app-text)}
                .app-modern-sidebar{width:272px}.app-modern-sidebar>div.flex-1{padding:10px 9px!important}.app-modern-sidebar .group{border:1px solid var(--app-line);border-radius:10px!important;margin-bottom:4px;background:color-mix(in srgb,var(--app-card) 84%,transparent);color:var(--app-text)!important}.app-modern-sidebar .group:hover{background:color-mix(in srgb,var(--app-accent) 9%,var(--app-card))!important}.app-modern-sidebar .group.bg-blue-600{background:var(--app-accent)!important;color:white!important}.app-menu-icon-box{width:36px;height:36px!important;border-radius:10px!important}.app-menu-number{min-width:37px!important;height:27px!important}.app-submenu-number{min-width:46px;height:23px;border-radius:8px;display:inline-flex;align-items:center;justify-content:center;font-size:9px;font-weight:900;border:1px solid color-mix(in srgb,var(--app-accent) 35%,transparent);background:color-mix(in srgb,var(--app-accent) 8%,var(--app-card));color:var(--app-text)}.app-modern-sidebar .group\/sub{margin:3px 0;padding:7px 8px!important;border:1px solid var(--app-line)!important;border-radius:9px!important;background:var(--app-card)!important;color:var(--app-text)!important}.app-modern-sidebar .group\/sub:hover,.app-modern-sidebar .group\/sub.bg-blue-600\/20{background:color-mix(in srgb,var(--app-accent) 12%,var(--app-card))!important;border-color:var(--app-accent)!important}.app-modern-sidebar [class*="opacity-0"]{opacity:1!important}.app-modern-sidebar .text-yellow-400{color:#f4b400!important}
                .app-help-center{background:var(--app-panel)!important}.app-help-header{padding:12px 18px!important}.app-primary-btn.compact{height:34px}.app-primary-btn.orange{background:#d97706}.app-tab-button.violet.active{background:#7c3aed}.app-manual-body{display:grid;grid-template-columns:minmax(0,1fr) 300px;gap:16px}.app-manual-aside{border:1px solid var(--app-line);border-radius:16px;padding:16px;background:var(--app-card)}.app-manual-aside h3{font-weight:900;display:flex;gap:8px;align-items:center}.app-manual-aside p{font-size:11px;color:var(--app-muted);margin-top:8px}.manual-star{position:absolute;right:12px;top:12px;width:30px;height:30px;border:1px solid var(--app-line);border-radius:999px;display:flex;align-items:center;justify-content:center;background:var(--app-card);color:var(--app-accent)}
                .app-segmented{display:flex;border:1px solid var(--app-line);border-radius:10px;overflow:hidden}.app-segmented button{padding:7px 12px;font-size:11px;font-weight:900;background:var(--app-card)}.app-segmented button.active{background:var(--app-accent);color:white}.app-suggestion-filters{border:1px solid var(--app-line);border-radius:14px;padding:9px;background:var(--app-card)}.app-suggestion-filters input,.app-suggestion-popup input,.app-suggestion-popup select,.app-suggestion-popup textarea,.app-config-popup input,.app-config-popup textarea,.app-action-popup input,.app-action-popup select,.app-action-popup textarea{width:100%;border:1px solid var(--app-line);border-radius:9px;padding:9px 11px;background:var(--app-card);color:var(--app-text)}.app-suggestion-table-wrap{border:1px solid var(--app-line);border-radius:15px;overflow:auto;background:var(--app-card)}.app-suggestion-table{width:100%;min-width:1280px;font-size:11px}.app-suggestion-table th{background:var(--app-accent);color:white;padding:10px;text-align:left;font-weight:900}.app-suggestion-table td{padding:9px;border-top:1px solid var(--app-line)}.suggestion-status,.suggestion-type{display:inline-flex;padding:5px 8px;border-radius:999px;background:#c026d3;color:white;font-weight:900}.suggestion-type{background:#7c3aed;border-radius:8px}.suggestion-actions{display:flex;gap:5px}.suggestion-actions button{width:29px;height:29px;border-radius:8px;border:1px solid var(--app-accent);color:var(--app-accent);display:flex;align-items:center;justify-content:center}.app-suggestion-popup{width:min(920px,96vw)}.app-config-popup{width:min(1380px,97vw);height:min(900px,94vh);display:flex;flex-direction:column}.app-config-popup>div.p-5{flex:1}.app-config-popup section{border:1px solid var(--app-line);border-radius:15px;padding:15px;background:var(--app-card)}.app-config-popup h4{font-weight:900;margin-bottom:12px}.app-config-popup label,.app-suggestion-popup label,.app-action-popup label{font-size:11px;font-weight:900;display:block}.app-config-popup label input,.app-action-popup label input,.app-action-popup label select,.app-action-popup label textarea,.app-suggestion-popup label input,.app-suggestion-popup label select,.app-suggestion-popup label textarea{margin-top:5px}.app-config-popup textarea{min-height:90px}.rich-editor-toolbar{border:1px solid var(--app-line);border-bottom:0;border-radius:9px 9px 0 0;padding:7px;display:flex;gap:5px}.rich-editor-toolbar button{border:1px solid var(--app-line);border-radius:5px;padding:3px 8px;font-size:10px}.app-suggestion-popup textarea{min-height:210px;border-radius:0 0 9px 9px}.app-action-popup{width:min(1150px,96vw);max-height:92vh;overflow:auto}.description-preview{border:1px solid var(--app-line);border-radius:12px;padding:14px;background:var(--app-card)}.app-logs-popup{width:min(1030px,96vw);height:min(760px,92vh);display:flex;flex-direction:column}.app-logs-popup>div.p-5{flex:1}.log-card{border:1px solid var(--app-line);border-radius:13px;padding:13px;background:var(--app-card);display:flex;flex-direction:column;gap:5px}.log-card small,.log-card span{font-size:10px;color:var(--app-muted)}.log-card b{font-size:13px}.log-card p{font-size:11px}
                @media(max-width:1050px){.app-user-chip{min-width:145px!important}.app-user-chip div{display:flex!important}.app-manual-body{grid-template-columns:1fr}.app-manual-aside{display:none}}


                /* v65_342 - fidelidade visual dos temas, menu e abas */
                .app-shell-acad{background:#020d18!important;color:#ecfffa!important}.facil-theme-acad body,.facil-theme-acad #root{background:#020d18!important}.facil-theme-acad .app-modern-sidebar{background:#073a32!important;border-color:#0f766e!important}.facil-theme-acad .app-topbar{background:#06352f!important;border-color:#0f766e!important}.facil-theme-acad .app-modern-tab{background:#07443a!important;border-color:#0e8f7d!important;color:#d7fff6!important}.facil-theme-acad .app-modern-tab.is-active{background:#0a5144!important;box-shadow:inset 0 3px 0 #00d6ae!important}.facil-theme-acad main,.facil-theme-acad .app-main-content{background:#020617!important}.facil-theme-acad .bg-white{background-color:#0b2f2a!important}.facil-theme-acad .bg-slate-50,.facil-theme-acad .bg-gray-50{background-color:#082720!important}.facil-theme-acad .text-gray-800,.facil-theme-acad .text-slate-900,.facil-theme-acad .text-gray-700{color:#ecfffa!important}.facil-theme-acad .border-gray-200,.facil-theme-acad .border-slate-200,.facil-theme-acad .border-gray-300{border-color:#1e6b5c!important}
                .app-sidebar-compact-header{height:86px!important;align-items:flex-start!important;padding:17px 18px!important}.app-sidebar-brand{display:flex!important;align-items:center!important;gap:10px!important}.app-sidebar-brand>div{display:flex;align-items:baseline;gap:7px}.app-sidebar-brand-icon{color:var(--app-accent);flex:none}.app-sidebar-brand span{font-size:9px!important}.app-sidebar-brand strong{font-size:23px!important}.app-sidebar-close{right:14px!important;top:14px!important}
                .app-modern-sidebar{width:302px!important}.app-modern-sidebar>div.flex-1{padding:12px 10px!important}.app-sidebar-main-item{height:58px!important;padding:8px 10px!important;margin-bottom:5px!important;border-radius:11px!important}.app-menu-icon-box{width:40px!important;height:40px!important;min-width:40px!important;border-color:var(--menu-icon-color)!important;color:var(--menu-icon-color)!important;background:color-mix(in srgb,var(--menu-icon-color) 11%,transparent)!important}.app-menu-icon-box svg{color:var(--menu-icon-color)!important}.app-menu-number{min-width:42px!important;width:42px!important;height:28px!important;background:#eaf9f5!important;color:#174e4a!important;border-color:#9fd8cf!important}.facil-theme-dark .app-menu-number,.facil-theme-acad .app-menu-number{background:#093d37!important;color:#f0fffb!important;border-color:#1d7669!important}.app-sidebar-main-item>div:first-child{min-width:0}.app-sidebar-main-item .font-semibold{font-size:13px!important;overflow:hidden;text-overflow:ellipsis}.app-menu-favorite,.app-submenu-favorite{width:34px!important;height:34px!important;min-width:34px!important;max-width:34px!important;padding:0!important;margin:0!important;display:flex!important;align-items:center!important;justify-content:center!important;border-radius:9px!important;border:1px solid #f4b400!important;background:#fff8cf!important;color:#f4b400!important;opacity:1!important;overflow:hidden!important}.app-menu-favorite svg{width:18px!important;height:18px!important}.app-submenu-favorite{width:28px!important;height:28px!important;min-width:28px!important}.app-submenu-favorite svg{width:14px!important;height:14px!important}.facil-theme-acad .app-menu-favorite,.facil-theme-acad .app-submenu-favorite,.facil-theme-dark .app-menu-favorite,.facil-theme-dark .app-submenu-favorite{background:#fff6bd!important;border-color:#ffe56b!important;color:#eab308!important}.app-sidebar-sub-item{min-height:42px!important;padding:7px 8px!important}.app-submenu-number{min-width:49px!important;color:var(--app-text)!important}.facil-theme-light .app-sidebar-sub-item{color:#557078!important;background:#fffaf2!important}.facil-theme-light .app-sidebar-sub-item:hover,.facil-theme-light .app-sidebar-sub-item.bg-blue-600\/20{background:#dfe9fa!important;color:#365a77!important}.app-modern-sidebar .ml-4.mt-1{margin-left:17px!important;padding-left:8px!important;border-color:color-mix(in srgb,var(--app-accent) 65%,transparent)!important}
                .facil-theme-acad .app-main-shell,.facil-theme-acad .app-main-shell>div,.facil-theme-acad .app-main-content,.facil-theme-acad main{background:#020f1c!important;color:#ecfffa!important}
                .facil-theme-acad main [class*="bg-slate-9"],.facil-theme-acad main [class*="bg-slate-8"],.facil-theme-acad main [class*="bg-gray-9"],.facil-theme-acad main [class*="bg-gray-8"]{background-color:#082b27!important}
                .facil-theme-acad main [class*="bg-slate-7"],.facil-theme-acad main [class*="bg-gray-7"]{background-color:#0b3a33!important}
                .facil-theme-acad main [class*="border-slate"],.facil-theme-acad main [class*="border-gray"]{border-color:#1c6f61!important}
                .facil-theme-acad main [class*="text-slate-"],.facil-theme-acad main [class*="text-gray-"]{color:#c8f3e9!important}
                .facil-theme-acad main table tbody tr{background:#0b302b!important}.facil-theme-acad main table tbody tr:nth-child(even){background:#0d3932!important}.facil-theme-acad main table thead{background:#073f37!important}
                .app-manual-reader{width:min(1500px,98vw)!important;height:min(900px,95vh)!important;max-width:none!important;display:flex!important;flex-direction:column!important;overflow:hidden!important}.app-manual-reader-grid{display:grid;grid-template-columns:1fr;height:100%;min-height:0}.app-manual-reader-grid.with-notes{grid-template-columns:minmax(0,1fr) 360px}.app-manual-document{min-height:0;background:#fff;overflow:hidden}.app-manual-document iframe{width:100%;height:100%;border:0;background:#fff}.app-manual-notes{padding:16px;overflow:auto;border-left:1px solid var(--app-line);background:var(--app-panel)}.app-manual-notes h4{font-size:16px;font-weight:900}.app-manual-notes p{font-size:11px;color:var(--app-muted);margin:5px 0 14px}.app-manual-notes textarea{width:100%;min-height:260px}.app-manual-note-tip{margin-top:14px;padding:12px;border:1px dashed var(--app-line);border-radius:12px;font-size:11px;color:var(--app-muted)}.app-complement-popup{width:min(760px,96vw)!important}.app-danger-btn{border:1px solid #fca5a5;color:#dc2626;background:#fff1f2;border-radius:10px;padding:10px 14px;font-weight:800}.app-dialog-header small{display:block;color:var(--app-accent);font-weight:800;text-transform:uppercase}

                .app-topbar{height:43px!important;min-height:43px!important;max-height:43px!important;display:grid!important;grid-template-columns:minmax(0,1fr) auto!important;overflow:hidden!important}.app-topbar-tabs{height:43px!important;padding:4px 4px 0!important;overflow-x:auto!important;overflow-y:hidden!important;white-space:nowrap!important;align-items:flex-end!important}.app-modern-tab{height:35px!important;min-width:145px!important;max-width:210px!important;flex:0 0 auto!important;padding:0 10px!important}.app-topbar-actions{height:43px!important;max-width:650px!important;padding:5px 8px!important;flex-wrap:nowrap!important;background:inherit!important;position:relative!important;z-index:3!important}.app-circle-action{width:31px!important;height:31px!important;min-width:31px!important}.app-zoom-control{height:31px!important;min-width:145px!important}.app-user-chip{height:33px!important;min-width:190px!important;max-width:235px!important;padding-right:7px!important}.app-user-chip div{display:flex!important;min-width:0!important;flex:1}.app-user-chip b{display:block!important;max-width:180px!important}.app-user-chip small{display:block!important}.app-topbar-tabs::-webkit-scrollbar{height:5px}.app-topbar-tabs::-webkit-scrollbar-thumb{background:var(--app-line);border-radius:999px}
                .facil-theme-light .app-modern-sidebar{background:#fffaf2!important}.facil-theme-light .app-sidebar-main-item{background:#fffdf9!important;border-color:#e6d3b7!important}.facil-theme-light .app-sidebar-main-item.bg-blue-600{background:#087f86!important}.facil-theme-light .app-menu-icon-box{background:#eef9f7!important}.facil-theme-light .app-sidebar-brand strong{color:#123438!important}
                @media(max-width:1250px){.app-modern-sidebar{width:285px!important}.app-user-chip{min-width:165px!important}.app-modern-tab{min-width:130px!important}.app-topbar-actions{max-width:570px!important}}

                /* v68_345 - colapso real, titulo do modulo, manuais e tema Acad integral */
                .app-modern-sidebar.is-collapsed{width:74px!important;min-width:74px!important;max-width:74px!important}
                .app-modern-sidebar.is-collapsed .app-sidebar-compact-header{height:72px!important;padding:0!important;align-items:center!important;justify-content:center!important}
                .app-modern-sidebar.is-collapsed .app-sidebar-close{position:static!important;margin:0!important}
                .app-modern-sidebar.is-collapsed>div.flex-1{padding:10px 7px!important}
                .app-modern-sidebar.is-collapsed .app-sidebar-main-item{width:56px!important;height:54px!important;padding:7px!important;margin:5px auto!important;justify-content:center!important}
                .app-modern-sidebar.is-collapsed .app-sidebar-main-item>div:first-child{justify-content:center!important;flex:none!important}
                .app-modern-sidebar.is-collapsed .app-menu-icon-box{margin:0!important}
                .app-modern-sidebar.is-collapsed .app-menu-number,.app-modern-sidebar.is-collapsed .app-menu-favorite,.app-modern-sidebar.is-collapsed .app-submenu-favorite,.app-modern-sidebar.is-collapsed .app-sidebar-sub-item,.app-modern-sidebar.is-collapsed .app-sidebar-main-item>div:last-child,.app-modern-sidebar.is-collapsed .ml-4.mt-1,.app-modern-sidebar.is-collapsed .text-\[10px\]{display:none!important}
                .app-sidebar-brand{align-items:center!important;gap:11px!important}.app-sidebar-brand-copy{display:flex!important;flex-direction:column!important;align-items:flex-start!important;line-height:1!important}.app-sidebar-brand-copy>span{font-size:9px!important;letter-spacing:.22em!important;margin-bottom:5px!important}.app-sidebar-brand-copy>div{display:flex!important;align-items:center!important;gap:7px!important}.app-sidebar-brand-copy strong{font-size:22px!important}.app-sidebar-people-icon{color:#f59e0b!important;filter:drop-shadow(0 0 5px rgba(245,158,11,.35))}
                .manual-item p,.app-help-center article p{display:-webkit-box!important;-webkit-line-clamp:3!important;-webkit-box-orient:vertical!important;overflow:hidden!important;max-height:4.8em!important}
                .app-manual-reader{min-width:640px!important;min-height:420px!important;max-width:none!important;max-height:none!important;border-radius:18px!important;box-shadow:0 25px 70px rgba(0,0,0,.38)!important}.app-manual-drag-header{cursor:move!important;user-select:none!important}.app-manual-reader-actions{flex-wrap:wrap!important;justify-content:flex-end!important}.app-manual-reader-grid{min-height:0!important;flex:1!important}.app-manual-document{height:100%!important}.app-manual-notes textarea{resize:vertical!important;min-height:250px!important}
                .facil-theme-acad,.facil-theme-acad body,.facil-theme-acad #root,.facil-theme-acad .app-main-shell,.facil-theme-acad .app-main-shell>div,.facil-theme-acad main,.facil-theme-acad .app-main-content{background:#031f1b!important;background-color:#031f1b!important;color:#ecfffa!important}
                .facil-theme-acad main>div,.facil-theme-acad .app-main-content>div{background-color:transparent!important}
                .facil-theme-acad main [class*="bg-slate-950"],.facil-theme-acad main [class*="bg-slate-900"],.facil-theme-acad main [class*="bg-gray-950"],.facil-theme-acad main [class*="bg-gray-900"]{background-color:#052b26!important}
                .facil-theme-acad main [class*="bg-slate-800"],.facil-theme-acad main [class*="bg-gray-800"]{background-color:#0a3a33!important}
                .facil-theme-acad main [class*="bg-slate-700"],.facil-theme-acad main [class*="bg-gray-700"]{background-color:#0d493f!important}
                .facil-theme-acad main .bg-white,.facil-theme-acad .app-dialog,.facil-theme-acad .app-help-center,.facil-theme-acad .app-manual-aside,.facil-theme-acad .app-manual-notes{background-color:#07352f!important;color:#ecfffa!important}
                .facil-theme-acad main table tbody tr{background:#08352f!important}.facil-theme-acad main table tbody tr:nth-child(even){background:#0a4038!important}.facil-theme-acad main table thead{background:#075b4d!important}
                .facil-theme-acad .app-manual-document{background:#fff!important;color:#172033!important}
                @media(max-width:900px){.app-manual-reader{min-width:0!important;width:98vw!important}.app-manual-reader-grid.with-notes{grid-template-columns:1fr!important}.app-manual-notes{display:none!important}}

                /* v69_346 - comportamento do menu e tema Acad espelhados do CTB */
                .app-modern-sidebar.is-collapsed {
                    width:64px !important;
                    min-width:64px !important;
                    max-width:64px !important;
                }
                .app-modern-sidebar.is-collapsed .app-sidebar-compact-header {
                    width:64px !important;
                    min-width:64px !important;
                    padding:0 !important;
                    height:76px !important;
                    align-items:center !important;
                    justify-content:center !important;
                }
                .app-modern-sidebar.is-collapsed .app-sidebar-close {
                    position:static !important;
                    margin:0 auto !important;
                    width:40px !important;
                    height:40px !important;
                    border-radius:12px !important;
                }
                .app-modern-sidebar.is-collapsed > .flex-1 {
                    padding-left:7px !important;
                    padding-right:7px !important;
                }
                .app-modern-sidebar.is-collapsed .app-sidebar-main-item {
                    width:50px !important;
                    height:50px !important;
                    min-height:50px !important;
                    padding:5px !important;
                    margin-left:auto !important;
                    margin-right:auto !important;
                    justify-content:center !important;
                }
                .app-modern-sidebar.is-collapsed .app-sidebar-main-item > div:first-child {
                    justify-content:center !important;
                    gap:0 !important;
                }
                .app-modern-sidebar.is-collapsed .app-menu-icon-box {
                    width:38px !important;
                    min-width:38px !important;
                    height:38px !important;
                    margin:0 !important;
                }
                .app-modern-sidebar.is-collapsed .app-menu-number,
                .app-modern-sidebar.is-collapsed .app-menu-favorite,
                .app-modern-sidebar.is-collapsed .app-submenu-favorite,
                .app-modern-sidebar.is-collapsed .app-sidebar-sub-item,
                .app-modern-sidebar.is-collapsed .app-submenu-number,
                .app-modern-sidebar.is-collapsed .app-sidebar-main-item span.font-semibold,
                .app-modern-sidebar.is-collapsed .app-sidebar-main-item > div:last-child,
                .app-modern-sidebar.is-collapsed .ml-4.mt-1,
                .app-modern-sidebar.is-collapsed .text-\[10px\] {
                    display:none !important;
                }
                .facil-theme-acad,
                .facil-theme-acad.app-shell-acad,
                .facil-theme-acad .app-main-shell,
                .facil-theme-acad main,
                .facil-theme-acad .app-main-content {
                    background:#071c1a !important;
                    background-color:#071c1a !important;
                    color:#ecfffa !important;
                }
                .facil-theme-acad .app-main-content > div,
                .facil-theme-acad main > div {
                    border-color:rgba(45,212,191,.26);
                }
                .facil-theme-acad [class*="bg-[#020617]"],
                .facil-theme-acad [class*="bg-[#020f1c]"],
                .facil-theme-acad [class*="bg-[#0F172A]"],
                .facil-theme-acad [class*="bg-[#0f172a]"],
                .facil-theme-acad [class*="bg-slate-950"],
                .facil-theme-acad [class*="bg-slate-900"] {
                    background-color:#071f1c !important;
                }
                .facil-theme-acad [class*="bg-[#1E293B]"],
                .facil-theme-acad [class*="bg-[#1e293b]"],
                .facil-theme-acad [class*="bg-slate-800"] {
                    background-color:#0b2f2a !important;
                }
                .facil-theme-acad [class*="bg-slate-700"] {
                    background-color:#0f3d35 !important;
                }
                .facil-theme-acad .app-help-center,
                .facil-theme-acad .app-dialog,
                .facil-theme-acad section,
                .facil-theme-acad article {
                    border-color:rgba(45,212,191,.28);
                }

                /* v70_347 - ajustes responsivos, tema claro e estados do menu */
                .manual-reader-header{height:86px!important;min-height:86px!important;max-height:86px!important;overflow:hidden!important;align-items:center!important}.manual-reader-title{max-height:62px!important;overflow:hidden!important}.manual-reader-subtitle{white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;max-width:100%!important}.manual-reader-header>button,.manual-reader-header>a{flex:none!important;white-space:nowrap!important}
                @media(max-width:1050px){.manual-reader-header{height:78px!important;min-height:78px!important;max-height:78px!important;padding:8px 10px!important;gap:6px!important}.manual-reader-subtitle{display:none!important}.manual-reader-header>button,.manual-reader-header>a{padding:6px 8px!important;font-size:10px!important}.manual-reader-header svg{width:17px;height:17px}}
                .central-ajuda-light{background:#fffdf9!important;color:#102a2b!important;border-color:#dfcdb4!important}.central-ajuda-light>div:first-child{background:#fffaf2!important;border-color:#dfcdb4!important}.central-ajuda-light .help-manual-card{background-image:none!important;background-color:#f3fbfb!important;border-color:#9ddfd9!important;color:#17383b!important}.central-ajuda-light .help-manual-card:nth-child(3n+2){background-color:#f2f5ff!important;border-color:#b7c8ff!important}.central-ajuda-light .help-manual-card:nth-child(3n){background-color:#fbf5ff!important;border-color:#d7baf4!important}.central-ajuda-light .help-manual-card h4,.central-ajuda-light .help-manual-card [class*="text-white"]{color:#17383b!important;-webkit-text-fill-color:#17383b!important}.central-ajuda-light .help-manual-card [class*="text-slate-500"]{color:#64748b!important}.central-ajuda-light .help-manual-card button.bg-teal-700{color:#fff!important;-webkit-text-fill-color:#fff!important}
                .central-ajuda-light .help-suggestions-panel{color:#102a2b!important}.central-ajuda-light .help-suggestions-panel table{background:#fff!important;color:#102a2b!important}.central-ajuda-light .help-suggestions-panel thead{background:#0f7c72!important;color:#fff!important}.central-ajuda-light .help-suggestions-panel tbody tr{background:#fffdf9!important;color:#102a2b!important;border-color:#e8d7bf!important}.central-ajuda-light .help-suggestions-panel tbody tr:nth-child(even){background:#f8f4ed!important}.central-ajuda-light .help-suggestions-panel tbody tr [class*="text-white"]{color:#102a2b!important;-webkit-text-fill-color:#102a2b!important}.central-ajuda-light .help-suggestions-panel tbody tr .bg-violet-700,.central-ajuda-light .help-suggestions-panel tbody tr .bg-violet-600,.central-ajuda-light .help-suggestions-panel tbody tr .bg-fuchsia-600{color:#fff!important;-webkit-text-fill-color:#fff!important}.central-ajuda-light .help-suggestions-panel input,.central-ajuda-light .help-suggestions-panel select{background:#fff!important;color:#102a2b!important;border-color:#dfcdb4!important}
                .facil-theme-light .app-sidebar-main-item.is-main-active{background:#0f8f92!important;color:#fff!important;border-color:#0f8f92!important}.facil-theme-light .app-sidebar-main-item.is-main-active .font-semibold,.facil-theme-light .app-sidebar-main-item.is-main-active>div:last-child{color:#fff!important;-webkit-text-fill-color:#fff!important}.facil-theme-light .app-sidebar-main-item:not(.is-main-active) .font-semibold{color:#31545a!important;-webkit-text-fill-color:#31545a!important}.facil-theme-light .app-sidebar-sub-item.is-sub-active{background:#d8e9ff!important;border-color:#8ab5ed!important;color:#173b5c!important;font-weight:900!important;box-shadow:inset 3px 0 0 #3178c6!important}.facil-theme-light .app-sidebar-sub-item.is-sub-active .app-submenu-number{background:#c8def8!important;border-color:#79a9df!important;color:#173b5c!important}.facil-theme-light .app-sidebar-sub-item:not(.is-sub-active){color:#526f75!important}
                .app-notification-panel{font-family:inherit!important}.app-notification-tabs button.active{background:var(--app-accent)!important;color:#fff!important}.facil-theme-light .app-notification-panel{background:#fffdf9!important;color:#102a2b!important;border-color:#dfcdb4!important}.facil-theme-light .app-notification-item:hover{background:#f7f0e7!important}


                /* v73_350 - menu lateral recolhido: somente icones principais */
                .app-modern-sidebar.is-collapsed > .flex-1 > div:first-child {
                    display:none !important;
                }
                .app-modern-sidebar.is-collapsed > .flex-1 {
                    padding:8px 5px !important;
                    overflow-x:hidden !important;
                }
                .app-modern-sidebar.is-collapsed .app-sidebar-main-item {
                    width:52px !important;
                    min-width:52px !important;
                    max-width:52px !important;
                    height:48px !important;
                    min-height:48px !important;
                    margin:3px auto !important;
                    padding:0 !important;
                    border:0 !important;
                    border-radius:0 !important;
                    background:transparent !important;
                    box-shadow:none !important;
                    outline:none !important;
                    overflow:visible !important;
                }
                .app-modern-sidebar.is-collapsed .app-sidebar-main-item:hover,
                .app-modern-sidebar.is-collapsed .app-sidebar-main-item.is-main-active,
                .app-modern-sidebar.is-collapsed .app-sidebar-main-item.bg-blue-600 {
                    background:transparent !important;
                    border:0 !important;
                    box-shadow:none !important;
                }
                .app-modern-sidebar.is-collapsed .app-sidebar-main-item > div:first-child {
                    width:52px !important;
                    min-width:52px !important;
                    height:48px !important;
                    flex:0 0 52px !important;
                    display:flex !important;
                    align-items:center !important;
                    justify-content:center !important;
                    gap:0 !important;
                    padding:0 !important;
                    margin:0 !important;
                }
                .app-modern-sidebar.is-collapsed .app-menu-icon-box {
                    width:auto !important;
                    min-width:0 !important;
                    height:auto !important;
                    padding:0 !important;
                    margin:0 !important;
                    border:0 !important;
                    border-radius:0 !important;
                    background:transparent !important;
                    box-shadow:none !important;
                }
                .app-modern-sidebar.is-collapsed .app-menu-icon-box svg {
                    width:22px !important;
                    height:22px !important;
                }
                .app-modern-sidebar.is-collapsed .app-menu-number,
                .app-modern-sidebar.is-collapsed .app-menu-favorite,
                .app-modern-sidebar.is-collapsed .app-submenu-favorite,
                .app-modern-sidebar.is-collapsed .app-sidebar-sub-item,
                .app-modern-sidebar.is-collapsed .app-submenu-number,
                .app-modern-sidebar.is-collapsed .app-submenu-notification,
                .app-modern-sidebar.is-collapsed .app-submenu-notification-secondary,
                .app-modern-sidebar.is-collapsed .ml-4.mt-1,
                .app-modern-sidebar.is-collapsed .app-sidebar-main-item > div:not(:first-child),
                .app-modern-sidebar.is-collapsed .app-sidebar-main-item span:not(.app-menu-icon-box) {
                    display:none !important;
                }
                .app-modern-sidebar.is-collapsed .app-sidebar-compact-header {
                    border-bottom:0 !important;
                }

                .app-sidebar-version{
                    margin-left:6px;
                    font-size:9px;
                    line-height:1;
                    font-weight:600;
                    opacity:.42;
                    letter-spacing:.01em;
                    white-space:nowrap;
                    align-self:flex-end;
                    padding-bottom:2px;
                }

                    @keyframes coinBounce { 0%, 100% { transform: translateY(0) scale(1); filter: drop-shadow(0 0 5px rgba(16, 185, 129, 0.5)); } 50% { transform: translateY(-8px) scale(1.1); filter: drop-shadow(0 0 15px rgba(16, 185, 129, 0.9)); } }
                    .animate-coin { animation: coinBounce 2s infinite ease-in-out; }
                `}</style>

                {/* HEADER COMPACTO E FLUIDO */}
                <div className={`py-2 px-5 rounded-xl shadow-md border relative overflow-hidden shrink-0 ${isDarkMode ? 'bg-gradient-to-r from-slate-900 to-slate-800 border-slate-700' : 'bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500 border-transparent text-white'}`}>
                    <div className="absolute right-0 top-0 h-full w-1/3 bg-white/10 skew-x-12 pointer-events-none"></div>
                    {/* CORREÇÃO: Adicionado w-full na div principal e alinhamentos explícitos nos filhos */}
                    <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-4 w-full">
                            {/* Lado Esquerdo (Ícone e Textos forçados em linha) */}
                            <div className="flex flex-row items-center justify-center md:justify-start gap-3 w-full md:w-auto">
                                <div className={`p-2.5 md:p-3 rounded-xl shadow-inner shrink-0 ${isDarkMode ? 'bg-slate-800' : 'bg-white/20'}`}>
                                    <Coins size={24} className={isDarkMode ? 'text-blue-400' : 'text-white'} />
                                </div>
                                <div className="flex flex-col text-left">
                                    <h2 className={`text-lg md:text-xl font-black leading-tight ${isDarkMode ? 'text-white' : 'text-white drop-shadow-sm'}`}>
                                        {currencyName}
                                    </h2>
                                    <p className={`text-[10px] md:text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-white/90 drop-shadow-sm'}`}>
                                        Moeda Virtual de Engajamento e Recompensa
                                    </p>
                                </div>
                            </div>

                            {/* Lado Direito (Informações e Botões) */}
                            <div className="flex flex-col sm:flex-row items-center justify-center md:justify-end gap-3 w-full md:w-auto shrink-0">
                                
                                {/* Data de Fechamento Movida para a Direita */}
                                {cutoffDateDisplay && (
                                    <div className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[10px] md:text-xs font-bold shadow-sm border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-yellow-400' : 'bg-black/10 border-white/20 text-yellow-200 drop-shadow-md'}`}>
                                        <Clock size={14} className="mb-0.5" />
                                        Fechamento do Período: {cutoffDateDisplay}
                                    </div>
                                )}

                                {/* Botões de Navegação Principais */}
                                <div className="flex flex-row items-center gap-2 w-full sm:w-auto">
                                    {isIdeiasEnabled && (
                                        <button onClick={() => setActiveTab('ideias')} className={`px-4 py-2.5 rounded-lg font-bold text-[10px] md:text-xs uppercase transition-all flex-1 sm:flex-none text-center flex items-center justify-center gap-1.5 ${activeTab === 'ideias' ? 'bg-white text-yellow-600 shadow-sm' : 'bg-black/20 text-white hover:bg-black/30'}`}>
                                            <span className="text-base leading-none drop-shadow">💡</span> Ideias
                                        </button>
                                    )}
                                    {isAcadNewsEnabled && (
                                        <button onClick={() => setActiveTab('acadnews')} className={`px-4 py-2.5 rounded-lg font-bold text-[10px] md:text-xs uppercase transition-all flex-1 sm:flex-none text-center flex items-center justify-center gap-1.5 ${activeTab === 'acadnews' ? 'bg-white text-orange-600 shadow-sm' : 'bg-black/20 text-white hover:bg-black/30'}`}>
                                            <div className="relative">
                                                <Globe size={14}/>
                                                {hasNewItems && <span className="absolute -top-1.5 -right-2 flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 border border-white"></span></span>}
                                            </div>
                                            AcadNews {hasNewItems && <span className="animate-bounce">🎉</span>}
                                        </button>
                                    )}
                                    <button onClick={() => setActiveTab('minha_carteira')} className={`px-4 py-2.5 rounded-lg font-bold text-[10px] md:text-xs uppercase transition-all flex-1 sm:flex-none text-center flex items-center justify-center gap-1.5 ${activeTab === 'minha_carteira' ? 'bg-white text-orange-600 shadow-sm' : 'bg-black/20 text-white hover:bg-black/30'}`}>
                                        <Coins size={14}/> Minha Carteira
                                    </button>
                                    {isAdmin && (
                                        <button onClick={() => setActiveTab('admin')} className={`px-4 py-2.5 rounded-lg font-bold text-[10px] md:text-xs uppercase transition-all flex-1 sm:flex-none text-center flex items-center justify-center gap-1.5 ${activeTab === 'admin' ? 'bg-white text-orange-600 shadow-sm' : 'bg-black/20 text-white hover:bg-black/30'}`}>
                                            <Settings size={14}/> Administração
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
                    {/* TELA DE IDEIAS */}
                    {activeTab === 'ideias' && (
                        <div className="space-y-4 animate-fade-in">
                            {/* CABEÇALHO MOTIVACIONAL */}
                            <div className={`p-6 rounded-xl shadow-md border relative overflow-hidden ${isDarkMode ? 'bg-gradient-to-r from-slate-900 to-slate-800 border-slate-700' : 'bg-gradient-to-r from-indigo-500 to-blue-600 border-transparent text-white'}`}>
                                <div className="absolute right-0 top-0 h-full w-1/3 bg-white/10 skew-x-12 pointer-events-none"></div>
                                <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-4">
                                    <div>
                                        <h3 className="font-black text-2xl flex items-center gap-2 mb-1"><span className="text-3xl">💡</span> Central de Inovação</h3>
                                        <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-indigo-50 drop-shadow-sm'}`}>Sua voz faz a diferença! Compartilhe sugestões para melhorar nossos processos, ambiente e resultados. As melhores ideias serão recompensadas!</p>
                                    </div>
                                    <Button onClick={() => { setIdeaForm({ id: null, title: '', description: '', defense: '', visibility: 'public' }); setIsIdeaModalOpen(true); }} className="bg-white text-indigo-600 hover:bg-indigo-50 shadow-lg border-0 whitespace-nowrap font-black px-6 py-3 h-auto text-sm" icon={Plus}>Enviar Nova Ideia</Button>
                                </div>
                            </div>

                            {/* CONTROLES E LISTAGEM */}
                            <div className="flex gap-2 border-b pb-2 dark:border-slate-700 overflow-x-auto custom-scrollbar">
                                <button onClick={() => setIdeaFilterMode('minhas')} className={`px-4 py-2 text-xs font-bold rounded-t-lg transition-colors whitespace-nowrap ${ideaFilterMode === 'minhas' ? 'bg-indigo-100 text-indigo-800 border-b-2 border-indigo-500 dark:bg-indigo-900/30 dark:text-indigo-400' : 'text-gray-500 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-800'}`}>Minhas Ideias</button>
                                <button onClick={() => setIdeaFilterMode('publicas')} className={`px-4 py-2 text-xs font-bold rounded-t-lg transition-colors whitespace-nowrap ${ideaFilterMode === 'publicas' ? 'bg-indigo-100 text-indigo-800 border-b-2 border-indigo-500 dark:bg-indigo-900/30 dark:text-indigo-400' : 'text-gray-500 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-800'}`}>Ideias Públicas</button>
                                {(isAdmin || myCollabRecord?.personType === 'Gestão') && (
                                    <div className="flex items-center ml-auto gap-2">
                                        <button onClick={() => setIdeaFilterMode('avaliar')} className={`px-4 py-2 text-xs font-bold rounded-t-lg transition-colors whitespace-nowrap flex items-center gap-1 ${ideaFilterMode === 'avaliar' ? 'bg-orange-100 text-orange-800 border-b-2 border-orange-500 dark:bg-orange-900/30 dark:text-orange-400' : 'text-gray-500 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-800'}`}><CheckCircle size={14}/> Para Avaliar</button>
                                        
                                        {ideaFilterMode === 'avaliar' && (
                                            <button onClick={() => setShowArchivedIdeas(!showArchivedIdeas)} className={`px-3 py-1.5 text-[10px] font-bold rounded-lg transition-colors shadow-sm border ${showArchivedIdeas ? 'bg-slate-700 text-white border-slate-700' : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-300'}`}>
                                                {showArchivedIdeas ? 'Ocultar Arquivadas' : '🗃️ Ver Arquivadas'}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {(() => {
                                    let displayIdeas = [];
                                    if (ideaFilterMode === 'minhas') {
                                        displayIdeas = ideasDB.filter(i => i.collabId === loggedCollabId).sort((a,b) => new Date(b.date) - new Date(a.date));
                                    } else if (ideaFilterMode === 'publicas') {
                                        displayIdeas = ideasDB.filter(i => i.visibility === 'public').sort((a,b) => new Date(b.date) - new Date(a.date));
                                    } else if (ideaFilterMode === 'avaliar') {
                                        // Regra de Roteamento para Gestores
                                        const routing = ruleIdeias.ideasRouting || 'Todos os gestores';
                                        displayIdeas = ideasDB.filter(i => {
                                            // LÓGICA DE ARQUIVAMENTO
                                            const isFinished = i.status === 'Aprovada' || i.status === 'Não aprovada';
                                            if (showArchivedIdeas ? !isFinished : isFinished) return false;
                                            
                                            // Se foi compartilhada comigo, eu vejo
                                            if (i.sharedWith?.includes(loggedCollabId)) return true;

                                            if (routing === 'Todos os gestores') return true;
                                            if (routing === 'Somente Diretoria' && (isAdmin || myCollabRecord?.departmentName?.toLowerCase().includes('diretoria'))) return true;
                                            if (routing === 'Somente Gestores do Departamento' && i.collabDeptId === myCollabRecord?.departmentId) return true;
                                            if (routing === 'Personalizado' && ruleIdeias.customEvaluators?.includes(loggedCollabId)) return true;
                                            
                                            return isAdmin; // Admin sempre vê como fallback
                                        }).sort((a,b) => new Date(b.date) - new Date(a.date));
                                    }

                                    if (displayIdeas.length === 0) return <div className="col-span-full text-center p-10 opacity-50 italic">Nenhuma ideia encontrada nesta categoria.</div>;

                                    return displayIdeas.map(idea => (
                                        <div key={idea.id} className={`p-4 rounded-xl border shadow-sm flex flex-col relative transition-transform hover:scale-[1.02] ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                            <div className="flex justify-between items-start mb-2">
                                                <span className={`px-2 py-0.5 text-[9px] font-bold uppercase rounded ${idea.status === 'Aprovada' ? 'bg-green-100 text-green-700' : idea.status === 'Pré-aprovada' ? 'bg-blue-100 text-blue-700' : idea.status === 'Não aprovada' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{idea.status || 'Pendente'}</span>
                                                <span className="text-[10px] opacity-60">{new Date(idea.date).toLocaleDateString('pt-BR')}</span>
                                            </div>
                                            <h4 className="font-bold text-sm mb-1">{idea.title}</h4>
                                            <p className="text-xs opacity-80 mb-3 line-clamp-3 flex-1">{idea.description}</p>
                                            <div className="text-[10px] opacity-60 mb-3 border-t pt-2 dark:border-slate-700">Por: <b>{idea.collabName}</b> {idea.visibility === 'private' && <span className="ml-2 text-red-500">🔒 Particular</span>}</div>
                                            
                                            <div className="flex gap-2 mt-auto">
                                                {ideaFilterMode === 'minhas' && idea.status === 'Pendente' && (
                                                    <>
                                                        <button onClick={() => { setIdeaForm(idea); setIsIdeaModalOpen(true); }} className={`flex-1 text-[10px] font-bold py-1.5 rounded transition-colors ${isDarkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Editar</button>
                                                        <button onClick={() => { if(window.confirm('Excluir ideia?')) delIdea(idea.id); }} className="bg-red-100 hover:bg-red-200 text-red-700 p-1.5 rounded transition-colors"><Trash2 size={14}/></button>
                                                    </>
                                                )}
    
                                                {ideaFilterMode === 'avaliar' && !showArchivedIdeas && (
                                                    <button onClick={() => setEvalIdeaData(idea)} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white text-[10px] font-bold py-2 rounded shadow-md transition-colors flex justify-center items-center gap-1"><CheckCircle size={14}/> Avaliar Ideia</button>
                                                )}
                                        
                                                {ideaFilterMode === 'avaliar' && showArchivedIdeas && (
                                                    <button onClick={async () => {
                                                        if(!window.confirm('Deseja desfazer a avaliação? A ideia voltará para análise e as moedas (se houver) serão estornadas.')) return;
                                                        // Remove o prêmio se existir
                                                        const rewardCoin = distDB.find(d => d.sourceId === `idea_rew_${idea.id}`);
                                                        if(rewardCoin) await delDist(rewardCoin.id);
                                                        
                                                        // Atualiza a ideia para pendente
                                                        await saveIdea(sanitizeForFirebase({...idea, status: 'Pendente', coinsAwarded: 0, evaluatorNotes: ''}));
                                                        alert('Avaliação desfeita com sucesso!');
                                                    }} className="w-full bg-orange-100 hover:bg-orange-200 text-orange-700 text-[10px] font-bold py-2 rounded transition-colors flex justify-center items-center gap-1"><RefreshCw size={14}/> Desfazer Avaliação</button>
                                                )}
                                        
                                                {ideaFilterMode === 'publicas' && (
                                                    <button onClick={() => { setIdeaForm(idea); setIsIdeaModalOpen(true); }} className={`w-full text-[10px] font-bold py-1.5 rounded transition-colors ${isDarkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Ver Detalhes</button>
                                                )}
                                            </div>
                                        </div>
                                    ));
                                })()}
                            </div>
                        </div>
                    )}

                    {/* TELA ACADNEWS */}
                    {activeTab === 'acadnews' && (
                        <div className="space-y-4 animate-fade-in">
                            
                        {/* CABEÇALHO DO MURAL E FILTROS */}
                            <div className={`flex justify-between items-center p-4 rounded-xl shadow-sm backdrop-blur-md border ${isDarkMode ? 'bg-slate-900/40 border-slate-700' : 'bg-white/60 border-orange-200'}`}>
                                <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                                    
                                    {/* TÍTULO E ÍCONE AGRUPADOS */}
                                    <div className="flex items-center gap-1 mr-2">
                                        <h3 className="font-bold text-lg flex items-center gap-2 text-orange-500"><Globe size={24}/> AcadNews</h3>
                                        <div className="relative group cursor-help flex items-center">
                                            <Info size={16} className="text-orange-400 hover:text-orange-500" />
                                            {/* CORREÇÃO: z-[9999] garante que ele ficará acima de qualquer outro elemento na tela */}
                                            <div className="absolute left-0 top-full mt-2 w-64 p-3 bg-slate-800 text-white text-[10px] rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-[9999] border border-slate-600">
                                                <p className="font-bold text-orange-400 mb-1 border-b border-slate-700 pb-1">O que aparece aqui?</p>
                                                <ul className="list-disc pl-4 space-y-1">
                                                    <li>Comunicados e Notícias do RH</li>
                                                    <li>Ideias Inovadoras Aprovadas</li>
                                                    <li>Projetos Concluídos e Equipe</li>
                                                    <li>Previsões de Novos Aportes na Caixinha</li>
                                                    <li>Metas e Alertas da Caixinha</li>
                                                    <li>Presentes, Reconhecimentos e Doações</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    <button onClick={() => setShowArchivedNews(!showArchivedNews)} className={`text-[10px] font-bold px-2 py-1.5 rounded transition-colors shadow-sm ${showArchivedNews ? 'bg-slate-700 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-slate-800 dark:text-slate-300'}`}>
                                        {showArchivedNews ? 'Ocultar Arquivados' : '🗃️ Ver Arquivados'}
                                    </button>

                                    <select 
                                        value={newsGroupMode} 
                                        onChange={(e) => {
                                            const newVal = e.target.value;
                                            setNewsGroupMode(newVal);
                                            localStorage.setItem('acadCoins_newsGroupMode', newVal);
                                        }}
                                        className={`text-[10px] font-bold px-2 py-1.5 rounded outline-none cursor-pointer shadow-sm border ${isDarkMode ? 'bg-slate-800 border-slate-600 text-slate-300' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                                    >
                                        <option value="none">⏳ Linha do Tempo (Padrão)</option>
                                        <option value="type">🗂️ Agrupar por Categoria</option>
                                        <option value="mine">👤 Minhas Interações</option>
                                    </select>
                                </div>
                                {isAdmin && (
                                    <Button onClick={() => setIsNewsModalOpen(true)} icon={Plus} className="bg-orange-500 hover:bg-orange-600 text-white text-xs h-8 border-0 shadow-md">
                                        Nova Notícia
                                    </Button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 items-start">
                                
                                {/* COLUNA ESQUERDA: FEED DE NOTÍCIAS E INTERAÇÕES (Ocupa 2/3) */}
                                <div className="md:col-span-2 space-y-4">
                                    {(() => {
                                        const baseFeed = acadNewsFeed.filter(item => showArchivedNews ? item.isArchived : !item.isArchived);

                                        if (baseFeed.length === 0) {
                                            return <div className="text-center p-10 opacity-50 italic bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm">Nenhuma publicação por aqui...</div>;
                                        }

                                        // Função padronizada para desenhar os cartões
                                        const renderItemCard = (item) => {
                                            let canArchive = false;
                                            if (isAdmin && item.isNews) canArchive = true;
                                            if (!item.isNews) {
                                                if (item.isDoacao && item.collabId === loggedCollabId) canArchive = true;
                                                if (!item.isDoacao && item.collabId === loggedCollabId) canArchive = true;
                                            }

                                            const ArchiveButton = () => canArchive && !item.isArchived ? (
                                                <button onClick={() => handleArchiveItem(item)} title="Arquivar Publicação" className="absolute top-3 right-3 text-[10px] font-bold px-2 py-1 rounded bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-800 transition-colors shadow-sm dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600">
                                                    🗂️ Arquivar
                                                </button>
                                            ) : null;

                                            if (item.isNews) {
                                                const isUrgent = item.type === 'Urgente';
                                                return (
                                                    <div key={item.id} className={`p-5 rounded-xl border shadow-sm relative overflow-hidden ${isUrgent ? (isDarkMode ? 'bg-red-900/30 border-red-800' : 'bg-red-50 border-red-200') : (isDarkMode ? 'bg-blue-900/30 border-blue-800' : 'bg-blue-50 border-blue-200')}`}>
                                                        <div className={`absolute top-0 left-0 w-1.5 h-full ${isUrgent ? 'bg-red-500' : 'bg-blue-500'}`}></div>
                                                        <div className="flex justify-between items-start mb-2 pl-2">
                                                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${isUrgent ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'}`}>{isUrgent ? '⚠️ Extraordinário' : '📰 Rotina'}</span>
                                                            <span className="text-[10px] opacity-70 pr-20">{new Date(item.date).toLocaleDateString('pt-BR')}</span>
                                                        </div>
                                                        <h4 className={`font-black text-lg pl-2 pr-20 ${isUrgent ? 'text-red-600 dark:text-red-400' : 'text-blue-700 dark:text-blue-400'}`}>{item.title}</h4>
                                                        <p className={`mt-2 pl-2 text-sm leading-relaxed whitespace-pre-wrap ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>{item.content}</p>
                                                        <ArchiveButton />
                                                    </div>
                                                );
                                            } else if (item.isIdea) {
                                                return (
                                                    <div key={item.id} className={`p-4 rounded-xl border shadow-sm flex gap-4 items-start relative ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gradient-to-r from-yellow-50 to-white border-yellow-200 hover:bg-yellow-50'}`}>
                                                        <div className="p-3 rounded-full shrink-0 bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400"><span className="text-xl">💡</span></div>
                                                        <div className="flex-1 pt-1 pr-20">
                                                            <div className="text-[10px] font-bold uppercase text-yellow-600 mb-1">Nova Ideia Aprovada!</div>
                                                            <div className="text-xs opacity-70 mb-1">{new Date(item.date+'T12:00:00').toLocaleDateString('pt-BR')} - Por: <span className="font-bold">{item.collabName}</span></div>
                                                            <h4 className={`font-black text-sm mb-1 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{item.title}</h4>
                                                            <p className="text-xs opacity-80 whitespace-pre-wrap">{item.description}</p>
                                                            {parseFloat(item.coinsAwarded) > 0 && <div className="mt-2 inline-block px-2 py-1 bg-green-100 text-green-700 text-[10px] font-bold rounded shadow-sm">Recebeu {item.coinsAwarded} {currencyName}! 🎉</div>}
                                                        </div>
                                                        <ArchiveButton />
                                                    </div>
                                                );
                                            } else if (item.isIdea) {
                                            return (
                                                <div key={item.id} className={`p-4 rounded-xl border shadow-sm flex gap-4 items-start relative ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gradient-to-r from-yellow-50 to-white border-yellow-200 hover:bg-yellow-50'}`}>
                                                    <div className="p-3 rounded-full shrink-0 bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400"><span className="text-xl">💡</span></div>
                                                    <div className="flex-1 pt-1 pr-20">
                                                        <div className="text-[10px] font-bold uppercase text-yellow-600 mb-1">Nova Ideia Aprovada!</div>
                                                        <div className="text-xs opacity-70 mb-1">{new Date(item.date+'T12:00:00').toLocaleDateString('pt-BR')}</div>
                                                        <h4 className={`font-black text-sm mb-1 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{item.title}</h4>
                                                        <p className="text-xs italic opacity-90 mt-2">Damos os parabéns a <b>{item.collabName}</b> pela ideia aprovada! Sua contribuição é fundamental para a empresa e equipe, muito obrigado! 🚀</p>
                                                        {parseFloat(item.coinsAwarded) > 0 && <div className="mt-2 inline-block px-2 py-1 bg-green-100 text-green-700 text-[10px] font-bold rounded shadow-sm">Recebeu {item.coinsAwarded} {currencyName}! 🎉</div>}
                                                    </div>
                                                    <ArchiveButton />
                                                </div>
                                            );
                                        } else if (item.isProjeto) {
                                            const projName = item.reason.replace('Participação no Projeto:', '').trim();
                                            return (
                                                <div key={item.id} className={`p-4 rounded-xl border shadow-sm flex gap-4 items-start relative ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gradient-to-r from-indigo-50 to-white border-indigo-200 hover:bg-indigo-50'}`}>
                                                    <div className="p-3 rounded-full shrink-0 bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400"><Briefcase size={20}/></div>
                                                    <div className="flex-1 pt-1 pr-20">
                                                        <div className="text-[10px] font-bold uppercase text-indigo-600 mb-1">Projeto Concluído! 🏆</div>
                                                        <h4 className={`font-black text-sm mb-1 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{projName}</h4>
                                                        <div className="text-xs opacity-70 mb-2">Finalizado em: {new Date(item.date+'T12:00:00').toLocaleDateString('pt-BR')}</div>
                                                        <p className="text-xs italic opacity-90 mb-2">Excelente trabalho de todos os envolvidos! O empenho de vocês faz a diferença nos resultados da nossa empresa. Parabéns equipe!</p>
                                                        <div className="text-[10px] font-bold mt-2">Participantes: <span className="font-normal opacity-80">{item.participants.join(', ')}</span></div>
                                                    </div>
                                                    <ArchiveButton />
                                                </div>
                                            );
                                        } else if (item.isPrevisto) {
                                            return (
                                                <div key={item.id} className={`p-4 rounded-xl border shadow-sm flex gap-4 items-start relative ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gradient-to-r from-emerald-50 to-white border-emerald-200 hover:bg-emerald-50'}`}>
                                                    <div className="p-3 rounded-full shrink-0 bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"><BadgeDollarSign size={20}/></div>
                                                    <div className="flex-1 pt-1 pr-20">
                                                        <div className="text-[10px] font-bold uppercase text-emerald-600 mb-1">Boas Notícias! 🚀</div>
                                                        <h4 className={`font-black text-sm mb-1 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>Novo Aporte Previsto na Caixinha</h4>
                                                        <p className="text-xs opacity-80 mb-2">{item.description}</p>
                                                        <div className="inline-block px-2 py-1 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded shadow-sm">Previsão: +{formatMoney(item.amount)} em {new Date(item.date+'T12:00:00').toLocaleDateString('pt-BR')}</div>
                                                    </div>
                                                    <ArchiveButton />
                                                </div>
                                            );
                                        } else if (item.isNivelCaixa) {
                                            const isMeta = item.tipoAviso === 'meta_saldo';
                                            return (
                                                <div key={item.id} className={`p-4 rounded-xl border shadow-sm flex gap-4 items-start relative ${isMeta ? (isDarkMode ? 'bg-slate-800 border-yellow-700' : 'bg-gradient-to-r from-yellow-50 to-white border-yellow-300 hover:bg-yellow-50') : (isDarkMode ? 'bg-slate-800 border-red-700' : 'bg-gradient-to-r from-red-50 to-white border-red-300 hover:bg-red-50')}`}>
                                                    <div className={`p-3 rounded-full shrink-0 ${isMeta ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400' : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'}`}>
                                                        {isMeta ? <TrendingUp size={20}/> : <AlertTriangle size={20}/>}
                                                    </div>
                                                    <div className="flex-1 pt-1">
                                                        <h4 className={`font-black text-sm mb-1 ${isMeta ? 'text-yellow-600 dark:text-yellow-500' : 'text-red-600 dark:text-red-500'}`}>{item.title}</h4>
                                                        <p className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{item.content}</p>
                                                    </div>
                                                </div>
                                            );
                                        } else if (item.isDoacao) {
                                            const fromWho = data.collaborators.find(c => c.id === item.collabId)?.name || 'Colega';
                                            return (
                                                <div key={item.id} className={`p-4 rounded-xl border shadow-sm flex gap-4 items-start relative ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                                                    <div className="p-3 rounded-full shrink-0 bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400"><Heart size={20}/></div>
                                                    <div className="flex-1 pt-1 pr-20">
                                                        <div className="text-xs opacity-70 mb-1">{new Date(item.date+'T12:00:00').toLocaleDateString('pt-BR')}</div>
                                                        <p className={`text-sm ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                                            <span className="font-bold">{fromWho}</span> fez uma doação especial!
                                                        </p>
                                                        <div className="mt-1 text-sm italic font-medium text-pink-600 dark:text-pink-400">"{item.reason}"</div>
                                                    </div>
                                                    <ArchiveButton />
                                                </div>
                                            );
                                        } else {
                                            const isGift = item.type === 'presente_recebido';
                                            const parts = (item.reason || '').split('| Motivo:');
                                            const fromWho = parts[0] ? parts[0].replace('Indicado por:', '').replace('Presente de:', '').trim() : 'Colega';
                                            const theReason = parts[1] ? parts[1].trim() : (isGift ? 'Enviou um presente surpresa!' : 'Reconhecimento positivo.');
                                            const toWho = data.collaborators.find(c => c.id === item.collabId)?.name || 'Colega';
                                            
                                            return (
                                                <div key={item.id} className={`p-4 rounded-xl border shadow-sm flex gap-4 items-start relative ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                                                    <div className={`p-3 rounded-full shrink-0 ${isGift ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400' : 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400'}`}>
                                                        {isGift ? <Gift size={20}/> : <Award size={20}/>}
                                                    </div>
                                                    <div className="flex-1 pt-1 pr-20">
                                                        <div className="text-xs opacity-70 mb-1">{new Date(item.date+'T12:00:00').toLocaleDateString('pt-BR')}</div>
                                                        <p className={`text-sm ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                                            <span className="font-bold">{fromWho}</span> {isGift ? 'presenteou' : 'reconheceu'} <span className="font-bold">{toWho}</span>
                                                        </p>
                                                        <div className={`mt-1 text-sm italic font-medium ${isGift ? 'text-purple-600 dark:text-purple-400' : 'text-cyan-600 dark:text-cyan-400'}`}>"{theReason}"</div>
                                                    </div>
                                                    <ArchiveButton />
                                                </div>
                                            );
                                        }
                                    };

                                        // LÓGICA MESTRE DE AGRUPAMENTO
                                        if (newsGroupMode === 'type') {
                                            const noticias = baseFeed.filter(i => i.isNews);
                                            const reconhecimentos = baseFeed.filter(i => !i.isNews && !i.isIdea && i.type === 'indicacao');
                                            const presentes = baseFeed.filter(i => !i.isNews && !i.isIdea && (i.type === 'presente_recebido' || i.isDoacao));
                                            const ideiasFeed = baseFeed.filter(i => i.isIdea);
                                            const projetosFeed = baseFeed.filter(i => i.isProjeto);
                                            const previstosFeed = baseFeed.filter(i => i.isPrevisto);
                                            const niveisFeed = baseFeed.filter(i => i.isNivelCaixa);

                                            // Helper para renderizar a categoria com COR PERSONALIZADA
                                            const renderCategory = (title, items, icon, colorClass) => {
                                                if (items.length === 0) return null;
                                                
                                                const hasNew = items.some(i => !i.isArchived && i.timestamp > lastSeenNewsTime);
                                                
                                                return (
                                                    <details open className="group">
                                                        <summary className={`font-bold text-xs uppercase opacity-90 mb-3 border-b pb-1 cursor-pointer flex justify-between items-center hover:opacity-100 transition-opacity ${colorClass}`} style={{listStyle: 'none'}}>
                                                            <div className="flex items-center gap-2">
                                                                {icon} {title} {hasNew && <span className="animate-bounce drop-shadow-md text-base" title="Novidades nesta categoria!">🎉</span>}
                                                            </div>
                                                            <div className="font-mono text-lg font-black px-2 opacity-60">
                                                                <span className="group-open:hidden">+</span>
                                                                <span className="hidden group-open:inline">-</span>
                                                            </div>
                                                        </summary>
                                                        <div className="space-y-3 animate-fade-in mt-2">{items.map(renderItemCard)}</div>
                                                    </details>
                                                );
                                            };

                                            return (
                                                <div className="space-y-6">
                                                    {renderCategory("Comunicados RH", noticias, "📰", "text-blue-600 border-blue-200 dark:text-blue-400 dark:border-blue-800")}
                                                    {renderCategory("Ideias Aprovadas", ideiasFeed, "💡", "text-yellow-600 border-yellow-200 dark:text-yellow-400 dark:border-yellow-800")}
                                                    {("Projetos Concluídos", projetosFeed, "🏆", "text-indigo-600 border-indigo-200 dark:text-indigo-400 dark:border-indigo-800")}
                                                    {renderCategory("Metas e Alertas", niveisFeed, "📈", "text-red-500 border-red-200 dark:text-red-400 dark:border-red-800")}
                                                    {renderCategory("Novos Aportes (Previstos)", previstosFeed, "🚀", "text-emerald-600 border-emerald-200 dark:text-emerald-400 dark:border-emerald-800")}
                                                    {renderCategory("Reconhecimentos", reconhecimentos, "🎖️", "text-cyan-600 border-cyan-200 dark:text-cyan-400 dark:border-cyan-800")}
                                                    {renderCategory("Presentes e Doações", presentes, "🎁", "text-purple-600 border-purple-200 dark:text-purple-400 dark:border-purple-800")}
                                                </div>
                                            );
                                        } else if (newsGroupMode === 'mine') {
                                            const minhas = baseFeed.filter(i => i.collabId === loggedCollabId || i.fromId === loggedCollabId);
                                            return (
                                                <div className="space-y-6">
                                                    <div>
                                                        <h4 className="font-bold text-xs uppercase opacity-70 mb-3 border-b pb-1 border-gray-200 dark:border-slate-700">👤 Minhas Interações</h4>
                                                        <div className="space-y-3">
                                                            {minhas.length > 0 ? minhas.map(renderItemCard) : <p className="text-xs opacity-50 italic">Nenhuma interação envolvendo você neste período.</p>}
                                                        </div>
                                                    </div>
                                                </div>
                                           );
                                        }

                                        // Padrão (Linha do Tempo / none)
                                        return <div className="space-y-4">{baseFeed.map(renderItemCard)}</div>;
                                    })()}
                                </div>

                                {/* COLUNA DIREITA: RANKING E RIFAS (Ocupa 1/3) */}
                                <div className="md:col-span-1 space-y-4 min-w-0">
                                    {ruleAcadNews.enableLeaderboard && (
                                        <div className={`p-5 rounded-xl border backdrop-blur-md shadow-sm ${isDarkMode ? 'bg-gradient-to-b from-slate-800/60 to-slate-900/60 border-slate-700' : 'bg-gradient-to-b from-orange-50/80 to-white/60 border-orange-200'}`}>
                                            <div className="flex items-center justify-between mb-4">
                                                <h4 className="font-black text-orange-500 uppercase tracking-wider flex items-center gap-2">
                                                    <TrendingUp size={16}/> Top Engajadores
                                                </h4>
                                                <div className="relative group cursor-help">
                                                    <Info size={16} className="text-orange-400 hover:text-orange-500" />
                                                    <div className="absolute right-0 top-full mt-2 w-56 p-3 bg-slate-800 text-white text-[10px] rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 border border-slate-600">
                                                        <p className="font-bold text-orange-400 mb-1 border-b border-slate-700 pb-1">Regras de Pontuação:</p>
                                                        <ul className="list-disc pl-4 space-y-1">
                                                            <li>Reconhecer um colega (+1)</li>
                                                            <li>Enviar presentes (+1)</li>
                                                            <li>Concluir projetos (+1)</li>
                                                            <li>Fazer doações sociais/colegas (+1)</li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                            <p className="text-[10px] opacity-70 mb-4 leading-tight mt-[-10px]">Os colegas que mais inspiram a equipe com suas ações.</p>
                                            
                                            <div className="space-y-3">
                                                {leaderboardStats.length === 0 ? (
                                                    <div className="text-center italic opacity-50 text-xs">Nenhum dado ainda.</div>
                                                ) : (
                                                    leaderboardStats.map((user, idx) => (
                                                        <div key={user.id} className="flex items-center gap-3 relative group">
                                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${idx === 0 ? 'bg-yellow-400 text-yellow-900' : idx === 1 ? 'bg-slate-300 text-slate-800' : idx === 2 ? 'bg-amber-600 text-white' : 'bg-black/10 dark:bg-white/10'}`}>{idx + 1}º</div>
                                                            <div className="flex-1 truncate font-bold text-sm cursor-help">{user.name}</div>
                                                            <div className="text-xs font-black text-orange-500">{user.count}</div>
                                                            
                                                            {/* Popup de Detalhes no Hover */}
                                                            <div className="absolute left-8 top-full mt-1 w-48 p-3 bg-slate-800 text-white text-[10px] rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 border border-slate-600 pointer-events-none">
                                                                <p className="font-bold text-orange-400 mb-2 border-b border-slate-700 pb-1">Ações Realizadas:</p>
                                                                <ul className="space-y-1.5">
                                                                    {user.indicacoes > 0 && <li className="flex justify-between"><span>Reconhecimentos:</span> <b>{user.indicacoes}</b></li>}
                                                                    {user.presentes > 0 && <li className="flex justify-between"><span>Presentes Dados:</span> <b>{user.presentes}</b></li>}
                                                                    {user.projetos > 0 && <li className="flex justify-between"><span>Projetos Concluídos:</span> <b>{user.projetos}</b></li>}
                                                                    {user.doacoes > 0 && <li className="flex justify-between"><span>Doações Feitas:</span> <b>{user.doacoes}</b></li>}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {ruleAcadNews.enableRaffles && (
                                        <div className={`p-5 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                            <h4 className="font-black text-yellow-500 uppercase tracking-wider mb-4 flex items-center gap-2"><Gift size={16}/> Ganhadores de Sorteios</h4>
                                            <div className="space-y-3">
                                                {raffleWinners.length === 0 ? (
                                                    <p className="text-[10px] text-center opacity-50 italic">Nenhum sorteio realizado ainda.</p>
                                                ) : (
                                                    raffleWinners.map((w, idx) => {
                                                        const themes = [
                                                            { bg: 'bg-yellow-50 dark:bg-yellow-900/20', border: 'border-yellow-200 dark:border-yellow-800/50', text: 'text-yellow-700 dark:text-yellow-400' },
                                                            { bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200 dark:border-emerald-800/50', text: 'text-emerald-700 dark:text-emerald-400' },
                                                            { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800/50', text: 'text-blue-700 dark:text-blue-400' },
                                                            { bg: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-200 dark:border-purple-800/50', text: 'text-purple-700 dark:text-purple-400' },
                                                            { bg: 'bg-rose-50 dark:bg-rose-900/20', border: 'border-rose-200 dark:border-rose-800/50', text: 'text-rose-700 dark:text-rose-400' }
                                                        ];
                                                        const theme = themes[idx % themes.length];

                                                        return (
                                                            <div key={w.id} className={`p-3 rounded-lg border shadow-sm ${theme.bg} ${theme.border}`}>
                                                                <div className="text-[9px] font-bold opacity-70 mb-1">{new Date(w.date).toLocaleDateString('pt-BR')}</div>
                                                                <div className={`font-black text-xs mb-1 ${theme.text}`}>
                                                                    {safeText(w.title, '-')}
                                                                </div>
                                                                <div className="text-[10px] leading-tight opacity-80 whitespace-pre-wrap">
                                                                    {safeText(w.content, '-')}
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        //</div>
                    )}

                    {activeTab === 'minha_carteira' && (
                        <div className="space-y-3">

                            {pendingTrainingApprovalsGrouped.length > 0 && (
                                <Card darkMode={isDarkMode}>
                                    <div className="mb-3">
                                        <h3 className="font-black text-base flex items-center gap-2">
                                            <BookOpen size={18} className="text-yellow-500" />
                                            Cursos pendentes de validação
                                        </h3>

                                        <p className={`text-[11px] mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                            Clique no nome para visualizar os cursos pendentes.
                                        </p>
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                        {pendingTrainingApprovalsGrouped.map(group => (
                                            <button
                                                key={group.collabId}
                                                type="button"
                                                onClick={() => setSelectedTrainingApprovalGroup(group)}
                                                title={`${safeText(group.collabName, 'Colaborador')} - ${group.items.length} curso(s) pendente(s)`}
                                                className={`max-w-[190px] px-3 py-2 rounded-lg border text-left transition-all hover:scale-[1.02] ${
                                                    isDarkMode
                                                        ? 'bg-slate-800 border-slate-700 hover:bg-slate-700 text-white'
                                                        : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-800'
                                                }`}
                                            >
                                                <div className="font-bold text-xs leading-tight truncate">
                                                    {getShortPersonName(group.collabName)}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </Card>
                            )}

                            {!loggedCollabId && (
                                <div className="p-3 bg-red-100 text-red-700 rounded-lg border border-red-200 flex items-center gap-2 text-xs">
                                    <AlertTriangle size={16} />
                                    <span>Seu e-mail não está vinculado ao cadastro. Procure o RH.</span>
                                </div>
                            )}

                            {selectedTrainingApprovalGroup && (
                                <div
                                    className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                                    onClick={() => setSelectedTrainingApprovalGroup(null)}
                                >
                                    <div
                                        className={`w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl border shadow-2xl ${
                                            isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'
                                        }`}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className={`sticky top-0 z-10 px-5 py-4 border-b flex items-center justify-between ${
                                            isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'
                                        }`}>
                                            <div>
                                                <h3 className={`font-black text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                                    {safeText(selectedTrainingApprovalGroup.collabName, 'Colaborador')}
                                                </h3>
                                                <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                                    {selectedTrainingApprovalGroup.items.length} curso(s) pendente(s) • {formatCoins(selectedTrainingApprovalGroup.totalCoins || 0)} AcadCoins bloqueados
                                                </p>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() => setSelectedTrainingApprovalGroup(null)}
                                                className={`px-3 py-2 rounded-lg text-xs font-bold ${
                                                    isDarkMode ? 'bg-slate-800 text-slate-200 hover:bg-slate-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                            >
                                                Fechar
                                            </button>
                                        </div>

                                        <div className="p-5 space-y-4">
                                            {selectedTrainingApprovalGroup.items.map(coin => (
                                                <div
                                                    key={coin.id}
                                                    className={`p-4 rounded-xl border ${
                                                        isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'
                                                    }`}
                                                >
                                                    <div className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                                        {safeText(coin.trainingTitle, 'Curso sem título')}
                                                    </div>

                                                    <div className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                                        {safeText(coin.reasonText, 'Curso pendente de aprovação')}
                                                    </div>

                                                    <div className="text-xs font-bold text-yellow-500 mt-2">
                                                        {formatCoins(coin.quantity || 0)} AcadCoins bloqueados
                                                    </div>

                                                    {Array.isArray(coin.links) && coin.links.length > 0 && (
                                                        <div className="flex flex-wrap gap-2 mt-3">
                                                            {coin.links.map(link => (
                                                                <a
                                                                    key={`${coin.id}-${link.url}`}
                                                                    href={link.url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg border text-[10px] font-bold transition-colors ${
                                                                        isDarkMode
                                                                            ? 'bg-slate-900 border-slate-600 text-blue-300 hover:bg-slate-700'
                                                                            : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                                                                    }`}
                                                                >
                                                                    <ExternalLink size={12} />
                                                                    {safeText(link.label, 'Anexo')}
                                                                </a>
                                                            ))}
                                                        </div>
                                                    )}

                                                    <div className="flex flex-wrap gap-2 mt-4">
                                                        <button
                                                            type="button"
                                                            onClick={async () => {
                                                                await approveTrainingCoins(coin, 'course_date');
                                                                setSelectedTrainingApprovalGroup(null);
                                                            }}
                                                            className="px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-bold"
                                                        >
                                                            Aprovar na data do curso
                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={async () => {
                                                                await approveTrainingCoins(coin, 'approval_date');
                                                                setSelectedTrainingApprovalGroup(null);
                                                            }}
                                                            className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold"
                                                        >
                                                            Aprovar na data da validação
                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={async () => {
                                                                await rejectTrainingCoins(coin);
                                                                setSelectedTrainingApprovalGroup(null);
                                                            }}
                                                            className="px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-bold"
                                                        >
                                                            Rejeitar
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {isDisconnected && (
                                <div className="p-3 bg-orange-100 text-orange-800 rounded-lg border border-orange-300 flex items-center gap-2 text-xs font-bold shadow-sm">
                                    <LogOut size={16} />
                                    <span>{disconnectedReason}</span>
                                </div>
                            )}

                            {/* CARDS SECUNDÁRIOS NA MINHA CARTEIRA */}
                            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">

                                {/* NOVO CARD: TOTAL CAIXINHA COM CLIQUE E Z-INDEX CORRIGIDO */}
                                <div
                                    className={`rounded-xl text-center py-2 border-0 shadow-sm relative transition-transform hover:scale-105 cursor-pointer ${
                                        isDarkMode
                                            ? 'bg-gradient-to-br from-amber-900/60 to-amber-800/60 border border-amber-700 text-white'
                                            : 'bg-gradient-to-br from-amber-400 to-yellow-500 text-white'
                                    } ${isCaixaPopupOpen ? 'z-[100]' : 'z-10'}`}
                                    onClick={() => setIsCaixaPopupOpen(true)}
                                >
                                    <h4 className={`text-[9px] font-bold uppercase tracking-wider flex items-center justify-center gap-1 ${
                                        isDarkMode ? 'text-amber-400' : 'text-amber-100'
                                    }`}>
                                        <Database size={12}/> Total Caixinha
                                    </h4>

                                    <div className={`text-xl lg:text-2xl font-black mt-0.5 ${
                                        isDarkMode ? 'text-amber-300' : 'text-white drop-shadow-md'
                                    }`}>
                                        {formatMoney(currentStats.tCaixa)}
                                    </div>

                                    {isCaixaPopupOpen && (
                                        <div
                                            className="fixed inset-0 z-[90] bg-transparent cursor-default"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsCaixaPopupOpen(false);
                                            }}
                                        ></div>
                                    )}

                                    {isCaixaPopupOpen && (
                                        <div
                                            className={`absolute top-full left-0 mt-3 w-72 sm:w-80 p-4 rounded-xl shadow-2xl border text-xs z-[999] text-left flex flex-col gap-3 animate-fade-in cursor-default ${
                                                isDarkMode ? 'bg-slate-800 text-white border-slate-600' : 'bg-white text-gray-800 border-gray-200'
                                            }`}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <div className={`absolute -top-1.5 left-8 w-3 h-3 rotate-45 border-t border-l ${
                                                isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'
                                            }`}></div>

                                            <div className="flex justify-between items-center border-b pb-1 mb-1 dark:border-slate-600">
                                                <h4 className={`font-bold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                                    Destinação (Regras)
                                                </h4>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setIsCaixaPopupOpen(false);
                                                    }}
                                                    className="text-gray-400 hover:text-red-500"
                                                >
                                                    <X size={14}/>
                                                </button>
                                            </div>

                                            <div className="space-y-1 bg-black/5 dark:bg-white/5 p-2 rounded">
                                                <div className="flex justify-between">
                                                    <span>P/ Moedas:</span>
                                                    <span className="font-bold">{config.percProportional}%</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span>Div. Igualitária:</span>
                                                    <span className="font-bold">{config.percEqual}%</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span>Fundo Empresa:</span>
                                                    <span className="font-bold">{config.percCompany}%</span>
                                                </div>
                                            </div>

                                            {(() => {
                                                const validCaixa = caixaDB.filter(c => c.date >= config.startDate && c.date <= todayStr && c.type !== 'Previsto');
                                                let ent = 0, sai = 0;

                                                validCaixa.forEach(c => {
                                                    const v = parseFloat(c.amount || 0);
                                                    if (v >= 0) ent += v;
                                                    else sai += Math.abs(v);
                                                });

                                                return (
                                                    <div className="grid grid-cols-2 gap-2 text-center">
                                                        <div className={`p-2 rounded border ${
                                                            isDarkMode ? 'bg-green-900/20 border-green-800 text-green-400' : 'bg-green-50 border-green-200 text-green-700'
                                                        }`}>
                                                            <span className="block text-[9px] uppercase font-bold opacity-70 mb-0.5">Entradas</span>
                                                            <span className="font-bold text-sm">+{formatMoney(ent)}</span>
                                                        </div>

                                                        <div className={`p-2 rounded border ${
                                                            isDarkMode ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-red-50 border-red-200 text-red-700'
                                                        }`}>
                                                            <span className="block text-[9px] uppercase font-bold opacity-70 mb-0.5">Saídas</span>
                                                            <span className="font-bold text-sm">-{formatMoney(sai)}</span>
                                                        </div>
                                                    </div>
                                                );
                                            })()}

                                            <Button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    generateCaixinhaPDF();
                                                    setIsCaixaPopupOpen(false);
                                                }}
                                                className="w-full bg-amber-500 hover:bg-amber-600 text-white shadow-md border-0 mt-1"
                                                icon={FileText}
                                            >
                                                Gerar Relatório Detalhado
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                <Card
                                    className={`text-center py-2 border-0 shadow-sm ${
                                        isDarkMode
                                            ? 'bg-gradient-to-br from-blue-900/60 to-blue-800/60 border border-blue-700 text-white'
                                            : 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white'
                                    }`}
                                    darkMode={isDarkMode}
                                >
                                    <h4 className={`text-[9px] font-bold uppercase tracking-wider flex items-center justify-center gap-1 ${
                                        isDarkMode ? 'text-blue-400' : 'text-blue-100'
                                    }`}>
                                        <Coins size={12}/> Minhas Moedas
                                    </h4>
                                    <div className={`text-xl lg:text-2xl font-black mt-0.5 ${
                                        isDarkMode ? 'text-blue-300' : 'text-white drop-shadow-md'
                                    }`}>
                                        {formatCoins(myTotalCoins)}
                                    </div>
                                </Card>

                                {/* CARD: COTAÇÃO ATUAL COM POPUP */}
                                <div
                                    className={`rounded-xl text-center py-2 border-0 shadow-sm relative transition-transform hover:scale-105 cursor-pointer ${
                                        isDarkMode
                                            ? 'bg-gradient-to-br from-purple-900/60 to-purple-800/60 border border-purple-700 text-white'
                                            : 'bg-gradient-to-br from-purple-500 to-fuchsia-600 text-white'
                                    } ${isCotacaoPopupOpen ? 'z-[100]' : 'z-10'}`}
                                    onClick={() => setIsCotacaoPopupOpen(true)}
                                >
                                    <h4 className={`text-[9px] font-bold uppercase tracking-wider flex items-center justify-center gap-1 ${
                                        isDarkMode ? 'text-purple-400' : 'text-purple-100'
                                    }`}>
                                        <TrendingUp size={12}/> Cotação Unitária
                                    </h4>

                                    <div className={`text-xl lg:text-2xl font-black mt-0.5 ${
                                        isDarkMode ? 'text-purple-300' : 'text-white drop-shadow-md'
                                    }`}>
                                        {formatQuote(unitValue)}
                                    </div>

                                    {isCotacaoPopupOpen && (
                                        <div
                                            className="fixed inset-0 z-[90] bg-transparent cursor-default"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsCotacaoPopupOpen(false);
                                            }}
                                        ></div>
                                    )}

                                    {isCotacaoPopupOpen && (
                                        <div
                                            className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 p-4 rounded-xl shadow-2xl border text-xs z-[110] text-left flex flex-col gap-2 animate-fade-in cursor-default ${
                                                isDarkMode ? 'bg-slate-800 text-white border-slate-600' : 'bg-white text-gray-800 border-gray-200'
                                            }`}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <div className={`absolute -top-1.5 left-1/2 -ml-1.5 w-3 h-3 rotate-45 border-t border-l ${
                                                isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'
                                            }`}></div>

                                            <div className="flex justify-between items-center border-b pb-1 dark:border-slate-600">
                                                <h4 className={`font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                                    Cálculo da Cotação
                                                </h4>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setIsCotacaoPopupOpen(false);
                                                    }}
                                                    className="text-gray-400 hover:text-red-500"
                                                >
                                                    <X size={14}/>
                                                </button>
                                            </div>

                                            <div className="flex justify-between mt-1">
                                                <span>Total Caixinha Disp.:</span>
                                                <span className="font-bold text-green-600">
                                                    {formatMoney(currentStats.poolProportional)}
                                                </span>
                                            </div>

                                            <div className="flex justify-between">
                                                <span>Moedas Distribuídas:</span>
                                                <span className="font-bold text-blue-600">
                                                    {formatCoins(currentStats.tDist)}
                                                </span>
                                            </div>

                                            <div className="border-t border-dashed my-1 dark:border-slate-600"></div>

                                            <div className="text-[10px] text-center opacity-70">
                                                Fórmula: (Total Disp. ÷ Moedas)
                                            </div>

                                            <div className="flex justify-between items-center bg-purple-50 dark:bg-purple-900/20 p-2 rounded border border-purple-100 dark:border-purple-800 mt-1">
                                                <span className="font-bold">Cotação Un.:</span>
                                                <span className="font-black text-purple-600 dark:text-purple-400">
                                                    {formatQuote(unitValue)}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <Card
                                    className={`text-center py-2 border-0 shadow-sm ${
                                        isDarkMode
                                            ? 'bg-gradient-to-br from-cyan-900/60 to-cyan-800/60 border border-cyan-700 text-white'
                                            : 'bg-gradient-to-br from-cyan-500 to-blue-500 text-white'
                                    }`}
                                    darkMode={isDarkMode}
                                >
                                    <h4 className={`text-[9px] font-bold uppercase tracking-wider flex items-center justify-center gap-1 ${
                                        isDarkMode ? 'text-cyan-400' : 'text-cyan-100'
                                    }`}>
                                        <BadgeDollarSign size={12}/> Valor das Minhas Moedas
                                    </h4>
                                    <div className={`text-xl lg:text-2xl font-black mt-0.5 ${
                                        isDarkMode ? 'text-cyan-300' : 'text-white drop-shadow-md'
                                    }`}>
                                        {formatMoney(myValFromCoins)}
                                    </div>
                                </Card>

                                {/* CARD: BÔNUS COM POPUP */}
                                <div
                                    className={`rounded-xl text-center py-2 border-0 shadow-sm relative transition-transform hover:scale-105 cursor-pointer ${
                                        isDarkMode
                                            ? 'bg-gradient-to-br from-teal-900/60 to-teal-800/60 border border-teal-700 text-white'
                                            : 'bg-gradient-to-br from-teal-400 to-emerald-500 text-white'
                                    } ${isBonusPopupOpen ? 'z-[100]' : 'z-10'}`}
                                    onClick={() => setIsBonusPopupOpen(true)}
                                >
                                    <h4 className={`text-[9px] font-bold uppercase tracking-wider flex items-center justify-center gap-1 ${
                                        isDarkMode ? 'text-teal-400' : 'text-teal-100'
                                    }`}>
                                        <Gift size={12}/> Bônus
                                    </h4>

                                    <div className={`text-xl lg:text-2xl font-black mt-0.5 ${
                                        isDarkMode ? 'text-teal-300' : 'text-white drop-shadow-md'
                                    }`}>
                                        {formatMoney(myValFromEqual)}
                                    </div>

                                    {isBonusPopupOpen && (
                                        <div
                                            className="fixed inset-0 z-[90] bg-transparent cursor-default"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsBonusPopupOpen(false);
                                            }}
                                        ></div>
                                    )}

                                    {isBonusPopupOpen && (
                                        <div
                                            className={`absolute top-full right-0 md:left-1/2 md:-translate-x-1/2 mt-3 w-64 p-4 rounded-xl shadow-2xl border text-xs z-[110] text-left flex flex-col gap-2 animate-fade-in cursor-default ${
                                                isDarkMode ? 'bg-slate-800 text-white border-slate-600' : 'bg-white text-gray-800 border-gray-200'
                                            }`}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <div className={`absolute -top-1.5 right-10 md:left-1/2 md:-ml-1.5 md:right-auto w-3 h-3 rotate-45 border-t border-l ${
                                                isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'
                                            }`}></div>

                                            <div className="flex justify-between items-center border-b pb-1 dark:border-slate-600">
                                                <h4 className={`font-bold ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`}>
                                                    Cálculo do Bônus
                                                </h4>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setIsBonusPopupOpen(false);
                                                    }}
                                                    className="text-gray-400 hover:text-red-500"
                                                >
                                                    <X size={14}/>
                                                </button>
                                            </div>

                                            <div className="flex justify-between mt-1">
                                                <span>Total Disponível:</span>
                                                <span className="font-bold text-green-600">
                                                    {formatMoney(currentStats.poolEqual)}
                                                </span>
                                            </div>

                                            <div className="flex justify-between">
                                                <span>Colab. Elegíveis:</span>
                                                <span className="font-bold text-blue-600">
                                                    {currentStats.activeCount}
                                                </span>
                                            </div>

                                            <div className="border-t border-dashed my-1 dark:border-slate-600"></div>

                                            <div className="text-[10px] text-center opacity-70">
                                                Fórmula: (Total Disp. ÷ Colaboradores)
                                            </div>

                                            <div className="flex justify-between items-center bg-teal-50 dark:bg-teal-900/20 p-2 rounded border border-teal-100 dark:border-teal-800 mt-1">
                                                <span className="font-bold">Resultado:</span>
                                                <span className="font-black text-teal-600 dark:text-teal-400">
                                                    {formatMoney(currentStats.equalBonusPerPerson)}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* CARTÃO DE DESTAQUE - TOTAL DISPONÍVEL + AÇÕES LATERAIS */}
                            <Card
                                className={`col-span-full flex flex-col md:flex-row items-center justify-between py-2 px-5 border-0 shadow-md relative overflow-hidden group ${
                                    isDarkMode
                                        ? 'bg-gradient-to-br from-slate-800 to-slate-900 border border-emerald-800'
                                        : 'bg-gradient-to-br from-emerald-500 to-teal-500 text-white'
                                }`}
                                darkMode={isDarkMode}
                            >
                                <div className="absolute inset-0 bg-white/5 pointer-events-none"></div>

                                <div className="relative z-10 flex flex-col items-center md:items-start text-center md:text-left mb-4 md:mb-0 shrink-0">
                                    <h4 className={`text-[10px] font-bold uppercase tracking-widest mb-0.5 flex items-center justify-center md:justify-start gap-1.5 ${
                                        isDarkMode ? 'text-emerald-400' : 'text-teal-100'
                                    }`}>
                                        <Sparkles size={14} className="text-yellow-300" /> Total Disponível
                                    </h4>

                                    <div
                                        className={`text-3xl lg:text-4xl font-black mb-1.5 animate-pulse cursor-help flex items-center justify-center md:justify-start gap-2 ${
                                            isDarkMode ? 'text-emerald-300' : 'text-white drop-shadow-md'
                                        }`}
                                        title="Soma dos seus Coins + Bônus Igualitário"
                                    >
                                        {formatMoney(myRealValue)} <span className="text-2xl lg:text-3xl drop-shadow-sm">🎉</span>
                                    </div>

                                    <div className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest border shadow-sm w-fit ${
                                        isDarkMode ? 'bg-emerald-900 text-emerald-200 border-emerald-700' : 'bg-black/10 text-white border-white/20 backdrop-blur-sm'
                                    }`}>
                                        <Clock size={10} className="inline mr-1 mb-0.5"/> Disponibilidade a Partir de: {nextPaymentDate}
                                    </div>
                                </div>

                                {(() => {
                                    const ruleAcadNews = getActiveRule(config.acadnewsHistory, todayStr);

                                    if (!ruleAcadNews.enableBadges || isDisconnected) {
                                        return <div className="flex-1 hidden md:block"></div>;
                                    }

                                    const myReceivedInds = distDB.filter(d => d.collabId === loggedCollabId && d.type === 'indicacao').length;
                                    const mySentInds = distDB.filter(d => d.fromId === loggedCollabId && d.type === 'indicacao').length;
                                    const myDonatedCoins = Math.abs(distDB.filter(d => d.collabId === loggedCollabId && d.type === 'doacao' && d.quantity < 0).reduce((a, b) => a + parseFloat(b.quantity), 0));
                                    const myPurchasedTickets = rafflesDB.filter(r => r.collabId === loggedCollabId).length;
                                    const mySentGifts = distDB.filter(d => d.fromId === loggedCollabId && d.type === 'presente_enviado').length;
                                    const myProjects = distDB.filter(d => d.collabId === loggedCollabId && d.type === 'projeto').length;
                                    const myPerformance = distDB.filter(d => d.collabId === loggedCollabId && d.type === 'av_desempenho').length;
                                    const myCoursesDone = distDB.filter(d => d.collabId === loggedCollabId && d.type === 'treinamento' && isCoinAvailable(d)).length;
                                    const myIdeasSubmitted = ideasDB.filter(i => i.collabId === loggedCollabId).length;
                                    const myIdeasApproved = ideasDB.filter(i => i.collabId === loggedCollabId && i.status === 'Aprovada').length;

                                    const earnedBadges = badgesDB.filter(b => {
                                        if (b.triggerType === 'received_indications') return myReceivedInds >= b.triggerValue;
                                        if (b.triggerType === 'sent_indications') return mySentInds >= b.triggerValue;
                                        if (b.triggerType === 'donated_coins') return myDonatedCoins >= b.triggerValue;
                                        if (b.triggerType === 'bought_tickets') return myPurchasedTickets >= b.triggerValue;
                                        if (b.triggerType === 'sent_gifts') return mySentGifts >= b.triggerValue;
                                        if (b.triggerType === 'projects_done') return myProjects >= b.triggerValue;
                                        if (b.triggerType === 'high_performance') return myPerformance >= b.triggerValue;
                                        if (b.triggerType === 'courses_done') return myCoursesDone >= b.triggerValue;
                                        if (b.triggerType === 'ideas_submitted') return myIdeasSubmitted >= b.triggerValue;
                                        if (b.triggerType === 'ideas_approved') return myIdeasApproved >= b.triggerValue;
                                        return false;
                                    });

                                    if (earnedBadges.length === 0) {
                                        return <div className="flex-1 hidden md:block"></div>;
                                    }

                                    return (
                                        <div className="relative z-10 flex-1 flex flex-col items-center md:items-start px-2 sm:px-6 overflow-hidden w-full mb-4 md:mb-0 max-w-[50%]">
                                            <span className={`text-[9px] font-bold uppercase tracking-widest mb-1.5 flex items-center gap-1 opacity-90 ${
                                                isDarkMode ? 'text-emerald-400' : 'text-emerald-100'
                                            }`}>
                                                <Award size={12}/> Minhas Conquistas
                                            </span>

                                            <div className="flex items-center gap-2 overflow-x-auto custom-scrollbar w-full pb-1">
                                                {earnedBadges.map(b => (
                                                    <div
                                                        key={b.id}
                                                        className={`px-2 py-1 rounded-lg border flex items-center gap-1.5 whitespace-nowrap shrink-0 cursor-help transition-transform hover:scale-105 shadow-sm ${
                                                            isDarkMode ? 'bg-emerald-900/50 border-emerald-700/50 text-emerald-100' : 'bg-white/20 border-white/30 text-white'
                                                        }`}
                                                        title={b.description}
                                                    >
                                                        <span className="text-sm">{b.emoji}</span>
                                                        <span className="text-xs font-bold">{b.name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })()}

                                <div className="relative z-10 flex flex-col sm:flex-row gap-2 w-full md:w-auto justify-end">
                                    {(isRecEnabled || isGiftEnabled || isProjEnabled || ruleAcadNews.enableRaffles) && (
                                        <div className="grid grid-cols-2 gap-2 w-full sm:w-[280px]">
                                            {isRecEnabled && (
                                                <button
                                                    onClick={() => setIsRecognizeModalOpen(true)}
                                                    disabled={isDisconnected}
                                                    className={`w-full h-full font-bold uppercase tracking-wider py-2 px-2 rounded-lg text-[10px] shadow-md flex justify-center items-center gap-1.5 border-0 transition-all ${
                                                        isDisconnected ? 'bg-cyan-500/50 text-white/70 cursor-not-allowed' : 'bg-cyan-500 hover:bg-cyan-600 text-white shadow-cyan-500/30'
                                                    }`}
                                                >
                                                    <Award size={14} className="shrink-0"/> <span className="truncate">Reconhecer</span>
                                                </button>
                                            )}

                                            {isGiftEnabled && (
                                                <button
                                                    onClick={() => setIsGiftModalOpen(true)}
                                                    disabled={isDisconnected || myGiftAllowanceLeft <= 0}
                                                    className={`w-full h-full font-bold uppercase tracking-wider py-2 px-2 rounded-lg text-[10px] shadow-md flex justify-center items-center gap-1.5 border-0 transition-all ${
                                                        isDisconnected || myGiftAllowanceLeft <= 0 ? 'bg-purple-500/50 text-white/70 cursor-not-allowed' : 'bg-purple-500 hover:bg-purple-600 text-white shadow-purple-500/30'
                                                    }`}
                                                    title={`Você tem ${myGiftAllowanceLeft} presentes restantes neste período.`}
                                                >
                                                    <Gift size={14} className="shrink-0"/> <span className="truncate">Presentear</span>
                                                </button>
                                            )}

                                            {isProjEnabled && (
                                                <button
                                                    onClick={() => setIsProjectWalletModalOpen(true)}
                                                    disabled={isDisconnected}
                                                    className={`w-full h-full font-bold uppercase tracking-wider py-2 px-2 rounded-lg text-[10px] shadow-md flex justify-center items-center gap-1.5 border-0 transition-all ${
                                                        isDisconnected ? 'bg-indigo-500/50 text-white/70 cursor-not-allowed' : 'bg-indigo-500 hover:bg-indigo-600 text-white shadow-indigo-500/30'
                                                    }`}
                                                >
                                                    <Briefcase size={14} className="shrink-0"/> <span className="truncate">Projetos</span>
                                                </button>
                                            )}

                                            {ruleAcadNews.enableRaffles && (
                                                <button
                                                    onClick={() => setIsRaffleModalOpen(true)}
                                                    disabled={isDisconnected}
                                                    className={`w-full h-full font-bold uppercase tracking-wider py-2 px-2 rounded-lg text-[10px] shadow-md flex justify-center items-center gap-1.5 border-0 transition-all ${
                                                        isDisconnected ? 'bg-yellow-500/50 text-white/70 cursor-not-allowed' : 'bg-yellow-500 hover:bg-yellow-600 text-white shadow-yellow-500/30'
                                                    }`}
                                                >
                                                    <Gift size={14} className="shrink-0"/> <span className="truncate">Sorteios</span>
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    <div className="flex flex-col gap-2 w-full sm:w-40">
                                        <button
                                            onClick={() => setIsFazerOBemModalOpen(true)}
                                            disabled={isDisconnected || myTotalCoins <= 0}
                                            className={`w-full flex-1 font-bold uppercase tracking-wider py-2 px-3 rounded-lg text-[10px] shadow-md flex justify-center items-center gap-1.5 border-0 transition-all ${
                                                isDisconnected || myTotalCoins <= 0 ? 'bg-pink-500/50 text-white/70 cursor-not-allowed' : 'bg-pink-400 hover:bg-pink-500 text-white shadow-pink-500/30'
                                            }`}
                                        >
                                            <Heart size={13}/> Vamos Fazer o Bem?
                                        </button>

                                        <button
                                            onClick={() => {
                                                if (redemptionStatus.isOpen) {
                                                    setIsRedeemModalOpen(true);
                                                } else {
                                                    alert(`A vitrine de prêmios ainda não está liberada.\n\nFique de olho: ${redemptionStatus.text}.`);
                                                }
                                            }}
                                            disabled={isDisconnected}
                                            className={`w-full flex-1 font-bold uppercase tracking-wider py-2 px-3 rounded-lg text-[10px] border-0 flex justify-center items-center gap-1.5 transition-all ${
                                                isDisconnected
                                                    ? 'bg-gray-400 text-white/50 cursor-not-allowed'
                                                    : redemptionStatus.isOpen
                                                        ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-500/40 cursor-pointer animate-pulse-once'
                                                        : 'bg-slate-400/80 hover:bg-slate-400 text-white shadow-sm cursor-pointer'
                                            }`}
                                        >
                                            {redemptionStatus.isOpen ? (
                                                <>
                                                    <Gift size={14}/> Resgatar Prêmios
                                                </>
                                            ) : (
                                                <div className="flex items-center gap-1.5">
                                                    <Clock size={12} className="opacity-80 mb-0.5"/>
                                                    <span className={redemptionStatus.isImminent ? 'animate-pulse text-yellow-200' : 'opacity-90'}>
                                                        {redemptionStatus.text}
                                                    </span>
                                                </div>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </Card>

                            {/* QUADROS INFERIORES: EXTRATO E CATÁLOGO */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                                {/* QUADRO ESQUERDA: EXTRATO */}
                                <div className={`rounded-xl border shadow-sm flex flex-col h-[400px] overflow-hidden ${
                                    isDarkMode ? 'bg-slate-800/80 border-slate-700' : 'bg-slate-50 border-slate-200'
                                }`}>
                                    <div className={`px-4 py-2 border-b font-bold flex items-center justify-between gap-2 ${
                                        isDarkMode ? 'bg-slate-900 border-slate-700 text-blue-400' : 'bg-white border-slate-200 text-blue-700'
                                    }`}>
                                        <div className="flex items-center gap-2">📜 Detalhamento dos {currencyName}</div>

                                        <div className="flex items-center gap-2">
                                            {config.enableRecognition && (
                                                <button
                                                    onClick={() => setShowIndicacoesModal(true)}
                                                    className="bg-cyan-500 hover:bg-cyan-600 text-white text-[10px] uppercase font-black px-3 py-1.5 rounded-lg shadow-sm flex items-center gap-1 transition-all"
                                                >
                                                    <Award size={14}/> Indicações
                                                </button>
                                            )}

                                            <button
                                                onClick={() => setShowPrevisaoModal(true)}
                                                className="bg-yellow-400 hover:bg-yellow-500 text-yellow-900 text-[10px] uppercase font-black px-3 py-1.5 rounded-lg shadow-sm flex items-center gap-1 transition-all"
                                            >
                                                <Clock size={14}/> Previsões
                                            </button>
                                        </div>
                                    </div>

                                    <div className="p-4 flex-1 flex flex-col min-h-0">
                                        <div className="flex gap-2 mb-3 items-center flex-wrap shrink-0">
                                            <select
                                                className={`w-full sm:w-28 p-1.5 border rounded text-xs outline-none ${
                                                    isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : ''
                                                }`}
                                                value={walletPeriod}
                                                onChange={e => setWalletPeriod(e.target.value)}
                                            >
                                                <option value="">Todos</option>
                                                {myWalletData.periods.map(p => (
                                                    <option key={p} value={p}>{p}</option>
                                                ))}
                                            </select>

                                            <div className="flex-1 relative min-w-[120px]">
                                                <Search className="absolute left-2 top-2 text-gray-400" size={12}/>
                                                <Input
                                                    className={`pl-7 text-xs h-7 ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : ''}`}
                                                    placeholder="Buscar motivo..."
                                                    value={walletSearch}
                                                    onChange={e => setWalletSearch(e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        <div className={`flex-1 overflow-y-auto custom-scrollbar border rounded-lg ${
                                            isDarkMode ? 'border-slate-700' : 'border-gray-200'
                                        }`}>
                                            <table className={`w-full text-[10px] md:text-xs text-left ${
                                                isDarkMode ? 'text-slate-300' : 'text-gray-600'
                                            }`}>
                                                <thead className={`text-[9px] uppercase font-bold sticky top-0 shadow-sm z-10 ${
                                                    isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-500'
                                                }`}>
                                                    <tr>
                                                        <th className="p-2 w-16">Data</th>
                                                        <th className="p-2">Motivo</th>
                                                        <th className="p-2 text-right">Coins</th>
                                                        <th className="p-2 text-right text-emerald-600">R$ Ref.</th>
                                                    </tr>
                                                </thead>

                                                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                                                    {myWalletData.rows.map((d, i) => (
                                                        <tr
                                                            key={d.id}
                                                            title={safeText(
                                                                hasDistributionCalcMemory(d) ? getDistributionCalcMemory(d) : d.reason,
                                                                '-'
                                                            )}
                                                            className={`${i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50')} ${isDisconnected ? 'opacity-40 grayscale' : ''} ${hasDistributionCalcMemory(d) ? 'cursor-help' : ''}`}
                                                        >
                                                            <td className="p-2 font-mono">
                                                                {d.date?.substring(5).replace('-', '/') || '-'}
                                                            </td>

                                                            <td
                                                                className={`p-2 truncate max-w-[100px] md:max-w-[200px] ${hasDistributionCalcMemory(d) ? 'underline decoration-dotted underline-offset-4' : ''}`}
                                                                title={safeText(
                                                                    hasDistributionCalcMemory(d) ? getDistributionCalcMemory(d) : d.reason,
                                                                    '-'
                                                                )}
                                                            >
                                                                {d.type === 'presente_recebido' && (
                                                                    <span className="bg-green-500 text-white px-1.5 py-0.5 rounded text-[8px] uppercase font-bold mr-1.5">
                                                                        Presente (Recebido)
                                                                    </span>
                                                                )}

                                                                {d.type === 'virtual_presente' && (
                                                                    <span className="bg-blue-600 text-white px-1.5 py-0.5 rounded text-[8px] uppercase font-bold mr-1.5">
                                                                        Presentear
                                                                    </span>
                                                                )}

                                                                <span>
                                                                    {safeText(d.reason, '-')}

                                                                    {d.approvalStatus === 'pending' && (
                                                                        <span className="ml-2 px-2 py-0.5 rounded bg-yellow-100 text-yellow-700 text-[10px] font-black border border-yellow-200">
                                                                            BLOQUEADO
                                                                        </span>
                                                                    )}

                                                                    {d.approvalStatus === 'approved' && d.type === 'treinamento' && (
                                                                        <span className="ml-2 px-2 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-black border border-green-200">
                                                                            APROVADO
                                                                        </span>
                                                                    )}

                                                                    {d.approvalStatus === 'rejected' && (
                                                                        <span className="ml-2 px-2 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-black border border-red-200">
                                                                            REJEITADO
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </td>

                                                            <td className={`p-2 text-right font-bold ${
                                                                d.approvalStatus === 'pending'
                                                                    ? 'text-yellow-500'
                                                                    : d.quantity < 0
                                                                        ? 'text-red-500'
                                                                        : 'text-blue-500'
                                                            }`}>
                                                                {d.approvalStatus === 'pending'
                                                                    ? `🔒 ${formatCoins(d.quantity)}`
                                                                    : `${d.quantity > 0 ? '+' : ''}${formatCoins(d.quantity)}`
                                                                }
                                                            </td>

                                                            <td className={`p-2 text-right font-bold ${
                                                                d.approvalStatus === 'pending'
                                                                    ? 'text-yellow-500'
                                                                    : d.quantity < 0
                                                                        ? 'text-red-500'
                                                                        : 'text-emerald-600'
                                                            }`}>
                                                                {d.approvalStatus === 'pending'
                                                                    ? '🔒'
                                                                    : `${d.quantity < 0 ? '-' : ''}${formatMoney(Math.abs(d.totalValue || 0))}`
                                                                }
                                                            </td>
                                                        </tr>
                                                    ))}

                                                    {myWalletData.rows.length === 0 && (
                                                        <tr>
                                                            <td colSpan="4" className="p-4 text-center italic opacity-60">
                                                                Nenhum registro encontrado.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                {/* QUADRO DIREITA: CATÁLOGO */}
                                <div className={`rounded-xl border shadow-sm flex flex-col h-[400px] overflow-hidden ${
                                    isDarkMode ? 'bg-amber-900/10 border-amber-800/30' : 'bg-amber-50/30 border-amber-200/60'
                                }`}>
                                    <div className={`px-4 py-2 border-b font-bold flex items-center gap-2 ${
                                        isDarkMode ? 'bg-slate-900 border-amber-800/50 text-amber-500' : 'bg-white border-amber-200/60 text-amber-600'
                                    }`}>
                                        🎁 Prêmios Disponíveis para Resgate
                                    </div>

                                    <div className="p-4 flex-1 overflow-y-auto custom-scrollbar space-y-2">
                                        <div className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                                            isDarkMode ? 'bg-slate-900 border-slate-700 hover:border-green-500' : 'bg-white border-gray-200 hover:border-green-500 shadow-sm'
                                        }`}>
                                            <div>
                                                <h5 className="font-bold text-xs text-green-600 flex items-center gap-1.5">
                                                    <BadgeDollarSign size={14}/> Receber em Dinheiro (Folha)
                                                </h5>
                                                <p className={`text-[9px] mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                                    Opção padrão. O saldo não resgatado vira dinheiro.
                                                </p>
                                            </div>
                                            <span className="text-[9px] uppercase font-bold bg-green-100 text-green-700 px-2 py-1 rounded">
                                                Automático
                                            </span>
                                        </div>

                                        {prizesDB.filter(p => p.quantity > 0 && (!p.targetPeriod || p.targetPeriod === currentPeriodStr)).map(p => {
                                            const isCoinPrize = !!p.coinsValue;
                                            const canAfford = isCoinPrize
                                                ? (myTotalCoins >= Number(p.coinsValue))
                                                : (myRealValue >= Number(p.value) && unitValue > 0);

                                            const isAvailable = p.isRaffle || redemptionStatus.isOpen;
                                            const canProceed = canAfford && !isDisconnected && isAvailable;

                                            return (
                                                <div
                                                    key={p.id}
                                                    className={`flex items-center justify-between p-3 rounded-xl border-2 transition-all ${
                                                        canProceed
                                                            ? (isDarkMode ? 'bg-slate-900 border-slate-700 hover:border-orange-500 cursor-pointer' : 'bg-white border-gray-200 hover:border-orange-500 cursor-pointer shadow-sm')
                                                            : 'opacity-50 grayscale cursor-not-allowed border-gray-100 bg-gray-50 dark:bg-slate-900/50 dark:border-slate-800'
                                                    }`}
                                                    onClick={() => {
                                                        if (canProceed) {
                                                            p.isRaffle ? handleBuyTicket(p) : handleRedeemPrize(p);
                                                        }
                                                    }}
                                                >
                                                    <div>
                                                        <h5 className={`font-bold text-xs ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                                            {p.name}
                                                            {p.isRaffle && (
                                                                <span className="ml-1 bg-yellow-100 text-yellow-800 text-[8px] px-1.5 py-0.5 rounded">
                                                                    Rifa
                                                                </span>
                                                            )}
                                                        </h5>

                                                        <p className={`text-[9px] mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                                            Custo: {isCoinPrize ? `${formatCoins(p.coinsValue)} Coins` : formatMoney(p.value)} | Restam: {p.quantity}
                                                        </p>
                                                    </div>

                                                    {canProceed ? (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                p.isRaffle ? handleBuyTicket(p) : handleRedeemPrize(p);
                                                            }}
                                                            className={`transition-colors text-white text-xs font-bold px-4 py-2 rounded-lg shadow-md cursor-pointer ${
                                                                p.isRaffle ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-orange-500 hover:bg-orange-600'
                                                            }`}
                                                        >
                                                            {p.isRaffle ? '🎟️ Comprar Bilhete' : 'Quero este!'}
                                                        </button>
                                                    ) : (!canAfford && isAvailable) ? (
                                                        <span className="text-[10px] font-bold text-red-500 text-right">
                                                            Saldo<br/>Insuficiente
                                                        </span>
                                                    ) : (
                                                        <span className="text-[10px] font-bold text-gray-500 text-right">
                                                            Aguarde<br/>Janela
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}

                                        {prizesDB.filter(p => p.quantity > 0 && (!p.targetPeriod || p.targetPeriod === currentPeriodStr)).length === 0 && (
                                            <div className="text-center p-6 text-xs opacity-50 italic">
                                                Nenhum prêmio extra no catálogo.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* MODAL FAZER O BEM */}
                    {isFazerOBemModalOpen && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsFazerOBemModalOpen(false)}>
                            <div className={`p-6 rounded-xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh] ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                    <h3 className="font-bold text-xl flex items-center gap-2 text-pink-500"><Heart size={24} className="fill-pink-500"/> Fazer o Bem</h3>
                                    <button onClick={() => setIsFazerOBemModalOpen(false)} className="hover:bg-black/10 p-1 rounded"><X size={20}/></button>
                                </div>

                                <div className="flex-1 overflow-y-auto custom-scrollbar space-y-5">
                                    <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                        Seus {currencyName} podem mudar o dia de alguém! Você tem <b className="text-blue-500">{formatCoins(myTotalCoins)} {currencyName}</b> disponíveis para doação. <br/>
                                        <span className="text-[10px] opacity-70">(O Bônus Igualitário não pode ser transferido).</span>
                                    </p>

                                    <div className={`flex border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                        <button onClick={() => setFobForm({...fobForm, type: 'colleague'})} className={`flex-1 py-2 font-bold text-sm border-b-2 transition-colors flex justify-center items-center gap-2 ${fobForm.type === 'colleague' ? 'border-pink-500 text-pink-500' : 'border-transparent opacity-60'}`}><Users size={16}/> Para Colegas</button>
                                        <button onClick={() => setFobForm({...fobForm, type: 'social'})} className={`flex-1 py-2 font-bold text-sm border-b-2 transition-colors flex justify-center items-center gap-2 ${fobForm.type === 'social' ? 'border-pink-500 text-pink-500' : 'border-transparent opacity-60'}`}><Globe size={16}/> Social Acad</button>
                                    </div>

                                    {/* CONTEÚDO: PARA COLEGAS (ATUALIZADO COM FILTRO DE ELEGIBILIDADE) */}
                                    {fobForm.type === 'colleague' && (
                                        <div className="space-y-4 animate-fade-in">
                                            <div>
                                                <div className="flex justify-between items-center mb-2">
                                                    <label className="text-xs font-bold uppercase block text-gray-500">1. Selecione os Colegas</label>
                                                    <div className="relative w-40">
                                                        <Search size={12} className="absolute left-2 top-2 text-gray-400"/>
                                                        <input 
                                                            type="text" 
                                                            placeholder="Buscar colega..." 
                                                            value={fobSearch} 
                                                            onChange={(e) => setFobSearch(e.target.value)} 
                                                            className={`w-full pl-7 py-1 text-xs border rounded outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                                                        />
                                                    </div>
                                                </div>

                                                <div className={`max-h-40 overflow-y-auto p-2 border rounded-lg custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                    {(() => {
                                                        const eligibleCollabs = data.collaborators.filter(c => {
                                                            if (c.id === loggedCollabId) return false;
                                                            if (c.participatesAcadCoins === false) return false;
                                                            const start = c.acadCoinsStartDate;
                                                            if (!start || start > todayStr) return false;
                                                            const end = c.acadCoinsEndDate;
                                                            if (end && end < todayStr) return false;
                                                            if (fobSearch && !c.name.toLowerCase().includes(fobSearch.toLowerCase())) return false;
                                                            return true;
                                                        }).sort((a,b)=>a.name.localeCompare(b.name));

                                                        if (eligibleCollabs.length === 0) return <p className="text-xs italic text-center p-4 opacity-50">Nenhum colega elegível encontrado.</p>;

                                                        return eligibleCollabs.map(c => (
                                                            <label key={c.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-white'}`}>
                                                                <input 
                                                                    type="checkbox" 
                                                                    className="w-4 h-4 text-pink-500 rounded border-gray-300 focus:ring-pink-500"
                                                                    checked={fobForm.targets.includes(c.id)}
                                                                    onChange={(e) => {
                                                                        const newTargets = e.target.checked 
                                                                            ? [...fobForm.targets, c.id] 
                                                                            : fobForm.targets.filter(id => id !== c.id);
                                                                        setFobForm({...fobForm, targets: newTargets});
                                                                    }}
                                                                />
                                                                <span className="text-sm font-medium">{c.name}</span>
                                                            </label>
                                                        ));
                                                    })()}
                                                </div>
                                            </div>

                                            <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer text-sm font-bold transition-colors ${
                                                fobForm.isAnonymous 
                                                ? (isDarkMode ? 'bg-pink-900/40 border-pink-700 text-pink-300' : 'bg-pink-100 border-pink-300 text-pink-800')
                                                : (isDarkMode ? 'bg-slate-900 border-slate-700 text-slate-400' : 'bg-gray-50 border-gray-300 text-gray-600')
                                            }`}>
                                                <input type="checkbox" className="w-4 h-4" checked={fobForm.isAnonymous} onChange={e=>setFobForm({...fobForm, isAnonymous: e.target.checked})} />
                                                Fazer doação anonimamente
                                            </label>
                                        </div>
                                    )}

                                    {fobForm.type === 'social' && (
                                        <div className="space-y-4 animate-fade-in">
                                            <div>
                                                <label className="text-xs font-bold uppercase mb-2 block text-gray-500">1. Escolha o Programa Social</label>
                                                <select 
                                                    className={`w-full p-3 border rounded-lg outline-none ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-gray-300'}`}
                                                    value={fobForm.socialProgram}
                                                    onChange={e=>setFobForm({...fobForm, socialProgram: e.target.value})}
                                                >
                                                    <option value="">Selecione a campanha ou projeto...</option>
                                                    {/* Lê da configuração global e gera as opções */}
                                                    {(config.socialProjects || 'Campanha Solidária Acad, Apoio Desastres Naturais, Natal Solidário').split(',').map(proj => {
                                                        const pName = proj.trim();
                                                        return pName ? <option key={pName} value={pName}>{pName}</option> : null;
                                                    })}
                                                    <option value="Outro (Notificar RH)">Outro (Notificar RH)</option>
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                    <div className="pt-2">
                                        <label className="text-xs font-bold uppercase mb-1 block text-gray-500">2. Quantidade ({currencyName}) que deseja enviar?</label>
                                        <div className="flex gap-4 items-center">
                                            <div className="relative flex-1">
                                                <Input 
                                                    type="number" 
                                                    step="0.1"
                                                    min="0.1"
                                                    className={`text-lg font-black pl-10 ${isDarkMode ? 'bg-slate-900 border-slate-700 text-pink-400' : 'text-pink-600'}`}
                                                    value={fobForm.coins} 
                                                    onChange={e => setFobForm({...fobForm, coins: e.target.value})} 
                                                    placeholder="Ex: 10" 
                                                />
                                                <Coins className="absolute left-3 top-2.5 text-pink-500 opacity-50" size={20}/>
                                            </div>
                                            <div className="flex-1 text-xs opacity-70">
                                                <div className="font-bold">Total que sairá da conta:</div>
                                                <div className="text-lg text-pink-500">{formatCoins(parseFloat(fobForm.coins || 0) * (fobForm.type === 'colleague' ? Math.max(fobForm.targets.length, 1) : 1))} Coins</div>
                                            </div>
                                        </div>
                                        {unitValue > 0 && parseFloat(fobForm.coins) > 0 && (
                                            <p className="text-[10px] mt-1 text-green-600 font-bold bg-green-50 p-1.5 rounded inline-block dark:bg-green-900/30 dark:text-green-400">
                                                Você está enviando o equivalente a {formatMoney(parseFloat(fobForm.coins) * unitValue)} por envio!
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-6 border-t pt-4 flex gap-3 dark:border-slate-700">
                                    <Button variant="secondary" onClick={() => setIsFazerOBemModalOpen(false)} className={isDarkMode ? 'bg-slate-700 text-white hover:bg-slate-600 border-slate-600' : ''}>Cancelar</Button>
                                    <Button onClick={handleFazerOBemSubmit} className="flex-1 bg-pink-500 hover:bg-pink-600 text-white font-bold border-0 shadow-lg shadow-pink-500/30">Confirmar Envio</Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* MODAL SORTEIOS E RIFAS (TRANSPARENTE E AUDITÁVEL) */}
                {isRaffleModalOpen && (
                    <div className="fixed inset-0 z-[160] flex items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-fade-in" onClick={() => setIsRaffleModalOpen(false)}>
                        <div className={`p-6 rounded-xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh] border border-white/20 bg-white/10`} onClick={e => e.stopPropagation()}>
                            
                            <div className="flex justify-between items-center mb-4 border-b border-white/20 pb-2">
                                <h3 className="font-black text-xl flex items-center gap-2 text-white drop-shadow-md"><Gift size={24}/> Vitrine de Sorteios</h3>
                                <button onClick={() => setIsRaffleModalOpen(false)} className="text-white/60 hover:text-white"><X size={24}/></button>
                            </div>

                            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-6 pr-2">
                                <p className="text-sm text-white/80 mb-4 text-center">Acompanhe os prêmios sendo rifados e participe! Os sorteios são realizados de forma automatizada e auditável.</p>
                                
                                {(() => {
                                    // Puxa todas as rifas, inclusive as finalizadas
                                    const allRaffles = prizesDB.filter(p => p.isRaffle);
                                    if (allRaffles.length === 0) return <div className="text-center italic opacity-60 text-white py-10">Nenhum sorteio cadastrado no catálogo.</div>;

                                    return (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {allRaffles.map(prize => {
                                                const tickets = rafflesDB.filter(r => r.prizeId === prize.id);
                                                const myTicketsCount = tickets.filter(t => t.collabId === loggedCollabId).length;
                                                
                                                // Se a quantidade é <= 0, indica que o sorteio já foi feito
                                                const isFinished = prize.quantity <= 0;

                                                return (
                                                    <div key={prize.id} className={`p-5 rounded-xl border border-white/20 backdrop-blur-xl shadow-lg relative overflow-hidden transition-all ${isFinished ? 'bg-black/60 opacity-90' : 'bg-black/40'}`}>
                                                        <div className="absolute -right-4 -top-4 text-7xl opacity-10 pointer-events-none">🎟️</div>
                                                        
                                                        {/* TAG de Encerrado */}
                                                        {isFinished && <div className="absolute top-2 right-2 bg-red-500 text-white text-[9px] uppercase font-bold px-2 py-0.5 rounded shadow-sm z-10">Sorteio Finalizado</div>}

                                                        <h4 className={`font-black text-2xl mb-1 pr-20 ${isFinished ? 'text-gray-400 line-through' : 'text-yellow-400'}`}>{prize.name}</h4>
                                                        <div className="text-xs font-bold text-white/70 uppercase mb-4 tracking-wider">Custo do Bilhete: {formatMoney(prize.value)}</div>
                                                        
                                                        <div className="bg-white/5 p-3 rounded-lg border border-white/10 mb-4 relative z-10">
                                                            <div className="text-[10px] font-bold uppercase text-white/50 mb-2 border-b border-white/10 pb-1">Bilhetes Vendidos ({tickets.length}):</div>
                                                            <div className="max-h-20 overflow-y-auto custom-scrollbar flex flex-wrap gap-1.5">
                                                                {tickets.length === 0 ? <span className="text-xs italic text-white/40">Nenhum bilhete comprado ainda.</span> : 
                                                                    tickets.map((t, idx) => {
                                                                        // Dá um destaque visual super legal para quem for o ganhador gravado no banco!
                                                                        const isWinner = isFinished && t.collabId === prize.lastWinnerId;
                                                                        const isMe = t.collabId === loggedCollabId;
                                                                        
                                                                        return (
                                                                            <span key={t.id + idx} className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${isWinner ? 'bg-red-500 text-white shadow-[0_0_8px_rgba(239,68,68,0.8)] border border-white z-10 scale-110' : isMe ? 'bg-yellow-500 text-black shadow-[0_0_5px_rgba(234,179,8,0.6)]' : 'bg-white/20 text-white opacity-60'}`} title={new Date(t.date).toLocaleString('pt-BR')}>
                                                                                #{idx + 1} - {t.collabName.split(' ')[0]}
                                                                            </span>
                                                                        );
                                                                    })
                                                                }
                                                            </div>
                                                        </div>

                                                        <div className="flex flex-col sm:flex-row items-center justify-between mt-auto gap-3 relative z-10">
                                                            <div className="text-xs font-bold text-white/80 flex items-center gap-1.5">
                                                                Meus Bilhetes: <span className="text-yellow-400 text-lg">{myTicketsCount}</span>
                                                            </div>
                                                            
                                                            {isAdmin && (
                                                                <div className="flex gap-2">
                                                                    {!isFinished ? (
                                                                        <Button 
                                                                            onClick={() => {
                                                                                if (tickets.length === 0) return alert("Nenhum bilhete vendido para este sorteio.");
                                                                                setActiveDrawRaffle({ prize, tickets, winner: null, isProcessing: false });
                                                                            }} 
                                                                            className="bg-red-500 hover:bg-red-600 text-white text-xs border-0 shadow-[0_0_10px_rgba(239,68,68,0.5)] whitespace-nowrap"
                                                                        >
                                                                            Preparar Sorteio!
                                                                        </Button>
                                                                    ) : (
                                                                        <>
                                                                            <Button onClick={() => handleUndoRaffle(prize)} className="bg-gray-600 hover:bg-gray-500 text-white text-[10px] px-2 py-1.5 h-auto border-0" title="Desfazer Sorteio">
                                                                                <X size={12} className="mr-1"/> Desfazer
                                                                            </Button>
                                                                            <Button onClick={() => handleRedrawRaffle(prize, tickets)} className="bg-orange-500 hover:bg-orange-600 text-white text-[10px] px-2 py-1.5 h-auto border-0 shadow-md" title="Sortear Novamente">
                                                                                <RefreshCw size={12} className="mr-1"/> Re-sortear
                                                                            </Button>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL DO SORTEIO AO VIVO */}
                {activeDrawRaffle && (
                    <div className="fixed inset-0 z-[170] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-fade-in">
                        <div className="p-6 rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh] border border-white/20 bg-slate-900 text-white" onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center border-b border-white/20 pb-3 mb-4 shrink-0">
                                <h3 className="font-black text-2xl flex items-center gap-2 text-yellow-400">
                                    <Gift size={24} /> Sorteio: {activeDrawRaffle.prize.name}
                                </h3>
                                <button onClick={() => !activeDrawRaffle.isProcessing && setActiveDrawRaffle(null)} className="text-white/50 hover:text-white"><X size={24}/></button>
                            </div>

                            <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-4">
                                {/* 1º QUADRO: Regras e Auditoria */}
                                <div className="bg-blue-900/30 p-4 rounded-lg border border-blue-500/30">
                                    <h4 className="font-bold text-sm text-blue-300 mb-1 flex items-center gap-1"><AlertTriangle size={14}/> Regras e Auditoria</h4>
                                    <p className="text-xs text-blue-100/70 leading-relaxed">
                                        O sorteio é realizado de forma 100% aleatória pelo sistema computacional. Cada bilhete comprado representa uma chance matemática exata e igual de ganhar. O processo é imutável e será registrado automaticamente no AcadNews para auditoria pública de todos os colaboradores.
                                    </p>
                                </div>

                                {/* 3º QUADRO: Total de Bilhetes no Globo (Coloquei acima para dar destaque) */}
                                <div className="flex justify-between items-center bg-yellow-500/20 p-4 rounded-lg border border-yellow-500/40">
                                    <span className="font-bold text-sm text-yellow-100 uppercase tracking-widest">Total de Bilhetes no Globo:</span>
                                    <span className="font-black text-3xl text-yellow-400 drop-shadow-md">{activeDrawRaffle.tickets.length}</span>
                                </div>

                                {/* 2º QUADRO: Relação de Participantes */}
                                <div>
                                    <h4 className="font-bold text-sm text-emerald-400 mb-2 flex items-center gap-1"><Users size={14}/> Relação de Concorrentes e Chances</h4>
                                    <div className="border border-white/10 rounded-lg overflow-hidden bg-white/5">
                                        <table className="w-full text-xs text-left">
                                            <thead className="bg-black/40 text-white/60">
                                                <tr>
                                                    <th className="p-3">Colaborador</th>
                                                    <th className="p-3 text-center">Bilhetes Comprados</th>
                                                    <th className="p-3 text-center">Chance de Ganhar</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-white/10">
                                                {(() => {
                                                    // Agrupa os bilhetes por pessoa
                                                    const groups = {};
                                                    activeDrawRaffle.tickets.forEach(t => {
                                                        if (!groups[t.collabId]) groups[t.collabId] = { collabId: t.collabId, collabName: t.collabName, count: 0 };
                                                        groups[t.collabId].count++;
                                                    });
                                                    const groupedTickets = Object.values(groups).sort((a,b) => b.count - a.count);

                                                    return groupedTickets.map(g => {
                                                        const isWinner = activeDrawRaffle.winner?.collabId === g.collabId;
                                                        const chance = ((g.count / activeDrawRaffle.tickets.length) * 100).toFixed(1);
                                                        
                                                        return (
                                                            <tr key={g.collabId} className={`transition-all duration-500 ${isWinner ? 'bg-red-600 text-white scale-[1.02] shadow-lg font-black' : 'hover:bg-white/5'}`}>
                                                                <td className="p-3 flex items-center gap-2 text-sm">
                                                                    {g.collabName} 
                                                                    {isWinner && <span className="animate-bounce text-xl ml-2">🏆 GANHADOR!</span>}
                                                                </td>
                                                                <td className="p-3 text-center font-bold text-yellow-400 text-sm">{g.count}</td>
                                                                <td className="p-3 text-center opacity-70 font-mono">{chance}%</td>
                                                            </tr>
                                                        );
                                                    });
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {/* FOOTER: BOTÕES DE AÇÃO */}
                            <div className="mt-4 pt-4 border-t border-white/20 flex justify-end gap-3 shrink-0">
                                <Button variant="secondary" onClick={() => !activeDrawRaffle.isProcessing && setActiveDrawRaffle(null)} className="bg-transparent text-white border-white/30 hover:bg-white/10">
                                    {activeDrawRaffle.winner ? 'Fechar Painel' : 'Cancelar'}
                                </Button>
                                
                                {!activeDrawRaffle.winner && (
                                    <Button 
                                        disabled={activeDrawRaffle.isProcessing}
                                        onClick={async () => {
                                            setActiveDrawRaffle(prev => ({ ...prev, isProcessing: true }));
                                            
                                            // Efeito de suspense rodando a roleta por 2.5 segundos
                                            setTimeout(async () => {
                                                const winnerTicket = activeDrawRaffle.tickets[Math.floor(Math.random() * activeDrawRaffle.tickets.length)];
                                                setActiveDrawRaffle(prev => ({ ...prev, winner: winnerTicket, isProcessing: false }));

                                                // Gravação silenciosa no Banco de Dados
                                                try {
                                                    const drawId = Date.now();
                                                    await savePrize(sanitizeForFirebase({ ...activeDrawRaffle.prize, quantity: 0, lastWinnerId: winnerTicket.collabId, lastDrawId: drawId }));
                                                    
                                                    await saveNews(sanitizeForFirebase({
                                                        id: `news_raffle_${drawId}`, prizeId: activeDrawRaffle.prize.id, date: new Date().toISOString(), type: 'Urgente',
                                                        title: `🎉 RESULTADO DO SORTEIO: ${activeDrawRaffle.prize.name}!`,
                                                        content: `O grande vencedor da Rifa/Sorteio "${activeDrawRaffle.prize.name}" foi:\n\n🏆 **${winnerTicket.collabName}** 🏆\n\nParabéns! O RH entrará em contato para a entrega do prêmio.\n\n(Transparência: Sorteio automático e auditável realizado entre ${activeDrawRaffle.tickets.length} bilhetes validados pelo sistema).`
                                                    }));

                                                    await saveDist(sanitizeForFirebase({
                                                        id: `dist_raffle_win_${drawId}`, date: todayStr, collabId: winnerTicket.collabId,
                                                        quantity: 0, reason: `Ganhador do Sorteio: ${activeDrawRaffle.prize.name}`, sourceId: `raffle_${activeDrawRaffle.prize.id}`, type: 'rifa_ganha'
                                                    }));
                                                } catch(e) {
                                                    alert("Atenção: O vencedor foi sorteado, mas houve um erro ao sincronizar com a nuvem: " + e.message);
                                                }
                                            }, 2500); 
                                        }} 
                                        className="bg-red-600 hover:bg-red-700 text-white font-black px-8 py-3 h-auto text-sm shadow-[0_0_15px_rgba(239,68,68,0.6)] border-0"
                                    >
                                        {activeDrawRaffle.isProcessing ? '🎲 Rodando a Roleta...' : '🎯 Realizar Sorteio Agora!'}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL DE RESGATE */}
                    {isRedeemModalOpen && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsRedeemModalOpen(false)}>
                            <div className={`p-6 rounded-xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh] ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                    <h3 className="font-bold text-xl flex items-center gap-2 text-orange-500"><Award size={24}/> Selecionar Prêmio</h3>
                                    <button onClick={() => setIsRedeemModalOpen(false)}><X size={20}/></button>
                                </div>
                                
                                <div className={`p-4 rounded-xl border mb-6 grid grid-cols-2 gap-4 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                    <div><span className="text-[10px] uppercase font-bold text-blue-500">Saldo em Coins (R$)</span><div className="text-lg font-bold">{formatMoney(myValFromCoins)}</div></div>
                                    <div><span className="text-[10px] uppercase font-bold text-teal-500">Bônus Igualitário</span><div className="text-lg font-bold">{formatMoney(myValFromEqual)}</div></div>
                                    <div className="col-span-2 border-t pt-2 dark:border-slate-700"><span className="text-[10px] uppercase font-bold text-emerald-600">Total Disponível</span><div className="text-2xl font-black text-emerald-500">{formatMoney(myRealValue)}</div></div>
                                </div>

                                <div className="flex-1 overflow-y-auto custom-scrollbar space-y-3">
                                    <h4 className="text-sm font-bold opacity-70 mb-2">Escolha na Vitrine:</h4>
                                    <p className="text-[10px] text-gray-500 mb-2 italic">Atenção: Ao resgatar, será descontado do seu saldo a quantidade exata de Coins correspondente ao valor do prêmio no momento.</p>
                                    
                                    {prizesDB.filter(p => p.quantity > 0 && (!p.targetPeriod || p.targetPeriod === currentPeriodStr)).map(p => {
                                        const isCoinPrize = !!p.coinsValue;
                                        const canAfford = isCoinPrize ? (myTotalCoins >= Number(p.coinsValue)) : (myRealValue >= Number(p.value) && unitValue > 0);
                                        
                                        return (
                                            <div key={p.id} className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${canAfford ? (isDarkMode ? 'bg-slate-900 border-slate-700 hover:border-orange-500 cursor-pointer' : 'bg-white border-gray-200 hover:border-orange-500 cursor-pointer shadow-sm') : 'opacity-50 grayscale cursor-not-allowed border-gray-100 bg-gray-50 dark:bg-slate-900/50 dark:border-slate-800'}`} 
                                                    onClick={() => { if(canAfford) { p.isRaffle ? handleBuyTicket(p) : handleRedeemPrize(p); } }}>
                                                <div>
                                                    <h5 className={`font-bold text-sm ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{p.name} {p.isRaffle && <span className="ml-1 bg-yellow-100 text-yellow-800 text-[8px] px-1.5 py-0.5 rounded">Rifa</span>}</h5>
                                                    <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Custo: {isCoinPrize ? `${formatCoins(p.coinsValue)} Coins` : formatMoney(p.value)}</p>
                                                </div>
                                                {canAfford ? (
                                                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); p.isRaffle ? handleBuyTicket(p) : handleRedeemPrize(p); }} className={`transition-colors text-white text-xs font-bold px-4 py-2 rounded-lg shadow-md cursor-pointer ${p.isRaffle ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-orange-500 hover:bg-orange-600'}`}>
                                                        {p.isRaffle ? '🎟️ Comprar Bilhete' : 'Quero este!'}
                                                    </button>
                                                ) : (
                                                    <span className="text-[10px] font-bold text-red-500 text-right">Saldo<br/>Insuficiente</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {prizesDB.filter(p => p.quantity > 0 && (!p.targetPeriod || p.targetPeriod === currentPeriodStr)).length === 0 && (
                                        <div className="text-center p-8 opacity-50 italic">Nenhum prêmio extra disponível no catálogo além do dinheiro.</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ABA DE ADMINISTRAÇÃO */}
                    {activeTab === 'admin' && isAdmin && (
                        <div className="space-y-4 mt-2">
                            <div className={`flex border-b overflow-x-auto custom-scrollbar ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                <button onClick={() => setAdminSubTab('caixa')} className={`px-4 py-3 font-bold text-sm whitespace-nowrap border-b-2 transition-colors ${adminSubTab === 'caixa' ? 'border-blue-500 text-blue-500' : 'border-transparent opacity-60'}`}>Controle da Caixinha</button>
                                <button onClick={() => setAdminSubTab('distribuicao')} className={`px-4 py-3 font-bold text-sm whitespace-nowrap border-b-2 transition-colors ${adminSubTab === 'distribuicao' ? 'border-blue-500 text-blue-500' : 'border-transparent opacity-60'}`}>Distribuição Manual</button>
                                <button onClick={() => setAdminSubTab('premiacao')} className={`px-4 py-3 font-bold text-sm whitespace-nowrap border-b-2 transition-colors flex items-center gap-2 ${adminSubTab === 'premiacao' ? 'border-orange-500 text-orange-500' : 'border-transparent opacity-60'}`}><Award size={16}/> Premiação / Resgates</button>
                                <button onClick={() => setAdminSubTab('totalizacao')} className={`px-4 py-3 font-bold text-sm whitespace-nowrap border-b-2 transition-colors ${adminSubTab === 'totalizacao' ? 'border-blue-500 text-blue-500' : 'border-transparent opacity-60'}`}>Totalização</button>
                                <button onClick={() => setAdminSubTab('projetos_admin')} className={`px-4 py-3 font-bold text-sm whitespace-nowrap border-b-2 transition-colors flex items-center gap-2 ${adminSubTab === 'projetos_admin' ? 'border-indigo-500 text-indigo-500' : 'border-transparent opacity-60'}`}><Briefcase size={16}/> Projetos</button>
                                <button onClick={() => setAdminSubTab('config')} className={`px-4 py-3 font-bold text-sm whitespace-nowrap border-b-2 transition-colors flex items-center gap-2 ${adminSubTab === 'config' ? 'border-blue-500 text-blue-500' : 'border-transparent opacity-60'}`}><Settings size={16}/> Configurações</button>
                            </div>

                            {/* SUB-ABA: PREMIAÇÃO E RESGATES */}
                                    {adminSubTab === 'premiacao' && (
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            {/* GESTÃO DO CATÁLOGO */}
                                            <Card darkMode={isDarkMode} className="flex flex-col h-[550px]">
                                                
                                                {/* NOVO CABEÇALHO COM O BOTÃO DE SORTEIO */}
                                                <div className="flex justify-between items-center mb-4 border-b pb-3 dark:border-slate-700">
                                                    <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Catálogo de Prêmios</h3>
                                                    
                                                    <button 
                                                        onClick={() => setIsRaffleModalOpen(true)}
                                                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold shadow-md transition-all text-xs"
                                                    >
                                                        <Gift size={16} /> Sortear Vencedor
                                                    </button>
                                                </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 flex-wrap">
                                            <div className="md:col-span-3">
                                                <label className={`text-[10px] font-bold uppercase mb-1 block ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Nome do Prêmio</label>
                                                <Input value={prizeForm.name} onChange={e=>setPrizeForm({...prizeForm, name:e.target.value})} placeholder="Ex: 🎧 Fone de Ouvido JBL" className={isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : ''}/>
                                            </div>
                                            <div>
                                                <label className={`text-[10px] font-bold uppercase mb-1 block ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Valor Fixo (R$)</label>
                                                <Input type="number" step="0.01" value={prizeForm.value || ''} onChange={e=>setPrizeForm({...prizeForm, value:e.target.value})} placeholder="Ex: 150.00" className={isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : ''}/>
                                            </div>
                                            <div className="flex items-center justify-center pt-5 font-bold text-xs opacity-50 uppercase">OU</div>
                                            <div>
                                                <label className={`text-[10px] font-bold uppercase mb-1 block ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Qtd. AcadCoins</label>
                                                <Input type="number" step="0.1" value={prizeForm.coinsValue || ''} onChange={e=>setPrizeForm({...prizeForm, coinsValue:e.target.value})} placeholder="Ex: 50" className={isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : ''}/>
                                            </div>
                                            <div className="md:col-span-3">
                                                <label className={`text-[10px] font-bold uppercase mb-1 block ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Qtd. Disponível em Estoque</label>
                                                <Input type="number" value={prizeForm.quantity} onChange={e=>setPrizeForm({...prizeForm, quantity:e.target.value})} className={isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : ''}/>
                                            </div>
                                            
                                            <div className="md:col-span-3 flex flex-col gap-2 p-3 rounded-lg border dark:border-slate-700 bg-black/5 dark:bg-white/5">
                                                <label className="flex items-center gap-2 cursor-pointer w-fit">
                                                    <input type="checkbox" checked={prizeForm.isRaffle || false} onChange={e=>setPrizeForm({...prizeForm, isRaffle: e.target.checked})} className="w-4 h-4 text-orange-500"/>
                                                    <span className={`text-xs font-bold uppercase ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>Este item é uma RIFA / SORTEIO</span>
                                                </label>
                                                {prizeForm.isRaffle && (
                                                    <div className="animate-fade-in mt-2 border-t dark:border-slate-600 pt-2">
                                                        <label className={`text-[10px] font-bold uppercase mb-1 block ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Máx. Bilhetes por Pessoa</label>
                                                        <Input type="number" min="1" value={prizeForm.maxTicketsPerUser} onChange={e=>setPrizeForm({...prizeForm, maxTicketsPerUser: e.target.value})} className={`w-32 ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : ''}`} placeholder="Ex: 1"/>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="md:col-span-3">
                                                <label className={`text-[10px] font-bold uppercase mb-1 block ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Período de Validade (Opcional)</label>
                                                <select 
                                                    className={`w-full p-2 border rounded text-xs outline-none ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-gray-300'}`}
                                                    value={prizeForm.targetPeriod}
                                                    onChange={e => setPrizeForm({...prizeForm, targetPeriod: e.target.value})}
                                                >
                                                    <option value="">Sempre Válido (Padrão)</option>
                                                    {availablePeriodsList.map(p => <option key={p} value={p}>{p}</option>)}
                                                </select>
                                            </div>
                                            <div className="md:col-span-3 flex justify-end gap-2 mt-2">
                                                {prizeForm.id && <Button variant="secondary" onClick={() => setPrizeForm({ id: null, name: '', value: '', coinsValue: '', quantity: '', targetPeriod: '', isRaffle: false, maxTicketsPerUser: 1 })} className={isDarkMode ? 'bg-slate-700 text-white border-slate-600' : ''}>Cancelar</Button>}
                                                <Button onClick={() => {
                                                    const hasR = !!prizeForm.value && Number(prizeForm.value) > 0;
                                                    const hasC = !!prizeForm.coinsValue && Number(prizeForm.coinsValue) > 0;
                                                    
                                                    if (!prizeForm.name || !prizeForm.quantity) return alert("Preencha nome e quantidade do estoque.");
                                                    if (hasR && hasC) return alert("Preencha APENAS o Valor (R$) OU a Qtd em Acadcoins, nunca os dois juntos.");
                                                    if (!hasR && !hasC) return alert("Você precisa preencher o Valor (R$) OU a Qtd em Acadcoins.");
                                                    
                                                    savePrize(sanitizeForFirebase({ 
                                                        ...prizeForm, 
                                                        id: prizeForm.id || Date.now().toString(), 
                                                        value: hasR ? parseFloat(prizeForm.value) : null, 
                                                        coinsValue: hasC ? parseFloat(prizeForm.coinsValue) : null,
                                                        quantity: parseInt(prizeForm.quantity), 
                                                        isRaffle: prizeForm.isRaffle || false, 
                                                        maxTicketsPerUser: parseInt(prizeForm.maxTicketsPerUser) || 1 
                                                    }));
                                                    setPrizeForm({ id: null, name: '', value: '', coinsValue: '', quantity: '', targetPeriod: '', isRaffle: false, maxTicketsPerUser: 1 });
                                                }} icon={prizeForm.id ? Save : Plus} className={prizeForm.id ? 'bg-blue-600' : 'bg-orange-500 hover:bg-orange-600'}>{prizeForm.id ? 'Salvar' : 'Adicionar ao Catálogo'}</Button>
                                            </div>
                                        </div>
                                        
                                        <div className={`flex-1 overflow-y-auto border rounded-lg custom-scrollbar ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                            <table className={`w-full text-xs text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                <thead className={`uppercase font-bold sticky top-0 shadow-sm ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>
                                                    <tr><th className="p-2">Item</th><th className="p-2 text-right">Custo</th><th className="p-2 text-center">Qtd</th><th className="p-2 text-center">Ações</th></tr>
                                                </thead>
                                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                    {prizesDB.map((p, idx) => {
                                                        const rowBg = idx % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');
                                                        return (
                                                            <tr key={p.id} className={`${rowBg} ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-orange-50'}`}>
                                                                <td className="p-2 font-bold">{p.name}<div className="text-[9px] font-normal opacity-70">Val: {p.targetPeriod || 'Livre'}</div></td>
                                                                <td className="p-2 text-right font-medium text-emerald-600">{p.coinsValue ? `${p.coinsValue} Coins` : formatMoney(p.value)}</td>
                                                                <td className={`p-2 text-center font-bold ${p.quantity <= 0 ? 'text-red-500' : ''}`}>{p.quantity}</td>
                                                                <td className="p-2 text-center">
                                                                    <div className="flex justify-center gap-1">
                                                                        <button onClick={() => setPrizeForm(p)} className="p-1.5 text-blue-500 hover:bg-blue-100 rounded"><Edit3 size={14}/></button>
                                                                        <button onClick={() => { if(window.confirm('Excluir Prêmio?')) delPrize(p.id) }} className="p-1.5 text-red-500 hover:bg-red-100 rounded"><Trash2 size={14}/></button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                    )})}
                                                    {prizesDB.length === 0 && <tr><td colSpan={4} className="p-8 text-center opacity-50 italic">Nenhum prêmio cadastrado.</td></tr>}
                                                </tbody>
                                            </table>
                                        </div>
                                    </Card>

                                    {/* GESTÃO DE RESGATES COM CANCELAMENTO */}
                                    <Card title="Solicitações de Resgate" darkMode={isDarkMode} className="flex flex-col h-[550px]">
                                        <div className="flex justify-end mb-3">
                                            <Button onClick={exportRedemptionsExcel} icon={FileSpreadsheet} className="bg-green-600 hover:bg-green-700 text-white text-xs py-1.5 h-8">
                                                Exportar Excel
                                            </Button>
                                        </div>
                                        <div className={`flex-1 overflow-y-auto border rounded-lg custom-scrollbar ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                            <table className={`w-full text-xs text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                <thead className={`uppercase font-bold sticky top-0 shadow-sm ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>
                                                    <tr>
                                                        <th className="p-2">Colaborador</th>
                                                        <th className="p-2">Prêmio</th>
                                                        <th className="p-2 text-right">Custo</th>
                                                        <th className="p-2 text-center">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                    {redemptionsDB.sort((a,b) => new Date(b.date) - new Date(a.date)).map((r, idx) => {
                                                        const rowBg = idx % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');
                                                        return (
                                                        <tr key={r.id} className={`${rowBg} ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-orange-50'} ${r.status === 'Cancelado' ? 'opacity-50 grayscale' : ''}`}>
                                                            <td className="p-2">
                                                                <div className="font-bold">{r.collabName}</div>
                                                                <div className="text-[9px] opacity-70">{new Date(r.date).toLocaleDateString('pt-BR')}</div>
                                                            </td>
                                                            <td className="p-2 font-medium">{r.prizeName}</td>
                                                            <td className="p-2 text-right">
                                                                <div className="font-bold text-orange-500">{formatMoney(r.value)}</div>
                                                            </td>
                                                            <td className="p-2 text-center">
                                                                <select 
                                                                    className={`p-1 text-[10px] font-bold rounded uppercase outline-none ${r.status === 'Entregue' ? 'bg-green-100 text-green-700 border-green-200' : r.status === 'Cancelado' ? 'bg-red-100 text-red-700 border-red-200' : 'bg-orange-100 text-orange-700 border-orange-200'}`}
                                                                    value={r.status}
                                                                    onChange={e => handleUpdateRedemptionStatus(r, e.target.value)}
                                                                >
                                                                    <option value="Solicitado">Solicitado</option>
                                                                    <option value="Em Processamento">Em Processamento</option>
                                                                    <option value="Entregue">Entregue</option>
                                                                    <option value="Cancelado">Cancelado</option>
                                                                </select>
                                                            </td>
                                                        </tr>
                                                    )})}
                                                    {redemptionsDB.length === 0 && <tr><td colSpan={4} className="p-8 text-center opacity-50 italic">Nenhum resgate solicitado ainda.</td></tr>}
                                                </tbody>
                                            </table>
                                        </div>
                                    </Card>
                                </div>
                            )}

                            {/* OUTROS BLOCOS ADMINISTRATIVOS MANTIDOS: Caixa, Dist, Tot */}
                            {adminSubTab === 'caixa' && (
                                <Card title="Aportes e Entradas na Caixinha" darkMode={isDarkMode}>
                                    <div className="flex gap-2 mb-4 items-end flex-wrap">
                                        <div className="w-32">
                                            <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Tipo</label>
                                            <select value={caixaForm.type} onChange={e=>setCaixaForm({...caixaForm, type: e.target.value})} className={`w-full p-2 border rounded outline-none text-sm h-10 ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-gray-300'}`}>
                                                <option value="Realizado">Realizado</option>
                                                <option value="Previsto">Previsto</option>
                                            </select>
                                        </div>
                                        <div className="w-32"><label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Data</label><Input type="date" value={caixaForm.date} onChange={e=>setCaixaForm({...caixaForm, date: e.target.value})} className={isDarkMode ? 'invert-calendar bg-slate-900 border-slate-700 text-white' : ''}/></div>
                                        <div className="w-32"><label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Valor (R$)</label><Input type="number" step="0.01" value={caixaForm.amount} onChange={e=>setCaixaForm({...caixaForm, amount: e.target.value})} className={isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : ''}/></div>
                                        <div className="flex-[2] min-w-[200px]"><label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Descrição</label><Input placeholder="Ex: Aporte Mensal" value={caixaForm.description} onChange={e=>setCaixaForm({...caixaForm, description: e.target.value})} className={isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : ''}/></div>
                                        <div className="flex gap-2 h-10">
                                            <Button onClick={handleAddCaixa} icon={caixaForm.id ? Save : Plus}>{caixaForm.id ? 'Salvar' : 'Adicionar'}</Button>
                                            {caixaForm.id && <Button variant="secondary" onClick={handleCancelCaixa}>Cancelar</Button>}
                                        </div>
                                    </div>
                                    <div className={`max-h-80 overflow-y-auto custom-scrollbar border rounded-lg ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                        <table className={`w-full text-sm text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                            <thead className={`text-xs uppercase font-bold sticky top-0 ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>
                                                <tr><th className="p-3">Tipo</th><th className="p-3">Data</th><th className="p-3">Descrição</th><th className="p-3 text-right">Valor</th><th className="p-3 text-center">Ações</th></tr>
                                            </thead>
                                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                {caixaDB.sort((a,b)=>new Date(b.date)-new Date(a.date)).map((c, i) => {
                                                    const isCancelado = c.type === 'Cancelado' || c.status === 'Cancelado';
                                                    const rowBg = i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');
                                                    
                                                    return (
                                                    <tr key={c.id} className={`${rowBg} ${c.type === 'Previsto' ? 'border-l-4 border-yellow-500' : ''} ${isCancelado ? 'opacity-60 text-gray-500 bg-gray-200 dark:bg-slate-900 line-through grayscale' : ''} transition-colors`}>
                                                        <td className="p-3 font-bold text-[10px] uppercase">
                                                            <span className={`px-2 py-1 rounded ${isCancelado ? 'bg-gray-300 text-gray-700 dark:bg-gray-700 dark:text-gray-300 no-underline inline-block' : c.type === 'Previsto' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>{c.type || 'Realizado'}</span>
                                                        </td>
                                                        <td className="p-3 font-mono text-xs">{new Date(c.date + 'T00:00:00').toLocaleDateString('pt-BR')}</td>
                                                        <td className="p-3">{c.description} {!isCancelado && c.date > todayStr && <span className="ml-2 text-[9px] bg-indigo-100 text-indigo-800 px-1 rounded border border-indigo-300 no-underline inline-block">Futuro</span>}</td>
                                                        <td className={`p-3 text-right font-bold ${isCancelado ? 'text-gray-500' : parseFloat(c.amount) < 0 ? '!text-red-500' : '!text-blue-500'}`}>
                                                            {parseFloat(c.amount) > 0 ? '+' : ''}{formatMoney(parseFloat(c.amount))}
                                                        </td>
                                                        <td className="p-3 text-center">
                                                            <div className="flex justify-center gap-2 items-center">
                                                                {/* NOVO BOTÃO DE EFETIVAR/CANCELAR */}
                                                                {(c.type === 'Previsto' || c.status === 'Previsto') && !isCancelado && (
                                                                    <button 
                                                                        onClick={() => { setActionBoxItem(c); setCancelReason(""); }}
                                                                        className="p-1 text-green-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-full transition-colors"
                                                                        title="Finalizar ou Cancelar Registro"
                                                                    >
                                                                        <CheckCircle size={20} />
                                                                    </button>
                                                                )}
                                                                <button onClick={() => handleEditCaixa(c)} className="text-blue-500 p-1.5 hover:bg-blue-100 rounded"><Edit3 size={16}/></button>
                                                                <button onClick={()=> {if(window.confirm('Excluir?')) delCaixa(c.id)}} className="text-red-500 p-1.5 hover:bg-red-100 rounded"><Trash2 size={16}/></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </Card>
                            )}

                            {adminSubTab === 'distribuicao' && (
                                <Card title={`Distribuição Manual de ${currencyName}`} darkMode={isDarkMode}>
                                    <div className="flex gap-2 mb-4 items-end flex-wrap">
                                        <div className="w-32"><label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Data</label><Input type="date" value={distForm.date} onChange={e=>setDistForm({...distForm, date: e.target.value})} className={isDarkMode ? 'invert-calendar bg-slate-900 border-slate-700 text-white' : ''}/></div>
                                        <div className="flex-[2] min-w-[200px]"><label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Colaborador Elegível</label>
                                            <select className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : 'border-gray-300'}`} value={distForm.collabId} onChange={e=>setDistForm({...distForm, collabId: e.target.value})}>
                                                <option value="">Selecione...</option>
                                                {data.collaborators.filter(c => {
                                                    if (c.participatesAcadCoins === false) return false;
                                                    const start = c.acadCoinsStartDate;
                                                    if (!start || start > distForm.date) return false;
                                                    const end = c.acadCoinsEndDate;
                                                    if (end && end < distForm.date) return false;
                                                    return true;
                                                }).sort((a,b) => a.name.localeCompare(b.name)).map(c => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="w-24"><label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Qtd</label><Input type="number" step="0.1" value={distForm.quantity} onChange={e=>setDistForm({...distForm, quantity: e.target.value})} className={isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : ''}/></div>
                                        <div className="flex-[2] min-w-[200px]"><label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Motivo</label><Input placeholder="Ex: Prêmio Meta Batida" value={distForm.reason} onChange={e=>setDistForm({...distForm, reason: e.target.value})} className={isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : ''}/></div>
                                        <div className="flex gap-2 h-10">
                                            <Button onClick={handleAddDist} icon={distForm.id ? Save : Plus}>{distForm.id ? 'Salvar' : 'Distribuir'}</Button>
                                            {distForm.id && <Button variant="secondary" onClick={handleCancelDist}>Cancelar</Button>}
                                        </div>
                                    </div>
                                    <div className={`max-h-80 overflow-y-auto custom-scrollbar border rounded-lg ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                        <table className={`w-full text-sm text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                            <thead className={`text-xs uppercase font-bold sticky top-0 ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>
                                                <tr><th className="p-3">Data</th><th className="p-3">Colaborador</th><th className="p-3">Motivo</th><th className="p-3 text-right">Qtd</th><th className="p-3 text-center">Ações</th></tr>
                                            </thead>
                                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                {distDB.sort((a,b)=>new Date(b.date)-new Date(a.date)).map((d, i) => {
                                                    const cProfile = data.collaborators.find(c=>c.id===d.collabId);
                                                    const start = cProfile?.acadCoinsStartDate;
                                                    const isBurned = cProfile && (cProfile.participatesAcadCoins === false || !start || start > todayStr || (cProfile.acadCoinsEndDate && cProfile.acadCoinsEndDate < todayStr));
                                                    return (
                                                        <tr
                                                            key={d.id}
                                                            title={hasDistributionCalcMemory(d) ? getDistributionCalcMemory(d) : d.reason}
                                                            className={`${i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50')} transition-colors ${hasDistributionCalcMemory(d) ? 'cursor-help' : ''}`}
                                                        >
                                                            <td className="p-3 font-mono text-xs">{new Date(d.date + 'T00:00:00').toLocaleDateString('pt-BR')}</td>
                                                            <td className="p-3 font-bold">{cProfile?.name || 'Desconhecido'} {isBurned && <span className="ml-2 text-[9px] bg-red-100 text-red-800 px-1 rounded border border-red-300 uppercase">Inativo</span>}</td>
                                                            <td className="p-3">
                                                                <span className={`${hasDistributionCalcMemory(d) ? 'underline decoration-dotted underline-offset-4' : ''}`}>
                                                                    {d.reason}
                                                                </span>
                                                            </td>
                                                            <td className={`p-3 text-right font-bold ${isBurned ? 'text-gray-400 line-through' : 'text-blue-500'}`}>{d.quantity > 0 ? '+' : ''}{parseFloat(d.quantity).toFixed(2)}</td>
                                                            <td className="p-3 text-center"><div className="flex justify-center gap-2"><button onClick={() => handleEditDist(d)} className="text-blue-500 p-1.5 hover:bg-blue-100 rounded"><Edit3 size={16}/></button><button onClick={()=> {if(window.confirm('Excluir?')) delDist(d.id)}} className="text-red-500 p-1.5 hover:bg-red-100 rounded"><Trash2 size={16}/></button></div></td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </Card>
                            )}

                            {adminSubTab === 'totalizacao' && (
                                <Card title="Relatórios Analíticos" darkMode={isDarkMode}>
                                    
                                    {/* BOTÕES DE ABAS DO RELATÓRIO */}
                                    <div className="flex gap-2 mb-4 border-b pb-4 dark:border-slate-700 overflow-x-auto custom-scrollbar">
                                        <button onClick={() => setTotReportType('saldos')} className={`px-4 py-2 font-bold text-xs rounded-lg transition-colors whitespace-nowrap ${totReportType === 'saldos' ? 'bg-blue-600 text-white shadow-md' : isDarkMode ? 'bg-slate-800 text-slate-400 hover:bg-slate-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Saldos Financeiros e Caixinha</button>
                                        <button onClick={() => setTotReportType('indicacoes')} className={`px-4 py-2 font-bold text-xs rounded-lg transition-colors whitespace-nowrap ${totReportType === 'indicacoes' ? 'bg-cyan-600 text-white shadow-md' : isDarkMode ? 'bg-slate-800 text-slate-400 hover:bg-slate-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Apuração de Indicações (Reconhecimento)</button>
                                    </div>

                                    <div className="flex gap-3 mb-4 items-end flex-wrap border-b pb-4 dark:border-slate-700">
                                        <div className="w-48"><label className={`text-[10px] font-bold uppercase opacity-70 block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Período de Apuração</label>
                                            <select className={`w-full p-2 border rounded text-xs outline-none ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : ''}`} value={totPeriod} onChange={e=>setTotPeriod(e.target.value)}>
                                                <option value="">Todos os Períodos</option>
                                                {totalizacaoData.periods.map(p => <option key={p} value={p}>{p}</option>)}
                                            </select>
                                        </div>
                                        <div className="w-48"><label className={`text-[10px] font-bold uppercase opacity-70 block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Departamento</label>
                                            <select className={`w-full p-2 border rounded text-xs outline-none ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : ''}`} value={totDept} onChange={e=>setTotDept(e.target.value)}>
                                                <option value="">Todos</option>
                                                {data.departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                            </select>
                                        </div>
                                        <div className="flex-1 min-w-[200px] relative"><label className={`text-[10px] font-bold uppercase opacity-70 block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Buscar Colaborador</label>
                                            <Search className="absolute left-2 top-7 text-gray-400" size={14}/>
                                            <Input className={`pl-8 text-xs h-8 ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : ''}`} placeholder="Nome ou email..." value={totSearch} onChange={e=>setTotSearch(e.target.value)} />
                                        </div>
                                        <div className="shrink-0 h-8 flex items-end">
                                            <Button onClick={() => setIsReportModalOpen(true)} className="bg-purple-600 hover:bg-purple-700 text-white shadow-md border-0 h-8 text-xs px-4" icon={FileText}>
                                                Relatório Avançado
                                            </Button>
                                        </div>
                                    </div>

                                    <div className={`max-h-[500px] overflow-y-auto custom-scrollbar border rounded-lg ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                        
                                        {/* TABELA DE SALDOS FINANCEIROS */}
                                        {totReportType === 'saldos' && (
                                            <table className={`w-full text-sm text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                <thead className={`text-[10px] uppercase font-bold sticky top-0 shadow-sm z-10 ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>
                                                    <tr>
                                                        <th className="p-3">Colaborador</th>
                                                        <th className="p-3">E-mail</th>
                                                        <th className="p-3">Depto</th>
                                                        <th className="p-3 text-center">Período</th>
                                                        <th className="p-3 text-right">Qtd {currencyName}</th>
                                                        <th className="p-3 text-right">Cotação Un.</th>
                                                        <th className="p-3 text-right text-blue-500">R$ s/{currencyName} Rec.</th>
                                                        <th className="p-3 text-right text-teal-500">R$ Bônus Igual.</th>
                                                        <th className="p-3 text-right text-emerald-600">Total a Receber</th>
                                                    </tr>
                                                </thead>
                                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                    {totalizacaoData.rows.map((r, i) => (
                                                        <tr key={`${r.collabId}_${r.period}`} className={`${i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50')} hover:brightness-95 transition-colors`}>
                                                            <td className="p-3 font-bold truncate max-w-[150px]" title={r.name}>{r.name}</td>
                                                            <td className="p-3 text-[10px] opacity-70 truncate max-w-[150px]">{r.email}</td>
                                                            <td className="p-3 text-xs">{r.deptName}</td>
                                                            <td className="p-3 text-center font-mono text-xs">{r.period}</td>
                                                            <td className="p-3 text-right font-bold">{formatCoins(r.totalCoins)}</td>
                                                            <td className="p-3 text-right text-xs opacity-70">{formatQuote(r.unitValue)}</td>
                                                            <td className="p-3 text-right text-blue-500 font-medium">{formatMoney(r.valProportional)}</td>
                                                            <td className="p-3 text-right text-teal-500 font-medium">{formatMoney(r.valEqual)}</td>
                                                            <td className="p-3 text-right font-black text-emerald-600">{formatMoney(r.totalValue)}</td>
                                                        </tr>
                                                    ))}
                                                    {totalizacaoData.rows.length === 0 && <tr><td colSpan="9" className="p-8 text-center italic opacity-60">Nenhum dado encontrado com os filtros atuais.</td></tr>}
                                                </tbody>
                                                <tfoot className={`sticky bottom-0 font-bold z-10 ${isDarkMode ? 'bg-slate-900 text-white' : 'bg-gray-200 text-gray-800'}`}>
                                                    <tr>
                                                        <td colSpan="4" className="p-3 text-right uppercase text-xs">Total Filtrado:</td>
                                                        <td className="p-3 text-right">{formatCoins(totalizacaoData.rows.reduce((a,c)=>a+c.totalCoins, 0))}</td>
                                                        <td className="p-3 text-right">-</td>
                                                        <td className="p-3 text-right text-blue-600">{formatMoney(totalizacaoData.rows.reduce((a,c)=>a+c.valProportional, 0))}</td>
                                                        <td className="p-3 text-right text-teal-600">{formatMoney(totalizacaoData.rows.reduce((a,c)=>a+c.valEqual, 0))}</td>
                                                        <td className="p-3 text-right text-emerald-600 text-base">{formatMoney(totalizacaoData.rows.reduce((a,c)=>a+c.totalValue, 0))}</td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        )}

                                        {/* TABELA DE APURAÇÃO DE INDICAÇÕES */}
                                        {totReportType === 'indicacoes' && (
                                            <table className={`w-full text-sm text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                <thead className={`text-[10px] uppercase font-bold sticky top-0 shadow-sm z-10 ${isDarkMode ? 'bg-slate-900 text-cyan-400' : 'bg-cyan-50 text-cyan-700'}`}>
                                                    <tr>
                                                        <th className="p-3 w-48">Quem Recebeu</th>
                                                        <th className="p-3 text-center">Período</th>
                                                        <th className="p-3 text-center">Qtd. Indicações</th>
                                                        <th className="p-3">Detalhes (Quem indicou e Motivo)</th>
                                                        <th className="p-3 text-center w-32">Recebeu Moedas?</th>
                                                        <th className="p-3 w-48">Justificativa / Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                    {recognitionReportData.map((r, i) => (
                                                        <tr key={`${r.collabId}_${r.period}`} className={`${i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50')} hover:brightness-95 transition-colors`}>
                                                            <td className="p-3 font-bold">{r.collabName}</td>
                                                            <td className="p-3 text-center font-mono text-xs">{r.period}</td>
                                                            <td className="p-3 text-center font-black text-lg">{r.total}</td>
                                                            <td className="p-3">
                                                                <div className="flex flex-col gap-1">
                                                                    {r.indications.map((ind, idx) => (
                                                                        <div key={idx} className={`text-[10px] p-1.5 rounded border ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-200'}`}>
                                                                            <span className="font-bold text-cyan-600 dark:text-cyan-400">{ind.senderName}</span> 
                                                                            {ind.isOutside && <span className="ml-1 px-1 bg-purple-100 text-purple-700 rounded text-[8px] uppercase font-bold">Outro Setor</span>}
                                                                            <span className="block mt-0.5 opacity-80">{ind.reason}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </td>
                                                            <td className="p-3 text-center">
                                                                <span className={`px-2 py-1 rounded-md text-[10px] font-black uppercase ${r.receivedCoins ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-red-100 text-red-700 border border-red-300'}`}>
                                                                    {r.status}
                                                                </span>
                                                            </td>
                                                            <td className="p-3 text-[10px] italic opacity-80 leading-tight">
                                                                {r.justification}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {recognitionReportData.length === 0 && <tr><td colSpan="6" className="p-8 text-center italic opacity-60">Nenhuma indicação processada nos filtros atuais.</td></tr>}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </Card>
                            )}

                            {/* CONFIGURAÇÕES - DASHBOARD DE CARDS E TELAS */}
                            {adminSubTab === 'config' && (
                                <Card className="flex flex-col h-[75vh] min-h-[500px] overflow-hidden" darkMode={isDarkMode}>
                                    
                                    {!activeConfigScreen ? (
                                        // ==========================================
                                        // TELA 1: GRID DE CARDS (MENU)
                                        // ==========================================
                                        <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
                                            <div className="mb-4">
                                                <h3 className={`font-black text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Configurações Globais</h3>
                                                <p className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Selecione um módulo abaixo para configurar as regras e salvar individualmente.</p>
                                            </div>

                                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                                                {/* CARD 1: Implantação (Azul Claro para Azul Médio) */}
                                                <div onClick={() => setActiveConfigScreen('implantacao')} className="cursor-pointer p-4 rounded-xl shadow-md border-0 transition-all hover:scale-105 flex flex-col items-center justify-center text-center min-h-[105px] bg-gradient-to-br from-blue-400 to-blue-600 text-white">
                                                    <Settings size={26} className="mb-2 text-white"/>
                                                    <h4 className="font-bold text-xs sm:text-sm leading-tight text-white drop-shadow-sm">Implantação e Nome</h4>
                                                </div>

                                                {/* CARD 2: Apuração (Roxo Vibrante) */}
                                                <div onClick={() => setActiveConfigScreen('apuracao')} className="cursor-pointer p-4 rounded-xl shadow-md border-0 transition-all hover:scale-105 flex flex-col items-center justify-center text-center min-h-[105px] bg-gradient-to-br from-purple-400 to-purple-600 text-white">
                                                    <Clock size={26} className="mb-2 text-white"/>
                                                    <h4 className="font-bold text-xs sm:text-sm leading-tight text-white drop-shadow-sm">Período e Resgates</h4>
                                                </div>

                                                {/* CARD 3: Caixinha (Verde Água / Turquesa) */}
                                                <div onClick={() => setActiveConfigScreen('caixinha')} className="cursor-pointer p-4 rounded-xl shadow-md border-0 transition-all hover:scale-105 flex flex-col items-center justify-center text-center min-h-[105px] bg-gradient-to-br from-teal-400 to-teal-600 text-white">
                                                    <BadgeDollarSign size={26} className="mb-2 text-white"/>
                                                    <h4 className="font-bold text-xs sm:text-sm leading-tight text-white drop-shadow-sm">Distribuição Caixinha</h4>
                                                </div>

                                                {/* CARD 4: Avaliação (Amarelo / Laranja Claro) */}
                                                <div onClick={() => setActiveConfigScreen('avaliacao')} className="cursor-pointer p-4 rounded-xl shadow-md border-0 transition-all hover:scale-105 flex flex-col items-center justify-center text-center min-h-[105px] bg-gradient-to-br from-amber-400 to-orange-500 text-white">
                                                    <ClipboardCheck size={26} className="mb-2 text-white"/>
                                                    <h4 className="font-bold text-xs sm:text-sm leading-tight text-white drop-shadow-sm">AEC - Acompanhamento de Evolução Continuada</h4>
                                                </div>

                                                {/* CARD 5: Treinamentos (Rosa / Magenta) */}
                                                <div onClick={() => setActiveConfigScreen('treinamentos')} className="cursor-pointer p-4 rounded-xl shadow-md border-0 transition-all hover:scale-105 flex flex-col items-center justify-center text-center min-h-[105px] bg-gradient-to-br from-pink-400 to-rose-500 text-white">
                                                    <BookOpen size={26} className="mb-2 text-white"/>
                                                    <h4 className="font-bold text-xs sm:text-sm leading-tight text-white drop-shadow-sm">Cursos e Treinamentos</h4>
                                                </div>

                                                {/* CARD 6: Projetos Sociais (Índigo / Azul Royal) */}
                                                <div onClick={() => setActiveConfigScreen('sociais')} className="cursor-pointer p-4 rounded-xl shadow-md border-0 transition-all hover:scale-105 flex flex-col items-center justify-center text-center min-h-[105px] bg-gradient-to-br from-indigo-400 to-indigo-600 text-white">
                                                    <Heart size={26} className="mb-2 text-white"/>
                                                    <h4 className="font-bold text-xs sm:text-sm leading-tight text-white drop-shadow-sm">Projetos Sociais</h4>
                                                </div>

                                                {/* CARD 7: Distribuição Regular (Vermelho / Laranja Escuro) */}
                                                <div onClick={() => setActiveConfigScreen('padrao')} className="cursor-pointer p-4 rounded-xl shadow-md border-0 transition-all hover:scale-105 flex flex-col items-center justify-center text-center min-h-[105px] bg-gradient-to-br from-red-500 to-orange-600 text-white">
                                                    <Gift size={26} className="mb-2 text-white"/>
                                                    <h4 className="font-bold text-xs sm:text-sm leading-tight text-white drop-shadow-sm">Distribuição Regular</h4>
                                                </div>

                                                {/* CARD 8: NOVO - ABSENTEÍSMO (Verde Esmeralda) */}
                                                <div onClick={() => setActiveConfigScreen('absenteismo')} className="cursor-pointer p-4 rounded-xl shadow-md border-0 transition-all hover:scale-105 flex flex-col items-center justify-center text-center min-h-[105px] bg-gradient-to-br from-emerald-400 to-emerald-600 text-white">
                                                    <Clock size={26} className="mb-2 text-white"/>
                                                    <h4 className="font-bold text-xs sm:text-sm leading-tight text-white drop-shadow-sm">Absenteísmo e Ponto</h4>
                                                </div>

                                                {/* CARD 9: RECONHECER COLEGA */}
                                                <div onClick={() => setActiveConfigScreen('reconhecimento')} className="cursor-pointer p-4 rounded-xl shadow-md border-0 transition-all hover:scale-105 flex flex-col items-center justify-center text-center min-h-[105px] bg-gradient-to-br from-cyan-400 to-blue-500 text-white">
                                                    <Award size={26} className="mb-2 text-white"/>
                                                    <h4 className="font-bold text-xs sm:text-sm leading-tight text-white drop-shadow-sm">Reconhecimento</h4>
                                                </div>

                                                {/* CARD 10: PRESENTEAR COLEGA */}
                                                <div onClick={() => setActiveConfigScreen('presentear')} className="cursor-pointer p-4 rounded-xl shadow-md border-0 transition-all hover:scale-105 flex flex-col items-center justify-center text-center min-h-[105px] bg-gradient-to-br from-fuchsia-400 to-purple-600 text-white">
                                                    <Gift size={26} className="mb-2 text-white"/>
                                                    <h4 className="font-bold text-xs sm:text-sm leading-tight text-white drop-shadow-sm">Presentear</h4>
                                                </div>

                                                {/* CARD 11: PROJETOS */}
                                                <div onClick={() => setActiveConfigScreen('projetos_config')} className="cursor-pointer p-4 rounded-xl shadow-md border-0 transition-all hover:scale-105 flex flex-col items-center justify-center text-center min-h-[105px] bg-gradient-to-br from-indigo-500 to-blue-700 text-white">
                                                    <Briefcase size={26} className="mb-2 text-white"/>
                                                    <h4 className="font-bold text-xs sm:text-sm leading-tight text-white drop-shadow-sm">Projetos</h4>
                                                </div>

                                                {/* CARD 11.5: IDEIAS E SUGESTÕES */}
                                                <div onClick={() => setActiveConfigScreen('ideias')} className="cursor-pointer p-4 rounded-xl shadow-md border-0 transition-all hover:scale-105 flex flex-col items-center justify-center text-center min-h-[105px] bg-gradient-to-br from-amber-400 to-yellow-600 text-white">
                                                    <span className="text-3xl mb-2 drop-shadow-sm">💡</span>
                                                    <h4 className="font-bold text-xs sm:text-sm leading-tight text-white drop-shadow-sm">Ideias e Sugestões</h4>
                                                </div>

                                                {/* CARD 12: ACADNEWS & GAMIFICAÇÃO */}
                                                <div onClick={() => setActiveConfigScreen('acadnews')} className="cursor-pointer p-4 rounded-xl shadow-md border-0 transition-all hover:scale-105 flex flex-col items-center justify-center text-center min-h-[105px] bg-gradient-to-br from-yellow-400 to-orange-500 text-white">
                                                    <Globe size={26} className="mb-2 text-white"/>
                                                    <h4 className="font-bold text-xs sm:text-sm leading-tight text-white drop-shadow-sm">AcadNews & Gamificação</h4>
                                                </div>

                                                {/* CARD 13: NÍVEIS DA CAIXINHA */}
                                                <div onClick={() => setActiveConfigScreen('niveisCaixa')} className="cursor-pointer p-4 rounded-xl shadow-md border-0 transition-all hover:scale-105 flex flex-col items-center justify-center text-center min-h-[105px] bg-gradient-to-br from-green-500 to-emerald-600 text-white">
                                                    <TrendingUp size={26} className="mb-2 text-white"/>
                                                    <h4 className="font-bold text-xs sm:text-sm leading-tight text-white drop-shadow-sm">Níveis da Caixinha</h4>
                                                </div>

                                                {/* CARD 14: CULTURA ACAD */}
                                                <div onClick={() => setActiveConfigScreen('cultura')} className="cursor-pointer p-4 rounded-xl shadow-md border-0 transition-all hover:scale-105 flex flex-col items-center justify-center text-center min-h-[105px] bg-gradient-to-br from-lime-500 to-green-600 text-white">
                                                    <BookOpen size={26} className="mb-2 text-white"/>
                                                    <h4 className="font-bold text-xs sm:text-sm leading-tight text-white drop-shadow-sm">Cultura Acad</h4>
                                                </div>

                                            </div>
                                        </div>
                                ) : (
                                        // ==========================================
                                        // TELA 2: FORMULÁRIO ESPECÍFICO ABERTO
                                        // ==========================================
                                        <div className="flex flex-col h-full min-h-0 animate-fade-in">
                                            
                                            {/* Cabeçalho da Tela de Detalhe (Botões Voltar e Salvar) */}
                                            <div className={`flex justify-between items-center p-4 border-b shrink-0 shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                <Button variant="secondary" onClick={() => { setActiveConfigScreen(null); setIsConfigModalOpen(false); }} className={isDarkMode ? 'bg-slate-700 text-white border-slate-600 hover:bg-slate-600' : ''}>
                                                    &larr; Voltar ao Menu
                                                </Button>
                                                
                                                <h3 className={`font-black hidden sm:block ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                                    Editando Configurações
                                                </h3>

                                                <Button 
                                                    onClick={async () => { 
                                                        try {
                                                            // Garante que o estado local com todos os históricos seja persistido
                                                            const payload = sanitizeForFirebase(localConfig);
                                                            if (!payload.id) payload.id = 'global';
                                                            await saveCoinConfig(payload);
                                                            
                                                            try { alert("Configurações de Treinamentos e Globais gravadas com sucesso!");
                                                            } catch(e) { console.log("Salvo com sucesso!"); }
                                                            setActiveConfigScreen(null);
                                                        } catch (err) {
                                                            try { alert("Erro ao salvar: " + err.message);
                                                            } catch(e) { console.error("Erro ao salvar", err); }
                                                        }
                                                    }}
                                                    icon={Save} 
                                                    className="bg-green-600 hover:bg-green-700 text-white shadow-md border-0"
                                                >
                                                Gravar Modificações
                                            </Button>
                                            </div>

                                            {/* Área de Conteúdo (Scrolável) */}
                                            <div className="flex-1 overflow-y-auto custom-scrollbar p-4 pb-10 min-h-0">
                                                
                                                {/* TELA: IMPLANTAÇÃO */}
                                                {activeConfigScreen === 'implantacao' && (
                                                    <div className={`p-4 grid grid-cols-1 md:grid-cols-2 gap-4 rounded-xl border ${isDarkMode ? 'bg-slate-900/50 border-blue-800/50' : 'bg-white border-blue-200'}`}>
                                                        <div>
                                                            <label className={`text-[11px] font-bold block mb-1 uppercase ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>Data de Implantação do Programa</label>
                                                            <input type="date" value={localConfig.startDate || ''} onChange={e => setLocalConfig({...localConfig, startDate: e.target.value})} className={`w-full p-2.5 border rounded-lg text-sm font-bold outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white invert-calendar' : 'bg-white border-gray-300'}`}/>
                                                        </div>
                                                        <div>
                                                            <label className={`text-[11px] font-bold block mb-1 uppercase ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>Nome da Moeda Virtual</label>
                                                            <input type="text" value={localConfig.currencyName || 'AcadCoins'} onChange={e => setLocalConfig({...localConfig, currencyName: e.target.value})} className={`w-full p-2.5 border rounded-lg text-sm font-bold outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-300'}`}/>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* TELA: APURAÇÃO E RESGATE COM HISTÓRICO */}
                                                {activeConfigScreen === 'apuracao' && (
                                                    <div className={`p-4 rounded-xl border animate-fade-in ${isDarkMode ? 'bg-slate-900/50 border-orange-800/50' : 'bg-white border-orange-200'}`}>
                                                        <div className="flex justify-between items-center mb-4">
                                                            <h4 className={`font-black uppercase tracking-wide flex items-center gap-2 ${isDarkMode ? 'text-orange-400' : 'text-orange-700'}`}><Clock size={18}/> Período e Resgates</h4>
                                                            <Button onClick={() => { setRuleForms({...ruleForms, apuracao: { id: null, validFrom: new Date().toISOString().split('T')[0], validTo: '', paymentPeriod: 'mes', cutoffDays: '', paymentDaysOffset: '', redemptionStartDay: '', redemptionStartTime: '10:00', redemptionEndDay: '', redemptionEndTime: '10:00' }}); setIsConfigModalOpen(true); }} icon={Plus} className="bg-orange-600 hover:bg-orange-700 text-white border-0 shadow-md">Novo Registro</Button>
                                                        </div>
                                                        
                                                        {renderConfigHistoryTable('apuracao', ['Período', 'Corte', 'Resgate'], (r) => (
                                                            <>
                                                                <td className="p-3 capitalize">{r.paymentPeriod}</td>
                                                                <td className="p-3">{r.cutoffDays} dias</td>
                                                                <td className="p-3 opacity-80 text-[10px]">Dia {r.redemptionStartDay} até {r.redemptionEndDay}</td>
                                                            </>
                                                        ))}

                                                        {isConfigModalOpen && (
                                                            <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsConfigModalOpen(false)}>
                                                                <div className={`p-6 rounded-xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto custom-scrollbar border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                                                    <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                                                        <h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}><Clock size={20}/> {ruleForms.apuracao.id ? 'Editar Regra de Apuração' : 'Nova Regra de Apuração'}</h3>
                                                                        <button onClick={() => setIsConfigModalOpen(false)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                                                                    </div>
                                                                    <div className="space-y-4">
                                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b pb-4 dark:border-slate-700">
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data Início (Validade)</label><input type="date" value={ruleForms.apuracao.validFrom} onChange={e=>setRuleForms({...ruleForms, apuracao: {...ruleForms.apuracao, validFrom: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white'}`}/></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data Fim (Opcional)</label><input type="date" value={ruleForms.apuracao.validTo} onChange={e=>setRuleForms({...ruleForms, apuracao: {...ruleForms.apuracao, validTo: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white'}`}/></div>
                                                                        </div>
                                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Período Apuração Base</label><select value={ruleForms.apuracao.paymentPeriod} onChange={e=>setRuleForms({...ruleForms, apuracao: {...ruleForms.apuracao, paymentPeriod: e.target.value}})} className={`w-full p-2.5 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`}><option value="mes">Mensal</option><option value="trimestre">Trimestral</option><option value="semestre">Semestral</option><option value="ano">Anual</option></select></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data de Corte (Atraso)</label><div className="flex items-center gap-2"><input type="number" value={ruleForms.apuracao.cutoffDays} onChange={e=>setRuleForms({...ruleForms, apuracao: {...ruleForms.apuracao, cutoffDays: e.target.value}})} className={`w-full p-2.5 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`}/><span className="text-[10px] font-bold opacity-70">dias</span></div></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Dias para Pagamento</label><div className="flex items-center gap-2"><input type="number" value={ruleForms.apuracao.paymentDaysOffset} onChange={e=>setRuleForms({...ruleForms, apuracao: {...ruleForms.apuracao, paymentDaysOffset: e.target.value}})} className={`w-full p-2.5 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`}/><span className="text-[10px] font-bold opacity-70">dias</span></div></div>
                                                                        </div>
                                                                        <h4 className="text-[11px] font-black mt-4 mb-2 uppercase tracking-wide opacity-70">Janela de Resgate de Prêmios (Catálogo)</h4>
                                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Dia Início</label><input type="number" value={ruleForms.apuracao.redemptionStartDay} onChange={e=>setRuleForms({...ruleForms, apuracao: {...ruleForms.apuracao, redemptionStartDay: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`}/></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Hora Início</label><input type="time" value={ruleForms.apuracao.redemptionStartTime} onChange={e=>setRuleForms({...ruleForms, apuracao: {...ruleForms.apuracao, redemptionStartTime: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`}/></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Dia Fim</label><input type="number" value={ruleForms.apuracao.redemptionEndDay} onChange={e=>setRuleForms({...ruleForms, apuracao: {...ruleForms.apuracao, redemptionEndDay: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`}/></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Hora Fim</label><input type="time" value={ruleForms.apuracao.redemptionEndTime} onChange={e=>setRuleForms({...ruleForms, apuracao: {...ruleForms.apuracao, redemptionEndTime: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`}/></div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex justify-end gap-2 mt-6 pt-4 border-t dark:border-slate-700">
                                                                        <Button variant="secondary" onClick={() => setIsConfigModalOpen(false)}>Cancelar</Button>
                                                                        <Button onClick={() => { handleSaveRule('apuracao'); setIsConfigModalOpen(false); }} icon={Save} className="bg-orange-600 hover:bg-orange-700 text-white border-0 shadow-md">Salvar Regra</Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* TELA: CAIXINHA COM HISTÓRICO */}
                                                {activeConfigScreen === 'caixinha' && (
                                                    <div className={`p-4 rounded-xl border animate-fade-in ${isDarkMode ? 'bg-slate-900/50 border-emerald-800/50' : 'bg-white border-emerald-200'}`}>
                                                        <div className="flex justify-between items-center mb-4">
                                                            <h4 className={`font-black uppercase tracking-wide flex items-center gap-2 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}><BadgeDollarSign size={18}/> Distribuição Caixinha</h4>
                                                            <Button onClick={() => { setRuleForms({...ruleForms, caixinha: { id: null, validFrom: new Date().toISOString().split('T')[0], validTo: '', percProportional: '', percEqual: '', percCompany: '' }}); setIsConfigModalOpen(true); }} icon={Plus} className="bg-emerald-600 hover:bg-emerald-700 text-white border-0 shadow-md">Novo Registro</Button>
                                                        </div>

                                                        {renderConfigHistoryTable('caixinha', ['% Moedas', '% Igualitária', '% Empresa'], (r) => (
                                                            <>
                                                                <td className="p-3 font-bold text-emerald-600">{r.percProportional}%</td>
                                                                <td className="p-3 font-bold text-teal-600">{r.percEqual}%</td>
                                                                <td className="p-3 opacity-70">{r.percCompany}%</td>
                                                            </>
                                                        ))}

                                                        {isConfigModalOpen && (
                                                            <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsConfigModalOpen(false)}>
                                                                <div className={`p-6 rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto custom-scrollbar border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                                                    <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                                                        <h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}><BadgeDollarSign size={20}/> {ruleForms.caixinha.id ? 'Editar Regra da Caixinha' : 'Nova Regra da Caixinha'}</h3>
                                                                        <button onClick={() => setIsConfigModalOpen(false)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                                                                    </div>
                                                                    <div className="space-y-4">
                                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b pb-4 dark:border-slate-700">
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data Início</label><input type="date" value={ruleForms.caixinha.validFrom} onChange={e=>setRuleForms({...ruleForms, caixinha: {...ruleForms.caixinha, validFrom: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white'}`}/></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data Fim</label><input type="date" value={ruleForms.caixinha.validTo} onChange={e=>setRuleForms({...ruleForms, caixinha: {...ruleForms.caixinha, validTo: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white'}`}/></div>
                                                                        </div>
                                                                        <div className="space-y-3">
                                                                            <div className="flex justify-between items-center"><span className="text-sm font-bold opacity-80">1. Pagto Moedas Coletadas</span><div className="flex gap-2 items-center"><input type="number" value={ruleForms.caixinha.percProportional} onChange={e=>setRuleForms({...ruleForms, caixinha: {...ruleForms.caixinha, percProportional: e.target.value}})} className={`w-20 p-2 text-center border rounded font-bold outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`}/>%</div></div>
                                                                            <div className="flex justify-between items-center"><span className="text-sm font-bold opacity-80">2. Divisão Igualitária</span><div className="flex gap-2 items-center"><input type="number" value={ruleForms.caixinha.percEqual} onChange={e=>setRuleForms({...ruleForms, caixinha: {...ruleForms.caixinha, percEqual: e.target.value}})} className={`w-full p-2 text-center border rounded font-bold outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`}/>%</div></div>
                                                                            <div className="flex justify-between items-center"><span className="text-sm font-bold opacity-80">3. Fundo Reserva Empresa</span><div className="flex gap-2 items-center"><input type="number" value={ruleForms.caixinha.percCompany} onChange={e=>setRuleForms({...ruleForms, caixinha: {...ruleForms.caixinha, percCompany: e.target.value}})} className={`w-full p-2 text-center border rounded font-bold outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`}/>%</div></div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex justify-end gap-2 mt-6 pt-4 border-t dark:border-slate-700">
                                                                        <Button variant="secondary" onClick={() => setIsConfigModalOpen(false)}>Cancelar</Button>
                                                                        <Button onClick={() => { handleSaveRule('caixinha'); setIsConfigModalOpen(false); }} icon={Save} className="bg-emerald-600 hover:bg-emerald-700 text-white border-0 shadow-md">Gravar Regra</Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* TELA: AEC - ACOMPANHAMENTO DE EVOLUÇÃO CONTINUADA */}
                                                {activeConfigScreen === 'avaliacao' && (
                                                    <div className={`p-4 space-y-4 rounded-xl border ${isDarkMode ? 'bg-slate-900/50 border-purple-800/50' : 'bg-white border-purple-200'}`}>
                                                        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                            <div className="flex justify-between items-center mb-4">
                                                                <div className="flex items-center gap-2 relative group">
                                                                    <h5 className={`font-bold uppercase tracking-wider text-[11px] ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>Fórmulas Qualitativas (Notas)</h5>
                                                                    <Info size={16} className="text-gray-400 cursor-help" />
                                                                    <div className="absolute top-full left-0 mt-2 w-96 p-4 bg-slate-800 text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none border border-slate-600">
                                                                        <p className="font-bold text-purple-400 mb-1 border-b border-slate-600 pb-1">Como configurar as Fórmulas:</p>
                                                                        <ul className="list-disc pl-4 space-y-1 mb-2">
                                                                            <li><b>Tipo:</b> Define quem entra na regra.</li>
                                                                            <li><b>Condição SE (Tipo Ambos):</b> Fórmula cima Gestão, baixo Colaborador.</li>
                                                                        </ul>
                                                                        <p className="font-bold text-yellow-400 mb-1 border-b border-slate-600 pb-1 mt-2">Variáveis de Cálculo:</p>
                                                                        <ul className="list-disc pl-4 space-y-1 font-mono text-[10px]">
                                                                            <li><b>AvQl</b>: Nota qualitativa</li>
                                                                            <li><b>AvQt</b>: Ocorrências</li>
                                                                        </ul>
                                                                    </div>
                                                                </div>
                                                                <Button size="sm" onClick={() => {
                                                                    const current = localConfig.qualiRules || [];
                                                                    setLocalConfig({...localConfig, qualiRules: [...current, { min: '', max: '', formula: '', tipo: 'Ambos', dataIni: '', dataFim: '', formulaColab: '' }]});
                                                                }} className="h-7 text-[10px] bg-purple-600 border-0 shadow-md"><Plus size={12} className="mr-1"/> Add Regra</Button>
                                                            </div>

                                                            <div className="space-y-2">
                                                                <div className="flex justify-end mb-2">
                                                                    <Button variant="secondary" size="sm" onClick={() => setShowInactiveRules(!showInactiveRules)} className="h-7 text-[9px] border-purple-300">
                                                                        {showInactiveRules ? 'Ocultar Inativos' : 'Ver Inativos'}
                                                                    </Button>
                                                                </div>

                                                                {(localConfig.qualiRules || []).filter(r => showInactiveRules || !r.dataFim).map((rule, idx) => (
                                                                    <div key={idx} className="flex flex-col sm:flex-row items-center gap-2 p-2 border rounded-lg mb-1 bg-black/5 dark:bg-white/5 text-[10px]">
                                                                        <div className="flex items-center gap-1 shrink-0">
                                                                            <span className="font-bold opacity-50">De:</span>
                                                                            <input type="number" step="0.1" value={rule.min} onChange={e => { const newRules = [...localConfig.qualiRules]; newRules[idx].min = e.target.value; setLocalConfig({...localConfig, qualiRules: newRules}); }} className="w-12 p-1 border rounded bg-transparent text-center" />
                                                                            <span className="font-bold opacity-50">Até:</span>
                                                                            <input type="number" step="0.1" value={rule.max} onChange={e => { const newRules = [...localConfig.qualiRules]; newRules[idx].max = e.target.value; setLocalConfig({...localConfig, qualiRules: newRules}); }} className="w-12 p-1 border rounded bg-transparent text-center" />
                                                                        </div>
                                                                        <select value={rule.tipo || 'Ambos'} onChange={e => { const newRules = [...localConfig.qualiRules]; newRules[idx].tipo = e.target.value; setLocalConfig({...localConfig, qualiRules: newRules}); }} className="p-1 border rounded bg-transparent font-bold w-24">
                                                                            <option value="Colaborador">Colaborador</option>
                                                                            <option value="Gestão">Gestão</option>
                                                                            <option value="Ambos">Ambos</option>
                                                                        </select>
                                                                        <div className="flex items-center gap-1 shrink-0">
                                                                            <input type="date" value={rule.dataIni || ''} onChange={e => { const newRules = [...localConfig.qualiRules]; newRules[idx].dataIni = e.target.value; setLocalConfig({...localConfig, qualiRules: newRules}); }} className="p-1 border rounded bg-transparent w-28" />
                                                                            <input type="date" value={rule.dataFim || ''} onChange={e => { const newRules = [...localConfig.qualiRules]; newRules[idx].dataFim = e.target.value; setLocalConfig({...localConfig, qualiRules: newRules}); }} className={`p-1 border rounded bg-transparent w-28 ${rule.dataFim ? 'border-red-400' : ''}`} />
                                                                        </div>
                                                                        <div className="flex-1 flex gap-2 w-full">
                                                                        <input type="text" value={rule.formula} onChange={e => { const newRules = [...localConfig.qualiRules]; newRules[idx].formula = e.target.value; setLocalConfig({...localConfig, qualiRules: newRules}); }} className={`w-full p-1.5 border rounded font-mono text-xs outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`} placeholder={rule.tipo === 'Ambos' ? 'Fórmula Gestão' : 'Fórmula'} />
                                                                        {rule.tipo === 'Ambos' && (
                                                                            <input type="text" value={rule.formulaColab} onChange={e => { const newRules = [...localConfig.qualiRules]; newRules[idx].formulaColab = e.target.value; setLocalConfig({...localConfig, qualiRules: newRules}); }} className={`w-full p-1.5 border rounded font-mono text-xs outline-none ${isDarkMode ? 'bg-slate-800 border-blue-800 text-blue-300' : 'bg-blue-50 border-blue-300 text-blue-800'}`} placeholder="Fórmula Colaborador" />
                                                                        )}
                                                                    </div>
                                                                        <button onClick={() => { const newRules = [...localConfig.qualiRules]; newRules.splice(idx, 1); setLocalConfig({...localConfig, qualiRules: newRules}); }} className="text-red-500 p-1 hover:bg-red-100 rounded shrink-0"><Trash2 size={14}/></button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        {/* REGRAS QUANTITATIVAS */}
                                                        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                            <div className="flex justify-between items-center mb-4">
                                                                <div className="flex items-center gap-2 relative group">
                                                                    <h5 className={`font-bold uppercase tracking-wider text-[11px] ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>Fórmulas Quantitativas (Erros)</h5>
                                                                    <Info size={16} className="text-gray-400 cursor-help" />
                                                                </div>
                                                                <Button size="sm" onClick={() => {
                                                                    const current = localConfig.quantiRules || [{ min: 0, max: 100, formula: localConfig.formulaQuanti || '' }];
                                                                    setLocalConfig({...localConfig, quantiRules: [...current, { min: '', max: '', formula: '' }]});
                                                                }} className="h-7 text-[10px] bg-purple-600 border-0 shadow-md"><Plus size={12} className="mr-1"/> Add Regra</Button>
                                                            </div>
                                                            <div className="space-y-2">
                                                                {(localConfig.quantiRules || []).filter(r => showInactiveRules || !r.dataFim).map((rule, idx) => (
                                                                    <div key={idx} className="flex flex-col sm:flex-row items-center gap-2 p-2 border rounded-lg mb-1 bg-black/5 dark:bg-white/5 text-[10px]">
                                                                        <div className="flex items-center gap-1 shrink-0">
                                                                            <span className="font-bold opacity-50">Erros De:</span>
                                                                            <input type="number" step="0.1" value={rule.min} onChange={e => { const newRules = [...localConfig.quantiRules]; newRules[idx].min = e.target.value; setLocalConfig({...localConfig, quantiRules: newRules}); }} className="w-12 p-1 border rounded bg-transparent text-center" />
                                                                            <span className="font-bold opacity-50">Até:</span>
                                                                            <input type="number" step="0.1" value={rule.max} onChange={e => { const newRules = [...localConfig.quantiRules]; newRules[idx].max = e.target.value; setLocalConfig({...localConfig, quantiRules: newRules}); }} className="w-12 p-1 border rounded bg-transparent text-center" />
                                                                        </div>
                                                                        <select value={rule.tipo || 'Ambos'} onChange={e => { const newRules = [...localConfig.quantiRules]; newRules[idx].tipo = e.target.value; setLocalConfig({...localConfig, quantiRules: newRules}); }} className="p-1 border rounded bg-transparent font-bold w-24">
                                                                            <option value="Colaborador">Colaborador</option>
                                                                            <option value="Gestão">Gestão</option>
                                                                            <option value="Ambos">Ambos</option>
                                                                        </select>
                                                                        <div className="flex items-center gap-1 shrink-0">
                                                                            <input type="date" value={rule.dataIni || ''} onChange={e => { const newRules = [...localConfig.quantiRules]; newRules[idx].dataIni = e.target.value; setLocalConfig({...localConfig, quantiRules: newRules}); }} className="p-1 border rounded bg-transparent w-28" />
                                                                            <input type="date" value={rule.dataFim || ''} onChange={e => { const newRules = [...localConfig.quantiRules]; newRules[idx].dataFim = e.target.value; setLocalConfig({...localConfig, quantiRules: newRules}); }} className={`p-1 border rounded bg-transparent w-28 ${rule.dataFim ? 'border-red-400' : ''}`} />
                                                                        </div>
                                                                        <div className="flex-1 flex gap-2 w-full">
                                                                        <input type="text" value={rule.formula} onChange={e => { const newRules = [...localConfig.quantiRules]; newRules[idx].formula = e.target.value; setLocalConfig({...localConfig, quantiRules: newRules}); }} className={`w-full p-1.5 border rounded font-mono text-xs outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`} placeholder={rule.tipo === 'Ambos' ? 'Fórmula Gestão' : 'Fórmula'} />
                                                                        {rule.tipo === 'Ambos' && (
                                                                            <input type="text" value={rule.formulaColab} onChange={e => { const newRules = [...localConfig.quantiRules]; newRules[idx].formulaColab = e.target.value; setLocalConfig({...localConfig, quantiRules: newRules}); }} className={`w-full p-1.5 border rounded font-mono text-xs outline-none ${isDarkMode ? 'bg-slate-800 border-blue-800 text-blue-300' : 'bg-blue-50 border-blue-300 text-blue-800'}`} placeholder="Fórmula Colaborador" />
                                                                        )}
                                                                    </div>
                                                                        <button onClick={() => { const newRules = [...localConfig.quantiRules]; newRules.splice(idx, 1); setLocalConfig({...localConfig, quantiRules: newRules}); }} className="text-red-500 p-1 hover:bg-red-100 rounded shrink-0"><Trash2 size={14}/></button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        <div className={`mt-4 p-4 rounded-xl border ${isDarkMode ? 'bg-purple-900/20 border-purple-800/50' : 'bg-purple-50 border-purple-200'}`}>
                                                            <div className={`mb-4 p-3 rounded-lg border flex items-start gap-3 ${isDarkMode ? 'bg-purple-900/40 border-purple-700' : 'bg-white border-purple-300 shadow-sm'}`}>
                                                                <input type="checkbox" id="autoGen" checked={localConfig.autoGenerateCoinsOnSignature || false} onChange={e => setLocalConfig({...localConfig, autoGenerateCoinsOnSignature: e.target.checked})} className="mt-1 w-4 h-4 text-purple-600 rounded cursor-pointer shrink-0"/>
                                                                <div>
                                                                    <label htmlFor="autoGen" className={`font-bold text-xs cursor-pointer ${isDarkMode ? 'text-purple-300' : 'text-purple-800'}`}>Gerar e Excluir Moedas Automaticamente</label>
                                                                    <p className={`text-[10px] mt-1 leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Se marcado, o sistema gerará os coins sozinho ao assinar avaliação.</p>
                                                                </div>
                                                            </div>

                                                            <div className={`transition-opacity ${localConfig.autoGenerateCoinsOnSignature ? 'opacity-40 pointer-events-none' : ''}`}>
                                                                <h4 className="text-xs font-bold text-purple-600 mb-2 flex items-center gap-2"><RefreshCw size={14}/> Gerar Lote Manual</h4>
                                                                <div className="flex gap-2 items-center flex-wrap">
                                                                    <input type="number" value={evalYear} onChange={e=>setEvalYear(e.target.value)} className={`w-20 p-2.5 border rounded-lg text-sm font-bold outline-none focus:ring-1 focus:ring-purple-500 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-300'}`} placeholder="Ano" />
                                                                    <select value={evalQuarter} onChange={e=>setEvalQuarter(e.target.value)} className={`p-2.5 border rounded-lg text-sm font-bold outline-none focus:ring-1 focus:ring-purple-500 cursor-pointer ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-300'}`}>
                                                                        <option value="q1">Q1 (1º Trim)</option>
                                                                        <option value="q2">Q2 (2º Trim)</option>
                                                                        <option value="q3">Q3 (3º Trim)</option>
                                                                        <option value="q4">Q4 (4º Trim)</option>
                                                                    </select>
                                                                    <Button onClick={handleGenerateFromEvals} className="bg-purple-600 hover:bg-purple-700 text-white shadow-md py-2.5 border-0" icon={RefreshCw}>Processar</Button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* TELA: CURSOS E TREINAMENTOS COM HISTÓRICO */}
                                                {activeConfigScreen === 'treinamentos' && (
                                                    <div className={`p-4 rounded-xl border animate-fade-in ${isDarkMode ? 'bg-slate-900/50 border-teal-800/50' : 'bg-white border-teal-200'}`}>
                                                        <div className={`p-4 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-teal-50 border-teal-200'}`}>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 border-b pb-4 dark:border-slate-700">
                                                                <div><label className="text-[10px] font-bold uppercase block mb-1">Data Início (Validade)</label><input type="date" value={ruleForms.treinamentos.validFrom} onChange={e=>setRuleForms({...ruleForms, treinamentos: {...ruleForms.treinamentos, validFrom: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white'}`}/></div>
                                                                <div><label className="text-[10px] font-bold uppercase block mb-1">Data Fim (Opcional)</label><input type="date" value={ruleForms.treinamentos.validTo} onChange={e=>setRuleForms({...ruleForms, treinamentos: {...ruleForms.treinamentos, validTo: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white'}`}/></div>
                                                            </div>
                                                            
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                                <div className={`p-4 rounded-xl border ${isDarkMode ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-gray-50'}`}>
                                                                    <label className={`text-[11px] font-bold block mb-1 uppercase ${isDarkMode ? 'text-teal-400' : 'text-teal-700'}`}>Fórmula de Multiplicação Na Área (D1)</label>
                                                                    <input type="text" value={ruleForms.treinamentos.formulaTrainingD1} onChange={e => setRuleForms({...ruleForms, treinamentos: {...ruleForms.treinamentos, formulaTrainingD1: e.target.value}})} className={`w-full p-2.5 border rounded-lg text-sm mb-4 outline-none font-mono ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} placeholder="Ex: HrD1 * 1" />
                                                                    
                                                                    <label className="text-[11px] font-bold block mb-1 uppercase text-red-500">Limite de Horas (Por Período)</label>
                                                                    <input type="number" step="0.1" value={ruleForms.treinamentos.maxTrainingHoursD1} onChange={e => setRuleForms({...ruleForms, treinamentos: {...ruleForms.treinamentos, maxTrainingHoursD1: e.target.value}})} className={`w-full p-2.5 border rounded-lg text-sm font-bold text-red-500 outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`} placeholder="Ex: 40" />
                                                                </div>
                                                                <div className={`p-4 rounded-xl border ${isDarkMode ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-gray-50'}`}>
                                                                    <label className={`text-[11px] font-bold block mb-1 uppercase ${isDarkMode ? 'text-teal-400' : 'text-teal-700'}`}>Fórmula de Multiplicação Demais Áreas (D2)</label>
                                                                    <input type="text" value={ruleForms.treinamentos.formulaTrainingD2} onChange={e => setRuleForms({...ruleForms, treinamentos: {...ruleForms.treinamentos, formulaTrainingD2: e.target.value}})} className={`w-full p-2.5 border rounded-lg text-sm mb-4 outline-none font-mono ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} placeholder="Ex: HrD2 * 1" />
                                                                    
                                                                    <label className="text-[11px] font-bold block mb-1 uppercase text-red-500">Limite de Horas (Por Período)</label>
                                                                    <input type="number" step="0.1" value={ruleForms.treinamentos.maxTrainingHoursD2} onChange={e => setRuleForms({...ruleForms, treinamentos: {...ruleForms.treinamentos, maxTrainingHoursD2: e.target.value}})} className={`w-full p-2.5 border rounded-lg text-sm font-bold text-red-500 outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`} placeholder="Ex: 20" />
                                                                </div>
                                                            </div>

                                                            <div className={`mt-4 p-4 rounded-xl border flex items-start gap-3 ${
                                                                isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'
                                                            }`}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={!!ruleForms.treinamentos.requireTrainingApproval}
                                                                    onChange={e => setRuleForms({
                                                                        ...ruleForms,
                                                                        treinamentos: {
                                                                            ...ruleForms.treinamentos,
                                                                            requireTrainingApproval: e.target.checked
                                                                        }
                                                                    })}
                                                                    className="mt-1 w-4 h-4 accent-teal-600"
                                                                />

                                                                <div>
                                                                    <label className={`text-xs font-black uppercase tracking-wider ${
                                                                        isDarkMode ? 'text-slate-200' : 'text-gray-700'
                                                                    }`}>
                                                                        Exigir validação manual do gestor para cursos
                                                                    </label>

                                                                    <p className={`text-[11px] mt-1 leading-relaxed ${
                                                                        isDarkMode ? 'text-slate-400' : 'text-gray-500'
                                                                    }`}>
                                                                        Quando ativo, as moedas do curso serão calculadas, mas ficarão bloqueadas até aprovação do gestor imediato.
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            <div className="flex justify-end mt-4">
                                                                <Button
                                                                    onClick={() => handleSaveRule('treinamentos')}
                                                                    icon={Save}
                                                                    className="bg-teal-600 text-white border-0 shadow-md"
                                                                >
                                                                    Adicionar / Atualizar Regra
                                                                </Button>
                                                            </div>
                                                        </div>

                                                        {renderConfigHistoryTable('treinamentos', ['Fórm. D1 (Max)', 'Fórm. D2 (Max)', 'Validação'], (r) => (
                                                            <>
                                                                <td className="p-3 font-mono text-xs">
                                                                    {r.formulaTrainingD1} <span className="text-red-500">({r.maxTrainingHoursD1}h)</span>
                                                                </td>

                                                                <td className="p-3 font-mono text-xs">
                                                                    {r.formulaTrainingD2} <span className="text-red-500">({r.maxTrainingHoursD2}h)</span>
                                                                </td>

                                                                <td className="p-3 text-xs font-bold">
                                                                    {r.requireTrainingApproval ? (
                                                                        <span className="px-2 py-1 rounded bg-yellow-100 text-yellow-700 border border-yellow-200">
                                                                            Manual
                                                                        </span>
                                                                    ) : (
                                                                        <span className="px-2 py-1 rounded bg-green-100 text-green-700 border border-green-200">
                                                                            Automática
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            </>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* TELA: PROJETOS SOCIAIS */}
                                                {activeConfigScreen === 'sociais' && (
                                                    <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-slate-900/50 border-pink-800/50' : 'bg-white border-pink-200'}`}>
                                                        <label className={`text-[11px] font-bold block uppercase mb-2 ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`}>Liste os projetos (Separados por vírgula)</label>
                                                        <textarea 
                                                            value={localConfig.socialProjects || 'Campanha Solidária Acad, Apoio Desastres Naturais, Natal Solidário'} 
                                                            onChange={e => setLocalConfig({...localConfig, socialProjects: e.target.value})} 
                                                            className={`w-full p-3 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-pink-500 resize-y min-h-[80px] custom-scrollbar ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-gray-50 border-gray-300'}`}
                                                            placeholder="Ex: Campanha do Agasalho..."
                                                        />
                                                    </div>
                                                )}

                                                {/* TELA: DISTRIBUIÇÃO REGULAR */}
                                                {activeConfigScreen === 'padrao' && (
                                                    <div className={`p-4 space-y-4 rounded-xl border animate-fade-in ${isDarkMode ? 'bg-slate-900/50 border-amber-800/50' : 'bg-white border-amber-200'}`}>
                                                        <div className="flex justify-between items-center mb-4">
                                                            <h4 className={`font-black uppercase tracking-wide flex items-center gap-2 ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}><Gift size={18}/> Distribuição Regular</h4>
                                                            <Button onClick={() => { setCoinRuleForm({ id: null, description: '', amount: '', periodicity: 'mensal', creditDay: '1', validFrom: new Date().toISOString().split('T')[0], validTo: '' }); setIsConfigModalOpen(true); }} icon={Plus} className="bg-amber-600 hover:bg-amber-700 text-white border-0 shadow-md">Novo Registro</Button>
                                                        </div>

                                                        <div className={`overflow-x-auto border rounded-lg ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                                            <table className={`w-full text-xs text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                                <thead className={`uppercase font-bold ${isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>
                                                                    <tr><th className="p-3">Descrição</th><th className="p-3 text-center">Frequência/Dia</th><th className="p-3 text-center">Início-Fim</th><th className="p-3 text-right">Qtd</th><th className="p-3 text-center">Ações</th></tr>
                                                                </thead>
                                                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                                    {defaultCoinHistory.sort((a,b)=>new Date(b.validFrom)-new Date(a.validFrom)).map((h) => (
                                                                        <tr key={h.id} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}>
                                                                            <td className="p-3 font-bold">{h.description}</td>
                                                                            <td className="p-3 text-center uppercase text-[10px]"><span className={`px-2 py-1 rounded-md border ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'}`}>{h.periodicity} (Dia {h.creditDay})</span></td>
                                                                            <td className="p-3 text-center font-mono">{new Date(h.validFrom).toLocaleDateString('pt-BR')} - {h.validTo ? new Date(h.validTo).toLocaleDateString('pt-BR') : 'Livre'}</td>
                                                                            <td className="p-3 text-right font-bold text-blue-500">{h.amount}</td>
                                                                            <td className="p-3 text-center flex justify-center gap-1">
                                                                                <button onClick={() => { setCoinRuleForm(h); setIsConfigModalOpen(true); }} className="text-blue-500 p-1.5 hover:bg-blue-100 rounded transition-colors"><Edit3 size={16}/></button>
                                                                                <button onClick={() => deleteDefaultCoin(h.id)} className="text-red-500 p-1.5 hover:bg-red-100 rounded transition-colors"><Trash2 size={16}/></button>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                    {defaultCoinHistory.length === 0 && <tr><td colSpan="5" className="p-6 text-center italic opacity-50">Nenhum registro configurado.</td></tr>}
                                                                </tbody>
                                                            </table>
                                                        </div>

                                                        {isConfigModalOpen && (
                                                            <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsConfigModalOpen(false)}>
                                                                <div className={`p-6 rounded-xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto custom-scrollbar border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                                                    <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                                                        <h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}><Gift size={20}/> {coinRuleForm.id ? 'Editar Regra Padrão' : 'Nova Regra Padrão'}</h3>
                                                                        <button onClick={() => setIsConfigModalOpen(false)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                                                                    </div>
                                                                    
                                                                    <div className="grid grid-cols-2 xl:grid-cols-6 gap-3 items-end">
                                                                        <div className="col-span-2">
                                                                            <label className={`text-[10px] font-bold block mb-1 uppercase ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Descrição</label>
                                                                            <Input placeholder="Ex: Bônus Mensal" value={coinRuleForm.description} onChange={e=>setCoinRuleForm({...coinRuleForm, description: e.target.value})} className={`h-10 text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/>
                                                                        </div>
                                                                        <div>
                                                                            <label className={`text-[10px] font-bold block mb-1 uppercase ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Qtd Moedas</label>
                                                                            <Input type="number" step="0.1" value={coinRuleForm.amount} onChange={e=>setCoinRuleForm({...coinRuleForm, amount: e.target.value})} className={`h-10 text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/>
                                                                        </div>
                                                                        <div>
                                                                            <label className={`text-[10px] font-bold block mb-1 uppercase ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Periodicidade</label>
                                                                            <select value={coinRuleForm.periodicity} onChange={e=>setCoinRuleForm({...coinRuleForm, periodicity: e.target.value})} className={`w-full h-10 px-2 border rounded-lg text-xs outline-none focus:ring-2 focus:ring-amber-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}>
                                                                                <option value="semanal">Semanal</option><option value="quinzenal">Quinzenal</option><option value="mensal">Mensal</option><option value="trimestral">Trimestral</option><option value="semestral">Semestral</option><option value="anual">Anual</option>
                                                                            </select>
                                                                        </div>
                                                                        <div>
                                                                            <label className={`text-[10px] font-bold block mb-1 uppercase ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Dia Crédito</label>
                                                                            <Input type="number" placeholder="Ex: 5" value={coinRuleForm.creditDay} onChange={e=>setCoinRuleForm({...coinRuleForm, creditDay: e.target.value})} className={`h-10 text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/>
                                                                        </div>
                                                                        <div>
                                                                            <label className={`text-[10px] font-bold block mb-1 uppercase ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Início</label>
                                                                            <Input type="date" value={coinRuleForm.validFrom} onChange={e=>setCoinRuleForm({...coinRuleForm, validFrom: e.target.value})} className={`h-10 text-xs ${isDarkMode ? 'invert-calendar bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/>
                                                                        </div>
                                                                        <div>
                                                                            <label className={`text-[10px] font-bold block mb-1 uppercase ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Fim (Opcional)</label>
                                                                            <Input type="date" value={coinRuleForm.validTo} onChange={e=>setCoinRuleForm({...coinRuleForm, validTo: e.target.value})} className={`h-10 text-xs ${isDarkMode ? 'invert-calendar bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/>
                                                                        </div>
                                                                    </div>
                                                                    
                                                                    <div className="flex justify-end gap-2 mt-6 pt-4 border-t dark:border-slate-700">
                                                                        <Button variant="secondary" onClick={() => setIsConfigModalOpen(false)}>Cancelar</Button>
                                                                        <Button onClick={() => {
                                                                            if(!coinRuleForm.amount || !coinRuleForm.validFrom || !coinRuleForm.description) return alert("Preencha Descrição, Qtd e Início.");
                                                                            saveDefaultCoin({ 
                                                                                id: coinRuleForm.id || Date.now().toString(), 
                                                                                description: coinRuleForm.description,
                                                                                amount: parseFloat(coinRuleForm.amount), 
                                                                                periodicity: coinRuleForm.periodicity,
                                                                                creditDay: coinRuleForm.creditDay,
                                                                                validFrom: coinRuleForm.validFrom,
                                                                                validTo: coinRuleForm.validTo || null
                                                                            });
                                                                            setCoinRuleForm({ id: null, description: '', amount: '', periodicity: 'mensal', creditDay: '1', validFrom: '', validTo: '' });
                                                                            setIsConfigModalOpen(false);
                                                                        }} icon={Save} className="bg-amber-600 hover:bg-amber-700 text-white border-0 shadow-md">Gravar Regra</Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* TELA: ABSENTEÍSMO E PONTO COM HISTÓRICO */}
                                                {activeConfigScreen === 'absenteismo' && (
                                                    <div className={`p-4 rounded-xl border animate-fade-in ${isDarkMode ? 'bg-slate-900/50 border-red-800/50' : 'bg-white border-red-200'}`}>
                                                        <div className="flex justify-between items-center mb-4">
                                                            <h4 className={`font-black uppercase tracking-wide flex items-center gap-2 ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}><Clock size={18}/> Absenteísmo e Ponto</h4>
                                                            <Button onClick={() => { setRuleForms({...ruleForms, absenteismo: { id: null, validFrom: new Date().toISOString().split('T')[0], validTo: '', daysToProcessPoints: '', coinsNoAbsences: '', coinsNoDelays: '', tolDelays: '', coinsNoManual: '', tolManual: '', coinsNoOvertime: '', tolOvertime: '' }}); setIsConfigModalOpen(true); }} icon={Plus} className="bg-red-600 hover:bg-red-700 text-white border-0 shadow-md">Novo Registro</Button>
                                                        </div>

                                                        {renderConfigHistoryTable('absenteismo', ['Faltas', 'Atrasos (Tol)', 'Manuais (Tol)', 'Extras (Tol)'], (r) => (
                                                            <>
                                                                <td className="p-3">{r.coinsNoAbsences}</td>
                                                                <td className="p-3">{r.coinsNoDelays} <span className="opacity-50">({r.tolDelays})</span></td>
                                                                <td className="p-3">{r.coinsNoManual} <span className="opacity-50">({r.tolManual})</span></td>
                                                                <td className="p-3">{r.coinsNoOvertime} <span className="opacity-50">({r.tolOvertime})</span></td>
                                                            </>
                                                        ))}

                                                        {isConfigModalOpen && (
                                                            <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsConfigModalOpen(false)}>
                                                                <div className={`p-6 rounded-xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto custom-scrollbar border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                                                    <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                                                        <h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}><Clock size={20}/> {ruleForms.absenteismo.id ? 'Editar Regra de Absenteísmo' : 'Nova Regra de Absenteísmo'}</h3>
                                                                        <button onClick={() => setIsConfigModalOpen(false)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                                                                    </div>
                                                                    <div className="space-y-4">
                                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b pb-4 dark:border-slate-700">
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data Início</label><input type="date" value={ruleForms.absenteismo.validFrom} onChange={e=>setRuleForms({...ruleForms, absenteismo: {...ruleForms.absenteismo, validFrom: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data Fim (Opcional)</label><input type="date" value={ruleForms.absenteismo.validTo} onChange={e=>setRuleForms({...ruleForms, absenteismo: {...ruleForms.absenteismo, validTo: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                        </div>
                                                                        <div className="mb-4">
                                                                            <label className="text-[10px] font-bold uppercase block mb-1">Dias p/ apuração (Após Fechamento)</label>
                                                                            <input type="number" value={ruleForms.absenteismo.daysToProcessPoints} onChange={e=>setRuleForms({...ruleForms, absenteismo: {...ruleForms.absenteismo, daysToProcessPoints: e.target.value}})} className={`w-32 p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} placeholder="Ex: 5"/>
                                                                        </div>
                                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                            <div className={`p-4 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                                                <label className="text-[10px] font-bold uppercase block mb-1">Moedas p/ Nenhuma Falta</label>
                                                                                <input type="number" step="0.1" value={ruleForms.absenteismo.coinsNoAbsences} onChange={e=>setRuleForms({...ruleForms, absenteismo: {...ruleForms.absenteismo, coinsNoAbsences: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/>
                                                                            </div>
                                                                            <div className={`p-4 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                                                <label className="text-[10px] font-bold uppercase block mb-1">Moedas p/ S/Atrasos (Valor e Tol.)</label>
                                                                                <div className="flex gap-2">
                                                                                    <input type="number" step="0.1" value={ruleForms.absenteismo.coinsNoDelays} onChange={e=>setRuleForms({...ruleForms, absenteismo: {...ruleForms.absenteismo, coinsNoDelays: e.target.value}})} className={`w-1/2 p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`} placeholder="Moedas"/>
                                                                                    <input type="number" value={ruleForms.absenteismo.tolDelays} onChange={e=>setRuleForms({...ruleForms, absenteismo: {...ruleForms.absenteismo, tolDelays: e.target.value}})} className={`w-1/2 p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`} placeholder="Tol."/>
                                                                                </div>
                                                                            </div>
                                                                            <div className={`p-4 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                                                <label className="text-[10px] font-bold uppercase block mb-1">Moedas p/ S/Manuais (Valor e Tol.)</label>
                                                                                <div className="flex gap-2">
                                                                                    <input type="number" step="0.1" value={ruleForms.absenteismo.coinsNoManual} onChange={e=>setRuleForms({...ruleForms, absenteismo: {...ruleForms.absenteismo, coinsNoManual: e.target.value}})} className={`w-1/2 p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`} placeholder="Moedas"/>
                                                                                    <input type="number" value={ruleForms.absenteismo.tolManual} onChange={e=>setRuleForms({...ruleForms, absenteismo: {...ruleForms.absenteismo, tolManual: e.target.value}})} className={`w-1/2 p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`} placeholder="Tol."/>
                                                                                </div>
                                                                            </div>
                                                                            <div className={`p-4 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                                                <label className="text-[10px] font-bold uppercase block mb-1">Moedas p/ S/Extras (Valor e Tol. Hr)</label>
                                                                                <div className="flex gap-2">
                                                                                    <input type="number" step="0.1" value={ruleForms.absenteismo.coinsNoOvertime} onChange={e=>setRuleForms({...ruleForms, absenteismo: {...ruleForms.absenteismo, coinsNoOvertime: e.target.value}})} className={`w-1/2 p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`} placeholder="Moedas"/>
                                                                                    <input type="number" value={ruleForms.absenteismo.tolOvertime} onChange={e=>setRuleForms({...ruleForms, absenteismo: {...ruleForms.absenteismo, tolOvertime: e.target.value}})} className={`w-1/2 p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`} placeholder="Tol."/>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex justify-end gap-2 mt-6 pt-4 border-t dark:border-slate-700">
                                                                        <Button variant="secondary" onClick={() => setIsConfigModalOpen(false)}>Cancelar</Button>
                                                                        <Button onClick={() => { handleSaveRule('absenteismo'); setIsConfigModalOpen(false); }} icon={Save} className="bg-red-600 hover:bg-red-700 text-white border-0 shadow-md">Gravar Regra</Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* TELA: RECONHECIMENTO COM HISTÓRICO */}
                                                {activeConfigScreen === 'reconhecimento' && (
                                                    <div className={`p-4 rounded-xl border animate-fade-in ${isDarkMode ? 'bg-slate-900/50 border-cyan-800/50' : 'bg-white border-cyan-200'}`}>
                                                        <div className="flex justify-between items-center mb-4">
                                                            <h4 className={`font-black uppercase tracking-wide flex items-center gap-2 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}><Award size={18}/> Reconhecimento</h4>
                                                            <div className="flex gap-2">
                                                                <Button variant="secondary" onClick={() => {
                                                                    const indications = distDB.filter(d => d.type === 'indicacao');
                                                                    const mutuals = [];
                                                                    const pairsChecked = new Set();
                                                                    indications.forEach(ind1 => {
                                                                        const a = ind1.fromId; const b = ind1.collabId;
                                                                        if (!a || !b || a === b) return;
                                                                        const pairKey = [a, b].sort().join('-');
                                                                        if (pairsChecked.has(pairKey)) return;
                                                                        const ind2 = indications.find(d => d.fromId === b && d.collabId === a);
                                                                        if (ind2) {
                                                                            const nameA = data.collaborators.find(c => c.id === a)?.name || 'Desconhecido';
                                                                            const nameB = data.collaborators.find(c => c.id === b)?.name || 'Desconhecido';
                                                                            const a_to_b = indications.filter(d => d.fromId === a && d.collabId === b).length;
                                                                            const b_to_a = indications.filter(d => d.fromId === b && d.collabId === a).length;
                                                                            mutuals.push({ pairKey, nameA, nameB, a_to_b, b_to_a, total: a_to_b + b_to_a });
                                                                            pairsChecked.add(pairKey);
                                                                        }
                                                                    });
                                                                    setAuditReport(mutuals.sort((x, y) => y.total - x.total));
                                                                }} icon={AlertTriangle} className={`text-xs h-8 ${isDarkMode ? 'border-red-800 text-red-400 hover:bg-red-900/30' : 'border-red-300 text-red-600 hover:bg-red-50'}`}>Auditoria</Button>
                                                                
                                                                <Button onClick={() => { setRuleForms({...ruleForms, reconhecimento: { id: null, validFrom: new Date().toISOString().split('T')[0], validTo: '', enableRecognition: false, minIndications: '', fractionOutsideTeam: '', coinsPerRecognition: '', maxIndicationsPerUser: '', recognitionReasons: '' }}); setIsConfigModalOpen(true); }} icon={Plus} className="bg-cyan-600 hover:bg-cyan-700 text-white border-0 shadow-md h-8 text-xs">Novo Registro</Button>
                                                            </div>
                                                        </div>

                                                        {/* RESULTADO DA AUDITORIA (CROSS-REFERENCE) */}
                                                        {auditReport && (
                                                            <div className={`mb-4 p-4 rounded-xl border animate-fade-in ${isDarkMode ? 'bg-slate-800 border-red-900/50' : 'bg-red-50 border-red-200'}`}>
                                                                <div className="flex justify-between items-center mb-3">
                                                                    <h5 className="font-bold text-red-600 dark:text-red-400 flex items-center gap-2"><AlertTriangle size={16}/> Relatório de Indicações Mútuas</h5>
                                                                    <button onClick={() => setAuditReport(null)} className="text-gray-400 hover:text-red-500"><X size={16}/></button>
                                                                </div>
                                                                <div className="max-h-48 overflow-y-auto custom-scrollbar pr-2">
                                                                    {auditReport.length === 0 ? (
                                                                        <p className="text-xs italic opacity-70 text-center py-4">Nenhuma troca de favores detectada no histórico de indicações.</p>
                                                                    ) : (
                                                                        <table className="w-full text-xs text-left">
                                                                            <thead className="opacity-70 font-bold border-b dark:border-slate-700">
                                                                                <tr><th className="py-2">Colaborador A</th><th className="py-2">Colaborador B</th><th className="py-2 text-center">A &rarr; B</th><th className="py-2 text-center">B &rarr; A</th><th className="py-2 text-center">Total Envolvido</th></tr>
                                                                            </thead>
                                                                            <tbody className="divide-y divide-dashed dark:divide-slate-700">
                                                                                {auditReport.map(r => (
                                                                                    <tr key={r.pairKey}>
                                                                                        <td className="py-2 font-medium">{r.nameA}</td>
                                                                                        <td className="py-2 font-medium">{r.nameB}</td>
                                                                                        <td className="py-2 text-center text-orange-600 dark:text-orange-400 font-bold">{r.a_to_b}x</td>
                                                                                        <td className="py-2 text-center text-orange-600 dark:text-orange-400 font-bold">{r.b_to_a}x</td>
                                                                                        <td className="py-2 text-center font-bold text-red-500">{r.total}x</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    )}
                                                                </div>
                                                                <p className="text-[10px] opacity-60 mt-3 italic">Atenção: A presença na lista acima não confirma fraude 100%, mas indica um padrão de "troca de favores" que o RH deve investigar nas justificativas.</p>
                                                            </div>
                                                        )}

                                                        {renderConfigHistoryTable('reconhecimento', ['Habilitado', 'Moedas', 'Mín Ind.', 'Fração Ext.'], (r) => (
                                                            <>
                                                                <td className="p-3">{r.enableRecognition ? 'Sim' : 'Não'}</td>
                                                                <td className="p-3 font-bold text-cyan-600">{r.coinsPerRecognition}</td>
                                                                <td className="p-3">{r.minIndications}</td>
                                                                <td className="p-3">{r.fractionOutsideTeam}</td>
                                                            </>
                                                        ))}

                                                        {isConfigModalOpen && (
                                                            <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsConfigModalOpen(false)}>
                                                                <div className={`p-6 rounded-xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto custom-scrollbar border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                                                    <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                                                        <h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}><Award size={20}/> {ruleForms.reconhecimento.id ? 'Editar Regra de Reconhecimento' : 'Nova Regra de Reconhecimento'}</h3>
                                                                        <button onClick={() => setIsConfigModalOpen(false)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                                                                    </div>
                                                                    <div className="space-y-4">
                                                                        <label className="flex items-center gap-2 cursor-pointer text-xs font-bold mb-4">
                                                                            <input type="checkbox" checked={ruleForms.reconhecimento.enableRecognition} onChange={e=>setRuleForms({...ruleForms, reconhecimento: {...ruleForms.reconhecimento, enableRecognition: e.target.checked}})} className="w-4 h-4 text-cyan-600"/> Habilitar Módulo
                                                                        </label>
                                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b pb-4 dark:border-slate-700">
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data Início</label><input type="date" value={ruleForms.reconhecimento.validFrom} onChange={e=>setRuleForms({...ruleForms, reconhecimento: {...ruleForms.reconhecimento, validFrom: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data Fim (Opcional)</label><input type="date" value={ruleForms.reconhecimento.validTo} onChange={e=>setRuleForms({...ruleForms, reconhecimento: {...ruleForms.reconhecimento, validTo: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                        </div>
                                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Mín. Indicações p/ Ganhar</label><input type="number" value={ruleForms.reconhecimento.minIndications} onChange={e=>setRuleForms({...ruleForms, reconhecimento: {...ruleForms.reconhecimento, minIndications: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Fração Externa (Ex: 2/3)</label><input type="text" value={ruleForms.reconhecimento.fractionOutsideTeam} onChange={e=>setRuleForms({...ruleForms, reconhecimento: {...ruleForms.reconhecimento, fractionOutsideTeam: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Moedas Recebidas (Prêmio)</label><input type="number" step="0.1" value={ruleForms.reconhecimento.coinsPerRecognition} onChange={e=>setRuleForms({...ruleForms, reconhecimento: {...ruleForms.reconhecimento, coinsPerRecognition: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Qtd. Máx Indicações/User</label><input type="number" value={ruleForms.reconhecimento.maxIndicationsPerUser} onChange={e=>setRuleForms({...ruleForms, reconhecimento: {...ruleForms.reconhecimento, maxIndicationsPerUser: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                            <div className="md:col-span-2"><label className="text-[10px] font-bold uppercase block mb-1">Motivos (Separados por vírgula)</label><textarea value={ruleForms.reconhecimento.recognitionReasons} onChange={e=>setRuleForms({...ruleForms, reconhecimento: {...ruleForms.reconhecimento, recognitionReasons: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none h-16 resize-y custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex justify-end gap-2 mt-6 pt-4 border-t dark:border-slate-700">
                                                                        <Button variant="secondary" onClick={() => setIsConfigModalOpen(false)}>Cancelar</Button>
                                                                        <Button onClick={() => { handleSaveRule('reconhecimento'); setIsConfigModalOpen(false); }} icon={Save} className="bg-cyan-600 hover:bg-cyan-700 text-white border-0 shadow-md">Gravar Regra</Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* TELA: PRESENTEAR COLEGA COM HISTÓRICO */}
                                                {activeConfigScreen === 'presentear' && (
                                                    <div className={`p-4 rounded-xl border animate-fade-in ${isDarkMode ? 'bg-slate-900/50 border-purple-800/50' : 'bg-white border-purple-200'}`}>
                                                        <div className="flex justify-between items-center mb-4">
                                                            <h4 className={`font-black uppercase tracking-wide flex items-center gap-2 ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}><Gift size={18}/> Presentear Colega</h4>
                                                            <Button onClick={() => { setRuleForms({...ruleForms, presentear: { id: null, validFrom: new Date().toISOString().split('T')[0], validTo: '', enableGifting: false, giftCoinsAllowance: '', releaseDateGift: '', giftPeriodicity: '' }}); setIsConfigModalOpen(true); }} icon={Plus} className="bg-purple-600 hover:bg-purple-700 text-white border-0 shadow-md">Novo Registro</Button>
                                                        </div>

                                                        {renderConfigHistoryTable('presentear', ['Habilitado', 'Moedas', 'Dia Liberação', 'Carência'], (r) => (
                                                            <>
                                                                <td className="p-3">{r.enableGifting ? 'Sim' : 'Não'}</td>
                                                                <td className="p-3 font-bold text-purple-600">{r.giftCoinsAllowance}</td>
                                                                <td className="p-3">{r.releaseDateGift}</td>
                                                                <td className="p-3">{r.giftPeriodicity} Meses</td>
                                                            </>
                                                        ))}

                                                        {isConfigModalOpen && (
                                                            <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsConfigModalOpen(false)}>
                                                                <div className={`p-6 rounded-xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto custom-scrollbar border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                                                    <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                                                        <h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}><Gift size={20}/> {ruleForms.presentear.id ? 'Editar Regra de Presentear' : 'Nova Regra de Presentear'}</h3>
                                                                        <button onClick={() => setIsConfigModalOpen(false)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                                                                    </div>
                                                                    <div className="space-y-4">
                                                                        <label className="flex items-center gap-2 cursor-pointer text-xs font-bold mb-4">
                                                                            <input type="checkbox" checked={ruleForms.presentear.enableGifting} onChange={e=>setRuleForms({...ruleForms, presentear: {...ruleForms.presentear, enableGifting: e.target.checked}})} className="w-4 h-4 text-purple-600"/> Habilitar Módulo
                                                                        </label>
                                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b pb-4 dark:border-slate-700">
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data Início</label><input type="date" value={ruleForms.presentear.validFrom} onChange={e=>setRuleForms({...ruleForms, presentear: {...ruleForms.presentear, validFrom: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data Fim (Opcional)</label><input type="date" value={ruleForms.presentear.validTo} onChange={e=>setRuleForms({...ruleForms, presentear: {...ruleForms.presentear, validTo: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                        </div>
                                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Moedas Permitidas p/ Envio</label><input type="number" step="0.1" value={ruleForms.presentear.giftCoinsAllowance} onChange={e=>setRuleForms({...ruleForms, presentear: {...ruleForms.presentear, giftCoinsAllowance: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Dia de Liberação</label><input type="number" min="1" max="31" value={ruleForms.presentear.releaseDateGift} onChange={e=>setRuleForms({...ruleForms, presentear: {...ruleForms.presentear, releaseDateGift: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Carência (Meses)</label><input type="number" min="0" value={ruleForms.presentear.giftPeriodicity} onChange={e=>setRuleForms({...ruleForms, presentear: {...ruleForms.presentear, giftPeriodicity: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                            <div className="col-span-full mt-2">
                                                                                <p className="text-[10px] opacity-70 leading-tight"><b>Regra Automática:</b> O colaborador ganha esse "limite" virtual a cada período na data de liberação configurada. Se não usar, expira. As moedas geradas vão direto para o recebedor e ganham a tag "Presentear". A carência impede o envio repetitivo para a mesma pessoa.</p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex justify-end gap-2 mt-6 pt-4 border-t dark:border-slate-700">
                                                                        <Button variant="secondary" onClick={() => setIsConfigModalOpen(false)}>Cancelar</Button>
                                                                        <Button onClick={() => { handleSaveRule('presentear'); setIsConfigModalOpen(false); }} icon={Save} className="bg-purple-600 hover:bg-purple-700 text-white border-0 shadow-md">Gravar Regra</Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                            {/* TELA: CONFIGURAÇÕES DE PROJETOS */}
                                                {activeConfigScreen === 'projetos_config' && (
                                                    <div className={`p-4 space-y-6 rounded-xl border ${isDarkMode ? 'bg-slate-900/50 border-indigo-800/50' : 'bg-white border-indigo-200'}`}>
                                                        <div className={`p-4 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-indigo-50 border-indigo-200'}`}>
                                                            <div className="flex items-center justify-between mb-4 border-b pb-2 dark:border-slate-700">
                                                                <h4 className={`font-black uppercase tracking-wide flex items-center gap-2 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-700'}`}><Briefcase size={18}/> Projetos Especiais</h4>
                                                                <label className="flex items-center gap-2 cursor-pointer text-xs font-bold">
                                                                    <input type="checkbox" checked={localConfig.enableProjects || false} onChange={e => setLocalConfig({...localConfig, enableProjects: e.target.checked})} className="w-4 h-4 text-indigo-600"/> Habilitar Botão
                                                                </label>
                                                            </div>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                <div><label className="text-[10px] font-bold uppercase block mb-1">Máx. de Moedas por Participante (Teto)</label><input type="number" step="0.1" value={localConfig.coinsPerProject || ''} onChange={e => setLocalConfig({...localConfig, coinsPerProject: parseFloat(e.target.value) || 0})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} placeholder="Ex: 50"/></div>
                                                                <div><label className="text-[10px] font-bold uppercase block mb-1">Dias para Crédito (Após Conclusão do Projeto)</label><div className="flex items-center gap-2"><input type="number" min="0" value={localConfig.daysToCreditProject || ''} onChange={e => setLocalConfig({...localConfig, daysToCreditProject: parseInt(e.target.value) || 0})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} placeholder="Ex: 5"/><span className="text-[10px] font-bold opacity-70">Dias</span></div></div>
                                                                <div className="col-span-full mt-2">
                                                                    <p className="text-[10px] opacity-70 leading-tight"><b>Dinâmica:</b> O limite definido acima é <b>INDIVIDUAL</b>. O "Responsável" avaliará a dedicação de cada participante e poderá distribuir de 0 até o limite máximo para cada pessoa. As moedas serão agendadas para o caixa (Previsão) com base na data de conclusão.</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                           {/* TELA: IDEIAS E SUGESTÕES COM HISTÓRICO */}
                                                {activeConfigScreen === 'ideias' && (
                                                    <div className={`p-4 rounded-xl border animate-fade-in ${isDarkMode ? 'bg-slate-900/50 border-amber-800/50' : 'bg-white border-amber-200'}`}>
                                                        <div className="flex justify-between items-center mb-4">
                                                            <h4 className={`font-black uppercase tracking-wide flex items-center gap-2 ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}><span className="text-lg">💡</span> Ideias e Sugestões</h4>
                                                            <Button onClick={() => { setRuleForms({...ruleForms, ideias: { id: null, validFrom: new Date().toISOString().split('T')[0], validTo: '', enableIdeas: false, ideasRouting: 'Todos os gestores', ideasAllowShare: false, ideasMaxCoins: '' }}); setIsConfigModalOpen(true); }} icon={Plus} className="bg-amber-600 hover:bg-amber-700 text-white border-0 shadow-md">Novo Registro</Button>
                                                        </div>

                                                        {renderConfigHistoryTable('ideias', ['Habilitado', 'Avaliador', 'Moedas Máx', 'Compartilhar'], (r) => (
                                                            <>
                                                                <td className="p-3 font-bold">{r.enableIdeas ? 'Sim' : 'Não'}</td>
                                                                <td className="p-3">{r.ideasRouting}</td>
                                                                <td className="p-3 font-bold text-amber-600">{r.ideasMaxCoins || '0'}</td>
                                                                <td className="p-3">{r.ideasAllowShare ? 'Sim' : 'Não'}</td>
                                                            </>
                                                        ))}

                                                        {isConfigModalOpen && (
                                                            <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsConfigModalOpen(false)}>
                                                                <div className={`p-6 rounded-xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto custom-scrollbar border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                                                    <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                                                        <h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}><span className="text-lg">💡</span> {ruleForms.ideias.id ? 'Editar Regra de Ideias' : 'Nova Regra de Ideias'}</h3>
                                                                        <button onClick={() => setIsConfigModalOpen(false)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                                                                    </div>
                                                                    <div className="space-y-4">
                                                                        <label className="flex items-center gap-2 cursor-pointer text-xs font-bold mb-4">
                                                                            <input type="checkbox" checked={ruleForms.ideias.enableIdeas} onChange={e=>setRuleForms({...ruleForms, ideias: {...ruleForms.ideias, enableIdeas: e.target.checked}})} className="w-4 h-4 text-amber-600"/> Habilitar Módulo
                                                                        </label>
                                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b pb-4 dark:border-slate-700">
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data Início</label><input type="date" value={ruleForms.ideias.validFrom} onChange={e=>setRuleForms({...ruleForms, ideias: {...ruleForms.ideias, validFrom: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data Fim (Opcional)</label><input type="date" value={ruleForms.ideias.validTo} onChange={e=>setRuleForms({...ruleForms, ideias: {...ruleForms.ideias, validTo: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                        </div>
                                                                        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 transition-opacity ${!ruleForms.ideias.enableIdeas ? 'opacity-50 pointer-events-none' : ''}`}>
                                                                            <div className="col-span-full md:col-span-1">
                                                                                <label className="text-[10px] font-bold uppercase block mb-1">Quem avaliará as ideias?</label>
                                                                                <select value={ruleForms.ideias.ideasRouting} onChange={e=>setRuleForms({...ruleForms, ideias: {...ruleForms.ideias, ideasRouting: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}>
                                                                                    <option value="Todos os gestores">Todos os gestores</option>
                                                                                    <option value="Somente Gestores do Departamento">Somente Gestores do Departamento</option>
                                                                                    <option value="Somente Diretoria">Somente Diretoria</option>
                                                                                    <option value="Personalizado">Personalizado</option>
                                                                                </select>
                                                                                {ruleForms.ideias.ideasRouting === 'Personalizado' && (
                                                                                    <div className={`mt-2 p-3 rounded-lg border flex flex-col gap-2 ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                                                                                        <label className="text-[9px] font-bold uppercase block opacity-70">Selecione os Gestores Avaliadores:</label>
                                                                                        <div className="relative shrink-0">
                                                                                            <Search size={12} className="absolute left-2 top-2 text-gray-400"/>
                                                                                            <input type="text" placeholder="Buscar gestor..." value={customEvalSearch} onChange={(e) => setCustomEvalSearch(e.target.value)} className={`w-full pl-7 py-1 text-xs border rounded outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} />
                                                                                        </div>
                                                                                        <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto custom-scrollbar pr-1">
                                                                                            {(() => {
                                                                                                const filteredGestores = data.collaborators.filter(c => c.personType === 'Gestão').filter(c => !customEvalSearch || c.name.toLowerCase().includes(customEvalSearch.toLowerCase())).sort((a,b)=>a.name.localeCompare(b.name));
                                                                                                if (filteredGestores.length === 0) return <span className="text-[10px] italic opacity-50">Nenhum gestor encontrado.</span>;
                                                                                                return filteredGestores.map(c => (
                                                                                                    <label key={c.id} className="flex items-center gap-2 cursor-pointer text-xs">
                                                                                                        <input type="checkbox" checked={ruleForms.ideias.customEvaluators?.includes(c.id) || false} onChange={(e) => { const current = ruleForms.ideias.customEvaluators || []; const next = e.target.checked ? [...current, c.id] : current.filter(id => id !== c.id); setRuleForms({...ruleForms, ideias: {...ruleForms.ideias, customEvaluators: next}}); }} className="w-3 h-3 text-amber-600 shrink-0"/>
                                                                                                        <span className="truncate">{c.name}</span>
                                                                                                    </label>
                                                                                                ));
                                                                                            })()}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                            <div>
                                                                                <label className="text-[10px] font-bold uppercase block mb-1">Máximo de Moedas por Ideia</label>
                                                                                <input type="number" min="0" value={ruleForms.ideias.ideasMaxCoins} onChange={e=>setRuleForms({...ruleForms, ideias: {...ruleForms.ideias, ideasMaxCoins: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} placeholder="Ex: 50"/>
                                                                            </div>
                                                                            <div className="flex items-center mt-4">
                                                                                <label className="flex items-center gap-2 cursor-pointer text-xs font-bold">
                                                                                    <input type="checkbox" checked={ruleForms.ideias.ideasAllowShare} onChange={e=>setRuleForms({...ruleForms, ideias: {...ruleForms.ideias, ideasAllowShare: e.target.checked}})} className="w-4 h-4 text-amber-600"/>
                                                                                    Permitir compartilhar com outros gestores
                                                                                </label>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex justify-end gap-2 mt-6 pt-4 border-t dark:border-slate-700">
                                                                        <Button variant="secondary" onClick={() => setIsConfigModalOpen(false)}>Cancelar</Button>
                                                                        <Button onClick={() => { handleSaveRule('ideias'); setIsConfigModalOpen(false); }} icon={Save} className="bg-amber-600 hover:bg-amber-700 text-white border-0 shadow-md">Gravar Regra</Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* TELA: ACADNEWS & GAMIFICAÇÃO COM HISTÓRICO */}
                                                {activeConfigScreen === 'acadnews' && (
                                                    <div className={`p-4 rounded-xl border animate-fade-in ${isDarkMode ? 'bg-slate-900/50 border-orange-800/50' : 'bg-white border-orange-200'}`}>
                                                        <div className="flex items-center justify-between mb-4">
                                                            <h4 className={`font-black uppercase tracking-wide flex items-center gap-2 ${isDarkMode ? 'text-orange-400' : 'text-orange-700'}`}><Globe size={18}/> AcadNews e Gamificação</h4>
                                                            <Button onClick={() => { setRuleForms({...ruleForms, acadnews: { id: null, validFrom: new Date().toISOString().split('T')[0], validTo: '', enableAcadNews: false, enableSocialWall: false, enableBadges: false, enableRaffles: false, enableLeaderboard: false, archiveDays: 7 }}); setIsConfigModalOpen(true); }} icon={Plus} className="bg-orange-600 hover:bg-orange-700 text-white border-0 shadow-md">Novo Registro</Button>
                                                        </div>

                                                        {renderConfigHistoryTable('acadnews', ['Aba Ativa', 'Mural', 'Ranking', 'Rifas', 'Selos', 'Arq. (Dias)'], (r) => (
                                                            <>
                                                                <td className="p-3 font-bold">{r.enableAcadNews ? 'Sim' : 'Não'}</td>
                                                                <td className="p-3">{r.enableSocialWall ? 'Ativo' : '-'}</td>
                                                                <td className="p-3">{r.enableLeaderboard ? 'Ativo' : '-'}</td>
                                                                <td className="p-3">{r.enableRaffles ? 'Ativo' : '-'}</td>
                                                                <td className="p-3">{r.enableBadges ? 'Ativo' : '-'}</td>
                                                                <td className="p-3 font-bold text-orange-500">{r.archiveDays || 7}</td>
                                                            </>
                                                        ))}

                                                        {/* CADASTRO DE SELOS FICA NA TELA PRINCIPAL */}
                                                        {ruleForms.acadnews.enableBadges && (
                                                            <div className={`mt-6 p-5 rounded-xl border shadow-sm animate-fade-in ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-emerald-50 border-emerald-200'}`}>
                                                                <h4 className={`font-black uppercase tracking-wide flex items-center gap-2 mb-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}><Award size={18}/> Cadastro de Selos (Conquistas)</h4>
                                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 mb-6 items-end">
                                                                    <div className="relative">
                                                                        <label className="text-[10px] font-bold uppercase block mb-1">Ícone</label>
                                                                        <button type="button" onClick={() => setShowEmojiPicker('badge')} className={`w-full p-2 border rounded-lg text-center text-lg outline-none transition-colors shadow-sm h-[38px] ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}>{badgeForm.emoji}</button>
                                                                        {showEmojiPicker === 'badge' && (
                                                                            <>
                                                                                <div className="fixed inset-0 z-40" onClick={() => setShowEmojiPicker(false)}></div>
                                                                                <div className={`absolute top-full left-0 mt-2 p-2 w-56 rounded-xl shadow-2xl border z-50 grid grid-cols-5 gap-1 animate-fade-in ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'}`}>
                                                                                    {['⭐','❤️','🍀','🏆','🚀','🧠','💡','🤝','🔥','💎','🏅','🎨','🛠️','🧩','🌟'].map(em => (
                                                                                        <button key={em} type="button" onClick={() => { setBadgeForm({...badgeForm, emoji: em}); setShowEmojiPicker(false); }} className={`p-2 text-xl rounded hover:scale-125 transition-transform ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}>{em}</button>
                                                                                    ))}
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                    <div className="lg:col-span-2">
                                                                        <label className="text-[10px] font-bold uppercase block mb-1">Nome da Conquista</label>
                                                                        <input type="text" value={badgeForm.name} onChange={e=>setBadgeForm({...badgeForm, name: e.target.value})} placeholder="Ex: Coração de Ouro" className={`w-full p-2 border rounded-lg text-sm outline-none h-[38px] ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/>
                                                                    </div>
                                                                    <div className="lg:col-span-3">
                                                                        <label className="text-[10px] font-bold uppercase block mb-1">Regra de Desbloqueio</label>
                                                                        <div className="flex gap-2">
                                                                            <select value={badgeForm.triggerType} onChange={e=>setBadgeForm({...badgeForm, triggerType: e.target.value})} className={`flex-1 p-2 border rounded-lg text-xs outline-none h-[38px] ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}>
                                                                                <option value="received_indications">Qtd. de Reconhecimentos Recebidos</option>
                                                                                <option value="sent_indications">Qtd. de Colegas Reconhecidos (Enviados)</option>
                                                                                <option value="donated_coins">Soma de Moedas Doadas (Fazer o Bem)</option>
                                                                                <option value="bought_tickets">Qtd. de Bilhetes de Rifa Comprados</option>
                                                                                <option value="sent_gifts">Qtd. de Presentes Mensais Enviados</option>
                                                                                <option value="projects_done">Qtd. de Projetos Especiais Concluídos</option>
                                                                                <option value="courses_done">Qtd. de Cursos e Treinamentos Feitos</option>
                                                                                <option value="ideas_submitted">Qtd. de Ideias Enviadas</option>
                                                                                <option value="ideas_approved">Qtd. de Ideias Aprovadas</option>
                                                                            </select>
                                                                            <input type="number" min="1" value={badgeForm.triggerValue} onChange={e=>setBadgeForm({...badgeForm, triggerValue: e.target.value})} placeholder="Alvo (Ex: 10)" className={`w-20 p-2 border rounded-lg text-sm text-center outline-none h-[38px] ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/>
                                                                        </div>
                                                                    </div>
                                                                    <div className="sm:col-span-2 lg:col-span-4">
                                                                        <label className="text-[10px] font-bold uppercase block mb-1">Descrição (Motivo exibido no Cartão)</label>
                                                                        <input type="text" value={badgeForm.description} onChange={e=>setBadgeForm({...badgeForm, description: e.target.value})} placeholder="Ex: Doou mais de 10 moedas para colegas." className={`w-full p-2 border rounded-lg text-sm outline-none h-[38px] ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/>
                                                                    </div>
                                                                    <div className="sm:col-span-2 lg:col-span-2 flex justify-end gap-2">
                                                                        {badgeForm.id && <Button variant="secondary" onClick={() => setBadgeForm({ id: null, name: '', emoji: '⭐', description: '', triggerType: 'received_indications', triggerValue: '' })} className="h-[38px]">Cancelar</Button>}
                                                                        <Button onClick={() => {
                                                                            if(!badgeForm.name || !badgeForm.triggerValue || !badgeForm.description) return alert("Preencha nome, alvo e descrição.");
                                                                            saveBadge(sanitizeForFirebase({ ...badgeForm, id: badgeForm.id || `badge_${Date.now()}`, triggerValue: parseFloat(badgeForm.triggerValue) }));
                                                                            setBadgeForm({ id: null, name: '', emoji: '⭐', description: '', triggerType: 'received_indications', triggerValue: '' });
                                                                        }} icon={Save} className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-md border-0 h-[38px] w-full">{badgeForm.id ? 'Salvar' : 'Criar Selo'}</Button>
                                                                    </div>
                                                                </div>
                                                                <div className={`overflow-x-auto border rounded-lg max-h-60 custom-scrollbar ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-white'}`}>
                                                                    <table className={`w-full text-xs text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                                        <thead className={`uppercase font-bold sticky top-0 ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>
                                                                            <tr><th className="p-3">Selo</th><th className="p-3">Descrição</th><th className="p-3 text-center">Regra</th><th className="p-3 text-center">Ações</th></tr>
                                                                        </thead>
                                                                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                                            {badgesDB.map((b) => (
                                                                                <tr key={b.id} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}>
                                                                                    <td className="p-3 font-bold flex items-center gap-2 text-sm"><span className="text-xl">{b.emoji}</span> {b.name}</td>
                                                                                    <td className="p-3 opacity-80">{b.description}</td>
                                                                                    <td className="p-3 text-center font-mono text-[10px] bg-black/5 dark:bg-white/5 rounded m-1">
                                                                                        {{received_indications: 'Rec. Recebidos', sent_indications: 'Rec. Enviados', donated_coins: 'Doações', bought_tickets: 'Rifas', sent_gifts: 'Presentes Env.', projects_done: 'Projetos', high_performance: 'Alto Desempenho', courses_done: 'Cursos Realizados', ideas_submitted: 'Ideias Env.', ideas_approved: 'Ideias Aprov.'}[b.triggerType] || b.triggerType} &ge; {b.triggerValue}
                                                                                    </td>
                                                                                    <td className="p-3 text-center flex justify-center gap-1">
                                                                                        <button onClick={() => { setBadgeForm(b); document.querySelector('.overflow-y-auto').scrollTo(0, document.body.scrollHeight); }} className="text-blue-500 p-1.5 hover:bg-blue-100 rounded"><Edit3 size={14}/></button>
                                                                                        <button onClick={() => { let confirmed = true; try { confirmed = window.confirm('Excluir selo?'); } catch(e) { confirmed = true; } if(confirmed) delBadge(b.id); }} className="text-red-500 p-1.5 hover:bg-red-100 rounded"><Trash2 size={14}/></button>
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                            {badgesDB.length === 0 && <tr><td colSpan="4" className="p-6 text-center italic opacity-50">Nenhum selo cadastrado.</td></tr>}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {isConfigModalOpen && (
                                                            <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsConfigModalOpen(false)}>
                                                                <div className={`p-6 rounded-xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto custom-scrollbar border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                                                    <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                                                        <h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}><Globe size={20}/> {ruleForms.acadnews.id ? 'Editar Regra de AcadNews' : 'Nova Regra de AcadNews'}</h3>
                                                                        <button onClick={() => setIsConfigModalOpen(false)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                                                                    </div>
                                                                    <div className="space-y-4">
                                                                        <label className="flex items-center gap-2 cursor-pointer text-xs font-bold mb-4">
                                                                            <input type="checkbox" checked={ruleForms.acadnews.enableAcadNews} onChange={e=>setRuleForms({...ruleForms, acadnews: {...ruleForms.acadnews, enableAcadNews: e.target.checked}})} className="w-4 h-4 text-orange-600"/> Habilitar Guia AcadNews
                                                                        </label>
                                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-b pb-4 dark:border-slate-700">
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data Início</label><input type="date" value={ruleForms.acadnews.validFrom} onChange={e=>setRuleForms({...ruleForms, acadnews: {...ruleForms.acadnews, validFrom: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data Fim (Opcional)</label><input type="date" value={ruleForms.acadnews.validTo} onChange={e=>setRuleForms({...ruleForms, acadnews: {...ruleForms.acadnews, validTo: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Dias para Arquivar</label><input type="number" min="1" value={ruleForms.acadnews.archiveDays || 7} onChange={e=>setRuleForms({...ruleForms, acadnews: {...ruleForms.acadnews, archiveDays: e.target.value}})} placeholder="Ex: 7" className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                        </div>
                                                                        <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 transition-opacity ${!ruleForms.acadnews.enableAcadNews ? 'opacity-50 pointer-events-none' : ''}`}>
                                                                            <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-300'}`}>
                                                                                <input type="checkbox" checked={ruleForms.acadnews.enableSocialWall} onChange={e=>setRuleForms({...ruleForms, acadnews: {...ruleForms.acadnews, enableSocialWall: e.target.checked}})} className="w-5 h-5 text-orange-600"/>
                                                                                <div><div className="font-bold text-xs">Mural Social Público</div><div className="text-[10px] opacity-70">Exibir interações de Presentes e Reconhecimentos para todos.</div></div>
                                                                            </label>
                                                                            <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-300'}`}>
                                                                                <input type="checkbox" checked={ruleForms.acadnews.enableLeaderboard} onChange={e=>setRuleForms({...ruleForms, acadnews: {...ruleForms.acadnews, enableLeaderboard: e.target.checked}})} className="w-5 h-5 text-orange-600"/>
                                                                                <div><div className="font-bold text-xs">Ranking de Engajamento</div><div className="text-[10px] opacity-70">Exibir Top Doadores e Reconhecedores.</div></div>
                                                                            </label>
                                                                            <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-300'}`}>
                                                                                <input type="checkbox" checked={ruleForms.acadnews.enableRaffles} onChange={e=>setRuleForms({...ruleForms, acadnews: {...ruleForms.acadnews, enableRaffles: e.target.checked}})} className="w-5 h-5 text-orange-600"/>
                                                                                <div><div className="font-bold text-xs">Sistema de Rifas / Sorteios</div><div className="text-[10px] opacity-70">Permitir marcação de produtos como Rifa no Catálogo.</div></div>
                                                                            </label>
                                                                            <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-300'}`}>
                                                                                <input type="checkbox" checked={ruleForms.acadnews.enableBadges} onChange={e=>setRuleForms({...ruleForms, acadnews: {...ruleForms.acadnews, enableBadges: e.target.checked}})} className="w-5 h-5 text-orange-600"/>
                                                                                <div><div className="font-bold text-xs">Sistema de Selos/Badges</div><div className="text-[10px] opacity-70">Exibir selos de conquista no perfil dos colaboradores.</div></div>
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex justify-end gap-2 mt-6 pt-4 border-t dark:border-slate-700">
                                                                        <Button variant="secondary" onClick={() => setIsConfigModalOpen(false)}>Cancelar</Button>
                                                                        <Button onClick={() => { handleSaveRule('acadnews'); setIsConfigModalOpen(false); }} icon={Save} className="bg-orange-600 hover:bg-orange-700 text-white border-0 shadow-md">Gravar Regra</Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                            {/* TELA: NÍVEIS DA CAIXINHA (NOTIFICAÇÕES) */}
                                                {activeConfigScreen === 'niveisCaixa' && (
                                                    <div className={`p-4 rounded-xl border animate-fade-in ${isDarkMode ? 'bg-slate-900/50 border-emerald-800/50' : 'bg-white border-emerald-200'}`}>
                                                        <div className="flex items-center justify-between mb-4">
                                                            <h4 className={`font-black uppercase tracking-wide flex items-center gap-2 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}><TrendingUp size={18}/> Níveis da Caixinha</h4>
                                                            <Button onClick={() => { setRuleForms({...ruleForms, niveisCaixa: { id: null, validFrom: new Date().toISOString().split('T')[0], validTo: '', tipoAviso: 'meta_saldo', tipoValor: 'fixo', valor: '', mensagem: 'Chegamos a {valor} de saldo na caixinha!' }}); setIsConfigModalOpen(true); }} icon={Plus} className="bg-emerald-600 hover:bg-emerald-700 text-white border-0 shadow-md">Novo Registro</Button>
                                                        </div>
                                                        
                                                        {renderConfigHistoryTable('niveisCaixa', ['Tipo', 'Valor Alvo', 'Mensagem'], (r) => (
                                                            <>
                                                                <td className="p-3 font-bold">{r.tipoAviso === 'meta_saldo' ? 'Meta Saldo' : 'Alerta Redução'}</td>
                                                                <td className="p-3 text-emerald-600 font-bold">{r.tipoValor === 'percentual' ? `${r.valor}%` : formatMoney(parseFloat(r.valor || 0))}</td>
                                                                <td className="p-3 text-[10px] opacity-80">{r.mensagem}</td>
                                                            </>
                                                        ))}

                                                        {isConfigModalOpen && (
                                                            <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsConfigModalOpen(false)}>
                                                                <div className={`p-6 rounded-xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto custom-scrollbar border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                                                    <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                                                        <h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}><TrendingUp size={20}/> {ruleForms.niveisCaixa.id ? 'Editar Regra de Nível' : 'Nova Regra de Nível'}</h3>
                                                                        <button onClick={() => setIsConfigModalOpen(false)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                                                                    </div>
                                                                    <div className="space-y-4">
                                                                        <p className="text-[10px] opacity-70 mb-4 leading-tight">Crie regras para disparar notícias automáticas no Mural quando o saldo do período atingir uma meta ou sofrer uma redução. Dica: use <b>{'{valor}'}</b> na frase para exibir o número alcançado.</p>
                                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b pb-4 dark:border-slate-700">
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data Início</label><input type="date" value={ruleForms.niveisCaixa?.validFrom || ''} onChange={e=>setRuleForms({...ruleForms, niveisCaixa: {...(ruleForms.niveisCaixa || {}), validFrom: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data Fim (Opcional)</label><input type="date" value={ruleForms.niveisCaixa?.validTo || ''} onChange={e=>setRuleForms({...ruleForms, niveisCaixa: {...(ruleForms.niveisCaixa || {}), validTo: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                        </div>
                                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                                            <div>
                                                                                <label className="text-[10px] font-bold uppercase block mb-1">Aviso Sobre:</label>
                                                                                <select value={ruleForms.niveisCaixa?.tipoAviso || 'meta_saldo'} onChange={e=>setRuleForms({...ruleForms, niveisCaixa: {...(ruleForms.niveisCaixa || {}), tipoAviso: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}>
                                                                                    <option value="meta_saldo">Meta de Saldo (Aumento)</option>
                                                                                    <option value="alerta_reducao">Alerta de Redução (Saída)</option>
                                                                                </select>
                                                                            </div>
                                                                            <div>
                                                                                <label className="text-[10px] font-bold uppercase block mb-1">Tipo de Medida</label>
                                                                                <select value={ruleForms.niveisCaixa?.tipoValor || 'fixo'} onChange={e=>setRuleForms({...ruleForms, niveisCaixa: {...(ruleForms.niveisCaixa || {}), tipoValor: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}>
                                                                                    <option value="fixo">Valor Fixo (R$)</option>
                                                                                    {ruleForms.niveisCaixa?.tipoAviso === 'alerta_reducao' && <option value="percentual">Percentual (%) sobre Entradas</option>}
                                                                                </select>
                                                                            </div>
                                                                            <div>
                                                                                <label className="text-[10px] font-bold uppercase block mb-1">Valor (R$ ou %)</label>
                                                                                <input type="number" step="0.01" value={ruleForms.niveisCaixa?.valor || ''} onChange={e=>setRuleForms({...ruleForms, niveisCaixa: {...(ruleForms.niveisCaixa || {}), valor: e.target.value}})} placeholder="Ex: 5000" className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/>
                                                                            </div>
                                                                            <div className="md:col-span-3">
                                                                                <label className="text-[10px] font-bold uppercase block mb-1">Frase Motivacional / Alerta</label>
                                                                                <textarea value={ruleForms.niveisCaixa?.mensagem || ''} onChange={e=>setRuleForms({...ruleForms, niveisCaixa: {...(ruleForms.niveisCaixa || {}), mensagem: e.target.value}})} placeholder="Ex: Uau! Chegamos a {valor} de saldo na caixinha!" className={`w-full p-2 border rounded-lg text-sm outline-none h-16 resize-y custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex justify-end gap-2 mt-6 pt-4 border-t dark:border-slate-700">
                                                                        <Button variant="secondary" onClick={() => setIsConfigModalOpen(false)}>Cancelar</Button>
                                                                        <Button onClick={() => { handleSaveRule('niveisCaixa'); setIsConfigModalOpen(false); }} icon={Save} className="bg-emerald-600 hover:bg-emerald-700 text-white border-0 shadow-md">Gravar Regra</Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                            </div>
                                                )}

                                            {/* TELA: CULTURA ACAD */}
                                            {activeConfigScreen === 'cultura' && (
                                                <div className={`p-4 rounded-xl border animate-fade-in ${isDarkMode ? 'bg-slate-900/50 border-lime-800/50' : 'bg-white border-lime-200'}`}>
                                                    <div className="flex items-center justify-between mb-4">
                                                        <h4 className={`font-black uppercase tracking-wide flex items-center gap-2 ${isDarkMode ? 'text-lime-400' : 'text-lime-700'}`}><BookOpen size={18}/> Cultura Acad</h4>
                                                        <Button onClick={() => { setRuleForms({...ruleForms, cultura: { id: null, validFrom: new Date().toISOString().split('T')[0], validTo: '', reward: '', penalty: '' }}); setIsConfigModalOpen(true); }} icon={Plus} className="bg-lime-600 hover:bg-lime-700 text-white border-0 shadow-md">Novo Registro</Button>
                                                    </div>

                                                    {renderConfigHistoryTable('cultura', ['Ganho (No Prazo)', 'Perda (Atraso)'], (r) => (
                                                        <>
                                                            <td className="p-3 font-bold text-green-600">+{r.reward}</td>
                                                            <td className="p-3 font-bold text-red-600">-{r.penalty}</td>
                                                        </>
                                                    ))}

                                                    {isConfigModalOpen && (
                                                        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsConfigModalOpen(false)}>
                                                            <div className={`p-6 rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto custom-scrollbar border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                                                <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                                                    <h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-lime-400' : 'text-lime-600'}`}><BookOpen size={20}/> {ruleForms.cultura.id ? 'Editar Regra de Cultura' : 'Nova Regra de Cultura'}</h3>
                                                                    <button onClick={() => setIsConfigModalOpen(false)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                                                                </div>
                                                                
                                                                <div className="space-y-4">
                                                                    <p className="text-[10px] opacity-70 mb-4 leading-tight">Defina quantas moedas o colaborador ganha ao dar ciência nos documentos da Cultura Acad dentro do prazo, e quantas ele perde se assinar com atraso.</p>
                                                                    
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b pb-4 dark:border-slate-700">
                                                                        <div><label className="text-[10px] font-bold uppercase block mb-1">Data Início</label><input type="date" value={ruleForms.cultura?.validFrom || ''} onChange={e=>setRuleForms({...ruleForms, cultura: {...(ruleForms.cultura || {}), validFrom: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                        <div><label className="text-[10px] font-bold uppercase block mb-1">Data Fim (Opcional)</label><input type="date" value={ruleForms.cultura?.validTo || ''} onChange={e=>setRuleForms({...ruleForms, cultura: {...(ruleForms.cultura || {}), validTo: e.target.value}})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar text-white' : 'bg-white border-gray-300'}`}/></div>
                                                                    </div>
                                                                    
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                        <div>
                                                                            <label className="text-[10px] font-bold uppercase block mb-1 text-green-600 dark:text-green-400">Ganho (No Prazo)</label>
                                                                            <div className="relative">
                                                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">+</span>
                                                                                <input type="number" step="0.1" min="0" value={ruleForms.cultura?.reward || ''} onChange={e=>setRuleForms({...ruleForms, cultura: {...(ruleForms.cultura || {}), reward: e.target.value}})} placeholder="Ex: 10" className={`w-full p-2 pl-7 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/>
                                                                            </div>
                                                                        </div>
                                                                        <div>
                                                                            <label className="text-[10px] font-bold uppercase block mb-1 text-red-600 dark:text-red-400">Perda (Com Atraso)</label>
                                                                            <div className="relative">
                                                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">-</span>
                                                                                <input type="number" step="0.1" min="0" value={ruleForms.cultura?.penalty || ''} onChange={e=>setRuleForms({...ruleForms, cultura: {...(ruleForms.cultura || {}), penalty: e.target.value}})} placeholder="Ex: 5" className={`w-full p-2 pl-7 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="flex justify-end gap-2 mt-6 pt-4 border-t dark:border-slate-700">
                                                                    <Button variant="secondary" onClick={() => setIsConfigModalOpen(false)}>Cancelar</Button>
                                                                    <Button onClick={() => { handleSaveRule('cultura'); setIsConfigModalOpen(false); }} icon={Save} className="bg-lime-600 hover:bg-lime-700 text-white border-0 shadow-md">Gravar Regra</Button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </Card>
                        )}

                            {/* SUB-ABA: GESTÃO DE PROJETOS (ADMIN) */}
                            {adminSubTab === 'projetos_admin' && (
                                <Card title="Cadastro e Gestão de Projetos" darkMode={isDarkMode}>
                                    <div className={`p-4 rounded-xl border mb-4 shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                            
                                            {/* NOVO CAMPO: SELETOR DE EMOJI */}
                                            <div className="relative">
                                                <label className="text-[10px] font-bold uppercase block mb-1">Emoji (Ícone)</label>
                                                <button 
                                                    type="button"
                                                    onClick={() => setShowEmojiPicker('project')}
                                                    className={`w-full p-1.5 border rounded-lg text-center text-2xl outline-none transition-colors shadow-sm ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white hover:bg-slate-800' : 'bg-white border-gray-300 hover:bg-gray-50'}`}
                                                >
                                                    {projectForm.emoji || '🚀'}
                                                </button>

                                                {showEmojiPicker && (
                                                    <>
                                                        <div className="fixed inset-0 z-40" onClick={() => setShowEmojiPicker(false)}></div>
                                                        <div className={`absolute top-full left-0 mt-2 p-2 w-56 rounded-xl shadow-2xl border z-50 grid grid-cols-5 gap-1 animate-fade-in ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'}`}>
                                                            {['🚀', '🎯', '💡', '💻', '📊', '🛠️', '🎨', '📢', '🤝', '🏆', '📱', '⚙️', '📈', '💰', '🌍', '⭐', '🔥', '⚡', '🧩', '🧪', '🌱', '🏗️', '📦', '🚚', '📝', '🏃', '📚', '🧠', '✅', '❤️'].map(em => (
                                                                <button
                                                                    key={em}
                                                                    type="button"
                                                                    onClick={() => { setProjectForm({...projectForm, emoji: em}); setShowEmojiPicker(false); }}
                                                                    className={`p-2 text-xl rounded hover:scale-125 transition-transform ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
                                                                >
                                                                    {em}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            <div className="md:col-span-3"><label className="text-[10px] font-bold uppercase block mb-1">Nome do Projeto</label><input type="text" placeholder="Ex: Implantação ERP" value={projectForm.name} onChange={e=>setProjectForm({...projectForm, name: e.target.value})} className={`w-full p-2 border rounded-lg text-sm outline-none h-[42px] ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}/></div>
                                            
                                            <div className="md:col-span-2">
                                                <label className="text-[10px] font-bold uppercase block mb-1">Responsável (Gestor do Bolo de Moedas)</label>
                                                <select value={projectForm.managerId} onChange={e=>setProjectForm({...projectForm, managerId: e.target.value})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}>
                                                    <option value="">Selecione...</option>
                                                    {data.collaborators.sort((a,b)=>a.name.localeCompare(b.name)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                                </select>
                                            </div>
                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data Início</label><input type="date" value={projectForm.startDate} onChange={e=>setProjectForm({...projectForm, startDate: e.target.value})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white invert-calendar' : 'bg-white border-gray-300'}`}/></div>
                                            <div><label className="text-[10px] font-bold uppercase block mb-1">Data Fim (Previsão)</label><input type="date" value={projectForm.endDate} onChange={e=>setProjectForm({...projectForm, endDate: e.target.value})} className={`w-full p-2 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white invert-calendar' : 'bg-white border-gray-300'}`}/></div>
                                            
                                            <div className="md:col-span-4">
                                                <div className="flex justify-between items-center mb-1">
                                                    <label className="text-[10px] font-bold uppercase">Participantes (Equipe do Projeto)</label>
                                                    <input 
                                                        type="text" 
                                                        placeholder="Buscar colaborador..." 
                                                        value={projectSearch} 
                                                        onChange={e => setProjectSearch(e.target.value)} 
                                                        className={`p-1 text-[10px] border rounded outline-none w-40 sm:w-56 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} 
                                                    />
                                                </div>
                                                <div className={`p-2 border rounded-lg max-h-32 overflow-y-auto custom-scrollbar grid grid-cols-2 sm:grid-cols-3 gap-2 ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-300'}`}>
                                                    {data.collaborators.filter(c => 
                                                        c.participatesAcadCoins !== false && 
                                                        (!projectSearch || c.name.toLowerCase().includes(projectSearch.toLowerCase()))
                                                    ).sort((a,b)=>a.name.localeCompare(b.name)).map(c => (
                                                        <label key={c.id} className="flex items-center gap-2 cursor-pointer text-xs truncate">
                                                            <input type="checkbox" className="w-3 h-3" checked={projectForm.participants?.includes(c.id) || false} 
                                                                onChange={(e) => {
                                                                    const isChecked = e.target.checked;
                                                                    const newParts = isChecked ? [...(projectForm.participants||[]), c.id] : (projectForm.participants||[]).filter(id => id !== c.id);
                                                                    setProjectForm({...projectForm, participants: newParts});
                                                                }}
                                                            /> {c.name}
                                                        </label>
                                                    ))}
                                                    {data.collaborators.filter(c => c.participatesAcadCoins !== false && (!projectSearch || c.name.toLowerCase().includes(projectSearch.toLowerCase()))).length === 0 && (
                                                        <span className="text-xs opacity-50 italic col-span-full text-center">Nenhum colaborador encontrado.</span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="md:col-span-4 flex justify-end gap-2 mt-2">
                                                {projectForm.id && <Button variant="secondary" onClick={() => setProjectForm({ id: null, emoji: '🚀', name: '', managerId: '', participants: [], startDate: '', endDate: '' })}>Cancelar</Button>}
                                                <Button onClick={() => {
                                                    if(!projectForm.name || !projectForm.managerId || !projectForm.endDate) return alert("Preencha Nome, Responsável e Data Fim.");
                                                    saveProject(sanitizeForFirebase({ ...projectForm, id: projectForm.id || `proj_${Date.now()}` }));
                                                    setProjectForm({ id: null, emoji: '🚀', name: '', managerId: '', participants: [], startDate: '', endDate: '' });
                                                }} icon={Save} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-md border-0">{projectForm.id ? 'Salvar Alterações' : 'Criar Projeto'}</Button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={`overflow-x-auto border rounded-lg ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                        <table className={`w-full text-xs text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                            <thead className={`uppercase font-bold ${isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>
                                                <tr><th className="p-3">Projeto</th><th className="p-3">Responsável</th><th className="p-3 text-center">Participantes</th><th className="p-3 text-center">Prazos</th><th className="p-3 text-center">Ações</th></tr>
                                            </thead>
                                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                {projectsDB.map((p) => {
                                                    const resp = data.collaborators.find(c => c.id === p.managerId)?.name || 'N/D';
                                                    return (
                                                        <tr key={p.id} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}>
                                                            <td className="p-3 font-bold text-sm"><span className="mr-2">{p.emoji}</span>{p.name}</td>
                                                            <td className="p-3 font-medium text-indigo-600 dark:text-indigo-400">{resp}</td>
                                                            <td className="p-3 text-center font-bold">{p.participants?.length || 0}</td>
                                                            <td className="p-3 text-center font-mono opacity-80">{p.startDate ? new Date(p.startDate+'T12:00:00').toLocaleDateString('pt-BR') : '-'} até {new Date(p.endDate+'T12:00:00').toLocaleDateString('pt-BR')}</td>
                                                            <td className="p-3 text-center">
                                                                <button onClick={() => setProjectForm(p)} className="text-blue-500 p-1.5 hover:bg-blue-100 rounded transition-colors"><Edit3 size={16}/></button>
                                                                <button onClick={() => { if(window.confirm("Excluir projeto?")) delProject(p.id); }} className="text-red-500 p-1.5 hover:bg-red-100 rounded transition-colors"><Trash2 size={16}/></button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                {projectsDB.length === 0 && <tr><td colSpan="5" className="p-6 text-center italic opacity-50">Nenhum projeto cadastrado.</td></tr>}
                                            </tbody>
                                        </table>
                                    </div>
                                </Card>
                            )}
                        </div>
                    )}
                </div>

                {/* --- MODAL DO RELATÓRIO AVANÇADO --- */}
                {isReportModalOpen && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => !isGeneratingReport && setIsReportModalOpen(false)}>
                        <div className={`p-6 rounded-xl w-full max-w-lg shadow-2xl flex flex-col ${isDarkMode ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white text-gray-800 border border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            
                            <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                <h3 className="font-bold text-lg flex items-center gap-2 text-purple-500"><FileText size={20}/> Gerador de Relatórios</h3>
                                <button onClick={() => !isGeneratingReport && setIsReportModalOpen(false)}><X size={20} className="text-gray-400 hover:text-red-500"/></button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="text-xs font-bold block mb-1">Formato do Arquivo</label>
                                    <div className="flex gap-2">
                                        <button onClick={() => setReportParams({...reportParams, format: 'pdf'})} className={`flex-1 py-2 rounded-lg font-bold text-sm border-2 transition-all flex justify-center gap-2 ${reportParams.format === 'pdf' ? 'border-red-500 bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400' : 'border-gray-200 text-gray-500 dark:border-slate-700 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700'}`}><FileText size={18}/> PDF</button>
                                        <button onClick={() => setReportParams({...reportParams, format: 'excel'})} className={`flex-1 py-2 rounded-lg font-bold text-sm border-2 transition-all flex justify-center gap-2 ${reportParams.format === 'excel' ? 'border-green-500 bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400' : 'border-gray-200 text-gray-500 dark:border-slate-700 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700'}`}><FileSpreadsheet size={18}/> Excel</button>
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="text-xs font-bold block mb-1">Tipo de Relatório</label>
                                    <select className={`w-full p-2.5 rounded-lg border text-sm outline-none font-bold ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={reportParams.type} onChange={e => setReportParams({...reportParams, type: e.target.value, collabId: 'todos'})}>
                                        <option value="geral">Totalizado (Visão Geral da Empresa)</option>
                                        <option value="geral_indiv">Totalizado com Individualização por Funcionário</option>
                                        <option value="individual">Individualizado (Página/Aba por Funcionário)</option>
                                    </select>
                                </div>

                                {/* REQUISITO 1: SELETOR DE FUNCIONÁRIO (Aparece apenas se Individualizado) */}
                                {reportParams.type === 'individual' && (
                                    <div className="animate-fade-in">
                                        <label className="text-xs font-bold block mb-1 text-purple-500">Selecione o Funcionário</label>
                                        <select 
                                            className={`w-full p-2.5 rounded-lg border text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`} 
                                            value={reportParams.collabId} 
                                            onChange={e => setReportParams({...reportParams, collabId: e.target.value})}
                                        >
                                            <option value="todos">Gerar para TODOS (Uma página para cada)</option>
                                            {data.collaborators.filter(c => c.participatesAcadCoins !== false).sort((a,b)=>a.name.localeCompare(b.name)).map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                    <label className="text-xs font-bold block mb-3 uppercase tracking-wider text-blue-500">Período de Análise</label>
                                    <div className="flex gap-4 mb-3">
                                        <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" name="rep_period" checked={reportParams.periodMode === 'trimestre'} onChange={() => setReportParams({...reportParams, periodMode: 'trimestre'})} className="text-blue-600 focus:ring-blue-500"/> Por Trimestre</label>
                                        <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" name="rep_period" checked={reportParams.periodMode === 'custom'} onChange={() => setReportParams({...reportParams, periodMode: 'custom'})} className="text-blue-600 focus:ring-blue-500"/> Data Específica</label>
                                    </div>

                                    {reportParams.periodMode === 'trimestre' ? (
                                        <div className="grid grid-cols-2 gap-3 animate-fade-in">
                                            <input type="number" className={`w-full p-2 rounded border text-sm font-bold text-center ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={reportParams.year} onChange={e => setReportParams({...reportParams, year: e.target.value})} placeholder="Ano"/>
                                            <select className={`w-full p-2 rounded border text-sm font-bold ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`} value={reportParams.quarter} onChange={e => setReportParams({...reportParams, quarter: e.target.value})}>
                                                <option value="q1">Q1 (Jan a Mar)</option><option value="q2">Q2 (Abr a Jun)</option><option value="q3">Q3 (Jul a Set)</option><option value="q4">Q4 (Out a Dez)</option>
                                            </select>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-3 animate-fade-in">
                                            <div><label className="text-[10px] uppercase font-bold block mb-1">De:</label><input type="date" className={`w-full p-2 rounded border text-sm ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white invert-calendar' : 'bg-white border-gray-300'}`} value={reportParams.startDate} onChange={e => setReportParams({...reportParams, startDate: e.target.value})}/></div>
                                            <div><label className="text-[10px] uppercase font-bold block mb-1">Até:</label><input type="date" className={`w-full p-2 rounded border text-sm ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white invert-calendar' : 'bg-white border-gray-300'}`} value={reportParams.endDate} onChange={e => setReportParams({...reportParams, endDate: e.target.value})}/></div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-end gap-2 mt-6 pt-4 border-t dark:border-slate-700">
                                <Button variant="secondary" onClick={() => setIsReportModalOpen(false)} disabled={isGeneratingReport} className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}>Cancelar</Button>
                                <Button onClick={async () => {
                                    setIsGeneratingReport(true);
                                    try {
                                        // ==========================================
                                        // MOTOR DE GERAÇÃO DO RELATÓRIO
                                        // ==========================================
                                        let dStart, dEnd;
                                        if (reportParams.periodMode === 'trimestre') {
                                            const y = reportParams.year;
                                            const qMap = { q1: [`${y}-01-01`, `${y}-03-31`], q2: [`${y}-04-01`, `${y}-06-30`], q3: [`${y}-07-01`, `${y}-09-30`], q4: [`${y}-10-01`, `${y}-12-31`] };
                                            [dStart, dEnd] = qMap[reportParams.quarter];
                                        } else {
                                                if (!reportParams.startDate || !reportParams.endDate) throw new Error("Preencha as datas do período.");
                                            dStart = reportParams.startDate; dEnd = reportParams.endDate;
                                        }
                                        
                                        // CORREÇÃO 1 (DATAS): Adicionado 'T12:00:00' para evitar que o fuso horário (GMT-3) atrase o dia impresso no PDF
                                        const periodTitle = reportParams.periodMode === 'trimestre' ?
                                            `${reportParams.quarter.toUpperCase()} de ${reportParams.year}` : `${new Date(dStart + 'T12:00:00').toLocaleDateString('pt-BR')} até ${new Date(dEnd + 'T12:00:00').toLocaleDateString('pt-BR')}`;

                                        // Filtra os dados
                                        const fDist = distDB.filter(d => d.date >= dStart && d.date <= dEnd);
                                        const fRed = redemptionsDB.filter(r => r.date.split('T')[0] >= dStart && r.date.split('T')[0] <= dEnd && r.status !== 'Cancelado');
                                        
                                        // Calcula Totais Globais
                                        const sumDoado = fDist.filter(d => d.quantity > 0 && d.type === 'doacao').reduce((acc, c) => acc + parseFloat(c.quantity), 0);
                                        const sumResgateCoins = Math.abs(fDist.filter(d => d.quantity < 0 && d.type === 'resgate').reduce((acc, c) => acc + parseFloat(c.quantity), 0));
                                        const sumBens = fRed.length;
                                        
                                        // NOVOS DADOS PARA RELATÓRIO GERAL
                                        const repStats = getStatsAtDate(dEnd);
                                        const totalCaixinha = repStats.tCaixa;
                                        const cotacaoAtual = repStats.uValue;
                                        const bonusUnitario = repStats.equalBonusPerPerson || 0;
                                        const totalBonusGlobal = repStats.poolEqual || 0;
                                        
                                        // CORREÇÃO 2 (MOEDAS): Vincula as moedas distribuídas diretamente ao motor da cotação, igualando a matemática e o Equivalente em R$
                                        const sumDist = repStats.tDist;
                                        const valorTotalDistribuido = sumDist * cotacaoAtual;
                                        const totalDisponivelParaDist = valorTotalDistribuido + totalBonusGlobal;
                                        
                                        const sumBensValue = fRed.reduce((acc, r) => acc + parseFloat(r.value || r.prizeValue || r.costInCoins || 0), 0);

                                        // Prepara Agrupamento por Funcionário
                                        const byUser = {};
                                        data.collaborators.forEach(c => {
                                            let isEligible = false;
                                            if (c.participatesAcadCoins !== false) {
                                                const start = c.acadCoinsStartDate;
                                                const end = c.acadCoinsEndDate;
                                                if ((!start || start <= dEnd) && (!end || end >= dEnd)) {
                                                    isEligible = true;
                                                }
                                            }

                                            byUser[c.id] = { 
                                                id: c.id, name: c.name, 
                                                dept: data.departments.find(dep=>dep.id===c.departmentId)?.name || 'N/D', 
                                                recebidos: 0, resgatados: 0, doados: 0, bens: 0, bensValue: 0, 
                                                bonus: isEligible ? bonusUnitario : 0, historico: [] 
                                            };
                                        });

                                        fDist.forEach(d => {
                                            if (!byUser[d.collabId]) return;
                                            byUser[d.collabId].historico.push(d);
                                            const q = parseFloat(d.quantity);
                                            if (q > 0 && d.type !== 'doacao') byUser[d.collabId].recebidos += q;
                                            if (q > 0 && d.type === 'doacao') byUser[d.collabId].recebidos += q; 
                                            if (q < 0 && d.type === 'doacao') byUser[d.collabId].doados += Math.abs(q);
                                            if (q < 0 && d.type === 'resgate') byUser[d.collabId].resgatados += Math.abs(q);
                                        });

                                        fRed.forEach(r => {
                                            if(byUser[r.collabId]) {
                                                byUser[r.collabId].bens += 1;
                                                byUser[r.collabId].bensValue += parseFloat(r.value || r.prizeValue || r.costInCoins || 0);
                                            }
                                        });

                                        // Filtra usuários ativos e ORDENA POR DEPARTAMENTO E NOME
                                        let activeUsers = Object.values(byUser).filter(u => u.historico.length > 0 || u.bens > 0 || u.bonus > 0);
                                        activeUsers.sort((a, b) => {
                                            const deptDiff = a.dept.localeCompare(b.dept);
                                            if (deptDiff !== 0) return deptDiff;
                                            return a.name.localeCompare(b.name);
                                        });
                                        
                                        if (reportParams.type === 'individual' && reportParams.collabId !== 'todos') {
                                            activeUsers = activeUsers.filter(u => u.id === reportParams.collabId);
                                        }

                                        if (activeUsers.length === 0) throw new Error("Nenhum dado financeiro encontrado no período.");

                                        // --- GERAÇÃO PDF ---
                                        if (reportParams.format === 'pdf') {
                                            if (typeof loadPdfLibs !== 'undefined') await loadPdfLibs();
                                            const PDFConstructor = (window.jspdf && window.jspdf.jsPDF) ? window.jspdf.jsPDF : window.jsPDF;
                                            const doc = new PDFConstructor('p', 'mm', 'a4');
                                            const primary = [30, 58, 138]; const accent = [16, 185, 129];
                                            
                                            const drawHeader = (title) => {
                                                doc.setFillColor(...primary); doc.rect(0, 0, 210, 25, 'F');
                                                doc.setTextColor(255); doc.setFontSize(16); doc.setFont("helvetica", "bold");
                                                doc.text(title, 14, 12);
                                                doc.setFontSize(10); doc.setFont("helvetica", "normal");
                                                doc.text(`Período: ${periodTitle}`, 14, 18);
                                                return 30;
                                            };

                                            const fmt = (val) => Number(val || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

                                            const drawReportCards = (startY, d) => {
                                                let currY = startY;
                                                const w = 58; 
                                                const gap = 4; 
                                                const x1 = 14; 
                                                const x2 = x1 + w + gap; 
                                                const x3 = x2 + w + gap; 
                                                
                                                const drawCard = (x, y, bgArr, title, val1, val2) => {
                                                    doc.setFillColor(...bgArr); 
                                                    doc.roundedRect(x, y, w, 22, 2, 2, 'F');
                                                    doc.setTextColor(255, 255, 255); 
                                                    doc.setFontSize(8); doc.setFont("helvetica", "bold"); 
                                                    doc.text(title, x+3, y+6);
                                                    doc.setFontSize(11); 
                                                    doc.text(val1, x+3, y+13);
                                                    if(val2) {
                                                        doc.setTextColor(240, 240, 240); 
                                                        doc.setFontSize(6.5); doc.setFont("helvetica", "normal"); 
                                                        doc.text(val2, x+3, y+18);
                                                    }
                                                };

                                                const vProp = d.caixinha * ((parseFloat(config.percProportional)||0)/100);
                                                const vEq = d.caixinha * ((parseFloat(config.percEqual)||0)/100);
                                                const vComp = d.caixinha * ((parseFloat(config.percCompany)||0)/100);

                                                drawCard(x1, currY, [245, 158, 11], "Total Caixinha", `R$ ${fmt(d.caixinha)}`, `Geral: R$ ${fmt(vProp)} | Igual: R$ ${fmt(vEq)}`);
                                                drawCard(x2, currY, [59, 130, 246], d.isIndiv ? "Recebido" : "Total Distribuído", `${fmt(d.distCoins)} Coins`, `Equiv: R$ ${fmt(d.distCoins * d.cotacao)}`);
                                                drawCard(x3, currY, [168, 85, 247], "Cotação 1 Coin", `R$ ${fmt(d.cotacao)}`, "Valor unitário no período");
                                                currY += 26;

                                                drawCard(x1, currY, [20, 184, 166], "Total Bônus", `R$ ${fmt(d.bonus)}`, "Distribuição Igualitária");
                                                drawCard(x2, currY, [16, 185, 129], "Disp. p/ Distribuição", `R$ ${fmt(d.totalDisp)}`, "Moedas Distribuídas + Bônus");
                                                drawCard(x3, currY, [239, 68, 68], "Total Resgatado", `${fmt(d.resgCoins)} Coins`, `Equiv: R$ ${fmt(d.resgCoins * d.cotacao)}`);
                                                currY += 26;

                                                drawCard(x1, currY, [234, 179, 8], "Total Doados", `${fmt(d.doadoCoins)} Coins`, `Equiv: R$ ${fmt(d.doadoCoins * d.cotacao)}`);
                                                drawCard(x2, currY, [236, 72, 153], "Bens Físicos Resgatados", `${d.bensQtd} Itens`, `Valor Acumulado: R$ ${fmt(d.bensVal)}`);

                                                currY += 28;
                                                return currY;
                                            };

                                            if (reportParams.type === 'geral' || reportParams.type === 'geral_indiv') {
                                                let y = drawHeader("Relatório Geral de AcadCoins");
                                                
                                                y = drawReportCards(y, {
                                                    isIndiv: false, caixinha: totalCaixinha, cotacao: cotacaoAtual,
                                                    distCoins: sumDist, resgCoins: sumResgateCoins, doadoCoins: sumDoado,
                                                    bensQtd: sumBens, bensVal: sumBensValue,
                                                    bonus: totalBonusGlobal, totalDisp: totalDisponivelParaDist
                                                });

                                                // Tabela Detalhada Individualização COM SUBTOTALIZAÇÃO POR DEPARTAMENTO
                                                if (reportParams.type === 'geral_indiv') {
                                                    doc.setFontSize(12); doc.setTextColor(0); doc.text("Detalhamento por Funcionário", 14, y);
                                                    
                                                    let tbData = [];
                                                    let tRecGeral = 0, tResgGeral = 0, tDoadoGeral = 0, tItensGeral = 0, tBonusGeral = 0, tPagarGeral = 0;
                                                    let tRecDept = 0, tResgDept = 0, tDoadoDept = 0, tItensDept = 0, tBonusDept = 0, tPagarDept = 0;
                                                    
                                                    let currentDept = activeUsers[0].dept;

                                                    const addSubtotalRow = (deptName) => {
                                                        tbData.push([
                                                            { content: `Subtotal ${deptName}:`, colSpan: 2, styles: { halign: 'right', fontStyle: 'bold', fillColor: [240, 244, 248] } },
                                                            { content: fmt(tRecDept), styles: { fillColor: [240, 244, 248], fontStyle: 'bold', halign: 'right' } },
                                                            { content: fmt(tResgDept), styles: { fillColor: [240, 244, 248], fontStyle: 'bold', halign: 'right' } },
                                                            { content: fmt(tDoadoDept), styles: { fillColor: [240, 244, 248], fontStyle: 'bold', halign: 'right' } },
                                                            { content: tItensDept.toString(), styles: { halign: 'center', fillColor: [240, 244, 248], fontStyle: 'bold' } },
                                                            { content: `R$ ${fmt(tBonusDept)}`, styles: { fillColor: [240, 244, 248], fontStyle: 'bold', halign: 'right' } },
                                                            { content: `R$ ${fmt(tPagarDept)}`, styles: { fillColor: [220, 252, 231], textColor: [21, 128, 61], fontStyle: 'bold', halign: 'right' } }
                                                        ]);
                                                        tRecDept = 0; tResgDept = 0; tDoadoDept = 0; tItensDept = 0; tBonusDept = 0; tPagarDept = 0;
                                                    };

                                                    activeUsers.forEach((u, index) => {
                                                        if (u.dept !== currentDept) {
                                                            addSubtotalRow(currentDept);
                                                            currentDept = u.dept;
                                                        }

                                                        const saldoMoedas = u.recebidos - u.resgatados - u.doados;
                                                        const totalPagar = (saldoMoedas * cotacaoAtual) + u.bonus;
                                                        
                                                        // Acumula Depto
                                                        tRecDept += u.recebidos; tResgDept += u.resgatados; tDoadoDept += u.doados;
                                                        tItensDept += u.bens; tBonusDept += u.bonus; tPagarDept += totalPagar;

                                                        // Acumula Geral
                                                        tRecGeral += u.recebidos; tResgGeral += u.resgatados; tDoadoGeral += u.doados;
                                                        tItensGeral += u.bens; tBonusGeral += u.bonus; tPagarGeral += totalPagar;

                                                        tbData.push([u.name, u.dept, fmt(u.recebidos), fmt(u.resgatados), fmt(u.doados), u.bens.toString(), `R$ ${fmt(u.bonus)}`, `R$ ${fmt(totalPagar)}`]);

                                                        if (index === activeUsers.length - 1) {
                                                            addSubtotalRow(currentDept);
                                                        }
                                                    });

                                                    doc.autoTable({ 
                                                        startY: y+5, 
                                                        head: [['Funcionário', 'Depto', 'Rec.', 'Resg.', 'Doado', 'Itens', 'Bônus', 'Total Pagar']], 
                                                        body: tbData, 
                                                        foot: [[
                                                            { content: 'TOTAL GERAL DA EMPRESA:', colSpan: 2, styles: { halign: 'right', fontStyle: 'bold' } },
                                                            fmt(tRecGeral), fmt(tResgGeral), fmt(tDoadoGeral), tItensGeral.toString(), `R$ ${fmt(tBonusGeral)}`, `R$ ${fmt(tPagarGeral)}`
                                                        ]],
                                                        theme: 'grid', 
                                                        headStyles: { fillColor: primary }, 
                                                        footStyles: { fillColor: [230, 230, 230], textColor: [0, 0, 0], fontStyle: 'bold' },
                                                        alternateRowStyles: { fillColor: [249, 250, 251] }, // ZEBRADO NAS LINHAS COMUNS
                                                        columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'center' }, 6: { halign: 'right' }, 7: { halign: 'right', fontStyle: 'bold', textColor: [22, 163, 74] } },
                                                        styles: { fontSize: 8, cellPadding: 1.5 }
                                                    });
                                                }
                                            } 
                                            else if (reportParams.type === 'individual') {
                                                activeUsers.forEach((u, idx) => {
                                                    if (idx > 0) doc.addPage();
                                                    let y = drawHeader("Extrato Individual AcadCoins");
                                                    
                                                    doc.setFontSize(12); doc.setTextColor(0); doc.setFont("helvetica", "bold"); doc.text(u.name, 14, y);
                                                    doc.setFontSize(10); doc.setFont("helvetica", "normal"); doc.text(`Departamento: ${u.dept}`, 14, y+6);
                                                    y += 12;

                                                    const userTotalDisp = (u.recebidos * cotacaoAtual) + u.bonus;

                                                    y = drawReportCards(y, {
                                                        isIndiv: true, caixinha: totalCaixinha, cotacao: cotacaoAtual,
                                                        distCoins: u.recebidos, resgCoins: u.resgatados, doadoCoins: u.doados,
                                                        bensQtd: u.bens, bensVal: u.bensValue,
                                                        bonus: u.bonus, totalDisp: userTotalDisp
                                                    });

                                                    let userSaldo = distDB.filter(d => d.collabId === u.id && d.date < dStart).reduce((a,b) => a + parseFloat(b.quantity), 0);
                                                    
                                                    doc.setFontSize(11); doc.setTextColor(0); doc.setFont("helvetica", "bold"); 
                                                    doc.text(`Histórico de Lançamentos (Saldo Anterior: ${fmt(userSaldo)} Coins)`, 14, y);
                                                    y += 4;

                                                    const sortedHist = u.historico.sort((a,b)=>new Date(a.date)-new Date(b.date));
                                                    let runningBalance = userSaldo;
                                                    const tbData = sortedHist.map(h => {
                                                        const q = parseFloat(h.quantity);
                                                        runningBalance += q;
                                                        return [
                                                            new Date(h.date+'T12:00:00').toLocaleDateString('pt-BR'), 
                                                            h.reason, 
                                                            q > 0 ? `+${fmt(q)}` : fmt(q),
                                                            fmt(runningBalance)
                                                        ];
                                                    });

                                                    doc.autoTable({ 
                                                        startY: y, 
                                                        head: [['Data', 'Descrição / Motivo', 'Valor (Coins)', 'Saldo Acumulado']], 
                                                        body: tbData, 
                                                        theme: 'grid', 
                                                        headStyles: { fillColor: primary }, 
                                                        alternateRowStyles: { fillColor: [249, 250, 251] },
                                                        columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right', fontStyle: 'bold' } } 
                                                    });
                                                });
                                            }

                                            window.open(doc.output('bloburl'), '_blank');
                                        } 
                                        // --- GERAÇÃO EXCEL COM SUBTOTALIZAÇÃO ---
                                        else {
                                            if (typeof loadImportLibs !== 'undefined') await loadImportLibs();
                                            if (!window.XLSX) throw new Error("Biblioteca XLSX não disponível.");

                                            let dataExport = [];
                                            if (reportParams.type === 'geral' || reportParams.type === 'geral_indiv') {
                                                dataExport.push({ "Período": periodTitle, "Total Caixinha": totalCaixinha, "Total Distribuído": sumDist, "Cotação 1 Coin": cotacaoAtual, "Total Bônus": totalBonusGlobal, "Total Disponível (Bônus + Dist)": totalDisponivelParaDist, "Total Resgatado (Geral)": sumResgateCoins, "Total Doado (Geral)": sumDoado, "Total Itens Resgatados": sumBens, "Valor Total Bens R$": sumBensValue });
                                                
                                                if (reportParams.type === 'geral_indiv') {
                                                    dataExport.push({});
                                                    dataExport.push({ "Período": "DETALHAMENTO POR FUNCIONÁRIO", "Depto": "", "Total Recebido (Coins)": "", "Total Resgatado": "", "Total Doado": "", "Total Itens Resgatados": "", "Bônus R$": "", "Total a Pagar R$": "" });
                                                    
                                                    let tRecGeral = 0, tResgGeral = 0, tDoadoGeral = 0, tItensGeral = 0, tBonusGeral = 0, tPagarGeral = 0;
                                                    let tRecDept = 0, tResgDept = 0, tDoadoDept = 0, tItensDept = 0, tBonusDept = 0, tPagarDept = 0;
                                                    
                                                    let currentDept = activeUsers[0].dept;

                                                    const pushDeptSubtotal = (deptName) => {
                                                        dataExport.push({ "Período": `SUBTOTAL ${deptName}:`, "Depto": "", "Total Recebido (Coins)": tRecDept, "Total Resgatado": tResgDept, "Total Doado": tDoadoDept, "Total Itens Resgatados": tItensDept, "Bônus R$": tBonusDept, "Total a Pagar R$": tPagarDept });
                                                        dataExport.push({}); // Linha em branco
                                                        tRecDept = 0; tResgDept = 0; tDoadoDept = 0; tItensDept = 0; tBonusDept = 0; tPagarDept = 0;
                                                    };

                                                    activeUsers.forEach((u, index) => {
                                                        if (u.dept !== currentDept) {
                                                            pushDeptSubtotal(currentDept);
                                                            currentDept = u.dept;
                                                        }

                                                        const saldoMoedas = u.recebidos - u.resgatados - u.doados;
                                                        const totalPagar = (saldoMoedas * cotacaoAtual) + u.bonus;
                                                        
                                                        // Acumula Depto
                                                        tRecDept += u.recebidos; tResgDept += u.resgatados; tDoadoDept += u.doados;
                                                        tItensDept += u.bens; tBonusDept += u.bonus; tPagarDept += totalPagar;

                                                        // Acumula Geral
                                                        tRecGeral += u.recebidos; tResgGeral += u.resgatados; tDoadoGeral += u.doados;
                                                        tItensGeral += u.bens; tBonusGeral += u.bonus; tPagarGeral += totalPagar;

                                                        dataExport.push({ "Período": u.name, "Depto": u.dept, "Total Recebido (Coins)": u.recebidos, "Total Resgatado": u.resgatados, "Total Doado": u.doados, "Total Itens Resgatados": u.bens, "Bônus R$": u.bonus, "Total a Pagar R$": totalPagar });
                                                        
                                                        if (index === activeUsers.length - 1) {
                                                            pushDeptSubtotal(currentDept);
                                                        }
                                                    });

                                                    dataExport.push({ "Período": "TOTAL GERAL DA EMPRESA", "Depto": "", "Total Recebido (Coins)": tRecGeral, "Total Resgatado": tResgGeral, "Total Doado": tDoadoGeral, "Total Itens Resgatados": tItensGeral, "Bônus R$": tBonusGeral, "Total a Pagar R$": tPagarGeral });
                                                }
                                            } else {
                                                activeUsers.forEach(u => {
                                                    dataExport.push({ "Colaborador": u.name, "Depto": u.dept, "Data": "", "Motivo": "TOTAIS", "Quantidade": `Rec: ${u.recebidos} | Resg: ${u.resgatados} | Doado: ${u.doados}` });
                                                    u.historico.sort((a,b)=>new Date(b.date)-new Date(a.date)).forEach(h => {
                                                        dataExport.push({ "Colaborador": "", "Depto": "", "Data": new Date(h.date+'T00:00:00').toLocaleDateString('pt-BR'), "Motivo": h.reason, "Quantidade": h.quantity });
                                                    });
                                                    dataExport.push({});
                                                });
                                            }

                                            const ws = window.XLSX.utils.json_to_sheet(dataExport);
                                            const wb = window.XLSX.utils.book_new();
                                            window.XLSX.utils.book_append_sheet(wb, ws, "Relatorio_AcadCoins");
                                            window.XLSX.writeFile(wb, `AcadCoins_${reportParams.type}_${new Date().toISOString().split('T')[0]}.xlsx`);
                                        }

                                        setIsReportModalOpen(false);
                                    } catch (error) {
                                        alert("Erro ao gerar relatório: " + error.message);
                                    } finally {
                                        setIsGeneratingReport(false);
                                    }
                                }} className="bg-purple-600 hover:bg-purple-700 text-white shadow-md border-0 w-32">
                                    {isGeneratingReport ? <Loader2 size={16} className="animate-spin mx-auto"/> : 'Gerar Relatório'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
                    {/* MODAL DE AÇÃO: CAIXINHA (EFETIVAR OU CANCELAR) */}
                    {actionBoxItem && (
                        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                            <div className={`w-full max-w-md rounded-xl shadow-2xl flex flex-col overflow-hidden ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`}>
                                <div className={`p-4 border-b flex justify-between items-center ${isDarkMode ? 'border-slate-700 bg-slate-900/50' : 'border-gray-200 bg-gray-50'}`}>
                                    <h3 className="font-bold text-lg flex items-center gap-2">
                                        <CheckCircle className="text-green-500" /> Gerenciar Registro
                                    </h3>
                                </div>

                                <div className="p-5 space-y-4">
                                    <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                        O que deseja fazer com o registro <strong>{actionBoxItem.description || 'selecionado'}</strong>?
                                    </p>

                                    <div className="space-y-1">
                                        <label className={`text-xs font-bold uppercase ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                            Motivo do Cancelamento <span className="text-red-500">* (Para cancelar)</span>
                                        </label>
                                        <textarea 
                                            value={cancelReason}
                                            onChange={(e) => setCancelReason(e.target.value)}
                                            placeholder="Informe por que está cancelando..."
                                            className={`w-full p-2 text-sm rounded border outline-none min-h-[80px] ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-gray-300'}`}
                                        />
                                    </div>
                                </div>

                                <div className={`p-4 border-t flex justify-end gap-3 ${isDarkMode ? 'border-slate-700 bg-slate-900/50' : 'border-gray-200 bg-gray-50'}`}>
                                    <button 
                                        onClick={() => setActionBoxItem(null)}
                                        className="px-4 py-2 text-sm font-bold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                                    >
                                        Voltar
                                    </button>
                                    <button 
                                        disabled={!cancelReason.trim()}
                                        onClick={() => {
                                            const updatedItem = {
                                                ...actionBoxItem,
                                                type: 'Cancelado',
                                                status: 'Cancelado',
                                                motivoCancelamento: cancelReason,
                                                tipoOriginal: actionBoxItem.tipoOriginal || actionBoxItem.type
                                            };
                                            saveCaixa(updatedItem);
                                            setActionBoxItem(null);
                                        }}
                                        className={`px-4 py-2 text-sm font-bold text-white rounded-lg transition-colors flex items-center gap-2 ${!cancelReason.trim() ? 'bg-slate-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600 shadow-md'}`}
                                    >
                                        <X size={16} /> Cancelar
                                    </button>
                                    <button 
                                        onClick={() => {
                                            const updatedItem = {
                                                ...actionBoxItem,
                                                type: 'Realizado',
                                                status: 'Realizado',
                                                tipoOriginal: actionBoxItem.tipoOriginal || actionBoxItem.type
                                            };
                                            saveCaixa(updatedItem);
                                            setActionBoxItem(null);
                                        }}
                                        className="px-4 py-2 text-sm font-bold text-white bg-green-500 hover:bg-green-600 rounded-lg shadow-md transition-colors flex items-center gap-2"
                                    >
                                      <CheckCircle size={16} /> Finalizar
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                {/* MODAL RECONHECER COLEGA */}
                {isRecognizeModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsRecognizeModalOpen(false)}>
                        <div className={`p-6 rounded-xl w-full max-w-md shadow-2xl flex flex-col ${isDarkMode ? 'bg-slate-800 text-white border-slate-700' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                <h3 className="font-bold text-lg flex items-center gap-2 text-cyan-500"><Award size={20}/> Reconhecer um Colega</h3>
                                <button onClick={() => setIsRecognizeModalOpen(false)}><X size={20}/></button>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label className="text-xs font-bold uppercase block mb-1 opacity-70">Colega a ser reconhecido</label>
                                    <select value={recognizeForm.targetId} onChange={e=>setRecognizeForm({...recognizeForm, targetId: e.target.value})} className={`w-full p-2.5 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}>
                                        <option value="">Selecione o colega...</option>
                                        {data.collaborators.filter(c => c.id !== loggedCollabId && c.participatesAcadCoins !== false).sort((a,b)=>a.name.localeCompare(b.name)).map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase block mb-1 opacity-70">Motivo do Reconhecimento</label>
                                    <select value={recognizeForm.reason} onChange={e=>setRecognizeForm({...recognizeForm, reason: e.target.value})} className={`w-full p-2.5 border rounded-lg text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}>
                                        <option value="">Selecione o motivo...</option>
                                        {((getActiveRule(localConfig.reconhecimentoHistory, todayStr).recognitionReasons) || localConfig.recognitionReasons || 'Compartilhar Conhecimento, Atitudes Positivas, Inovação e sugestão de melhoria, Auxílio Intersetorial').split(',').map(r => r.trim()).filter(Boolean).map(r => (
                                            <option key={r} value={r}>{r}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end gap-2 border-t pt-4 dark:border-slate-700">
                                <Button variant="secondary" onClick={() => setIsRecognizeModalOpen(false)}>Cancelar</Button>
                                <Button onClick={async () => {
                                    if(!recognizeForm.targetId || !recognizeForm.reason) return alert("Preencha todos os campos.");
                                    // Registra a indicação (O admin roda a apuração na tela de config)
                                    await saveDist(sanitizeForFirebase({ id: `ind_${Date.now()}`, date: todayStr, collabId: recognizeForm.targetId, fromId: loggedCollabId, quantity: 0, reason: `Indicado por: ${myCollabRecord?.name} | Motivo: ${recognizeForm.reason}`, type: 'indicacao' }));
                                    alert("Reconhecimento enviado! O RH fará a apuração dos prêmios no fechamento.");
                                    setIsRecognizeModalOpen(false); setRecognizeForm({targetId:'', reason:''});
                                }} className="bg-cyan-500 hover:bg-cyan-600 text-white border-0">Enviar Indicação</Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL PRESENTEAR COLEGA */}
                {isGiftModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsGiftModalOpen(false)}>
                        <div className={`p-6 rounded-xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh] ${isDarkMode ? 'bg-slate-800 text-white border-slate-700' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                <h3 className="font-bold text-lg flex items-center gap-2 text-purple-500"><Gift size={20}/> Presentear Colegas</h3>
                                <button onClick={() => setIsGiftModalOpen(false)}><X size={20}/></button>
                            </div>
                            
                            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4">
                                <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg text-xs leading-relaxed">
                                    Você tem <b>{myGiftAllowanceLeft} moedas de presente</b> disponíveis. Elas expiram no fechamento e não descontam do seu saldo real.
                                </div>

                                {/* BUSCA E SELEÇÃO DE COLEGAS */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="text-xs font-bold uppercase block opacity-70">1. Selecione os Colegas</label>
                                        <div className="relative w-40">
                                            <Search size={12} className="absolute left-2 top-2 text-gray-400"/>
                                            <input 
                                                type="text" placeholder="Buscar..." value={giftSearch} onChange={(e) => setGiftSearch(e.target.value)} 
                                                className={`w-full pl-7 py-1 text-xs border rounded outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                                            />
                                        </div>
                                    </div>
                                    <div className={`max-h-40 overflow-y-auto p-2 border rounded-lg custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                        {(() => {
                                            const periodicityMonths = parseInt(config.giftPeriodicity) || 0;
                                            
                                            const eligibleCollabs = data.collaborators.filter(c => {
                                                if (c.id === loggedCollabId || c.participatesAcadCoins === false) return false;
                                                if (giftSearch && !c.name.toLowerCase().includes(giftSearch.toLowerCase())) return false;
                                                
                                                // Filtro Automático de Carência: Esconde os colegas que já ganharam presente recentemente
                                                if (periodicityMonths > 0) {
                                                    const pastGifts = distDB.filter(d => d.fromId === loggedCollabId && d.collabId === c.id && d.type === 'presente_recebido').sort((a,b) => new Date(b.date) - new Date(a.date));
                                                    if (pastGifts.length > 0) {
                                                        const lastGiftDate = new Date(pastGifts[0].date + 'T12:00:00');
                                                        const currentDate = new Date();
                                                        const monthDiff = (currentDate.getFullYear() - lastGiftDate.getFullYear()) * 12 + (currentDate.getMonth() - lastGiftDate.getMonth());
                                                        if (monthDiff < periodicityMonths) return false; // Oculta o colega da lista
                                                    }
                                                }
                                                return true;
                                            }).sort((a,b)=>a.name.localeCompare(b.name));

                                            if (eligibleCollabs.length === 0) return <p className="text-xs italic text-center p-4 opacity-50">Nenhum colega elegível encontrado (ou todos estão no período de carência).</p>;
                                            
                                            return eligibleCollabs.map(c => (
                                                <label key={c.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-white'}`}>
                                                    <input 
                                                        type="checkbox" 
                                                        className="w-4 h-4 text-purple-500 rounded border-gray-300 focus:ring-purple-500"
                                                        checked={giftForm.targets.includes(c.id)}
                                                        onChange={(e) => {
                                                            const newTargets = e.target.checked 
                                                                ? [...giftForm.targets, c.id] 
                                                                : giftForm.targets.filter(id => id !== c.id);
                                                            setGiftForm({...giftForm, targets: newTargets});
                                                        }}
                                                    />
                                                    <span className="text-sm font-medium">{c.name}</span>
                                                </label>
                                            ));
                                        })()}
                                    </div>
                                </div>

                                {/* QUANTIDADE DE MOEDAS */}
                                <div className="pt-2">
                                    <label className="text-xs font-bold uppercase mb-1 block opacity-70">2. Moedas para CADA colega selecionado</label>
                                    <div className="flex gap-4 items-center">
                                        <input 
                                            type="number" step="0.5" min="0.5" 
                                            className={`w-32 p-2 text-lg font-black text-center border rounded outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-purple-400' : 'text-purple-600 border-gray-300'}`}
                                            value={giftForm.coinsPerTarget} 
                                            onChange={e => setGiftForm({...giftForm, coinsPerTarget: parseFloat(e.target.value) || 0})} 
                                        />
                                        <div className="flex-1 text-xs opacity-70">
                                            <div className="font-bold">Total a ser distribuído:</div>
                                            <div className={`text-lg font-bold ${(giftForm.targets.length * giftForm.coinsPerTarget) > myGiftAllowanceLeft ? 'text-red-500' : 'text-purple-500'}`}>
                                                {giftForm.targets.length * giftForm.coinsPerTarget} moedas
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end gap-2 border-t pt-4 dark:border-slate-700">
                                <Button variant="secondary" onClick={() => setIsGiftModalOpen(false)}>Cancelar</Button>
                                <Button onClick={async () => {
                                    const totalCoins = giftForm.targets.length * giftForm.coinsPerTarget;
                                    
                                    if(giftForm.targets.length === 0) return alert("Selecione pelo menos um colega.");
                                    if(giftForm.coinsPerTarget <= 0) return alert("Informe uma quantidade válida de moedas.");
                                    if(totalCoins > myGiftAllowanceLeft) return alert(`Saldo insuficiente. Você está tentando enviar ${totalCoins}, mas só tem ${myGiftAllowanceLeft} disponíveis.`);
                                    
                                    // VALIDAÇÃO: Data de Liberação
                                    const today = new Date();
                                    const maxDaysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
                                    const releaseDay = Math.min(parseInt(config.releaseDateGift) || 1, maxDaysInMonth);
                                    
                                    if (today.getDate() < releaseDay) {
                                        return alert(`Os créditos para presentear só estarão liberados a partir do dia ${releaseDay} deste mês.`);
                                    }

                                    const uniqueIdBase = Date.now().toString() + Math.random().toString(36).substr(2, 5);
                                    
                                    // Grava a saída total do doador (para abater do limite virtual)
                                    await saveDist(sanitizeForFirebase({ 
                                        id: `gft_out_${uniqueIdBase}`, date: todayStr, collabId: loggedCollabId, fromId: loggedCollabId, 
                                        quantity: 0, reason: `Enviou ${totalCoins} moedas de presente para ${giftForm.targets.length} colega(s)`, type: 'presente_enviado',
                                        usedAllowance: totalCoins // Usa esse campo extra para descontar do saldo virtual
                                    }));
                                    
                                    // Grava a entrada individual para cada colega
                                    for (let i = 0; i < giftForm.targets.length; i++) {
                                        const targetId = giftForm.targets[i];
                                        await saveDist(sanitizeForFirebase({ 
                                            id: `gft_in_${uniqueIdBase}_${i}`, date: todayStr, collabId: targetId, fromId: loggedCollabId, 
                                            quantity: giftForm.coinsPerTarget, reason: `Presente de: ${myCollabRecord?.name}`, type: 'presente_recebido' 
                                        }));
                                    }
                                    
                                    alert("Presentes enviados com sucesso!");
                                    setIsGiftModalOpen(false); 
                                    setGiftForm({ targets: [], coinsPerTarget: 1 });
                                    setGiftSearch('');
                                }} className="bg-purple-500 hover:bg-purple-600 text-white border-0">Confirmar Envio</Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL INDICAÇÕES RECEBIDAS */}
                {showIndicacoesModal && (
                    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setShowIndicacoesModal(false)}>
                        <div className={`p-6 rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[80vh] border ${isDarkMode ? 'bg-slate-800 text-white border-slate-700' : 'bg-white text-gray-800 border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                <h3 className="font-bold text-lg flex items-center gap-2 text-cyan-500"><Award size={20}/> Indicações Recebidas</h3>
                                <button onClick={() => setShowIndicacoesModal(false)}><X size={20}/></button>
                            </div>
                            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4">
                                <p className="text-xs opacity-80 mb-2">Abaixo estão os reconhecimentos que você recebeu dos seus colegas. (Os prêmios por indicação são apurados pelo RH no fechamento do período).</p>
                                {(() => {
                                    // Filtra as indicações recebidas pelo usuário logado
                                    const minhasIndicacoes = distDB.filter(d => d.collabId === loggedCollabId && d.type === 'indicacao').sort((a,b) => new Date(b.date) - new Date(a.date));
                                    
                                    if(minhasIndicacoes.length === 0) return <p className="text-center italic opacity-50 py-8 text-sm">Você ainda não recebeu nenhuma indicação neste ciclo.</p>;

                                    return (
                                        <table className="w-full text-xs text-left">
                                            <thead>
                                                <tr className="opacity-70 font-bold border-b dark:border-slate-700">
                                                    <th className="py-2 w-24">Data</th>
                                                    <th>Detalhes do Reconhecimento</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-dashed dark:divide-slate-700">
                                                {minhasIndicacoes.map(ind => {
                                                    // O motivo é salvo como "Indicado por: Nome | Motivo: X", vamos quebrar isso para exibir bonitinho
                                                    const partes = (ind.reason || '').split('| Motivo:');
                                                    const quemIndicou = partes[0] ? partes[0].replace('Indicado por:', '').trim() : 'Colega';
                                                    const motivoReal = partes[1] ? partes[1].trim() : 'Reconhecimento Positivo';

                                                    return (
                                                        <tr key={ind.id} className="opacity-90 hover:opacity-100">
                                                            <td className="py-3 font-mono align-top">{new Date(ind.date + 'T12:00:00').toLocaleDateString('pt-BR')}</td>
                                                            <td className="py-3">
                                                                <div className="font-bold text-cyan-600 dark:text-cyan-400 text-sm mb-1">{motivoReal}</div>
                                                                <div className="text-[10px] opacity-70">Enviado por: <span className="font-bold">{quemIndicou}</span></div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL PROJETOS DA EMPRESA (E DISTRIBUIÇÃO PELO RESPONSÁVEL) */}
                {isProjectWalletModalOpen && (
                    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => { setIsProjectWalletModalOpen(false); setActiveProjectView(null); }}>
                        <div className={`p-6 rounded-xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh] border ${isDarkMode ? 'bg-slate-800 text-white border-slate-700' : 'bg-white text-gray-800 border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                <div className="flex items-center gap-3">
                                    {activeProjectView && (
                                        <button onClick={() => setActiveProjectView(null)} className={`text-xs font-bold px-2 py-1 rounded transition-colors ${isDarkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'}`}>
                                            &larr; Voltar
                                        </button>
                                    )}
                                    <h3 className="font-bold text-lg flex items-center gap-2 text-indigo-500"><Briefcase size={20}/> Projetos Especiais</h3>
                                </div>
                                <button onClick={() => { setIsProjectWalletModalOpen(false); setActiveProjectView(null); }} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                            </div>
                            
                            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-6 pr-2">
                                {!activeProjectView ? (
                                    // --- GRID DE CARDS DE PROJETOS (VISÃO PÚBLICA) ---
                                    <>
                                        <p className="text-xs opacity-80 mb-2">Acompanhe os projetos em andamento na empresa. O responsável de cada projeto avaliará a dedicação e participação da equipe.</p>
                                        
                                        {projectsDB.length === 0 ? (
                                            <p className="text-center italic opacity-50 py-8 text-sm">Nenhum projeto ativo no momento.</p>
                                        ) : (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {projectsDB.map((proj, idx) => {
                                                    const respName = data.collaborators.find(c => c.id === proj.managerId)?.name || 'N/D';
                                                    // Paleta de gradientes rotativa para os cards ficarem bem bonitos e variados
                                                    const gradients = [
                                                        'from-blue-500 to-indigo-600', 'from-emerald-400 to-teal-500',
                                                        'from-orange-400 to-red-500', 'from-purple-500 to-fuchsia-600',
                                                        'from-cyan-400 to-blue-500', 'from-pink-500 to-rose-500'
                                                    ];
                                                    const grad = gradients[idx % gradients.length];
                                                    
                                                    return (
                                                        <div 
                                                            key={proj.id} 
                                                            onClick={() => setActiveProjectView(proj.id)} 
                                                            className={`cursor-pointer p-4 rounded-xl shadow-md border-0 transition-all hover:scale-105 flex flex-col min-h-[130px] bg-gradient-to-br ${grad} text-white relative overflow-hidden group`}
                                                        >
                                                            <div className="absolute -right-4 -bottom-4 text-7xl opacity-20 group-hover:scale-110 transition-transform">{proj.emoji}</div>
                                                            <h4 className="font-black text-lg leading-tight drop-shadow-sm relative z-10 w-4/5">{proj.name}</h4>
                                                            <div className="mt-auto pt-4 relative z-10">
                                                                <div className="text-[10px] font-bold uppercase opacity-80">Responsável:</div>
                                                                <div className="text-xs truncate font-medium">{respName}</div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    // --- DETALHE DO PROJETO SELECIONADO ---
                                    (() => {
                                        const proj = projectsDB.find(p => p.id === activeProjectView);
                                        if(!proj) return <p className="text-center">Projeto não encontrado.</p>;

                                        const isManager = proj.managerId === loggedCollabId;
                                        const respName = data.collaborators.find(c => c.id === proj.managerId)?.name || 'N/D';
                                        const partsData = data.collaborators.filter(c => proj.participants?.includes(c.id));
                                        
                                        // Limite Máximo POR PESSOA
                                        const maxPerPerson = parseFloat(config.coinsPerProject) || 0;
                                        
                                        // Calcula Data Prevista do Crédito
                                        const endD = new Date(proj.endDate + 'T12:00:00');
                                        endD.setDate(endD.getDate() + (parseInt(config.daysToCreditProject) || 0));
                                        const prevDate = endD.toISOString().split('T')[0];

                                        return (
                                            <div className={`p-4 rounded-xl border shadow-sm animate-fade-in ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-indigo-50/30 border-indigo-200'}`}>
                                                <div className="flex justify-between items-start mb-3 border-b pb-3 dark:border-slate-700">
                                                    <div>
                                                        <h4 className="font-black text-lg text-indigo-600 dark:text-indigo-400">{proj.emoji} {proj.name}</h4>
                                                        <div className="text-xs opacity-80 mt-1">Responsável: <b>{isManager ? 'Você' : respName}</b> | Prazo: {new Date(proj.endDate+'T12:00:00').toLocaleDateString('pt-BR')}</div>
                                                    </div>
                                                    <div className="text-right">
                                                        <span className="block text-[10px] uppercase font-bold opacity-70">Limite p/ Participante</span>
                                                        <span className="text-xl font-black text-emerald-500">Até {maxPerPerson}</span>
                                                    </div>
                                                </div>

                                                <div className="mb-4">
                                                    <span className="text-[10px] font-bold uppercase opacity-70 block mb-2">Equipe do Projeto ({partsData.length}):</span>
                                                    <div className="flex flex-wrap gap-2">
                                                        {partsData.map(c => <span key={c.id} className={`px-2 py-1 rounded text-[10px] font-bold ${c.id === loggedCollabId ? 'bg-indigo-500 text-white shadow-sm' : isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-white border border-gray-200 shadow-sm'}`}>{c.name}</span>)}
                                                    </div>
                                                </div>

                                                {/* ÁREA EXCLUSIVA DO RESPONSÁVEL */}
                                                {isManager && (
                                                    <div className={`mt-4 p-4 rounded-xl border ${isDarkMode ? 'bg-slate-800 border-indigo-900/50' : 'bg-white border-indigo-100 shadow-inner'}`}>
                                                        <div className="flex justify-between items-center mb-3">
                                                            <h5 className="font-bold text-xs uppercase text-indigo-500 flex items-center gap-2"><Award size={14}/> Avaliação em Lote da Equipe</h5>
                                                            <span className={`text-[10px] font-bold px-2 py-1 rounded bg-indigo-100 text-indigo-700`}>Teto: {maxPerPerson} Coins/Pessoa</span>
                                                        </div>
                                                        
                                                        <p className="text-[10px] opacity-70 mb-3">Informe abaixo a quantidade de moedas que cada colaborador merece. Deixe em branco os que não receberão nesta etapa.</p>

                                                        <div className="flex flex-col gap-2 max-h-60 overflow-y-auto custom-scrollbar pr-2 mb-4">
                                                            {partsData.map(c => {
                                                                const alreadyReceived = distDB.filter(d => d.sourceId === `proj_${proj.id}` && d.collabId === c.id).reduce((a,b) => a + parseFloat(b.quantity||0), 0);
                                                                const left = maxPerPerson - alreadyReceived;
                                                                
                                                                return (
                                                                    <div key={c.id} className={`flex items-center justify-between p-2.5 rounded border ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
                                                                        <div className="flex flex-col">
                                                                            <span className="font-bold text-xs">{c.name}</span>
                                                                            <span className={`text-[9px] ${left <= 0 ? 'text-red-500 font-bold' : 'opacity-70'}`}>
                                                                                Recebeu: {alreadyReceived} / {maxPerPerson} {left > 0 ? `(Resta: ${left})` : '(Limite Atingido)'}
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex items-center gap-2">
                                                                            <input 
                                                                                type="number" step="0.1" min="0" max={left} disabled={left <= 0} 
                                                                                id={`val_coin_${proj.id}_${c.id}`} 
                                                                                className={`w-20 p-1.5 border rounded text-xs font-bold text-center outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white disabled:opacity-50' : 'bg-gray-50 border-gray-300 disabled:bg-gray-100 disabled:opacity-50'}`} 
                                                                                placeholder="0.0"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>

                                                        <div className="flex justify-end pt-3 border-t dark:border-slate-700">
                                                            <Button onClick={async () => {
                                                                const batch = [];
                                                                
                                                                // Coleta todos os inputs que foram preenchidos
                                                                for (const c of partsData) {
                                                                    const inputEl = document.getElementById(`val_coin_${proj.id}_${c.id}`);
                                                                    if (!inputEl || !inputEl.value) continue;
                                                                    
                                                                    const val = parseFloat(inputEl.value);
                                                                    if (val > 0) {
                                                                        const alreadyReceived = distDB.filter(d => d.sourceId === `proj_${proj.id}` && d.collabId === c.id).reduce((a,b) => a + parseFloat(b.quantity||0), 0);
                                                                        const left = maxPerPerson - alreadyReceived;
                                                                        
                                                                        if (val > left) return alert(`Valor de ${val} Coins para "${c.name}" excede o limite restante (${left}). Operação cancelada.`);
                                                                        
                                                                        batch.push({ cId: c.id, colName: c.name, val });
                                                                    }
                                                                }
                                                                
                                                                if (batch.length === 0) return alert("Preencha o valor (maior que 0) para pelo menos um participante da lista.");
                                                                
                                                                // Mostra o resumo antes de confirmar
                                                                const msg = batch.map(b => `• ${b.colName}: ${b.val} Coins`).join('\n');
                                                                if (!window.confirm(`Confirma a distribuição em lote abaixo?\n\n${msg}\n\nOs créditos serão agendados como PREVISTOS para a data ${new Date(prevDate+'T12:00:00').toLocaleDateString('pt-BR')}.`)) return;

                                                                // Processa os salvamentos
                                                                for (const b of batch) {
                                                                    const uniqueId = Date.now().toString() + Math.random().toString(36).substr(2, 4);
                                                                    
                                                                    // Salva na distribuição
                                                                    await saveDist(sanitizeForFirebase({
                                                                        id: `proj_dist_${uniqueId}_${b.cId}`, date: prevDate, collabId: b.cId, quantity: b.val,
                                                                        reason: `Participação no Projeto: ${proj.name}`, type: 'projeto', sourceId: `proj_${proj.id}`
                                                                    }));
                                                                    
                                                                    // Gera Previsão na Caixinha
                                                                    await saveCaixa(sanitizeForFirebase({
                                                                        id: `proj_cx_${uniqueId}_${b.cId}`, type: 'Previsto', date: prevDate, amount: -(b.val), 
                                                                        description: `[Previsto] Pgto Projeto ${proj.name} p/ ${b.colName}`
                                                                    }));
                                                                    
                                                                    // Limpa o input
                                                                    document.getElementById(`val_coin_${proj.id}_${b.cId}`).value = '';
                                                                }
                                                                
                                                                alert("Distribuição em lote realizada com sucesso!");
                                                            }} className="bg-indigo-600 hover:bg-indigo-700 text-white h-10 text-xs border-0 shadow-md w-full sm:w-auto">
                                                                Confirmar Distribuição em Lote
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL NOVA IDEIA / VISUALIZAR IDEIA */}
                {isIdeaModalOpen && (
                    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsIdeaModalOpen(false)}>
                        <div className={`p-6 rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh] border ${isDarkMode ? 'bg-slate-800 text-white border-slate-700' : 'bg-white text-gray-800 border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                <h3 className="font-bold text-lg flex items-center gap-2 text-yellow-500"><span className="text-xl">💡</span> {ideaForm.id ? (ideaFilterMode === 'publicas' ? 'Detalhes da Ideia' : 'Editar Ideia') : 'Nova Ideia'}</h3>
                                <button onClick={() => setIsIdeaModalOpen(false)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                            </div>
                            
                            <div className="space-y-4 flex-1 overflow-y-auto custom-scrollbar pr-2">
                                <div>
                                    <label className="text-xs font-bold uppercase block mb-1 opacity-70">Nome da Ideia</label>
                                    <input type="text" value={ideaForm.title} onChange={e=>setIdeaForm({...ideaForm, title: e.target.value})} disabled={ideaFilterMode === 'publicas'} placeholder="Dê um nome curto e objetivo..." className={`w-full p-2.5 border rounded-lg text-sm outline-none font-bold ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white disabled:opacity-50' : 'bg-gray-50 border-gray-300 disabled:opacity-50'}`}/>
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase block mb-1 opacity-70">Descrição da Ideia</label>
                                    <textarea value={ideaForm.description} onChange={e=>setIdeaForm({...ideaForm, description: e.target.value})} disabled={ideaFilterMode === 'publicas'} placeholder="Descreva detalhadamente como a ideia funciona..." className={`w-full p-2.5 border rounded-lg text-sm outline-none min-h-[100px] resize-y custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white disabled:opacity-50' : 'bg-gray-50 border-gray-300 disabled:opacity-50'}`}/>
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase block mb-1 opacity-70">Defenda sua Ideia</label>
                                    <p className="text-[10px] opacity-60 mb-1">Explique os motivos pelos quais ela é boa e pode ser útil para a empresa.</p>
                                    <textarea value={ideaForm.defense} onChange={e=>setIdeaForm({...ideaForm, defense: e.target.value})} disabled={ideaFilterMode === 'publicas'} placeholder="Quais problemas ela resolve? Quais os benefícios?" className={`w-full p-2.5 border rounded-lg text-sm outline-none min-h-[80px] resize-y custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white disabled:opacity-50' : 'bg-gray-50 border-gray-300 disabled:opacity-50'}`}/>
                                </div>
                                
                                {ideaFilterMode !== 'publicas' && (
                                    <div>
                                        <label className="text-xs font-bold uppercase block mb-2 opacity-70">Visibilidade</label>
                                        <div className="flex gap-4">
                                            <label className={`flex-1 flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${ideaForm.visibility === 'public' ? 'bg-yellow-50 border-yellow-400 dark:bg-yellow-900/20 dark:border-yellow-600' : isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-200'}`}>
                                                <input type="radio" checked={ideaForm.visibility === 'public'} onChange={() => setIdeaForm({...ideaForm, visibility: 'public'})} className="w-4 h-4 text-yellow-500"/>
                                                <div><div className="font-bold text-xs text-yellow-600 dark:text-yellow-400">Pública</div><div className="text-[9px] opacity-70">Todos os colegas poderão ver e se inspirar.</div></div>
                                            </label>
                                            <label className={`flex-1 flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${ideaForm.visibility === 'private' ? 'bg-red-50 border-red-400 dark:bg-red-900/20 dark:border-red-600' : isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-200'}`}>
                                                <input type="radio" checked={ideaForm.visibility === 'private'} onChange={() => setIdeaForm({...ideaForm, visibility: 'private'})} className="w-4 h-4 text-red-500"/>
                                                <div><div className="font-bold text-xs text-red-600 dark:text-red-400">Particular</div><div className="text-[9px] opacity-70">Apenas você e os gestores avaliadores verão.</div></div>
                                            </label>
                                        </div>
                                    </div>
                                )}

                                {/* Mostrar retorno do avaliador se houver */}
                                {ideaForm.evaluatorNotes && (
                                    <div className={`p-4 mt-2 rounded-lg border-l-4 ${ideaForm.status === 'Aprovada' ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : ideaForm.status === 'Não aprovada' ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'}`}>
                                        <h5 className="font-bold text-xs uppercase mb-1 opacity-70">Feedback do Gestor:</h5>
                                        <p className="text-sm italic">{ideaForm.evaluatorNotes}</p>
                                    </div>
                                )}
                            </div>

                            {ideaFilterMode !== 'publicas' && (
                                <div className="flex justify-end gap-2 mt-4 pt-4 border-t dark:border-slate-700">
                                    <Button variant="secondary" onClick={() => setIsIdeaModalOpen(false)}>Cancelar</Button>
                                    <Button onClick={async () => {
                                        if(!ideaForm.title || !ideaForm.description || !ideaForm.defense) return alert("Preencha todos os campos da ideia.");
                                        await saveIdea(sanitizeForFirebase({
                                            ...ideaForm,
                                            id: ideaForm.id || `idea_${Date.now()}`,
                                            date: ideaForm.id ? ideaForm.date : new Date().toISOString(),
                                            collabId: loggedCollabId,
                                            collabName: myCollabRecord?.name,
                                            collabDeptId: myCollabRecord?.departmentId,
                                            status: ideaForm.status || 'Pendente',
                                            coinsAwarded: ideaForm.coinsAwarded || 0,
                                            sharedWith: ideaForm.sharedWith || []
                                        }));
                                        alert("Ideia gravada com sucesso!");
                                        setIsIdeaModalOpen(false);
                                    }} className="bg-yellow-500 hover:bg-yellow-600 text-white border-0 shadow-md">
                                        Gravar Ideia
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* MODAL AVALIAR IDEIA (GESTOR) */}
                {evalIdeaData && (
                    <div className="fixed inset-0 z-[160] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setEvalIdeaData(null)}>
                        <div className={`p-6 rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh] border ${isDarkMode ? 'bg-slate-800 text-white border-slate-700' : 'bg-white text-gray-800 border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                <h3 className="font-bold text-lg flex items-center gap-2 text-indigo-500"><CheckCircle size={20}/> Avaliar Ideia</h3>
                                <button onClick={() => setEvalIdeaData(null)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                            </div>
                            
                            <div className="space-y-4 flex-1 overflow-y-auto custom-scrollbar pr-2">
                                <div className={`p-4 rounded-lg border bg-black/5 dark:bg-white/5`}>
                                    <div className="text-[10px] font-bold uppercase opacity-70 mb-1">Autor: {evalIdeaData.collabName}</div>
                                    <h4 className="font-black text-lg mb-2">{evalIdeaData.title}</h4>
                                    <p className="text-sm mb-3 whitespace-pre-wrap">{evalIdeaData.description}</p>
                                    <div className="text-[10px] font-bold uppercase opacity-70 mb-1 border-t pt-2 dark:border-slate-600">Defesa:</div>
                                    <p className="text-xs italic opacity-90">{evalIdeaData.defense}</p>
                                </div>

                                <div className="border-t pt-4 dark:border-slate-700">
                                    <label className="text-xs font-bold uppercase block mb-1 opacity-70">Parecer / Status</label>
                                    <div className="flex gap-2 mb-4">
                                        <label className={`flex-1 flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${evalIdeaData.status === 'Aprovada' ? 'bg-green-100 border-green-500 text-green-800 dark:bg-green-900/30 dark:text-green-400' : isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-200'}`}>
                                            <input type="radio" checked={evalIdeaData.status === 'Aprovada'} onChange={() => setEvalIdeaData({...evalIdeaData, status: 'Aprovada'})} className="w-4 h-4 text-green-500"/>
                                            <span className="font-bold text-xs">Aprovada</span>
                                        </label>
                                        <label className={`flex-1 flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${evalIdeaData.status === 'Pré-aprovada' ? 'bg-blue-100 border-blue-500 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' : isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-200'}`}>
                                            <input type="radio" checked={evalIdeaData.status === 'Pré-aprovada'} onChange={() => setEvalIdeaData({...evalIdeaData, status: 'Pré-aprovada'})} className="w-4 h-4 text-blue-500"/>
                                            <span className="font-bold text-xs">Pré-Aprovada</span>
                                        </label>
                                        <label className={`flex-1 flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${evalIdeaData.status === 'Não aprovada' ? 'bg-red-100 border-red-500 text-red-800 dark:bg-red-900/30 dark:text-red-400' : isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-200'}`}>
                                            <input type="radio" checked={evalIdeaData.status === 'Não aprovada'} onChange={() => setEvalIdeaData({...evalIdeaData, status: 'Não aprovada'})} className="w-4 h-4 text-red-500"/>
                                            <span className="font-bold text-xs">Não Aprovada</span>
                                        </label>
                                    </div>

                                    <label className="text-xs font-bold uppercase block mb-1 opacity-70">Observações do Gestor (Feedback)</label>
                                    <textarea value={evalIdeaData.evaluatorNotes || ''} onChange={e=>setEvalIdeaData({...evalIdeaData, evaluatorNotes: e.target.value})} placeholder="Escreva o feedback para o colaborador..." className={`w-full p-2.5 border rounded-lg text-sm outline-none min-h-[80px] resize-y custom-scrollbar mb-4 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-gray-50 border-gray-300'}`}/>

                                    <div className="flex gap-4">
                                        <div className="flex-1">
                                            <label className="text-xs font-bold uppercase block mb-1 opacity-70 flex items-center gap-1">Moedas a Receber <span className="opacity-50 lowercase normal-case text-[9px]">(Teto: {ruleIdeias.ideasMaxCoins || 0})</span></label>
                                            <input type="number" min="0" max={ruleIdeias.ideasMaxCoins || 999} value={evalIdeaData.coinsAwarded || ''} onChange={e=>setEvalIdeaData({...evalIdeaData, coinsAwarded: e.target.value})} className={`w-full p-2.5 border rounded-lg text-sm font-bold outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600 text-yellow-400' : 'bg-gray-50 border-gray-300 text-yellow-600'}`} placeholder="Ex: 50"/>
                                        </div>
                                        
                                        {ruleIdeias.ideasAllowShare && (
                                            <div className="flex-1 flex flex-col">
                                                <label className="text-xs font-bold uppercase block mb-1 opacity-70">Compartilhar Visibilidade Com:</label>
                                                <div className={`flex-1 p-2 border rounded-lg flex flex-col gap-2 ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-300'}`}>
                                                    
                                                    {/* CAMPO DE BUSCA */}
                                                    <div className="relative shrink-0">
                                                        <Search size={12} className="absolute left-2 top-2 text-gray-400"/>
                                                        <input type="text" placeholder="Buscar colega..." value={shareIdeaSearch} onChange={(e) => setShareIdeaSearch(e.target.value)} className={`w-full pl-7 py-1 text-xs border rounded outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`} />
                                                    </div>

                                                    <div className="max-h-24 overflow-y-auto custom-scrollbar grid grid-cols-1 gap-2 pr-1">
                                                        {(() => {
                                                            const filteredShare = data.collaborators
                                                                .filter(c => c.id !== loggedCollabId) // Remove a própria pessoa da lista
                                                                .filter(c => !shareIdeaSearch || c.name.toLowerCase().includes(shareIdeaSearch.toLowerCase()))
                                                                .sort((a,b)=>a.name.localeCompare(b.name));
                                                                
                                                            if (filteredShare.length === 0) return <span className="text-[10px] italic opacity-50">Nenhum colega encontrado.</span>;
                                                            
                                                            return filteredShare.map(c => (
                                                                <label key={c.id} className="flex items-center gap-2 cursor-pointer text-[10px] truncate" title={c.name}>
                                                                    <input 
                                                                        type="checkbox" 
                                                                        className="w-3 h-3 text-indigo-500 rounded border-gray-300 focus:ring-indigo-500 shrink-0" 
                                                                        checked={evalIdeaData.sharedWith?.includes(c.id) || false} 
                                                                        onChange={(e) => {
                                                                            const current = evalIdeaData.sharedWith || [];
                                                                            const next = e.target.checked ? [...current, c.id] : current.filter(sid => sid !== c.id);
                                                                            setEvalIdeaData({...evalIdeaData, sharedWith: next});
                                                                        }}
                                                                    /> 
                                                                    <span className="truncate">{c.name}</span>
                                                                </label>
                                                            ));
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-2 mt-4 pt-4 border-t dark:border-slate-700">
                                <Button variant="secondary" onClick={() => setEvalIdeaData(null)}>Cancelar</Button>
                                <Button onClick={async () => {
                                    if (evalIdeaData.status === 'Aprovada' && (!evalIdeaData.coinsAwarded || evalIdeaData.coinsAwarded <= 0)) {
                                        if(!window.confirm("A ideia foi aprovada mas não há moedas de recompensa. Continuar?")) return;
                                    }
                                    
                                    // Salvar a ideia atualizada
                                    await saveIdea(sanitizeForFirebase(evalIdeaData));
                                    
                                    // Se for aprovada e tiver moedas, lança o crédito na carteira do colaborador
                                    if (evalIdeaData.status === 'Aprovada' && parseFloat(evalIdeaData.coinsAwarded) > 0) {
                                        // Verifica se já não pagamos por essa ideia antes (evitar duplicação se o gestor editar)
                                        const exists = distDB.some(d => d.sourceId === `idea_rew_${evalIdeaData.id}`);
                                        if (!exists) {
                                            await saveDist(sanitizeForFirebase({
                                                id: `idea_rew_${evalIdeaData.id}`,
                                                date: todayStr,
                                                collabId: evalIdeaData.collabId,
                                                quantity: parseFloat(evalIdeaData.coinsAwarded),
                                                reason: `Premiação pela Ideia Aprovada: ${evalIdeaData.title}`,
                                                type: 'premiacao_ideia',
                                                sourceId: `idea_rew_${evalIdeaData.id}`
                                            }));
                                        }
                                    }

                                    alert("Avaliação gravada com sucesso!");
                                    setEvalIdeaData(null);
                                }} className="bg-indigo-500 hover:bg-indigo-600 text-white border-0 shadow-md">
                                    Salvar Avaliação
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* MODAL NOVA NOTÍCIA (ACADNEWS) */}
                {isNewsModalOpen && (
                    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsNewsModalOpen(false)}>
                        <div className={`p-6 rounded-xl w-full max-w-lg shadow-2xl flex flex-col border ${isDarkMode ? 'bg-slate-800 text-white border-slate-700' : 'bg-white text-gray-800 border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                <h3 className="font-bold text-lg flex items-center gap-2 text-orange-500"><Globe size={20}/> Publicar no AcadNews</h3>
                                <button onClick={() => setIsNewsModalOpen(false)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                            </div>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="text-xs font-bold uppercase block mb-1 opacity-70">Tipo de Notícia</label>
                                    <div className="flex gap-2">
                                        <button onClick={() => setNewsForm({...newsForm, type: 'Rotineira'})} className={`flex-1 py-2 rounded-lg font-bold text-xs border transition-colors ${newsForm.type === 'Rotineira' ? 'bg-blue-100 border-blue-500 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'border-gray-200 dark:border-slate-600 opacity-60'}`}>📰 Rotineira</button>
                                        <button onClick={() => setNewsForm({...newsForm, type: 'Urgente'})} className={`flex-1 py-2 rounded-lg font-bold text-xs border transition-colors ${newsForm.type === 'Urgente' ? 'bg-red-100 border-red-500 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'border-gray-200 dark:border-slate-600 opacity-60'}`}>⚠️ Urgente / Extraordinária</button>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase block mb-1 opacity-70">Título da Notícia</label>
                                    <input type="text" value={newsForm.title} onChange={e=>setNewsForm({...newsForm, title: e.target.value})} placeholder="Ex: Resultado da Meta Semestral!" className={`w-full p-2.5 border rounded-lg text-sm outline-none font-bold ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-gray-50 border-gray-300'}`}/>
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase block mb-1 opacity-70">Conteúdo (Mensagem)</label>
                                    <textarea value={newsForm.content} onChange={e=>setNewsForm({...newsForm, content: e.target.value})} placeholder="Digite as informações detalhadas aqui..." className={`w-full p-2.5 border rounded-lg text-sm outline-none min-h-[120px] resize-y custom-scrollbar ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-gray-50 border-gray-300'}`}/>
                                </div>
                            </div>

                            <div className="flex justify-end gap-2 mt-6 pt-4 border-t dark:border-slate-700">
                                <Button variant="secondary" onClick={() => setIsNewsModalOpen(false)}>Cancelar</Button>
                                <Button onClick={async () => {
                                    if(!newsForm.title || !newsForm.content) return alert("Preencha o título e o conteúdo da notícia.");
                                    await saveNews(sanitizeForFirebase({
                                        id: `news_${Date.now()}`,
                                        date: new Date().toISOString(),
                                        type: newsForm.type,
                                        title: newsForm.title,
                                        content: newsForm.content
                                    }));
                                    setIsNewsModalOpen(false);
                                    setNewsForm({ id: null, type: 'Rotineira', title: '', content: '' });
                                }} className="bg-orange-500 hover:bg-orange-600 text-white border-0 shadow-md">
                                    Publicar Agora
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL PREVISÃO MINHA CARTEIRA */}
                {showPrevisaoModal && (
                    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setShowPrevisaoModal(false)}>
                        <div className={`p-6 rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[80vh] border ${isDarkMode ? 'bg-slate-800 text-white border-slate-700' : 'bg-white text-gray-800 border-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                <h3 className="font-bold text-lg flex items-center gap-2 text-yellow-500"><Clock size={20}/> Previsões Futuras da Caixinha</h3>
                                <button onClick={() => setShowPrevisaoModal(false)}><X size={20}/></button>
                            </div>
                            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4">
                                <p className="text-xs opacity-80 mb-2">Abaixo estão os aportes programados para a caixinha global. Os valores estarão disponíveis após serem efetivados nas datas previstas.</p>
                               {(() => {
                                        const hojeStr = todayStr;

                                        // REGRA 4: Filtra apenas itens da Caixinha que não venceram
                                        const previstosFiltrados = caixaDB.filter(item => {
                                            const dataPagamento = item.date;
                                            
                                            // Esconde itens cuja data já passou do vencimento
                                            if (dataPagamento < hojeStr) return false; 
                                            
                                            const isPrevisto = item.type === 'Previsto' || item.status === 'Previsto';
                                            const isCancelado = item.type === 'Cancelado' || item.status === 'Cancelado';
                                            const isRealizado = item.type === 'Realizado' || item.status === 'Realizado';

                                            // Só mostra itens realizados SE a origem deles foi 'Previsto' (Marcado no botão Finalizar)
                                            if (isRealizado && item.tipoOriginal !== 'Previsto') return false;
                                            
                                            return isPrevisto || isCancelado || isRealizado;
                                        });

                                        if(previstosFiltrados.length === 0) return <p className="text-center italic opacity-50 py-8 text-sm">Nenhuma previsão futura registrada.</p>;

                                        // Agrupa por período como era no design aprovado (Caixinhas separadas)
                                        const grouped = {};
                                        previstosFiltrados.forEach(p => {
                                            const per = getPeriodStr(p.date);
                                            if(!grouped[per]) grouped[per] = { items: [], total: 0 };
                                            grouped[per].items.push(p);
                                            
                                            // Se estiver cancelado, não soma no Totalizador daquele período
                                            if (p.type !== 'Cancelado' && p.status !== 'Cancelado') {
                                                grouped[per].total += parseFloat(p.amount || 0);
                                            }
                                        });

                                        return Object.keys(grouped).sort().map(per => {
                                            // REGRA: Verifica se todos os itens desse período foram cancelados para "acizentar" o quadro
                                            const allCancelled = grouped[per].items.every(i => i.type === 'Cancelado' || i.status === 'Cancelado');

                                            return (
                                                <div key={per} className={`p-3 rounded-lg border shadow-sm ${allCancelled ? (isDarkMode ? 'bg-slate-800 border-slate-700 opacity-70' : 'bg-gray-100 border-gray-300 opacity-80') : (isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-yellow-50/30 border-yellow-200')}`}>
                                                    <div className={`flex justify-between items-center border-b pb-2 mb-2 dark:border-slate-700 ${allCancelled ? 'opacity-60' : ''}`}>
                                                        <span className={`font-black text-sm uppercase ${allCancelled ? 'text-gray-500 dark:text-gray-400' : 'text-yellow-600 dark:text-yellow-500'}`}>Período Referência: {per}</span>
                                                        <span className={`font-bold text-sm px-2 py-0.5 rounded shadow-sm ${allCancelled ? 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300' : 'bg-yellow-100 text-yellow-800'}`}>Total Previsto: {formatMoney(grouped[per].total)}</span>
                                                    </div>
                                                    
                                                    <table className="w-full text-xs text-left">
                                                        <thead>
                                                            <tr className="opacity-70 font-bold">
                                                                <th className="py-1 w-24">Data Crédito</th>
                                                                <th>Histórico</th>
                                                                <th className="text-right">Valor</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-dashed dark:divide-slate-700">
                                                            {grouped[per].items.map(item => {
                                                                const isCancelado = item.type === 'Cancelado' || item.status === 'Cancelado';
                                                                const isEfetivado = item.type === 'Realizado' || item.status === 'Realizado';

                                                                return (
                                                                    <tr key={item.id} className={`opacity-90 hover:opacity-100 transition-colors ${isCancelado ? (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-100') : ''}`}>
                                                                        <td className="py-3 font-mono align-top">
                                                                            {new Date((item.dataPagamento || item.date) + 'T12:00:00').toLocaleDateString('pt-BR')}
                                                                        </td>
                                                                        <td className="py-3">
                                                                            <div className={`font-bold ${isCancelado ? 'line-through text-gray-400 dark:text-gray-500' : ''}`}>
                                                                                {item.description || item.reason || item.descricao}
                                                                            </div>
                                                                            {isEfetivado && (
                                                                                <div className="mt-1 inline-block px-1.5 py-0.5 bg-green-100 text-green-700 text-[9px] font-bold uppercase rounded border border-green-200">
                                                                                    ✔ Efetivado
                                                                                </div>
                                                                            )}
                                                                            {isCancelado && (
                                                                                <div className="mt-1">
                                                                                    <span className="inline-block px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[9px] font-bold uppercase rounded border border-gray-300 dark:border-gray-600 mb-1">
                                                                                        ✖ Cancelado
                                                                                    </span>
                                                                                    <div className="text-[10px] text-gray-500 italic">
                                                                                        Obs: {item.motivoCancelamento || "Motivo não informado."}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                        <td className={`text-right font-medium align-top py-3 ${isCancelado ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-emerald-600 dark:text-emerald-400'}`}>
                                                                            {formatMoney(parseFloat(item.amount || item.quantity || item.valor || 0))}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            );
                                        });
                                })()}
                            </div>
                        </div>
                    </div>
                )}

            </div>
        );
    };




    /* ===============================================================================
     * [END_MOD_ACAD_COINS_SYSTEM]
     * FIM DO BLOCO: AcadCoinsSystem
     * [START_MOD_CARD_NAVIGATION_MENU]
     * INICIO DO BLOCO: CardNavigationMenu
     * =============================================================================== */
    const CardNavigationMenu = ({ 
        menuStructure, 
        hasModuleAccess, 
        effectivePermissions, 
        isDarkMode, 
        onNavigate, 
        currentUser 
    }) => {
        const [selectedMenu, setSelectedMenu] = React.useState(null);

        const cardLayout = currentUser?.preferences?.cardLayout || 'star';
        const cardShape = currentUser?.preferences?.cardShape || 'circle';

        // --- LÓGICA DE ZOOM INDIVIDUAL POR TELA ---
        
        // 1. Determina o ID do contexto atual (Se null, é a raiz 'root', senão é o ID do menu, ex: 'reg')
        const contextId = selectedMenu ? selectedMenu.id : 'root';
        const storageKey = `sys_scale_${contextId}`;

        // 2. Estado do Zoom (Inicia lendo do contexto atual ou padrão 2)
        const [viewScale, setViewScale] = React.useState(2);

        // 3. Efeito: Sempre que mudar de tela (Menu), carrega o zoom salvo para AQUELA tela
        React.useEffect(() => {
            const savedScale = localStorage.getItem(storageKey);
            if (savedScale) {
                setViewScale(parseInt(savedScale));
            } else {
                setViewScale(2); // Padrão se nunca foi configurado
            }
        }, [storageKey]);

        // 4. Função para alterar e salvar o zoom no contexto atual
        const changeScale = (newVal) => {
            setViewScale(newVal);
            localStorage.setItem(storageKey, newVal);
        };

        // --- HELPERS DE ESTILO DINÂMICO (4 NÍVEIS) ---

        // 1. Grid do Menu Principal
        const getMainGridClasses = () => {
            if (viewScale === 1) return "grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3"; 
            if (viewScale === 3) return "grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6"; 
            if (viewScale === 4) return "grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8"; 
            return "grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"; // Nível 2
        };

        // 2. Grid dos Submenus (Formas Geométricas)
        const getSubGridClasses = () => {
            if (viewScale === 1) return "grid-cols-3 md:grid-cols-5 lg:grid-cols-8 gap-3"; 
            if (viewScale === 3) return "grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5"; 
            if (viewScale === 4) return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"; 
            return "grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4"; // Nível 2
        };

        // 3. Altura do Card
        const getCardHeight = () => {
            if (viewScale === 1) return "h-24 md:h-28";
            if (viewScale === 3) return "h-40 md:h-48";
            if (viewScale === 4) return "h-52 md:h-64";
            return "h-32 md:h-40"; // Nível 2
        };

        // 4. Tamanho dos Ícones
        const getIconSize = () => {
            if (viewScale === 1) return 24;
            if (viewScale === 3) return 48;
            if (viewScale === 4) return 72;
            return 36; // Nível 2
        };

        // 5. Tamanho do Texto
        const getTextSize = () => {
            if (viewScale === 1) return "text-[10px] md:text-xs";
            if (viewScale === 3) return "text-sm md:text-lg";
            if (viewScale === 4) return "text-lg md:text-2xl";
            return "text-xs md:text-base"; // Nível 2
        };

        // 6. Tamanho das Formas Geométricas (Submenu)
        const getShapeClasses = (shape) => {
            // Nível 1 (Pequeno)
            if (viewScale === 1) {
                switch(shape) {
                    case 'rounded-square': return 'w-20 h-20 rounded-2xl';
                    case 'rounded-rect': return 'w-28 h-14 rounded-xl px-1';
                    case 'pill': return 'w-28 h-10 rounded-full px-2';
                    case 'rhombus': return 'w-20 h-20 rounded-2xl rotate-45';
                    case 'triangle': return 'w-24 h-24 rounded-xl [clip-path:polygon(50%_0%,0%_100%,100%_100%)] flex items-end pb-2';
                    case 'circle': default: return 'w-20 h-20 rounded-full';
                }
            }
            // Nível 3 (Grande)
            if (viewScale === 3) {
                switch(shape) {
                    case 'rounded-square': return 'w-40 h-40 rounded-[2rem]';
                    case 'rounded-rect': return 'w-56 h-28 rounded-2xl px-3';
                    case 'pill': return 'w-56 h-20 rounded-full px-4';
                    case 'rhombus': return 'w-36 h-36 rounded-[2rem] rotate-45';
                    case 'triangle': return 'w-44 h-44 rounded-2xl [clip-path:polygon(50%_0%,0%_100%,100%_100%)] flex items-end pb-6';
                    case 'circle': default: return 'w-40 h-40 rounded-full';
                }
            }
            // Nível 4 (Extra Grande)
            if (viewScale === 4) {
                switch(shape) {
                    case 'rounded-square': return 'w-56 h-56 rounded-[3rem]';
                    case 'rounded-rect': return 'w-72 h-36 rounded-3xl px-6';
                    case 'pill': return 'w-72 h-28 rounded-full px-8';
                    case 'rhombus': return 'w-48 h-48 rounded-[3rem] rotate-45';
                    case 'triangle': return 'w-60 h-60 rounded-3xl [clip-path:polygon(50%_0%,0%_100%,100%_100%)] flex items-end pb-10';
                    case 'circle': default: return 'w-56 h-56 rounded-full';
                }
            }
            // Nível 2 (Normal)
            switch(shape) {
                case 'rounded-square': return 'w-32 h-32 rounded-3xl';
                case 'rounded-rect': return 'w-48 h-24 rounded-2xl px-2';
                case 'pill': return 'w-48 h-16 rounded-full px-4';
                case 'rhombus': return 'w-28 h-28 rounded-3xl rotate-45';
                case 'triangle': return 'w-36 h-36 rounded-2xl [clip-path:polygon(50%_0%,0%_100%,100%_100%)] flex items-end pb-6';
                case 'circle': default: return 'w-32 h-32 rounded-full';
            }
        };

        const shapeClass = getShapeClasses(cardShape);

        // Componente de Controle de Zoom (Atualizado para usar changeScale)
        const ZoomControls = () => (
            <div className={`flex items-center gap-2 px-2 py-1 rounded-full border shadow-sm z-[150] bg-opacity-90 backdrop-blur-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                <button onClick={(e) => { e.stopPropagation(); changeScale(1); }} className={`p-1.5 rounded-full transition-all ${viewScale === 1 ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'}`} title="Compacto"><Grid size={14} /></button>
                <input type="range" min="1" max="4" step="1" value={viewScale} onClick={(e)=>e.stopPropagation()} onChange={(e) => changeScale(parseInt(e.target.value))} className="w-16 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-indigo-600"/>
                <button onClick={(e) => { e.stopPropagation(); changeScale(4); }} className={`p-1.5 rounded-full transition-all ${viewScale === 4 ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'}`} title="Extra Grande"><Maximize2 size={14} /></button>
            </div>
        );

        // Validação de segurança por cadastro
        const canAccessSub = (sub) => {
            if (!sub.permKey) return true;
            return effectivePermissions[sub.permKey] === true;
        };

        const availableMenus = menuStructure.filter((menu) => {
            if (menu.id === 'card_menu') return false;
            if (menu.id === 'favorites') return true; 
            const hasParentAccess = effectivePermissions[menu.permission] === true;
            if (!hasParentAccess && menu.permission) return false;
            const subs = menu.subItems || menu.innerTabs || [];
            if (subs.length > 0) return subs.some((sub) => canAccessSub(sub));
            return true;
        });

        const CARD_COLORS = [
            'from-blue-600 to-cyan-500', 'from-purple-600 to-fuchsia-500', 'from-emerald-600 to-teal-400',
            'from-orange-500 to-amber-400', 'from-pink-600 to-rose-400', 'from-indigo-600 to-blue-500',
            'from-red-600 to-orange-500', 'from-teal-600 to-emerald-400', 'from-sky-500 to-blue-400',
            'from-violet-600 to-purple-500', 'from-rose-500 to-pink-500', 'from-amber-500 to-yellow-400',
            'from-lime-500 to-green-600', 'from-cyan-600 to-blue-700', 'from-fuchsia-700 to-purple-800',
            'from-emerald-600 to-green-700', 'from-slate-600 to-slate-800', 'from-rose-400 to-orange-400',
            'from-blue-400 to-indigo-500', 'from-amber-400 to-red-500'
        ];

        // =========================================================
        // 1. VISÃO: MENU PRINCIPAL (GRID)
        // =========================================================
        if (!selectedMenu) {
            return (
                <div className="p-4 md:p-8 max-w-7xl mx-auto animate-fade-in mt-2 md:mt-10 pb-20">
                    <div className="flex flex-col md:flex-row justify-between items-center mb-6 md:mb-10 gap-4">
                        <h2 className={`text-xl md:text-2xl font-black text-center uppercase tracking-widest ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            Navegação do Sistema
                        </h2>
                        {/* Controle de Zoom Injetado Aqui */}
                        <ZoomControls />
                    </div>
                    
                    <div className={`grid ${getMainGridClasses()} animate-fade-in transition-all duration-300`}>
                        {availableMenus.map((menu, idx) => {
                            const colorClass = CARD_COLORS[idx % CARD_COLORS.length];
                            const accessibleSubs = (menu.subItems || menu.innerTabs || []).filter(canAccessSub);
                            return (
                                <div 
                                    key={menu.id} 
                                    onClick={() => {
                                        if (accessibleSubs.length === 1) onNavigate(`${menu.id}:${accessibleSubs[0].id}`);
                                        else if (accessibleSubs.length > 1) setSelectedMenu(menu);
                                        else onNavigate(menu.id);
                                    }}
                                    className={`${getCardHeight()} rounded-3xl shadow-lg cursor-pointer transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl flex flex-col items-center justify-center gap-2 md:gap-3 bg-gradient-to-br ${colorClass} text-white relative overflow-hidden group border border-white/10`}
                                >
                                    <menu.icon size={getIconSize()} className="drop-shadow-md group-hover:scale-110 transition-transform" />
                                    <span className={`font-bold tracking-wide drop-shadow-md text-center px-2 ${getTextSize()}`}>{menu.label}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }

        const availableSubItems = (selectedMenu.subItems || selectedMenu.innerTabs || []).filter(canAccessSub);
        const centerColor = CARD_COLORS[availableMenus.indexOf(selectedMenu) % CARD_COLORS.length];

        // =========================================================
        // 2. VISÃO: SUBMENU - LISTA (MODERNA)
        // =========================================================
        if (cardLayout === 'list') {
            const listPadding = viewScale === 1 ? 'p-3' : (viewScale === 4 ? 'p-8' : (viewScale === 3 ? 'p-6' : 'p-4 md:p-5'));
            const listText = viewScale === 1 ? 'text-xs' : (viewScale === 4 ? 'text-2xl' : (viewScale === 3 ? 'text-xl' : 'text-sm md:text-lg'));

            return (
                <div className="p-4 md:p-8 max-w-4xl mx-auto animate-fade-in pb-32">
                    <div className="flex justify-between items-center mb-6">
                        <button onClick={() => setSelectedMenu(null)} className={`p-2 px-4 rounded-full shadow-md flex items-center gap-2 font-bold text-xs md:text-sm transition-colors ${isDarkMode ? 'bg-slate-800 text-white hover:bg-slate-700' : 'bg-white text-gray-800 hover:bg-gray-100'}`}>
                            <ArrowLeft size={16}/> Voltar
                        </button>
                        {/* Controle de Zoom também aqui */}
                        <ZoomControls />
                    </div>

                    <div className="flex flex-col gap-4">
                        {availableSubItems.map((sub, i) => (
                            <div 
                                key={sub.id} 
                                onClick={() => onNavigate(`${selectedMenu.id}:${sub.id}`)} 
                                className={`${listPadding} rounded-2xl shadow-md cursor-pointer transition-all hover:scale-[1.02] flex items-center justify-between border-2 border-white/10 bg-gradient-to-r ${CARD_COLORS[(i + 5) % CARD_COLORS.length]} text-white`}
                            >
                                <span className={`font-bold drop-shadow-md ${listText}`}>{sub.label}</span>
                                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                                    <ArrowLeft size={18} className="rotate-180" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        // =========================================================
        // 3. VISÃO: SUBMENU - GRID (FORMAS GEOMÉTRICAS)
        // =========================================================
        if (cardLayout === 'grid') {
            return (
                <div className="p-4 md:p-8 max-w-7xl mx-auto animate-fade-in pb-32">
                    <div className="flex justify-between items-center mb-8">
                        <button onClick={() => setSelectedMenu(null)} className={`p-2 px-4 rounded-full shadow-md flex items-center gap-2 font-bold text-sm transition-colors ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`}><ArrowLeft size={16}/> Voltar</button>
                        {/* Controle de Zoom também aqui */}
                        <ZoomControls />
                    </div>
                    
                    {/* Usa a grid dinâmica (getSubGridClasses) */}
                    <div className={`grid ${getSubGridClasses()} gap-6`}>
                        {availableSubItems.map((sub, i) => (
                            <div key={sub.id} onClick={() => onNavigate(`${selectedMenu.id}:${sub.id}`)} className={`${shapeClass} shadow-xl cursor-pointer transition-all duration-300 hover:scale-105 flex flex-col items-center justify-center p-2 text-center border-2 border-white/20 bg-gradient-to-br ${CARD_COLORS[(i + 5) % CARD_COLORS.length]} text-white mx-auto`}>
                                <div className={`${cardShape === 'rhombus' ? '-rotate-45' : ''} font-bold leading-tight drop-shadow-md ${getTextSize()}`}>{sub.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        // =========================================================
        // 4. VISÃO: SUBMENU - RADIAL / ARCO / ORBITA
        // =========================================================
        const itemCount = availableSubItems.length;
        // Ajusta o raio base com o Zoom
        const scaleFactor = viewScale === 1 ? 0.7 : (viewScale === 4 ? 1.6 : (viewScale === 3 ? 1.3 : 1));
        const dynamicBaseRadius = Math.min(window.innerHeight * 0.3, Math.max(180, itemCount * 25)) * scaleFactor;
        const containerSize = (dynamicBaseRadius * 2) + 600; // Aumentado para evitar corte no zoom 4

        return (
            <div className="relative w-full h-[calc(100vh-80px)] flex items-center justify-center animate-fade-in overflow-hidden">
                
                <div className="absolute top-6 left-6 z-[120] flex gap-4">
                    <button onClick={() => setSelectedMenu(null)} className={`p-2 px-4 rounded-full shadow-md flex items-center gap-2 font-bold text-sm transition-colors ${isDarkMode ? 'bg-slate-800 text-white hover:bg-slate-700' : 'bg-white text-gray-800 hover:bg-gray-100'}`}>
                        <ArrowLeft size={16}/> Voltar
                    </button>
                    {/* Controle de Zoom também aqui */}
                    <ZoomControls />
                </div>
                
                <div className="relative flex items-center justify-center" style={{ width: containerSize, height: containerSize, transform: 'scale(0.85)' }}>
                    {/* Linhas de Conexão */}
                    <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible">
                        {availableSubItems.map((sub, i) => {
                            let r = dynamicBaseRadius;
                            let angle = (i * (360 / itemCount) - 90) * (Math.PI / 180);

                            if (cardLayout === 'irregular') {
                                const variations = [50, -70, 100, -40, 85, -20];
                                r = dynamicBaseRadius + variations[i % variations.length];
                                const shifts = [25, -20, 30, -35, 15, -40];
                                angle = ((i * (360 / itemCount)) + shifts[i % shifts.length] - 90) * (Math.PI / 180);
                            } else if (cardLayout === 'solar') {
                                r = (130 * scaleFactor) + (i * 50 * scaleFactor);
                                angle = (i * 137.5) * (Math.PI / 180);
                            } else if (cardLayout === 'arch') {
                                const step = Math.PI / (itemCount > 1 ? itemCount - 1 : 1);
                                angle = Math.PI + (i * step);
                            }

                            const x2 = 50 + (r * Math.cos(angle) / (containerSize / 2)) * 50; 
                            const y2 = 50 + (r * Math.sin(angle) / (containerSize / 2)) * 50;
                            
                            return cardLayout === 'solar' ? 
                                <circle key={i} cx="50%" cy="50%" r={r} fill="none" stroke={isDarkMode ? '#334155' : '#cbd5e1'} strokeWidth="1" strokeDasharray="4,4" /> :
                                <line key={i} x1="50%" y1="50%" x2={`${x2}%`} y2={`${y2}%`} stroke={isDarkMode ? '#475569' : '#cbd5e1'} strokeWidth="2" strokeDasharray="4,4" className="opacity-40" />;
                        })}
                    </svg>

                    {/* Nó Central */}
                    <div onClick={() => setSelectedMenu(null)} className={`absolute z-[110] rounded-full shadow-2xl flex flex-col items-center justify-center gap-2 bg-gradient-to-br ${centerColor} text-white cursor-pointer hover:scale-105 transition-transform border-[6px] border-white dark:border-slate-800 ${viewScale===1 ? 'w-24 h-24' : (viewScale===4 ? 'w-64 h-64' : (viewScale===3 ? 'w-56 h-56' : 'w-32 h-32 md:w-44 md:h-44'))}`}>
                        <selectedMenu.icon size={getIconSize() * 1.5} className="drop-shadow-md" />
                        <span className={`font-black text-center drop-shadow-md leading-tight uppercase tracking-widest px-2 ${getTextSize()}`}>{selectedMenu.label}</span>
                    </div>

                    {/* Submenus (Satélites) */}
                    {availableSubItems.map((sub, i) => {
                        let r = dynamicBaseRadius;
                        let angle = (i * (360 / itemCount) - 90) * (Math.PI / 180);

                        if (cardLayout === 'irregular') {
                            const variations = [50, -70, 100, -40, 85, -20];
                            r = dynamicBaseRadius + variations[i % variations.length];
                            const shifts = [25, -20, 30, -35, 15, -40];
                            angle = ((i * (360 / itemCount)) + shifts[i % shifts.length] - 90) * (Math.PI / 180);
                        } else if (cardLayout === 'solar') {
                            r = (130 * scaleFactor) + (i * 50 * scaleFactor);
                            angle = (i * 137.5) * (Math.PI / 180);
                        } else if (cardLayout === 'arch') {
                            const step = Math.PI / (itemCount > 1 ? itemCount - 1 : 1);
                            angle = Math.PI + (i * step);
                        }

                        const x = r * Math.cos(angle);
                        const y = r * Math.sin(angle);
                        const subColor = CARD_COLORS[(i + 8) % CARD_COLORS.length];

                        return (
                            <div 
                                key={sub.id} 
                                onClick={() => onNavigate(`${selectedMenu.id}:${sub.id}`)} 
                                className={`absolute z-[100] ${shapeClass} shadow-2xl cursor-pointer transition-transform hover:scale-110 flex flex-col items-center justify-center p-3 text-center border-2 border-white/30 bg-gradient-to-br ${subColor} text-white`} 
                                style={{ 
                                    left: '50%',
                                    top: '50%',
                                    transform: `translate(${x}px, ${y}px) translate(-50%, -50%) ${cardShape === 'rhombus' ? 'rotate(45deg)' : ''}` 
                                }}
                            >
                                <div className={`${cardShape === 'rhombus' ? '-rotate-45' : ''} font-black leading-tight drop-shadow-lg uppercase ${getTextSize()}`}>
                                    {sub.label}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // ============================================================================
    // COMPONENTE: GESTÃO DO PONTO (RELATÓRIOS AVANÇADOS)
    // ============================================================================


    /* ===============================================================================
     * [END_MOD_CARD_NAVIGATION_MENU]
     * FIM DO BLOCO: CardNavigationMenu
     * [START_MOD_PONTO_REPORTS_MODULE]
     * INICIO DO BLOCO: PontoReportsModule
     * =============================================================================== */
    const PontoReportsModule = ({ isDarkMode, currentUser, db, collaborators, departments, pontoConfig }) => {
        const [filters, setFilters] = React.useState({
            startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
            endDate: new Date().toISOString().split('T')[0],
            departmentId: '',
            personType: 'Todos',
            reportType: 'extras',
            movtoType: 'Todos', // NOVO: Filtro de Movimento
            search: ''          // NOVO: Busca livre
        });
        const [records, setRecords] = React.useState([]);
        const [loading, setLoading] = React.useState(false);

        React.useEffect(() => {
            if (!db) return;
            setLoading(true);
            // Busca todo o histórico até a Data Final para poder calcular os Saldos reais
            const q = query(
                collection(db, `${FIRESTORE_PATH_PUBLIC}/time_records`),
                where('date', '<=', filters.endDate)
            );
            const unsub = onSnapshot(q, (snap) => {
                setRecords(snap.docs.map(d => ({id: d.id, ...d.data()})));
                setLoading(false);
            });
            return () => unsub();
        }, [db, filters.endDate]); // Depende apenas da endDate agora

        const processedData = React.useMemo(() => {
            let filteredCollabs = collaborators.filter(c => {
                if (filters.departmentId && c.departmentId !== filters.departmentId) return false;
                if (filters.personType !== 'Todos' && c.personType !== filters.personType) return false;
                if (currentUser.role !== 'SuperAdm' && c.departmentId !== currentUser.departmentId) return false;
                return true;
            });

            const allowedUserIds = filteredCollabs.map(c => c.id);
            const allRecords = records.filter(r => allowedUserIds.includes(r.userId));
            const periodRecords = allRecords.filter(r => r.date >= filters.startDate); // Filtro local para os que exigem Data Inicial

            let reportRows = [];
            const contractMins = timeToMinutesPonto(pontoConfig?.workHours || '08:00');
            const tolerance = parseInt(pontoConfig?.tolerance || 10);

            // 1. RELAÇÃO DE JUSTIFICATIVAS OU ATESTADOS (Usa o período)
            if (filters.reportType === 'justificativas' || filters.reportType === 'atestados') {
                const isAtestadoReport = filters.reportType === 'atestados';
                periodRecords.forEach(r => {
                    if (r.type === 'Justificativa') {
                        const cat = (r.category || '').toLowerCase();
                        const isAtestado = cat.includes('atestado') || cat.includes('médic') || cat.includes('medic');
                        if ((isAtestadoReport && isAtestado) || (!isAtestadoReport && !isAtestado)) {
                            const collab = filteredCollabs.find(c => c.id === r.userId);
                            
                            let infoStr = `Motivo: ${r.category} | ${r.details || ''} ${r.obs ? '| Obs: '+r.obs : ''}`;
                            if (isAtestadoReport && r.attachmentUrl) {
                                infoStr += `\nLink do Atestado: ${r.attachmentUrl}`;
                            }

                            reportRows.push({
                                id: r.id,
                                collabName: collab?.name || 'N/D',
                                department: departments.find(d => d.id === collab?.departmentId)?.name || 'N/D',
                                personType: collab?.personType || 'N/D',
                                date: new Date(r.date + 'T12:00:00').toLocaleDateString('pt-BR'),
                                info: infoStr,
                                value: r.status === 'Approved' ? 'Abonado/Validado' : 'Pendente',
                                isNegative: r.status === 'Pending'
                            });
                        }
                    }
                });
            }
            // 2. RELAÇÃO DE ATRASOS (Usa o período)
            else if (filters.reportType === 'atrasos') {
                periodRecords.forEach(r => {
                    if (r.status === 'Pending' && (r.source === 'digital' || r.source === 'manual')) {
                        const collab = filteredCollabs.find(c => c.id === r.userId);
                        reportRows.push({
                                id: r.id,
                                collabName: collab?.name || 'N/D',
                                department: departments.find(d => d.id === collab?.departmentId)?.name || 'N/D',
                                personType: collab?.personType || 'N/D',
                                date: new Date(r.date + 'T12:00:00').toLocaleDateString('pt-BR'),
                                info: `Registro: ${r.type} às ${r.time} (${r.source === 'manual' ? 'Manual' : 'App'})`,
                                value: 'Fora do Padrão',
                                isNegative: true
                            });
                    }
                });
            }
            // 3. BANCO DE HORAS (SALDOS ACUMULADOS TOTAIS ATÉ A DATA FINAL)
            else if (filters.reportType === 'extras') {
                filteredCollabs.forEach(collab => {
                    // CORREÇÃO: Só contabiliza no relatório batidas aprovadas (ou sem status, como os registros antigos)
                    const userRecords = allRecords.filter(r => r.userId === collab.id && r.type !== 'Justificativa' && r.type !== 'DayNote' && r.type !== 'RHNote' && r.type !== 'Observation' && (r.status === 'Approved' || !r.status));
                    const userJustifications = allRecords.filter(r => r.userId === collab.id && r.type === 'Justificativa' && r.status === 'Approved');
                    
                    const byDate = {};
                    userRecords.forEach(p => {
                        if (!byDate[p.date]) byDate[p.date] = [];
                        byDate[p.date].push(p);
                    });

                    // Inicia com o saldo do cadastro
                    let totalMins = parseInt(collab.initialBalanceMinutes) || 0;
                    const todayStr = new Date().toISOString().split('T')[0];
                    
                    Object.keys(byDate).forEach(d => {
                        if (d < (collab.initialDate || '2020-01-01')) return; // Ignora anteriores à implantação
                        
                        const punches = byDate[d].sort((a,b) => a.time.localeCompare(b.time));
                        let worked = 0;
                        for(let i=0; i<punches.length-1; i+=2) {
                            worked += (timeToMinutesPonto(punches[i+1].time) - timeToMinutesPonto(punches[i].time));
                        }
                        const isWeekend = new Date(d+'T12:00:00').getDay() === 0 || new Date(d+'T12:00:00').getDay() === 6;
                        let diff = 0;

                        if (d === todayStr) {
                            if (punches.length >= 4) {
                                diff = isWeekend ? worked : (worked - contractMins);
                                if (!isWeekend && Math.abs(diff) <= tolerance) diff = 0;
                            } else {
                                diff = isWeekend ? worked : 0;
                            }
                        } else if (d > todayStr) {
                            diff = worked;
                        } else {
                            diff = isWeekend ? worked : (worked - contractMins);
                            if (!isWeekend && Math.abs(diff) <= tolerance) diff = 0;
                        }
                        
                        // Abona se tiver justificativa aprovada no dia
                        const hasAbono = userJustifications.some(j => j.date === d);
                        if (hasAbono && diff < 0) diff = 0;

                        totalMins += diff;
                    });
                    
                    reportRows.push({
                        id: collab.id,
                        collabName: collab.name,
                        department: departments.find(d => d.id === collab.departmentId)?.name || 'N/D',
                        personType: collab.personType || 'N/D',
                        date: `Até ${filters.endDate.split('-').reverse().join('/')}`,
                        info: `Saldo Acumulado Real`,
                        value: minutesToTimePonto(totalMins) + ' h',
                        isNegative: totalMins < 0
                    });
                });
            }
            // 4. MOVIMENTO DO BANCO DE HORAS (Filtrado no período exato)
            else if (filters.reportType === 'movto') {
                filteredCollabs.forEach(collab => {
                    // CORREÇÃO: Só contabiliza no relatório batidas aprovadas
                    const userRecords = periodRecords.filter(r => r.userId === collab.id && r.type !== 'Justificativa' && r.type !== 'DayNote' && r.type !== 'RHNote' && r.type !== 'Observation' && (r.status === 'Approved' || !r.status));
                    const userJustifications = periodRecords.filter(r => r.userId === collab.id && r.type === 'Justificativa' && r.status === 'Approved');
                    const userRHNotes = periodRecords.filter(r => r.userId === collab.id && r.type === 'RHNote'); // Para buscar Pagas/Compensadas

                    const byDate = {};
                    userRecords.forEach(p => {
                        if (!byDate[p.date]) byDate[p.date] = [];
                        byDate[p.date].push(p);
                    });

                    let sumPositivas = 0;
                    let sumNegativas = 0;
                    let sumPagas = 0;
                    let sumCompensadas = 0;

                    // Calcula as diárias do período
                    const todayStr = new Date().toISOString().split('T')[0];
                    Object.keys(byDate).forEach(d => {
                        const punches = byDate[d].sort((a,b) => a.time.localeCompare(b.time));
                        let worked = 0;
                        for(let i=0; i<punches.length-1; i+=2) {
                            worked += (timeToMinutesPonto(punches[i+1].time) - timeToMinutesPonto(punches[i].time));
                        }
                        const isWeekend = new Date(d+'T12:00:00').getDay() === 0 || new Date(d+'T12:00:00').getDay() === 6;
                        let diff = 0;

                        if (d === todayStr) {
                            if (punches.length >= 4) {
                                diff = isWeekend ? worked : (worked - contractMins);
                                if (!isWeekend && Math.abs(diff) <= tolerance) diff = 0;
                            } else {
                                diff = isWeekend ? worked : 0;
                            }
                        } else if (d > todayStr) {
                            diff = worked;
                        } else {
                            diff = isWeekend ? worked : (worked - contractMins);
                            if (!isWeekend && Math.abs(diff) <= tolerance) diff = 0;
                        }
                        
                        const hasAbono = userJustifications.some(j => j.date === d);
                        if (hasAbono && diff < 0) diff = 0;

                        if (diff > 0) sumPositivas += diff;
                        if (diff < 0) sumNegativas += diff;
                    });

                    // Varre as notas de RH procurando abatimentos manuais
                    userRHNotes.forEach(note => {
                        const txt = (note.obs || '').toLowerCase();
                        if (txt.includes('paga') || txt.includes('pagamento')) sumPagas += 1; // Registro simples
                        if (txt.includes('compensad')) sumCompensadas += 1;
                    });

                    const pushRow = (tipo, min, isNeg) => {
                        if (min !== 0) {
                            reportRows.push({
                                id: `${collab.id}_${tipo}`,
                                collabName: collab.name,
                                department: departments.find(d => d.id === collab.departmentId)?.name || 'N/D',
                                personType: collab.personType || 'N/D',
                                date: `${filters.startDate.split('-').reverse().join('/')} a ${filters.endDate.split('-').reverse().join('/')}`,
                                info: `Movimento: ${tipo}`,
                                value: typeof min === 'number' ? minutesToTimePonto(min) + ' h' : min,
                                isNegative: isNeg
                            });
                        }
                    };

                    if (filters.movtoType === 'Todos' || filters.movtoType === 'Positivas') pushRow('Horas Extras Positivas', sumPositivas, false);
                    if (filters.movtoType === 'Todos' || filters.movtoType === 'Negativas') pushRow('Horas Extras Negativas', sumNegativas, true);
                    if (filters.movtoType === 'Todos' || filters.movtoType === 'Pagas') pushRow('Horas Extras Pagas', sumPagas > 0 ? `${sumPagas} registro(s)` : 0, false);
                    if (filters.movtoType === 'Todos' || filters.movtoType === 'Compensadas') pushRow('Horas Compensadas', sumCompensadas > 0 ? `${sumCompensadas} registro(s)` : 0, false);
                });
            }
            
            // --- APLICA A BUSCA LIVRE GERAL ---
            if (filters.search) {
                const s = filters.search.toLowerCase();
                reportRows = reportRows.filter(r => 
                    r.collabName.toLowerCase().includes(s) || 
                    r.department.toLowerCase().includes(s) || 
                    r.info.toLowerCase().includes(s) || 
                    r.value.toLowerCase().includes(s)
                );
            }

            return reportRows.sort((a,b) => a.collabName.localeCompare(b.collabName));
        }, [records, filters, collaborators, departments, currentUser, pontoConfig]);

        const handleExportPDF = async () => {
            if (typeof loadPdfLibs !== 'undefined') await loadPdfLibs();
            const PDFConstructor = (window.jspdf && window.jspdf.jsPDF) ? window.jspdf.jsPDF : window.jsPDF;
            const doc = new PDFConstructor('l', 'mm', 'a4');
            
            doc.setFillColor(79, 70, 229);
            doc.rect(0, 0, 297, 20, 'F');
            doc.setTextColor(255);
            doc.setFontSize(16);
            doc.setFont("helvetica", "bold");
            
            const titles = {
                extras: 'Banco de Horas (Saldo Atualizado)',
                justificativas: 'Relação de Justificativas / Ausências',
                atrasos: 'Relação de Atrasos e Adiantos',
                atestados: 'Relação de Atestados Médicos',
                movto: 'Movimento do Banco de Horas'
            };
            
            doc.text(`Relatório: ${titles[filters.reportType]}`, 14, 13);
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            if (filters.reportType === 'extras') {
                doc.text(`Posição em: ${filters.endDate.split('-').reverse().join('/')}`, 240, 13);
            } else {
                doc.text(`Período: ${filters.startDate.split('-').reverse().join('/')} a ${filters.endDate.split('-').reverse().join('/')}`, 220, 13);
            }

            const tableBody = processedData.map(r => [
                r.collabName,
                r.department,
                r.personType,
                r.date,
                r.info,
                r.value
            ]);

            doc.autoTable({
                startY: 25,
                head: [['Nome', 'Depto', 'Tipo', 'Data/Período', 'Detalhes / Info', 'Valor/Status']],
                body: tableBody,
                theme: 'grid',
                styles: { fontSize: 8 },
                headStyles: { fillColor: [79, 70, 229] },
                columnStyles: { 5: { fontStyle: 'bold', halign: 'center' } },
                didParseCell: (data) => {
                    if (data.section === 'body' && data.column.index === 5) {
                        const raw = data.cell.raw;
                        if (raw.includes('-') || raw === 'Pendente' || raw === 'Fora do Padrão') {
                            data.cell.styles.textColor = [220, 38, 38];
                        } else {
                            data.cell.styles.textColor = [22, 163, 74];
                        }
                    }
                }
            });
            
            // MODIFICADO: Abre o PDF numa nova guia em vez de baixar automaticamente (ITEM E)
            window.open(doc.output('bloburl'), '_blank');
        };

        const handleExportExcel = async () => {
            if (typeof loadImportLibs !== 'undefined') await loadImportLibs();
            if (!window.XLSX) return alert("Biblioteca XLSX não disponível.");
            const dataExport = processedData.map(r => ({
                "Nome do Colaborador": r.collabName,
                "Departamento": r.department,
                "Tipo de Pessoa": r.personType,
                "Data / Período": r.date,
                "Detalhes da Ocorrência": r.info,
                "Valor / Status": r.value
            }));
            const ws = window.XLSX.utils.json_to_sheet(dataExport);
            const wb = window.XLSX.utils.book_new();
            window.XLSX.utils.book_append_sheet(wb, ws, "Relatorio");
            window.XLSX.writeFile(wb, `Gestao_Ponto_${filters.reportType}.xlsx`);
        };

        return (
            <div className="flex flex-col h-[600px] animate-fade-in">
                <div className={`p-4 rounded-xl border shadow-sm mb-4 ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-3 items-end">
                        
                        {/* TIPO DE RELATÓRIO */}
                        <div className="col-span-2 md:col-span-2">
                            <label className={`text-[10px] font-bold uppercase opacity-70 block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Tipo de Relatório</label>
                            <select className={`w-full p-2 border rounded text-xs outline-none font-bold focus:ring-2 focus:ring-indigo-500 ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white text-indigo-700 border-indigo-200'}`} value={filters.reportType} onChange={e=>setFilters({...filters, reportType: e.target.value})}>
                                <option value="extras">1. Banco de Horas (Saldos Acumulados)</option>
                                <option value="movto">2. Movimento Banco de Horas</option>
                                <option value="justificativas">3. Relação de Justificativas</option>
                                <option value="atrasos">4. Relação de Atrasos/Adiantos</option>
                                <option value="atestados">5. Relação de Atestados</option>
                            </select>
                        </div>

                        {/* DATAS COM REGRA CONDICIONAL DE OCULTAÇÃO */}
                        {filters.reportType !== 'extras' && (
                            <div>
                                <label className={`text-[10px] font-bold uppercase opacity-70 block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Data Inicial</label>
                                <input type="date" className={`w-full p-2 border rounded text-xs outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white invert-calendar' : 'bg-white'}`} value={filters.startDate} onChange={e=>setFilters({...filters, startDate: e.target.value})}/>
                            </div>
                        )}
                        <div>
                            <label className={`text-[10px] font-bold uppercase opacity-70 block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Data Final</label>
                            <input type="date" className={`w-full p-2 border rounded text-xs outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white invert-calendar' : 'bg-white'}`} value={filters.endDate} onChange={e=>setFilters({...filters, endDate: e.target.value})}/>
                        </div>

                        {/* FILTRO DE MOVIMENTO (SÓ APARECE SE TIPO = MOVTO) */}
                        {filters.reportType === 'movto' && (
                            <div className="col-span-2">
                                <label className={`text-[10px] font-bold uppercase text-orange-600 block mb-1`}>Filtro Específico (Movimentação)</label>
                                <select className={`w-full p-2 border border-orange-300 rounded text-xs outline-none ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-orange-50 text-orange-800'}`} value={filters.movtoType} onChange={e=>setFilters({...filters, movtoType: e.target.value})}>
                                    <option value="Todos">Todos os Movimentos</option>
                                    <option value="Positivas">Somente Horas Extras Positivas</option>
                                    <option value="Negativas">Somente Horas Extras Negativas</option>
                                    <option value="Compensadas">Horas Compensadas</option>
                                    <option value="Pagas">Horas Extras Pagas</option>
                                </select>
                            </div>
                        )}

                        {/* DEPARTAMENTO E PÚBLICO */}
                        <div>
                            <label className={`text-[10px] font-bold uppercase opacity-70 block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Departamento</label>
                            <select className={`w-full p-2 border rounded text-xs outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`} value={filters.departmentId} onChange={e=>setFilters({...filters, departmentId: e.target.value})}>
                                <option value="">Todos</option>
                                {departments.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className={`text-[10px] font-bold uppercase opacity-70 block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Público</label>
                            <select className={`w-full p-2 border rounded text-xs outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`} value={filters.personType} onChange={e=>setFilters({...filters, personType: e.target.value})}>
                                <option value="Todos">Ambos</option>
                                <option value="Colaborador">Colaboradores</option>
                                <option value="Gestão">Gestão</option>
                            </select>
                        </div>

                        {/* BUSCA LIVRE (NOVO CAMPO) */}
                        <div className="col-span-2">
                            <label className={`text-[10px] font-bold uppercase opacity-70 block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Busca Livre</label>
                            <div className="relative">
                                <Search className="absolute left-2 top-2 text-gray-400" size={14}/>
                                <input type="text" placeholder="Pesquisar por nome, detalhe..." className={`w-full pl-8 p-2 border rounded text-xs outline-none focus:ring-1 focus:ring-indigo-500 ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`} value={filters.search} onChange={e=>setFilters({...filters, search: e.target.value})} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-between items-center mb-2">
                    <span className={`text-xs font-bold opacity-70 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Total de Registros: {processedData.length}</span>
                    <div className="flex gap-2">
                        <button onClick={handleExportPDF} className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-1.5 px-3 rounded flex items-center gap-1 transition-colors shadow-sm"><FileText size={14}/> PDF (Nova Guia)</button>
                        <button onClick={handleExportExcel} className="bg-green-600 hover:bg-green-700 text-white text-xs font-bold py-1.5 px-3 rounded flex items-center gap-1 transition-colors shadow-sm"><FileSpreadsheet size={14}/> Excel</button>
                    </div>
                </div>

                <div className={`flex-1 overflow-y-auto border rounded-xl shadow-sm custom-scrollbar ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                    <table className="w-full text-xs text-left">
                        <thead className={`uppercase font-bold sticky top-0 shadow-sm z-10 ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-600'}`}>
                            <tr>
                                <th className="p-3">Nome / Depto</th>
                                <th className="p-3 text-center">Tipo</th>
                                <th className="p-3 text-center">Data / Período</th>
                                <th className="p-3">Detalhes</th>
                                <th className="p-3 text-center">Status / Valor</th>
                            </tr>
                        </thead>
                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                            {loading ? <tr><td colSpan="5" className="p-8 text-center"><Loader2 className="animate-spin mx-auto text-indigo-500" size={32}/></td></tr> :
                            processedData.length === 0 ? <tr><td colSpan="5" className={`p-8 text-center italic opacity-50 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Nenhum dado encontrado para este filtro.</td></tr> :
                            processedData.map((row, i) => (
                                <tr key={row.id + i} className={isDarkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-white hover:bg-gray-50'}>
                                    <td className="p-3">
                                        <div className={`font-bold ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{row.collabName}</div>
                                        <div className={`text-[9px] opacity-70 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{row.department}</div>
                                    </td>
                                    <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded border text-[9px] font-bold uppercase ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-300' : 'bg-gray-100 text-gray-600'}`}>{row.personType}</span></td>
                                    <td className={`p-3 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>{row.date}</td>
                                    <td className={`p-3 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{row.info}</td>
                                    <td className={`p-3 text-center font-bold ${row.isNegative ? 'text-red-500' : 'text-green-600'}`}>{row.value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    /**
     * ============================================================================
     * ORCHESTRATOR: APP PRINCIPAL (SUÍTE CORPORATIVA)
     * ============================================================================
     */


    /* ===============================================================================
     * [END_MOD_PONTO_REPORTS_MODULE]
     * FIM DO BLOCO: PontoReportsModule
     * [START_MOD_APP_MAIN]
     * INICIO DO BLOCO: App
     * =============================================================================== */

// v69_346 - Central de Ajuda alinhada ao projeto CTB.
const PLANILHA_PROXY_URL_AJUDA = 'https://apiproxy-lqn64wkzdq-uc.a.run.app';
const normalizarEmailCentralAjuda = (valor) => String(valor || '').trim().toLowerCase();

// CENTRAL DE AJUDA, MANUAL E SUGESTOES DE MELHORIAS
// ============================================================================
const SUGESTOES_MELHORIAS_COLECOES = {
  CFG: 'SugestoesMelhorias_CFG',
  SUGESTOES: 'SugestoesMelhorias_Sugestões'
};

const SUGESTOES_STATUS = [
  'Enviado',
  'Em Análise',
  'Em Fila de Desenvolvimento',
  'Em Desenvolvimento',
  'Pausado',
  'Não será feito',
  'Em Revisão',
  'Em fase de testes',
  'Concluído'
];

const SUGESTOES_PRIORIDADES = ['Não definida', 'Baixa', 'Média', 'Alta', 'Urgente'];
const classeStatusSugestao = (status = 'Enviado') => ({
  'Enviado': 'bg-sky-600 text-white',
  'Em Análise': 'bg-indigo-600 text-white',
  'Em Fila de Desenvolvimento': 'bg-violet-600 text-white',
  'Em Desenvolvimento': 'bg-blue-700 text-white',
  'Pausado': 'bg-amber-600 text-white',
  'Não será feito': 'bg-slate-600 text-white',
  'Em Revisão': 'bg-fuchsia-600 text-white',
  'Em fase de testes': 'bg-orange-600 text-white',
  'Concluído': 'bg-emerald-600 text-white'
}[status] || 'bg-cyan-700 text-white');
const classeTipoSugestao = (tipo = 'Sugestão') => ({
  'Sugestão': 'bg-violet-600 text-white',
  'Correção': 'bg-rose-600 text-white',
  'Melhoria': 'bg-emerald-600 text-white',
  'Dúvida': 'bg-amber-600 text-white'
}[tipo] || 'bg-slate-600 text-white');
const SUGESTOES_CONFIG_CACHE_KEY = 'facil_sugestoes_melhorias_config_v1';
const CONFIG_SUGESTOES_PADRAO = {
  id: 'config_geral',
  tipo: 'config_geral',
  driveFolderId: '1Uni-hfbm8zC7GgC0RlJxxS7FknGSogrx',
  appsScriptUrl: '',
  uploadToken: '',
  manualDriveFolderId: '1GQepxn7VH_sxJXKYpXWuCiJGT0yeqwaQ',
  manualAppsScriptUrl: '',
  superAdminEmails: ['mauriciobremide@grupoacad.com.br'],
  usuariosResponsaveis: []
};

const lerCacheConfigSugestoes = () => {
  try {
    const bruto = localStorage.getItem(SUGESTOES_CONFIG_CACHE_KEY);
    const dados = bruto ? JSON.parse(bruto) : null;
    return dados && typeof dados === 'object' ? dados : null;
  } catch { return null; }
};
const salvarCacheConfigSugestoes = (dados) => {
  try { localStorage.setItem(SUGESTOES_CONFIG_CACHE_KEY, JSON.stringify(dados || {})); }
  catch (error) { console.warn('[SUGESTOES] Não foi possível salvar o cache das configurações:', error); }
};
const configSugestoesTemDados = (item = {}) => Boolean(
  item.appsScriptUrl || item.uploadToken || item.driveFolderId || item.manualAppsScriptUrl || item.manualDriveFolderId ||
  (Array.isArray(item.superAdminEmails) && item.superAdminEmails.length) ||
  (Array.isArray(item.usuariosResponsaveis) && item.usuariosResponsaveis.length)
);
const escolherConfigSugestoes = (lista = []) => {
  const candidatos = (lista || []).filter(item => item && (
    item.tipo === 'config_geral' || item.id === 'config_geral' ||
    Object.prototype.hasOwnProperty.call(item, 'appsScriptUrl') ||
    Object.prototype.hasOwnProperty.call(item, 'uploadToken') ||
    Object.prototype.hasOwnProperty.call(item, 'driveFolderId') ||
    Object.prototype.hasOwnProperty.call(item, 'manualAppsScriptUrl') ||
    Object.prototype.hasOwnProperty.call(item, 'manualDriveFolderId') ||
    Object.prototype.hasOwnProperty.call(item, 'superAdminEmails')
  ));
  return candidatos.sort((a, b) => {
    const dataB = Date.parse(b.atualizadoEm || b.updatedAt || b.criadoEm || '') || 0;
    const dataA = Date.parse(a.atualizadoEm || a.updatedAt || a.criadoEm || '') || 0;
    const score = x => (x.id === 'config_geral' ? 20 : 0) + (x.tipo === 'config_geral' ? 10 : 0) + (configSugestoesTemDados(x) ? 5 : 0);
    return (score(b) - score(a)) || (dataB - dataA);
  })[0] || null;
};

const caminhoSugestoesPublico = (colecao) => `${FIRESTORE_PATH_PUBLIC}/${colecao}`;
const normalizarTextoSugestoes = (valor) => String(valor || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
const gerarIdSugestoes = (prefixo = 'registro') => `${prefixo}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
const formatarDataHoraSugestoes = (valor) => {
  if (!valor) return '-';
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? String(valor) : data.toLocaleString('pt-BR');
};
const formatarDataSugestoes = (valor) => {
  if (!valor) return '-';
  const texto = String(valor).trim();
  const matchIso = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (matchIso) return `${matchIso[3]}/${matchIso[2]}/${matchIso[1]}`;
  const data = new Date(texto);
  if (Number.isNaN(data.getTime())) return texto;
  return data.toLocaleDateString('pt-BR');
};
const obterNomeUsuarioSugestoes = (usuario) => usuario?.name || usuario?.displayName || usuario?.email || 'Usuário';
const sanitizarHtmlSugestoes = (html = '') => String(html || '')
  .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
  .replace(/\son\w+=("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
  .replace(/javascript:/gi, '');
const htmlParaTextoSugestoes = (html = '') => String(html || '')
  .replace(/<br\s*\/?>/gi, '\n')
  .replace(/<\/p>/gi, '\n')
  .replace(/<\/div>/gi, '\n')
  .replace(/<[^>]+>/g, ' ')
  .replace(/&nbsp;/g, ' ')
  .replace(/&amp;/g, '&')
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .replace(/\s+\n/g, '\n')
  .replace(/\n{3,}/g, '\n\n')
  .trim();

function CentralAjudaSugestoes({ aberto, onClose, isDarkMode = false, user = null, telaAtual = '', focoNotificacoesSugestoes = 0, onNotificacoesSugestoesChange = null, modoAba = false }) {
  const db = getFirestore();
  const [aba, setAba] = useState('manual');
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState(null);
  const [cfgDocs, setCfgDocs] = useState([]);
  const [sugestoes, setSugestoes] = useState([]);
  const [pesquisaManual, setPesquisaManual] = useState('');
  const [faqAberta, setFaqAberta] = useState(null);
  const [manuaisDrive, setManuaisDrive] = useState(() => { try { return JSON.parse(localStorage.getItem('facil_manuais_drive_indice_v1') || '[]'); } catch { return []; } });
  const [manualDriveAberto, setManualDriveAberto] = useState(null);
  const [manualAnotacoes, setManualAnotacoes] = useState({ nota: '', marcacoes: [] });
  const [novaMarcacaoManual, setNovaMarcacaoManual] = useState({ texto: '', cor: 'amarelo', comentario: '' });
  const [painelAnotacoesAberto, setPainelAnotacoesAberto] = useState(true);
  const [manualModuloFiltro, setManualModuloFiltro] = useState('Todos');
  const [visualizacaoManual, setVisualizacaoManual] = useState(() => { try { return localStorage.getItem('facil_manuais_visualizacao_v1') || 'cards'; } catch { return 'cards'; } });
  const [favoritosManuais, setFavoritosManuais] = useState(() => { try { return JSON.parse(localStorage.getItem(`facil_manuais_favoritos_v1_${normalizarEmailCentralAjuda(user?.email || 'usuario')}`) || '[]'); } catch { return []; } });
  const [carregandoManuais, setCarregandoManuais] = useState(false);
  const [popupNovaSugestao, setPopupNovaSugestao] = useState(false);
  const [sugestaoEmEdicao, setSugestaoEmEdicao] = useState(null);
  const [popupComplemento, setPopupComplemento] = useState(null);
  const [textoComplemento, setTextoComplemento] = useState('');
  const [popupHistorico, setPopupHistorico] = useState(null);
  const [filtroStatusAberto, setFiltroStatusAberto] = useState(false);
  const [popupConfig, setPopupConfig] = useState(false);
  const [sugestaoAdmin, setSugestaoAdmin] = useState(null);
  const [arquivos, setArquivos] = useState([]);
  const [formSugestao, setFormSugestao] = useState({ tipo: 'Sugestão', tituloCurto: '', tela: telaAtual || '', descricao: '', descricaoHtml: '' });
  const [ordenacaoSugestoes, setOrdenacaoSugestoes] = useState({ campo: '', direcao: '' });
  const editorSugestaoRef = useRef(null);
  const [filtros, setFiltros] = useState({ usuario: '', tela: '', data: '', status: [], prioridade: '', analista: '', desenvolvedor: '', livre: '', conclusao: 'pendentes' });
  const [config, setConfig] = useState(() => ({
    ...CONFIG_SUGESTOES_PADRAO,
    ...(lerCacheConfigSugestoes() || {})
  }));
  const [faqForm, setFaqForm] = useState({ id: '', pergunta: '', resposta: '', tela: '', categoria: '', palavrasChaveTexto: '', ordem: 0, ativo: true });
  const [posicaoCentralAjuda, setPosicaoCentralAjuda] = useState(() => {
    try { return JSON.parse(localStorage.getItem('facil_central_ajuda_posicao_v1') || '{"x":0,"y":0}'); } catch { return { x: 0, y: 0 }; }
  });
  const [posicaoManualAberto, setPosicaoManualAberto] = useState(() => {
    try { return JSON.parse(localStorage.getItem('facil_manual_aberto_posicao_v1') || '{"x":0,"y":0}'); } catch { return { x: 0, y: 0 }; }
  });
  const dragCentralRef = useRef(null);
  const dragManualRef = useRef(null);

  useEffect(() => {
    if (configSugestoesTemDados(config)) salvarCacheConfigSugestoes(config);
  }, [config]);
  useEffect(() => {
    try { localStorage.setItem('facil_central_ajuda_posicao_v1', JSON.stringify(posicaoCentralAjuda)); } catch {}
  }, [posicaoCentralAjuda]);
  useEffect(() => {
    try { localStorage.setItem('facil_manual_aberto_posicao_v1', JSON.stringify(posicaoManualAberto)); } catch {}
  }, [posicaoManualAberto]);


  const emailUsuario = normalizarEmailCentralAjuda(user?.email || '');
  useEffect(() => { try { localStorage.setItem(`facil_manuais_favoritos_v1_${emailUsuario || 'usuario'}`, JSON.stringify(favoritosManuais || [])); } catch {} }, [favoritosManuais, emailUsuario]);
  useEffect(() => { try { localStorage.setItem('facil_manuais_visualizacao_v1', visualizacaoManual); } catch {} }, [visualizacaoManual]);
  const superAdminEmails = (config.superAdminEmails || []).map(normalizarEmailCentralAjuda);
  const roleUsuarioAjuda = String(user?.role || '').trim().toLowerCase();
  const isSuperAdm = roleUsuarioAjuda === 'superadm' || roleUsuarioAjuda === 'superadmin' || user?.isSuperAdmin === true || superAdminEmails.includes(emailUsuario);
  const faqs = useMemo(() => cfgDocs.filter(item => item.tipo === 'faq' && item.ativo !== false), [cfgDocs]);
  const acessosFaq = useMemo(() => cfgDocs.filter(item => item.tipo === 'faq_acesso'), [cfgDocs]);

  const chamarApiManualDrive = async (action, params = {}) => {
    const base = String(config.manualAppsScriptUrl || '').trim();
    if (!base) throw new Error('A URL do Apps Script do Manual ainda não foi configurada.');
    const url = new URL(base);
    url.searchParams.set('action', action);
    Object.entries(params || {}).forEach(([chave, valor]) => {
      if (valor !== undefined && valor !== null && String(valor) !== '') url.searchParams.set(chave, String(valor));
    });
    try {
      const resp = await fetch(url.toString(), { method: 'GET', redirect: 'follow' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      if (!json?.ok) throw new Error(json?.erro || 'Falha ao consultar o manual.');
      return json.resultado;
    } catch (erroDireto) {
      const proxyResp = await fetch(PLANILHA_PROXY_URL_AJUDA, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUrl: url.toString(), method: 'GET', headers: {} })
      });
      const proxyJson = await proxyResp.json();
      const resposta = proxyJson?.resultado || proxyJson?.data || proxyJson;
      if (!proxyResp.ok || resposta?.ok === false) throw new Error(resposta?.erro || proxyJson?.erro || erroDireto?.message || 'Falha ao consultar o manual.');
      return resposta?.resultado ?? resposta;
    }
  };

  const explicarErroManualDrive = (error) => {
    const texto = String(error?.message || error || 'falha de conexão.');
    if (/SpreadsheetApp/i.test(texto) || /spreadsheets/i.test(texto)) {
      return 'O Apps Script dos manuais está tentando usar SpreadsheetApp, mas a implantação atual não tem autorização para planilhas. Publique a versão V02 do Apps Script dos Manuais, que monta o índice diretamente pela pasta do Drive e não depende de SpreadsheetApp.';
    }
    if (/permission|permiss/i.test(texto)) {
      return `Permissão insuficiente no Apps Script dos manuais: ${texto}. Confirme se a implantação executa como o proprietário do script e se essa conta tem acesso à pasta dos manuais.`;
    }
    return texto;
  };

  const normalizarIndiceManuaisDrive = (lista = []) => {
    const itens = (Array.isArray(lista) ? lista : []).map(item => ({
      ...item,
      idDocumentoGoogle: extrairIdDocumentoGoogle(item),
      titulo: item?.titulo || item?.name || item?.nome || item?.title || 'Manual sem título',
      atualizadoEm: item?.atualizadoEm || item?.modifiedTime || item?.modifiedAt || item?.updatedAt || item?.createdTime || item?.criadoEm || ''
    }));
    const scoreData = (item) => Date.parse(item.atualizadoEm || item.modifiedTime || item.createdTime || '') || 0;
    const mapa = new Map();
    itens.sort((a, b) => scoreData(b) - scoreData(a)).forEach(item => {
      const chave = normalizarTextoSugestoes([obterModuloManual(item), item.menu || '', item.area || '', item.titulo || ''].join('|'));
      if (!mapa.has(chave)) mapa.set(chave, item);
    });
    return Array.from(mapa.values()).sort((a, b) => String(a.titulo || '').localeCompare(String(b.titulo || ''), 'pt-BR'));
  };

  const limparCacheManuaisDrive = () => {
    try { localStorage.removeItem('facil_manuais_drive_indice_v1'); } catch {}
    setManuaisDrive([]);
  };

  const carregarIndiceManuaisDrive = async ({ silencioso = false, limparCache = false } = {}) => {
    if (!config.manualAppsScriptUrl) return;
    if (limparCache) limparCacheManuaisDrive();
    setCarregandoManuais(true);
    try {
      const lista = await chamarApiManualDrive('listarManuais', { t: Date.now() });
      const normalizada = normalizarIndiceManuaisDrive(lista);
      setManuaisDrive(normalizada);
      try { localStorage.setItem('facil_manuais_drive_indice_v1', JSON.stringify(normalizada)); } catch {}
    } catch (error) {
      console.warn('[MANUAL] Falha ao carregar índice do Drive:', error);
      const cache = (() => { try { return JSON.parse(localStorage.getItem('facil_manuais_drive_indice_v1') || '[]'); } catch { return []; } })();
      if (Array.isArray(cache) && cache.length) setManuaisDrive(normalizarIndiceManuaisDrive(cache));
      if (!silencioso) setMensagem({ tipo: 'erro', texto: `Não foi possível carregar o manual do Drive: ${explicarErroManualDrive(error)}` });
    } finally { setCarregandoManuais(false); }
  };

  const extrairIdDocumentoGoogle = (manual) => {
    const urlOriginal = String(manual?.url || manual?.webViewLink || manual?.link || '').trim();
    const porUrl = urlOriginal.match(/\/document\/d\/([^/]+)/)?.[1] || urlOriginal.match(/[?&]id=([^&]+)/)?.[1] || '';
    if (porUrl) return porUrl;
    const candidatos = [manual?.documentId, manual?.document_id, manual?.fileId, manual?.file_id, manual?.driveId, manual?.googleId, manual?.idGoogle, manual?.idArquivo, manual?.arquivoId, manual?.id];
    return String(candidatos.find(v => /^[a-zA-Z0-9_-]{25,}$/.test(String(v || ''))) || '').trim();
  };

  const obterUrlVisualizacaoManual = (manual) => {
    const docId = extrairIdDocumentoGoogle(manual);
    if (docId) return `https://docs.google.com/document/d/${docId}/preview?embedded=true&rm=minimal&v=${Date.now()}`;
    const urlOriginal = String(manual?.url || manual?.webViewLink || manual?.link || '').trim();
    if (urlOriginal) return urlOriginal.replace('/edit', '/preview?embedded=true').replace('/view', '/preview?embedded=true');
    return '';
  };

  const obterUrlAbrirManual = (manual) => {
    const docId = extrairIdDocumentoGoogle(manual);
    if (docId) return `https://docs.google.com/document/d/${docId}/preview?v=${Date.now()}`;
    return String(manual?.url || manual?.webViewLink || manual?.link || '').trim();
  };

  const chaveAnotacoesManual = (manualId) => `facil_manual_anotacoes_privadas_v1_${emailUsuario || 'usuario'}_${manualId || 'sem_id'}`;
  const carregarAnotacoesManual = (manualId) => {
    try {
      const dados = JSON.parse(localStorage.getItem(chaveAnotacoesManual(manualId)) || '{}');
      return { nota: dados?.nota || '', marcacoes: Array.isArray(dados?.marcacoes) ? dados.marcacoes : [] };
    } catch { return { nota: '', marcacoes: [] }; }
  };
  const salvarAnotacoesManual = (manualId, anotacoes) => {
    const payload = { nota: anotacoes?.nota || '', marcacoes: Array.isArray(anotacoes?.marcacoes) ? anotacoes.marcacoes : [], atualizadoEm: new Date().toISOString() };
    setManualAnotacoes(payload);
    try { localStorage.setItem(chaveAnotacoesManual(manualId), JSON.stringify(payload)); } catch {}
  };
  const adicionarMarcacaoManual = () => {
    const texto = String(novaMarcacaoManual.texto || '').trim();
    if (!(manualDriveAberto?.idDocumentoGoogle || manualDriveAberto?.id || manualDriveAberto?.url) || !texto) return;
    const nova = { id: gerarIdSugestoes('marcacao'), texto, cor: novaMarcacaoManual.cor || 'amarelo', comentario: String(novaMarcacaoManual.comentario || '').trim(), criadoEm: new Date().toISOString() };
    const atualizada = { ...manualAnotacoes, marcacoes: [nova, ...(manualAnotacoes.marcacoes || [])] };
    salvarAnotacoesManual(manualDriveAberto.idDocumentoGoogle || manualDriveAberto.id || manualDriveAberto.url, atualizada);
    setNovaMarcacaoManual({ texto: '', cor: 'amarelo', comentario: '' });
  };
  const removerMarcacaoManual = (marcacaoId) => {
    if (!(manualDriveAberto?.idDocumentoGoogle || manualDriveAberto?.id || manualDriveAberto?.url)) return;
    const atualizada = { ...manualAnotacoes, marcacoes: (manualAnotacoes.marcacoes || []).filter(item => item.id !== marcacaoId) };
    salvarAnotacoesManual(manualDriveAberto.idDocumentoGoogle || manualDriveAberto.id || manualDriveAberto.url, atualizada);
  };

  const abrirManualDrive = (manual) => {
    const docId = extrairIdDocumentoGoogle(manual);
    const urlVisualizacao = obterUrlVisualizacaoManual(manual);
    if (!docId && !urlVisualizacao) return;
    const manualNormalizado = { ...manual, idDocumentoGoogle: docId, urlVisualizacao, urlAbrir: obterUrlAbrirManual(manual), abertoEm: new Date().toISOString() };
    setManualDriveAberto(manualNormalizado);
    setPainelAnotacoesAberto(true);
    setManualAnotacoes(carregarAnotacoesManual(docId || manual.id || manual.url));
    setNovaMarcacaoManual({ texto: '', cor: 'amarelo', comentario: '' });
  };


  const carregarDados = async () => {
    setCarregando(true);
    setMensagem(null);
    try {
      const [cfgSnap, sugestoesSnap] = await Promise.all([
        getDocs(collection(db, caminhoSugestoesPublico(SUGESTOES_MELHORIAS_COLECOES.CFG))),
        getDocs(collection(db, caminhoSugestoesPublico(SUGESTOES_MELHORIAS_COLECOES.SUGESTOES)))
      ]);
      const cfgLista = cfgSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const sugestoesLista = sugestoesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const cfgGeral = escolherConfigSugestoes(cfgLista);
      if (cfgGeral) {
        const configMesclada = {
          ...CONFIG_SUGESTOES_PADRAO,
          ...(lerCacheConfigSugestoes() || {}),
          ...cfgGeral,
          id: 'config_geral',
          tipo: 'config_geral',
          superAdminEmails: Array.isArray(cfgGeral.superAdminEmails) && cfgGeral.superAdminEmails.length
            ? cfgGeral.superAdminEmails
            : ((lerCacheConfigSugestoes() || {}).superAdminEmails || CONFIG_SUGESTOES_PADRAO.superAdminEmails),
          usuariosResponsaveis: Array.isArray(cfgGeral.usuariosResponsaveis)
            ? cfgGeral.usuariosResponsaveis
            : ((lerCacheConfigSugestoes() || {}).usuariosResponsaveis || [])
        };
        setConfig(configMesclada);
        salvarCacheConfigSugestoes(configMesclada);
      }
      setCfgDocs(cfgLista);
      setSugestoes(sugestoesLista.sort((a, b) => Date.parse(b.criadoEm || '') - Date.parse(a.criadoEm || '')));
    } catch (error) {
      console.error('[SUGESTOES] Erro ao carregar:', error);
      setMensagem({ tipo: 'erro', texto: `Não foi possível carregar a central: ${error?.message || 'falha de conexão.'}` });
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (!aberto) return;
    setFormSugestao(prev => ({ ...prev, tela: prev.tela || telaAtual || '' }));
    carregarDados();
    if (!manuaisDrive.length) carregarIndiceManuaisDrive({ silencioso: true });
  }, [aberto]);

  const obterModuloManual = (manual) => {
    const bruto = String(manual?.modulo || manual?.módulo || manual?.sistema || manual?.produto || manual?.app || '').trim();
    if (!bruto) return 'Pessoas';
    const normalizado = normalizarTextoSugestoes(bruto);
    if (normalizado === 'ctb' || normalizado.includes('contabil')) return 'CTB';
    if (normalizado.includes('pessoas') || normalizado.includes('gestao de pessoas')) return 'Pessoas';
    if (normalizado.includes('clientes')) return 'Clientes';
    if (normalizado.includes('hub')) return 'Hub';
    return bruto;
  };

  const chaveManual = (manual) => extrairIdDocumentoGoogle(manual) || String(manual?.url || manual?.id || manual?.titulo || 'manual');
  const manualEhFavorito = (manual) => favoritosManuais.includes(chaveManual(manual));
  const alternarFavoritoManual = (manual) => {
    const chave = chaveManual(manual);
    if (!chave) return;
    setFavoritosManuais(prev => prev.includes(chave) ? prev.filter(item => item !== chave) : [chave, ...prev]);
  };

  const modulosManuaisDisponiveis = useMemo(() => {
    const mods = new Set(['Todos']);
    (Array.isArray(manuaisDrive) ? manuaisDrive : []).forEach(item => mods.add(obterModuloManual(item)));
    return Array.from(mods);
  }, [manuaisDrive]);

  const faqsFiltradas = useMemo(() => {
    const termo = normalizarTextoSugestoes(pesquisaManual);
    if (!termo) return faqs;
    return faqs.filter(item => normalizarTextoSugestoes([item.pergunta, item.resposta, item.tela, ...(item.palavrasChave || [])].join(' ')).includes(termo));
  }, [faqs, pesquisaManual]);

  const manuaisDriveFiltrados = useMemo(() => {
    const termo = normalizarTextoSugestoes(pesquisaManual);
    const lista = Array.isArray(manuaisDrive) ? manuaisDrive : [];
    return lista.filter(item => {
      const moduloOk = manualModuloFiltro === 'Todos' || obterModuloManual(item) === manualModuloFiltro;
      if (!moduloOk) return false;
      if (!termo) return true;
      return normalizarTextoSugestoes([obterModuloManual(item), item.menu, item.area, item.titulo, ...(item.palavrasChave || [])].join(' ')).includes(termo);
    });
  }, [manuaisDrive, pesquisaManual, manualModuloFiltro]);

  const manuaisFavoritos = useMemo(() => {
    const lista = Array.isArray(manuaisDrive) ? manuaisDrive : [];
    return lista.filter(item => favoritosManuais.includes(chaveManual(item))).slice(0, 8);
  }, [manuaisDrive, favoritosManuais]);

  const temasManuais = [
    { bg: 'from-cyan-50 to-blue-50 dark:from-cyan-950/50 dark:to-blue-950/30', border: 'border-cyan-200 dark:border-cyan-800', chip: 'bg-cyan-600 text-white', title: 'text-cyan-950 dark:text-cyan-100' },
    { bg: 'from-emerald-50 to-teal-50 dark:from-emerald-950/50 dark:to-teal-950/30', border: 'border-emerald-200 dark:border-emerald-800', chip: 'bg-emerald-600 text-white', title: 'text-emerald-950 dark:text-emerald-100' },
    { bg: 'from-violet-50 to-fuchsia-50 dark:from-violet-950/50 dark:to-fuchsia-950/30', border: 'border-violet-200 dark:border-violet-800', chip: 'bg-violet-600 text-white', title: 'text-violet-950 dark:text-violet-100' },
    { bg: 'from-amber-50 to-orange-50 dark:from-amber-950/50 dark:to-orange-950/30', border: 'border-amber-200 dark:border-amber-800', chip: 'bg-amber-500 text-slate-950', title: 'text-amber-950 dark:text-amber-100' },
    { bg: 'from-rose-50 to-pink-50 dark:from-rose-950/50 dark:to-pink-950/30', border: 'border-rose-200 dark:border-rose-800', chip: 'bg-rose-600 text-white', title: 'text-rose-950 dark:text-rose-100' },
    { bg: 'from-indigo-50 to-sky-50 dark:from-indigo-950/50 dark:to-sky-950/30', border: 'border-indigo-200 dark:border-indigo-800', chip: 'bg-indigo-600 text-white', title: 'text-indigo-950 dark:text-indigo-100' }
  ];
  const hashManual = (texto = '') => String(texto).split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const temaManual = (manual, index = 0) => temasManuais[(hashManual(`${manual?.menu || ''}${manual?.area || ''}${manual?.titulo || ''}`) + index) % temasManuais.length];
  const classeMosaicoManual = (manual, index = 0) => {
    const titulo = String(manual?.titulo || '');
    const area = normalizarTextoSugestoes(`${manual?.menu || ''} ${manual?.area || ''} ${titulo}`);
    if (index === 0) return 'col-span-4 md:col-span-4 xl:col-span-5 row-span-4';
    if (/visao geral|dashboard/.test(area)) return 'col-span-4 md:col-span-4 xl:col-span-4 row-span-3';
    if (/integracao|configuracao|configura/.test(area)) return 'col-span-3 md:col-span-3 xl:col-span-3 row-span-2';
    if (/conferencia|resumo|detalhamento|consulta/.test(area)) return 'col-span-3 md:col-span-4 xl:col-span-3 row-span-3';
    if (titulo.length > 64) return 'col-span-4 md:col-span-4 xl:col-span-4 row-span-3';
    const padroes = ['col-span-3 md:col-span-3 xl:col-span-3 row-span-2','col-span-4 md:col-span-4 xl:col-span-3 row-span-3','col-span-3 md:col-span-3 xl:col-span-4 row-span-2','col-span-2 md:col-span-2 xl:col-span-2 row-span-2'];
    return padroes[index % padroes.length];
  };

  const corMarcacaoManual = (cor) => cor === 'verde' ? 'bg-emerald-200/70 dark:bg-emerald-700/60' : cor === 'azul' ? 'bg-blue-200/70 dark:bg-blue-700/60' : cor === 'rosa' ? 'bg-rose-200/70 dark:bg-rose-700/60' : 'bg-amber-200/80 dark:bg-amber-600/70';
  const destacarLinhaManual = (linha, marcacoes = []) => {
    let partes = [{ texto: String(linha || ''), cor: null }];
    (marcacoes || []).filter(m => String(m.texto || '').trim()).forEach(m => {
      const alvo = String(m.texto || '').trim();
      const novas = [];
      partes.forEach(parte => {
        if (parte.cor) { novas.push(parte); return; }
        const base = parte.texto || '';
        const idx = base.toLowerCase().indexOf(alvo.toLowerCase());
        if (idx < 0) { novas.push(parte); return; }
        if (idx > 0) novas.push({ texto: base.slice(0, idx), cor: null });
        novas.push({ texto: base.slice(idx, idx + alvo.length), cor: m.cor || 'amarelo' });
        if (idx + alvo.length < base.length) novas.push({ texto: base.slice(idx + alvo.length), cor: null });
      });
      partes = novas;
    });
    return partes.map((parte, i) => parte.cor ? <mark key={i} className={`rounded px-1 ${corMarcacaoManual(parte.cor)}`}>{parte.texto}</mark> : <React.Fragment key={i}>{parte.texto}</React.Fragment>);
  };
  const renderizarManualComMarcacoes = () => {
    const texto = String(manualDriveAberto?.conteudo || '').trim();
    if (!texto) return <div className="p-8 text-sm text-slate-500">Carregando texto para marcações. Se o texto não carregar, use a visualização do Google Docs.</div>;
    const linhas = texto.split(/\r?\n+/);
    return <div className="max-w-4xl mx-auto p-8 bg-white dark:bg-slate-950 min-h-full text-slate-900 dark:text-slate-100"><h2 className="text-3xl font-black text-cyan-700 dark:text-cyan-300 mb-4">{manualDriveAberto?.titulo}</h2>{linhas.map((linha, idx) => { const l = linha.trim(); if (!l) return null; const isTitulo = /^\d+\./.test(l); return <p key={idx} className={`${isTitulo ? 'mt-7 mb-3 text-xl font-black text-slate-900 dark:text-white' : 'my-2 text-sm leading-7'}`}>{destacarLinhaManual(l, manualAnotacoes.marcacoes || [])}</p>; })}</div>;
  };

  const faqsMaisAcessadas = useMemo(() => {
    const limite = Date.now() - (10 * 24 * 60 * 60 * 1000);
    const contagem = new Map();
    acessosFaq.forEach(item => {
      const data = Date.parse(item.acessadoEm || '');
      if (!item.faqId || !Number.isFinite(data) || data < limite) return;
      contagem.set(item.faqId, (contagem.get(item.faqId) || 0) + 1);
    });
    return faqs.map(faq => ({ ...faq, acessos10Dias: contagem.get(faq.id) || 0 }))
      .filter(item => item.acessos10Dias > 0)
      .sort((a, b) => b.acessos10Dias - a.acessos10Dias)
      .slice(0, 8);
  }, [faqs, acessosFaq]);

  const registrarAcessoFaq = async (faq) => {
    if (!faq?.id) return;
    setFaqAberta(faq);
    const registro = {
      tipo: 'faq_acesso', faqId: faq.id, faqPergunta: faq.pergunta || '', tela: faq.tela || '',
      usuarioEmail: emailUsuario, usuarioNome: obterNomeUsuarioSugestoes(user), acessadoEm: new Date().toISOString()
    };
    const id = gerarIdSugestoes('acesso');
    setCfgDocs(prev => [...prev, { id, ...registro }]);
    try { await setDoc(doc(db, caminhoSugestoesPublico(SUGESTOES_MELHORIAS_COLECOES.CFG), id), registro); }
    catch (error) { console.warn('[SUGESTOES] Falha ao registrar acesso FAQ:', error); }
  };

  const sugestoesFiltradas = useMemo(() => {
    const verificar = (valor, filtro) => !filtro || normalizarTextoSugestoes(valor).includes(normalizarTextoSugestoes(filtro));
    const filtradas = sugestoes.filter(item => {
      const status = item.status || 'Enviado';
      const statusEncerrado = ['Concluído', 'Não será feito'].includes(status);
      const atendeConclusao = filtros.conclusao === 'todas'
        || (filtros.conclusao === 'concluidas' ? statusEncerrado : !statusEncerrado);
      const atendeStatus = !Array.isArray(filtros.status) || filtros.status.length === 0 || filtros.status.includes(status);
      const livre = [item.tituloCurto, item.titulo, item.usuarioNome, item.usuarioEmail, item.tela, item.criadoEm, item.status, item.prioridade, item.responsavelAnaliseNome, item.responsavelAnaliseEmail, item.responsavelDesenvolvimentoNome, item.responsavelDesenvolvimentoEmail, item.tipo, item.descricao, item.codigo].join(' ');
      return atendeConclusao
        && atendeStatus
        && verificar(`${item.usuarioNome} ${item.usuarioEmail}`, filtros.usuario)
        && verificar(item.tela, filtros.tela)
        && verificar(formatarDataHoraSugestoes(item.criadoEm), filtros.data)
        && verificar(item.prioridade, filtros.prioridade)
        && verificar(`${item.responsavelAnaliseNome || ''} ${item.responsavelAnaliseEmail || ''}`, filtros.analista)
        && verificar(`${item.responsavelDesenvolvimentoNome || ''} ${item.responsavelDesenvolvimentoEmail || ''}`, filtros.desenvolvedor)
        && verificar(livre, filtros.livre);
    });
    if (!ordenacaoSugestoes.campo || !ordenacaoSugestoes.direcao) return filtradas;
    const obterValor = (item) => {
      const campo = ordenacaoSugestoes.campo;
      if (campo === 'data') return Date.parse(item.criadoEm || '') || 0;
      if (campo === 'anexos') return Number(item.anexos?.length || 0);
      if (campo === 'titulo') return item.tituloCurto || item.titulo || '';
      if (campo === 'analista') return item.responsavelAnaliseNome || item.responsavelAnaliseEmail || '';
      if (campo === 'desenvolvedor') return item.responsavelDesenvolvimentoNome || item.responsavelDesenvolvimentoEmail || '';
      if (campo === 'prazo') return item.prazoConclusaoEtapa || item.prazoConclusao || '';
      return item[campo] || '';
    };
    return [...filtradas].sort((a, b) => {
      const va = obterValor(a); const vb = obterValor(b);
      const comparacao = typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb), 'pt-BR', { numeric: true, sensitivity: 'base' });
      return ordenacaoSugestoes.direcao === 'asc' ? comparacao : -comparacao;
    });
  }, [sugestoes, filtros, ordenacaoSugestoes]);

  const alternarOrdenacaoSugestoes = (campo) => setOrdenacaoSugestoes(atual => {
    if (atual.campo !== campo) return { campo, direcao: 'asc' };
    if (atual.direcao === 'asc') return { campo, direcao: 'desc' };
    return { campo: '', direcao: '' };
  });
  const cabecalhoOrdenavelSugestoes = (campo, rotulo, classe = '') => {
    const ativo = ordenacaoSugestoes.campo === campo;
    const indicador = ativo ? (ordenacaoSugestoes.direcao === 'asc' ? ' ↑' : ' ↓') : ' ↕';
    return <th className={`p-2.5 text-left ${classe}`}><button type="button" onClick={() => alternarOrdenacaoSugestoes(campo)} className="w-full text-left font-black hover:text-cyan-700 dark:hover:text-cyan-300" title="Clique: A-Z; segundo clique: Z-A; terceiro clique: ordem original">{rotulo}{indicador}</button></th>;
  };

  const alternarFiltroStatus = (status) => setFiltros(prev => ({
    ...prev,
    status: (prev.status || []).includes(status)
      ? (prev.status || []).filter(item => item !== status)
      : [...(prev.status || []), status]
  }));

  const usuarioEhAutor = (item) => normalizarEmailCentralAjuda(item?.usuarioEmail || '') === emailUsuario;
  const podeEditarOuExcluir = (item) => usuarioEhAutor(item) && (item?.status || 'Enviado') === 'Enviado';

  const obterAdminsSugestoes = () => Array.from(new Set([
    ...(config.superAdminEmails || []),
    ...(config.usuariosResponsaveis || []).map(u => u?.email)
  ].map(normalizarEmailCentralAjuda).filter(Boolean)));
  const usuarioEhAdminSugestoes = (email = emailUsuario) => isSuperAdm || obterAdminsSugestoes().includes(normalizarEmailCentralAjuda(email));
  const criarNotificacaoSugestao = ({ tipo, destino = 'admins', titulo, mensagem, criadoEm = new Date().toISOString(), autorEmail = emailUsuario, autorNome = obterNomeUsuarioSugestoes(user) }) => ({
    id: gerarIdSugestoes('notif'),
    tipo,
    destino,
    titulo,
    mensagem,
    criadoEm,
    autorEmail: normalizarEmailCentralAjuda(autorEmail),
    autorNome,
    lidaPor: []
  });
  const notificacaoVisivelParaUsuario = (sugestao, notificacao) => {
    if (!notificacao) return false;
    const destino = String(notificacao.destino || '').toLowerCase();
    const lidaPor = (notificacao.lidaPor || []).map(normalizarEmailCentralAjuda);
    if (notificacao.tipo === 'nova_sugestao_admin' && destino === 'admins') {
      // Notificação de sugestão nova para administradores só sai quando o primeiro status for alterado.
      return usuarioEhAdminSugestoes() && (sugestao?.status || 'Enviado') === 'Enviado';
    }
    if (lidaPor.includes(emailUsuario)) return false;
    if (destino === 'admins') return usuarioEhAdminSugestoes();
    if (destino === 'solicitante') return usuarioEhAutor(sugestao);
    if (notificacao.destinatarioEmail) return normalizarEmailCentralAjuda(notificacao.destinatarioEmail) === emailUsuario;
    return false;
  };
  const sugestoesComNotificacoes = useMemo(() => {
    return (sugestoes || []).flatMap(sugestao => (sugestao.notificacoes || [])
      .filter(notif => notificacaoVisivelParaUsuario(sugestao, notif))
      .map(notif => ({ ...notif, sugestao }))
    ).sort((a, b) => Date.parse(b.criadoEm || '') - Date.parse(a.criadoEm || ''));
  }, [sugestoes, emailUsuario, isSuperAdm, config]);
  useEffect(() => {
    if (typeof onNotificacoesSugestoesChange === 'function') {
      onNotificacoesSugestoesChange(sugestoesComNotificacoes.length || 0);
    }
  }, [sugestoesComNotificacoes.length, onNotificacoesSugestoesChange]);

  useEffect(() => {
    if (!aberto || !focoNotificacoesSugestoes) return;
    setAba('sugestoes');
    setFiltros(prev => ({ ...prev, conclusao: 'pendentes' }));
  }, [aberto, focoNotificacoesSugestoes]);

  const usuarioTemNotificacaoNoItem = (item) => (item?.notificacoes || []).some(notif => notificacaoVisivelParaUsuario(item, notif));
  const marcarNotificacaoComoVisualizada = async (sugestao, notificacao) => {
    if (!sugestao?.id || !notificacao?.id) return sugestao;
    if (notificacao.tipo === 'nova_sugestao_admin' && (sugestao.status || 'Enviado') === 'Enviado') return sugestao;
    const atualizada = {
      ...sugestao,
      notificacoes: (sugestao.notificacoes || []).map(n => n.id === notificacao.id
        ? { ...n, lidaPor: Array.from(new Set([...(n.lidaPor || []).map(normalizarEmailCentralAjuda), emailUsuario])) }
        : n)
    };
    try {
      await setDoc(doc(db, caminhoSugestoesPublico(SUGESTOES_MELHORIAS_COLECOES.SUGESTOES), sugestao.id), atualizada, { merge: true });
      setSugestoes(prev => prev.map(s => s.id === sugestao.id ? atualizada : s));
    } catch (error) { console.warn('[SUGESTOES] Falha ao marcar notificação como visualizada:', error); }
    return atualizada;
  };
  const abrirNotificacaoSugestao = async (registroNotif) => {
    const item = registroNotif?.sugestao;
    if (!item?.id) return;
    const atualizado = await marcarNotificacaoComoVisualizada(item, registroNotif);
    setAba('sugestoes');
    setFiltros(prev => ({ ...prev, livre: item.codigo || item.id, status: [], conclusao: 'todas' }));
    if (usuarioEhAdminSugestoes()) abrirAdministracao(atualizado);
    else setPopupHistorico(atualizado);
  };

  const sincronizarDescricaoSugestao = () => {
    const html = sanitizarHtmlSugestoes(editorSugestaoRef.current?.innerHTML || formSugestao.descricaoHtml || '');
    const texto = htmlParaTextoSugestoes(html);
    setFormSugestao(prev => ({ ...prev, descricaoHtml: html, descricao: texto }));
    return { html, texto };
  };
  const comandoEditorSugestao = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorSugestaoRef.current?.focus();
    sincronizarDescricaoSugestao();
  };

  const abrirNovaSugestao = () => {
    setSugestaoEmEdicao(null);
    setFormSugestao({ tipo: 'Sugestão', tituloCurto: '', tela: telaAtual || '', descricao: '', descricaoHtml: '' });
    setArquivos([]);
    setPopupNovaSugestao(true);
    setTimeout(() => { if (editorSugestaoRef.current) editorSugestaoRef.current.innerHTML = ''; }, 60);
  };

  const abrirEdicaoSugestao = (item) => {
    if (!podeEditarOuExcluir(item)) return;
    const html = sanitizarHtmlSugestoes(item.descricaoHtml || (item.descricao ? String(item.descricao).replace(/\n/g, '<br/>') : ''));
    setSugestaoEmEdicao(item);
    setFormSugestao({ tipo: item.tipo || 'Sugestão', tituloCurto: item.tituloCurto || item.titulo || '', tela: item.tela || '', descricao: item.descricao || htmlParaTextoSugestoes(html), descricaoHtml: html });
    setArquivos([]);
    setPopupNovaSugestao(true);
    setTimeout(() => { if (editorSugestaoRef.current) editorSugestaoRef.current.innerHTML = html; }, 60);
  };

  const excluirSugestaoUsuario = async (item) => {
    if (!podeEditarOuExcluir(item) || !window.confirm('Excluir definitivamente esta sugestão ainda não analisada?')) return;
    setSalvando(true); setMensagem(null);
    try {
      await deleteDoc(doc(db, caminhoSugestoesPublico(SUGESTOES_MELHORIAS_COLECOES.SUGESTOES), item.id));
      setSugestoes(prev => prev.filter(s => s.id !== item.id));
      setMensagem({ tipo: 'sucesso', texto: 'Sugestão excluída.' });
    } catch (error) {
      setMensagem({ tipo: 'erro', texto: `Erro ao excluir: ${error?.message || 'falha de conexão.'}` });
    } finally { setSalvando(false); }
  };

  const excluirSugestaoAdmin = async (item = sugestaoAdmin) => {
    if (!isSuperAdm || !item?.id || !window.confirm('Excluir definitivamente esta sugestão? Esta ação não poderá ser desfeita.')) return;
    setSalvando(true); setMensagem(null);
    try {
      await deleteDoc(doc(db, caminhoSugestoesPublico(SUGESTOES_MELHORIAS_COLECOES.SUGESTOES), item.id));
      setSugestoes(prev => prev.filter(s => s.id !== item.id));
      setSugestaoAdmin(null);
      setMensagem({ tipo: 'sucesso', texto: 'Sugestão excluída pelo administrador.' });
    } catch (error) {
      setMensagem({ tipo: 'erro', texto: `Erro ao excluir sugestão: ${error?.message || 'falha de conexão.'}` });
    } finally { setSalvando(false); }
  };

  const concluirComoNaoRealizada = async (item = sugestaoAdmin) => {
    if (!isSuperAdm || !item?.id) return;
    if (!window.confirm('Concluir esta solicitação como “Não será feito”?')) return;
    setSalvando(true); setMensagem(null);
    try {
      const agora = new Date().toISOString();
      const anterior = sugestoes.find(s => s.id === item.id) || item;
      const alterado = {
        ...anterior,
        ...item,
        status: 'Não será feito',
        atualizadoEm: agora,
        encerradoEm: agora,
        encerradoPor: emailUsuario,
        historico: [...(anterior.historico || []), {
          data: agora,
          usuarioNome: obterNomeUsuarioSugestoes(user),
          usuarioEmail: emailUsuario,
          acao: `Solicitação concluída como não realizada${item.observacaoAdmin ? `: ${item.observacaoAdmin}` : ''}`,
          status: 'Não será feito',
          observacao: item.observacaoAdmin || ''
        }],
        notificacoes: [
          ...(anterior.notificacoes || []),
          criarNotificacaoSugestao({ tipo: 'status_solicitante', destino: 'solicitante', titulo: 'Sugestão concluída como não realizada', mensagem: `Sua sugestão ${item.codigo || item.id} foi concluída como Não será feito.`, criadoEm: agora })
        ]
      };
      await setDoc(doc(db, caminhoSugestoesPublico(SUGESTOES_MELHORIAS_COLECOES.SUGESTOES), item.id), alterado, { merge: true });
      setSugestoes(prev => prev.map(s => s.id === item.id ? alterado : s));
      setSugestaoAdmin(null);
      setMensagem({ tipo: 'sucesso', texto: 'Solicitação concluída como não realizada.' });
    } catch (error) {
      setMensagem({ tipo: 'erro', texto: `Erro ao concluir solicitação: ${error?.message || 'falha de conexão.'}` });
    } finally { setSalvando(false); }
  };

  const salvarComplementoUsuario = async () => {
    const item = popupComplemento;
    if (!item?.id || !usuarioEhAutor(item) || !textoComplemento.trim()) return;
    setSalvando(true); setMensagem(null);
    try {
      const agora = new Date().toISOString();
      const anexosNovos = await enviarAnexosDrive(item.id);
      const historico = [...(item.historico || []), {
        data: agora,
        usuarioNome: obterNomeUsuarioSugestoes(user),
        usuarioEmail: emailUsuario,
        acao: 'Complemento/correção enviado pelo solicitante',
        observacao: textoComplemento.trim(),
        status: item.status || 'Enviado'
      }];
      const deveNotificarAdmins = (item.status || 'Enviado') !== 'Enviado';
      const alterado = {
        ...item,
        anexos: [...(item.anexos || []), ...anexosNovos],
        historico,
        atualizadoEm: agora,
        solicitantePediuDesconsideracao: /desconsider|cancel|não considerar|nao considerar/i.test(textoComplemento),
        ultimoComplementoSolicitante: textoComplemento.trim(),
        notificacoes: [
          ...(item.notificacoes || []),
          ...(deveNotificarAdmins ? [criarNotificacaoSugestao({ tipo: 'comentario_solicitante_admin', destino: 'admins', titulo: 'Comentário em sugestão', mensagem: `${obterNomeUsuarioSugestoes(user)} adicionou comentário/anexo na sugestão ${item.codigo || item.id}.`, criadoEm: agora })] : [])
        ]
      };
      await setDoc(doc(db, caminhoSugestoesPublico(SUGESTOES_MELHORIAS_COLECOES.SUGESTOES), item.id), alterado, { merge: true });
      setSugestoes(prev => prev.map(s => s.id === item.id ? alterado : s));
      setPopupComplemento(null); setTextoComplemento(''); setArquivos([]);
      setMensagem({ tipo: 'sucesso', texto: 'Informação enviada aos administradores.' });
    } catch (error) {
      setMensagem({ tipo: 'erro', texto: `Erro ao enviar informação: ${error?.message || 'falha de conexão.'}` });
    } finally { setSalvando(false); }
  };

  const lerArquivoBase64 = (arquivo) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').split(',').pop() || '');
    reader.onerror = () => reject(reader.error || new Error('Falha ao ler anexo.'));
    reader.readAsDataURL(arquivo);
  });

  const enviarAnexosDrive = async (sugestaoId) => {
    if (!arquivos.length) return [];
    if (!config.appsScriptUrl) throw new Error('A URL do Apps Script para anexos ainda não foi configurada pelo super administrador.');
    const resultado = [];
    for (const arquivo of arquivos) {
      const base64 = await lerArquivoBase64(arquivo);
      const payloadUpload = {
        acao: 'salvarAnexoSugestao',
        token: config.uploadToken || '',
        folderId: config.driveFolderId,
        sugestaoId,
        nomeArquivo: arquivo.name,
        mimeType: arquivo.type || 'application/octet-stream',
        base64
      };

      let resposta;
      let json;
      let erroDireto = null;

      // A chamada direta ao Web App do Apps Script pode ser bloqueada pelo navegador
      // por CORS/redirecionamento de autenticação. Primeiro tentamos diretamente e,
      // em caso de falha de rede, usamos o proxy já adotado pelos demais módulos.
      try {
        resposta = await fetch(config.appsScriptUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify(payloadUpload)
        });

        const textoResposta = await resposta.text();
        try {
          json = JSON.parse(textoResposta || '{}');
        } catch {
          throw new Error(`O Apps Script retornou uma resposta inválida (HTTP ${resposta.status}). Verifique se a URL termina em /exec e se a implantação está ativa.`);
        }

        if (!resposta.ok) {
          throw new Error(json?.erro || `Falha HTTP ${resposta.status} ao enviar ${arquivo.name}.`);
        }
      } catch (erro) {
        erroDireto = erro;
        console.warn('[SUGESTOES] Upload direto falhou. Tentando via proxy:', erro);

        const respostaProxy = await fetch(PLANILHA_PROXY_URL_AJUDA, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            targetUrl: config.appsScriptUrl,
            method: 'POST',
            headers: { 'Content-Type': 'text/plain;charset=utf-8' },
            body: JSON.stringify(payloadUpload)
          })
        });

        const textoProxy = await respostaProxy.text();
        let retornoProxy;
        try {
          retornoProxy = JSON.parse(textoProxy || '{}');
        } catch {
          throw new Error(`Falha no proxy de upload. Resposta inválida (HTTP ${respostaProxy.status}). Erro direto: ${erroDireto?.message || 'Failed to fetch'}`);
        }

        if (!respostaProxy.ok || retornoProxy?.ok === false) {
          throw new Error(retornoProxy?.erro || retornoProxy?.message || retornoProxy?.error || `O proxy não conseguiu acessar o Apps Script (HTTP ${respostaProxy.status}).`);
        }

        // O proxy pode devolver o JSON do Apps Script diretamente ou dentro de resultado/body/data.
        json = retornoProxy?.resultado ?? retornoProxy?.body ?? retornoProxy?.data ?? retornoProxy;
        if (typeof json === 'string') {
          try { json = JSON.parse(json); } catch { json = { ok: false, erro: json }; }
        }
      }

      if (!json?.ok) throw new Error(json?.erro || `Falha ao enviar ${arquivo.name}.`);
      resultado.push({ nome: arquivo.name, url: json.url, fileId: json.fileId, mimeType: arquivo.type || '' });
    }
    return resultado;
  };

  const salvarNovaSugestao = async () => {
    const descricaoAtual = sincronizarDescricaoSugestao();
    if (!formSugestao.tituloCurto.trim()) { setMensagem({ tipo: 'erro', texto: 'Informe um título curto para a sugestão.' }); return; }
    if (!formSugestao.tela.trim()) { setMensagem({ tipo: 'erro', texto: 'Informe a tela do sistema.' }); return; }
    if (!descricaoAtual.texto.trim()) { setMensagem({ tipo: 'erro', texto: 'Descreva a sugestão ou correção.' }); return; }
    setSalvando(true); setMensagem(null);
    const id = sugestaoEmEdicao?.id || gerarIdSugestoes('sugestao');
    const agora = new Date().toISOString();
    try {
      const anexosNovos = await enviarAnexosDrive(id);
      if (sugestaoEmEdicao) {
        if (!podeEditarOuExcluir(sugestaoEmEdicao)) throw new Error('Esta sugestão já foi analisada e não pode mais ser editada diretamente. Use Complementar/corrigir.');
        const registro = {
          ...sugestaoEmEdicao,
          tipo: formSugestao.tipo,
          tituloCurto: formSugestao.tituloCurto.trim(),
          tela: formSugestao.tela.trim(),
          descricao: descricaoAtual.texto.trim(),
          descricaoHtml: descricaoAtual.html,
          anexos: [...(sugestaoEmEdicao.anexos || []), ...anexosNovos],
          atualizadoEm: agora,
          historico: [...(sugestaoEmEdicao.historico || []), { data: agora, usuarioNome: obterNomeUsuarioSugestoes(user), usuarioEmail: emailUsuario, acao: 'Sugestão editada pelo solicitante', status: sugestaoEmEdicao.status || 'Enviado' }]
        };
        await setDoc(doc(db, caminhoSugestoesPublico(SUGESTOES_MELHORIAS_COLECOES.SUGESTOES), id), registro, { merge: true });
        setSugestoes(prev => prev.map(item => item.id === id ? registro : item));
        setMensagem({ tipo: 'sucesso', texto: 'Sugestão atualizada com sucesso.' });
      } else {
        const registro = {
          codigo: id, tipo: formSugestao.tipo, tituloCurto: formSugestao.tituloCurto.trim(), tela: formSugestao.tela.trim(), descricao: descricaoAtual.texto.trim(), descricaoHtml: descricaoAtual.html,
          usuarioId: user?.id || '', usuarioNome: obterNomeUsuarioSugestoes(user), usuarioEmail: emailUsuario,
          criadoEm: agora, atualizadoEm: agora, status: 'Enviado', prioridade: 'Não definida',
          responsavelAnaliseNome: '', responsavelAnaliseEmail: '', responsavelDesenvolvimentoNome: '', responsavelDesenvolvimentoEmail: '',
          anexos: anexosNovos,
          notificacoes: [criarNotificacaoSugestao({ tipo: 'nova_sugestao_admin', destino: 'admins', titulo: 'Nova sugestão enviada', mensagem: `${obterNomeUsuarioSugestoes(user)} enviou a sugestão “${formSugestao.tituloCurto.trim()}” em ${formSugestao.tela.trim()}.`, criadoEm: agora })],
          historico: [{ data: agora, usuarioNome: obterNomeUsuarioSugestoes(user), usuarioEmail: emailUsuario, acao: 'Sugestão enviada', status: 'Enviado' }]
        };
        await setDoc(doc(db, caminhoSugestoesPublico(SUGESTOES_MELHORIAS_COLECOES.SUGESTOES), id), registro);
        setSugestoes(prev => [{ id, ...registro }, ...prev]);
        setMensagem({ tipo: 'sucesso', texto: 'Sugestão enviada com sucesso.' });
      }
      setPopupNovaSugestao(false); setSugestaoEmEdicao(null); setArquivos([]);
      setFormSugestao({ tipo: 'Sugestão', tituloCurto: '', tela: telaAtual || '', descricao: '', descricaoHtml: '' });
    } catch (error) {
      console.error('[SUGESTOES] Erro ao salvar:', error);
      setMensagem({ tipo: 'erro', texto: `Não foi possível salvar: ${error?.message || 'falha inesperada.'}` });
    } finally { setSalvando(false); }
  };

  const abrirAdministracao = async (item) => {
    let atual = { ...item };
    if (isSuperAdm && item.status === 'Enviado') {
      const agora = new Date().toISOString();
      atual = {
        ...item, status: 'Em Análise', atualizadoEm: agora,
        responsavelAnaliseNome: item.responsavelAnaliseNome || obterNomeUsuarioSugestoes(user),
        responsavelAnaliseEmail: item.responsavelAnaliseEmail || emailUsuario,
        historico: [...(item.historico || []), { data: agora, usuarioNome: obterNomeUsuarioSugestoes(user), usuarioEmail: emailUsuario, acao: 'Sugestão aberta para análise', status: 'Em Análise' }],
        notificacoes: [
          ...(item.notificacoes || []),
          criarNotificacaoSugestao({ tipo: 'status_solicitante', destino: 'solicitante', titulo: 'Status da sugestão alterado', mensagem: `Sua sugestão ${item.codigo || item.id} mudou de Enviado para Em Análise.`, criadoEm: agora })
        ]
      };
      try {
        await setDoc(doc(db, caminhoSugestoesPublico(SUGESTOES_MELHORIAS_COLECOES.SUGESTOES), item.id), atual, { merge: true });
        setSugestoes(prev => prev.map(s => s.id === item.id ? atual : s));
      } catch (error) { console.warn('[SUGESTOES] Falha ao marcar em análise:', error); }
    }
    setSugestaoAdmin(atual);
  };

  const salvarAdministracao = async () => {
    if (!isSuperAdm || !sugestaoAdmin?.id) return;
    setSalvando(true); setMensagem(null);
    try {
      const anterior = sugestoes.find(s => s.id === sugestaoAdmin.id) || {};
      const agora = new Date().toISOString();
      const responsavelAnalise = (config.usuariosResponsaveis || []).find(u => normalizarEmailCentralAjuda(u.email) === normalizarEmailCentralAjuda(sugestaoAdmin.responsavelAnaliseEmail));
      const responsavelDev = (config.usuariosResponsaveis || []).find(u => normalizarEmailCentralAjuda(u.email) === normalizarEmailCentralAjuda(sugestaoAdmin.responsavelDesenvolvimentoEmail));
      const statusMudou = sugestaoAdmin.status !== anterior.status;
      const alterado = {
        ...sugestaoAdmin,
        responsavelAnaliseNome: responsavelAnalise?.nome || sugestaoAdmin.responsavelAnaliseNome || '',
        responsavelDesenvolvimentoNome: responsavelDev?.nome || sugestaoAdmin.responsavelDesenvolvimentoNome || '',
        atualizadoEm: agora,
        historico: [...(anterior.historico || []), {
          data: agora, usuarioNome: obterNomeUsuarioSugestoes(user), usuarioEmail: emailUsuario,
          acao: statusMudou ? `Status alterado de ${anterior.status || '-'} para ${sugestaoAdmin.status}` : 'Dados administrativos atualizados',
          status: sugestaoAdmin.status, prioridade: sugestaoAdmin.prioridade || 'Não definida', observacao: sugestaoAdmin.observacaoAdmin || ''
        }],
        notificacoes: [
          ...(anterior.notificacoes || []),
          ...(statusMudou ? [criarNotificacaoSugestao({ tipo: 'status_solicitante', destino: 'solicitante', titulo: 'Status da sugestão alterado', mensagem: `Sua sugestão ${sugestaoAdmin.codigo || sugestaoAdmin.id} mudou de ${anterior.status || '-'} para ${sugestaoAdmin.status}.`, criadoEm: agora })] : [])
        ]
      };
      await setDoc(doc(db, caminhoSugestoesPublico(SUGESTOES_MELHORIAS_COLECOES.SUGESTOES), sugestaoAdmin.id), alterado, { merge: true });
      setSugestoes(prev => prev.map(s => s.id === alterado.id ? alterado : s));
      setSugestaoAdmin(null);
      setMensagem({ tipo: 'sucesso', texto: 'Sugestão atualizada com sucesso.' });
    } catch (error) { setMensagem({ tipo: 'erro', texto: `Erro ao atualizar: ${error?.message || 'falha de conexão.'}` }); }
    finally { setSalvando(false); }
  };

  const salvarConfigGeral = async () => {
    if (!isSuperAdm) return;
    setSalvando(true); setMensagem(null);
    try {
      const payload = {
        ...CONFIG_SUGESTOES_PADRAO,
        ...config,
        id: 'config_geral',
        tipo: 'config_geral',
        superAdminEmails: Array.from(new Set((config.superAdminEmails || []).map(v => String(v || '').trim().toLowerCase()).filter(Boolean))),
        usuariosResponsaveis: (config.usuariosResponsaveis || []).filter(u => u?.nome || u?.email),
        atualizadoEm: new Date().toISOString(),
        atualizadoPor: emailUsuario
      };
      await setDoc(doc(db, caminhoSugestoesPublico(SUGESTOES_MELHORIAS_COLECOES.CFG), 'config_geral'), payload, { merge: true });
      setConfig(payload);
      salvarCacheConfigSugestoes(payload);
      setCfgDocs(prev => [{ id: 'config_geral', ...payload }, ...prev.filter(item => item.id !== 'config_geral' && item.tipo !== 'config_geral')]);
      setMensagem({ tipo: 'sucesso', texto: 'Configurações salvas e protegidas no banco e no cache local.' });
    } catch (error) { setMensagem({ tipo: 'erro', texto: `Erro ao salvar configuração: ${error?.message || 'falha de conexão.'}` }); }
    finally { setSalvando(false); }
  };

  const salvarFaq = async () => {
    if (!isSuperAdm || !faqForm.pergunta.trim() || !faqForm.resposta.trim()) return;
    setSalvando(true);
    try {
      const id = faqForm.id || gerarIdSugestoes('faq');
      const payload = { ...faqForm, id: undefined, tipo: 'faq', palavrasChave: String(faqForm.palavrasChaveTexto || '').split(/[,;\n]+/).map(v => v.trim()).filter(Boolean), atualizadoEm: new Date().toISOString(), atualizadoPor: emailUsuario }; delete payload.palavrasChaveTexto;
      await setDoc(doc(db, caminhoSugestoesPublico(SUGESTOES_MELHORIAS_COLECOES.CFG), id), payload, { merge: true });
      setCfgDocs(prev => [{ id, ...payload }, ...prev.filter(item => item.id !== id)]);
      setFaqForm({ id: '', pergunta: '', resposta: '', tela: '', categoria: '', palavrasChaveTexto: '', ordem: 0, ativo: true });
    } catch (error) { setMensagem({ tipo: 'erro', texto: `Erro ao salvar FAQ: ${error?.message || 'falha.'}` }); }
    finally { setSalvando(false); }
  };

  const excluirFaq = async (faq) => {
    if (!isSuperAdm || !faq?.id || !window.confirm('Excluir esta FAQ?')) return;
    try {
      await deleteDoc(doc(db, caminhoSugestoesPublico(SUGESTOES_MELHORIAS_COLECOES.CFG), faq.id));
      setCfgDocs(prev => prev.filter(item => item.id !== faq.id));
    } catch (error) { setMensagem({ tipo: 'erro', texto: `Erro ao excluir FAQ: ${error?.message || 'falha.'}` }); }
  };

  const adicionarResponsavel = () => setConfig(prev => ({ ...prev, usuariosResponsaveis: [...(prev.usuariosResponsaveis || []), { nome: '', email: '' }] }));
  const atualizarResponsavel = (index, campo, valor) => setConfig(prev => ({ ...prev, usuariosResponsaveis: (prev.usuariosResponsaveis || []).map((u, i) => i === index ? { ...u, [campo]: valor } : u) }));
  const removerResponsavel = (index) => setConfig(prev => ({ ...prev, usuariosResponsaveis: (prev.usuariosResponsaveis || []).filter((_, i) => i !== index) }));

  if (!aberto && !manualDriveAberto) return null;
  const modalClass = isDarkMode ? 'bg-slate-950 border-slate-700 text-slate-100' : 'bg-white border-slate-200 text-slate-900';
  const inputClass = `w-full rounded-lg border px-3 py-2 text-sm outline-none ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white focus:border-cyan-500' : 'bg-white border-slate-300 text-slate-900 focus:border-cyan-600'}`;
  const cardClass = isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200';
  const iniciarArrasteCentralAjuda = (event) => {
    if (event.button !== 0) return;
    if (event.target?.closest?.('button,a,input,textarea,select,iframe')) return;
    const inicio = { mouseX: event.clientX, mouseY: event.clientY, x: posicaoCentralAjuda.x || 0, y: posicaoCentralAjuda.y || 0 };
    dragCentralRef.current = inicio;
    const mover = (ev) => setPosicaoCentralAjuda({ x: inicio.x + ev.clientX - inicio.mouseX, y: inicio.y + ev.clientY - inicio.mouseY });
    const soltar = () => { dragCentralRef.current = null; document.removeEventListener('mousemove', mover); document.removeEventListener('mouseup', soltar); try { localStorage.setItem('facil_central_ajuda_posicao_v1', JSON.stringify(posicaoCentralAjuda)); } catch {} };
    document.addEventListener('mousemove', mover);
    document.addEventListener('mouseup', soltar);
  };
  const iniciarArrasteManualAberto = (event) => {
    if (event.button !== 0) return;
    if (event.target?.closest?.('button,a,input,textarea,select,iframe')) return;
    const inicio = { mouseX: event.clientX, mouseY: event.clientY, x: posicaoManualAberto.x || 0, y: posicaoManualAberto.y || 0 };
    dragManualRef.current = inicio;
    const mover = (ev) => setPosicaoManualAberto({ x: inicio.x + ev.clientX - inicio.mouseX, y: inicio.y + ev.clientY - inicio.mouseY });
    const soltar = () => { dragManualRef.current = null; document.removeEventListener('mousemove', mover); document.removeEventListener('mouseup', soltar); try { localStorage.setItem('facil_manual_aberto_posicao_v1', JSON.stringify(posicaoManualAberto)); } catch {} };
    document.addEventListener('mousemove', mover);
    document.addEventListener('mouseup', soltar);
  };
  const resetarPosicaoCentralAjuda = () => { const zero = { x: 0, y: 0 }; setPosicaoCentralAjuda(zero); try { localStorage.setItem('facil_central_ajuda_posicao_v1', JSON.stringify(zero)); } catch {} };
  const resetarPosicaoManualAberto = () => { const zero = { x: 0, y: 0 }; setPosicaoManualAberto(zero); try { localStorage.setItem('facil_manual_aberto_posicao_v1', JSON.stringify(zero)); } catch {} };

  return <div className={modoAba ? "flex-1 min-h-0 overflow-hidden p-3 md:p-4" : "fixed inset-0 z-[99990] pointer-events-none flex items-center justify-center p-3 md:p-6"}>
    <div style={modoAba ? undefined : { transform: `translate(${posicaoCentralAjuda.x || 0}px, ${posicaoCentralAjuda.y || 0}px)` }} className={`central-ajuda-root ${!isDarkMode ? 'central-ajuda-light' : 'central-ajuda-dark'} ${!aberto ? 'hidden' : modoAba ? 'flex' : 'pointer-events-auto flex'} ${modoAba ? 'w-full h-full rounded-xl shadow-sm' : 'w-[min(1500px,98vw)] h-[min(900px,94vh)] rounded-2xl shadow-2xl'} border flex-col overflow-hidden text-[14px] ${modalClass}`}>
      <div onMouseDown={modoAba ? undefined : iniciarArrasteCentralAjuda} className={`px-5 py-4 border-b flex items-center gap-3 ${modoAba ? '' : 'cursor-move select-none'} ${isDarkMode ? 'border-slate-800 bg-slate-900/80' : 'border-slate-200 bg-slate-50'}`} title={modoAba ? "Central de ajuda aberta como aba" : "Clique e arraste para mover a janela de ajuda"}>
        <CircleHelp size={22} className="text-cyan-600"/>
        <div><h2 className="text-lg font-black">Ajuda e Melhorias</h2><p className="text-xs text-slate-500">Manual do sistema, sugestões e correções</p></div>
        <div className="ml-auto flex items-center gap-1 self-stretch">
          <button onClick={() => setAba('manual')} style={aba === 'manual' ? { color: '#ffffff', WebkitTextFillColor: '#ffffff' } : undefined} className={`h-9 px-3 rounded-lg font-black text-sm border inline-flex items-center gap-2 ${aba === 'manual' ? 'bg-cyan-700 border-cyan-700 !text-white' : isDarkMode ? 'border-slate-700 text-slate-200 hover:bg-slate-800' : 'bg-white border-cyan-300 text-cyan-700 hover:bg-cyan-50'}`}><BookOpen size={15}/>Manual Sistema</button>
          <button onClick={() => setAba('sugestoes')} className={`h-9 px-3 rounded-lg font-black text-sm border inline-flex items-center gap-2 ${aba === 'sugestoes' ? 'bg-violet-700 border-violet-700 text-white' : isDarkMode ? 'border-slate-700 text-slate-200 hover:bg-slate-800' : 'bg-white border-violet-300 text-violet-700 hover:bg-violet-50'}`}><Megaphone size={15}/>Sugestões</button>
        </div>
        <button onClick={carregarDados} className={`h-9 px-3 rounded-lg border inline-flex items-center gap-2 text-xs font-black ${isDarkMode ? 'border-slate-700 text-white hover:bg-slate-800' : 'bg-teal-700 border-teal-700 text-white hover:bg-teal-800'}`}><RefreshCw size={14}/>{carregando ? 'Atualizando...' : 'Atualizar'}</button>
        {!modoAba && <button type="button" onClick={resetarPosicaoCentralAjuda} className="h-9 px-3 rounded-lg border border-slate-300 dark:border-slate-700 inline-flex items-center gap-2 text-xs font-black" title="Centralizar janela">Centralizar</button>}<button type="button" onClick={onClose} className="h-9 w-9 rounded-lg border border-slate-300 dark:border-slate-700 inline-flex items-center justify-center"><X size={16}/></button>
      </div>
      {mensagem && <div className={`mx-5 mt-3 rounded-xl px-4 py-2 text-sm font-bold ${mensagem.tipo === 'erro' ? 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/40 dark:text-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200'}`}>{mensagem.texto}</div>}
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-5">
        {aba === 'manual' && <div className="space-y-4">
          <div className={`rounded-2xl border p-4 ${cardClass}`}>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-end">
              <label className="text-xs font-black uppercase text-slate-500">Pesquisar no manual
                <div className="relative mt-2"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/><input className={`${inputClass} pl-9`} value={pesquisaManual} onChange={e => setPesquisaManual(e.target.value)} placeholder="Digite menu, tela, botão, dúvida ou palavra-chave..."/></div>
              </label>
              <button type="button" onClick={() => carregarIndiceManuaisDrive({ limparCache: true })} className="h-10 px-4 rounded-lg bg-teal-700 hover:bg-teal-800 text-white font-black text-xs inline-flex items-center justify-center gap-2"><RefreshCw size={14}/>{carregandoManuais ? 'Atualizando...' : 'Atualizar manuais'}</button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 items-center"><span className="text-xs font-black uppercase text-slate-500">Módulo</span>{modulosManuaisDisponiveis.map(mod => { const ativoModulo = manualModuloFiltro === mod; return <button key={mod} type="button" onClick={() => setManualModuloFiltro(mod)} className={`px-3 py-1.5 rounded-full border text-xs font-black ${ativoModulo ? 'bg-teal-700 border-teal-700 text-white' : isDarkMode ? 'bg-slate-900 border-slate-700 text-teal-200' : 'bg-white border-teal-300 text-teal-700'}`} style={ativoModulo ? { backgroundColor: '#0f766e', borderColor: '#0f766e', color: '#fff', WebkitTextFillColor: '#fff' } : { backgroundColor: isDarkMode ? undefined : '#fff', color: isDarkMode ? '#99f6e4' : '#0f766e', WebkitTextFillColor: isDarkMode ? '#99f6e4' : '#0f766e' }}>{mod}</button>; })}<label className="ml-auto inline-flex items-center gap-2 text-xs font-black text-slate-500"><span>Visualização</span><select value={visualizacaoManual} onChange={e => setVisualizacaoManual(e.target.value)} className="h-9 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-xs font-black text-teal-800 dark:text-teal-200"><option value="cards">Cards</option><option value="vitrine">Vitrine moderna</option><option value="lista">Lista</option></select></label></div>
          </div>
          {!config.manualAppsScriptUrl && !manuaisDrive.length && <div className="rounded-2xl border border-amber-200 bg-amber-50 text-amber-800 p-4 text-sm font-bold">A URL do Apps Script dos manuais ainda não está configurada. Use Configuração para informar a URL publicada ou clique em Atualizar após configurar.</div>}
          {carregandoManuais && <div className="rounded-2xl border border-cyan-200 bg-cyan-50 text-cyan-800 p-4 text-sm font-bold">Carregando manuais do Google Drive...</div>}
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-4">
            <div>
              <div className="flex items-center justify-between mb-3"><h3 className="font-black text-base">Manuais do sistema</h3><span className="text-xs text-slate-500">{manuaisDriveFiltrados.length} arquivo(s)</span></div>
              {manuaisDriveFiltrados.length > 0 ? (
                visualizacaoManual === 'lista' ? <div className={`rounded-2xl border overflow-hidden ${cardClass}`}>{manuaisDriveFiltrados.map((manual, idx) => { const tema = temaManual(manual, idx); return <div key={chaveManual(manual) || idx} role="button" tabIndex={0} onClick={() => abrirManualDrive(manual)} onKeyDown={e => { if (e.key === 'Enter') abrirManualDrive(manual); }} className={`help-manual-card p-3 flex items-center gap-3 border-b last:border-0 cursor-pointer bg-gradient-to-r ${tema.bg} ${tema.border} hover:shadow-sm dark:hover:bg-slate-800`}><div className="h-9 w-9 rounded-xl bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-200 flex items-center justify-center"><BookOpen size={17}/></div><div className="flex-1 min-w-0"><div className="text-[10px] font-black uppercase text-slate-500">{obterModuloManual(manual)} • {manual.menu || manual.area || 'Manual'}</div><div className="font-black text-sm truncate">{manual.titulo || 'Manual sem título'}</div></div><button type="button" onClick={(e) => { e.stopPropagation(); alternarFavoritoManual(manual); }} className="h-8 w-8 rounded-full bg-white/80 dark:bg-slate-900/80 border inline-flex items-center justify-center" title="Favoritar manual"><Star size={14} className={manualEhFavorito(manual) ? 'text-amber-500' : 'text-slate-400'} fill={manualEhFavorito(manual) ? 'currentColor' : 'none'}/></button></div>; })}</div>
                : visualizacaoManual === 'vitrine' ? <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{manuaisDriveFiltrados.map((manual, idx) => { const tema = temaManual(manual, idx); return <article key={chaveManual(manual) || idx} className={`help-manual-card group relative min-h-[190px] overflow-hidden rounded-[24px] border bg-gradient-to-br ${tema.bg} ${tema.border} p-5 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl`}><div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-white/35 blur-sm dark:bg-white/5"/><div className="relative flex h-full flex-col"><div className="flex items-start gap-3"><div className={`h-12 w-12 shrink-0 rounded-2xl ${tema.chip} flex items-center justify-center shadow-sm`}><BookOpen size={22}/></div><div className="min-w-0 flex-1"><div className="text-[10px] font-black uppercase tracking-wider text-slate-500">{obterModuloManual(manual)} • {manual.menu || 'Manual'}</div><h4 className={`mt-1 text-base font-black leading-tight ${tema.title}`}>{manual.titulo || 'Manual sem título'}</h4></div><button type="button" onClick={() => alternarFavoritoManual(manual)} className="h-9 w-9 shrink-0 rounded-full border bg-white/85 dark:bg-slate-900/80 inline-flex items-center justify-center" title="Favoritar manual"><Star size={16} className={manualEhFavorito(manual) ? 'text-amber-500' : 'text-slate-400'} fill={manualEhFavorito(manual) ? 'currentColor' : 'none'}/></button></div><p className="mt-3 line-clamp-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">{manual.resumo || `Orientações e procedimentos de ${manual.area || manual.menu || 'uso do sistema'}.`}</p><div className="mt-auto flex items-end justify-between gap-3 pt-4"><div className="flex flex-wrap gap-1">{[manual.area, manual.menu].filter(Boolean).slice(0,2).map(tag => <span key={tag} className="rounded-full border border-white/60 bg-white/65 px-2 py-1 text-[10px] font-bold dark:border-slate-700 dark:bg-slate-900/60">{tag}</span>)}</div><button type="button" onClick={() => abrirManualDrive(manual)} className="shrink-0 rounded-xl bg-teal-700 px-4 py-2 text-xs font-black text-white shadow-sm hover:bg-teal-800 inline-flex items-center gap-2">Abrir <ChevronRight size={14}/></button></div></div></article>; })}</div>
                : <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">{manuaisDriveFiltrados.map((manual, idx) => { const tema = temaManual(manual, idx); return <div key={chaveManual(manual) || idx} role="button" tabIndex={0} onClick={() => abrirManualDrive(manual)} onKeyDown={e => { if (e.key === 'Enter') abrirManualDrive(manual); }} className={`help-manual-card rounded-2xl border p-4 bg-gradient-to-br ${tema.bg} ${tema.border} shadow-sm relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow`}><button type="button" onClick={(e) => { e.stopPropagation(); alternarFavoritoManual(manual); }} className="absolute right-3 top-3 h-8 w-8 rounded-full bg-white/80 dark:bg-slate-900/80 border inline-flex items-center justify-center" title="Favoritar manual"><Star size={14} className={manualEhFavorito(manual) ? 'text-amber-500' : 'text-slate-400'} fill={manualEhFavorito(manual) ? 'currentColor' : 'none'}/></button><div className={`h-10 w-10 rounded-xl ${tema.chip} flex items-center justify-center mb-3`}><BookOpen size={18}/></div><div className="text-[10px] font-black uppercase text-slate-500 pr-8">{obterModuloManual(manual)} • {manual.menu || manual.area || 'Manual'}</div><h4 className={`mt-1 font-black text-sm leading-snug pr-6 ${tema.title}`}>{manual.titulo || 'Manual sem título'}</h4><div className="mt-3 flex flex-wrap gap-1">{[manual.area, manual.menu].filter(Boolean).slice(0,2).map(tag => <span key={tag} className="px-2 py-1 rounded-full bg-white/70 dark:bg-slate-900/70 text-[10px] font-bold">{tag}</span>)}</div><button type="button" onClick={(e) => { e.stopPropagation(); abrirManualDrive(manual); }} className="mt-4 w-full px-3 py-2 rounded-lg bg-teal-700 hover:bg-teal-800 text-white text-xs font-black">Abrir manual</button></div>; })}</div>
              ) : <div className={`rounded-2xl border p-8 text-center ${cardClass}`}><BookOpen className="mx-auto text-slate-400 mb-2"/><div className="font-black">Nenhum manual encontrado.</div><p className="text-xs text-slate-500 mt-1">Clique em Atualizar manuais ou revise a configuração do Apps Script.</p></div>}
            </div>
            <aside className={`rounded-2xl border p-4 ${cardClass}`}><h3 className="font-black text-base flex items-center gap-2"><Star size={16} className="text-amber-500"/>Meus manuais favoritos</h3>{manuaisFavoritos.length ? <div className="mt-3 space-y-2">{manuaisFavoritos.map((manual, idx) => <button key={chaveManual(manual) || idx} type="button" onClick={() => abrirManualDrive(manual)} className="w-full text-left rounded-xl border border-slate-200 dark:border-slate-700 p-3 hover:bg-slate-50 dark:hover:bg-slate-800"><div className="text-xs font-black">{manual.titulo}</div><div className="text-[10px] text-slate-500 mt-1">{manual.menu || manual.area || obterModuloManual(manual)}</div></button>)}</div> : <div className="mt-3 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-4 text-xs text-slate-500">Favorite manuais pelo ícone de estrela nos cards.</div>}<div className="mt-5"><h3 className="font-black text-base">Perguntas rápidas</h3>{faqsFiltradas.slice(0,6).map(faq => <button key={faq.id} type="button" onClick={() => registrarAcessoFaq(faq)} className="mt-2 w-full text-left rounded-xl border border-slate-200 dark:border-slate-700 p-3 text-xs hover:bg-slate-50 dark:hover:bg-slate-800"><b>{faq.pergunta}</b><div className="text-slate-500 mt-1 line-clamp-2">{faq.resposta}</div></button>)}</div></aside>
          </div>
        </div>}
        {aba === 'sugestoes' && <div className="help-suggestions-panel space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={abrirNovaSugestao} className="px-4 py-2.5 rounded-lg border border-teal-600 text-teal-700 dark:text-teal-200 bg-white dark:bg-slate-900 hover:bg-teal-700 hover:text-white font-bold text-sm inline-flex items-center gap-2"><Plus size={16}/>+ Sugestão</button>
            {isSuperAdm && <button type="button" onClick={() => setPopupConfig(true)} className="px-4 py-2.5 rounded-lg border border-teal-600 text-teal-700 dark:text-teal-200 bg-white dark:bg-slate-900 hover:bg-teal-700 hover:text-white font-bold text-sm inline-flex items-center gap-2"><Settings size={16}/>Configuração</button>}
            <div className="ml-auto inline-flex rounded-lg border border-slate-300 dark:border-slate-700 overflow-hidden">
              {[['pendentes','Pendentes'],['concluidas','Concluídas'],['todas','Todas']].map(([valor,label]) => <button key={valor} onClick={() => setFiltros(p => ({ ...p, conclusao: valor }))} className={`px-3 py-2 text-xs font-black ${filtros.conclusao === valor ? 'bg-cyan-600 text-white' : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300'}`}>{label}</button>)}
            </div>
            <div className="text-xs text-slate-500">{sugestoesFiltradas.length} registro(s)</div>
          </div>
          <div className={`rounded-2xl border p-2 ${cardClass}`}>
            <div className="grid grid-cols-[minmax(120px,1fr)_110px_minmax(185px,1.15fr)_minmax(155px,1fr)_minmax(145px,1fr)_minmax(165px,1.1fr)_minmax(200px,1.35fr)] items-center gap-2 min-w-[1180px] overflow-visible pb-1 text-sm">
              <input className={inputClass} placeholder="Usuário" value={filtros.usuario} onChange={e => setFiltros(p => ({ ...p, usuario: e.target.value }))}/>
              <input className={inputClass} placeholder="Data" value={filtros.data} onChange={e => setFiltros(p => ({ ...p, data: e.target.value }))}/>
              <div className="relative z-[220]">
                <button type="button" onClick={() => setFiltroStatusAberto(v => !v)} className={`${inputClass} text-left flex items-center justify-between`}><span className="truncate">{(filtros.status || []).length ? `${filtros.status.length} status selecionado(s)` : 'Todos os status'}</span><ChevronDown size={14}/></button>
                {filtroStatusAberto && <div className={`absolute z-[999] top-full left-0 mt-1 w-72 max-h-80 overflow-y-auto rounded-xl border shadow-xl p-2 ${modalClass}`}>{SUGESTOES_STATUS.map(status => <label key={status} className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 rounded"><input type="checkbox" checked={(filtros.status || []).includes(status)} onChange={() => alternarFiltroStatus(status)}/>{status}</label>)}<button onClick={() => setFiltros(p => ({ ...p, status: [] }))} className="mt-1 w-full px-2 py-1.5 rounded-lg border text-xs font-black">Limpar status</button></div>}
              </div>
              <select className={inputClass} value={filtros.prioridade} onChange={e => setFiltros(p => ({ ...p, prioridade: e.target.value }))}><option value="">Todas prioridades</option>{SUGESTOES_PRIORIDADES.map(s => <option key={s}>{s}</option>)}</select>
              <input className={inputClass} placeholder="Resp. análise" value={filtros.analista} onChange={e => setFiltros(p => ({ ...p, analista: e.target.value }))}/>
              <input className={inputClass} placeholder="Resp. desenvolvimento" value={filtros.desenvolvedor} onChange={e => setFiltros(p => ({ ...p, desenvolvedor: e.target.value }))}/>
              <div className="relative"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/><input className={`${inputClass} pl-9`} placeholder="Pesquisa livre" value={filtros.livre} onChange={e => setFiltros(p => ({ ...p, livre: e.target.value }))}/></div>
            </div>
          </div>
          <div className={`rounded-2xl border overflow-hidden ${cardClass}`}>
            <table className="w-full table-fixed text-sm leading-snug">
              <colgroup>
                <col className="w-[11%]"/><col className="w-[11%]"/><col className="w-[8%]"/><col className="w-[15%]"/>
                <col className="w-[8%]"/><col className="w-[10%]"/><col className="w-[10%]"/><col className="w-[8%]"/>
                <col className="w-[7%]"/><col className="w-[4%]"/><col className="w-[8%]"/>
              </colgroup>
              <thead className="bg-slate-200 dark:bg-slate-800">
                <tr>
                  {cabecalhoOrdenavelSugestoes('status', 'Status')}{cabecalhoOrdenavelSugestoes('usuarioNome', 'Usuário')}{cabecalhoOrdenavelSugestoes('data', 'Data')}{cabecalhoOrdenavelSugestoes('titulo', 'Título curto')}
                  {cabecalhoOrdenavelSugestoes('prioridade', 'Prioridade')}{cabecalhoOrdenavelSugestoes('analista', 'Resp. análise')}{cabecalhoOrdenavelSugestoes('desenvolvedor', 'Resp. desenvolv.')}
                  {cabecalhoOrdenavelSugestoes('prazo', 'Prazo etapa')}{cabecalhoOrdenavelSugestoes('tipo', 'Tipo')}<th className="p-2 text-center"><button type="button" onClick={() => alternarOrdenacaoSugestoes('anexos')} className="font-black">Anexos {ordenacaoSugestoes.campo === 'anexos' ? (ordenacaoSugestoes.direcao === 'asc' ? '↑' : '↓') : '↕'}</button></th><th className="p-2 text-center">Ações</th>
                </tr>
              </thead>
              <tbody>{sugestoesFiltradas.map((item, idx) => <tr key={item.id} className={`border-t border-slate-200 dark:border-slate-700 align-top ${idx % 2 ? 'bg-slate-50 dark:bg-slate-900/60' : 'bg-white dark:bg-slate-950'}`}>
                <td className="p-2.5 break-words"><span style={{ color: '#ffffff', WebkitTextFillColor: '#ffffff' }} className={`inline-block px-2.5 py-1.5 rounded-full font-black !text-white ${classeStatusSugestao(item.status || 'Enviado')}`}>{item.status || 'Enviado'}</span>{usuarioTemNotificacaoNoItem(item) && <button type="button" onClick={() => { const notif = (item.notificacoes || []).find(n => notificacaoVisivelParaUsuario(item, n)); if (notif) abrirNotificacaoSugestao({ ...notif, sugestao: item }); }} className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white align-middle" title="Há notificação pendente neste item"><Bell size={11}/></button>}{item.solicitantePediuDesconsideracao && <div className="text-[9px] text-amber-700 mt-1 font-black">Desconsideração solicitada</div>}</td>
                <td className="p-2.5 break-words"><div className="font-black">{item.usuarioNome || '-'}</div></td>
                <td className="p-2.5 break-words">{formatarDataSugestoes(item.criadoEm)}</td>
                <td className="p-2.5 break-words"><div className="font-black text-slate-900 dark:text-white">{item.tituloCurto || item.titulo || '-'}</div></td>
                <td className="p-2 break-words">{item.prioridade || 'Não definida'}</td>
                <td className="p-2 break-words">{item.responsavelAnaliseNome || item.responsavelAnaliseEmail || '-'}</td>
                <td className="p-2 break-words">{item.responsavelDesenvolvimentoNome || item.responsavelDesenvolvimentoEmail || '-'}</td>
                <td className="p-2 break-words">{formatarDataSugestoes(item.prazoConclusaoEtapa || item.prazoConclusao)}</td>
                <td className="p-2 break-words"><span style={{ color: '#ffffff', WebkitTextFillColor: '#ffffff' }} className={`inline-flex px-2.5 py-1.5 rounded-lg font-black !text-white ${classeTipoSugestao(item.tipo || 'Sugestão')}`}>{item.tipo || 'Sugestão'}</span></td>
                <td className="p-2 text-center">{item.anexos?.length || 0}</td>
                <td className="p-2"><div className="flex flex-row flex-wrap items-center justify-center gap-1">
                  {isSuperAdm && <button type="button" title="Administrar" aria-label="Administrar" onClick={() => abrirAdministracao(item)} className="h-8 w-8 rounded-lg bg-cyan-600 text-white inline-flex items-center justify-center"><Settings size={14}/></button>}
                  {usuarioEhAutor(item) && podeEditarOuExcluir(item) && <>
                    <button type="button" title="Editar minha sugestão" onClick={() => abrirEdicaoSugestao(item)} className="h-7 w-7 rounded-lg border border-blue-300 text-blue-600 inline-flex items-center justify-center"><Pencil size={13}/></button>
                    <button type="button" title="Excluir minha sugestão" onClick={() => excluirSugestaoUsuario(item)} className="h-7 w-7 rounded-lg border border-red-300 text-red-600 inline-flex items-center justify-center"><Trash2 size={13}/></button>
                  </>}
                  {usuarioEhAutor(item) && !podeEditarOuExcluir(item) && <button type="button" title="Complementar, corrigir ou pedir desconsideração" onClick={() => { setPopupComplemento(item); setTextoComplemento(''); setArquivos([]); }} className="h-7 w-7 rounded-lg border border-amber-300 text-amber-700 inline-flex items-center justify-center"><MessageCircle size={13}/></button>}
                  <button type="button" title="Ver movimentações e logs" onClick={() => setPopupHistorico(item)} className="h-7 w-7 rounded-lg border border-slate-300 text-slate-600 dark:text-slate-300 inline-flex items-center justify-center"><History size={13}/></button>
                </div></td>
              </tr>)}</tbody>
            </table>
            {sugestoesFiltradas.length === 0 && <div className="p-10 text-center text-slate-500">Nenhuma sugestão localizada.</div>}
          </div>
        </div>}
      </div>
    </div>

    {manualDriveAberto && <div className="fixed inset-0 z-[99995] pointer-events-none flex items-center justify-center p-3"><div style={{ transform: `translate(${posicaoManualAberto.x || 0}px, ${posicaoManualAberto.y || 0}px)`, resize: 'both', minWidth: '420px', minHeight: '360px', maxWidth: 'none', maxHeight: 'none' }} className={`pointer-events-auto w-[min(1380px,98vw)] h-[min(860px,94vh)] overflow-hidden rounded-2xl border shadow-2xl flex flex-col ${modalClass}`} title="A janela pode ser arrastada pelo cabeçalho e redimensionada pelo canto inferior direito."><div onMouseDown={iniciarArrasteManualAberto} className="manual-reader-header p-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3 cursor-move select-none" title="Clique e arraste para mover a janela do manual"><BookOpen className="text-cyan-600 shrink-0"/><div className="manual-reader-title flex-1 min-w-0"><div className="text-[10px] font-black uppercase text-cyan-600 truncate">{manualDriveAberto.menu || 'Manual'} • {manualDriveAberto.area || ''}</div><h3 className="text-lg font-black leading-tight truncate">{manualDriveAberto.titulo}</h3><p className="manual-reader-subtitle text-[10px] text-slate-500 leading-tight">Leitura aberta em modo visualização. Suas anotações ficam salvas apenas para o seu usuário neste navegador.</p></div><button onClick={() => alternarFavoritoManual(manualDriveAberto)} className={`px-3 py-2 rounded-lg border text-xs font-black inline-flex items-center gap-1 ${manualEhFavorito(manualDriveAberto) ? 'bg-amber-100 text-amber-700 border-amber-300' : ''}`}><Star size={14} fill={manualEhFavorito(manualDriveAberto) ? 'currentColor' : 'none'}/>Favorito</button><button onClick={() => setPainelAnotacoesAberto(v => !v)} className="px-3 py-2 rounded-lg border text-xs font-black">{painelAnotacoesAberto ? 'Ocultar coluna de anotações' : 'Mostrar minhas anotações'}</button>{(manualDriveAberto.urlAbrir || manualDriveAberto.urlVisualizacao || manualDriveAberto.url) && <a href={manualDriveAberto.urlAbrir || manualDriveAberto.urlVisualizacao || manualDriveAberto.url} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg border text-xs font-black inline-flex items-center gap-1"><ExternalLink size={14}/>Abrir</a>}<button type="button" onClick={resetarPosicaoManualAberto} className="px-3 py-2 rounded-lg border text-xs font-black">Centralizar</button><button type="button" onClick={() => setManualDriveAberto(null)}><X/></button></div><div className={`flex-1 min-h-0 grid grid-cols-1 ${painelAnotacoesAberto ? 'lg:grid-cols-[1fr_360px]' : ''}`}><div className="min-h-0 bg-slate-100 dark:bg-slate-950">{manualDriveAberto.urlVisualizacao ? <iframe title={manualDriveAberto.titulo || 'Manual'} src={manualDriveAberto.urlVisualizacao} className="w-full h-full bg-white"/> : <div className="p-6 overflow-y-auto custom-scrollbar whitespace-pre-wrap text-sm leading-6">{manualDriveAberto.conteudo || 'Conteúdo não carregado.'}</div>}</div>{painelAnotacoesAberto && <aside className="min-h-0 border-l border-slate-200 dark:border-slate-700 p-4 overflow-y-auto custom-scrollbar"><h4 className="font-black text-base">Minhas anotações</h4><p className="text-xs text-slate-500 mt-1">Anotações locais e privadas deste manual. Elas não alteram o Google Docs original e não aparecem para outros usuários.</p><textarea className={`${inputClass} mt-3 min-h-[220px]`} placeholder="Anotações gerais deste manual..." value={manualAnotacoes.nota || ''} onChange={e => salvarAnotacoesManual(manualDriveAberto.idDocumentoGoogle || manualDriveAberto.id || manualDriveAberto.url, { ...manualAnotacoes, nota: e.target.value })}/><div className="mt-3 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-3 text-xs text-slate-500">Para destacar visualmente trechos no próprio documento, use os recursos nativos do Google Docs ao abrir o arquivo em uma aba separada. As anotações desta lateral são particulares do sistema.</div></aside>}</div></div></div>}
    {popupNovaSugestao && <div className="fixed inset-0 z-[99997] pointer-events-auto bg-slate-950/70 flex items-center justify-center p-4"><div className={`w-[min(760px,96vw)] rounded-2xl border shadow-2xl ${modalClass}`}><div className="p-5 border-b border-slate-200 dark:border-slate-700 flex items-center"><Megaphone className="text-violet-500 mr-3"/><div><h3 className="text-lg font-black">{sugestaoEmEdicao ? 'Editar sugestão' : 'Nova sugestão'}</h3><p className="text-xs text-slate-500">Registre sugestão, correção ou melhoria do sistema.</p></div><button type="button" onClick={() => { setPopupNovaSugestao(false); setSugestaoEmEdicao(null); }} className="ml-auto"><X/></button></div><div className="p-5 space-y-3"><label className="text-xs font-black block">Título curto<input maxLength={100} className={`${inputClass} mt-1`} value={formSugestao.tituloCurto || ''} onChange={e => setFormSugestao(p => ({ ...p, tituloCurto: e.target.value }))} placeholder="Ex.: Ocultar botão da IA quando não houver pendências"/></label><div className="grid grid-cols-1 md:grid-cols-2 gap-3"><label className="text-xs font-black">Tipo<select className={`${inputClass} mt-1`} value={formSugestao.tipo} onChange={e => setFormSugestao(p => ({ ...p, tipo: e.target.value }))}><option>Sugestão</option><option>Correção</option><option>Melhoria</option><option>Dúvida</option></select></label><label className="text-xs font-black">Tela<input className={`${inputClass} mt-1`} value={formSugestao.tela} onChange={e => setFormSugestao(p => ({ ...p, tela: e.target.value }))}/></label></div><label className="text-xs font-black block">Descrição<div className={`mt-1 rounded-xl border ${isDarkMode ? 'border-slate-700 bg-slate-950' : 'border-amber-200 bg-white'} overflow-hidden`}><div className="flex flex-wrap gap-1 p-2 border-b border-slate-200 dark:border-slate-700"><button type="button" onClick={() => comandoEditorSugestao('bold')} className="px-2 py-1 rounded border text-xs font-black">B</button><button type="button" onClick={() => comandoEditorSugestao('italic')} className="px-2 py-1 rounded border text-xs italic">I</button><button type="button" onClick={() => comandoEditorSugestao('underline')} className="px-2 py-1 rounded border text-xs underline">U</button><button type="button" onClick={() => comandoEditorSugestao('insertUnorderedList')} className="px-2 py-1 rounded border text-xs">Lista</button><button type="button" onClick={() => comandoEditorSugestao('insertOrderedList')} className="px-2 py-1 rounded border text-xs">1,2,3</button><button type="button" onClick={() => comandoEditorSugestao('formatBlock', 'p')} className="px-2 py-1 rounded border text-xs">Parágrafo</button></div><div ref={editorSugestaoRef} contentEditable suppressContentEditableWarning onBlur={sincronizarDescricaoSugestao} className="min-h-[170px] p-3 text-sm outline-none prose prose-sm max-w-none dark:prose-invert" data-placeholder="Descreva a situação, o comportamento esperado e, se possível, como reproduzir. Use negrito, listas, parágrafos e destaques quando necessário." dangerouslySetInnerHTML={{__html: sanitizarHtmlSugestoes(formSugestao.descricaoHtml || '')}} /></div></label><label className="text-xs font-black block">Anexos<input type="file" multiple className={`${inputClass} mt-1`} onChange={e => setArquivos(Array.from(e.target.files || []))}/></label>{arquivos.length > 0 && <div className="text-xs text-slate-500">{arquivos.length} arquivo(s) selecionado(s)</div>}</div><div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2"><Button variant="secondary" onClick={() => { setPopupNovaSugestao(false); setSugestaoEmEdicao(null); }}>Cancelar</Button><Button onClick={salvarNovaSugestao} disabled={salvando} icon={Save}>{salvando ? 'Salvando...' : 'Salvar sugestão'}</Button></div></div></div>}
    {popupComplemento && <div className="fixed inset-0 z-[99997] pointer-events-auto bg-slate-950/70 flex items-center justify-center p-4"><div className={`w-[min(760px,96vw)] rounded-2xl border shadow-2xl ${modalClass}`}><div className="p-5 border-b border-slate-200 dark:border-slate-700 flex items-center"><MessageCircle className="text-amber-500 mr-3"/><div><h3 className="text-lg font-black">Complementar sugestão</h3><p className="text-xs text-slate-500">Inclua comentário, correção ou pedido de desconsideração.</p></div><button type="button" onClick={() => setPopupComplemento(null)} className="ml-auto"><X/></button></div><div className="p-5 space-y-3"><div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 text-xs"><b>{popupComplemento.codigo || popupComplemento.id}</b> — {popupComplemento.tela || '-'}</div><textarea className={`${inputClass} min-h-[160px]`} value={textoComplemento} onChange={e => setTextoComplemento(e.target.value)} placeholder="Escreva o complemento ou correção..."/><label className="text-xs font-black block">Anexos<input type="file" multiple className={`${inputClass} mt-1`} onChange={e => setArquivos(Array.from(e.target.files || []))}/></label></div><div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-between gap-2"><Button variant="danger" onClick={() => concluirComoNaoRealizada(popupComplemento)} disabled={salvando}>Pedir desconsideração</Button><div className="flex gap-2"><Button variant="secondary" onClick={() => setPopupComplemento(null)}>Cancelar</Button><Button onClick={salvarComplementoUsuario} disabled={salvando} icon={Save}>Salvar complemento</Button></div></div></div></div>}
    {popupHistorico && <div className="fixed inset-0 z-[99997] pointer-events-auto bg-slate-950/70 flex items-center justify-center p-4"><div className={`w-[min(860px,96vw)] max-h-[90vh] rounded-2xl border shadow-2xl flex flex-col ${modalClass}`}><div className="p-5 border-b border-slate-200 dark:border-slate-700 flex items-center"><History className="text-cyan-500 mr-3"/><div><h3 className="text-lg font-black">Movimentações e logs</h3><p className="text-xs text-slate-500">{popupHistorico.codigo || popupHistorico.id} • {popupHistorico.tela || '-'}</p></div><button type="button" onClick={() => setPopupHistorico(null)} className="ml-auto"><X/></button></div><div className="p-5 overflow-y-auto custom-scrollbar space-y-2">{(popupHistorico.historico || []).length === 0 ? <div className="text-sm text-slate-500 text-center py-8">Sem movimentações registradas.</div> : (popupHistorico.historico || []).map((h, idx) => <div key={idx} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3"><div className="text-xs text-slate-500">{formatarDataHoraSugestoes(h.data)} • {h.usuarioNome || h.usuarioEmail || '-'}</div><div className="font-black text-sm mt-1">{h.acao || '-'}</div>{h.observacao && <div className="text-sm mt-1 whitespace-pre-wrap">{h.observacao}</div>}<div className="text-[11px] text-slate-500 mt-1">Status: {h.status || '-'}</div></div>)}</div><div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end"><Button variant="secondary" onClick={() => setPopupHistorico(null)}>Fechar</Button></div></div></div>}
    {sugestaoAdmin && isSuperAdm && <div className="fixed inset-0 z-[99997] pointer-events-auto bg-slate-950/70 flex items-center justify-center p-4"><div className={`w-[min(980px,96vw)] max-h-[92vh] rounded-2xl border shadow-2xl flex flex-col ${modalClass}`}><div className="p-5 border-b border-slate-200 dark:border-slate-700 flex items-center"><Settings className="text-cyan-500 mr-3"/><div><h3 className="text-lg font-black">Administrar sugestão</h3><p className="text-xs text-slate-500">{sugestaoAdmin.codigo || sugestaoAdmin.id} • {sugestaoAdmin.tela || '-'}</p></div><button type="button" onClick={() => setSugestaoAdmin(null)} className="ml-auto"><X/></button></div><div className="p-5 overflow-y-auto custom-scrollbar space-y-4"><label className="text-xs font-black block">Título curto<input className={`${inputClass} mt-1`} value={sugestaoAdmin.tituloCurto || sugestaoAdmin.titulo || ''} onChange={e => setSugestaoAdmin(p => ({ ...p, tituloCurto: e.target.value }))}/></label><div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 text-sm prose prose-sm max-w-none dark:prose-invert" dangerouslySetInnerHTML={{__html: sanitizarHtmlSugestoes(sugestaoAdmin.descricaoHtml || (sugestaoAdmin.descricao ? String(sugestaoAdmin.descricao).replace(/\n/g, '<br/>') : '-'))}} />{Array.isArray(sugestaoAdmin.anexos) && sugestaoAdmin.anexos.length > 0 && <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3"><div className="text-xs font-black uppercase text-slate-500 mb-2">Anexos enviados</div><div className="flex flex-wrap gap-2">{sugestaoAdmin.anexos.map((anexo, idx) => { const urlAnexo = anexo.url || anexo.webViewLink || anexo.link || anexo.downloadUrl || anexo.fileUrl || (anexo.fileId ? `https://drive.google.com/file/d/${anexo.fileId}/view` : ''); const nomeAnexo = anexo.nome || anexo.name || anexo.nomeArquivo || `Anexo ${idx + 1}`; return urlAnexo ? <a key={idx} href={urlAnexo} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg border border-cyan-200 bg-cyan-50 text-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-200 dark:border-cyan-800 text-xs font-black inline-flex items-center gap-2"><ExternalLink size={13}/>{nomeAnexo}</a> : <span key={idx} className="px-3 py-2 rounded-lg border text-xs font-bold">{nomeAnexo}</span>; })}</div></div>}<div className="grid grid-cols-1 md:grid-cols-2 gap-3"><label className="text-xs font-black">Status<select className={`${inputClass} mt-1`} value={sugestaoAdmin.status || 'Enviado'} onChange={e => setSugestaoAdmin(p => ({ ...p, status: e.target.value }))}>{SUGESTOES_STATUS.map(s => <option key={s}>{s}</option>)}</select></label><label className="text-xs font-black">Prioridade<select className={`${inputClass} mt-1`} value={sugestaoAdmin.prioridade || 'Não definida'} onChange={e => setSugestaoAdmin(p => ({ ...p, prioridade: e.target.value }))}>{SUGESTOES_PRIORIDADES.map(s => <option key={s}>{s}</option>)}</select></label><label className="text-xs font-black">Responsável análise<select className={`${inputClass} mt-1`} value={sugestaoAdmin.responsavelAnaliseEmail || ''} onChange={e => setSugestaoAdmin(p => ({ ...p, responsavelAnaliseEmail: e.target.value }))}><option value="">Não definido</option>{(config.usuariosResponsaveis || []).map(u => <option key={u.email} value={u.email}>{u.nome || u.email}</option>)}</select></label><label className="text-xs font-black">Responsável desenvolvimento<select className={`${inputClass} mt-1`} value={sugestaoAdmin.responsavelDesenvolvimentoEmail || ''} onChange={e => setSugestaoAdmin(p => ({ ...p, responsavelDesenvolvimentoEmail: e.target.value }))}><option value="">Não definido</option>{(config.usuariosResponsaveis || []).map(u => <option key={u.email} value={u.email}>{u.nome || u.email}</option>)}</select></label><label className="text-xs font-black">Prazo de conclusão da etapa<input type="date" className={`${inputClass} mt-1`} value={String(sugestaoAdmin.prazoConclusaoEtapa || '').slice(0,10)} onChange={e => setSugestaoAdmin(p => ({ ...p, prazoConclusaoEtapa: e.target.value }))}/></label><label className="text-xs font-black md:col-span-2">Observação administrativa<textarea className={`${inputClass} mt-1 min-h-[90px]`} value={sugestaoAdmin.observacaoAdmin || ''} onChange={e => setSugestaoAdmin(p => ({ ...p, observacaoAdmin: e.target.value }))}/></label></div></div><div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-between gap-2"><Button variant="danger" onClick={() => excluirSugestaoAdmin(sugestaoAdmin)} disabled={salvando}>Excluir</Button><div className="flex gap-2"><Button variant="secondary" onClick={() => setSugestaoAdmin(null)}>Cancelar</Button><Button onClick={salvarAdministracao} disabled={salvando} icon={Save}>Salvar administração</Button></div></div></div></div>}
    {popupConfig && isSuperAdm && <div className="fixed inset-0 z-[99996] pointer-events-auto bg-slate-950/70 flex items-center justify-center p-4"><div className={`w-[min(1150px,98vw)] max-h-[94vh] overflow-y-auto rounded-2xl border shadow-2xl ${modalClass}`}><div className="p-5 border-b border-slate-200 dark:border-slate-700 flex items-center"><Settings className="text-amber-500 mr-3"/><div><h3 className="text-lg font-black">Configuração da Central de Ajuda</h3><p className="text-xs text-slate-500">Uso exclusivo do super administrador</p></div><button onClick={() => setPopupConfig(false)} className="ml-auto"><X/></button></div><div className="p-5 space-y-6"><div className={`rounded-2xl border p-4 ${cardClass}`}><h4 className="font-black mb-3">Integração com Google Drive</h4><div className="grid grid-cols-1 md:grid-cols-2 gap-3"><label className="text-xs font-black">ID da pasta<input className={`${inputClass} mt-1`} value={config.driveFolderId || ''} onChange={e => setConfig(p => ({ ...p, driveFolderId: e.target.value }))}/></label><label className="text-xs font-black">URL publicada do Apps Script<input className={`${inputClass} mt-1`} value={config.appsScriptUrl || ''} onChange={e => setConfig(p => ({ ...p, appsScriptUrl: e.target.value }))} placeholder="https://script.google.com/macros/s/.../exec"/></label><label className="text-xs font-black md:col-span-2">Token de upload<input type="password" className={`${inputClass} mt-1`} value={config.uploadToken || ''} onChange={e => setConfig(p => ({ ...p, uploadToken: e.target.value }))} placeholder="Use o mesmo valor configurado no Script Properties"/></label></div><div className="mt-3 flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300"><AlertTriangle size={15} className="shrink-0"/>O token no navegador não substitui regras de segurança. Restrinja o Web App ao domínio sempre que possível e aplique regras do Firestore para impedir alterações administrativas por usuários comuns.</div></div><div className={`rounded-2xl border p-4 ${cardClass}`}><h4 className="font-black mb-3">Manuais do sistema no Google Drive</h4><div className="grid grid-cols-1 md:grid-cols-2 gap-3"><label className="text-xs font-black">ID da pasta dos manuais<input className={`${inputClass} mt-1`} value={config.manualDriveFolderId || ''} onChange={e => setConfig(p => ({ ...p, manualDriveFolderId: e.target.value }))}/></label><label className="text-xs font-black">URL publicada do Apps Script do Manual<input className={`${inputClass} mt-1`} value={config.manualAppsScriptUrl || ''} onChange={e => setConfig(p => ({ ...p, manualAppsScriptUrl: e.target.value }))} placeholder="https://script.google.com/macros/s/.../exec"/></label></div><p className="mt-3 text-xs text-slate-500">O índice é carregado uma vez e guardado em cache local. Para separar CTB, Pessoas, Clientes, Hub e outros, informe no índice dos manuais a coluna/campo <b>modulo</b>. Quando o campo não existir, o sistema assume CTB.</p></div><div className={`rounded-2xl border p-4 ${cardClass}`}><h4 className="font-black mb-3">Super administradores</h4><textarea className={`${inputClass} min-h-[80px]`} value={(config.superAdminEmails || []).join('\n')} onChange={e => setConfig(p => ({ ...p, superAdminEmails: e.target.value.split(/[\n,;]+/).map(v => v.trim()).filter(Boolean) }))} placeholder="um-email@dominio.com por linha"/></div><div className={`rounded-2xl border p-4 ${cardClass}`}><div className="flex items-center mb-3"><h4 className="font-black">Usuários responsáveis</h4><button onClick={adicionarResponsavel} className="ml-auto px-3 py-2 rounded-lg bg-cyan-600 text-white text-xs font-black inline-flex items-center gap-2"><Plus size={14}/>Adicionar</button></div><div className="space-y-2">{(config.usuariosResponsaveis || []).map((u, i) => <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2"><input className={inputClass} placeholder="Nome" value={u.nome || ''} onChange={e => atualizarResponsavel(i, 'nome', e.target.value)}/><input className={inputClass} placeholder="E-mail" value={u.email || ''} onChange={e => atualizarResponsavel(i, 'email', e.target.value)}/><button onClick={() => removerResponsavel(i)} className="h-10 w-10 rounded-lg border border-red-200 text-red-600 inline-flex items-center justify-center"><Trash2 size={15}/></button></div>)}</div></div><div className={`rounded-2xl border p-4 ${cardClass}`}><h4 className="font-black mb-3">Cadastro de FAQ</h4><div className="grid grid-cols-1 md:grid-cols-2 gap-3"><input className={inputClass} placeholder="Tela do sistema" value={faqForm.tela} onChange={e => setFaqForm(p => ({ ...p, tela: e.target.value }))}/><input className={inputClass} placeholder="Pergunta" value={faqForm.pergunta} onChange={e => setFaqForm(p => ({ ...p, pergunta: e.target.value }))}/><input className={inputClass} placeholder="Categoria (ex.: Tributos)" value={faqForm.categoria || ''} onChange={e => setFaqForm(p => ({ ...p, categoria: e.target.value }))}/><input className={inputClass} placeholder="Palavras-chave separadas por vírgula" value={faqForm.palavrasChaveTexto || ''} onChange={e => setFaqForm(p => ({ ...p, palavrasChaveTexto: e.target.value }))}/><textarea className={`${inputClass} md:col-span-2 min-h-[120px]`} placeholder="Resposta" value={faqForm.resposta} onChange={e => setFaqForm(p => ({ ...p, resposta: e.target.value }))}/><label className="inline-flex items-center gap-2 text-sm font-bold"><input type="checkbox" checked={faqForm.ativo !== false} onChange={e => setFaqForm(p => ({ ...p, ativo: e.target.checked }))}/>FAQ ativa</label><div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setFaqForm({ id: '', pergunta: '', resposta: '', tela: '', categoria: '', palavrasChaveTexto: '', ordem: 0, ativo: true })}>Limpar</Button><Button onClick={salvarFaq} disabled={salvando} icon={Save}>Salvar FAQ</Button></div></div><div className="mt-4 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">{faqs.map((faq, idx) => <div key={faq.id} className={`p-3 flex items-start gap-3 border-b last:border-0 border-slate-200 dark:border-slate-700 ${idx % 2 ? 'bg-slate-50 dark:bg-slate-900' : ''}`}><div className="flex-1"><div className="font-black text-sm">{faq.pergunta}</div><div className="text-xs text-slate-500">{faq.tela || 'Geral'}</div></div><button onClick={() => setFaqForm({ id: faq.id, pergunta: faq.pergunta || '', resposta: faq.resposta || '', tela: faq.tela || '', categoria: faq.categoria || '', palavrasChaveTexto: (faq.palavrasChave || []).join(', '), ordem: faq.ordem || 0, ativo: faq.ativo !== false })} className="p-2 rounded-lg border"><Pencil size={14}/></button><button onClick={() => excluirFaq(faq)} className="p-2 rounded-lg border border-red-200 text-red-600"><Trash2 size={14}/></button></div>)}</div></div></div><div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2"><Button variant="secondary" onClick={() => setPopupConfig(false)}>Fechar</Button><Button onClick={salvarConfigGeral} disabled={salvando} icon={Save}>{salvando ? 'Salvando...' : 'Salvar configurações'}</Button></div></div></div>}
  </div>;
}

// ============================================================================
// MÓDULO 2: CONCILIADOR CONTÁBIL


    function App() {

        // ========================================================================
        // 1. ESTADOS GLOBAIS (HOOKS)
        // ========================================================================

        // --- Configuração do Tema (escuro, verde Acad e claro renovado) ---
        const [themeMode, setThemeMode] = useState(() => {
            try { return localStorage.getItem('gestao_pessoas_theme_mode_v1') || 'dark'; } catch { return 'dark'; }
        });
        const isDarkMode = themeMode !== 'light';
        const setIsDarkMode = (valueOrUpdater) => {
            const atual = themeMode !== 'light';
            const proximo = typeof valueOrUpdater === 'function' ? valueOrUpdater(atual) : valueOrUpdater;
            setThemeMode(proximo ? 'dark' : 'light');
        };
        const [systemZoom, setSystemZoom] = useState(() => {
            try { return Number(localStorage.getItem('gestao_pessoas_zoom_v1') || 100); } catch { return 100; }
        });
        const [cacheModalOpen, setCacheModalOpen] = useState(false);
        const [helpCenterOpen, setHelpCenterOpen] = useState(false);
        const [helpTab, setHelpTab] = useState('manual');
        const [manualView, setManualView] = useState(() => {
            try { return localStorage.getItem('gestao_pessoas_manual_view_v1') || 'cards'; } catch { return 'cards'; }
        });
        const [manualSearch, setManualSearch] = useState('');
        const [helpManualsData, setHelpManualsData] = useState([]);
        const [helpManualsLoading, setHelpManualsLoading] = useState(false);
        const [helpManualOpen, setHelpManualOpen] = useState(null);
        const [helpManualNotes, setHelpManualNotes] = useState('');
        const [helpManualNotesVisible, setHelpManualNotesVisible] = useState(true);
        const [helpManualPosition, setHelpManualPosition] = useState(() => {
            try { return JSON.parse(localStorage.getItem('gestao_pessoas_manual_position_v1') || '{"x":0,"y":0}'); }
            catch { return { x: 0, y: 0 }; }
        });
        const helpManualDragRef = useRef(null);
        const [helpSuggestion, setHelpSuggestion] = useState({ title: '', type: 'Sugestão', screen: '', description: '' });
        const [helpComplementSuggestion, setHelpComplementSuggestion] = useState(null);
        const [helpComplementText, setHelpComplementText] = useState('');
        const [helpComplementFiles, setHelpComplementFiles] = useState([]);
        const [helpSuggestions, setHelpSuggestions] = useState([]);
        const [helpSuggestionsLoading, setHelpSuggestionsLoading] = useState(false);
        const [newSuggestionOpen, setNewSuggestionOpen] = useState(false);
        const [helpConfigOpen, setHelpConfigOpen] = useState(false);
        const [helpActionSuggestion, setHelpActionSuggestion] = useState(null);
        const [helpLogsSuggestion, setHelpLogsSuggestion] = useState(null);
        const [helpSuggestionFilter, setHelpSuggestionFilter] = useState('pendentes');
        const [helpSuggestionSearch, setHelpSuggestionSearch] = useState('');
        const [helpConfig, setHelpConfig] = useState({ driveFolderId: '', appsScriptUrl: '', uploadToken: '', manualDriveFolderId: '', manualAppsScriptUrl: '', superAdmins: 'mauriciobremide@grupoacad.com.br', responsaveis: [{ nome: 'Mauricio Bremide', email: 'mauriciobremide@grupoacad.com.br' }], faq: { tela: '', pergunta: '', categoria: '', palavras: '', resposta: '', ativo: true } });
        const [cacheSelection, setCacheSelection] = useState({ avaliacoes: false, rh: false, ponto: false, produtividade: false, manuais: false, preferencias: false });

        // Aplica e persiste o tema somente no componente principal, onde os estados existem.
        useEffect(() => {
            const root = document.documentElement;
            root.classList.toggle('dark', themeMode !== 'light');
            root.classList.toggle('facil-theme-acad', themeMode === 'acad');
            root.classList.toggle('facil-theme-light', themeMode === 'light');
            root.classList.toggle('facil-theme-dark', themeMode === 'dark');
            try { localStorage.setItem('gestao_pessoas_theme_mode_v1', themeMode); } catch {}
        }, [themeMode]);

        // Aplica e persiste o zoom global do sistema.
        useEffect(() => {
            const zoomSeguro = Math.max(70, Math.min(140, Number(systemZoom) || 100));
            document.documentElement.style.fontSize = `${zoomSeguro}%`;
            try { localStorage.setItem('gestao_pessoas_zoom_v1', String(zoomSeguro)); } catch {}
        }, [systemZoom]);

        // Mantém a preferência de visualização dos manuais.
        useEffect(() => {
            try { localStorage.setItem('gestao_pessoas_manual_view_v1', manualView); } catch {}
        }, [manualView]);

        const iniciarArrasteManualAjuda = (event) => {
            if (event.button !== 0 || event.target?.closest?.('button,a,input,textarea,select,iframe')) return;
            const inicio = { mouseX: event.clientX, mouseY: event.clientY, x: helpManualPosition.x || 0, y: helpManualPosition.y || 0 };
            helpManualDragRef.current = inicio;
            const mover = (ev) => setHelpManualPosition({ x: inicio.x + ev.clientX - inicio.mouseX, y: inicio.y + ev.clientY - inicio.mouseY });
            const soltar = () => {
                helpManualDragRef.current = null;
                document.removeEventListener('mousemove', mover);
                document.removeEventListener('mouseup', soltar);
            };
            document.addEventListener('mousemove', mover);
            document.addEventListener('mouseup', soltar);
        };
        const centralizarManualAjuda = () => setHelpManualPosition({ x: 0, y: 0 });
        useEffect(() => {
            try { localStorage.setItem('gestao_pessoas_manual_position_v1', JSON.stringify(helpManualPosition)); } catch {}
        }, [helpManualPosition]);

        // --- Controle de Sessão e Usuário ---
        const [currentUser, setCurrentUser] = useState(null);

        // --- LÓGICA DE DRAG & DROP (Baseada no Hub.txt) ---
        const dragItem = useRef();
        const dragOverItem = useRef();

        // Função que processa a troca de posição ao soltar
        const handleDragSort = async () => {
            // Valida se há itens sendo arrastados
            if (dragItem.current === null || dragOverItem.current === null) return;
            
            // Copia o array de favoritos
            const _favs = [...(currentUser.preferences?.favorites || [])];
            
            // Remove o item da posição original
            const draggedItemContent = _favs.splice(dragItem.current, 1)[0];
            
            // Insere na nova posição (onde o mouse está em cima)
            _favs.splice(dragOverItem.current, 0, draggedItemContent);

            // Reseta as referências
            dragItem.current = null;
            dragOverItem.current = null;

            // Atualiza Visualmente na hora (Feedback instantâneo)
            const updatedUser = { ...currentUser, preferences: { ...currentUser.preferences, favorites: _favs } };
            setCurrentUser(updatedUser);

            // Salva no Banco
            if (typeof actions !== 'undefined' && actions.saveUserPreferences) {
                await actions.saveUserPreferences({ favorites: _favs });
            }
        };

        const [isCollapsed, setIsCollapsed] = useState(false);

        const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
        const [notificationTab, setNotificationTab] = useState('active');
        const [archivedNotificationIds, setArchivedNotificationIds] = useState(
            currentUser?.preferences?.archivedNotificationIds || []
        );

        // Mantem a memoria de notificacoes lidas sincronizada apos login/refresh.
        // Antes, o estado iniciava vazio quando currentUser ainda nao havia carregado,
        // fazendo notificacoes arquivadas voltarem ao atualizar a pagina.
        useEffect(() => {
            const savedIds = currentUser?.preferences?.archivedNotificationIds;
            setArchivedNotificationIds(Array.isArray(savedIds) ? savedIds : []);
        }, [currentUser?.id, JSON.stringify(currentUser?.preferences?.archivedNotificationIds || [])]);

             // --- ESTADOS E DADOS DO SORTEIO ---
            const [isRaffleModalOpen, setIsRaffleModalOpen] = useState(false);
            const [raffleWinner, setRaffleWinner] = useState(null);

            // Exemplo de dados (substitua pelos dados reais depois)
            const mockParticipantesSorteio = [
                { id: 1, name: "Ana Silva", tickets: 5 },
                { id: 2, name: "Carlos Santos", tickets: 2 },
                { id: 3, name: "Beatriz Costa", tickets: 10 },
                { id: 4, name: "João Oliveira", tickets: 1 },
            ];

            // Lógica de Sorteio com Peso
            const handleDrawWinner = () => {
                const totalTickets = mockParticipantesSorteio.reduce((acc, curr) => acc + curr.tickets, 0);
                if (totalTickets === 0) return;

                const randomTicket = Math.floor(Math.random() * totalTickets) + 1;
                let ticketCounter = 0;

                for (const participant of mockParticipantesSorteio) {
                    ticketCounter += participant.tickets;
                    if (randomTicket <= ticketCounter) {
                        setRaffleWinner(participant);
                        break;
                    }
                }
            };
        // --- LÓGICA DE FAVORITOS (LOCALIZAÇÃO CORRETA: TOPO) ---
        const [isFavTreeModalOpen, setIsFavTreeModalOpen] = useState(false);
        const [expandedFavGroups, setExpandedFavGroups] = useState({});

        // --- LÓGICA DE ORGANIZAÇÃO DE MENUS (SUB-GRUPOS PERSONALIZADOS) ---
        const [isMenuOrganizerOpen, setIsMenuOrganizerOpen] = useState(false);
        const [orgModule, setOrgModule] = useState('collab_center'); // Módulo selecionado no modal
        const [newMenuGroupName, setNewMenuGroupName] = useState('');
        const [menuOrganizerConfig, setMenuOrganizerConfig] = useState({ groups: {}, mapping: {} });

        const openMenuOrganizer = () => {
            // Carrega a configuração salva do usuário ou inicia vazio
            setMenuOrganizerConfig(currentUser?.preferences?.menuConfig || { groups: {}, mapping: {} });
            setIsMenuOrganizerOpen(true);
        };

        const handleAddMenuGroup = () => {
            if (!newMenuGroupName.trim()) return;
            const groupName = newMenuGroupName.trim();
            
            setMenuOrganizerConfig(prev => {
                const currentGroups = prev.groups[orgModule] || [];
                if (currentGroups.includes(groupName)) {
                    alert("Grupo já existe neste módulo.");
                    return prev;
                }
                return {
                    ...prev,
                    groups: { ...prev.groups, [orgModule]: [...currentGroups, groupName] }
                };
            });
            setNewMenuGroupName('');
        };

        const handleRemoveMenuGroup = (groupName) => {
            if (!window.confirm(`Deseja excluir o grupo "${groupName}"?\nOs itens voltarão para a lista principal.`)) return;
            
            setMenuOrganizerConfig(prev => {
                // Remove o grupo
                const newGroups = (prev.groups[orgModule] || []).filter(g => g !== groupName);
                // Remove o mapeamento dos itens que estavam nesse grupo
                const newMapping = { ...prev.mapping };
                Object.keys(newMapping).forEach(key => {
                    if (newMapping[key] === groupName) delete newMapping[key];
                });
                
                return { groups: { ...prev.groups, [orgModule]: newGroups }, mapping: newMapping };
            });
        };

        const handleSaveMenuOrganization = async () => {
            setCurrentUser(prev => ({...prev, preferences: {...prev.preferences, menuConfig: menuOrganizerConfig}}));
            if (actions.saveUserPreferences) {
                await actions.saveUserPreferences({ menuConfig: menuOrganizerConfig });
            }
            setIsMenuOrganizerOpen(false);
        };

        
        // --- LÓGICA DE PASTAS E GRUPOS PERSONALIZADOS DO MENU ---
        const [expandedCustomFolders, setExpandedCustomFolders] = useState({});
        const [newFolderName, setNewFolderName] = useState('');

        const handleAddCustomFolder = async () => {
            if (!newFolderName.trim()) return;
            const currentFolders = currentUser?.preferences?.customFolders || [];
            if (currentFolders.includes(newFolderName.trim())) {
                alert("Já existe um grupo com este nome.");
                return;
            }
            const newFolders = [...currentFolders, newFolderName.trim()];
            setCurrentUser(prev => ({...prev, preferences: {...prev.preferences, customFolders: newFolders}}));
            if (actions.saveUserPreferences) await actions.saveUserPreferences({ customFolders: newFolders });
            setNewFolderName('');
        };

        const handleRemoveCustomFolder = async (folderName) => {
            if(!window.confirm(`Deseja excluir o grupo "${folderName}"?\nOs atalhos vinculados voltarão para a raiz do seu menu.`)) return;
            
            const currentFolders = currentUser?.preferences?.customFolders || [];
            const newFolders = currentFolders.filter(f => f !== folderName);
            
            // Remove as pessoas dessa pasta, jogando pra raiz (vazio)
            const currentFavs = currentUser?.preferences?.favorites || [];
            const newFavs = currentFavs.map(f => f.folder === folderName ? { ...f, folder: '' } : f);
            
            setCurrentUser(prev => ({...prev, preferences: {...prev.preferences, customFolders: newFolders, favorites: newFavs}}));
            if (actions.saveUserPreferences) await actions.saveUserPreferences({ customFolders: newFolders, favorites: newFavs });
        };

        const updateFavFolder = async (targetSys, folderName) => {
            const currentFavs = currentUser?.preferences?.favorites || [];
            const newFavs = currentFavs.map(f => f.targetSys === targetSys ? { ...f, folder: folderName } : f);
            setCurrentUser(prev => ({...prev, preferences: {...prev.preferences, favorites: newFavs}}));
            if (actions.saveUserPreferences) await actions.saveUserPreferences({ favorites: newFavs });
        };

        // --- LÓGICA DE FAVORITOS ATUALIZADA (CORRIGIDA) ---
        const toggleFavorite = async (item, preferredSize = 'normal') => {
        if (!currentUser) return;
        const prefs = currentUser.preferences || {};
        const currentFavs = prefs.favorites || [];
        const exists = currentFavs.find(f => f.targetSys === item.targetSys);
        
        let newFavs;
        if (exists) {
            if (item.forceUpdate) {
                newFavs = currentFavs.map(f => f.targetSys === item.targetSys ? { ...f, size: item.size } : f);
            } else {
                newFavs = currentFavs.filter(f => f.targetSys !== item.targetSys);
            }
        } else {
            const cleanLabel = item.label.includes('>') ? item.label.split('>').pop().trim() : item.label;
            
            const colors = [
                'from-blue-600 to-blue-400', 'from-purple-600 to-purple-400', 'from-emerald-600 to-emerald-400', 
                'from-orange-500 to-amber-500', 'from-pink-600 to-rose-400', 'from-indigo-600 to-blue-500',
                'from-teal-500 to-cyan-400', 'from-red-600 to-orange-500', 'from-fuchsia-600 to-purple-500', 
                'from-slate-700 to-slate-500', 'from-lime-500 to-green-400', 'from-violet-600 to-indigo-500',
                'from-sky-500 to-blue-400', 'from-yellow-500 to-orange-400', 'from-rose-500 to-pink-500',
                'from-cyan-600 to-teal-500', 'from-emerald-700 to-green-500', 'from-blue-800 to-indigo-700',
                'from-amber-600 to-yellow-500', 'from-zinc-700 to-stone-500'
            ];
            
            // Atribui a cor baseada na quantidade atual de favoritos
        const assignedColor = colors[currentFavs.length % colors.length];
            
            newFavs = [...currentFavs, { 
                id: Date.now().toString(), 
                label: cleanLabel, 
                fullPath: item.label, 
                targetSys: item.targetSys, 
                iconName: item.iconName || 'Star', 
                color: assignedColor, // <--- CORREÇÃO: Alterado de randomColor para assignedColor
                size: preferredSize 
            }];
        }
        
        // Atualiza Estado e Banco
        setCurrentUser(prev => ({ ...prev, preferences: { ...prev.preferences, favorites: newFavs } }));
        if (typeof actions !== 'undefined' && actions.saveUserPreferences) {
            await actions.saveUserPreferences({ favorites: newFavs });
        }
        };

        // 2. Definir Tela Inicial (ATUALIZADO)
        const setStartupScreen = async (targetSys) => {
            if (!currentUser) return;

            // A. Atualiza a flag 'principalAcesso' na lista de favoritos
            const currentFavs = [...(currentUser.preferences?.favorites || [])];
            
            // Marca o item selecionado como true, os outros como false
            const updatedFavs = currentFavs.map(f => ({
                ...f,
                principalAcesso: f.targetSys === targetSys
            }));

            // B. Prepara o objeto de preferências atualizado
            const updatedPreferences = { 
                ...currentUser.preferences, 
                defaultScreen: targetSys, 
                favorites: updatedFavs 
            };

            // C. Atualiza estado local (UI)
            setCurrentUser(prev => ({ 
                ...prev, 
                preferences: updatedPreferences
            }));

            // D. Persiste no Firebase
            if (typeof actions !== 'undefined' && actions.saveUserPreferences) {
                await actions.saveUserPreferences({ 
                    defaultScreen: targetSys,
                    favorites: updatedFavs 
                });
            }
        };


        // 3. Helper isFavorite
        const isFavorite = (targetSys) => {
            return (currentUser?.preferences?.favorites || []).some(f => f.targetSys === targetSys);
        };
        const [authReady, setAuthReady] = useState(false);
        const [showChangePassword, setShowChangePassword] = useState(false);

        // --- Controle de Navegação ---
        const [sys, setSys] = useState(null);
        const sysRef = useRef(null);
        useEffect(() => { sysRef.current = sys; }, [sys]);
        const [openId, setOpenId] = useState(null);
        // Abas superiores dos módulos abertos, para navegação rápida sem precisar fechar e abrir menus.
        const [openModuleTabs, setOpenModuleTabs] = useState([]);

        // Limpa o cache do sessionStorage de avaliações que não estão mais ativas/abertas nas abas
        useEffect(() => {
            try {
                // Encontra qual ID de avaliação está ativo nas abas abertas no topo
                const activeEvalTab = openModuleTabs.find(t => t.moduleId === 'eval');
                const activeEvalId = activeEvalTab ? activeEvalTab.openId : null;

                // Varre as chaves do sessionStorage
                const keysToRemove = [];
                for (let i = 0; i < sessionStorage.length; i++) {
                    const key = sessionStorage.key(i);
                    if (key && key.startsWith('acad_eval_work_state_')) {
                        const evalId = key.replace('acad_eval_work_state_', '');
                        // Se o ID da avaliação no cache não é o ID ativo nas abas, removemos do cache
                        if (evalId !== activeEvalId) {
                            keysToRemove.push(key);
                        }
                    }
                }
                keysToRemove.forEach(key => sessionStorage.removeItem(key));
            } catch (e) {
                console.warn('Erro ao limpar cache do sessionStorage:', e);
            }
        }, [openModuleTabs]);

        const [expandedMenus, setExpandedMenus] = useState({});
        const [currentProject, setCurrentProject] = useState('av_desempenho');

        // --- MODO DE VISÃO CARDS ---
        const isCardViewMode = currentUser?.preferences?.isCardViewMode || false;

        const toggleCardViewMode = async () => {
            const newVal = !isCardViewMode;
            if (actions.saveUserPreferences) {
                await actions.saveUserPreferences({ isCardViewMode: newVal });
            }
            if (newVal) {
                setSys('card_menu');
            } else {
                setSys('favorites');
            }
            setCurrentUser(prev => ({...prev, preferences: {...prev.preferences, isCardViewMode: newVal}}));
        };    

        // ... outros estados do ponto ...
        const [employeesMeta, setEmployeesMeta] = useState({});
        const [mockSignatures, setMockSignatures] = useState([]);

        

        // --- Estados de Modais Globais ---
        const [modal, setModal] = useState(null);
        const [selectedEval, setSelectedEval] = useState(null);
        const [isGenerating, setIsGenerating] = useState(false); // <--- Adicionar esta linha
        const [validationAlert, setValidationAlert] = useState(null);

        // --- Estados Específicos de Abas e Filtros ---
        const [isCollabEvalModalOpen, setIsCollabEvalModalOpen] = useState(false);
        const [collabViewYear, setCollabViewYear] = useState(new Date().getFullYear().toString());
        const [isProcessingBatch, setIsProcessingBatch] = useState(false);
        const [showAllOverride, setShowAllOverride] = useState(false);

        // --- Estados para "Minhas Avaliações" (Central Colaborador) ---
        const [collabFeedbackModal, setCollabFeedbackModal] = useState({
            isOpen: false,
            evalId: null,
            text: '',
            quarter: '',
            tab: 'insert',
            editingId: null
        });
        const [collabConfirmModal, setCollabConfirmModal] = useState({
            isOpen: false,
            type: '',
            data: null
        });

        // --- Estados de Sincronização e Menus ---
        const [syncMenuOpen, setSyncMenuOpen] = useState(null);
        const [syncModalConfig, setSyncModalConfig] = useState({
            isOpen: false,
            title: '',
            type: '',
            source: '',
            callback: null
        });
        const [batchYearInput, setBatchYearInput] = useState(new Date().getFullYear().toString());

        // --- Filtros de Análise Gerencial ---
        const [analiseFilters, setAnaliseFilters] = useState({
            startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
            endDate: new Date().toISOString().split('T')[0],
            dept: '',
            personType: 'Todos',
            showTotalSalary: false
        });

            // --- ESTADOS DO FORMULÁRIO DE TREINAMENTO ---
        const [isTrainingModalOpen, setIsTrainingModalOpen] = useState(false);
        const [trainingModalTab, setTrainingModalTab] = useState('dados');
        const [trainingDeadlineForm, setTrainingDeadlineForm] = useState({ id: null, name: '', departmentId: '', personType: 'Todos', q1: '', q2: '', q3: '', q4: '' }); // NOVO: Formulário da regra

        // --- ESTADOS DO MÓDULO DE PONTO ---
        const [pontoTab, setPontoTab] = useState('clock');
        const [pontoRecords, setPontoRecords] = useState([]); // Armazena os pontos (Mock por enquanto)
        const { data: pontoConfig, saveItem: savePontoConfig, deleteItem: deletePontoConfig } = useFirestoreData('pontoConfig', [], authReady, currentUser);
        const { data: pontoSituations, saveItem: savePontoSituations, deleteItem: deletePontoSituations } = useFirestoreData('pontoSituations', [], authReady, currentUser);

        const [isUploading, setIsUploading] = useState(false);

        // --- ESTADOS: CULTURA ACAD ---
        const { data: culturaRecords, saveItem: saveCulturaRecord, deleteItem: deleteCulturaRecord } = useFirestoreData('culturaAcad', [], true, currentUser);
        const [culturaSearch, setCulturaSearch] = useState('');
        const [showCulturaModal, setShowCulturaModal] = useState(false);
        const [showCulturaUpdateModal, setShowCulturaUpdateModal] = useState(false);
        const [culturaForm, setCulturaForm] = useState({ 
            id: null, nome: '', link: '', exigeAssinatura: false, 
            dataInicio: '', dataFim: '', departamento: 'Todos', tipo: 'Todos', status: 'Rascunho' 
        });
        const [culturaUpdateMsg, setCulturaUpdateMsg] = useState('');
        const hasCulturaManagePerm = currentUser?.role === 'SuperAdm' || currentUser?.permissions?.manage_cultura_acad === true;
        
        // --- ESTADOS: FEEDBACKS E MODO ADMIN (CULTURA ACAD) ---
        const { data: culturaFeedbacks, saveItem: saveCulturaFeedback } = useFirestoreData('culturaFeedbacks', [], true, currentUser);
        const [isCulturaAdminMode, setIsCulturaAdminMode] = useState(false);
        const [showFeedbackModal, setShowFeedbackModal] = useState(false);
        const [feedbackForm, setFeedbackForm] = useState({ id: null, tipo: 'Dúvida', documentoId: '', parteDocumento: '', mensagem: '', isAnonimo: false, status: 'Pendente' });
        const [feedbackModalTab, setFeedbackModalTab] = useState('nova'); // Controle das abas do Modal RH
        const [hrResponseText, setHrResponseText] = useState({});

        const [hrCulturaTab, setHrCulturaTab] = useState('feedbacks');
        const [hrCulturaSearch, setHrCulturaSearch] = useState('');

        const [showRespondedFeedbacks, setShowRespondedFeedbacks] = useState(false);
        const [feedbackLayout, setFeedbackLayout] = useState('cards'); // 'cards' ou 'linhas'
        
        const hrCulturaBadgeCount = culturaFeedbacks.filter(f => f.status === 'Pendente').length;

        const collabCulturaBadgeCount = culturaFeedbacks.filter(f =>
            f.userId === currentUser?.id &&
            f.status === 'Respondido' &&
            !f.lidoColab
        ).length;

        const collabCulturaDocsPendingCount = useMemo(() => {
            if (!currentUser) return 0;

            return (culturaRecords || []).filter(r => {
                if (r.status !== 'Liberado') return false;
                if (!r.exigeAssinatura) return false;

                const hasAcknowledge = (r.ciencias || []).some(c =>
                    c?.userId === currentUser?.id ||
                    c?.userName === currentUser?.name
                );

                if (hasAcknowledge) return false;

                const isGestor = currentUser?.role === 'Gestor' || currentUser?.role === 'SuperAdm';
                if (r.tipo === 'Gestão' && !isGestor) return false;
                if (r.tipo === 'Colaborador' && isGestor) return false;

                return true;
            }).length;
        }, [culturaRecords, currentUser]);

        const collabCulturaTotalNotifications = collabCulturaBadgeCount + collabCulturaDocsPendingCount;

                 
            // Novos estados para a aba de Capacitações
        const [trainingSearch, setTrainingSearch] = useState('');
        const [editingTrainingId, setEditingTrainingId] = useState(null); // Controla se é edição
        
        // Dados do formulário
        const [trainingForm, setTrainingForm] = useState({
            id: null,
            date: '',
            title: '',
            type: 'Área Atuação',
            hours: '',
            durationStr: '', // <--- OBRIGATÓRIO
            hasCert: 'Sim',
            oldCertLink: null,
            oldSummaryLink: null
        });

        // Arquivos (separados para facilitar lógica condicional)
        const [trainingFiles, setTrainingFiles] = useState({
            cert: null,    // Para certificado
            summary: null, // Para resumo
            print: null    // Para prints
        });

        // --- ESTADO PARA CONFIRMAÇÃO DE EXCLUSÃO (CORREÇÃO DO ERRO DO CONSOLE) ---
            const [confirmModal, setConfirmModal] = useState({
                isOpen: false,
                title: '',
                message: '',
                onConfirm: null
            });

            
        // --- FUNÇÃO PARA DELETAR NO DRIVE (CORRIGIDA) ---
        const deleteFileFromDrive = async (fileUrl) => {
            if (!fileUrl) return;
            
            // 1. Extrai o ID do arquivo do link do Google Drive
            // Padrão: .../d/FILE_ID/view... ou id=FILE_ID
            const idMatch = fileUrl.match(/[-\w]{25,}/);
            const fileId = idMatch ? idMatch[0] : null;

            if (!fileId) {
                console.warn("ID do arquivo não encontrado na URL:", fileUrl);
                return;
            }

            // URL DO SEU SCRIPT GOOGLE (Verifique se é a mesma usada no upload)
                const SCRIPT_URL = "https://script.google.com/macros/s/AKfycbySpFDKhRT_DiNaKsG5G86izYnTvbtU6nbZQoQngFbi2BvCWBCzuw_hIMRDoHN1ug_A/exec";
        
            try {
                await fetch(SCRIPT_URL, {
                    method: 'POST',
                    // Envia 'id' explicitamente ao invés de 'url'
                    body: JSON.stringify({ action: 'delete', id: fileId }), 
                    headers: { "Content-Type": "text/plain;charset=utf-8" }
                });
                console.log("Arquivo deletado do Drive ID:", fileId);
            } catch (e) {
                console.error("Erro ao deletar arquivo do Drive:", e);
            }
        };

        // --- Referência para evitar loop de redirecionamento ---
        const initialLoadRef = React.useRef(true);

        // ========================================================================
        // 2. DATA HOOKS (FIRESTORE)
        // ========================================================================
        const { data: depts, saveItem: saveDept, deleteItem: deleteDept } = useFirestoreData('departments', SEED_DATA.departments, authReady, currentUser);
        const { data: mgrs, saveItem: saveManager, deleteItem: deleteManager } = useFirestoreData('managers', SEED_DATA.managers, authReady, currentUser);
        const { data: collabs, saveItem: saveCollab, deleteItem: deleteCollab } = useFirestoreData('collaborators', SEED_DATA.collaborators, authReady, currentUser);
        const { data: acadCoinsConfig } = useFirestoreData('acadCoinsConfig', [], authReady, currentUser);
        const { data: evals, saveItem: saveEvaluation, deleteItem: deleteEvaluation } = useFirestoreData('evaluations', SEED_DATA.evaluations, authReady, currentUser);
        const { data: aps, saveItem: saveApontamento, deleteItem: deleteApontamento } = useFirestoreData('apontamentos', SEED_DATA.apontamentos, authReady, currentUser);
        const { data: qQuali, saveItem: saveQuali, deleteItem: deleteQuali } = useFirestoreData('qualiCriteria', DEFAULT_QUALI, authReady, currentUser);
        const { data: qQuanti, saveItem: saveQuanti, deleteItem: deleteQuanti } = useFirestoreData('quantiCriteria', DEFAULT_QUANTI, authReady, currentUser);
        const { data: gConf, saveItem: saveConfig, deleteItem: deleteConfig } = useFirestoreData('globalConfigs', DEFAULT_GLOBAL, authReady, currentUser);
        const { data: eTypes, saveItem: saveType, deleteItem: deleteType } = useFirestoreData('evaluationTypes', SEED_DATA.evaluationTypes, authReady, currentUser);
        const { data: msgs, saveItem: saveMsg, deleteItem: deleteMsg } = useFirestoreData('pdfMessages', SEED_DATA.pdfMessages, authReady, currentUser);
        const { data: pcs, saveItem: savePCS, deleteItem: deletePCS } = useFirestoreData('pcs', SEED_DATA.pcs, authReady, currentUser);
        const { data: users, saveItem: saveUser, deleteItem: deleteUser } = useFirestoreData('users', SEED_DATA.users, authReady, currentUser);
        const { data: aiConfigs, saveItem: saveAIConfig } = useFirestoreData('aiConfigs', [], authReady, currentUser);
        const { data: activePermissionsList, saveItem: saveActivePermissions } = useFirestoreData('activePermissions', [SEED_DATA.activePermissions], authReady, currentUser);

        // Hooks usados pela Central de Notificacoes do AcadCoins.
        // Otimizacao: so abre listeners se o usuario realmente pode ver AcadCoins.
        const acadCoinsBellEnabled =
            currentUser?.role === 'SuperAdm' ||
            currentUser?.permissions?.collab_tab_acad_coins === true ||
            currentUser?.permissions?.acad_coins_admin === true;

        const { data: acadCoinsDistBell } = useFirestoreData('acadCoinsDist', [], authReady, currentUser, [], acadCoinsBellEnabled);
        const { data: acadCoinsPrizesBell } = useFirestoreData('acadCoinsPrizes', [], authReady, currentUser, [], acadCoinsBellEnabled);
        const { data: acadCoinsIdeasBell } = useFirestoreData('acadCoinsIdeas', [], authReady, currentUser, [], acadCoinsBellEnabled);
        const { data: acadCoinsNewsBell } = useFirestoreData('acadCoinsNews', [], authReady, currentUser, [], acadCoinsBellEnabled);
        const { data: minWageHistory, saveItem: saveMinWageHistory, deleteItem: deleteMinWageHistory } = useFirestoreData('minWageHistory', SEED_DATA.minWageHistory, authReady, currentUser);
        const { data: gestaoMetasPercepcao, saveItem: saveGestaoMetasPercepcao, deleteItem: deleteGestaoMetasPercepcao } = useFirestoreData('gestaoMetasPercepcao', SEED_DATA.gestaoMetasPercepcao, authReady, currentUser);
        const acadCoinsConfigDB = acadCoinsConfig;
        const isSuperAdmin = currentUser?.role === 'SuperAdm';

            const auditEnabled =
            isSuperAdmin ||
            currentUser?.permissions?.access_tab_audit === true;

            const rhEnabled =
            isSuperAdmin ||
            currentUser?.permissions?.access_rh === true ||
            currentUser?.permissions?.access_rh_central === true ||
            currentUser?.permissions?.access_talent === true;

            const surveysEnabled =
            isSuperAdmin ||
            currentUser?.permissions?.access_sub_surveys === true ||
            currentUser?.permissions?.collab_tab_surveys === true ||
            currentUser?.permissions?.access_rh_surveys === true ||
            currentUser?.permissions?.access_surveys_config === true;

            const acadCoinsEnabled =
            isSuperAdmin ||
            currentUser?.permissions?.collab_tab_acad_coins === true ||
            currentUser?.permissions?.acad_coins_admin === true;

            const trainingEnabled =
            isSuperAdmin ||
            currentUser?.permissions?.access_tab_training === true ||
            currentUser?.permissions?.access_sub_capacitacoes === true ||
            currentUser?.permissions?.collab_tab_training === true;

            const perceptionEnabled =
            isSuperAdmin ||
            currentUser?.permissions?.access_sub_percepcao === true ||
            currentUser?.permissions?.collab_tab_perception === true;

            const recognitionEnabled =
            isSuperAdmin ||
            currentUser?.permissions?.collab_tab_recognition === true;

            const recruitmentEnabled =
            isSuperAdmin ||
            currentUser?.permissions?.access_talent === true ||
            currentUser?.permissions?.access_rh === true;
            
        const { data: trainingData, saveItem: saveTraining, deleteItem: deleteTraining } = useFirestoreData('trainingData', [], authReady, currentUser, [], trainingEnabled);
        const { data: trainingDeadlines, saveItem: saveTrainingDeadline, deleteItem: deleteTrainingDeadline } = useFirestoreData('trainingDeadlines', [], authReady, currentUser, [], trainingEnabled);
        const { data: auditLogs, saveItem: saveAuditLog } = useFirestoreData('audit_logs', [], authReady, currentUser, [], auditEnabled);
        const { data: permissionProfiles, saveItem: savePermissionProfile, deleteItem: deletePermissionProfile } = useFirestoreData('permissionProfiles', [], authReady, currentUser);
        const { data: teamPerception, saveItem: saveTeamPerception, deleteItem: deleteTeamPerception } = useFirestoreData('teamPerception', [], authReady, currentUser, [], perceptionEnabled);
        const { data: perceptionCycles, saveItem: savePerceptionCycle, deleteItem: deletePerceptionCycle } = useFirestoreData('perceptionCycles', [], authReady, currentUser, [], perceptionEnabled);
        const { data: perceptionQuestions, saveItem: savePerceptionQuestion, deleteItem: deletePerceptionQuestion } = useFirestoreData('perceptionQuestions', [], authReady, currentUser, [], perceptionEnabled);
        const { data: deadlines, saveItem: saveDeadline, deleteItem: deleteDeadline } = useFirestoreData('deadlines', [], authReady, currentUser);
        const { data: competencias, saveItem: saveCompetencia, deleteItem: deleteCompetencia } = useFirestoreData('competenciasMap', [], authReady, currentUser);
        const { data: confTypes, saveItem: saveConfType, deleteItem: deleteConfType } = useFirestoreData('conferenciaTypes', [], authReady, currentUser);    
        const { data: surveyList, saveItem: saveSurvey, deleteItem: deleteSurvey } = useFirestoreData('surveys', [], authReady, currentUser, [], surveysEnabled);
        const { data: surveyResponses, saveItem: saveSurveyResponse, deleteItem: deleteSurveyResponse } = useFirestoreData('surveyResponses', [], authReady, currentUser, [], surveysEnabled);
        const { data: recruitmentTests, saveItem: saveRecruitmentTest, deleteItem: deleteRecruitmentTest } = useFirestoreData('recruitmentTests', [], authReady, currentUser, [], recruitmentEnabled);
const { data: resumes, saveItem: saveResume, deleteItem: deleteResume } = useFirestoreData('resumes', SEED_RH.resumes, authReady, currentUser, [], rhEnabled);
        const { data: defaultCoinHistory, saveItem: saveDefaultCoin, deleteItem: deleteDefaultCoin } = useFirestoreData('defaultCoinHistory', [], authReady, currentUser, [], acadCoinsEnabled);
const { data: recognitions, saveItem: saveRecognition, deleteItem: deleteRecognition } = useFirestoreData('recognitions', [], authReady, currentUser, [], recognitionEnabled);
        const activePermissions = activePermissionsList[0] || {};
        const currencyName = acadCoinsConfig[0]?.currencyName || 'AcadCoins';

        // ========================================================================
        // 3. PREPARAÇÃO DE DADOS (DATA) - CRÍTICO: DEVE ESTAR AQUI ANTES DE SER USADO
        // ========================================================================
        const data = useMemo(() => ({
            evaluations: evals,
            collaborators: collabs,
            departments: depts,
            managers: mgrs,
            apontamentos: aps,
            qualiCriteria: qQuali,
            quantiCriteria: qQuanti,
            globalConfigs: gConf,
            evaluationTypes: eTypes,
            pdfMessages: msgs,
            pcs,
            minWageHistory,
            users,
            aiConfigs,
            activePermissions,
            gestaoMetasPercepcao,
            trainingData,
            trainingDeadlines,
            auditLogs,
            teamPerception,
            deadlines,
            competencias,
            perceptionCycles,
            conferenciaTypes: confTypes,
            permissionProfiles,
            perceptionQuestions,
            pontoConfig,
            pontoSituations,
            surveys: surveyList,          // <--- Adicionar
            surveyResponses,              // <--- Adicionar
            recruitmentTests,
            resumes, 
            recognitions,
            acadCoinsConfig, // <--- ADICIONADO AQUI: Injeta o banco do AcadCoins no sistema global
            // --------------------------------------------
            minWage: minWageHistory.length > 0 ? [...minWageHistory].sort((a, b) => new Date(b.validFrom) - new Date(a.validFrom))[0] : { value: 0 }
        }), [evals, collabs, depts, mgrs, aps, qQuali, qQuanti, gConf, eTypes, msgs, pcs, minWageHistory, users, aiConfigs, activePermissions, gestaoMetasPercepcao, trainingData, auditLogs, teamPerception, deadlines, competencias, confTypes, perceptionCycles, permissionProfiles, perceptionQuestions, pontoConfig, pontoSituations, surveyList, surveyResponses, recruitmentTests, resumes, trainingDeadlines, recognitions, acadCoinsConfig]); // <-- ADICIONADO NAS DEPENDÊNCIAS
        
        // ========================================================================
        // CÁLCULO DE PESQUISAS PENDENTES (COM REGRAS DE PRAZO E RECORRÊNCIA)
        // ========================================================================
        const pendingSurveysCount = useMemo(() => {
            if (!currentUser) return 0;
            const today = new Date();
            const todayStr = today.toISOString().split('T')[0];
            
            return (data.surveys || []).filter(s => {
                // 1. Deve estar ativa
                if (!s.isActive) return false;
                
                // 2. REGRA: Pesquisas sem prazo definido NÃO geram notificação
                if (!s.deadline) return false;
                
                // 3. Se tiver prazo, não pode estar vencida
                if (s.deadline < todayStr) return false;

                // 4. Verificação de Público Alvo (Se não for para mim, não conta)
                // A) Depto
                const myDeptId = currentUser.departmentId;
                const isDeptAllowed = (s.targetDepartments || []).length === 0 || (s.targetDepartments || []).includes(myDeptId);
                if (!isDeptAllowed) return false;
                // B) Tipo
                let isTypeAllowed = false;
                const myRole = currentUser.role; 
                const isManagerial = ['Gestor', 'Diretoria', 'RH', 'SuperAdm'].includes(myRole);
                if (s.targetAudienceType === 'all') isTypeAllowed = true;
                else if (s.targetAudienceType === 'management') isTypeAllowed = isManagerial;
                else if (s.targetAudienceType === 'contributors') isTypeAllowed = !isManagerial; 
                else if (s.targetAudienceType === 'custom') isTypeAllowed = (s.targetUsers || []).includes(currentUser.id);
                if (!isTypeAllowed) return false;

                // --- LÓGICA DE RESPOSTA E RECORRÊNCIA ---
                
                // Busca minhas respostas para esta pesquisa
                const myResponses = (data.surveyResponses || []).filter(r => r.surveyId === s.id && r.userId === currentUser.id);
                const hasResponded = myResponses.length > 0;

                // Se nunca respondeu: É pendente (pois já passou pelo filtro de ter prazo)
                if (!hasResponded) return true;

                // Se já respondeu:
                
                // Se NÃO tem recorrência e NÃO permite múltiplas -> Não é pendente
                if (s.recurrenceType === 'none' && !s.allowMultiple) return false;

                // Se permite múltiplas sem recorrência (livre) -> Não gera notificação constante (opção de design: para não poluir o menu)
                if (s.recurrenceType === 'none' && s.allowMultiple) return false;

                // Se TEM recorrência: Verifica se já passou o tempo
                if (s.recurrenceType !== 'none') {
                    // Pega a data da última resposta
                    const lastResponse = myResponses.sort((a,b) => new Date(b.date) - new Date(a.date))[0];
                    const lastDate = new Date(lastResponse.date);
                    
                    // Calcula intervalo em dias
                    const interval = s.recurrenceType === 'custom' ? (parseInt(s.customInterval) || 30) : parseInt(s.recurrenceType);
                    
                    // Calcula data da próxima disponibilidade
                    const nextAvailableDate = new Date(lastDate);
                    nextAvailableDate.setDate(lastDate.getDate() + interval);

                    // Se hoje já passou da data disponível -> É pendente novamente
                    if (today >= nextAvailableDate) return true;
                    
                    // Caso contrário (ainda no intervalo de espera) -> Não é pendente
                    return false;
                }

                return false;
            }).length;
        }, [data.surveys, data.surveyResponses, currentUser]);

        // --- NOVO: CÁLCULO DE LEMBRETES VENCIDOS PARA O RH ---
        const pendingHRRemindersCount = useMemo(() => {
            if (!currentUser) return 0;
            const isManagerial = ['Gestor', 'Diretoria', 'RH', 'SuperAdm'].includes(currentUser.role);
            if (!isManagerial && !currentUser.permissions?.access_rh_surveys) return 0;

            const today = new Date().toISOString().split('T')[0];
            let count = 0;
            
            (data.surveyResponses || []).forEach(r => {
                if (r.followUpCompleted) return; // Ignora se já concluiu
                if (r.followUpReminders && r.followUpReminders.length > 0) {
                    // Se algum lembrete for menor ou igual a hoje
                    if (r.followUpReminders.some(d => d && d <= today)) {
                        count++;
                    }
                }
            });
            return count;
        }, [data.surveyResponses, currentUser]);

        // --- NOVO: CÁLCULO DE ELOGIOS RECENTES E FESTIVOS ---
        const recognitionNotifications = useMemo(() => {
            if (!data.recognitions || !currentUser) return { total: 0, isForMe: false };
            
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            
            let totalCount = 0;
            let hasOneForMe = false;
            
            data.recognitions.forEach(rec => {
                if (rec.archived) return;
                const recDate = new Date(rec.createdAt);
                if (recDate < sevenDaysAgo) return;

                // Compatibilidade com array de IDs
                const tIds = rec.targetIds || [rec.targetId];

                let canView = false;
                if (rec.visibility === 'public') canView = true;
                else if (rec.visibility === 'private') canView = currentUser.id === rec.authorId || tIds.includes(currentUser.id);
                else if (rec.visibility === 'custom') canView = currentUser.id === rec.authorId || tIds.includes(currentUser.id) || (rec.viewers || []).includes(currentUser.id);
                
                if (canView) {
                    totalCount++;
                    // Verifica se este elogio NOVO foi direcionado diretamente a este usuário
                    if (tIds.includes(currentUser.id)) {
                        hasOneForMe = true;
                    }
                }
            });

            return { total: totalCount, isForMe: hasOneForMe };
        }, [data.recognitions, currentUser]);

        const bellLoggedCollabId = useMemo(() => {
        if (!currentUser || !data?.collaborators) return null;

        const userEmail = (currentUser.email || '').trim().toLowerCase();
        const found = data.collaborators.find(c => c.email?.trim().toLowerCase() === userEmail);

        return found ? found.id : null;
    }, [currentUser, data?.collaborators]);
    
        const acadCoinsGiftReceivedCount = useMemo(() => {
            if (!bellLoggedCollabId) return 0;

            return (acadCoinsDistBell || []).filter(d =>
                d?.collabId === bellLoggedCollabId &&
                d?.type === 'presente_recebido' &&
                parseFloat(d?.quantity || 0) > 0
            ).length;
        }, [acadCoinsDistBell, bellLoggedCollabId]);

        const acadCoinsDonationReceivedCount = useMemo(() => {
            if (!bellLoggedCollabId) return 0;

            return (acadCoinsDistBell || []).filter(d =>
                d?.collabId === bellLoggedCollabId &&
                d?.type === 'doacao' &&
                parseFloat(d?.quantity || 0) > 0
            ).length;
        }, [acadCoinsDistBell, bellLoggedCollabId]);

        const acadCoinsRecognitionReceivedCount = useMemo(() => {
            if (!bellLoggedCollabId) return 0;

            return (acadCoinsDistBell || []).filter(d =>
                d?.collabId === bellLoggedCollabId &&
                d?.type === 'indicacao'
            ).length;
        }, [acadCoinsDistBell, bellLoggedCollabId]);

        const acadCoinsIdeaApprovedCount = useMemo(() => {
            if (!bellLoggedCollabId) return 0;

            return (acadCoinsIdeasBell || []).filter(i =>
                i?.collabId === bellLoggedCollabId &&
                i?.status === 'Aprovada' &&
                !i?.archived
            ).length;
        }, [acadCoinsIdeasBell, bellLoggedCollabId]);

        const acadCoinsRaffleWinnerCount = useMemo(() => {
            if (!bellLoggedCollabId) return 0;

            return (acadCoinsPrizesBell || []).filter(p =>
                p?.lastWinnerId === bellLoggedCollabId &&
                !!p?.lastDrawId
            ).length;
        }, [acadCoinsPrizesBell, bellLoggedCollabId]);

        const acadCoinsOtherPositiveCreditsCount = useMemo(() => {
            if (!bellLoggedCollabId) return 0;

            const excludedTypes = [
                'presente_recebido',
                'doacao',
                'indicacao',
                'premiacao_ideia'
            ];

            return (acadCoinsDistBell || []).filter(d =>
                d?.collabId === bellLoggedCollabId &&
                parseFloat(d?.quantity || 0) > 0 &&
                !excludedTypes.includes(d?.type)
            ).length;
        }, [acadCoinsDistBell, bellLoggedCollabId]);

        const acadCoinsPendingTrainingApprovalItems = useMemo(() => {
            const currentUserId = String(currentUser?.id || '');
            const currentUserEmail = String(currentUser?.email || '').trim().toLowerCase();

            const collabList = data?.collaborators || [];
            const managerList = data?.managers || [];

            const myCollab = collabList.find(c =>
                String(c.email || '').trim().toLowerCase() === currentUserEmail
            );

            const myManager = managerList.find(m =>
                String(m.email || '').trim().toLowerCase() === currentUserEmail
            );

            return (acadCoinsDistBell || []).filter(d => {
                if (d?.type !== 'treinamento') return false;
                if (d?.approvalStatus !== 'pending') return false;

                if (currentUser?.role === 'SuperAdm') return true;
                if (currentUser?.permissions?.acad_coins_admin === true) return true;

                const targetUserIds = Array.isArray(d.approvalTargetUserIds)
                    ? d.approvalTargetUserIds.map(String)
                    : [];

                const targetUserEmails = Array.isArray(d.approvalTargetUserEmails)
                    ? d.approvalTargetUserEmails.map(e => String(e || '').trim().toLowerCase())
                    : [];

                if (currentUserId && targetUserIds.includes(currentUserId)) return true;
                if (currentUserEmail && targetUserEmails.includes(currentUserEmail)) return true;

                // fallback para registros antigos, sem approvalTargetUserIds/emails
                const targetCollab = collabList.find(c =>
                    String(c.id) === String(d.collabId)
                );

                const managerIds = Array.isArray(targetCollab?.managerIds)
                    ? targetCollab.managerIds.map(String)
                    : [];

                if (myCollab && managerIds.includes(String(myCollab.id))) return true;
                if (myManager && managerIds.includes(String(myManager.id))) return true;

                return false;
            });
        }, [acadCoinsDistBell, currentUser, data?.collaborators, data?.managers]);

        const acadCoinsPendingTrainingApprovalCount = acadCoinsPendingTrainingApprovalItems.length;

        const acadCoinsPendingTrainingApprovalKey = useMemo(() => {
    return acadCoinsPendingTrainingApprovalItems
        .map(d => String(d.id))
        .sort()
        .join('|');
}, [acadCoinsPendingTrainingApprovalItems]);

        const acadCoinsPendingTrainingApprovalNotifications = useMemo(() => {
            const grouped = {};

            (acadCoinsPendingTrainingApprovalItems || []).forEach(item => {
                const collab = (data?.collaborators || []).find(c =>
                    String(c.id) === String(item.collabId)
                );

                const fullName = String(collab?.name || item.trainingCollaboratorName || 'Colaborador').trim();
                const nameParts = fullName.split(/\s+/).filter(Boolean);

                const shortName = nameParts.length > 1
                    ? `${nameParts[0]} ${nameParts[1]}`
                    : fullName;

                const key = String(item.collabId || item.id || 'sem-colab');

                if (!grouped[key]) {
                    grouped[key] = {
                        id: `acadcoins-training-approval-pending-${key}`,
                        title: '',
                        description: 'Abrir Central Colaborador > AcadCoins > Minha Carteira.',
                        count: 0,
                        targetSys: 'collab_center:acad_coins',
                        openMenuId: 'acad_coins',
                        moduleLabel: 'Central Colaborador',
                        shortName
                    };
                }

                grouped[key].count += 1;
            });

            return Object.values(grouped).map(n => ({
                ...n,
                title: `${n.count} curso(s) pendente(s) de aprovação - ${n.shortName}`
            }));
        }, [acadCoinsPendingTrainingApprovalItems, data?.collaborators]);

const acadCoinsNotificationsTotal =
    acadCoinsGiftReceivedCount +
    acadCoinsDonationReceivedCount +
    acadCoinsRecognitionReceivedCount +
    acadCoinsIdeaApprovedCount +
    acadCoinsRaffleWinnerCount +
    acadCoinsOtherPositiveCreditsCount +
    acadCoinsPendingTrainingApprovalItems.length;

        // ========================================================================
        // 4. LÓGICA DO COLABORADOR (DEPENDE DE DATA.COLLABORATORS)
        // ========================================================================

        // 1. Vínculo de ID
        const loggedCollabId = useMemo(() => {
            if (!currentUser || !collabs) return null;
            const userEmail = (currentUser.email || '').trim().toLowerCase();
            const found = collabs.find(c => {
                const cEmail = (c.email || '').trim().toLowerCase();
                return cEmail === userEmail;
            });
            if (!found) {
                console.warn(`[APP] Vínculo falhou. Login: "${userEmail}" não encontrado em Collaborators.`);
                return null;
            }
            return found.id;
        }, [currentUser, collabs]);

        // 2. Filtro de Treinamentos - Minha Evolução
        // Exibe TODOS os cursos do colaborador no ano selecionado, sem depender do trimestre estar fechado.
        // O vínculo usa preferencialmente o e-mail cadastrado no colaborador para não depender de diferenças no usuário logado.
        const collaboratorTrainings = useMemo(() => {
            if (!trainingData) return [];

            const linkedCollab = (collabs || []).find(c => String(c.id) === String(loggedCollabId));
            const targetEmail = String(linkedCollab?.email || currentUser?.email || '').trim().toLowerCase();
            if (!targetEmail) return [];

            const parseTrainingDate = (value) => {
                const raw = String(value || '').trim();
                if (!raw) return null;
                const br = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                if (br) return new Date(Number(br[3]), Number(br[2]) - 1, Number(br[1]), 12, 0, 0);
                const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
                if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]), 12, 0, 0);
                const d = new Date(raw);
                return Number.isNaN(d.getTime()) ? null : d;
            };

            return trainingData
                .filter(t => {
                    const trainingEmail = String(t?.email || '').trim().toLowerCase();
                    if (trainingEmail !== targetEmail) return false;

                    const explicitYear = String(t?.year || '').trim();
                    if (explicitYear) return explicitYear === String(collabViewYear);

                    const parsed = parseTrainingDate(t?.date);
                    return parsed ? String(parsed.getFullYear()) === String(collabViewYear) : false;
                })
                .sort((a, b) => {
                    const da = parseTrainingDate(a?.date);
                    const db = parseTrainingDate(b?.date);
                    return (db?.getTime() || 0) - (da?.getTime() || 0);
                });
        }, [currentUser, trainingData, collabViewYear, collabs, loggedCollabId]);

        // 3. Stats de Horas
        const trainingStatsDisplay = useMemo(() => {
            let d1 = 0, d2 = 0;
            collaboratorTrainings.forEach(t => {
                const typeLower = (t.type || '').toLowerCase();
                const h = parseFloat(t.hours) || 0;
                if (typeLower.includes('1') || typeLower.includes('atuação') || typeLower.includes('atuacao')) {
                    d1 += h;
                } else {
                    d2 += h;
                }
            });
            return { d1, d2, total: d1 + d2 };
        }, [collaboratorTrainings]);

        // 4. Timeline
        const timeline = useMemo(() => {
            const quarters = [];
            let y = parseInt(collabViewYear);
            let q = 4;
            for (let i = 0; i < 8; i++) {
                quarters.push({
                    id: `${y}-q${q}`,
                    label: `${y} (${q}ºT)`,
                    year: String(y),
                    qKey: `q${q}`
                });
                q--;
                if (q < 1) {
                    q = 4;
                    y--;
                }
            }
            return quarters;
        }, [collabViewYear]);

        // Em AvaliacaoSystem -> useMemo avDesFeedbacksYear

                // Feedbacks visíveis ao colaborador em Minha Evolução.
                // Mantém a mesma regra do relatório: AvDes (RH), considerações do colaborador
                // e feedbacks POSITIVOS da Gestão, somente até o último trimestre assinado.
                const avDesFeedbacksYear = useMemo(() => {
                    if (!loggedCollabId) return [];

                    const targetEval = evals.find(e =>
                        String(e.collaboratorId) === String(loggedCollabId) &&
                        String(e.periodYear) === String(collabViewYear)
                    );
                    if (!targetEval || !targetEval.feedbacksHistory) return [];

                    const signatures = Array.isArray(targetEval.managerSignatures)
                        ? targetEval.managerSignatures
                        : (targetEval.managerSignature ? [targetEval.managerSignature] : []);

                    const signedQuarterNumber = (sig) => {
                        const ref = String(sig?.statusRef || sig?.quarter || sig?.status || '').toUpperCase();
                        if (ref.includes('T4') || ref.includes('4º') || ref.includes('FINALIZADA') || ref.includes('ASSINADA')) return 4;
                        if (ref.includes('T3') || ref.includes('3º')) return 3;
                        if (ref.includes('T2') || ref.includes('2º')) return 2;
                        if (ref.includes('T1') || ref.includes('1º')) return 1;
                        return 0;
                    };

                    let maxSignedQuarter = signatures.reduce((maxQ, sig) => Math.max(maxQ, signedQuarterNumber(sig)), 0);
                    if (targetEval.status === 'Concluída Financeiro') maxSignedQuarter = 4;

                    const feedbackQuarterNumber = (f) => {
                        const ref = String(f?.quarter || '').toUpperCase();
                        if (ref.includes('Q4') || ref.includes('T4') || ref.includes('4º')) return 4;
                        if (ref.includes('Q3') || ref.includes('T3') || ref.includes('3º')) return 3;
                        if (ref.includes('Q2') || ref.includes('T2') || ref.includes('2º')) return 2;
                        if (ref.includes('Q1') || ref.includes('T1') || ref.includes('1º')) return 1;

                        const rawDate = f?.date || f?.timestamp;
                        if (!rawDate) return 0;
                        const d = f?.date && /^\d{4}-\d{2}-\d{2}$/.test(String(f.date))
                            ? new Date(`${f.date}T12:00:00`)
                            : new Date(rawDate);
                        return Number.isNaN(d.getTime()) ? 0 : Math.floor(d.getMonth() / 3) + 1;
                    };

                    return Object.values(targetEval.feedbacksHistory)
                        .filter(f => {
                            const qNum = feedbackQuarterNumber(f);
                            const released = qNum > 0 && qNum <= maxSignedQuarter;
                            const baseVisible = f?.type === 'AvDes' || f?.type === 'Colaborador';
                            const positiveManager = f?.type === 'Gestão' && String(f?.nature || '').trim().toLowerCase() === 'positivo';
                            return released && (baseVisible || positiveManager);
                        })
                        .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
                }, [evals, loggedCollabId, collabViewYear]);

        // --- GERAÇÃO DE PDF COMPLETA (VERSÃO FINAL EXPANDIDA) ---
        const generatePDF = useCallback(async (evParam, typeParam) => {
            // 1. Definição do Alvo
            const evRaw = evParam || selectedEval;
            const type = typeParam || 'Completo';

            // Validação inicial
            if (!evRaw) {
                return;
            }

            setIsGenerating(true);

            try {
                // ===========================================================
                // 1. LÓGICA DE FILTRAGEM DE DADOS (SEGURANÇA / HIGIENE)
                // ===========================================================
                
                // Cria uma cópia profunda para não alterar a visualização na tela
                const ev = JSON.parse(JSON.stringify(evRaw));

                // Determina até qual trimestre está OFICIALMENTE LIBERADO no PDF.
                // Regra de segurança: trimestre com dado lançado, mas ainda sem assinatura, NÃO aparece.
                // Isso evita que ocorrências/notas do trimestre seguinte vazem antes do fechamento.
                const mgrSigs = Array.isArray(ev.managerSignatures)
                    ? ev.managerSignatures
                    : (ev.managerSignature ? [ev.managerSignature] : []);

                const getSignedQuarterNumber = (sig) => {
                    const ref = String(sig?.statusRef || sig?.quarter || sig?.status || '').toUpperCase();
                    if (ref.includes('T4') || ref.includes('4º') || ref.includes('FINALIZADA') || ref.includes('ASSINADA')) return 4;
                    if (ref.includes('T3') || ref.includes('3º')) return 3;
                    if (ref.includes('T2') || ref.includes('2º')) return 2;
                    if (ref.includes('T1') || ref.includes('1º')) return 1;
                    return 0;
                };

                let maxAllowedQ = mgrSigs.reduce((maxQ, sig) => Math.max(maxQ, getSignedQuarterNumber(sig)), 0);

                // Arquivamento financeiro pressupõe ciclo integralmente concluído.
                if (ev.status === 'Concluída Financeiro') maxAllowedQ = 4;

                const getQuarterNumberFromValue = (quarterValue, dateValue, timestampValue) => {
                    const raw = String(quarterValue || '').toUpperCase();
                    if (raw.includes('Q4') || raw.includes('T4') || raw.includes('4º')) return 4;
                    if (raw.includes('Q3') || raw.includes('T3') || raw.includes('3º')) return 3;
                    if (raw.includes('Q2') || raw.includes('T2') || raw.includes('2º')) return 2;
                    if (raw.includes('Q1') || raw.includes('T1') || raw.includes('1º')) return 1;

                    let d = null;
                    if (dateValue) d = new Date(String(dateValue).length === 10 ? `${dateValue}T12:00:00` : dateValue);
                    else if (timestampValue) d = new Date(timestampValue);
                    if (!d || Number.isNaN(d.getTime())) return 0;
                    return Math.floor(d.getMonth() / 3) + 1;
                };

                const formatPdfGrade = (value) => {
                    if (value === null || value === undefined || value === '') return '-';

                    const num = Number(value);
                    if (!isNaN(num)) return num.toFixed(1);

                    return String(value);
                };

                const formatPdfCount = (value) => {
                    if (value === null || value === undefined || value === '') return '-';

                    const num = Number(value);
                    if (!isNaN(num)) return String(num);

                    return String(value);
                };

                const getQuarterWeightedPoints = (error, q) => {
                    const qtd = Number(error?.[q] || 0);
                    const peso = Number(error?.peso || 0);
                    return qtd * peso;
                };

                // Aplica a limpeza dos dados futuros (não assinados)
                if (maxAllowedQ < 4) {
                    // Limpa Critérios Qualitativos
                    if (ev.qualitative && ev.qualitative.criteria) {
                        ev.qualitative.criteria.forEach(c => {
                            if (maxAllowedQ < 1) {
                                c.q1 = null;
                            }
                            if (maxAllowedQ < 2) {
                                c.q2 = null;
                            }
                            if (maxAllowedQ < 3) {
                                c.q3 = null;
                            }
                            if (maxAllowedQ < 4) {
                                c.q4 = null;
                            }
                        });
                    }
                    
                    // Limpa Erros Quantitativos
                    if (ev.quantitative && ev.quantitative.errors) {
                        ev.quantitative.errors.forEach(e => {
                            if (maxAllowedQ < 1) {
                                e.q1 = 0;
                            }
                            if (maxAllowedQ < 2) {
                                e.q2 = 0;
                            }
                            if (maxAllowedQ < 3) {
                                e.q3 = 0;
                            }
                            if (maxAllowedQ < 4) {
                                e.q4 = 0;
                            }
                        });
                    }
                    
                    // Limpa Metas de Gestão
                    if (ev.gestaoMetasPercepcao) {
                        ev.gestaoMetasPercepcao.forEach(item => {
                            if (item.scores) {
                                if (maxAllowedQ < 1) {
                                    item.scores.q1 = null;
                                }
                                if (maxAllowedQ < 2) {
                                    item.scores.q2 = null;
                                }
                                if (maxAllowedQ < 3) {
                                    item.scores.q3 = null;
                                }
                                if (maxAllowedQ < 4) {
                                    item.scores.q4 = null;
                                }
                            }
                        });
                    }
                }

                // Recalcula as notas finais baseadas APENAS nos dados filtrados
                const evWithScores = recalculateFinalAverage(ev);
                const isGestao = ev.personType === 'Gestão';

                // ===========================================================
                // 2. INICIALIZAÇÃO DO PDF
                // ===========================================================

                if (typeof loadPdfLibs !== 'undefined') {
                    await loadPdfLibs();
                } else if (!window.jspdf) {
                    throw new Error("Biblioteca PDF não carregada. Verifique sua conexão.");
                }

                const PDFConstructor = (window.jspdf && window.jspdf.jsPDF) ? window.jspdf.jsPDF : window.jsPDF;
                const doc = new PDFConstructor('p', 'mm', 'a4');
                
                const margin = 14;
                let y = 20;

                // Dados Auxiliares para Cabeçalho
                const collabData = data.collaborators.find(c => c.id === ev.collaboratorId);
                
                let realDeptName = ev.department;
                if (collabData) {
                    const deptObj = data.departments.find(d => d.id === collabData.departmentId);
                    if (deptObj) {
                        realDeptName = deptObj.name;
                    }
                }

                const linkedManagers = data.managers.filter(m => {
                    return collabData && collabData.managerIds && collabData.managerIds.includes(m.id);
                });
                
                const managerNameFallback = linkedManagers.length > 0 
                    ? linkedManagers.map(m => m.name).join(' / ') 
                    : 'Gestor(a) Responsável';

                // ===========================================================
                // 3. HELPERS DE DESENHO (FUNÇÕES INTERNAS)
                // ===========================================================

                // Helper: Tabela de Percepção (Página 2)
                const drawPerceptionSection = (startYPosition) => {
                    const perceptionCols = (data.perceptionCycles || []).sort((a, b) => {
                        return (a.order || 0) - (b.order || 0);
                    });
                    
                    if (perceptionCols.length === 0) {
                        return startYPosition;
                    }

                    const targetEmail = collabData ? collabData.email.toLowerCase() : '';
                    
                    const rawRecords = (data.teamPerception || []).filter(p => {
                        const pManagerEmail = p.manager_email ? p.manager_email.toLowerCase() : '';
                        const hasDate = p.date && p.date.endsWith(ev.periodYear);
                        return pManagerEmail === targetEmail && hasDate;
                    });

                    if (rawRecords.length === 0) {
                        return startYPosition;
                    }

                    const tableBody = rawRecords.map(rec => {
                        const row = [rec.date, String(rec.nota || '-')];
                        perceptionCols.forEach(col => {
                            const val = rec[col.linkedField || 'nota'] || '-';
                            row.push(String(val));
                        });
                        return row;
                    });

                    if (startYPosition + 40 > doc.internal.pageSize.getHeight()) { 
                        doc.addPage('a4', 'l'); 
                        startYPosition = 20; 
                    }

                    doc.setFontSize(12); 
                    doc.setTextColor(230, 81, 0); 
                    doc.setFont('helvetica', 'bold');
                    doc.text("DETALHAMENTO: PERCEPÇÃO DA EQUIPE", 14, startYPosition);
                    startYPosition += 6;

                    const headers = ["DATA", "NOTA", ...perceptionCols.map(c => c.description)];

                    doc.autoTable({
                    startY: startYPosition,
                    head: [headers],
                    body: tableBody,
                    theme: 'grid',
                    styles: { 
                        fontSize: 7, 
                        cellPadding: 2, 
                        overflow: 'linebreak',
                        valign: 'middle' // Garante que o texto fique no meio verticalmente
                    },
                    headStyles: { 
                        fillColor: [239, 108, 0], 
                        textColor: [255, 255, 255], 
                        halign: 'center', 
                        fontStyle: 'bold' 
                    },
                    columnStyles: {
                        1: { halign: 'center', fontStyle: 'bold' }, // Coluna 1 (NOTA) centralizada
                    },
                    didParseCell: (data) => {
                    // Aplicamos a lógica em todas as colunas de dados da tabela (seção body)
                    if (data.section === 'body') {
                        const cellValue = String(data.cell.raw).trim();

                        // Mapeamento completo de cores para garantir que todos os campos pintem
                        if (cellValue.includes('Insatisfatório')) {
                            data.cell.styles.fillColor = [244, 67, 54]; // Vermelho
                            data.cell.styles.textColor = [255, 255, 255]; // Letra Branca
                        } else if (cellValue.includes('Regular')) {
                            data.cell.styles.fillColor = [230, 81, 0]; // Laranja
                            data.cell.styles.textColor = [255, 255, 255];
                        } else if (cellValue.includes('Bom') && !cellValue.includes('Muito Bom')) {
                            data.cell.styles.fillColor = [245, 127, 23]; // Amarelo Escuro
                            data.cell.styles.textColor = [255, 255, 255];
                        } else if (cellValue.includes('Muito Bom')) {
                            data.cell.styles.fillColor = [46, 125, 50]; // Verde
                            data.cell.styles.textColor = [255, 255, 255];
                        } else if (cellValue.includes('Excelente')) {
                            data.cell.styles.fillColor = [21, 101, 192]; // Azul
                            data.cell.styles.textColor = [255, 255, 255];
                        }

                        // Centralizar apenas a coluna da NOTA (índice 1)
                        if (data.column.index === 1) {
                            data.cell.styles.halign = 'center';
                        }
                    }
                }
                });
                    return doc.lastAutoTable.finalY + 10;
                };

        // Helper: Quadro de Assinaturas (CORRIGIDO: INVALID DATE + LINHAS EXTRAS)
                const drawSignatureTable = (startYPosition) => {
                    const year = evRaw.periodYear; 
                    
                    const headers = [
                        "TIPO ASSINATURA", 
                        `1º TRIM/${year}`, 
                        `2º TRIM/${year}`, 
                        `3º TRIM/${year}`, 
                        `4º TRIM/${year}`
                    ];

                    const formatSigCell = (sigData) => {
                        if (!sigData) {
                            return "Aguardando assinatura...";
                        }
                        
                        let dateStr = '-';
                        
                        // --- CORREÇÃO DE DATA (INVALID DATE) ---
                        // 1. Se já vier como string formatada BR (ex: "25/10/2023, 14:00")
                        if (sigData.date && typeof sigData.date === 'string' && sigData.date.includes('/')) {
                            // Pega apenas a parte da data, ignorando a hora após a vírgula ou espaço
                            dateStr = sigData.date.split(',')[0].split(' ')[0].trim();
                        } 
                        // 2. Se for ISO (YYYY-MM-DD) ou Timestamp numérico
                        else if (sigData.date) {
                            try {
                                const d = new Date(sigData.date);
                                // Verifica se a data é válida
                                if (!isNaN(d.getTime())) {
                                    dateStr = d.toLocaleDateString('pt-BR');
                                } else {
                                    dateStr = String(sigData.date); // Fallback: mostra como está
                                }
                            } catch (e) {
                                dateStr = '-';
                            }
                        }

                        return `Assinado em: ${dateStr}\n\n${sigData.name}\nID: ${sigData.id || 'N/D'}`;
                    };

                    // 1. Linha GESTOR
                    const mgrRow = ["ASSINATURA\nGESTOR"];
                    let mgrSigs = [];
                    if (evRaw.managerSignatures) {
                        mgrSigs = evRaw.managerSignatures;
                    } else if (evRaw.managerSignature) {
                        mgrSigs = [evRaw.managerSignature];
                    }
                    
                    const quartersMap = [['T1', '1º'], ['T2', '2º'], ['T3', '3º'], ['T4', '4º', 'FINALIZADA', 'ASSINADA']];

                    quartersMap.forEach(qTags => {
                        const signaturesFound = mgrSigs.filter(s => {
                            const ref = (s.statusRef || '').toUpperCase();
                            return qTags.some(tag => ref.includes(tag));
                        });

                        if (signaturesFound.length > 0) {
                            const textContent = signaturesFound.map(sig => 
                                // Passa o objeto completo para o formatador corrigido
                                formatSigCell(sig).replace('Assinado em: ', '') // Pequeno ajuste para evitar duplicar texto se necessário, ou usar direto
                            ).join('\n----------------\n');
                            
                            // Reconstrói a string formatada usando o helper (mais seguro)
                            // Como pode haver múltiplas, pegamos a mais recente ou formatamos individualmente
                            const formattedSigs = signaturesFound.map(sig => {
                                // Extração manual da data corrigida para este loop específico
                                let dStr = '-';
                                if (sig.date && typeof sig.date === 'string' && sig.date.includes('/')) dStr = sig.date.split(',')[0].split(' ')[0].trim();
                                else if (sig.date) try { dStr = new Date(sig.date).toLocaleDateString('pt-BR'); } catch(e){}
                                
                                return `Assinado em: ${dStr}\n${sig.gestorName || sig.name}\n(ID: ${sig.gestorId || sig.id})`;
                            }).join('\n----\n');

                            mgrRow.push(formattedSigs);
                        } else {
                            mgrRow.push("Aguardando assinatura\nGestor");
                        }
                    });

                    // 2. Linha COLABORADOR
                    const colRow = ["ASSINATURA\nCOLABORADOR"];
                    const colSigs = evRaw.collabSignatures || {};
                    
                    ['q1', 'q2', 'q3', 'q4'].forEach(qKey => {
                        const sig = colSigs[qKey];
                        if (sig) {
                            // Garante email
                            let emailDisplay = sig.email;
                            if (!emailDisplay) {
                                const cRegister = data.collaborators.find(c => c.id === ev.collaboratorId);
                                if (cRegister) emailDisplay = cRegister.email;
                            }
                            colRow.push(formatSigCell({
                                date: sig.date,
                                name: sig.name || ev.collaboratorName, 
                                id: sig.id
                            }));
                        } else {
                            colRow.push("Aguardando assinatura\nColaborador");
                        }
                    });

                    // --- 3. Linha DIRETORIA ---
                    const dirSig = evRaw.diretoriaSignature;
                    const dirRow = [
                        "ASSINATURA\nDIRETORIA",
                        "-", "-", "-", // T1-T3
                        dirSig ? formatSigCell(dirSig) : "Aguardando Validação" // T4
                    ];

                    // --- 4. Linha RH ---
                    const rhSig = evRaw.rhSignature;
                    const rhRow = [
                        "ASSINATURA\nRH",
                        "-", "-", "-", // T1-T3
                        rhSig ? formatSigCell(rhSig) : "Aguardando Validação" // T4
                    ];

                    // Montagem do Corpo
                    const tableBody = [mgrRow, colRow];
                    if (evRaw.personType !== 'Gestão') {
                        tableBody.push(dirRow);
                    }
                    tableBody.push(rhRow);

                    if (startYPosition + 70 > doc.internal.pageSize.getHeight()) {
                        doc.addPage();
                        startYPosition = 20;
                    }

                    doc.autoTable({
                        startY: startYPosition,
                        head: [headers],
                        body: tableBody,
                        theme: 'grid',
                        styles: { 
                            fontSize: 6, 
                            cellPadding: 2, 
                            valign: 'middle', 
                            halign: 'center', 
                            lineWidth: 0.1, 
                            lineColor: [200, 200, 200],
                            overflow: 'linebreak' 
                        },
                        headStyles: { 
                            fillColor: [30, 58, 138], 
                            textColor: [255, 255, 255], 
                            fontStyle: 'bold', 
                            halign: 'center',
                            minCellHeight: 8
                        },
                        columnStyles: {
                            0: { fontStyle: 'bold', fillColor: [243, 244, 246], cellWidth: 30, valign: 'middle' }, 
                            1: { cellWidth: 40 }, 
                            2: { cellWidth: 40 }, 
                            3: { cellWidth: 40 }, 
                            4: { cellWidth: 40 }
                        },
                        didParseCell: (data) => {
                            if (data.section === 'body') {
                                if (data.column.index > 0 && typeof data.cell.raw === 'string' && data.cell.raw.includes("Aguardando")) {
                                    data.cell.styles.textColor = [150, 150, 150];
                                    data.cell.styles.fontStyle = 'italic';
                                }
                                if (data.row.index >= 2 && data.column.index === 0) { 
                                    data.cell.styles.fillColor = [230, 230, 230];
                                }
                            }
                        }
                    });

                    return doc.lastAutoTable.finalY + 10;
                };

                // ===========================================================
                // 4. CONSTRUÇÃO DO CORPO DO PDF
                // ===========================================================

                // Cabeçalho e Logo
                const logoUrl = data.globalConfigs && data.globalConfigs[0] ? data.globalConfigs[0].logoUrl : null;
                
                if (logoUrl) {
                    try {
                        doc.addImage(logoUrl, 'PNG', 14, 10, 25, 25);
                        doc.setFontSize(11); 
                        doc.setFont("helvetica", "bold"); 
                        doc.text("AEC - ACOMPANHAMENTO DE EVOLUÇÃO CONTINUADA", 105, 25, { align: "center" });
                        y = 45;
                    } catch (e) {
                        doc.setFillColor(18, 169, 149); 
                        doc.rect(0, 0, 210, 30, 'F'); 
                        doc.setTextColor(255, 255, 255); 
                        doc.setFontSize(11); 
                        doc.setFont("helvetica", "bold"); 
                        doc.text("AEC - ACOMPANHAMENTO DE EVOLUÇÃO CONTINUADA", margin, 20);
                        y = 40;
                    }
                } else {
                    doc.setFillColor(18, 169, 149); 
                    doc.rect(0, 0, 210, 30, 'F'); 
                    doc.setTextColor(255, 255, 255); 
                    doc.setFontSize(11); 
                    doc.setFont("helvetica", "bold"); 
                    doc.text("AEC - ACOMPANHAMENTO DE EVOLUÇÃO CONTINUADA", margin, 20);
                    y = 40;
                }

                // Tabela de Identificação
                const identificationData = [
                    ["Colaborador:", ev.collaboratorName, "Cargo:", ev.role || '-'],
                    ["Departamento:", realDeptName, "Gestor(es):", managerNameFallback],
                    ["Ano Avaliado:", ev.periodYear, "Tipo:", ev.evalTypeName || 'Padrão']
                ];

                doc.autoTable({
                    startY: y, 
                    body: identificationData, 
                    theme: 'grid',
                    styles: { 
                        fontSize: 9, 
                        cellPadding: 1.5, 
                        lineColor: [200, 200, 200], 
                        textColor: [0,0,0] 
                    },
                    columnStyles: { 
                        0: { fontStyle: 'bold', fillColor: [240, 240, 240], cellWidth: 30 }, 
                        1: { cellWidth: 70 }, 
                        2: { fontStyle: 'bold', fillColor: [240, 240, 240], cellWidth: 25 }, 
                        3: { cellWidth: 'auto' } 
                    }
                });
                
                y = doc.lastAutoTable.finalY + 10;

                // ===========================================================
                // 5. CONTEÚDO BASEADO NO TIPO (SIMPLES / COMPLETO)
                // ===========================================================
                
                // CASO A: SIMPLIFICADO GESTÃO
                if (type === 'Simplificado Gestão' && isGestao) {
                    doc.setFontSize(12); 
                    doc.setTextColor(0, 0, 0); 
                    doc.setFont('helvetica', 'bold'); 
                    doc.text("QUADRO RESUMO DE PONTUAÇÃO (GESTÃO)", margin, y); 
                    y += 6;

                    // Funções de resumo
                    const getQualiQ = (q) => { 
                        let sum = 0, count = 0; 
                        if (evWithScores.qualitative && evWithScores.qualitative.criteria) {
                            evWithScores.qualitative.criteria.forEach(c => {
                                if (c[q] !== null && c[q] !== '' && c[q] !== undefined) { 
                                    sum += parseFloat(c[q]); 
                                    count++; 
                                }
                            }); 
                        }
                        if (count === 0) return 0; 
                        return (sum / count) * 2.5; 
                    };

                    const getGestaoItemQ = (typeFilter, q) => { 
                        if (!evWithScores.gestaoMetasPercepcao) return 0; 
                        const items = evWithScores.gestaoMetasPercepcao.filter(i => {
                            if (typeFilter === 'Metas') {
                                return (i.type === 'Metas');
                            } else {
                                return (i.type === 'Percepção' || i.name.toLowerCase().includes('percepção'));
                            }
                        }); 
                        return items.reduce((acc, item) => {
                            const val = item.scores && item.scores[q] ? parseFloat(item.scores[q]) : 0;
                            return acc + val;
                        }, 0); 
                    };

                    const summaryData = [
                        ["1. Qualitativa (Máx: 40)", getQualiQ('q1').toFixed(2), getQualiQ('q2').toFixed(2), getQualiQ('q3').toFixed(2), getQualiQ('q4').toFixed(2), evWithScores.qualitative.finalScoreGestaoQuali?.toFixed(2), '#0b5394'],
                        ["2. Plan. Estratégico (Máx: 20)", getGestaoItemQ('Metas', 'q1').toFixed(2), getGestaoItemQ('Metas', 'q2').toFixed(2), getGestaoItemQ('Metas', 'q3').toFixed(2), getGestaoItemQ('Metas', 'q4').toFixed(2), evWithScores.totalGestaoMetas?.toFixed(2), '#ef4444'],
                        ["3. Percepção Equipe (Máx: 20)", getGestaoItemQ('Percepção', 'q1').toFixed(2), getGestaoItemQ('Percepção', 'q2').toFixed(2), getGestaoItemQ('Percepção', 'q3').toFixed(2), getGestaoItemQ('Percepção', 'q4').toFixed(2), evWithScores.totalGestaoPercepcao?.toFixed(2), '#f97316'],
                        ["4. Capacitações (Máx: 20)", "-", "-", "-", "-", evWithScores.atualizacao?.total?.toFixed(2), '#9333ea'],
                        [`Total Final`, "", "", "", "", evWithScores.finalScoreGestao?.toFixed(2), "TOTAL_ROW"]
                    ];

                    doc.autoTable({
                        startY: y, 
                        head: [['Componente', 'Trim 1', 'Trim 2', 'Trim 3', 'Trim 4', 'Total Obtido']],
                        body: summaryData.map(row => row.slice(0, 6)),
                        theme: 'grid',
                        headStyles: { halign: 'center', fillColor: [220, 220, 220], textColor: [0, 0, 0], fontStyle: 'bold' },
                        columnStyles: { 
                            0: { cellWidth: 60, fontStyle: 'bold' }, 
                            1: { halign: 'center' }, 
                            2: { halign: 'center' }, 
                            3: { halign: 'center' }, 
                            4: { halign: 'center' }, 
                            5: { halign: 'center', fontStyle: 'bold' } 
                        },
                        didParseCell: (data) => {
                            if (data.section === 'body' && data.row.index < 4) {
                                const color = summaryData[data.row.index][6];
                                const rgb = hexToRgb(color);
                                if (data.column.index === 5 && rgb) {
                                    data.cell.styles.fillColor = rgb;
                                    data.cell.styles.textColor = [255, 255, 255];
                                }
                            }
                        },
                        willDrawCell: (data) => {
                            if (data.section === 'body' && data.row.index === 4) { 
                                data.row.height = data.row.height * 1.1; 
                            }
                        },
                        didDrawCell: (data) => {
                            if (data.section === 'body' && data.row.index === 4 && data.column.index === 5) {
                                const score = evWithScores.finalScoreGestao?.toFixed(4) || '0.0000';
                                const total = " / 100.00";
                                const fullText = score + total;
                                
                                doc.setFontSize(11); 
                                doc.setFont('helvetica', 'bold'); 
                                doc.setTextColor(16, 185, 129);
                                
                                const textWidth = doc.getTextWidth(fullText);
                                const centerX = data.cell.x + (data.cell.width / 2) - (textWidth / 2);
                                const centerY = data.cell.y + (data.cell.height / 2) + 1.5;
                                
                                doc.text(fullText, centerX, centerY);
                                
                                doc.setFontSize(10); 
                                doc.setTextColor(0, 0, 0); 
                                doc.setFont('helvetica', 'normal');
                            }
                        }
                    });
                    y = doc.lastAutoTable.finalY + 3;

                // CASO B: SIMPLIFICADO COLABORADOR
                } else if (type === 'Simplificado Colaborador' && !isGestao) {
                    doc.setFontSize(12); 
                    doc.setTextColor(0, 0, 0); 
                    doc.setFont(undefined, 'bold'); 
                    doc.text("1. AVALIAÇÃO QUALITATIVA", margin, y); 
                    y += 5;
                    
                    const qualiRows = (evWithScores.qualitative?.criteria || []).map(c => [
                        c.name || '-',
                        c.dim || '-',
                        formatPdfGrade(c.q1),
                        formatPdfGrade(c.q2),
                        formatPdfGrade(c.q3),
                        formatPdfGrade(c.q4)
                    ]);
                    doc.autoTable({ 
                        startY: y, 
                        head: [['Critério', 'Dimensão', 'Q1', 'Q2', 'Q3', 'Q4']], 
                        body: qualiRows, 
                        theme: 'grid', 
                        headStyles: { fillColor: [11, 83, 148], fontSize: 8, halign: 'center', textColor: [255, 255, 255] }, 
                        bodyStyles: { fontSize: 7 },
                        columnStyles: { 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' } }
                    });
                    y = doc.lastAutoTable.finalY + 8;
                    
                    doc.setFillColor(219, 234, 254);
                    doc.setDrawColor(96, 165, 250);
                    doc.roundedRect(125, y - 4, 70, 8, 2, 2, 'FD');
                    doc.setFontSize(9);
                    doc.setTextColor(30, 64, 175);
                    doc.setFont('helvetica', 'bold');
                    doc.text(`Média Qualitativa: ${evWithScores.qualitative.finalAverage.toFixed(4)}`, 192, y + 1, { align: 'right' });
                    doc.setTextColor(0, 0, 0);
                    y += 11;

                    doc.setFontSize(12); 
                    doc.setFont(undefined, 'bold'); 
                    doc.text("2. AVALIAÇÃO QUANTITATIVA", margin, y); 
                    y += 5;
                    
                    // CORREÇÃO AQUI: Ordenação Numérica Natural e Alinhamento
                    const sortedQuantiErrors = [...(evWithScores.quantitative?.errors || [])].sort((a, b) =>
                        (a.name || '').localeCompare((b.name || ''), undefined, { numeric: true })
                    );

                    const quantiRows = sortedQuantiErrors.map(e => {
                        const totalRow = ['q1', 'q2', 'q3', 'q4'].reduce(
                            (sum, q) => sum + getQuarterWeightedPoints(e, q),
                            0
                        );

                        return [
                            formatOccurrenceTerm(e.name) || '-',
                            Number(e.peso || 0).toFixed(1),
                            formatPdfCount(e.q1),
                            formatPdfCount(e.q2),
                            formatPdfCount(e.q3),
                            formatPdfCount(e.q4),
                            totalRow.toFixed(1)
                        ];
                    });
                    
                    doc.autoTable({ 
                        startY: y, 
                        head: [['Tipo de Ocorrência', 'Peso', 'Q1', 'Q2', 'Q3', 'Q4', 'Total Pts']], 
                        body: quantiRows, 
                        theme: 'grid', 
                        headStyles: { fillColor: [192, 57, 43], fontSize: 8, halign: 'center', textColor: [255, 255, 255] }, 
                        bodyStyles: { fontSize: 7 },
                        columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' }, 6: { halign: 'center' } }
                    });
                    y = doc.lastAutoTable.finalY + 8; 
                    
                    doc.setFillColor(254, 215, 170);
                    doc.setDrawColor(251, 146, 60);
                    doc.roundedRect(125, y - 4, 70, 8, 2, 2, 'FD');
                    doc.setFontSize(9);
                    doc.setTextColor(154, 52, 18);
                    doc.setFont('helvetica', 'bold');
                    doc.text(`Média Quantitativa: ${evWithScores.quantitative.meanPoints.toFixed(4)}`, 192, y + 1, { align: 'right' });
                    doc.setTextColor(0, 0, 0);
                    y += 11;

                // CASO C: COMPLETO (AMBOS)
                } else if (type === 'Completo') {
                    doc.setFontSize(12); 
                    doc.setTextColor(0, 0, 0); 
                    doc.setFont(undefined, 'bold'); 
                    doc.text("1. AVALIAÇÃO QUALITATIVA", margin, y);

                    const qualiRows = (evWithScores.qualitative?.criteria || []).map(c => [
                        c.name || '-',
                        c.dim || '-',
                        formatPdfGrade(c.q1),
                        formatPdfGrade(c.q2),
                        formatPdfGrade(c.q3),
                        formatPdfGrade(c.q4)
                    ]);
                    doc.autoTable({
                        startY: y + 5,
                        head: [['Critério', 'Dimensão', 'Q1', 'Q2', 'Q3', 'Q4']],
                        body: qualiRows,
                        theme: 'grid',
                        headStyles: { fillColor: [11, 83, 148] },
                        // CORREÇÃO: Alinhamento Qualitativo
                        columnStyles: { 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' } },
                        foot: [[
                            { content: isGestao ? 'Pontuação Qualitativa Total (0-40):' : 'Média Qualitativa Final (0-10):', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold', fillColor: [239, 246, 255], textColor: [30, 64, 175] } },
                            { content: isGestao ? evWithScores.qualitative.finalScoreGestaoQuali?.toFixed(4) : evWithScores.qualitative.finalAverage?.toFixed(4), styles: { fontStyle: 'bold', fillColor: [219, 234, 254], textColor: [30, 64, 175], halign: 'center', lineColor: [96, 165, 250], lineWidth: 0.4 } }
                        ]]
                    });
                    y = doc.lastAutoTable.finalY + 10;

                    if (!isGestao) {
                        doc.setFontSize(12);
                        doc.text("2. AVALIAÇÃO QUANTITATIVA", margin, y);
                        
                        // CORREÇÃO AQUI: Ordenação Numérica Natural e Alinhamento
                        const sortedQuantiErrors = [...(evWithScores.quantitative?.errors || [])].sort((a, b) =>
                            (a.name || '').localeCompare((b.name || ''), undefined, { numeric: true })
                        );

                        const quantiRows = sortedQuantiErrors.map(e => [
                            formatOccurrenceTerm(e.name) || '-',
                            Number(e.peso || 0).toFixed(1),
                            formatPdfCount(e.q1),
                            formatPdfCount(e.q2),
                            formatPdfCount(e.q3),
                            formatPdfCount(e.q4)
                        ]);
                        
                        doc.autoTable({
                            startY: y + 5,
                            head: [['Ocorrência', 'Peso', 'Q1', 'Q2', 'Q3', 'Q4']],
                            body: quantiRows,
                            theme: 'grid',
                            headStyles: { fillColor: [192, 57, 43] },
                            columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' } },
                            foot: [[
                                { content: 'Média de Pontos Perdidos:', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 247, 237], textColor: [154, 52, 18] } },
                                { content: evWithScores.quantitative.meanPoints?.toFixed(4), styles: { fontStyle: 'bold', fillColor: [254, 215, 170], textColor: [154, 52, 18], halign: 'center', lineColor: [251, 146, 60], lineWidth: 0.4 } }
                            ]]
                        });
                        y = doc.lastAutoTable.finalY + 10;
                    } else {
                        if (y + 60 > doc.internal.pageSize.getHeight()) { doc.addPage(); y = 40; }
                        doc.setFontSize(12);
                        doc.text("2. RESULTADOS (METAS E PERCEPÇÃO)", margin, y);
                        const metasRows = (evWithScores.gestaoMetasPercepcao || []).map(item => {
                            const totalItem = ['q1', 'q2', 'q3', 'q4'].reduce((acc, q) => acc + parseFloat(item.scores?.[q] || 0), 0);
                            return [item.name, item.scores.q1 || '-', item.scores.q2 || '-', item.scores.q3 || '-', item.scores.q4 || '-', totalItem.toFixed(1)];
                        });
                        doc.autoTable({
                            startY: y + 5,
                            head: [['Item', 'Q1', 'Q2', 'Q3', 'Q4', 'Total']],
                            body: metasRows,
                            theme: 'grid',
                            foot: [[
                                { content: 'Total Metas + Percepção (0-40):', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold' } },
                                { content: evWithScores.totalGestaoMetasPercepcao?.toFixed(2), styles: { fontStyle: 'bold', fillColor: [255, 237, 213] } }
                            ]]
                        });
                        y = doc.lastAutoTable.finalY + 10;

                        if (y + 40 > doc.internal.pageSize.getHeight()) { doc.addPage(); y = 40; }
                        doc.setFontSize(12);
                        doc.setTextColor(0, 0, 0);
                        doc.text("3. EXTRAS: ATUALIZAÇÃO E RECICLAGEM", margin, y);

                        const updateRows = [
                            ["Na Área (D1) - Min 36h", (evWithScores.atualizacao?.D1_hours || 0) + "h", (evWithScores.atualizacao?.D1_nota || 0).toFixed(2)],
                            ["Outras Áreas (D2) - Min 18h", (evWithScores.atualizacao?.D2_hours || 0) + "h", (evWithScores.atualizacao?.D2_nota || 0).toFixed(2)]
                        ];

                        doc.autoTable({
                            startY: y + 5,
                            head: [['Categoria', 'Horas Registradas', 'Pontos Obtidos']],
                            body: updateRows,
                            theme: 'grid',
                            headStyles: { fillColor: [147, 51, 234] },
                            foot: [[
                                { content: 'Total Pontos Extras (0-20):', colSpan: 2, styles: { halign: 'right', fontStyle: 'bold' } },
                                { content: evWithScores.atualizacao?.total?.toFixed(2), styles: { fontStyle: 'bold', fillColor: [243, 232, 255] } }
                            ]]
                        });
                        y = doc.lastAutoTable.finalY + 10;
                    }
                }

                // =================================================================
                // 4. FEEDBACKS E MENSAGENS FINAIS
                // =================================================================
                
                // FEEDBACKS VISÍVEIS AO AVALIADO EM "MINHAS AVALIAÇÕES"
                // Regra: exibir considerações do colaborador/RH e feedbacks positivos da gestão, sempre limitados aos trimestres assinados.
                const feedbacksVisiveisAoAvaliado = Object.values(ev.feedbacksHistory || {})
                    .filter(f => {
                        const qNum = getQuarterNumberFromValue(f?.quarter, f?.date, f?.timestamp);
                        const isQuarterReleased = qNum > 0 && qNum <= maxAllowedQ;
                        const isCollaboratorVisible = f?.type === 'Colaborador' || f?.type === 'AvDes';
                        // Feedback positivo registrado pela gestão também deve aparecer ao colaborador.
                        const isPositiveManagerFeedback = f?.type === 'Gestão' && String(f?.nature || '').toLowerCase() === 'positivo';
                        return isQuarterReleased && (isCollaboratorVisible || isPositiveManagerFeedback);
                    })
                    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

                // PONTOS DE ATENÇÃO / FEEDBACKS - SIMPLIFICADO
                if (type.startsWith('Simplificado')) {
                    if (feedbacksVisiveisAoAvaliado.length > 0) {
                        if (y + 40 > doc.internal.pageSize.getHeight()) { doc.addPage(); y = 40; }

                        y += 5;
                        doc.setFontSize(12); 
                        doc.setTextColor(185, 28, 28); 
                        doc.setFont(undefined, 'bold');
                        doc.text("FEEDBACKS DA AVALIAÇÃO", margin, y);
                        y += 4;
                        
                        const feedbackRows = feedbacksVisiveisAoAvaliado.map(f => [
                            f.timestamp ? new Date(f.timestamp).toLocaleDateString('pt-BR') : '-',
                            f.type === 'AvDes' ? 'AvDes (RH)' : (f.type === 'Gestão' ? 'Gestão - Positivo' : 'Colaborador'),
                            f.quarter ? f.quarter.toUpperCase() : '-',
                            f.text || ''
                        ]);
                        
                        doc.autoTable({
                            startY: y,
                            head: [['Data', 'Tipo', 'Ref.', 'Descrição']],
                            body: feedbackRows,
                            theme: 'grid',
                            headStyles: { fillColor: [220, 38, 38], textColor: [255, 255, 255] },
                            columnStyles: {
                                0: { cellWidth: 25 },
                                1: { cellWidth: 28 },
                                2: { cellWidth: 18 },
                                3: { cellWidth: 'auto' }
                            },
                            styles: { fontSize: 9 }
                        });

                        y = doc.lastAutoTable.finalY + 15;
                    }
                } 
                // FEEDBACKS - COMPLETO
                else if (type === 'Completo') {
                    if (y + 40 > doc.internal.pageSize.getHeight()) { doc.addPage(); y = 40; }

                    doc.setFontSize(12); 
                    doc.setTextColor(0, 0, 0); 
                    doc.setFont(undefined, 'bold');
                    doc.text(isGestao ? "4. FEEDBACKS DA AVALIAÇÃO" : "3. FEEDBACKS DA AVALIAÇÃO", margin, y);

                    if (feedbacksVisiveisAoAvaliado.length > 0) {
                        const feedbackRows = feedbacksVisiveisAoAvaliado.map(f => [
                            f.timestamp ? new Date(f.timestamp).toLocaleDateString('pt-BR') : '-',
                            f.type === 'AvDes' ? 'AvDes (RH)' : (f.type === 'Gestão' ? 'Gestão - Positivo' : 'Colaborador'),
                            f.quarter?.toUpperCase() || '-',
                            f.text || ''
                        ]);

                        doc.autoTable({
                            startY: y + 5,
                            head: [['Data', 'Tipo', 'Trim', 'Descrição']],
                            body: feedbackRows,
                            theme: 'grid',
                            headStyles: { fillColor: [243, 156, 18], textColor: [255, 255, 255] },
                            columnStyles: {
                                0: { cellWidth: 25 },
                                1: { cellWidth: 30 },
                                2: { cellWidth: 18 },
                                3: { cellWidth: 'auto' }
                            },
                            styles: { fontSize: 9 }
                        });

                        y = doc.lastAutoTable.finalY + 10;
                    } else {
                        doc.setFontSize(10);
                        doc.setFont(undefined, 'normal');
                        doc.text("Nenhum feedback visível ao avaliado registrado.", margin, y + 10);
                        y += 20;
                    }



                    // OCORRÊNCIAS - COMPLETO
                    if (y + 40 > doc.internal.pageSize.getHeight()) { doc.addPage(); y = 40; }
                    y += 5;
                    doc.setFontSize(12); 
                    doc.setTextColor(0, 0, 0); 
                    doc.setFont(undefined, 'bold');
                    const sectionNum = isGestao ? "5." : "4.";
                    doc.text(`${sectionNum} RELAÇÃO DE CONFERÊNCIAS (OCORRÊNCIAS)`, margin, y);

                    const occurrences = (data.apontamentos || []).filter(ap => {
                        if (ap.collaboratorId !== ev.collaboratorId) return false;
                        if (!String(ap.date || '').startsWith(String(ev.periodYear))) return false;
                        const qNum = getQuarterNumberFromValue('', ap.date, ap.timestamp);
                        return qNum > 0 && qNum <= maxAllowedQ;
                    }).sort((a, b) => new Date(b.date) - new Date(a.date));

                    if (occurrences.length > 0) {
                        const occRows = occurrences.map(ap => [
                            new Date(ap.date + 'T00:00:00').toLocaleDateString('pt-BR'),
                            ap.weight || ap.peso || 0,
                            ap.type,
                            ap.description
                        ]);
                        doc.autoTable({
                            startY: y + 5,
                            head: [['Data', 'Peso', 'Tipo', 'Descrição']],
                            body: occRows,
                            theme: 'grid',
                            headStyles: { fillColor: [192, 57, 43], textColor: [255, 255, 255], halign: 'center' },
                            columnStyles: { 0: { cellWidth: 25 }, 1: { cellWidth: 15 }, 2: { cellWidth: 50 }, 3: { cellWidth: 'auto' } },
                            styles: { cellPadding: 2 }
                        });
                        y = doc.lastAutoTable.finalY + 10;
                    } else {
                        doc.setFontSize(10); doc.setFont(undefined, 'normal');
                        doc.text("Nenhuma ocorrência registrada neste período.", margin, y + 10);
                        y += 20;
                    }
                }

                // MENSAGEM FINAL (PROMOÇÃO/PREMIAÇÃO)
                const resultLabel = ev.labels?.find(l => l.text === 'Promoção' || l.text === 'Premiação');
                const isConcluded = ev.status === 'Finalizada' || ev.status === 'T4-Concluido' || ev.status === 'Concluída Financeiro';

                if (type.startsWith('Simplificado') && resultLabel && isConcluded) {
                    const msg = data.pdfMessages.find(m => m.type === resultLabel.text && m.personType === ev.personType);
                    if (msg) {
                        const nextYear = (parseInt(ev.periodYear) + 1).toString();
                        const replaceVars = (text) => text ? text.replace(/{AnoAtual}/g, ev.periodYear).replace(/{AnoSeg}/g, nextYear).replace(/{colaborador}/g, ev.collaboratorName).replace(/{gestor}/g, managerNameFallback) : "";
                        const finalTitle = replaceVars(msg.title);
                        const finalBody = replaceVars(msg.body);
                        const rgbBg = hexToRgb(msg.bgColor); const rgbText = hexToRgb(msg.textColor);

                        if (y + 50 > doc.internal.pageSize.getHeight()) { doc.addPage(); y = 40; }
                        if (rgbBg) doc.setFillColor(rgbBg[0], rgbBg[1], rgbBg[2]);
                        doc.roundedRect(margin, y, 182, 40, 5, 5, 'F');
                        if (rgbText) doc.setTextColor(rgbText[0], rgbText[1], rgbText[2]);
                        doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.text(finalTitle, 105, y + 15, { align: 'center' });
                        doc.setFontSize(11); doc.setFont("helvetica", "normal"); doc.text(doc.splitTextToSize(finalBody, 170), 105, y + 25, { align: 'center' });
                        y += 50;
                    }
                }

                // =================================================================
                // 5. FINALIZAÇÃO: QUADRO DE ASSINATURAS E PÁGINA 2
                // =================================================================

                // Verifica espaço para assinaturas. Se não couber, nova página.
                const estimatedSignatureHeight = 60;
                if (y + estimatedSignatureHeight > doc.internal.pageSize.getHeight()) {
                    doc.addPage();
                    y = 20;
                } else {
                    y += 10;
                }

                // 2. Desenha o Quadro de Assinaturas
                y = drawSignatureTable(y);

                // 3. Página 2 (Percepção) - Lógica ajustada para filtrar por Gestor Alvo e Trimestre
                    const targetEmail = collabData ? collabData.email.toLowerCase() : '';

                    const hasPerception = (data.teamPerception || []).some(p => {
                        const pManagerEmail = p.manager_email ? p.manager_email.toLowerCase() : '';
                        const pDate = p.date || ''; // Ex: "25/10/2023"
                        
                        // Verifica se o destinatário é a Joice (targetEmail)
                        const isToThisManager = pManagerEmail === targetEmail;
                        // Verifica se pertence ao ano correto
                        const isCorrectYear = pDate.endsWith(ev.periodYear);
                        
                        // IMPORTANTE: Aqui filtramos para que a página só exista se houver dados do TRIMESTRE atual
                        // A função drawPerceptionSection já filtra os registros, aqui validamos a existência
                        return isToThisManager && isCorrectYear;
                    });

                    // SÓ ADICIONA A PÁGINA SE: 
                    // 1. O avaliado for do tipo 'Gestão'
                    // 2. Existirem percepções cujo manager_email seja o da Joice (exemplo) no ano da avaliação
                    if (ev.personType === 'Gestão' && hasPerception) {
                        // Verificamos novamente dentro de drawPerceptionSection se o filtro de trimestre retornará linhas
                        // Para garantir que não saia uma página com cabeçalho mas sem dados.
                        doc.addPage('a4', 'l'); 
                        drawPerceptionSection(20);
                    }

                // 4. Salvar/Abrir
                const cleanType = type.includes('Simplificado') ? 'Simplificado' : 'Completo';
                const safeFileName = `${ev.collaboratorName}-${realDeptName}-${ev.evalTypeName || 'Padrao'}-${cleanType}-${ev.periodYear}`.replace(/[^a-z0-9\-]/gi, '_');

                doc.setProperties({ title: safeFileName });
                const pdfUrl = doc.output('bloburl');
                window.open(pdfUrl, '_blank');

            } catch (e) {
                console.error("Erro PDF:", e);
                alert("Erro ao gerar PDF: " + e.message);
            }
            setIsGenerating(false);
            setModal(null);
        }, [selectedEval, data, recalculateFinalAverage]);


        // ========================================================================
        // 6. ACTIONS (LOG ACTION, ETC)
        // ========================================================================
        const syncApontamentos = useCallback((newAps) => {
            if (!db) return;
            const batch = writeBatch(db);
            newAps.forEach(a => {
                const docId = a.id ? String(a.id) : Date.now().toString();
                const docRef = doc(db, `${FIRESTORE_PATH_PUBLIC}/apontamentos`, docId);
                const docData = { ...a };
                delete docData.id;
                batch.set(docRef, docData);
            });
            batch.commit().catch(e => { console.error("Erro batch:", e); alert("Erro sync: " + e.message); });
        }, [saveApontamento]);

        const actions = useMemo(() => ({
            // --- CORREÇÃO 1: FUNÇÃO SAVE ITEM GENÉRICA (ESSENCIAL PARA PESQUISAS) ---
        saveItem: async (collectionName, payload) => {
                const map = {
                    'surveys': saveSurvey,
                    'surveyResponses': saveSurveyResponse,
                    'recruitmentTests': saveRecruitmentTest,
                    'resumes': saveResume,
                    'recognitions': saveRecognition,
                    'pontoConfig': savePontoConfig,
                    'pontoSituations': savePontoSituations,
                    // --- COLEÇÕES ACADCOINS (CORRIGIDAS PARA AMBIENTE DE TESTE) ---
                    'acadCoinsConfig': async (p) => { if(db) await setDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/acadCoinsConfig`, p.id), p); },
                    'acadCoinsDist': async (p) => { if(db) await setDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/acadCoinsDist`, p.id), p); },
                    'acadCoinsCaixa': async (p) => { if(db) await setDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/acadCoinsCaixa`, p.id), p); },
                    'acadCoinsPrizes': async (p) => { if(db) await setDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/acadCoinsPrizes`, p.id), p); },
                    'acadCoinsRedemptions': async (p) => { if(db) await setDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/acadCoinsRedemptions`, p.id), p); },
                    'defaultCoinHistory': async (p) => { if(db) await setDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/defaultCoinHistory`, p.id), p); }
                };
                
                if (map[collectionName]) {
                    return await map[collectionName](payload);
                } else {
                    console.error(`Coleção '${collectionName}' não mapeada em saveItem.`);
                    return Promise.reject(new Error(`Coleção ${collectionName} não configurada.`));
                }
            },

            logAction: async (actionType, payload = {}) => {
                if (!db || !currentUser) return;

                try {
                    const normalizeDetails = (input) => {
                        if (!input) return [];

                        if (Array.isArray(input)) {
                            return input.map(item => ({
                                campo: item?.campo || item?.field || 'N/D',
                                de: item?.de ?? item?.from ?? '',
                                para: item?.para ?? item?.to ?? ''
                            }));
                        }

                        if (typeof input === 'object') {
                            if ('campo' in input || 'de' in input || 'para' in input) {
                                return [{
                                    campo: input?.campo || 'N/D',
                                    de: input?.de ?? '',
                                    para: input?.para ?? ''
                                }];
                            }

                            return Object.entries(input).map(([campo, valor]) => ({
                                campo,
                                de: '',
                                para: typeof valor === 'object' ? JSON.stringify(valor) : String(valor ?? '')
                            }));
                        }

                        return [{
                            campo: 'mensagem',
                            de: '',
                            para: String(input)
                        }];
                    };

                    const newLog = {
                        id: Date.now().toString(),
                        timestamp: Date.now(),
                        date: new Date().toISOString(),
                        action: actionType,
                        entity: payload.entity || payload.entidade || 'GERAL',
                        recordId: payload.recordId || payload.id || '',
                        userId: currentUser.email || currentUser.id || '',
                        userName: currentUser.name || '',
                        userRole: currentUser.role || '',
                        details: JSON.stringify(
                            normalizeDetails(payload.details || payload.alteracoes || payload.changes || payload)
                        )
                    };

                    await saveAuditLog(newLog);
                } catch (e) {
                    console.error("Erro ao gravar auditoria:", e);
                }
            },
            saveDept, saveManager, saveCollab, 
            saveCriteria: (c) => { if(c.dim !== undefined) saveQuali(c); else saveQuanti(c); },
            saveType, saveMsg, saveConfig, savePCS, 
            saveGestaoMetasPercepcao, deletePCS, 
            importPCSBatch: (newItems) => newItems.forEach(savePCS), 
            addMinWageHistory: saveMinWageHistory, updateMinWageHistory: saveMinWageHistory, deleteMinWageHistory, 
            saveTraining, saveTrainingDeadline, deleteTrainingDeadline, saveDeadline, deleteDeadline, 
            saveAIConfig,
            saveCompetencia, deleteCompetencia, saveTeamPerception, deleteTeamPerception,
            saveConferenciaType: saveConfType, deleteConferenciaType: deleteConfType,
            savePerceptionCycle, deletePerceptionCycle,
            savePermissionProfile, deletePermissionProfile,
            
            // Expondo diretamente caso precise
            saveSurvey, deleteSurvey, saveSurveyResponse,
            savePontoConfig, savePontoSituations, // <--- ADICIONADO AQUI PARA CORRIGIR O ERRO

            // --- CORREÇÃO 2: GENERIC DELETE COM 'SURVEYS' ---
            genericDelete: (type, id) => { 
                const map = { 
                    dept: deleteDept, manager: deleteManager, collab: deleteCollab, 
                    criteria: (id) => { deleteQuali(id); deleteQuanti(id); }, 
                    types: deleteType, messages: deleteMsg, config: deleteConfig, 
                    pcs: deletePCS, gestao: deleteGestaoMetasPercepcao, 
                    training: deleteTraining, trainingDeadlines: deleteTrainingDeadline, prazos: deleteDeadline, 
                    competencias: deleteCompetencia, teamPerception: deleteTeamPerception, 
                    conferencia: deleteConfType,
                    surveys: deleteSurvey,
                    recruitmentTests: deleteRecruitmentTest,
                    resumes: deleteResume,
                    surveyResponses: deleteSurveyResponse ,
                    recognitions: deleteRecognition,
                    pontoConfig: deletePontoConfig,
                    pontoSituations: deletePontoSituations,                
                };
                
                if(map[type]) {
                    map[type](id);
                } else {
                    console.error(`Generic Delete: Tipo '${type}' não encontrado.`);
                }
            },
            // -----------------------------------------------

            addApontamento: (a) => syncApontamentos([...aps.filter(x => x.id !== a.id), a]), 
            delApontamento: (id) => deleteApontamento(id), 
            saveEvaluation, delete: deleteEvaluation, 
            updateStatus: (id, s) => { const ev = evals.find(e => e.id === id); if(ev) saveEvaluation({...ev, status: s}); }, 
            edit: (ev) => {
                // Força a reabertura mesmo quando o usuário já estava no menu Avaliação/lista.
                // Isso evita o clique no olho não renderizar o conteúdo por openId igual ao anterior.
                setSys('eval');
                setOpenId(null);
                setTimeout(() => setOpenId(ev.id), 0);
            }, 
            setOpenId, saveUser, deleteUser, 
            updateActivePermissions: (p) => saveActivePermissions({ id: activePermissions.id || 'global_config', ...p }),
            saveUserPreferences: async (partialPrefs) => {
                if (!db || !currentUser || !currentUser.id) return;
                
                // --- CORREÇÃO: BLOQUEIO DE ERRO NO MODO TESTE ---
                // Se for o usuário do SEED (u1), não tenta gravar no banco para evitar o erro "No document to update".
                if (currentUser.id === 'u1' || currentUser.id === 'god_mode') {
                    console.log("Modo Teste: Preferências mantidas apenas em memória.");
                    return;
                }
                // ------------------------------------------------

                try {
                    const userRef = doc(db, `${FIRESTORE_PATH_PUBLIC}/users`, currentUser.id);
                    // Mescla o mapa completo para não apagar defaultScreen, favoritos ou preferências de outros módulos.
                    const mergedPreferences = { ...(currentUser.preferences || {}), ...(partialPrefs || {}) };
                    await setDoc(userRef, { preferences: mergedPreferences }, { merge: true });
                    setCurrentUser(prev => prev ? ({ ...prev, preferences: { ...(prev.preferences || {}), ...(partialPrefs || {}) } }) : prev);
                } catch (e) { console.error("Erro ao salvar preferencias:", e); }
            },
            syncTrainingsGlobal: async (yearInput) => { return { success: true, count: 0 }; },
            syncTeamPerceptionGlobal: async (yearInput) => { return { success: true, count: 0 }; }

        }), [
            aps, evals, activePermissions, saveDept, deleteDept, saveManager, deleteManager, saveCollab, deleteCollab, 
            saveQuali, deleteQuali, saveQuanti, deleteQuanti, saveType, deleteType, saveMsg, deleteMsg, saveConfig, deleteConfig, 
            savePCS, deletePCS, saveApontamento, deleteApontamento, deleteEvaluation, saveUser, deleteUser, 
            saveActivePermissions, saveMinWageHistory, deleteMinWageHistory, syncApontamentos, saveEvaluation, 
            saveGestaoMetasPercepcao, deleteGestaoMetasPercepcao, saveTraining, deleteTraining, saveTrainingDeadline, deleteTrainingDeadline, saveDeadline, deleteDeadline, saveAIConfig, 
            saveAuditLog, saveTeamPerception, deleteTeamPerception, saveConfType, deleteConfType, savePerceptionCycle, deletePerceptionCycle, 
            trainingData, collabs, depts, currentUser, savePermissionProfile, deletePermissionProfile,
            saveSurvey, deleteSurvey, saveSurveyResponse
            // -----------------------------------------
        ]);

        // ========================================================================
        // 7. EFEITOS (USE EFFECT)
        // ========================================================================
        useEffect(() => {
            setIsNotificationsOpen(false);
        }, [sys]);

        useEffect(() => {
            try {
                const app = initializeApp(firebaseConfig);
                db = getFirestore(app);
                auth = getAuth(app);
                setPersistence(auth, browserSessionPersistence)
                    .then(() => {
                        const unsubscribe = onAuthStateChanged(auth, async (user) => {
                            setAuthReady(true);
                        });
                        return () => unsubscribe();
                    })
                    .catch((error) => {
                        console.error(error);
                        setAuthReady(true);
                    });
            } catch (error) {
                console.error(error);
                setAuthReady(true);
            }
        }, []);

        // --- EFFECT: AUTENTICAÇÃO E CARGA INICIAL (CORRIGIDO) ---
        // --- EFFECT: AUTENTICAÇÃO E CARGA INICIAL (CORRIGIDO) ---
useEffect(() => {
    if (!authReady || !db) return;
    const auth = getAuth();
    
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
            const email = String(firebaseUser.email || '').trim().toLowerCase();

            if (!email) {
                setCurrentUser(null);
                return;
            }

            const usersRef = collection(db, `${FIRESTORE_PATH_PUBLIC}/users`);
            const userQuery = query(usersRef, where('email', '==', email));
            
            const unsubSnapshot = onSnapshot(userQuery, async (snapshot) => {
                if (!snapshot.empty) {
                    const userDoc = snapshot.docs[0];
                    const userData = userDoc.data();

                    const finalUser = {
                        id: userDoc.id,
                        uid: firebaseUser.uid,
                        email: firebaseUser.email,
                        displayName: firebaseUser.displayName,
                        photoURL: firebaseUser.photoURL,
                        ...userData
                    };

                    if (userData.googleUid !== firebaseUser.uid) {
                        try {
                            await setDoc(
                                doc(db, `${FIRESTORE_PATH_PUBLIC}/users`, String(userDoc.id)),
                                {
                                    googleUid: firebaseUser.uid,
                                    authProvider: 'google',
                                    isGoogleAuth: true,
                                    email
                                },
                                { merge: true }
                            );
                        } catch (syncError) {
                            console.warn("Não foi possível atualizar googleUid:", syncError);
                        }
                    }
                    
                    setCurrentUser(finalUser);

                    // Lógica de Decisão de Tela Inicial
                    // Só executa se o sistema ainda não decidiu a rota (sys === null)
                    if (sysRef.current === null) {
                                const prefs = finalUser.preferences || {};
                                const favs = prefs.favorites || [];
                                
                                // 1. Prioridade: Busca favorito marcado como principal
                                const mainAccessItem = favs.find(f => f.principalAcesso === true);
                                
                                // 2. Fallback: Busca configuração legado
                                const savedDefault = prefs.defaultScreen;

                                // 3. Define o alvo
                                let target = mainAccessItem ? mainAccessItem.targetSys : (savedDefault || getFirstAllowedModule(finalUser));
                                
                                // Proteção: Se target for 'favorites', mas não houver favoritos, vai para o dash
                                if (target === 'favorites' && favs.length === 0) target = 'dash';

                                // Aplica o redirecionamento
                                if (target && target.includes(':')) {
                                    const parts = target.split(':');

                                    /*
                                    * Mantém o caminho completo.
                                    * Exemplo: collab_center:my_growth
                                    */
                                    setSys(target);
                                    setTimeout(() => setOpenId(parts[1]), 100);
                                } else {
                                    setSys(target || 'dash');
                                    setOpenId(null);
                                }
                                
                                // Evita re-execução desnecessária
                                initialLoadRef.current = false;
                            }
                        }
                    });
                    return () => unsubSnapshot();
                } else {
                    setCurrentUser(null);
                    setSys('dash'); 
                }
            });

            return () => unsubscribeAuth();
        }, [authReady]); // Removido 'sys' das dependências para evitar loop

        useEffect(() => {
            if (!currentUser?.preferences) return;
            // Não sobrescreve o tema Verde Acad ao trocar de aba ou quando o snapshot
            // do usuário é atualizado. O modo local é a fonte principal; preferências
            // antigas dark/light são usadas apenas quando ainda não existe escolha local.
            let savedLocal = '';
            try { savedLocal = localStorage.getItem('gestao_pessoas_theme_mode_v1') || ''; } catch {}
            if (['dark', 'acad', 'light'].includes(savedLocal)) return;
            const savedPreference = currentUser.preferences.themeMode || currentUser.preferences.theme;
            if (['dark', 'acad', 'light'].includes(savedPreference)) setThemeMode(savedPreference);
        }, [currentUser?.id]);

        // ========================================================================
        // 8. FUNÇÕES DE SUPORTE
        // ========================================================================
        const toggleTheme = () => {
            const next = themeMode === 'dark' ? 'acad' : themeMode === 'acad' ? 'light' : 'dark';
            setThemeMode(next);
            actions.saveUserPreferences({ theme: next, themeMode: next });
        };

        // --- FUNÇÃO AUXILIAR: UPLOAD PARA GOOGLE DRIVE ---
        const uploadFileToDrive = async (file, customName) => {
            // *** COLE A URL DO SEU SCRIPT AQUI ***
            const SCRIPT_URL = "https://script.google.com/macros/s/AKfycbySpFDKhRT_DiNaKsG5G86izYnTvbtU6nbZQoQngFbi2BvCWBCzuw_hIMRDoHN1ug_A/exec";

            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = async () => {
                    const base64 = reader.result.split(',')[1];
                    
                    const payload = {
                        filename: customName,
                        mimeType: file.type,
                        file: base64
                    };

                    try {
                        // Envio via fetch (mode no-cors é padrão para GAS, mas usamos text/plain para contornar)
                        const response = await fetch(SCRIPT_URL, {
                            method: 'POST',
                            body: JSON.stringify(payload),
                            headers: { "Content-Type": "text/plain;charset=utf-8" }
                        });
                        const result = await response.json();
                        
                        if (result.status === 'success') resolve(result.url);
                        else reject(result.message);
                    } catch (e) {
                        reject(e.message);
                    }
                };
                reader.onerror = error => reject(error);
            });
        };

        const processTrainingAcadCoins = async (trainingDataToSave) => {
            const parseNumber = (val, fallback = 0) => {
                if (val === undefined || val === null || val === '') return fallback;

                const parsed = parseFloat(String(val).replace(',', '.'));
                return Number.isNaN(parsed) ? fallback : parsed;
            };

            const getIsoDate = (dateValue) => {
                const raw = String(dateValue || '').trim();

                if (!raw) return new Date().toISOString().split('T')[0];

                if (raw.includes('/')) {
                    const [dd, mm, yyyy] = raw.split('/');
                    return `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
                }

                if (raw.includes('-')) {
                    return raw.split('T')[0];
                }

                return new Date().toISOString().split('T')[0];
            };

            const getActiveRuleByDate = (historyArray, isoDate) => {
                if (!Array.isArray(historyArray) || historyArray.length === 0) return null;

                return [...historyArray]
                    .sort((a, b) => String(b.validFrom || '').localeCompare(String(a.validFrom || '')))
                    .find(rule =>
                        (!rule.validFrom || rule.validFrom <= isoDate) &&
                        (!rule.validTo || rule.validTo >= isoDate)
                    ) || null;
            };

            const evaluateTrainingFormula = (formula, hoursValue) => {
                if (!formula) return 0;

                try {
                    let parsed = String(formula)
                        .toUpperCase()
                        .replace(/HRD1/g, String(hoursValue))
                        .replace(/HRD2/g, String(hoursValue));

                    parsed = parsed
                        .replace(/,/g, '.')
                        .replace(/[^0-9+\-*/(). ]/g, '');

                    if (!parsed.trim()) return 0;

                    const result = new Function(`return (${parsed});`)();

                    return Number.isNaN(result) || result < 0 ? 0 : result;
                } catch (err) {
                    console.error('Erro ao avaliar fórmula de treinamento:', err, formula);
                    return 0;
                }
            };

            try {
                const isoDate = getIsoDate(trainingDataToSave.date);

                const configSource =
                    (Array.isArray(data?.acadCoinsConfig) && data.acadCoinsConfig[0]) ||
                    (Array.isArray(acadCoinsConfigDB) && acadCoinsConfigDB[0]) ||
                    null;

                if (!configSource) {
                    return {
                        generated: false,
                        reason: 'Configuração do AcadCoins não carregada.'
                    };
                }

                const targetCollab = (data.collaborators || []).find(c =>
                    String(c.email || '').trim().toLowerCase() ===
                    String(trainingDataToSave.email || currentUser?.email || '').trim().toLowerCase()
                );

                if (!targetCollab) {
                    return {
                        generated: false,
                        reason: `Colaborador não encontrado para o e-mail ${trainingDataToSave.email || currentUser?.email || '-'}`
                    };
                }

                if (targetCollab.participatesAcadCoins === false) {
                    return {
                        generated: false,
                        reason: `${targetCollab.name} não participa do AcadCoins.`
                    };
                }

                const startDate = targetCollab.acadCoinsStartDate || targetCollab.startDate;
                const endDate = targetCollab.acadCoinsEndDate || targetCollab.endDate;

                if (startDate && isoDate < startDate) {
                    return {
                        generated: false,
                        reason: `Curso anterior ao início de elegibilidade do colaborador. Curso: ${isoDate}. Início: ${startDate}.`
                    };
                }

                if (endDate && isoDate > endDate) {
                    return {
                        generated: false,
                        reason: `Curso posterior ao fim de elegibilidade do colaborador. Curso: ${isoDate}. Fim: ${endDate}.`
                    };
                }

                const trainingRule = getActiveRuleByDate(configSource.treinamentosHistory, isoDate);

                if (!trainingRule) {
                    return {
                        generated: false,
                        reason: `Não existe regra de treinamento ativa para a data ${isoDate}.`
                    };
                }

                const apuracaoRule = getActiveRuleByDate(configSource.apuracaoHistory, isoDate);
                const paymentPeriod =
                    apuracaoRule?.paymentPeriod ||
                    configSource.paymentPeriod ||
                    'mes';

                const typeText = String(trainingDataToSave.type || '').toLowerCase();

                const isD1 =
                    typeText.includes('1') ||
                    typeText.includes('atuação') ||
                    typeText.includes('atuacao') ||
                    typeText.includes('área atuação') ||
                    typeText.includes('area atuacao');

                const formulaToUse = isD1
                    ? trainingRule.formulaTrainingD1
                    : trainingRule.formulaTrainingD2;

                const limitToUse = isD1
                    ? parseNumber(trainingRule.maxTrainingHoursD1, 0)
                    : parseNumber(trainingRule.maxTrainingHoursD2, 0);

                if (!formulaToUse) {
                    return {
                        generated: false,
                        reason: `Regra ativa encontrada, mas sem fórmula para ${isD1 ? 'D1' : 'D2'}.`
                    };
                }

                if (limitToUse <= 0) {
                    return {
                        generated: false,
                        reason: `Regra ativa encontrada, mas limite de horas para ${isD1 ? 'D1' : 'D2'} está zerado.`
                    };
                }

                const newHours = parseNumber(trainingDataToSave.hours, 0);

                if (newHours <= 0) {
                    return {
                        generated: false,
                        reason: 'Curso sem carga horária válida.'
                    };
                }

                const courseDate = new Date(`${isoDate}T12:00:00`);
                const year = courseDate.getFullYear();
                const month = courseDate.getMonth() + 1;

                let periodKey = `${year}`;

                if (paymentPeriod === 'mes') {
                    periodKey = `${year}-M${String(month).padStart(2, '0')}`;
                } else if (paymentPeriod === 'trimestre') {
                    periodKey = `${year}-Q${Math.ceil(month / 3)}`;
                } else if (paymentPeriod === 'semestre') {
                    periodKey = `${year}-S${Math.ceil(month / 6)}`;
                }

                const getTrainingPeriodKey = (trainingDateValue) => {
                    const tIsoDate = getIsoDate(trainingDateValue);
                    const tDate = new Date(`${tIsoDate}T12:00:00`);
                    const tYear = tDate.getFullYear();
                    const tMonth = tDate.getMonth() + 1;

                    if (paymentPeriod === 'mes') {
                        return `${tYear}-M${String(tMonth).padStart(2, '0')}`;
                    }

                    if (paymentPeriod === 'trimestre') {
                        return `${tYear}-Q${Math.ceil(tMonth / 3)}`;
                    }

                    if (paymentPeriod === 'semestre') {
                        return `${tYear}-S${Math.ceil(tMonth / 6)}`;
                    }

                    return `${tYear}`;
                };

                const previousTrainingsSamePeriod = (data.trainingData || []).filter(t => {
                    if (String(t.id) === String(trainingDataToSave.id)) return false;

                    const sameEmail =
                        String(t.email || '').trim().toLowerCase() ===
                        String(trainingDataToSave.email || '').trim().toLowerCase();

                    if (!sameEmail) return false;

                    const tTypeText = String(t.type || '').toLowerCase();

                    const tIsD1 =
                        tTypeText.includes('1') ||
                        tTypeText.includes('atuação') ||
                        tTypeText.includes('atuacao') ||
                        tTypeText.includes('área atuação') ||
                        tTypeText.includes('area atuacao');

                    if (tIsD1 !== isD1) return false;

                    return getTrainingPeriodKey(t.date) === periodKey;
                });

                const usedHours = previousTrainingsSamePeriod.reduce(
                    (acc, item) => acc + parseNumber(item.hours, 0),
                    0
                );

                const remainingHours = limitToUse - usedHours;

                if (remainingHours <= 0) {
                    return {
                        generated: false,
                        reason: `Limite de ${limitToUse.toFixed(2)}h já consumido no período ${periodKey}.`
                    };
                }

                const hoursToConvert = Math.min(newHours, remainingHours);
                const coinsToGive = parseNumber(
                    evaluateTrainingFormula(formulaToUse, hoursToConvert).toFixed(2),
                    0
                );

                if (coinsToGive <= 0) {
                    return {
                        generated: false,
                        reason: `Fórmula retornou zero moedas. Fórmula: ${formulaToUse}. Horas consideradas: ${hoursToConvert.toFixed(2)}h.`
                    };
                }

                const limitMsg =
                    newHours > remainingHours
                        ? ` (Limitado a ${hoursToConvert.toFixed(1)}h restantes do limite do período)`
                        : '';

                const trainingCoinDocId = `training_${String(trainingDataToSave.id).replace(/[^a-zA-Z0-9_-]/g, '_')}`;

                const requiresApproval = !!trainingRule.requireTrainingApproval;

                const approvalManagerIds = Array.isArray(targetCollab.managerIds)
                    ? targetCollab.managerIds.map(String)
                    : [];

                const approvalTargetUsers = (data.users || []).filter(u => {
                    if (u?.role === 'SuperAdm') return true;
                    if (u?.permissions?.acad_coins_admin === true) return true;

                    const userEmail = String(u.email || '').trim().toLowerCase();

                    const userAsCollab = (data.collaborators || []).find(c =>
                        String(c.email || '').trim().toLowerCase() === userEmail
                    );

                    const userAsManager = (data.managers || []).find(m =>
                        String(m.email || '').trim().toLowerCase() === userEmail
                    );

                    return (
                        userAsCollab && approvalManagerIds.includes(String(userAsCollab.id))
                    ) || (
                        userAsManager && approvalManagerIds.includes(String(userAsManager.id))
                    );
                });

                const approvalTargetUserIds = approvalTargetUsers.map(u => String(u.id));
                const approvalTargetUserEmails = approvalTargetUsers.map(u => String(u.email || '').trim().toLowerCase()).filter(Boolean);

                const calcMemory = [
                    'Memória de cálculo - Curso/Treinamento',
                    `Colaborador: ${targetCollab.name || '-'}`,
                    `Curso: ${trainingDataToSave.description || '-'}`,
                    `Tipo: ${isD1 ? 'D1 - Área de Atuação' : 'D2 - Demais Áreas'}`,
                    `Data do curso: ${isoDate}`,
                    `Período de apuração: ${periodKey}`,
                    `Regra ativa desde: ${trainingRule.validFrom || '-'}`,
                    `Horas do curso: ${newHours.toFixed(2)}h`,
                    `Horas já lançadas no período/tipo: ${usedHours.toFixed(2)}h`,
                    `Limite configurado para o período/tipo: ${limitToUse.toFixed(2)}h`,
                    `Saldo disponível antes deste curso: ${remainingHours.toFixed(2)}h`,
                    `Horas convertidas em moedas: ${hoursToConvert.toFixed(2)}h`,
                    `Fórmula aplicada: ${formulaToUse}`,
                    `Resultado: ${coinsToGive.toFixed(2)} AcadCoins`
                ].join('\n');

                await actions.saveItem('acadCoinsDist', {
                    id: trainingCoinDocId,
                    date: requiresApproval
                        ? new Date().toISOString().split('T')[0]
                        : isoDate,

                    collabId: targetCollab.id,
                    quantity: coinsToGive,

                   sourceId: trainingDataToSave.id,
                    sourceType: 'trainingData',
                    type: 'treinamento',

                    trainingTitle: trainingDataToSave.description || '',
                    trainingType: trainingDataToSave.type || '',
                    trainingCertLink: trainingDataToSave.certLink || '',
                    trainingSummaryLink: trainingDataToSave.summaryLink || '',

                    status: requiresApproval ? 'blocked' : 'approved',
                    blocked: requiresApproval,
                    pendingApproval: requiresApproval,
                    approvalStatus: requiresApproval ? 'pending' : 'approved',

                    approvalTargetUserIds: requiresApproval ? approvalTargetUserIds : [],
                    approvalTargetUserEmails: requiresApproval ? approvalTargetUserEmails : [],
                    approvalTargetManagerIds: requiresApproval ? approvalManagerIds : [],

                    approvedAt: requiresApproval ? '' : new Date().toISOString(),
                    approvedBy: requiresApproval ? '' : currentUser?.id || '',
                    approvedByName: requiresApproval ? '' : currentUser?.name || '',

                    effectiveDate: isoDate,
                    originalCourseDate: isoDate,

                    reason: requiresApproval
                        ? `Curso pendente de aprovação: ${trainingDataToSave.description} - ${trainingDataToSave.hours}h${limitMsg}`
                        : `Curso: ${trainingDataToSave.description} - ${trainingDataToSave.hours}h${limitMsg}`,

                    calcMemory: [
                        calcMemory,
                        '',
                        requiresApproval
                            ? 'Status: pendente de validação manual do gestor. Moedas bloqueadas e fora do saldo disponível.'
                            : 'Status: aprovado automaticamente conforme regra vigente.'
                    ].join('\n')
                });

                return {
                    generated: true,
                    coins: coinsToGive,
                    blocked: requiresApproval,
                    reason: requiresApproval
                        ? `${coinsToGive.toFixed(2)} AcadCoins calculados, mas pendentes de aprovação do gestor.`
                        : `${coinsToGive.toFixed(2)} AcadCoins gerados para ${targetCollab.name}.`
                };

            } catch (error) {
                console.error('Erro ao processar AcadCoins do treinamento:', error);

                return {
                    generated: false,
                    reason: `Erro ao processar AcadCoins: ${error.message}`
                };
            }
        };

        // --- HANDLER: SALVAR CURSO (ATUALIZADO: 2 ANEXOS OBRIGATÓRIOS SE NÃO TIVER CERTIFICADO) ---
        const handleSaveTraining = async () => {
            // 1. Validação de Campos de Texto
            if (!trainingForm.date || !trainingForm.title || !trainingForm.durationStr) {
                return setValidationAlert({
                    title: "Campos Obrigatórios", 
                    message: "Preencha a Data, o Título e a Carga Horária."
                });
            }
            
            if (!trainingForm.durationStr.includes(':')) {
                return setValidationAlert({
                    title: "Formato Inválido", 
                    message: "Use o formato HH:MM:SS (Ex: 01:30:00)."
                });
            }

            // 2. Validação de Anexos (REGRA ESTRITA)
            let hasAttachment = false;

            if (trainingForm.hasCert === 'Sim') {
                // Se tem certificado: Basta 1 arquivo (Certificado)
                if (trainingFiles.cert || (trainingForm.id && trainingForm.oldCertLink)) {
                    hasAttachment = true;
                }
                if (!hasAttachment) {
                    return setValidationAlert({ title: "Anexo Pendente", message: "É obrigatório anexar o arquivo do Certificado." });
                }
            } else {
                // Se NÃO tem certificado: OBRIGATÓRIO TER OS DOIS (Resumo E Evidência)
                const hasSummary = trainingFiles.summary || (trainingForm.id && trainingForm.oldSummaryLink);
                const hasPrint = trainingFiles.print || (trainingForm.id && trainingForm.oldCertLink); 
                
                // ... (código existente da validação de anexos) ...
                if (!hasSummary || !hasPrint) {
                    return setValidationAlert({ 
                        title: "Anexos Pendentes", 
                        message: "Para cursos sem certificado oficial, é OBRIGATÓRIO anexar:\n1. O Resumo do conteúdo\n2. Uma Evidência (Print/Foto)" 
                    });
                }
            }

            // --- NOVA VALIDAÇÃO DE PRAZO LIMITE DO TRIMESTRE ---
            const courseDate = new Date(trainingForm.date + "T12:00:00");
            const courseMonth = courseDate.getMonth() + 1;
            const courseYear = courseDate.getFullYear();
            const courseQuarter = courseMonth <= 3 ? 'q1' : courseMonth <= 6 ? 'q2' : courseMonth <= 9 ? 'q3' : 'q4';

            // Busca a regra mais específica primeiro
            let deadlineRule = data.trainingDeadlines.find(d => d.departmentId === currentUser.departmentId && d.personType === currentUser.personType);
            if (!deadlineRule) deadlineRule = data.trainingDeadlines.find(d => d.departmentId === currentUser.departmentId && d.personType === 'Todos');
            if (!deadlineRule) deadlineRule = data.trainingDeadlines.find(d => (d.departmentId === '' || d.departmentId === 'all') && d.personType === currentUser.personType);
            if (!deadlineRule) deadlineRule = data.trainingDeadlines.find(d => (d.departmentId === '' || d.departmentId === 'all') && d.personType === 'Todos');

            if (deadlineRule && deadlineRule[courseQuarter]) {
                const [d, m] = deadlineRule[courseQuarter].split('/');
            let deadlineYear = courseYear;
            
            // Lógica para fechamento de trimestre:
            // Se o curso é do Q4 (Out, Nov, Dez), o prazo m/d geralmente é em Janeiro (mês 1) do ano seguinte.
            if (courseQuarter === 'q4' && parseInt(m) <= 3) {
                deadlineYear = courseYear + 1;
            } 
            // Se o curso é de anos anteriores ao ano atual, ele deve bloquear automaticamente 
            // a menos que a regra seja explicitamente retroativa (o que não é o caso aqui)
            
            const deadlineDate = new Date(deadlineYear, parseInt(m) - 1, parseInt(d), 23, 59, 59);
            const today = new Date();
            
            // Debug para console (opcional para você ver o que o sistema está calculando)
            console.log("Data do Curso:", trainingForm.date, "Trimestre:", courseQuarter);
            console.log("Prazo calculado:", deadlineDate.toLocaleString(), "Hoje:", today.toLocaleString());

            if (today > deadlineDate) {
                return setValidationAlert({ 
                    title: "Prazo Esgotado", 
                    message: `O envio de cursos referentes ao ${courseQuarter.toUpperCase()} de ${courseYear} encerrou no dia ${deadlineRule[courseQuarter]}/${deadlineYear}.\n\nRegra Aplicada: ${deadlineRule.name}` 
                });
            }
                
                if (today > deadlineDate) {
                    return setValidationAlert({ 
                        title: "Prazo Esgotado", 
                        message: `O envio de cursos referentes ao ${courseQuarter.toUpperCase()} / ${courseYear} encerrou no dia ${deadlineRule[courseQuarter]}/${deadlineYear}.\n\nRegra Aplicada: ${deadlineRule.name}` 
                    });
                }
            }
            // ---------------------------------------------------

            setIsUploading(true);

            try {
                // 3. Preparação dos Arquivos
                const safeEmail = currentUser.email.split('@')[0];
                const safeDate = trainingForm.date.replaceAll('-', '');
                const safeTitle = trainingForm.title.replace(/[^a-z0-9]/gi, '_').substring(0, 15);
                const baseFileName = `${safeEmail}-${safeDate}-${safeTitle}`;

                let certLink = trainingForm.oldCertLink || '';
                let summaryLink = trainingForm.oldSummaryLink || '';

                // 4. Lógica de Delete (Limpeza de antigos na edição)
                if (trainingForm.id) {
                    if (trainingForm.hasCert === 'Sim' && trainingFiles.cert && trainingForm.oldCertLink) {
                        await deleteFileFromDrive(trainingForm.oldCertLink);
                    }
                    // Se mudou de Sim para Não, ou está atualizando arquivos do Não
                    if (trainingForm.hasCert === 'Não') {
                        // Se enviou novo resumo, apaga antigo
                        if (trainingFiles.summary && trainingForm.oldSummaryLink) await deleteFileFromDrive(trainingForm.oldSummaryLink);
                        // Se enviou novo print, apaga antigo (Print fica salvo no certLink no banco)
                        if (trainingFiles.print && trainingForm.oldCertLink) await deleteFileFromDrive(trainingForm.oldCertLink); 
                    }
                }

                // 5. Upload dos Novos Arquivos
                if (trainingForm.hasCert === 'Sim' && trainingFiles.cert) {
                    const ext = trainingFiles.cert.name.split('.').pop();
                    certLink = await uploadFileToDrive(trainingFiles.cert, `${baseFileName}-CERT.${ext}`);
                } else if (trainingForm.hasCert === 'Não') {
                    if (trainingFiles.summary) {
                        const ext = trainingFiles.summary.name.split('.').pop();
                        summaryLink = await uploadFileToDrive(trainingFiles.summary, `${baseFileName}-RESUMO.${ext}`);
                    }
                    if (trainingFiles.print) {
                        const ext = trainingFiles.print.name.split('.').pop();
                        certLink = await uploadFileToDrive(trainingFiles.print, `${baseFileName}-PRINT.${ext}`);
                    }
                }

                // 6. Converter HH:MM:SS para Decimal
                const parts = trainingForm.durationStr.split(':').map(Number);
                const h = parts[0] || 0; const m = parts[1] || 0; const s = parts[2] || 0;
                const decimalHours = h + (m / 60) + (s / 3600);

                // 7. Salvar no Banco
                const trainingDataToSave = {
                    id: trainingForm.id || Date.now().toString(),
                    email: currentUser.email,
                    date: trainingForm.date.split('-').reverse().join('/'),
                    description: trainingForm.title,
                    type: trainingForm.type,
                    hours: parseFloat(decimalHours.toFixed(4)),
                    year: trainingForm.date.split('-')[0],
                    certLink: certLink,
                    summaryLink: summaryLink,
                    departmentId: currentUser.departmentId,
                    departmentName: data.departments.find(d => d.id === currentUser.departmentId)?.name || 'N/D',
                    origin: 'app_interno'
                };

               await actions.saveTraining(trainingDataToSave);

                const coinResult = await processTrainingAcadCoins(trainingDataToSave);

                setIsTrainingModalOpen(false);
                setTrainingModalTab('dados');

                setValidationAlert({
                    title: "Sucesso!",
                    message: coinResult.generated
                        ? `Curso salvo e arquivos anexados com sucesso.\n\nAcadCoins: ${coinResult.reason}`
                        : `Curso salvo e arquivos anexados com sucesso.\n\nAcadCoins não gerados: ${coinResult.reason}`
                });

                // Reset
                setTrainingForm({
                    id: null,
                    date: '',
                    title: '',
                    type: 'Área Atuação',
                    durationStr: '',
                    hours: '',
                    hasCert: 'Sim',
                    oldCertLink: null,
                    oldSummaryLink: null
                });

                setTrainingFiles({
                    cert: null,
                    summary: null,
                    print: null
                });

              

          } catch (error) {
                console.error(error);
                setValidationAlert({title: "Erro no Envio", message: error.message});
            } finally {
                setIsUploading(false);
            }
        };
                


        // Handler para abrir Edição
        const handleEditTraining = (t) => {
            // Converte DD/MM/YYYY -> YYYY-MM-DD para o input date
            const parts = t.date.split('/');
            const isoDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
            
            setTrainingForm({
                id: t.id,
                date: isoDate,
                title: t.description,
                type: t.type,
                hours: t.hours,
                // AQUI ESTAVA O PROBLEMA: Precisamos recriar o durationStr ao editar
                durationStr: formatDecimalToTime(t.hours), 
                hasCert: t.certLink && !t.summaryLink ? 'Sim' : 'Não',
                oldCertLink: t.certLink,
                oldSummaryLink: t.summaryLink
            });
            setIsTrainingModalOpen(true);
        };

            const handleDeleteTraining = (t) => {
                setConfirmModal({
                    isOpen: true,
                    title: "Excluir Curso",
                    message: "Deseja realmente excluir este curso e seus anexos? Esta ação também excluirá AcadCoins vinculados ao curso.",
                    onConfirm: async () => {
                        try {
                            if (!t?.id) {
                                throw new Error("Curso sem ID válido para exclusão.");
                            }

                            // 1. Deleta arquivos do Drive
                            if (t.certLink) await deleteFileFromDrive(t.certLink);
                            if (t.summaryLink) await deleteFileFromDrive(t.summaryLink);

                            // 2. Deleta moedas vinculadas ao curso
                            let removedCoinsCount = 0;

                            const distRef = collection(db, `${FIRESTORE_PATH_PUBLIC}/acadCoinsDist`);
                            const trainingCoinsQuery = query(
                                distRef,
                                where('type', '==', 'treinamento')
                            );

                            const trainingCoinsSnap = await getDocs(trainingCoinsQuery);
                            const batch = writeBatch(db);

                            const deterministicCoinId = `training_${String(t.id).replace(/[^a-zA-Z0-9_-]/g, '_')}`;
                            const courseTitle = String(t.description || t.title || '').trim().toLowerCase();

                            trainingCoinsSnap.forEach(docSnap => {
                                const coin = {
                                    id: docSnap.id,
                                    ...docSnap.data()
                                };

                                const sameSourceId =
                                    String(coin.sourceId || '') === String(t.id);

                                const sameDeterministicId =
                                    String(docSnap.id) === String(deterministicCoinId);

                                if (sameSourceId || sameDeterministicId) {
                                batch.delete(docSnap.ref);
                                removedCoinsCount++;
                            }
                            });

                            // Segurança extra: tenta apagar diretamente o ID determinístico mesmo que não tenha vindo na query
                            batch.delete(doc(db, `${FIRESTORE_PATH_PUBLIC}/acadCoinsDist`, deterministicCoinId));

                            await batch.commit();

                            // 3. Deleta o curso diretamente da coleção correta
                            await deleteDoc(doc(db, `${FIRESTORE_PATH_PUBLIC}/trainingData`, String(t.id)));

                            setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });

                            setValidationAlert({
                                title: "Sucesso!",
                                message: removedCoinsCount > 0
                                    ? `Curso excluído com sucesso.\n\nTambém foram excluídos ${removedCoinsCount} lançamento(s) de AcadCoins vinculados.`
                                    : "Curso excluído com sucesso.\n\nNenhum lançamento de AcadCoins vinculado foi encontrado."
                            });
                        } catch (error) {
                            console.error(error);
                            setValidationAlert({
                                title: "Erro ao excluir curso",
                                message: error.message
                            });
                        }
                    }
                });
            };


        const handleRequestAccess = useCallback(async (formData) => {
        const normalizedEmail = (formData.email || '').trim().toLowerCase();

        if (users.find(u => (u.email || '').trim().toLowerCase() === normalizedEmail)) {
            return { success: false, msg: "Email já cadastrado." };
        }

        const isGoogleAuth = formData.isGoogleAuth === true;

        const newUser = {
            id: Date.now().toString(),
            name: formData.name,
            nickname: formData.nickname,
            email: normalizedEmail,
            departmentId: formData.departmentId,
            role: 'Solicitante',
            isPending: true,
            isGoogleAuth,
            authProvider: isGoogleAuth ? 'google' : 'local',
            permissions: {},
            startDate: new Date().toISOString().split('T')[0],
            endDate: ''
        };

        if (!isGoogleAuth) {
            newUser.password = '123456';
        }

        await saveUser(newUser);
        return { success: true, msg: "Solicitação enviada!" };
    }, [users, saveUser]);

        const handlePasswordChange = useCallback(async () => {
        // Fluxo legado desativado.
        // Mantido apenas para compatibilidade temporária durante a limpeza.
        setShowChangePassword(false);
    }, []);


        // --- FUNÇÃO DE UPLOAD PARA O DRIVE ---
        const uploadToDrive = async (file) => {
            // URL DO SEU SCRIPT (Cole a URL gerada no Passo 1 aqui)
            const SCRIPT_URL = "https://script.google.com/macros/s/AKfycbySpFDKhRT_DiNaKsG5G86izYnTvbtU6nbZQoQngFbi2BvCWBCzuw_hIMRDoHN1ug_A/exec";

            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = async () => {
                    const base64 = reader.result.split(',')[1]; // Remove o cabeçalho "data:image..."
                    
                    const payload = {
                        filename: `${Date.now()}_${file.name}`, // Nome único
                        mimeType: file.type,
                        file: base64
                    };

                    try {
                        // O 'no-cors' é necessário para scripts do Google, mas dificulta ler a resposta direta.
                        // Para ler a resposta JSON, usamos o método POST padrão, mas o Google redireciona.
                        // A forma mais robusta em React puro:
                        const response = await fetch(SCRIPT_URL, {
                            method: 'POST',
                            body: JSON.stringify(payload),
                            // Mode 'text/plain' evita preflight OPTIONS que o Google Script não suporta bem
                            headers: { "Content-Type": "text/plain;charset=utf-8" } 
                        });
                        
                        const result = await response.json();
                        
                        if (result.status === 'success') {
                            resolve(result.url); // Retorna o link do Drive
                        } else {
                            reject("Erro no Script: " + result.message);
                        }
                    } catch (e) {
                        reject("Erro de conexão: " + e.message);
                    }
                };
                reader.onerror = error => reject(error);
            });
        };

        const handleLogout = async () => {
            await signOut(auth);
            setCurrentUser(null);
            setSys('dash');
            window.location.reload();
        };

        const toggleMenu = (menuId) => {
        setExpandedMenus(prev => ({ ...prev, [menuId]: !prev[menuId] }));
    };

        const handleOpenNotification = async (notification) => {
            if (!notification || !currentUser) return;

            // Notificação apenas informativa para o gestor:
            // ao clicar, marca como lida/arquivada e não abre tela.
            if (String(notification.id || '').startsWith('eval_collab_signed_')) {
                await handleArchiveNotification(notification.id);
                setIsNotificationsOpen(false);
                return;
            }

            if (!notification.targetSys) return;

            const perms = { ...(activePermissions || {}), ...((currentUser && currentUser.permissions) || {}) };
            const isSuper = currentUser.role === 'SuperAdm';
            const can = (key) => isSuper || perms[key] === true;

            const canAccessNotificationTarget = (targetSys) => {
                switch (targetSys) {
                    case 'collab_center:my_evals':
                        return can('access_collab_center') && can('collab_tab_signatures');

                    case 'collab_center:perception':
                        return can('access_collab_center') && can('collab_tab_perception');

                    case 'collab_center:acad_coins':
                        return can('access_collab_center') && can('collab_tab_acad_coins');

                    case 'rh:central':
                        return can('access_rh') && can('access_rh_central');

                    case 'dash':
                        return can('access_dash') && can('viewGestaoDashEval');

                    default:
                        return false;
                }
            };

            if (!canAccessNotificationTarget(notification.targetSys)) {
                setIsNotificationsOpen(false);
                return;
            }

            if (notification.targetSys.includes(':')) {
                setSys(notification.targetSys);
            } else {
                setSys(notification.targetSys);
                if (notification.openMenuId) {
                    setTimeout(() => setOpenId(notification.openMenuId), 50);
                }
            }

            setIsNotificationsOpen(false);
        };

        // =====================================================
        // HELPERS - CENTRAL DE NOTIFICAÇÕES DA AVALIAÇÃO
        // =====================================================
        const normalizeManagerSignatures = (ev) => {
            if (!ev) return [];
            if (Array.isArray(ev.managerSignatures)) return ev.managerSignatures;
            if (ev.managerSignature) return [ev.managerSignature];
            return [];
        };

        const getQuarterNumberFromStatusRef = (statusRef = '') => {
            const ref = String(statusRef || '').toUpperCase();

            if (ref.includes('T1') || ref.includes('1º')) return 1;
            if (ref.includes('T2') || ref.includes('2º')) return 2;
            if (ref.includes('T3') || ref.includes('3º')) return 3;
            if (ref.includes('T4') || ref.includes('4º') || ref.includes('FINALIZADA') || ref.includes('ASSINADA')) return 4;

            return null;
        };

        const getDepartmentById = (deptId) => {
            return data.departments.find(d => d.id === deptId);
        };

        const isRHUser = (user) => {
            if (!user) return false;
            const dept = getDepartmentById(user.departmentId);
            const deptName = (dept?.name || '').toUpperCase();
            return user.role === 'SuperAdm' || user.permissions?.access_rh || user.permissions?.access_rh_central || deptName.includes('RH');
        };

        const isDIRUser = (user) => {
            if (!user) return false;
            const dept = getDepartmentById(user.departmentId);
            const deptName = (dept?.name || '').toUpperCase();
            return user.role === 'SuperAdm' || deptName === 'DIR' || deptName.includes('DIRETORIA') || deptName.includes('DIR.');
        };

        const isEvaluationManagerForCurrentUser = (ev) => {
            if (!currentUser || !ev) return false;

            const collab = (data.collaborators || []).find(c => c.id === ev.collaboratorId);
            if (!collab || !Array.isArray(collab.managerIds) || collab.managerIds.length === 0) return false;

            const currentEmail = (currentUser.email || '').toLowerCase().trim();

            const linkedManagers = (data.managers || []).filter(m =>
                collab.managerIds.includes(m.id)
            );

            if (linkedManagers.length === 0) return false;

            return linkedManagers.some(m =>
                (m.email || '').toLowerCase().trim() === currentEmail
            );
        };

        const isEvaluationCollaboratorForCurrentUser = (ev) => {
            if (!currentUser || !ev) return false;

            const collab = data.collaborators.find(c => c.id === ev.collaboratorId);
            if (!collab) return false;

            return (collab.email || '').toLowerCase().trim() === (currentUser.email || '').toLowerCase().trim();
        };

        const persistArchivedNotificationIds = async (ids) => {
            const nextIds = Array.from(new Set((ids || []).filter(Boolean)));
            const nextPreferences = {
                ...(currentUser?.preferences || {}),
                archivedNotificationIds: nextIds
            };

            setArchivedNotificationIds(nextIds);
            setCurrentUser(prev => prev ? ({
                ...prev,
                preferences: {
                    ...(prev.preferences || {}),
                    archivedNotificationIds: nextIds
                }
            }) : prev);

            if (currentUser?.id && db) {
                await setDoc(
                    doc(db, `${FIRESTORE_PATH_PUBLIC}/users`, currentUser.id),
                    { preferences: nextPreferences },
                    { merge: true }
                );
            }
        };

        const handleArchiveNotification = async (notificationId) => {
            if (!notificationId) return;
            const baseIds = Array.isArray(archivedNotificationIds) ? archivedNotificationIds : [];
            if (baseIds.includes(notificationId)) return;
            await persistArchivedNotificationIds([...baseIds, notificationId]);
        };

        const handleRestoreNotification = async (notificationId) => {
            if (!notificationId) return;
            const baseIds = Array.isArray(archivedNotificationIds) ? archivedNotificationIds : [];
            await persistArchivedNotificationIds(baseIds.filter(id => id !== notificationId));
        };

        // --- FUNÇÃO CORRIGIDA DE REDIRECIONAMENTO ---
        // ========================================================================
        // [APP_GET_FIRST_ALLOWED_MODULE]
        // BLOCO APP - DEFINICAO DO PRIMEIRO MODULO PERMITIDO / REDIRECIONAMENTO
        // ========================================================================
        const getFirstAllowedModule = (user) => {
            if (!user) return 'dash';
            const perms = { ...activePermissions, ...(user.permissions || {}) };
            
            // CORREÇÃO DEFINITIVA: SuperAdm SEMPRE true. Outros apenas se for true.
            const check = (key) => user.role === 'SuperAdm' ? true : perms[key] === true;

            // 1. Dashboard 
            if (check('access_dash')) return 'dash';

            // 2. Central do Colaborador 
            if (check('access_collab_center')) {
                if (check('collab_tab_training')) return 'collab_center:training';
                if (check('collab_tab_perception')) return 'collab_center:perception';
                if (check('collab_tab_signatures')) return 'collab_center:my_evals';
                if (check('collab_tab_growth')) return 'collab_center:my_growth';
                if (check('collab_tab_time')) return 'collab_center:time_tracking'; 
                return 'collab_center:training';
            }

            // 3. Cadastros
            if (check('access_reg')) {
                if (check('access_tab_dept')) return 'reg:dept';
                return 'reg'; 
            }

            // 4. Outros Módulos
            if (check('access_apt')) return 'apt';
            if (check('access_eval')) return 'eval';

            if (check('access_rh')) {
                if (check('access_rh_central') || check('access_tab_fin_central')) return 'rh:central';
                if (check('access_rh_feedbacks') || check('access_tab_feedbacks')) return 'rh:feedbacks';
                if (check('access_talent')) return 'rh:talents';
                if (check('access_rh_surveys')) return 'rh:surveys';
                if (check('hr_cultura_acad')) return 'rh:hr_cultura_acad';
                return 'rh';
            }

            if (check('access_fin')) {
                if (check('access_tab_proj')) return 'fin:proj';
                if (check('access_tab_analise')) return 'fin:analise';
                return 'fin';
            }

            return 'collab_center'; 
        };


        const handleLogin = useCallback(async (email, password = null, options = {}) => {
    const normalizedEmail = (email || '').trim().toLowerCase();
    const authMode = options.authMode || 'local';

    let user = null;

    if (authMode === 'google') {
            user = users.find(
                u => (u.email || '').trim().toLowerCase() === normalizedEmail
            );

            // Fallback direto no Firestore para evitar erro no primeiro login
            // quando a lista users ainda não carregou.
            if (!user && db) {
                try {
                    const usersRef = collection(db, `${FIRESTORE_PATH_PUBLIC}/users`);
                    const userByEmailQuery = query(
                        usersRef,
                        where('email', '==', normalizedEmail)
                    );

                    const userByEmailSnap = await getDocs(userByEmailQuery);

                    if (!userByEmailSnap.empty) {
                        const userDoc = userByEmailSnap.docs[0];

                        user = {
                            id: userDoc.id,
                            ...userDoc.data()
                        };
                    }
                } catch (error) {
                    console.error("Erro ao buscar usuário Google diretamente no Firestore:", error);
                }
            }
        } else {
            user = users.find(
                u =>
                    (u.email || '').trim().toLowerCase() === normalizedEmail &&
                    (u.password || '') === String(password || '').trim()
            );
        }

    const isTestEnv =
        typeof window !== 'undefined' &&
        !window.location.hostname.includes('firebaseapp.com') &&
        !window.location.hostname.includes('web.app');

    // Preview local só para o deus
    if (authMode === 'local' && isTestEnv && normalizedEmail === 'super@adm.com') {
        if (!user) {
            user = {
                id: 'god_mode',
                name: 'Administrador DEV',
                email: 'super@adm.com',
                role: 'SuperAdm',
                password: '123',
                departmentId: 'd_ctb',
                authProvider: 'local',
                permissions: { viewGestao: true }
            };
        } else {
            user = { ...user, role: 'SuperAdm', authProvider: 'local' };

            if (user.permissions) {
                Object.keys(user.permissions).forEach((key) => {
                    if (user.permissions[key] === false) delete user.permissions[key];
                });
            } else {
                user.permissions = {};
            }

            user.permissions.viewGestao = true;
        }
    }

    if (!user) {
        return {
            success: false,
            msg: authMode === 'google' ? 'Usuário Google não vinculado.' : 'Inválido'
        };
    }

    if (user.isPending) {
        return { success: false, msg: 'Acesso aguardando aprovação.' };
    }

    if (authMode === 'google') {
        user = {
            ...user,
            isGoogleAuth: true,
            authProvider: 'google'
        };
    }

    const prefs = user.preferences || {};
    const favs = prefs.favorites || [];
    const mainAccessItem = favs.find((f) => f.principalAcesso === true);
    const savedDefault = prefs.defaultScreen;

    let targetCandidate = mainAccessItem
        ? mainAccessItem.targetSys
        : (savedDefault || getFirstAllowedModule(user));

    const canAccessTarget = (target) => {
        if (!target) return false;
        if (user.role === 'SuperAdm') return true;

        const mainModule = target.split(':')[0];
        if (mainModule === 'favorites') return true;

        const menuMap = {
            dash: 'access_dash',
            reg: 'access_reg',
            apt: 'access_apt',
            eval: 'access_eval',
            fin: 'access_fin',
            rh: 'access_rh',
            collab_center: 'access_collab_center',
            ponto: 'access_proj_folha_ponto',
            prod: 'access_proj_contr_prod'
        };

        const userPerms = { ...activePermissionsList, ...(user.permissions || {}) };
        const permKey = menuMap[mainModule];

        if (!permKey) return true;
        if (user.role === 'SuperAdm') return userPerms[permKey] !== false;

        return userPerms[permKey] === true;
    };

    let finalTarget = targetCandidate;
    if (!canAccessTarget(targetCandidate)) {
        finalTarget = getFirstAllowedModule(user);
    }

    if (finalTarget === 'favorites' && favs.length === 0) {
        finalTarget = getFirstAllowedModule(user);
    }

    setCurrentUser(user);

    setTimeout(() => {
        setSys(finalTarget || 'dash');

        if (finalTarget && finalTarget.includes(':')) {
            const parts = finalTarget.split(':');
            setOpenId(parts[1]);
        } else {
            setOpenId(null);
        }
    }, 50);

    return { success: true };
}, [users, activePermissionsList, getFirstAllowedModule]);


        const pendingUsersCount = users.filter(u => u.isPending).length;

        const effectivePermissions = { ...activePermissions, ...(currentUser?.permissions || {}) };

        const allowAcadCoinsNotifications =
            currentUser?.role === 'SuperAdm' ||
            effectivePermissions.collab_tab_acad_coins === true ||
            effectivePermissions.acad_coins_admin === true;

        const hasModuleAccess = (permKey) => {
            if (!permKey) return true;
            return effectivePermissions[permKey] === true;
        };

        const buildEvaluationNotifications = () => {
            if (!currentUser || !data?.evaluations) return [];

            const items = [];

            const normalizeManagerSignatures = (ev) => {
                if (!ev) return [];
                if (Array.isArray(ev.managerSignatures)) return ev.managerSignatures;
                if (ev.managerSignature) return [ev.managerSignature];
                return [];
            };

            const getQuarterNumberFromStatusRef = (statusRef = '') => {
                const ref = String(statusRef || '').toUpperCase();

                if (ref.includes('T1') || ref.includes('1º')) return 1;
                if (ref.includes('T2') || ref.includes('2º')) return 2;
                if (ref.includes('T3') || ref.includes('3º')) return 3;
                if (ref.includes('T4') || ref.includes('4º') || ref.includes('FINALIZADA') || ref.includes('ASSINADA')) return 4;

                return null;
            };

            const getDepartmentById = (deptId) => {
                return data.departments.find(d => d.id === deptId);
            };

            const getManagerEmailsByDepartmentKeyword = (keywordList = []) => {
                const targetDeptIds = (data.departments || [])
                    .filter(d => {
                        const deptName = (d.name || '').toUpperCase().trim();
                    return keywordList.some(k => deptName === k || deptName.includes(k));
                })
                .map(d => d.id);

            if (targetDeptIds.length === 0) return [];

            return (data.managers || [])
                .filter(m => targetDeptIds.includes(m.departmentId))
                .map(m => (m.email || '').toLowerCase().trim())
                .filter(Boolean);
        };

        const isRHUser = (user) => {
            if (!user) return false;

            const currentEmail = (user.email || '').toLowerCase().trim();
            const rhManagerEmails = getManagerEmailsByDepartmentKeyword(['RH', 'RECURSOS HUMANOS']);

            return rhManagerEmails.includes(currentEmail);
        };

        const isDIRUser = (user) => {
            if (!user) return false;

            const currentEmail = (user.email || '').toLowerCase().trim();
            const dirManagerEmails = getManagerEmailsByDepartmentKeyword(['DIR', 'DIRETORIA']);

            return dirManagerEmails.includes(currentEmail);
        };

            const isEvaluationManagerForCurrentUser = (ev) => {
                if (!currentUser || !ev) return false;

                const collab = (data.collaborators || []).find(c => c.id === ev.collaboratorId);
                if (!collab || !Array.isArray(collab.managerIds) || collab.managerIds.length === 0) return false;

                const currentEmail = (currentUser.email || '').toLowerCase().trim();

                const linkedManagers = (data.managers || []).filter(m =>
                    collab.managerIds.includes(m.id)
                );

                if (linkedManagers.length === 0) return false;

                return linkedManagers.some(m =>
                    (m.email || '').toLowerCase().trim() === currentEmail
                );
            };

            const isEvaluationCollaboratorForCurrentUser = (ev) => {
                if (!currentUser || !ev) return false;

                const collab = data.collaborators.find(c => c.id === ev.collaboratorId);
                if (!collab) return false;

                return (collab.email || '').toLowerCase().trim() === (currentUser.email || '').toLowerCase().trim();
            };

            data.evaluations.forEach(ev => {
                const collab = data.collaborators.find(c => c.id === ev.collaboratorId);
                if (!collab) return;

                const dept = getDepartmentById(collab.departmentId);
                const deptLabel = dept?.name || ev.department || 'Sem Depto';

                const mgrSigs = normalizeManagerSignatures(ev);
                const colSigs = ev.collabSignatures || {};

                // A) gestor assinou -> colaborador
                mgrSigs.forEach(sig => {
                    const qNum = getQuarterNumberFromStatusRef(sig.statusRef);
                    if (!qNum) return;

                    const qKey = `q${qNum}`;
                    const collabAlreadySigned = !!colSigs[qKey];

                    if (!collabAlreadySigned && isEvaluationCollaboratorForCurrentUser(ev)) {
                        items.push({
                            id: `eval_mgr_signed_${ev.id}_${qKey}`,
                            title: `Assinatura do gestor pendente do colaborador`,
                            description: `${ev.collaboratorName} - ${deptLabel} - o gestor já assinou ${qKey.toUpperCase()}. Agora falta sua assinatura.`,
                            count: 1,
                            targetSys: 'collab_center:my_evals',
                            openMenuId: 'my_evals',
                            moduleLabel: 'Central Colaborador'
                        });
                    }
                });

                // B) colaborador assinou -> gestor
                Object.entries(colSigs).forEach(([qKey, sig]) => {
                    const qNum = parseInt(String(qKey).replace('q', ''), 10);
                    if (![1, 2, 3, 4].includes(qNum)) return;

                    const managerAlreadySigned = mgrSigs.some(
                        m => getQuarterNumberFromStatusRef(m.statusRef) === qNum
                    );

                    const collabSigned = !!colSigs[qKey];

                    const managerNeedsAction =
                        managerAlreadySigned &&
                        collabSigned &&
                        isEvaluationManagerForCurrentUser(ev);

                    if (managerNeedsAction) {
                        items.push({
                            id: `eval_collab_signed_${ev.id}_${qKey}`,
                            title: `Assinatura do colaborador aguardando gestor`,
                            description: `${ev.collaboratorName} - ${deptLabel} - o colaborador assinou ${qKey.toUpperCase()} e aguarda validação do gestor.`,
                            count: 1,
                            targetSys: 'dash',
                            openMenuId: ev.id,
                            moduleLabel: 'Dashboard'
                        });
                    }
                });

                // C) gestor + colaborador assinaram -> RH e DIR
                [1, 2, 3, 4].forEach(qNum => {
                    const qKey = `q${qNum}`;
                    const managerSigned = mgrSigs.some(m => getQuarterNumberFromStatusRef(m.statusRef) === qNum);
                    const collabSigned = !!colSigs[qKey];

                    if (!managerSigned || !collabSigned) return;

                    if (isRHUser(currentUser) && !ev.rhSignature) {
                        items.push({
                            id: `eval_rh_pending_${ev.id}_${qKey}`,
                            title: `Validação RH pendente`,
                            description: `${ev.collaboratorName} - ${deptLabel} - gestor e colaborador já assinaram ${qKey.toUpperCase()}. Falta validação do RH.`,
                            count: 1,
                            targetSys: 'rh:central',
                            openMenuId: 'central',
                            moduleLabel: 'RH'
                        });
                    }

                    if (isDIRUser(currentUser) && ev.personType !== 'Gestão' && !ev.diretoriaSignature) {
                        items.push({
                            id: `eval_dir_pending_${ev.id}_${qKey}`,
                            title: `Validação Diretoria pendente`,
                            description: `${ev.collaboratorName} - ${deptLabel} - gestor e colaborador já assinaram ${qKey.toUpperCase()}. Falta validação da Diretoria.`,
                            count: 1,
                            targetSys: 'rh:central',
                            openMenuId: 'central',
                            moduleLabel: 'RH'
                        });
                    }
                });
            });

            // D) período de avaliação da gestão aberto -> colaborador
            (data.perceptionCycles || []).forEach(cycle => {
                if (!cycle) return;

                const isOpen =
                    cycle.isOpen === true ||
                    cycle.status === 'Aberto' ||
                    cycle.active === true;

                if (!isOpen) return;

                const myCollab = (data.collaborators || []).find(c =>
                    (c.email || '').toLowerCase().trim() === (currentUser?.email || '').toLowerCase().trim()
                );

                if (!myCollab) return;

                items.push({
                    id: `gestao_cycle_open_${cycle.id}_${myCollab.id}`,
                    title: `Avaliação da gestão disponível`,
                    description: `O período de avaliação da gestão está aberto para você.`,
                    count: 1,
                    targetSys: 'collab_center:perception',
                    openMenuId: 'perception',
                    moduleLabel: 'Central Colaborador'
                });
            });

            return items;
        };

        const evaluationNotifications = useMemo(
            () => buildEvaluationNotifications(),
            [currentUser, data.evaluations, data.collaborators, data.departments, data.managers, data.perceptionCycles]
        );

        
        const systemNotifications = useMemo(() => {
            const items = [];

            if (pendingUsersCount > 0 && effectivePermissions.access_tab_users === true) {
                items.push({
                    id: 'users-pending-approval',
                    title: `${pendingUsersCount} solicitação(ões) de acesso pendente(s)`,
                    description: 'Abrir Cadastros > Usuários > Gestão de Usuários.',
                    count: pendingUsersCount,
                    targetSys: 'reg:users:list',
                    openMenuId: 'users',
                    moduleLabel: 'Cadastros'
                });
            }

            if (pendingSurveysCount > 0 && effectivePermissions.access_sub_surveys === true) {
                items.push({
                    id: 'collab-surveys-pending',
                    title: `${pendingSurveysCount} pesquisa(s) aguardando sua resposta`,
                    description: 'Abrir Central Colaborador > Pesquisas & Clima.',
                    count: pendingSurveysCount,
                    targetSys: 'collab_center:surveys',
                    openMenuId: 'surveys',
                    moduleLabel: 'Central Colaborador'
                });
            }

            if (pendingHRRemindersCount > 0 && effectivePermissions.access_rh_surveys === true) {
                items.push({
                    id: 'rh-followup-reminders',
                    title: `${pendingHRRemindersCount} lembrete(s) de acompanhamento pendente(s)`,
                    description: 'Abrir RH > Gestão Pesquisa e Clima.',
                    count: pendingHRRemindersCount,
                    targetSys: 'rh:surveys',
                    openMenuId: 'surveys',
                    moduleLabel: 'RH'
                });
            }

            if (collabCulturaTotalNotifications > 0 && effectivePermissions.collab_tab_cultura_acad === true) {
                items.push({
                    id: 'collab-cultura-acad',
                    title: `${collabCulturaTotalNotifications} item(ns) na Cultura Acad`,
                    description: 'Há ciência pendente e/ou retorno do RH não lido. Abrir Central Colaborador > Cultura Acad.',
                    count: collabCulturaTotalNotifications,
                    targetSys: 'collab_center:cultura_acad',
                    openMenuId: 'cultura_acad',
                    moduleLabel: 'Central Colaborador'
                });
            }

            if (hrCulturaBadgeCount > 0 && effectivePermissions.hr_cultura_acad === true) {
                items.push({
                    id: 'rh-cultura-acad',
                    title: `${hrCulturaBadgeCount} feedback(s) de Cultura Acad pendente(s)`,
                    description: 'Abrir RH > Gestão Cultura Acad.',
                    count: hrCulturaBadgeCount,
                    targetSys: 'rh:hr_cultura_acad',
                    openMenuId: 'hr_cultura_acad',
                    moduleLabel: 'RH'
                });
            }

            if (recognitionNotifications?.total > 0 && effectivePermissions.collab_tab_recognition === true) {
                items.push({
                    id: 'recognition-recent',
                    title: recognitionNotifications.isForMe
                        ? `Você tem ${recognitionNotifications.total} reconhecimento(s) recente(s)`
                        : `${recognitionNotifications.total} reconhecimento(s) recente(s) disponível(is)`,
                    description: 'Abrir Central Colaborador > Reconhecimento.',
                    count: recognitionNotifications.total,
                    targetSys: 'collab_center:recognition',
                    openMenuId: 'recognition',
                    moduleLabel: 'Central Colaborador'
                });
            }

            if (acadCoinsGiftReceivedCount > 0 && allowAcadCoinsNotifications) {
                items.push({
                    id: 'acadcoins-gifts-received',
                    title: `${acadCoinsGiftReceivedCount} presente(s) recebido(s) no AcadCoins`,
                    description: 'Abrir Central Colaborador > Acadcoins.',
                    count: acadCoinsGiftReceivedCount,
                    targetSys: 'collab_center:acad_coins',
                    openMenuId: 'acad_coins',
                    moduleLabel: 'Central Colaborador'
                });
            }

            if (acadCoinsDonationReceivedCount > 0 && allowAcadCoinsNotifications) {
                items.push({
                    id: 'acadcoins-donations-received',
                    title: `${acadCoinsDonationReceivedCount} doação(ões) recebida(s) no AcadCoins`,
                    description: 'Abrir Central Colaborador > Acadcoins.',
                    count: acadCoinsDonationReceivedCount,
                    targetSys: 'collab_center:acad_coins',
                    openMenuId: 'acad_coins',
                    moduleLabel: 'Central Colaborador'
                });
            }

            if (acadCoinsRecognitionReceivedCount > 0 && allowAcadCoinsNotifications) {
                items.push({
                    id: 'acadcoins-recognitions-received',
                    title: `${acadCoinsRecognitionReceivedCount} reconhecimento(s) recebido(s) no AcadCoins`,
                    description: 'Abrir Central Colaborador > Acadcoins.',
                    count: acadCoinsRecognitionReceivedCount,
                    targetSys: 'collab_center:acad_coins',
                    openMenuId: 'acad_coins',
                    moduleLabel: 'Central Colaborador'
                });
            }

            if (acadCoinsRaffleWinnerCount > 0 && allowAcadCoinsNotifications) {
                items.push({
                    id: 'acadcoins-raffle-winner',
                    title: `${acadCoinsRaffleWinnerCount} sorteio(s) ganho(s) no AcadCoins`,
                    description: 'Abrir Central Colaborador > Acadcoins.',
                    count: acadCoinsRaffleWinnerCount,
                    targetSys: 'collab_center:acad_coins',
                    openMenuId: 'acad_coins',
                    moduleLabel: 'Central Colaborador'
                });
            }

            if (acadCoinsOtherPositiveCreditsCount > 0 && allowAcadCoinsNotifications) {
                items.push({
                    id: 'acadcoins-other-positive-credits',
                    title: `${acadCoinsOtherPositiveCreditsCount} crédito(s) de moedas recebido(s)`,
                    description: 'Abrir Central Colaborador > Acadcoins.',
                    count: acadCoinsOtherPositiveCreditsCount,
                    targetSys: 'collab_center:acad_coins',
                    openMenuId: 'acad_coins',
                    moduleLabel: 'Central Colaborador'
                });
            }

            if (acadCoinsPendingTrainingApprovalNotifications.length > 0) {
                items.push(...acadCoinsPendingTrainingApprovalNotifications);
            }

            if (acadCoinsIdeaApprovedCount > 0 && allowAcadCoinsNotifications) {
                items.push({
                    id: 'acadcoins-approved-ideas',
                    title: `${acadCoinsIdeaApprovedCount} ideia(s) aprovada(s) no AcadCoins`,
                    description: 'Abrir Central Colaborador > Acadcoins.',
                    count: acadCoinsIdeaApprovedCount,
                    targetSys: 'collab_center:acad_coins',
                    openMenuId: 'acad_coins',
                    moduleLabel: 'Central Colaborador'
                });
            }

            items.push(...evaluationNotifications);

            return items;
        }, [
            pendingUsersCount,
            pendingSurveysCount,
            pendingHRRemindersCount,
            collabCulturaTotalNotifications,
            hrCulturaBadgeCount,
            recognitionNotifications,
            acadCoinsGiftReceivedCount,
            acadCoinsDonationReceivedCount,
            acadCoinsRecognitionReceivedCount,
            acadCoinsIdeaApprovedCount,
            acadCoinsRaffleWinnerCount,
            acadCoinsOtherPositiveCreditsCount,
            acadCoinsPendingTrainingApprovalItems,
            acadCoinsPendingTrainingApprovalNotifications,
            effectivePermissions,
            allowAcadCoinsNotifications,
            evaluationNotifications
        ]);

        const safeNotifications = Array.isArray(systemNotifications) ? systemNotifications : [];
            const safeArchivedIds = Array.isArray(archivedNotificationIds) ? archivedNotificationIds : [];

            const activeNotifications = useMemo(
                () => safeNotifications.filter(item => {
                    if (!item || !item.id) return false;

                    if (String(item.id).startsWith('acadcoins-training-approval-pending-')) {
                        return true;
                    }

                    return !safeArchivedIds.includes(item.id);
                }),
                [safeNotifications, safeArchivedIds]
            );

            const archivedNotifications = useMemo(
                () => safeNotifications.filter(item => {
                    if (!item || !item.id) return false;

                    if (String(item.id).startsWith('acadcoins-training-approval-pending-')) {
                        return false;
                    }

                    return safeArchivedIds.includes(item.id);
                }),
                [safeNotifications, safeArchivedIds]
            );

            const unreadNotificationsTotal = activeNotifications.reduce((acc, item) => acc + (item.count || 1), 0);

        // ========================================================================
        // [APP_MENU_STRUCTURE_AV_DES]
        // BLOCO APP - ESTRUTURA DO MENU LATERAL PRINCIPAL
        // ========================================================================
        const menuStructureAvDes = [
            { id: 'favorites', label: 'Favoritos', icon: Star, permission: null },
            { id: 'dash', label: 'Dashboard', icon: LayoutDashboard, permission: 'access_dash' },
            
            {
                id: 'collab_center', 
                label: 'Central Colaborador', 
                icon: User, 
                permission: 'access_collab_center', 
                subItems: [
                    { id: 'time_tracking', label: 'Gestão de Ponto', permKey: 'collab_tab_time' },
                    { id: 'produtividade', label: 'Produtividade', permKey: 'access_prod_basic' }, // <--- CHAVE CORRIGIDA AQUI
                    { 
                            id: 'training', 
                            label: 'Capacitações', 
                            permKey: 'collab_tab_training' 
                        },
                        { 
                            id: 'cultura_acad', 
                            label: 'Cultura Acad', 
                            permKey: 'collab_tab_cultura_acad' 
                        },
        //...
                    { id: 'perception', label: 'Avaliar Gestão', permKey: 'collab_tab_perception' },
                    { id: 'my_evals', label: 'Minhas Avaliações', permKey: 'collab_tab_signatures' }, 
                    { id: 'my_growth', label: 'Minha Evolução', permKey: 'collab_tab_growth' },
                    { id: 'surveys', label: 'Pesquisas & Clima', permKey: 'access_sub_surveys' },
                    { id: 'recognition', label: 'Reconhecimento', permKey: 'collab_tab_recognition' },
                    { id: 'acad_coins', label: currencyName, permKey: 'collab_tab_acad_coins' } // NOME DINÂMICO
                ]
            },

            {
                id: 'reg', label: 'Cadastros', icon: Database, permission: 'access_reg',
                subItems: [
                    { id: 'dept', label: 'Departamentos', permKey: 'access_tab_dept' },
                    { id: 'manager', label: 'Gestores', permKey: 'access_tab_manager' },
                    
                    // DADOS EXTRAS (innerTabs) - Invisíveis no Menu Principal, Visíveis nos Favoritos
                    { 
                        id: 'criteria', label: 'Critérios Avaliação', permKey: 'access_tab_criteria',
                        innerTabs: [
                            { id: 'criterios', label: 'Lista de Critérios', permKey: 'access_sub_criteria_list' },
                            { id: 'conferencia', label: 'Tipos de Conferência', permKey: 'access_sub_conference_types' }
                        ]
                    },
                    
                    { id: 'types', label: 'Tipos Avaliação', permKey: 'access_tab_types' },
                    { id: 'collab', label: 'Pessoas', permKey: 'access_tab_collab' },
                    
                    { 
                        id: 'config', label: 'Parâmetros Avaliação', permKey: 'access_tab_config',
                        innerTabs: [
                            { id: 'metricas', label: 'Métricas Globais', permKey: 'access_sub_metricas' },
                            { id: 'deadlines', label: 'Prazos', permKey: 'access_sub_deadlines' },
                            { id: 'messages', label: 'Mensagens PDF', permKey: 'access_sub_messages' },
                            { id: 'competencias', label: 'Competências', permKey: 'access_sub_competencias' },
                            { id: 'minWage', label: 'Salário Mínimo', permKey: 'access_sub_minwage' },
                            { id: 'logo', label: 'Logomarca', permKey: 'access_sub_logo' }
                        ]
                    },
                    
                    { id: 'pcs', label: 'Cargos e Salários', permKey: 'access_tab_pcs' },
                    
                    { 
                        id: 'ponto', label: 'Ponto (Regras/Sit)', permKey: 'access_tab_ponto',
                        innerTabs: [
                            { id: 'config', label: 'Regras Gerais', permKey: 'access_sub_ponto_rules' },
                            { id: 'situations', label: 'Situações (Justificativas)', permKey: 'access_sub_ponto_sits' }
                        ]
                    },
                    
                    { 
                        id: 'users', label: 'Usuários', permKey: 'access_tab_users',
                        innerTabs: [
                            { id: 'list', label: 'Gestão de Usuários', permKey: 'access_sub_users_manage' },
                            { id: 'audit', label: 'Auditoria e Logs', permKey: 'access_tab_audit' }
                        ]
                    },

                    { 
                        id: 'training', label: 'Dados Externos', permKey: 'access_tab_training',
                        innerTabs: [
                            { id: 'capacitacoes', label: 'Cursos e Treinamentos', permKey: 'access_sub_capacitacoes' },
                            { id: 'percepcao', label: 'Percepção da Equipe', permKey: 'access_sub_percepcao' },
                            { id: 'ia', label: 'IA', permKey: 'access_sub_capacitacoes' }
                        ]
                    }, 
                    
                    { id: 'backup', label: 'Backup e Restore', permKey: 'access_tab_backup' },
                ]
            },
            { id: 'apt', label: 'Ocorrências Técnicas', icon: AlertCircle, permission: 'access_apt' },
            { id: 'eval', label: 'Avaliação', icon: ClipboardCheck, permission: 'access_eval' },
            
            {
                id: 'fin', label: 'Gerencial', icon: BadgeDollarSign, permission: 'access_fin',
                subItems: [
                    { id: 'proj', label: 'Estimativa Financeira', permKey: 'access_tab_proj' },
                    { id: 'analise', label: 'Análise Gerencial', permKey: 'access_tab_analise' }
                ]
            },
            {
                id: 'rh', label: 'RH', icon: Users, permission: 'access_rh',
                subItems: [
                    { id: 'central', label: 'Central Avaliações', permKey: 'access_rh_central' },
                    { id: 'feedbacks', label: 'Acomp. PDIs | Feedbacks', permKey: 'access_rh_feedbacks' },
                    { id: 'talents', label: 'Gestão de Talentos', permKey: 'access_talent' },
                    { id: 'surveys', label: 'Gestão Pesquisa e Clima', permKey: 'access_rh_surveys' },
                    { id: 'hr_cultura_acad', label: 'Gestão Cultura Acad', permKey: 'hr_cultura_acad' }
                ]
            }
        ];

        
        /// 1. Proteção contra sys nulo (Safety Check)
        const safeSys = sys || ''; 
        // CORREÇÃO: Adicionado activeSubTabId
        const [activeModuleId, activeTabId, activeSubTabId] = safeSys.split(':'); 
        
        // ========================================================================
        // [APP_ACTIVE_MODULE_RESOLUTION]
        // BLOCO APP - RESOLUCAO DO MODULO/TAB ATIVOS
        // ========================================================================
        const activeModuleInfo = menuStructureAvDes.find(i => i.id === activeModuleId);

        const openTabColors = [
            'from-orange-500 to-amber-500',
            'from-pink-600 to-rose-500',
            'from-blue-600 to-indigo-500',
            'from-emerald-600 to-teal-500',
            'from-purple-600 to-violet-500',
            'from-cyan-600 to-sky-500',
            'from-red-600 to-orange-500',
            'from-slate-700 to-slate-500'
        ];

        const getStableOpenTabColor = (targetSys) => {
            const key = String(targetSys || '');
            let hash = 0;
            for (let i = 0; i < key.length; i++) hash = ((hash << 5) - hash) + key.charCodeAt(i);
            return openTabColors[Math.abs(hash) % openTabColors.length];
        };

        const getOpenModuleTabInfo = (targetSys) => {
            const target = String(targetSys || '').trim();
            if (!target) return null;

            const parts = target.split(':');
            const moduleId = parts[0];
            const tabId = parts[1];
            const subTabId = parts[2];
            const moduleInfo = menuStructureAvDes.find(m => m.id === moduleId);
            if (!moduleInfo || moduleId === 'favorites' || moduleId === 'card_menu') return null;

            const tabInfo = moduleInfo.subItems?.find(t => t.id === tabId);
            const subTabInfo = tabInfo?.innerTabs?.find(t => t.id === subTabId);

            let stateOpenId = tabId || null;
            let extraLabel = '';
            if (moduleId === 'eval' && openId) {
                stateOpenId = openId;
                const ev = data?.evaluations?.find(e => String(e.id) === String(openId));
                extraLabel = ev?.collaboratorName ? ` / ${ev.collaboratorName}` : '';
            }

            const labelParts = [moduleInfo.label, tabInfo?.label, subTabInfo?.label].filter(Boolean);
            const baseLabel = labelParts.join(' / ');

            return {
                targetSys: target,
                moduleId,
                tabId,
                subTabId,
                label: `${baseLabel}${extraLabel}`,
                shortLabel: moduleId === 'eval' && extraLabel ? (extraLabel.replace(' / ', '') || moduleInfo.label) : (subTabInfo?.label || tabInfo?.label || moduleInfo.label),
                openId: stateOpenId,
                icon: moduleInfo.icon || FileText,
                color: getStableOpenTabColor(target)
            };
        };

        useEffect(() => {
            const tabInfo = getOpenModuleTabInfo(sys);
            if (!tabInfo) return;

            setOpenModuleTabs(prev => {
                const existingIndex = prev.findIndex(t => t.targetSys === tabInfo.targetSys);
                if (existingIndex >= 0) {
                    const next = [...prev];
                    // Atualiza o estado salvo da aba sem reposicionar. Assim as abas não mudam de lugar ao clicar.
                    next[existingIndex] = { ...next[existingIndex], ...tabInfo, color: next[existingIndex].color || tabInfo.color };
                    return next;
                }
                return [...prev, tabInfo].slice(-8);
            });
        }, [sys, openId, data?.evaluations]);

        const handleNavigateOpenModuleTab = (targetSys) => {
            const existingTab = openModuleTabs.find(t => t.targetSys === targetSys);
            const tabInfo = existingTab || getOpenModuleTabInfo(targetSys);
            if (!tabInfo) return;
            setSys(tabInfo.targetSys);
            setOpenId(tabInfo.openId || null);
        };

        const handleCloseOpenModuleTab = (targetSys, event) => {
            event?.preventDefault?.();
            event?.stopPropagation?.();

            setOpenModuleTabs(prev => {
                const next = prev.filter(t => t.targetSys !== targetSys);
                if (sys === targetSys) {
                    const fallback = next[next.length - 1];
                    if (fallback) {
                        setTimeout(() => handleNavigateOpenModuleTab(fallback.targetSys), 0);
                    } else {
                        setTimeout(() => { setSys('favorites'); setOpenId(null); }, 0);
                    }
                }
                return next;
            });
        };

        useEffect(() => {
            if (activeModuleId !== 'collab_center') return;
            if (activeTabId) return;

            const collabMenu = menuStructureAvDes.find(m => m.id === 'collab_center');

            const firstAllowedSubItem = collabMenu?.subItems?.find(sub => {
                if (!sub.permKey) return true;
                return hasModuleAccess(sub.permKey);
            });

            const fallbackTab = firstAllowedSubItem?.id || 'time_tracking';

            setSys(`collab_center:${fallbackTab}`);
            setOpenId(fallbackTab);
        }, [activeModuleId, activeTabId, currentUser?.id]);

        

        useEffect(() => {
            if (activeModuleId !== 'collab_center') return;
            if (activeTabId) return;

            const collabMenu = menuStructureAvDes.find(m => m.id === 'collab_center');

            const firstAllowedSubItem = collabMenu?.subItems?.find(sub => {
                if (!sub.permKey) return true;
                return hasModuleAccess(sub.permKey);
            });

            const fallbackTab = firstAllowedSubItem?.id || 'time_tracking';

            setSys(`collab_center:${fallbackTab}`);
            setOpenId(fallbackTab);
        }, [activeModuleId, activeTabId, currentUser?.id]);

        const getQuarterLabelForConsideration = (q, year) => {
            const labels = {
                q1: `1º Trim/${year}`,
                q2: `2º Trim/${year}`,
                q3: `3º Trim/${year}`,
                q4: `4º Trim/${year}`
            };

            return labels[q] || q;
        };

        const getQuarterEndDateForConsideration = (q, year) => {
            const y = Number(year);

            const map = {
                q1: new Date(y, 2, 31, 23, 59, 59),
                q2: new Date(y, 5, 30, 23, 59, 59),
                q3: new Date(y, 8, 30, 23, 59, 59),
                q4: new Date(y, 11, 31, 23, 59, 59)
            };

            return map[q] || new Date();
        };

        const isQuarterSignedByManagerForConsideration = (evaluation, q) => {
            if (!evaluation || !q) return false;

            const qNum = String(q).replace('q', '');
            const tTag = `T${qNum}`;

            if (
                evaluation.status === 'Finalizada' ||
                evaluation.status === 'T4-Concluido' ||
                evaluation.status === 'Concluída Financeiro' ||
                evaluation.status === 'Assinada pelo Gestor'
            ) {
                return true;
            }

            if (evaluation.status === `${tTag}-Concluido`) return true;

            const signatures = evaluation.managerSignatures || [];

            return signatures.some(s => {
                const ref = String(s.statusRef || s.quarter || s.status || '').toUpperCase();
                return ref.includes(tTag);
            });
        };

        const getCollabConsiderationsByEval = (evaluation) => {
                if (!evaluation?.feedbacksHistory) return [];

                return Object.values(evaluation.feedbacksHistory)
                    .filter(f => f?.type === 'Colaborador')
                    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
            };

            const canCreateCollabConsideration = (evaluation, quarter = '') => {
                    if (!evaluation) return false;

                    const hasRHSig = !!evaluation.rhSignature;
                    const hasDiretoriaSig = !!evaluation.diretoriaSignature;
                    const isArchived = evaluation.status === 'Concluída Financeiro';

                    const hasCollabSigForQuarter = quarter
                        ? !!evaluation.collabSignatures?.[quarter]
                        : Object.values(evaluation.collabSignatures || {}).some(Boolean);

                    return !hasCollabSigForQuarter &&
                        !hasRHSig &&
                        !hasDiretoriaSig &&
                        !isArchived;
                };

                const isCollabConsiderationOwner = (feedback) => {
                    if (!feedback || !currentUser) return false;

                    const currentUserId = String(currentUser.id || '');
                    const currentUserEmail = String(currentUser.email || '').trim().toLowerCase();

                    const createdBy = String(feedback.createdBy || '');
                    const createdByEmail = String(feedback.createdByEmail || '').trim().toLowerCase();

                    if (currentUserId && createdBy && currentUserId === createdBy) return true;
                    if (currentUserEmail && createdByEmail && currentUserEmail === createdByEmail) return true;

                    return false;
                };

                const canEditOrDeleteCollabConsideration = (evaluation, quarter = '', feedback = null) => {
                    if (!canCreateCollabConsideration(evaluation, quarter)) return false;

                    return isCollabConsiderationOwner(feedback);
                };

        // Carregamento da Central de Ajuda deve permanecer antes de qualquer retorno
        // condicional do componente para preservar a ordem dos Hooks do React.
        const HELP_SUGGESTIONS_COLLECTION = 'SugestoesMelhorias_Sugestões';
        const HELP_CONFIG_COLLECTION = 'SugestoesMelhorias_CFG';
        const helpCollectionPath = (name) => `${FIRESTORE_PATH_PUBLIC}/${name}`;

        const loadHelpSuggestions = async () => {
            if (!db) return;
            setHelpSuggestionsLoading(true);
            try {
                const snap = await getDocs(collection(db, helpCollectionPath(HELP_SUGGESTIONS_COLLECTION)));
                const list = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b) => Date.parse(b.criadoEm || b.createdAt || '') - Date.parse(a.criadoEm || a.createdAt || ''));
                setHelpSuggestions(list);
            } catch (error) {
                console.error('Erro ao carregar sugestões:', error);
            } finally { setHelpSuggestionsLoading(false); }
        };

        useEffect(() => { if (helpCenterOpen) loadHelpSuggestions(); }, [helpCenterOpen]);

        const loadHelpConfig = async () => {
            if (!db) return;
            try {
                const snap = await getDoc(doc(db, helpCollectionPath(HELP_CONFIG_COLLECTION), 'config_geral'));
                if (!snap.exists()) return;
                const data = snap.data() || {};
                setHelpConfig(prev => ({
                    ...prev,
                    driveFolderId: data.driveFolderId || '',
                    appsScriptUrl: data.appsScriptUrl || '',
                    uploadToken: data.uploadToken || '',
                    manualDriveFolderId: data.manualDriveFolderId || '',
                    manualAppsScriptUrl: data.manualAppsScriptUrl || '',
                    superAdmins: Array.isArray(data.superAdminEmails) ? data.superAdminEmails.join('\n') : (data.superAdmins || prev.superAdmins),
                    responsaveis: Array.isArray(data.usuariosResponsaveis) ? data.usuariosResponsaveis : (Array.isArray(data.responsaveis) ? data.responsaveis : prev.responsaveis)
                }));
            } catch (error) {
                console.error('Erro ao carregar configuração da Central de Ajuda:', error);
            }
        };

        useEffect(() => {
            if (helpCenterOpen || helpConfigOpen) loadHelpConfig();
        }, [helpCenterOpen, helpConfigOpen]);

        const normalizeHelpManual = (item = {}, index = 0) => ({
            ...item,
            id: item.id || item.fileId || item.documentId || item.googleId || `manual_${index}`,
            module: item.module || item.modulo || item.módulo || item.sistema || 'Pessoas',
            area: item.area || item.menu || item.categoria || 'Manual',
            title: item.title || item.titulo || item.name || item.nome || 'Manual sem título',
            summary: item.summary || item.resumo || item.descricao || item.description || '',
            url: item.url || item.webViewLink || item.link || '',
            documentId: item.documentId || item.fileId || item.googleId || '',
            content: item.content || item.conteudo || item.text || item.texto || ''
        });

        const refreshHelpManuals = async ({ silent = false } = {}) => {
            const base = String(helpConfig.manualAppsScriptUrl || '').trim();
            if (!base) {
                if (!silent) alert('Configure a URL publicada do Apps Script dos manuais.');
                return;
            }
            setHelpManualsLoading(true);
            try {
                const url = new URL(base);
                url.searchParams.set('action', 'listarManuais');
                url.searchParams.set('folderId', helpConfig.manualDriveFolderId || '');
                url.searchParams.set('t', String(Date.now()));
                const response = await fetch(url.toString(), { method: 'GET', redirect: 'follow' });
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const json = await response.json();
                const raw = json?.resultado ?? json?.data ?? json?.items ?? json;
                const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.manuais) ? raw.manuais : []);
                const normalized = list.map(normalizeHelpManual);
                setHelpManualsData(normalized);
                try { localStorage.setItem('gestao_pessoas_help_manuals_v1', JSON.stringify(normalized)); } catch {}
                if (!silent) alert(`${normalized.length} manual(is) atualizado(s).`);
            } catch (error) {
                console.error('Erro ao atualizar manuais:', error);
                const cached = (() => { try { return JSON.parse(localStorage.getItem('gestao_pessoas_help_manuals_v1') || '[]'); } catch { return []; } })();
                if (Array.isArray(cached) && cached.length) setHelpManualsData(cached);
                if (!silent) alert(`Não foi possível atualizar os manuais: ${error?.message || 'falha de conexão.'}`);
            } finally { setHelpManualsLoading(false); }
        };

        useEffect(() => {
            if (!helpCenterOpen) return;
            const cached = (() => { try { return JSON.parse(localStorage.getItem('gestao_pessoas_help_manuals_v1') || '[]'); } catch { return []; } })();
            if (Array.isArray(cached) && cached.length) setHelpManualsData(cached);
        }, [helpCenterOpen]);

        // ========================================================================
        // [APP_RENDER_MAIN]
        // BLOCO APP - RENDERIZACAO PRINCIPAL (RETURNS, LAYOUT, MENU E MODULOS)
        // ========================================================================

        // 1. Tela de Carregamento Global (Auth)
        if (!authReady) {
            return (
                <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                    <Loader2 size={48} className="animate-spin text-blue-500" />
                </div>
            );
        }
        
        // 2. Tela de Login (Se não houver usuário)
        if (!currentUser || currentUser.role === 'Guest') {
            return <AuthScreen onLogin={handleLogin} onRequestAccess={handleRequestAccess} departments={depts} usersList={users} />;
        }

        // 3. Tela de Carregamento de Preferências (CORREÇÃO DO LOOP E TELA BRANCA)
        // Se o usuário está logado, mas o sistema ainda não decidiu a tela (sys === null), mostra loading
        if (sys === null) {
            return (
                <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center gap-4">
                    <Loader2 size={48} className="animate-spin text-purple-500" />
                    <p className="text-slate-400 text-sm animate-pulse">Lendo suas preferências...</p>
                </div>
            );
        }

        // 4. Fluxo de troca de senha removido.
        // Contas Google não exigem senha local obrigatória.

        



        const cycleThemeMode = () => setThemeMode(prev => prev === 'dark' ? 'acad' : prev === 'acad' ? 'light' : 'dark');

        const cacheModules = [
            ['avaliacoes', 'Avaliações', ['gestao_pessoas_', 'evaluation_', 'feedback_']],
            ['rh', 'Gestão de Pessoas / RH', ['rh_', 'recruitment_', 'survey_']],
            ['ponto', 'Ponto e banco de horas', ['ponto_', 'timesheet_', 'clock_']],
            ['produtividade', 'Produtividade', ['produtividade_', 'task_', 'obrigacao_']],
            ['manuais', 'Manuais e Central de Ajuda', ['facil_manuais_', 'gestao_pessoas_manual_', 'help_']],
            ['preferencias', 'Preferências locais', ['gestao_pessoas_zoom_', 'gestao_pessoas_theme_', 'menuOrganizer', 'openTabs']]
        ];

        const clearSelectedCache = async () => {
            const selected = Object.entries(cacheSelection).filter(([, enabled]) => enabled).map(([key]) => key);
            if (!selected.length) return alert('Selecione ao menos um módulo para limpar.');
            const prefixes = cacheModules.filter(([key]) => selected.includes(key)).flatMap(([, , keys]) => keys);
            try {
                Object.keys(localStorage).forEach(key => {
                    if (prefixes.some(prefix => key.toLowerCase().includes(String(prefix).toLowerCase()))) localStorage.removeItem(key);
                });
                if (selected.includes('avaliacoes') || selected.includes('rh') || selected.includes('ponto') || selected.includes('produtividade')) {
                    if (typeof indexedDB !== 'undefined' && indexedDB.databases) {
                        const dbs = await indexedDB.databases();
                        for (const item of dbs || []) {
                            const name = String(item?.name || '').toLowerCase();
                            if (name && prefixes.some(prefix => name.includes(String(prefix).replace(/[_-]/g, '').toLowerCase()))) indexedDB.deleteDatabase(item.name);
                        }
                    }
                }
                setCacheModalOpen(false);
                setCacheSelection({ avaliacoes: false, rh: false, ponto: false, produtividade: false, manuais: false, preferencias: false });
                alert('Cache selecionado limpo com sucesso. Configurações e dados do Firebase foram preservados.');
            } catch (error) {
                console.error('Erro ao limpar cache:', error);
                alert('Não foi possível concluir toda a limpeza do cache. Verifique o console.');
            }
        };

        const helpManuals = [
            { id: 'm1', module: 'Pessoas', area: 'Avaliações', title: 'AEC - Acompanhamento de Evolução Continuada e critérios', summary: 'Acompanhamento contínuo, critérios qualitativos, quantitativos, extras, feedbacks e evolução.' },
            { id: 'm2', module: 'Pessoas', area: 'RH', title: 'Acompanhamento de PDIs e feedbacks', summary: 'Criação, vínculo, histórico, conclusão e acompanhamento de feedbacks.' },
            { id: 'm3', module: 'Pessoas', area: 'Ponto', title: 'Ponto, justificativas e banco de horas', summary: 'Marcações, justificativas, assinaturas, espelhos e relatórios.' },
            { id: 'm4', module: 'Pessoas', area: 'Cadastros', title: 'Usuários, permissões e departamentos', summary: 'Cadastros estruturais, perfis, permissões e regras de acesso.' },
            { id: 'm5', module: 'Pessoas', area: 'AcadCoins', title: 'AcadCoins, reconhecimentos e premiações', summary: 'Distribuições, ideias, prêmios e histórico de movimentações.' },
            { id: 'm6', module: 'Pessoas', area: 'Produtividade', title: 'Controle de produtividade', summary: 'Obrigações, cronômetros, análises, importações e relatórios.' }
        ];
        const effectiveHelpManuals = helpManualsData.length ? helpManualsData : helpManuals;
        const filteredHelpManuals = effectiveHelpManuals.filter(item => !manualSearch.trim() || `${item.module} ${item.area} ${item.title} ${item.summary}`.toLowerCase().includes(manualSearch.toLowerCase()));
        const helpManualKey = (manual) => String(manual?.documentId || manual?.fileId || manual?.id || manual?.url || manual?.title || 'manual');
        const getHelpManualPreviewUrl = (manual) => {
            const docId = manual?.documentId || manual?.fileId || '';
            if (docId) return `https://docs.google.com/document/d/${docId}/preview?embedded=true&rm=minimal`;
            const raw = String(manual?.url || '');
            return raw.replace('/edit', '/preview?embedded=true').replace('/view', '/preview?embedded=true');
        };
        const getHelpManualOriginalUrl = (manual) => {
            const docId = manual?.documentId || manual?.fileId || '';
            if (docId) return `https://docs.google.com/document/d/${docId}/edit`;
            return String(manual?.url || '');
        };
        const openHelpManual = (manual) => {
            const previewUrl = getHelpManualPreviewUrl(manual);
            if (!previewUrl && !manual?.content) return alert('Este manual não possui link ou conteúdo de visualização.');
            const normalized = { ...manual, previewUrl, originalUrl: getHelpManualOriginalUrl(manual) };
            setHelpManualOpen(normalized);
            setHelpManualNotesVisible(true);
            try { setHelpManualNotes(localStorage.getItem(`gestao_pessoas_manual_notes_v1_${helpManualKey(manual)}`) || ''); } catch { setHelpManualNotes(''); }
        };
        const saveHelpManualNotes = (value) => {
            setHelpManualNotes(value);
            if (!helpManualOpen) return;
            try { localStorage.setItem(`gestao_pessoas_manual_notes_v1_${helpManualKey(helpManualOpen)}`, value); } catch {}
        };

        const saveHelpSuggestion = async () => {
            if (!helpSuggestion.title.trim() || !helpSuggestion.description.trim()) return alert('Informe o título e a descrição.');
            try {
                const id = `sugestao_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
                const now = new Date().toISOString();
                const payload = {
                    codigo: id, tipo: helpSuggestion.type, tituloCurto: helpSuggestion.title.trim(), tela: helpSuggestion.screen || 'Ajuda e Melhorias',
                    descricao: helpSuggestion.description.trim(), descricaoHtml: helpSuggestion.description.trim().replace(/\n/g, '<br/>'),
                    usuarioId: currentUser?.id || '', usuarioNome: currentUser?.name || currentUser?.displayName || '', usuarioEmail: currentUser?.email || '',
                    criadoEm: now, atualizadoEm: now, status: 'Enviado', prioridade: 'Não definida', anexos: [],
                    historico: [{ data: now, usuarioNome: currentUser?.name || currentUser?.email || 'Usuário', usuarioEmail: currentUser?.email || '', acao: 'Sugestão enviada', status: 'Enviado' }]
                };
                if (db) await setDoc(doc(db, helpCollectionPath(HELP_SUGGESTIONS_COLLECTION), id), payload);
                setHelpSuggestions(prev => [{ id, ...payload }, ...prev]);
                setHelpSuggestion({ title: '', type: 'Sugestão', screen: '', description: '' });
                setNewSuggestionOpen(false);
                alert('Sugestão enviada com sucesso.');
            } catch (error) {
                console.error('Erro ao salvar sugestão:', error);
                alert('Não foi possível salvar a sugestão.');
            }
        };

        const saveHelpComplement = async ({ requestDiscard = false } = {}) => {
            const item = helpComplementSuggestion;
            if (!item?.id || !db) return;
            const text = String(helpComplementText || '').trim();
            if (!requestDiscard && !text) return alert('Escreva o complemento ou correção.');
            const now = new Date().toISOString();
            const authorName = currentUser?.name || currentUser?.displayName || currentUser?.email || 'Usuário';
            const observation = requestDiscard ? (text || 'Solicitação de desconsideração enviada pelo solicitante.') : text;
            const updated = {
                ...item,
                atualizadoEm: now,
                solicitantePediuDesconsideracao: requestDiscard || item.solicitantePediuDesconsideracao === true,
                ultimoComplementoSolicitante: observation,
                historico: [...(item.historico || []), {
                    data: now, usuarioNome: authorName, usuarioEmail: currentUser?.email || '',
                    acao: requestDiscard ? 'Pedido de desconsideração enviado pelo solicitante' : 'Complemento/correção enviado pelo solicitante',
                    observacao: observation, status: item.status || 'Enviado'
                }],
                notificacoes: [...(item.notificacoes || []), {
                    id: `notif_${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
                    tipo: requestDiscard ? 'desconsideracao_solicitante_admin' : 'comentario_solicitante_admin',
                    destino: 'admins',
                    titulo: requestDiscard ? 'Pedido de desconsideração' : 'Complemento em sugestão',
                    mensagem: `${authorName} ${requestDiscard ? 'pediu a desconsideração da' : 'complementou a'} sugestão ${item.codigo || item.id}.`,
                    criadoEm: now, autorEmail: currentUser?.email || '', autorNome: authorName, lidaPor: []
                }]
            };
            try {
                await setDoc(doc(db, helpCollectionPath(HELP_SUGGESTIONS_COLLECTION), item.id), updated, { merge: true });
                setHelpSuggestions(prev => prev.map(x => x.id === item.id ? updated : x));
                setHelpComplementSuggestion(null); setHelpComplementText(''); setHelpComplementFiles([]);
                alert(requestDiscard ? 'Pedido de desconsideração enviado.' : 'Complemento salvo e administradores notificados.');
            } catch (error) {
                console.error('Erro ao complementar sugestão:', error);
                alert('Não foi possível salvar o complemento.');
            }
        };

        const saveHelpSuggestionAdmin = async () => {
            if (!helpActionSuggestion?.id || !db) return;
            const now = new Date().toISOString();
            const current = helpSuggestions.find(x => x.id === helpActionSuggestion.id) || helpActionSuggestion;
            const updated = { ...current, ...helpActionSuggestion, atualizadoEm: now, historico: [...(current.historico || []), { data: now, usuarioNome: currentUser?.name || currentUser?.email || 'Usuário', usuarioEmail: currentUser?.email || '', acao: 'Dados administrativos atualizados', status: helpActionSuggestion.status || 'Enviado', observacao: helpActionSuggestion.observacaoAdmin || '' }] };
            await setDoc(doc(db, helpCollectionPath(HELP_SUGGESTIONS_COLLECTION), updated.id), updated, { merge: true });
            setHelpSuggestions(prev => prev.map(x => x.id === updated.id ? updated : x));
            setHelpActionSuggestion(null);
        };

        const saveHelpConfig = async () => {
            if (!db) return;
            const payload = { id:'config_geral', tipo:'config_geral', driveFolderId: helpConfig.driveFolderId || '', appsScriptUrl: helpConfig.appsScriptUrl || '', uploadToken: helpConfig.uploadToken || '', manualDriveFolderId: helpConfig.manualDriveFolderId || '', manualAppsScriptUrl: helpConfig.manualAppsScriptUrl || '', superAdminEmails:String(helpConfig.superAdmins||'').split(/[;,\n]+/).map(x=>x.trim()).filter(Boolean), usuariosResponsaveis:helpConfig.responsaveis||[], atualizadoEm:new Date().toISOString(), atualizadoPor:currentUser?.email||'' };
            await setDoc(doc(db, helpCollectionPath(HELP_CONFIG_COLLECTION), 'config_geral'), payload, { merge:true });
            setHelpConfigOpen(false);
        };

        const filteredHelpSuggestions = helpSuggestions.filter(item => {
            const closed = ['Concluído','Não será feito'].includes(item.status);
            if (helpSuggestionFilter === 'pendentes' && closed) return false;
            if (helpSuggestionFilter === 'concluidas' && !closed) return false;
            const q = helpSuggestionSearch.trim().toLowerCase();
            if (!q) return true;
            return [item.status,item.usuarioNome,item.tituloCurto,item.tela,item.prioridade,item.tipo].join(' ').toLowerCase().includes(q);
        });

        return (
            <div className={`min-h-screen flex font-sans transition-colors duration-300 facil-theme-${themeMode} app-shell-${themeMode} ${themeMode === 'light' ? 'bg-[#f7f4ee] text-slate-900' : themeMode === 'acad' ? 'dark bg-[#071c1a] text-emerald-50' : 'dark bg-slate-950 text-slate-100'}`}>
                <style>{`
                :root { --app-accent:#0891b2; --app-accent-2:#4f46e5; --app-bg:#020617; --app-panel:#0f172a; --app-card:#1e293b; --app-line:#334155; --app-text:#f8fafc; --app-muted:#94a3b8; }
                .facil-theme-acad { --app-accent:#00a889; --app-accent-2:#0f766e; --app-bg:#061f1c; --app-panel:#0b2f2a; --app-card:#0f3d35; --app-line:#1e6b5c; --app-text:#ecfffa; --app-muted:#99d9cb; }
                .facil-theme-light { --app-accent:#0f7c72; --app-accent-2:#d97706; --app-bg:#f7f4ee; --app-panel:#fffaf2; --app-card:#ffffff; --app-line:#dfcdb4; --app-text:#102a2b; --app-muted:#64748b; }
                .app-modern-sidebar { background:linear-gradient(180deg,var(--app-panel),var(--app-bg)); border-color:var(--app-line); }
                .facil-theme-light .app-modern-sidebar { color:#123438; background:#fffaf2; }
                .facil-theme-light .app-modern-sidebar [class*="text-slate-"] { color:#49666b !important; }
                .app-menu-icon-box { width:34px;height:34px;border-radius:10px;display:inline-flex;align-items:center;justify-content:center;border:1px solid color-mix(in srgb,var(--app-accent) 50%,transparent);background:color-mix(in srgb,var(--app-accent) 12%,transparent); }
                .app-menu-number { min-width:34px;height:26px;border-radius:9px;display:inline-flex;align-items:center;justify-content:center;font-size:11px;font-weight:900;background:color-mix(in srgb,var(--app-accent) 9%,white);color:var(--app-text);border:1px solid color-mix(in srgb,var(--app-accent) 35%,transparent); }
                .facil-theme-light .app-menu-number { color:#123438; }
                .app-topbar { min-height:58px;display:flex;align-items:stretch;background:color-mix(in srgb,var(--app-panel) 92%,transparent);border-color:var(--app-line); }
                .app-modern-tab { min-width:145px;max-width:240px;height:42px;padding:0 12px;border:1px solid var(--app-line);border-bottom:none;border-radius:12px 12px 0 0;display:flex;align-items:center;gap:8px;font-size:12px;font-weight:800;color:var(--app-muted);background:color-mix(in srgb,var(--app-card) 85%,transparent); }
                .app-modern-tab.is-active { color:var(--app-text);border-color:var(--app-accent);box-shadow:inset 0 3px 0 var(--app-accent);background:var(--app-card); }
                .app-tab-dot { color:var(--app-accent); }.app-tab-close{margin-left:auto;padding:3px;border-radius:999px}.app-tab-close:hover{background:rgba(148,163,184,.2)}
                .app-circle-action { width:38px;height:38px;border-radius:999px;display:inline-flex;align-items:center;justify-content:center;border:1px solid var(--app-line);color:var(--app-accent);background:var(--app-card);position:relative; }.app-circle-action:hover{transform:translateY(-1px);box-shadow:0 5px 16px rgba(0,0,0,.15)}
                .app-logout-action{color:#fb7185}.app-notification-badge{position:absolute;right:-4px;top:-5px;background:#ef4444;color:white;border-radius:999px;min-width:18px;height:18px;padding:0 4px;font-size:9px;font-weight:900;display:flex;align-items:center;justify-content:center}
                .app-zoom-control { height:38px;border:1px solid var(--app-line);border-radius:12px;display:flex;align-items:center;overflow:hidden;background:var(--app-card);color:var(--app-text)}.app-zoom-control button{height:100%;padding:0 10px;font-weight:900}.app-zoom-control strong{padding:0 10px;color:#2563eb;font-size:12px}
                .app-user-chip{height:40px;display:flex;align-items:center;gap:8px;padding:3px 10px 3px 4px;border-left:1px solid var(--app-line);color:var(--app-text)}.app-user-chip img,.app-user-chip>span{width:34px;height:34px;border-radius:999px;object-fit:cover;background:var(--app-accent);color:white;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:900}.app-user-chip div{display:flex;flex-direction:column;line-height:1.1}.app-user-chip b{font-size:11px;white-space:nowrap}.app-user-chip small{font-size:9px;color:var(--app-muted)}
                .app-dialog{background:var(--app-card);border-color:var(--app-line);color:var(--app-text)}.app-dialog-header{padding:18px 22px;border-bottom:1px solid var(--app-line);display:flex;align-items:center;gap:12px}.app-dialog-header>svg{color:var(--app-accent)}.app-dialog-header h3{font-size:18px;font-weight:900}.app-dialog-header p{font-size:11px;color:var(--app-muted);margin-top:2px}.app-dialog-header>button,.app-close-btn{margin-left:auto}.app-dialog-footer{padding:14px 20px;border-top:1px solid var(--app-line);display:flex;justify-content:flex-end;gap:10px}
                .app-cache-option{display:flex;gap:12px;align-items:flex-start;border:1px solid var(--app-line);border-radius:14px;padding:14px;cursor:pointer}.app-cache-option.is-selected{border-color:var(--app-accent);background:color-mix(in srgb,var(--app-accent) 10%,transparent)}.app-cache-option b{display:block}.app-cache-option small{display:block;color:var(--app-muted);margin-top:3px}.app-primary-btn,.app-secondary-btn{height:40px;border-radius:10px;padding:0 16px;display:inline-flex;align-items:center;justify-content:center;gap:7px;font-weight:900;font-size:12px}.app-primary-btn{background:linear-gradient(135deg,var(--app-accent),var(--app-accent-2));color:white}.app-secondary-btn{border:1px solid var(--app-line);color:var(--app-text)}
                .app-tab-button{height:38px;padding:0 14px;border:1px solid var(--app-line);border-radius:10px;display:inline-flex;align-items:center;gap:7px;font-size:12px;font-weight:900}.app-tab-button.active{background:var(--app-accent);color:white;border-color:var(--app-accent)}
                .app-help-search{display:flex;gap:12px;align-items:end;border:1px solid var(--app-line);border-radius:16px;padding:15px;background:color-mix(in srgb,var(--app-panel) 55%,transparent)}.app-help-search input,.app-help-search select,.app-suggestion-form input,.app-suggestion-form select,.app-suggestion-form textarea{width:100%;border:1px solid var(--app-line);border-radius:10px;background:var(--app-panel);color:var(--app-text);padding:10px 12px;outline:none}.app-help-search input{padding-left:36px}.app-help-search label{font-size:10px;font-weight:900;color:var(--app-muted)}
                .manual-layout{margin-top:16px}.manual-cards{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}.manual-vitrine{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px}.manual-lista{display:flex;flex-direction:column;gap:8px}.manual-item{position:relative;border:1px solid var(--app-line);border-radius:18px;padding:16px;background:linear-gradient(135deg,color-mix(in srgb,var(--app-accent) 9%,var(--app-card)),var(--app-card));display:flex;gap:12px;align-items:flex-start;min-height:165px}.manual-lista .manual-item{min-height:auto;align-items:center}.manual-vitrine .manual-item{min-height:210px}.manual-icon{width:42px;height:42px;border-radius:13px;background:var(--app-accent);color:white;display:flex;align-items:center;justify-content:center;flex:none}.manual-content{flex:1}.manual-content small{font-size:9px;text-transform:uppercase;font-weight:900;color:var(--app-muted)}.manual-content h4{font-size:14px;font-weight:900;margin-top:4px}.manual-content p{font-size:11px;color:var(--app-muted);margin-top:8px;line-height:1.5}.manual-open{position:absolute;left:16px;right:16px;bottom:14px;background:var(--app-accent);color:white;border-radius:9px;padding:8px;font-size:11px;font-weight:900}.manual-lista .manual-open{position:static;width:auto;margin-left:auto}.app-suggestion-form{border:1px solid var(--app-line);border-radius:18px;padding:22px;background:var(--app-panel);display:flex;flex-direction:column;gap:14px}.app-suggestion-form h3{font-size:20px;font-weight:900}.app-suggestion-form p{font-size:12px;color:var(--app-muted)}.app-suggestion-form label{font-size:11px;font-weight:900}.app-suggestion-form textarea{min-height:220px;margin-top:6px}.app-suggestion-form input,.app-suggestion-form select{margin-top:6px}
                .facil-theme-light .app-topbar,.facil-theme-light .app-dialog,.facil-theme-light .app-modern-tab,.facil-theme-light .app-circle-action,.facil-theme-light .app-zoom-control{box-shadow:0 4px 18px rgba(89,65,35,.07)}
                @media(max-width:1100px){.app-user-chip div{display:none}.manual-cards{grid-template-columns:repeat(2,minmax(0,1fr))}.app-topbar-actions{overflow-x:auto}.app-zoom-control{display:none}}@media(max-width:760px){.manual-cards,.manual-vitrine{grid-template-columns:1fr}.app-user-chip{display:none}.app-modern-tab{min-width:120px}.app-help-search{flex-direction:column;align-items:stretch}}


                /* v63_340 - refinamentos de barra, menu e central de ajuda */
                .app-topbar{min-height:41px!important;height:41px!important}.app-topbar-tabs{padding-top:3px!important}.app-modern-tab{height:30px!important;min-width:130px!important;border-radius:9px 9px 0 0!important;font-size:11px!important}.app-topbar-actions{padding:3px 8px!important;gap:5px!important;overflow:visible!important}.app-circle-action{width:29px!important;height:29px!important}.app-zoom-control{height:29px!important;border-radius:9px!important}.app-zoom-control button{padding:0 8px!important}.app-zoom-control strong{padding:0 8px!important;font-size:11px!important}.app-user-chip{height:31px!important;min-width:168px!important;padding-right:8px!important;overflow:visible!important}.app-user-chip img,.app-user-chip>span{width:27px!important;height:27px!important}.app-user-chip div{display:flex!important;min-width:120px!important}.app-user-chip b{display:block!important;font-size:10px!important;max-width:150px;overflow:hidden;text-overflow:ellipsis}.app-user-chip small{font-size:8px!important}
                .app-sidebar-compact-header{height:82px;padding:14px 16px;border-bottom:1px solid var(--app-line);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;position:relative;background:color-mix(in srgb,var(--app-panel) 82%,transparent)}.app-sidebar-close{position:absolute;right:12px;top:12px;width:34px;height:34px;border-radius:999px;border:1px solid var(--app-line);display:flex;align-items:center;justify-content:center;color:var(--app-accent);background:var(--app-card)}.app-sidebar-brand{display:flex;align-items:baseline;gap:7px}.app-sidebar-brand span{font-size:11px;font-weight:900;letter-spacing:.18em;color:var(--app-accent)}.app-sidebar-brand strong{font-size:22px;font-weight:900;color:var(--app-text)}
                .app-modern-sidebar{width:272px}.app-modern-sidebar>div.flex-1{padding:10px 9px!important}.app-modern-sidebar .group{border:1px solid var(--app-line);border-radius:10px!important;margin-bottom:4px;background:color-mix(in srgb,var(--app-card) 84%,transparent);color:var(--app-text)!important}.app-modern-sidebar .group:hover{background:color-mix(in srgb,var(--app-accent) 9%,var(--app-card))!important}.app-modern-sidebar .group.bg-blue-600{background:var(--app-accent)!important;color:white!important}.app-menu-icon-box{width:36px;height:36px!important;border-radius:10px!important}.app-menu-number{min-width:37px!important;height:27px!important}.app-submenu-number{min-width:46px;height:23px;border-radius:8px;display:inline-flex;align-items:center;justify-content:center;font-size:9px;font-weight:900;border:1px solid color-mix(in srgb,var(--app-accent) 35%,transparent);background:color-mix(in srgb,var(--app-accent) 8%,var(--app-card));color:var(--app-text)}.app-modern-sidebar .group\/sub{margin:3px 0;padding:7px 8px!important;border:1px solid var(--app-line)!important;border-radius:9px!important;background:var(--app-card)!important;color:var(--app-text)!important}.app-modern-sidebar .group\/sub:hover,.app-modern-sidebar .group\/sub.bg-blue-600\/20{background:color-mix(in srgb,var(--app-accent) 12%,var(--app-card))!important;border-color:var(--app-accent)!important}.app-modern-sidebar [class*="opacity-0"]{opacity:1!important}.app-modern-sidebar .text-yellow-400{color:#f4b400!important}
                .app-help-center{background:var(--app-panel)!important}.app-help-header{padding:12px 18px!important}.app-primary-btn.compact{height:34px}.app-primary-btn.orange{background:#d97706}.app-tab-button.violet.active{background:#7c3aed}.app-manual-body{display:grid;grid-template-columns:minmax(0,1fr) 300px;gap:16px}.app-manual-aside{border:1px solid var(--app-line);border-radius:16px;padding:16px;background:var(--app-card)}.app-manual-aside h3{font-weight:900;display:flex;gap:8px;align-items:center}.app-manual-aside p{font-size:11px;color:var(--app-muted);margin-top:8px}.manual-star{position:absolute;right:12px;top:12px;width:30px;height:30px;border:1px solid var(--app-line);border-radius:999px;display:flex;align-items:center;justify-content:center;background:var(--app-card);color:var(--app-accent)}
                .app-segmented{display:flex;border:1px solid var(--app-line);border-radius:10px;overflow:hidden}.app-segmented button{padding:7px 12px;font-size:11px;font-weight:900;background:var(--app-card)}.app-segmented button.active{background:var(--app-accent);color:white}.app-suggestion-filters{border:1px solid var(--app-line);border-radius:14px;padding:9px;background:var(--app-card)}.app-suggestion-filters input,.app-suggestion-popup input,.app-suggestion-popup select,.app-suggestion-popup textarea,.app-config-popup input,.app-config-popup textarea,.app-action-popup input,.app-action-popup select,.app-action-popup textarea{width:100%;border:1px solid var(--app-line);border-radius:9px;padding:9px 11px;background:var(--app-card);color:var(--app-text)}.app-suggestion-table-wrap{border:1px solid var(--app-line);border-radius:15px;overflow:auto;background:var(--app-card)}.app-suggestion-table{width:100%;min-width:1280px;font-size:11px}.app-suggestion-table th{background:var(--app-accent);color:white;padding:10px;text-align:left;font-weight:900}.app-suggestion-table td{padding:9px;border-top:1px solid var(--app-line)}.suggestion-status,.suggestion-type{display:inline-flex;padding:5px 8px;border-radius:999px;background:#c026d3;color:white;font-weight:900}.suggestion-type{background:#7c3aed;border-radius:8px}.suggestion-actions{display:flex;gap:5px}.suggestion-actions button{width:29px;height:29px;border-radius:8px;border:1px solid var(--app-accent);color:var(--app-accent);display:flex;align-items:center;justify-content:center}.app-suggestion-popup{width:min(920px,96vw)}.app-config-popup{width:min(1380px,97vw);height:min(900px,94vh);display:flex;flex-direction:column}.app-config-popup>div.p-5{flex:1}.app-config-popup section{border:1px solid var(--app-line);border-radius:15px;padding:15px;background:var(--app-card)}.app-config-popup h4{font-weight:900;margin-bottom:12px}.app-config-popup label,.app-suggestion-popup label,.app-action-popup label{font-size:11px;font-weight:900;display:block}.app-config-popup label input,.app-action-popup label input,.app-action-popup label select,.app-action-popup label textarea,.app-suggestion-popup label input,.app-suggestion-popup label select,.app-suggestion-popup label textarea{margin-top:5px}.app-config-popup textarea{min-height:90px}.rich-editor-toolbar{border:1px solid var(--app-line);border-bottom:0;border-radius:9px 9px 0 0;padding:7px;display:flex;gap:5px}.rich-editor-toolbar button{border:1px solid var(--app-line);border-radius:5px;padding:3px 8px;font-size:10px}.app-suggestion-popup textarea{min-height:210px;border-radius:0 0 9px 9px}.app-action-popup{width:min(1150px,96vw);max-height:92vh;overflow:auto}.description-preview{border:1px solid var(--app-line);border-radius:12px;padding:14px;background:var(--app-card)}.app-logs-popup{width:min(1030px,96vw);height:min(760px,92vh);display:flex;flex-direction:column}.app-logs-popup>div.p-5{flex:1}.log-card{border:1px solid var(--app-line);border-radius:13px;padding:13px;background:var(--app-card);display:flex;flex-direction:column;gap:5px}.log-card small,.log-card span{font-size:10px;color:var(--app-muted)}.log-card b{font-size:13px}.log-card p{font-size:11px}
                @media(max-width:1050px){.app-user-chip{min-width:145px!important}.app-user-chip div{display:flex!important}.app-manual-body{grid-template-columns:1fr}.app-manual-aside{display:none}}


                /* v65_342 - fidelidade visual dos temas, menu e abas */
                .app-shell-acad{background:#020d18!important;color:#ecfffa!important}.facil-theme-acad body,.facil-theme-acad #root{background:#020d18!important}.facil-theme-acad .app-modern-sidebar{background:#073a32!important;border-color:#0f766e!important}.facil-theme-acad .app-topbar{background:#06352f!important;border-color:#0f766e!important}.facil-theme-acad .app-modern-tab{background:#07443a!important;border-color:#0e8f7d!important;color:#d7fff6!important}.facil-theme-acad .app-modern-tab.is-active{background:#0a5144!important;box-shadow:inset 0 3px 0 #00d6ae!important}.facil-theme-acad main,.facil-theme-acad .app-main-content{background:#020617!important}.facil-theme-acad .bg-white{background-color:#0b2f2a!important}.facil-theme-acad .bg-slate-50,.facil-theme-acad .bg-gray-50{background-color:#082720!important}.facil-theme-acad .text-gray-800,.facil-theme-acad .text-slate-900,.facil-theme-acad .text-gray-700{color:#ecfffa!important}.facil-theme-acad .border-gray-200,.facil-theme-acad .border-slate-200,.facil-theme-acad .border-gray-300{border-color:#1e6b5c!important}
                .app-sidebar-compact-header{height:86px!important;align-items:flex-start!important;padding:17px 18px!important}.app-sidebar-brand{display:flex!important;align-items:center!important;gap:10px!important}.app-sidebar-brand>div{display:flex;align-items:baseline;gap:7px}.app-sidebar-brand-icon{color:var(--app-accent);flex:none}.app-sidebar-brand span{font-size:9px!important}.app-sidebar-brand strong{font-size:23px!important}.app-sidebar-close{right:14px!important;top:14px!important}
                .app-modern-sidebar{width:302px!important}.app-modern-sidebar>div.flex-1{padding:12px 10px!important}.app-sidebar-main-item{height:58px!important;padding:8px 10px!important;margin-bottom:5px!important;border-radius:11px!important}.app-menu-icon-box{width:40px!important;height:40px!important;min-width:40px!important;border-color:var(--menu-icon-color)!important;color:var(--menu-icon-color)!important;background:color-mix(in srgb,var(--menu-icon-color) 11%,transparent)!important}.app-menu-icon-box svg{color:var(--menu-icon-color)!important}.app-menu-number{min-width:42px!important;width:42px!important;height:28px!important;background:#eaf9f5!important;color:#174e4a!important;border-color:#9fd8cf!important}.facil-theme-dark .app-menu-number,.facil-theme-acad .app-menu-number{background:#093d37!important;color:#f0fffb!important;border-color:#1d7669!important}.app-sidebar-main-item>div:first-child{min-width:0}.app-sidebar-main-item .font-semibold{font-size:13px!important;overflow:hidden;text-overflow:ellipsis}.app-menu-favorite,.app-submenu-favorite{width:34px!important;height:34px!important;min-width:34px!important;max-width:34px!important;padding:0!important;margin:0!important;display:flex!important;align-items:center!important;justify-content:center!important;border-radius:9px!important;border:1px solid #f4b400!important;background:#fff8cf!important;color:#f4b400!important;opacity:1!important;overflow:hidden!important}.app-menu-favorite svg{width:18px!important;height:18px!important}.app-submenu-favorite{width:28px!important;height:28px!important;min-width:28px!important}.app-submenu-favorite svg{width:14px!important;height:14px!important}.facil-theme-acad .app-menu-favorite,.facil-theme-acad .app-submenu-favorite,.facil-theme-dark .app-menu-favorite,.facil-theme-dark .app-submenu-favorite{background:#fff6bd!important;border-color:#ffe56b!important;color:#eab308!important}.app-sidebar-sub-item{min-height:42px!important;padding:7px 8px!important}.app-submenu-number{min-width:49px!important;color:var(--app-text)!important}.facil-theme-light .app-sidebar-sub-item{color:#557078!important;background:#fffaf2!important}.facil-theme-light .app-sidebar-sub-item:hover,.facil-theme-light .app-sidebar-sub-item.bg-blue-600\/20{background:#dfe9fa!important;color:#365a77!important}.app-modern-sidebar .ml-4.mt-1{margin-left:17px!important;padding-left:8px!important;border-color:color-mix(in srgb,var(--app-accent) 65%,transparent)!important}
                .facil-theme-acad .app-main-shell,.facil-theme-acad .app-main-shell>div,.facil-theme-acad .app-main-content,.facil-theme-acad main{background:#020f1c!important;color:#ecfffa!important}
                .facil-theme-acad main [class*="bg-slate-9"],.facil-theme-acad main [class*="bg-slate-8"],.facil-theme-acad main [class*="bg-gray-9"],.facil-theme-acad main [class*="bg-gray-8"]{background-color:#082b27!important}
                .facil-theme-acad main [class*="bg-slate-7"],.facil-theme-acad main [class*="bg-gray-7"]{background-color:#0b3a33!important}
                .facil-theme-acad main [class*="border-slate"],.facil-theme-acad main [class*="border-gray"]{border-color:#1c6f61!important}
                .facil-theme-acad main [class*="text-slate-"],.facil-theme-acad main [class*="text-gray-"]{color:#c8f3e9!important}
                .facil-theme-acad main table tbody tr{background:#0b302b!important}.facil-theme-acad main table tbody tr:nth-child(even){background:#0d3932!important}.facil-theme-acad main table thead{background:#073f37!important}
                .app-manual-reader{width:min(1500px,98vw)!important;height:min(900px,95vh)!important;max-width:none!important;display:flex!important;flex-direction:column!important;overflow:hidden!important}.app-manual-reader-grid{display:grid;grid-template-columns:1fr;height:100%;min-height:0}.app-manual-reader-grid.with-notes{grid-template-columns:minmax(0,1fr) 360px}.app-manual-document{min-height:0;background:#fff;overflow:hidden}.app-manual-document iframe{width:100%;height:100%;border:0;background:#fff}.app-manual-notes{padding:16px;overflow:auto;border-left:1px solid var(--app-line);background:var(--app-panel)}.app-manual-notes h4{font-size:16px;font-weight:900}.app-manual-notes p{font-size:11px;color:var(--app-muted);margin:5px 0 14px}.app-manual-notes textarea{width:100%;min-height:260px}.app-manual-note-tip{margin-top:14px;padding:12px;border:1px dashed var(--app-line);border-radius:12px;font-size:11px;color:var(--app-muted)}.app-complement-popup{width:min(760px,96vw)!important}.app-danger-btn{border:1px solid #fca5a5;color:#dc2626;background:#fff1f2;border-radius:10px;padding:10px 14px;font-weight:800}.app-dialog-header small{display:block;color:var(--app-accent);font-weight:800;text-transform:uppercase}

                .app-topbar{height:43px!important;min-height:43px!important;max-height:43px!important;display:grid!important;grid-template-columns:minmax(0,1fr) auto!important;overflow:hidden!important}.app-topbar-tabs{height:43px!important;padding:4px 4px 0!important;overflow-x:auto!important;overflow-y:hidden!important;white-space:nowrap!important;align-items:flex-end!important}.app-modern-tab{height:35px!important;min-width:145px!important;max-width:210px!important;flex:0 0 auto!important;padding:0 10px!important}.app-topbar-actions{height:43px!important;max-width:650px!important;padding:5px 8px!important;flex-wrap:nowrap!important;background:inherit!important;position:relative!important;z-index:3!important}.app-circle-action{width:31px!important;height:31px!important;min-width:31px!important}.app-zoom-control{height:31px!important;min-width:145px!important}.app-user-chip{height:33px!important;min-width:190px!important;max-width:235px!important;padding-right:7px!important}.app-user-chip div{display:flex!important;min-width:0!important;flex:1}.app-user-chip b{display:block!important;max-width:180px!important}.app-user-chip small{display:block!important}.app-topbar-tabs::-webkit-scrollbar{height:5px}.app-topbar-tabs::-webkit-scrollbar-thumb{background:var(--app-line);border-radius:999px}
                .facil-theme-light .app-modern-sidebar{background:#fffaf2!important}.facil-theme-light .app-sidebar-main-item{background:#fffdf9!important;border-color:#e6d3b7!important}.facil-theme-light .app-sidebar-main-item.bg-blue-600{background:#087f86!important}.facil-theme-light .app-menu-icon-box{background:#eef9f7!important}.facil-theme-light .app-sidebar-brand strong{color:#123438!important}
                @media(max-width:1250px){.app-modern-sidebar{width:285px!important}.app-user-chip{min-width:165px!important}.app-modern-tab{min-width:130px!important}.app-topbar-actions{max-width:570px!important}}

                /* v69_346 - comportamento do menu e tema Acad no App principal */
                .app-modern-sidebar.is-collapsed {
                    width:64px !important;
                    min-width:64px !important;
                    max-width:64px !important;
                }
                .app-modern-sidebar.is-collapsed .app-sidebar-compact-header {
                    width:64px !important;
                    min-width:64px !important;
                    padding:0 !important;
                    height:76px !important;
                    align-items:center !important;
                    justify-content:center !important;
                }
                .app-modern-sidebar.is-collapsed .app-sidebar-close {
                    position:static !important;
                    margin:0 auto !important;
                    width:40px !important;
                    height:40px !important;
                    border-radius:12px !important;
                }
                .app-modern-sidebar.is-collapsed > .flex-1 { padding-left:7px !important; padding-right:7px !important; }
                .app-modern-sidebar.is-collapsed .app-sidebar-main-item {
                    width:50px !important; height:50px !important; min-height:50px !important;
                    padding:5px !important; margin-left:auto !important; margin-right:auto !important; justify-content:center !important;
                }
                .app-modern-sidebar.is-collapsed .app-sidebar-main-item > div:first-child { justify-content:center !important; gap:0 !important; }
                .app-modern-sidebar.is-collapsed .app-menu-icon-box { width:38px !important; min-width:38px !important; height:38px !important; margin:0 !important; }
                .app-modern-sidebar.is-collapsed .app-menu-number,
                .app-modern-sidebar.is-collapsed .app-menu-favorite,
                .app-modern-sidebar.is-collapsed .app-submenu-favorite,
                .app-modern-sidebar.is-collapsed .app-sidebar-sub-item,
                .app-modern-sidebar.is-collapsed .app-submenu-number,
                .app-modern-sidebar.is-collapsed .app-sidebar-main-item span.font-semibold,
                .app-modern-sidebar.is-collapsed .app-sidebar-main-item > div:last-child,
                .app-modern-sidebar.is-collapsed .ml-4.mt-1,
                .app-modern-sidebar.is-collapsed .text-\[10px\] { display:none !important; }
                .facil-theme-acad,
                .facil-theme-acad.app-shell-acad,
                .facil-theme-acad .app-main-shell,
                .facil-theme-acad main,
                .facil-theme-acad .app-main-content {
                    background:#071c1a !important; background-color:#071c1a !important; color:#ecfffa !important;
                }
                .facil-theme-acad [class*="bg-[#020617]"],
                .facil-theme-acad [class*="bg-[#020f1c]"],
                .facil-theme-acad [class*="bg-[#0F172A]"],
                .facil-theme-acad [class*="bg-[#0f172a]"],
                .facil-theme-acad [class*="bg-slate-950"],
                .facil-theme-acad [class*="bg-slate-900"] { background-color:#071f1c !important; }
                .facil-theme-acad [class*="bg-[#1E293B]"],
                .facil-theme-acad [class*="bg-[#1e293b]"],
                .facil-theme-acad [class*="bg-slate-800"] { background-color:#0b2f2a !important; }
                .facil-theme-acad [class*="bg-slate-700"] { background-color:#0f3d35 !important; }
                .facil-theme-acad .app-help-center,
                .facil-theme-acad .app-dialog,
                .facil-theme-acad section,
                .facil-theme-acad article { border-color:rgba(45,212,191,.28); }



                /* ==========================================================
                   V71_348 - REFINAMENTOS GLOBAIS DE TEMA / MENU / ABAS
                   ========================================================== */
                .app-sidebar-compact-header{
                    min-height:86px!important;height:86px!important;padding:13px 52px 13px 16px!important;
                    display:flex!important;align-items:center!important;justify-content:flex-start!important;
                }
                .app-sidebar-brand{
                    width:100%!important;display:grid!important;grid-template-columns:27px minmax(0,1fr)!important;
                    grid-template-rows:auto auto!important;column-gap:10px!important;row-gap:1px!important;
                    align-items:center!important;justify-content:start!important;white-space:nowrap!important;
                }
                .app-sidebar-brand-icon{
                    grid-column:1!important;grid-row:1 / span 2!important;width:24px!important;height:24px!important;
                    align-self:center!important;color:var(--app-accent)!important;
                }
                .app-sidebar-brand-copy{
                    grid-column:2!important;grid-row:1 / span 2!important;display:grid!important;
                    grid-template-rows:14px 27px!important;align-items:center!important;justify-items:start!important;
                    min-width:0!important;line-height:1!important;
                }
                .app-sidebar-brand-copy>span{
                    display:block!important;margin:0!important;padding:0!important;font-size:9px!important;
                    line-height:12px!important;letter-spacing:.22em!important;font-weight:900!important;
                    color:var(--app-accent)!important;-webkit-text-fill-color:var(--app-accent)!important;
                }
                .app-sidebar-brand-copy>div{
                    display:flex!important;align-items:center!important;gap:7px!important;margin:0!important;padding:0!important;
                    min-width:0!important;
                }
                .app-sidebar-brand-copy strong{
                    display:block!important;font-size:22px!important;line-height:24px!important;font-weight:900!important;
                    color:var(--app-text)!important;-webkit-text-fill-color:var(--app-text)!important;
                }
                .app-sidebar-people-icon{
                    width:17px!important;height:17px!important;flex:none!important;color:#f59e0b!important;
                    stroke:#f59e0b!important;filter:drop-shadow(0 0 4px rgba(245,158,11,.28));
                }
                .facil-theme-light .app-sidebar-brand-copy strong{color:#123438!important;-webkit-text-fill-color:#123438!important}

                /* Reserva uma faixa exclusiva para estrela e notificações dos submenus. */
                .app-sidebar-sub-item{padding-right:48px!important;overflow:visible!important}
                .app-sidebar-sub-item .app-submenu-favorite{
                    position:absolute!important;right:5px!important;top:50%!important;transform:translateY(-50%)!important;
                    z-index:5!important;margin:0!important;
                }
                .app-sidebar-sub-item .app-submenu-notification{right:40px!important;z-index:7!important;white-space:nowrap!important}
                .app-sidebar-sub-item .app-submenu-notification-secondary{right:66px!important;z-index:7!important;white-space:nowrap!important}

                /* Estrela com contorno mais escuro para manter contraste nos três temas. */
                .app-menu-favorite,.app-submenu-favorite{
                    border-color:#d89200!important;box-shadow:inset 0 0 0 1px rgba(131,83,0,.22),0 1px 2px rgba(0,0,0,.08)!important;
                }
                .app-menu-favorite svg,.app-submenu-favorite svg{
                    stroke:#9a6700!important;stroke-width:2.25!important;
                }
                .app-menu-favorite.text-yellow-400 svg,.app-submenu-favorite.text-yellow-400 svg{
                    fill:#facc15!important;stroke:#8a5b00!important;
                }
                .facil-theme-acad .app-menu-favorite,.facil-theme-acad .app-submenu-favorite,
                .facil-theme-dark .app-menu-favorite,.facil-theme-dark .app-submenu-favorite{
                    border-color:#c88700!important;background:#fff3a6!important;color:#eab308!important;
                }

                /* Abas abertas: cada posição recebe identidade cromática em QUALQUER tema. */
                .app-modern-tab:nth-child(8n+1){--tab-accent:#06b6d4!important}
                .app-modern-tab:nth-child(8n+2){--tab-accent:#8b5cf6!important}
                .app-modern-tab:nth-child(8n+3){--tab-accent:#3b82f6!important}
                .app-modern-tab:nth-child(8n+4){--tab-accent:#10b981!important}
                .app-modern-tab:nth-child(8n+5){--tab-accent:#f97316!important}
                .app-modern-tab:nth-child(8n+6){--tab-accent:#ec4899!important}
                .app-modern-tab:nth-child(8n+7){--tab-accent:#6366f1!important}
                .app-modern-tab:nth-child(8n){--tab-accent:#14b8a6!important}
                .facil-theme-light .app-modern-tab{
                    background:color-mix(in srgb,var(--tab-accent) 10%,#fffaf2)!important;
                    border-color:color-mix(in srgb,var(--tab-accent) 58%,#dcc7a9)!important;
                    color:#294a52!important;
                }
                .facil-theme-light .app-modern-tab.is-active{
                    background:color-mix(in srgb,var(--tab-accent) 18%,#fff)!important;
                    border-color:var(--tab-accent)!important;color:#17353b!important;
                    box-shadow:inset 0 3px 0 var(--tab-accent),0 -1px 8px color-mix(in srgb,var(--tab-accent) 18%,transparent)!important;
                }
                .facil-theme-dark .app-modern-tab{
                    background:color-mix(in srgb,var(--tab-accent) 14%,#0f172a)!important;
                    border-color:color-mix(in srgb,var(--tab-accent) 60%,#334155)!important;
                    color:#dbeafe!important;
                }
                .facil-theme-dark .app-modern-tab.is-active{
                    background:color-mix(in srgb,var(--tab-accent) 25%,#111827)!important;border-color:var(--tab-accent)!important;
                }
                .facil-theme-acad .app-modern-tab{
                    background:color-mix(in srgb,var(--tab-accent) 18%,#073a32)!important;
                    border-color:color-mix(in srgb,var(--tab-accent) 65%,#167363)!important;
                    color:#e7fff9!important;
                }
                .facil-theme-acad .app-modern-tab.is-active{
                    background:color-mix(in srgb,var(--tab-accent) 30%,#08443b)!important;border-color:var(--tab-accent)!important;
                    box-shadow:inset 0 3px 0 var(--tab-accent),0 -1px 9px color-mix(in srgb,var(--tab-accent) 22%,transparent)!important;
                }

                /* Tema Acad: neutraliza definitivamente os azuis/pretos estruturais do tema escuro. */
                html.facil-theme-acad,html.facil-theme-acad body,html.facil-theme-acad #root,
                html.facil-theme-acad .app-shell-acad,html.facil-theme-acad .app-main-shell,
                html.facil-theme-acad main,html.facil-theme-acad .app-main-content{
                    background:#052a25!important;background-color:#052a25!important;color:#ecfffa!important;
                }
                html.facil-theme-acad main [class*="bg-slate-950"],
                html.facil-theme-acad main [class*="bg-slate-900"],
                html.facil-theme-acad main [class*="bg-gray-950"],
                html.facil-theme-acad main [class*="bg-gray-900"],
                html.facil-theme-acad main [class*="bg-[#020617]"],
                html.facil-theme-acad main [class*="bg-[#020f1c]"],
                html.facil-theme-acad main [class*="bg-[#0f172a]"],
                html.facil-theme-acad main [class*="bg-[#0F172A]"]{
                    background:#07352f!important;background-color:#07352f!important;
                }
                html.facil-theme-acad main [class*="bg-slate-800"],
                html.facil-theme-acad main [class*="bg-gray-800"],
                html.facil-theme-acad main [class*="bg-[#1e293b]"],
                html.facil-theme-acad main [class*="bg-[#1E293B]"]{
                    background:#0a4038!important;background-color:#0a4038!important;
                }
                html.facil-theme-acad main [class*="bg-slate-700"],
                html.facil-theme-acad main [class*="bg-gray-700"]{
                    background:#0d4b41!important;background-color:#0d4b41!important;
                }
                html.facil-theme-acad main [style*="#020617"],html.facil-theme-acad main [style*="#020f1c"],
                html.facil-theme-acad main [style*="#0f172a"],html.facil-theme-acad main [style*="#0F172A"],
                html.facil-theme-acad main [style*="rgb(2, 6, 23)"],html.facil-theme-acad main [style*="rgb(15, 23, 42)"]{
                    background:#07352f!important;background-color:#07352f!important;
                }
                html.facil-theme-acad main [style*="#1e293b"],html.facil-theme-acad main [style*="#1E293B"],
                html.facil-theme-acad main [style*="rgb(30, 41, 59)"]{
                    background:#0a4038!important;background-color:#0a4038!important;
                }
                html.facil-theme-acad main table thead,
                html.facil-theme-acad main table thead tr,
                html.facil-theme-acad main table thead th{
                    background:#07332e!important;background-color:#07332e!important;border-color:#1e6b5c!important;color:#bdece2!important;
                }
                html.facil-theme-acad main table tbody tr{background:#0a3b34!important;background-color:#0a3b34!important}
                html.facil-theme-acad main table tbody tr:nth-child(even){background:#0c463d!important;background-color:#0c463d!important}
                html.facil-theme-acad main .border-slate-800,html.facil-theme-acad main .border-slate-700,
                html.facil-theme-acad main [class*="border-slate"],html.facil-theme-acad main [class*="border-gray"]{border-color:#1d6d5f!important}
                html.facil-theme-acad main .text-slate-500,html.facil-theme-acad main .text-slate-400,
                html.facil-theme-acad main [class*="text-gray-500"],html.facil-theme-acad main [class*="text-gray-400"]{color:#a8ded3!important}
                html.facil-theme-acad main input,html.facil-theme-acad main select,html.facil-theme-acad main textarea{
                    background:#0a3a33!important;border-color:#277969!important;color:#ecfffa!important;
                }
                html.facil-theme-acad main input::placeholder,html.facil-theme-acad main textarea::placeholder{color:#8dc9bd!important}



                /* ==========================================================
                   V72_349 - TEMA CLARO / CABECALHO / SUBMENU
                   ========================================================== */
                /* Cabeçalho do módulo: ícone lateral proporcional e linhas compactas. */
                .app-sidebar-compact-header{
                    min-height:76px!important;height:76px!important;padding:10px 48px 10px 14px!important;
                    justify-content:center!important;
                }
                .app-sidebar-brand{
                    display:grid!important;grid-template-columns:38px minmax(0,1fr)!important;
                    grid-template-rows:11px 25px!important;column-gap:9px!important;row-gap:0!important;
                    align-items:center!important;width:100%!important;
                }
                .app-sidebar-brand-icon{
                    grid-column:1!important;grid-row:1 / span 2!important;width:34px!important;height:34px!important;
                    align-self:center!important;justify-self:center!important;color:#0f9f91!important;stroke:#0f9f91!important;
                    stroke-width:1.9!important;
                }
                .app-sidebar-brand-copy{
                    grid-column:2!important;grid-row:1 / span 2!important;display:grid!important;
                    grid-template-rows:11px 25px!important;row-gap:0!important;align-items:center!important;
                    justify-items:start!important;line-height:1!important;
                }
                .app-sidebar-brand-copy>span{
                    margin:0!important;padding:0!important;font-size:8.5px!important;line-height:10px!important;
                    letter-spacing:.2em!important;font-weight:900!important;color:#0f9f91!important;
                    -webkit-text-fill-color:#0f9f91!important;
                }
                .app-sidebar-brand-copy>div{margin:0!important;padding:0!important;gap:6px!important;line-height:1!important}
                .app-sidebar-brand-copy strong{
                    margin:0!important;padding:0!important;font-size:21px!important;line-height:23px!important;font-weight:900!important;
                }
                .app-sidebar-people-icon{width:16px!important;height:16px!important;margin-top:1px!important}
                .app-sidebar-close{top:11px!important;right:12px!important}

                /* Submenu selecionado: destaque inequívoco no tema claro. */
                .facil-theme-light .app-sidebar-sub-item.is-sub-active{
                    background:#d8eef1!important;border:1px solid #7ec7cc!important;border-left:4px solid #0f8f92!important;
                    color:#0c4f56!important;-webkit-text-fill-color:#0c4f56!important;font-weight:900!important;
                    box-shadow:0 1px 2px rgba(15,143,146,.08),inset 0 0 0 1px rgba(255,255,255,.45)!important;
                }
                .facil-theme-light .app-sidebar-sub-item.is-sub-active span:not(.app-submenu-notification):not(.app-submenu-notification-secondary){
                    color:#0c4f56!important;-webkit-text-fill-color:#0c4f56!important;font-weight:900!important;
                }
                .facil-theme-light .app-sidebar-sub-item.is-sub-active .app-submenu-number{
                    background:#bfe4e8!important;border-color:#65b7bd!important;color:#0b555b!important;
                    -webkit-text-fill-color:#0b555b!important;
                }

                /* Central de ajuda em tema claro: remove qualquer herança do tema escuro. */
                html.facil-theme-light .central-ajuda-root,
                html.facil-theme-light .central-ajuda-light{
                    background:#fffdf9!important;background-color:#fffdf9!important;color:#102a2b!important;
                    -webkit-text-fill-color:initial!important;border-color:#dfcdb4!important;
                }
                html.facil-theme-light .central-ajuda-light>div:first-child{
                    background:#fffaf2!important;background-color:#fffaf2!important;border-color:#dfcdb4!important;color:#102a2b!important;
                }
                html.facil-theme-light .central-ajuda-light .help-manual-card{
                    background:#eaf9fd!important;background-color:#eaf9fd!important;background-image:none!important;
                    border-color:#80d4e2!important;color:#123946!important;-webkit-text-fill-color:initial!important;
                    box-shadow:0 1px 3px rgba(16,74,82,.06)!important;
                }
                html.facil-theme-light .central-ajuda-light .help-manual-card:nth-child(3n+2){
                    background:#eef3ff!important;background-color:#eef3ff!important;border-color:#adc5ff!important;
                }
                html.facil-theme-light .central-ajuda-light .help-manual-card:nth-child(3n){
                    background:#faf5ff!important;background-color:#faf5ff!important;border-color:#d5b7ef!important;
                }
                html.facil-theme-light .central-ajuda-light .help-manual-card:nth-child(4n){
                    background:#fff8e9!important;background-color:#fff8e9!important;border-color:#f1c76d!important;
                }
                html.facil-theme-light .central-ajuda-light .help-manual-card h4,
                html.facil-theme-light .central-ajuda-light .help-manual-card .font-black{
                    color:#123946!important;-webkit-text-fill-color:#123946!important;
                }
                html.facil-theme-light .central-ajuda-light .help-manual-card [class*="text-slate-500"],
                html.facil-theme-light .central-ajuda-light .help-manual-card [class*="text-slate-600"]{
                    color:#56717a!important;-webkit-text-fill-color:#56717a!important;
                }
                html.facil-theme-light .central-ajuda-light .help-manual-card span[class*="bg-white"],
                html.facil-theme-light .central-ajuda-light .help-manual-card button[class*="bg-white"]{
                    background:#fff!important;background-color:#fff!important;color:#173c45!important;border-color:#d7c6ad!important;
                }
                html.facil-theme-light .central-ajuda-light .help-manual-card button.bg-teal-700,
                html.facil-theme-light .central-ajuda-light .help-manual-card button[class*="bg-teal-700"]{
                    background:#0f8077!important;background-color:#0f8077!important;color:#fff!important;
                    -webkit-text-fill-color:#fff!important;border-color:#0f8077!important;
                }
                html.facil-theme-light .central-ajuda-light aside{
                    background:#fffdf9!important;background-color:#fffdf9!important;color:#17383b!important;border-color:#dfcdb4!important;
                }

                /* Sugestões no tema claro: botões, filtros e tabela sem fundos escuros. */
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel{
                    background:transparent!important;color:#102a2b!important;-webkit-text-fill-color:initial!important;
                }
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel>div:first-child>button{
                    background:#fffdf9!important;background-color:#fffdf9!important;color:#0f6f70!important;
                    -webkit-text-fill-color:#0f6f70!important;border-color:#79bfc0!important;
                }
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel>div:first-child>button:hover{
                    background:#e8f6f4!important;background-color:#e8f6f4!important;color:#075d60!important;
                }
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel>div:first-child .inline-flex.overflow-hidden{
                    background:#fff!important;border-color:#d8c8b2!important;
                }
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel>div:first-child .inline-flex.overflow-hidden button{
                    background:#fff!important;background-color:#fff!important;color:#425b61!important;-webkit-text-fill-color:#425b61!important;
                }
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel>div:first-child .inline-flex.overflow-hidden button.bg-cyan-600{
                    background:#1497ad!important;background-color:#1497ad!important;color:#fff!important;-webkit-text-fill-color:#fff!important;
                }
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel table{
                    background:#fffdf9!important;background-color:#fffdf9!important;color:#102a2b!important;
                    border-color:#dccbb3!important;
                }
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel table thead,
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel table thead tr,
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel table thead th{
                    background:#0f7f78!important;background-color:#0f7f78!important;color:#fff!important;
                    -webkit-text-fill-color:#fff!important;border-color:#0b6863!important;
                }
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel table tbody tr,
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel table tbody tr.bg-white,
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel table tbody tr[class*="bg-slate"]{
                    background:#fffdf9!important;background-color:#fffdf9!important;color:#17383b!important;
                    -webkit-text-fill-color:initial!important;border-color:#eadbc6!important;
                }
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel table tbody tr:nth-child(even){
                    background:#f8f3eb!important;background-color:#f8f3eb!important;
                }
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel table tbody td,
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel table tbody td div{
                    color:#17383b!important;-webkit-text-fill-color:#17383b!important;
                }
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel table tbody .bg-fuchsia-600,
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel table tbody .bg-violet-600,
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel table tbody .bg-violet-700,
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel table tbody .bg-cyan-600,
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel table tbody .bg-emerald-600,
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel table tbody .bg-orange-600,
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel table tbody .bg-red-500{
                    color:#fff!important;-webkit-text-fill-color:#fff!important;
                }
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel input,
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel select,
                html.facil-theme-light .central-ajuda-light .help-suggestions-panel button[class*="border-slate"]{
                    background:#fff!important;background-color:#fff!important;color:#17383b!important;border-color:#d9c8b0!important;
                }

                    @keyframes coinBounce { 0%, 100% { transform: translateY(0) scale(1); filter: drop-shadow(0 0 5px rgba(16, 185, 129, 0.5)); } 50% { transform: translateY(-8px) scale(1.1); filter: drop-shadow(0 0 15px rgba(16, 185, 129, 0.9)); } }
                    .animate-coin { animation: coinBounce 2s infinite ease-in-out; }
                `}</style>

        {!isCardViewMode && (
                    <aside className={`${isCollapsed ? 'is-collapsed w-[74px]' : 'w-72'} app-modern-sidebar text-white flex flex-col shadow-xl z-30 transition-all duration-300 border-r shrink-0 relative`}>
        
                                        {/* Cabeçalho lateral compacto: controles globais ficam na barra superior */}
                                        <div className="app-sidebar-compact-header">
                                            <button onClick={() => setIsCollapsed(!isCollapsed)} className="app-sidebar-close" title={isCollapsed ? 'Expandir menu' : 'Recolher menu'}>
                                                {isCollapsed ? <Menu size={16}/> : <X size={17}/>} 
                                            </button>
                                            {!isCollapsed && <div className="app-sidebar-brand"><User size={24} className="app-sidebar-brand-icon"/><div className="app-sidebar-brand-copy"><span>MÓDULO</span><div><strong>Pessoas</strong><Users size={17} className="app-sidebar-people-icon"/><span className="app-sidebar-version" title={`Build ${APP_BUILD_ID}`}>{APP_VERSION}</span></div></div></div>}
                                        </div>

                                <div className="flex-1 overflow-y-auto py-4 px-3 space-y-1 custom-scrollbar">
                                    <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-2 mb-2 mt-2`}>
        {!isCollapsed && <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Menu Principal</span>}
        {/* Botão de Organizar Menus - oculto quando a barra estiver recolhida */}
        {!isCollapsed && (
            <button onClick={openMenuOrganizer} className="text-slate-500 hover:text-blue-400 transition-colors" title="Organizar Submenus em Pastas">
                <Settings2 size={16}/>
            </button>
        )}
    </div>
                            
                            {/* Mapeamento dos Itens do Menu */}
                            {menuStructureAvDes.map((item, menuIndex) => {
                                if (!hasModuleAccess(item.permission) && item.id !== 'favorites') return null;
                                
                                const isModuleActive = activeModuleId === item.id;
                                const hasSubItems = item.subItems && item.subItems.length > 0;
                                const isExpanded = expandedMenus[item.id];
                                
                                const showStarMain = item.id !== 'favorites';
                                const isFavMain = isFavorite(item.id);
                                const menuIconPalette = ['#f4b400','#22c55e','#06b6d4','#8b5cf6','#fb7185','#f97316','#14b8a6','#a855f7','#3b82f6','#ec4899'];
                                const menuIconColor = menuIconPalette[menuIndex % menuIconPalette.length];

                                // --- LÓGICA DE RENDERIZAÇÃO DE SUB-ITENS ---
                                // Função auxiliar para renderizar uma única linha de submenu (Para não repetir código)
                                const renderSubItemRow = (sub, subIndex = 0) => {
                                    const subTarget = `${item.id}:${sub.id}`;
                                    const isSubActive = (isModuleActive && activeTabId === sub.id) || safeSys === subTarget;
                                    const isFavSub = isFavorite(subTarget);

                                    return (
                                        <div key={sub.id} className={`app-sidebar-sub-item ${isSubActive ? 'is-sub-active' : ''} group/sub relative w-full flex items-center justify-between px-3 py-2 rounded-md text-xs font-medium transition-colors cursor-pointer ${isSubActive ? 'bg-blue-600/20 text-white font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
                                            <div onClick={() => { setSys(subTarget); setTimeout(() => setOpenId(sub.id), 50); }} className="flex-1 flex items-center gap-2 text-left min-w-0">
                                                <span className="app-submenu-number">{String(menuIndex + 1).padStart(2, '0')}.{String(subIndex + 1).padStart(2, '0')}</span>
                                                <span className="truncate">{sub.label}</span>
                                            </div>
                                            
                                            <div 
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite({ targetSys: subTarget, label: `${item.label} > ${sub.label}` }); }}
                                                className={`app-submenu-favorite ml-2 relative z-50 rounded-full cursor-pointer transition-all ${isFavSub ? 'opacity-100 text-yellow-400' : 'opacity-0 group-hover/sub:opacity-100 text-slate-600 hover:text-yellow-400'}`}
                                                title={isFavSub ? "Remover dos Favoritos" : "Adicionar aos Favoritos"}
                                            >
                                                <Star size={14} fill={isFavSub ? "currentColor" : "none"} />
                                            </div>
                                            
                                            {/* Badges de Notificação */}
                                            {sub.id === 'users' && pendingUsersCount > 0 && (<span className="app-submenu-notification absolute right-11 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>)}
                                            {sub.id === 'surveys' && pendingSurveysCount > 0 && (<span className="app-submenu-notification absolute right-11 top-1/2 -translate-y-1/2 bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm animate-pulse">{pendingSurveysCount}</span>)}
                                            {sub.id === 'surveys' && pendingHRRemindersCount > 0 && (<span className="app-submenu-notification-secondary absolute right-[68px] top-1/2 -translate-y-1/2 bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm animate-pulse" title="Lembretes de Acompanhamento Pendentes">{pendingHRRemindersCount}</span>)}
                                            
                                            {sub.id === 'cultura_acad' && collabCulturaTotalNotifications > 0 && (
                                            <span className="app-submenu-notification absolute right-11 top-1/2 -translate-y-1/2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm animate-pulse" title="Ciências pendentes ou retornos do RH não lidos">
                                                {collabCulturaTotalNotifications}
                                            </span>
                                            )}
                                            

                                            {sub.id === 'hr_cultura_acad' && hrCulturaBadgeCount > 0 && (<span className="app-submenu-notification absolute right-11 top-1/2 -translate-y-1/2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm animate-pulse" title="Novos Feedbacks de Cultura">{hrCulturaBadgeCount}</span>)}
                                            {sub.id === 'recognition' && recognitionNotifications?.total > 0 && (
                                                recognitionNotifications.isForMe 
                                                    ? <span className="app-submenu-notification absolute right-11 top-1/2 -translate-y-1/2 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow-lg animate-bounce border border-white">🎉 {recognitionNotifications.total}</span>
                                                    : <span className="app-submenu-notification absolute right-11 top-1/2 -translate-y-1/2 bg-blue-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">{recognitionNotifications.total}</span>
                                            )}
                                            {sub.id === 'acad_coins' && acadCoinsNotificationsTotal > 0 && (
                                                <span
                                                    className="app-submenu-notification absolute right-11 top-1/2 -translate-y-1/2 bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm animate-pulse"
                                                    title="Novas interações do AcadCoins"
                                                >
                                                    {acadCoinsNotificationsTotal}
                                                </span>
                                            )}
                                        </div>
                                    );
                                };

                                return (
                                    <div key={item.id}>
                                        {/* Módulo Principal */}
                                        <div style={{ '--menu-icon-color': menuIconColor }} className={`app-sidebar-main-item ${isModuleActive ? 'is-main-active' : ''} group relative w-full flex items-center justify-between px-4 py-2.5 rounded-lg transition-all duration-200 cursor-pointer ${isModuleActive ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
                                            <div 
                                                className="flex-1 flex items-center gap-3 text-left"
                                                onClick={() => { if (hasSubItems) { toggleMenu(item.id); } else { setSys(item.id); setOpenId(null); } }}
                                            >
                                                <span className="app-menu-icon-box"><item.icon size={18} className={isModuleActive ? 'text-cyan-300' : ''} /></span>
                                                {!isCollapsed && <span className="app-menu-number">{String(menuIndex + 1).padStart(2, '0')}</span>}
                                                {!isCollapsed && <span className={`font-semibold text-sm whitespace-nowrap ${isModuleActive ? 'text-white' : ''}`}>{item.label}</span>}
                                            </div>
                                            
                                            {!isCollapsed && <div className="flex items-center gap-2">
                                                {showStarMain && !hasSubItems && (
                                                    <div 
                                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite({ targetSys: item.id, label: item.label }); }}
                                                        className={`app-menu-favorite relative z-50 rounded-full cursor-pointer transition-all ${isFavMain ? 'opacity-100 text-yellow-400' : 'opacity-0 group-hover:opacity-100 text-slate-500 hover:text-yellow-400'}`}
                                                        title={isFavMain ? "Remover dos Favoritos" : "Adicionar aos Favoritos"}
                                                    >
                                                        <Star size={16} fill={isFavMain ? "currentColor" : "none"} />
                                                    </div>
                                                )}
                                                {hasSubItems && (
                                                    <div onClick={() => toggleMenu(item.id)}>
                                                        {isExpanded ? <ChevronDown size={14} className="transition-transform" /> : <ChevronRight size={14} className="transition-transform" />}
                                                    </div>
                                                )}
                                            </div>}
                                        </div>

                                        {/* Subitens Agrupados (Nova Lógica) */}
                                        {hasSubItems && isExpanded && !isCollapsed && (
                                            <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-slate-700 pl-2">
                                                {(() => {
                                                    // Verifica Permissões Base
                                                    const allowedSubs = item.subItems.filter(sub => {
                                                        if (!sub.permKey) return true;
                                                        return effectivePermissions[sub.permKey] === true;
                                                    });

                                                    // Recupera Configuração do Usuário
                                                    const config = currentUser?.preferences?.menuConfig || { groups: {}, mapping: {} };
                                                    const moduleGroups = config.groups[item.id] || [];
                                                    const mapping = config.mapping || {};

                                                    // Separa em Grupos
                                                    const groupedSubs = {};
                                                    moduleGroups.forEach(g => groupedSubs[g] = []);
                                                    const ungroupedSubs = [];

                                                    allowedSubs.forEach(sub => {
                                                        const g = mapping[sub.id];
                                                        if (g && moduleGroups.includes(g)) {
                                                            groupedSubs[g].push(sub);
                                                        } else {
                                                            ungroupedSubs.push(sub);
                                                        }
                                                    });

                                                    return (
                                                        <>
                                                            {/* Renderiza Grupos Personalizados */}
                                                            {moduleGroups.map(groupName => {
                                                                const itemsInGroup = groupedSubs[groupName];
                                                                if (itemsInGroup.length === 0) return null;
                                                                return (
                                                                    <div key={groupName} className="mb-2">
                                                                        <div className="px-3 pt-2 pb-1 text-[10px] font-black text-blue-400 uppercase tracking-widest border-b border-slate-700/50 mb-1 flex items-center gap-1">
                                                                            <Folder size={10}/> {groupName}
                                                                        </div>
                                                                        {itemsInGroup.map((sub, subIndex) => renderSubItemRow(sub, subIndex))}
                                                                    </div>
                                                                );
                                                            })}
                                                            
                                                            {/* Renderiza Itens Soltos */}
                                                            {ungroupedSubs.length > 0 && (
                                                                <div className="mt-1">
                                                                    {moduleGroups.length > 0 && (
                                                                        <div className="px-3 pt-2 pb-1 text-[9px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-700/50 mb-1">
                                                                            Outros Itens
                                                                        </div>
                                                                    )}
                                                                    {ungroupedSubs.map((sub, subIndex) => renderSubItemRow(sub, subIndex))}
                                                                </div>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        
                        {/* MODAL DE SORTEIO */}
                            {isRaffleModalOpen && (
                                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                                    <div className={`w-full max-w-2xl rounded-xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`}>
                                        
                                        {/* Cabeçalho */}
                                        <div className={`p-4 border-b flex justify-between items-center ${isDarkMode ? 'border-slate-700 bg-slate-900/50' : 'border-gray-200 bg-gray-50'}`}>
                                            <h2 className="text-xl font-black flex items-center gap-2 tracking-tight">
                                                <Gift className="text-indigo-500" /> SORTEIO OFICIAL
                                            </h2>
                                            <button onClick={() => { setIsRaffleModalOpen(false); setRaffleWinner(null); }} className="hover:text-red-500 transition-colors">
                                                <X size={24}/>
                                            </button>
                                        </div>

                                        <div className="p-6 overflow-y-auto custom-scrollbar flex-1 space-y-6 relative">
                                            
                                            {/* 1º Quadro: Regras e Auditoria */}
                                            <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                                                <h3 className={`font-bold text-sm mb-2 flex items-center gap-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-800'}`}>
                                                    <ShieldCheck size={18} /> Regras e Auditoria do Sorteio
                                                </h3>
                                                <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-blue-200' : 'text-blue-700'}`}>
                                                    O sorteio é executado de forma aleatória (RNG), baseando-se no peso dos bilhetes. 
                                                    A probabilidade matemática de vitória é estritamente proporcional à quantidade de bilhetes acumulados pelo participante. 
                                                    O resultado processado pelo algoritmo é inviolável, definitivo e pronto para auditoria.
                                                </p>
                                            </div>

                                            {/* ALERTA DE VENCEDOR NA TELA */}
                                            {raffleWinner && (
                                                <div className="bg-red-500 p-6 rounded-xl text-center shadow-lg border-2 border-red-400 animate-bounce">
                                                    <Trophy className="mx-auto text-yellow-300 mb-2" size={48} />
                                                    <h3 className="text-lg font-black text-red-100 uppercase tracking-widest">Vencedor(a) Sorteado!</h3>
                                                    <p className="text-4xl font-black text-white mt-2 drop-shadow-md">{raffleWinner.name}</p>
                                                    <p className="text-sm text-red-100 mt-2 font-medium bg-red-600/50 inline-block px-3 py-1 rounded-full">
                                                        Tinha {raffleWinner.tickets} bilhete(s)
                                                    </p>
                                                </div>
                                            )}

                                            {/* 2º Quadro: Relação de Participantes */}
                                            <div>
                                                <h3 className={`font-bold text-xs uppercase tracking-wider mb-3 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                                    Relação de Participantes
                                                </h3>
                                                <div className={`border rounded-lg overflow-hidden ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                                    <table className="w-full text-sm text-left">
                                                        <thead className={`text-xs uppercase font-bold ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>
                                                            <tr>
                                                                <th className="p-3">Nome</th>
                                                                <th className="p-3 text-center w-32">Bilhetes</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-200'}`}>
                                                            {mockParticipantesSorteio.map((p) => {
                                                                const isWinner = raffleWinner?.id === p.id;
                                                                return (
                                                                    <tr key={p.id} className={`${isWinner ? 'bg-red-500/20' : (isDarkMode ? 'bg-slate-800' : 'bg-white')} transition-colors`}>
                                                                        <td className={`p-3 font-medium ${isWinner ? 'text-red-500 font-black text-base' : ''}`}>
                                                                            {p.name} {isWinner && '🏆'}
                                                                        </td>
                                                                        <td className={`p-3 text-center ${isWinner ? 'text-red-500 font-black text-base' : ''}`}>
                                                                            <span className={`px-2 py-1 rounded text-xs font-bold ${isWinner ? 'bg-red-500/30' : (isDarkMode ? 'bg-slate-700' : 'bg-gray-100')}`}>
                                                                                {p.tickets}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>

                                            {/* 3º Quadro: Total de Bilhetes */}
                                            <div className={`p-5 rounded-lg flex justify-between items-center border ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-100 border-gray-300'}`}>
                                                <span className={`font-black uppercase tracking-widest text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                                                    Total de Bilhetes Adquiridos
                                                </span>
                                                <span className="text-3xl font-black text-indigo-500">
                                                    {mockParticipantesSorteio.reduce((acc, curr) => acc + curr.tickets, 0)}
                                                </span>
                                            </div>

                                        </div>

                                        {/* Rodapé / Botão de Ação */}
                                        <div className={`p-4 border-t flex justify-end gap-3 ${isDarkMode ? 'border-slate-700 bg-slate-900/50' : 'border-gray-200 bg-gray-50'}`}>
                                            <button 
                                                onClick={() => { setIsRaffleModalOpen(false); setRaffleWinner(null); }}
                                                className={`px-4 py-2 rounded-lg font-bold text-sm transition-colors ${isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-gray-600 hover:bg-gray-200'}`}
                                            >
                                                Fechar
                                            </button>
                                            <button 
                                                onClick={handleDrawWinner}
                                                disabled={raffleWinner !== null}
                                                className={`px-6 py-2 rounded-lg font-bold text-sm shadow-lg flex items-center gap-2 transition-all ${raffleWinner ? 'bg-slate-500 cursor-not-allowed opacity-50' : 'bg-indigo-600 hover:bg-indigo-700 text-white hover:scale-105'}`}
                                            >
                                                <Gift size={18} /> {raffleWinner ? 'Sorteio Finalizado' : 'Executar Sorteio Agora'}
                                            </button>
                                        </div>

                                    </div>
                                </div>
                            )}

                        {/* MODAL DE ORGANIZAÇÃO DE MENUS */}
                        {isMenuOrganizerOpen && (
                            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsMenuOrganizerOpen(false)}>
                                <div className={`rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                    <div className={`p-4 border-b flex justify-between items-center ${isDarkMode ? 'border-slate-700 bg-slate-900' : 'border-gray-100 bg-gray-50'}`}>
                                        <div>
                                            <h3 className="font-bold text-lg flex items-center gap-2"><Settings2 size={20} className="text-blue-500"/> Personalizar Meus Menus</h3>
                                            <p className="text-xs opacity-70 mt-1">Crie pastas para organizar seus atalhos na barra lateral.</p>
                                        </div>
                                        <button onClick={() => setIsMenuOrganizerOpen(false)}><X size={20}/></button>
                                    </div>
                                    
                                    {/* Seleção de Módulo e Criação de Grupo */}
                                    <div className={`p-5 border-b flex flex-col gap-4 shrink-0 ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-blue-50/50 border-gray-200'}`}>
                                        <div>
                                            <label className={`text-xs font-bold uppercase tracking-wider block mb-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>1. Selecione o Menu Principal</label>
                                            <select 
                                                value={orgModule}
                                                onChange={e => setOrgModule(e.target.value)}
                                                className={`w-full h-10 px-3 text-sm font-bold rounded border outline-none focus:ring-1 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                                            >
                                                {menuStructureAvDes.filter(m => m.subItems && m.subItems.length > 0 && hasModuleAccess(m.permission)).map(m => (
                                                    <option key={m.id} value={m.id}>{m.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className={`text-xs font-bold uppercase tracking-wider block mb-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>2. Criar Nova Pasta (Grupo)</label>
                                            <div className="flex gap-2">
                                                <input 
                                                    value={newMenuGroupName} 
                                                    onChange={e=>setNewMenuGroupName(e.target.value)} 
                                                    placeholder="Ex: Meus Dados, Avaliações..." 
                                                    className={`h-9 px-3 text-xs flex-1 rounded border outline-none focus:ring-1 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                                                />
                                                <Button onClick={handleAddMenuGroup} className="h-9 px-4 text-xs font-bold shrink-0 shadow-sm"><Plus size={14}/> Criar Pasta</Button>
                                            </div>
                                        </div>
                                        
                                        {/* Exibe grupos criados */}
                                        {(menuOrganizerConfig.groups[orgModule] || []).length > 0 && (
                                            <div className="flex flex-wrap gap-2 pt-2 border-t border-dashed border-gray-300 dark:border-slate-700">
                                                {(menuOrganizerConfig.groups[orgModule] || []).map(g => (
                                                    <span key={g} className={`text-[10px] font-bold px-2 py-1.5 rounded flex items-center gap-2 shadow-sm ${isDarkMode ? 'bg-blue-900/40 text-blue-300 border border-blue-800' : 'bg-white border border-blue-200 text-blue-700'}`}>
                                                        <Folder size={12}/> {g} 
                                                        <button onClick={() => handleRemoveMenuGroup(g)} className="hover:text-red-500 ml-1 bg-black/5 dark:bg-white/10 p-0.5 rounded-full" title="Excluir Pasta"><X size={10}/></button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Lista de Submenus para Atribuição */}
                                    <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                                        <label className={`text-xs font-bold uppercase tracking-wider block mb-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>3. Organize os Atalhos</label>
                                        
                                        <div className={`border rounded-lg overflow-hidden ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                            <table className="w-full text-sm text-left">
                                                <thead className={`text-xs uppercase font-bold ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>
                                                    <tr>
                                                        <th className="p-3">Atalho (Submenu)</th>
                                                        <th className="p-3 w-64">Mover para a Pasta</th>
                                                    </tr>
                                                </thead>
                                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                    {(() => {
                                                        const activeModule = menuStructureAvDes.find(m => m.id === orgModule);
                                                        if (!activeModule || !activeModule.subItems) return null;

                                                        const allowedSubs = activeModule.subItems.filter(sub => {
                                                            if (!sub.permKey) return true;
                                                            return effectivePermissions[sub.permKey] === true;
                                                        });

                                                        return allowedSubs.map((sub, idx) => (
                                                            <tr key={sub.id} className={idx % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50')}>
                                                                <td className="p-3 font-bold">{sub.label}</td>
                                                                <td className="p-3">
                                                                    <select 
                                                                        value={menuOrganizerConfig.mapping[sub.id] || ''}
                                                                        onChange={(e) => {
                                                                            const val = e.target.value;
                                                                            setMenuOrganizerConfig(prev => {
                                                                                const newMap = { ...prev.mapping };
                                                                                if (val) newMap[sub.id] = val;
                                                                                else delete newMap[sub.id];
                                                                                return { ...prev, mapping: newMap };
                                                                            });
                                                                        }}
                                                                        className={`w-full p-2 rounded border text-xs outline-none focus:border-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-blue-300' : 'bg-white border-gray-300 text-blue-700'}`}
                                                                    >
                                                                        <option value="">(Raiz do Menu)</option>
                                                                        {(menuOrganizerConfig.groups[orgModule] || []).map(g => (
                                                                            <option key={g} value={g}>{g}</option>
                                                                        ))}
                                                                    </select>
                                                                </td>
                                                            </tr>
                                                        ));
                                                    })()}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    
                                    <div className={`p-4 border-t flex justify-end gap-3 ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-100 bg-gray-50'}`}>
                                        <Button variant="secondary" onClick={() => setIsMenuOrganizerOpen(false)}>Cancelar</Button>
                                        <Button onClick={handleSaveMenuOrganization} className="bg-blue-600 text-white hover:bg-blue-700 shadow-md">Salvar Organização</Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </aside>
                )}

                {/* --- CONTEÚDO PRINCIPAL --- */}
                <main className={`flex-1 h-screen overflow-y-auto relative scroll-smooth app-main-shell ${themeMode === 'light' ? 'bg-slate-50' : themeMode === 'acad' ? 'bg-[#041b18]' : 'bg-slate-950'}`}>
                    
                    <div className="app-topbar sticky top-0 z-50 border-b backdrop-blur-xl">
                        <div className="app-topbar-tabs flex-1 min-w-0 flex items-end gap-1 overflow-x-auto custom-scrollbar px-3 pt-2">
                            {openModuleTabs.map((tab) => {
                                const Icon = tab.icon || FileText;
                                const isActive = tab.targetSys === safeSys;
                                return <button key={tab.targetSys} type="button" onClick={() => handleNavigateOpenModuleTab(tab.targetSys)} className={`app-modern-tab ${isActive ? 'is-active' : ''}`} title={tab.label}>
                                    <span className="app-tab-dot"><Icon size={12}/></span>
                                    <span className="truncate">{tab.shortLabel}</span>
                                    <span role="button" tabIndex={0} onClick={(e) => handleCloseOpenModuleTab(tab.targetSys, e)} onKeyDown={(e) => { if (e.key === 'Enter') handleCloseOpenModuleTab(tab.targetSys, e); }} className="app-tab-close"><X size={12}/></span>
                                </button>;
                            })}
                        </div>
                        <div className="app-topbar-actions shrink-0 flex items-center gap-1.5 px-3 py-2">
                            <button type="button" onClick={() => setCacheModalOpen(true)} className="app-circle-action" title="Limpeza seletiva do cache"><Database size={16}/></button>
                            <button type="button" onClick={cycleThemeMode} className="app-circle-action" title={`Tema atual: ${themeMode === 'acad' ? 'Verde Acad' : themeMode === 'light' ? 'Claro' : 'Escuro'}`}>{themeMode === 'light' ? <Sun size={16}/> : themeMode === 'acad' ? <Sparkles size={16}/> : <Moon size={16}/>}</button>
                            <button type="button" onClick={() => setHelpCenterOpen(true)} className="app-circle-action" title="Ajuda, manuais e sugestões"><CircleHelp size={17}/></button>
                            <div className="app-zoom-control"><button onClick={() => setSystemZoom(z => Math.max(70, z - 10))}>A-</button><strong>{systemZoom}%</strong><button onClick={() => setSystemZoom(z => Math.min(140, z + 10))}>A+</button></div>
                            <button type="button" onClick={() => { setIsNotificationsOpen(prev => !prev); setNotificationTab('active'); }} className="app-circle-action relative" title="Notificações"><Bell size={16}/>{unreadNotificationsTotal > 0 && <span className="app-notification-badge">{unreadNotificationsTotal > 99 ? '99+' : unreadNotificationsTotal}</span>}</button>
                            <button type="button" onClick={handleLogout} className="app-circle-action app-logout-action" title="Sair"><LogOut size={16}/></button>
                            <div className="app-user-chip">
                                {(currentUser?.photoURL || currentUser?.photoUrl || currentUser?.avatar) ? <img src={currentUser.photoURL || currentUser.photoUrl || currentUser.avatar} alt="Usuário"/> : <span>{String(currentUser?.name || currentUser?.email || 'U').split(/\s+/).slice(0,2).map(v => v[0]).join('').toUpperCase()}</span>}
                                <div className="app-user-chip-name-only"><b>{currentUser?.name || 'Usuário'}</b></div>
                            </div>
                        </div>
                    </div>
                    {isNotificationsOpen && <div className="app-notification-panel fixed right-3 top-[50px] z-[99999] w-[min(410px,calc(100vw-24px))] max-h-[72vh] overflow-hidden rounded-2xl border shadow-2xl" style={{ background: 'var(--app-card)', borderColor: 'var(--app-line)', color: 'var(--app-text)' }}>
                        <div className="app-notification-head flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--app-line)' }}><b className="text-sm">Notificações</b><button className="w-8 h-8 rounded-lg inline-flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/10" onClick={() => setIsNotificationsOpen(false)}><X size={15}/></button></div>
                        <div className="app-notification-tabs grid grid-cols-2 border-b" style={{ borderColor: 'var(--app-line)' }}><button className={`py-2.5 text-xs font-black ${notificationTab === 'active' ? 'active' : ''}`} onClick={() => setNotificationTab('active')}>Ativas ({activeNotifications.length})</button><button className={`py-2.5 text-xs font-black ${notificationTab === 'archived' ? 'active' : ''}`} onClick={() => setNotificationTab('archived')}>Arquivadas ({archivedNotifications.length})</button></div>
                        <div className="app-notification-list max-h-[55vh] overflow-y-auto">{(notificationTab === 'active' ? activeNotifications : archivedNotifications).map(item => <div key={item.id} className="app-notification-item flex items-start gap-2 px-4 py-3 border-b" style={{ borderColor: 'var(--app-line)' }}><button className="flex-1 min-w-0 text-left" onClick={() => handleOpenNotification(item)}><b className="block text-xs leading-5">{item.title || item.label || item.message || 'Notificação'}</b><small className="block text-[10px] leading-4 mt-0.5" style={{ color: 'var(--app-muted)' }}>{item.description || item.subtitle || item.moduleLabel || ''}</small></button>{notificationTab === 'active' ? <button className="shrink-0 w-8 h-8 rounded-lg inline-flex items-center justify-center" title="Arquivar" onClick={() => handleArchiveNotification(item.id)}><Archive size={14}/></button> : <button className="shrink-0 w-8 h-8 rounded-lg inline-flex items-center justify-center" title="Restaurar" onClick={() => handleRestoreNotification(item.id)}><RefreshCw size={14}/></button>}</div>)}{!(notificationTab === 'active' ? activeNotifications : archivedNotifications).length && <div className="p-5 text-center text-xs opacity-60">Nenhuma notificação.</div>}</div>
                    </div>}
                    
                    <div className="p-6 lg:p-10 max-w-[1600px] mx-auto animate-fade-in pb-20 app-main-content">
            {/* --- TELA DE FAVORITOS (VERSÃO COMPLETA E CORRIGIDA) --- */}
                    {activeModuleId === 'favorites' && (
                        <div className="space-y-6 animate-fade-in max-w-[1600px] mx-auto pb-20 mt-6">
                            
                            {/* HEADER FUNDIDO: TÍTULO + CONTROLES (Tela Inicial e Botão Gerenciar) */}
                            <div className="flex flex-col md:flex-row justify-between items-center gap-4 border-b pb-4 border-gray-200 dark:border-slate-700">
                                
                                {/* Lado Esquerdo: Título com Estrela Amarela */}
                                <div>
                                    <h2 className={`text-2xl font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                        <Star className="text-yellow-400" fill="currentColor" size={28}/> 
                                        Meus Favoritos
                                    </h2>
                                    <p className={`text-sm mt-1 ml-9 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                        Clique e arraste para organizar. Defina sua tela inicial abaixo.
                                    </p>
                                </div>

                                {/* Lado Direito: Controles e Visão */}
                                <div className={`flex items-center gap-3 p-2 rounded-lg border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                                    
                                    {/* --- CONTROLE MODO DE VISÃO (APENAS SE PERMITIDO) --- */}
                                    {(() => {
                                        // Valida se usuário tem a permissão master ou a tag liberada
                                        const canChangeView = currentUser?.role === 'SuperAdm' ? effectivePermissions['allow_view_change'] !== false : effectivePermissions['allow_view_change'] === true;
                                        
                                        if (!canChangeView) return null;
                                        
                                        return (
                                        <>
                                            <div className="flex items-center gap-2 border-r pr-3 border-gray-300 dark:border-slate-600">
                                                <LayoutDashboard size={16} className="text-orange-500 hidden sm:block"/>
                                                <div className="flex flex-col">
                                                    <label className={`text-[9px] font-bold uppercase ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Visão do Sistema</label>
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        <span className={`text-[10px] font-bold ${!isCardViewMode ? 'text-blue-500' : 'text-gray-400'}`}>Menu Lat.</span>
                                                        <label className="relative inline-flex items-center cursor-pointer" title="Alternar visão do sistema">
                                                            <input type="checkbox" className="sr-only peer" checked={isCardViewMode} onChange={toggleCardViewMode} />
                                                            <div className="w-7 h-4 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-blue-600"></div>
                                                        </label>
                                                        <span className={`text-[10px] font-bold ${isCardViewMode ? 'text-blue-500' : 'text-gray-400'}`}>Cards</span>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* CONFIGURAÇÕES DE CARDS */}
                                            {isCardViewMode && (
                                                <div className="flex items-center gap-3 border-r pr-3 border-gray-300 dark:border-slate-600">
                                                    <div className="flex flex-col gap-1.5">
                                                        <div className="flex items-center justify-between gap-2">
                                                            <label className={`text-[9px] font-bold uppercase w-12 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Layout:</label>
                                                            <select 
                                                                value={currentUser?.preferences?.cardLayout || 'star'} 
                                                                onChange={async (e) => {
                                                                    const val = e.target.value;
                                                                    if (actions.saveUserPreferences) await actions.saveUserPreferences({ cardLayout: val });
                                                                    setCurrentUser(prev => ({...prev, preferences: {...prev.preferences, cardLayout: val}}));
                                                                }}
                                                                className={`text-[10px] font-bold p-0.5 rounded outline-none border cursor-pointer ${isDarkMode ? 'bg-slate-800 text-blue-400 border-slate-600' : 'bg-white text-blue-600 border-gray-300'}`}
                                                            >
                                                                <option value="star">Estrela Circular</option>
                                                                <option value="irregular">Estrela Irregular</option>
                                                                <option value="arch">Arco Semicírculo</option>
                                                                <option value="solar">Órbita Solar</option>
                                                                <option value="grid">Grid Tradicional</option>
                                                                <option value="list">Lista Moderna</option>
                                                            </select>
                                                        </div>
                                                        <div className="flex items-center justify-between gap-2">
                                                            <label className={`text-[9px] font-bold uppercase w-12 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Formato:</label>
                                                            <select 
                                                                value={currentUser?.preferences?.cardShape || 'circle'} 
                                                                onChange={async (e) => {
                                                                    const val = e.target.value;
                                                                    if (actions.saveUserPreferences) await actions.saveUserPreferences({ cardShape: val });
                                                                    setCurrentUser(prev => ({...prev, preferences: {...prev.preferences, cardShape: val}}));
                                                                }}
                                                                className={`text-[10px] font-bold p-0.5 rounded outline-none border cursor-pointer ${isDarkMode ? 'bg-slate-800 text-purple-400 border-slate-600' : 'bg-white text-purple-600 border-gray-300'}`}
                                                            >
                                                                <option value="circle">Círculos</option>
                                                                <option value="rounded-square">Quadrados Curvos</option>
                                                                <option value="rounded-rect">Retângulos Curvos</option>
                                                                <option value="pill">Pílula</option>
                                                                <option value="rhombus">Losango Curvo</option>
                                                                <option value="triangle">Triângulo Curvo</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                        );
                                    })()}

                                    {/* Dropdown de Configuração da Tela Inicial */}
                                    <div className="flex items-center gap-2 border-r pr-3 border-gray-300 dark:border-slate-600">
                                        <Fingerprint size={16} className="text-purple-500"/>
                                        <div className="flex flex-col">
                                            <label className={`text-[9px] font-bold uppercase ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Tela Inicial</label>
                                            <select 
                                                value={currentUser?.preferences?.defaultScreen || 'dash'} 
                                                onChange={(e) => setStartupScreen(e.target.value)}
                                                className={`py-0.5 text-xs font-bold outline-none cursor-pointer bg-transparent w-40 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}
                                            >
                                                <option value="dash">Dashboard (Padrão)</option>
                                                <option value="card_menu">::: Menu Principal (Cards)</option>
                                                <option value="favorites">★ Menu Favoritos</option> 
                                                <optgroup label="Meus Atalhos">
                                                    {(currentUser?.preferences?.favorites || []).map(f => (
                                                        <option key={f.targetSys} value={f.targetSys}>
                                                            {f.label.includes('>') ? f.label.split('>').pop().trim() : f.label}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Botão Gerenciar */}
                                    <button 
                                        onClick={() => setIsFavTreeModalOpen(true)}
                                        className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs shadow-md flex items-center gap-2 transition-transform hover:scale-105"
                                    >
                                        <List size={14}/> Gerenciar
                                    </button>
                                </div>
                            </div>

                            {/* GRID DE FAVORITOS (COM LÓGICA DE DRAG & DROP E RESIZE) */}
                            {(currentUser?.preferences?.favorites || []).length === 0 ? (
                                <div className={`p-16 text-center border-2 border-dashed rounded-xl ${isDarkMode ? 'border-slate-700 text-slate-500' : 'border-gray-300 text-gray-400'}`}>
                                    <Star size={40} className="mx-auto mb-4 opacity-30"/>
                                    <p>Sua lista está vazia. Use o botão "Gerenciar" acima para adicionar itens.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4 grid-flow-row-dense auto-rows-[80px]">
                                    {(currentUser.preferences.favorites).map((fav, index) => {
                                        
                                        // Configuração de Tamanho do Card (Span no Grid)
                                        let gridClass = "col-span-1 row-span-2"; // Normal
                                        if (fav.size === 'small') gridClass = "col-span-1 row-span-1"; 
                                        if (fav.size === 'wide') gridClass = "col-span-2 row-span-2"; 
                                        if (fav.size === 'large') gridClass = "col-span-2 row-span-4"; 

                                        // Limpeza do Label (Remove o caminho "Menu > Submenu")
                                        const cleanLabel = fav.label.includes('>') ? fav.label.split('>').pop().trim() : fav.label;

                                        return (
                                            <div 
                                                key={fav.id}
                                                // --- EVENTOS DE DRAG & DROP ---
                                                draggable
                                                onDragStart={(e) => {
                                                    dragItem.current = index;
                                                    e.dataTransfer.effectAllowed = "move";
                                                    // Efeito visual ao começar a arrastar
                                                    e.currentTarget.style.opacity = '0.5';
                                                }}
                                                onDragEnter={(e) => {
                                                    dragOverItem.current = index;
                                                    e.preventDefault();
                                                }}
                                                onDragOver={(e) => e.preventDefault()}
                                                onDragEnd={(e) => {
                                                    e.currentTarget.style.opacity = '1';
                                                    handleDragSort();
                                                }}
                                                // --- EVENTO DE NAVEGAÇÃO ---
                                                onClick={() => {
                                                    const target = String(fav.targetSys || '').trim();

                                                    if (!target) return;

                                                    /*
                                                    * IMPORTANTE:
                                                    * Para submenus, precisamos manter o target completo.
                                                    *
                                                    * Exemplo correto:
                                                    * collab_center:my_growth
                                                    *
                                                    * Antes estava fazendo setSys('collab_center'),
                                                    * por isso carregava só a saudação da Central Colaborador.
                                                    */
                                                    setSys(target);

                                                    if (target.includes(':')) {
                                                        const parts = target.split(':');
                                                        setTimeout(() => setOpenId(parts[1]), 50);
                                                    } else {
                                                        setOpenId(null);
                                                    }
                                                }}  
                                                // --- ESTILIZAÇÃO DO CARD ---
                                                className={`
                                                    relative rounded-xl shadow-md cursor-grab active:cursor-grabbing 
                                                    transition-all duration-200 group flex flex-col justify-center px-4 
                                                    bg-gradient-to-br ${fav.color || 'from-blue-600 to-cyan-500'} text-white overflow-hidden
                                                    ${gridClass} hover:brightness-110
                                                `}
                                            >
                                                {/* Controles Flutuantes (Só aparecem no Hover) */}
                                                <div className="absolute top-1 right-1 z-20 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 bg-black/20 rounded p-1 backdrop-blur-sm pointer-events-auto">
                                                    {/* Botão Mudar Tamanho */}
                                                    <button 
                                                        onClick={(e) => { 
                                                            e.stopPropagation(); 
                                                            const sizes = ['normal', 'wide', 'small', 'large'];
                                                            const next = sizes[(sizes.indexOf(fav.size || 'normal') + 1) % sizes.length];
                                                            toggleFavorite({ ...fav, forceUpdate: true, size: next });
                                                        }} 
                                                        className="p-1 hover:text-yellow-300 text-white" 
                                                        title="Mudar Tamanho"
                                                    >
                                                        <Maximize2 size={10}/>
                                                    </button>
                                                    
                                                    {/* Botão Remover */}
                                                    <button 
                                                        onClick={(e) => { 
                                                            e.stopPropagation(); 
                                                            toggleFavorite({ targetSys: fav.targetSys }); 
                                                        }} 
                                                        className="p-1 hover:text-red-400 text-white" 
                                                        title="Remover"
                                                    >
                                                        <X size={10}/>
                                                    </button>
                                                </div>

                                                {/* Conteúdo do Card */}
                                                <div className="relative z-10 pointer-events-none">
                                                    <h3 className={`font-bold leading-tight truncate ${fav.size === 'small' ? 'text-xs flex items-center gap-2' : 'text-sm md:text-base mb-1'}`}>
                                                        {fav.size === 'small' && <Star size={10} fill="currentColor"/>}
                                                        {cleanLabel}
                                                    </h3>
                                                    {fav.size !== 'small' && (
                                                        <p className="text-[9px] opacity-80 uppercase tracking-wide truncate">
                                                            {fav.label.includes('>') ? fav.label.split('>')[0].trim() : 'Módulo Principal'}
                                                        </p>
                                                    )}
                                                </div>
                                                
                                                {/* Ícone de Fundo Decorativo */}
                                                <Star size={fav.size === 'small' ? 24 : 60} className="absolute -bottom-2 -left-2 opacity-10 rotate-12 pointer-events-none"/>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* MODAL DE GERENCIAMENTO (ÁRVORE DE MENU COM PASTAS) */}
                            {isFavTreeModalOpen && (
                                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setIsFavTreeModalOpen(false)}>
                                    <div className={`rounded-xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                        <div className={`p-4 border-b flex justify-between items-center ${isDarkMode ? 'border-slate-700 bg-slate-900' : 'border-gray-100 bg-gray-50'}`}>
                                            <h3 className="font-bold text-lg flex items-center gap-2"><List size={20}/> Organizar Meu Menu</h3>
                                            <button onClick={() => setIsFavTreeModalOpen(false)}><X size={20}/></button>
                                        </div>
                                        
                                        {/* --- WIDGET DE CRIAÇÃO DE GRUPOS --- */}
                                        <div className={`p-5 border-b flex flex-col gap-3 shrink-0 ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-blue-50/50 border-gray-200'}`}>
                                            <label className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>Criar Novo Grupo (Pasta)</label>
                                            <div className="flex gap-2">
                                                <input 
                                                    value={newFolderName} 
                                                    onChange={e=>setNewFolderName(e.target.value)} 
                                                    placeholder="Ex: Meus Envios, Minhas Avaliações..." 
                                                    className={`h-9 px-3 text-xs flex-1 rounded border outline-none focus:ring-1 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                                                />
                                                <Button onClick={handleAddCustomFolder} className="h-9 px-4 text-xs font-bold shrink-0 shadow-sm"><Plus size={14}/> Criar Grupo</Button>
                                            </div>
                                            
                                            {(currentUser?.preferences?.customFolders || []).length > 0 && (
                                                <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-dashed border-gray-300 dark:border-slate-700">
                                                    {(currentUser?.preferences?.customFolders || []).map(f => (
                                                        <span key={f} className={`text-[10px] font-bold px-2 py-1.5 rounded flex items-center gap-2 shadow-sm ${isDarkMode ? 'bg-blue-900/40 text-blue-300 border border-blue-800' : 'bg-white border border-blue-200 text-blue-700'}`}>
                                                            <Folder size={12}/> {f} 
                                                            <button onClick={() => handleRemoveCustomFolder(f)} className="hover:text-red-500 ml-1 bg-black/5 dark:bg-white/10 p-0.5 rounded-full"><X size={10}/></button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                                            <p className={`text-xs font-bold uppercase mb-4 opacity-70 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Atribua os atalhos aos seus grupos:</p>
                                            
                                            {(() => {
                                                const renderTree = (items, prefix = '', depth = 0) => {
                                                return items.map(item => {
                                                    // Verifica permissões
                                                    if (item.permission && !hasModuleAccess(item.permission)) return null;
                                                    if (item.permKey && effectivePermissions[item.permKey] === false && currentUser.role !== 'SuperAdm') return null;
                                                    if (item.id === 'favorites') return null;

                                                    // IDs
                                                    const currentTarget = prefix ? `${prefix}:${item.id}` : item.id;
                                                    
                                                    // Verifica se tem filhos
                                                    const children = item.subItems || item.innerTabs;
                                                    const hasChildren = children && children.length > 0;
                                                    
                                                    const isExpanded = expandedFavGroups[currentTarget];
                                                    const isFav = isFavorite(currentTarget);
                                                    const favData = (currentUser?.preferences?.favorites || []).find(f => f.targetSys === currentTarget);
                                                    
                                                    const saveLabel = item.label;

                                                    const paddingLeft = depth * 24 + 12;

                                                    return (
                                                        <div key={currentTarget} className="flex flex-col">
                                                            <div 
                                                                className={`flex items-center gap-3 py-2.5 pr-2 border-b border-dashed transition-colors
                                                                ${isDarkMode ? 'border-slate-800 hover:bg-slate-800/50' : 'border-gray-200 hover:bg-gray-50'}
                                                                ${depth === 0 ? (isDarkMode ? 'bg-slate-900/50 font-bold mt-2 rounded-t' : 'bg-gray-100/80 font-bold mt-2 rounded-t text-gray-800') : ''}`}
                                                                style={{ paddingLeft: `${paddingLeft}px` }}
                                                            >
                                                                {hasChildren ? (
                                                                    <button 
                                                                        type="button"
                                                                        onClick={(e) => { e.stopPropagation(); setExpandedFavGroups(prev => ({...prev, [currentTarget]: !prev[currentTarget]})); }}
                                                                        className={`p-1 rounded transition-transform ${isExpanded ? 'rotate-90' : ''} ${isDarkMode ? 'text-gray-400 hover:text-blue-500' : 'text-gray-500 hover:text-blue-600'}`}
                                                                    >
                                                                        <ChevronRight size={14}/>
                                                                    </button>
                                                                ) : (
                                                                    <div className="w-6 h-6"></div> 
                                                                )}

                                                                <div 
                                                                    className={`cursor-pointer relative flex items-center justify-center p-1.5 rounded-full transition-all ${isFav ? 'text-yellow-400 bg-yellow-400/10 shadow-sm' : 'text-gray-300 hover:text-gray-400'}`}
                                                                    onClick={(e) => { e.preventDefault(); toggleFavorite({ targetSys: currentTarget, label: saveLabel }); }}
                                                                >
                                                                    <Star size={18} fill={isFav ? "currentColor" : "none"} strokeWidth={isFav ? 0 : 2} />
                                                                </div>

                                                                <div className="flex flex-1 items-center justify-between gap-4">
                                                                    <span 
                                                                        className={`text-sm cursor-pointer select-none truncate ${isFav ? (isDarkMode ? 'text-yellow-200 font-bold' : 'text-blue-700 font-bold') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                                                                        onClick={(e) => { if(hasChildren) setExpandedFavGroups(prev => ({...prev, [currentTarget]: !prev[currentTarget]})); else toggleFavorite({ targetSys: currentTarget, label: saveLabel }); }}
                                                                    >
                                                                        {item.label}
                                                                    </span>
                                                                    
                                                                    {/* SELETOR DE GRUPO (Aparece apenas se estiver favoritado E se existirem pastas) */}
                                                                    {isFav && (currentUser?.preferences?.customFolders?.length > 0) && (
                                                                        <select 
                                                                            value={favData?.folder || ''} 
                                                                            onChange={(e) => updateFavFolder(currentTarget, e.target.value)}
                                                                            className={`text-[10px] p-1.5 rounded border outline-none w-[140px] shadow-sm font-bold truncate ${isDarkMode ? 'bg-slate-900 border-slate-600 text-blue-300 focus:border-blue-500' : 'bg-white border-gray-300 text-blue-700 focus:border-blue-500'}`}
                                                                        >
                                                                            <option value="">(Solto na Raiz)</option>
                                                                            {currentUser.preferences.customFolders.map(f => <option key={f} value={f}>{f}</option>)}
                                                                        </select>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {hasChildren && isExpanded && (
                                                                <div className={`animate-fade-in border-l ml-6 ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                                                    {renderTree(children, currentTarget, depth + 1)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                });
                                            };
                                                return renderTree(menuStructureAvDes);
                                            })()}
                                        </div>
                                        
                                        <div className={`p-4 border-t flex justify-end ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-100 bg-gray-50'}`}>
                                            <Button onClick={() => setIsFavTreeModalOpen(false)} className="bg-blue-600 text-white hover:bg-blue-700 px-8 shadow-md">Pronto</Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                        {/* [APP_RENDER__CARD_MENU] RENDERIZACAO DO MODO CARDS (VISAO NOVA) */}
                        {activeModuleId === 'card_menu' && isCardViewMode && (
                            <CardNavigationMenu 
                                menuStructure={menuStructureAvDes} 
                                hasModuleAccess={hasModuleAccess} 
                                effectivePermissions={effectivePermissions} 
                                isDarkMode={isDarkMode} 
                                currentUser={currentUser}
                                onNavigate={(target) => setSys(target)}
                            />
                        )}

                        {/* [APP_RENDER__DASHBOARD] BLOQUEIO RIGIDO DO DASHBOARD */}
                        {activeModuleId === 'dash' && hasModuleAccess('access_dash') && (
                            <DashboardSystem 
                                data={data} 
                                actions={actions} 
                                onDelete={actions.delete} 
                                onUpdateStatus={actions.updateStatus} 
                                onEdit={actions.edit} 
                                permissions={effectivePermissions} 
                                currentUser={currentUser} 
                                isDarkMode={isDarkMode} 
                            />
                        )}
                        {/* [APP_RENDER__CADASTROS] */}
                        {activeModuleId === 'reg' && (
                            <CadastrosSystem 
                                data={data} 
                                actions={actions} 
                                currentUser={currentUser} 
                                authReady={authReady} 
                                pendingUsersCount={pendingUsersCount} 
                                initialTab={activeTabId || 'dept'} 
                                initialSubTab={activeSubTabId} // <--- ADICIONADO AQUI
                                isDarkMode={isDarkMode} 
                            />
                        )}
                    
                        {/* [APP_RENDER__APONTAMENTOS] */}
                        {activeModuleId === 'apt' && <ApontamentosSystem data={data} actions={actions} permissions={effectivePermissions} currentUser={currentUser} isDarkMode={isDarkMode} />}
                        {/* [APP_RENDER__AVALIACAO] */}
                        {activeModuleId === 'eval' && <AvaliacaoSystem data={data} actions={actions} openEvalId={openId} permissions={effectivePermissions} currentUser={currentUser} isDarkMode={isDarkMode} />}
                        {/* [APP_RENDER__FINANCEIRO] */}
                        {activeModuleId === 'fin' && (
                            <FinanceiroSystem
                                data={data}
                                actions={actions}
                                permissions={effectivePermissions}
                                currentUser={currentUser}
                                initialTab={activeTabId || 'proj'}
                                isDarkMode={isDarkMode}
                            />
                        )}

                        {activeModuleId === 'rh' && ['central', 'feedbacks'].includes(activeTabId) && (
                            <FinanceiroSystem
                                data={data}
                                actions={actions}
                                permissions={effectivePermissions}
                                currentUser={currentUser}
                                initialTab={activeTabId}
                                isDarkMode={isDarkMode}
                            />
                        )}

                        {activeModuleId === 'rh' && !['central', 'feedbacks', 'hr_cultura_acad'].includes(activeTabId) && (
                            <RHSystem
                                data={data}
                                actions={actions}
                                currentUser={currentUser}
                                isDarkMode={isDarkMode}
                                initialTab={activeTabId}
                            />
                        )}
                      
                        
                    {/* [APP_RENDER__COLLAB_CENTER] */}
                    {activeModuleId === 'collab_center' && (
                            <div className="space-y-3 animate-fade-in">
                                
                            {/* --- HERO SECTION: SAUDAÇÃO E MOTIVAÇÃO (COMPACTO) --- */}
                                <div className={
                                    `relative p-3 rounded-xl shadow-lg overflow-hidden ${isDarkMode ? 'bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900 border border-slate-700' : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white'}`}>
                                    {/* Elementos decorativos de fundo */}
                                    <div className="absolute top-0 right-0 -mt-10 -mr-10 w-32 h-32 bg-white opacity-10 rounded-full blur-2xl pointer-events-none"></div>
                                    <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-32 h-32 bg-white opacity-10 rounded-full blur-2xl pointer-events-none"></div>

                                    <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-3">
                                        <div className="flex-1">
                                            <h2 className="text-lg md:text-xl font-bold mb-1 flex items-center gap-2">
                                                Olá, {currentUser?.nickname || currentUser?.name?.split(' ')[0]}! <span className="text-xl">👋</span>
                                            </h2>
                                            <p className={`text-xs md:text-sm leading-tight opacity-90 font-medium max-w-3xl ${isDarkMode ? 'text-slate-300' : 'text-indigo-50'}`}>
                                                Este espaço centraliza suas informações para impulsionar seu crescimento profissional.
                                            </p>
                                        </div>
                                        
                                        {/* Card da Frase do Dia (Compacto) */}
                                        <div className={`w-full md:w-80 p-2 rounded-lg backdrop-blur-md border border-white/20 shadow-inner flex flex-col gap-1 ${isDarkMode ? 'bg-black/30 text-slate-200' : 'bg-white/10 text-white'}`}>
                                            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider opacity-75">
                                                <Sparkles size={10} className="text-yellow-300"/> Inspiração do Dia
                                            </div>
                                            <p className="text-[10px] italic leading-tight line-clamp-2" title="Passe o mouse para ler tudo se estiver cortado">
                                                "{(() => {
                                                    const today = new Date();
                                                    const start = new Date(today.getFullYear(), 0, 0);
                                                    const diff = today - start;
                                                    const oneDay = 1000 * 60 * 60 * 24;
                                                    const dayOfYear = Math.floor(diff / oneDay);
                                                    return MOTIVATIONAL_QUOTES[dayOfYear % MOTIVATIONAL_QUOTES.length];
                                                })()}"
                                            </p>
                                        </div>
                                    </div>
                                </div>


                                            {/* [APP_RENDER__COLLAB_CENTER__SURVEYS] ABA: PESQUISAS E CLIMA */}
                                            {activeTabId === 'surveys' && (
                                                <div className="h-[calc(100vh-200px)] overflow-hidden">
                                                    <SurveysSystem 
                                                        data={data} 
                                                        actions={actions} 
                                                        currentUser={currentUser} 
                                                        isDarkMode={isDarkMode} 
                                                    />
                                                </div>
                                            )}
                                    {/* [APP_RENDER__COLLAB_CENTER__TIME_TRACKING] ABA: GESTAO DE PONTO */}
                                    {activeTabId === 'time_tracking' && (
                                            <div className="space-y-6 animate-fade-in">
                                                
                                                {/* Sub-Navegação Ponto (Compacta) */}
                                                <Card className={`p-1 flex gap-2 overflow-x-auto ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`} darkMode={isDarkMode}>
                                                    {hasModuleAccess('ponto_tab_clock') && (
                                                        <button 
                                                            onClick={() => setPontoTab('clock')} 
                                                            className={`flex-1 py-1.5 px-3 rounded-md text-xs font-bold flex items-center justify-center gap-2 transition-all whitespace-nowrap 
                                                            ${pontoTab === 'clock' ? 'bg-blue-600 text-white shadow-sm' : (isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-gray-600 hover:bg-gray-100')}`}
                                                        >
                                                            <Clock size={14}/> Registro de Ponto
                                                        </button>
                                                    )}
                                                    
                                                    {hasModuleAccess('ponto_tab_bank') && (
                                                        <button 
                                                            onClick={() => setPontoTab('bank')} 
                                                            className={`flex-1 py-1.5 px-3 rounded-md text-xs font-bold flex items-center justify-center gap-2 transition-all whitespace-nowrap 
                                                            ${pontoTab === 'bank' ? 'bg-purple-600 text-white shadow-sm' : (isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-gray-600 hover:bg-gray-100')}`}
                                                        >
                                                            <Calendar size={14}/> Banco de Horas 23
                                                        </button>
                                                    )}

                                                    {hasModuleAccess('ponto_tab_reports') && (
                                                        <button 
                                                            onClick={() => setPontoTab('reports')} 
                                                            className={`flex-1 py-1.5 px-3 rounded-md text-xs font-bold flex items-center justify-center gap-2 transition-all whitespace-nowrap 
                                                            ${pontoTab === 'reports' ? 'bg-indigo-600 text-white shadow-sm' : (isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-gray-600 hover:bg-gray-100')}`}
                                                        >
                                                            <FileSpreadsheet size={14}/> Gestão do Ponto
                                                        </button>
                                                    )}
                                                </Card>

                                                    {/* Conteúdo Dinâmico com Div Livre para evitar cortes nos popups */}
                                                    <div className={`min-h-[500px] p-6 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                                        
                                                        {pontoTab === 'reports' && (
                                                            <PontoReportsModule 
                                                                isDarkMode={isDarkMode} 
                                                                currentUser={currentUser} 
                                                                db={db}
                                                                collaborators={data.collaborators}
                                                                departments={data.departments}
                                                                pontoConfig={Array.isArray(data.pontoConfig) ? data.pontoConfig[0] : data.pontoConfig}
                                                            />
                                                        )}

                                                        {pontoTab === 'clock' && (
                                                            <ClockModule 
                                                                isDarkMode={isDarkMode} 
                                                                effectiveUser={currentUser} 
                                                                db={db}
                                                                collaborators={data.collaborators}
                                                                isMockMode={false} 
                                                                mockRecords={pontoRecords} 
                                                                setMockRecords={setPontoRecords}
                                                                situations={pontoSituations}
                                                                permissions={effectivePermissions}
                                                                globalConfigs={data.globalConfigs}
                                                                config={Array.isArray(data.pontoConfig) ? data.pontoConfig[0] : data.pontoConfig}
                                                            />
                                                        )}

                                                    {pontoTab === 'bank' && (
                                                            <BankModule 
                                                                isDarkMode={isDarkMode} 
                                                                effectiveUser={currentUser} 
                                                                db={db} // Garante o banco real
                                                                isMockMode={!db} 
                                                                mockRecords={pontoRecords}
                                                                setMockRecords={setPontoRecords}
                                                                mockSignatures={mockSignatures || []} 
                                                                setMockSignatures={setMockSignatures || (() => {})} 
                                                                employeesMeta={employeesMeta} 
                                                                config={pontoConfig}
                                                                logoUrl={data.globalConfigs?.[0]?.logoUrl}
                                                                collaborators={data.collaborators}
                                                                logAction={actions.logAction}
                                                                departments={data.departments} 
                                                            />
                                                        )}
                                                    </div>
                                            </div>
                                        )}

                                            {/* [APP_RENDER__COLLAB_CENTER__RECOGNITION] ABA RECONHECIMENTO */}
                                            {activeTabId === 'recognition' && (
                                                <div className="h-[calc(100vh-200px)] overflow-hidden">
                                                    <RecognitionTab 
                                                        data={data} 
                                                        actions={actions} 
                                                        currentUser={currentUser} 
                                                        isDarkMode={isDarkMode} 
                                                    />
                                                </div>
                                            )}
                                            
                                            {/* [APP_RENDER__COLLAB_CENTER__CULTURA_ACAD] ABA CULTURA ACAD */}
                                            {activeTabId === 'cultura_acad' && (
                                                <div className="space-y-6 animate-fade-in">
                                                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                                                        <h2 className={`text-2xl font-black flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                                                            <BookOpen className="text-blue-500" /> {isCulturaAdminMode ? 'Cultura Acad: Configurações' : 'Cultura Acad'}
                                                        </h2>
                                                        <div className="flex gap-3 w-full md:w-auto">
                                                            {!isCulturaAdminMode && (
                                                                <div className="relative w-full md:w-64">
                                                                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} size={16} />
                                                                    <input 
                                                                        type="text" 
                                                                        placeholder="Pesquisar documento..." 
                                                                        value={culturaSearch}
                                                                        onChange={(e) => setCulturaSearch(e.target.value)}
                                                                        className={`w-full pl-9 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-300'}`}
                                                                    />
                                                                </div>
                                                            )}
                                                            
                                                            {isCulturaAdminMode ? (
                                                                <>
                                                                    <Button variant="secondary" onClick={() => setIsCulturaAdminMode(false)}>Voltar</Button>
                                                                    <Button 
                                                                        onClick={() => {
                                                                            setCulturaForm({ id: null, nome: '', link: '', exigeAssinatura: false, dataInicio: new Date().toISOString().split('T')[0], dataFim: '', dataLimite: '', departamento: 'Todos', tipo: 'Todos', status: 'Rascunho' });
                                                                            setShowCulturaModal(true);
                                                                        }}
                                                                        className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                                                                    >
                                                                        Novo Registro
                                                                    </Button>
                                                                </>
                                                            ) : (
                                                                hasCulturaManagePerm && (
                                                                    <Button 
                                                                        onClick={() => setIsCulturaAdminMode(true)}
                                                                        className="bg-slate-700 hover:bg-slate-800 text-white shadow-sm"
                                                                        icon={Settings}
                                                                    >
                                                                        Configurações
                                                                    </Button>
                                                                )
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* MODO COLABORADOR (VISÃO SIMPLIFICADA) */}
                                                    {!isCulturaAdminMode && (
                                                        <div className={`rounded-xl shadow-sm border overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                                            <table className={`w-full text-sm text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                                <thead className={`text-xs uppercase font-bold ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-50 text-gray-500'}`}>
                                                                    <tr>
                                                                        <th className="p-4">Nome do Registro</th>
                                                                        <th className="p-4 text-center">Link de Acesso</th>
                                                                        <th className="p-4 text-center">Exige Assinatura?</th>
                                                                        <th className="p-4 text-center">Data Limite</th>
                                                                        <th className="p-4 text-center">Data/Hora Ciência</th>
                                                                        <th className="p-4 text-center">Status</th>
                                                                        <th className="p-4 text-center">Ações</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                                    {culturaRecords
                                                                        .filter(r => r.nome.toLowerCase().includes(culturaSearch.toLowerCase()) && r.status === 'Liberado')
                                                                        .filter(r => {
                                                                            const isGestor = currentUser?.role === 'Gestor' || currentUser?.role === 'SuperAdm'; 
                                                                            if (r.tipo === 'Gestão' && !isGestor) return false;
                                                                            if (r.tipo === 'Colaborador' && isGestor) return false;
                                                                            return true;
                                                                        })
                                                                        .map((record, i) => {
                                                                            // BUSCA CORRIGIDA: Usa currentUser e localiza a ficha dinamicamente para evitar erro de escopo
                                                                                    // 1. Localiza a ficha do colaborador uma única vez
                                                                            const colabFicha = data?.collaborators?.find(col => 
                                                                                col.email?.toLowerCase() === currentUser?.email?.toLowerCase()
                                                                            );

                                                                            // 2. Define se o documento está assinado (Verificação Tripla)
                                                                            const hasAcknowledge = (record.ciencias || []).some(c => 
                                                                                c?.userId === currentUser?.id || 
                                                                                (colabFicha && c?.userId === colabFicha.id) ||
                                                                                (c?.userName && c.userName.trim().toLowerCase() === currentUser?.name?.trim().toLowerCase())
                                                                            );

                                                                            const temRespostaNaoLida = culturaFeedbacks.some(f => f?.documentoId === record.id && f?.userId === currentUser?.id && f?.status === 'Respondido' && !f?.lidoColab);
                                                                                    
                                                                            return (
                                                                                <tr key={record.id} className={i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50')}>
                                                                                    <td className="p-4">
                                                                                        <div className="font-bold">
                                                                                            <span className={isDarkMode ? 'text-blue-300' : 'text-blue-700'}>{record.nome}</span>
                                                                                        </div>
                                                                                        {temRespostaNaoLida && <span className="text-[9px] bg-red-500 text-white px-2 py-0.5 rounded-full animate-pulse mt-1 inline-block shadow-sm">Nova Resposta do RH</span>}
                                                                                    </td>
                                                                                    <td className="p-4 text-center">
                                                                                        <a href={record.link} target="_blank" rel="noreferrer" title="Acessar Documento" className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-bold text-xs bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 rounded-lg border border-blue-200 dark:border-blue-800 transition-colors">
                                                                                            <ExternalLink size={14} /> Acessar
                                                                                        </a>
                                                                                    </td>
                                                                                    <td className="p-4 text-center">
                                                                                        <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded border ${record.exigeAssinatura ? 'bg-yellow-100 text-yellow-800 border-yellow-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                                                                            {record.exigeAssinatura ? 'Sim' : 'Não'}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className="p-4 text-center">
                                                                                        <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded border ${isDarkMode ? 'bg-slate-700 text-slate-300 border-slate-600' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                                                                            {record.dataLimite ? new Date(record.dataLimite + 'T00:00:00').toLocaleDateString('pt-BR') : 'Sem limite'}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className="p-3 text-center">
                                                                                        <span className={`px-2 py-1 rounded text-[10px] font-bold ${hasAcknowledge ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                                                            {hasAcknowledge ? 'CIENTE' : 'PENDENTE'}
                                                                                        </span>
                                                                                    </td>

                                                                                    {/* NOVA CÉLULA: Só mostra dados se hasAcknowledge for true */}
                                                                                    <td className="p-3 text-center text-[10px] font-medium">
                                                                                        {hasAcknowledge ? (
                                                                                            (() => {
                                                                                                const c = (record.ciencias || []).find(ciencia => 
                                                                                                    ciencia?.userId === currentUser?.id || 
                                                                                                    ciencia?.userId === colabFicha?.id ||
                                                                                                    ciencia?.userName === currentUser?.name
                                                                                                );
                                                                                                return c ? `${c.data} ${c.hora}` : '--';
                                                                                            })()
                                                                                        ) : (
                                                                                            <span className="opacity-30">--</span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td className="p-4 text-center">
                                                                                <div className="flex justify-center items-center gap-2">
                                                                                    {/* 1. BOTÃO DE ASSINAR (Só aparece se PENDENTE) */}
                                                                                    {!hasAcknowledge && record.exigeAssinatura && (
                                                                                        <button 
                                                                                            onClick={async () => {
                                                                                                if (record.exigeAssinatura && !window.confirm(`Você confirma a leitura e o entendimento de "${record.nome}"?`)) return;
                                                                                                
                                                                                                const auditId = 'AUDIT-' + Math.random().toString(36).substr(2, 9).toUpperCase();
                                                                                                const novaCiencia = { 
                                                                                                    userId: colabFicha ? colabFicha.id : currentUser?.id, 
                                                                                                    userName: currentUser?.name, 
                                                                                                    data: new Date().toLocaleDateString('pt-BR'), 
                                                                                                    hora: new Date().toLocaleTimeString('pt-BR'), 
                                                                                                    auditId: auditId 
                                                                                                };
                                                                                                
                                                                                                const cienciasLimpas = (record.ciencias || []).filter(c => c && c.userId);
                                                                                                await saveCulturaRecord({ ...record, ciencias: [...cienciasLimpas, novaCiencia] });
                                                                                                alert("Ciência registrada com sucesso!");
                                                                                            }}
                                                                                            className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded shadow-sm transition-colors"
                                                                                            title="Dar Ciência / Assinar"
                                                                                        >
                                                                                            <CheckSquare size={16}/>
                                                                                        </button>
                                                                                    )}
                                                                                    
                                                                                    {/* 2. BOTÃO DE FEEDBACK / FALAR COM RH */}
                                                                                    {(() => {
                                                                                        const myFeedbacks = culturaFeedbacks.filter(f => f?.documentoId === record.id && f?.userId === currentUser?.id).sort((a,b) => b.dataEnvio.localeCompare(a.dataEnvio));
                                                                                        const latestFeedback = myFeedbacks[0];
                                                                                        const isPendente = latestFeedback?.status === 'Pendente';
                                                                                        const isRespondido = latestFeedback?.status === 'Respondido';
                                                                                        
                                                                                        let btnClass = "bg-orange-100 hover:bg-orange-200 text-orange-700";
                                                                                        if (temRespostaNaoLida) btnClass = "bg-red-500 hover:bg-red-600 text-white animate-pulse";
                                                                                        else if (isPendente) btnClass = "bg-blue-100 hover:bg-blue-200 text-blue-700";
                                                                                        else if (isRespondido) btnClass = "bg-green-100 hover:bg-green-200 text-green-700";

                                                                                        return (
                                                                                            <button 
                                                                                                onClick={() => {
                                                                                                    if (temRespostaNaoLida) {
                                                                                                        const fNaoLidos = myFeedbacks.filter(f => f?.status === 'Respondido' && !f?.lidoColab);
                                                                                                        fNaoLidos.forEach(f => saveCulturaFeedback({...f, lidoColab: true}));
                                                                                                    }
                                                                                                    if (latestFeedback && isPendente) setFeedbackForm(latestFeedback);
                                                                                                    else setFeedbackForm({ id: null, tipo: 'Dúvida', documentoId: record.id, parteDocumento: '', mensagem: '', isAnonimo: false, status: 'Pendente' });
                                                                                                    
                                                                                                    setFeedbackModalTab(isRespondido ? 'historico' : 'nova');
                                                                                                    setShowFeedbackModal(true);
                                                                                                }}
                                                                                                className={`p-2 rounded shadow-sm transition-colors ${btnClass}`}
                                                                                                title="Dúvidas, Elogios e Sugestões"
                                                                                            >
                                                                                                <MessageCircle size={16}/>
                                                                                            </button>
                                                                                        );
                                                                                    })()}
                                                                                </div>
                                                                            </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    {culturaRecords.length === 0 && (<tr><td colSpan="6" className="p-8 text-center italic text-gray-400">Nenhum registro publicado.</td></tr>)}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}

                                                    {/* MODO CONFIGURAÇÕES (VISÃO DE GESTÃO COMPLETA QUE FIZEMOS ANTES) */}
                                                    {isCulturaAdminMode && (
                                                        <div className={`rounded-xl shadow-sm border overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                                                            <table className={`w-full text-sm text-left ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                                {/* CABEÇALHO E BODY DA TABELA DE CONFIGURAÇÕES (CÓDIGO ANTERIOR DOS BOTÕES DE EDITAR, EXCLUIR, ATUALIZAR) */}
                                                                <thead className={`text-xs uppercase font-bold ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-50 text-gray-500'}`}>
                                                                    <tr>
                                                                        <th className="p-4">Documento / Link</th>
                                                                        <th className="p-4 text-center">Validade</th>
                                                                        <th className="p-4 text-center">Público / Depto</th>
                                                                        <th className="p-4 text-center">Status</th>
                                                                        <th className="p-4 text-center">Ações Gerenciais</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                                    {culturaRecords.map((record, i) => (
                                                                        <tr key={record.id} className={i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50')}>
                                                                            <td className="p-4 font-bold">
                                                                                {record.nome} {record.exigeAssinatura && <span className="text-[9px] text-yellow-600">(Assinatura)</span>}
                                                                            </td>
                                                                            <td className="p-4 text-center text-xs opacity-80">{record.dataInicio} até {record.dataFim || 'Indeterminado'}</td>
                                                                            <td className="p-4 text-center text-[10px]"><span className="uppercase font-bold">{record.tipo}</span><br/>{record.departamento}</td>
                                                                            <td className="p-4 text-center font-bold text-xs">{record.status}</td>
                                                                            <td className="p-4 text-center flex justify-center gap-1">
                                                                                {record.status === 'Rascunho' && <button onClick={() => saveCulturaRecord({ ...record, status: 'Liberado' })} className="p-1.5 bg-green-100 text-green-700 rounded"><Send size={14}/></button>}
                                                                                {record.status === 'Liberado' && <button onClick={() => { setCulturaForm(record); setShowCulturaUpdateModal(true); }} className="p-1.5 bg-orange-100 text-orange-700 rounded"><RefreshCw size={14}/></button>}
                                                                                <button onClick={() => { setCulturaForm(record); setShowCulturaModal(true); }} className="p-1.5 bg-blue-100 text-blue-700 rounded"><Edit3 size={14}/></button>
                                                                                <button onClick={() => { if(window.confirm('Excluir permanentemente?')) deleteCulturaRecord(record.id); }} className="p-1.5 bg-red-100 text-red-600 rounded"><Trash2 size={14}/></button>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* [APP_RENDER__COLLAB_CENTER__ACAD_COINS] ABA ACADCOINS */}
                                            {activeTabId === 'acad_coins' && (
                                                <div className="h-[calc(100vh-200px)] overflow-hidden">
                                                    <AcadCoinsSystem data={data} actions={actions} currentUser={currentUser} isDarkMode={isDarkMode} />
                                                </div>
                                            )}

                                            {/* [APP_RENDER__COLLAB_CENTER__PRODUTIVIDADE] ABA: CONTROLE DE PRODUTIVIDADE */}
                                            {activeTabId === 'produtividade' && (
                                                <div className="h-[calc(100vh-200px)] overflow-hidden">
                                                    <ControleProdutividadeSystem 
                                                        currentUser={currentUser} 
                                                        isDarkMode={isDarkMode} 
                                                    />
                                                </div>
                                            )}

                                            {/* [APP_RENDER__COLLAB_CENTER__MY_GROWTH] ABA: MINHA EVOLUCAO */}
                                            {activeTabId === 'my_growth' && (
                                                <div className="space-y-6 animate-fade-in">
                                                    <div className={`flex justify-between items-center p-3 rounded-xl shadow-sm border transition-colors ${isDarkMode ? 'bg-slate-800 border-slate-700' : '!bg-white border-gray-200'}`}>
                                                        <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : '!text-gray-800'}`}>Central de Evolução</h3>
                                                        <div className="flex items-center gap-2">
                                                            {/* CORREÇÃO: Forçando cor do texto do label no modo claro */}
                                                            <label className={`text-sm font-bold mr-2 ${isDarkMode ? 'text-white' : '!text-gray-700'}`}>Ano Ref:</label>
                                                            
                                                            {/* CORREÇÃO: Forçando fundo branco e texto preto no input no modo claro */}
                                                            <input 
                                                                type="number" 
                                                                className={`border p-1.5 rounded w-20 text-center font-bold outline-none focus:ring-2 focus:ring-blue-500 
                                                                ${isDarkMode ? 'bg-slate-900 text-white border-slate-600' : '!bg-white !text-gray-900 !border-gray-300'}`} 
                                                                value={collabViewYear} 
                                                                onChange={(e) => setCollabViewYear(e.target.value)} 
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* 1. QUADRO EVOLUÇÃO TRIMESTRAL (Agora só mostra popups AvDes) */}
                                                    <Card title="Visão Geral: Evolução Trimestral (8T)" darkMode={isDarkMode}>
                                                        <div className="min-h-[200px]">
                                                            <QuarterlyEvolutionTable data={data} currentUser={currentUser} analiseFilters={{ dept: '', personType: 'Todos', endDate: `${collabViewYear}-12-31` }} isDarkMode={isDarkMode} hideInternalFilter={true} showSelfOnly={true} />
                                                        </div>
                                                    </Card>

                                                    {/* 2. QUADRO CURSOS E TREINAMENTOS (Zebrado + Cores + Formato Hora) */}
                                                    <Card title={`Histórico de Cursos e Treinamentos (${collabViewYear})`} darkMode={isDarkMode}>
                                                        <div className="grid grid-cols-3 gap-4 mb-6 border-b pb-4 dark:border-slate-700">
                                                            <div className="text-center">
                                                                <span className="text-[10px] font-bold uppercase text-blue-500">Área de Atuação (D1)</span>
                                                                <div className="text-2xl font-black text-blue-600">{trainingStatsDisplay.d1.toFixed(1)}h</div>
                                                            </div>
                                                            <div className="text-center border-x dark:border-slate-700">
                                                                <span className="text-[10px] font-bold uppercase text-green-500">Demais Áreas (D2)</span>
                                                                <div className="text-2xl font-black text-green-600">{trainingStatsDisplay.d2.toFixed(1)}h</div>
                                                            </div>
                                                            <div className="text-center">
                                                                <span className="text-[10px] font-bold uppercase text-purple-500">Total Acumulado</span>
                                                                <div className="text-2xl font-black text-purple-600">{trainingStatsDisplay.total.toFixed(1)}h</div>
                                                            </div>
                                                        </div>
                                                        <div className="overflow-x-auto">
                                                            <table className="w-full text-[11px] text-left border-collapse">
                                                                <thead>
                                                                    <tr className="border-b text-gray-400 uppercase dark:border-slate-700">
                                                                        <th className="p-3">Data</th>
                                                                        <th className="p-3">Descrição</th>
                                                                        <th className="p-3 text-center">Tipo</th>
                                                                        <th className="p-3 text-center">Horas</th>
                                                                        <th className="p-3 text-center">Links</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y dark:divide-slate-700">
                                                                    {collaboratorTrainings.length > 0 ? collaboratorTrainings.map((t, idx) => {
                                                                        // Lógica de Zebra
                                                                        const rowBg = idx % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');
                                                                        
                                                                        // Lógica de Cores do Tipo
                                                                        const isAtuacao = t.type.includes('1') || t.type.includes('Atuação') || t.type.includes('atuacao');
                                                                        const badgeClass = isAtuacao 
                                                                            ? (isDarkMode ? 'bg-blue-900 text-blue-200 border-blue-800' : 'bg-blue-600 text-white border-blue-600') 
                                                                            : (isDarkMode ? 'bg-emerald-900 text-emerald-200 border-emerald-800' : 'bg-emerald-600 text-white border-emerald-600');

                                                                        return (
                                                                            // CORREÇÃO AQUI: hover:bg-gray-100 no claro
                                                                                <tr key={t.id} className={`${rowBg} ${isDarkMode ? 'hover:bg-slate-700' : 'hover:!bg-gray-200'} transition-colors`}>
                                                                                <td className="p-3 whitespace-nowrap font-mono">{t.date}</td>
                                                                                <td className="p-3 font-bold">{t.description}</td>
                                                                                <td className="p-3 text-center">
                                                                                    <span className={`px-2 py-1 rounded text-[10px] font-bold border whitespace-nowrap shadow-sm ${badgeClass}`}>
                                                                                        {t.type}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="p-3 text-center font-bold text-purple-600 text-xs">
                                                                                    {formatDecimalToTime(t.hours)}h
                                                                                </td>
                                                                                <td className="p-3 text-center">
                                                                                    <div className="flex justify-center gap-2">
                                                                                        {t.summaryLink && <a href={t.summaryLink} target="_blank" className="text-blue-500 hover:scale-110 transition-transform"><FileText size={16} /></a>}
                                                                                        {t.certLink && <a href={t.certLink} target="_blank" className="text-green-500 hover:scale-110 transition-transform"><Award size={16} /></a>}
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    }) : <tr><td colSpan={5} className="p-4 text-center text-gray-400">Nenhum curso neste ano.</td></tr>}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </Card>

                                                    
                                                    {/* 3. QUADRO DETALHAMENTO DE PERFORMANCE (Agrupado, Colorido e Colapsado) */}
                                                    <Card title={`Detalhamento de Performance: Qualitativa e Quantitativa (${collabViewYear})`} darkMode={isDarkMode}>
                                                        {(() => {
                                                            const targetEval = data.evaluations.find(e => e.collaboratorId === loggedCollabId && e.periodYear === collabViewYear);
                                                            if (!targetEval) return <div className="p-8 text-center text-gray-400 italic">Nenhuma avaliação iniciada para o ano {collabViewYear}.</div>;
                                                            
                                                            const isGestao = targetEval.personType === 'Gestão';

                                                            // 1. AGRUPAMENTO À PROVA DE FALHAS
                                                            const groupedCriteria = (targetEval.qualitative?.criteria || []).reduce((acc, c) => {
                                                                const dimName = c.dim || c.dimension || c.grupo || c.group || c.categoria || 'Critérios Avaliados';
                                                                if (!acc[dimName]) acc[dimName] = [];
                                                                acc[dimName].push(c);
                                                                return acc;
                                                            }, {});

                                                            // 2. PALETA DOS AGRUPAMENTOS
                                                            const groupPalettes = [
                                                                { bg: 'bg-blue-50', text: 'text-blue-800', border: 'border-blue-200', darkBg: 'bg-blue-900/20', darkText: 'text-blue-300', darkBorder: 'border-blue-800/50' },
                                                                { bg: 'bg-emerald-50', text: 'text-emerald-800', border: 'border-emerald-200', darkBg: 'bg-emerald-900/20', darkText: 'text-emerald-300', darkBorder: 'border-emerald-800/50' },
                                                                { bg: 'bg-purple-50', text: 'text-purple-800', border: 'border-purple-200', darkBg: 'bg-purple-900/20', darkText: 'text-purple-300', darkBorder: 'border-purple-800/50' },
                                                                { bg: 'bg-orange-50', text: 'text-orange-800', border: 'border-orange-200', darkBg: 'bg-orange-900/20', darkText: 'text-orange-300', darkBorder: 'border-orange-800/50' },
                                                                { bg: 'bg-pink-50', text: 'text-pink-800', border: 'border-pink-200', darkBg: 'bg-pink-900/20', darkText: 'text-pink-300', darkBorder: 'border-pink-800/50' }
                                                            ];

                                                            // 3. CORES DAS NOTAS QUALITATIVAS
                                                            // Colaborador: usa a mesma escala visual do preenchimento da avaliação.
                                                            // Gestão: mantém a regra proporcional atual, pois já está correta.
                                                            const getScoreStyle = (val, isGestaoMode) => {
                                                                if (val === null || val === undefined || val === '') {
                                                                    return isDarkMode
                                                                        ? 'bg-slate-800 text-slate-500 border-slate-700'
                                                                        : 'bg-gray-50 text-gray-300 border-gray-200';
                                                                }

                                                                const n = parseFloat(val);

                                                                if (Number.isNaN(n)) {
                                                                    return isDarkMode
                                                                        ? 'bg-slate-800 text-slate-500 border-slate-700'
                                                                        : 'bg-gray-50 text-gray-300 border-gray-200';
                                                                }

                                                                // Gestão: mantém comportamento proporcional existente.
                                                                if (isGestaoMode) {
                                                                    const pct = (n / 5) * 100;

                                                                    if (pct >= 85) return 'bg-blue-600 text-white border-blue-700';
                                                                    if (pct >= 70) return 'bg-green-600 text-white border-green-700';
                                                                    if (pct >= 50) return 'bg-yellow-500 text-black border-yellow-600';
                                                                    if (pct >= 30) return 'bg-orange-500 text-white border-orange-600';
                                                                    return 'bg-red-600 text-white border-red-700';
                                                                }

                                                                // Colaborador: mesma escala do preenchimento.
                                                                if (n <= 1.5) return 'bg-red-500 text-white border-red-600';
                                                                if (n <= 2.5) return 'bg-orange-500 text-white border-orange-600';
                                                                if (n <= 3.5) return 'bg-green-500 text-white border-green-600';
                                                                if (n <= 4.5) return 'bg-blue-500 text-white border-blue-600';

                                                                return 'bg-indigo-600 text-white border-indigo-700';
                                                            };

                                                            const qualiLegendItems = [
                                                                {
                                                                    value: 1,
                                                                    label: 'Abaixo do Esperado',
                                                                    className: 'bg-red-500 text-white border-red-600'
                                                                },
                                                                {
                                                                    value: 2,
                                                                    label: 'Parcialmente do Esperado',
                                                                    className: 'bg-orange-500 text-white border-orange-600'
                                                                },
                                                                {
                                                                    value: 3,
                                                                    label: 'Atinge o Esperado',
                                                                    className: 'bg-green-500 text-white border-green-600'
                                                                },
                                                                {
                                                                    value: 4,
                                                                    label: 'Acima do Esperado',
                                                                    className: 'bg-blue-500 text-white border-blue-600'
                                                                }
                                                            ];

                                                            const getQuantiBadgeStyle = (errType, eItem, count) => {
                                                                if (!count || count <= 0) return null;

                                                                const colorSource = eItem || errType || {};

                                                                return {
                                                                    backgroundColor: colorSource.bgColor || '#ef4444',
                                                                    color: colorSource.textColor || '#ffffff',
                                                                    borderColor: colorSource.bgColor || '#ef4444'
                                                                };
                                                            };

                                                            return (
                                                                <div className="space-y-6">

                                                                {!isGestao && (
                                                                        <div className={`p-3 rounded-lg border flex flex-wrap items-center gap-4 ${
                                                                            isDarkMode
                                                                                ? 'bg-slate-800/60 border-slate-700 text-slate-300'
                                                                                : 'bg-white border-gray-200 text-gray-700'
                                                                        }`}>
                                                                            <span className={`text-[10px] font-black uppercase tracking-wider ${
                                                                                isDarkMode ? 'text-slate-400' : 'text-gray-600'
                                                                            }`}>
                                                                                Legenda:
                                                                            </span>

                                                                            {qualiLegendItems.map(item => (
                                                                                <div key={item.value} className="flex items-center gap-2">
                                                                                    <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold shadow-sm border ${item.className}`}>
                                                                                        {item.value}
                                                                                    </div>
                                                                                    <span className={`text-xs font-medium ${
                                                                                        isDarkMode ? 'text-slate-400' : 'text-gray-600'
                                                                                    }`}>
                                                                                        {item.label}
                                                                                    </span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {/* === SEÇÃO QUALITATIVA (Removido o atributo 'open') === */}
                                                                    <div className="space-y-4">
                                                                        {Object.entries(groupedCriteria).map(([dimension, criteria], idx) => {
                                                                            const palette = groupPalettes[idx % groupPalettes.length];
                                                                            return (
                                                                            <details key={dimension} className={`group border rounded-xl overflow-hidden shadow-sm ${isDarkMode ? `${palette.darkBorder} bg-slate-800/20` : `${palette.border} bg-white`}`}>
                                                                                <summary className={`flex items-center justify-between p-3 cursor-pointer list-none ${isDarkMode ? `${palette.darkBg} ${palette.darkText}` : `${palette.bg} ${palette.text}`}`}>
                                                                                    <span className="font-bold uppercase text-xs tracking-widest">{dimension}</span>
                                                                                    <div className="transition-transform group-open:rotate-180 opacity-60">
                                                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                                                                    </div>
                                                                                </summary>
                                                                                
                                                                                <div className="overflow-x-auto">
                                                                                    <table className="w-full text-[10px] border-collapse">
                                                                                        <thead>
                                                                                            <tr className={`font-bold border-b ${isDarkMode ? 'bg-slate-900/50 text-slate-400 border-slate-700' : 'bg-gray-50 text-gray-500'}`}>
                                                                                                <th className="p-3 text-left w-64">Critério</th>
                                                                                                {timeline.map(t => <th key={t.id} className="p-3 text-center border-l dark:border-slate-700 min-w-[80px]">{t.label}</th>)}
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody className="divide-y dark:divide-slate-700">
                                                                                            {criteria.map((crit, rowIdx) => {
                                                                                                const rowBg = rowIdx % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');
                                                                                                return (
                                                                                                    <tr key={crit.id} className={`${rowBg} ${isDarkMode ? 'hover:bg-slate-700' : 'hover:!bg-gray-100'} transition-colors`}>
                                                                                                        <td className={`p-3 border-r font-medium ${isDarkMode ? 'border-slate-700 text-slate-300' : 'border-gray-100 text-gray-700'}`}>{crit.name}</td>
                                                                                                        {timeline.map((t, idxT) => {
                                                                                                            const evYear = data.evaluations.find(e => e.collaboratorId === loggedCollabId && e.periodYear === t.year);
                                                                                                            let val = null;
                                                                                                            let isVisible = false;
                                                                                                            if (evYear) {
                                                                                                                const isArchived = evYear.status === 'Concluída Financeiro';
                                                                                                                const signatures = evYear.managerSignatures || [];
                                                                                                                const qTag = t.qKey.replace('q', 'T').toUpperCase(); 
                                                                                                                const hasSig = signatures.some(s => {
                                                                                                                    const ref = (s.statusRef || '').toUpperCase();
                                                                                                                    return ref.includes(qTag) || ref.includes('FINALIZADA') || ref.includes('ASSINADA');
                                                                                                                });
                                                                                                                if (isArchived || hasSig) isVisible = true;
                                                                                                            }
                                                                                                            if (isVisible && evYear?.qualitative?.criteria) {
                                                                                                                const cItem = evYear.qualitative.criteria.find(x => x.name === crit.name);
                                                                                                                if (cItem && cItem[t.qKey]) val = parseFloat(cItem[t.qKey]);
                                                                                                            }
                                                                                                            let Icon = null;
                                                                                                            const prevT = timeline[idxT + 1];
                                                                                                            if (prevT && val !== null) {
                                                                                                                const prevEv = data.evaluations.find(e => e.collaboratorId === loggedCollabId && e.periodYear === prevT.year);
                                                                                                                if (prevEv?.qualitative?.criteria) {
                                                                                                                    const prevC = prevEv.qualitative.criteria.find(x => x.name === crit.name);
                                                                                                                    if (prevC && prevC[prevT.qKey]) {
                                                                                                                        const prevVal = parseFloat(prevC[prevT.qKey]);
                                                                                                                        if (val > prevVal) Icon = <TrendingUp size={12} className="text-white drop-shadow-md" />;
                                                                                                                        else if (val < prevVal) Icon = <TrendingDown size={12} className="text-white drop-shadow-md" />;
                                                                                                                        else Icon = <Minus size={12} className="opacity-50" />;
                                                                                                                    }
                                                                                                                }
                                                                                                            }
                                                                                                            return (
                                                                                                                <td key={t.id} className={`p-2 text-center border-r ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                                                                                                    {val !== null ? (
                                                                                                                        <div className={`mx-auto w-16 py-1.5 flex items-center justify-center gap-1 rounded shadow-sm border ${getScoreStyle(val, isGestao)}`}>
                                                                                                                            <span className="font-bold text-[11px]">{val.toFixed(1)}</span>
                                                                                                                            {Icon}
                                                                                                                        </div>
                                                                                                                    ) : <span className="text-gray-300">-</span>}
                                                                                                                </td>
                                                                                                            );
                                                                                                        })}
                                                                                                    </tr>
                                                                                                );
                                                                                            })}
                                                                                        </tbody>
                                                                                    </table>
                                                                                </div>
                                                                            </details>
                                                                        )})}
                                                                    </div>

                                                                    {/* === SEÇÃO QUANTITATIVA (Removido o atributo 'open') === */}
                                                                    {!isGestao && targetEval.quantitative?.errors && (
                                                                        <details className={`group border rounded-xl overflow-hidden shadow-sm ${isDarkMode ? 'border-red-900/30 bg-slate-800/20' : 'border-red-100 bg-white'}`}>
                                                                            <summary className={`flex items-center justify-between p-3 cursor-pointer list-none ${isDarkMode ? 'bg-red-900/40 text-red-400' : 'bg-red-50 text-red-800'}`}>
                                                                                <span className="font-bold uppercase text-xs tracking-widest">Avaliação Quantitativa</span>
                                                                                <div className="transition-transform group-open:rotate-180 opacity-60">
                                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                                                                </div>
                                                                            </summary>
                                                                            <div className="overflow-x-auto">
                                                                                <table className="w-full text-[10px] border-collapse">
                                                                                    <thead>
                                                                                        <tr className={`font-bold border-b ${isDarkMode ? 'bg-slate-900/50 text-slate-400 border-slate-700' : 'bg-gray-50 text-gray-500'}`}>
                                                                                            <th className="p-3 text-left w-64">Tipo de Ocorrência</th>
                                                                                            {timeline.map(t => <th key={t.id} className="p-3 text-center border-l dark:border-slate-700 min-w-[80px]">{t.label}</th>)}
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody className="divide-y dark:divide-slate-700">
                                                                                        {targetEval.quantitative.errors.map((errType, idx) => {
                                                                                            const rowBg = idx % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');
                                                                                            return (
                                                                                                <tr key={errType.name} className={`${rowBg} hover:bg-red-50/50 dark:hover:bg-slate-700 transition-colors`}>
                                                                                                    <td className={`p-3 border-r font-medium ${isDarkMode ? 'border-slate-700 text-slate-300' : 'border-gray-100 text-gray-700'}`}>
                                                                                                        {formatOccurrenceTerm(errType.name)} <span className="text-[9px] opacity-50 ml-1">(Peso {errType.peso})</span>
                                                                                                    </td>
                                                                                                    {timeline.map((t, idxT) => {
                                                                                                        const evYear = data.evaluations.find(e => e.collaboratorId === loggedCollabId && e.periodYear === t.year);
                                                                                                        let count = 0;
                                                                                                        let hasData = false;
                                                                                                        let isVisible = false;
                                                                                                        let eItem = null;
                                                                                                        if (evYear) {
                                                                                                            const isArchived = evYear.status === 'Concluída Financeiro';
                                                                                                            const signatures = evYear.managerSignatures || [];
                                                                                                            const qTag = t.qKey.replace('q', 'T').toUpperCase();
                                                                                                            const hasSig = signatures.some(s => {
                                                                                                                const ref = (s.statusRef || '').toUpperCase();
                                                                                                                return ref.includes(qTag) || ref.includes('FINALIZADA') || ref.includes('ASSINADA');
                                                                                                            });
                                                                                                            if (isArchived || hasSig) isVisible = true;
                                                                                                        }
                                                                                                        const isStarted = evYear?.qualitative?.criteria?.some(c => c[t.qKey] !== null && c[t.qKey] !== '');
                                                                                                        if (isVisible && isStarted && evYear?.quantitative?.errors) {
                                                                                                            hasData = true;
                                                                                                            eItem = evYear.quantitative.errors.find(x => x.name === errType.name);
                                                                                                            if (eItem && eItem[t.qKey]) count = parseInt(eItem[t.qKey]);
                                                                                                        }
                                                                                                        let Icon = null;
                                                                                                        const prevT = timeline[idxT + 1];
                                                                                                        if (prevT && hasData) {
                                                                                                            const prevEv = data.evaluations.find(e => e.collaboratorId === loggedCollabId && e.periodYear === prevT.year);
                                                                                                            const prevStarted = prevEv?.qualitative?.criteria?.some(c => c[prevT.qKey] !== null);
                                                                                                            if (prevStarted) {
                                                                                                                const prevEItem = prevEv?.quantitative?.errors?.find(x => x.name === errType.name);
                                                                                                                const prevCount = prevEItem ? (parseInt(prevEItem[prevT.qKey]) || 0) : 0;
                                                                                                                if (count > prevCount) Icon = <TrendingUp size={10} className="text-red-500" />;
                                                                                                                else if (count < prevCount) Icon = <TrendingDown size={10} className="text-green-500" />;
                                                                                                                else Icon = <Minus size={10} className="text-gray-300" />;
                                                                                                            }
                                                                                                        }
                                                                                                        return (
                                                                                                            <td key={t.id} className={`p-2 text-center border-r ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                                                                                                {hasData ? (
                                                                                                                    <div className="flex items-center justify-center gap-1">
                                                                                                                        {count > 0 ? (
                                                                                                                            <span
                                                                                                                                className="inline-flex min-w-[42px] h-7 px-2 rounded-md border items-center justify-center font-black text-[11px] shadow-sm"
                                                                                                                                style={getQuantiBadgeStyle(errType, eItem, count)}
                                                                                                                            >
                                                                                                                                {count}
                                                                                                                            </span>
                                                                                                                        ) : (
                                                                                                                            <span className={`inline-flex min-w-[42px] h-7 px-2 rounded-md border items-center justify-center font-black text-[11px] ${
                                                                                                                                isDarkMode
                                                                                                                                    ? 'bg-slate-800 text-slate-500 border-slate-700'
                                                                                                                                    : 'bg-gray-50 text-gray-400 border-gray-200'
                                                                                                                            }`}>
                                                                                                                                0
                                                                                                                            </span>
                                                                                                                        )}

                                                                                                                        {Icon}
                                                                                                                    </div>
                                                                                                                ) : (
                                                                                                                    <span className="text-gray-300">-</span>
                                                                                                                )}
                                                                                                            </td>
                                                                                                        );
                                                                                                    })}
                                                                                                </tr>
                                                                                            );
                                                                                        })}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        </details>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}
                                                    </Card>

                                                    {/* 4. QUADRO FEEDBACKS */}
                                                    <Card title={`Histórico de Feedbacks (${collabViewYear})`} darkMode={isDarkMode}>
                                                        <div className="space-y-3">
                                                            {avDesFeedbacksYear.length > 0 ? avDesFeedbacksYear.map(f => {
                                                                const isRh = f.type === 'AvDes';
                                                                const isPositiveManager = f.type === 'Gestão' && String(f?.nature || '').trim().toLowerCase() === 'positivo';

                                                                return (
                                                                    <div
                                                                        key={f.id || `${f.timestamp}-${f.type}`}
                                                                        className={`p-3 rounded flex gap-3 border-l-4 ${
                                                                            isRh
                                                                                ? 'bg-red-50 dark:bg-red-900/10 border-red-500'
                                                                                : isPositiveManager
                                                                                    ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-500'
                                                                                    : 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-500'
                                                                        }`}
                                                                    >
                                                                        <div className={`${isRh ? 'text-red-500' : isPositiveManager ? 'text-blue-500' : 'text-emerald-500'} mt-0.5`}>
                                                                            <AlertCircle size={16} />
                                                                        </div>

                                                                        <div>
                                                                            <div className={`text-[10px] font-bold mb-1 ${
                                                                                isRh ? 'text-red-600' : isPositiveManager ? 'text-blue-600' : 'text-emerald-600'
                                                                            }`}>
                                                                                {f.timestamp ? new Date(f.timestamp).toLocaleDateString('pt-BR') : '-'}
                                                                                {' - '}
                                                                                {isRh ? 'AvDes (RH)' : isPositiveManager ? 'Gestão - Positivo' : 'Colaborador'}
                                                                                {' - Ref: '}
                                                                                {f.quarter ? f.quarter.toUpperCase() : 'Geral'}
                                                                            </div>

                                                                            <p className={`text-xs whitespace-pre-wrap font-bold ${
                                                                                isDarkMode ? 'text-gray-100' : '!text-gray-900'
                                                                            }`}>
                                                                                {f.text || '-'}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }) : (
                                                                <div className="text-center p-4 text-gray-400 text-xs italic">
                                                                    Nenhum feedback registrado neste ano.
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Card>
                                                </div>
                                            )}

                                        {/* [APP_RENDER__COLLAB_CENTER__MY_EVALS] */}
                                        {activeTabId === 'my_evals' && (
                                            <div className="space-y-4 animate-fade-in">
                                                <Card className="p-0 overflow-hidden" darkMode={isDarkMode}>
                                                    <div className={`p-4 border-b font-bold flex items-center gap-2 ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-800'}`}>
                                                        <ClipboardCheck size={18} className="text-blue-500"/> Minhas Avaliações e Assinaturas
                                                    </div>
                                                    <div className="overflow-x-auto">
                                                        <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                            <thead className={`text-xs uppercase font-bold ${isDarkMode ? 'bg-slate-900 text-slate-400 border-b border-slate-700' : 'bg-gray-100 text-gray-600 border-b border-gray-200'}`}>
                                                                <tr>
                                                                    <th className="p-4">Ano Ref.</th>
                                                                    <th className="p-4">Gestor Responsável</th>
                                                                    <th className="p-4">Status Atual</th>
                                                                    <th className="p-4 text-center w-56">Ações / Assinatura</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700 bg-slate-800' : 'divide-gray-100 bg-white'}`}>
                                                                {evals.filter(e => {
                                                                    if (e.collaboratorId !== loggedCollabId) return false;
                                                                    const mgrSigs = e.managerSignatures || [];
                                                                    const hasAnySig = mgrSigs.length > 0;
                                                                    const isArchived = e.status === 'Concluída Financeiro' || e.status === 'Finalizada' || e.status === 'T4-Concluido' || e.status.includes('Concluido');
                                                                    return hasAnySig || isArchived;
                                                            }).map(ev => {
                                                                        const isArchived = ev.status === 'Concluída Financeiro';
                                                                        
                                                                        // 1. Definição do Nível do Status (Texto)
                                                                        const statusMap = { 'Em Andamento': 0, 'T1-Concluido': 1, 'T2-Concluido': 2, 'T3-Concluido': 3, 'T4-Concluido': 4, 'Finalizada': 4, 'Assinada pelo Gestor': 4 };
                                                                        let currentSystemLevel = statusMap[ev.status] || 0;

                                                                        // 2. DETECÇÃO INTELIGENTE DE NÍVEL (CORREÇÃO DE LEGENDA)
                                                                        // Se o gestor já assinou trimestres avançados, usamos essa assinatura como verdade,
                                                                        // ignorando se o status textual ficou "atrasado".
                                                                        const mgrSigs = ev.managerSignatures || [];
                                                                        let maxMgrSigLevel = 0; 
                                                                        mgrSigs.forEach(s => {
                                                                            const ref = (s.statusRef || '').toUpperCase();
                                                                            if(ref.includes('T4') || ref.includes('FINALIZADA') || ref.includes('ASSINADA')) maxMgrSigLevel = 4;
                                                                            else if(ref.includes('T3') && maxMgrSigLevel < 3) maxMgrSigLevel = 3; // Corrigido
                                                                            else if(ref.includes('T2') && maxMgrSigLevel < 2) maxMgrSigLevel = 2; // Corrigido
                                                                            else if(ref.includes('T1') && maxMgrSigLevel < 1) maxMgrSigLevel = 1; // Corrigido
                                                                        });

                                                                        // O nível efetivo é o maior entre o Status Oficial e o que o Gestor já assinou
                                                                        const effectiveLevel = Math.max(currentSystemLevel, maxMgrSigLevel);
                                                                        
                                                                        const colSigs = ev.collabSignatures || {};
                                                                        const hasManagerSignedAny = mgrSigs.length > 0;

                                                                        const checkSig = (role, qNum) => {
                                                                            const tag = `T${qNum}`;
                                                                            if (role === 'mgr') {
                                                                                // Verifica se existe assinatura do gestor para este nível ou superior
                                                                                return maxMgrSigLevel >= qNum;
                                                                            }
                                                                            if (role === 'col') return !!colSigs[`q${qNum}`];
                                                                            return false;
                                                                        };

                                                                        // 3. Status Visual (Usando effectiveLevel)
                                                                        let displayStatus = 'EM ANDAMENTO';
                                                                        let statusColor = isDarkMode ? 'bg-blue-900/30 text-blue-400 border-blue-800' : 'bg-blue-50 text-blue-700 border-blue-100';
                                                                        let actionType = 'none'; 
                                                                        let targetQuarter = null; 

                                                                        if (isArchived) {
                                                                            displayStatus = 'ARQUIVADA';
                                                                            statusColor = isDarkMode ? 'bg-slate-700 text-slate-400 border-slate-600' : 'bg-gray-100 text-gray-500 border-gray-200';
                                                                        } else if (effectiveLevel === 0) {
                                                                            displayStatus = 'EM ANDAMENTO (Preenchimento)';
                                                                        } else {
                                                                            let foundPending = false;
                                                                            // Itera até o nível efetivo descoberto
                                                                            for (let q = 1; q <= effectiveLevel; q++) {
                                                                                const mgrDone = checkSig('mgr', q);
                                                                                const colDone = checkSig('col', q);

                                                                                if (!mgrDone) {
                                                                                    // Raro cair aqui com a nova lógica, mas mantemos por segurança
                                                                                    displayStatus = `PENDENTE ASSINATURA GESTOR T${q}`;
                                                                                    statusColor = isDarkMode ? 'bg-orange-900/30 text-orange-400 border-orange-800' : 'bg-orange-100 text-orange-700 border-orange-200';
                                                                                    foundPending = true;
                                                                                    break; 
                                                                                } else if (!colDone) {
                                                                                    displayStatus = `PENDENTE ASSINATURA COLABORADOR T${q}`;
                                                                                    statusColor = isDarkMode ? 'bg-yellow-900/30 text-yellow-300 border-yellow-800' : 'bg-yellow-100 text-yellow-800 border-yellow-200';
                                                                                    actionType = 'sign';
                                                                                    targetQuarter = `q${q}`;
                                                                                    foundPending = true;
                                                                                    break;
                                                                                }
                                                                            }

                                                                            if (!foundPending) {
                                                                                // Se tudo até aqui está assinado por ambos...
                                                                                if (effectiveLevel === 4) {
                                                                                    // Verifica validações finais
                                                                                    const hasDiretoriaSig = !!ev.diretoriaSignature;
                                                                                    const hasRHSig = !!ev.rhSignature;
                                                                                    const isGestaoType = ev.personType === 'Gestão';
                                                                                    const isFullyValidated = hasRHSig && (isGestaoType || hasDiretoriaSig);

                                                                                    if (isFullyValidated) {
                                                                                        displayStatus = 'CONCLUÍDA (Aguardando Arquivamento)';
                                                                                        statusColor = isDarkMode ? 'bg-green-900/30 text-green-400 border-green-800' : 'bg-green-100 text-green-700 border-green-200';
                                                                                    } else {
                                                                                        displayStatus = 'PENDENTE ASSINATURA RH / DIRETORIA';
                                                                                        statusColor = isDarkMode ? 'bg-purple-900/30 text-purple-300 border-purple-800' : 'bg-purple-100 text-purple-700 border-purple-200';
                                                                                    }
                                                                                    actionType = 'undo';
                                                                                    targetQuarter = 'q4';
                                                                                } else {
                                                                                    displayStatus = `AGUARDANDO FECHAMENTO T${effectiveLevel + 1}`;
                                                                                    statusColor = isDarkMode ? 'bg-slate-800 text-slate-400 border-slate-700' : 'bg-gray-50 text-gray-500 border-gray-200';
                                                                                    actionType = 'undo';
                                                                                    targetQuarter = `q${effectiveLevel}`;
                                                                                }
                                                                            }
                                                                        }

                                                                    // Lógica para o botão de desfazer (último assinado)
                                                                    const isSignedQ4 = checkSig('col', 4);
                                                                    const isSignedQ3 = checkSig('col', 3);
                                                                    const isSignedQ2 = checkSig('col', 2);
                                                                    const isSignedQ1 = checkSig('col', 1);
                                                                    let lastSignedQ = isSignedQ4 ? 'q4' : isSignedQ3 ? 'q3' : isSignedQ2 ? 'q2' : isSignedQ1 ? 'q1' : null;

                                                                    return (
                                                                        <tr key={ev.id} className={`transition-colors ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-blue-50/50'}`}>
                                                                            <td className={`p-4 font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{ev.periodYear}</td>
                                                                            <td className="p-4">{ev.managerName || 'N/D'}</td>
                                                                            <td className="p-4">
                                                                                <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border ${statusColor}`}>
                                                                                    {displayStatus}
                                                                                </span>
                                                                            </td>
                                                                            
                                                                            <td className="p-4 text-center">
                                                                                <div className="flex justify-center gap-2">
                                                                                    
                                                                                    {/* 1. IMPRIMIR (RESTAURADO) */}
                                                                                    <button 
                                                                                        onClick={() => {
                                                                                            if (hasManagerSignedAny) {
                                                                                                setSelectedEval(ev);
                                                                                                setModal('pdf'); // Usa o modal de PDF global
                                                                                            } else {
                                                                                                alert("O PDF só estará disponível após a assinatura do gestor.");
                                                                                            }
                                                                                        }}
                                                                                        disabled={!hasManagerSignedAny}
                                                                                        className={`p-2 rounded-lg transition-colors ${!hasManagerSignedAny ? 'text-gray-300 cursor-not-allowed opacity-50' : (isDarkMode ? 'text-blue-400 hover:bg-slate-600' : 'text-blue-600 hover:bg-blue-50')}`}
                                                                                        title="Imprimir Relatório"
                                                                                    >
                                                                                        <Printer size={18}/>
                                                                                    </button>

                                                                                    {/* 2. FEEDBACK / CONSIDERAÇÕES (RESTAURADO) */}
                                                                                    <button 
                                                                                        onClick={() => !isArchived && setCollabFeedbackModal({
                                                                                            isOpen: true,
                                                                                            evalId: ev.id,
                                                                                            text: '',
                                                                                            quarter: '',
                                                                                            tab: 'insert',
                                                                                            editingId: null
                                                                                        })}
                                                                                        disabled={isArchived}
                                                                                        className={`p-2 rounded-lg transition-colors ${isArchived ? 'text-gray-300 cursor-not-allowed opacity-50' : (isDarkMode ? 'text-purple-400 hover:bg-slate-600' : 'text-purple-500 hover:bg-purple-50')}`}
                                                                                        title="Minhas Considerações"
                                                                                    >
                                                                                        <MessageSquare size={18}/>
                                                                                    </button>

                                                                                    {/* 3. ASSINAR / DESFAZER (BOTÃO PRINCIPAL) */}
                                                                                    <button 
                                                                                        onClick={() => {
                                                                                            if (actionType === 'sign') {
                                                                                                setCollabConfirmModal({ isOpen: true, type: 'sign_quarter', data: ev, quarter: targetQuarter });
                                                                                            } else if (actionType === 'undo') {
                                                                                                setCollabConfirmModal({ isOpen: true, type: 'unsign_options', data: ev, quarter: lastSignedQ });
                                                                                            }
                                                                                        }}
                                                                                        disabled={actionType === 'none' || isArchived}
                                                                                        className={`p-2 rounded-lg transition-all relative group flex items-center justify-center shadow-sm border ${
                                                                                            isArchived 
                                                                                                ? 'text-gray-400 border-transparent cursor-not-allowed opacity-50'
                                                                                                : actionType === 'sign' 
                                                                                                    ? 'bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-600 animate-pulse font-bold' 
                                                                                                    : actionType === 'undo'
                                                                                                        ? 'bg-white text-green-600 border-green-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200' 
                                                                                                        : 'text-gray-300 border-transparent opacity-30 cursor-not-allowed'
                                                                                        }`} 
                                                                                        title={
                                                                                            isArchived ? "Avaliação Arquivada" :
                                                                                            actionType === 'sign' ? `Clique para Assinar ${targetQuarter?.toUpperCase()}` :
                                                                                            actionType === 'undo' ? "Gerenciar / Desfazer Assinaturas" :
                                                                                            "Aguardando Gestor"
                                                                                        }
                                                                                    >
                                                                                        {actionType === 'sign' && <><PenTool size={16} className="mr-2"/> Assinar {targetQuarter?.replace('q','T')}</>}
                                                                                        {actionType === 'undo' && <><CheckCircle size={16} className="mr-2"/> Assinado</>}
                                                                                        {actionType === 'none' && <Clock size={16}/>}
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                                {evals.filter(e => e.collaboratorId === loggedCollabId).length === 0 && (
                                                                    <tr><td colSpan={4} className={`p-8 text-center italic ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Nenhuma avaliação disponível.</td></tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </Card>

                                                {(() => null)() /* Helpers das considerações do colaborador */}

                                                {/*
                                                    Regras:
                                                    - 4 opções fixas de trimestre conforme ano da avaliação.
                                                    - Só permite gravar trimestre já assinado pela gestão.
                                                    - Ao gravar, timestamp/data = último dia do trimestre escolhido.
                                                */}

                                                {/* --- MODAL DE FEEDBACK (Considerações do Colaborador) --- */}
                                                {collabFeedbackModal.isOpen && (() => {
                                                    const targetEval = evals.find(e => e.id === collabFeedbackModal.evalId);
                                                    const evalYear = targetEval?.periodYear || new Date().getFullYear();

                                                    const quarterOptions = ['q1', 'q2', 'q3', 'q4'].map(q => ({
                                                        value: q,
                                                        label: getQuarterLabelForConsideration(q, evalYear),
                                                        signed: isQuarterSignedByManagerForConsideration(targetEval, q)
                                                    }));

                                                    const considerations = getCollabConsiderationsByEval(targetEval);
                                                    const canCreateConsiderationForEval = canCreateCollabConsideration(targetEval);

                                                    return (
                                                        <div
                                                            className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] backdrop-blur-sm p-4 animate-fade-in"
                                                            onClick={() => setCollabFeedbackModal({
                                                                isOpen: false,
                                                                evalId: null,
                                                                text: '',
                                                                quarter: '',
                                                                tab: 'insert'
                                                            })}
                                                        >
                                                            <div
                                                                className={`p-6 rounded-xl w-full max-w-3xl shadow-2xl border ${
                                                                    isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'
                                                                }`}
                                                                onClick={e => e.stopPropagation()}
                                                            >
                                                                <div className={`flex justify-between items-center mb-4 border-b pb-2 ${
                                                                    isDarkMode ? 'border-slate-700' : 'border-gray-100'
                                                                }`}>
                                                                    <div>
                                                                        <h3 className="font-bold text-lg flex items-center gap-2">
                                                                            <MessageSquare size={20} className="text-purple-500"/>
                                                                            Minhas Considerações
                                                                        </h3>
                                                                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                                                            Ano referência: {evalYear}
                                                                        </p>
                                                                    </div>

                                                                    <button
                                                                        onClick={() => setCollabFeedbackModal({
                                                                            isOpen: false,
                                                                            evalId: null,
                                                                            text: '',
                                                                            quarter: '',
                                                                            tab: 'insert'
                                                                        })}
                                                                        className="text-gray-400 hover:text-red-500 transition-colors"
                                                                    >
                                                                        <X size={20} />
                                                                    </button>
                                                                </div>

                                                                {/* ABAS */}
                                                                <div className={`flex gap-2 mb-4 border-b ${
                                                                    isDarkMode ? 'border-slate-700' : 'border-gray-200'
                                                                }`}>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setCollabFeedbackModal(prev => ({
                                                                            ...prev,
                                                                            tab: 'insert'
                                                                        }))}
                                                                        className={`px-4 py-2 text-xs font-bold rounded-t-lg ${
                                                                            collabFeedbackModal.tab === 'insert'
                                                                                ? 'bg-purple-600 text-white'
                                                                                : isDarkMode
                                                                                    ? 'bg-slate-900 text-slate-300 hover:bg-slate-700'
                                                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                                        }`}
                                                                    >
                                                                        {collabFeedbackModal.editingId ? 'Editar Consideração' : 'Inserir Consideração'}
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setCollabFeedbackModal(prev => ({
                                                                            ...prev,
                                                                            tab: 'history'
                                                                        }))}
                                                                        className={`px-4 py-2 text-xs font-bold rounded-t-lg ${
                                                                            collabFeedbackModal.tab === 'history'
                                                                                ? 'bg-purple-600 text-white'
                                                                                : isDarkMode
                                                                                    ? 'bg-slate-900 text-slate-300 hover:bg-slate-700'
                                                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                                        }`}
                                                                    >
                                                                        Ver Considerações
                                                                    </button>
                                                                </div>

                                                                {/* ABA 1: INSERIR */}
                                                                {collabFeedbackModal.tab === 'insert' && (
                                                                    <div className="space-y-4">
                                                                        <div>
                                                                            <label className={`block text-xs font-bold mb-1 ${
                                                                                isDarkMode ? 'text-slate-300' : 'text-gray-700'
                                                                            }`}>
                                                                                Trimestre
                                                                            </label>

                                                                            <select
                                                                                value={collabFeedbackModal.quarter || ''}
                                                                                onChange={e => setCollabFeedbackModal(prev => ({
                                                                                    ...prev,
                                                                                    quarter: e.target.value
                                                                                }))}
                                                                                className={`w-full p-3 border rounded outline-none focus:ring-2 focus:ring-purple-500 ${
                                                                                    isDarkMode
                                                                                        ? 'bg-slate-900 border-slate-600 text-white'
                                                                                        : 'bg-white border-gray-300 text-gray-800'
                                                                                }`}
                                                                            >
                                                                                <option value="">Selecione o trimestre...</option>

                                                                                {quarterOptions.map(opt => (
                                                                                    <option
                                                                                        key={opt.value}
                                                                                        value={opt.value}
                                                                                        disabled={!opt.signed}
                                                                                    >
                                                                                        {opt.label}{!opt.signed ? ' - aguardando assinatura da gestão' : ''}
                                                                                    </option>
                                                                                ))}
                                                                            </select>

                                                                            <p className={`text-[11px] mt-1 ${
                                                                                isDarkMode ? 'text-slate-400' : 'text-gray-500'
                                                                            }`}>
                                                                                Só é permitido inserir considerações para trimestres já assinados pela gestão.
                                                                            </p>
                                                                        </div>

                                                                        <div>
                                                                            <label className={`block text-xs font-bold mb-1 ${
                                                                                isDarkMode ? 'text-slate-300' : 'text-gray-700'
                                                                            }`}>
                                                                                Consideração
                                                                            </label>

                                                                            <textarea
                                                                                className={`w-full p-3 border rounded h-32 resize-none outline-none focus:ring-2 focus:ring-purple-500 custom-scrollbar ${
                                                                                    isDarkMode
                                                                                        ? 'bg-slate-900 border-slate-600 text-white placeholder-slate-500'
                                                                                        : 'bg-white border-gray-300 text-gray-800'
                                                                                }`}
                                                                                placeholder="Escreva suas considerações para o trimestre selecionado."
                                                                                value={collabFeedbackModal.text || ''}
                                                                                onChange={e => setCollabFeedbackModal(prev => ({
                                                                                    ...prev,
                                                                                    text: e.target.value
                                                                                }))}
                                                                                autoFocus
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* ABA 2: HISTÓRICO */}
                                                                {collabFeedbackModal.tab === 'history' && (
                                                                    <div className="space-y-3 max-h-[420px] overflow-y-auto custom-scrollbar pr-1">
                                                                        {!canCreateConsiderationForEval && (
                                                                            <div className={`p-3 rounded-lg border text-xs font-bold ${
                                                                                isDarkMode
                                                                                    ? 'bg-yellow-900/20 border-yellow-800 text-yellow-300'
                                                                                    : 'bg-yellow-50 border-yellow-200 text-yellow-700'
                                                                            }`}>
                                                                                Edição e exclusão bloqueadas quando o trimestre já foi assinado pelo colaborador, quando a avaliação possui assinatura de RH/Diretoria, está arquivada, ou quando a consideração foi criada por outro usuário.
                                                                            </div>
                                                                        )}

                                                                        {considerations.length > 0 ? considerations.map(f => (
                                                                            <div
                                                                                key={f.id || `${f.timestamp}-${f.quarter}`}
                                                                                className={`p-4 rounded-xl border ${
                                                                                    isDarkMode
                                                                                        ? 'bg-slate-900 border-slate-700'
                                                                                        : 'bg-gray-50 border-gray-200'
                                                                                }`}
                                                                            >
                                                                                <div className="flex flex-wrap justify-between gap-2 mb-2">
                                                                                    <div>
                                                                                        <span className="text-xs font-black text-purple-500">
                                                                                            {getQuarterLabelForConsideration(f.quarter, evalYear)}
                                                                                        </span>

                                                                                        <span className={`ml-2 text-[11px] font-bold ${
                                                                                            isDarkMode ? 'text-slate-400' : 'text-gray-500'
                                                                                        }`}>
                                                                                            {f.timestamp
                                                                                                ? new Date(f.timestamp).toLocaleDateString('pt-BR')
                                                                                                : '-'
                                                                                            }
                                                                                        </span>
                                                                                    </div>

                                                                                    <div className="flex gap-2">
                                                                                        <button
                                                                                            type="button"
                                                                                            disabled={!canEditOrDeleteCollabConsideration(targetEval, f.quarter, f)}
                                                                                            onClick={() => {
                                                                                                if (!canEditOrDeleteCollabConsideration(targetEval, f.quarter, f)) {
                                                                                                    return alert("Você só pode editar considerações criadas por você e que ainda não estejam bloqueadas por assinatura.");
                                                                                                }

                                                                                                setCollabFeedbackModal(prev => ({
                                                                                                    ...prev,
                                                                                                    tab: 'insert',
                                                                                                    editingId: f.id,
                                                                                                    quarter: f.quarter || '',
                                                                                                    text: f.text || ''
                                                                                                }));
                                                                                            }}
                                                                                            className={`px-2 py-1 rounded text-[10px] font-bold border ${
                                                                                                canEditOrDeleteCollabConsideration(targetEval, f.quarter, f)
                                                                                                    ? isDarkMode
                                                                                                        ? 'bg-blue-900/30 border-blue-700 text-blue-300 hover:bg-blue-800'
                                                                                                        : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                                                                                                    : 'opacity-40 cursor-not-allowed bg-gray-100 text-gray-400 border-gray-200'
                                                                                            }`}
                                                                                            title={
                                                                                                canEditOrDeleteCollabConsideration(targetEval, f.quarter, f)
                                                                                                    ? 'Editar consideração'
                                                                                                    : 'Bloqueado: somente o usuário que criou a consideração pode editar, desde que não haja assinatura do colaborador/RH/Diretoria ou arquivamento.'
                                                                                            }
                                                                                        >
                                                                                            Editar
                                                                                        </button>

                                                                                        <button
                                                                                        type="button"
                                                                                        disabled={!canEditOrDeleteCollabConsideration(targetEval, f.quarter, f)}
                                                                                        onClick={() => {
                                                                                            if (!canEditOrDeleteCollabConsideration(targetEval, f.quarter, f)) {
                                                                                                return alert("Você só pode excluir considerações criadas por você e que ainda não estejam bloqueadas por assinatura.");
                                                                                            }

                                                                                            if (!window.confirm("Deseja realmente excluir esta consideração?")) return;

                                                                                            if (!targetEval) return alert("Avaliação não encontrada.");

                                                                                            const updatedHistory = { ...(targetEval.feedbacksHistory || {}) };
                                                                                            delete updatedHistory[f.id];

                                                                                            actions.saveEvaluation({
                                                                                                ...targetEval,
                                                                                                feedbacksHistory: updatedHistory
                                                                                            });

                                                                                            alert("Consideração excluída com sucesso.");
                                                                                        }}
                                                                                        className={`px-2 py-1 rounded text-[10px] font-bold border ${
                                                                                            canEditOrDeleteCollabConsideration(targetEval, f.quarter, f)
                                                                                                ? isDarkMode
                                                                                                    ? 'bg-red-900/30 border-red-700 text-red-300 hover:bg-red-800'
                                                                                                    : 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                                                                                                : 'opacity-40 cursor-not-allowed bg-gray-100 text-gray-400 border-gray-200'
                                                                                        }`}
                                                                                        title={
                                                                                            canEditOrDeleteCollabConsideration(targetEval, f.quarter, f)
                                                                                                ? 'Excluir consideração'
                                                                                                : 'Bloqueado: somente o usuário que criou a consideração pode excluir, desde que não haja assinatura do colaborador/RH/Diretoria ou arquivamento.'
                                                                                        }
                                                                                    >
                                                                                        Excluir
                                                                                    </button>
                                                                                    </div>
                                                                                </div>

                                                                                <p className={`text-sm whitespace-pre-wrap ${
                                                                                    isDarkMode ? 'text-slate-100' : 'text-gray-800'
                                                                                }`}>
                                                                                    {f.text || '-'}
                                                                                </p>
                                                                            </div>
                                                                        )) : (
                                                                            <div className="text-center p-6 text-gray-400 text-xs italic">
                                                                                Nenhuma consideração registrada para este ano.
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                <div className={`flex justify-end gap-2 mt-6 pt-4 border-t ${
                                                                    isDarkMode ? 'border-slate-700' : 'border-gray-100'
                                                                }`}>
                                                                    <Button
                                                                        variant="secondary"
                                                                        onClick={() => setCollabFeedbackModal({
                                                                            isOpen: false,
                                                                            evalId: null,
                                                                            text: '',
                                                                            quarter: '',
                                                                            tab: 'insert'
                                                                        })}
                                                                        className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}
                                                                    >
                                                                        Fechar
                                                                    </Button>

                                                                    {collabFeedbackModal.tab === 'insert' && (
                                                                        <Button
                                                                            onClick={() => {
                                                                                if (!collabFeedbackModal.quarter) {
                                                                                    return alert("Selecione o trimestre.");
                                                                                }

                                                                                const selectedQuarter = quarterOptions.find(q =>
                                                                                    q.value === collabFeedbackModal.quarter
                                                                                );

                                                                                if (!selectedQuarter?.signed) {
                                                                                    return alert("Não é permitido inserir considerações para trimestre ainda não assinado pela gestão.");
                                                                                }

                                                                                if (!String(collabFeedbackModal.text || '').trim()) {
                                                                                    return alert("Escreva suas observações para este trimestre.");
                                                                                }

                                                                                if (!targetEval) {
                                                                                    return alert("Avaliação não encontrada.");
                                                                                }

                                                                                const quarterEndDate = getQuarterEndDateForConsideration(
                                                                                    collabFeedbackModal.quarter,
                                                                                    evalYear
                                                                                );

                                                                                const isEditing = !!collabFeedbackModal.editingId;
                                                                                    const previousFeedback = isEditing
                                                                                        ? targetEval.feedbacksHistory?.[collabFeedbackModal.editingId]
                                                                                        : null;

                                                                                    if (isEditing) {
                                                                                        if (!canEditOrDeleteCollabConsideration(targetEval, collabFeedbackModal.quarter, previousFeedback)) {
                                                                                            return alert("Você só pode editar considerações criadas por você e que ainda não estejam bloqueadas por assinatura.");
                                                                                        }
                                                                                    } else {
                                                                                        if (!canCreateCollabConsideration(targetEval, collabFeedbackModal.quarter)) {
                                                                                            return alert("Não é permitido inserir considerações para trimestre já assinado pelo colaborador, ou após assinatura do RH/Diretoria ou arquivamento da avaliação.");
                                                                                        }
                                                                                    }

                                                                                    const feedbackId = isEditing
                                                                                        ? collabFeedbackModal.editingId
                                                                                        : Date.now().toString();

                                                                                    const previousFeedbackData = isEditing
                                                                                        ? (targetEval.feedbacksHistory?.[feedbackId] || {})
                                                                                        : {};

                                                                                    const newFeedback = {
                                                                                        ...previousFeedbackData,
                                                                                        id: feedbackId,
                                                                                        type: 'Colaborador',
                                                                                        quarter: collabFeedbackModal.quarter,
                                                                                        text: String(collabFeedbackModal.text || '').trim(),

                                                                                        // Data oficial da consideração: último dia do trimestre
                                                                                        date: quarterEndDate.toISOString().split('T')[0],
                                                                                        timestamp: quarterEndDate.getTime(),

                                                                                        createdAt: previousFeedbackData.createdAt || new Date().toISOString(),
                                                                                        createdBy: previousFeedbackData.createdBy || currentUser?.id || '',
                                                                                        createdByName: previousFeedbackData.createdByName || currentUser?.name || '',
                                                                                        createdByEmail: previousFeedbackData.createdByEmail || currentUser?.email || '',

                                                                                        updatedAt: isEditing ? new Date().toISOString() : '',
                                                                                        updatedBy: isEditing ? currentUser?.id || '' : '',
                                                                                        updatedByName: isEditing ? currentUser?.name || '' : '',
                                                                                        updatedByEmail: isEditing ? currentUser?.email || '' : '',

                                                                                        color: '#10b981'
                                                                                    };

                                                                                    const updatedHistory = {
                                                                                        ...(targetEval.feedbacksHistory || {}),
                                                                                        [feedbackId]: newFeedback
                                                                                    };

                                                                                    actions.saveEvaluation({
                                                                                        ...targetEval,
                                                                                        feedbacksHistory: updatedHistory
                                                                                    });

                                                                                    alert(isEditing ? "Consideração atualizada com sucesso!" : "Consideração gravada com sucesso!");

                                                                                    setCollabFeedbackModal({
                                                                                        isOpen: true,
                                                                                        evalId: targetEval.id,
                                                                                        text: '',
                                                                                        quarter: '',
                                                                                        tab: 'history',
                                                                                        editingId: null
                                                                                    });
                                                                            }}
                                                                            className="bg-purple-600 text-white hover:bg-purple-700 shadow-md"
                                                                        >
                                                                            Gravar
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}

                                                {/* --- MODAL DE CONFIRMAÇÃO DE ASSINATURA/DESFAZER --- */}
                                                    {collabConfirmModal.isOpen && (
                                                        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] backdrop-blur-sm p-4 animate-fade-in" onClick={() => setCollabConfirmModal({ isOpen: false, type: '', data: null })}>
                                                            <div className={`p-6 rounded-xl w-96 shadow-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                                                
                                                                {/* OPÇÕES DE DESFAZER (NOVO REQUISITO 6b) */}
                                                                {collabConfirmModal.type === 'unsign_options' && (
                                                                    <>
                                                                        <div className="flex items-center gap-3 mb-4 text-red-600">
                                                                            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full"><History size={24}/></div>
                                                                            <h3 className="font-bold text-lg">Desfazer Assinatura</h3>
                                                                        </div>
                                                                        <p className={`text-sm mb-6 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                                            Você possui assinaturas válidas. Como deseja prosseguir?
                                                                        </p>
                                                                        <div className="flex flex-col gap-3">
                                                                            {/* Opção 1: Desfazer APENAS a última (Respeita ordem cronológica) */}
                                                                            <button 
                                                                                onClick={() => setCollabConfirmModal({ ...collabConfirmModal, type: 'unsign_quarter' })} 
                                                                                className="w-full flex items-center justify-between p-3 rounded-lg border hover:shadow-md transition-all border-gray-200 bg-white hover:bg-gray-50 text-gray-700 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-600"
                                                                            >
                                                                                <div className="flex items-center gap-3">
                                                                                    <span className="text-red-500 font-bold bg-red-100 px-2 py-1 rounded text-xs">-1</span>
                                                                                    <span className="font-bold text-sm">Desfazer Última ({collabConfirmModal.quarter?.toUpperCase()})</span>
                                                                                </div>
                                                                                <ChevronRight size={16}/>
                                                                            </button>

                                                                            {/* Opção 2: Desfazer TUDO */}
                                                                            <button 
                                                                                onClick={() => setCollabConfirmModal({ ...collabConfirmModal, type: 'unsign_all' })} 
                                                                                className="w-full flex items-center justify-between p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/40 transition-all"
                                                                            >
                                                                                <div className="flex items-center gap-3">
                                                                                    <Trash2 size={16}/>
                                                                                    <span className="font-bold text-sm">Desfazer TODAS</span>
                                                                                </div>
                                                                                <ChevronRight size={16}/>
                                                                            </button>
                                                                        </div>
                                                                        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-700 flex justify-center">
                                                                            <button onClick={() => setCollabConfirmModal({ isOpen: false })} className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">Cancelar</button>
                                                                        </div>
                                                                    </>
                                                                )}

                {/* CONFIRMAR ASSINATURA (MANTIDO) */}
                {collabConfirmModal.type === 'sign_quarter' && (
                    <>
                        <div className="flex items-center gap-3 mb-4 text-green-600">
                            <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/30"><PenTool size={24}/></div>
                            <h3 className="font-bold text-lg">Assinar {collabConfirmModal.quarter?.toUpperCase()}?</h3>
                        </div>
                        <p className={`text-sm mb-6 leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                            Ao confirmar, será gerado um registro permanente de ciência em suas anotações.
                        </p>
                        <div className="flex justify-end gap-2">
                            <Button variant="secondary" onClick={() => setCollabConfirmModal({ isOpen: false })}>Cancelar</Button>
                            <Button onClick={async () => {
                                const ev = collabConfirmModal.data;
                                const q = collabConfirmModal.quarter; 
                                
                                const newSigs = { ...(ev.collabSignatures || {}), [q]: { date: new Date().toLocaleString('pt-BR'), id: currentUser.id, name: currentUser.name, email: currentUser.email } };
                                
                                // Cascata inversa para garantir consistência
                                if (q === 'q4') { newSigs.q3 = newSigs.q3 || newSigs.q4; newSigs.q2 = newSigs.q2 || newSigs.q4; newSigs.q1 = newSigs.q1 || newSigs.q4; }
                                if (q === 'q3') { newSigs.q2 = newSigs.q2 || newSigs.q3; newSigs.q1 = newSigs.q1 || newSigs.q3; }
                                if (q === 'q2') { newSigs.q1 = newSigs.q1 || newSigs.q2; }

                                const noteId = `COLLAB_SIG_${ev.id}_${q}`;
                                const signatureNote = { id: noteId, text: `Ciência do ${q.toUpperCase()} realizada eletronicamente.`, date: new Date().toISOString().split('T')[0], tipo: 'Assinatura', prioridade: 'Alta', status: 'Concluída', timestamp: Date.now(), colorIndex: 2 };
                                const updatedNotes = { ...ev.personalNotes, [noteId]: signatureNote };
                                
                                await actions.saveEvaluation({ ...ev, collabSignatures: newSigs, personalNotes: updatedNotes });
                                setCollabConfirmModal({ isOpen: false });
                            }} className="bg-green-600 text-white hover:bg-green-700 shadow-md">Confirmar Assinatura</Button>
                        </div>
                    </>
                )}

                {/* REMOVER ÚLTIMA ASSINATURA */}
                {collabConfirmModal.type === 'unsign_quarter' && (
                    <>
                        <div className="flex items-center gap-3 mb-4 text-red-600">
                            <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/30"><History size={24}/></div>
                            <h3 className="font-bold text-lg">Desfazer {collabConfirmModal.quarter?.toUpperCase()}?</h3>
                        </div>
                        <p className={`text-sm mb-6 leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                            A assinatura deste trimestre será removida. Trimestres anteriores (se houver) permanecerão assinados.
                        </p>
                        <div className="flex justify-end gap-2">
                            <Button variant="secondary" onClick={() => setCollabConfirmModal({ isOpen: false })}>Cancelar</Button>
                            <Button onClick={async () => {
                                const ev = collabConfirmModal.data;
                                const q = collabConfirmModal.quarter;
                                
                                const newSigs = { ...ev.collabSignatures };
                                delete newSigs[q];
                                
                                const noteId = `COLLAB_SIG_${ev.id}_${q}`;
                                const currentNotes = { ...ev.personalNotes };
                                if (currentNotes[noteId]) delete currentNotes[noteId];
                                
                                await actions.saveEvaluation({ ...ev, collabSignatures: newSigs, personalNotes: currentNotes });
                                setCollabConfirmModal({ isOpen: false });
                            }} className="bg-red-600 text-white hover:bg-red-700 shadow-md">Sim, Remover</Button>
                        </div>
                    </>
                )}

                {/* REMOVER TODAS AS ASSINATURAS (NOVO) */}
                {collabConfirmModal.type === 'unsign_all' && (
                    <>
                        <div className="flex items-center gap-3 mb-4 text-red-600">
                            <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/30"><Trash2 size={24}/></div>
                            <h3 className="font-bold text-lg">Zerar Assinaturas?</h3>
                        </div>
                        <p className={`text-sm mb-6 leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                            Esta ação removerá <b>todas</b> as suas assinaturas para esta avaliação (T1, T2, T3, T4).
                        </p>
                        <div className="flex justify-end gap-2">
                            <Button variant="secondary" onClick={() => setCollabConfirmModal({ isOpen: false })} className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}>Cancelar</Button>
                            <Button onClick={async () => {
                                const ev = collabConfirmModal.data;
                                
                                // Remove todas as assinaturas do colaborador
                                const newSigs = {}; 
                                
                                // Remove todas as notas de assinatura do colaborador
                                const currentNotes = { ...ev.personalNotes };
                                ['q1', 'q2', 'q3', 'q4'].forEach(q => {
                                    const noteId = `COLLAB_SIG_${ev.id}_${q}`;
                                    if (currentNotes[noteId]) delete currentNotes[noteId];
                                });
                                
                                await actions.saveEvaluation({ ...ev, collabSignatures: newSigs, personalNotes: currentNotes });
                                setCollabConfirmModal({ isOpen: false });
                            }} className="bg-red-600 text-white hover:bg-red-700 shadow-md">Confirmar Reset</Button>
                        </div>
                    </>
                )}
                                        </div>
                                    </div>
                                    )}
                                            </div>
                                        )}

                                {/* [APP_RENDER__COLLAB_CENTER__TRAINING] */}
                                {activeTabId === 'training' && (
                                    <div className="space-y-6 animate-fade-in">
                                        {(() => {
                                            const userEmail = (currentUser.email || '').trim().toLowerCase();
                                            const allMyTrainings = trainingData.filter(t => t.email?.trim().toLowerCase() === userEmail)
                                                .sort((a, b) => { try { return new Date(b.date.split('/').reverse().join('-')) - new Date(a.date.split('/').reverse().join('-')); } catch (e) { return 0; } });

                                            return (
                                                <>
                                                    {/* 1. QUADRO DE ENVIO COM GRADIENTE (COMPACTO) */}
                                                    <div className={`p-4 rounded-xl shadow-lg border flex flex-col md:flex-row justify-between items-center gap-3 relative overflow-hidden ${isDarkMode ? 'bg-gradient-to-r from-slate-800 to-slate-900 border-slate-700' : 'bg-gradient-to-r from-blue-600 to-indigo-700 border-transparent text-white'}`}>
                                                        <div className="absolute right-0 top-0 h-full w-1/3 bg-white/10 skew-x-12 pointer-events-none"></div>
                                                    
                                                        <div className="flex items-center gap-3 z-10">
                                                            <div className={`p-3 rounded-full shadow-inner ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-white/20 text-white'}`}>
                                                                <BookOpen size={24}/>
                                                            </div>
                                                            <div>
                                                                <h3 className="font-bold text-lg text-white">Meus Cursos e Capacitações</h3>
                                                                <p className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-blue-100'}`}>Gerencie seus certificados e pontuação (D1/D2).</p>
                                                            </div>
                                                        </div>
                                                        <button 
                                                            onClick={() => {
                                                                setTrainingForm({ id: null, date: '', title: '', type: 'Área Atuação', hours: '', durationStr: '', hasCert: 'Sim', oldCertLink: null, oldSummaryLink: null });
                                                                setTrainingFiles({ cert: null, summary: null, print: null });
                                                                setIsTrainingModalOpen(true);
                                                            }}
                                                            className={`px-4 py-2 rounded-lg font-bold text-xs shadow-xl transition-transform hover:scale-105 flex items-center gap-2 z-10 ${isDarkMode ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-white text-blue-700 hover:bg-blue-50'}`}
                                                        >
                                                            <Upload size={16}/> Enviar Novo Curso
                                                        </button>
                                                    </div>

                                                    {/* 2. TABELA DE HISTÓRICO */}
                                                    <Card className="p-0 overflow-hidden" darkMode={isDarkMode}>
                                                        <div className={`p-3 border-b flex justify-between items-center ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                            <div className="relative w-64">
                                                                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} size={14} />
                                                                <input className={`w-full pl-9 py-1.5 text-xs rounded border outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`} placeholder="Pesquisar curso..." value={trainingSearch} onChange={e => setTrainingSearch(e.target.value)} />
                                                            </div>
                                                            <div className={`text-xs font-bold ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Total: {allMyTrainings.length}</div>
                                                        </div>
                                                        <div className="overflow-x-auto">
                                                            <table className={`w-full text-sm text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                                <thead className={`text-xs uppercase font-bold ${isDarkMode ? 'bg-slate-900 text-slate-400 border-b border-slate-700' : 'bg-gray-100 text-gray-600 border-b border-gray-200'}`}>
                                                                    <tr>
                                                                        <th className="p-4 w-32">Conclusão</th>
                                                                        <th className="p-4">Título do Curso</th>
                                                                        <th className="p-4 w-40 text-center">Tipo</th>
                                                                        <th className="p-4 w-32 text-center">Duração</th>
                                                                        <th className="p-4 w-32 text-center">Anexos</th>
                                                                        <th className="p-4 w-24 text-center">Ações</th>
                                                                    </tr>
                                                                </thead>
                                                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700 bg-slate-800' : 'divide-gray-100 bg-white'}`}>
                                                                        {allMyTrainings.map((t, index) => { // Adicionado index aqui
                                                                            // Lógica de zebra (Par/Impar)
                                                                            const rowClass = index % 2 === 0 
                                                                                ? (isDarkMode ? 'bg-slate-800' : 'bg-white') 
                                                                                : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');

                                                                            // Lógica de Cores Fortes para o Tipo (Fundo escuro + Letra Branca)
                                                                            const isAtuacao = t.type.includes('1') || t.type.includes('Atuação') || t.type.includes('atuacao');
                                                                            const badgeClass = isAtuacao 
                                                                                ? (isDarkMode ? 'bg-blue-900 text-blue-200 border-blue-800' : 'bg-blue-600 text-white border-blue-600') 
                                                                                : (isDarkMode ? 'bg-emerald-900 text-emerald-200 border-emerald-800' : 'bg-emerald-600 text-white border-emerald-600');

                                                                            return (
                                                                                <tr key={t.id} className={`${rowClass} transition-colors ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-blue-50'}`}>
                                                                                    <td className="p-4 font-mono text-xs">{t.date}</td>
                                                                                    <td className={`p-4 font-bold ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{t.description}</td>
                                                                                    
                                                                                    {/* Coluna TIPO ajustada com cor sólida */}
                                                                                    <td className="p-4 text-center">
                                                                                        <span className={`px-3 py-1 rounded-md text-[10px] font-bold border whitespace-nowrap shadow-sm ${badgeClass}`}>
                                                                                            {t.type}
                                                                                        </span>
                                                                                    </td>
                                                                                    
                                                                                    {/* Coluna DURAÇÃO formatada */}
                                                                                    <td className="p-4 text-center font-bold text-purple-600">
                                                                                        {formatDecimalToTime(t.hours)}h
                                                                                    </td>
                                                                                    
                                                                                    <td className="p-4 text-center">
                                                                                        <div className="flex justify-center gap-2">
                                                                                            {t.certLink && <a href={t.certLink} target="_blank" className="text-green-600 hover:text-green-800"><Award size={18}/></a>}
                                                                                            {t.summaryLink && <a href={t.summaryLink} target="_blank" className="text-blue-600 hover:text-blue-800"><FileText size={18}/></a>}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="p-4 text-center">
                                                                                        <div className="flex justify-center gap-2">
                                                                                            <button onClick={() => handleEditTraining(t)} className="text-blue-500 hover:bg-blue-100 p-1.5 rounded"><Edit3 size={16}/></button>
                                                                                            <button onClick={() => handleDeleteTraining(t)} className="text-red-500 hover:bg-red-100 p-1.5 rounded"><Trash2 size={16}/></button>
                                                                                        </div>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                        {allMyTrainings.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-gray-400">Nenhum curso registrado.</td></tr>}
                                                                    </tbody>
                                                            </table>
                                                        </div>
                                                    </Card>

                                                    {/* MODAL DE CADASTRO COM ABAS (CURSO / CONFIGURAÇÕES) */}
        
        
                                                    {isTrainingModalOpen && (
                                                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4" onClick={() => !isUploading && setIsTrainingModalOpen(false)}>
                                                        <div className={`rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[95vh] ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                                            
                                                            {/* CABEÇALHO E ABAS */}
                                                            <div className={`flex justify-between items-center p-4 border-b shrink-0 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                                                <h3 className="font-bold text-lg flex items-center gap-2">
                                                                    <BookOpen size={20} className="text-blue-500"/> 
                                                                    {trainingForm.id ? 'Editar Curso' : 'Registrar Novo Curso'}
                                                                </h3>
                                                                {!isUploading && <button onClick={() => setIsTrainingModalOpen(false)} className="hover:text-red-500"><X size={20}/></button>}
                                                            </div>

                                                            <div className={`flex border-b shrink-0 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                                                <button onClick={() => setTrainingModalTab('dados')} className={`flex-1 py-3 text-sm font-bold border-b-2 transition-colors flex justify-center gap-2 ${trainingModalTab === 'dados' ? 'border-blue-500 text-blue-500' : 'border-transparent opacity-60'}`}>
                                                                    <FileText size={16}/> Dados do Curso
                                                                </button>
                                                                {(currentUser.role === 'SuperAdm' || currentUser.role === 'Gestor') && (
                                                                    <button onClick={() => setTrainingModalTab('config')} className={`flex-1 py-3 text-sm font-bold border-b-2 transition-colors flex justify-center gap-2 ${trainingModalTab === 'config' ? 'border-orange-500 text-orange-500' : 'border-transparent opacity-60'}`}>
                                                                        <Settings size={16}/> Configuração de Prazos
                                                                    </button>
                                                                )}
                                                            </div>

                                                            {/* CONTEÚDO DAS ABAS */}
                                                            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                                                                
                                                                {/* ABA 1: DADOS DO CURSO (FORMULÁRIO EXISTENTE) */}
                                                                {trainingModalTab === 'dados' && (
                                                                    <div className="space-y-3 animate-fade-in">
                                                                        <div className={`grid grid-cols-2 gap-4 mb-4 p-2 rounded border ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                                            <div><span className="text-[10px] font-bold block opacity-50 uppercase">Nome</span><span className="text-xs font-bold">{currentUser.name}</span></div>
                                                                            <div><span className="text-[10px] font-bold block opacity-50 uppercase">Email</span><span className="text-xs font-bold">{currentUser.email}</span></div>
                                                                        </div>
                                                                        <div className="grid grid-cols-2 gap-3">
                                                                            <div><label className="text-xs font-bold block mb-1">Data Conclusão</label><input type="date" className={`w-full p-2 border rounded text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar' : ''}`} value={trainingForm.date} onChange={e => setTrainingForm({...trainingForm, date: e.target.value})} /></div>
                                                                            <div><label className="text-xs font-bold block mb-1">Carga Horária (HH:MM:SS)</label><input type="text" className={`w-full p-2 border rounded text-xs text-center font-mono ${isDarkMode ? 'bg-slate-900 border-slate-600' : ''}`} placeholder="00:00:00" value={trainingForm.durationStr || ''} onChange={e => setTrainingForm(prev => ({ ...prev, durationStr: e.target.value.replace(/[^0-9:]/g, '') }))} maxLength={8} /><p className="text-[9px] text-gray-400 mt-1">Exemplo: 01:30:00</p></div>
                                                                        </div>
                                                                        <div><label className="text-xs font-bold block mb-1">Título do Curso</label><input type="text" className={`w-full p-2 border rounded text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600' : ''}`} placeholder="Nome do curso..." value={trainingForm.title} onChange={e => setTrainingForm({...trainingForm, title: e.target.value})} /></div>
                                                                        <div><label className="text-xs font-bold block mb-1">Tipo do Curso</label><select className={`w-full p-2 border rounded text-xs ${isDarkMode ? 'bg-slate-900 border-slate-600' : ''}`} value={trainingForm.type} onChange={e => setTrainingForm({...trainingForm, type: e.target.value})}><option value="Área Atuação">Área de Atuação (D1)</option><option value="Demais Áreas">Demais Áreas (D2)</option></select></div>
                                                                        <div className={`p-3 border rounded-lg ${isDarkMode ? 'bg-blue-900/10 border-blue-800' : 'bg-blue-50 border-blue-100'}`}><label className={`text-xs font-bold block mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>Possui Certificado?</label><div className="flex gap-4"><label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="hasCert" checked={trainingForm.hasCert === 'Sim'} onChange={() => setTrainingForm({...trainingForm, hasCert: 'Sim'})}/><span className="text-xs">Sim, tenho certificado</span></label><label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="hasCert" checked={trainingForm.hasCert === 'Não'} onChange={() => setTrainingForm({...trainingForm, hasCert: 'Não'})}/><span className="text-xs">Não (Anexar Evidências)</span></label></div></div>
                                                                        <div className={`space-y-3 border-t pt-3 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                                                            {trainingForm.id && <p className="text-[10px] text-orange-500 italic mb-2">* Upload de novos arquivos substituirá os atuais.</p>}
                                                                            {trainingForm.hasCert === 'Sim' ? (
                                                                                    <div>
                                                                                        <label className="text-xs font-bold block mb-1">
                                                                                            Anexar Certificado (PDF/Img)
                                                                                        </label>

                                                                                        <label className={`flex items-center justify-between w-full px-3 py-2 rounded-lg border cursor-pointer text-xs font-bold transition-all ${
                                                                                            isDarkMode
                                                                                                ? 'bg-slate-900 border-slate-600 text-slate-200 hover:bg-slate-800'
                                                                                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                                                                        }`}>

                                                                                            <div className="flex items-center gap-2">
                                                                                                <Paperclip size={16} className="text-blue-500" />
                                                                                                <span>
                                                                                                    {trainingFiles.cert
                                                                                                        ? trainingFiles.cert.name
                                                                                                        : 'Clique para selecionar o arquivo'}
                                                                                                </span>
                                                                                            </div>

                                                                                            <UploadCloud size={18} className="text-green-500" />

                                                                                            <input
                                                                                                type="file"
                                                                                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                                                                className="hidden"
                                                                                                onChange={e =>
                                                                                                    setTrainingFiles(prev => ({
                                                                                                        ...prev,
                                                                                                        cert: e.target.files?.[0] || null
                                                                                                    }))
                                                                                                }
                                                                                            />
                                                                                        </label>
                                                                                    </div>
                                                                                ) : (
                                                                                <div className="space-y-3">
                                                                                    <div>
                                                                                        <label className="text-xs font-bold block mb-1">
                                                                                            Anexar Resumo (PDF/Txt/Word)
                                                                                        </label>

                                                                                        <label className={`flex items-center justify-between w-full px-3 py-2 rounded-lg border cursor-pointer text-xs font-bold transition-all ${
                                                                                            isDarkMode
                                                                                                ? 'bg-slate-900 border-slate-600 text-slate-200 hover:bg-slate-800'
                                                                                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                                                                        }`}>
                                                                                            <div className="flex items-center gap-2">
                                                                                                <Paperclip size={16} className="text-orange-500" />
                                                                                                <span>
                                                                                                    {trainingFiles.summary
                                                                                                        ? trainingFiles.summary.name
                                                                                                        : 'Clique para anexar o resumo'}
                                                                                                </span>
                                                                                            </div>

                                                                                            <UploadCloud size={18} className="text-green-500" />

                                                                                            <input
                                                                                                type="file"
                                                                                                accept=".pdf,.txt,.doc,.docx"
                                                                                                className="hidden"
                                                                                                onChange={e =>
                                                                                                    setTrainingFiles(prev => ({
                                                                                                        ...prev,
                                                                                                        summary: e.target.files?.[0] || null
                                                                                                    }))
                                                                                                }
                                                                                            />
                                                                                        </label>

                                                                                        <p className="text-[10px] text-slate-400 mt-1">
                                                                                            Obrigatório para cursos sem certificado oficial.
                                                                                        </p>
                                                                                    </div>

                                                                                    <div>
                                                                                        <label className="text-xs font-bold block mb-1">
                                                                                            Anexar Print/Evidência (Img/PDF)
                                                                                        </label>

                                                                                        <label className={`flex items-center justify-between w-full px-3 py-2 rounded-lg border cursor-pointer text-xs font-bold transition-all ${
                                                                                            isDarkMode
                                                                                                ? 'bg-slate-900 border-slate-600 text-slate-200 hover:bg-slate-800'
                                                                                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                                                                        }`}>
                                                                                            <div className="flex items-center gap-2">
                                                                                                <Paperclip size={16} className="text-blue-500" />
                                                                                                <span>
                                                                                                    {trainingFiles.print
                                                                                                        ? trainingFiles.print.name
                                                                                                        : 'Clique para anexar o print/evidência'}
                                                                                                </span>
                                                                                            </div>

                                                                                            <UploadCloud size={18} className="text-green-500" />

                                                                                            <input
                                                                                                type="file"
                                                                                                accept=".pdf,.jpg,.jpeg,.png"
                                                                                                className="hidden"
                                                                                                onChange={e =>
                                                                                                    setTrainingFiles(prev => ({
                                                                                                        ...prev,
                                                                                                        print: e.target.files?.[0] || null
                                                                                                    }))
                                                                                                }
                                                                                            />
                                                                                        </label>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* ABA 2: CONFIGURAÇÕES DE PRAZO (ADMIN) */}
                                                                {trainingModalTab === 'config' && (
                                                                    <div className="space-y-4 animate-fade-in">
                                                                        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                                            <h4 className="font-bold text-xs uppercase mb-3 opacity-70">Cadastrar Nova Regra de Prazo</h4>
                                                                            <div className="grid grid-cols-2 gap-3 mb-3">
                                                                                <div className="col-span-2">
                                                                                    <label className="text-[10px] font-bold uppercase block mb-1">Nome da Regra</label>
                                                                                    <input type="text" className={`w-full p-2 rounded border text-xs outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white'}`} placeholder="Ex: Prazos Contabilidade" value={trainingDeadlineForm.name} onChange={e=>setTrainingDeadlineForm({...trainingDeadlineForm, name: e.target.value})} />
                                                                                </div>
                                                                                <div>
                                                                                    <label className="text-[10px] font-bold uppercase block mb-1">Departamento</label>
                                                                                    <select className={`w-full p-2 rounded border text-xs outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white'}`} value={trainingDeadlineForm.departmentId} onChange={e=>setTrainingDeadlineForm({...trainingDeadlineForm, departmentId: e.target.value})}>
                                                                                        <option value="">Aplicar a Todos</option>
                                                                                        {data.departments.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                                                                                    </select>
                                                                                </div>
                                                                                <div>
                                                                                    <label className="text-[10px] font-bold uppercase block mb-1">Tipo de Pessoa</label>
                                                                                    <select className={`w-full p-2 rounded border text-xs outline-none ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white'}`} value={trainingDeadlineForm.personType} onChange={e=>setTrainingDeadlineForm({...trainingDeadlineForm, personType: e.target.value})}>
                                                                                        <option value="Colaborador">Colaborador</option>
                                                                                        <option value="Gestão">Gestão</option>
                                                                                        <option value="Todos">Todos</option>
                                                                                    </select>
                                                                                </div>
                                                                            </div>

                                                                            <div className={`grid grid-cols-2 gap-3 p-3 rounded border ${isDarkMode ? 'bg-orange-900/20 border-orange-800' : 'bg-orange-50 border-orange-200'}`}>
                                                                                <div><label className="text-[10px] font-bold uppercase block mb-1">1º Trim (DD/MM)</label><input className="w-full text-center p-1.5 border rounded text-xs outline-none" placeholder="Ex: 03/04" value={trainingDeadlineForm.q1} onChange={e=>setTrainingDeadlineForm({...trainingDeadlineForm, q1: e.target.value})} maxLength={5}/></div>
                                                                                <div><label className="text-[10px] font-bold uppercase block mb-1">2º Trim (DD/MM)</label><input className="w-full text-center p-1.5 border rounded text-xs outline-none" placeholder="Ex: 03/07" value={trainingDeadlineForm.q2} onChange={e=>setTrainingDeadlineForm({...trainingDeadlineForm, q2: e.target.value})} maxLength={5}/></div>
                                                                                <div><label className="text-[10px] font-bold uppercase block mb-1">3º Trim (DD/MM)</label><input className="w-full text-center p-1.5 border rounded text-xs outline-none" placeholder="Ex: 03/10" value={trainingDeadlineForm.q3} onChange={e=>setTrainingDeadlineForm({...trainingDeadlineForm, q3: e.target.value})} maxLength={5}/></div>
                                                                                <div><label className="text-[10px] font-bold uppercase block mb-1">4º Trim (DD/MM)</label><input className="w-full text-center p-1.5 border rounded text-xs outline-none" placeholder="Ex: 03/01" value={trainingDeadlineForm.q4} onChange={e=>setTrainingDeadlineForm({...trainingDeadlineForm, q4: e.target.value})} maxLength={5}/></div>
                                                                            </div>
                                                                            
                                                                            <div className="flex justify-end mt-3">
                                                                                <Button onClick={() => {
                                                                                    if (!trainingDeadlineForm.name || !trainingDeadlineForm.q1) return alert("Preencha Nome e pelo menos o T1.");
                                                                                    actions.saveTrainingDeadline({ ...trainingDeadlineForm, id: trainingDeadlineForm.id || Date.now().toString() });
                                                                                    setTrainingDeadlineForm({ id: null, name: '', departmentId: '', personType: 'Todos', q1: '', q2: '', q3: '', q4: '' });
                                                                                }} icon={Save} className="bg-orange-500 hover:bg-orange-600 text-white shadow-md border-0 h-8 text-xs">Salvar Regra</Button>
                                                                            </div>
                                                                        </div>

                                                                        {/* LISTAGEM DE REGRAS CADASTRADAS */}
                                                                        <div className={`overflow-x-auto border rounded-xl ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                                                            <table className="w-full text-xs text-left">
                                                                                <thead className={`font-bold uppercase ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>
                                                                                    <tr><th className="p-3">Regra</th><th className="p-3 text-center">T1</th><th className="p-3 text-center">T2</th><th className="p-3 text-center">T3</th><th className="p-3 text-center">T4</th><th className="p-3 text-center">Ações</th></tr>
                                                                                </thead>
                                                                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                                                    {(data.trainingDeadlines || []).map(d => (
                                                                                        <tr key={d.id} className={isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-50'}>
                                                                                            <td className="p-3">
                                                                                                <div className="font-bold">{d.name}</div>
                                                                                                <div className="text-[9px] opacity-70 mt-0.5">Depto: {d.departmentId ? data.departments.find(dep=>dep.id===d.departmentId)?.name : 'Geral'} | {d.personType}</div>
                                                                                            </td>
                                                                                            <td className="p-3 text-center font-mono">{d.q1}</td>
                                                                                            <td className="p-3 text-center font-mono">{d.q2}</td>
                                                                                            <td className="p-3 text-center font-mono">{d.q3}</td>
                                                                                            <td className="p-3 text-center font-mono">{d.q4}</td>
                                                                                            <td className="p-3 text-center">
                                                                                                <div className="flex justify-center gap-1">
                                                                                                    <button onClick={() => setTrainingDeadlineForm(d)} className="text-blue-500 hover:bg-blue-100 p-1.5 rounded"><Edit3 size={14}/></button>
                                                                                                    <button onClick={() => actions.genericDelete('trainingDeadlines', d.id)} className="text-red-500 hover:bg-red-100 p-1.5 rounded"><Trash2 size={14}/></button>
                                                                                                </div>
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                    {(data.trainingDeadlines || []).length === 0 && <tr><td colSpan="6" className="p-6 text-center italic opacity-50">Nenhuma regra de prazo configurada.</td></tr>}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* FOOTER DOS BOTÕES (Apenas na aba de envio de dados) */}
                                                            {trainingModalTab === 'dados' && (
                                                                <div className={`flex justify-end gap-2 p-4 border-t shrink-0 ${isDarkMode ? 'border-slate-700 bg-slate-900' : 'border-gray-200 bg-gray-50'}`}>
                                                                    <Button variant="secondary" onClick={() => setIsTrainingModalOpen(false)} disabled={isUploading} className={isDarkMode ? 'bg-slate-800 text-slate-300 border-slate-600' : ''}>Cancelar</Button>
                                                                    <Button onClick={handleSaveTraining} disabled={isUploading} className="bg-blue-600 hover:bg-blue-700 text-white shadow-md border-0">
                                                                        {isUploading ? <><Loader2 size={16} className="animate-spin mr-2"/> Enviando...</> : "Enviar Certificado"}
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                )}

                                                {/* [APP_RENDER__COLLAB_CENTER__PERCEPTION] */}
                                                {activeTabId === 'perception' && (
                                                    <div className="space-y-6 animate-fade-in">
                                                        {(() => {
                                                            const userEmail = (currentUser.email || '').trim().toLowerCase();
                                                            const myPerceptions = (data.teamPerception || []).filter(p => p.email?.trim().toLowerCase() === userEmail)
                                                                .sort((a,b) => b.id.localeCompare(a.id));

                                                            // 1. Prepara as COLUNAS baseadas na Configuração do Admin (perceptionCycles)
                                                            // Isso garante que o título seja o configurado em "Dados Externos"
                                                            const visibleColumns = (data.perceptionCycles || [])
                                                                .sort((a,b) => (a.order || 0) - (b.order || 0));

                                                            // 2. Lógica de Prazos (Mantida)
                                                            const perceptionConfig = data.globalConfigs.find(c => c.id === 'perception_dates') || { overrideStart: '', overrideEnd: '' };
                                                            const checkPerceptionOpen = () => {
                                                                const today = new Date();
                                                                const currentMonth = today.getMonth(); 
                                                                const currentDay = today.getDate();
                                                                if (perceptionConfig.overrideStart && perceptionConfig.overrideEnd) {
                                                                    const start = new Date(perceptionConfig.overrideStart + 'T00:00:00');
                                                                    const end = new Date(perceptionConfig.overrideEnd + 'T23:59:59');
                                                                    if (today >= start && today <= end) return true;
                                                                }
                                                                const allowedMonths = [0, 3, 6, 9];
                                                                if (allowedMonths.includes(currentMonth) && currentDay >= 1 && currentDay <= 10) return true;
                                                                return false;
                                                            };
                                                            const isOpen = checkPerceptionOpen();

                                                            const getRatingStyle = (val) => {
                                                                const v = Math.round(parseFloat(val));
                                                                if (v === 1) return { label: 'Insatisfatório', color: 'bg-red-100 text-red-700 border-red-200' };
                                                                if (v === 2) return { label: 'Regular', color: 'bg-orange-100 text-orange-700 border-orange-200' };
                                                                if (v === 3) return { label: 'Bom', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' };
                                                                if (v === 4) return { label: 'Muito Bom', color: 'bg-green-100 text-green-700 border-green-200' };
                                                                if (v === 5) return { label: 'Excelente', color: 'bg-blue-100 text-blue-700 border-blue-200' };
                                                                return { label: '-', color: 'bg-gray-100 text-gray-500' };
                                                            };

                                                            return (
                                                                <>
                                                                    {/* 1. HEADER AVALIAR GESTÃO (COMPACTO) */}
                                                                    <div className={`p-3 rounded-xl shadow-lg border flex flex-col md:flex-row justify-between items-center gap-2 relative overflow-hidden ${isDarkMode ? 'bg-gradient-to-r from-slate-800 to-slate-900 border-slate-700' : 'bg-gradient-to-r from-purple-600 to-fuchsia-700 border-transparent text-white'}`}>
                                                                        <div className="absolute right-0 top-0 h-full w-1/3 bg-white/10 skew-x-12 pointer-events-none"></div>
                                                                        <div className="flex items-center gap-3 z-10">
                                                                            <div className={`p-2 rounded-full shadow-inner ${isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-white/20 text-white'}`}>
                                                                                {isOpen ? <Users size={22}/> : <Lock size={22}/>}
                                                                            </div>
                                                                            <div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <h3 className="font-bold text-lg text-white">Avaliação de Gestão</h3>
                                                                                    <div className={`text-[9px] font-bold px-1.5 py-0.5 rounded inline-block ${isOpen ? 'bg-green-400/20 text-green-100 border border-green-400/30' : 'bg-red-900/30 text-red-200 border border-red-800/50'}`}>
                                                                                        {isOpen ? "ABERTO" : "FECHADO"}
                                                                                    </div>
                                                                                </div>
                                                                                <p className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-purple-100'}`}>Contribua para o desenvolvimento da liderança.</p>
                                                                            </div>
                                                                        </div>
                                                                        <button 
                                                                            onClick={() => isOpen ? setIsCollabEvalModalOpen(true) : alert("O formulário está fechado.")}
                                                                            disabled={!isOpen}
                                                                            className={`px-4 py-2 rounded-lg font-bold text-xs shadow-xl transition-transform hover:scale-105 flex items-center gap-2 z-10 ${isOpen ? (isDarkMode ? 'bg-purple-600 hover:bg-purple-500 text-white' : 'bg-white text-purple-700 hover:bg-purple-50') : 'bg-gray-400 text-gray-200 cursor-not-allowed opacity-80'}`}
                                                                        >
                                                                            {isOpen ? <><Star size={16}/> Avaliar Líder</> : <><Lock size={16}/> Aguarde o Prazo</>}
                                                                        </button>
                                                                    </div>

                                                                    {/* 2. TABELA DE ENVIOS DETALHADA */}
                                                                    <Card className="p-0 overflow-hidden" darkMode={isDarkMode}>
                                                                        <div className={`p-3 border-b flex justify-between items-center ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                                            <div className={`text-xs font-bold ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                                                                Histórico de Envios: {myPerceptions.length}
                                                                            </div>
                                                                        </div>
                                                                        <div className="overflow-x-auto">
                                                                            <table className={`w-full text-xs text-left border-collapse ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                                                <thead className={`text-[10px] uppercase font-bold ${isDarkMode ? 'bg-slate-900 text-slate-400 border-b border-slate-700' : 'bg-gray-100 text-gray-600 border-b border-gray-200'}`}>
                                                                                    <tr>
                                                                                        <th className="p-2 w-24 border-r dark:border-slate-700">Data Ref.</th>
                                                                                        <th className="p-2 w-32 border-r dark:border-slate-700">Gestor Avaliado</th>
                                                                                        
                                                                                        {/* COLUNAS DINÂMICAS (Iterando Cycles) */}
                                                                                        {visibleColumns.map((col, idx) => {
                                                                                            // Encontra a pergunta original para o Tooltip
                                                                                            const fullQuestion = (data.perceptionQuestions || []).find(q => q.id === col.linkedQuestionId);
                                                                                            const tooltipText = fullQuestion ? fullQuestion.text : "Descrição detalhada não disponível.";
                                                                                            
                                                                                            // Zebrado Coluna (Header)
                                                                                            const bgCol = idx % 2 !== 0 ? (isDarkMode ? 'bg-white/5' : 'bg-black/5') : '';

                                                                                            return (
                                                                                                <th key={col.id} className={`p-2 text-center w-28 border-r dark:border-slate-700 relative group cursor-help ${bgCol}`}>
                                                                                                    <span className="block leading-tight">{col.description}</span>
                                                                                                    
                                                                                                    {/* Tooltip Hover */}
                                                                                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-slate-800 text-white text-[10px] p-2 rounded shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 normal-case font-normal leading-relaxed text-left border border-slate-600">
                                                                                                        <div className="font-bold text-yellow-400 mb-1 border-b border-white/20 pb-1">Pergunta Completa:</div>
                                                                                                        {tooltipText}
                                                                                                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-800 rotate-45 border-b border-r border-slate-600"></div>
                                                                                                    </div>
                                                                                                </th>
                                                                                            );
                                                                                        })}
                                                                                    </tr>
                                                                                </thead>
                                                                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700 bg-slate-800' : 'divide-gray-100 bg-white'}`}>
                                                                                        {myPerceptions.map((p, rowIdx) => {
                                                                                            // Zebrado Linha
                                                                                            const rowClass = rowIdx % 2 === 0 
                                                                                                ? (isDarkMode ? 'bg-slate-800' : 'bg-white') 
                                                                                                : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');

                                                                                            return (
                                                                                                // CORREÇÃO AQUI: hover:bg-blue-50 (suave) no claro, hover:bg-slate-700 no escuro
                                                                                                <tr key={p.id} className={`transition-colors ${rowClass} ${isDarkMode ? 'hover:bg-slate-700' : 'hover:!bg-gray-200'}`}>
                                                                                                    <td className="p-2 font-mono text-[10px] border-r dark:border-slate-700">{p.date}</td>
                                                                                                    <td className={`p-2 font-bold border-r dark:border-slate-700 text-[11px] ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{p.gestor_avaliado}</td>
                                                                                                
                                                                                                {/* CÉLULAS DAS RESPOSTAS */}
                                                                                                {visibleColumns.map((col, colIdx) => {
                                                                                                    const answer = p.respostas_detalhadas?.[col.linkedQuestionId];
                                                                                                    const val = answer?.value || 0;
                                                                                                    const obs = answer?.obs || '';
                                                                                                    const style = getRatingStyle(val);
                                                                                                    
                                                                                                    // Zebrado Coluna (Body) - Coincide com o Header
                                                                                                    const colZebraClass = colIdx % 2 !== 0 ? (isDarkMode ? 'bg-black/10' : 'bg-gray-100/50') : '';

                                                                                                    // Tenta determinar se é escala ou texto
                                                                                                    const fullQuestion = (data.perceptionQuestions || []).find(q => q.id === col.linkedQuestionId);
                                                                                                    const isScale = fullQuestion?.type === 'scale';

                                                                                                    return (
                                                                                                        <td key={col.id} className={`p-2 text-center border-r dark:border-slate-700 align-middle ${colZebraClass}`}>
                                                                                                            {isScale ? (
                                                                                                                <div className="relative group flex justify-center">
                                                                                                                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold border shadow-sm cursor-default whitespace-nowrap ${style.color}`}>
                                                                                                                        {val > 0 ? style.label : 'N/A'}
                                                                                                                    </span>
                                                                                                                    {obs && (
                                                                                                                        <div className="absolute bottom-full mb-1 w-60 bg-slate-800 text-white text-[10px] p-2 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-left border border-slate-600">
                                                                                                                            <span className="font-bold text-yellow-400 block border-b border-white/20 pb-0.5 mb-1">Justificativa:</span>
                                                                                                                            "{obs}"
                                                                                                                        </div>
                                                                                                                    )}
                                                                                                                </div>
                                                                                                            ) : (
                                                                                                                <div className="relative group">
                                                                                                                    <div className="text-[10px] italic opacity-70 truncate max-w-[100px] mx-auto cursor-help">
                                                                                                                        {obs || answer?.value || '-'}
                                                                                                                    </div>
                                                                                                                    {(obs || answer?.value) && (
                                                                                                                        <div className="absolute bottom-full right-0 mb-1 w-64 bg-slate-800 text-white text-[10px] p-2 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-left border border-slate-600">
                                                                                                                            {obs || answer?.value}
                                                                                                                        </div>
                                                                                                                    )}
                                                                                                                </div>
                                                                                                            )}
                                                                                                        </td>
                                                                                                    );
                                                                                                })}
                                                                                            </tr>
                                                                                        );
                                                                                    })}
                                                                                    {myPerceptions.length === 0 && <tr><td colSpan={visibleColumns.length + 2} className="p-8 text-center text-gray-400 italic">Nenhuma avaliação realizada.</td></tr>}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </Card>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                )}                        
                                                </div>
                        )}
                        



                        {/* MODAL GLOBAL DE ALERTA (DINÂMICO: SUCESSO vs ERRO) */}
                        {validationAlert && (
                            <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setValidationAlert(null)}>
                                <div className={`p-6 rounded-xl w-96 shadow-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()}>
                                    
                                    {(() => {
                                        // Verifica se é Sucesso pelo título
                                        const isSuccess = validationAlert.title === 'Sucesso!' || validationAlert.title === 'Sucesso';
                                        const isAISuggestion = validationAlert.type === 'aiSuggestion';
                                        
                                        return (
                                            <>
                                                <div className={`flex items-center gap-3 mb-4 ${isSuccess ? 'text-green-500' : isAISuggestion ? 'text-purple-500' : 'text-red-500'}`}>
                                                    <div className={`p-2 rounded-full ${isSuccess ? 'bg-green-100' : isAISuggestion ? 'bg-purple-100' : 'bg-red-100'}`}>
                                                        {isSuccess ? <CheckCircle size={24} /> : isAISuggestion ? <Sparkles size={24} /> : <AlertTriangle size={24} />}
                                                    </div>
                                                    <h3 className="font-bold text-lg">{validationAlert.title || 'Atenção'}</h3>
                                                </div>
                                                
                                                <p className={`text-sm mb-6 whitespace-pre-wrap leading-relaxed max-h-[45vh] overflow-y-auto custom-scrollbar ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                                    {validationAlert.message || (typeof validationAlert === 'string' ? validationAlert : 'Erro desconhecido')}
                                                </p>
                                                
                                                <div className="flex justify-end gap-2">
                                                    <Button 
                                                        variant="secondary"
                                                        onClick={() => setValidationAlert(null)} 
                                                        className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : ''}
                                                    >
                                                        {validationAlert.cancelText || (isSuccess ? 'Continuar' : 'Entendi')}
                                                    </Button>
                                                    {validationAlert.onConfirm && (
                                                        <Button
                                                            onClick={validationAlert.onConfirm}
                                                            className={`${isAISuggestion ? 'bg-purple-600 hover:bg-purple-700' : 'bg-red-600 hover:bg-red-700'} text-white shadow-md`}
                                                        >
                                                            {validationAlert.confirmText || 'Confirmar'}
                                                        </Button>
                                                    )}
                                                </div>
                                            </>
                                        );
                                    })()}
                                    
                                </div>
                            </div>
                        )}

                        {/* --- ABA RH: GESTÃO DE CULTURA ACAD (FEEDBACKS E CIÊNCIAS) --- */}
                                            {activeTabId === 'hr_cultura_acad' && (
                                                <div className="space-y-6 animate-fade-in flex flex-col h-full">
                                                    <h2 className={`text-2xl font-black flex items-center gap-2 shrink-0 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                                                        <MessageSquare className="text-orange-500" /> Gestão de Cultura Acad
                                                    </h2>

                                                    {/* NAV DE ABAS (RH) */}
                                                    <div className={`flex border-b shrink-0 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                                        <button 
                                                            onClick={() => setHrCulturaTab('feedbacks')} 
                                                            className={`px-6 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${hrCulturaTab === 'feedbacks' ? 'border-orange-500 text-orange-500' : 'border-transparent opacity-60 hover:opacity-100'}`}
                                                        >
                                                            <MessageCircle size={16}/> Dúvidas, Elogios e Sugestões
                                                        </button>
                                                        <button 
                                                            onClick={() => setHrCulturaTab('ciencias')} 
                                                            className={`px-6 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${hrCulturaTab === 'ciencias' ? 'border-blue-500 text-blue-500' : 'border-transparent opacity-60 hover:opacity-100'}`}
                                                        >
                                                            <CheckSquare size={16}/> Gerenciar Ciências (Leituras)
                                                        </button>
                                                    </div>
                                                    
                                                    <div className="flex-1 overflow-y-auto custom-scrollbar">
                                                        
                                                        {/* ABA 1: FEEDBACKS */}
                                                            {hrCulturaTab === 'feedbacks' && (() => {
                                                                const feedbacksFiltrados = culturaFeedbacks.filter(f => showRespondedFeedbacks ? f.status === 'Respondido' : f.status === 'Pendente');

                                                                return (
                                                                    <div className="pb-10">
                                                                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3 mt-4">
                                                                            <button 
                                                                                onClick={() => setShowRespondedFeedbacks(!showRespondedFeedbacks)}
                                                                                className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors ${showRespondedFeedbacks ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-orange-100 text-orange-700 border border-orange-300'}`}
                                                                            >
                                                                                {showRespondedFeedbacks ? '✓ Exibindo: Respondidas (Ver Pendentes)' : '⚠️ Exibindo: Pendentes (Ver Respondidas)'}
                                                                            </button>

                                                                            <div className="flex bg-gray-200 dark:bg-slate-700 rounded-lg p-1">
                                                                                <button onClick={() => setFeedbackLayout('cards')} className={`p-1.5 rounded ${feedbackLayout === 'cards' ? 'bg-white dark:bg-slate-600 shadow-sm' : 'text-gray-500 hover:bg-gray-300 dark:hover:bg-slate-600'}`} title="Visão em Cards"><Grid size={16}/></button>
                                                                                <button onClick={() => setFeedbackLayout('linhas')} className={`p-1.5 rounded ${feedbackLayout === 'linhas' ? 'bg-white dark:bg-slate-600 shadow-sm' : 'text-gray-500 hover:bg-gray-300 dark:hover:bg-slate-600'}`} title="Visão em Lista"><List size={16}/></button>
                                                                            </div>
                                                                        </div>

                                                                        {feedbackLayout === 'cards' ? (
                                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                                                {feedbacksFiltrados.sort((a,b) => new Date(b.dataEnvio.split('/').reverse().join('-')) - new Date(a.dataEnvio.split('/').reverse().join('-'))).map(fb => {
                                                                                    const docReferencia = culturaRecords.find(r => r.id === fb.documentoId);
                                                                                    const isPendente = fb.status === 'Pendente';
                                                                                    
                                                                                    return (
                                                                                        <div key={fb.id} className={`p-5 rounded-xl border shadow-sm flex flex-col justify-between ${isPendente ? 'bg-orange-50 border-orange-200 dark:bg-orange-900/10 dark:border-orange-800' : (isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200')}`}>
                                                                                            <div>
                                                                                                <div className="flex justify-between items-start mb-2">
                                                                                                    <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${fb.tipo === 'Elogio' ? 'bg-green-100 text-green-700' : (fb.tipo === 'Dúvida' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700')}`}>
                                                                                                        {fb.tipo}
                                                                                                    </span>
                                                                                                    <span className="text-[10px] font-bold text-gray-400">{fb.dataEnvio}</span>
                                                                                                </div>
                                                                                                <p className="text-xs font-bold opacity-70 mb-2">Por: {fb.isAnonimo ? 'Anônimo' : fb.userName}</p>
                                                                                                
                                                                                                {docReferencia && (
                                                                                                    <div className="mb-2">
                                                                                                        <p className="text-[10px] font-bold text-blue-500">Ref: {docReferencia.nome}</p>
                                                                                                        {fb.parteDocumento && (
                                                                                                            <p className="text-[10px] font-bold text-orange-600 dark:text-orange-400">Trecho: {fb.parteDocumento}</p>
                                                                                                        )}
                                                                                                    </div>
                                                                                                )}
                                                                                                
                                                                                                <div className="text-sm italic p-3 bg-white dark:bg-slate-900 rounded border border-gray-100 dark:border-slate-700 mb-4">
                                                                                                    "{fb.mensagem}"
                                                                                                </div>
                                                                                            </div>

                                                                                            {isPendente ? (
                                                                                                <div className="mt-auto">
                                                                                                    <textarea placeholder="Sua resposta..." value={hrResponseText[fb.id] || ''} onChange={e => setHrResponseText({...hrResponseText, [fb.id]: e.target.value})} className="w-full text-xs p-2 rounded border outline-none focus:ring-1 focus:ring-orange-500 mb-2 dark:bg-slate-900 dark:border-slate-700"/>
                                                                                                    <button disabled={!hrResponseText[fb.id]} onClick={() => { saveCulturaFeedback({...fb, status: 'Respondido', respostaRh: hrResponseText[fb.id], dataResposta: new Date().toLocaleDateString('pt-BR'), lidoColab: false}); setHrResponseText({...hrResponseText, [fb.id]: ''}); }} className={`w-full py-1.5 rounded text-xs font-bold flex items-center justify-center gap-1 transition-colors ${!hrResponseText[fb.id] ? 'bg-gray-300 text-gray-500' : 'bg-orange-500 text-white hover:bg-orange-600'}`}><Reply size={14}/> Responder</button>
                                                                                                </div>
                                                                                            ) : (
                                                                                                <div className="mt-auto p-3 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-900/30 rounded">
                                                                                                    <p className="text-[10px] font-bold text-green-600 uppercase mb-1">Respondido em {fb.dataResposta}:</p>
                                                                                                    <p className="text-xs text-green-900 dark:text-green-100">{fb.respostaRh}</p>
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                                {feedbacksFiltrados.length === 0 && <p className="text-sm text-gray-400 italic col-span-3">Nenhum feedback {showRespondedFeedbacks ? 'respondido' : 'pendente'}.</p>}
                                                                            </div>
                                                                        ) : (
                                                                            <div className={`overflow-x-auto border rounded-xl shadow-sm ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-white'}`}>
                                                                                <table className="w-full text-xs text-left">
                                                                                    <thead className={`font-bold uppercase ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>
                                                                                        <tr>
                                                                                            <th className="p-3 w-20">Data</th>
                                                                                            <th className="p-3">Colaborador</th>
                                                                                            <th className="p-3">Tipo</th>
                                                                                            <th className="p-3">Documento Ref.</th>
                                                                                            <th className="p-3 w-1/3">Mensagem</th>
                                                                                            <th className="p-3 w-64 text-center">Resposta / Ação</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                                                        {feedbacksFiltrados.sort((a,b) => new Date(b.dataEnvio.split('/').reverse().join('-')) - new Date(a.dataEnvio.split('/').reverse().join('-'))).map((fb, i) => {
                                                                                            const docReferencia = culturaRecords.find(r => r.id === fb.documentoId);
                                                                                            const rowClass = i % 2 === 0 ? (isDarkMode ? 'bg-slate-800' : 'bg-white') : (isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50');
                                                                                            
                                                                                            return (
                                                                                                <tr key={fb.id} className={rowClass}>
                                                                                                    <td className="p-3">{fb.dataEnvio}</td>
                                                                                                    <td className="p-3 font-bold">{fb.isAnonimo ? 'Anônimo' : fb.userName}</td>
                                                                                                    <td className="p-3">
                                                                                                        <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${fb.tipo === 'Elogio' ? 'bg-green-100 text-green-700' : (fb.tipo === 'Dúvida' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700')}`}>
                                                                                                            {fb.tipo}
                                                                                                        </span>
                                                                                                    </td>
                                                                                                    <td className="p-3">
                                                                                                        {docReferencia ? docReferencia.nome : '-'}
                                                                                                        {fb.parteDocumento && <div className="text-[9px] text-orange-500 mt-0.5 font-bold">Trecho: {fb.parteDocumento}</div>}
                                                                                                    </td>
                                                                                                    <td className="p-3 italic">"{fb.mensagem}"</td>
                                                                                                    <td className="p-3">
                                                                                                        {fb.status === 'Pendente' ? (
                                                                                                            <div className="flex gap-2">
                                                                                                                <textarea placeholder="Responder..." value={hrResponseText[fb.id] || ''} onChange={e => setHrResponseText({...hrResponseText, [fb.id]: e.target.value})} className={`w-full text-[10px] p-1.5 rounded border outline-none ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-300'}`}/>
                                                                                                                <button disabled={!hrResponseText[fb.id]} onClick={() => { saveCulturaFeedback({...fb, status: 'Respondido', respostaRh: hrResponseText[fb.id], dataResposta: new Date().toLocaleDateString('pt-BR'), lidoColab: false}); setHrResponseText({...hrResponseText, [fb.id]: ''}); }} className={`px-2 rounded text-white shadow-sm ${!hrResponseText[fb.id] ? 'bg-gray-400' : 'bg-orange-500 hover:bg-orange-600'}`}><Reply size={14}/></button>
                                                                                                            </div>
                                                                                                        ) : (
                                                                                                            <div className="text-[10px] bg-green-50 text-green-800 p-2 rounded border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800">
                                                                                                                <b>Respondido ({fb.dataResposta}):</b> {fb.respostaRh}
                                                                                                            </div>
                                                                                                        )}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            );
                                                                                        })}
                                                                                        {feedbacksFiltrados.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-gray-400 italic">Nenhum registro encontrado nesta visão.</td></tr>}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })()}

                                                        {/* ABA 2: GERENCIAR CIÊNCIAS */}
                                                        {hrCulturaTab === 'ciencias' && (() => {
                                                            // 1. CARREGAMENTO DOS DADOS BRUTOS (Sem filtros iniciais para evitar listas vazias)
                                                            const todosColab = data?.collaborators || [];
                                                            const todosDocs = culturaRecords || [];
                                                            const busca = (hrCulturaSearch || "").toLowerCase();

                                                            let pendentes = [];
                                                            let realizados = [];

                                                            // 2. PROCESSAMENTO UNITÁRIO
                                                            todosColab.forEach(collab => {
                                                                // Ignora apenas quem foi deletado ou não tem nome (segurança de dados)
                                                                if (!collab.name) return;

                                                                // Identifica o departamento
                                                                const depto = data?.departments?.find(d => d.id === collab.departmentId)?.name || 'N/D';

                                                                todosDocs.forEach(doc => {
                                                                    // Consideramos apenas documentos que não estão em rascunho
                                                                    if (doc.status === 'Arquivado') return;

                                                                    // BUSCA DE CIÊNCIA (Vínculo por ID da Ficha OU ID do Usuário OU Email)
                                                                    const cienciaObj = (doc.ciencias || []).find(c => 
                                                                        c?.userId === collab.id || 
                                                                        c?.userId === collab.userId ||
                                                                        (c?.userName && c.userName.toLowerCase() === collab.name.toLowerCase())
                                                                    );

                                                                    if (cienciaObj) {
                                                                        realizados.push({
                                                                            collabId: collab.id,
                                                                            collabName: collab.name,
                                                                            collabDept: depto,
                                                                            docName: doc.nome,
                                                                            data: cienciaObj.data,
                                                                            hora: cienciaObj.hora,
                                                                            auditId: cienciaObj.auditId
                                                                        });
                                                                    } else {
                                                                        // Se não há ciência e o documento está liberado, é pendente
                                                                        if (doc.status === 'Liberado') {
                                                                            pendentes.push({
                                                                                collabName: collab.name,
                                                                                collabDept: depto,
                                                                                docName: doc.nome
                                                                            });
                                                                        }
                                                                    }
                                                                });
                                                            });

                                                            // 3. AGRUPAMENTO DE REALIZADOS (Para não repetir o nome do funcionário várias vezes na tabela)
                                                            const realizadosAgrupados = Object.values(realizados.reduce((acc, curr) => {
                                                                if (!acc[curr.collabId]) {
                                                                    acc[curr.collabId] = { collabName: curr.collabName, collabDept: curr.collabDept, lidos: [] };
                                                                }
                                                                acc[curr.collabId].lidos.push(curr);
                                                                return acc;
                                                            }, {}));

                                                            // 4. FILTRO DE BUSCA DA INTERFACE
                                                            const pendentesFinal = pendentes.filter(p => p.collabName.toLowerCase().includes(busca) || p.docName.toLowerCase().includes(busca));
                                                            const realizadosFinal = realizadosAgrupados.filter(r => r.collabName.toLowerCase().includes(busca));

                                                            return (
                                                                <div className="space-y-4 animate-fade-in">
                                                                    {/* BARRA DE PESQUISA SUPERIOR */}
                                                                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mt-2">
                                                                        <div className="relative w-full max-w-md">
                                                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                                                            <input 
                                                                                type="text"
                                                                                placeholder="Pesquisar colaborador ou documento..."
                                                                                className={`w-full pl-9 pr-4 py-2 rounded-xl border text-xs outline-none focus:ring-2 focus:ring-blue-500 transition-all ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`}
                                                                                value={hrCulturaSearch}
                                                                                onChange={e => setHrCulturaSearch(e.target.value)}
                                                                            />
                                                                        </div>
                                                                    </div>

                                                                    {/* CONTAINER DE TELA DIVIDIDA - LADO A LADO */}
                                                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-[600px]">
                                                                        
                                                                        {/* QUADRO ESQUERDA: PENDENTES */}
                                                                        <div className={`flex flex-col rounded-xl border shadow-sm overflow-hidden ${isDarkMode ? 'bg-slate-800/50 border-red-900/30' : 'bg-white border-red-100'}`}>
                                                                            <div className={`px-4 py-3 border-b flex items-center justify-between font-bold ${isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-700'}`}>
                                                                                <div className="flex items-center gap-2 text-sm"><AlertTriangle size={16}/> Pendentes ({pendentesFinal.length})</div>
                                                                            </div>
                                                                            <div className="flex-1 overflow-y-auto custom-scrollbar">
                                                                                <table className="w-full text-[11px] text-left">
                                                                                    <thead className={`text-[10px] uppercase font-bold sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-50 text-gray-500'}`}>
                                                                                        <tr>
                                                                                            <th className="p-3">Colaborador</th>
                                                                                            <th className="p-3">Depto</th>
                                                                                            <th className="p-3 text-right">Documento</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                                                        {pendentesFinal.map((p, idx) => (
                                                                                            <tr key={idx} className={isDarkMode ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'}>
                                                                                                <td className="p-3 font-bold truncate max-w-[120px]" title={p.collabName}>{p.collabName}</td>
                                                                                                <td className="p-3 opacity-70">{p.collabDept}</td>
                                                                                                <td className="p-3 text-right text-red-500 font-bold truncate max-w-[100px]" title={p.docName}>{p.docName}</td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </table>
                                                                                {pendentesFinal.length === 0 && <div className="p-10 text-center italic opacity-50 text-xs">Nenhuma pendência encontrada.</div>}
                                                                            </div>
                                                                        </div>

                                                                        {/* QUADRO DIREITA: CIENTES (AJUSTADO) */}
                                                                            <div className={`flex flex-col rounded-xl border shadow-sm overflow-hidden ${isDarkMode ? 'bg-slate-800/50 border-green-900/30' : 'bg-white border-green-100'}`}>
                                                                                <div className={`px-4 py-3 border-b flex items-center justify-between font-bold ${isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-50 text-green-700'}`}>
                                                                                    <div className="flex items-center gap-2 text-sm"><CheckCircle size={16}/> Cientes ({realizadosFinal.length})</div>
                                                                                </div>
                                                                                <div className="flex-1 overflow-y-auto custom-scrollbar">
                                                                                    <table className="w-full text-[11px] text-left">
                                                                                        <thead className={`text-[10px] uppercase font-bold sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-50 text-gray-500'}`}>
                                                                                            <tr>
                                                                                                <th className="p-3">Colaborador</th>
                                                                                                <th className="p-3">Depto</th>
                                                                                                <th className="p-3 w-[40%]">Documentos Lidos</th>
                                                                                                <th className="p-3 text-right">Data/Hora/Auditoria</th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                                                                                            {realizadosFinal.map((r, idx) => (
                                                                                                <tr key={idx} className={isDarkMode ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'}>
                                                                                                    <td className="p-3 font-bold truncate max-w-[110px]" title={r.collabName}>{r.collabName}</td>
                                                                                                    <td className="p-3 opacity-70">{r.collabDept}</td>
                                                                                                    <td className="p-3">
                                                                                                        <div className="flex flex-wrap gap-1">
                                                                                                            {r.lidos.map((doc, dIdx) => (
                                                                                                                <div key={dIdx} className="text-[9px] bg-green-100 text-green-800 px-2 py-0.5 rounded border border-green-200 font-medium w-full lg:w-auto" title={doc.docName}>
                                                                                                                    {doc.docName}
                                                                                                                </div>
                                                                                                            ))}
                                                                                                        </div>
                                                                                                    </td>
                                                                                                    <td className="p-3 text-right whitespace-nowrap">
                                                                                                        {r.lidos.map((doc, dIdx) => (
                                                                                                            <div key={dIdx} className="text-[9px] mb-1 leading-tight flex flex-col lg:flex-row lg:justify-end lg:gap-1">
                                                                                                                <span className="font-black text-slate-700 dark:text-slate-300">{doc.data} {doc.hora}</span>
                                                                                                                <span className="opacity-40 font-mono text-[8px] bg-black/5 px-1 rounded">ID: {doc.auditId}</span>
                                                                                                            </div>
                                                                                                        ))}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            ))}
                                                                                        </tbody>
                                                                                    </table>
                                                                                    {realizadosFinal.length === 0 && <div className="p-10 text-center italic opacity-50 text-xs">Nenhum registro assinado.</div>}
                                                                                </div>
                                                                            </div>

                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            )}
                                                    {/* --- MODAL: NOVO/EDITAR CULTURA ACAD --- */}
                                            {showCulturaModal && (
                                                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setShowCulturaModal(false)}>
                                                    <div className={`w-full max-w-2xl rounded-xl shadow-2xl p-6 border ${isDarkMode ? 'bg-slate-800 text-white border-slate-700' : 'bg-white text-gray-800 border-gray-200'}`} onClick={e => e.stopPropagation()}>
                                                        <div className="flex justify-between items-center mb-4 border-b pb-2 dark:border-slate-700">
                                                            <h3 className="font-bold text-xl flex items-center gap-2 text-blue-500">
                                                                <BookOpen size={20}/> {culturaForm.id ? 'Editar Registro (Silencioso)' : 'Novo Registro - Cultura Acad'}
                                                            </h3>
                                                            <button onClick={() => setShowCulturaModal(false)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                                                        </div>
                                                        
                                                        <div className="grid grid-cols-2 gap-4 mb-4 max-h-[60vh] overflow-y-auto custom-scrollbar pr-2">
                                                            <div className="col-span-2">
                                                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Nome de Apresentação *</label>
                                                                <input type="text" value={culturaForm.nome} onChange={e=>setCulturaForm({...culturaForm, nome: e.target.value})} placeholder="Ex: Política de Home Office v2" className={`w-full p-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-300'}`} />
                                                            </div>
                                                            <div className="col-span-2">
                                                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Link de Acesso (Drive, Docs, PDF) *</label>
                                                                <input type="url" value={culturaForm.link} onChange={e=>setCulturaForm({...culturaForm, link: e.target.value})} placeholder="https://..." className={`w-full p-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-300'}`} />
                                                            </div>
                                                            <div>
                                                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Data Início *</label>
                                                                <input type="date" value={culturaForm.dataInicio} onChange={e=>setCulturaForm({...culturaForm, dataInicio: e.target.value})} className={`w-full p-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar' : 'bg-gray-50 border-gray-300'}`} />
                                                            </div>
                                                            <div>
                                                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Data Fim (Opcional)</label>
                                                                <input type="date" value={culturaForm.dataFim} onChange={e=>setCulturaForm({...culturaForm, dataFim: e.target.value})} className={`w-full p-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar' : 'bg-gray-50 border-gray-300'}`} />
                                                            </div>
                                                            <div>
                                                                <label className={`text-xs font-bold block mb-1 text-orange-500`}>Data Limite (Ciência) *</label>
                                                                <input type="date" value={culturaForm.dataLimite || ''} onChange={e=>setCulturaForm({...culturaForm, dataLimite: e.target.value})} className={`w-full p-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 invert-calendar' : 'bg-orange-50 border-orange-200'}`} />
                                                            </div>
                                                            <div>
                                                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Departamento</label>
                                                                <select value={culturaForm.departamento} onChange={e=>setCulturaForm({...culturaForm, departamento: e.target.value})} className={`w-full p-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-300'}`}>
                                                                    <option value="Todos">Todos os Departamentos</option>
                                                                    {data.departments.map(d=><option key={d.id} value={d.name}>{d.name}</option>)}
                                                                </select>
                                                            </div>
                                                            <div>
                                                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Tipo de Público</label>
                                                                <select value={culturaForm.tipo} onChange={e=>setCulturaForm({...culturaForm, tipo: e.target.value})} className={`w-full p-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-300'}`}>
                                                                    <option value="Todos">Todos</option>
                                                                    <option value="Gestão">Apenas Gestão</option>
                                                                    <option value="Colaborador">Apenas Colaborador</option>
                                                                </select>
                                                            </div>
                                                            <div className="col-span-2 flex items-center gap-3 mt-2 p-3 rounded-lg border bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
                                                                <input type="checkbox" id="chkAssina" checked={culturaForm.exigeAssinatura} onChange={e=>setCulturaForm({...culturaForm, exigeAssinatura: e.target.checked})} className="w-5 h-5 cursor-pointer text-blue-600" />
                                                                <label htmlFor="chkAssina" className="text-sm font-bold cursor-pointer text-yellow-800 dark:text-yellow-300">
                                                                    Exige leitura e aceite obrigatório (Assinatura Digital)
                                                                </label>
                                                            </div>
                                                        </div>

                                                        <div className={`flex justify-end gap-3 pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                                            <Button variant="secondary" onClick={() => setShowCulturaModal(false)}>Cancelar</Button>
                                                            <Button onClick={() => {
                                                                if (!culturaForm.nome || !culturaForm.link || !culturaForm.dataInicio) return alert("Preencha Nome, Link e Data de Início.");
                                                                const id = culturaForm.id || Date.now().toString();
                                                                saveCulturaRecord({ ...culturaForm, id });
                                                                setShowCulturaModal(false);
                                                            }} className="bg-blue-600 hover:bg-blue-700 text-white border-0 shadow-md">Salvar Registro</Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* --- MODAL: FEEDBACK CULTURA ACAD (DÚVIDA/SUGESTÃO/ELOGIO) --- */}
                                                {showFeedbackModal && (() => {
                                                    const historico = culturaFeedbacks.filter(f => f.documentoId === feedbackForm.documentoId && f.userId === currentUser?.id).sort((a,b) => new Date(b.dataEnvio.split('/').reverse().join('-')) - new Date(a.dataEnvio.split('/').reverse().join('-')));
                                                    
                                                    return (
                                                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setShowFeedbackModal(false)}>
                                                        <div className={`w-full max-w-lg rounded-xl shadow-2xl p-0 border flex flex-col max-h-[90vh] overflow-hidden ${isDarkMode ? 'bg-slate-800 text-white border-slate-700' : 'bg-white text-gray-800 border-gray-200'}`} onClick={e => e.stopPropagation()}>
                                                            
                                                            <div className={`p-4 border-b flex justify-between items-center ${isDarkMode ? 'border-slate-700 bg-slate-900/50' : 'border-gray-200 bg-gray-50'}`}>
                                                                <h3 className="font-bold text-lg flex items-center gap-2"><MessageCircle className="text-orange-500"/> Fale com o RH</h3>
                                                                <button onClick={() => setShowFeedbackModal(false)} className="hover:text-red-500"><X size={20}/></button>
                                                            </div>

                                                            {/* ABAS DO MODAL */}
                                                            <div className={`flex border-b shrink-0 ${isDarkMode ? 'border-slate-700 bg-slate-900/30' : 'border-gray-200 bg-gray-50'}`}>
                                                                <button onClick={() => setFeedbackModalTab('nova')} className={`flex-1 py-3 text-sm font-bold border-b-2 transition-colors ${feedbackModalTab === 'nova' ? 'border-blue-500 text-blue-500 bg-blue-50/10' : 'border-transparent opacity-60'}`}>
                                                                    {feedbackForm.id ? 'Editar Envio' : 'Novo Envio'}
                                                                </button>
                                                                <button onClick={() => setFeedbackModalTab('historico')} className={`flex-1 py-3 text-sm font-bold border-b-2 transition-colors ${feedbackModalTab === 'historico' ? 'border-orange-500 text-orange-500 bg-orange-50/10' : 'border-transparent opacity-60'}`}>
                                                                    Histórico de Respostas ({historico.length})
                                                                </button>
                                                            </div>
                                                            
                                                            <div className="overflow-y-auto custom-scrollbar flex-1 p-6">
                                                                {/* ABA 1: NOVO ENVIO / EDIÇÃO */}
                                                                {feedbackModalTab === 'nova' && (
                                                                    <div className="space-y-4 animate-fade-in">
                                                                        <div>
                                                                            <label className="text-xs font-bold block mb-1">Tipo de Interação</label>
                                                                            <select value={feedbackForm.tipo} onChange={e=>setFeedbackForm({...feedbackForm, tipo: e.target.value})} className={`w-full p-2 border rounded text-sm outline-none focus:border-orange-500 ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-300'}`}>
                                                                                <option value="Dúvida">Dúvida</option>
                                                                                <option value="Sugestão">Sugestão</option>
                                                                                <option value="Elogio">Elogio</option>
                                                                            </select>
                                                                        </div>
                                                                        {feedbackForm.tipo !== 'Elogio' && (
                                                                            <>
                                                                                <div>
                                                                                    <label className="text-xs font-bold block mb-1">Documento Selecionado</label>
                                                                                    <select disabled value={feedbackForm.documentoId} className={`w-full p-2 border rounded text-sm opacity-70 cursor-not-allowed ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-400' : 'bg-gray-100 border-gray-300 text-gray-500'}`}>
                                                                                        {culturaRecords.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
                                                                                    </select>
                                                                                </div>
                                                                                <div>
                                                                                    <label className="text-xs font-bold block mb-1">Qual parte do documento? (Opcional)</label>
                                                                                    <input type="text" placeholder="Ex: Parágrafo 3, Tópico 2.1..." value={feedbackForm.parteDocumento} onChange={e=>setFeedbackForm({...feedbackForm, parteDocumento: e.target.value})} className={`w-full p-2 border rounded text-sm outline-none focus:ring-1 focus:ring-orange-500 ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-300'}`} />
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                        <div>
                                                                            <label className="text-xs font-bold block mb-1">Descreva sua {feedbackForm.tipo}</label>
                                                                            <textarea value={feedbackForm.mensagem} onChange={e=>setFeedbackForm({...feedbackForm, mensagem: e.target.value})} className={`w-full p-3 border rounded h-24 text-sm resize-none outline-none focus:ring-1 focus:ring-orange-500 ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-300'}`}></textarea>
                                                                        </div>
                                                                        <div className="flex items-center gap-2 mt-1">
                                                                            <input type="checkbox" id="chkAnonimo" checked={feedbackForm.isAnonimo} onChange={e=>setFeedbackForm({...feedbackForm, isAnonimo: e.target.checked})} className="w-4 h-4 cursor-pointer text-orange-500" />
                                                                            <label htmlFor="chkAnonimo" className="text-xs font-bold cursor-pointer opacity-80">Enviar de forma Anônima</label>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* ABA 2: HISTÓRICO */}
                                                                {feedbackModalTab === 'historico' && (
                                                                    <div className="space-y-4 animate-fade-in">
                                                                        {historico.length === 0 ? (
                                                                            <p className="text-center text-gray-400 italic py-8">Nenhum histórico para este documento.</p>
                                                                        ) : (
                                                                            historico.map(fb => (
                                                                                <div key={fb.id} className={`p-4 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                                                    <div className="flex justify-between items-center mb-2">
                                                                                        <span className={`font-bold px-2 py-0.5 rounded text-[10px] uppercase ${fb.tipo === 'Elogio' ? 'bg-green-100 text-green-700' : (fb.tipo === 'Dúvida' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700')}`}>{fb.tipo}</span>
                                                                                        <span className="opacity-60 text-[10px] font-bold">{fb.dataEnvio}</span>
                                                                                    </div>
                                                                                    {fb.parteDocumento && <p className="text-[10px] text-orange-600 dark:text-orange-400 font-bold mb-1">Trecho: {fb.parteDocumento}</p>}
                                                                                    <p className="text-sm italic mb-3 text-gray-700 dark:text-slate-300">"{fb.mensagem}"</p>
                                                                                    
                                                                                    {fb.status === 'Respondido' ? (
                                                                                        <div className="mt-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                                                                            <p className="text-[10px] font-bold text-green-700 dark:text-green-400 uppercase mb-1 flex items-center gap-1"><CheckSquare size={12}/> Resposta do RH ({fb.dataResposta}):</p>
                                                                                            <p className="text-sm text-green-900 dark:text-green-100">{fb.respostaRh}</p>
                                                                                        </div>
                                                                                    ) : (
                                                                                        <div className="mt-2 text-[10px] font-bold text-blue-500 uppercase bg-blue-50 dark:bg-blue-900/20 p-2 rounded">Aguardando resposta do RH...</div>
                                                                                    )}
                                                                                </div>
                                                                            ))
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className={`flex justify-end gap-3 p-4 border-t shrink-0 ${isDarkMode ? 'border-slate-700 bg-slate-900/50' : 'border-gray-200 bg-gray-50'}`}>
                                                                <Button variant="secondary" onClick={() => setShowFeedbackModal(false)}>Fechar</Button>
                                                                {feedbackModalTab === 'nova' && (
                                                                    <Button onClick={() => {
                                                                        if (feedbackForm.tipo !== 'Elogio' && !feedbackForm.documentoId) return alert("Selecione o documento referente.");
                                                                        if (!feedbackForm.mensagem) return alert("Preencha a mensagem.");
                                                                        saveCulturaFeedback({ ...feedbackForm, id: feedbackForm.id || Date.now().toString(), userId: currentUser.id, userName: currentUser.name, dataEnvio: feedbackForm.dataEnvio || new Date().toLocaleDateString('pt-BR') });
                                                                        setShowFeedbackModal(false);
                                                                        alert(feedbackForm.id ? "Atualizado com sucesso!" : "Enviado com sucesso para o RH!");
                                                                    }} className="bg-orange-500 hover:bg-orange-600 text-white shadow-md">
                                                                        {feedbackForm.id ? 'Atualizar Envio' : 'Enviar ao RH'}
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    );
                                                })()}

                                            {/* --- MODAL: ATUALIZAR VERSÃO (NOVA VERSÃO) --- */}
                                            {showCulturaUpdateModal && (
                                                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={() => setShowCulturaUpdateModal(false)}>
                                                    <div className={`w-full max-w-md rounded-xl shadow-2xl p-6 border ${isDarkMode ? 'bg-slate-800 text-white border-slate-700' : 'bg-white text-gray-800 border-gray-200'}`} onClick={e => e.stopPropagation()}>
                                                        <h3 className="font-bold text-xl mb-2 flex items-center gap-2 border-b pb-2 dark:border-slate-700">
                                                            <RefreshCw size={20} className="text-orange-500"/> Atualizar Versão
                                                        </h3>
                                                        <p className="text-xs mb-4 opacity-80 leading-relaxed">
                                                            Você está criando uma nova versão de <b>{culturaForm.nome}</b>.<br/>
                                                            <span className="text-red-500 font-bold">Importante:</span> Todos os registros de leitura anteriores serão arquivados e os colaboradores terão que dar "Ciência" novamente.
                                                        </p>
                                                        
                                                        <div className="space-y-4 mb-6">
                                                            <div>
                                                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Novo Link de Acesso (Deixe igual se apenas alterou o texto no mesmo doc)</label>
                                                                <input type="url" value={culturaForm.link} onChange={e=>setCulturaForm({...culturaForm, link: e.target.value})} className={`w-full p-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-500 ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-300'}`} />
                                                            </div>
                                                            <div>
                                                                <label className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Motivo da Atualização / O que mudou? *</label>
                                                                <textarea 
                                                                    value={culturaUpdateMsg} 
                                                                    onChange={e=>setCulturaUpdateMsg(e.target.value)} 
                                                                    placeholder="Ex: Alteramos o parágrafo 3 para incluir a nova regra..." 
                                                                    className={`w-full p-3 border rounded-lg h-24 text-sm outline-none focus:ring-2 focus:ring-orange-500 resize-none ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-300'}`}
                                                                ></textarea>
                                                            </div>
                                                        </div>

                                                        <div className={`flex justify-end gap-3 pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                                                            <Button variant="secondary" onClick={() => { setShowCulturaUpdateModal(false); setCulturaUpdateMsg(''); }}>Cancelar</Button>
                                                            <Button disabled={!culturaUpdateMsg.trim()} onClick={async () => {
                                                                const updateLog = { 
                                                                    data: new Date().toLocaleDateString('pt-BR'), 
                                                                    hora: new Date().toLocaleTimeString('pt-BR'), 
                                                                    motivo: culturaUpdateMsg,
                                                                    antigasCiencias: culturaForm.ciencias || [] // Backup das ciências antigas
                                                                };
                                                                const updatedRecord = {
                                                                    ...culturaForm, 
                                                                    ciencias: [], // Zera as leituras
                                                                    historicoAtualizacao: [...(culturaForm.historicoAtualizacao || []), updateLog]
                                                                };
                                                                await saveCulturaRecord(updatedRecord);
                                                                if (actions.logAction) actions.logAction('CULTURA_ACAD_ATUALIZADO', { nome: culturaForm.nome });
                                                                setShowCulturaUpdateModal(false);
                                                                setCulturaUpdateMsg('');
                                                                alert("Atualização concluída! A leitura foi zerada para que todos possam assinar novamente.");
                                                            }} className={`border-0 shadow-md ${!culturaUpdateMsg.trim() ? 'bg-gray-400 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600 text-white'}`}>
                                                                Confirmar Atualização
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                    {/* MODAL DE PDF (GLOBAL) */}
                                        {(modal === 'pdf' || modal === 'pdf_collab_view') && selectedEval && (
                                            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm" onClick={() => setModal(null)}>
                                                <div className={`p-6 rounded-lg w-80 shadow-2xl ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`} onClick={e => e.stopPropagation()}>
                                                    <h3 className="font-bold mb-4 flex items-center gap-2"><Printer size={20} /> Gerar Relatório PDF</h3>
                                                    <p className="text-xs text-gray-500 mb-4">Selecione o modelo de impressão:</p>
                                                    
                                                    <Button className="w-full mb-2" onClick={() => generatePDF(selectedEval, 'Completo')}>
                                                        Completo
                                                    </Button>
                                                    
                                                    <Button variant="secondary" className="w-full mb-2" onClick={() => generatePDF(selectedEval, selectedEval.personType === 'Gestão' ? 'Simplificado Gestão' : 'Simplificado Colaborador')}>
                                                        Simplificado
                                                    </Button>
                                                    
                                                    <Button variant="danger" className="w-full" onClick={() => setModal(null)}>
                                                        Cancelar
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                        
                                    {/* --- MODAL DE AVALIAÇÃO (CENTRAL COLABORADOR) --- */}
                                    {isCollabEvalModalOpen && (
                                
                        <PerceptionFormModal 
                                            isOpen={isCollabEvalModalOpen}
                                            onClose={() => setIsCollabEvalModalOpen(false)}
                                            data={data}
                                            questions={perceptionQuestions} // Certifique-se que esta variável vem do useFirestoreData no App
                                            currentUser={currentUser}
                        
                        isDarkMode={isDarkMode}
                                            onSave={(newEntry) => {
                                                actions.saveTeamPerception(newEntry);
                                                alert("Avaliação registrada com sucesso!");
                                                setIsCollabEvalModalOpen(false);
                                            }}
                                        />
                                    )}

                        {/* MODAL DE SYNC (GENÉRICO) */}
                        {syncModalConfig.isOpen && (
                
                            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                                <div className={`p-6 rounded-xl w-96 shadow-2xl animate-fade-in space-y-4 ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white'}`}>
                                    <h3 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                        <RefreshCw size={18}/> {syncModalConfig.title}
                                    </h3>
                                    <div>
                                        <label 
                className={`text-xs font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                                            Ano Referência
                                        </label>
                                        <Input type="number" value={batchYearInput} onChange={e => setBatchYearInput(e.target.value)} className={`text-center text-lg tracking-widest font-bold ${isDarkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`} maxLength={4}/>
                                        <p className={`text-[10px] mt-2 ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                                            A ação será executada para o ano informado.
                                        </p>
            
                        </div>
                                    <div className="flex justify-end gap-2 pt-2">
                                        <Button variant="secondary" onClick={() => setSyncModalConfig(prev => ({...prev, isOpen: false}))} disabled={isProcessingBatch} className={isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600' : ''}>Cancelar</Button>
                                        <Button onClick={() => syncModalConfig.callback(batchYearInput)} disabled={isProcessingBatch} className="bg-blue-600 hover:bg-blue-700 text-white">
                                            {isProcessingBatch ? <><Loader2 size={16} className="animate-spin mr-2"/> Processando...</> : "Confirmar"}
                                        </Button>
    
                                </div>
                                </div>
                            </div>
                        )}


                        {cacheModalOpen && <div className="fixed inset-0 z-[1000] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setCacheModalOpen(false)}>
                            <div className="app-dialog w-full max-w-3xl rounded-2xl border shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
                                <div className="app-dialog-header"><Database size={23}/><div><h3>Limpeza seletiva do cache</h3><p>Escolha somente os módulos que deseja limpar. Dados e configurações do Firebase não serão apagados.</p></div><button onClick={() => setCacheModalOpen(false)}><X/></button></div>
                                <div className="p-5 space-y-2">{cacheModules.map(([key, label]) => <label key={key} className={`app-cache-option ${cacheSelection[key] ? 'is-selected' : ''}`}><input type="checkbox" checked={cacheSelection[key]} onChange={e => setCacheSelection(prev => ({ ...prev, [key]: e.target.checked }))}/><div><b>{label}</b><small>Remove somente dados temporários e caches locais deste módulo.</small></div></label>)}</div>
                                <div className="app-dialog-footer"><button className="app-secondary-btn" onClick={() => setCacheModalOpen(false)}>Cancelar</button><button className="app-primary-btn" onClick={clearSelectedCache}><Trash2 size={15}/>Limpar selecionados</button></div>
                            </div>
                        </div>}

                        {helpCenterOpen && (
                            <CentralAjudaSugestoes
                                aberto={helpCenterOpen}
                                onClose={() => setHelpCenterOpen(false)}
                                isDarkMode={isDarkMode}
                                user={currentUser}
                                telaAtual={openModuleTabs.find(tab => tab.targetSys === safeSys)?.label || menuStructureAvDes.find(item => item.id === activeModuleId)?.label || 'Gestão de Pessoas'}
                                modoAba={false}
                            />
                        )}

                    </div>
                </main>
                </div>
        );
    }

    /**
     * ============================================================================
     * ESTILOS GLOBAIS E COMPONENTE RAIZ
     * ============================================================================
     */


    /* ===============================================================================
     * [END_MOD_APP_MAIN]
     * FIM DO BLOCO: App
     * [START_MOD_GLOBAL_STYLES]
     * INICIO DO BLOCO: GlobalStyles
     * =============================================================================== */
    const GlobalStyles = () => (
        <style jsx="true">{`
            /* --- IMPORTAÇÃO DE FONTES --- */
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700&display=swap');
            
            /* --- RESET BÁSICO --- */
            body { 
                font-family: 'Inter', sans-serif; 
            
        background-color: #f3f4f6; 
            }

            /* --- UTILITÁRIOS DE INPUT --- */
            /* Remove setas de inputs numéricos */
            input[type=number]::-webkit-inner-spin-button, 
            input[type=number]::-webkit-outer-spin-button { 
                -webkit-appearance: none; 
                margin: 0; 
            }
            input[type=number] { -moz-appearance: textfield; }

            /* --- ANIMAÇÕES --- */
            .animate-fade-in { animation: fadeIn 0.3s ease-in-out; }
            
            @keyframes fadeIn { 
                from { opacity: 0; transform: translateY(10px); } 
                to { opacity: 1; transform: translateY(0); } 
            }
            
            .badge-pulse { animation: pulse-red 2s infinite; }
            
            @keyframes pulse-red 
    {
                0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
                70% { box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
                100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
            }

            /* =========================================================
            OVERRIDES PARA DARK MODE (MODO ESCURO)
            ========================================================= */
            
            .dark input, .dark select, .dark textarea, .dark .form-select, .dark .form-control {
                background-color: #1e293b !important; color: #f1f5f9 !important; border: 1px solid #475569 !important; border-radius: 0.5rem !important;
            }
            .dark label, .dark .text-gray-500, .dark .text-gray-600, .dark .text-gray-700 {
                color: #cbd5e1 !important;
            }

            /* Popups */
            .dark .float-popup, .dark .float-popup > div {
                background-color: #1e293b !important; border-color: #334155 !important; color: #e2e8f0 !important;
            }
    
        .dark .float-popup > div:first-child {
                background-color: #0f172a !important; border-bottom: 1px solid #334155 !important;
            }
            .dark .float-popup li, .dark .float-popup span { color: #e2e8f0 !important; }
            .dark .float-popup .bg-gray-50 { background-color: #334155 !important; border-color: #475569 !important; }

            /* Tabelas */
            .dark td .bg-white, .dark td .bg-gray-50 { background-color: #0f172a !important; border-color: #334155 !important; color: #f8fafc !important; }
            .dark thead th { background-color: #0f172a !important; color: #94a3b8 !important; border-bottom: 1px solid #334155 !important; }
            .dark tbody tr { background-color: #1e293b !important; border-bottom: 1px solid #334155 !important; }
            .dark tbody tr:hover { background-color: #334155 !important; }
            .dark td { color: #f1f5f9 !important; }

            /* Status Colors Dark */
            .dark .bg-blue-50 { background-color: #172554 !important; color: #bfdbfe !important; border-color: #1e40af !important; }
            .dark .bg-green-100, .dark .bg-green-50 { background-color: #064e3b !important; color: #6ee7b7 !important; border-color: #065f46 !important; }
            .dark .bg-red-50 { background-color: #450a0a 
    !important; color: #fca5a5 !important; border-color: #991b1b !important; }
            .dark .bg-yellow-50, .dark .bg-orange-50 { background-color: #422006 !important; color: #fcd34d !important; border-color: #ca8a04 !important; }
            .dark option { background-color: #1e293b; color: #f1f5f9; }
            
            .q1-text-color { color: #2563eb; } .dark .q1-text-color { color: #60a5fa; }
            .q2-text-color { color: #f59e0b; } .dark .q2-text-color { color: #fbbf24; }
            .q3-text-color { color: #ef4444; } .dark .q3-text-color { color: #f87171; }
            .q4-text-color { color: #10b981; } .dark .q4-text-color { color: #34d399; }

            /* Componentes Padrão */
            .float-popup { position: fixed; z-index: 9999; background: white; border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.1); border: 1px solid #e5e7eb; pointer-events: none; min-width: 220px; font-size: 0.75rem; }
            .float-popup::after { content: ''; position: absolute; left: 50%; margin-left: -6px; border-width: 6px; border-style: solid; }
            .float-popup[data-placement="top"]::after { top: 100%; border-color: white transparent transparent transparent; }
            .dark .float-popup[data-placement="top"]::after { border-color: #1e293b transparent transparent transparent !important; }
        
        .float-popup[data-placement="bottom"]::after { bottom: 100%; border-color: transparent transparent white transparent; }
            .dark .float-popup[data-placement="bottom"]::after { border-color: transparent transparent #0f172a transparent !important; }
        
            .tooltip-trigger { position: relative; display: inline-flex; align-items: center; justify-content: center; }
            .tooltip-bubble { visibility: hidden; width: 140px; background-color: #374151; color: #fff; text-align: center; border-radius: 6px; padding: 6px; position: absolute; z-index: 50; bottom: 125%; left: 50%; margin-left: -70px; opacity: 0; transition: opacity 0.3s; font-size: 11px; font-weight: 500; pointer-events: none; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
            .tooltip-bubble::after { content: ""; position: absolute; top: 100%; left: 50%; margin-left: -5px; border-width: 5px; border-style: solid; border-color: #374151 transparent transparent transparent; }
            .tooltip-trigger:hover .tooltip-bubble { visibility: visible; opacity: 1; }
        
            ::-webkit-scrollbar { width: 6px; height: 6px; }
            ::-webkit-scrollbar-track { background: #f1f1f1; }
            .dark ::-webkit-scrollbar-track { background: #0f172a; }
            ::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 3px; }
            .dark ::-webkit-scrollbar-thumb { background: #475569; }

            .align-middle-cell > div { height: 100%; display: flex; flex-direction: column; justify-content: center;
    align-items: center; }

            /* v76_353 - Gestão de usuários, perfis e permissionamentos */
            .gp-users-admin { border-radius: 14px !important; }
            .gp-users-admin table { font-size: 11px !important; }
            .gp-users-admin thead th { background:#0f7f86 !important; color:#fff !important; text-transform:none !important; letter-spacing:0 !important; padding:11px 12px !important; }
            .gp-users-admin tbody td { padding:11px 12px !important; }
            html:not(.dark) .gp-users-admin tbody tr:nth-child(odd) { background:#fffaf2 !important; }
            html:not(.dark) .gp-users-admin tbody tr:nth-child(even) { background:#fff !important; }
            html:not(.dark) .gp-users-admin tbody tr:hover { background:#f6efe5 !important; }
            .gp-profile-modal { border:1px solid rgba(148,163,184,.35); }
            html:not(.dark) .gp-profile-modal { background:#fffaf2 !important; }
            .gp-profile-modal input, .gp-profile-modal select { font-size:12px; }
            .gp-profile-modal .custom-scrollbar { scrollbar-width:thin; }
            @media (max-width: 1100px) {
                .gp-users-admin { overflow:auto !important; }
                .gp-users-admin table { min-width:1180px; }
                .gp-profile-modal { max-width:96vw !important; }
            }

        `}</style>
    );

    // --- COMPONENTE PÚBLICO: VISUALIZAÇÃO DA FOLHA DE PONTO VIA QR CODE ---


    /* ===============================================================================
     * [END_MOD_GLOBAL_STYLES]
     * FIM DO BLOCO: GlobalStyles
     * [START_MOD_PUBLIC_POINT_SHEET]
     * INICIO DO BLOCO: PublicPointSheet
     * =============================================================================== */
    const PublicPointSheet = ({ sigId }) => {
        const [status, setStatus] = useState('loading');
        const [cpf, setCpf] = useState('');
        const [month, setMonth] = useState('');
        const [sigData, setSigData] = useState(null);
        const [errorMsg, setErrorMsg] = useState('');

        useEffect(() => {
            const fetchSig = async () => {
                try {
                    let appInstance;
                    try { appInstance = getApp(); } catch (e) { appInstance = initializeApp(firebaseConfig); }
                    const db = getFirestore(appInstance);
                    
                    const cleanId = sigId ? String(sigId).trim().replace(/\/+$/, '') : '';
                    const docRef = doc(db, `${FIRESTORE_PATH_PUBLIC}/point_signatures`, cleanId);
                    const docSnap = await getDoc(docRef);
                    
                    if (docSnap.exists()) {
                        setSigData(docSnap.data());
                        setStatus('login');
                    } else {
                        setStatus('error');
                        setErrorMsg('Documento não encontrado ou link inválido.');
                    }
                } catch (e) {
                    setStatus('error');
                    setErrorMsg('Falha de conexão.');
                }
            };
            fetchSig();
        }, [sigId]);

        const handleLogin = (e) => {
            e.preventDefault();
            const cleanCpf = cpf.replace(/\D/g, '');
            const targetCpf = (sigData.userCpf || '').replace(/\D/g, '');
            if (cleanCpf === targetCpf && month === sigData.month) {
                setStatus('view');
            } else {
                setErrorMsg('CPF ou Mês Referência incorretos. Verifique e tente novamente.');
            }
        };

        // Helper nativo para não depender do escopo de fora
        const toTimePonto = (mins) => {
            if (isNaN(mins) || mins === null) return "00:00";
            const sign = mins < 0 ? "-" : "";
            const m = Math.abs(mins);
            const h = Math.floor(m / 60);
            const mn = Math.round(m % 60);
            return `${sign}${String(h).padStart(2, '0')}:${String(mn).padStart(2, '0')}`;
        };

        const handlePrintPDF = async () => {
            if (!sigData.sheetData) return alert("Este documento foi gerado antes da atualização do sistema e não possui o espelho salvo. Peça ao RH para gerar o PDF internamente.");
            
            // Carrega PDF dinamicamente
            if (typeof window.loadPdfLibs !== 'undefined') {
                await window.loadPdfLibs();
            } else {
                await new Promise((resolve) => {
                    const s = document.createElement('script');
                    s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
                    s.onload = () => {
                        const s2 = document.createElement('script');
                        s2.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.31/jspdf.plugin.autotable.min.js";
                        s2.onload = resolve;
                        document.head.appendChild(s2);
                    };
                    document.head.appendChild(s);
                });
            }

            if (!window.jspdf) return alert("Biblioteca PDF não carregada.");
            
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF('p', 'mm', 'a4');
            
            const primaryColor = [30, 58, 138];
            const accentColor = [15, 23, 42]; 
            const lightGray = [241, 245, 249];
            const borderColor = [203, 213, 225];
            
            const userName = (sigData.userName || '').toUpperCase();
            const userCpf = sigData.userCpf || 'N/D';
            const userMatricula = sigData.userMatricula || 'N/D';
            const fullRole = sigData.userRole || '-';
            const userDept = sigData.userDept || 'GERAL';
            const companyName = sigData.companyName || "ACAD ASSESSORIA CONTABIL";
            const companyCNPJ = sigData.companyCNPJ || "04.550.717/0001-68";

            const [ano, mes] = sigData.month.split('-');
            const lastDay = new Date(ano, mes, 0).getDate();
            const periodoTexto = `01/${mes}/${ano} A ${lastDay}/${mes}/${ano}`;
            
            doc.setFillColor(...primaryColor); 
            doc.rect(0, 0, 210, 12, 'F');
            doc.setFont("helvetica", "bold"); 
            doc.setTextColor(255, 255, 255); 
            doc.setFontSize(12); 
            doc.text("FOLHA DE PONTO INDIVIDUAL", 105, 8, { align: "center" }); 

            const startY = 16; 
            const boxHeight = 28; 
            doc.setFillColor(...lightGray); 
            doc.setDrawColor(...borderColor); 
            doc.roundedRect(10, startY, 190, boxHeight, 2, 2, 'FD'); 
            
            doc.setFillColor(...primaryColor); 
            doc.roundedRect(10, startY, 3, boxHeight, 2, 2, 'F'); 
            doc.rect(11, startY, 2, boxHeight, 'F'); 

            const writeLabelVal = (label, val, x, y, isBoldVal = false) => { 
                doc.setFontSize(6); 
                doc.setTextColor(100, 116, 139); 
                doc.setFont("helvetica", "bold");
                doc.text(label, x, y); 
                doc.setFontSize(8); 
                doc.setTextColor(...accentColor); 
                doc.setFont("helvetica", isBoldVal ? "bold" : "normal"); 
                doc.text(String(val).substring(0, 40), x, y + 3.5); 
            }; 

            const r1 = startY + 6; 
            writeLabelVal("COLABORADOR:", userName, 18, r1, true); 
            writeLabelVal("CPF:", userCpf, 90, r1); 
            writeLabelVal("MATRÍCULA:", userMatricula, 120, r1); 
            writeLabelVal("FUNÇÃO:", fullRole, 145, r1); 

            const r2 = startY + 16; 
            writeLabelVal("EMPRESA:", companyName, 18, r2); 
            writeLabelVal("CNPJ:", companyCNPJ, 90, r2); 
            
            doc.setFontSize(6);
            doc.setTextColor(100, 116, 139); 
            doc.text("DEPARTAMENTO:", 120, r2); 
            doc.setFontSize(9);
            doc.setTextColor(...primaryColor); 
            doc.setFont("helvetica", "bold"); 
            doc.text(userDept, 120, r2 + 3.5); 

            doc.setFillColor(...primaryColor); 
            doc.roundedRect(145, startY + 13, 30, 10, 1, 1, 'F'); 
            doc.setTextColor(255); 
            doc.setFontSize(5); 
            doc.text("PERÍODO:", 160, startY + 16.5, { align: "center" }); 
            doc.setFontSize(7); 
            doc.setFont("helvetica", "bold"); 
            doc.text(periodoTexto, 160, startY + 20.5, { align: "center" });

            if (sigData.qrCodeUrl) {
                try {
                    doc.addImage(sigData.qrCodeUrl, 'PNG', 178, startY + 4, 18, 18);
                    doc.setFontSize(4.5);
                    doc.setTextColor(100, 116, 139);
                    doc.text("VALIDAÇÃO DIGITAL", 187, startY + 24, { align: "center" });
                } catch (e) { console.log(e); }
            }

            const tableBody = sigData.sheetData.map(row => {
                const p = row.punches.map(punch => punch.time);
                let extraEnt = [], extraSai = [];
                for (let i = 4; i < p.length; i += 2) { 
                    if (p[i]) extraEnt.push(p[i]); 
                    if (p[i+1]) extraSai.push(p[i+1]); 
                }
                
                const obsList = [];
                if (row.justificationsText) obsList.push(row.justificationsText);
                if (row.obsNote) obsList.push(`Obs: ${row.obsNote}`);
                const observacoesFinal = obsList.join(' | ');

                return [
                    row.display.substring(0, 5), 
                    row.week.toUpperCase().substring(0, 3), 
                    p[0]||'', p[1]||'', p[2]||'', p[3]||'', 
                    extraEnt.join('\n'), extraSai.join('\n'), 
                    row.worked, 
                    toTimePonto(row.balance),
                    observacoesFinal
                ];
            });

            doc.autoTable({
                startY: startY + boxHeight + 3,
                head: [
                    [{ content: 'DATA', styles: { valign: 'middle' } }, { content: 'DIA', styles: { valign: 'middle' } }, { content: 'JORNADA NORMAL', colSpan: 4, styles: { halign: 'center' } }, { content: 'HE / EXTRAS', colSpan: 2, styles: { halign: 'center' } }, { content: 'TOTAIS', colSpan: 2, styles: { halign: 'center' } }, { content: 'OBSERVAÇÕES', styles: { valign: 'middle', halign: 'center' } }], 
                    ['Data', 'Dia', 'Entrada', 'S. Almoço', 'V. Almoço', 'Saída', 'Ent. Extra', 'Saí. Extra', 'Hrs Trab', 'Saldo', 'Observações']
                ],
                body: tableBody,
                theme: 'grid',
                styles: { fontSize: 5.5, cellPadding: 0.6, lineColor: [200, 200, 200], lineWidth: 0.1, valign: 'middle', textColor: [50, 50, 50], overflow: 'linebreak' },
                headStyles: { fillColor: primaryColor, textColor: [255], fontStyle: 'bold', halign: 'center', lineWidth: 0.1, minCellHeight: 5 },
                columnStyles: { 
                    0: { cellWidth: 10, halign: 'center' }, 1: { cellWidth: 8, halign: 'center' }, 
                    2: { cellWidth: 11, halign: 'center' }, 3: { cellWidth: 12, halign: 'center' }, 
                    4: { cellWidth: 12, halign: 'center' }, 5: { cellWidth: 11, halign: 'center' }, 
                    6: { cellWidth: 14, halign: 'center', fontStyle: 'bold', textColor: [22, 163, 74] }, 
                    7: { cellWidth: 14, halign: 'center', fontStyle: 'bold', textColor: [220, 38, 38] }, 
                    8: { cellWidth: 12, halign: 'center', fontStyle: 'bold' }, 9: { cellWidth: 12, halign: 'center' },
                    10: { cellWidth: 'auto', halign: 'left' } 
                },
                didParseCell: (data) => { 
                    if (data.section === 'body') { 
                        if (sigData.sheetData[data.row.index]?.isWeekend) data.cell.styles.fillColor = [241, 245, 249]; 
                        else if (data.row.index % 2 === 0) data.cell.styles.fillColor = [255, 255, 255]; 
                        if (data.column.index === 9 && data.cell.raw && data.cell.raw.includes('-')) data.cell.styles.textColor = [220, 0, 0]; 
                    } 
                },
                margin: { left: 10, right: 10, bottom: 2 }
            });

            let footerY = doc.lastAutoTable.finalY + 2; 

            doc.setFillColor(248, 250, 252); doc.setDrawColor(200); doc.roundedRect(10, footerY, 190, 7, 2, 2, 'FD');
            doc.setFontSize(6.5); doc.setFont("helvetica", "bold"); doc.setTextColor(100); doc.text("RESUMO BANCO DE HORAS:", 15, footerY + 5);
            doc.setTextColor(0); doc.setFont("helvetica", "normal"); doc.text(`Saldo Anterior:`, 70, footerY + 5); 
            doc.setFont("helvetica", "bold"); doc.text(toTimePonto(sigData.summary?.initial), 92, footerY + 5);
            doc.setFont("helvetica", "normal"); 
            const resultPeriod = (sigData.summary?.final || 0) - (sigData.summary?.initial || 0); 
            doc.text(`Resultado Período:`, 115, footerY + 5);
            if(resultPeriod < 0) doc.setTextColor(220, 38, 38); else doc.setTextColor(22, 163, 74); 
            doc.setFont("helvetica", "bold"); doc.text(toTimePonto(resultPeriod), 142, footerY + 5);
            doc.setTextColor(0); doc.text("SALDO FINAL:", 165, footerY + 5); 
            if((sigData.summary?.final||0) < 0) doc.setTextColor(220, 38, 38); else doc.setTextColor(22, 163, 74); 
            doc.text(toTimePonto(sigData.summary?.final), 187, footerY + 5);

            footerY += 9;
            doc.setFillColor(248, 250, 252); doc.setDrawColor(200); doc.roundedRect(10, footerY, 190, 7, 2, 2, 'FD');
            doc.setFontSize(6.5); doc.setFont("helvetica", "bold"); doc.setTextColor(100); doc.text("JORNADA DE TRABALHO BASE:", 15, footerY + 5);
            doc.setTextColor(...primaryColor); doc.setFont("helvetica", "bold"); doc.text(sigData.ruleString || "BASE: 08:00 ÀS 17:30 | ALMOÇO: 12:00 ÀS 13:00", 60, footerY + 5);

            footerY += 9;
            doc.setFillColor(...primaryColor); doc.rect(10, footerY, 190, 5, 'F'); 
            doc.setTextColor(255); doc.setFontSize(6.5); doc.setFont("helvetica", "bold"); 
            doc.text("ASSINATURAS ELETRÔNICAS", 105, footerY + 3.5, { align: "center" });

            let sigY = footerY + 5;
            const signaturesToPrint = [];
            signaturesToPrint.push({ role: "COLABORADOR", name: userName.substring(0, 30), date: new Date(sigData.date).toLocaleDateString('pt-BR'), hash: sigData.hash });
            
            if (sigData.managerSignature) signaturesToPrint.push({ role: "GESTOR", name: sigData.managerSignature.managerName.substring(0, 30), date: new Date(sigData.managerSignature.date).toLocaleDateString('pt-BR'), hash: sigData.managerSignature.hash });
            else signaturesToPrint.push({ role: "GESTOR", name: "Aguardando", date: "-", hash: "-" });

            doc.autoTable({
                startY: sigY, head: [['PAPEL', 'NOME', 'DATA', 'HASH / ID']], 
                body: signaturesToPrint.map(s => [s.role, s.name, s.date, s.hash]), 
                theme: 'grid', styles: { fontSize: 5.5, cellPadding: 1, textColor: [50], lineColor: [200] }, 
                headStyles: { fillColor: [240], textColor: [50], fontStyle: 'bold', halign: 'left', minCellHeight: 5 }, 
                columnStyles: { 0: { cellWidth: 25, fontStyle: 'bold' }, 1: { cellWidth: 65 }, 2: { cellWidth: 20, halign: 'center' }, 3: { cellWidth: 'auto', fontStyle: 'italic' } }, 
                margin: { left: 10, right: 10, bottom: 2 }
            });

            doc.setTextColor(150); doc.setFontSize(4.5); doc.setFont("helvetica", "italic"); 
            doc.text("Documento gerado eletronicamente pelo Sistema de Gestão de Pessoas (ACAD). Validade jurídica conforme MP 2.200-2/2001.", 105, doc.internal.pageSize.getHeight() - 4, { align: "center" });

            window.open(doc.output('bloburl'), '_blank');
        };

        if (status === 'loading') return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white"><svg className="animate-spin h-10 w-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg></div>;
        
        if (status === 'error') return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4 text-center">
                <div className="bg-slate-800 p-8 rounded-xl shadow-2xl max-w-md w-full border border-red-500/30">
                    <div className="text-red-500 text-5xl mb-4">⚠️</div>
                    <h2 className="text-white text-xl font-bold mb-2">Acesso Negado</h2>
                    <p className="text-slate-400">{errorMsg}</p>
                </div>
            </div>
        );

        if (status === 'login') return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
                <div className="bg-slate-800 rounded-3xl shadow-2xl p-8 max-w-md w-full border border-slate-700 animate-fade-in">
                    <div className="w-16 h-16 bg-blue-900/30 text-blue-400 rounded-full flex items-center justify-center mx-auto mb-4"><span className="text-3xl">🛡️</span></div>
                    <h2 className="text-2xl font-black text-center text-white mb-2">Acesso Restrito</h2>
                    <p className="text-center text-slate-400 text-sm mb-6">Valide sua identidade para visualizar o espelho de ponto assinado.</p>
                    
                    {errorMsg && <div className="bg-red-900/30 border border-red-800 text-red-400 p-3 rounded-lg text-sm font-bold mb-4 flex items-center gap-2">⚠️ {errorMsg}</div>}
                    
                    <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                            <label className="text-xs font-bold text-slate-400 block mb-1">Seu CPF (Apenas números)</label>
                            <input type="text" required value={cpf} onChange={e=>setCpf(e.target.value)} className="w-full bg-slate-900 border border-slate-700 text-white p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-mono" placeholder="00011122233" maxLength="14"/>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-400 block mb-1">Mês de Referência</label>
                            <input type="month" required value={month} onChange={e=>setMonth(e.target.value)} className="w-full bg-slate-900 border border-slate-700 text-white p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none invert-calendar"/>
                        </div>
                        <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 rounded-xl shadow-lg mt-4 flex justify-center items-center gap-2">
                            Validar Acesso →
                        </button>
                    </form>
                </div>
            </div>
        );

        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center py-10 px-4">
                <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden animate-fade-in">
                    <div className="bg-blue-600 p-6 text-white flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="text-center md:text-left">
                            <h2 className="text-2xl font-black">Comprovante de Assinatura</h2>
                            <p className="opacity-90">Espelho Eletrônico de Ponto - {sigData.month}</p>
                        </div>
                        <button onClick={handlePrintPDF} className="bg-white text-blue-600 px-6 py-3 rounded-xl font-black shadow-lg hover:bg-blue-50 transition-transform hover:scale-105 flex items-center gap-2 w-full md:w-auto justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
                            Salvar Recibo em PDF
                        </button>
                    </div>
                    <div className="p-8 space-y-6">
                        <div className="flex flex-wrap justify-between items-center border-b pb-4 gap-4">
                            <div>
                                <p className="text-xs text-gray-500 uppercase font-bold">Colaborador</p>
                                <p className="text-lg font-black text-gray-800">{sigData.userName}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-gray-500 uppercase font-bold">Assinado em</p>
                                <p className="text-sm font-mono font-bold text-gray-700">{new Date(sigData.date).toLocaleString('pt-BR')}</p>
                            </div>
                        </div>
                        
                        <div>
                            <p className="text-xs text-gray-500 uppercase font-bold mb-2">Resumo do Mês</p>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 text-center">
                                    <span className="text-xs text-gray-500 block mb-1">Saldo Anterior</span>
                                    <span className={`text-xl font-black ${sigData.summary?.initial < 0 ? 'text-red-500' : 'text-blue-600'}`}>{toTimePonto(sigData.summary?.initial)} h</span>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 text-center">
                                    <span className="text-xs text-gray-500 block mb-1">Saldo Mês Final</span>
                                    <span className={`text-xl font-black ${sigData.summary?.final < 0 ? 'text-red-500' : 'text-green-600'}`}>{toTimePonto(sigData.summary?.final)} h</span>
                                </div>
                            </div>
                        </div>

                        {/* RENDERIZAÇÃO DA TABELA DO ESPELHO SE EXISTIR NO BANCO */}
                        {sigData.sheetData && (
                            <div className="border rounded-xl overflow-hidden">
                                <div className="bg-gray-100 p-2 border-b text-xs font-bold text-gray-600 text-center uppercase tracking-wider">
                                    Detalhamento das Marcações
                                </div>
                                <div className="overflow-x-auto max-h-96 custom-scrollbar">
                                    <table className="w-full text-xs text-left">
                                        <thead className="bg-gray-50 border-b sticky top-0 font-bold uppercase text-[10px] text-gray-500">
                                            <tr>
                                                <th className="p-2">Data</th>
                                                <th className="p-2">Batidas</th>
                                                <th className="p-2 text-center">Horas Trab.</th>
                                                <th className="p-2 text-center">Saldo Dia</th>
                                                <th className="p-2">Observações</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {sigData.sheetData.map((row, i) => {
                                                const p = row.punches.map(punch => punch.time);
                                                const obsList = [];
                                                if (row.justificationsText) obsList.push(row.justificationsText);
                                                if (row.obsNote) obsList.push(`Obs: ${row.obsNote}`);
                                                const obsFinal = obsList.join(' | ');

                                                return (
                                                    <tr key={i} className={row.isWeekend ? 'bg-gray-50' : 'bg-white'}>
                                                        <td className="p-2 whitespace-nowrap font-bold">
                                                            {row.display.substring(0, 5)} <span className="text-[9px] uppercase font-normal text-gray-400">{row.week}</span>
                                                        </td>
                                                        <td className="p-2 font-mono">
                                                            <div className="flex flex-wrap gap-1">
                                                                {p.map((time, tIdx) => <span key={tIdx} className="bg-gray-100 border px-1 rounded font-bold">{time}</span>)}
                                                            </div>
                                                        </td>
                                                        <td className="p-2 text-center font-bold">{row.worked}</td>
                                                        <td className={`p-2 text-center font-bold ${row.balance < 0 ? 'text-red-500' : 'text-green-500'}`}>
                                                            {toTimePonto(row.balance)}
                                                        </td>
                                                        <td className="p-2 text-[10px] leading-tight max-w-[200px] truncate" title={obsFinal}>{obsFinal || '-'}</td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl text-sm text-blue-800">
                            <p className="font-bold flex items-center gap-2 mb-1">✅ Autenticidade Digital Comprovada</p>
                            <p>Este documento foi assinado eletronicamente e possui validade jurídica. A chave criptográfica (Hash) que garante a integridade desta assinatura é:</p>
                            <p className="font-mono text-xs bg-white p-2 rounded mt-2 border border-blue-100 break-all select-all">{sigData.hash}</p>
                        </div>
                        
                        {sigData.qrCodeUrl && (
                            <div className="flex flex-col items-center mt-6 pt-6 border-t border-gray-200">
                                <span className="text-xs text-gray-500 font-bold uppercase mb-2">QR Code de Validação</span>
                                <img src={sigData.qrCodeUrl} alt="QR Code Validação" className="w-32 h-32 border p-1 rounded-lg" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };



    /* ===============================================================================
     * [END_MOD_PUBLIC_POINT_SHEET]
     * FIM DO BLOCO: PublicPointSheet
     * [START_MOD_ROOT_COMPONENT]
     * INICIO DO BLOCO: RootComponent
     * =============================================================================== */
    const RootComponent = () => {
        // CAPTURA A URL COM SEGURANÇA PARA CELULARES
        const urlParams = new URLSearchParams(window.location.search);
        let publicTestId = urlParams.get('publicTest');
        let publicSheetId = urlParams.get('publicSheet'); // <--- NOVO
        
        // Tratamento para remover barras ("/") extras adicionadas por leitores de QR no celular
        if (publicTestId && publicTestId.endsWith('/')) publicTestId = publicTestId.slice(0, -1);
        if (publicSheetId && publicSheetId.endsWith('/')) publicSheetId = publicSheetId.slice(0, -1);

        // MODO: PROVA DO CANDIDATO
        if (publicTestId) {
            return (
                <>
                    <GlobalStyles />
                    <PublicTestRunner testId={publicTestId} />
                </>
            );
        }
        
        // MODO: VER ESPELHO DE PONTO (QR CODE)
        if (publicSheetId) {
            return (
                <>
                    <GlobalStyles />
                    <PublicPointSheet sigId={publicSheetId} />
                </>
            );
        }

        // MODO NORMAL (SISTEMA INTERNO)
        return (
            <>
                <GlobalStyles />
                <App />
            </>
        );
    };

    /* ===============================================================================
     * [END_MOD_ROOT_COMPONENT]
     * FIM DO BLOCO: RootComponent
     * [END_SEC_08_UI_COMPLEXOS]
     * [SEC_09_APP_BOOTSTRAP]
     * FIM DA ESTRUTURA PRINCIPAL / BOOTSTRAP FINAL
     * =============================================================================== */
    export default RootComponent;